/**
 * Tłumaczenia UI. Klucze grupowane wg obszaru (common, alerts, ...).
 * Język domyślny: en (fallback). Pozostałe języki dodawane przyrostowo —
 * brakujące klucze automatycznie spadają na en (i18next fallbackLng).
 *
 * Dodanie nowego języka: dopisz wpis do LANGUAGES (kod + nazwa natywna)
 * oraz blok tłumaczeń do `resources`. Konwersja kolejnych stron = dopisanie
 * nowych kluczy do KAŻDEGO języka (en obowiązkowo, reszta opcjonalnie).
 */

/** Wszystkie wspierane języki europejskie (kody ISO 639-1 / 639-2). */
export const SUPPORTED_LANGUAGES = [
  'en',
  'pl',
  'de',
  'fr',
  'es',
  'it',
  'pt',
  'nl',
  'da',
  'sv',
  'fi',
  'cs',
  'sk',
  'sl',
  'hr',
  'hu',
  'ro',
  'bg',
  'el',
  'et',
  'lv',
  'lt',
  'mt',
  'ga',
  'no',
  'is',
  'uk',
  'sr',
  'sq',
  'mk',
  'bs',
] as const;

/** Nazwy natywne — do menu wyboru języka. */
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  pl: 'Polski',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  da: 'Dansk',
  sv: 'Svenska',
  fi: 'Suomi',
  cs: 'Čeština',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
  hr: 'Hrvatski',
  hu: 'Magyar',
  ro: 'Română',
  bg: 'Български',
  el: 'Ελληνικά',
  et: 'Eesti',
  lv: 'Latviešu',
  lt: 'Lietuvių',
  mt: 'Malti',
  ga: 'Gaeilge',
  no: 'Norsk',
  is: 'Íslenska',
  uk: 'Українська',
  sr: 'Српски',
  sq: 'Shqip',
  mk: 'Македонски',
  bs: 'Bosanski',
};

type Dict = Record<string, string>;
type Bundle = { common: Dict; alerts: Dict };

const en: Bundle = {
  common: {
    loading: 'Loading…',
    language: 'Language',
  },
  alerts: {
    title: 'Weather alerts',
    activeFor: 'Active for {{name}}',
    yourLocation: 'your location',
    selectInstallation: '(select an installation)',
    none: 'No active weather alerts for your location.',
    mapTitle: 'Alerts map',
    mapSubtitle: '{{count}} active alerts. Colour = level (green/yellow/orange/red).',
    from: 'From',
    to: 'To',
    country: 'Country',
    level: 'level',
    types: 'Types',
    alertsCount: '{{count}} alerts',
    type_wind: 'Wind',
    type_snow_ice: 'Snow / Ice',
    type_thunderstorm: 'Thunderstorm',
    type_fog: 'Fog',
    type_high_temperature: 'High temperature',
    type_low_temperature: 'Low temperature',
    type_coastal: 'Coastal event',
    type_forest_fire: 'Forest fire',
    type_avalanche: 'Avalanche',
    type_rain: 'Rain',
    type_rain_flood: 'Rain-flood',
    type_flood: 'Flood',
    type_unknown: 'Unknown',
    severity_Minor: 'Minor',
    severity_Moderate: 'Moderate',
    severity_Severe: 'Severe',
    severity_Extreme: 'Extreme',
    severity_Unknown: 'Unknown',
    updatedAt: "Updated",
    refresh: "Refresh",
    refreshing: "Refreshing…",
    refreshTitle: "Force refresh from MeteoAlarm",
  },
};

const pl: Bundle = {
  common: { loading: 'Ładowanie…', language: 'Język' },
  alerts: {
    title: 'Ostrzeżenia pogodowe',
    activeFor: 'Aktywne dla {{name}}',
    yourLocation: 'Twojej lokalizacji',
    selectInstallation: '(wybierz instalację)',
    none: 'Brak aktywnych ostrzeżeń pogodowych w Twojej lokalizacji.',
    mapTitle: 'Mapa ostrzeżeń',
    mapSubtitle:
      '{{count}} aktywnych ostrzeżeń. Kolor = poziom (zielony/żółty/pomarańczowy/czerwony).',
    from: 'Od',
    to: 'Do',
    country: 'Kraj',
    level: 'poziom',
    types: 'Typy',
    alertsCount: 'liczba alertów: {{count}}',
    type_wind: 'Wiatr',
    type_snow_ice: 'Śnieg / Lód',
    type_thunderstorm: 'Burza',
    type_fog: 'Mgła',
    type_high_temperature: 'Wysoka temperatura',
    type_low_temperature: 'Niska temperatura',
    type_coastal: 'Wybrzeże',
    type_forest_fire: 'Pożar lasu',
    type_avalanche: 'Lawina',
    type_rain: 'Deszcz',
    type_rain_flood: 'Powódź opadowa',
    type_flood: 'Powódź',
    type_unknown: 'Nieznane',
    severity_Minor: 'Niski',
    severity_Moderate: 'Umiarkowany',
    severity_Severe: 'Poważny',
    severity_Extreme: 'Ekstremalny',
    severity_Unknown: 'Nieznany',
    updatedAt: "Aktualizacja",
    refresh: "Odśwież",
    refreshing: "Odświeżanie…",
    refreshTitle: "Wymuś odświeżenie z MeteoAlarm",
  },
};

const de: Bundle = {
  common: { loading: 'Wird geladen…', language: 'Sprache' },
  alerts: {
    title: 'Wetterwarnungen',
    activeFor: 'Aktiv für {{name}}',
    yourLocation: 'Ihren Standort',
    selectInstallation: '(Anlage auswählen)',
    none: 'Keine aktiven Wetterwarnungen für Ihren Standort.',
    mapTitle: 'Warnkarte',
    mapSubtitle: '{{count}} aktive Warnungen. Farbe = Stufe (grün/gelb/orange/rot).',
    from: 'Von',
    to: 'Bis',
    country: 'Land',
    level: 'Stufe',
    types: 'Arten',
    alertsCount: '{{count}} Warnungen',
    type_wind: 'Wind',
    type_snow_ice: 'Schnee / Eis',
    type_thunderstorm: 'Gewitter',
    type_fog: 'Nebel',
    type_high_temperature: 'Hohe Temperatur',
    type_low_temperature: 'Niedrige Temperatur',
    type_coastal: 'Küstenereignis',
    type_forest_fire: 'Waldbrand',
    type_avalanche: 'Lawine',
    type_rain: 'Regen',
    type_rain_flood: 'Regen-Hochwasser',
    type_flood: 'Hochwasser',
    type_unknown: 'Unbekannt',
    severity_Minor: 'Gering',
    severity_Moderate: 'Mäßig',
    severity_Severe: 'Schwer',
    severity_Extreme: 'Extrem',
    severity_Unknown: 'Unbekannt',
    updatedAt: "Aktualisiert",
    refresh: "Aktualisieren",
    refreshing: "Wird aktualisiert…",
    refreshTitle: "Aktualisierung von MeteoAlarm erzwingen",
  },
};

const fr: Bundle = {
  common: { loading: 'Chargement…', language: 'Langue' },
  alerts: {
    title: 'Alertes météo',
    activeFor: 'Actives pour {{name}}',
    yourLocation: 'votre emplacement',
    selectInstallation: '(sélectionnez une installation)',
    none: 'Aucune alerte météo active pour votre emplacement.',
    mapTitle: 'Carte des alertes',
    mapSubtitle: '{{count}} alertes actives. Couleur = niveau (vert/jaune/orange/rouge).',
    from: 'De',
    to: 'À',
    country: 'Pays',
    level: 'niveau',
    types: 'Types',
    alertsCount: '{{count}} alertes',
    type_wind: 'Vent',
    type_snow_ice: 'Neige / Verglas',
    type_thunderstorm: 'Orage',
    type_fog: 'Brouillard',
    type_high_temperature: 'Température élevée',
    type_low_temperature: 'Basse température',
    type_coastal: 'Phénomène côtier',
    type_forest_fire: 'Feu de forêt',
    type_avalanche: 'Avalanche',
    type_rain: 'Pluie',
    type_rain_flood: 'Pluie-inondation',
    type_flood: 'Inondation',
    type_unknown: 'Inconnu',
    severity_Minor: 'Mineur',
    severity_Moderate: 'Modéré',
    severity_Severe: 'Sévère',
    severity_Extreme: 'Extrême',
    severity_Unknown: 'Inconnu',
    updatedAt: "Mis à jour",
    refresh: "Actualiser",
    refreshing: "Actualisation…",
    refreshTitle: "Forcer l’actualisation depuis MeteoAlarm",
  },
};

const es: Bundle = {
  common: { loading: 'Cargando…', language: 'Idioma' },
  alerts: {
    title: 'Avisos meteorológicos',
    activeFor: 'Activos para {{name}}',
    yourLocation: 'su ubicación',
    selectInstallation: '(seleccione una instalación)',
    none: 'No hay avisos meteorológicos activos para su ubicación.',
    mapTitle: 'Mapa de avisos',
    mapSubtitle: '{{count}} avisos activos. Color = nivel (verde/amarillo/naranja/rojo).',
    from: 'Desde',
    to: 'Hasta',
    country: 'País',
    level: 'nivel',
    types: 'Tipos',
    alertsCount: '{{count}} avisos',
    type_wind: 'Viento',
    type_snow_ice: 'Nieve / Hielo',
    type_thunderstorm: 'Tormenta',
    type_fog: 'Niebla',
    type_high_temperature: 'Temperatura alta',
    type_low_temperature: 'Temperatura baja',
    type_coastal: 'Fenómeno costero',
    type_forest_fire: 'Incendio forestal',
    type_avalanche: 'Avalancha',
    type_rain: 'Lluvia',
    type_rain_flood: 'Lluvia-inundación',
    type_flood: 'Inundación',
    type_unknown: 'Desconocido',
    severity_Minor: 'Menor',
    severity_Moderate: 'Moderado',
    severity_Severe: 'Grave',
    severity_Extreme: 'Extremo',
    severity_Unknown: 'Desconocido',
    updatedAt: "Actualizado",
    refresh: "Actualizar",
    refreshing: "Actualizando…",
    refreshTitle: "Forzar actualización desde MeteoAlarm",
  },
};

const it: Bundle = {
  common: { loading: 'Caricamento…', language: 'Lingua' },
  alerts: {
    title: 'Allerte meteo',
    activeFor: 'Attive per {{name}}',
    yourLocation: 'la tua posizione',
    selectInstallation: '(seleziona un impianto)',
    none: 'Nessuna allerta meteo attiva per la tua posizione.',
    mapTitle: 'Mappa delle allerte',
    mapSubtitle: '{{count}} allerte attive. Colore = livello (verde/giallo/arancione/rosso).',
    from: 'Da',
    to: 'A',
    country: 'Paese',
    level: 'livello',
    types: 'Tipi',
    alertsCount: '{{count}} allerte',
    type_wind: 'Vento',
    type_snow_ice: 'Neve / Ghiaccio',
    type_thunderstorm: 'Temporale',
    type_fog: 'Nebbia',
    type_high_temperature: 'Temperatura elevata',
    type_low_temperature: 'Temperatura bassa',
    type_coastal: 'Evento costiero',
    type_forest_fire: 'Incendio boschivo',
    type_avalanche: 'Valanga',
    type_rain: 'Pioggia',
    type_rain_flood: 'Pioggia-alluvione',
    type_flood: 'Alluvione',
    type_unknown: 'Sconosciuto',
    severity_Minor: 'Lieve',
    severity_Moderate: 'Moderato',
    severity_Severe: 'Grave',
    severity_Extreme: 'Estremo',
    severity_Unknown: 'Sconosciuto',
    updatedAt: "Aggiornato",
    refresh: "Aggiorna",
    refreshing: "Aggiornamento…",
    refreshTitle: "Forza aggiornamento da MeteoAlarm",
  },
};

const pt: Bundle = {
  common: { loading: 'A carregar…', language: 'Idioma' },
  alerts: {
    title: 'Avisos meteorológicos',
    activeFor: 'Ativos para {{name}}',
    yourLocation: 'a sua localização',
    selectInstallation: '(selecione uma instalação)',
    none: 'Não há avisos meteorológicos ativos para a sua localização.',
    mapTitle: 'Mapa de avisos',
    mapSubtitle: '{{count}} avisos ativos. Cor = nível (verde/amarelo/laranja/vermelho).',
    from: 'De',
    to: 'Até',
    country: 'País',
    level: 'nível',
    types: 'Tipos',
    alertsCount: '{{count}} avisos',
    type_wind: 'Vento',
    type_snow_ice: 'Neve / Gelo',
    type_thunderstorm: 'Trovoada',
    type_fog: 'Nevoeiro',
    type_high_temperature: 'Temperatura alta',
    type_low_temperature: 'Temperatura baixa',
    type_coastal: 'Fenómeno costeiro',
    type_forest_fire: 'Incêndio florestal',
    type_avalanche: 'Avalanche',
    type_rain: 'Chuva',
    type_rain_flood: 'Chuva-inundação',
    type_flood: 'Inundação',
    type_unknown: 'Desconhecido',
    severity_Minor: 'Menor',
    severity_Moderate: 'Moderado',
    severity_Severe: 'Grave',
    severity_Extreme: 'Extremo',
    severity_Unknown: 'Desconhecido',
    updatedAt: "Atualizado",
    refresh: "Atualizar",
    refreshing: "A atualizar…",
    refreshTitle: "Forçar atualização do MeteoAlarm",
  },
};

const nl: Bundle = {
  common: { loading: 'Laden…', language: 'Taal' },
  alerts: {
    title: 'Weerwaarschuwingen',
    activeFor: 'Actief voor {{name}}',
    yourLocation: 'uw locatie',
    selectInstallation: '(selecteer een installatie)',
    none: 'Geen actieve weerwaarschuwingen voor uw locatie.',
    mapTitle: 'Waarschuwingskaart',
    mapSubtitle: '{{count}} actieve waarschuwingen. Kleur = niveau (groen/geel/oranje/rood).',
    from: 'Van',
    to: 'Tot',
    country: 'Land',
    level: 'niveau',
    types: 'Soorten',
    alertsCount: '{{count}} waarschuwingen',
    type_wind: 'Wind',
    type_snow_ice: 'Sneeuw / IJzel',
    type_thunderstorm: 'Onweer',
    type_fog: 'Mist',
    type_high_temperature: 'Hoge temperatuur',
    type_low_temperature: 'Lage temperatuur',
    type_coastal: 'Kustfenomeen',
    type_forest_fire: 'Bosbrand',
    type_avalanche: 'Lawine',
    type_rain: 'Regen',
    type_rain_flood: 'Regen-overstroming',
    type_flood: 'Overstroming',
    type_unknown: 'Onbekend',
    severity_Minor: 'Gering',
    severity_Moderate: 'Matig',
    severity_Severe: 'Ernstig',
    severity_Extreme: 'Extreem',
    severity_Unknown: 'Onbekend',
    updatedAt: "Bijgewerkt",
    refresh: "Vernieuwen",
    refreshing: "Vernieuwen…",
    refreshTitle: "Verversen van MeteoAlarm forceren",
  },
};

const cs: Bundle = {
  common: { loading: 'Načítání…', language: 'Jazyk' },
  alerts: {
    title: 'Výstrahy počasí',
    activeFor: 'Aktivní pro {{name}}',
    yourLocation: 'vaši polohu',
    selectInstallation: '(vyberte instalaci)',
    none: 'Žádné aktivní výstrahy počasí pro vaši polohu.',
    mapTitle: 'Mapa výstrah',
    mapSubtitle: '{{count}} aktivních výstrah. Barva = stupeň (zelená/žlutá/oranžová/červená).',
    from: 'Od',
    to: 'Do',
    country: 'Země',
    level: 'stupeň',
    types: 'Typy',
    alertsCount: 'počet výstrah: {{count}}',
    type_wind: 'Vítr',
    type_snow_ice: 'Sníh / Led',
    type_thunderstorm: 'Bouřka',
    type_fog: 'Mlha',
    type_high_temperature: 'Vysoká teplota',
    type_low_temperature: 'Nízká teplota',
    type_coastal: 'Pobřežní jev',
    type_forest_fire: 'Lesní požár',
    type_avalanche: 'Lavina',
    type_rain: 'Déšť',
    type_rain_flood: 'Přívalová povodeň',
    type_flood: 'Povodeň',
    type_unknown: 'Neznámé',
    severity_Minor: 'Nízká',
    severity_Moderate: 'Střední',
    severity_Severe: 'Vysoká',
    severity_Extreme: 'Extrémní',
    severity_Unknown: 'Neznámá',
    updatedAt: "Opdateret",
    refresh: "Opdater",
    refreshing: "Opdaterer…",
    refreshTitle: "Tving opdatering fra MeteoAlarm",
  },
};

const sk: Bundle = {
  common: { loading: 'Načítava sa…', language: 'Jazyk' },
  alerts: {
    title: 'Výstrahy počasia',
    activeFor: 'Aktívne pre {{name}}',
    yourLocation: 'vašu polohu',
    selectInstallation: '(vyberte inštaláciu)',
    none: 'Žiadne aktívne výstrahy počasia pre vašu polohu.',
    mapTitle: 'Mapa výstrah',
    mapSubtitle: '{{count}} aktívnych výstrah. Farba = stupeň (zelená/žltá/oranžová/červená).',
    from: 'Od',
    to: 'Do',
    country: 'Krajina',
    level: 'stupeň',
    types: 'Typy',
    alertsCount: 'počet výstrah: {{count}}',
    type_wind: 'Vietor',
    type_snow_ice: 'Sneh / Ľad',
    type_thunderstorm: 'Búrka',
    type_fog: 'Hmla',
    type_high_temperature: 'Vysoká teplota',
    type_low_temperature: 'Nízka teplota',
    type_coastal: 'Pobrežný jav',
    type_forest_fire: 'Lesný požiar',
    type_avalanche: 'Lavína',
    type_rain: 'Dážď',
    type_rain_flood: 'Prívalová povodeň',
    type_flood: 'Povodeň',
    type_unknown: 'Neznáme',
    severity_Minor: 'Nízka',
    severity_Moderate: 'Stredná',
    severity_Severe: 'Vysoká',
    severity_Extreme: 'Extrémna',
    severity_Unknown: 'Neznáma',
    updatedAt: "Uppdaterad",
    refresh: "Uppdatera",
    refreshing: "Uppdaterar…",
    refreshTitle: "Tvinga uppdatering från MeteoAlarm",
  },
};

const lt: Bundle = {
  common: { loading: 'Įkeliama…', language: 'Kalba' },
  alerts: {
    title: 'Orų įspėjimai',
    activeFor: 'Aktyvūs vietai {{name}}',
    yourLocation: 'jūsų vietai',
    selectInstallation: '(pasirinkite įrenginį)',
    none: 'Jūsų vietai nėra aktyvių orų įspėjimų.',
    mapTitle: 'Įspėjimų žemėlapis',
    mapSubtitle: '{{count}} aktyvių įspėjimų. Spalva = lygis (žalia/geltona/oranžinė/raudona).',
    from: 'Nuo',
    to: 'Iki',
    country: 'Šalis',
    level: 'lygis',
    types: 'Tipai',
    alertsCount: 'įspėjimų: {{count}}',
    type_wind: 'Vėjas',
    type_snow_ice: 'Sniegas / Ledas',
    type_thunderstorm: 'Perkūnija',
    type_fog: 'Rūkas',
    type_high_temperature: 'Aukšta temperatūra',
    type_low_temperature: 'Žema temperatūra',
    type_coastal: 'Pakrantės reiškinys',
    type_forest_fire: 'Miško gaisras',
    type_avalanche: 'Lavina',
    type_rain: 'Lietus',
    type_rain_flood: 'Liūties potvynis',
    type_flood: 'Potvynis',
    type_unknown: 'Nežinoma',
    severity_Minor: 'Nedidelis',
    severity_Moderate: 'Vidutinis',
    severity_Severe: 'Didelis',
    severity_Extreme: 'Ekstremalus',
    severity_Unknown: 'Nežinomas',
    updatedAt: "Päivitetty",
    refresh: "Päivitä",
    refreshing: "Päivittää…",
    refreshTitle: "Pakota päivitys MeteoAlarmista",
  },
};

const hu: Bundle = {
  common: { loading: 'Betöltés…', language: 'Nyelv' },
  alerts: {
    title: 'Időjárási riasztások',
    activeFor: 'Aktív erre: {{name}}',
    yourLocation: 'a helyszínére',
    selectInstallation: '(válasszon telepítést)',
    none: 'Nincs aktív időjárási riasztás a helyszínén.',
    mapTitle: 'Riasztási térkép',
    mapSubtitle: '{{count}} aktív riasztás. Szín = szint (zöld/sárga/narancs/piros).',
    from: 'Ettől',
    to: 'Eddig',
    country: 'Ország',
    level: 'szint',
    types: 'Típusok',
    alertsCount: '{{count}} riasztás',
    type_wind: 'Szél',
    type_snow_ice: 'Hó / Jég',
    type_thunderstorm: 'Zivatar',
    type_fog: 'Köd',
    type_high_temperature: 'Magas hőmérséklet',
    type_low_temperature: 'Alacsony hőmérséklet',
    type_coastal: 'Parti jelenség',
    type_forest_fire: 'Erdőtűz',
    type_avalanche: 'Lavina',
    type_rain: 'Eső',
    type_rain_flood: 'Esőzés okozta árvíz',
    type_flood: 'Árvíz',
    type_unknown: 'Ismeretlen',
    severity_Minor: 'Csekély',
    severity_Moderate: 'Mérsékelt',
    severity_Severe: 'Súlyos',
    severity_Extreme: 'Extrém',
    severity_Unknown: 'Ismeretlen',
    updatedAt: "Aktualizováno",
    refresh: "Obnovit",
    refreshing: "Obnovování…",
    refreshTitle: "Vynutit obnovení z MeteoAlarm",
  },
};

const uk: Bundle = {
  common: { loading: 'Завантаження…', language: 'Мова' },
  alerts: {
    title: 'Погодні попередження',
    activeFor: 'Активні для {{name}}',
    yourLocation: 'вашого розташування',
    selectInstallation: '(виберіть установку)',
    none: 'Немає активних погодних попереджень для вашого розташування.',
    mapTitle: 'Карта попереджень',
    mapSubtitle:
      '{{count}} активних попереджень. Колір = рівень (зелений/жовтий/помаранчевий/червоний).',
    from: 'Від',
    to: 'До',
    country: 'Країна',
    level: 'рівень',
    types: 'Типи',
    alertsCount: 'попереджень: {{count}}',
    type_wind: 'Вітер',
    type_snow_ice: 'Сніг / Ожеледь',
    type_thunderstorm: 'Гроза',
    type_fog: 'Туман',
    type_high_temperature: 'Висока температура',
    type_low_temperature: 'Низька температура',
    type_coastal: 'Прибережне явище',
    type_forest_fire: 'Лісова пожежа',
    type_avalanche: 'Лавина',
    type_rain: 'Дощ',
    type_rain_flood: 'Зливова повінь',
    type_flood: 'Повінь',
    type_unknown: 'Невідомо',
    severity_Minor: 'Незначний',
    severity_Moderate: 'Помірний',
    severity_Severe: 'Сильний',
    severity_Extreme: 'Екстремальний',
    severity_Unknown: 'Невідомий',
    updatedAt: "Aktualizované",
    refresh: "Obnoviť",
    refreshing: "Obnovuje sa…",
    refreshTitle: "Vynútiť obnovenie z MeteoAlarm",
  },
};

const da: Bundle = {
  common: { loading: 'Indlæser…', language: 'Sprog' },
  alerts: {
    title: 'Vejrvarsler',
    activeFor: 'Aktive for {{name}}',
    yourLocation: 'din placering',
    selectInstallation: '(vælg et anlæg)',
    none: 'Ingen aktive vejrvarsler for din placering.',
    mapTitle: 'Varselkort',
    mapSubtitle: '{{count}} aktive varsler. Farve = niveau (grøn/gul/orange/rød).',
    from: 'Fra',
    to: 'Til',
    country: 'Land',
    level: 'niveau',
    types: 'Typer',
    alertsCount: '{{count}} varsler',
    type_wind: 'Vind',
    type_snow_ice: 'Sne / Is',
    type_thunderstorm: 'Tordenvejr',
    type_fog: 'Tåge',
    type_high_temperature: 'Høj temperatur',
    type_low_temperature: 'Lav temperatur',
    type_coastal: 'Kysthændelse',
    type_forest_fire: 'Skovbrand',
    type_avalanche: 'Lavine',
    type_rain: 'Regn',
    type_rain_flood: 'Regn-oversvømmelse',
    type_flood: 'Oversvømmelse',
    type_unknown: 'Ukendt',
    severity_Minor: 'Mindre',
    severity_Moderate: 'Moderat',
    severity_Severe: 'Alvorlig',
    severity_Extreme: 'Ekstrem',
    severity_Unknown: 'Ukendt',
    updatedAt: "Posodobljeno",
    refresh: "Osveži",
    refreshing: "Osveževanje…",
    refreshTitle: "Prisilno osveži iz MeteoAlarm",
  },
};

const sv: Bundle = {
  common: { loading: 'Laddar…', language: 'Språk' },
  alerts: {
    title: 'Vädervarningar',
    activeFor: 'Aktiva för {{name}}',
    yourLocation: 'din plats',
    selectInstallation: '(välj en anläggning)',
    none: 'Inga aktiva vädervarningar för din plats.',
    mapTitle: 'Varningskarta',
    mapSubtitle: '{{count}} aktiva varningar. Färg = nivå (grön/gul/orange/röd).',
    from: 'Från',
    to: 'Till',
    country: 'Land',
    level: 'nivå',
    types: 'Typer',
    alertsCount: '{{count}} varningar',
    type_wind: 'Vind',
    type_snow_ice: 'Snö / Is',
    type_thunderstorm: 'Åska',
    type_fog: 'Dimma',
    type_high_temperature: 'Hög temperatur',
    type_low_temperature: 'Låg temperatur',
    type_coastal: 'Kusthändelse',
    type_forest_fire: 'Skogsbrand',
    type_avalanche: 'Lavin',
    type_rain: 'Regn',
    type_rain_flood: 'Regnöversvämning',
    type_flood: 'Översvämning',
    type_unknown: 'Okänd',
    severity_Minor: 'Mindre',
    severity_Moderate: 'Måttlig',
    severity_Severe: 'Allvarlig',
    severity_Extreme: 'Extrem',
    severity_Unknown: 'Okänd',
    updatedAt: "Ažurirano",
    refresh: "Osvježi",
    refreshing: "Osvježavanje…",
    refreshTitle: "Prisilno osvježi iz MeteoAlarm",
  },
};

const fi: Bundle = {
  common: { loading: 'Ladataan…', language: 'Kieli' },
  alerts: {
    title: 'Säävaroitukset',
    activeFor: 'Aktiiviset kohteelle {{name}}',
    yourLocation: 'sijaintiasi',
    selectInstallation: '(valitse laitos)',
    none: 'Ei aktiivisia säävaroituksia sijainnillesi.',
    mapTitle: 'Varoituskartta',
    mapSubtitle:
      '{{count}} aktiivista varoitusta. Väri = taso (vihreä/keltainen/oranssi/punainen).',
    from: 'Alkaen',
    to: 'Asti',
    country: 'Maa',
    level: 'taso',
    types: 'Tyypit',
    alertsCount: '{{count}} varoitusta',
    type_wind: 'Tuuli',
    type_snow_ice: 'Lumi / Jää',
    type_thunderstorm: 'Ukkonen',
    type_fog: 'Sumu',
    type_high_temperature: 'Korkea lämpötila',
    type_low_temperature: 'Matala lämpötila',
    type_coastal: 'Rannikkoilmiö',
    type_forest_fire: 'Metsäpalo',
    type_avalanche: 'Lumivyöry',
    type_rain: 'Sade',
    type_rain_flood: 'Sadetulva',
    type_flood: 'Tulva',
    type_unknown: 'Tuntematon',
    severity_Minor: 'Vähäinen',
    severity_Moderate: 'Kohtalainen',
    severity_Severe: 'Vakava',
    severity_Extreme: 'Äärimmäinen',
    severity_Unknown: 'Tuntematon',
    updatedAt: "Frissítve",
    refresh: "Frissítés",
    refreshing: "Frissítés…",
    refreshTitle: "Frissítés kényszerítése a MeteoAlarmból",
  },
};

const sl: Bundle = {
  common: { loading: 'Nalaganje…', language: 'Jezik' },
  alerts: {
    title: 'Vremenska opozorila',
    activeFor: 'Aktivna za {{name}}',
    yourLocation: 'vašo lokacijo',
    selectInstallation: '(izberite napravo)',
    none: 'Ni aktivnih vremenskih opozoril za vašo lokacijo.',
    mapTitle: 'Zemljevid opozoril',
    mapSubtitle: '{{count}} aktivnih opozoril. Barva = stopnja (zelena/rumena/oranžna/rdeča).',
    from: 'Od',
    to: 'Do',
    country: 'Država',
    level: 'stopnja',
    types: 'Vrste',
    alertsCount: 'opozoril: {{count}}',
    type_wind: 'Veter',
    type_snow_ice: 'Sneg / Led',
    type_thunderstorm: 'Nevihta',
    type_fog: 'Megla',
    type_high_temperature: 'Visoka temperatura',
    type_low_temperature: 'Nizka temperatura',
    type_coastal: 'Obalni pojav',
    type_forest_fire: 'Gozdni požar',
    type_avalanche: 'Plaz',
    type_rain: 'Dež',
    type_rain_flood: 'Hudourniška poplava',
    type_flood: 'Poplava',
    type_unknown: 'Neznano',
    severity_Minor: 'Manjše',
    severity_Moderate: 'Zmerno',
    severity_Severe: 'Resno',
    severity_Extreme: 'Ekstremno',
    severity_Unknown: 'Neznano',
    updatedAt: "Actualizat",
    refresh: "Reîmprospătare",
    refreshing: "Se reîmprospătează…",
    refreshTitle: "Forțează reîmprospătarea de la MeteoAlarm",
  },
};

const hr: Bundle = {
  common: { loading: 'Učitavanje…', language: 'Jezik' },
  alerts: {
    title: 'Vremenska upozorenja',
    activeFor: 'Aktivna za {{name}}',
    yourLocation: 'vašu lokaciju',
    selectInstallation: '(odaberite postrojenje)',
    none: 'Nema aktivnih vremenskih upozorenja za vašu lokaciju.',
    mapTitle: 'Karta upozorenja',
    mapSubtitle: '{{count}} aktivnih upozorenja. Boja = razina (zelena/žuta/narančasta/crvena).',
    from: 'Od',
    to: 'Do',
    country: 'Država',
    level: 'razina',
    types: 'Vrste',
    alertsCount: 'upozorenja: {{count}}',
    type_wind: 'Vjetar',
    type_snow_ice: 'Snijeg / Led',
    type_thunderstorm: 'Grmljavinsko nevrijeme',
    type_fog: 'Magla',
    type_high_temperature: 'Visoka temperatura',
    type_low_temperature: 'Niska temperatura',
    type_coastal: 'Obalni događaj',
    type_forest_fire: 'Šumski požar',
    type_avalanche: 'Lavina',
    type_rain: 'Kiša',
    type_rain_flood: 'Bujična poplava',
    type_flood: 'Poplava',
    type_unknown: 'Nepoznato',
    severity_Minor: 'Manje',
    severity_Moderate: 'Umjereno',
    severity_Severe: 'Ozbiljno',
    severity_Extreme: 'Ekstremno',
    severity_Unknown: 'Nepoznato',
    updatedAt: "Актуализирано",
    refresh: "Опресни",
    refreshing: "Опресняване…",
    refreshTitle: "Принудително опресни от MeteoAlarm",
  },
};

const ro: Bundle = {
  common: { loading: 'Se încarcă…', language: 'Limbă' },
  alerts: {
    title: 'Avertizări meteo',
    activeFor: 'Active pentru {{name}}',
    yourLocation: 'locația dvs.',
    selectInstallation: '(selectați o instalație)',
    none: 'Nu există avertizări meteo active pentru locația dvs.',
    mapTitle: 'Harta avertizărilor',
    mapSubtitle: '{{count}} avertizări active. Culoare = nivel (verde/galben/portocaliu/roșu).',
    from: 'De la',
    to: 'Până la',
    country: 'Țară',
    level: 'nivel',
    types: 'Tipuri',
    alertsCount: '{{count}} avertizări',
    type_wind: 'Vânt',
    type_snow_ice: 'Zăpadă / Gheață',
    type_thunderstorm: 'Furtună',
    type_fog: 'Ceață',
    type_high_temperature: 'Temperatură ridicată',
    type_low_temperature: 'Temperatură scăzută',
    type_coastal: 'Fenomen costier',
    type_forest_fire: 'Incendiu de pădure',
    type_avalanche: 'Avalanșă',
    type_rain: 'Ploaie',
    type_rain_flood: 'Inundație pluvială',
    type_flood: 'Inundație',
    type_unknown: 'Necunoscut',
    severity_Minor: 'Minor',
    severity_Moderate: 'Moderat',
    severity_Severe: 'Sever',
    severity_Extreme: 'Extrem',
    severity_Unknown: 'Necunoscut',
    updatedAt: "Ενημερώθηκε",
    refresh: "Ανανέωση",
    refreshing: "Ανανέωση…",
    refreshTitle: "Εξαναγκασμός ανανέωσης από το MeteoAlarm",
  },
};

const bg: Bundle = {
  common: { loading: 'Зареждане…', language: 'Език' },
  alerts: {
    title: 'Метеорологични предупреждения',
    activeFor: 'Активни за {{name}}',
    yourLocation: 'вашето местоположение',
    selectInstallation: '(изберете инсталация)',
    none: 'Няма активни метеорологични предупреждения за вашето местоположение.',
    mapTitle: 'Карта на предупрежденията',
    mapSubtitle: '{{count}} активни предупреждения. Цвят = ниво (зелено/жълто/оранжево/червено).',
    from: 'От',
    to: 'До',
    country: 'Държава',
    level: 'ниво',
    types: 'Видове',
    alertsCount: 'предупреждения: {{count}}',
    type_wind: 'Вятър',
    type_snow_ice: 'Сняг / Лед',
    type_thunderstorm: 'Гръмотевична буря',
    type_fog: 'Мъгла',
    type_high_temperature: 'Висока температура',
    type_low_temperature: 'Ниска температура',
    type_coastal: 'Крайбрежно явление',
    type_forest_fire: 'Горски пожар',
    type_avalanche: 'Лавина',
    type_rain: 'Дъжд',
    type_rain_flood: 'Поройно наводнение',
    type_flood: 'Наводнение',
    type_unknown: 'Неизвестно',
    severity_Minor: 'Незначително',
    severity_Moderate: 'Умерено',
    severity_Severe: 'Сериозно',
    severity_Extreme: 'Екстремно',
    severity_Unknown: 'Неизвестно',
    updatedAt: "Värskendatud",
    refresh: "Värskenda",
    refreshing: "Värskendamine…",
    refreshTitle: "Sundvärskendus MeteoAlarmist",
  },
};

const el: Bundle = {
  common: { loading: 'Φόρτωση…', language: 'Γλώσσα' },
  alerts: {
    title: 'Καιρικές προειδοποιήσεις',
    activeFor: 'Ενεργές για {{name}}',
    yourLocation: 'την τοποθεσία σας',
    selectInstallation: '(επιλέξτε εγκατάσταση)',
    none: 'Δεν υπάρχουν ενεργές καιρικές προειδοποιήσεις για την τοποθεσία σας.',
    mapTitle: 'Χάρτης προειδοποιήσεων',
    mapSubtitle:
      '{{count}} ενεργές προειδοποιήσεις. Χρώμα = επίπεδο (πράσινο/κίτρινο/πορτοκαλί/κόκκινο).',
    from: 'Από',
    to: 'Έως',
    country: 'Χώρα',
    level: 'επίπεδο',
    types: 'Τύποι',
    alertsCount: '{{count}} προειδοποιήσεις',
    type_wind: 'Άνεμος',
    type_snow_ice: 'Χιόνι / Πάγος',
    type_thunderstorm: 'Καταιγίδα',
    type_fog: 'Ομίχλη',
    type_high_temperature: 'Υψηλή θερμοκρασία',
    type_low_temperature: 'Χαμηλή θερμοκρασία',
    type_coastal: 'Παράκτιο φαινόμενο',
    type_forest_fire: 'Δασική πυρκαγιά',
    type_avalanche: 'Χιονοστιβάδα',
    type_rain: 'Βροχή',
    type_rain_flood: 'Πλημμύρα από βροχή',
    type_flood: 'Πλημμύρα',
    type_unknown: 'Άγνωστο',
    severity_Minor: 'Ήσσονος σημασίας',
    severity_Moderate: 'Μέτρια',
    severity_Severe: 'Σοβαρή',
    severity_Extreme: 'Ακραία',
    severity_Unknown: 'Άγνωστη',
    updatedAt: "Atjaunināts",
    refresh: "Atsvaidzināt",
    refreshing: "Atsvaidzina…",
    refreshTitle: "Piespiedu atsvaidzināšana no MeteoAlarm",
  },
};

const et: Bundle = {
  common: { loading: 'Laadimine…', language: 'Keel' },
  alerts: {
    title: 'Ilmahoiatused',
    activeFor: 'Aktiivsed asukohale {{name}}',
    yourLocation: 'teie asukohale',
    selectInstallation: '(valige paigaldis)',
    none: 'Teie asukohale pole aktiivseid ilmahoiatusi.',
    mapTitle: 'Hoiatuste kaart',
    mapSubtitle: '{{count}} aktiivset hoiatust. Värv = tase (roheline/kollane/oranž/punane).',
    from: 'Alates',
    to: 'Kuni',
    country: 'Riik',
    level: 'tase',
    types: 'Tüübid',
    alertsCount: '{{count}} hoiatust',
    type_wind: 'Tuul',
    type_snow_ice: 'Lumi / Jää',
    type_thunderstorm: 'Äike',
    type_fog: 'Udu',
    type_high_temperature: 'Kõrge temperatuur',
    type_low_temperature: 'Madal temperatuur',
    type_coastal: 'Rannikunähtus',
    type_forest_fire: 'Metsapõleng',
    type_avalanche: 'Laviin',
    type_rain: 'Vihm',
    type_rain_flood: 'Vihmast tingitud üleujutus',
    type_flood: 'Üleujutus',
    type_unknown: 'Teadmata',
    severity_Minor: 'Väike',
    severity_Moderate: 'Mõõdukas',
    severity_Severe: 'Tõsine',
    severity_Extreme: 'Äärmuslik',
    severity_Unknown: 'Teadmata',
    updatedAt: "Atnaujinta",
    refresh: "Atnaujinti",
    refreshing: "Atnaujinama…",
    refreshTitle: "Priverstinai atnaujinti iš MeteoAlarm",
  },
};

const lv: Bundle = {
  common: { loading: 'Notiek ielāde…', language: 'Valoda' },
  alerts: {
    title: 'Laikapstākļu brīdinājumi',
    activeFor: 'Aktīvi vietai {{name}}',
    yourLocation: 'jūsu atrašanās vietai',
    selectInstallation: '(izvēlieties iekārtu)',
    none: 'Jūsu atrašanās vietai nav aktīvu laikapstākļu brīdinājumu.',
    mapTitle: 'Brīdinājumu karte',
    mapSubtitle: '{{count}} aktīvi brīdinājumi. Krāsa = līmenis (zaļš/dzeltens/oranžs/sarkans).',
    from: 'No',
    to: 'Līdz',
    country: 'Valsts',
    level: 'līmenis',
    types: 'Veidi',
    alertsCount: 'brīdinājumi: {{count}}',
    type_wind: 'Vējš',
    type_snow_ice: 'Sniegs / Ledus',
    type_thunderstorm: 'Pērkona negaiss',
    type_fog: 'Migla',
    type_high_temperature: 'Augsta temperatūra',
    type_low_temperature: 'Zema temperatūra',
    type_coastal: 'Piekrastes parādība',
    type_forest_fire: 'Meža ugunsgrēks',
    type_avalanche: 'Lavīna',
    type_rain: 'Lietus',
    type_rain_flood: 'Lietus plūdi',
    type_flood: 'Plūdi',
    type_unknown: 'Nezināms',
    severity_Minor: 'Neliels',
    severity_Moderate: 'Mērens',
    severity_Severe: 'Nopietns',
    severity_Extreme: 'Ekstrēms',
    severity_Unknown: 'Nezināms',
    updatedAt: "Aġġornat",
    refresh: "Aġġorna",
    refreshing: "Aġġornament…",
    refreshTitle: "Aġġornament sfurzat minn MeteoAlarm",
  },
};

const mt: Bundle = {
  common: { loading: 'Qed jitgħabba…', language: 'Lingwa' },
  alerts: {
    title: 'Twissijiet tat-temp',
    activeFor: 'Attivi għal {{name}}',
    yourLocation: 'il-post tiegħek',
    selectInstallation: '(agħżel installazzjoni)',
    none: 'Ebda twissija tat-temp attiva għal post tiegħek.',
    mapTitle: 'Mappa tat-twissijiet',
    mapSubtitle: '{{count}} twissijiet attivi. Kulur = livell (aħdar/isfar/oranġjo/aħmar).',
    from: 'Minn',
    to: 'Sa',
    country: 'Pajjiż',
    level: 'livell',
    types: 'Tipi',
    alertsCount: '{{count}} twissijiet',
    type_wind: 'Riħ',
    type_snow_ice: 'Borra / Silġ',
    type_thunderstorm: 'Maltempata bir-ragħad',
    type_fog: 'Ċpar',
    type_high_temperature: 'Temperatura għolja',
    type_low_temperature: 'Temperatura baxxa',
    type_coastal: 'Fenomenu kostali',
    type_forest_fire: 'Nar fil-foresti',
    type_avalanche: 'Valanga',
    type_rain: 'Xita',
    type_rain_flood: 'Għargħar mix-xita',
    type_flood: 'Għargħar',
    type_unknown: 'Mhux magħruf',
    severity_Minor: 'Minuri',
    severity_Moderate: 'Moderat',
    severity_Severe: 'Sever',
    severity_Extreme: 'Estrem',
    severity_Unknown: 'Mhux magħruf',
    updatedAt: "Nuashonraithe",
    refresh: "Athnuaigh",
    refreshing: "Á athnuachan…",
    refreshTitle: "Brúigh athnuachan ó MeteoAlarm",
  },
};

const ga: Bundle = {
  common: { loading: 'Ag lódáil…', language: 'Teanga' },
  alerts: {
    title: 'Rabhaidh aimsire',
    activeFor: 'Gníomhach do {{name}}',
    yourLocation: 'do shuíomh',
    selectInstallation: '(roghnaigh suiteáil)',
    none: 'Níl aon rabhadh aimsire gníomhach do do shuíomh.',
    mapTitle: 'Léarscáil rabhaidh',
    mapSubtitle: '{{count}} rabhadh gníomhach. Dath = leibhéal (glas/buí/oráiste/dearg).',
    from: 'Ó',
    to: 'Go',
    country: 'Tír',
    level: 'leibhéal',
    types: 'Cineálacha',
    alertsCount: '{{count}} rabhadh',
    type_wind: 'Gaoth',
    type_snow_ice: 'Sneachta / Oighear',
    type_thunderstorm: 'Stoirm thoirní',
    type_fog: 'Ceo',
    type_high_temperature: 'Teocht ard',
    type_low_temperature: 'Teocht íseal',
    type_coastal: 'Imeacht cósta',
    type_forest_fire: 'Dóiteán foraoise',
    type_avalanche: 'Maidhm shneachta',
    type_rain: 'Báisteach',
    type_rain_flood: 'Tuile bháistí',
    type_flood: 'Tuile',
    type_unknown: 'Anaithnid',
    severity_Minor: 'Mion',
    severity_Moderate: 'Measartha',
    severity_Severe: 'Trom',
    severity_Extreme: 'Antoisceach',
    severity_Unknown: 'Anaithnid',
    updatedAt: "Oppdatert",
    refresh: "Oppdater",
    refreshing: "Oppdaterer…",
    refreshTitle: "Tving oppdatering fra MeteoAlarm",
  },
};

const no: Bundle = {
  common: { loading: 'Laster…', language: 'Språk' },
  alerts: {
    title: 'Værvarsler',
    activeFor: 'Aktive for {{name}}',
    yourLocation: 'din plassering',
    selectInstallation: '(velg et anlegg)',
    none: 'Ingen aktive værvarsler for din plassering.',
    mapTitle: 'Varselkart',
    mapSubtitle: '{{count}} aktive varsler. Farge = nivå (grønn/gul/oransje/rød).',
    from: 'Fra',
    to: 'Til',
    country: 'Land',
    level: 'nivå',
    types: 'Typer',
    alertsCount: '{{count}} varsler',
    type_wind: 'Vind',
    type_snow_ice: 'Snø / Is',
    type_thunderstorm: 'Tordenvær',
    type_fog: 'Tåke',
    type_high_temperature: 'Høy temperatur',
    type_low_temperature: 'Lav temperatur',
    type_coastal: 'Kysthendelse',
    type_forest_fire: 'Skogbrann',
    type_avalanche: 'Snøskred',
    type_rain: 'Regn',
    type_rain_flood: 'Regnflom',
    type_flood: 'Flom',
    type_unknown: 'Ukjent',
    severity_Minor: 'Mindre',
    severity_Moderate: 'Moderat',
    severity_Severe: 'Alvorlig',
    severity_Extreme: 'Ekstrem',
    severity_Unknown: 'Ukjent',
    updatedAt: "Uppfært",
    refresh: "Endurnýja",
    refreshing: "Endurnýjun…",
    refreshTitle: "Þvinga endurnýjun frá MeteoAlarm",
  },
};

const is: Bundle = {
  common: { loading: 'Hleður…', language: 'Tungumál' },
  alerts: {
    title: 'Veðurviðvaranir',
    activeFor: 'Virkar fyrir {{name}}',
    yourLocation: 'staðsetningu þína',
    selectInstallation: '(veldu virkjun)',
    none: 'Engar virkar veðurviðvaranir fyrir staðsetningu þína.',
    mapTitle: 'Viðvörunarkort',
    mapSubtitle: '{{count}} virkar viðvaranir. Litur = stig (grænt/gult/appelsínugult/rautt).',
    from: 'Frá',
    to: 'Til',
    country: 'Land',
    level: 'stig',
    types: 'Tegundir',
    alertsCount: '{{count}} viðvaranir',
    type_wind: 'Vindur',
    type_snow_ice: 'Snjór / Ís',
    type_thunderstorm: 'Þrumuveður',
    type_fog: 'Þoka',
    type_high_temperature: 'Hár hiti',
    type_low_temperature: 'Lágur hiti',
    type_coastal: 'Strandfyrirbæri',
    type_forest_fire: 'Skógareldur',
    type_avalanche: 'Snjóflóð',
    type_rain: 'Rigning',
    type_rain_flood: 'Rigningarflóð',
    type_flood: 'Flóð',
    type_unknown: 'Óþekkt',
    severity_Minor: 'Minniháttar',
    severity_Moderate: 'Miðlungs',
    severity_Severe: 'Alvarlegt',
    severity_Extreme: 'Mjög alvarlegt',
    severity_Unknown: 'Óþekkt',
    updatedAt: "Оновлено",
    refresh: "Оновити",
    refreshing: "Оновлення…",
    refreshTitle: "Примусово оновити з MeteoAlarm",
  },
};

const sr: Bundle = {
  common: { loading: 'Učitavanje…', language: 'Jezik' },
  alerts: {
    title: 'Vremenska upozorenja',
    activeFor: 'Aktivna za {{name}}',
    yourLocation: 'vašu lokaciju',
    selectInstallation: '(izaberite postrojenje)',
    none: 'Nema aktivnih vremenskih upozorenja za vašu lokaciju.',
    mapTitle: 'Mapa upozorenja',
    mapSubtitle: '{{count}} aktivnih upozorenja. Boja = nivo (zelena/žuta/narandžasta/crvena).',
    from: 'Od',
    to: 'Do',
    country: 'Država',
    level: 'nivo',
    types: 'Vrste',
    alertsCount: 'upozorenja: {{count}}',
    type_wind: 'Vetar',
    type_snow_ice: 'Sneg / Led',
    type_thunderstorm: 'Grmljavinska oluja',
    type_fog: 'Magla',
    type_high_temperature: 'Visoka temperatura',
    type_low_temperature: 'Niska temperatura',
    type_coastal: 'Priobalni događaj',
    type_forest_fire: 'Šumski požar',
    type_avalanche: 'Lavina',
    type_rain: 'Kiša',
    type_rain_flood: 'Bujična poplava',
    type_flood: 'Poplava',
    type_unknown: 'Nepoznato',
    severity_Minor: 'Manje',
    severity_Moderate: 'Umereno',
    severity_Severe: 'Ozbiljno',
    severity_Extreme: 'Ekstremno',
    severity_Unknown: 'Nepoznato',
    updatedAt: "Ажурирано",
    refresh: "Освежи",
    refreshing: "Освежавање…",
    refreshTitle: "Принудно освежи са MeteoAlarm",
  },
};

const sq: Bundle = {
  common: { loading: 'Po ngarkohet…', language: 'Gjuha' },
  alerts: {
    title: 'Paralajmërime moti',
    activeFor: 'Aktive për {{name}}',
    yourLocation: 'vendndodhjen tuaj',
    selectInstallation: '(zgjidhni një instalim)',
    none: 'Nuk ka paralajmërime moti aktive për vendndodhjen tuaj.',
    mapTitle: 'Harta e paralajmërimeve',
    mapSubtitle:
      '{{count}} paralajmërime aktive. Ngjyra = niveli (jeshile/verdhë/portokalli/kuqe).',
    from: 'Nga',
    to: 'Deri',
    country: 'Shteti',
    level: 'niveli',
    types: 'Llojet',
    alertsCount: '{{count}} paralajmërime',
    type_wind: 'Erë',
    type_snow_ice: 'Borë / Akull',
    type_thunderstorm: 'Stuhi me bubullima',
    type_fog: 'Mjegull',
    type_high_temperature: 'Temperaturë e lartë',
    type_low_temperature: 'Temperaturë e ulët',
    type_coastal: 'Dukuri bregdetare',
    type_forest_fire: 'Zjarr pylli',
    type_avalanche: 'Orteku',
    type_rain: 'Shi',
    type_rain_flood: 'Përmbytje nga shiu',
    type_flood: 'Përmbytje',
    type_unknown: 'E panjohur',
    severity_Minor: 'I vogël',
    severity_Moderate: 'I moderuar',
    severity_Severe: 'I rëndë',
    severity_Extreme: 'Ekstrem',
    severity_Unknown: 'E panjohur',
    updatedAt: "Përditësuar",
    refresh: "Rifresko",
    refreshing: "Po rifreskohet…",
    refreshTitle: "Detyro rifreskimin nga MeteoAlarm",
  },
};

const mk: Bundle = {
  common: { loading: 'Се вчитува…', language: 'Јазик' },
  alerts: {
    title: 'Временски предупредувања',
    activeFor: 'Активни за {{name}}',
    yourLocation: 'вашата локација',
    selectInstallation: '(изберете инсталација)',
    none: 'Нема активни временски предупредувања за вашата локација.',
    mapTitle: 'Карта на предупредувања',
    mapSubtitle: '{{count}} активни предупредувања. Боја = ниво (зелена/жолта/портокалова/црвена).',
    from: 'Од',
    to: 'До',
    country: 'Држава',
    level: 'ниво',
    types: 'Видови',
    alertsCount: 'предупредувања: {{count}}',
    type_wind: 'Ветер',
    type_snow_ice: 'Снег / Мраз',
    type_thunderstorm: 'Грмежи',
    type_fog: 'Магла',
    type_high_temperature: 'Висока температура',
    type_low_temperature: 'Ниска температура',
    type_coastal: 'Крајбрежна појава',
    type_forest_fire: 'Шумски пожар',
    type_avalanche: 'Лавина',
    type_rain: 'Дожд',
    type_rain_flood: 'Поплава од дожд',
    type_flood: 'Поплава',
    type_unknown: 'Непознато',
    severity_Minor: 'Мало',
    severity_Moderate: 'Умерено',
    severity_Severe: 'Сериозно',
    severity_Extreme: 'Екстремно',
    severity_Unknown: 'Непознато',
    updatedAt: "Ажурирано",
    refresh: "Освежи",
    refreshing: "Освежување…",
    refreshTitle: "Принудно освежи од MeteoAlarm",
  },
};

const bs: Bundle = {
  common: { loading: 'Učitavanje…', language: 'Jezik' },
  alerts: {
    title: 'Vremenska upozorenja',
    activeFor: 'Aktivna za {{name}}',
    yourLocation: 'vašu lokaciju',
    selectInstallation: '(odaberite postrojenje)',
    none: 'Nema aktivnih vremenskih upozorenja za vašu lokaciju.',
    mapTitle: 'Karta upozorenja',
    mapSubtitle: '{{count}} aktivnih upozorenja. Boja = nivo (zelena/žuta/narandžasta/crvena).',
    from: 'Od',
    to: 'Do',
    country: 'Država',
    level: 'nivo',
    types: 'Vrste',
    alertsCount: 'upozorenja: {{count}}',
    type_wind: 'Vjetar',
    type_snow_ice: 'Snijeg / Led',
    type_thunderstorm: 'Grmljavinska oluja',
    type_fog: 'Magla',
    type_high_temperature: 'Visoka temperatura',
    type_low_temperature: 'Niska temperatura',
    type_coastal: 'Priobalni događaj',
    type_forest_fire: 'Šumski požar',
    type_avalanche: 'Lavina',
    type_rain: 'Kiša',
    type_rain_flood: 'Bujična poplava',
    type_flood: 'Poplava',
    type_unknown: 'Nepoznato',
    severity_Minor: 'Manje',
    severity_Moderate: 'Umjereno',
    severity_Severe: 'Ozbiljno',
    severity_Extreme: 'Ekstremno',
    severity_Unknown: 'Nepoznato',
    updatedAt: "Ažurirano",
    refresh: "Osvježi",
    refreshing: "Osvježavanje…",
    refreshTitle: "Prisilno osvježi iz MeteoAlarm",
  },
};

/** Emoji flagi (przybliżenie: język → reprezentatywny kraj). */
export const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇬🇧',
  pl: '🇵🇱',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  it: '🇮🇹',
  pt: '🇵🇹',
  nl: '🇳🇱',
  da: '🇩🇰',
  sv: '🇸🇪',
  fi: '🇫🇮',
  cs: '🇨🇿',
  sk: '🇸🇰',
  sl: '🇸🇮',
  hr: '🇭🇷',
  hu: '🇭🇺',
  ro: '🇷🇴',
  bg: '🇧🇬',
  el: '🇬🇷',
  et: '🇪🇪',
  lv: '🇱🇻',
  lt: '🇱🇹',
  mt: '🇲🇹',
  ga: '🇮🇪',
  no: '🇳🇴',
  is: '🇮🇸',
  uk: '🇺🇦',
  sr: '🇷🇸',
  sq: '🇦🇱',
  mk: '🇲🇰',
  bs: '🇧🇦',
};

/** Klucze wspólne i nawigacji — krótkie, więc trzymane osobno i scalane do `translation`. */
const extras: Record<string, { common: Dict; nav: Dict }> = {
  en: {
    common: { logout: 'Log out', clientPortal: 'Client Portal' },
    nav: {
      dashboard: 'Dashboard',
      usage: 'Usage',
      correction: 'Correction',
      webhooks: 'Webhooks',
      alerts: 'Alerts',
      help: 'Help',
      settings: 'Settings',
    },
  },
  pl: {
    common: { logout: 'Wyloguj się', clientPortal: 'Portal Klienta' },
    nav: {
      dashboard: 'Pulpit',
      usage: 'Użycie',
      correction: 'Korekta',
      webhooks: 'Webhooki',
      alerts: 'Ostrzeżenia',
      help: 'Pomoc',
      settings: 'Ustawienia',
    },
  },
  de: {
    common: { logout: 'Abmelden', clientPortal: 'Kundenportal' },
    nav: {
      dashboard: 'Übersicht',
      usage: 'Nutzung',
      correction: 'Korrektur',
      webhooks: 'Webhooks',
      alerts: 'Warnungen',
      help: 'Hilfe',
      settings: 'Einstellungen',
    },
  },
  fr: {
    common: { logout: 'Se déconnecter', clientPortal: 'Portail client' },
    nav: {
      dashboard: 'Tableau de bord',
      usage: 'Utilisation',
      correction: 'Correction',
      webhooks: 'Webhooks',
      alerts: 'Alertes',
      help: 'Aide',
      settings: 'Paramètres',
    },
  },
  es: {
    common: { logout: 'Cerrar sesión', clientPortal: 'Portal del cliente' },
    nav: {
      dashboard: 'Panel',
      usage: 'Uso',
      correction: 'Corrección',
      webhooks: 'Webhooks',
      alerts: 'Avisos',
      help: 'Ayuda',
      settings: 'Configuración',
    },
  },
  it: {
    common: { logout: 'Esci', clientPortal: 'Portale cliente' },
    nav: {
      dashboard: 'Pannello',
      usage: 'Utilizzo',
      correction: 'Correzione',
      webhooks: 'Webhooks',
      alerts: 'Allerte',
      help: 'Aiuto',
      settings: 'Impostazioni',
    },
  },
  pt: {
    common: { logout: 'Sair', clientPortal: 'Portal do cliente' },
    nav: {
      dashboard: 'Painel',
      usage: 'Utilização',
      correction: 'Correção',
      webhooks: 'Webhooks',
      alerts: 'Avisos',
      help: 'Ajuda',
      settings: 'Definições',
    },
  },
  nl: {
    common: { logout: 'Uitloggen', clientPortal: 'Klantportaal' },
    nav: {
      dashboard: 'Dashboard',
      usage: 'Verbruik',
      correction: 'Correctie',
      webhooks: 'Webhooks',
      alerts: 'Waarschuwingen',
      help: 'Help',
      settings: 'Instellingen',
    },
  },
  da: {
    common: { logout: 'Log ud', clientPortal: 'Kundeportal' },
    nav: {
      dashboard: 'Oversigt',
      usage: 'Forbrug',
      correction: 'Korrektion',
      webhooks: 'Webhooks',
      alerts: 'Varsler',
      help: 'Hjælp',
      settings: 'Indstillinger',
    },
  },
  sv: {
    common: { logout: 'Logga ut', clientPortal: 'Kundportal' },
    nav: {
      dashboard: 'Översikt',
      usage: 'Användning',
      correction: 'Korrigering',
      webhooks: 'Webhooks',
      alerts: 'Varningar',
      help: 'Hjälp',
      settings: 'Inställningar',
    },
  },
  fi: {
    common: { logout: 'Kirjaudu ulos', clientPortal: 'Asiakasportaali' },
    nav: {
      dashboard: 'Yleisnäkymä',
      usage: 'Käyttö',
      correction: 'Korjaus',
      webhooks: 'Webhookit',
      alerts: 'Varoitukset',
      help: 'Ohje',
      settings: 'Asetukset',
    },
  },
  cs: {
    common: { logout: 'Odhlásit se', clientPortal: 'Klientský portál' },
    nav: {
      dashboard: 'Přehled',
      usage: 'Využití',
      correction: 'Korekce',
      webhooks: 'Webhooky',
      alerts: 'Výstrahy',
      help: 'Nápověda',
      settings: 'Nastavení',
    },
  },
  sk: {
    common: { logout: 'Odhlásiť sa', clientPortal: 'Klientský portál' },
    nav: {
      dashboard: 'Prehľad',
      usage: 'Využitie',
      correction: 'Korekcia',
      webhooks: 'Webhooky',
      alerts: 'Výstrahy',
      help: 'Pomocník',
      settings: 'Nastavenia',
    },
  },
  sl: {
    common: { logout: 'Odjava', clientPortal: 'Portal za stranke' },
    nav: {
      dashboard: 'Nadzorna plošča',
      usage: 'Uporaba',
      correction: 'Popravek',
      webhooks: 'Webhooki',
      alerts: 'Opozorila',
      help: 'Pomoč',
      settings: 'Nastavitve',
    },
  },
  hr: {
    common: { logout: 'Odjava', clientPortal: 'Korisnički portal' },
    nav: {
      dashboard: 'Nadzorna ploča',
      usage: 'Korištenje',
      correction: 'Ispravak',
      webhooks: 'Webhookovi',
      alerts: 'Upozorenja',
      help: 'Pomoć',
      settings: 'Postavke',
    },
  },
  hu: {
    common: { logout: 'Kijelentkezés', clientPortal: 'Ügyfélportál' },
    nav: {
      dashboard: 'Irányítópult',
      usage: 'Használat',
      correction: 'Korrekció',
      webhooks: 'Webhookok',
      alerts: 'Riasztások',
      help: 'Súgó',
      settings: 'Beállítások',
    },
  },
  ro: {
    common: { logout: 'Deconectare', clientPortal: 'Portal client' },
    nav: {
      dashboard: 'Tablou de bord',
      usage: 'Utilizare',
      correction: 'Corecție',
      webhooks: 'Webhook-uri',
      alerts: 'Avertizări',
      help: 'Ajutor',
      settings: 'Setări',
    },
  },
  bg: {
    common: { logout: 'Изход', clientPortal: 'Клиентски портал' },
    nav: {
      dashboard: 'Табло',
      usage: 'Употреба',
      correction: 'Корекция',
      webhooks: 'Уебхукове',
      alerts: 'Предупреждения',
      help: 'Помощ',
      settings: 'Настройки',
    },
  },
  el: {
    common: { logout: 'Αποσύνδεση', clientPortal: 'Πύλη πελάτη' },
    nav: {
      dashboard: 'Πίνακας',
      usage: 'Χρήση',
      correction: 'Διόρθωση',
      webhooks: 'Webhooks',
      alerts: 'Προειδοποιήσεις',
      help: 'Βοήθεια',
      settings: 'Ρυθμίσεις',
    },
  },
  et: {
    common: { logout: 'Logi välja', clientPortal: 'Kliendiportaal' },
    nav: {
      dashboard: 'Töölaud',
      usage: 'Kasutus',
      correction: 'Parandus',
      webhooks: 'Webhookid',
      alerts: 'Hoiatused',
      help: 'Abi',
      settings: 'Sätted',
    },
  },
  lv: {
    common: { logout: 'Atteikties', clientPortal: 'Klienta portāls' },
    nav: {
      dashboard: 'Panelis',
      usage: 'Lietojums',
      correction: 'Korekcija',
      webhooks: 'Webhooki',
      alerts: 'Brīdinājumi',
      help: 'Palīdzība',
      settings: 'Iestatījumi',
    },
  },
  lt: {
    common: { logout: 'Atsijungti', clientPortal: 'Kliento portalas' },
    nav: {
      dashboard: 'Skydelis',
      usage: 'Naudojimas',
      correction: 'Korekcija',
      webhooks: 'Webhookai',
      alerts: 'Įspėjimai',
      help: 'Pagalba',
      settings: 'Nustatymai',
    },
  },
  mt: {
    common: { logout: 'Illoggja barra', clientPortal: 'Portal tal-klijent' },
    nav: {
      dashboard: 'Dashboard',
      usage: 'Użu',
      correction: 'Korrezzjoni',
      webhooks: 'Webhooks',
      alerts: 'Twissijiet',
      help: 'Għajnuna',
      settings: 'Settings',
    },
  },
  ga: {
    common: { logout: 'Logáil amach', clientPortal: 'Tairseach an chliaint' },
    nav: {
      dashboard: 'Deais',
      usage: 'Úsáid',
      correction: 'Ceartúchán',
      webhooks: 'Webhooks',
      alerts: 'Rabhaidh',
      help: 'Cabhair',
      settings: 'Socruithe',
    },
  },
  no: {
    common: { logout: 'Logg ut', clientPortal: 'Kundeportal' },
    nav: {
      dashboard: 'Oversikt',
      usage: 'Forbruk',
      correction: 'Korreksjon',
      webhooks: 'Webhooks',
      alerts: 'Varsler',
      help: 'Hjelp',
      settings: 'Innstillinger',
    },
  },
  is: {
    common: { logout: 'Skrá út', clientPortal: 'Viðskiptavinagátt' },
    nav: {
      dashboard: 'Yfirlit',
      usage: 'Notkun',
      correction: 'Leiðrétting',
      webhooks: 'Webhooks',
      alerts: 'Viðvaranir',
      help: 'Hjálp',
      settings: 'Stillingar',
    },
  },
  uk: {
    common: { logout: 'Вийти', clientPortal: 'Портал клієнта' },
    nav: {
      dashboard: 'Панель',
      usage: 'Використання',
      correction: 'Корекція',
      webhooks: 'Вебхуки',
      alerts: 'Попередження',
      help: 'Допомога',
      settings: 'Налаштування',
    },
  },
  sr: {
    common: { logout: 'Odjava', clientPortal: 'Korisnički portal' },
    nav: {
      dashboard: 'Kontrolna tabla',
      usage: 'Korišćenje',
      correction: 'Ispravka',
      webhooks: 'Webhookovi',
      alerts: 'Upozorenja',
      help: 'Pomoć',
      settings: 'Podešavanja',
    },
  },
  sq: {
    common: { logout: 'Dilni', clientPortal: 'Portali i klientit' },
    nav: {
      dashboard: 'Paneli',
      usage: 'Përdorimi',
      correction: 'Korrigjimi',
      webhooks: 'Webhooks',
      alerts: 'Paralajmërime',
      help: 'Ndihmë',
      settings: 'Cilësimet',
    },
  },
  mk: {
    common: { logout: 'Одјави се', clientPortal: 'Клиентски портал' },
    nav: {
      dashboard: 'Контролна табла',
      usage: 'Употреба',
      correction: 'Корекција',
      webhooks: 'Вебхукови',
      alerts: 'Предупредувања',
      help: 'Помош',
      settings: 'Поставки',
    },
  },
  bs: {
    common: { logout: 'Odjava', clientPortal: 'Korisnički portal' },
    nav: {
      dashboard: 'Kontrolna ploča',
      usage: 'Korištenje',
      correction: 'Ispravka',
      webhooks: 'Webhookovi',
      alerts: 'Upozorenja',
      help: 'Pomoć',
      settings: 'Postavke',
    },
  },
};

/** Namespace 'auth' — strony logowania / rejestracji. */
const authTr: Record<string, Dict> = {
  en: {
    login: 'Sign in',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    fillAllFields: 'Please fill in all fields',
    forgotPassword: 'Forgot your password?',
    noAccountRegister: "Don't have an account? Sign up",
    adminLink: 'Administrator? Go to the admin panel',
    email: 'E-mail',
    password: 'Password',
  },
  pl: {
    login: 'Logowanie',
    signIn: 'Zaloguj się',
    signingIn: 'Logowanie…',
    fillAllFields: 'Proszę wypełnić wszystkie pola',
    forgotPassword: 'Zapomniałeś hasła?',
    noAccountRegister: 'Nie masz konta? Zarejestruj się',
    adminLink: 'Jesteś administratorem? Przejdź do panelu admina',
    email: 'E-mail',
    password: 'Hasło',
  },
  de: {
    login: 'Anmelden',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung…',
    fillAllFields: 'Bitte alle Felder ausfüllen',
    forgotPassword: 'Passwort vergessen?',
    noAccountRegister: 'Kein Konto? Registrieren',
    adminLink: 'Administrator? Zum Admin-Panel',
    email: 'E-Mail',
    password: 'Passwort',
  },
  fr: {
    login: 'Connexion',
    signIn: 'Se connecter',
    signingIn: 'Connexion…',
    fillAllFields: 'Veuillez remplir tous les champs',
    forgotPassword: 'Mot de passe oublié ?',
    noAccountRegister: "Pas de compte ? S'inscrire",
    adminLink: 'Administrateur ? Aller au panneau admin',
    email: 'E-mail',
    password: 'Mot de passe',
  },
  es: {
    login: 'Iniciar sesión',
    signIn: 'Iniciar sesión',
    signingIn: 'Iniciando sesión…',
    fillAllFields: 'Por favor, rellene todos los campos',
    forgotPassword: '¿Olvidó su contraseña?',
    noAccountRegister: '¿No tiene cuenta? Regístrese',
    adminLink: '¿Administrador? Ir al panel de administración',
    email: 'Correo electrónico',
    password: 'Contraseña',
  },
  it: {
    login: 'Accedi',
    signIn: 'Accedi',
    signingIn: 'Accesso…',
    fillAllFields: 'Compila tutti i campi',
    forgotPassword: 'Password dimenticata?',
    noAccountRegister: 'Non hai un account? Registrati',
    adminLink: 'Amministratore? Vai al pannello admin',
    email: 'E-mail',
    password: 'Password',
  },
  pt: {
    login: 'Iniciar sessão',
    signIn: 'Iniciar sessão',
    signingIn: 'A iniciar sessão…',
    fillAllFields: 'Por favor, preencha todos os campos',
    forgotPassword: 'Esqueceu a palavra-passe?',
    noAccountRegister: 'Não tem conta? Registe-se',
    adminLink: 'Administrador? Ir para o painel de administração',
    email: 'E-mail',
    password: 'Palavra-passe',
  },
  nl: {
    login: 'Inloggen',
    signIn: 'Inloggen',
    signingIn: 'Inloggen…',
    fillAllFields: 'Vul alle velden in',
    forgotPassword: 'Wachtwoord vergeten?',
    noAccountRegister: 'Geen account? Registreren',
    adminLink: 'Beheerder? Ga naar het beheerpaneel',
    email: 'E-mail',
    password: 'Wachtwoord',
  },
  da: {
    login: 'Log ind',
    signIn: 'Log ind',
    signingIn: 'Logger ind…',
    fillAllFields: 'Udfyld alle felter',
    forgotPassword: 'Glemt adgangskode?',
    noAccountRegister: 'Ingen konto? Registrer dig',
    adminLink: 'Administrator? Gå til admin-panelet',
    email: 'E-mail',
    password: 'Adgangskode',
  },
  sv: {
    login: 'Logga in',
    signIn: 'Logga in',
    signingIn: 'Loggar in…',
    fillAllFields: 'Fyll i alla fält',
    forgotPassword: 'Glömt lösenord?',
    noAccountRegister: 'Inget konto? Registrera dig',
    adminLink: 'Administratör? Gå till adminpanelen',
    email: 'E-post',
    password: 'Lösenord',
  },
  fi: {
    login: 'Kirjaudu sisään',
    signIn: 'Kirjaudu sisään',
    signingIn: 'Kirjaudutaan…',
    fillAllFields: 'Täytä kaikki kentät',
    forgotPassword: 'Unohditko salasanan?',
    noAccountRegister: 'Ei tiliä? Rekisteröidy',
    adminLink: 'Ylläpitäjä? Siirry hallintapaneeliin',
    email: 'Sähköposti',
    password: 'Salasana',
  },
  cs: {
    login: 'Přihlášení',
    signIn: 'Přihlásit se',
    signingIn: 'Přihlašování…',
    fillAllFields: 'Vyplňte prosím všechna pole',
    forgotPassword: 'Zapomněli jste heslo?',
    noAccountRegister: 'Nemáte účet? Zaregistrujte se',
    adminLink: 'Jste administrátor? Přejít do administrace',
    email: 'E-mail',
    password: 'Heslo',
  },
  sk: {
    login: 'Prihlásenie',
    signIn: 'Prihlásiť sa',
    signingIn: 'Prihlasovanie…',
    fillAllFields: 'Vyplňte prosím všetky polia',
    forgotPassword: 'Zabudli ste heslo?',
    noAccountRegister: 'Nemáte účet? Zaregistrujte sa',
    adminLink: 'Ste administrátor? Prejsť do administrácie',
    email: 'E-mail',
    password: 'Heslo',
  },
  sl: {
    login: 'Prijava',
    signIn: 'Prijava',
    signingIn: 'Prijavljanje…',
    fillAllFields: 'Izpolnite vsa polja',
    forgotPassword: 'Ste pozabili geslo?',
    noAccountRegister: 'Nimate računa? Registrirajte se',
    adminLink: 'Ste skrbnik? Pojdite na skrbniško ploščo',
    email: 'E-pošta',
    password: 'Geslo',
  },
  hr: {
    login: 'Prijava',
    signIn: 'Prijavi se',
    signingIn: 'Prijava…',
    fillAllFields: 'Ispunite sva polja',
    forgotPassword: 'Zaboravili ste lozinku?',
    noAccountRegister: 'Nemate račun? Registrirajte se',
    adminLink: 'Administrator? Idite na administracijsku ploču',
    email: 'E-pošta',
    password: 'Lozinka',
  },
  hu: {
    login: 'Bejelentkezés',
    signIn: 'Bejelentkezés',
    signingIn: 'Bejelentkezés…',
    fillAllFields: 'Kérjük, töltsön ki minden mezőt',
    forgotPassword: 'Elfelejtette a jelszavát?',
    noAccountRegister: 'Nincs fiókja? Regisztráljon',
    adminLink: 'Adminisztrátor? Tovább az admin felületre',
    email: 'E-mail',
    password: 'Jelszó',
  },
  ro: {
    login: 'Autentificare',
    signIn: 'Autentificare',
    signingIn: 'Autentificare…',
    fillAllFields: 'Vă rugăm completați toate câmpurile',
    forgotPassword: 'Ați uitat parola?',
    noAccountRegister: 'Nu aveți cont? Înregistrați-vă',
    adminLink: 'Sunteți administrator? Mergeți la panoul de administrare',
    email: 'E-mail',
    password: 'Parolă',
  },
  bg: {
    login: 'Вход',
    signIn: 'Влизане',
    signingIn: 'Влизане…',
    fillAllFields: 'Моля, попълнете всички полета',
    forgotPassword: 'Забравена парола?',
    noAccountRegister: 'Нямате акаунт? Регистрирайте се',
    adminLink: 'Администратор? Към администраторския панел',
    email: 'Имейл',
    password: 'Парола',
  },
  el: {
    login: 'Σύνδεση',
    signIn: 'Σύνδεση',
    signingIn: 'Σύνδεση…',
    fillAllFields: 'Συμπληρώστε όλα τα πεδία',
    forgotPassword: 'Ξεχάσατε τον κωδικό;',
    noAccountRegister: 'Δεν έχετε λογαριασμό; Εγγραφείτε',
    adminLink: 'Διαχειριστής; Μετάβαση στον πίνακα διαχείρισης',
    email: 'E-mail',
    password: 'Κωδικός',
  },
  et: {
    login: 'Logi sisse',
    signIn: 'Logi sisse',
    signingIn: 'Sisselogimine…',
    fillAllFields: 'Täitke kõik väljad',
    forgotPassword: 'Unustasite parooli?',
    noAccountRegister: 'Pole kontot? Registreeruge',
    adminLink: 'Administraator? Mine halduspaneeli',
    email: 'E-post',
    password: 'Parool',
  },
  lv: {
    login: 'Pieslēgšanās',
    signIn: 'Pieslēgties',
    signingIn: 'Notiek pieslēgšanās…',
    fillAllFields: 'Lūdzu aizpildiet visus laukus',
    forgotPassword: 'Aizmirsāt paroli?',
    noAccountRegister: 'Nav konta? Reģistrējieties',
    adminLink: 'Esat administrators? Doties uz administratora paneli',
    email: 'E-pasts',
    password: 'Parole',
  },
  lt: {
    login: 'Prisijungimas',
    signIn: 'Prisijungti',
    signingIn: 'Prisijungiama…',
    fillAllFields: 'Užpildykite visus laukus',
    forgotPassword: 'Pamiršote slaptažodį?',
    noAccountRegister: 'Neturite paskyros? Registruokitės',
    adminLink: 'Esate administratorius? Eikite į administratoriaus skydelį',
    email: 'El. paštas',
    password: 'Slaptažodis',
  },
  mt: {
    login: 'Idħol',
    signIn: 'Idħol',
    signingIn: 'Dieħel…',
    fillAllFields: 'Jekk jogħġbok imla l-oqsma kollha',
    forgotPassword: 'Insejt il-password?',
    noAccountRegister: "M'għandekx kont? Irreġistra",
    adminLink: 'Amministratur? Mur fil-pannell tal-amministrazzjoni',
    email: 'E-mail',
    password: 'Password',
  },
  ga: {
    login: 'Logáil isteach',
    signIn: 'Logáil isteach',
    signingIn: 'Ag logáil isteach…',
    fillAllFields: 'Líon isteach gach réimse le do thoil',
    forgotPassword: 'Dearmad ar an bhfocal faire?',
    noAccountRegister: 'Gan cuntas? Cláraigh',
    adminLink: 'Riarthóir? Téigh go dtí an painéal riaracháin',
    email: 'Ríomhphost',
    password: 'Focal faire',
  },
  no: {
    login: 'Logg inn',
    signIn: 'Logg inn',
    signingIn: 'Logger inn…',
    fillAllFields: 'Fyll ut alle felt',
    forgotPassword: 'Glemt passord?',
    noAccountRegister: 'Ingen konto? Registrer deg',
    adminLink: 'Administrator? Gå til adminpanelet',
    email: 'E-post',
    password: 'Passord',
  },
  is: {
    login: 'Innskráning',
    signIn: 'Skrá inn',
    signingIn: 'Skrái inn…',
    fillAllFields: 'Vinsamlegast fylltu út alla reiti',
    forgotPassword: 'Gleymdir þú lykilorðinu?',
    noAccountRegister: 'Ekki með reikning? Skráðu þig',
    adminLink: 'Stjórnandi? Fara í stjórnborðið',
    email: 'Netfang',
    password: 'Lykilorð',
  },
  uk: {
    login: 'Вхід',
    signIn: 'Увійти',
    signingIn: 'Вхід…',
    fillAllFields: 'Будь ласка, заповніть усі поля',
    forgotPassword: 'Забули пароль?',
    noAccountRegister: 'Немає облікового запису? Зареєструйтеся',
    adminLink: 'Адміністратор? Перейти до панелі адміністратора',
    email: 'Електронна пошта',
    password: 'Пароль',
  },
  sr: {
    login: 'Prijava',
    signIn: 'Prijavi se',
    signingIn: 'Prijava…',
    fillAllFields: 'Popunite sva polja',
    forgotPassword: 'Zaboravili ste lozinku?',
    noAccountRegister: 'Nemate nalog? Registrujte se',
    adminLink: 'Administrator? Idite na administratorski panel',
    email: 'E-pošta',
    password: 'Lozinka',
  },
  sq: {
    login: 'Hyrje',
    signIn: 'Hyr',
    signingIn: 'Po hyn…',
    fillAllFields: 'Ju lutemi plotësoni të gjitha fushat',
    forgotPassword: 'Harruat fjalëkalimin?',
    noAccountRegister: 'Nuk keni llogari? Regjistrohuni',
    adminLink: 'Administrator? Shko te paneli i administrimit',
    email: 'E-mail',
    password: 'Fjalëkalimi',
  },
  mk: {
    login: 'Најава',
    signIn: 'Најави се',
    signingIn: 'Најавување…',
    fillAllFields: 'Ве молиме пополнете ги сите полиња',
    forgotPassword: 'Заборавена лозинка?',
    noAccountRegister: 'Немате сметка? Регистрирајте се',
    adminLink: 'Администратор? Одете на администраторскиот панел',
    email: 'Е-пошта',
    password: 'Лозинка',
  },
  bs: {
    login: 'Prijava',
    signIn: 'Prijavi se',
    signingIn: 'Prijava…',
    fillAllFields: 'Popunite sva polja',
    forgotPassword: 'Zaboravili ste lozinku?',
    noAccountRegister: 'Nemate račun? Registrujte se',
    adminLink: 'Administrator? Idite na administratorski panel',
    email: 'E-pošta',
    password: 'Lozinka',
  },
};

/** Namespace 'settings' — strona ustawień profilu klienta. */
const settingsTr: Record<string, Dict> = {
  en: {
    title: 'Profile settings',
    profileInfo: 'Profile information',
    personalData: 'Personal details',
    fullName: 'Full name',
    emailAddress: 'Email address',
    phone: 'Phone',
    companyData: 'Company details',
    companyName: 'Company name',
    nip: 'Tax ID (NIP)',
    address: 'Address',
    street: 'Street',
    city: 'City',
    postalCode: 'Postal code',
    country: 'Country',
    accountCreated: 'Account created',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    min8: 'Minimum 8 characters',
    confirmNewPassword: 'Confirm new password',
    changingPassword: 'Changing password…',
    changePasswordBtn: 'Change password',
    profileLoadFailed: 'Failed to load profile',
    profileUpdated: 'Profile updated successfully',
    passwordChanged: 'Password changed successfully',
    pwTooShort: 'New password must be at least 8 characters',
    pwMismatch: 'Passwords do not match',
    pwSameAsCurrent: 'New password must be different from the current one',
  },
  pl: {
    title: 'Ustawienia profilu',
    profileInfo: 'Informacje o profilu',
    personalData: 'Dane osobowe',
    fullName: 'Imię i nazwisko',
    emailAddress: 'Adres e-mail',
    phone: 'Telefon',
    companyData: 'Dane firmy',
    companyName: 'Nazwa firmy',
    nip: 'NIP',
    address: 'Adres',
    street: 'Ulica',
    city: 'Miasto',
    postalCode: 'Kod pocztowy',
    country: 'Kraj',
    accountCreated: 'Data utworzenia konta',
    saving: 'Zapisywanie…',
    saveChanges: 'Zapisz zmiany',
    changePassword: 'Zmiana hasła',
    currentPassword: 'Obecne hasło',
    newPassword: 'Nowe hasło',
    min8: 'Minimum 8 znaków',
    confirmNewPassword: 'Potwierdź nowe hasło',
    changingPassword: 'Zmiana hasła…',
    changePasswordBtn: 'Zmień hasło',
    profileLoadFailed: 'Nie udało się załadować profilu',
    profileUpdated: 'Profil zaktualizowany pomyślnie',
    passwordChanged: 'Hasło zmienione pomyślnie',
    pwTooShort: 'Nowe hasło musi mieć co najmniej 8 znaków',
    pwMismatch: 'Hasła nie są identyczne',
    pwSameAsCurrent: 'Nowe hasło musi być inne niż obecne',
  },
  de: {
    title: 'Profileinstellungen',
    profileInfo: 'Profilinformationen',
    personalData: 'Persönliche Daten',
    fullName: 'Vollständiger Name',
    emailAddress: 'E-Mail-Adresse',
    phone: 'Telefon',
    companyData: 'Firmendaten',
    companyName: 'Firmenname',
    nip: 'Steuernummer (NIP)',
    address: 'Adresse',
    street: 'Straße',
    city: 'Stadt',
    postalCode: 'Postleitzahl',
    country: 'Land',
    accountCreated: 'Konto erstellt',
    saving: 'Wird gespeichert…',
    saveChanges: 'Änderungen speichern',
    changePassword: 'Passwort ändern',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    min8: 'Mindestens 8 Zeichen',
    confirmNewPassword: 'Neues Passwort bestätigen',
    changingPassword: 'Passwort wird geändert…',
    changePasswordBtn: 'Passwort ändern',
    profileLoadFailed: 'Profil konnte nicht geladen werden',
    profileUpdated: 'Profil erfolgreich aktualisiert',
    passwordChanged: 'Passwort erfolgreich geändert',
    pwTooShort: 'Neues Passwort muss mindestens 8 Zeichen haben',
    pwMismatch: 'Passwörter stimmen nicht überein',
    pwSameAsCurrent: 'Neues Passwort muss sich vom aktuellen unterscheiden',
  },
  fr: {
    title: 'Paramètres du profil',
    profileInfo: 'Informations du profil',
    personalData: 'Données personnelles',
    fullName: 'Nom complet',
    emailAddress: 'Adresse e-mail',
    phone: 'Téléphone',
    companyData: "Données de l'entreprise",
    companyName: "Nom de l'entreprise",
    nip: 'Numéro fiscal (NIP)',
    address: 'Adresse',
    street: 'Rue',
    city: 'Ville',
    postalCode: 'Code postal',
    country: 'Pays',
    accountCreated: 'Compte créé',
    saving: 'Enregistrement…',
    saveChanges: 'Enregistrer les modifications',
    changePassword: 'Changer le mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    min8: 'Minimum 8 caractères',
    confirmNewPassword: 'Confirmer le nouveau mot de passe',
    changingPassword: 'Changement du mot de passe…',
    changePasswordBtn: 'Changer le mot de passe',
    profileLoadFailed: 'Échec du chargement du profil',
    profileUpdated: 'Profil mis à jour avec succès',
    passwordChanged: 'Mot de passe changé avec succès',
    pwTooShort: 'Le nouveau mot de passe doit comporter au moins 8 caractères',
    pwMismatch: 'Les mots de passe ne correspondent pas',
    pwSameAsCurrent: "Le nouveau mot de passe doit être différent de l'actuel",
  },
  es: {
    title: 'Configuración del perfil',
    profileInfo: 'Información del perfil',
    personalData: 'Datos personales',
    fullName: 'Nombre completo',
    emailAddress: 'Correo electrónico',
    phone: 'Teléfono',
    companyData: 'Datos de la empresa',
    companyName: 'Nombre de la empresa',
    nip: 'NIF (NIP)',
    address: 'Dirección',
    street: 'Calle',
    city: 'Ciudad',
    postalCode: 'Código postal',
    country: 'País',
    accountCreated: 'Cuenta creada',
    saving: 'Guardando…',
    saveChanges: 'Guardar cambios',
    changePassword: 'Cambiar contraseña',
    currentPassword: 'Contraseña actual',
    newPassword: 'Nueva contraseña',
    min8: 'Mínimo 8 caracteres',
    confirmNewPassword: 'Confirmar nueva contraseña',
    changingPassword: 'Cambiando contraseña…',
    changePasswordBtn: 'Cambiar contraseña',
    profileLoadFailed: 'No se pudo cargar el perfil',
    profileUpdated: 'Perfil actualizado correctamente',
    passwordChanged: 'Contraseña cambiada correctamente',
    pwTooShort: 'La nueva contraseña debe tener al menos 8 caracteres',
    pwMismatch: 'Las contraseñas no coinciden',
    pwSameAsCurrent: 'La nueva contraseña debe ser diferente de la actual',
  },
  it: {
    title: 'Impostazioni del profilo',
    profileInfo: 'Informazioni del profilo',
    personalData: 'Dati personali',
    fullName: 'Nome completo',
    emailAddress: 'Indirizzo e-mail',
    phone: 'Telefono',
    companyData: "Dati dell'azienda",
    companyName: "Nome dell'azienda",
    nip: 'Codice fiscale (NIP)',
    address: 'Indirizzo',
    street: 'Via',
    city: 'Città',
    postalCode: 'Codice postale',
    country: 'Paese',
    accountCreated: 'Account creato',
    saving: 'Salvataggio…',
    saveChanges: 'Salva modifiche',
    changePassword: 'Cambia password',
    currentPassword: 'Password attuale',
    newPassword: 'Nuova password',
    min8: 'Minimo 8 caratteri',
    confirmNewPassword: 'Conferma nuova password',
    changingPassword: 'Modifica password…',
    changePasswordBtn: 'Cambia password',
    profileLoadFailed: 'Impossibile caricare il profilo',
    profileUpdated: 'Profilo aggiornato con successo',
    passwordChanged: 'Password cambiata con successo',
    pwTooShort: 'La nuova password deve contenere almeno 8 caratteri',
    pwMismatch: 'Le password non corrispondono',
    pwSameAsCurrent: 'La nuova password deve essere diversa da quella attuale',
  },
  pt: {
    title: 'Definições do perfil',
    profileInfo: 'Informações do perfil',
    personalData: 'Dados pessoais',
    fullName: 'Nome completo',
    emailAddress: 'Endereço de e-mail',
    phone: 'Telefone',
    companyData: 'Dados da empresa',
    companyName: 'Nome da empresa',
    nip: 'NIF (NIP)',
    address: 'Morada',
    street: 'Rua',
    city: 'Cidade',
    postalCode: 'Código postal',
    country: 'País',
    accountCreated: 'Conta criada',
    saving: 'A guardar…',
    saveChanges: 'Guardar alterações',
    changePassword: 'Alterar palavra-passe',
    currentPassword: 'Palavra-passe atual',
    newPassword: 'Nova palavra-passe',
    min8: 'Mínimo 8 caracteres',
    confirmNewPassword: 'Confirmar nova palavra-passe',
    changingPassword: 'A alterar palavra-passe…',
    changePasswordBtn: 'Alterar palavra-passe',
    profileLoadFailed: 'Não foi possível carregar o perfil',
    profileUpdated: 'Perfil atualizado com sucesso',
    passwordChanged: 'Palavra-passe alterada com sucesso',
    pwTooShort: 'A nova palavra-passe deve ter pelo menos 8 caracteres',
    pwMismatch: 'As palavras-passe não coincidem',
    pwSameAsCurrent: 'A nova palavra-passe deve ser diferente da atual',
  },
  nl: {
    title: 'Profielinstellingen',
    profileInfo: 'Profielinformatie',
    personalData: 'Persoonlijke gegevens',
    fullName: 'Volledige naam',
    emailAddress: 'E-mailadres',
    phone: 'Telefoon',
    companyData: 'Bedrijfsgegevens',
    companyName: 'Bedrijfsnaam',
    nip: 'Btw-nummer (NIP)',
    address: 'Adres',
    street: 'Straat',
    city: 'Stad',
    postalCode: 'Postcode',
    country: 'Land',
    accountCreated: 'Account aangemaakt',
    saving: 'Opslaan…',
    saveChanges: 'Wijzigingen opslaan',
    changePassword: 'Wachtwoord wijzigen',
    currentPassword: 'Huidig wachtwoord',
    newPassword: 'Nieuw wachtwoord',
    min8: 'Minimaal 8 tekens',
    confirmNewPassword: 'Bevestig nieuw wachtwoord',
    changingPassword: 'Wachtwoord wijzigen…',
    changePasswordBtn: 'Wachtwoord wijzigen',
    profileLoadFailed: 'Kon profiel niet laden',
    profileUpdated: 'Profiel succesvol bijgewerkt',
    passwordChanged: 'Wachtwoord succesvol gewijzigd',
    pwTooShort: 'Nieuw wachtwoord moet minimaal 8 tekens bevatten',
    pwMismatch: 'Wachtwoorden komen niet overeen',
    pwSameAsCurrent: 'Nieuw wachtwoord moet verschillen van het huidige',
  },
  da: {
    title: 'Profilindstillinger',
    profileInfo: 'Profiloplysninger',
    personalData: 'Personlige oplysninger',
    fullName: 'Fulde navn',
    emailAddress: 'E-mailadresse',
    phone: 'Telefon',
    companyData: 'Virksomhedsoplysninger',
    companyName: 'Virksomhedsnavn',
    nip: 'Skattenummer (NIP)',
    address: 'Adresse',
    street: 'Gade',
    city: 'By',
    postalCode: 'Postnummer',
    country: 'Land',
    accountCreated: 'Konto oprettet',
    saving: 'Gemmer…',
    saveChanges: 'Gem ændringer',
    changePassword: 'Skift adgangskode',
    currentPassword: 'Nuværende adgangskode',
    newPassword: 'Ny adgangskode',
    min8: 'Minimum 8 tegn',
    confirmNewPassword: 'Bekræft ny adgangskode',
    changingPassword: 'Ændrer adgangskode…',
    changePasswordBtn: 'Skift adgangskode',
    profileLoadFailed: 'Kunne ikke indlæse profil',
    profileUpdated: 'Profil opdateret',
    passwordChanged: 'Adgangskode ændret',
    pwTooShort: 'Ny adgangskode skal være mindst 8 tegn',
    pwMismatch: 'Adgangskoderne stemmer ikke overens',
    pwSameAsCurrent: 'Ny adgangskode skal være forskellig fra den nuværende',
  },
  sv: {
    title: 'Profilinställningar',
    profileInfo: 'Profilinformation',
    personalData: 'Personuppgifter',
    fullName: 'Fullständigt namn',
    emailAddress: 'E-postadress',
    phone: 'Telefon',
    companyData: 'Företagsuppgifter',
    companyName: 'Företagsnamn',
    nip: 'Skattenummer (NIP)',
    address: 'Adress',
    street: 'Gata',
    city: 'Stad',
    postalCode: 'Postnummer',
    country: 'Land',
    accountCreated: 'Konto skapat',
    saving: 'Sparar…',
    saveChanges: 'Spara ändringar',
    changePassword: 'Byt lösenord',
    currentPassword: 'Nuvarande lösenord',
    newPassword: 'Nytt lösenord',
    min8: 'Minst 8 tecken',
    confirmNewPassword: 'Bekräfta nytt lösenord',
    changingPassword: 'Byter lösenord…',
    changePasswordBtn: 'Byt lösenord',
    profileLoadFailed: 'Kunde inte ladda profilen',
    profileUpdated: 'Profilen uppdaterades',
    passwordChanged: 'Lösenordet ändrades',
    pwTooShort: 'Nytt lösenord måste vara minst 8 tecken',
    pwMismatch: 'Lösenorden matchar inte',
    pwSameAsCurrent: 'Nytt lösenord måste skilja sig från det nuvarande',
  },
  fi: {
    title: 'Profiiliasetukset',
    profileInfo: 'Profiilitiedot',
    personalData: 'Henkilötiedot',
    fullName: 'Koko nimi',
    emailAddress: 'Sähköpostiosoite',
    phone: 'Puhelin',
    companyData: 'Yrityksen tiedot',
    companyName: 'Yrityksen nimi',
    nip: 'Verotunnus (NIP)',
    address: 'Osoite',
    street: 'Katu',
    city: 'Kaupunki',
    postalCode: 'Postinumero',
    country: 'Maa',
    accountCreated: 'Tili luotu',
    saving: 'Tallennetaan…',
    saveChanges: 'Tallenna muutokset',
    changePassword: 'Vaihda salasana',
    currentPassword: 'Nykyinen salasana',
    newPassword: 'Uusi salasana',
    min8: 'Vähintään 8 merkkiä',
    confirmNewPassword: 'Vahvista uusi salasana',
    changingPassword: 'Vaihdetaan salasanaa…',
    changePasswordBtn: 'Vaihda salasana',
    profileLoadFailed: 'Profiilin lataaminen epäonnistui',
    profileUpdated: 'Profiili päivitetty',
    passwordChanged: 'Salasana vaihdettu',
    pwTooShort: 'Uuden salasanan on oltava vähintään 8 merkkiä',
    pwMismatch: 'Salasanat eivät täsmää',
    pwSameAsCurrent: 'Uuden salasanan on oltava eri kuin nykyinen',
  },
  cs: {
    title: 'Nastavení profilu',
    profileInfo: 'Informace o profilu',
    personalData: 'Osobní údaje',
    fullName: 'Celé jméno',
    emailAddress: 'E-mailová adresa',
    phone: 'Telefon',
    companyData: 'Údaje o firmě',
    companyName: 'Název firmy',
    nip: 'DIČ (NIP)',
    address: 'Adresa',
    street: 'Ulice',
    city: 'Město',
    postalCode: 'PSČ',
    country: 'Země',
    accountCreated: 'Účet vytvořen',
    saving: 'Ukládání…',
    saveChanges: 'Uložit změny',
    changePassword: 'Změna hesla',
    currentPassword: 'Současné heslo',
    newPassword: 'Nové heslo',
    min8: 'Minimálně 8 znaků',
    confirmNewPassword: 'Potvrďte nové heslo',
    changingPassword: 'Měním heslo…',
    changePasswordBtn: 'Změnit heslo',
    profileLoadFailed: 'Nepodařilo se načíst profil',
    profileUpdated: 'Profil úspěšně aktualizován',
    passwordChanged: 'Heslo úspěšně změněno',
    pwTooShort: 'Nové heslo musí mít alespoň 8 znaků',
    pwMismatch: 'Hesla se neshodují',
    pwSameAsCurrent: 'Nové heslo musí být jiné než současné',
  },
  sk: {
    title: 'Nastavenia profilu',
    profileInfo: 'Informácie o profile',
    personalData: 'Osobné údaje',
    fullName: 'Celé meno',
    emailAddress: 'E-mailová adresa',
    phone: 'Telefón',
    companyData: 'Údaje o firme',
    companyName: 'Názov firmy',
    nip: 'DIČ (NIP)',
    address: 'Adresa',
    street: 'Ulica',
    city: 'Mesto',
    postalCode: 'PSČ',
    country: 'Krajina',
    accountCreated: 'Účet vytvorený',
    saving: 'Ukladá sa…',
    saveChanges: 'Uložiť zmeny',
    changePassword: 'Zmena hesla',
    currentPassword: 'Súčasné heslo',
    newPassword: 'Nové heslo',
    min8: 'Minimálne 8 znakov',
    confirmNewPassword: 'Potvrďte nové heslo',
    changingPassword: 'Mením heslo…',
    changePasswordBtn: 'Zmeniť heslo',
    profileLoadFailed: 'Nepodarilo sa načítať profil',
    profileUpdated: 'Profil úspešne aktualizovaný',
    passwordChanged: 'Heslo úspešne zmenené',
    pwTooShort: 'Nové heslo musí mať aspoň 8 znakov',
    pwMismatch: 'Heslá sa nezhodujú',
    pwSameAsCurrent: 'Nové heslo musí byť iné ako súčasné',
  },
  sl: {
    title: 'Nastavitve profila',
    profileInfo: 'Informacije o profilu',
    personalData: 'Osebni podatki',
    fullName: 'Polno ime',
    emailAddress: 'E-poštni naslov',
    phone: 'Telefon',
    companyData: 'Podatki o podjetju',
    companyName: 'Ime podjetja',
    nip: 'Davčna številka (NIP)',
    address: 'Naslov',
    street: 'Ulica',
    city: 'Mesto',
    postalCode: 'Poštna številka',
    country: 'Država',
    accountCreated: 'Račun ustvarjen',
    saving: 'Shranjevanje…',
    saveChanges: 'Shrani spremembe',
    changePassword: 'Spremeni geslo',
    currentPassword: 'Trenutno geslo',
    newPassword: 'Novo geslo',
    min8: 'Najmanj 8 znakov',
    confirmNewPassword: 'Potrdite novo geslo',
    changingPassword: 'Spreminjanje gesla…',
    changePasswordBtn: 'Spremeni geslo',
    profileLoadFailed: 'Profila ni bilo mogoče naložiti',
    profileUpdated: 'Profil uspešno posodobljen',
    passwordChanged: 'Geslo uspešno spremenjeno',
    pwTooShort: 'Novo geslo mora imeti vsaj 8 znakov',
    pwMismatch: 'Gesli se ne ujemata',
    pwSameAsCurrent: 'Novo geslo mora biti drugačno od trenutnega',
  },
  hr: {
    title: 'Postavke profila',
    profileInfo: 'Informacije o profilu',
    personalData: 'Osobni podaci',
    fullName: 'Puno ime',
    emailAddress: 'E-mail adresa',
    phone: 'Telefon',
    companyData: 'Podaci o tvrtki',
    companyName: 'Naziv tvrtke',
    nip: 'OIB (NIP)',
    address: 'Adresa',
    street: 'Ulica',
    city: 'Grad',
    postalCode: 'Poštanski broj',
    country: 'Država',
    accountCreated: 'Račun stvoren',
    saving: 'Spremanje…',
    saveChanges: 'Spremi promjene',
    changePassword: 'Promjena lozinke',
    currentPassword: 'Trenutna lozinka',
    newPassword: 'Nova lozinka',
    min8: 'Najmanje 8 znakova',
    confirmNewPassword: 'Potvrdite novu lozinku',
    changingPassword: 'Mijenjam lozinku…',
    changePasswordBtn: 'Promijeni lozinku',
    profileLoadFailed: 'Nije moguće učitati profil',
    profileUpdated: 'Profil uspješno ažuriran',
    passwordChanged: 'Lozinka uspješno promijenjena',
    pwTooShort: 'Nova lozinka mora imati najmanje 8 znakova',
    pwMismatch: 'Lozinke se ne podudaraju',
    pwSameAsCurrent: 'Nova lozinka mora biti drugačija od trenutne',
  },
  hu: {
    title: 'Profilbeállítások',
    profileInfo: 'Profilinformációk',
    personalData: 'Személyes adatok',
    fullName: 'Teljes név',
    emailAddress: 'E-mail cím',
    phone: 'Telefon',
    companyData: 'Céges adatok',
    companyName: 'Cégnév',
    nip: 'Adószám (NIP)',
    address: 'Cím',
    street: 'Utca',
    city: 'Város',
    postalCode: 'Irányítószám',
    country: 'Ország',
    accountCreated: 'Fiók létrehozva',
    saving: 'Mentés…',
    saveChanges: 'Változások mentése',
    changePassword: 'Jelszó módosítása',
    currentPassword: 'Jelenlegi jelszó',
    newPassword: 'Új jelszó',
    min8: 'Legalább 8 karakter',
    confirmNewPassword: 'Új jelszó megerősítése',
    changingPassword: 'Jelszó módosítása…',
    changePasswordBtn: 'Jelszó módosítása',
    profileLoadFailed: 'A profil betöltése sikertelen',
    profileUpdated: 'Profil sikeresen frissítve',
    passwordChanged: 'Jelszó sikeresen módosítva',
    pwTooShort: 'Az új jelszónak legalább 8 karakter hosszúnak kell lennie',
    pwMismatch: 'A jelszavak nem egyeznek',
    pwSameAsCurrent: 'Az új jelszónak különböznie kell a jelenlegitől',
  },
  ro: {
    title: 'Setări profil',
    profileInfo: 'Informații profil',
    personalData: 'Date personale',
    fullName: 'Nume complet',
    emailAddress: 'Adresă de e-mail',
    phone: 'Telefon',
    companyData: 'Date companie',
    companyName: 'Numele companiei',
    nip: 'CUI (NIP)',
    address: 'Adresă',
    street: 'Stradă',
    city: 'Oraș',
    postalCode: 'Cod poștal',
    country: 'Țară',
    accountCreated: 'Cont creat',
    saving: 'Se salvează…',
    saveChanges: 'Salvează modificările',
    changePassword: 'Schimbă parola',
    currentPassword: 'Parola actuală',
    newPassword: 'Parola nouă',
    min8: 'Minimum 8 caractere',
    confirmNewPassword: 'Confirmă parola nouă',
    changingPassword: 'Se schimbă parola…',
    changePasswordBtn: 'Schimbă parola',
    profileLoadFailed: 'Nu s-a putut încărca profilul',
    profileUpdated: 'Profil actualizat cu succes',
    passwordChanged: 'Parolă schimbată cu succes',
    pwTooShort: 'Parola nouă trebuie să aibă cel puțin 8 caractere',
    pwMismatch: 'Parolele nu coincid',
    pwSameAsCurrent: 'Parola nouă trebuie să fie diferită de cea actuală',
  },
  bg: {
    title: 'Настройки на профила',
    profileInfo: 'Информация за профила',
    personalData: 'Лични данни',
    fullName: 'Пълно име',
    emailAddress: 'Имейл адрес',
    phone: 'Телефон',
    companyData: 'Данни за фирмата',
    companyName: 'Име на фирмата',
    nip: 'ЕИК (NIP)',
    address: 'Адрес',
    street: 'Улица',
    city: 'Град',
    postalCode: 'Пощенски код',
    country: 'Държава',
    accountCreated: 'Профил създаден',
    saving: 'Запазване…',
    saveChanges: 'Запази промените',
    changePassword: 'Смяна на паролата',
    currentPassword: 'Текуща парола',
    newPassword: 'Нова парола',
    min8: 'Минимум 8 знака',
    confirmNewPassword: 'Потвърдете новата парола',
    changingPassword: 'Смяна на паролата…',
    changePasswordBtn: 'Смени паролата',
    profileLoadFailed: 'Неуспешно зареждане на профила',
    profileUpdated: 'Профилът е актуализиран успешно',
    passwordChanged: 'Паролата е сменена успешно',
    pwTooShort: 'Новата парола трябва да е поне 8 знака',
    pwMismatch: 'Паролите не съвпадат',
    pwSameAsCurrent: 'Новата парола трябва да е различна от текущата',
  },
  el: {
    title: 'Ρυθμίσεις προφίλ',
    profileInfo: 'Πληροφορίες προφίλ',
    personalData: 'Προσωπικά στοιχεία',
    fullName: 'Πλήρες όνομα',
    emailAddress: 'Διεύθυνση e-mail',
    phone: 'Τηλέφωνο',
    companyData: 'Στοιχεία εταιρείας',
    companyName: 'Επωνυμία εταιρείας',
    nip: 'ΑΦΜ (NIP)',
    address: 'Διεύθυνση',
    street: 'Οδός',
    city: 'Πόλη',
    postalCode: 'Ταχυδρομικός κώδικας',
    country: 'Χώρα',
    accountCreated: 'Λογαριασμός δημιουργήθηκε',
    saving: 'Αποθήκευση…',
    saveChanges: 'Αποθήκευση αλλαγών',
    changePassword: 'Αλλαγή κωδικού',
    currentPassword: 'Τρέχων κωδικός',
    newPassword: 'Νέος κωδικός',
    min8: 'Τουλάχιστον 8 χαρακτήρες',
    confirmNewPassword: 'Επιβεβαίωση νέου κωδικού',
    changingPassword: 'Αλλαγή κωδικού…',
    changePasswordBtn: 'Αλλαγή κωδικού',
    profileLoadFailed: 'Αποτυχία φόρτωσης προφίλ',
    profileUpdated: 'Το προφίλ ενημερώθηκε',
    passwordChanged: 'Ο κωδικός άλλαξε',
    pwTooShort: 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες',
    pwMismatch: 'Οι κωδικοί δεν ταιριάζουν',
    pwSameAsCurrent: 'Ο νέος κωδικός πρέπει να διαφέρει από τον τρέχοντα',
  },
  et: {
    title: 'Profiili sätted',
    profileInfo: 'Profiili teave',
    personalData: 'Isikuandmed',
    fullName: 'Täisnimi',
    emailAddress: 'E-posti aadress',
    phone: 'Telefon',
    companyData: 'Ettevõtte andmed',
    companyName: 'Ettevõtte nimi',
    nip: 'Maksukood (NIP)',
    address: 'Aadress',
    street: 'Tänav',
    city: 'Linn',
    postalCode: 'Sihtnumber',
    country: 'Riik',
    accountCreated: 'Konto loodud',
    saving: 'Salvestamine…',
    saveChanges: 'Salvesta muudatused',
    changePassword: 'Muuda parooli',
    currentPassword: 'Praegune parool',
    newPassword: 'Uus parool',
    min8: 'Vähemalt 8 märki',
    confirmNewPassword: 'Kinnita uus parool',
    changingPassword: 'Parooli muutmine…',
    changePasswordBtn: 'Muuda parooli',
    profileLoadFailed: 'Profiili laadimine ebaõnnestus',
    profileUpdated: 'Profiil edukalt uuendatud',
    passwordChanged: 'Parool edukalt muudetud',
    pwTooShort: 'Uus parool peab olema vähemalt 8 märki',
    pwMismatch: 'Paroolid ei kattu',
    pwSameAsCurrent: 'Uus parool peab erinema praegusest',
  },
  lv: {
    title: 'Profila iestatījumi',
    profileInfo: 'Profila informācija',
    personalData: 'Personas dati',
    fullName: 'Pilns vārds',
    emailAddress: 'E-pasta adrese',
    phone: 'Tālrunis',
    companyData: 'Uzņēmuma dati',
    companyName: 'Uzņēmuma nosaukums',
    nip: 'Nodokļu numurs (NIP)',
    address: 'Adrese',
    street: 'Iela',
    city: 'Pilsēta',
    postalCode: 'Pasta indekss',
    country: 'Valsts',
    accountCreated: 'Konts izveidots',
    saving: 'Saglabā…',
    saveChanges: 'Saglabāt izmaiņas',
    changePassword: 'Mainīt paroli',
    currentPassword: 'Pašreizējā parole',
    newPassword: 'Jaunā parole',
    min8: 'Vismaz 8 rakstzīmes',
    confirmNewPassword: 'Apstipriniet jauno paroli',
    changingPassword: 'Maina paroli…',
    changePasswordBtn: 'Mainīt paroli',
    profileLoadFailed: 'Neizdevās ielādēt profilu',
    profileUpdated: 'Profils veiksmīgi atjaunināts',
    passwordChanged: 'Parole veiksmīgi nomainīta',
    pwTooShort: 'Jaunajai parolei jābūt vismaz 8 rakstzīmēm',
    pwMismatch: 'Paroles nesakrīt',
    pwSameAsCurrent: 'Jaunajai parolei jāatšķiras no pašreizējās',
  },
  lt: {
    title: 'Profilio nustatymai',
    profileInfo: 'Profilio informacija',
    personalData: 'Asmens duomenys',
    fullName: 'Vardas ir pavardė',
    emailAddress: 'El. pašto adresas',
    phone: 'Telefonas',
    companyData: 'Įmonės duomenys',
    companyName: 'Įmonės pavadinimas',
    nip: 'Mokesčių kodas (NIP)',
    address: 'Adresas',
    street: 'Gatvė',
    city: 'Miestas',
    postalCode: 'Pašto kodas',
    country: 'Šalis',
    accountCreated: 'Paskyra sukurta',
    saving: 'Įrašoma…',
    saveChanges: 'Įrašyti pakeitimus',
    changePassword: 'Keisti slaptažodį',
    currentPassword: 'Dabartinis slaptažodis',
    newPassword: 'Naujas slaptažodis',
    min8: 'Bent 8 simboliai',
    confirmNewPassword: 'Patvirtinkite naują slaptažodį',
    changingPassword: 'Keičiamas slaptažodis…',
    changePasswordBtn: 'Keisti slaptažodį',
    profileLoadFailed: 'Nepavyko įkelti profilio',
    profileUpdated: 'Profilis sėkmingai atnaujintas',
    passwordChanged: 'Slaptažodis sėkmingai pakeistas',
    pwTooShort: 'Naujas slaptažodis turi būti bent 8 simbolių',
    pwMismatch: 'Slaptažodžiai nesutampa',
    pwSameAsCurrent: 'Naujas slaptažodis turi skirtis nuo dabartinio',
  },
  mt: {
    title: 'Settings tal-profil',
    profileInfo: 'Informazzjoni tal-profil',
    personalData: 'Dettalji personali',
    fullName: 'Isem sħiħ',
    emailAddress: 'Indirizz e-mail',
    phone: 'Telefon',
    companyData: 'Dettalji tal-kumpanija',
    companyName: 'Isem tal-kumpanija',
    nip: 'Numru tat-taxxa (NIP)',
    address: 'Indirizz',
    street: 'Triq',
    city: 'Belt',
    postalCode: 'Kodiċi postali',
    country: 'Pajjiż',
    accountCreated: 'Kont maħluq',
    saving: 'Qed jissejvja…',
    saveChanges: 'Issejvja l-bidliet',
    changePassword: 'Ibdel il-password',
    currentPassword: 'Password attwali',
    newPassword: 'Password ġdida',
    min8: 'Minimu 8 karattri',
    confirmNewPassword: 'Ikkonferma l-password ġdida',
    changingPassword: 'Qed tinbidel il-password…',
    changePasswordBtn: 'Ibdel il-password',
    profileLoadFailed: 'Ma setax jiġi mgħobbi l-profil',
    profileUpdated: "Profil aġġornat b'suċċess",
    passwordChanged: "Password mibdula b'suċċess",
    pwTooShort: 'Il-password ġdida trid tkun mill-inqas 8 karattri',
    pwMismatch: 'Il-passwords ma jaqblux',
    pwSameAsCurrent: 'Il-password ġdida trid tkun differenti mill-attwali',
  },
  ga: {
    title: 'Socruithe próifíle',
    profileInfo: 'Eolas próifíle',
    personalData: 'Sonraí pearsanta',
    fullName: 'Ainm iomlán',
    emailAddress: 'Seoladh ríomhphoist',
    phone: 'Fón',
    companyData: 'Sonraí cuideachta',
    companyName: 'Ainm na cuideachta',
    nip: 'Uimhir chánach (NIP)',
    address: 'Seoladh',
    street: 'Sráid',
    city: 'Cathair',
    postalCode: 'Cód poist',
    country: 'Tír',
    accountCreated: 'Cuntas cruthaithe',
    saving: 'Á shábháil…',
    saveChanges: 'Sábháil athruithe',
    changePassword: 'Athraigh focal faire',
    currentPassword: 'Focal faire reatha',
    newPassword: 'Focal faire nua',
    min8: 'Ar a laghad 8 gcarachtar',
    confirmNewPassword: 'Deimhnigh focal faire nua',
    changingPassword: 'Focal faire á athrú…',
    changePasswordBtn: 'Athraigh focal faire',
    profileLoadFailed: 'Theip ar lódáil na próifíle',
    profileUpdated: 'Próifíl nuashonraithe',
    passwordChanged: 'Focal faire athraithe',
    pwTooShort: 'Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire nua',
    pwMismatch: 'Níl na focail faire ag teacht le chéile',
    pwSameAsCurrent: 'Caithfidh an focal faire nua a bheith difriúil ón gceann reatha',
  },
  no: {
    title: 'Profilinnstillinger',
    profileInfo: 'Profilinformasjon',
    personalData: 'Personopplysninger',
    fullName: 'Fullt navn',
    emailAddress: 'E-postadresse',
    phone: 'Telefon',
    companyData: 'Firmaopplysninger',
    companyName: 'Firmanavn',
    nip: 'Skattenummer (NIP)',
    address: 'Adresse',
    street: 'Gate',
    city: 'By',
    postalCode: 'Postnummer',
    country: 'Land',
    accountCreated: 'Konto opprettet',
    saving: 'Lagrer…',
    saveChanges: 'Lagre endringer',
    changePassword: 'Bytt passord',
    currentPassword: 'Nåværende passord',
    newPassword: 'Nytt passord',
    min8: 'Minst 8 tegn',
    confirmNewPassword: 'Bekreft nytt passord',
    changingPassword: 'Endrer passord…',
    changePasswordBtn: 'Bytt passord',
    profileLoadFailed: 'Kunne ikke laste profilen',
    profileUpdated: 'Profil oppdatert',
    passwordChanged: 'Passord endret',
    pwTooShort: 'Nytt passord må være minst 8 tegn',
    pwMismatch: 'Passordene stemmer ikke overens',
    pwSameAsCurrent: 'Nytt passord må være forskjellig fra det nåværende',
  },
  is: {
    title: 'Stillingar notanda',
    profileInfo: 'Upplýsingar um notanda',
    personalData: 'Persónuupplýsingar',
    fullName: 'Fullt nafn',
    emailAddress: 'Netfang',
    phone: 'Sími',
    companyData: 'Upplýsingar um fyrirtæki',
    companyName: 'Nafn fyrirtækis',
    nip: 'Skattnúmer (NIP)',
    address: 'Heimilisfang',
    street: 'Gata',
    city: 'Borg',
    postalCode: 'Póstnúmer',
    country: 'Land',
    accountCreated: 'Aðgangur stofnaður',
    saving: 'Vista…',
    saveChanges: 'Vista breytingar',
    changePassword: 'Breyta lykilorði',
    currentPassword: 'Núverandi lykilorð',
    newPassword: 'Nýtt lykilorð',
    min8: 'Að minnsta kosti 8 stafir',
    confirmNewPassword: 'Staðfesta nýtt lykilorð',
    changingPassword: 'Breyti lykilorði…',
    changePasswordBtn: 'Breyta lykilorði',
    profileLoadFailed: 'Ekki tókst að hlaða notanda',
    profileUpdated: 'Notandi uppfærður',
    passwordChanged: 'Lykilorð breytt',
    pwTooShort: 'Nýtt lykilorð verður að vera að minnsta kosti 8 stafir',
    pwMismatch: 'Lykilorðin stemma ekki',
    pwSameAsCurrent: 'Nýtt lykilorð verður að vera annað en það núverandi',
  },
  uk: {
    title: 'Налаштування профілю',
    profileInfo: 'Інформація про профіль',
    personalData: 'Особисті дані',
    fullName: 'Повне ім’я',
    emailAddress: 'Адреса електронної пошти',
    phone: 'Телефон',
    companyData: 'Дані компанії',
    companyName: 'Назва компанії',
    nip: 'Податковий номер (NIP)',
    address: 'Адреса',
    street: 'Вулиця',
    city: 'Місто',
    postalCode: 'Поштовий індекс',
    country: 'Країна',
    accountCreated: 'Обліковий запис створено',
    saving: 'Збереження…',
    saveChanges: 'Зберегти зміни',
    changePassword: 'Зміна пароля',
    currentPassword: 'Поточний пароль',
    newPassword: 'Новий пароль',
    min8: 'Мінімум 8 символів',
    confirmNewPassword: 'Підтвердьте новий пароль',
    changingPassword: 'Зміна пароля…',
    changePasswordBtn: 'Змінити пароль',
    profileLoadFailed: 'Не вдалося завантажити профіль',
    profileUpdated: 'Профіль успішно оновлено',
    passwordChanged: 'Пароль успішно змінено',
    pwTooShort: 'Новий пароль має містити щонайменше 8 символів',
    pwMismatch: 'Паролі не збігаються',
    pwSameAsCurrent: 'Новий пароль має відрізнятися від поточного',
  },
  sr: {
    title: 'Podešavanja profila',
    profileInfo: 'Informacije o profilu',
    personalData: 'Lični podaci',
    fullName: 'Puno ime',
    emailAddress: 'E-mail adresa',
    phone: 'Telefon',
    companyData: 'Podaci o firmi',
    companyName: 'Naziv firme',
    nip: 'PIB (NIP)',
    address: 'Adresa',
    street: 'Ulica',
    city: 'Grad',
    postalCode: 'Poštanski broj',
    country: 'Država',
    accountCreated: 'Nalog kreiran',
    saving: 'Čuvanje…',
    saveChanges: 'Sačuvaj izmene',
    changePassword: 'Promena lozinke',
    currentPassword: 'Trenutna lozinka',
    newPassword: 'Nova lozinka',
    min8: 'Najmanje 8 znakova',
    confirmNewPassword: 'Potvrdite novu lozinku',
    changingPassword: 'Menjam lozinku…',
    changePasswordBtn: 'Promeni lozinku',
    profileLoadFailed: 'Nije moguće učitati profil',
    profileUpdated: 'Profil uspešno ažuriran',
    passwordChanged: 'Lozinka uspešno promenjena',
    pwTooShort: 'Nova lozinka mora imati najmanje 8 znakova',
    pwMismatch: 'Lozinke se ne podudaraju',
    pwSameAsCurrent: 'Nova lozinka mora biti drugačija od trenutne',
  },
  sq: {
    title: 'Cilësimet e profilit',
    profileInfo: 'Informacioni i profilit',
    personalData: 'Të dhëna personale',
    fullName: 'Emri i plotë',
    emailAddress: 'Adresa e-mail',
    phone: 'Telefoni',
    companyData: 'Të dhënat e kompanisë',
    companyName: 'Emri i kompanisë',
    nip: 'Numri i taksave (NIP)',
    address: 'Adresa',
    street: 'Rruga',
    city: 'Qyteti',
    postalCode: 'Kodi postar',
    country: 'Shteti',
    accountCreated: 'Llogaria u krijua',
    saving: 'Po ruhet…',
    saveChanges: 'Ruaj ndryshimet',
    changePassword: 'Ndrysho fjalëkalimin',
    currentPassword: 'Fjalëkalimi aktual',
    newPassword: 'Fjalëkalimi i ri',
    min8: 'Të paktën 8 karaktere',
    confirmNewPassword: 'Konfirmo fjalëkalimin e ri',
    changingPassword: 'Po ndryshohet fjalëkalimi…',
    changePasswordBtn: 'Ndrysho fjalëkalimin',
    profileLoadFailed: 'Profili nuk u ngarkua',
    profileUpdated: 'Profili u përditësua me sukses',
    passwordChanged: 'Fjalëkalimi u ndryshua me sukses',
    pwTooShort: 'Fjalëkalimi i ri duhet të ketë të paktën 8 karaktere',
    pwMismatch: 'Fjalëkalimet nuk përputhen',
    pwSameAsCurrent: 'Fjalëkalimi i ri duhet të jetë i ndryshëm nga aktuali',
  },
  mk: {
    title: 'Поставки на профилот',
    profileInfo: 'Информации за профилот',
    personalData: 'Лични податоци',
    fullName: 'Целосно име',
    emailAddress: 'Е-пошта адреса',
    phone: 'Телефон',
    companyData: 'Податоци за компанијата',
    companyName: 'Име на компанијата',
    nip: 'Даночен број (NIP)',
    address: 'Адреса',
    street: 'Улица',
    city: 'Град',
    postalCode: 'Поштенски код',
    country: 'Држава',
    accountCreated: 'Сметката е создадена',
    saving: 'Зачувување…',
    saveChanges: 'Зачувај промени',
    changePassword: 'Промена на лозинка',
    currentPassword: 'Тековна лозинка',
    newPassword: 'Нова лозинка',
    min8: 'Најмалку 8 знаци',
    confirmNewPassword: 'Потврдете ја новата лозинка',
    changingPassword: 'Се менува лозинката…',
    changePasswordBtn: 'Промени лозинка',
    profileLoadFailed: 'Не може да се вчита профилот',
    profileUpdated: 'Профилот е успешно ажуриран',
    passwordChanged: 'Лозинката е успешно променета',
    pwTooShort: 'Новата лозинка мора да има најмалку 8 знаци',
    pwMismatch: 'Лозинките не се совпаѓаат',
    pwSameAsCurrent: 'Новата лозинка мора да биде различна од тековната',
  },
  bs: {
    title: 'Postavke profila',
    profileInfo: 'Informacije o profilu',
    personalData: 'Lični podaci',
    fullName: 'Puno ime',
    emailAddress: 'E-mail adresa',
    phone: 'Telefon',
    companyData: 'Podaci o firmi',
    companyName: 'Naziv firme',
    nip: 'PDV broj (NIP)',
    address: 'Adresa',
    street: 'Ulica',
    city: 'Grad',
    postalCode: 'Poštanski broj',
    country: 'Država',
    accountCreated: 'Račun kreiran',
    saving: 'Spremanje…',
    saveChanges: 'Spremi izmjene',
    changePassword: 'Promjena lozinke',
    currentPassword: 'Trenutna lozinka',
    newPassword: 'Nova lozinka',
    min8: 'Najmanje 8 znakova',
    confirmNewPassword: 'Potvrdite novu lozinku',
    changingPassword: 'Mijenjam lozinku…',
    changePasswordBtn: 'Promijeni lozinku',
    profileLoadFailed: 'Nije moguće učitati profil',
    profileUpdated: 'Profil uspješno ažuriran',
    passwordChanged: 'Lozinka uspješno promijenjena',
    pwTooShort: 'Nova lozinka mora imati najmanje 8 znakova',
    pwMismatch: 'Lozinke se ne podudaraju',
    pwSameAsCurrent: 'Nova lozinka mora biti drugačija od trenutne',
  },
};

/** Namespace 'dashboard' — strona główna portalu klienta. */
const dashboardTr: Record<string, Dict> = {
  en: {
    welcome: 'Welcome, {{name}}!',
    subtitle: 'Client Portal Dashboard — Solar Forecast API',
    totalRequests: 'Total requests',
    requestsThisMonth: 'Requests this month',
    requestsToday: 'Requests today',
    forecastFetchFailed: 'Failed to fetch forecast',
    installationDetails: 'Installation details',
    csvTitle: 'Download CSV with forecast & production history (30 days)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Download PDF report for a selected month',
    pdfReport: 'PDF report',
    edit: 'Edit',
    location: 'Location',
    latitude: 'Latitude',
    longitude: 'Longitude',
    mapTitle: 'Installation location map',
    stringConfig: 'String configuration',
    azimuth: 'Azimuth',
    tilt: 'Tilt',
    panels: 'panels',
    noInstallation: 'No installation. Add your first installation to see details.',
    productionCorrection: 'Production correction',
    correctionAvailable: 'Available in your plan',
    correctionUnavailable: 'Not available in your plan',
    manage: 'Manage',
    apiDocs: 'API documentation',
    apiDocsDesc: 'Request examples, parameters, response formats and error codes.',
    apiHelp: 'API help',
    downloadPdfReport: 'Download PDF report',
    year: 'Year',
    month: 'Month',
    cancel: 'Cancel',
    download: 'Download',
    string: 'String',
  },
  pl: {
    welcome: 'Witaj, {{name}}!',
    subtitle: 'Dashboard Portalu Klienta — Solar Forecast API',
    totalRequests: 'Całkowita liczba zapytań',
    requestsThisMonth: 'Zapytania w tym miesiącu',
    requestsToday: 'Zapytania dzisiaj',
    forecastFetchFailed: 'Nie udało się pobrać prognozy',
    installationDetails: 'Szczegóły instalacji',
    csvTitle: 'Pobierz CSV z historią prognoz i produkcji (30 dni)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Pobierz raport PDF za wybrany miesiąc',
    pdfReport: 'Raport PDF',
    edit: 'Edytuj',
    location: 'Lokalizacja',
    latitude: 'Szerokość',
    longitude: 'Długość',
    mapTitle: 'Mapa lokalizacji instalacji',
    stringConfig: 'Konfiguracja stringów',
    azimuth: 'Azymut',
    tilt: 'Nachylenie',
    panels: 'paneli',
    noInstallation: 'Brak instalacji. Dodaj pierwszą instalację, aby zobaczyć szczegóły.',
    productionCorrection: 'Korekcja produkcji',
    correctionAvailable: 'Dostępna w Twoim planie',
    correctionUnavailable: 'Niedostępna w Twoim planie',
    manage: 'Zarządzaj',
    apiDocs: 'Dokumentacja API',
    apiDocsDesc: 'Przykłady zapytań, parametry, formaty odpowiedzi i kody błędów.',
    apiHelp: 'Pomoc z API',
    downloadPdfReport: 'Pobierz raport PDF',
    year: 'Rok',
    month: 'Miesiąc',
    cancel: 'Anuluj',
    download: 'Pobierz',
    string: 'String',
  },
  de: {
    welcome: 'Willkommen, {{name}}!',
    subtitle: 'Kundenportal-Dashboard — Solar Forecast API',
    totalRequests: 'Anfragen insgesamt',
    requestsThisMonth: 'Anfragen diesen Monat',
    requestsToday: 'Anfragen heute',
    forecastFetchFailed: 'Prognose konnte nicht abgerufen werden',
    installationDetails: 'Anlagendetails',
    csvTitle: 'CSV mit Prognose- und Produktionsverlauf herunterladen (30 Tage)',
    csv30d: 'CSV 30T',
    pdfTitle: 'PDF-Bericht für einen ausgewählten Monat herunterladen',
    pdfReport: 'PDF-Bericht',
    edit: 'Bearbeiten',
    location: 'Standort',
    latitude: 'Breitengrad',
    longitude: 'Längengrad',
    mapTitle: 'Standortkarte der Anlage',
    stringConfig: 'String-Konfiguration',
    azimuth: 'Azimut',
    tilt: 'Neigung',
    panels: 'Module',
    noInstallation: 'Keine Anlage. Fügen Sie Ihre erste Anlage hinzu, um Details zu sehen.',
    productionCorrection: 'Produktionskorrektur',
    correctionAvailable: 'In Ihrem Tarif verfügbar',
    correctionUnavailable: 'In Ihrem Tarif nicht verfügbar',
    manage: 'Verwalten',
    apiDocs: 'API-Dokumentation',
    apiDocsDesc: 'Anfragebeispiele, Parameter, Antwortformate und Fehlercodes.',
    apiHelp: 'API-Hilfe',
    downloadPdfReport: 'PDF-Bericht herunterladen',
    year: 'Jahr',
    month: 'Monat',
    cancel: 'Abbrechen',
    download: 'Herunterladen',
    string: 'String',
  },
  fr: {
    welcome: 'Bienvenue, {{name}} !',
    subtitle: 'Tableau de bord du portail client — Solar Forecast API',
    totalRequests: 'Requêtes totales',
    requestsThisMonth: 'Requêtes ce mois-ci',
    requestsToday: "Requêtes aujourd'hui",
    forecastFetchFailed: 'Échec de la récupération de la prévision',
    installationDetails: "Détails de l'installation",
    csvTitle: "Télécharger le CSV avec l'historique des prévisions et de la production (30 jours)",
    csv30d: 'CSV 30j',
    pdfTitle: 'Télécharger le rapport PDF pour un mois sélectionné',
    pdfReport: 'Rapport PDF',
    edit: 'Modifier',
    location: 'Emplacement',
    latitude: 'Latitude',
    longitude: 'Longitude',
    mapTitle: "Carte de l'emplacement de l'installation",
    stringConfig: 'Configuration des chaînes',
    azimuth: 'Azimut',
    tilt: 'Inclinaison',
    panels: 'panneaux',
    noInstallation:
      'Aucune installation. Ajoutez votre première installation pour voir les détails.',
    productionCorrection: 'Correction de production',
    correctionAvailable: 'Disponible dans votre forfait',
    correctionUnavailable: 'Indisponible dans votre forfait',
    manage: 'Gérer',
    apiDocs: 'Documentation API',
    apiDocsDesc: "Exemples de requêtes, paramètres, formats de réponse et codes d'erreur.",
    apiHelp: 'Aide API',
    downloadPdfReport: 'Télécharger le rapport PDF',
    year: 'Année',
    month: 'Mois',
    cancel: 'Annuler',
    download: 'Télécharger',
    string: 'Chaîne',
  },
  es: {
    welcome: '¡Bienvenido, {{name}}!',
    subtitle: 'Panel del portal del cliente — Solar Forecast API',
    totalRequests: 'Solicitudes totales',
    requestsThisMonth: 'Solicitudes este mes',
    requestsToday: 'Solicitudes hoy',
    forecastFetchFailed: 'No se pudo obtener la previsión',
    installationDetails: 'Detalles de la instalación',
    csvTitle: 'Descargar CSV con historial de previsión y producción (30 días)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Descargar informe PDF de un mes seleccionado',
    pdfReport: 'Informe PDF',
    edit: 'Editar',
    location: 'Ubicación',
    latitude: 'Latitud',
    longitude: 'Longitud',
    mapTitle: 'Mapa de ubicación de la instalación',
    stringConfig: 'Configuración de cadenas',
    azimuth: 'Acimut',
    tilt: 'Inclinación',
    panels: 'paneles',
    noInstallation: 'Sin instalación. Añada su primera instalación para ver los detalles.',
    productionCorrection: 'Corrección de producción',
    correctionAvailable: 'Disponible en su plan',
    correctionUnavailable: 'No disponible en su plan',
    manage: 'Gestionar',
    apiDocs: 'Documentación de la API',
    apiDocsDesc: 'Ejemplos de solicitudes, parámetros, formatos de respuesta y códigos de error.',
    apiHelp: 'Ayuda de la API',
    downloadPdfReport: 'Descargar informe PDF',
    year: 'Año',
    month: 'Mes',
    cancel: 'Cancelar',
    download: 'Descargar',
    string: 'Cadena',
  },
  it: {
    welcome: 'Benvenuto, {{name}}!',
    subtitle: 'Dashboard del portale cliente — Solar Forecast API',
    totalRequests: 'Richieste totali',
    requestsThisMonth: 'Richieste questo mese',
    requestsToday: 'Richieste oggi',
    forecastFetchFailed: 'Impossibile recuperare la previsione',
    installationDetails: "Dettagli dell'impianto",
    csvTitle: 'Scarica CSV con cronologia previsioni e produzione (30 giorni)',
    csv30d: 'CSV 30g',
    pdfTitle: 'Scarica il report PDF per un mese selezionato',
    pdfReport: 'Report PDF',
    edit: 'Modifica',
    location: 'Posizione',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    mapTitle: "Mappa della posizione dell'impianto",
    stringConfig: 'Configurazione delle stringhe',
    azimuth: 'Azimut',
    tilt: 'Inclinazione',
    panels: 'pannelli',
    noInstallation: 'Nessun impianto. Aggiungi il tuo primo impianto per vedere i dettagli.',
    productionCorrection: 'Correzione della produzione',
    correctionAvailable: 'Disponibile nel tuo piano',
    correctionUnavailable: 'Non disponibile nel tuo piano',
    manage: 'Gestisci',
    apiDocs: 'Documentazione API',
    apiDocsDesc: 'Esempi di richieste, parametri, formati di risposta e codici di errore.',
    apiHelp: 'Aiuto API',
    downloadPdfReport: 'Scarica il report PDF',
    year: 'Anno',
    month: 'Mese',
    cancel: 'Annulla',
    download: 'Scarica',
    string: 'Stringa',
  },
  pt: {
    welcome: 'Bem-vindo, {{name}}!',
    subtitle: 'Painel do portal do cliente — Solar Forecast API',
    totalRequests: 'Total de pedidos',
    requestsThisMonth: 'Pedidos este mês',
    requestsToday: 'Pedidos hoje',
    forecastFetchFailed: 'Não foi possível obter a previsão',
    installationDetails: 'Detalhes da instalação',
    csvTitle: 'Descarregar CSV com histórico de previsão e produção (30 dias)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Descarregar relatório PDF de um mês selecionado',
    pdfReport: 'Relatório PDF',
    edit: 'Editar',
    location: 'Localização',
    latitude: 'Latitude',
    longitude: 'Longitude',
    mapTitle: 'Mapa da localização da instalação',
    stringConfig: 'Configuração das strings',
    azimuth: 'Azimute',
    tilt: 'Inclinação',
    panels: 'painéis',
    noInstallation: 'Sem instalação. Adicione a sua primeira instalação para ver os detalhes.',
    productionCorrection: 'Correção de produção',
    correctionAvailable: 'Disponível no seu plano',
    correctionUnavailable: 'Indisponível no seu plano',
    manage: 'Gerir',
    apiDocs: 'Documentação da API',
    apiDocsDesc: 'Exemplos de pedidos, parâmetros, formatos de resposta e códigos de erro.',
    apiHelp: 'Ajuda da API',
    downloadPdfReport: 'Descarregar relatório PDF',
    year: 'Ano',
    month: 'Mês',
    cancel: 'Cancelar',
    download: 'Descarregar',
    string: 'String',
  },
  nl: {
    welcome: 'Welkom, {{name}}!',
    subtitle: 'Klantportaal-dashboard — Solar Forecast API',
    totalRequests: 'Totaal aantal verzoeken',
    requestsThisMonth: 'Verzoeken deze maand',
    requestsToday: 'Verzoeken vandaag',
    forecastFetchFailed: 'Kon prognose niet ophalen',
    installationDetails: 'Installatiedetails',
    csvTitle: 'CSV met prognose- en productiegeschiedenis downloaden (30 dagen)',
    csv30d: 'CSV 30d',
    pdfTitle: 'PDF-rapport voor een geselecteerde maand downloaden',
    pdfReport: 'PDF-rapport',
    edit: 'Bewerken',
    location: 'Locatie',
    latitude: 'Breedtegraad',
    longitude: 'Lengtegraad',
    mapTitle: 'Kaart van installatielocatie',
    stringConfig: 'Stringconfiguratie',
    azimuth: 'Azimut',
    tilt: 'Helling',
    panels: 'panelen',
    noInstallation: 'Geen installatie. Voeg uw eerste installatie toe om details te zien.',
    productionCorrection: 'Productiecorrectie',
    correctionAvailable: 'Beschikbaar in uw abonnement',
    correctionUnavailable: 'Niet beschikbaar in uw abonnement',
    manage: 'Beheren',
    apiDocs: 'API-documentatie',
    apiDocsDesc: 'Verzoekvoorbeelden, parameters, antwoordformaten en foutcodes.',
    apiHelp: 'API-hulp',
    downloadPdfReport: 'PDF-rapport downloaden',
    year: 'Jaar',
    month: 'Maand',
    cancel: 'Annuleren',
    download: 'Downloaden',
    string: 'String',
  },
  da: {
    welcome: 'Velkommen, {{name}}!',
    subtitle: 'Kundeportal-dashboard — Solar Forecast API',
    totalRequests: 'Anmodninger i alt',
    requestsThisMonth: 'Anmodninger denne måned',
    requestsToday: 'Anmodninger i dag',
    forecastFetchFailed: 'Kunne ikke hente prognose',
    installationDetails: 'Anlægsdetaljer',
    csvTitle: 'Download CSV med prognose- og produktionshistorik (30 dage)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Download PDF-rapport for en valgt måned',
    pdfReport: 'PDF-rapport',
    edit: 'Rediger',
    location: 'Placering',
    latitude: 'Breddegrad',
    longitude: 'Længdegrad',
    mapTitle: 'Kort over anlæggets placering',
    stringConfig: 'Strengkonfiguration',
    azimuth: 'Azimut',
    tilt: 'Hældning',
    panels: 'paneler',
    noInstallation: 'Intet anlæg. Tilføj dit første anlæg for at se detaljer.',
    productionCorrection: 'Produktionskorrektion',
    correctionAvailable: 'Tilgængelig i din plan',
    correctionUnavailable: 'Ikke tilgængelig i din plan',
    manage: 'Administrer',
    apiDocs: 'API-dokumentation',
    apiDocsDesc: 'Anmodningseksempler, parametre, svarformater og fejlkoder.',
    apiHelp: 'API-hjælp',
    downloadPdfReport: 'Download PDF-rapport',
    year: 'År',
    month: 'Måned',
    cancel: 'Annuller',
    download: 'Download',
    string: 'Streng',
  },
  sv: {
    welcome: 'Välkommen, {{name}}!',
    subtitle: 'Kundportal-instrumentpanel — Solar Forecast API',
    totalRequests: 'Totalt antal förfrågningar',
    requestsThisMonth: 'Förfrågningar denna månad',
    requestsToday: 'Förfrågningar idag',
    forecastFetchFailed: 'Kunde inte hämta prognos',
    installationDetails: 'Anläggningsdetaljer',
    csvTitle: 'Ladda ner CSV med prognos- och produktionshistorik (30 dagar)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Ladda ner PDF-rapport för en vald månad',
    pdfReport: 'PDF-rapport',
    edit: 'Redigera',
    location: 'Plats',
    latitude: 'Latitud',
    longitude: 'Longitud',
    mapTitle: 'Karta över anläggningens plats',
    stringConfig: 'Strängkonfiguration',
    azimuth: 'Azimut',
    tilt: 'Lutning',
    panels: 'paneler',
    noInstallation: 'Ingen anläggning. Lägg till din första anläggning för att se detaljer.',
    productionCorrection: 'Produktionskorrigering',
    correctionAvailable: 'Tillgänglig i din plan',
    correctionUnavailable: 'Inte tillgänglig i din plan',
    manage: 'Hantera',
    apiDocs: 'API-dokumentation',
    apiDocsDesc: 'Förfrågningsexempel, parametrar, svarsformat och felkoder.',
    apiHelp: 'API-hjälp',
    downloadPdfReport: 'Ladda ner PDF-rapport',
    year: 'År',
    month: 'Månad',
    cancel: 'Avbryt',
    download: 'Ladda ner',
    string: 'Sträng',
  },
  fi: {
    welcome: 'Tervetuloa, {{name}}!',
    subtitle: 'Asiakasportaalin koontinäyttö — Solar Forecast API',
    totalRequests: 'Pyyntöjä yhteensä',
    requestsThisMonth: 'Pyynnöt tässä kuussa',
    requestsToday: 'Pyynnöt tänään',
    forecastFetchFailed: 'Ennusteen haku epäonnistui',
    installationDetails: 'Laitoksen tiedot',
    csvTitle: 'Lataa CSV ennuste- ja tuotantohistorialla (30 päivää)',
    csv30d: 'CSV 30 pv',
    pdfTitle: 'Lataa PDF-raportti valitulle kuukaudelle',
    pdfReport: 'PDF-raportti',
    edit: 'Muokkaa',
    location: 'Sijainti',
    latitude: 'Leveysaste',
    longitude: 'Pituusaste',
    mapTitle: 'Laitoksen sijaintikartta',
    stringConfig: 'Sarjojen kokoonpano',
    azimuth: 'Atsimuutti',
    tilt: 'Kallistus',
    panels: 'paneelia',
    noInstallation: 'Ei laitosta. Lisää ensimmäinen laitoksesi nähdäksesi tiedot.',
    productionCorrection: 'Tuotannon korjaus',
    correctionAvailable: 'Saatavilla suunnitelmassasi',
    correctionUnavailable: 'Ei saatavilla suunnitelmassasi',
    manage: 'Hallitse',
    apiDocs: 'API-dokumentaatio',
    apiDocsDesc: 'Pyyntöesimerkkejä, parametreja, vastausmuotoja ja virhekoodeja.',
    apiHelp: 'API-ohje',
    downloadPdfReport: 'Lataa PDF-raportti',
    year: 'Vuosi',
    month: 'Kuukausi',
    cancel: 'Peruuta',
    download: 'Lataa',
    string: 'Sarja',
  },
  cs: {
    welcome: 'Vítejte, {{name}}!',
    subtitle: 'Přehled klientského portálu — Solar Forecast API',
    totalRequests: 'Celkem požadavků',
    requestsThisMonth: 'Požadavky tento měsíc',
    requestsToday: 'Požadavky dnes',
    forecastFetchFailed: 'Nepodařilo se načíst předpověď',
    installationDetails: 'Podrobnosti instalace',
    csvTitle: 'Stáhnout CSV s historií předpovědí a výroby (30 dní)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Stáhnout PDF report za vybraný měsíc',
    pdfReport: 'PDF report',
    edit: 'Upravit',
    location: 'Poloha',
    latitude: 'Zeměpisná šířka',
    longitude: 'Zeměpisná délka',
    mapTitle: 'Mapa polohy instalace',
    stringConfig: 'Konfigurace stringů',
    azimuth: 'Azimut',
    tilt: 'Sklon',
    panels: 'panelů',
    noInstallation: 'Žádná instalace. Přidejte svou první instalaci pro zobrazení podrobností.',
    productionCorrection: 'Korekce výroby',
    correctionAvailable: 'Dostupné ve vašem tarifu',
    correctionUnavailable: 'Nedostupné ve vašem tarifu',
    manage: 'Spravovat',
    apiDocs: 'Dokumentace API',
    apiDocsDesc: 'Příklady požadavků, parametry, formáty odpovědí a chybové kódy.',
    apiHelp: 'Nápověda k API',
    downloadPdfReport: 'Stáhnout PDF report',
    year: 'Rok',
    month: 'Měsíc',
    cancel: 'Zrušit',
    download: 'Stáhnout',
    string: 'String',
  },
  sk: {
    welcome: 'Vitajte, {{name}}!',
    subtitle: 'Prehľad klientského portálu — Solar Forecast API',
    totalRequests: 'Spolu požiadaviek',
    requestsThisMonth: 'Požiadavky tento mesiac',
    requestsToday: 'Požiadavky dnes',
    forecastFetchFailed: 'Nepodarilo sa načítať predpoveď',
    installationDetails: 'Podrobnosti inštalácie',
    csvTitle: 'Stiahnuť CSV s históriou predpovedí a výroby (30 dní)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Stiahnuť PDF report za vybraný mesiac',
    pdfReport: 'PDF report',
    edit: 'Upraviť',
    location: 'Poloha',
    latitude: 'Zemepisná šírka',
    longitude: 'Zemepisná dĺžka',
    mapTitle: 'Mapa polohy inštalácie',
    stringConfig: 'Konfigurácia stringov',
    azimuth: 'Azimut',
    tilt: 'Sklon',
    panels: 'panelov',
    noInstallation: 'Žiadna inštalácia. Pridajte svoju prvú inštaláciu pre zobrazenie podrobností.',
    productionCorrection: 'Korekcia výroby',
    correctionAvailable: 'Dostupné vo vašom tarife',
    correctionUnavailable: 'Nedostupné vo vašom tarife',
    manage: 'Spravovať',
    apiDocs: 'Dokumentácia API',
    apiDocsDesc: 'Príklady požiadaviek, parametre, formáty odpovedí a chybové kódy.',
    apiHelp: 'Pomocník k API',
    downloadPdfReport: 'Stiahnuť PDF report',
    year: 'Rok',
    month: 'Mesiac',
    cancel: 'Zrušiť',
    download: 'Stiahnuť',
    string: 'String',
  },
  sl: {
    welcome: 'Dobrodošli, {{name}}!',
    subtitle: 'Nadzorna plošča portala za stranke — Solar Forecast API',
    totalRequests: 'Skupaj zahtev',
    requestsThisMonth: 'Zahteve ta mesec',
    requestsToday: 'Zahteve danes',
    forecastFetchFailed: 'Napovedi ni bilo mogoče pridobiti',
    installationDetails: 'Podrobnosti naprave',
    csvTitle: 'Prenesi CSV z zgodovino napovedi in proizvodnje (30 dni)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Prenesi PDF poročilo za izbrani mesec',
    pdfReport: 'PDF poročilo',
    edit: 'Uredi',
    location: 'Lokacija',
    latitude: 'Zemljepisna širina',
    longitude: 'Zemljepisna dolžina',
    mapTitle: 'Zemljevid lokacije naprave',
    stringConfig: 'Konfiguracija nizov',
    azimuth: 'Azimut',
    tilt: 'Naklon',
    panels: 'panelov',
    noInstallation: 'Ni naprave. Dodajte svojo prvo napravo za ogled podrobnosti.',
    productionCorrection: 'Popravek proizvodnje',
    correctionAvailable: 'Na voljo v vašem paketu',
    correctionUnavailable: 'Ni na voljo v vašem paketu',
    manage: 'Upravljaj',
    apiDocs: 'Dokumentacija API',
    apiDocsDesc: 'Primeri zahtev, parametri, oblike odgovorov in kode napak.',
    apiHelp: 'Pomoč za API',
    downloadPdfReport: 'Prenesi PDF poročilo',
    year: 'Leto',
    month: 'Mesec',
    cancel: 'Prekliči',
    download: 'Prenesi',
    string: 'Niz',
  },
  hr: {
    welcome: 'Dobrodošli, {{name}}!',
    subtitle: 'Nadzorna ploča korisničkog portala — Solar Forecast API',
    totalRequests: 'Ukupno zahtjeva',
    requestsThisMonth: 'Zahtjevi ovaj mjesec',
    requestsToday: 'Zahtjevi danas',
    forecastFetchFailed: 'Nije moguće dohvatiti prognozu',
    installationDetails: 'Detalji postrojenja',
    csvTitle: 'Preuzmi CSV s poviješću prognoza i proizvodnje (30 dana)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Preuzmi PDF izvješće za odabrani mjesec',
    pdfReport: 'PDF izvješće',
    edit: 'Uredi',
    location: 'Lokacija',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    mapTitle: 'Karta lokacije postrojenja',
    stringConfig: 'Konfiguracija nizova',
    azimuth: 'Azimut',
    tilt: 'Nagib',
    panels: 'panela',
    noInstallation: 'Nema postrojenja. Dodajte svoje prvo postrojenje za prikaz detalja.',
    productionCorrection: 'Ispravak proizvodnje',
    correctionAvailable: 'Dostupno u vašem planu',
    correctionUnavailable: 'Nedostupno u vašem planu',
    manage: 'Upravljaj',
    apiDocs: 'API dokumentacija',
    apiDocsDesc: 'Primjeri zahtjeva, parametri, formati odgovora i kodovi grešaka.',
    apiHelp: 'API pomoć',
    downloadPdfReport: 'Preuzmi PDF izvješće',
    year: 'Godina',
    month: 'Mjesec',
    cancel: 'Odustani',
    download: 'Preuzmi',
    string: 'Niz',
  },
  hu: {
    welcome: 'Üdvözöljük, {{name}}!',
    subtitle: 'Ügyfélportál irányítópult — Solar Forecast API',
    totalRequests: 'Összes kérés',
    requestsThisMonth: 'Kérések ebben a hónapban',
    requestsToday: 'Kérések ma',
    forecastFetchFailed: 'Az előrejelzés lekérése sikertelen',
    installationDetails: 'Telepítés részletei',
    csvTitle: 'CSV letöltése előrejelzési és termelési előzményekkel (30 nap)',
    csv30d: 'CSV 30 nap',
    pdfTitle: 'PDF jelentés letöltése egy kiválasztott hónapra',
    pdfReport: 'PDF jelentés',
    edit: 'Szerkesztés',
    location: 'Helyszín',
    latitude: 'Szélességi fok',
    longitude: 'Hosszúsági fok',
    mapTitle: 'A telepítés helyének térképe',
    stringConfig: 'String konfiguráció',
    azimuth: 'Azimut',
    tilt: 'Dőlés',
    panels: 'panel',
    noInstallation: 'Nincs telepítés. Adja hozzá első telepítését a részletek megtekintéséhez.',
    productionCorrection: 'Termeléskorrekció',
    correctionAvailable: 'Elérhető a csomagjában',
    correctionUnavailable: 'Nem érhető el a csomagjában',
    manage: 'Kezelés',
    apiDocs: 'API dokumentáció',
    apiDocsDesc: 'Kérési példák, paraméterek, válaszformátumok és hibakódok.',
    apiHelp: 'API súgó',
    downloadPdfReport: 'PDF jelentés letöltése',
    year: 'Év',
    month: 'Hónap',
    cancel: 'Mégse',
    download: 'Letöltés',
    string: 'String',
  },
  ro: {
    welcome: 'Bun venit, {{name}}!',
    subtitle: 'Tablou de bord portal client — Solar Forecast API',
    totalRequests: 'Total cereri',
    requestsThisMonth: 'Cereri luna aceasta',
    requestsToday: 'Cereri astăzi',
    forecastFetchFailed: 'Nu s-a putut prelua prognoza',
    installationDetails: 'Detalii instalație',
    csvTitle: 'Descarcă CSV cu istoricul prognozelor și producției (30 zile)',
    csv30d: 'CSV 30 z',
    pdfTitle: 'Descarcă raport PDF pentru o lună selectată',
    pdfReport: 'Raport PDF',
    edit: 'Editează',
    location: 'Locație',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    mapTitle: 'Harta locației instalației',
    stringConfig: 'Configurație string-uri',
    azimuth: 'Azimut',
    tilt: 'Înclinare',
    panels: 'panouri',
    noInstallation: 'Nicio instalație. Adăugați prima instalație pentru a vedea detaliile.',
    productionCorrection: 'Corecție producție',
    correctionAvailable: 'Disponibil în planul dvs.',
    correctionUnavailable: 'Indisponibil în planul dvs.',
    manage: 'Gestionează',
    apiDocs: 'Documentație API',
    apiDocsDesc: 'Exemple de cereri, parametri, formate de răspuns și coduri de eroare.',
    apiHelp: 'Ajutor API',
    downloadPdfReport: 'Descarcă raport PDF',
    year: 'An',
    month: 'Lună',
    cancel: 'Anulează',
    download: 'Descarcă',
    string: 'String',
  },
  bg: {
    welcome: 'Добре дошли, {{name}}!',
    subtitle: 'Табло на клиентския портал — Solar Forecast API',
    totalRequests: 'Общо заявки',
    requestsThisMonth: 'Заявки този месец',
    requestsToday: 'Заявки днес',
    forecastFetchFailed: 'Неуспешно извличане на прогнозата',
    installationDetails: 'Детайли за инсталацията',
    csvTitle: 'Изтегляне на CSV с история на прогнози и производство (30 дни)',
    csv30d: 'CSV 30 д',
    pdfTitle: 'Изтегляне на PDF отчет за избран месец',
    pdfReport: 'PDF отчет',
    edit: 'Редактиране',
    location: 'Местоположение',
    latitude: 'Географска ширина',
    longitude: 'Географска дължина',
    mapTitle: 'Карта на местоположението на инсталацията',
    stringConfig: 'Конфигурация на стрингове',
    azimuth: 'Азимут',
    tilt: 'Наклон',
    panels: 'панела',
    noInstallation: 'Няма инсталация. Добавете първата си инсталация, за да видите детайли.',
    productionCorrection: 'Корекция на производството',
    correctionAvailable: 'Налично във вашия план',
    correctionUnavailable: 'Недостъпно във вашия план',
    manage: 'Управление',
    apiDocs: 'API документация',
    apiDocsDesc: 'Примери за заявки, параметри, формати на отговори и кодове за грешки.',
    apiHelp: 'API помощ',
    downloadPdfReport: 'Изтегляне на PDF отчет',
    year: 'Година',
    month: 'Месец',
    cancel: 'Отказ',
    download: 'Изтегляне',
    string: 'Стринг',
  },
  el: {
    welcome: 'Καλώς ήρθατε, {{name}}!',
    subtitle: 'Πίνακας ελέγχου πύλης πελάτη — Solar Forecast API',
    totalRequests: 'Σύνολο αιτημάτων',
    requestsThisMonth: 'Αιτήματα αυτόν τον μήνα',
    requestsToday: 'Αιτήματα σήμερα',
    forecastFetchFailed: 'Αποτυχία λήψης πρόβλεψης',
    installationDetails: 'Λεπτομέρειες εγκατάστασης',
    csvTitle: 'Λήψη CSV με ιστορικό προβλέψεων και παραγωγής (30 ημέρες)',
    csv30d: 'CSV 30η',
    pdfTitle: 'Λήψη αναφοράς PDF για επιλεγμένο μήνα',
    pdfReport: 'Αναφορά PDF',
    edit: 'Επεξεργασία',
    location: 'Τοποθεσία',
    latitude: 'Γεωγραφικό πλάτος',
    longitude: 'Γεωγραφικό μήκος',
    mapTitle: 'Χάρτης τοποθεσίας εγκατάστασης',
    stringConfig: 'Διαμόρφωση string',
    azimuth: 'Αζιμούθιο',
    tilt: 'Κλίση',
    panels: 'πάνελ',
    noInstallation:
      'Καμία εγκατάσταση. Προσθέστε την πρώτη σας εγκατάσταση για να δείτε λεπτομέρειες.',
    productionCorrection: 'Διόρθωση παραγωγής',
    correctionAvailable: 'Διαθέσιμο στο πρόγραμμά σας',
    correctionUnavailable: 'Μη διαθέσιμο στο πρόγραμμά σας',
    manage: 'Διαχείριση',
    apiDocs: 'Τεκμηρίωση API',
    apiDocsDesc: 'Παραδείγματα αιτημάτων, παράμετροι, μορφές απαντήσεων και κωδικοί σφαλμάτων.',
    apiHelp: 'Βοήθεια API',
    downloadPdfReport: 'Λήψη αναφοράς PDF',
    year: 'Έτος',
    month: 'Μήνας',
    cancel: 'Άκυρο',
    download: 'Λήψη',
    string: 'String',
  },
  et: {
    welcome: 'Tere tulemast, {{name}}!',
    subtitle: 'Kliendiportaali töölaud — Solar Forecast API',
    totalRequests: 'Päringuid kokku',
    requestsThisMonth: 'Päringud sel kuul',
    requestsToday: 'Päringud täna',
    forecastFetchFailed: 'Prognoosi ei õnnestunud hankida',
    installationDetails: 'Paigaldise üksikasjad',
    csvTitle: 'Laadi alla CSV prognooside ja toodangu ajalooga (30 päeva)',
    csv30d: 'CSV 30 p',
    pdfTitle: 'Laadi alla PDF-aruanne valitud kuu kohta',
    pdfReport: 'PDF-aruanne',
    edit: 'Muuda',
    location: 'Asukoht',
    latitude: 'Laiuskraad',
    longitude: 'Pikkuskraad',
    mapTitle: 'Paigaldise asukoha kaart',
    stringConfig: 'Stringide konfiguratsioon',
    azimuth: 'Asimuut',
    tilt: 'Kalle',
    panels: 'paneeli',
    noInstallation: 'Paigaldist pole. Lisa oma esimene paigaldis, et üksikasju näha.',
    productionCorrection: 'Toodangu korrektsioon',
    correctionAvailable: 'Saadaval teie paketis',
    correctionUnavailable: 'Pole teie paketis saadaval',
    manage: 'Halda',
    apiDocs: 'API dokumentatsioon',
    apiDocsDesc: 'Päringunäited, parameetrid, vastusevormingud ja veakoodid.',
    apiHelp: 'API abi',
    downloadPdfReport: 'Laadi alla PDF-aruanne',
    year: 'Aasta',
    month: 'Kuu',
    cancel: 'Tühista',
    download: 'Laadi alla',
    string: 'String',
  },
  lv: {
    welcome: 'Laipni lūdzam, {{name}}!',
    subtitle: 'Klientu portāla informācijas panelis — Solar Forecast API',
    totalRequests: 'Kopā pieprasījumu',
    requestsThisMonth: 'Pieprasījumi šomēnes',
    requestsToday: 'Pieprasījumi šodien',
    forecastFetchFailed: 'Neizdevās iegūt prognozi',
    installationDetails: 'Iekārtas informācija',
    csvTitle: 'Lejupielādēt CSV ar prognožu un ražošanas vēsturi (30 dienas)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Lejupielādēt PDF atskaiti par izvēlēto mēnesi',
    pdfReport: 'PDF atskaite',
    edit: 'Rediģēt',
    location: 'Atrašanās vieta',
    latitude: 'Ģeogrāfiskais platums',
    longitude: 'Ģeogrāfiskais garums',
    mapTitle: 'Iekārtas atrašanās vietas karte',
    stringConfig: 'Virkņu konfigurācija',
    azimuth: 'Azimuts',
    tilt: 'Slīpums',
    panels: 'paneļi',
    noInstallation: 'Nav iekārtas. Pievienojiet savu pirmo iekārtu, lai redzētu informāciju.',
    productionCorrection: 'Ražošanas korekcija',
    correctionAvailable: 'Pieejams jūsu plānā',
    correctionUnavailable: 'Nav pieejams jūsu plānā',
    manage: 'Pārvaldīt',
    apiDocs: 'API dokumentācija',
    apiDocsDesc: 'Pieprasījumu piemēri, parametri, atbilžu formāti un kļūdu kodi.',
    apiHelp: 'API palīdzība',
    downloadPdfReport: 'Lejupielādēt PDF atskaiti',
    year: 'Gads',
    month: 'Mēnesis',
    cancel: 'Atcelt',
    download: 'Lejupielādēt',
    string: 'Virkne',
  },
  lt: {
    welcome: 'Sveiki, {{name}}!',
    subtitle: 'Kliento portalo skydelis — Solar Forecast API',
    totalRequests: 'Iš viso užklausų',
    requestsThisMonth: 'Užklausos šį mėnesį',
    requestsToday: 'Užklausos šiandien',
    forecastFetchFailed: 'Nepavyko gauti prognozės',
    installationDetails: 'Įrenginio informacija',
    csvTitle: 'Atsisiųsti CSV su prognozių ir gamybos istorija (30 dienų)',
    csv30d: 'CSV 30 d.',
    pdfTitle: 'Atsisiųsti PDF ataskaitą už pasirinktą mėnesį',
    pdfReport: 'PDF ataskaita',
    edit: 'Redaguoti',
    location: 'Vieta',
    latitude: 'Platuma',
    longitude: 'Ilguma',
    mapTitle: 'Įrenginio vietos žemėlapis',
    stringConfig: 'Eilučių konfigūracija',
    azimuth: 'Azimutas',
    tilt: 'Pasvirimas',
    panels: 'plokščių',
    noInstallation: 'Nėra įrenginio. Pridėkite pirmąjį įrenginį, kad matytumėte informaciją.',
    productionCorrection: 'Gamybos korekcija',
    correctionAvailable: 'Pasiekiama jūsų plane',
    correctionUnavailable: 'Nepasiekiama jūsų plane',
    manage: 'Tvarkyti',
    apiDocs: 'API dokumentacija',
    apiDocsDesc: 'Užklausų pavyzdžiai, parametrai, atsakymų formatai ir klaidų kodai.',
    apiHelp: 'API pagalba',
    downloadPdfReport: 'Atsisiųsti PDF ataskaitą',
    year: 'Metai',
    month: 'Mėnuo',
    cancel: 'Atšaukti',
    download: 'Atsisiųsti',
    string: 'Eilutė',
  },
  mt: {
    welcome: 'Merħba, {{name}}!',
    subtitle: 'Dashboard tal-portal tal-klijent — Solar Forecast API',
    totalRequests: "Total ta' talbiet",
    requestsThisMonth: 'Talbiet dan ix-xahar',
    requestsToday: 'Talbiet illum',
    forecastFetchFailed: 'Ma setgħetx tinkiseb il-previżjoni',
    installationDetails: 'Dettalji tal-installazzjoni',
    csvTitle: 'Niżżel CSV bl-istorja tal-previżjonijiet u l-produzzjoni (30 jum)',
    csv30d: 'CSV 30j',
    pdfTitle: 'Niżżel rapport PDF għal xahar magħżul',
    pdfReport: 'Rapport PDF',
    edit: 'Editja',
    location: 'Post',
    latitude: 'Latitudni',
    longitude: 'Lonġitudni',
    mapTitle: 'Mappa tal-post tal-installazzjoni',
    stringConfig: 'Konfigurazzjoni tal-istrings',
    azimuth: 'Azimut',
    tilt: 'Inklinazzjoni',
    panels: 'pannelli',
    noInstallation:
      'L-ebda installazzjoni. Żid l-ewwel installazzjoni tiegħek biex tara d-dettalji.',
    productionCorrection: 'Korrezzjoni tal-produzzjoni',
    correctionAvailable: 'Disponibbli fil-pjan tiegħek',
    correctionUnavailable: 'Mhux disponibbli fil-pjan tiegħek',
    manage: 'Immaniġġja',
    apiDocs: 'Dokumentazzjoni API',
    apiDocsDesc: "Eżempji ta' talbiet, parametri, formati ta' tweġibiet u kodiċi ta' żball.",
    apiHelp: 'Għajnuna API',
    downloadPdfReport: 'Niżżel rapport PDF',
    year: 'Sena',
    month: 'Xahar',
    cancel: 'Ikkanċella',
    download: 'Niżżel',
    string: 'String',
  },
  ga: {
    welcome: 'Fáilte, {{name}}!',
    subtitle: 'Painéal tairsí cliaint — Solar Forecast API',
    totalRequests: 'Iarratais san iomlán',
    requestsThisMonth: 'Iarratais an mhí seo',
    requestsToday: 'Iarratais inniu',
    forecastFetchFailed: 'Theip ar an réamhaisnéis a fháil',
    installationDetails: 'Sonraí suiteála',
    csvTitle: 'Íoslódáil CSV le stair réamhaisnéise agus táirgthe (30 lá)',
    csv30d: 'CSV 30l',
    pdfTitle: 'Íoslódáil tuairisc PDF do mhí roghnaithe',
    pdfReport: 'Tuairisc PDF',
    edit: 'Cuir in eagar',
    location: 'Suíomh',
    latitude: 'Domhanleithead',
    longitude: 'Domhanfhad',
    mapTitle: 'Léarscáil suíomh na suiteála',
    stringConfig: 'Cumraíocht slabhraí',
    azimuth: 'Asaimit',
    tilt: 'Claonadh',
    panels: 'painéil',
    noInstallation: 'Gan suiteáil. Cuir do chéad suiteáil leis chun sonraí a fheiceáil.',
    productionCorrection: 'Ceartú táirgthe',
    correctionAvailable: 'Ar fáil i do phlean',
    correctionUnavailable: 'Níl sé ar fáil i do phlean',
    manage: 'Bainistigh',
    apiDocs: 'Doiciméadúchán API',
    apiDocsDesc: 'Samplaí iarratais, paraiméadair, formáidí freagartha agus cóid earráide.',
    apiHelp: 'Cabhair API',
    downloadPdfReport: 'Íoslódáil tuairisc PDF',
    year: 'Bliain',
    month: 'Mí',
    cancel: 'Cealaigh',
    download: 'Íoslódáil',
    string: 'Slabhra',
  },
  no: {
    welcome: 'Velkommen, {{name}}!',
    subtitle: 'Kundeportal-dashbord — Solar Forecast API',
    totalRequests: 'Forespørsler totalt',
    requestsThisMonth: 'Forespørsler denne måneden',
    requestsToday: 'Forespørsler i dag',
    forecastFetchFailed: 'Kunne ikke hente prognosen',
    installationDetails: 'Anleggsdetaljer',
    csvTitle: 'Last ned CSV med prognose- og produksjonshistorikk (30 dager)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Last ned PDF-rapport for en valgt måned',
    pdfReport: 'PDF-rapport',
    edit: 'Rediger',
    location: 'Plassering',
    latitude: 'Breddegrad',
    longitude: 'Lengdegrad',
    mapTitle: 'Kart over anleggets plassering',
    stringConfig: 'Strengkonfigurasjon',
    azimuth: 'Asimut',
    tilt: 'Helning',
    panels: 'paneler',
    noInstallation: 'Ingen anlegg. Legg til ditt første anlegg for å se detaljer.',
    productionCorrection: 'Produksjonskorreksjon',
    correctionAvailable: 'Tilgjengelig i planen din',
    correctionUnavailable: 'Ikke tilgjengelig i planen din',
    manage: 'Administrer',
    apiDocs: 'API-dokumentasjon',
    apiDocsDesc: 'Forespørselseksempler, parametere, svarformater og feilkoder.',
    apiHelp: 'API-hjelp',
    downloadPdfReport: 'Last ned PDF-rapport',
    year: 'År',
    month: 'Måned',
    cancel: 'Avbryt',
    download: 'Last ned',
    string: 'Streng',
  },
  is: {
    welcome: 'Velkomin, {{name}}!',
    subtitle: 'Stjórnborð viðskiptavinagáttar — Solar Forecast API',
    totalRequests: 'Beiðnir samtals',
    requestsThisMonth: 'Beiðnir í þessum mánuði',
    requestsToday: 'Beiðnir í dag',
    forecastFetchFailed: 'Ekki tókst að sækja spá',
    installationDetails: 'Upplýsingar um uppsetningu',
    csvTitle: 'Hlaða niður CSV með sögu spáa og framleiðslu (30 dagar)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Hlaða niður PDF-skýrslu fyrir valinn mánuð',
    pdfReport: 'PDF-skýrsla',
    edit: 'Breyta',
    location: 'Staðsetning',
    latitude: 'Breiddargráða',
    longitude: 'Lengdargráða',
    mapTitle: 'Kort af staðsetningu uppsetningar',
    stringConfig: 'Strengjastilling',
    azimuth: 'Áttarhorn',
    tilt: 'Halli',
    panels: 'spjöld',
    noInstallation:
      'Engin uppsetning. Bættu við fyrstu uppsetningunni þinni til að sjá upplýsingar.',
    productionCorrection: 'Framleiðsluleiðrétting',
    correctionAvailable: 'Í boði í áskriftinni þinni',
    correctionUnavailable: 'Ekki í boði í áskriftinni þinni',
    manage: 'Stjórna',
    apiDocs: 'API-skjölun',
    apiDocsDesc: 'Dæmi um beiðnir, færibreytur, svarsnið og villukóðar.',
    apiHelp: 'API-hjálp',
    downloadPdfReport: 'Hlaða niður PDF-skýrslu',
    year: 'Ár',
    month: 'Mánuður',
    cancel: 'Hætta við',
    download: 'Hlaða niður',
    string: 'Strengur',
  },
  uk: {
    welcome: 'Вітаємо, {{name}}!',
    subtitle: 'Панель порталу клієнта — Solar Forecast API',
    totalRequests: 'Усього запитів',
    requestsThisMonth: 'Запити цього місяця',
    requestsToday: 'Запити сьогодні',
    forecastFetchFailed: 'Не вдалося отримати прогноз',
    installationDetails: 'Деталі установки',
    csvTitle: 'Завантажити CSV з історією прогнозів і виробництва (30 днів)',
    csv30d: 'CSV 30 д',
    pdfTitle: 'Завантажити PDF-звіт за обраний місяць',
    pdfReport: 'PDF-звіт',
    edit: 'Редагувати',
    location: 'Розташування',
    latitude: 'Широта',
    longitude: 'Довгота',
    mapTitle: 'Карта розташування установки',
    stringConfig: 'Конфігурація стрингів',
    azimuth: 'Азимут',
    tilt: 'Нахил',
    panels: 'панелей',
    noInstallation: 'Немає установки. Додайте першу установку, щоб побачити деталі.',
    productionCorrection: 'Корекція виробництва',
    correctionAvailable: 'Доступно у вашому плані',
    correctionUnavailable: 'Недоступно у вашому плані',
    manage: 'Керувати',
    apiDocs: 'Документація API',
    apiDocsDesc: 'Приклади запитів, параметри, формати відповідей і коди помилок.',
    apiHelp: 'Довідка API',
    downloadPdfReport: 'Завантажити PDF-звіт',
    year: 'Рік',
    month: 'Місяць',
    cancel: 'Скасувати',
    download: 'Завантажити',
    string: 'Стрінг',
  },
  sr: {
    welcome: 'Dobrodošli, {{name}}!',
    subtitle: 'Kontrolna tabla korisničkog portala — Solar Forecast API',
    totalRequests: 'Ukupno zahteva',
    requestsThisMonth: 'Zahtevi ovog meseca',
    requestsToday: 'Zahtevi danas',
    forecastFetchFailed: 'Nije moguće preuzeti prognozu',
    installationDetails: 'Detalji postrojenja',
    csvTitle: 'Preuzmi CSV sa istorijom prognoza i proizvodnje (30 dana)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Preuzmi PDF izveštaj za izabrani mesec',
    pdfReport: 'PDF izveštaj',
    edit: 'Izmeni',
    location: 'Lokacija',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    mapTitle: 'Mapa lokacije postrojenja',
    stringConfig: 'Konfiguracija nizova',
    azimuth: 'Azimut',
    tilt: 'Nagib',
    panels: 'panela',
    noInstallation: 'Nema postrojenja. Dodajte svoje prvo postrojenje da biste videli detalje.',
    productionCorrection: 'Ispravka proizvodnje',
    correctionAvailable: 'Dostupno u vašem planu',
    correctionUnavailable: 'Nedostupno u vašem planu',
    manage: 'Upravljaj',
    apiDocs: 'API dokumentacija',
    apiDocsDesc: 'Primeri zahteva, parametri, formati odgovora i kodovi grešaka.',
    apiHelp: 'API pomoć',
    downloadPdfReport: 'Preuzmi PDF izveštaj',
    year: 'Godina',
    month: 'Mesec',
    cancel: 'Otkaži',
    download: 'Preuzmi',
    string: 'Niz',
  },
  sq: {
    welcome: 'Mirë se vini, {{name}}!',
    subtitle: 'Paneli i portalit të klientit — Solar Forecast API',
    totalRequests: 'Gjithsej kërkesa',
    requestsThisMonth: 'Kërkesa këtë muaj',
    requestsToday: 'Kërkesa sot',
    forecastFetchFailed: 'Marrja e parashikimit dështoi',
    installationDetails: 'Detajet e instalimit',
    csvTitle: 'Shkarko CSV me historikun e parashikimeve dhe prodhimit (30 ditë)',
    csv30d: 'CSV 30d',
    pdfTitle: 'Shkarko raportin PDF për një muaj të zgjedhur',
    pdfReport: 'Raport PDF',
    edit: 'Modifiko',
    location: 'Vendndodhja',
    latitude: 'Gjerësia gjeografike',
    longitude: 'Gjatësia gjeografike',
    mapTitle: 'Harta e vendndodhjes së instalimit',
    stringConfig: 'Konfigurimi i vargjeve',
    azimuth: 'Azimuti',
    tilt: 'Pjerrësia',
    panels: 'panele',
    noInstallation: 'Asnjë instalim. Shtoni instalimin tuaj të parë për të parë detajet.',
    productionCorrection: 'Korrigjimi i prodhimit',
    correctionAvailable: 'I disponueshëm në planin tuaj',
    correctionUnavailable: 'I padisponueshëm në planin tuaj',
    manage: 'Menaxho',
    apiDocs: 'Dokumentacioni API',
    apiDocsDesc: 'Shembuj kërkesash, parametra, formate përgjigjesh dhe kode gabimesh.',
    apiHelp: 'Ndihmë API',
    downloadPdfReport: 'Shkarko raportin PDF',
    year: 'Viti',
    month: 'Muaji',
    cancel: 'Anulo',
    download: 'Shkarko',
    string: 'Varg',
  },
  mk: {
    welcome: 'Добредојдовте, {{name}}!',
    subtitle: 'Контролна табла на клиентскиот портал — Solar Forecast API',
    totalRequests: 'Вкупно барања',
    requestsThisMonth: 'Барања овој месец',
    requestsToday: 'Барања денес',
    forecastFetchFailed: 'Не може да се преземе прогнозата',
    installationDetails: 'Детали за инсталацијата',
    csvTitle: 'Преземи CSV со историја на прогнози и производство (30 дена)',
    csv30d: 'CSV 30 д',
    pdfTitle: 'Преземи PDF извештај за избран месец',
    pdfReport: 'PDF извештај',
    edit: 'Уреди',
    location: 'Локација',
    latitude: 'Географска ширина',
    longitude: 'Географска должина',
    mapTitle: 'Карта на локацијата на инсталацијата',
    stringConfig: 'Конфигурација на стрингови',
    azimuth: 'Азимут',
    tilt: 'Наклон',
    panels: 'панели',
    noInstallation: 'Нема инсталација. Додадете ја вашата прва инсталација за да видите детали.',
    productionCorrection: 'Корекција на производството',
    correctionAvailable: 'Достапно во вашиот план',
    correctionUnavailable: 'Недостапно во вашиот план',
    manage: 'Управувај',
    apiDocs: 'API документација',
    apiDocsDesc: 'Примери на барања, параметри, формати на одговори и кодови за грешки.',
    apiHelp: 'API помош',
    downloadPdfReport: 'Преземи PDF извештај',
    year: 'Година',
    month: 'Месец',
    cancel: 'Откажи',
    download: 'Преземи',
    string: 'Стринг',
  },
  bs: {
    welcome: 'Dobrodošli, {{name}}!',
    subtitle: 'Kontrolna ploča korisničkog portala — Solar Forecast API',
    totalRequests: 'Ukupno zahtjeva',
    requestsThisMonth: 'Zahtjevi ovog mjeseca',
    requestsToday: 'Zahtjevi danas',
    forecastFetchFailed: 'Nije moguće preuzeti prognozu',
    installationDetails: 'Detalji postrojenja',
    csvTitle: 'Preuzmi CSV s historijom prognoza i proizvodnje (30 dana)',
    csv30d: 'CSV 30 d',
    pdfTitle: 'Preuzmi PDF izvještaj za odabrani mjesec',
    pdfReport: 'PDF izvještaj',
    edit: 'Uredi',
    location: 'Lokacija',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    mapTitle: 'Karta lokacije postrojenja',
    stringConfig: 'Konfiguracija nizova',
    azimuth: 'Azimut',
    tilt: 'Nagib',
    panels: 'panela',
    noInstallation: 'Nema postrojenja. Dodajte svoje prvo postrojenje da vidite detalje.',
    productionCorrection: 'Ispravka proizvodnje',
    correctionAvailable: 'Dostupno u vašem planu',
    correctionUnavailable: 'Nedostupno u vašem planu',
    manage: 'Upravljaj',
    apiDocs: 'API dokumentacija',
    apiDocsDesc: 'Primjeri zahtjeva, parametri, formati odgovora i kodovi grešaka.',
    apiHelp: 'API pomoć',
    downloadPdfReport: 'Preuzmi PDF izvještaj',
    year: 'Godina',
    month: 'Mjesec',
    cancel: 'Otkaži',
    download: 'Preuzmi',
    string: 'Niz',
  },
};

/** Namespace 'portal' — współdzielone komponenty portalu (klucz API, wykres użycia, wybór instalacji). */
const portalTr: Record<string, Dict> = {
  en: {
    apiKeyTitle: 'Your API key',
    statusActive: 'Active',
    statusExpired: 'Expired',
    statusInactive: 'Inactive',
    apiKeyLabel: 'API key',
    copied: 'Copied!',
    copy: 'Copy',
    expires: 'Expires',
    regenContact: 'To regenerate the API key, contact the administrator.',
    requestsOverTime: 'Requests over time (last 30 days)',
    noData: 'No data to display',
    noInstallationsYet: "You don't have any installations yet.",
    addFirstInstallation: 'Add your first installation',
    yourInstallations: 'Your installations',
    addInstallation: 'Add installation',
    installationN: 'Installation {{n}}',
    newInstallationName: 'New installation name',
    confirm: 'Confirm',
    cancel: 'Cancel',
  },
  pl: {
    apiKeyTitle: 'Twój klucz API',
    statusActive: 'Aktywny',
    statusExpired: 'Wygasły',
    statusInactive: 'Nieaktywny',
    apiKeyLabel: 'Klucz API',
    copied: 'Skopiowano!',
    copy: 'Skopiuj',
    expires: 'Wygasa',
    regenContact: 'W sprawie regeneracji klucza API skontaktuj się z administratorem.',
    requestsOverTime: 'Żądania w czasie (ostatnie 30 dni)',
    noData: 'Brak danych do wyświetlenia',
    noInstallationsYet: 'Nie masz jeszcze żadnej instalacji.',
    addFirstInstallation: 'Dodaj pierwszą instalację',
    yourInstallations: 'Twoje instalacje',
    addInstallation: 'Dodaj instalację',
    installationN: 'Instalacja {{n}}',
    newInstallationName: 'Nowa nazwa instalacji',
    confirm: 'Potwierdź',
    cancel: 'Anuluj',
  },
  de: {
    apiKeyTitle: 'Ihr API-Schlüssel',
    statusActive: 'Aktiv',
    statusExpired: 'Abgelaufen',
    statusInactive: 'Inaktiv',
    apiKeyLabel: 'API-Schlüssel',
    copied: 'Kopiert!',
    copy: 'Kopieren',
    expires: 'Läuft ab',
    regenContact: 'Zur Neugenerierung des API-Schlüssels wenden Sie sich an den Administrator.',
    requestsOverTime: 'Anfragen im Zeitverlauf (letzte 30 Tage)',
    noData: 'Keine Daten zum Anzeigen',
    noInstallationsYet: 'Sie haben noch keine Anlage.',
    addFirstInstallation: 'Erste Anlage hinzufügen',
    yourInstallations: 'Ihre Anlagen',
    addInstallation: 'Anlage hinzufügen',
    installationN: 'Anlage {{n}}',
    newInstallationName: 'Neuer Anlagenname',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
  },
  fr: {
    apiKeyTitle: 'Votre clé API',
    statusActive: 'Active',
    statusExpired: 'Expirée',
    statusInactive: 'Inactive',
    apiKeyLabel: 'Clé API',
    copied: 'Copié !',
    copy: 'Copier',
    expires: 'Expire',
    regenContact: "Pour régénérer la clé API, contactez l'administrateur.",
    requestsOverTime: 'Requêtes dans le temps (30 derniers jours)',
    noData: 'Aucune donnée à afficher',
    noInstallationsYet: "Vous n'avez encore aucune installation.",
    addFirstInstallation: 'Ajouter votre première installation',
    yourInstallations: 'Vos installations',
    addInstallation: 'Ajouter une installation',
    installationN: 'Installation {{n}}',
    newInstallationName: "Nouveau nom de l'installation",
    confirm: 'Confirmer',
    cancel: 'Annuler',
  },
  es: {
    apiKeyTitle: 'Su clave API',
    statusActive: 'Activa',
    statusExpired: 'Caducada',
    statusInactive: 'Inactiva',
    apiKeyLabel: 'Clave API',
    copied: '¡Copiado!',
    copy: 'Copiar',
    expires: 'Caduca',
    regenContact: 'Para regenerar la clave API, contacte con el administrador.',
    requestsOverTime: 'Solicitudes a lo largo del tiempo (últimos 30 días)',
    noData: 'No hay datos para mostrar',
    noInstallationsYet: 'Aún no tiene ninguna instalación.',
    addFirstInstallation: 'Añadir su primera instalación',
    yourInstallations: 'Sus instalaciones',
    addInstallation: 'Añadir instalación',
    installationN: 'Instalación {{n}}',
    newInstallationName: 'Nuevo nombre de la instalación',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
  },
  it: {
    apiKeyTitle: 'La tua chiave API',
    statusActive: 'Attiva',
    statusExpired: 'Scaduta',
    statusInactive: 'Inattiva',
    apiKeyLabel: 'Chiave API',
    copied: 'Copiato!',
    copy: 'Copia',
    expires: 'Scade',
    regenContact: "Per rigenerare la chiave API, contatta l'amministratore.",
    requestsOverTime: 'Richieste nel tempo (ultimi 30 giorni)',
    noData: 'Nessun dato da visualizzare',
    noInstallationsYet: 'Non hai ancora nessun impianto.',
    addFirstInstallation: 'Aggiungi il tuo primo impianto',
    yourInstallations: 'I tuoi impianti',
    addInstallation: 'Aggiungi impianto',
    installationN: 'Impianto {{n}}',
    newInstallationName: "Nuovo nome dell'impianto",
    confirm: 'Conferma',
    cancel: 'Annulla',
  },
  pt: {
    apiKeyTitle: 'A sua chave API',
    statusActive: 'Ativa',
    statusExpired: 'Expirada',
    statusInactive: 'Inativa',
    apiKeyLabel: 'Chave API',
    copied: 'Copiado!',
    copy: 'Copiar',
    expires: 'Expira',
    regenContact: 'Para regenerar a chave API, contacte o administrador.',
    requestsOverTime: 'Pedidos ao longo do tempo (últimos 30 dias)',
    noData: 'Sem dados para mostrar',
    noInstallationsYet: 'Ainda não tem nenhuma instalação.',
    addFirstInstallation: 'Adicionar a sua primeira instalação',
    yourInstallations: 'As suas instalações',
    addInstallation: 'Adicionar instalação',
    installationN: 'Instalação {{n}}',
    newInstallationName: 'Novo nome da instalação',
    confirm: 'Confirmar',
    cancel: 'Cancelar',
  },
  nl: {
    apiKeyTitle: 'Uw API-sleutel',
    statusActive: 'Actief',
    statusExpired: 'Verlopen',
    statusInactive: 'Inactief',
    apiKeyLabel: 'API-sleutel',
    copied: 'Gekopieerd!',
    copy: 'Kopiëren',
    expires: 'Verloopt',
    regenContact: 'Neem contact op met de beheerder om de API-sleutel opnieuw te genereren.',
    requestsOverTime: 'Verzoeken in de tijd (laatste 30 dagen)',
    noData: 'Geen gegevens om weer te geven',
    noInstallationsYet: 'U heeft nog geen installatie.',
    addFirstInstallation: 'Voeg uw eerste installatie toe',
    yourInstallations: 'Uw installaties',
    addInstallation: 'Installatie toevoegen',
    installationN: 'Installatie {{n}}',
    newInstallationName: 'Nieuwe installatienaam',
    confirm: 'Bevestigen',
    cancel: 'Annuleren',
  },
  da: {
    apiKeyTitle: 'Din API-nøgle',
    statusActive: 'Aktiv',
    statusExpired: 'Udløbet',
    statusInactive: 'Inaktiv',
    apiKeyLabel: 'API-nøgle',
    copied: 'Kopieret!',
    copy: 'Kopier',
    expires: 'Udløber',
    regenContact: 'Kontakt administratoren for at regenerere API-nøglen.',
    requestsOverTime: 'Anmodninger over tid (sidste 30 dage)',
    noData: 'Ingen data at vise',
    noInstallationsYet: 'Du har endnu ingen anlæg.',
    addFirstInstallation: 'Tilføj dit første anlæg',
    yourInstallations: 'Dine anlæg',
    addInstallation: 'Tilføj anlæg',
    installationN: 'Anlæg {{n}}',
    newInstallationName: 'Nyt anlægsnavn',
    confirm: 'Bekræft',
    cancel: 'Annuller',
  },
  sv: {
    apiKeyTitle: 'Din API-nyckel',
    statusActive: 'Aktiv',
    statusExpired: 'Utgången',
    statusInactive: 'Inaktiv',
    apiKeyLabel: 'API-nyckel',
    copied: 'Kopierad!',
    copy: 'Kopiera',
    expires: 'Går ut',
    regenContact: 'Kontakta administratören för att återskapa API-nyckeln.',
    requestsOverTime: 'Förfrågningar över tid (senaste 30 dagarna)',
    noData: 'Inga data att visa',
    noInstallationsYet: 'Du har inga anläggningar ännu.',
    addFirstInstallation: 'Lägg till din första anläggning',
    yourInstallations: 'Dina anläggningar',
    addInstallation: 'Lägg till anläggning',
    installationN: 'Anläggning {{n}}',
    newInstallationName: 'Nytt anläggningsnamn',
    confirm: 'Bekräfta',
    cancel: 'Avbryt',
  },
  fi: {
    apiKeyTitle: 'API-avaimesi',
    statusActive: 'Aktiivinen',
    statusExpired: 'Vanhentunut',
    statusInactive: 'Ei aktiivinen',
    apiKeyLabel: 'API-avain',
    copied: 'Kopioitu!',
    copy: 'Kopioi',
    expires: 'Vanhenee',
    regenContact: 'Ota yhteyttä ylläpitäjään API-avaimen uudistamiseksi.',
    requestsOverTime: 'Pyynnöt ajan myötä (viimeiset 30 päivää)',
    noData: 'Ei näytettäviä tietoja',
    noInstallationsYet: 'Sinulla ei ole vielä laitoksia.',
    addFirstInstallation: 'Lisää ensimmäinen laitoksesi',
    yourInstallations: 'Laitoksesi',
    addInstallation: 'Lisää laitos',
    installationN: 'Laitos {{n}}',
    newInstallationName: 'Uusi laitoksen nimi',
    confirm: 'Vahvista',
    cancel: 'Peruuta',
  },
  cs: {
    apiKeyTitle: 'Váš API klíč',
    statusActive: 'Aktivní',
    statusExpired: 'Vypršel',
    statusInactive: 'Neaktivní',
    apiKeyLabel: 'API klíč',
    copied: 'Zkopírováno!',
    copy: 'Kopírovat',
    expires: 'Vyprší',
    regenContact: 'Pro obnovení API klíče kontaktujte administrátora.',
    requestsOverTime: 'Požadavky v čase (posledních 30 dní)',
    noData: 'Žádná data k zobrazení',
    noInstallationsYet: 'Zatím nemáte žádnou instalaci.',
    addFirstInstallation: 'Přidejte svou první instalaci',
    yourInstallations: 'Vaše instalace',
    addInstallation: 'Přidat instalaci',
    installationN: 'Instalace {{n}}',
    newInstallationName: 'Nový název instalace',
    confirm: 'Potvrdit',
    cancel: 'Zrušit',
  },
  sk: {
    apiKeyTitle: 'Váš API kľúč',
    statusActive: 'Aktívny',
    statusExpired: 'Vypršal',
    statusInactive: 'Neaktívny',
    apiKeyLabel: 'API kľúč',
    copied: 'Skopírované!',
    copy: 'Kopírovať',
    expires: 'Vyprší',
    regenContact: 'Pre obnovenie API kľúča kontaktujte administrátora.',
    requestsOverTime: 'Požiadavky v čase (posledných 30 dní)',
    noData: 'Žiadne dáta na zobrazenie',
    noInstallationsYet: 'Zatiaľ nemáte žiadnu inštaláciu.',
    addFirstInstallation: 'Pridajte svoju prvú inštaláciu',
    yourInstallations: 'Vaše inštalácie',
    addInstallation: 'Pridať inštaláciu',
    installationN: 'Inštalácia {{n}}',
    newInstallationName: 'Nový názov inštalácie',
    confirm: 'Potvrdiť',
    cancel: 'Zrušiť',
  },
  sl: {
    apiKeyTitle: 'Vaš API ključ',
    statusActive: 'Aktiven',
    statusExpired: 'Potekel',
    statusInactive: 'Neaktiven',
    apiKeyLabel: 'API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    expires: 'Poteče',
    regenContact: 'Za ponovno generiranje API ključa se obrnite na skrbnika.',
    requestsOverTime: 'Zahteve skozi čas (zadnjih 30 dni)',
    noData: 'Ni podatkov za prikaz',
    noInstallationsYet: 'Še nimate nobene naprave.',
    addFirstInstallation: 'Dodajte svojo prvo napravo',
    yourInstallations: 'Vaše naprave',
    addInstallation: 'Dodaj napravo',
    installationN: 'Naprava {{n}}',
    newInstallationName: 'Novo ime naprave',
    confirm: 'Potrdi',
    cancel: 'Prekliči',
  },
  hr: {
    apiKeyTitle: 'Vaš API ključ',
    statusActive: 'Aktivan',
    statusExpired: 'Istekao',
    statusInactive: 'Neaktivan',
    apiKeyLabel: 'API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    expires: 'Istječe',
    regenContact: 'Za ponovno generiranje API ključa kontaktirajte administratora.',
    requestsOverTime: 'Zahtjevi kroz vrijeme (posljednjih 30 dana)',
    noData: 'Nema podataka za prikaz',
    noInstallationsYet: 'Još nemate nijedno postrojenje.',
    addFirstInstallation: 'Dodajte svoje prvo postrojenje',
    yourInstallations: 'Vaša postrojenja',
    addInstallation: 'Dodaj postrojenje',
    installationN: 'Postrojenje {{n}}',
    newInstallationName: 'Novi naziv postrojenja',
    confirm: 'Potvrdi',
    cancel: 'Odustani',
  },
  hu: {
    apiKeyTitle: 'Az Ön API-kulcsa',
    statusActive: 'Aktív',
    statusExpired: 'Lejárt',
    statusInactive: 'Inaktív',
    apiKeyLabel: 'API-kulcs',
    copied: 'Másolva!',
    copy: 'Másolás',
    expires: 'Lejár',
    regenContact: 'Az API-kulcs újragenerálásához forduljon az adminisztrátorhoz.',
    requestsOverTime: 'Kérések az idő során (utolsó 30 nap)',
    noData: 'Nincs megjeleníthető adat',
    noInstallationsYet: 'Még nincs egyetlen telepítése sem.',
    addFirstInstallation: 'Adja hozzá első telepítését',
    yourInstallations: 'Az Ön telepítései',
    addInstallation: 'Telepítés hozzáadása',
    installationN: 'Telepítés {{n}}',
    newInstallationName: 'Új telepítésnév',
    confirm: 'Megerősítés',
    cancel: 'Mégse',
  },
  ro: {
    apiKeyTitle: 'Cheia dvs. API',
    statusActive: 'Activă',
    statusExpired: 'Expirată',
    statusInactive: 'Inactivă',
    apiKeyLabel: 'Cheie API',
    copied: 'Copiat!',
    copy: 'Copiază',
    expires: 'Expiră',
    regenContact: 'Pentru regenerarea cheii API, contactați administratorul.',
    requestsOverTime: 'Cereri în timp (ultimele 30 de zile)',
    noData: 'Nu există date de afișat',
    noInstallationsYet: 'Nu aveți încă nicio instalație.',
    addFirstInstallation: 'Adăugați prima dvs. instalație',
    yourInstallations: 'Instalațiile dvs.',
    addInstallation: 'Adaugă instalație',
    installationN: 'Instalație {{n}}',
    newInstallationName: 'Nume nou al instalației',
    confirm: 'Confirmă',
    cancel: 'Anulează',
  },
  bg: {
    apiKeyTitle: 'Вашият API ключ',
    statusActive: 'Активен',
    statusExpired: 'Изтекъл',
    statusInactive: 'Неактивен',
    apiKeyLabel: 'API ключ',
    copied: 'Копирано!',
    copy: 'Копирай',
    expires: 'Изтича',
    regenContact: 'За повторно генериране на API ключа се свържете с администратора.',
    requestsOverTime: 'Заявки във времето (последните 30 дни)',
    noData: 'Няма данни за показване',
    noInstallationsYet: 'Все още нямате инсталация.',
    addFirstInstallation: 'Добавете първата си инсталация',
    yourInstallations: 'Вашите инсталации',
    addInstallation: 'Добави инсталация',
    installationN: 'Инсталация {{n}}',
    newInstallationName: 'Ново име на инсталацията',
    confirm: 'Потвърди',
    cancel: 'Отказ',
  },
  el: {
    apiKeyTitle: 'Το κλειδί API σας',
    statusActive: 'Ενεργό',
    statusExpired: 'Έληξε',
    statusInactive: 'Ανενεργό',
    apiKeyLabel: 'Κλειδί API',
    copied: 'Αντιγράφηκε!',
    copy: 'Αντιγραφή',
    expires: 'Λήγει',
    regenContact: 'Για αναδημιουργία του κλειδιού API, επικοινωνήστε με τον διαχειριστή.',
    requestsOverTime: 'Αιτήματα με την πάροδο του χρόνου (τελευταίες 30 ημέρες)',
    noData: 'Δεν υπάρχουν δεδομένα για εμφάνιση',
    noInstallationsYet: 'Δεν έχετε ακόμη καμία εγκατάσταση.',
    addFirstInstallation: 'Προσθέστε την πρώτη σας εγκατάσταση',
    yourInstallations: 'Οι εγκαταστάσεις σας',
    addInstallation: 'Προσθήκη εγκατάστασης',
    installationN: 'Εγκατάσταση {{n}}',
    newInstallationName: 'Νέο όνομα εγκατάστασης',
    confirm: 'Επιβεβαίωση',
    cancel: 'Άκυρο',
  },
  et: {
    apiKeyTitle: 'Teie API-võti',
    statusActive: 'Aktiivne',
    statusExpired: 'Aegunud',
    statusInactive: 'Mitteaktiivne',
    apiKeyLabel: 'API-võti',
    copied: 'Kopeeritud!',
    copy: 'Kopeeri',
    expires: 'Aegub',
    regenContact: 'API-võtme uuesti loomiseks võtke ühendust administraatoriga.',
    requestsOverTime: 'Päringud aja jooksul (viimased 30 päeva)',
    noData: 'Pole andmeid kuvamiseks',
    noInstallationsYet: 'Teil pole veel ühtegi paigaldist.',
    addFirstInstallation: 'Lisage oma esimene paigaldis',
    yourInstallations: 'Teie paigaldised',
    addInstallation: 'Lisa paigaldis',
    installationN: 'Paigaldis {{n}}',
    newInstallationName: 'Uus paigaldise nimi',
    confirm: 'Kinnita',
    cancel: 'Tühista',
  },
  lv: {
    apiKeyTitle: 'Jūsu API atslēga',
    statusActive: 'Aktīva',
    statusExpired: 'Beidzies derīgums',
    statusInactive: 'Neaktīva',
    apiKeyLabel: 'API atslēga',
    copied: 'Nokopēts!',
    copy: 'Kopēt',
    expires: 'Beidzas',
    regenContact: 'Lai atjaunotu API atslēgu, sazinieties ar administratoru.',
    requestsOverTime: 'Pieprasījumi laika gaitā (pēdējās 30 dienas)',
    noData: 'Nav datu, ko parādīt',
    noInstallationsYet: 'Jums vēl nav nevienas iekārtas.',
    addFirstInstallation: 'Pievienojiet savu pirmo iekārtu',
    yourInstallations: 'Jūsu iekārtas',
    addInstallation: 'Pievienot iekārtu',
    installationN: 'Iekārta {{n}}',
    newInstallationName: 'Jauns iekārtas nosaukums',
    confirm: 'Apstiprināt',
    cancel: 'Atcelt',
  },
  lt: {
    apiKeyTitle: 'Jūsų API raktas',
    statusActive: 'Aktyvus',
    statusExpired: 'Pasibaigęs',
    statusInactive: 'Neaktyvus',
    apiKeyLabel: 'API raktas',
    copied: 'Nukopijuota!',
    copy: 'Kopijuoti',
    expires: 'Baigiasi',
    regenContact: 'Norėdami iš naujo sugeneruoti API raktą, susisiekite su administratoriumi.',
    requestsOverTime: 'Užklausos laikui bėgant (paskutinės 30 dienų)',
    noData: 'Nėra duomenų rodymui',
    noInstallationsYet: 'Dar neturite jokio įrenginio.',
    addFirstInstallation: 'Pridėkite pirmąjį įrenginį',
    yourInstallations: 'Jūsų įrenginiai',
    addInstallation: 'Pridėti įrenginį',
    installationN: 'Įrenginys {{n}}',
    newInstallationName: 'Naujas įrenginio pavadinimas',
    confirm: 'Patvirtinti',
    cancel: 'Atšaukti',
  },
  mt: {
    apiKeyTitle: 'Iċ-ċavetta API tiegħek',
    statusActive: 'Attiva',
    statusExpired: 'Skadiet',
    statusInactive: 'Inattiva',
    apiKeyLabel: 'Ċavetta API',
    copied: 'Ikkopjat!',
    copy: 'Ikkopja',
    expires: 'Tiskadi',
    regenContact: 'Biex tirriġenera ċ-ċavetta API, ikkuntattja lill-amministratur.',
    requestsOverTime: 'Talbiet maż-żmien (l-aħħar 30 jum)',
    noData: "L-ebda dejta x'turi",
    noInstallationsYet: "Għad m'għandekx installazzjoni.",
    addFirstInstallation: 'Żid l-ewwel installazzjoni tiegħek',
    yourInstallations: 'L-installazzjonijiet tiegħek',
    addInstallation: 'Żid installazzjoni',
    installationN: 'Installazzjoni {{n}}',
    newInstallationName: 'Isem ġdid tal-installazzjoni',
    confirm: 'Ikkonferma',
    cancel: 'Ikkanċella',
  },
  ga: {
    apiKeyTitle: "D'eochair API",
    statusActive: 'Gníomhach',
    statusExpired: 'As feidhm',
    statusInactive: 'Neamhghníomhach',
    apiKeyLabel: 'Eochair API',
    copied: 'Cóipeáilte!',
    copy: 'Cóipeáil',
    expires: 'Téann as feidhm',
    regenContact: 'Chun an eochair API a athghiniúint, déan teagmháil leis an riarthóir.',
    requestsOverTime: 'Iarratais le himeacht ama (na 30 lá deireanacha)',
    noData: 'Gan sonraí le taispeáint',
    noInstallationsYet: 'Níl aon suiteáil agat fós.',
    addFirstInstallation: 'Cuir do chéad suiteáil leis',
    yourInstallations: 'Do shuiteálacha',
    addInstallation: 'Cuir suiteáil leis',
    installationN: 'Suiteáil {{n}}',
    newInstallationName: 'Ainm nua suiteála',
    confirm: 'Deimhnigh',
    cancel: 'Cealaigh',
  },
  no: {
    apiKeyTitle: 'Din API-nøkkel',
    statusActive: 'Aktiv',
    statusExpired: 'Utløpt',
    statusInactive: 'Inaktiv',
    apiKeyLabel: 'API-nøkkel',
    copied: 'Kopiert!',
    copy: 'Kopier',
    expires: 'Utløper',
    regenContact: 'Kontakt administratoren for å regenerere API-nøkkelen.',
    requestsOverTime: 'Forespørsler over tid (siste 30 dager)',
    noData: 'Ingen data å vise',
    noInstallationsYet: 'Du har ingen anlegg ennå.',
    addFirstInstallation: 'Legg til ditt første anlegg',
    yourInstallations: 'Dine anlegg',
    addInstallation: 'Legg til anlegg',
    installationN: 'Anlegg {{n}}',
    newInstallationName: 'Nytt anleggsnavn',
    confirm: 'Bekreft',
    cancel: 'Avbryt',
  },
  is: {
    apiKeyTitle: 'API-lykillinn þinn',
    statusActive: 'Virkur',
    statusExpired: 'Útrunninn',
    statusInactive: 'Óvirkur',
    apiKeyLabel: 'API-lykill',
    copied: 'Afritað!',
    copy: 'Afrita',
    expires: 'Rennur út',
    regenContact: 'Hafðu samband við stjórnanda til að endurnýja API-lykilinn.',
    requestsOverTime: 'Beiðnir yfir tíma (síðustu 30 dagar)',
    noData: 'Engin gögn til að sýna',
    noInstallationsYet: 'Þú ert ekki með neina uppsetningu ennþá.',
    addFirstInstallation: 'Bættu við fyrstu uppsetningunni þinni',
    yourInstallations: 'Uppsetningarnar þínar',
    addInstallation: 'Bæta við uppsetningu',
    installationN: 'Uppsetning {{n}}',
    newInstallationName: 'Nýtt uppsetningarheiti',
    confirm: 'Staðfesta',
    cancel: 'Hætta við',
  },
  uk: {
    apiKeyTitle: 'Ваш ключ API',
    statusActive: 'Активний',
    statusExpired: 'Прострочений',
    statusInactive: 'Неактивний',
    apiKeyLabel: 'Ключ API',
    copied: 'Скопійовано!',
    copy: 'Копіювати',
    expires: 'Закінчується',
    regenContact: 'Для повторної генерації ключа API зверніться до адміністратора.',
    requestsOverTime: 'Запити з часом (останні 30 днів)',
    noData: 'Немає даних для відображення',
    noInstallationsYet: 'У вас ще немає жодної установки.',
    addFirstInstallation: 'Додайте першу установку',
    yourInstallations: 'Ваші установки',
    addInstallation: 'Додати установку',
    installationN: 'Установка {{n}}',
    newInstallationName: 'Нова назва установки',
    confirm: 'Підтвердити',
    cancel: 'Скасувати',
  },
  sr: {
    apiKeyTitle: 'Vaš API ključ',
    statusActive: 'Aktivan',
    statusExpired: 'Istekao',
    statusInactive: 'Neaktivan',
    apiKeyLabel: 'API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    expires: 'Ističe',
    regenContact: 'Za ponovno generisanje API ključa kontaktirajte administratora.',
    requestsOverTime: 'Zahtevi tokom vremena (poslednjih 30 dana)',
    noData: 'Nema podataka za prikaz',
    noInstallationsYet: 'Još nemate nijedno postrojenje.',
    addFirstInstallation: 'Dodajte svoje prvo postrojenje',
    yourInstallations: 'Vaša postrojenja',
    addInstallation: 'Dodaj postrojenje',
    installationN: 'Postrojenje {{n}}',
    newInstallationName: 'Novi naziv postrojenja',
    confirm: 'Potvrdi',
    cancel: 'Otkaži',
  },
  sq: {
    apiKeyTitle: 'Çelësi juaj API',
    statusActive: 'Aktiv',
    statusExpired: 'Skaduar',
    statusInactive: 'Joaktiv',
    apiKeyLabel: 'Çelësi API',
    copied: 'U kopjua!',
    copy: 'Kopjo',
    expires: 'Skadon',
    regenContact: 'Për të rigjeneruar çelësin API, kontaktoni administratorin.',
    requestsOverTime: 'Kërkesat me kalimin e kohës (30 ditët e fundit)',
    noData: 'Nuk ka të dhëna për të shfaqur',
    noInstallationsYet: 'Nuk keni ende asnjë instalim.',
    addFirstInstallation: 'Shtoni instalimin tuaj të parë',
    yourInstallations: 'Instalimet tuaja',
    addInstallation: 'Shto instalim',
    installationN: 'Instalimi {{n}}',
    newInstallationName: 'Emër i ri i instalimit',
    confirm: 'Konfirmo',
    cancel: 'Anulo',
  },
  mk: {
    apiKeyTitle: 'Вашиот API клуч',
    statusActive: 'Активен',
    statusExpired: 'Истечен',
    statusInactive: 'Неактивен',
    apiKeyLabel: 'API клуч',
    copied: 'Копирано!',
    copy: 'Копирај',
    expires: 'Истекува',
    regenContact: 'За повторно генерирање на API клучот, контактирајте го администраторот.',
    requestsOverTime: 'Барања со текот на времето (последните 30 дена)',
    noData: 'Нема податоци за прикажување',
    noInstallationsYet: 'Сè уште немате ниту една инсталација.',
    addFirstInstallation: 'Додадете ја вашата прва инсталација',
    yourInstallations: 'Вашите инсталации',
    addInstallation: 'Додади инсталација',
    installationN: 'Инсталација {{n}}',
    newInstallationName: 'Ново име на инсталацијата',
    confirm: 'Потврди',
    cancel: 'Откажи',
  },
  bs: {
    apiKeyTitle: 'Vaš API ključ',
    statusActive: 'Aktivan',
    statusExpired: 'Istekao',
    statusInactive: 'Neaktivan',
    apiKeyLabel: 'API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    expires: 'Ističe',
    regenContact: 'Za ponovno generisanje API ključa kontaktirajte administratora.',
    requestsOverTime: 'Zahtjevi tokom vremena (posljednjih 30 dana)',
    noData: 'Nema podataka za prikaz',
    noInstallationsYet: 'Još nemate nijedno postrojenje.',
    addFirstInstallation: 'Dodajte svoje prvo postrojenje',
    yourInstallations: 'Vaša postrojenja',
    addInstallation: 'Dodaj postrojenje',
    installationN: 'Postrojenje {{n}}',
    newInstallationName: 'Novi naziv postrojenja',
    confirm: 'Potvrdi',
    cancel: 'Otkaži',
  },
};

/** Namespace 'webhooks' — strona webhooków portalu. */
const webhooksTr: Record<string, Dict> = {
  en: {
    title: 'Webhooks',
    description:
      'Receive events via POST from your system. The request includes the X-Webhook-Event and X-Webhook-Signature headers (HMAC SHA-256 of the body).',
    saveSecret: 'Save the secret — we will not show it again:',
    okSaved: 'OK, saved',
    addNew: 'Add a new webhook',
    adding: 'Adding…',
    addWebhook: 'Add webhook',
    yourWebhooks: 'Your webhooks',
    noWebhooks: 'No webhooks configured.',
    thUrl: 'URL',
    thEvents: 'Events',
    thSecret: 'Secret',
    thActive: 'Active',
    yes: 'YES',
    no: 'NO',
    delete: 'Delete',
    confirmDelete: 'Delete this webhook?',
    ev_production_deviation: 'Production deviation (>30%)',
    ev_key_expiring: 'API key expiring soon',
    ev_key_expired: 'API key expired',
    ev_plan_changed: 'Plan changed',
  },
  pl: {
    title: 'Webhooki',
    description:
      'Odbieraj zdarzenia POST-em ze swojego systemu. Request zawiera nagłówki X-Webhook-Event i X-Webhook-Signature (HMAC SHA-256 z body).',
    saveSecret: 'Zapisz secret — nie pokażemy go ponownie:',
    okSaved: 'OK, zapisałem',
    addNew: 'Dodaj nowy webhook',
    adding: 'Dodaję…',
    addWebhook: 'Dodaj webhook',
    yourWebhooks: 'Twoje webhooki',
    noWebhooks: 'Brak skonfigurowanych webhooków.',
    thUrl: 'URL',
    thEvents: 'Eventy',
    thSecret: 'Secret',
    thActive: 'Aktywny',
    yes: 'TAK',
    no: 'NIE',
    delete: 'Usuń',
    confirmDelete: 'Usunąć ten webhook?',
    ev_production_deviation: 'Odchylenie produkcji (>30%)',
    ev_key_expiring: 'Klucz API wygasa wkrótce',
    ev_key_expired: 'Klucz API wygasł',
    ev_plan_changed: 'Zmiana planu',
  },
  de: {
    title: 'Webhooks',
    description:
      'Empfangen Sie Ereignisse per POST von Ihrem System. Die Anfrage enthält die Header X-Webhook-Event und X-Webhook-Signature (HMAC SHA-256 des Bodys).',
    saveSecret: 'Speichern Sie das Secret — wir zeigen es nicht erneut an:',
    okSaved: 'OK, gespeichert',
    addNew: 'Neuen Webhook hinzufügen',
    adding: 'Wird hinzugefügt…',
    addWebhook: 'Webhook hinzufügen',
    yourWebhooks: 'Ihre Webhooks',
    noWebhooks: 'Keine Webhooks konfiguriert.',
    thUrl: 'URL',
    thEvents: 'Ereignisse',
    thSecret: 'Secret',
    thActive: 'Aktiv',
    yes: 'JA',
    no: 'NEIN',
    delete: 'Löschen',
    confirmDelete: 'Diesen Webhook löschen?',
    ev_production_deviation: 'Produktionsabweichung (>30%)',
    ev_key_expiring: 'API-Schlüssel läuft bald ab',
    ev_key_expired: 'API-Schlüssel abgelaufen',
    ev_plan_changed: 'Tarif geändert',
  },
  fr: {
    title: 'Webhooks',
    description:
      'Recevez des événements par POST depuis votre système. La requête inclut les en-têtes X-Webhook-Event et X-Webhook-Signature (HMAC SHA-256 du corps).',
    saveSecret: "Enregistrez le secret — nous ne l'afficherons plus :",
    okSaved: "OK, c'est noté",
    addNew: 'Ajouter un nouveau webhook',
    adding: 'Ajout…',
    addWebhook: 'Ajouter le webhook',
    yourWebhooks: 'Vos webhooks',
    noWebhooks: 'Aucun webhook configuré.',
    thUrl: 'URL',
    thEvents: 'Événements',
    thSecret: 'Secret',
    thActive: 'Actif',
    yes: 'OUI',
    no: 'NON',
    delete: 'Supprimer',
    confirmDelete: 'Supprimer ce webhook ?',
    ev_production_deviation: 'Écart de production (>30%)',
    ev_key_expiring: 'Clé API expirant bientôt',
    ev_key_expired: 'Clé API expirée',
    ev_plan_changed: 'Forfait modifié',
  },
  es: {
    title: 'Webhooks',
    description:
      'Reciba eventos mediante POST desde su sistema. La solicitud incluye las cabeceras X-Webhook-Event y X-Webhook-Signature (HMAC SHA-256 del cuerpo).',
    saveSecret: 'Guarde el secreto — no lo mostraremos de nuevo:',
    okSaved: 'OK, lo he guardado',
    addNew: 'Añadir un nuevo webhook',
    adding: 'Añadiendo…',
    addWebhook: 'Añadir webhook',
    yourWebhooks: 'Sus webhooks',
    noWebhooks: 'No hay webhooks configurados.',
    thUrl: 'URL',
    thEvents: 'Eventos',
    thSecret: 'Secreto',
    thActive: 'Activo',
    yes: 'SÍ',
    no: 'NO',
    delete: 'Eliminar',
    confirmDelete: '¿Eliminar este webhook?',
    ev_production_deviation: 'Desviación de producción (>30%)',
    ev_key_expiring: 'Clave API caducando pronto',
    ev_key_expired: 'Clave API caducada',
    ev_plan_changed: 'Plan cambiado',
  },
  it: {
    title: 'Webhook',
    description:
      'Ricevi eventi tramite POST dal tuo sistema. La richiesta include le intestazioni X-Webhook-Event e X-Webhook-Signature (HMAC SHA-256 del body).',
    saveSecret: 'Salva il secret — non lo mostreremo di nuovo:',
    okSaved: 'OK, salvato',
    addNew: 'Aggiungi un nuovo webhook',
    adding: 'Aggiunta…',
    addWebhook: 'Aggiungi webhook',
    yourWebhooks: 'I tuoi webhook',
    noWebhooks: 'Nessun webhook configurato.',
    thUrl: 'URL',
    thEvents: 'Eventi',
    thSecret: 'Secret',
    thActive: 'Attivo',
    yes: 'SÌ',
    no: 'NO',
    delete: 'Elimina',
    confirmDelete: 'Eliminare questo webhook?',
    ev_production_deviation: 'Deviazione di produzione (>30%)',
    ev_key_expiring: 'Chiave API in scadenza',
    ev_key_expired: 'Chiave API scaduta',
    ev_plan_changed: 'Piano modificato',
  },
  pt: {
    title: 'Webhooks',
    description:
      'Receba eventos via POST do seu sistema. O pedido inclui os cabeçalhos X-Webhook-Event e X-Webhook-Signature (HMAC SHA-256 do corpo).',
    saveSecret: 'Guarde o secret — não o mostraremos novamente:',
    okSaved: 'OK, guardei',
    addNew: 'Adicionar um novo webhook',
    adding: 'A adicionar…',
    addWebhook: 'Adicionar webhook',
    yourWebhooks: 'Os seus webhooks',
    noWebhooks: 'Nenhum webhook configurado.',
    thUrl: 'URL',
    thEvents: 'Eventos',
    thSecret: 'Secret',
    thActive: 'Ativo',
    yes: 'SIM',
    no: 'NÃO',
    delete: 'Eliminar',
    confirmDelete: 'Eliminar este webhook?',
    ev_production_deviation: 'Desvio de produção (>30%)',
    ev_key_expiring: 'Chave API a expirar em breve',
    ev_key_expired: 'Chave API expirada',
    ev_plan_changed: 'Plano alterado',
  },
  nl: {
    title: 'Webhooks',
    description:
      'Ontvang gebeurtenissen via POST vanuit uw systeem. Het verzoek bevat de headers X-Webhook-Event en X-Webhook-Signature (HMAC SHA-256 van de body).',
    saveSecret: 'Bewaar het secret — we tonen het niet opnieuw:',
    okSaved: 'OK, opgeslagen',
    addNew: 'Een nieuwe webhook toevoegen',
    adding: 'Toevoegen…',
    addWebhook: 'Webhook toevoegen',
    yourWebhooks: 'Uw webhooks',
    noWebhooks: 'Geen webhooks geconfigureerd.',
    thUrl: 'URL',
    thEvents: 'Gebeurtenissen',
    thSecret: 'Secret',
    thActive: 'Actief',
    yes: 'JA',
    no: 'NEE',
    delete: 'Verwijderen',
    confirmDelete: 'Deze webhook verwijderen?',
    ev_production_deviation: 'Productieafwijking (>30%)',
    ev_key_expiring: 'API-sleutel verloopt binnenkort',
    ev_key_expired: 'API-sleutel verlopen',
    ev_plan_changed: 'Abonnement gewijzigd',
  },
  da: {
    title: 'Webhooks',
    description:
      'Modtag hændelser via POST fra dit system. Anmodningen indeholder headerne X-Webhook-Event og X-Webhook-Signature (HMAC SHA-256 af body).',
    saveSecret: 'Gem secret — vi viser den ikke igen:',
    okSaved: 'OK, gemt',
    addNew: 'Tilføj en ny webhook',
    adding: 'Tilføjer…',
    addWebhook: 'Tilføj webhook',
    yourWebhooks: 'Dine webhooks',
    noWebhooks: 'Ingen webhooks konfigureret.',
    thUrl: 'URL',
    thEvents: 'Hændelser',
    thSecret: 'Secret',
    thActive: 'Aktiv',
    yes: 'JA',
    no: 'NEJ',
    delete: 'Slet',
    confirmDelete: 'Slet denne webhook?',
    ev_production_deviation: 'Produktionsafvigelse (>30%)',
    ev_key_expiring: 'API-nøgle udløber snart',
    ev_key_expired: 'API-nøgle udløbet',
    ev_plan_changed: 'Plan ændret',
  },
  sv: {
    title: 'Webhooks',
    description:
      'Ta emot händelser via POST från ditt system. Förfrågan innehåller rubrikerna X-Webhook-Event och X-Webhook-Signature (HMAC SHA-256 av body).',
    saveSecret: 'Spara hemligheten — vi visar den inte igen:',
    okSaved: 'OK, sparat',
    addNew: 'Lägg till en ny webhook',
    adding: 'Lägger till…',
    addWebhook: 'Lägg till webhook',
    yourWebhooks: 'Dina webhooks',
    noWebhooks: 'Inga webhooks konfigurerade.',
    thUrl: 'URL',
    thEvents: 'Händelser',
    thSecret: 'Hemlighet',
    thActive: 'Aktiv',
    yes: 'JA',
    no: 'NEJ',
    delete: 'Ta bort',
    confirmDelete: 'Ta bort denna webhook?',
    ev_production_deviation: 'Produktionsavvikelse (>30%)',
    ev_key_expiring: 'API-nyckel går snart ut',
    ev_key_expired: 'API-nyckel utgången',
    ev_plan_changed: 'Plan ändrad',
  },
  fi: {
    title: 'Webhookit',
    description:
      'Vastaanota tapahtumia POST-pyynnöillä järjestelmästäsi. Pyyntö sisältää otsikot X-Webhook-Event ja X-Webhook-Signature (rungon HMAC SHA-256).',
    saveSecret: 'Tallenna salaisuus — emme näytä sitä uudelleen:',
    okSaved: 'OK, tallennettu',
    addNew: 'Lisää uusi webhook',
    adding: 'Lisätään…',
    addWebhook: 'Lisää webhook',
    yourWebhooks: 'Webhookisi',
    noWebhooks: 'Ei määritettyjä webhookeja.',
    thUrl: 'URL',
    thEvents: 'Tapahtumat',
    thSecret: 'Salaisuus',
    thActive: 'Aktiivinen',
    yes: 'KYLLÄ',
    no: 'EI',
    delete: 'Poista',
    confirmDelete: 'Poistetaanko tämä webhook?',
    ev_production_deviation: 'Tuotantopoikkeama (>30%)',
    ev_key_expiring: 'API-avain vanhenee pian',
    ev_key_expired: 'API-avain vanhentunut',
    ev_plan_changed: 'Suunnitelma muuttunut',
  },
  cs: {
    title: 'Webhooky',
    description:
      'Přijímejte události přes POST ze svého systému. Požadavek obsahuje hlavičky X-Webhook-Event a X-Webhook-Signature (HMAC SHA-256 těla).',
    saveSecret: 'Uložte secret — znovu jej nezobrazíme:',
    okSaved: 'OK, uloženo',
    addNew: 'Přidat nový webhook',
    adding: 'Přidávám…',
    addWebhook: 'Přidat webhook',
    yourWebhooks: 'Vaše webhooky',
    noWebhooks: 'Žádné webhooky nenakonfigurovány.',
    thUrl: 'URL',
    thEvents: 'Události',
    thSecret: 'Secret',
    thActive: 'Aktivní',
    yes: 'ANO',
    no: 'NE',
    delete: 'Smazat',
    confirmDelete: 'Smazat tento webhook?',
    ev_production_deviation: 'Odchylka výroby (>30%)',
    ev_key_expiring: 'API klíč brzy vyprší',
    ev_key_expired: 'API klíč vypršel',
    ev_plan_changed: 'Tarif změněn',
  },
  sk: {
    title: 'Webhooky',
    description:
      'Prijímajte udalosti cez POST zo svojho systému. Požiadavka obsahuje hlavičky X-Webhook-Event a X-Webhook-Signature (HMAC SHA-256 tela).',
    saveSecret: 'Uložte secret — znova ho nezobrazíme:',
    okSaved: 'OK, uložené',
    addNew: 'Pridať nový webhook',
    adding: 'Pridávam…',
    addWebhook: 'Pridať webhook',
    yourWebhooks: 'Vaše webhooky',
    noWebhooks: 'Žiadne webhooky nenakonfigurované.',
    thUrl: 'URL',
    thEvents: 'Udalosti',
    thSecret: 'Secret',
    thActive: 'Aktívny',
    yes: 'ÁNO',
    no: 'NIE',
    delete: 'Odstrániť',
    confirmDelete: 'Odstrániť tento webhook?',
    ev_production_deviation: 'Odchýlka výroby (>30%)',
    ev_key_expiring: 'API kľúč čoskoro vyprší',
    ev_key_expired: 'API kľúč vypršal',
    ev_plan_changed: 'Tarif zmenený',
  },
  sl: {
    title: 'Webhooki',
    description:
      'Prejemajte dogodke prek POST iz svojega sistema. Zahteva vsebuje glavi X-Webhook-Event in X-Webhook-Signature (HMAC SHA-256 telesa).',
    saveSecret: 'Shranite skrivnost — ne bomo je prikazali znova:',
    okSaved: 'V redu, shranjeno',
    addNew: 'Dodaj nov webhook',
    adding: 'Dodajanje…',
    addWebhook: 'Dodaj webhook',
    yourWebhooks: 'Vaši webhooki',
    noWebhooks: 'Ni konfiguriranih webhookov.',
    thUrl: 'URL',
    thEvents: 'Dogodki',
    thSecret: 'Skrivnost',
    thActive: 'Aktiven',
    yes: 'DA',
    no: 'NE',
    delete: 'Izbriši',
    confirmDelete: 'Izbrišem ta webhook?',
    ev_production_deviation: 'Odstopanje proizvodnje (>30%)',
    ev_key_expiring: 'API ključ kmalu poteče',
    ev_key_expired: 'API ključ je potekel',
    ev_plan_changed: 'Paket spremenjen',
  },
  hr: {
    title: 'Webhookovi',
    description:
      'Primajte događaje putem POST-a iz svog sustava. Zahtjev sadrži zaglavlja X-Webhook-Event i X-Webhook-Signature (HMAC SHA-256 tijela).',
    saveSecret: 'Spremite tajnu — nećemo je ponovno prikazati:',
    okSaved: 'U redu, spremio sam',
    addNew: 'Dodaj novi webhook',
    adding: 'Dodajem…',
    addWebhook: 'Dodaj webhook',
    yourWebhooks: 'Vaši webhookovi',
    noWebhooks: 'Nema konfiguriranih webhookova.',
    thUrl: 'URL',
    thEvents: 'Događaji',
    thSecret: 'Tajna',
    thActive: 'Aktivan',
    yes: 'DA',
    no: 'NE',
    delete: 'Izbriši',
    confirmDelete: 'Izbrisati ovaj webhook?',
    ev_production_deviation: 'Odstupanje proizvodnje (>30%)',
    ev_key_expiring: 'API ključ uskoro istječe',
    ev_key_expired: 'API ključ je istekao',
    ev_plan_changed: 'Plan promijenjen',
  },
  hu: {
    title: 'Webhookok',
    description:
      'Fogadjon eseményeket POST-tal a rendszeréből. A kérés tartalmazza az X-Webhook-Event és X-Webhook-Signature fejléceket (a törzs HMAC SHA-256).',
    saveSecret: 'Mentse el a titkot — nem fogjuk újra megjeleníteni:',
    okSaved: 'OK, elmentettem',
    addNew: 'Új webhook hozzáadása',
    adding: 'Hozzáadás…',
    addWebhook: 'Webhook hozzáadása',
    yourWebhooks: 'Az Ön webhookjai',
    noWebhooks: 'Nincsenek konfigurált webhookok.',
    thUrl: 'URL',
    thEvents: 'Események',
    thSecret: 'Titok',
    thActive: 'Aktív',
    yes: 'IGEN',
    no: 'NEM',
    delete: 'Törlés',
    confirmDelete: 'Törli ezt a webhookot?',
    ev_production_deviation: 'Termeléseltérés (>30%)',
    ev_key_expiring: 'API-kulcs hamarosan lejár',
    ev_key_expired: 'API-kulcs lejárt',
    ev_plan_changed: 'Csomag módosult',
  },
  ro: {
    title: 'Webhook-uri',
    description:
      'Primiți evenimente prin POST din sistemul dvs. Cererea include anteturile X-Webhook-Event și X-Webhook-Signature (HMAC SHA-256 al corpului).',
    saveSecret: 'Salvați secretul — nu îl vom afișa din nou:',
    okSaved: 'OK, am salvat',
    addNew: 'Adăugați un nou webhook',
    adding: 'Se adaugă…',
    addWebhook: 'Adaugă webhook',
    yourWebhooks: 'Webhook-urile dvs.',
    noWebhooks: 'Niciun webhook configurat.',
    thUrl: 'URL',
    thEvents: 'Evenimente',
    thSecret: 'Secret',
    thActive: 'Activ',
    yes: 'DA',
    no: 'NU',
    delete: 'Șterge',
    confirmDelete: 'Ștergeți acest webhook?',
    ev_production_deviation: 'Abatere de producție (>30%)',
    ev_key_expiring: 'Cheie API care expiră curând',
    ev_key_expired: 'Cheie API expirată',
    ev_plan_changed: 'Plan modificat',
  },
  bg: {
    title: 'Уебхукове',
    description:
      'Получавайте събития чрез POST от вашата система. Заявката включва заглавките X-Webhook-Event и X-Webhook-Signature (HMAC SHA-256 на тялото).',
    saveSecret: 'Запазете тайната — няма да я покажем отново:',
    okSaved: 'ОК, запазих',
    addNew: 'Добавяне на нов уебхук',
    adding: 'Добавяне…',
    addWebhook: 'Добави уебхук',
    yourWebhooks: 'Вашите уебхукове',
    noWebhooks: 'Няма конфигурирани уебхукове.',
    thUrl: 'URL',
    thEvents: 'Събития',
    thSecret: 'Тайна',
    thActive: 'Активен',
    yes: 'ДА',
    no: 'НЕ',
    delete: 'Изтрий',
    confirmDelete: 'Да изтрия ли този уебхук?',
    ev_production_deviation: 'Отклонение в производството (>30%)',
    ev_key_expiring: 'API ключ изтича скоро',
    ev_key_expired: 'API ключ изтекъл',
    ev_plan_changed: 'План променен',
  },
  el: {
    title: 'Webhooks',
    description:
      'Λαμβάνετε συμβάντα μέσω POST από το σύστημά σας. Το αίτημα περιλαμβάνει τις κεφαλίδες X-Webhook-Event και X-Webhook-Signature (HMAC SHA-256 του σώματος).',
    saveSecret: 'Αποθηκεύστε το μυστικό — δεν θα το εμφανίσουμε ξανά:',
    okSaved: 'Εντάξει, το αποθήκευσα',
    addNew: 'Προσθήκη νέου webhook',
    adding: 'Προσθήκη…',
    addWebhook: 'Προσθήκη webhook',
    yourWebhooks: 'Τα webhook σας',
    noWebhooks: 'Δεν έχουν διαμορφωθεί webhook.',
    thUrl: 'URL',
    thEvents: 'Συμβάντα',
    thSecret: 'Μυστικό',
    thActive: 'Ενεργό',
    yes: 'ΝΑΙ',
    no: 'ΟΧΙ',
    delete: 'Διαγραφή',
    confirmDelete: 'Διαγραφή αυτού του webhook;',
    ev_production_deviation: 'Απόκλιση παραγωγής (>30%)',
    ev_key_expiring: 'Κλειδί API που λήγει σύντομα',
    ev_key_expired: 'Κλειδί API έληξε',
    ev_plan_changed: 'Πρόγραμμα άλλαξε',
  },
  et: {
    title: 'Webhookid',
    description:
      'Võtke sündmusi vastu POST-iga oma süsteemist. Päring sisaldab päiseid X-Webhook-Event ja X-Webhook-Signature (keha HMAC SHA-256).',
    saveSecret: 'Salvestage saladus — me ei näita seda uuesti:',
    okSaved: 'OK, salvestasin',
    addNew: 'Lisa uus webhook',
    adding: 'Lisamine…',
    addWebhook: 'Lisa webhook',
    yourWebhooks: 'Teie webhookid',
    noWebhooks: 'Webhookke pole konfigureeritud.',
    thUrl: 'URL',
    thEvents: 'Sündmused',
    thSecret: 'Saladus',
    thActive: 'Aktiivne',
    yes: 'JAH',
    no: 'EI',
    delete: 'Kustuta',
    confirmDelete: 'Kustutada see webhook?',
    ev_production_deviation: 'Toodangu kõrvalekalle (>30%)',
    ev_key_expiring: 'API-võti aegub varsti',
    ev_key_expired: 'API-võti aegunud',
    ev_plan_changed: 'Pakett muudetud',
  },
  lv: {
    title: 'Webhooki',
    description:
      'Saņemiet notikumus, izmantojot POST no savas sistēmas. Pieprasījums ietver galvenes X-Webhook-Event un X-Webhook-Signature (ķermeņa HMAC SHA-256).',
    saveSecret: 'Saglabājiet noslēpumu — mēs to vairs nerādīsim:',
    okSaved: 'Labi, saglabāju',
    addNew: 'Pievienot jaunu webhook',
    adding: 'Pievieno…',
    addWebhook: 'Pievienot webhook',
    yourWebhooks: 'Jūsu webhooki',
    noWebhooks: 'Nav konfigurētu webhook.',
    thUrl: 'URL',
    thEvents: 'Notikumi',
    thSecret: 'Noslēpums',
    thActive: 'Aktīvs',
    yes: 'JĀ',
    no: 'NĒ',
    delete: 'Dzēst',
    confirmDelete: 'Dzēst šo webhook?',
    ev_production_deviation: 'Ražošanas novirze (>30%)',
    ev_key_expiring: 'API atslēga drīz beigsies',
    ev_key_expired: 'API atslēga beigusies',
    ev_plan_changed: 'Plāns mainīts',
  },
  lt: {
    title: 'Webhookai',
    description:
      'Gaukite įvykius per POST iš savo sistemos. Užklausa apima antraštes X-Webhook-Event ir X-Webhook-Signature (kūno HMAC SHA-256).',
    saveSecret: 'Išsaugokite paslaptį — daugiau jos nerodysime:',
    okSaved: 'Gerai, išsaugojau',
    addNew: 'Pridėti naują webhook',
    adding: 'Pridedama…',
    addWebhook: 'Pridėti webhook',
    yourWebhooks: 'Jūsų webhookai',
    noWebhooks: 'Webhookų nesukonfigūruota.',
    thUrl: 'URL',
    thEvents: 'Įvykiai',
    thSecret: 'Paslaptis',
    thActive: 'Aktyvus',
    yes: 'TAIP',
    no: 'NE',
    delete: 'Ištrinti',
    confirmDelete: 'Ištrinti šį webhook?',
    ev_production_deviation: 'Gamybos nuokrypis (>30%)',
    ev_key_expiring: 'API raktas netrukus baigsis',
    ev_key_expired: 'API raktas baigėsi',
    ev_plan_changed: 'Planas pakeistas',
  },
  mt: {
    title: 'Webhooks',
    description:
      "Irċievi avvenimenti permezz ta' POST mis-sistema tiegħek. It-talba tinkludi l-headers X-Webhook-Event u X-Webhook-Signature (HMAC SHA-256 tal-body).",
    saveSecret: 'Issejvja s-sigriet — mhux se nuruh mill-ġdid:',
    okSaved: 'OK, issejvjajt',
    addNew: 'Żid webhook ġdid',
    adding: 'Qed iżid…',
    addWebhook: 'Żid webhook',
    yourWebhooks: 'Il-webhooks tiegħek',
    noWebhooks: 'L-ebda webhook ikkonfigurat.',
    thUrl: 'URL',
    thEvents: 'Avvenimenti',
    thSecret: 'Sigriet',
    thActive: 'Attiv',
    yes: 'IVA',
    no: 'LE',
    delete: 'Ħassar',
    confirmDelete: 'Tħassar dan il-webhook?',
    ev_production_deviation: 'Devjazzjoni tal-produzzjoni (>30%)',
    ev_key_expiring: 'Ċavetta API li se tiskadi dalwaqt',
    ev_key_expired: 'Ċavetta API skadiet',
    ev_plan_changed: 'Pjan inbidel',
  },
  ga: {
    title: 'Webhooks',
    description:
      'Faigh imeachtaí trí POST ó do chóras. Áirítear leis an iarratas na ceanntásca X-Webhook-Event agus X-Webhook-Signature (HMAC SHA-256 den chorp).',
    saveSecret: 'Sábháil an rún — ní thaispeánfaimid arís é:',
    okSaved: 'Ceart go leor, shábháil mé é',
    addNew: 'Cuir webhook nua leis',
    adding: 'Á chur leis…',
    addWebhook: 'Cuir webhook leis',
    yourWebhooks: 'Do webhooks',
    noWebhooks: 'Níl aon webhook cumraithe.',
    thUrl: 'URL',
    thEvents: 'Imeachtaí',
    thSecret: 'Rún',
    thActive: 'Gníomhach',
    yes: 'TÁ',
    no: 'NÍL',
    delete: 'Scrios',
    confirmDelete: 'An scriostar an webhook seo?',
    ev_production_deviation: 'Diall táirgthe (>30%)',
    ev_key_expiring: 'Eochair API ag dul as feidhm go luath',
    ev_key_expired: 'Eochair API as feidhm',
    ev_plan_changed: 'Plean athraithe',
  },
  no: {
    title: 'Webhooks',
    description:
      'Motta hendelser via POST fra systemet ditt. Forespørselen inkluderer headerne X-Webhook-Event og X-Webhook-Signature (HMAC SHA-256 av body).',
    saveSecret: 'Lagre hemmeligheten — vi viser den ikke igjen:',
    okSaved: 'OK, lagret',
    addNew: 'Legg til en ny webhook',
    adding: 'Legger til…',
    addWebhook: 'Legg til webhook',
    yourWebhooks: 'Dine webhooks',
    noWebhooks: 'Ingen webhooks konfigurert.',
    thUrl: 'URL',
    thEvents: 'Hendelser',
    thSecret: 'Hemmelighet',
    thActive: 'Aktiv',
    yes: 'JA',
    no: 'NEI',
    delete: 'Slett',
    confirmDelete: 'Slette denne webhooken?',
    ev_production_deviation: 'Produksjonsavvik (>30%)',
    ev_key_expiring: 'API-nøkkel utløper snart',
    ev_key_expired: 'API-nøkkel utløpt',
    ev_plan_changed: 'Plan endret',
  },
  is: {
    title: 'Webhooks',
    description:
      'Taktu á móti atburðum með POST frá kerfinu þínu. Beiðnin inniheldur hausana X-Webhook-Event og X-Webhook-Signature (HMAC SHA-256 af meginmáli).',
    saveSecret: 'Vistaðu leyndarmálið — við sýnum það ekki aftur:',
    okSaved: 'Í lagi, ég vistaði það',
    addNew: 'Bæta við nýjum webhook',
    adding: 'Bæti við…',
    addWebhook: 'Bæta við webhook',
    yourWebhooks: 'Webhook-arnir þínir',
    noWebhooks: 'Engir webhook-ar stilltir.',
    thUrl: 'URL',
    thEvents: 'Atburðir',
    thSecret: 'Leyndarmál',
    thActive: 'Virkur',
    yes: 'JÁ',
    no: 'NEI',
    delete: 'Eyða',
    confirmDelete: 'Eyða þessum webhook?',
    ev_production_deviation: 'Framleiðslufrávik (>30%)',
    ev_key_expiring: 'API-lykill rennur fljótlega út',
    ev_key_expired: 'API-lykill útrunninn',
    ev_plan_changed: 'Áskrift breytt',
  },
  uk: {
    title: 'Вебхуки',
    description:
      'Отримуйте події через POST зі своєї системи. Запит містить заголовки X-Webhook-Event і X-Webhook-Signature (HMAC SHA-256 тіла).',
    saveSecret: 'Збережіть секрет — ми більше його не покажемо:',
    okSaved: 'Гаразд, я зберіг',
    addNew: 'Додати новий вебхук',
    adding: 'Додавання…',
    addWebhook: 'Додати вебхук',
    yourWebhooks: 'Ваші вебхуки',
    noWebhooks: 'Вебхуки не налаштовані.',
    thUrl: 'URL',
    thEvents: 'Події',
    thSecret: 'Секрет',
    thActive: 'Активний',
    yes: 'ТАК',
    no: 'НІ',
    delete: 'Видалити',
    confirmDelete: 'Видалити цей вебхук?',
    ev_production_deviation: 'Відхилення виробництва (>30%)',
    ev_key_expiring: 'Ключ API скоро закінчиться',
    ev_key_expired: 'Ключ API закінчився',
    ev_plan_changed: 'План змінено',
  },
  sr: {
    title: 'Webhookovi',
    description:
      'Primajte događaje putem POST-a iz svog sistema. Zahtev sadrži zaglavlja X-Webhook-Event i X-Webhook-Signature (HMAC SHA-256 tela).',
    saveSecret: 'Sačuvajte tajnu — nećemo je ponovo prikazati:',
    okSaved: 'U redu, sačuvao sam',
    addNew: 'Dodaj novi webhook',
    adding: 'Dodajem…',
    addWebhook: 'Dodaj webhook',
    yourWebhooks: 'Vaši webhookovi',
    noWebhooks: 'Nema konfigurisanih webhookova.',
    thUrl: 'URL',
    thEvents: 'Događaji',
    thSecret: 'Tajna',
    thActive: 'Aktivan',
    yes: 'DA',
    no: 'NE',
    delete: 'Obriši',
    confirmDelete: 'Obrisati ovaj webhook?',
    ev_production_deviation: 'Odstupanje proizvodnje (>30%)',
    ev_key_expiring: 'API ključ uskoro ističe',
    ev_key_expired: 'API ključ je istekao',
    ev_plan_changed: 'Plan promenjen',
  },
  sq: {
    title: 'Webhooks',
    description:
      'Merrni ngjarje me POST nga sistemi juaj. Kërkesa përfshin headerët X-Webhook-Event dhe X-Webhook-Signature (HMAC SHA-256 i trupit).',
    saveSecret: 'Ruani sekretin — nuk do ta tregojmë përsëri:',
    okSaved: 'Në rregull, e ruajta',
    addNew: 'Shto një webhook të ri',
    adding: 'Po shtohet…',
    addWebhook: 'Shto webhook',
    yourWebhooks: 'Webhook-et tuaj',
    noWebhooks: 'Asnjë webhook i konfiguruar.',
    thUrl: 'URL',
    thEvents: 'Ngjarjet',
    thSecret: 'Sekreti',
    thActive: 'Aktiv',
    yes: 'PO',
    no: 'JO',
    delete: 'Fshi',
    confirmDelete: 'Të fshihet ky webhook?',
    ev_production_deviation: 'Devijim i prodhimit (>30%)',
    ev_key_expiring: 'Çelësi API skadon së shpejti',
    ev_key_expired: 'Çelësi API skadoi',
    ev_plan_changed: 'Plani u ndryshua',
  },
  mk: {
    title: 'Вебхукови',
    description:
      'Примајте настани преку POST од вашиот систем. Барањето ги вклучува заглавијата X-Webhook-Event и X-Webhook-Signature (HMAC SHA-256 на телото).',
    saveSecret: 'Зачувајте ја тајната — нема да ја прикажеме повторно:',
    okSaved: 'Во ред, зачував',
    addNew: 'Додади нов вебхук',
    adding: 'Се додава…',
    addWebhook: 'Додади вебхук',
    yourWebhooks: 'Вашите вебхукови',
    noWebhooks: 'Нема конфигурирани вебхукови.',
    thUrl: 'URL',
    thEvents: 'Настани',
    thSecret: 'Тајна',
    thActive: 'Активен',
    yes: 'ДА',
    no: 'НЕ',
    delete: 'Избриши',
    confirmDelete: 'Да го избришам овој вебхук?',
    ev_production_deviation: 'Отстапување на производството (>30%)',
    ev_key_expiring: 'API клучот наскоро истекува',
    ev_key_expired: 'API клучот истече',
    ev_plan_changed: 'Планот е променет',
  },
  bs: {
    title: 'Webhookovi',
    description:
      'Primajte događaje putem POST-a iz svog sistema. Zahtjev sadrži zaglavlja X-Webhook-Event i X-Webhook-Signature (HMAC SHA-256 tijela).',
    saveSecret: 'Sačuvajte tajnu — nećemo je ponovo prikazati:',
    okSaved: 'U redu, sačuvao sam',
    addNew: 'Dodaj novi webhook',
    adding: 'Dodajem…',
    addWebhook: 'Dodaj webhook',
    yourWebhooks: 'Vaši webhookovi',
    noWebhooks: 'Nema konfiguriranih webhookova.',
    thUrl: 'URL',
    thEvents: 'Događaji',
    thSecret: 'Tajna',
    thActive: 'Aktivan',
    yes: 'DA',
    no: 'NE',
    delete: 'Obriši',
    confirmDelete: 'Obrisati ovaj webhook?',
    ev_production_deviation: 'Odstupanje proizvodnje (>30%)',
    ev_key_expiring: 'API ključ uskoro ističe',
    ev_key_expired: 'API ključ je istekao',
    ev_plan_changed: 'Plan promijenjen',
  },
};

/** Namespace 'correction' — strona korekcji produkcji portalu. */
const correctionTr: Record<string, Dict> = {
  en: {
    title: 'Production correction',
    selectInstallation: 'Select an installation',
    selectInstallationHint:
      'Select an installation from the list above to manage production correction.',
    unavailableTitle: "Correction not available on this installation's plan",
    unavailableHint:
      "Production correction is not enabled for the selected installation's plan. Contact the administrator to get access.",
    subtitle: 'Submit actual production data to improve forecast accuracy.',
    defaultInForecasts: 'Default correction in forecasts',
    defaultHint: 'When enabled, forecasts are automatically corrected based on your data.',
    disableAria: 'Disable default correction',
    enableAria: 'Enable default correction',
    on: 'Enabled',
    off: 'Disabled',
    statsTitle: 'Correction statistics',
    days: '{{count}} days',
    noStats: 'No statistical data. Submit production data to see a comparison.',
    factorsTitle: 'Correction factors',
    thHourUtc: 'Hour (UTC)',
    thFactor: 'Factor',
    thSamples: 'Sample count',
  },
  pl: {
    title: 'Korekcja produkcji',
    selectInstallation: 'Wybierz instalację',
    selectInstallationHint: 'Wybierz instalację z listy powyżej, aby zarządzać korekcją produkcji.',
    unavailableTitle: 'Korekcja niedostępna w planie tej instalacji',
    unavailableHint:
      'Funkcja korekcji produkcji nie jest włączona dla planu wybranej instalacji. Skontaktuj się z administratorem, aby uzyskać dostęp.',
    subtitle: 'Wprowadzaj rzeczywiste dane produkcji, aby poprawić dokładność prognoz.',
    defaultInForecasts: 'Domyślna korekcja w prognozach',
    defaultHint: 'Gdy włączone, prognozy będą automatycznie korygowane na podstawie Twoich danych.',
    disableAria: 'Wyłącz domyślną korekcję',
    enableAria: 'Włącz domyślną korekcję',
    on: 'Włączona',
    off: 'Wyłączona',
    statsTitle: 'Statystyki korekcji',
    days: '{{count}} dni',
    noStats: 'Brak danych statystycznych. Wprowadź dane produkcji, aby zobaczyć porównanie.',
    factorsTitle: 'Współczynniki korekcji',
    thHourUtc: 'Godzina (UTC)',
    thFactor: 'Współczynnik',
    thSamples: 'Liczba próbek',
  },
  de: {
    title: 'Produktionskorrektur',
    selectInstallation: 'Anlage auswählen',
    selectInstallationHint:
      'Wählen Sie eine Anlage aus der obigen Liste, um die Produktionskorrektur zu verwalten.',
    unavailableTitle: 'Korrektur im Tarif dieser Anlage nicht verfügbar',
    unavailableHint:
      'Die Produktionskorrektur ist für den Tarif der ausgewählten Anlage nicht aktiviert. Kontaktieren Sie den Administrator, um Zugriff zu erhalten.',
    subtitle:
      'Geben Sie tatsächliche Produktionsdaten ein, um die Prognosegenauigkeit zu verbessern.',
    defaultInForecasts: 'Standardkorrektur in Prognosen',
    defaultHint: 'Wenn aktiviert, werden Prognosen automatisch anhand Ihrer Daten korrigiert.',
    disableAria: 'Standardkorrektur deaktivieren',
    enableAria: 'Standardkorrektur aktivieren',
    on: 'Aktiviert',
    off: 'Deaktiviert',
    statsTitle: 'Korrekturstatistik',
    days: '{{count}} Tage',
    noStats:
      'Keine statistischen Daten. Geben Sie Produktionsdaten ein, um einen Vergleich zu sehen.',
    factorsTitle: 'Korrekturfaktoren',
    thHourUtc: 'Stunde (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Anzahl Stichproben',
  },
  fr: {
    title: 'Correction de production',
    selectInstallation: 'Sélectionnez une installation',
    selectInstallationHint:
      'Sélectionnez une installation dans la liste ci-dessus pour gérer la correction de production.',
    unavailableTitle: 'Correction non disponible dans le forfait de cette installation',
    unavailableHint:
      "La correction de production n'est pas activée pour le forfait de l'installation sélectionnée. Contactez l'administrateur pour y accéder.",
    subtitle:
      'Soumettez des données de production réelles pour améliorer la précision des prévisions.',
    defaultInForecasts: 'Correction par défaut dans les prévisions',
    defaultHint:
      'Lorsque activé, les prévisions sont automatiquement corrigées en fonction de vos données.',
    disableAria: 'Désactiver la correction par défaut',
    enableAria: 'Activer la correction par défaut',
    on: 'Activée',
    off: 'Désactivée',
    statsTitle: 'Statistiques de correction',
    days: '{{count}} jours',
    noStats:
      'Aucune donnée statistique. Soumettez des données de production pour voir une comparaison.',
    factorsTitle: 'Facteurs de correction',
    thHourUtc: 'Heure (UTC)',
    thFactor: 'Facteur',
    thSamples: "Nombre d'échantillons",
  },
  es: {
    title: 'Corrección de producción',
    selectInstallation: 'Seleccione una instalación',
    selectInstallationHint:
      'Seleccione una instalación de la lista anterior para gestionar la corrección de producción.',
    unavailableTitle: 'Corrección no disponible en el plan de esta instalación',
    unavailableHint:
      'La corrección de producción no está habilitada para el plan de la instalación seleccionada. Contacte con el administrador para obtener acceso.',
    subtitle: 'Envíe datos reales de producción para mejorar la precisión de las previsiones.',
    defaultInForecasts: 'Corrección predeterminada en las previsiones',
    defaultHint:
      'Cuando está habilitada, las previsiones se corrigen automáticamente según sus datos.',
    disableAria: 'Desactivar corrección predeterminada',
    enableAria: 'Activar corrección predeterminada',
    on: 'Activada',
    off: 'Desactivada',
    statsTitle: 'Estadísticas de corrección',
    days: '{{count}} días',
    noStats: 'No hay datos estadísticos. Envíe datos de producción para ver una comparación.',
    factorsTitle: 'Factores de corrección',
    thHourUtc: 'Hora (UTC)',
    thFactor: 'Factor',
    thSamples: 'Número de muestras',
  },
  it: {
    title: 'Correzione della produzione',
    selectInstallation: 'Seleziona un impianto',
    selectInstallationHint:
      "Seleziona un impianto dall'elenco sopra per gestire la correzione della produzione.",
    unavailableTitle: 'Correzione non disponibile nel piano di questo impianto',
    unavailableHint:
      "La correzione della produzione non è abilitata per il piano dell'impianto selezionato. Contatta l'amministratore per ottenere l'accesso.",
    subtitle: 'Invia dati di produzione reali per migliorare la precisione delle previsioni.',
    defaultInForecasts: 'Correzione predefinita nelle previsioni',
    defaultHint:
      'Quando abilitata, le previsioni vengono corrette automaticamente in base ai tuoi dati.',
    disableAria: 'Disabilita correzione predefinita',
    enableAria: 'Abilita correzione predefinita',
    on: 'Abilitata',
    off: 'Disabilitata',
    statsTitle: 'Statistiche di correzione',
    days: '{{count}} giorni',
    noStats: 'Nessun dato statistico. Invia dati di produzione per vedere un confronto.',
    factorsTitle: 'Fattori di correzione',
    thHourUtc: 'Ora (UTC)',
    thFactor: 'Fattore',
    thSamples: 'Numero di campioni',
  },
  pt: {
    title: 'Correção de produção',
    selectInstallation: 'Selecione uma instalação',
    selectInstallationHint:
      'Selecione uma instalação da lista acima para gerir a correção de produção.',
    unavailableTitle: 'Correção não disponível no plano desta instalação',
    unavailableHint:
      'A correção de produção não está ativada para o plano da instalação selecionada. Contacte o administrador para obter acesso.',
    subtitle: 'Submeta dados de produção reais para melhorar a precisão das previsões.',
    defaultInForecasts: 'Correção predefinida nas previsões',
    defaultHint:
      'Quando ativada, as previsões são corrigidas automaticamente com base nos seus dados.',
    disableAria: 'Desativar correção predefinida',
    enableAria: 'Ativar correção predefinida',
    on: 'Ativada',
    off: 'Desativada',
    statsTitle: 'Estatísticas de correção',
    days: '{{count}} dias',
    noStats: 'Sem dados estatísticos. Submeta dados de produção para ver uma comparação.',
    factorsTitle: 'Fatores de correção',
    thHourUtc: 'Hora (UTC)',
    thFactor: 'Fator',
    thSamples: 'Número de amostras',
  },
  nl: {
    title: 'Productiecorrectie',
    selectInstallation: 'Selecteer een installatie',
    selectInstallationHint:
      'Selecteer een installatie uit de bovenstaande lijst om de productiecorrectie te beheren.',
    unavailableTitle: 'Correctie niet beschikbaar in het abonnement van deze installatie',
    unavailableHint:
      'Productiecorrectie is niet ingeschakeld voor het abonnement van de geselecteerde installatie. Neem contact op met de beheerder voor toegang.',
    subtitle: 'Dien werkelijke productiegegevens in om de prognosenauwkeurigheid te verbeteren.',
    defaultInForecasts: 'Standaardcorrectie in prognoses',
    defaultHint:
      'Wanneer ingeschakeld, worden prognoses automatisch gecorrigeerd op basis van uw gegevens.',
    disableAria: 'Standaardcorrectie uitschakelen',
    enableAria: 'Standaardcorrectie inschakelen',
    on: 'Ingeschakeld',
    off: 'Uitgeschakeld',
    statsTitle: 'Correctiestatistieken',
    days: '{{count}} dagen',
    noStats: 'Geen statistische gegevens. Dien productiegegevens in om een vergelijking te zien.',
    factorsTitle: 'Correctiefactoren',
    thHourUtc: 'Uur (UTC)',
    thFactor: 'Factor',
    thSamples: 'Aantal monsters',
  },
  da: {
    title: 'Produktionskorrektion',
    selectInstallation: 'Vælg et anlæg',
    selectInstallationHint:
      'Vælg et anlæg fra listen ovenfor for at administrere produktionskorrektion.',
    unavailableTitle: 'Korrektion ikke tilgængelig i dette anlægs plan',
    unavailableHint:
      'Produktionskorrektion er ikke aktiveret for det valgte anlægs plan. Kontakt administratoren for at få adgang.',
    subtitle: 'Indsend faktiske produktionsdata for at forbedre prognosenøjagtigheden.',
    defaultInForecasts: 'Standardkorrektion i prognoser',
    defaultHint: 'Når aktiveret, korrigeres prognoser automatisk baseret på dine data.',
    disableAria: 'Deaktiver standardkorrektion',
    enableAria: 'Aktiver standardkorrektion',
    on: 'Aktiveret',
    off: 'Deaktiveret',
    statsTitle: 'Korrektionsstatistik',
    days: '{{count}} dage',
    noStats: 'Ingen statistiske data. Indsend produktionsdata for at se en sammenligning.',
    factorsTitle: 'Korrektionsfaktorer',
    thHourUtc: 'Time (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Antal prøver',
  },
  sv: {
    title: 'Produktionskorrigering',
    selectInstallation: 'Välj en anläggning',
    selectInstallationHint:
      'Välj en anläggning från listan ovan för att hantera produktionskorrigering.',
    unavailableTitle: 'Korrigering inte tillgänglig i denna anläggnings plan',
    unavailableHint:
      'Produktionskorrigering är inte aktiverad för den valda anläggningens plan. Kontakta administratören för åtkomst.',
    subtitle: 'Skicka in faktiska produktionsdata för att förbättra prognosnoggrannheten.',
    defaultInForecasts: 'Standardkorrigering i prognoser',
    defaultHint: 'När aktiverad korrigeras prognoser automatiskt baserat på dina data.',
    disableAria: 'Inaktivera standardkorrigering',
    enableAria: 'Aktivera standardkorrigering',
    on: 'Aktiverad',
    off: 'Inaktiverad',
    statsTitle: 'Korrigeringsstatistik',
    days: '{{count}} dagar',
    noStats: 'Inga statistiska data. Skicka in produktionsdata för att se en jämförelse.',
    factorsTitle: 'Korrigeringsfaktorer',
    thHourUtc: 'Timme (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Antal prover',
  },
  fi: {
    title: 'Tuotannon korjaus',
    selectInstallation: 'Valitse laitos',
    selectInstallationHint:
      'Valitse laitos yllä olevasta luettelosta hallitaksesi tuotannon korjausta.',
    unavailableTitle: 'Korjaus ei ole käytettävissä tämän laitoksen suunnitelmassa',
    unavailableHint:
      'Tuotannon korjausta ei ole otettu käyttöön valitun laitoksen suunnitelmassa. Ota yhteyttä ylläpitäjään saadaksesi käyttöoikeuden.',
    subtitle: 'Lähetä todelliset tuotantotiedot ennusteen tarkkuuden parantamiseksi.',
    defaultInForecasts: 'Oletuskorjaus ennusteissa',
    defaultHint: 'Kun käytössä, ennusteita korjataan automaattisesti tietojesi perusteella.',
    disableAria: 'Poista oletuskorjaus käytöstä',
    enableAria: 'Ota oletuskorjaus käyttöön',
    on: 'Käytössä',
    off: 'Pois käytöstä',
    statsTitle: 'Korjaustilastot',
    days: '{{count}} päivää',
    noStats: 'Ei tilastotietoja. Lähetä tuotantotiedot nähdäksesi vertailun.',
    factorsTitle: 'Korjauskertoimet',
    thHourUtc: 'Tunti (UTC)',
    thFactor: 'Kerroin',
    thSamples: 'Näytteiden määrä',
  },
  cs: {
    title: 'Korekce výroby',
    selectInstallation: 'Vyberte instalaci',
    selectInstallationHint: 'Vyberte instalaci ze seznamu výše pro správu korekce výroby.',
    unavailableTitle: 'Korekce není dostupná v tarifu této instalace',
    unavailableHint:
      'Korekce výroby není povolena pro tarif vybrané instalace. Kontaktujte administrátora pro získání přístupu.',
    subtitle: 'Zadávejte skutečná data výroby pro zlepšení přesnosti předpovědí.',
    defaultInForecasts: 'Výchozí korekce v předpovědích',
    defaultHint: 'Když je povoleno, předpovědi se automaticky korigují na základě vašich dat.',
    disableAria: 'Zakázat výchozí korekci',
    enableAria: 'Povolit výchozí korekci',
    on: 'Povoleno',
    off: 'Zakázáno',
    statsTitle: 'Statistiky korekce',
    days: '{{count}} dní',
    noStats: 'Žádná statistická data. Zadejte data výroby pro zobrazení porovnání.',
    factorsTitle: 'Korekční koeficienty',
    thHourUtc: 'Hodina (UTC)',
    thFactor: 'Koeficient',
    thSamples: 'Počet vzorků',
  },
  sk: {
    title: 'Korekcia výroby',
    selectInstallation: 'Vyberte inštaláciu',
    selectInstallationHint: 'Vyberte inštaláciu zo zoznamu vyššie pre správu korekcie výroby.',
    unavailableTitle: 'Korekcia nie je dostupná v tarife tejto inštalácie',
    unavailableHint:
      'Korekcia výroby nie je povolená pre tarif vybranej inštalácie. Kontaktujte administrátora pre získanie prístupu.',
    subtitle: 'Zadávajte skutočné dáta výroby pre zlepšenie presnosti predpovedí.',
    defaultInForecasts: 'Predvolená korekcia v predpovediach',
    defaultHint: 'Keď je povolené, predpovede sa automaticky korigujú na základe vašich dát.',
    disableAria: 'Zakázať predvolenú korekciu',
    enableAria: 'Povoliť predvolenú korekciu',
    on: 'Povolené',
    off: 'Zakázané',
    statsTitle: 'Štatistiky korekcie',
    days: '{{count}} dní',
    noStats: 'Žiadne štatistické dáta. Zadajte dáta výroby pre zobrazenie porovnania.',
    factorsTitle: 'Korekčné koeficienty',
    thHourUtc: 'Hodina (UTC)',
    thFactor: 'Koeficient',
    thSamples: 'Počet vzoriek',
  },
  sl: {
    title: 'Popravek proizvodnje',
    selectInstallation: 'Izberite napravo',
    selectInstallationHint:
      'Izberite napravo s seznama zgoraj za upravljanje popravka proizvodnje.',
    unavailableTitle: 'Popravek ni na voljo v paketu te naprave',
    unavailableHint:
      'Popravek proizvodnje ni omogočen za paket izbrane naprave. Obrnite se na skrbnika za dostop.',
    subtitle: 'Pošljite dejanske podatke o proizvodnji za izboljšanje natančnosti napovedi.',
    defaultInForecasts: 'Privzeti popravek v napovedih',
    defaultHint: 'Ko je omogočeno, se napovedi samodejno popravijo na podlagi vaših podatkov.',
    disableAria: 'Onemogoči privzeti popravek',
    enableAria: 'Omogoči privzeti popravek',
    on: 'Omogočeno',
    off: 'Onemogočeno',
    statsTitle: 'Statistika popravkov',
    days: '{{count}} dni',
    noStats: 'Ni statističnih podatkov. Pošljite podatke o proizvodnji za prikaz primerjave.',
    factorsTitle: 'Faktorji popravka',
    thHourUtc: 'Ura (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Število vzorcev',
  },
  hr: {
    title: 'Ispravak proizvodnje',
    selectInstallation: 'Odaberite postrojenje',
    selectInstallationHint:
      'Odaberite postrojenje s popisa iznad za upravljanje ispravkom proizvodnje.',
    unavailableTitle: 'Ispravak nije dostupan u planu ovog postrojenja',
    unavailableHint:
      'Ispravak proizvodnje nije omogućen za plan odabranog postrojenja. Kontaktirajte administratora za pristup.',
    subtitle: 'Pošaljite stvarne podatke o proizvodnji za poboljšanje točnosti prognoza.',
    defaultInForecasts: 'Zadani ispravak u prognozama',
    defaultHint: 'Kada je omogućeno, prognoze se automatski ispravljaju na temelju vaših podataka.',
    disableAria: 'Onemogući zadani ispravak',
    enableAria: 'Omogući zadani ispravak',
    on: 'Omogućeno',
    off: 'Onemogućeno',
    statsTitle: 'Statistika ispravaka',
    days: '{{count}} dana',
    noStats: 'Nema statističkih podataka. Pošaljite podatke o proizvodnji za prikaz usporedbe.',
    factorsTitle: 'Faktori ispravka',
    thHourUtc: 'Sat (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Broj uzoraka',
  },
  hu: {
    title: 'Termeléskorrekció',
    selectInstallation: 'Válasszon telepítést',
    selectInstallationHint:
      'Válasszon telepítést a fenti listából a termeléskorrekció kezeléséhez.',
    unavailableTitle: 'A korrekció nem érhető el ennek a telepítésnek a csomagjában',
    unavailableHint:
      'A termeléskorrekció nincs engedélyezve a kiválasztott telepítés csomagjában. Forduljon az adminisztrátorhoz a hozzáférésért.',
    subtitle: 'Adjon meg tényleges termelési adatokat az előrejelzés pontosságának javításához.',
    defaultInForecasts: 'Alapértelmezett korrekció az előrejelzésekben',
    defaultHint:
      'Ha engedélyezve van, az előrejelzések automatikusan korrigálódnak az adatai alapján.',
    disableAria: 'Alapértelmezett korrekció letiltása',
    enableAria: 'Alapértelmezett korrekció engedélyezése',
    on: 'Engedélyezve',
    off: 'Letiltva',
    statsTitle: 'Korrekciós statisztikák',
    days: '{{count}} nap',
    noStats:
      'Nincsenek statisztikai adatok. Adjon meg termelési adatokat az összehasonlítás megtekintéséhez.',
    factorsTitle: 'Korrekciós tényezők',
    thHourUtc: 'Óra (UTC)',
    thFactor: 'Tényező',
    thSamples: 'Minták száma',
  },
  ro: {
    title: 'Corecție producție',
    selectInstallation: 'Selectați o instalație',
    selectInstallationHint:
      'Selectați o instalație din lista de mai sus pentru a gestiona corecția producției.',
    unavailableTitle: 'Corecția nu este disponibilă în planul acestei instalații',
    unavailableHint:
      'Corecția producției nu este activată pentru planul instalației selectate. Contactați administratorul pentru a obține acces.',
    subtitle: 'Trimiteți date reale de producție pentru a îmbunătăți precizia prognozelor.',
    defaultInForecasts: 'Corecție implicită în prognoze',
    defaultHint: 'Când este activată, prognozele sunt corectate automat pe baza datelor dvs.',
    disableAria: 'Dezactivează corecția implicită',
    enableAria: 'Activează corecția implicită',
    on: 'Activată',
    off: 'Dezactivată',
    statsTitle: 'Statistici corecție',
    days: '{{count}} zile',
    noStats: 'Nu există date statistice. Trimiteți date de producție pentru a vedea o comparație.',
    factorsTitle: 'Factori de corecție',
    thHourUtc: 'Oră (UTC)',
    thFactor: 'Factor',
    thSamples: 'Număr de eșantioane',
  },
  bg: {
    title: 'Корекция на производството',
    selectInstallation: 'Изберете инсталация',
    selectInstallationHint:
      'Изберете инсталация от списъка по-горе, за да управлявате корекцията на производството.',
    unavailableTitle: 'Корекцията не е налична в плана на тази инсталация',
    unavailableHint:
      'Корекцията на производството не е активирана за плана на избраната инсталация. Свържете се с администратора за достъп.',
    subtitle: 'Изпращайте реални данни за производството, за да подобрите точността на прогнозите.',
    defaultInForecasts: 'Корекция по подразбиране в прогнозите',
    defaultHint:
      'Когато е активирано, прогнозите се коригират автоматично въз основа на вашите данни.',
    disableAria: 'Деактивиране на корекцията по подразбиране',
    enableAria: 'Активиране на корекцията по подразбиране',
    on: 'Активирана',
    off: 'Деактивирана',
    statsTitle: 'Статистика на корекциите',
    days: '{{count}} дни',
    noStats: 'Няма статистически данни. Изпратете данни за производството, за да видите сравнение.',
    factorsTitle: 'Корекционни коефициенти',
    thHourUtc: 'Час (UTC)',
    thFactor: 'Коефициент',
    thSamples: 'Брой проби',
  },
  el: {
    title: 'Διόρθωση παραγωγής',
    selectInstallation: 'Επιλέξτε εγκατάσταση',
    selectInstallationHint:
      'Επιλέξτε εγκατάσταση από την παραπάνω λίστα για να διαχειριστείτε τη διόρθωση παραγωγής.',
    unavailableTitle: 'Η διόρθωση δεν είναι διαθέσιμη στο πρόγραμμα αυτής της εγκατάστασης',
    unavailableHint:
      'Η διόρθωση παραγωγής δεν είναι ενεργοποιημένη για το πρόγραμμα της επιλεγμένης εγκατάστασης. Επικοινωνήστε με τον διαχειριστή για πρόσβαση.',
    subtitle:
      'Υποβάλετε πραγματικά δεδομένα παραγωγής για να βελτιώσετε την ακρίβεια των προβλέψεων.',
    defaultInForecasts: 'Προεπιλεγμένη διόρθωση στις προβλέψεις',
    defaultHint:
      'Όταν είναι ενεργοποιημένη, οι προβλέψεις διορθώνονται αυτόματα βάσει των δεδομένων σας.',
    disableAria: 'Απενεργοποίηση προεπιλεγμένης διόρθωσης',
    enableAria: 'Ενεργοποίηση προεπιλεγμένης διόρθωσης',
    on: 'Ενεργοποιημένη',
    off: 'Απενεργοποιημένη',
    statsTitle: 'Στατιστικά διόρθωσης',
    days: '{{count}} ημέρες',
    noStats:
      'Δεν υπάρχουν στατιστικά δεδομένα. Υποβάλετε δεδομένα παραγωγής για να δείτε σύγκριση.',
    factorsTitle: 'Συντελεστές διόρθωσης',
    thHourUtc: 'Ώρα (UTC)',
    thFactor: 'Συντελεστής',
    thSamples: 'Αριθμός δειγμάτων',
  },
  et: {
    title: 'Toodangu korrektsioon',
    selectInstallation: 'Valige paigaldis',
    selectInstallationHint:
      'Valige paigaldis ülaltoodud loendist toodangu korrektsiooni haldamiseks.',
    unavailableTitle: 'Korrektsioon pole selle paigaldise paketis saadaval',
    unavailableHint:
      'Toodangu korrektsioon pole valitud paigaldise paketis lubatud. Juurdepääsu saamiseks võtke ühendust administraatoriga.',
    subtitle: 'Esitage tegelikud toodanguandmed prognoosi täpsuse parandamiseks.',
    defaultInForecasts: 'Vaikekorrektsioon prognoosides',
    defaultHint: 'Kui lubatud, korrigeeritakse prognoose automaatselt teie andmete põhjal.',
    disableAria: 'Keela vaikekorrektsioon',
    enableAria: 'Luba vaikekorrektsioon',
    on: 'Lubatud',
    off: 'Keelatud',
    statsTitle: 'Korrektsiooni statistika',
    days: '{{count}} päeva',
    noStats: 'Statistilisi andmeid pole. Esitage toodanguandmed võrdluse nägemiseks.',
    factorsTitle: 'Korrektsioonitegurid',
    thHourUtc: 'Tund (UTC)',
    thFactor: 'Tegur',
    thSamples: 'Valimite arv',
  },
  lv: {
    title: 'Ražošanas korekcija',
    selectInstallation: 'Izvēlieties iekārtu',
    selectInstallationHint:
      'Izvēlieties iekārtu no augstāk esošā saraksta, lai pārvaldītu ražošanas korekciju.',
    unavailableTitle: 'Korekcija nav pieejama šīs iekārtas plānā',
    unavailableHint:
      'Ražošanas korekcija nav iespējota izvēlētās iekārtas plānam. Lai iegūtu piekļuvi, sazinieties ar administratoru.',
    subtitle: 'Iesniedziet faktiskos ražošanas datus, lai uzlabotu prognozes precizitāti.',
    defaultInForecasts: 'Noklusējuma korekcija prognozēs',
    defaultHint:
      'Kad iespējots, prognozes tiek automātiski koriģētas, pamatojoties uz jūsu datiem.',
    disableAria: 'Atspējot noklusējuma korekciju',
    enableAria: 'Iespējot noklusējuma korekciju',
    on: 'Iespējots',
    off: 'Atspējots',
    statsTitle: 'Korekcijas statistika',
    days: '{{count}} dienas',
    noStats: 'Nav statistikas datu. Iesniedziet ražošanas datus, lai redzētu salīdzinājumu.',
    factorsTitle: 'Korekcijas koeficienti',
    thHourUtc: 'Stunda (UTC)',
    thFactor: 'Koeficients',
    thSamples: 'Paraugu skaits',
  },
  lt: {
    title: 'Gamybos korekcija',
    selectInstallation: 'Pasirinkite įrenginį',
    selectInstallationHint:
      'Pasirinkite įrenginį iš aukščiau esančio sąrašo, kad galėtumėte valdyti gamybos korekciją.',
    unavailableTitle: 'Korekcija nepasiekiama šio įrenginio plane',
    unavailableHint:
      'Gamybos korekcija neįjungta pasirinkto įrenginio plane. Norėdami gauti prieigą, susisiekite su administratoriumi.',
    subtitle: 'Pateikite faktinius gamybos duomenis, kad pagerintumėte prognozės tikslumą.',
    defaultInForecasts: 'Numatytoji korekcija prognozėse',
    defaultHint: 'Kai įjungta, prognozės automatiškai koreguojamos pagal jūsų duomenis.',
    disableAria: 'Išjungti numatytąją korekciją',
    enableAria: 'Įjungti numatytąją korekciją',
    on: 'Įjungta',
    off: 'Išjungta',
    statsTitle: 'Korekcijos statistika',
    days: '{{count}} d.',
    noStats: 'Nėra statistinių duomenų. Pateikite gamybos duomenis, kad pamatytumėte palyginimą.',
    factorsTitle: 'Korekcijos koeficientai',
    thHourUtc: 'Valanda (UTC)',
    thFactor: 'Koeficientas',
    thSamples: 'Mėginių skaičius',
  },
  mt: {
    title: 'Korrezzjoni tal-produzzjoni',
    selectInstallation: 'Agħżel installazzjoni',
    selectInstallationHint:
      "Agħżel installazzjoni mil-lista ta' fuq biex timmaniġġja l-korrezzjoni tal-produzzjoni.",
    unavailableTitle: "Il-korrezzjoni mhix disponibbli fil-pjan ta' din l-installazzjoni",
    unavailableHint:
      'Il-korrezzjoni tal-produzzjoni mhix attivata għall-pjan tal-installazzjoni magħżula. Ikkuntattja lill-amministratur biex tikseb aċċess.',
    subtitle:
      'Issottometti dejta reali tal-produzzjoni biex ittejjeb il-preċiżjoni tal-previżjonijiet.',
    defaultInForecasts: 'Korrezzjoni default fil-previżjonijiet',
    defaultHint:
      'Meta attivata, il-previżjonijiet jiġu kkoreġġuti awtomatikament abbażi tad-dejta tiegħek.',
    disableAria: 'Iddiżattiva l-korrezzjoni default',
    enableAria: 'Attiva l-korrezzjoni default',
    on: 'Attivata',
    off: 'Diżattivata',
    statsTitle: 'Statistika tal-korrezzjoni',
    days: '{{count}} jiem',
    noStats: 'L-ebda dejta statistika. Issottometti dejta tal-produzzjoni biex tara paragun.',
    factorsTitle: "Fatturi ta' korrezzjoni",
    thHourUtc: 'Siegħa (UTC)',
    thFactor: 'Fattur',
    thSamples: "Numru ta' kampjuni",
  },
  ga: {
    title: 'Ceartúchán táirgthe',
    selectInstallation: 'Roghnaigh suiteáil',
    selectInstallationHint:
      'Roghnaigh suiteáil ón liosta thuas chun ceartúchán táirgthe a bhainistiú.',
    unavailableTitle: 'Níl an ceartúchán ar fáil i bplean na suiteála seo',
    unavailableHint:
      'Níl ceartúchán táirgthe cumasaithe do phlean na suiteála roghnaithe. Déan teagmháil leis an riarthóir chun rochtain a fháil.',
    subtitle: 'Cuir isteach sonraí táirgthe iarbhír chun cruinneas na réamhaisnéise a fheabhsú.',
    defaultInForecasts: 'Ceartúchán réamhshocraithe i réamhaisnéisí',
    defaultHint:
      'Nuair atá sé cumasaithe, ceartaítear réamhaisnéisí go huathoibríoch bunaithe ar do shonraí.',
    disableAria: 'Díchumasaigh an ceartúchán réamhshocraithe',
    enableAria: 'Cumasaigh an ceartúchán réamhshocraithe',
    on: 'Cumasaithe',
    off: 'Díchumasaithe',
    statsTitle: 'Staitisticí ceartúcháin',
    days: '{{count}} lá',
    noStats: 'Gan sonraí staitistiúla. Cuir isteach sonraí táirgthe chun comparáid a fheiceáil.',
    factorsTitle: 'Fachtóirí ceartúcháin',
    thHourUtc: 'Uair (UTC)',
    thFactor: 'Fachtóir',
    thSamples: 'Líon na samplaí',
  },
  no: {
    title: 'Produksjonskorreksjon',
    selectInstallation: 'Velg et anlegg',
    selectInstallationHint:
      'Velg et anlegg fra listen ovenfor for å administrere produksjonskorreksjon.',
    unavailableTitle: 'Korreksjon ikke tilgjengelig i dette anleggets plan',
    unavailableHint:
      'Produksjonskorreksjon er ikke aktivert for det valgte anleggets plan. Kontakt administratoren for å få tilgang.',
    subtitle: 'Send inn faktiske produksjonsdata for å forbedre prognosenøyaktigheten.',
    defaultInForecasts: 'Standardkorreksjon i prognoser',
    defaultHint: 'Når aktivert, korrigeres prognoser automatisk basert på dataene dine.',
    disableAria: 'Deaktiver standardkorreksjon',
    enableAria: 'Aktiver standardkorreksjon',
    on: 'Aktivert',
    off: 'Deaktivert',
    statsTitle: 'Korreksjonsstatistikk',
    days: '{{count}} dager',
    noStats: 'Ingen statistiske data. Send inn produksjonsdata for å se en sammenligning.',
    factorsTitle: 'Korreksjonsfaktorer',
    thHourUtc: 'Time (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Antall prøver',
  },
  is: {
    title: 'Framleiðsluleiðrétting',
    selectInstallation: 'Veldu virkjun',
    selectInstallationHint:
      'Veldu virkjun af listanum að ofan til að stjórna framleiðsluleiðréttingu.',
    unavailableTitle: 'Leiðrétting er ekki í boði í áskrift þessarar virkjunar',
    unavailableHint:
      'Framleiðsluleiðrétting er ekki virk fyrir áskrift valdrar virkjunar. Hafðu samband við stjórnanda til að fá aðgang.',
    subtitle: 'Sendu inn raunveruleg framleiðslugögn til að bæta nákvæmni spáa.',
    defaultInForecasts: 'Sjálfgefin leiðrétting í spám',
    defaultHint: 'Þegar virkt eru spár sjálfkrafa leiðréttar á grundvelli gagna þinna.',
    disableAria: 'Slökkva á sjálfgefinni leiðréttingu',
    enableAria: 'Kveikja á sjálfgefinni leiðréttingu',
    on: 'Virkt',
    off: 'Óvirkt',
    statsTitle: 'Tölfræði leiðréttinga',
    days: '{{count}} dagar',
    noStats: 'Engin tölfræðigögn. Sendu inn framleiðslugögn til að sjá samanburð.',
    factorsTitle: 'Leiðréttingarstuðlar',
    thHourUtc: 'Klukkustund (UTC)',
    thFactor: 'Stuðull',
    thSamples: 'Fjöldi sýna',
  },
  uk: {
    title: 'Корекція виробництва',
    selectInstallation: 'Виберіть установку',
    selectInstallationHint:
      'Виберіть установку зі списку вище, щоб керувати корекцією виробництва.',
    unavailableTitle: 'Корекція недоступна в плані цієї установки',
    unavailableHint:
      'Корекція виробництва не ввімкнена для плану обраної установки. Зверніться до адміністратора, щоб отримати доступ.',
    subtitle: 'Надсилайте фактичні дані виробництва, щоб покращити точність прогнозів.',
    defaultInForecasts: 'Корекція за замовчуванням у прогнозах',
    defaultHint: 'Коли ввімкнено, прогнози автоматично коригуються на основі ваших даних.',
    disableAria: 'Вимкнути корекцію за замовчуванням',
    enableAria: 'Увімкнути корекцію за замовчуванням',
    on: 'Увімкнено',
    off: 'Вимкнено',
    statsTitle: 'Статистика корекції',
    days: '{{count}} днів',
    noStats: 'Немає статистичних даних. Надішліть дані виробництва, щоб побачити порівняння.',
    factorsTitle: 'Коефіцієнти корекції',
    thHourUtc: 'Година (UTC)',
    thFactor: 'Коефіцієнт',
    thSamples: 'Кількість зразків',
  },
  sr: {
    title: 'Ispravka proizvodnje',
    selectInstallation: 'Izaberite postrojenje',
    selectInstallationHint:
      'Izaberite postrojenje sa liste iznad da biste upravljali ispravkom proizvodnje.',
    unavailableTitle: 'Ispravka nije dostupna u planu ovog postrojenja',
    unavailableHint:
      'Ispravka proizvodnje nije omogućena za plan izabranog postrojenja. Kontaktirajte administratora za pristup.',
    subtitle: 'Pošaljite stvarne podatke o proizvodnji da biste poboljšali tačnost prognoza.',
    defaultInForecasts: 'Podrazumevana ispravka u prognozama',
    defaultHint: 'Kada je omogućeno, prognoze se automatski ispravljaju na osnovu vaših podataka.',
    disableAria: 'Onemogući podrazumevanu ispravku',
    enableAria: 'Omogući podrazumevanu ispravku',
    on: 'Omogućeno',
    off: 'Onemogućeno',
    statsTitle: 'Statistika ispravki',
    days: '{{count}} dana',
    noStats:
      'Nema statističkih podataka. Pošaljite podatke o proizvodnji da biste videli poređenje.',
    factorsTitle: 'Faktori ispravke',
    thHourUtc: 'Sat (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Broj uzoraka',
  },
  sq: {
    title: 'Korrigjimi i prodhimit',
    selectInstallation: 'Zgjidhni një instalim',
    selectInstallationHint:
      'Zgjidhni një instalim nga lista më sipër për të menaxhuar korrigjimin e prodhimit.',
    unavailableTitle: 'Korrigjimi nuk është i disponueshëm në planin e këtij instalimi',
    unavailableHint:
      'Korrigjimi i prodhimit nuk është aktivizuar për planin e instalimit të zgjedhur. Kontaktoni administratorin për të marrë akses.',
    subtitle: 'Dërgoni të dhëna reale të prodhimit për të përmirësuar saktësinë e parashikimeve.',
    defaultInForecasts: 'Korrigjimi i parazgjedhur në parashikime',
    defaultHint:
      'Kur aktivizohet, parashikimet korrigjohen automatikisht bazuar në të dhënat tuaja.',
    disableAria: 'Çaktivizo korrigjimin e parazgjedhur',
    enableAria: 'Aktivizo korrigjimin e parazgjedhur',
    on: 'Aktivizuar',
    off: 'Çaktivizuar',
    statsTitle: 'Statistikat e korrigjimit',
    days: '{{count}} ditë',
    noStats: 'Nuk ka të dhëna statistikore. Dërgoni të dhëna prodhimi për të parë një krahasim.',
    factorsTitle: 'Faktorët e korrigjimit',
    thHourUtc: 'Ora (UTC)',
    thFactor: 'Faktori',
    thSamples: 'Numri i mostrave',
  },
  mk: {
    title: 'Корекција на производството',
    selectInstallation: 'Изберете инсталација',
    selectInstallationHint:
      'Изберете инсталација од списокот погоре за да управувате со корекцијата на производството.',
    unavailableTitle: 'Корекцијата не е достапна во планот на оваа инсталација',
    unavailableHint:
      'Корекцијата на производството не е овозможена за планот на избраната инсталација. Контактирајте го администраторот за пристап.',
    subtitle:
      'Испратете реални податоци за производството за да ја подобрите точноста на прогнозите.',
    defaultInForecasts: 'Стандардна корекција во прогнозите',
    defaultHint:
      'Кога е овозможено, прогнозите автоматски се коригираат врз основа на вашите податоци.',
    disableAria: 'Оневозможи стандардна корекција',
    enableAria: 'Овозможи стандардна корекција',
    on: 'Овозможено',
    off: 'Оневозможено',
    statsTitle: 'Статистика на корекции',
    days: '{{count}} дена',
    noStats:
      'Нема статистички податоци. Испратете податоци за производството за да видите споредба.',
    factorsTitle: 'Коефициенти на корекција',
    thHourUtc: 'Час (UTC)',
    thFactor: 'Коефициент',
    thSamples: 'Број на примероци',
  },
  bs: {
    title: 'Ispravka proizvodnje',
    selectInstallation: 'Odaberite postrojenje',
    selectInstallationHint:
      'Odaberite postrojenje s liste iznad da biste upravljali ispravkom proizvodnje.',
    unavailableTitle: 'Ispravka nije dostupna u planu ovog postrojenja',
    unavailableHint:
      'Ispravka proizvodnje nije omogućena za plan odabranog postrojenja. Kontaktirajte administratora za pristup.',
    subtitle: 'Pošaljite stvarne podatke o proizvodnji da biste poboljšali tačnost prognoza.',
    defaultInForecasts: 'Zadana ispravka u prognozama',
    defaultHint: 'Kada je omogućeno, prognoze se automatski ispravljaju na osnovu vaših podataka.',
    disableAria: 'Onemogući zadanu ispravku',
    enableAria: 'Omogući zadanu ispravku',
    on: 'Omogućeno',
    off: 'Onemogućeno',
    statsTitle: 'Statistika ispravki',
    days: '{{count}} dana',
    noStats:
      'Nema statističkih podataka. Pošaljite podatke o proizvodnji da biste vidjeli poređenje.',
    factorsTitle: 'Faktori ispravke',
    thHourUtc: 'Sat (UTC)',
    thFactor: 'Faktor',
    thSamples: 'Broj uzoraka',
  },
};

/** Namespace 'register' — strona rejestracji portalu. */
const registerTr: Record<string, Dict> = {
  en: {
    title: 'Registration',
    subtitle: 'Create an account in the Solar Forecast API portal',
    stepAccount: 'Account details',
    stepInstallation: 'Installation details',
    confirmPassword: 'Confirm password',
    name: 'Name',
    activationCode: 'Activation code',
    checkingCode: 'Checking code…',
    planInfo: '✓ Plan: {{plan}} — max strings: {{count}}',
    haveAccount: 'Already have an account? Sign in',
    next: 'Next',
    back: 'Back',
    registering: 'Registering…',
    skipConfig: 'Skip — configure later',
    register: 'Register',
    accountCreated: 'Account created!',
    apiKeyGenerated: 'Your API key has been generated.',
    yourApiKey: 'Your API key:',
    saveKeyWarning: 'Save this key — it will not be shown again!',
    goToLogin: 'Go to login',
    errInvalidEmail: 'Invalid e-mail address',
    errPwShort: 'Password must be at least 8 characters',
    errPwUpper: 'Password must contain an uppercase letter',
    errPwLower: 'Password must contain a lowercase letter',
    errPwDigit: 'Password must contain a digit',
    errPwMismatch: 'Passwords must match',
    errNameShort: 'Name must be at least 2 characters',
    errCodeLen: 'Activation code must be 8 characters',
    errCodeInvalid: 'Activation code is invalid',
    errCodeCheck: 'Failed to check activation code',
    errLat: 'Latitude must be between -90 and 90',
    errLon: 'Longitude must be between -180 and 180',
  },
  pl: {
    title: 'Rejestracja',
    subtitle: 'Utwórz konto w portalu Solar Forecast API',
    stepAccount: 'Dane konta',
    stepInstallation: 'Dane instalacji',
    confirmPassword: 'Potwierdź hasło',
    name: 'Nazwa',
    activationCode: 'Kod aktywacyjny',
    checkingCode: 'Sprawdzanie kodu…',
    planInfo: '✓ Plan: {{plan}} — maksymalna liczba stringów: {{count}}',
    haveAccount: 'Masz już konto? Zaloguj się',
    next: 'Dalej',
    back: 'Wstecz',
    registering: 'Rejestrowanie…',
    skipConfig: 'Pomiń — skonfiguruj później',
    register: 'Zarejestruj się',
    accountCreated: 'Konto zostało utworzone!',
    apiKeyGenerated: 'Twój klucz API został wygenerowany.',
    yourApiKey: 'Twój klucz API:',
    saveKeyWarning: 'Zapisz ten klucz — nie będzie ponownie wyświetlony!',
    goToLogin: 'Przejdź do logowania',
    errInvalidEmail: 'Nieprawidłowy adres e-mail',
    errPwShort: 'Hasło musi mieć min. 8 znaków',
    errPwUpper: 'Hasło musi zawierać wielką literę',
    errPwLower: 'Hasło musi zawierać małą literę',
    errPwDigit: 'Hasło musi zawierać cyfrę',
    errPwMismatch: 'Hasła muszą być identyczne',
    errNameShort: 'Nazwa musi mieć min. 2 znaki',
    errCodeLen: 'Kod aktywacyjny musi mieć 8 znaków',
    errCodeInvalid: 'Kod aktywacyjny jest nieprawidłowy',
    errCodeCheck: 'Nie udało się sprawdzić kodu aktywacyjnego',
    errLat: 'Szerokość musi być między -90 a 90',
    errLon: 'Długość musi być między -180 a 180',
  },
  de: {
    title: 'Registrierung',
    subtitle: 'Erstellen Sie ein Konto im Solar Forecast API-Portal',
    stepAccount: 'Kontodaten',
    stepInstallation: 'Anlagendaten',
    confirmPassword: 'Passwort bestätigen',
    name: 'Name',
    activationCode: 'Aktivierungscode',
    checkingCode: 'Code wird geprüft…',
    planInfo: '✓ Tarif: {{plan}} — max. Strings: {{count}}',
    haveAccount: 'Haben Sie bereits ein Konto? Anmelden',
    next: 'Weiter',
    back: 'Zurück',
    registering: 'Registrierung…',
    skipConfig: 'Überspringen — später konfigurieren',
    register: 'Registrieren',
    accountCreated: 'Konto wurde erstellt!',
    apiKeyGenerated: 'Ihr API-Schlüssel wurde generiert.',
    yourApiKey: 'Ihr API-Schlüssel:',
    saveKeyWarning: 'Speichern Sie diesen Schlüssel — er wird nicht erneut angezeigt!',
    goToLogin: 'Zur Anmeldung',
    errInvalidEmail: 'Ungültige E-Mail-Adresse',
    errPwShort: 'Passwort muss mindestens 8 Zeichen haben',
    errPwUpper: 'Passwort muss einen Großbuchstaben enthalten',
    errPwLower: 'Passwort muss einen Kleinbuchstaben enthalten',
    errPwDigit: 'Passwort muss eine Ziffer enthalten',
    errPwMismatch: 'Passwörter müssen übereinstimmen',
    errNameShort: 'Name muss mindestens 2 Zeichen haben',
    errCodeLen: 'Aktivierungscode muss 8 Zeichen haben',
    errCodeInvalid: 'Aktivierungscode ist ungültig',
    errCodeCheck: 'Aktivierungscode konnte nicht geprüft werden',
    errLat: 'Breitengrad muss zwischen -90 und 90 liegen',
    errLon: 'Längengrad muss zwischen -180 und 180 liegen',
  },
  fr: {
    title: 'Inscription',
    subtitle: 'Créez un compte sur le portail Solar Forecast API',
    stepAccount: 'Données du compte',
    stepInstallation: "Données de l'installation",
    confirmPassword: 'Confirmer le mot de passe',
    name: 'Nom',
    activationCode: "Code d'activation",
    checkingCode: 'Vérification du code…',
    planInfo: '✓ Forfait : {{plan}} — chaînes max : {{count}}',
    haveAccount: 'Vous avez déjà un compte ? Se connecter',
    next: 'Suivant',
    back: 'Retour',
    registering: 'Inscription…',
    skipConfig: 'Passer — configurer plus tard',
    register: "S'inscrire",
    accountCreated: 'Le compte a été créé !',
    apiKeyGenerated: 'Votre clé API a été générée.',
    yourApiKey: 'Votre clé API :',
    saveKeyWarning: 'Enregistrez cette clé — elle ne sera plus affichée !',
    goToLogin: 'Aller à la connexion',
    errInvalidEmail: 'Adresse e-mail invalide',
    errPwShort: 'Le mot de passe doit comporter au moins 8 caractères',
    errPwUpper: 'Le mot de passe doit contenir une majuscule',
    errPwLower: 'Le mot de passe doit contenir une minuscule',
    errPwDigit: 'Le mot de passe doit contenir un chiffre',
    errPwMismatch: 'Les mots de passe doivent correspondre',
    errNameShort: 'Le nom doit comporter au moins 2 caractères',
    errCodeLen: "Le code d'activation doit comporter 8 caractères",
    errCodeInvalid: "Le code d'activation est invalide",
    errCodeCheck: "Échec de la vérification du code d'activation",
    errLat: 'La latitude doit être comprise entre -90 et 90',
    errLon: 'La longitude doit être comprise entre -180 et 180',
  },
  es: {
    title: 'Registro',
    subtitle: 'Cree una cuenta en el portal Solar Forecast API',
    stepAccount: 'Datos de la cuenta',
    stepInstallation: 'Datos de la instalación',
    confirmPassword: 'Confirmar contraseña',
    name: 'Nombre',
    activationCode: 'Código de activación',
    checkingCode: 'Comprobando código…',
    planInfo: '✓ Plan: {{plan}} — cadenas máx.: {{count}}',
    haveAccount: '¿Ya tiene una cuenta? Iniciar sesión',
    next: 'Siguiente',
    back: 'Atrás',
    registering: 'Registrando…',
    skipConfig: 'Omitir — configurar más tarde',
    register: 'Registrarse',
    accountCreated: '¡La cuenta ha sido creada!',
    apiKeyGenerated: 'Su clave API ha sido generada.',
    yourApiKey: 'Su clave API:',
    saveKeyWarning: '¡Guarde esta clave — no se mostrará de nuevo!',
    goToLogin: 'Ir al inicio de sesión',
    errInvalidEmail: 'Dirección de correo electrónico no válida',
    errPwShort: 'La contraseña debe tener al menos 8 caracteres',
    errPwUpper: 'La contraseña debe contener una letra mayúscula',
    errPwLower: 'La contraseña debe contener una letra minúscula',
    errPwDigit: 'La contraseña debe contener un dígito',
    errPwMismatch: 'Las contraseñas deben coincidir',
    errNameShort: 'El nombre debe tener al menos 2 caracteres',
    errCodeLen: 'El código de activación debe tener 8 caracteres',
    errCodeInvalid: 'El código de activación no es válido',
    errCodeCheck: 'No se pudo comprobar el código de activación',
    errLat: 'La latitud debe estar entre -90 y 90',
    errLon: 'La longitud debe estar entre -180 y 180',
  },
  it: {
    title: 'Registrazione',
    subtitle: 'Crea un account nel portale Solar Forecast API',
    stepAccount: 'Dati account',
    stepInstallation: "Dati dell'impianto",
    confirmPassword: 'Conferma password',
    name: 'Nome',
    activationCode: 'Codice di attivazione',
    checkingCode: 'Verifica del codice…',
    planInfo: '✓ Piano: {{plan}} — stringhe max: {{count}}',
    haveAccount: 'Hai già un account? Accedi',
    next: 'Avanti',
    back: 'Indietro',
    registering: 'Registrazione…',
    skipConfig: 'Salta — configura più tardi',
    register: 'Registrati',
    accountCreated: "L'account è stato creato!",
    apiKeyGenerated: 'La tua chiave API è stata generata.',
    yourApiKey: 'La tua chiave API:',
    saveKeyWarning: 'Salva questa chiave — non verrà mostrata di nuovo!',
    goToLogin: 'Vai al login',
    errInvalidEmail: 'Indirizzo e-mail non valido',
    errPwShort: 'La password deve contenere almeno 8 caratteri',
    errPwUpper: 'La password deve contenere una lettera maiuscola',
    errPwLower: 'La password deve contenere una lettera minuscola',
    errPwDigit: 'La password deve contenere una cifra',
    errPwMismatch: 'Le password devono corrispondere',
    errNameShort: 'Il nome deve contenere almeno 2 caratteri',
    errCodeLen: 'Il codice di attivazione deve contenere 8 caratteri',
    errCodeInvalid: 'Il codice di attivazione non è valido',
    errCodeCheck: 'Impossibile verificare il codice di attivazione',
    errLat: 'La latitudine deve essere compresa tra -90 e 90',
    errLon: 'La longitudine deve essere compresa tra -180 e 180',
  },
  pt: {
    title: 'Registo',
    subtitle: 'Crie uma conta no portal Solar Forecast API',
    stepAccount: 'Dados da conta',
    stepInstallation: 'Dados da instalação',
    confirmPassword: 'Confirmar palavra-passe',
    name: 'Nome',
    activationCode: 'Código de ativação',
    checkingCode: 'A verificar código…',
    planInfo: '✓ Plano: {{plan}} — strings máx.: {{count}}',
    haveAccount: 'Já tem uma conta? Iniciar sessão',
    next: 'Seguinte',
    back: 'Voltar',
    registering: 'A registar…',
    skipConfig: 'Ignorar — configurar mais tarde',
    register: 'Registar',
    accountCreated: 'A conta foi criada!',
    apiKeyGenerated: 'A sua chave API foi gerada.',
    yourApiKey: 'A sua chave API:',
    saveKeyWarning: 'Guarde esta chave — não será mostrada novamente!',
    goToLogin: 'Ir para o início de sessão',
    errInvalidEmail: 'Endereço de e-mail inválido',
    errPwShort: 'A palavra-passe deve ter pelo menos 8 caracteres',
    errPwUpper: 'A palavra-passe deve conter uma letra maiúscula',
    errPwLower: 'A palavra-passe deve conter uma letra minúscula',
    errPwDigit: 'A palavra-passe deve conter um dígito',
    errPwMismatch: 'As palavras-passe devem coincidir',
    errNameShort: 'O nome deve ter pelo menos 2 caracteres',
    errCodeLen: 'O código de ativação deve ter 8 caracteres',
    errCodeInvalid: 'O código de ativação é inválido',
    errCodeCheck: 'Não foi possível verificar o código de ativação',
    errLat: 'A latitude deve estar entre -90 e 90',
    errLon: 'A longitude deve estar entre -180 e 180',
  },
  nl: {
    title: 'Registratie',
    subtitle: 'Maak een account aan in het Solar Forecast API-portaal',
    stepAccount: 'Accountgegevens',
    stepInstallation: 'Installatiegegevens',
    confirmPassword: 'Bevestig wachtwoord',
    name: 'Naam',
    activationCode: 'Activeringscode',
    checkingCode: 'Code controleren…',
    planInfo: '✓ Abonnement: {{plan}} — max. strings: {{count}}',
    haveAccount: 'Heeft u al een account? Inloggen',
    next: 'Volgende',
    back: 'Terug',
    registering: 'Registreren…',
    skipConfig: 'Overslaan — later configureren',
    register: 'Registreren',
    accountCreated: 'Het account is aangemaakt!',
    apiKeyGenerated: 'Uw API-sleutel is gegenereerd.',
    yourApiKey: 'Uw API-sleutel:',
    saveKeyWarning: 'Bewaar deze sleutel — hij wordt niet opnieuw getoond!',
    goToLogin: 'Naar inloggen',
    errInvalidEmail: 'Ongeldig e-mailadres',
    errPwShort: 'Wachtwoord moet minimaal 8 tekens bevatten',
    errPwUpper: 'Wachtwoord moet een hoofdletter bevatten',
    errPwLower: 'Wachtwoord moet een kleine letter bevatten',
    errPwDigit: 'Wachtwoord moet een cijfer bevatten',
    errPwMismatch: 'Wachtwoorden moeten overeenkomen',
    errNameShort: 'Naam moet minimaal 2 tekens bevatten',
    errCodeLen: 'Activeringscode moet 8 tekens bevatten',
    errCodeInvalid: 'Activeringscode is ongeldig',
    errCodeCheck: 'Kon activeringscode niet controleren',
    errLat: 'Breedtegraad moet tussen -90 en 90 liggen',
    errLon: 'Lengtegraad moet tussen -180 en 180 liggen',
  },
  da: {
    title: 'Registrering',
    subtitle: 'Opret en konto i Solar Forecast API-portalen',
    stepAccount: 'Kontooplysninger',
    stepInstallation: 'Anlægsoplysninger',
    confirmPassword: 'Bekræft adgangskode',
    name: 'Navn',
    activationCode: 'Aktiveringskode',
    checkingCode: 'Kontrollerer kode…',
    planInfo: '✓ Plan: {{plan}} — maks. strenge: {{count}}',
    haveAccount: 'Har du allerede en konto? Log ind',
    next: 'Næste',
    back: 'Tilbage',
    registering: 'Registrerer…',
    skipConfig: 'Spring over — konfigurer senere',
    register: 'Registrer',
    accountCreated: 'Kontoen er oprettet!',
    apiKeyGenerated: 'Din API-nøgle er genereret.',
    yourApiKey: 'Din API-nøgle:',
    saveKeyWarning: 'Gem denne nøgle — den vises ikke igen!',
    goToLogin: 'Gå til login',
    errInvalidEmail: 'Ugyldig e-mailadresse',
    errPwShort: 'Adgangskoden skal være mindst 8 tegn',
    errPwUpper: 'Adgangskoden skal indeholde et stort bogstav',
    errPwLower: 'Adgangskoden skal indeholde et lille bogstav',
    errPwDigit: 'Adgangskoden skal indeholde et ciffer',
    errPwMismatch: 'Adgangskoderne skal stemme overens',
    errNameShort: 'Navnet skal være mindst 2 tegn',
    errCodeLen: 'Aktiveringskoden skal være 8 tegn',
    errCodeInvalid: 'Aktiveringskoden er ugyldig',
    errCodeCheck: 'Kunne ikke kontrollere aktiveringskoden',
    errLat: 'Breddegrad skal være mellem -90 og 90',
    errLon: 'Længdegrad skal være mellem -180 og 180',
  },
  sv: {
    title: 'Registrering',
    subtitle: 'Skapa ett konto i Solar Forecast API-portalen',
    stepAccount: 'Kontouppgifter',
    stepInstallation: 'Anläggningsuppgifter',
    confirmPassword: 'Bekräfta lösenord',
    name: 'Namn',
    activationCode: 'Aktiveringskod',
    checkingCode: 'Kontrollerar kod…',
    planInfo: '✓ Plan: {{plan}} — max strängar: {{count}}',
    haveAccount: 'Har du redan ett konto? Logga in',
    next: 'Nästa',
    back: 'Tillbaka',
    registering: 'Registrerar…',
    skipConfig: 'Hoppa över — konfigurera senare',
    register: 'Registrera',
    accountCreated: 'Kontot har skapats!',
    apiKeyGenerated: 'Din API-nyckel har genererats.',
    yourApiKey: 'Din API-nyckel:',
    saveKeyWarning: 'Spara denna nyckel — den visas inte igen!',
    goToLogin: 'Gå till inloggning',
    errInvalidEmail: 'Ogiltig e-postadress',
    errPwShort: 'Lösenordet måste vara minst 8 tecken',
    errPwUpper: 'Lösenordet måste innehålla en versal',
    errPwLower: 'Lösenordet måste innehålla en gemen',
    errPwDigit: 'Lösenordet måste innehålla en siffra',
    errPwMismatch: 'Lösenorden måste matcha',
    errNameShort: 'Namnet måste vara minst 2 tecken',
    errCodeLen: 'Aktiveringskoden måste vara 8 tecken',
    errCodeInvalid: 'Aktiveringskoden är ogiltig',
    errCodeCheck: 'Kunde inte kontrollera aktiveringskoden',
    errLat: 'Latitud måste vara mellan -90 och 90',
    errLon: 'Longitud måste vara mellan -180 och 180',
  },
  fi: {
    title: 'Rekisteröinti',
    subtitle: 'Luo tili Solar Forecast API -portaaliin',
    stepAccount: 'Tilin tiedot',
    stepInstallation: 'Laitoksen tiedot',
    confirmPassword: 'Vahvista salasana',
    name: 'Nimi',
    activationCode: 'Aktivointikoodi',
    checkingCode: 'Tarkistetaan koodia…',
    planInfo: '✓ Suunnitelma: {{plan}} — sarjoja enint.: {{count}}',
    haveAccount: 'Onko sinulla jo tili? Kirjaudu sisään',
    next: 'Seuraava',
    back: 'Takaisin',
    registering: 'Rekisteröidään…',
    skipConfig: 'Ohita — määritä myöhemmin',
    register: 'Rekisteröidy',
    accountCreated: 'Tili on luotu!',
    apiKeyGenerated: 'API-avaimesi on luotu.',
    yourApiKey: 'API-avaimesi:',
    saveKeyWarning: 'Tallenna tämä avain — sitä ei näytetä uudelleen!',
    goToLogin: 'Siirry kirjautumiseen',
    errInvalidEmail: 'Virheellinen sähköpostiosoite',
    errPwShort: 'Salasanan on oltava vähintään 8 merkkiä',
    errPwUpper: 'Salasanan on sisällettävä iso kirjain',
    errPwLower: 'Salasanan on sisällettävä pieni kirjain',
    errPwDigit: 'Salasanan on sisällettävä numero',
    errPwMismatch: 'Salasanojen on täsmättävä',
    errNameShort: 'Nimen on oltava vähintään 2 merkkiä',
    errCodeLen: 'Aktivointikoodin on oltava 8 merkkiä',
    errCodeInvalid: 'Aktivointikoodi on virheellinen',
    errCodeCheck: 'Aktivointikoodin tarkistus epäonnistui',
    errLat: 'Leveysasteen on oltava välillä -90 ja 90',
    errLon: 'Pituusasteen on oltava välillä -180 ja 180',
  },
  cs: {
    title: 'Registrace',
    subtitle: 'Vytvořte si účet v portálu Solar Forecast API',
    stepAccount: 'Údaje účtu',
    stepInstallation: 'Údaje instalace',
    confirmPassword: 'Potvrďte heslo',
    name: 'Jméno',
    activationCode: 'Aktivační kód',
    checkingCode: 'Kontrola kódu…',
    planInfo: '✓ Tarif: {{plan}} — max stringů: {{count}}',
    haveAccount: 'Máte již účet? Přihlásit se',
    next: 'Další',
    back: 'Zpět',
    registering: 'Registrace…',
    skipConfig: 'Přeskočit — nastavit později',
    register: 'Zaregistrovat se',
    accountCreated: 'Účet byl vytvořen!',
    apiKeyGenerated: 'Váš API klíč byl vygenerován.',
    yourApiKey: 'Váš API klíč:',
    saveKeyWarning: 'Uložte si tento klíč — znovu se nezobrazí!',
    goToLogin: 'Přejít na přihlášení',
    errInvalidEmail: 'Neplatná e-mailová adresa',
    errPwShort: 'Heslo musí mít alespoň 8 znaků',
    errPwUpper: 'Heslo musí obsahovat velké písmeno',
    errPwLower: 'Heslo musí obsahovat malé písmeno',
    errPwDigit: 'Heslo musí obsahovat číslici',
    errPwMismatch: 'Hesla se musí shodovat',
    errNameShort: 'Jméno musí mít alespoň 2 znaky',
    errCodeLen: 'Aktivační kód musí mít 8 znaků',
    errCodeInvalid: 'Aktivační kód je neplatný',
    errCodeCheck: 'Nepodařilo se ověřit aktivační kód',
    errLat: 'Zeměpisná šířka musí být mezi -90 a 90',
    errLon: 'Zeměpisná délka musí být mezi -180 a 180',
  },
  sk: {
    title: 'Registrácia',
    subtitle: 'Vytvorte si účet v portáli Solar Forecast API',
    stepAccount: 'Údaje účtu',
    stepInstallation: 'Údaje inštalácie',
    confirmPassword: 'Potvrďte heslo',
    name: 'Meno',
    activationCode: 'Aktivačný kód',
    checkingCode: 'Kontrola kódu…',
    planInfo: '✓ Tarif: {{plan}} — max stringov: {{count}}',
    haveAccount: 'Máte už účet? Prihlásiť sa',
    next: 'Ďalej',
    back: 'Späť',
    registering: 'Registrácia…',
    skipConfig: 'Preskočiť — nastaviť neskôr',
    register: 'Zaregistrovať sa',
    accountCreated: 'Účet bol vytvorený!',
    apiKeyGenerated: 'Váš API kľúč bol vygenerovaný.',
    yourApiKey: 'Váš API kľúč:',
    saveKeyWarning: 'Uložte si tento kľúč — znova sa nezobrazí!',
    goToLogin: 'Prejsť na prihlásenie',
    errInvalidEmail: 'Neplatná e-mailová adresa',
    errPwShort: 'Heslo musí mať aspoň 8 znakov',
    errPwUpper: 'Heslo musí obsahovať veľké písmeno',
    errPwLower: 'Heslo musí obsahovať malé písmeno',
    errPwDigit: 'Heslo musí obsahovať číslicu',
    errPwMismatch: 'Heslá sa musia zhodovať',
    errNameShort: 'Meno musí mať aspoň 2 znaky',
    errCodeLen: 'Aktivačný kód musí mať 8 znakov',
    errCodeInvalid: 'Aktivačný kód je neplatný',
    errCodeCheck: 'Nepodarilo sa overiť aktivačný kód',
    errLat: 'Zemepisná šírka musí byť medzi -90 a 90',
    errLon: 'Zemepisná dĺžka musí byť medzi -180 a 180',
  },
  sl: {
    title: 'Registracija',
    subtitle: 'Ustvarite račun v portalu Solar Forecast API',
    stepAccount: 'Podatki računa',
    stepInstallation: 'Podatki naprave',
    confirmPassword: 'Potrdite geslo',
    name: 'Ime',
    activationCode: 'Aktivacijska koda',
    checkingCode: 'Preverjanje kode…',
    planInfo: '✓ Paket: {{plan}} — največ nizov: {{count}}',
    haveAccount: 'Že imate račun? Prijava',
    next: 'Naprej',
    back: 'Nazaj',
    registering: 'Registracija…',
    skipConfig: 'Preskoči — nastavi pozneje',
    register: 'Registriraj se',
    accountCreated: 'Račun je bil ustvarjen!',
    apiKeyGenerated: 'Vaš API ključ je bil ustvarjen.',
    yourApiKey: 'Vaš API ključ:',
    saveKeyWarning: 'Shranite ta ključ — ne bo prikazan znova!',
    goToLogin: 'Pojdi na prijavo',
    errInvalidEmail: 'Neveljaven e-poštni naslov',
    errPwShort: 'Geslo mora imeti vsaj 8 znakov',
    errPwUpper: 'Geslo mora vsebovati veliko črko',
    errPwLower: 'Geslo mora vsebovati malo črko',
    errPwDigit: 'Geslo mora vsebovati številko',
    errPwMismatch: 'Gesli se morata ujemati',
    errNameShort: 'Ime mora imeti vsaj 2 znaka',
    errCodeLen: 'Aktivacijska koda mora imeti 8 znakov',
    errCodeInvalid: 'Aktivacijska koda je neveljavna',
    errCodeCheck: 'Aktivacijske kode ni bilo mogoče preveriti',
    errLat: 'Zemljepisna širina mora biti med -90 in 90',
    errLon: 'Zemljepisna dolžina mora biti med -180 in 180',
  },
  hr: {
    title: 'Registracija',
    subtitle: 'Stvorite račun u portalu Solar Forecast API',
    stepAccount: 'Podaci računa',
    stepInstallation: 'Podaci postrojenja',
    confirmPassword: 'Potvrdite lozinku',
    name: 'Ime',
    activationCode: 'Aktivacijski kod',
    checkingCode: 'Provjera koda…',
    planInfo: '✓ Plan: {{plan}} — maks. nizova: {{count}}',
    haveAccount: 'Već imate račun? Prijava',
    next: 'Dalje',
    back: 'Natrag',
    registering: 'Registracija…',
    skipConfig: 'Preskoči — postavi kasnije',
    register: 'Registriraj se',
    accountCreated: 'Račun je stvoren!',
    apiKeyGenerated: 'Vaš API ključ je generiran.',
    yourApiKey: 'Vaš API ključ:',
    saveKeyWarning: 'Spremite ovaj ključ — neće se ponovno prikazati!',
    goToLogin: 'Idi na prijavu',
    errInvalidEmail: 'Neispravna e-mail adresa',
    errPwShort: 'Lozinka mora imati najmanje 8 znakova',
    errPwUpper: 'Lozinka mora sadržavati veliko slovo',
    errPwLower: 'Lozinka mora sadržavati malo slovo',
    errPwDigit: 'Lozinka mora sadržavati znamenku',
    errPwMismatch: 'Lozinke se moraju podudarati',
    errNameShort: 'Ime mora imati najmanje 2 znaka',
    errCodeLen: 'Aktivacijski kod mora imati 8 znakova',
    errCodeInvalid: 'Aktivacijski kod je neispravan',
    errCodeCheck: 'Nije moguće provjeriti aktivacijski kod',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
  },
  hu: {
    title: 'Regisztráció',
    subtitle: 'Hozzon létre fiókot a Solar Forecast API portálon',
    stepAccount: 'Fiókadatok',
    stepInstallation: 'Telepítési adatok',
    confirmPassword: 'Jelszó megerősítése',
    name: 'Név',
    activationCode: 'Aktiválási kód',
    checkingCode: 'Kód ellenőrzése…',
    planInfo: '✓ Csomag: {{plan}} — max. stringek: {{count}}',
    haveAccount: 'Már van fiókja? Bejelentkezés',
    next: 'Tovább',
    back: 'Vissza',
    registering: 'Regisztráció…',
    skipConfig: 'Kihagyás — konfigurálás később',
    register: 'Regisztráció',
    accountCreated: 'A fiók létrejött!',
    apiKeyGenerated: 'Az API-kulcsa elkészült.',
    yourApiKey: 'Az Ön API-kulcsa:',
    saveKeyWarning: 'Mentse el ezt a kulcsot — nem jelenik meg újra!',
    goToLogin: 'Tovább a bejelentkezéshez',
    errInvalidEmail: 'Érvénytelen e-mail cím',
    errPwShort: 'A jelszónak legalább 8 karakter hosszúnak kell lennie',
    errPwUpper: 'A jelszónak tartalmaznia kell egy nagybetűt',
    errPwLower: 'A jelszónak tartalmaznia kell egy kisbetűt',
    errPwDigit: 'A jelszónak tartalmaznia kell egy számjegyet',
    errPwMismatch: 'A jelszavaknak egyezniük kell',
    errNameShort: 'A névnek legalább 2 karakter hosszúnak kell lennie',
    errCodeLen: 'Az aktiválási kódnak 8 karakter hosszúnak kell lennie',
    errCodeInvalid: 'Az aktiválási kód érvénytelen',
    errCodeCheck: 'Nem sikerült ellenőrizni az aktiválási kódot',
    errLat: 'A szélességi foknak -90 és 90 között kell lennie',
    errLon: 'A hosszúsági foknak -180 és 180 között kell lennie',
  },
  ro: {
    title: 'Înregistrare',
    subtitle: 'Creați un cont pe portalul Solar Forecast API',
    stepAccount: 'Date cont',
    stepInstallation: 'Date instalație',
    confirmPassword: 'Confirmați parola',
    name: 'Nume',
    activationCode: 'Cod de activare',
    checkingCode: 'Se verifică codul…',
    planInfo: '✓ Plan: {{plan}} — string-uri max.: {{count}}',
    haveAccount: 'Aveți deja un cont? Autentificare',
    next: 'Înainte',
    back: 'Înapoi',
    registering: 'Se înregistrează…',
    skipConfig: 'Omiteți — configurați mai târziu',
    register: 'Înregistrați-vă',
    accountCreated: 'Contul a fost creat!',
    apiKeyGenerated: 'Cheia dvs. API a fost generată.',
    yourApiKey: 'Cheia dvs. API:',
    saveKeyWarning: 'Salvați această cheie — nu va fi afișată din nou!',
    goToLogin: 'Mergeți la autentificare',
    errInvalidEmail: 'Adresă de e-mail nevalidă',
    errPwShort: 'Parola trebuie să aibă cel puțin 8 caractere',
    errPwUpper: 'Parola trebuie să conțină o literă mare',
    errPwLower: 'Parola trebuie să conțină o literă mică',
    errPwDigit: 'Parola trebuie să conțină o cifră',
    errPwMismatch: 'Parolele trebuie să coincidă',
    errNameShort: 'Numele trebuie să aibă cel puțin 2 caractere',
    errCodeLen: 'Codul de activare trebuie să aibă 8 caractere',
    errCodeInvalid: 'Codul de activare este nevalid',
    errCodeCheck: 'Nu s-a putut verifica codul de activare',
    errLat: 'Latitudinea trebuie să fie între -90 și 90',
    errLon: 'Longitudinea trebuie să fie între -180 și 180',
  },
  bg: {
    title: 'Регистрация',
    subtitle: 'Създайте акаунт в портала Solar Forecast API',
    stepAccount: 'Данни за акаунта',
    stepInstallation: 'Данни за инсталацията',
    confirmPassword: 'Потвърдете паролата',
    name: 'Име',
    activationCode: 'Код за активиране',
    checkingCode: 'Проверка на кода…',
    planInfo: '✓ План: {{plan}} — макс. стрингове: {{count}}',
    haveAccount: 'Вече имате акаунт? Вход',
    next: 'Напред',
    back: 'Назад',
    registering: 'Регистриране…',
    skipConfig: 'Пропусни — настрой по-късно',
    register: 'Регистрирай се',
    accountCreated: 'Акаунтът е създаден!',
    apiKeyGenerated: 'Вашият API ключ е генериран.',
    yourApiKey: 'Вашият API ключ:',
    saveKeyWarning: 'Запазете този ключ — няма да бъде показан отново!',
    goToLogin: 'Към входа',
    errInvalidEmail: 'Невалиден имейл адрес',
    errPwShort: 'Паролата трябва да е поне 8 знака',
    errPwUpper: 'Паролата трябва да съдържа главна буква',
    errPwLower: 'Паролата трябва да съдържа малка буква',
    errPwDigit: 'Паролата трябва да съдържа цифра',
    errPwMismatch: 'Паролите трябва да съвпадат',
    errNameShort: 'Името трябва да е поне 2 знака',
    errCodeLen: 'Кодът за активиране трябва да е 8 знака',
    errCodeInvalid: 'Кодът за активиране е невалиден',
    errCodeCheck: 'Неуспешна проверка на кода за активиране',
    errLat: 'Географската ширина трябва да е между -90 и 90',
    errLon: 'Географската дължина трябва да е между -180 и 180',
  },
  el: {
    title: 'Εγγραφή',
    subtitle: 'Δημιουργήστε λογαριασμό στην πύλη Solar Forecast API',
    stepAccount: 'Στοιχεία λογαριασμού',
    stepInstallation: 'Στοιχεία εγκατάστασης',
    confirmPassword: 'Επιβεβαίωση κωδικού',
    name: 'Όνομα',
    activationCode: 'Κωδικός ενεργοποίησης',
    checkingCode: 'Έλεγχος κωδικού…',
    planInfo: '✓ Πρόγραμμα: {{plan}} — μέγ. strings: {{count}}',
    haveAccount: 'Έχετε ήδη λογαριασμό; Σύνδεση',
    next: 'Επόμενο',
    back: 'Πίσω',
    registering: 'Εγγραφή…',
    skipConfig: 'Παράλειψη — ρύθμιση αργότερα',
    register: 'Εγγραφή',
    accountCreated: 'Ο λογαριασμός δημιουργήθηκε!',
    apiKeyGenerated: 'Το κλειδί API σας δημιουργήθηκε.',
    yourApiKey: 'Το κλειδί API σας:',
    saveKeyWarning: 'Αποθηκεύστε αυτό το κλειδί — δεν θα εμφανιστεί ξανά!',
    goToLogin: 'Μετάβαση στη σύνδεση',
    errInvalidEmail: 'Μη έγκυρη διεύθυνση e-mail',
    errPwShort: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες',
    errPwUpper: 'Ο κωδικός πρέπει να περιέχει κεφαλαίο γράμμα',
    errPwLower: 'Ο κωδικός πρέπει να περιέχει πεζό γράμμα',
    errPwDigit: 'Ο κωδικός πρέπει να περιέχει ψηφίο',
    errPwMismatch: 'Οι κωδικοί πρέπει να ταιριάζουν',
    errNameShort: 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
    errCodeLen: 'Ο κωδικός ενεργοποίησης πρέπει να έχει 8 χαρακτήρες',
    errCodeInvalid: 'Ο κωδικός ενεργοποίησης δεν είναι έγκυρος',
    errCodeCheck: 'Αποτυχία ελέγχου κωδικού ενεργοποίησης',
    errLat: 'Το γεωγραφικό πλάτος πρέπει να είναι μεταξύ -90 και 90',
    errLon: 'Το γεωγραφικό μήκος πρέπει να είναι μεταξύ -180 και 180',
  },
  et: {
    title: 'Registreerimine',
    subtitle: 'Looge konto Solar Forecast API portaalis',
    stepAccount: 'Konto andmed',
    stepInstallation: 'Paigaldise andmed',
    confirmPassword: 'Kinnitage parool',
    name: 'Nimi',
    activationCode: 'Aktiveerimiskood',
    checkingCode: 'Koodi kontrollimine…',
    planInfo: '✓ Pakett: {{plan}} — max stringe: {{count}}',
    haveAccount: 'Kas teil on juba konto? Logige sisse',
    next: 'Edasi',
    back: 'Tagasi',
    registering: 'Registreerimine…',
    skipConfig: 'Jäta vahele — seadista hiljem',
    register: 'Registreeru',
    accountCreated: 'Konto on loodud!',
    apiKeyGenerated: 'Teie API-võti on loodud.',
    yourApiKey: 'Teie API-võti:',
    saveKeyWarning: 'Salvestage see võti — seda ei näidata uuesti!',
    goToLogin: 'Mine sisselogimisse',
    errInvalidEmail: 'Vigane e-posti aadress',
    errPwShort: 'Parool peab olema vähemalt 8 märki',
    errPwUpper: 'Parool peab sisaldama suurtähte',
    errPwLower: 'Parool peab sisaldama väiketähte',
    errPwDigit: 'Parool peab sisaldama numbrit',
    errPwMismatch: 'Paroolid peavad kattuma',
    errNameShort: 'Nimi peab olema vähemalt 2 märki',
    errCodeLen: 'Aktiveerimiskood peab olema 8 märki',
    errCodeInvalid: 'Aktiveerimiskood on vigane',
    errCodeCheck: 'Aktiveerimiskoodi kontrollimine ebaõnnestus',
    errLat: 'Laiuskraad peab olema vahemikus -90 kuni 90',
    errLon: 'Pikkuskraad peab olema vahemikus -180 kuni 180',
  },
  lv: {
    title: 'Reģistrācija',
    subtitle: 'Izveidojiet kontu Solar Forecast API portālā',
    stepAccount: 'Konta dati',
    stepInstallation: 'Iekārtas dati',
    confirmPassword: 'Apstipriniet paroli',
    name: 'Vārds',
    activationCode: 'Aktivizācijas kods',
    checkingCode: 'Koda pārbaude…',
    planInfo: '✓ Plāns: {{plan}} — maks. virknes: {{count}}',
    haveAccount: 'Jums jau ir konts? Pieslēgties',
    next: 'Tālāk',
    back: 'Atpakaļ',
    registering: 'Reģistrācija…',
    skipConfig: 'Izlaist — konfigurēt vēlāk',
    register: 'Reģistrēties',
    accountCreated: 'Konts ir izveidots!',
    apiKeyGenerated: 'Jūsu API atslēga ir ģenerēta.',
    yourApiKey: 'Jūsu API atslēga:',
    saveKeyWarning: 'Saglabājiet šo atslēgu — tā netiks rādīta vēlreiz!',
    goToLogin: 'Doties uz pieslēgšanos',
    errInvalidEmail: 'Nederīga e-pasta adrese',
    errPwShort: 'Parolei jābūt vismaz 8 rakstzīmēm',
    errPwUpper: 'Parolei jāsatur lielais burts',
    errPwLower: 'Parolei jāsatur mazais burts',
    errPwDigit: 'Parolei jāsatur cipars',
    errPwMismatch: 'Parolēm jāsakrīt',
    errNameShort: 'Vārdam jābūt vismaz 2 rakstzīmēm',
    errCodeLen: 'Aktivizācijas kodam jābūt 8 rakstzīmēm',
    errCodeInvalid: 'Aktivizācijas kods ir nederīgs',
    errCodeCheck: 'Neizdevās pārbaudīt aktivizācijas kodu',
    errLat: 'Ģeogrāfiskajam platumam jābūt no -90 līdz 90',
    errLon: 'Ģeogrāfiskajam garumam jābūt no -180 līdz 180',
  },
  lt: {
    title: 'Registracija',
    subtitle: 'Sukurkite paskyrą Solar Forecast API portale',
    stepAccount: 'Paskyros duomenys',
    stepInstallation: 'Įrenginio duomenys',
    confirmPassword: 'Patvirtinkite slaptažodį',
    name: 'Vardas',
    activationCode: 'Aktyvavimo kodas',
    checkingCode: 'Tikrinamas kodas…',
    planInfo: '✓ Planas: {{plan}} — daugiausiai eilučių: {{count}}',
    haveAccount: 'Jau turite paskyrą? Prisijungti',
    next: 'Toliau',
    back: 'Atgal',
    registering: 'Registruojama…',
    skipConfig: 'Praleisti — konfigūruoti vėliau',
    register: 'Registruotis',
    accountCreated: 'Paskyra sukurta!',
    apiKeyGenerated: 'Jūsų API raktas sugeneruotas.',
    yourApiKey: 'Jūsų API raktas:',
    saveKeyWarning: 'Išsaugokite šį raktą — jis nebus rodomas dar kartą!',
    goToLogin: 'Eiti į prisijungimą',
    errInvalidEmail: 'Neteisingas el. pašto adresas',
    errPwShort: 'Slaptažodį turi sudaryti bent 8 simboliai',
    errPwUpper: 'Slaptažodyje turi būti didžioji raidė',
    errPwLower: 'Slaptažodyje turi būti mažoji raidė',
    errPwDigit: 'Slaptažodyje turi būti skaitmuo',
    errPwMismatch: 'Slaptažodžiai turi sutapti',
    errNameShort: 'Vardą turi sudaryti bent 2 simboliai',
    errCodeLen: 'Aktyvavimo kodą turi sudaryti 8 simboliai',
    errCodeInvalid: 'Aktyvavimo kodas neteisingas',
    errCodeCheck: 'Nepavyko patikrinti aktyvavimo kodo',
    errLat: 'Platuma turi būti nuo -90 iki 90',
    errLon: 'Ilguma turi būti nuo -180 iki 180',
  },
  mt: {
    title: 'Reġistrazzjoni',
    subtitle: 'Oħloq kont fil-portal Solar Forecast API',
    stepAccount: 'Dettalji tal-kont',
    stepInstallation: 'Dettalji tal-installazzjoni',
    confirmPassword: 'Ikkonferma l-password',
    name: 'Isem',
    activationCode: 'Kodiċi tal-attivazzjoni',
    checkingCode: 'Qed jiġi vverifikat il-kodiċi…',
    planInfo: '✓ Pjan: {{plan}} — strings massimi: {{count}}',
    haveAccount: 'Diġà għandek kont? Idħol',
    next: 'Li jmiss',
    back: 'Lura',
    registering: 'Qed jirreġistra…',
    skipConfig: 'Aqbeż — ikkonfigura aktar tard',
    register: 'Irreġistra',
    accountCreated: 'Il-kont inħoloq!',
    apiKeyGenerated: 'Iċ-ċavetta API tiegħek ġiet iġġenerata.',
    yourApiKey: 'Iċ-ċavetta API tiegħek:',
    saveKeyWarning: 'Issejvja din iċ-ċavetta — mhux se tintwera mill-ġdid!',
    goToLogin: 'Mur fil-login',
    errInvalidEmail: 'Indirizz e-mail invalidu',
    errPwShort: 'Il-password trid tkun mill-inqas 8 karattri',
    errPwUpper: 'Il-password trid ikollha ittra kbira',
    errPwLower: 'Il-password trid ikollha ittra żgħira',
    errPwDigit: 'Il-password trid ikollha ċifra',
    errPwMismatch: 'Il-passwords iridu jaqblu',
    errNameShort: 'L-isem irid ikun mill-inqas 2 karattri',
    errCodeLen: 'Il-kodiċi tal-attivazzjoni jrid ikun 8 karattri',
    errCodeInvalid: 'Il-kodiċi tal-attivazzjoni huwa invalidu',
    errCodeCheck: 'Ma setax jiġi vverifikat il-kodiċi tal-attivazzjoni',
    errLat: 'Il-latitudni trid tkun bejn -90 u 90',
    errLon: 'Il-lonġitudni trid tkun bejn -180 u 180',
  },
  ga: {
    title: 'Clárú',
    subtitle: 'Cruthaigh cuntas i dtairseach Solar Forecast API',
    stepAccount: 'Sonraí an chuntais',
    stepInstallation: 'Sonraí suiteála',
    confirmPassword: 'Deimhnigh an focal faire',
    name: 'Ainm',
    activationCode: 'Cód gníomhachtaithe',
    checkingCode: 'Ag seiceáil an chóid…',
    planInfo: '✓ Plean: {{plan}} — uasmhéid slabhraí: {{count}}',
    haveAccount: 'An bhfuil cuntas agat cheana? Logáil isteach',
    next: 'Ar aghaidh',
    back: 'Ar ais',
    registering: 'Á chlárú…',
    skipConfig: 'Scipeáil — cumraigh níos déanaí',
    register: 'Cláraigh',
    accountCreated: 'Cruthaíodh an cuntas!',
    apiKeyGenerated: "Gineadh d'eochair API.",
    yourApiKey: "D'eochair API:",
    saveKeyWarning: 'Sábháil an eochair seo — ní thaispeánfar arís í!',
    goToLogin: 'Téigh go dtí an logáil isteach',
    errInvalidEmail: 'Seoladh ríomhphoist neamhbhailí',
    errPwShort: 'Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire',
    errPwUpper: 'Caithfidh ceannlitir a bheith san fhocal faire',
    errPwLower: 'Caithfidh litir bheag a bheith san fhocal faire',
    errPwDigit: 'Caithfidh digit a bheith san fhocal faire',
    errPwMismatch: 'Caithfidh na focail faire a bheith ag teacht le chéile',
    errNameShort: 'Caithfidh 2 charachtar ar a laghad a bheith san ainm',
    errCodeLen: 'Caithfidh 8 gcarachtar a bheith sa chód gníomhachtaithe',
    errCodeInvalid: 'Tá an cód gníomhachtaithe neamhbhailí',
    errCodeCheck: 'Theip ar an gcód gníomhachtaithe a sheiceáil',
    errLat: 'Caithfidh an domhanleithead a bheith idir -90 agus 90',
    errLon: 'Caithfidh an domhanfhad a bheith idir -180 agus 180',
  },
  no: {
    title: 'Registrering',
    subtitle: 'Opprett en konto i Solar Forecast API-portalen',
    stepAccount: 'Kontodetaljer',
    stepInstallation: 'Anleggsdetaljer',
    confirmPassword: 'Bekreft passord',
    name: 'Navn',
    activationCode: 'Aktiveringskode',
    checkingCode: 'Kontrollerer kode…',
    planInfo: '✓ Plan: {{plan}} — maks strenger: {{count}}',
    haveAccount: 'Har du allerede en konto? Logg inn',
    next: 'Neste',
    back: 'Tilbake',
    registering: 'Registrerer…',
    skipConfig: 'Hopp over — konfigurer senere',
    register: 'Registrer',
    accountCreated: 'Kontoen er opprettet!',
    apiKeyGenerated: 'API-nøkkelen din er generert.',
    yourApiKey: 'API-nøkkelen din:',
    saveKeyWarning: 'Lagre denne nøkkelen — den vises ikke igjen!',
    goToLogin: 'Gå til innlogging',
    errInvalidEmail: 'Ugyldig e-postadresse',
    errPwShort: 'Passordet må være minst 8 tegn',
    errPwUpper: 'Passordet må inneholde en stor bokstav',
    errPwLower: 'Passordet må inneholde en liten bokstav',
    errPwDigit: 'Passordet må inneholde et siffer',
    errPwMismatch: 'Passordene må stemme overens',
    errNameShort: 'Navnet må være minst 2 tegn',
    errCodeLen: 'Aktiveringskoden må være 8 tegn',
    errCodeInvalid: 'Aktiveringskoden er ugyldig',
    errCodeCheck: 'Kunne ikke kontrollere aktiveringskoden',
    errLat: 'Breddegrad må være mellom -90 og 90',
    errLon: 'Lengdegrad må være mellom -180 og 180',
  },
  is: {
    title: 'Skráning',
    subtitle: 'Búðu til aðgang á Solar Forecast API gáttinni',
    stepAccount: 'Aðgangsupplýsingar',
    stepInstallation: 'Upplýsingar um uppsetningu',
    confirmPassword: 'Staðfestu lykilorð',
    name: 'Nafn',
    activationCode: 'Virkjunarkóði',
    checkingCode: 'Athuga kóða…',
    planInfo: '✓ Áskrift: {{plan}} — hámark strengja: {{count}}',
    haveAccount: 'Ertu nú þegar með aðgang? Skrá inn',
    next: 'Áfram',
    back: 'Til baka',
    registering: 'Skrái…',
    skipConfig: 'Sleppa — stilla síðar',
    register: 'Skrá',
    accountCreated: 'Aðgangurinn var stofnaður!',
    apiKeyGenerated: 'API-lykillinn þinn hefur verið búinn til.',
    yourApiKey: 'API-lykillinn þinn:',
    saveKeyWarning: 'Vistaðu þennan lykil — hann verður ekki sýndur aftur!',
    goToLogin: 'Fara í innskráningu',
    errInvalidEmail: 'Ógilt netfang',
    errPwShort: 'Lykilorð verður að vera að minnsta kosti 8 stafir',
    errPwUpper: 'Lykilorð verður að innihalda hástaf',
    errPwLower: 'Lykilorð verður að innihalda lágstaf',
    errPwDigit: 'Lykilorð verður að innihalda tölustaf',
    errPwMismatch: 'Lykilorðin verða að stemma',
    errNameShort: 'Nafn verður að vera að minnsta kosti 2 stafir',
    errCodeLen: 'Virkjunarkóði verður að vera 8 stafir',
    errCodeInvalid: 'Virkjunarkóði er ógildur',
    errCodeCheck: 'Ekki tókst að athuga virkjunarkóða',
    errLat: 'Breiddargráða verður að vera á milli -90 og 90',
    errLon: 'Lengdargráða verður að vera á milli -180 og 180',
  },
  uk: {
    title: 'Реєстрація',
    subtitle: 'Створіть обліковий запис на порталі Solar Forecast API',
    stepAccount: 'Дані облікового запису',
    stepInstallation: 'Дані установки',
    confirmPassword: 'Підтвердьте пароль',
    name: 'Ім’я',
    activationCode: 'Код активації',
    checkingCode: 'Перевірка коду…',
    planInfo: '✓ План: {{plan}} — макс. стрінгів: {{count}}',
    haveAccount: 'Вже маєте обліковий запис? Увійти',
    next: 'Далі',
    back: 'Назад',
    registering: 'Реєстрація…',
    skipConfig: 'Пропустити — налаштувати пізніше',
    register: 'Зареєструватися',
    accountCreated: 'Обліковий запис створено!',
    apiKeyGenerated: 'Ваш ключ API згенеровано.',
    yourApiKey: 'Ваш ключ API:',
    saveKeyWarning: 'Збережіть цей ключ — він більше не відображатиметься!',
    goToLogin: 'Перейти до входу',
    errInvalidEmail: 'Неправильна адреса електронної пошти',
    errPwShort: 'Пароль має містити щонайменше 8 символів',
    errPwUpper: 'Пароль має містити велику літеру',
    errPwLower: 'Пароль має містити малу літеру',
    errPwDigit: 'Пароль має містити цифру',
    errPwMismatch: 'Паролі мають збігатися',
    errNameShort: 'Ім’я має містити щонайменше 2 символи',
    errCodeLen: 'Код активації має містити 8 символів',
    errCodeInvalid: 'Код активації недійсний',
    errCodeCheck: 'Не вдалося перевірити код активації',
    errLat: 'Широта має бути від -90 до 90',
    errLon: 'Довгота має бути від -180 до 180',
  },
  sr: {
    title: 'Registracija',
    subtitle: 'Kreirajte nalog na portalu Solar Forecast API',
    stepAccount: 'Podaci naloga',
    stepInstallation: 'Podaci postrojenja',
    confirmPassword: 'Potvrdite lozinku',
    name: 'Ime',
    activationCode: 'Aktivacioni kod',
    checkingCode: 'Provera koda…',
    planInfo: '✓ Plan: {{plan}} — maks. nizova: {{count}}',
    haveAccount: 'Već imate nalog? Prijava',
    next: 'Dalje',
    back: 'Nazad',
    registering: 'Registracija…',
    skipConfig: 'Preskoči — podesi kasnije',
    register: 'Registruj se',
    accountCreated: 'Nalog je kreiran!',
    apiKeyGenerated: 'Vaš API ključ je generisan.',
    yourApiKey: 'Vaš API ključ:',
    saveKeyWarning: 'Sačuvajte ovaj ključ — neće biti ponovo prikazan!',
    goToLogin: 'Idi na prijavu',
    errInvalidEmail: 'Nevažeća e-mail adresa',
    errPwShort: 'Lozinka mora imati najmanje 8 znakova',
    errPwUpper: 'Lozinka mora sadržati veliko slovo',
    errPwLower: 'Lozinka mora sadržati malo slovo',
    errPwDigit: 'Lozinka mora sadržati cifru',
    errPwMismatch: 'Lozinke se moraju podudarati',
    errNameShort: 'Ime mora imati najmanje 2 znaka',
    errCodeLen: 'Aktivacioni kod mora imati 8 znakova',
    errCodeInvalid: 'Aktivacioni kod je nevažeći',
    errCodeCheck: 'Nije moguće proveriti aktivacioni kod',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
  },
  sq: {
    title: 'Regjistrim',
    subtitle: 'Krijoni një llogari në portalin Solar Forecast API',
    stepAccount: 'Të dhënat e llogarisë',
    stepInstallation: 'Të dhënat e instalimit',
    confirmPassword: 'Konfirmo fjalëkalimin',
    name: 'Emri',
    activationCode: 'Kodi i aktivizimit',
    checkingCode: 'Po kontrollohet kodi…',
    planInfo: '✓ Plani: {{plan}} — vargje maks.: {{count}}',
    haveAccount: 'Keni tashmë një llogari? Hyni',
    next: 'Tjetra',
    back: 'Mbrapa',
    registering: 'Po regjistrohet…',
    skipConfig: 'Anashkalo — konfiguro më vonë',
    register: 'Regjistrohu',
    accountCreated: 'Llogaria u krijua!',
    apiKeyGenerated: 'Çelësi juaj API u gjenerua.',
    yourApiKey: 'Çelësi juaj API:',
    saveKeyWarning: 'Ruani këtë çelës — nuk do të shfaqet përsëri!',
    goToLogin: 'Shko te hyrja',
    errInvalidEmail: 'Adresë e-mail e pavlefshme',
    errPwShort: 'Fjalëkalimi duhet të ketë të paktën 8 karaktere',
    errPwUpper: 'Fjalëkalimi duhet të përmbajë një shkronjë të madhe',
    errPwLower: 'Fjalëkalimi duhet të përmbajë një shkronjë të vogël',
    errPwDigit: 'Fjalëkalimi duhet të përmbajë një shifër',
    errPwMismatch: 'Fjalëkalimet duhet të përputhen',
    errNameShort: 'Emri duhet të ketë të paktën 2 karaktere',
    errCodeLen: 'Kodi i aktivizimit duhet të ketë 8 karaktere',
    errCodeInvalid: 'Kodi i aktivizimit është i pavlefshëm',
    errCodeCheck: 'Kontrolli i kodit të aktivizimit dështoi',
    errLat: 'Gjerësia gjeografike duhet të jetë midis -90 dhe 90',
    errLon: 'Gjatësia gjeografike duhet të jetë midis -180 dhe 180',
  },
  mk: {
    title: 'Регистрација',
    subtitle: 'Создадете сметка на порталот Solar Forecast API',
    stepAccount: 'Податоци за сметката',
    stepInstallation: 'Податоци за инсталацијата',
    confirmPassword: 'Потврдете ја лозинката',
    name: 'Име',
    activationCode: 'Код за активирање',
    checkingCode: 'Проверка на кодот…',
    planInfo: '✓ План: {{plan}} — макс. стрингови: {{count}}',
    haveAccount: 'Веќе имате сметка? Најава',
    next: 'Следно',
    back: 'Назад',
    registering: 'Регистрирање…',
    skipConfig: 'Прескокни — конфигурирај подоцна',
    register: 'Регистрирај се',
    accountCreated: 'Сметката е создадена!',
    apiKeyGenerated: 'Вашиот API клуч е генериран.',
    yourApiKey: 'Вашиот API клуч:',
    saveKeyWarning: 'Зачувајте го овој клуч — нема да биде прикажан повторно!',
    goToLogin: 'Оди на најава',
    errInvalidEmail: 'Невалидна е-пошта адреса',
    errPwShort: 'Лозинката мора да има најмалку 8 знаци',
    errPwUpper: 'Лозинката мора да содржи голема буква',
    errPwLower: 'Лозинката мора да содржи мала буква',
    errPwDigit: 'Лозинката мора да содржи цифра',
    errPwMismatch: 'Лозинките мора да се совпаѓаат',
    errNameShort: 'Името мора да има најмалку 2 знаци',
    errCodeLen: 'Кодот за активирање мора да има 8 знаци',
    errCodeInvalid: 'Кодот за активирање е невалиден',
    errCodeCheck: 'Не може да се провери кодот за активирање',
    errLat: 'Географската ширина мора да биде помеѓу -90 и 90',
    errLon: 'Географската должина мора да биде помеѓу -180 и 180',
  },
  bs: {
    title: 'Registracija',
    subtitle: 'Kreirajte račun na portalu Solar Forecast API',
    stepAccount: 'Podaci računa',
    stepInstallation: 'Podaci postrojenja',
    confirmPassword: 'Potvrdite lozinku',
    name: 'Ime',
    activationCode: 'Aktivacijski kod',
    checkingCode: 'Provjera koda…',
    planInfo: '✓ Plan: {{plan}} — maks. nizova: {{count}}',
    haveAccount: 'Već imate račun? Prijava',
    next: 'Dalje',
    back: 'Nazad',
    registering: 'Registracija…',
    skipConfig: 'Preskoči — podesi kasnije',
    register: 'Registruj se',
    accountCreated: 'Račun je kreiran!',
    apiKeyGenerated: 'Vaš API ključ je generisan.',
    yourApiKey: 'Vaš API ključ:',
    saveKeyWarning: 'Sačuvajte ovaj ključ — neće biti ponovo prikazan!',
    goToLogin: 'Idi na prijavu',
    errInvalidEmail: 'Nevažeća e-mail adresa',
    errPwShort: 'Lozinka mora imati najmanje 8 znakova',
    errPwUpper: 'Lozinka mora sadržavati veliko slovo',
    errPwLower: 'Lozinka mora sadržavati malo slovo',
    errPwDigit: 'Lozinka mora sadržavati cifru',
    errPwMismatch: 'Lozinke se moraju podudarati',
    errNameShort: 'Ime mora imati najmanje 2 znaka',
    errCodeLen: 'Aktivacijski kod mora imati 8 znakova',
    errCodeInvalid: 'Aktivacijski kod je nevažeći',
    errCodeCheck: 'Nije moguće provjeriti aktivacijski kod',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
  },
};

/** Namespace 'components' — współdzielone komponenty (formularz instalacji, wykres korekcji, modale). */
const componentsTr: Record<string, Dict> = {
  en: {
    location: 'Location',
    stringConfig: 'String configuration',
    latitude: 'Latitude',
    longitude: 'Longitude',
    azimuthDeg: 'Azimuth (°)',
    tiltDeg: 'Tilt angle (°)',
    panelCount: 'Number of panels',
    panelSizeM2: 'Panel area (m²)',
    efficiencyPct: 'Efficiency (%)',
    string: 'String',
    howManyStrings: 'How many strings do you want to configure? (max: {{count}})',
    configureLater:
      'You can configure fewer strings than your plan allows. You can add the missing ones later in account settings.',
    hourlyComparison: 'Hourly comparison (avg Wh)',
    dailyComparison: 'Daily comparison (sum Wh)',
    forecast: 'Forecast',
    actual: 'Actual',
    noData: 'No data to display',
    editInstallation: 'Edit installation',
    addNewInstallation: 'Add a new installation',
    cancel: 'Cancel',
    back: 'Back',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    errLat: 'Latitude must be between -90 and 90',
    errLon: 'Longitude must be between -180 and 180',
    enterCode: 'Enter the activation code.',
    codeInvalidOrUsed: 'The activation code is invalid or has already been used.',
    verifyCode: 'Verify code',
    addCodeIntro: 'To add a new installation you need an activation code. Enter it below.',
    planMaxStrings: 'Plan: {{name}} (max {{count}} strings)',
    installationNameOpt: 'Installation name (optional)',
    installationNameHelper: 'A name helps tell installations apart.',
    addInstallationBtn: 'Add installation',
  },
  pl: {
    location: 'Lokalizacja',
    stringConfig: 'Konfiguracja stringów',
    latitude: 'Szerokość geograficzna',
    longitude: 'Długość geograficzna',
    azimuthDeg: 'Azymut (°)',
    tiltDeg: 'Kąt nachylenia (°)',
    panelCount: 'Liczba paneli',
    panelSizeM2: 'Powierzchnia panelu (m²)',
    efficiencyPct: 'Sprawność (%)',
    string: 'String',
    howManyStrings: 'Ile stringów chcesz skonfigurować? (maksymalnie: {{count}})',
    configureLater:
      'Możesz skonfigurować mniej stringów niż pozwala plan. Brakujące możesz dodać później w ustawieniach konta.',
    hourlyComparison: 'Porównanie godzinowe (średnia Wh)',
    dailyComparison: 'Porównanie dzienne (suma Wh)',
    forecast: 'Prognoza',
    actual: 'Rzeczywistość',
    noData: 'Brak danych do wyświetlenia',
    editInstallation: 'Edytuj instalację',
    addNewInstallation: 'Dodaj nową instalację',
    cancel: 'Anuluj',
    back: 'Wstecz',
    saving: 'Zapisywanie…',
    saveChanges: 'Zapisz zmiany',
    errLat: 'Szerokość musi być między -90 a 90',
    errLon: 'Długość musi być między -180 a 180',
    enterCode: 'Wprowadź kod aktywacyjny.',
    codeInvalidOrUsed: 'Kod aktywacyjny jest nieprawidłowy lub został już użyty.',
    verifyCode: 'Weryfikuj kod',
    addCodeIntro:
      'Aby dodać nową instalację, potrzebujesz kodu aktywacyjnego. Wprowadź go poniżej.',
    planMaxStrings: 'Plan: {{name}} (maks. {{count}} stringów)',
    installationNameOpt: 'Nazwa instalacji (opcjonalnie)',
    installationNameHelper: 'Nazwa ułatwi rozróżnianie instalacji.',
    addInstallationBtn: 'Dodaj instalację',
  },
  de: {
    location: 'Standort',
    stringConfig: 'String-Konfiguration',
    latitude: 'Breitengrad',
    longitude: 'Längengrad',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Neigungswinkel (°)',
    panelCount: 'Anzahl Module',
    panelSizeM2: 'Modulfläche (m²)',
    efficiencyPct: 'Wirkungsgrad (%)',
    string: 'String',
    howManyStrings: 'Wie viele Strings möchten Sie konfigurieren? (max: {{count}})',
    configureLater:
      'Sie können weniger Strings konfigurieren, als Ihr Tarif erlaubt. Die fehlenden können Sie später in den Kontoeinstellungen hinzufügen.',
    hourlyComparison: 'Stündlicher Vergleich (Ø Wh)',
    dailyComparison: 'Täglicher Vergleich (Summe Wh)',
    forecast: 'Prognose',
    actual: 'Tatsächlich',
    noData: 'Keine Daten zum Anzeigen',
    editInstallation: 'Anlage bearbeiten',
    addNewInstallation: 'Neue Anlage hinzufügen',
    cancel: 'Abbrechen',
    back: 'Zurück',
    saving: 'Wird gespeichert…',
    saveChanges: 'Änderungen speichern',
    errLat: 'Breitengrad muss zwischen -90 und 90 liegen',
    errLon: 'Längengrad muss zwischen -180 und 180 liegen',
    enterCode: 'Geben Sie den Aktivierungscode ein.',
    codeInvalidOrUsed: 'Der Aktivierungscode ist ungültig oder wurde bereits verwendet.',
    verifyCode: 'Code überprüfen',
    addCodeIntro:
      'Um eine neue Anlage hinzuzufügen, benötigen Sie einen Aktivierungscode. Geben Sie ihn unten ein.',
    planMaxStrings: 'Tarif: {{name}} (max {{count}} Strings)',
    installationNameOpt: 'Anlagenname (optional)',
    installationNameHelper: 'Ein Name hilft, Anlagen zu unterscheiden.',
    addInstallationBtn: 'Anlage hinzufügen',
  },
  fr: {
    location: 'Emplacement',
    stringConfig: 'Configuration des chaînes',
    latitude: 'Latitude',
    longitude: 'Longitude',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: "Angle d'inclinaison (°)",
    panelCount: 'Nombre de panneaux',
    panelSizeM2: 'Surface du panneau (m²)',
    efficiencyPct: 'Rendement (%)',
    string: 'Chaîne',
    howManyStrings: 'Combien de chaînes voulez-vous configurer ? (max : {{count}})',
    configureLater:
      'Vous pouvez configurer moins de chaînes que ce que permet votre forfait. Vous pouvez ajouter les manquantes plus tard dans les paramètres du compte.',
    hourlyComparison: 'Comparaison horaire (moy. Wh)',
    dailyComparison: 'Comparaison quotidienne (somme Wh)',
    forecast: 'Prévision',
    actual: 'Réel',
    noData: 'Aucune donnée à afficher',
    editInstallation: "Modifier l'installation",
    addNewInstallation: 'Ajouter une nouvelle installation',
    cancel: 'Annuler',
    back: 'Retour',
    saving: 'Enregistrement…',
    saveChanges: 'Enregistrer les modifications',
    errLat: 'La latitude doit être comprise entre -90 et 90',
    errLon: 'La longitude doit être comprise entre -180 et 180',
    enterCode: "Saisissez le code d'activation.",
    codeInvalidOrUsed: "Le code d'activation est invalide ou a déjà été utilisé.",
    verifyCode: 'Vérifier le code',
    addCodeIntro:
      "Pour ajouter une nouvelle installation, vous avez besoin d'un code d'activation. Saisissez-le ci-dessous.",
    planMaxStrings: 'Forfait : {{name}} (max {{count}} chaînes)',
    installationNameOpt: "Nom de l'installation (facultatif)",
    installationNameHelper: 'Un nom aide à distinguer les installations.',
    addInstallationBtn: "Ajouter l'installation",
  },
  es: {
    location: 'Ubicación',
    stringConfig: 'Configuración de cadenas',
    latitude: 'Latitud',
    longitude: 'Longitud',
    azimuthDeg: 'Acimut (°)',
    tiltDeg: 'Ángulo de inclinación (°)',
    panelCount: 'Número de paneles',
    panelSizeM2: 'Superficie del panel (m²)',
    efficiencyPct: 'Eficiencia (%)',
    string: 'Cadena',
    howManyStrings: '¿Cuántas cadenas desea configurar? (máx.: {{count}})',
    configureLater:
      'Puede configurar menos cadenas de las que permite su plan. Las que falten puede añadirlas más tarde en la configuración de la cuenta.',
    hourlyComparison: 'Comparación horaria (med. Wh)',
    dailyComparison: 'Comparación diaria (suma Wh)',
    forecast: 'Previsión',
    actual: 'Real',
    noData: 'No hay datos para mostrar',
    editInstallation: 'Editar instalación',
    addNewInstallation: 'Añadir una nueva instalación',
    cancel: 'Cancelar',
    back: 'Atrás',
    saving: 'Guardando…',
    saveChanges: 'Guardar cambios',
    errLat: 'La latitud debe estar entre -90 y 90',
    errLon: 'La longitud debe estar entre -180 y 180',
    enterCode: 'Introduzca el código de activación.',
    codeInvalidOrUsed: 'El código de activación no es válido o ya se ha utilizado.',
    verifyCode: 'Verificar código',
    addCodeIntro:
      'Para añadir una nueva instalación necesita un código de activación. Introdúzcalo a continuación.',
    planMaxStrings: 'Plan: {{name}} (máx. {{count}} cadenas)',
    installationNameOpt: 'Nombre de la instalación (opcional)',
    installationNameHelper: 'Un nombre ayuda a distinguir las instalaciones.',
    addInstallationBtn: 'Añadir instalación',
  },
  it: {
    location: 'Posizione',
    stringConfig: 'Configurazione delle stringhe',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Angolo di inclinazione (°)',
    panelCount: 'Numero di pannelli',
    panelSizeM2: 'Superficie del pannello (m²)',
    efficiencyPct: 'Efficienza (%)',
    string: 'Stringa',
    howManyStrings: 'Quante stringhe vuoi configurare? (max: {{count}})',
    configureLater:
      "Puoi configurare meno stringhe di quante ne consenta il tuo piano. Quelle mancanti puoi aggiungerle più tardi nelle impostazioni dell'account.",
    hourlyComparison: 'Confronto orario (media Wh)',
    dailyComparison: 'Confronto giornaliero (somma Wh)',
    forecast: 'Previsione',
    actual: 'Reale',
    noData: 'Nessun dato da visualizzare',
    editInstallation: 'Modifica impianto',
    addNewInstallation: 'Aggiungi un nuovo impianto',
    cancel: 'Annulla',
    back: 'Indietro',
    saving: 'Salvataggio…',
    saveChanges: 'Salva modifiche',
    errLat: 'La latitudine deve essere compresa tra -90 e 90',
    errLon: 'La longitudine deve essere compresa tra -180 e 180',
    enterCode: 'Inserisci il codice di attivazione.',
    codeInvalidOrUsed: 'Il codice di attivazione non è valido o è già stato utilizzato.',
    verifyCode: 'Verifica codice',
    addCodeIntro:
      'Per aggiungere un nuovo impianto serve un codice di attivazione. Inseriscilo qui sotto.',
    planMaxStrings: 'Piano: {{name}} (max {{count}} stringhe)',
    installationNameOpt: "Nome dell'impianto (facoltativo)",
    installationNameHelper: 'Un nome aiuta a distinguere gli impianti.',
    addInstallationBtn: 'Aggiungi impianto',
  },
  pt: {
    location: 'Localização',
    stringConfig: 'Configuração das strings',
    latitude: 'Latitude',
    longitude: 'Longitude',
    azimuthDeg: 'Azimute (°)',
    tiltDeg: 'Ângulo de inclinação (°)',
    panelCount: 'Número de painéis',
    panelSizeM2: 'Área do painel (m²)',
    efficiencyPct: 'Eficiência (%)',
    string: 'String',
    howManyStrings: 'Quantas strings deseja configurar? (máx.: {{count}})',
    configureLater:
      'Pode configurar menos strings do que o seu plano permite. As que faltam pode adicionar mais tarde nas definições da conta.',
    hourlyComparison: 'Comparação horária (méd. Wh)',
    dailyComparison: 'Comparação diária (soma Wh)',
    forecast: 'Previsão',
    actual: 'Real',
    noData: 'Sem dados para mostrar',
    editInstallation: 'Editar instalação',
    addNewInstallation: 'Adicionar uma nova instalação',
    cancel: 'Cancelar',
    back: 'Voltar',
    saving: 'A guardar…',
    saveChanges: 'Guardar alterações',
    errLat: 'A latitude deve estar entre -90 e 90',
    errLon: 'A longitude deve estar entre -180 e 180',
    enterCode: 'Introduza o código de ativação.',
    codeInvalidOrUsed: 'O código de ativação é inválido ou já foi utilizado.',
    verifyCode: 'Verificar código',
    addCodeIntro:
      'Para adicionar uma nova instalação precisa de um código de ativação. Introduza-o abaixo.',
    planMaxStrings: 'Plano: {{name}} (máx. {{count}} strings)',
    installationNameOpt: 'Nome da instalação (opcional)',
    installationNameHelper: 'Um nome ajuda a distinguir as instalações.',
    addInstallationBtn: 'Adicionar instalação',
  },
  nl: {
    location: 'Locatie',
    stringConfig: 'Stringconfiguratie',
    latitude: 'Breedtegraad',
    longitude: 'Lengtegraad',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Hellingshoek (°)',
    panelCount: 'Aantal panelen',
    panelSizeM2: 'Paneeloppervlak (m²)',
    efficiencyPct: 'Rendement (%)',
    string: 'String',
    howManyStrings: 'Hoeveel strings wilt u configureren? (max: {{count}})',
    configureLater:
      'U kunt minder strings configureren dan uw abonnement toestaat. De ontbrekende kunt u later toevoegen in de accountinstellingen.',
    hourlyComparison: 'Uurvergelijking (gem. Wh)',
    dailyComparison: 'Dagvergelijking (som Wh)',
    forecast: 'Prognose',
    actual: 'Werkelijk',
    noData: 'Geen gegevens om weer te geven',
    editInstallation: 'Installatie bewerken',
    addNewInstallation: 'Een nieuwe installatie toevoegen',
    cancel: 'Annuleren',
    back: 'Terug',
    saving: 'Opslaan…',
    saveChanges: 'Wijzigingen opslaan',
    errLat: 'Breedtegraad moet tussen -90 en 90 liggen',
    errLon: 'Lengtegraad moet tussen -180 en 180 liggen',
    enterCode: 'Voer de activeringscode in.',
    codeInvalidOrUsed: 'De activeringscode is ongeldig of is al gebruikt.',
    verifyCode: 'Code verifiëren',
    addCodeIntro:
      'Om een nieuwe installatie toe te voegen heeft u een activeringscode nodig. Voer deze hieronder in.',
    planMaxStrings: 'Abonnement: {{name}} (max {{count}} strings)',
    installationNameOpt: 'Installatienaam (optioneel)',
    installationNameHelper: 'Een naam helpt installaties te onderscheiden.',
    addInstallationBtn: 'Installatie toevoegen',
  },
  da: {
    location: 'Placering',
    stringConfig: 'Strengkonfiguration',
    latitude: 'Breddegrad',
    longitude: 'Længdegrad',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Hældningsvinkel (°)',
    panelCount: 'Antal paneler',
    panelSizeM2: 'Panelareal (m²)',
    efficiencyPct: 'Virkningsgrad (%)',
    string: 'Streng',
    howManyStrings: 'Hvor mange strenge vil du konfigurere? (maks: {{count}})',
    configureLater:
      'Du kan konfigurere færre strenge end din plan tillader. De manglende kan du tilføje senere i kontoindstillingerne.',
    hourlyComparison: 'Timesammenligning (gns. Wh)',
    dailyComparison: 'Dagssammenligning (sum Wh)',
    forecast: 'Prognose',
    actual: 'Faktisk',
    noData: 'Ingen data at vise',
    editInstallation: 'Rediger anlæg',
    addNewInstallation: 'Tilføj et nyt anlæg',
    cancel: 'Annuller',
    back: 'Tilbage',
    saving: 'Gemmer…',
    saveChanges: 'Gem ændringer',
    errLat: 'Breddegrad skal være mellem -90 og 90',
    errLon: 'Længdegrad skal være mellem -180 og 180',
    enterCode: 'Indtast aktiveringskoden.',
    codeInvalidOrUsed: 'Aktiveringskoden er ugyldig eller er allerede brugt.',
    verifyCode: 'Bekræft kode',
    addCodeIntro:
      'For at tilføje et nyt anlæg skal du bruge en aktiveringskode. Indtast den nedenfor.',
    planMaxStrings: 'Plan: {{name}} (maks {{count}} strenge)',
    installationNameOpt: 'Anlægsnavn (valgfrit)',
    installationNameHelper: 'Et navn hjælper med at skelne anlæg.',
    addInstallationBtn: 'Tilføj anlæg',
  },
  sv: {
    location: 'Plats',
    stringConfig: 'Strängkonfiguration',
    latitude: 'Latitud',
    longitude: 'Longitud',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Lutningsvinkel (°)',
    panelCount: 'Antal paneler',
    panelSizeM2: 'Panelyta (m²)',
    efficiencyPct: 'Verkningsgrad (%)',
    string: 'Sträng',
    howManyStrings: 'Hur många strängar vill du konfigurera? (max: {{count}})',
    configureLater:
      'Du kan konfigurera färre strängar än din plan tillåter. De som saknas kan du lägga till senare i kontoinställningarna.',
    hourlyComparison: 'Timjämförelse (medel Wh)',
    dailyComparison: 'Dagjämförelse (summa Wh)',
    forecast: 'Prognos',
    actual: 'Faktisk',
    noData: 'Inga data att visa',
    editInstallation: 'Redigera anläggning',
    addNewInstallation: 'Lägg till en ny anläggning',
    cancel: 'Avbryt',
    back: 'Tillbaka',
    saving: 'Sparar…',
    saveChanges: 'Spara ändringar',
    errLat: 'Latitud måste vara mellan -90 och 90',
    errLon: 'Longitud måste vara mellan -180 och 180',
    enterCode: 'Ange aktiveringskoden.',
    codeInvalidOrUsed: 'Aktiveringskoden är ogiltig eller har redan använts.',
    verifyCode: 'Verifiera kod',
    addCodeIntro:
      'För att lägga till en ny anläggning behöver du en aktiveringskod. Ange den nedan.',
    planMaxStrings: 'Plan: {{name}} (max {{count}} strängar)',
    installationNameOpt: 'Anläggningsnamn (valfritt)',
    installationNameHelper: 'Ett namn hjälper till att skilja anläggningar åt.',
    addInstallationBtn: 'Lägg till anläggning',
  },
  fi: {
    location: 'Sijainti',
    stringConfig: 'Sarjojen kokoonpano',
    latitude: 'Leveysaste',
    longitude: 'Pituusaste',
    azimuthDeg: 'Atsimuutti (°)',
    tiltDeg: 'Kallistuskulma (°)',
    panelCount: 'Paneelien lukumäärä',
    panelSizeM2: 'Paneelin pinta-ala (m²)',
    efficiencyPct: 'Hyötysuhde (%)',
    string: 'Sarja',
    howManyStrings: 'Kuinka monta sarjaa haluat määrittää? (enint.: {{count}})',
    configureLater:
      'Voit määrittää vähemmän sarjoja kuin suunnitelmasi sallii. Puuttuvat voit lisätä myöhemmin tilin asetuksissa.',
    hourlyComparison: 'Tuntivertailu (ka. Wh)',
    dailyComparison: 'Päivävertailu (summa Wh)',
    forecast: 'Ennuste',
    actual: 'Toteutunut',
    noData: 'Ei näytettäviä tietoja',
    editInstallation: 'Muokkaa laitosta',
    addNewInstallation: 'Lisää uusi laitos',
    cancel: 'Peruuta',
    back: 'Takaisin',
    saving: 'Tallennetaan…',
    saveChanges: 'Tallenna muutokset',
    errLat: 'Leveysasteen on oltava välillä -90 ja 90',
    errLon: 'Pituusasteen on oltava välillä -180 ja 180',
    enterCode: 'Syötä aktivointikoodi.',
    codeInvalidOrUsed: 'Aktivointikoodi on virheellinen tai jo käytetty.',
    verifyCode: 'Vahvista koodi',
    addCodeIntro: 'Uuden laitoksen lisäämiseen tarvitset aktivointikoodin. Syötä se alle.',
    planMaxStrings: 'Suunnitelma: {{name}} (enint. {{count}} sarjaa)',
    installationNameOpt: 'Laitoksen nimi (valinnainen)',
    installationNameHelper: 'Nimi auttaa erottamaan laitokset.',
    addInstallationBtn: 'Lisää laitos',
  },
  cs: {
    location: 'Poloha',
    stringConfig: 'Konfigurace stringů',
    latitude: 'Zeměpisná šířka',
    longitude: 'Zeměpisná délka',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Úhel sklonu (°)',
    panelCount: 'Počet panelů',
    panelSizeM2: 'Plocha panelu (m²)',
    efficiencyPct: 'Účinnost (%)',
    string: 'String',
    howManyStrings: 'Kolik stringů chcete nakonfigurovat? (max: {{count}})',
    configureLater:
      'Můžete nakonfigurovat méně stringů, než povoluje váš tarif. Chybějící můžete přidat později v nastavení účtu.',
    hourlyComparison: 'Hodinové srovnání (prům. Wh)',
    dailyComparison: 'Denní srovnání (součet Wh)',
    forecast: 'Předpověď',
    actual: 'Skutečnost',
    noData: 'Žádná data k zobrazení',
    editInstallation: 'Upravit instalaci',
    addNewInstallation: 'Přidat novou instalaci',
    cancel: 'Zrušit',
    back: 'Zpět',
    saving: 'Ukládání…',
    saveChanges: 'Uložit změny',
    errLat: 'Zeměpisná šířka musí být mezi -90 a 90',
    errLon: 'Zeměpisná délka musí být mezi -180 a 180',
    enterCode: 'Zadejte aktivační kód.',
    codeInvalidOrUsed: 'Aktivační kód je neplatný nebo již byl použit.',
    verifyCode: 'Ověřit kód',
    addCodeIntro: 'Pro přidání nové instalace potřebujete aktivační kód. Zadejte jej níže.',
    planMaxStrings: 'Tarif: {{name}} (max {{count}} stringů)',
    installationNameOpt: 'Název instalace (volitelné)',
    installationNameHelper: 'Název pomáhá rozlišovat instalace.',
    addInstallationBtn: 'Přidat instalaci',
  },
  sk: {
    location: 'Poloha',
    stringConfig: 'Konfigurácia stringov',
    latitude: 'Zemepisná šírka',
    longitude: 'Zemepisná dĺžka',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Uhol sklonu (°)',
    panelCount: 'Počet panelov',
    panelSizeM2: 'Plocha panelu (m²)',
    efficiencyPct: 'Účinnosť (%)',
    string: 'String',
    howManyStrings: 'Koľko stringov chcete nakonfigurovať? (max: {{count}})',
    configureLater:
      'Môžete nakonfigurovať menej stringov, než povoľuje váš tarif. Chýbajúce môžete pridať neskôr v nastaveniach účtu.',
    hourlyComparison: 'Hodinové porovnanie (priem. Wh)',
    dailyComparison: 'Denné porovnanie (súčet Wh)',
    forecast: 'Predpoveď',
    actual: 'Skutočnosť',
    noData: 'Žiadne dáta na zobrazenie',
    editInstallation: 'Upraviť inštaláciu',
    addNewInstallation: 'Pridať novú inštaláciu',
    cancel: 'Zrušiť',
    back: 'Späť',
    saving: 'Ukladá sa…',
    saveChanges: 'Uložiť zmeny',
    errLat: 'Zemepisná šírka musí byť medzi -90 a 90',
    errLon: 'Zemepisná dĺžka musí byť medzi -180 a 180',
    enterCode: 'Zadajte aktivačný kód.',
    codeInvalidOrUsed: 'Aktivačný kód je neplatný alebo už bol použitý.',
    verifyCode: 'Overiť kód',
    addCodeIntro: 'Na pridanie novej inštalácie potrebujete aktivačný kód. Zadajte ho nižšie.',
    planMaxStrings: 'Tarif: {{name}} (max {{count}} stringov)',
    installationNameOpt: 'Názov inštalácie (voliteľné)',
    installationNameHelper: 'Názov pomáha rozlišovať inštalácie.',
    addInstallationBtn: 'Pridať inštaláciu',
  },
  sl: {
    location: 'Lokacija',
    stringConfig: 'Konfiguracija nizov',
    latitude: 'Zemljepisna širina',
    longitude: 'Zemljepisna dolžina',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Naklonski kot (°)',
    panelCount: 'Število panelov',
    panelSizeM2: 'Površina panela (m²)',
    efficiencyPct: 'Učinkovitost (%)',
    string: 'Niz',
    howManyStrings: 'Koliko nizov želite nastaviti? (največ: {{count}})',
    configureLater:
      'Nastavite lahko manj nizov, kot dovoljuje vaš paket. Manjkajoče lahko dodate pozneje v nastavitvah računa.',
    hourlyComparison: 'Urna primerjava (povp. Wh)',
    dailyComparison: 'Dnevna primerjava (vsota Wh)',
    forecast: 'Napoved',
    actual: 'Dejansko',
    noData: 'Ni podatkov za prikaz',
    editInstallation: 'Uredi napravo',
    addNewInstallation: 'Dodaj novo napravo',
    cancel: 'Prekliči',
    back: 'Nazaj',
    saving: 'Shranjevanje…',
    saveChanges: 'Shrani spremembe',
    errLat: 'Zemljepisna širina mora biti med -90 in 90',
    errLon: 'Zemljepisna dolžina mora biti med -180 in 180',
    enterCode: 'Vnesite aktivacijsko kodo.',
    codeInvalidOrUsed: 'Aktivacijska koda je neveljavna ali je že bila uporabljena.',
    verifyCode: 'Preveri kodo',
    addCodeIntro: 'Za dodajanje nove naprave potrebujete aktivacijsko kodo. Vnesite jo spodaj.',
    planMaxStrings: 'Paket: {{name}} (največ {{count}} nizov)',
    installationNameOpt: 'Ime naprave (neobvezno)',
    installationNameHelper: 'Ime pomaga razlikovati naprave.',
    addInstallationBtn: 'Dodaj napravo',
  },
  hr: {
    location: 'Lokacija',
    stringConfig: 'Konfiguracija nizova',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Kut nagiba (°)',
    panelCount: 'Broj panela',
    panelSizeM2: 'Površina panela (m²)',
    efficiencyPct: 'Učinkovitost (%)',
    string: 'Niz',
    howManyStrings: 'Koliko nizova želite konfigurirati? (najviše: {{count}})',
    configureLater:
      'Možete konfigurirati manje nizova nego što dopušta vaš plan. Nedostajuće možete dodati kasnije u postavkama računa.',
    hourlyComparison: 'Usporedba po satu (pros. Wh)',
    dailyComparison: 'Dnevna usporedba (zbroj Wh)',
    forecast: 'Prognoza',
    actual: 'Stvarno',
    noData: 'Nema podataka za prikaz',
    editInstallation: 'Uredi postrojenje',
    addNewInstallation: 'Dodaj novo postrojenje',
    cancel: 'Odustani',
    back: 'Natrag',
    saving: 'Spremanje…',
    saveChanges: 'Spremi promjene',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
    enterCode: 'Unesite aktivacijski kod.',
    codeInvalidOrUsed: 'Aktivacijski kod je neispravan ili je već iskorišten.',
    verifyCode: 'Provjeri kod',
    addCodeIntro:
      'Za dodavanje novog postrojenja potreban vam je aktivacijski kod. Unesite ga ispod.',
    planMaxStrings: 'Plan: {{name}} (najviše {{count}} nizova)',
    installationNameOpt: 'Naziv postrojenja (neobavezno)',
    installationNameHelper: 'Naziv pomaže u razlikovanju postrojenja.',
    addInstallationBtn: 'Dodaj postrojenje',
  },
  hu: {
    location: 'Helyszín',
    stringConfig: 'String konfiguráció',
    latitude: 'Szélességi fok',
    longitude: 'Hosszúsági fok',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Dőlésszög (°)',
    panelCount: 'Panelek száma',
    panelSizeM2: 'Panel területe (m²)',
    efficiencyPct: 'Hatásfok (%)',
    string: 'String',
    howManyStrings: 'Hány stringet szeretne beállítani? (max: {{count}})',
    configureLater:
      'Kevesebb stringet is beállíthat, mint amennyit a csomagja megenged. A hiányzókat később hozzáadhatja a fiókbeállításokban.',
    hourlyComparison: 'Óránkénti összehasonlítás (átl. Wh)',
    dailyComparison: 'Napi összehasonlítás (összeg Wh)',
    forecast: 'Előrejelzés',
    actual: 'Tényleges',
    noData: 'Nincs megjeleníthető adat',
    editInstallation: 'Telepítés szerkesztése',
    addNewInstallation: 'Új telepítés hozzáadása',
    cancel: 'Mégse',
    back: 'Vissza',
    saving: 'Mentés…',
    saveChanges: 'Változások mentése',
    errLat: 'A szélességi foknak -90 és 90 között kell lennie',
    errLon: 'A hosszúsági foknak -180 és 180 között kell lennie',
    enterCode: 'Adja meg az aktiválási kódot.',
    codeInvalidOrUsed: 'Az aktiválási kód érvénytelen, vagy már felhasználták.',
    verifyCode: 'Kód ellenőrzése',
    addCodeIntro: 'Új telepítés hozzáadásához aktiválási kódra van szüksége. Adja meg alább.',
    planMaxStrings: 'Csomag: {{name}} (max {{count}} string)',
    installationNameOpt: 'Telepítés neve (opcionális)',
    installationNameHelper: 'A név segít megkülönböztetni a telepítéseket.',
    addInstallationBtn: 'Telepítés hozzáadása',
  },
  ro: {
    location: 'Locație',
    stringConfig: 'Configurație string-uri',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Unghi de înclinare (°)',
    panelCount: 'Număr de panouri',
    panelSizeM2: 'Suprafața panoului (m²)',
    efficiencyPct: 'Eficiență (%)',
    string: 'String',
    howManyStrings: 'Câte string-uri doriți să configurați? (max: {{count}})',
    configureLater:
      'Puteți configura mai puține string-uri decât permite planul dvs. Pe cele lipsă le puteți adăuga mai târziu în setările contului.',
    hourlyComparison: 'Comparație orară (med. Wh)',
    dailyComparison: 'Comparație zilnică (sumă Wh)',
    forecast: 'Prognoză',
    actual: 'Real',
    noData: 'Nu există date de afișat',
    editInstallation: 'Editează instalația',
    addNewInstallation: 'Adaugă o nouă instalație',
    cancel: 'Anulează',
    back: 'Înapoi',
    saving: 'Se salvează…',
    saveChanges: 'Salvează modificările',
    errLat: 'Latitudinea trebuie să fie între -90 și 90',
    errLon: 'Longitudinea trebuie să fie între -180 și 180',
    enterCode: 'Introduceți codul de activare.',
    codeInvalidOrUsed: 'Codul de activare este nevalid sau a fost deja folosit.',
    verifyCode: 'Verifică codul',
    addCodeIntro:
      'Pentru a adăuga o nouă instalație aveți nevoie de un cod de activare. Introduceți-l mai jos.',
    planMaxStrings: 'Plan: {{name}} (max {{count}} string-uri)',
    installationNameOpt: 'Numele instalației (opțional)',
    installationNameHelper: 'Un nume ajută la distingerea instalațiilor.',
    addInstallationBtn: 'Adaugă instalație',
  },
  bg: {
    location: 'Местоположение',
    stringConfig: 'Конфигурация на стрингове',
    latitude: 'Географска ширина',
    longitude: 'Географска дължина',
    azimuthDeg: 'Азимут (°)',
    tiltDeg: 'Ъгъл на наклон (°)',
    panelCount: 'Брой панели',
    panelSizeM2: 'Площ на панела (m²)',
    efficiencyPct: 'Ефективност (%)',
    string: 'Стринг',
    howManyStrings: 'Колко стринга искате да конфигурирате? (макс.: {{count}})',
    configureLater:
      'Можете да конфигурирате по-малко стрингове, отколкото позволява вашият план. Липсващите можете да добавите по-късно в настройките на акаунта.',
    hourlyComparison: 'Почасово сравнение (ср. Wh)',
    dailyComparison: 'Дневно сравнение (сума Wh)',
    forecast: 'Прогноза',
    actual: 'Действително',
    noData: 'Няма данни за показване',
    editInstallation: 'Редактиране на инсталацията',
    addNewInstallation: 'Добавяне на нова инсталация',
    cancel: 'Отказ',
    back: 'Назад',
    saving: 'Запазване…',
    saveChanges: 'Запази промените',
    errLat: 'Географската ширина трябва да е между -90 и 90',
    errLon: 'Географската дължина трябва да е между -180 и 180',
    enterCode: 'Въведете кода за активиране.',
    codeInvalidOrUsed: 'Кодът за активиране е невалиден или вече е използван.',
    verifyCode: 'Провери кода',
    addCodeIntro:
      'За да добавите нова инсталация, ви е необходим код за активиране. Въведете го по-долу.',
    planMaxStrings: 'План: {{name}} (макс. {{count}} стринга)',
    installationNameOpt: 'Име на инсталацията (по избор)',
    installationNameHelper: 'Името помага за разграничаване на инсталациите.',
    addInstallationBtn: 'Добави инсталация',
  },
  el: {
    location: 'Τοποθεσία',
    stringConfig: 'Διαμόρφωση string',
    latitude: 'Γεωγραφικό πλάτος',
    longitude: 'Γεωγραφικό μήκος',
    azimuthDeg: 'Αζιμούθιο (°)',
    tiltDeg: 'Γωνία κλίσης (°)',
    panelCount: 'Αριθμός πάνελ',
    panelSizeM2: 'Επιφάνεια πάνελ (m²)',
    efficiencyPct: 'Απόδοση (%)',
    string: 'String',
    howManyStrings: 'Πόσα string θέλετε να διαμορφώσετε; (μέγ.: {{count}})',
    configureLater:
      'Μπορείτε να διαμορφώσετε λιγότερα string από όσα επιτρέπει το πρόγραμμά σας. Τα υπόλοιπα μπορείτε να τα προσθέσετε αργότερα στις ρυθμίσεις λογαριασμού.',
    hourlyComparison: 'Ωριαία σύγκριση (μέσος Wh)',
    dailyComparison: 'Ημερήσια σύγκριση (άθροισμα Wh)',
    forecast: 'Πρόβλεψη',
    actual: 'Πραγματικό',
    noData: 'Δεν υπάρχουν δεδομένα για εμφάνιση',
    editInstallation: 'Επεξεργασία εγκατάστασης',
    addNewInstallation: 'Προσθήκη νέας εγκατάστασης',
    cancel: 'Άκυρο',
    back: 'Πίσω',
    saving: 'Αποθήκευση…',
    saveChanges: 'Αποθήκευση αλλαγών',
    errLat: 'Το γεωγραφικό πλάτος πρέπει να είναι μεταξύ -90 και 90',
    errLon: 'Το γεωγραφικό μήκος πρέπει να είναι μεταξύ -180 και 180',
    enterCode: 'Εισαγάγετε τον κωδικό ενεργοποίησης.',
    codeInvalidOrUsed: 'Ο κωδικός ενεργοποίησης δεν είναι έγκυρος ή έχει ήδη χρησιμοποιηθεί.',
    verifyCode: 'Επαλήθευση κωδικού',
    addCodeIntro:
      'Για να προσθέσετε νέα εγκατάσταση χρειάζεστε κωδικό ενεργοποίησης. Εισαγάγετέ τον παρακάτω.',
    planMaxStrings: 'Πρόγραμμα: {{name}} (μέγ. {{count}} string)',
    installationNameOpt: 'Όνομα εγκατάστασης (προαιρετικό)',
    installationNameHelper: 'Ένα όνομα βοηθά στη διάκριση των εγκαταστάσεων.',
    addInstallationBtn: 'Προσθήκη εγκατάστασης',
  },
  et: {
    location: 'Asukoht',
    stringConfig: 'Stringide konfiguratsioon',
    latitude: 'Laiuskraad',
    longitude: 'Pikkuskraad',
    azimuthDeg: 'Asimuut (°)',
    tiltDeg: 'Kaldenurk (°)',
    panelCount: 'Paneelide arv',
    panelSizeM2: 'Paneeli pindala (m²)',
    efficiencyPct: 'Kasutegur (%)',
    string: 'String',
    howManyStrings: 'Mitu stringi soovite seadistada? (max: {{count}})',
    configureLater:
      'Saate seadistada vähem stringe, kui teie pakett lubab. Puuduvad saate lisada hiljem konto sätetes.',
    hourlyComparison: 'Tunnine võrdlus (keskm. Wh)',
    dailyComparison: 'Päevane võrdlus (summa Wh)',
    forecast: 'Prognoos',
    actual: 'Tegelik',
    noData: 'Pole andmeid kuvamiseks',
    editInstallation: 'Muuda paigaldist',
    addNewInstallation: 'Lisa uus paigaldis',
    cancel: 'Tühista',
    back: 'Tagasi',
    saving: 'Salvestamine…',
    saveChanges: 'Salvesta muudatused',
    errLat: 'Laiuskraad peab olema vahemikus -90 kuni 90',
    errLon: 'Pikkuskraad peab olema vahemikus -180 kuni 180',
    enterCode: 'Sisestage aktiveerimiskood.',
    codeInvalidOrUsed: 'Aktiveerimiskood on vigane või juba kasutatud.',
    verifyCode: 'Kontrolli koodi',
    addCodeIntro: 'Uue paigaldise lisamiseks vajate aktiveerimiskoodi. Sisestage see allpool.',
    planMaxStrings: 'Pakett: {{name}} (max {{count}} stringi)',
    installationNameOpt: 'Paigaldise nimi (valikuline)',
    installationNameHelper: 'Nimi aitab paigaldisi eristada.',
    addInstallationBtn: 'Lisa paigaldis',
  },
  lv: {
    location: 'Atrašanās vieta',
    stringConfig: 'Virkņu konfigurācija',
    latitude: 'Ģeogrāfiskais platums',
    longitude: 'Ģeogrāfiskais garums',
    azimuthDeg: 'Azimuts (°)',
    tiltDeg: 'Slīpuma leņķis (°)',
    panelCount: 'Paneļu skaits',
    panelSizeM2: 'Paneļa laukums (m²)',
    efficiencyPct: 'Efektivitāte (%)',
    string: 'Virkne',
    howManyStrings: 'Cik virkņu vēlaties konfigurēt? (maks.: {{count}})',
    configureLater:
      'Varat konfigurēt mazāk virkņu, nekā atļauj jūsu plāns. Trūkstošās varat pievienot vēlāk konta iestatījumos.',
    hourlyComparison: 'Stundu salīdzinājums (vid. Wh)',
    dailyComparison: 'Dienas salīdzinājums (summa Wh)',
    forecast: 'Prognoze',
    actual: 'Faktiskā',
    noData: 'Nav datu, ko parādīt',
    editInstallation: 'Rediģēt iekārtu',
    addNewInstallation: 'Pievienot jaunu iekārtu',
    cancel: 'Atcelt',
    back: 'Atpakaļ',
    saving: 'Saglabā…',
    saveChanges: 'Saglabāt izmaiņas',
    errLat: 'Ģeogrāfiskajam platumam jābūt no -90 līdz 90',
    errLon: 'Ģeogrāfiskajam garumam jābūt no -180 līdz 180',
    enterCode: 'Ievadiet aktivizācijas kodu.',
    codeInvalidOrUsed: 'Aktivizācijas kods ir nederīgs vai jau izmantots.',
    verifyCode: 'Pārbaudīt kodu',
    addCodeIntro:
      'Lai pievienotu jaunu iekārtu, jums nepieciešams aktivizācijas kods. Ievadiet to zemāk.',
    planMaxStrings: 'Plāns: {{name}} (maks. {{count}} virknes)',
    installationNameOpt: 'Iekārtas nosaukums (neobligāti)',
    installationNameHelper: 'Nosaukums palīdz atšķirt iekārtas.',
    addInstallationBtn: 'Pievienot iekārtu',
  },
  lt: {
    location: 'Vieta',
    stringConfig: 'Eilučių konfigūracija',
    latitude: 'Platuma',
    longitude: 'Ilguma',
    azimuthDeg: 'Azimutas (°)',
    tiltDeg: 'Pasvirimo kampas (°)',
    panelCount: 'Plokščių skaičius',
    panelSizeM2: 'Plokštės plotas (m²)',
    efficiencyPct: 'Naudingumas (%)',
    string: 'Eilutė',
    howManyStrings: 'Kiek eilučių norite sukonfigūruoti? (maks.: {{count}})',
    configureLater:
      'Galite sukonfigūruoti mažiau eilučių, nei leidžia jūsų planas. Trūkstamas galite pridėti vėliau paskyros nustatymuose.',
    hourlyComparison: 'Valandos palyginimas (vid. Wh)',
    dailyComparison: 'Dienos palyginimas (suma Wh)',
    forecast: 'Prognozė',
    actual: 'Faktinė',
    noData: 'Nėra duomenų rodymui',
    editInstallation: 'Redaguoti įrenginį',
    addNewInstallation: 'Pridėti naują įrenginį',
    cancel: 'Atšaukti',
    back: 'Atgal',
    saving: 'Įrašoma…',
    saveChanges: 'Įrašyti pakeitimus',
    errLat: 'Platuma turi būti nuo -90 iki 90',
    errLon: 'Ilguma turi būti nuo -180 iki 180',
    enterCode: 'Įveskite aktyvavimo kodą.',
    codeInvalidOrUsed: 'Aktyvavimo kodas neteisingas arba jau panaudotas.',
    verifyCode: 'Patikrinti kodą',
    addCodeIntro:
      'Norėdami pridėti naują įrenginį, jums reikia aktyvavimo kodo. Įveskite jį žemiau.',
    planMaxStrings: 'Planas: {{name}} (maks. {{count}} eilutės)',
    installationNameOpt: 'Įrenginio pavadinimas (neprivaloma)',
    installationNameHelper: 'Pavadinimas padeda atskirti įrenginius.',
    addInstallationBtn: 'Pridėti įrenginį',
  },
  mt: {
    location: 'Post',
    stringConfig: 'Konfigurazzjoni tal-istrings',
    latitude: 'Latitudni',
    longitude: 'Lonġitudni',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: "Angolu ta' inklinazzjoni (°)",
    panelCount: "Numru ta' pannelli",
    panelSizeM2: 'Erja tal-pannell (m²)',
    efficiencyPct: 'Effiċjenza (%)',
    string: 'String',
    howManyStrings: 'Kemm-il string trid tikkonfigura? (massimu: {{count}})',
    configureLater:
      "Tista' tikkonfigura inqas strings milli jippermetti l-pjan tiegħek. Dawk neqsin tista' żżidhom aktar tard fis-settings tal-kont.",
    hourlyComparison: 'Paragun fis-siegħa (medja Wh)',
    dailyComparison: "Paragun ta' kuljum (somma Wh)",
    forecast: 'Previżjoni',
    actual: 'Reali',
    noData: "L-ebda dejta x'turi",
    editInstallation: 'Editja l-installazzjoni',
    addNewInstallation: 'Żid installazzjoni ġdida',
    cancel: 'Ikkanċella',
    back: 'Lura',
    saving: 'Qed jissejvja…',
    saveChanges: 'Issejvja l-bidliet',
    errLat: 'Il-latitudni trid tkun bejn -90 u 90',
    errLon: 'Il-lonġitudni trid tkun bejn -180 u 180',
    enterCode: 'Daħħal il-kodiċi tal-attivazzjoni.',
    codeInvalidOrUsed: 'Il-kodiċi tal-attivazzjoni huwa invalidu jew diġà ntuża.',
    verifyCode: 'Ivverifika l-kodiċi',
    addCodeIntro:
      'Biex iżżid installazzjoni ġdida għandek bżonn kodiċi tal-attivazzjoni. Daħħlu hawn taħt.',
    planMaxStrings: 'Pjan: {{name}} (massimu {{count}} strings)',
    installationNameOpt: 'Isem tal-installazzjoni (mhux obbligatorju)',
    installationNameHelper: 'Isem jgħin biex tiddistingwi l-installazzjonijiet.',
    addInstallationBtn: 'Żid installazzjoni',
  },
  ga: {
    location: 'Suíomh',
    stringConfig: 'Cumraíocht slabhraí',
    latitude: 'Domhanleithead',
    longitude: 'Domhanfhad',
    azimuthDeg: 'Asaimit (°)',
    tiltDeg: 'Uillinn chlaonta (°)',
    panelCount: 'Líon na bpainéal',
    panelSizeM2: 'Achar an phainéil (m²)',
    efficiencyPct: 'Éifeachtúlacht (%)',
    string: 'Slabhra',
    howManyStrings: 'Cé mhéad slabhra atá tú ag iarraidh a chumrú? (uasmhéid: {{count}})',
    configureLater:
      'Is féidir leat níos lú slabhraí a chumrú ná mar a cheadaíonn do phlean. Is féidir na cinn atá in easnamh a chur leis níos déanaí i socruithe an chuntais.',
    hourlyComparison: 'Comparáid uaire (meán Wh)',
    dailyComparison: 'Comparáid laethúil (suim Wh)',
    forecast: 'Réamhaisnéis',
    actual: 'Iarbhír',
    noData: 'Gan sonraí le taispeáint',
    editInstallation: 'Cuir an tsuiteáil in eagar',
    addNewInstallation: 'Cuir suiteáil nua leis',
    cancel: 'Cealaigh',
    back: 'Ar ais',
    saving: 'Á shábháil…',
    saveChanges: 'Sábháil athruithe',
    errLat: 'Caithfidh an domhanleithead a bheith idir -90 agus 90',
    errLon: 'Caithfidh an domhanfhad a bheith idir -180 agus 180',
    enterCode: 'Cuir isteach an cód gníomhachtaithe.',
    codeInvalidOrUsed: 'Tá an cód gníomhachtaithe neamhbhailí nó úsáidte cheana.',
    verifyCode: 'Fíoraigh an cód',
    addCodeIntro:
      'Chun suiteáil nua a chur leis, teastaíonn cód gníomhachtaithe uait. Cuir isteach thíos é.',
    planMaxStrings: 'Plean: {{name}} (uasmhéid {{count}} slabhra)',
    installationNameOpt: 'Ainm na suiteála (roghnach)',
    installationNameHelper: 'Cabhraíonn ainm le suiteálacha a aithint óna chéile.',
    addInstallationBtn: 'Cuir suiteáil leis',
  },
  no: {
    location: 'Plassering',
    stringConfig: 'Strengkonfigurasjon',
    latitude: 'Breddegrad',
    longitude: 'Lengdegrad',
    azimuthDeg: 'Asimut (°)',
    tiltDeg: 'Helningsvinkel (°)',
    panelCount: 'Antall paneler',
    panelSizeM2: 'Panelareal (m²)',
    efficiencyPct: 'Virkningsgrad (%)',
    string: 'Streng',
    howManyStrings: 'Hvor mange strenger vil du konfigurere? (maks: {{count}})',
    configureLater:
      'Du kan konfigurere færre strenger enn planen din tillater. De manglende kan du legge til senere i kontoinnstillingene.',
    hourlyComparison: 'Timesammenligning (gj.snitt Wh)',
    dailyComparison: 'Dagssammenligning (sum Wh)',
    forecast: 'Prognose',
    actual: 'Faktisk',
    noData: 'Ingen data å vise',
    editInstallation: 'Rediger anlegg',
    addNewInstallation: 'Legg til et nytt anlegg',
    cancel: 'Avbryt',
    back: 'Tilbake',
    saving: 'Lagrer…',
    saveChanges: 'Lagre endringer',
    errLat: 'Breddegrad må være mellom -90 og 90',
    errLon: 'Lengdegrad må være mellom -180 og 180',
    enterCode: 'Skriv inn aktiveringskoden.',
    codeInvalidOrUsed: 'Aktiveringskoden er ugyldig eller allerede brukt.',
    verifyCode: 'Bekreft kode',
    addCodeIntro:
      'For å legge til et nytt anlegg trenger du en aktiveringskode. Skriv den inn nedenfor.',
    planMaxStrings: 'Plan: {{name}} (maks {{count}} strenger)',
    installationNameOpt: 'Anleggsnavn (valgfritt)',
    installationNameHelper: 'Et navn hjelper med å skille anlegg.',
    addInstallationBtn: 'Legg til anlegg',
  },
  is: {
    location: 'Staðsetning',
    stringConfig: 'Strengjastilling',
    latitude: 'Breiddargráða',
    longitude: 'Lengdargráða',
    azimuthDeg: 'Áttarhorn (°)',
    tiltDeg: 'Hallahorn (°)',
    panelCount: 'Fjöldi spjalda',
    panelSizeM2: 'Flatarmál spjalds (m²)',
    efficiencyPct: 'Nýtni (%)',
    string: 'Strengur',
    howManyStrings: 'Hversu marga strengi viltu stilla? (hámark: {{count}})',
    configureLater:
      'Þú getur stillt færri strengi en áskriftin þín leyfir. Þá sem vantar geturðu bætt við síðar í aðgangsstillingum.',
    hourlyComparison: 'Klukkustundasamanburður (meðaltal Wh)',
    dailyComparison: 'Daglegur samanburður (samtals Wh)',
    forecast: 'Spá',
    actual: 'Raunveruleiki',
    noData: 'Engin gögn til að sýna',
    editInstallation: 'Breyta uppsetningu',
    addNewInstallation: 'Bæta við nýrri uppsetningu',
    cancel: 'Hætta við',
    back: 'Til baka',
    saving: 'Vista…',
    saveChanges: 'Vista breytingar',
    errLat: 'Breiddargráða verður að vera á milli -90 og 90',
    errLon: 'Lengdargráða verður að vera á milli -180 og 180',
    enterCode: 'Sláðu inn virkjunarkóðann.',
    codeInvalidOrUsed: 'Virkjunarkóðinn er ógildur eða þegar notaður.',
    verifyCode: 'Staðfesta kóða',
    addCodeIntro:
      'Til að bæta við nýrri uppsetningu þarftu virkjunarkóða. Sláðu hann inn hér að neðan.',
    planMaxStrings: 'Áskrift: {{name}} (hámark {{count}} strengir)',
    installationNameOpt: 'Heiti uppsetningar (valfrjálst)',
    installationNameHelper: 'Heiti hjálpar til við að greina uppsetningar í sundur.',
    addInstallationBtn: 'Bæta við uppsetningu',
  },
  uk: {
    location: 'Розташування',
    stringConfig: 'Конфігурація стрінгів',
    latitude: 'Широта',
    longitude: 'Довгота',
    azimuthDeg: 'Азимут (°)',
    tiltDeg: 'Кут нахилу (°)',
    panelCount: 'Кількість панелей',
    panelSizeM2: 'Площа панелі (m²)',
    efficiencyPct: 'Ефективність (%)',
    string: 'Стрінг',
    howManyStrings: 'Скільки стрінгів ви хочете налаштувати? (макс.: {{count}})',
    configureLater:
      'Ви можете налаштувати менше стрінгів, ніж дозволяє ваш план. Відсутні можна додати пізніше в налаштуваннях облікового запису.',
    hourlyComparison: 'Погодинне порівняння (сер. Wh)',
    dailyComparison: 'Денне порівняння (сума Wh)',
    forecast: 'Прогноз',
    actual: 'Фактично',
    noData: 'Немає даних для відображення',
    editInstallation: 'Редагувати установку',
    addNewInstallation: 'Додати нову установку',
    cancel: 'Скасувати',
    back: 'Назад',
    saving: 'Збереження…',
    saveChanges: 'Зберегти зміни',
    errLat: 'Широта має бути від -90 до 90',
    errLon: 'Довгота має бути від -180 до 180',
    enterCode: 'Введіть код активації.',
    codeInvalidOrUsed: 'Код активації недійсний або вже використаний.',
    verifyCode: 'Перевірити код',
    addCodeIntro: 'Щоб додати нову установку, вам потрібен код активації. Введіть його нижче.',
    planMaxStrings: 'План: {{name}} (макс. {{count}} стрінгів)',
    installationNameOpt: 'Назва установки (необов’язково)',
    installationNameHelper: 'Назва допомагає розрізняти установки.',
    addInstallationBtn: 'Додати установку',
  },
  sr: {
    location: 'Lokacija',
    stringConfig: 'Konfiguracija nizova',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Ugao nagiba (°)',
    panelCount: 'Broj panela',
    panelSizeM2: 'Površina panela (m²)',
    efficiencyPct: 'Efikasnost (%)',
    string: 'Niz',
    howManyStrings: 'Koliko nizova želite da konfigurišete? (najviše: {{count}})',
    configureLater:
      'Možete konfigurisati manje nizova nego što vaš plan dozvoljava. Nedostajuće možete dodati kasnije u podešavanjima naloga.',
    hourlyComparison: 'Poređenje po satu (pros. Wh)',
    dailyComparison: 'Dnevno poređenje (zbir Wh)',
    forecast: 'Prognoza',
    actual: 'Stvarno',
    noData: 'Nema podataka za prikaz',
    editInstallation: 'Izmeni postrojenje',
    addNewInstallation: 'Dodaj novo postrojenje',
    cancel: 'Otkaži',
    back: 'Nazad',
    saving: 'Čuvanje…',
    saveChanges: 'Sačuvaj izmene',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
    enterCode: 'Unesite aktivacioni kod.',
    codeInvalidOrUsed: 'Aktivacioni kod je nevažeći ili je već iskorišćen.',
    verifyCode: 'Proveri kod',
    addCodeIntro:
      'Za dodavanje novog postrojenja potreban vam je aktivacioni kod. Unesite ga ispod.',
    planMaxStrings: 'Plan: {{name}} (najviše {{count}} nizova)',
    installationNameOpt: 'Naziv postrojenja (opciono)',
    installationNameHelper: 'Naziv pomaže u razlikovanju postrojenja.',
    addInstallationBtn: 'Dodaj postrojenje',
  },
  sq: {
    location: 'Vendndodhja',
    stringConfig: 'Konfigurimi i vargjeve',
    latitude: 'Gjerësia gjeografike',
    longitude: 'Gjatësia gjeografike',
    azimuthDeg: 'Azimuti (°)',
    tiltDeg: 'Këndi i pjerrësisë (°)',
    panelCount: 'Numri i paneleve',
    panelSizeM2: 'Sipërfaqja e panelit (m²)',
    efficiencyPct: 'Efikasiteti (%)',
    string: 'Varg',
    howManyStrings: 'Sa vargje doni të konfiguroni? (maks.: {{count}})',
    configureLater:
      "Mund të konfiguroni më pak vargje sesa lejon plani juaj. Ato që mungojnë mund t'i shtoni më vonë në cilësimet e llogarisë.",
    hourlyComparison: 'Krahasim orar (mes. Wh)',
    dailyComparison: 'Krahasim ditor (shuma Wh)',
    forecast: 'Parashikim',
    actual: 'Reale',
    noData: 'Nuk ka të dhëna për të shfaqur',
    editInstallation: 'Modifiko instalimin',
    addNewInstallation: 'Shto një instalim të ri',
    cancel: 'Anulo',
    back: 'Mbrapa',
    saving: 'Po ruhet…',
    saveChanges: 'Ruaj ndryshimet',
    errLat: 'Gjerësia gjeografike duhet të jetë midis -90 dhe 90',
    errLon: 'Gjatësia gjeografike duhet të jetë midis -180 dhe 180',
    enterCode: 'Vendosni kodin e aktivizimit.',
    codeInvalidOrUsed: 'Kodi i aktivizimit është i pavlefshëm ose është përdorur tashmë.',
    verifyCode: 'Verifiko kodin',
    addCodeIntro:
      'Për të shtuar një instalim të ri ju nevojitet një kod aktivizimi. Vendoseni më poshtë.',
    planMaxStrings: 'Plani: {{name}} (maks. {{count}} vargje)',
    installationNameOpt: 'Emri i instalimit (opsionale)',
    installationNameHelper: 'Një emër ndihmon në dallimin e instalimeve.',
    addInstallationBtn: 'Shto instalim',
  },
  mk: {
    location: 'Локација',
    stringConfig: 'Конфигурација на стрингови',
    latitude: 'Географска ширина',
    longitude: 'Географска должина',
    azimuthDeg: 'Азимут (°)',
    tiltDeg: 'Агол на наклон (°)',
    panelCount: 'Број на панели',
    panelSizeM2: 'Површина на панелот (m²)',
    efficiencyPct: 'Ефикасност (%)',
    string: 'Стринг',
    howManyStrings: 'Колку стрингови сакате да конфигурирате? (макс.: {{count}})',
    configureLater:
      'Можете да конфигурирате помалку стрингови отколку што дозволува вашиот план. Тие што недостасуваат можете да ги додадете подоцна во поставките на сметката.',
    hourlyComparison: 'Споредба по час (просек Wh)',
    dailyComparison: 'Дневна споредба (збир Wh)',
    forecast: 'Прогноза',
    actual: 'Реално',
    noData: 'Нема податоци за прикажување',
    editInstallation: 'Уреди инсталација',
    addNewInstallation: 'Додади нова инсталација',
    cancel: 'Откажи',
    back: 'Назад',
    saving: 'Зачувување…',
    saveChanges: 'Зачувај промени',
    errLat: 'Географската ширина мора да биде помеѓу -90 и 90',
    errLon: 'Географската должина мора да биде помеѓу -180 и 180',
    enterCode: 'Внесете го кодот за активирање.',
    codeInvalidOrUsed: 'Кодот за активирање е невалиден или веќе е искористен.',
    verifyCode: 'Провери код',
    addCodeIntro: 'За да додадете нова инсталација ви треба код за активирање. Внесете го подолу.',
    planMaxStrings: 'План: {{name}} (макс. {{count}} стрингови)',
    installationNameOpt: 'Име на инсталацијата (опционално)',
    installationNameHelper: 'Името помага во разликување на инсталациите.',
    addInstallationBtn: 'Додади инсталација',
  },
  bs: {
    location: 'Lokacija',
    stringConfig: 'Konfiguracija nizova',
    latitude: 'Geografska širina',
    longitude: 'Geografska dužina',
    azimuthDeg: 'Azimut (°)',
    tiltDeg: 'Ugao nagiba (°)',
    panelCount: 'Broj panela',
    panelSizeM2: 'Površina panela (m²)',
    efficiencyPct: 'Efikasnost (%)',
    string: 'Niz',
    howManyStrings: 'Koliko nizova želite konfigurirati? (najviše: {{count}})',
    configureLater:
      'Možete konfigurirati manje nizova nego što vaš plan dozvoljava. Nedostajuće možete dodati kasnije u postavkama računa.',
    hourlyComparison: 'Poređenje po satu (pros. Wh)',
    dailyComparison: 'Dnevno poređenje (zbir Wh)',
    forecast: 'Prognoza',
    actual: 'Stvarno',
    noData: 'Nema podataka za prikaz',
    editInstallation: 'Uredi postrojenje',
    addNewInstallation: 'Dodaj novo postrojenje',
    cancel: 'Otkaži',
    back: 'Nazad',
    saving: 'Spremanje…',
    saveChanges: 'Spremi izmjene',
    errLat: 'Geografska širina mora biti između -90 i 90',
    errLon: 'Geografska dužina mora biti između -180 i 180',
    enterCode: 'Unesite aktivacijski kod.',
    codeInvalidOrUsed: 'Aktivacijski kod je nevažeći ili je već iskorišten.',
    verifyCode: 'Provjeri kod',
    addCodeIntro:
      'Za dodavanje novog postrojenja potreban vam je aktivacijski kod. Unesite ga ispod.',
    planMaxStrings: 'Plan: {{name}} (najviše {{count}} nizova)',
    installationNameOpt: 'Naziv postrojenja (opcionalno)',
    installationNameHelper: 'Naziv pomaže u razlikovanju postrojenja.',
    addInstallationBtn: 'Dodaj postrojenje',
  },
};

/** Namespace 'components' — dodatkowe klucze (LocationPicker, RegenerateApiKeyModal). */
const componentsTr2: Record<string, Dict> = {
  en: {
    locNotFound: 'Location not found',
    locSearching: 'Searching…',
    locSearch: 'Search',
    locSearchPlaceholder: 'Enter an address, e.g. Warsaw, Marszałkowska 1',
    locClickHint: 'Click on the map to pick a location or enter an address above.',
    regenTitle: 'Regenerate API key',
    regenWarningTitle: 'Warning!',
    regenWarning:
      'The old API key will stop working immediately after the new one is generated. Make sure you update the key in all applications that use it.',
    regenerating: 'Generating…',
    regenBtn: 'Regenerate key',
    regenNewTitle: 'New API key generated!',
    regenNewBody: 'The key is available in the client portal.',
    newApiKey: 'New API key',
    copied: 'Copied!',
    copy: 'Copy',
    close: 'Close',
  },
  pl: {
    locNotFound: 'Nie znaleziono lokalizacji',
    locSearching: 'Szukam…',
    locSearch: 'Szukaj',
    locSearchPlaceholder: 'Wpisz adres, np. Warszawa, ul. Marszałkowska 1',
    locClickHint: 'Kliknij na mapie, aby wybrać lokalizację, lub wpisz adres powyżej.',
    regenTitle: 'Regeneruj klucz API',
    regenWarningTitle: 'Uwaga!',
    regenWarning:
      'Stary klucz API przestanie działać natychmiast po wygenerowaniu nowego. Upewnij się, że zaktualizujesz klucz we wszystkich aplikacjach, które z niego korzystają.',
    regenerating: 'Generowanie…',
    regenBtn: 'Regeneruj klucz',
    regenNewTitle: 'Nowy klucz API wygenerowany!',
    regenNewBody: 'Klucz jest dostępny w panelu klienta.',
    newApiKey: 'Nowy klucz API',
    copied: 'Skopiowano!',
    copy: 'Skopiuj',
    close: 'Zamknij',
  },
  de: {
    locNotFound: 'Standort nicht gefunden',
    locSearching: 'Suche…',
    locSearch: 'Suchen',
    locSearchPlaceholder: 'Adresse eingeben, z. B. Berlin, Unter den Linden 1',
    locClickHint:
      'Klicken Sie auf die Karte, um einen Standort zu wählen, oder geben Sie oben eine Adresse ein.',
    regenTitle: 'API-Schlüssel neu generieren',
    regenWarningTitle: 'Achtung!',
    regenWarning:
      'Der alte API-Schlüssel funktioniert sofort nach Generierung des neuen nicht mehr. Stellen Sie sicher, dass Sie den Schlüssel in allen Anwendungen aktualisieren, die ihn verwenden.',
    regenerating: 'Wird generiert…',
    regenBtn: 'Schlüssel neu generieren',
    regenNewTitle: 'Neuer API-Schlüssel generiert!',
    regenNewBody: 'Der Schlüssel ist im Kundenportal verfügbar.',
    newApiKey: 'Neuer API-Schlüssel',
    copied: 'Kopiert!',
    copy: 'Kopieren',
    close: 'Schließen',
  },
  fr: {
    locNotFound: 'Emplacement introuvable',
    locSearching: 'Recherche…',
    locSearch: 'Rechercher',
    locSearchPlaceholder: 'Saisissez une adresse, p. ex. Paris, rue de Rivoli 1',
    locClickHint:
      'Cliquez sur la carte pour choisir un emplacement ou saisissez une adresse ci-dessus.',
    regenTitle: 'Régénérer la clé API',
    regenWarningTitle: 'Attention !',
    regenWarning:
      "L'ancienne clé API cessera de fonctionner immédiatement après la génération de la nouvelle. Assurez-vous de mettre à jour la clé dans toutes les applications qui l'utilisent.",
    regenerating: 'Génération…',
    regenBtn: 'Régénérer la clé',
    regenNewTitle: 'Nouvelle clé API générée !',
    regenNewBody: 'La clé est disponible dans le portail client.',
    newApiKey: 'Nouvelle clé API',
    copied: 'Copié !',
    copy: 'Copier',
    close: 'Fermer',
  },
  es: {
    locNotFound: 'Ubicación no encontrada',
    locSearching: 'Buscando…',
    locSearch: 'Buscar',
    locSearchPlaceholder: 'Introduzca una dirección, p. ej. Madrid, Gran Vía 1',
    locClickHint:
      'Haga clic en el mapa para elegir una ubicación o introduzca una dirección arriba.',
    regenTitle: 'Regenerar clave API',
    regenWarningTitle: '¡Atención!',
    regenWarning:
      'La clave API antigua dejará de funcionar inmediatamente después de generar la nueva. Asegúrese de actualizar la clave en todas las aplicaciones que la utilizan.',
    regenerating: 'Generando…',
    regenBtn: 'Regenerar clave',
    regenNewTitle: '¡Nueva clave API generada!',
    regenNewBody: 'La clave está disponible en el portal del cliente.',
    newApiKey: 'Nueva clave API',
    copied: '¡Copiado!',
    copy: 'Copiar',
    close: 'Cerrar',
  },
  it: {
    locNotFound: 'Posizione non trovata',
    locSearching: 'Ricerca…',
    locSearch: 'Cerca',
    locSearchPlaceholder: 'Inserisci un indirizzo, es. Roma, via del Corso 1',
    locClickHint: 'Clicca sulla mappa per scegliere una posizione o inserisci un indirizzo sopra.',
    regenTitle: 'Rigenera chiave API',
    regenWarningTitle: 'Attenzione!',
    regenWarning:
      'La vecchia chiave API smetterà di funzionare immediatamente dopo la generazione di quella nuova. Assicurati di aggiornare la chiave in tutte le applicazioni che la utilizzano.',
    regenerating: 'Generazione…',
    regenBtn: 'Rigenera chiave',
    regenNewTitle: 'Nuova chiave API generata!',
    regenNewBody: 'La chiave è disponibile nel portale cliente.',
    newApiKey: 'Nuova chiave API',
    copied: 'Copiato!',
    copy: 'Copia',
    close: 'Chiudi',
  },
  pt: {
    locNotFound: 'Localização não encontrada',
    locSearching: 'A procurar…',
    locSearch: 'Procurar',
    locSearchPlaceholder: 'Introduza um endereço, p. ex. Lisboa, Rua Augusta 1',
    locClickHint: 'Clique no mapa para escolher uma localização ou introduza um endereço acima.',
    regenTitle: 'Regenerar chave API',
    regenWarningTitle: 'Atenção!',
    regenWarning:
      'A chave API antiga deixará de funcionar imediatamente após a geração da nova. Certifique-se de que atualiza a chave em todas as aplicações que a utilizam.',
    regenerating: 'A gerar…',
    regenBtn: 'Regenerar chave',
    regenNewTitle: 'Nova chave API gerada!',
    regenNewBody: 'A chave está disponível no portal do cliente.',
    newApiKey: 'Nova chave API',
    copied: 'Copiado!',
    copy: 'Copiar',
    close: 'Fechar',
  },
  nl: {
    locNotFound: 'Locatie niet gevonden',
    locSearching: 'Zoeken…',
    locSearch: 'Zoeken',
    locSearchPlaceholder: 'Voer een adres in, bijv. Amsterdam, Damrak 1',
    locClickHint: 'Klik op de kaart om een locatie te kiezen of voer hierboven een adres in.',
    regenTitle: 'API-sleutel opnieuw genereren',
    regenWarningTitle: 'Let op!',
    regenWarning:
      'De oude API-sleutel werkt onmiddellijk niet meer nadat de nieuwe is gegenereerd. Zorg ervoor dat u de sleutel bijwerkt in alle applicaties die hem gebruiken.',
    regenerating: 'Genereren…',
    regenBtn: 'Sleutel opnieuw genereren',
    regenNewTitle: 'Nieuwe API-sleutel gegenereerd!',
    regenNewBody: 'De sleutel is beschikbaar in het klantportaal.',
    newApiKey: 'Nieuwe API-sleutel',
    copied: 'Gekopieerd!',
    copy: 'Kopiëren',
    close: 'Sluiten',
  },
  da: {
    locNotFound: 'Placering ikke fundet',
    locSearching: 'Søger…',
    locSearch: 'Søg',
    locSearchPlaceholder: 'Indtast en adresse, f.eks. København, Strøget 1',
    locClickHint: 'Klik på kortet for at vælge en placering, eller indtast en adresse ovenfor.',
    regenTitle: 'Regenerer API-nøgle',
    regenWarningTitle: 'Advarsel!',
    regenWarning:
      'Den gamle API-nøgle holder op med at virke, så snart den nye er genereret. Sørg for at opdatere nøglen i alle applikationer, der bruger den.',
    regenerating: 'Genererer…',
    regenBtn: 'Regenerer nøgle',
    regenNewTitle: 'Ny API-nøgle genereret!',
    regenNewBody: 'Nøglen er tilgængelig i kundeportalen.',
    newApiKey: 'Ny API-nøgle',
    copied: 'Kopieret!',
    copy: 'Kopier',
    close: 'Luk',
  },
  sv: {
    locNotFound: 'Plats hittades inte',
    locSearching: 'Söker…',
    locSearch: 'Sök',
    locSearchPlaceholder: 'Ange en adress, t.ex. Stockholm, Drottninggatan 1',
    locClickHint: 'Klicka på kartan för att välja en plats eller ange en adress ovan.',
    regenTitle: 'Återskapa API-nyckel',
    regenWarningTitle: 'Varning!',
    regenWarning:
      'Den gamla API-nyckeln slutar fungera omedelbart efter att den nya har genererats. Se till att du uppdaterar nyckeln i alla applikationer som använder den.',
    regenerating: 'Genererar…',
    regenBtn: 'Återskapa nyckel',
    regenNewTitle: 'Ny API-nyckel genererad!',
    regenNewBody: 'Nyckeln finns tillgänglig i kundportalen.',
    newApiKey: 'Ny API-nyckel',
    copied: 'Kopierad!',
    copy: 'Kopiera',
    close: 'Stäng',
  },
  fi: {
    locNotFound: 'Sijaintia ei löytynyt',
    locSearching: 'Haetaan…',
    locSearch: 'Hae',
    locSearchPlaceholder: 'Anna osoite, esim. Helsinki, Mannerheimintie 1',
    locClickHint: 'Napsauta karttaa valitaksesi sijainnin tai anna osoite yllä.',
    regenTitle: 'Luo API-avain uudelleen',
    regenWarningTitle: 'Huomio!',
    regenWarning:
      'Vanha API-avain lakkaa toimimasta heti uuden luomisen jälkeen. Varmista, että päivität avaimen kaikkiin sitä käyttäviin sovelluksiin.',
    regenerating: 'Luodaan…',
    regenBtn: 'Luo avain uudelleen',
    regenNewTitle: 'Uusi API-avain luotu!',
    regenNewBody: 'Avain on saatavilla asiakasportaalissa.',
    newApiKey: 'Uusi API-avain',
    copied: 'Kopioitu!',
    copy: 'Kopioi',
    close: 'Sulje',
  },
  cs: {
    locNotFound: 'Poloha nenalezena',
    locSearching: 'Hledám…',
    locSearch: 'Hledat',
    locSearchPlaceholder: 'Zadejte adresu, např. Praha, Václavské náměstí 1',
    locClickHint: 'Kliknutím na mapu vyberte polohu nebo zadejte adresu výše.',
    regenTitle: 'Obnovit API klíč',
    regenWarningTitle: 'Pozor!',
    regenWarning:
      'Starý API klíč přestane fungovat ihned po vygenerování nového. Ujistěte se, že klíč aktualizujete ve všech aplikacích, které ho používají.',
    regenerating: 'Generuji…',
    regenBtn: 'Obnovit klíč',
    regenNewTitle: 'Nový API klíč vygenerován!',
    regenNewBody: 'Klíč je dostupný v klientském portálu.',
    newApiKey: 'Nový API klíč',
    copied: 'Zkopírováno!',
    copy: 'Kopírovat',
    close: 'Zavřít',
  },
  sk: {
    locNotFound: 'Poloha sa nenašla',
    locSearching: 'Hľadám…',
    locSearch: 'Hľadať',
    locSearchPlaceholder: 'Zadajte adresu, napr. Bratislava, Hlavná 1',
    locClickHint: 'Kliknutím na mapu vyberte polohu alebo zadajte adresu vyššie.',
    regenTitle: 'Obnoviť API kľúč',
    regenWarningTitle: 'Pozor!',
    regenWarning:
      'Starý API kľúč prestane fungovať ihneď po vygenerovaní nového. Uistite sa, že kľúč aktualizujete vo všetkých aplikáciách, ktoré ho používajú.',
    regenerating: 'Generujem…',
    regenBtn: 'Obnoviť kľúč',
    regenNewTitle: 'Nový API kľúč vygenerovaný!',
    regenNewBody: 'Kľúč je dostupný v klientskom portáli.',
    newApiKey: 'Nový API kľúč',
    copied: 'Skopírované!',
    copy: 'Kopírovať',
    close: 'Zavrieť',
  },
  sl: {
    locNotFound: 'Lokacija ni najdena',
    locSearching: 'Iščem…',
    locSearch: 'Iskanje',
    locSearchPlaceholder: 'Vnesite naslov, npr. Ljubljana, Prešernov trg 1',
    locClickHint: 'Kliknite na zemljevid za izbiro lokacije ali vnesite naslov zgoraj.',
    regenTitle: 'Ponovno ustvari API ključ',
    regenWarningTitle: 'Opozorilo!',
    regenWarning:
      'Stari API ključ bo prenehal delovati takoj po ustvarjanju novega. Poskrbite, da ključ posodobite v vseh aplikacijah, ki ga uporabljajo.',
    regenerating: 'Ustvarjanje…',
    regenBtn: 'Ponovno ustvari ključ',
    regenNewTitle: 'Nov API ključ ustvarjen!',
    regenNewBody: 'Ključ je na voljo v portalu za stranke.',
    newApiKey: 'Nov API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    close: 'Zapri',
  },
  hr: {
    locNotFound: 'Lokacija nije pronađena',
    locSearching: 'Tražim…',
    locSearch: 'Traži',
    locSearchPlaceholder: 'Unesite adresu, npr. Zagreb, Ilica 1',
    locClickHint: 'Kliknite na kartu za odabir lokacije ili unesite adresu iznad.',
    regenTitle: 'Ponovno generiraj API ključ',
    regenWarningTitle: 'Upozorenje!',
    regenWarning:
      'Stari API ključ prestat će raditi odmah nakon generiranja novog. Pobrinite se da ažurirate ključ u svim aplikacijama koje ga koriste.',
    regenerating: 'Generiranje…',
    regenBtn: 'Ponovno generiraj ključ',
    regenNewTitle: 'Novi API ključ generiran!',
    regenNewBody: 'Ključ je dostupan u korisničkom portalu.',
    newApiKey: 'Novi API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    close: 'Zatvori',
  },
  hu: {
    locNotFound: 'A helyszín nem található',
    locSearching: 'Keresés…',
    locSearch: 'Keresés',
    locSearchPlaceholder: 'Adjon meg egy címet, pl. Budapest, Andrássy út 1',
    locClickHint:
      'Kattintson a térképre a helyszín kiválasztásához, vagy adjon meg egy címet fent.',
    regenTitle: 'API-kulcs újragenerálása',
    regenWarningTitle: 'Figyelem!',
    regenWarning:
      'A régi API-kulcs azonnal megszűnik működni az új generálása után. Győződjön meg róla, hogy frissíti a kulcsot minden alkalmazásban, amely használja.',
    regenerating: 'Generálás…',
    regenBtn: 'Kulcs újragenerálása',
    regenNewTitle: 'Új API-kulcs generálva!',
    regenNewBody: 'A kulcs elérhető az ügyfélportálon.',
    newApiKey: 'Új API-kulcs',
    copied: 'Másolva!',
    copy: 'Másolás',
    close: 'Bezárás',
  },
  ro: {
    locNotFound: 'Locația nu a fost găsită',
    locSearching: 'Se caută…',
    locSearch: 'Caută',
    locSearchPlaceholder: 'Introduceți o adresă, ex. București, Calea Victoriei 1',
    locClickHint: 'Faceți clic pe hartă pentru a alege o locație sau introduceți o adresă mai sus.',
    regenTitle: 'Regenerează cheia API',
    regenWarningTitle: 'Atenție!',
    regenWarning:
      'Vechea cheie API va înceta să funcționeze imediat după generarea celei noi. Asigurați-vă că actualizați cheia în toate aplicațiile care o folosesc.',
    regenerating: 'Se generează…',
    regenBtn: 'Regenerează cheia',
    regenNewTitle: 'Cheie API nouă generată!',
    regenNewBody: 'Cheia este disponibilă în portalul clientului.',
    newApiKey: 'Cheie API nouă',
    copied: 'Copiat!',
    copy: 'Copiază',
    close: 'Închide',
  },
  bg: {
    locNotFound: 'Местоположението не е намерено',
    locSearching: 'Търсене…',
    locSearch: 'Търси',
    locSearchPlaceholder: 'Въведете адрес, напр. София, бул. Витоша 1',
    locClickHint:
      'Щракнете върху картата, за да изберете местоположение, или въведете адрес по-горе.',
    regenTitle: 'Регенериране на API ключ',
    regenWarningTitle: 'Внимание!',
    regenWarning:
      'Старият API ключ ще спре да работи веднага след генериране на новия. Уверете се, че актуализирате ключа във всички приложения, които го използват.',
    regenerating: 'Генериране…',
    regenBtn: 'Регенерирай ключа',
    regenNewTitle: 'Нов API ключ генериран!',
    regenNewBody: 'Ключът е достъпен в клиентския портал.',
    newApiKey: 'Нов API ключ',
    copied: 'Копирано!',
    copy: 'Копирай',
    close: 'Затвори',
  },
  el: {
    locNotFound: 'Η τοποθεσία δεν βρέθηκε',
    locSearching: 'Αναζήτηση…',
    locSearch: 'Αναζήτηση',
    locSearchPlaceholder: 'Εισαγάγετε μια διεύθυνση, π.χ. Αθήνα, Πανεπιστημίου 1',
    locClickHint:
      'Κάντε κλικ στον χάρτη για να επιλέξετε τοποθεσία ή εισαγάγετε μια διεύθυνση παραπάνω.',
    regenTitle: 'Αναδημιουργία κλειδιού API',
    regenWarningTitle: 'Προσοχή!',
    regenWarning:
      'Το παλιό κλειδί API θα σταματήσει να λειτουργεί αμέσως μετά τη δημιουργία του νέου. Βεβαιωθείτε ότι ενημερώνετε το κλειδί σε όλες τις εφαρμογές που το χρησιμοποιούν.',
    regenerating: 'Δημιουργία…',
    regenBtn: 'Αναδημιουργία κλειδιού',
    regenNewTitle: 'Νέο κλειδί API δημιουργήθηκε!',
    regenNewBody: 'Το κλειδί είναι διαθέσιμο στην πύλη πελάτη.',
    newApiKey: 'Νέο κλειδί API',
    copied: 'Αντιγράφηκε!',
    copy: 'Αντιγραφή',
    close: 'Κλείσιμο',
  },
  et: {
    locNotFound: 'Asukohta ei leitud',
    locSearching: 'Otsin…',
    locSearch: 'Otsi',
    locSearchPlaceholder: 'Sisestage aadress, nt Tallinn, Viru 1',
    locClickHint: 'Asukoha valimiseks klõpsake kaardil või sisestage aadress ülal.',
    regenTitle: 'Loo API-võti uuesti',
    regenWarningTitle: 'Tähelepanu!',
    regenWarning:
      'Vana API-võti lakkab töötamast kohe pärast uue loomist. Veenduge, et uuendate võtit kõigis seda kasutavates rakendustes.',
    regenerating: 'Loomine…',
    regenBtn: 'Loo võti uuesti',
    regenNewTitle: 'Uus API-võti loodud!',
    regenNewBody: 'Võti on saadaval kliendiportaalis.',
    newApiKey: 'Uus API-võti',
    copied: 'Kopeeritud!',
    copy: 'Kopeeri',
    close: 'Sulge',
  },
  lv: {
    locNotFound: 'Atrašanās vieta nav atrasta',
    locSearching: 'Meklē…',
    locSearch: 'Meklēt',
    locSearchPlaceholder: 'Ievadiet adresi, piem., Rīga, Brīvības 1',
    locClickHint:
      'Noklikšķiniet uz kartes, lai izvēlētos atrašanās vietu, vai ievadiet adresi augstāk.',
    regenTitle: 'Atjaunot API atslēgu',
    regenWarningTitle: 'Uzmanību!',
    regenWarning:
      'Vecā API atslēga pārstās darboties tūlīt pēc jaunās ģenerēšanas. Pārliecinieties, ka atjaunojat atslēgu visās lietojumprogrammās, kas to izmanto.',
    regenerating: 'Ģenerē…',
    regenBtn: 'Atjaunot atslēgu',
    regenNewTitle: 'Jauna API atslēga ģenerēta!',
    regenNewBody: 'Atslēga ir pieejama klientu portālā.',
    newApiKey: 'Jauna API atslēga',
    copied: 'Nokopēts!',
    copy: 'Kopēt',
    close: 'Aizvērt',
  },
  lt: {
    locNotFound: 'Vieta nerasta',
    locSearching: 'Ieškoma…',
    locSearch: 'Ieškoti',
    locSearchPlaceholder: 'Įveskite adresą, pvz., Vilnius, Gedimino pr. 1',
    locClickHint:
      'Spustelėkite žemėlapyje, kad pasirinktumėte vietą, arba įveskite adresą aukščiau.',
    regenTitle: 'Pakartotinai sugeneruoti API raktą',
    regenWarningTitle: 'Dėmesio!',
    regenWarning:
      'Senas API raktas nustos veikti iškart sugeneravus naują. Įsitikinkite, kad atnaujinote raktą visose jį naudojančiose programose.',
    regenerating: 'Generuojama…',
    regenBtn: 'Sugeneruoti raktą iš naujo',
    regenNewTitle: 'Naujas API raktas sugeneruotas!',
    regenNewBody: 'Raktas pasiekiamas kliento portale.',
    newApiKey: 'Naujas API raktas',
    copied: 'Nukopijuota!',
    copy: 'Kopijuoti',
    close: 'Uždaryti',
  },
  mt: {
    locNotFound: 'Il-post ma nstabx',
    locSearching: 'Qed ifittex…',
    locSearch: 'Fittex',
    locSearchPlaceholder: 'Daħħal indirizz, eż. Valletta, Triq ir-Repubblika 1',
    locClickHint: 'Ikklikkja fuq il-mappa biex tagħżel post jew daħħal indirizz hawn fuq.',
    regenTitle: 'Iġġenera ċ-ċavetta API mill-ġdid',
    regenWarningTitle: 'Twissija!',
    regenWarning:
      'Iċ-ċavetta API qadima se tieqaf taħdem immedjatament wara li tiġi ġġenerata l-ġdida. Kun żgur li taġġorna ċ-ċavetta fl-applikazzjonijiet kollha li jużawha.',
    regenerating: 'Qed jiġġenera…',
    regenBtn: 'Iġġenera ċ-ċavetta mill-ġdid',
    regenNewTitle: 'Ċavetta API ġdida ġġenerata!',
    regenNewBody: 'Iċ-ċavetta hija disponibbli fil-portal tal-klijent.',
    newApiKey: 'Ċavetta API ġdida',
    copied: 'Ikkopjat!',
    copy: 'Ikkopja',
    close: 'Agħlaq',
  },
  ga: {
    locNotFound: 'Suíomh gan aimsiú',
    locSearching: 'Ag cuardach…',
    locSearch: 'Cuardaigh',
    locSearchPlaceholder: 'Cuir isteach seoladh, m.sh. Baile Átha Cliath, Sráid Uí Chonaill 1',
    locClickHint: 'Cliceáil ar an léarscáil chun suíomh a roghnú nó cuir isteach seoladh thuas.',
    regenTitle: 'Athghin eochair API',
    regenWarningTitle: 'Rabhadh!',
    regenWarning:
      'Stopfaidh an tseaneochair API ag obair láithreach tar éis an cheann nua a ghiniúint. Bí cinnte go nuashonraíonn tú an eochair i ngach feidhmchlár a úsáideann í.',
    regenerating: 'Á ghiniúint…',
    regenBtn: 'Athghin eochair',
    regenNewTitle: 'Eochair API nua ginte!',
    regenNewBody: 'Tá an eochair ar fáil sa tairseach cliaint.',
    newApiKey: 'Eochair API nua',
    copied: 'Cóipeáilte!',
    copy: 'Cóipeáil',
    close: 'Dún',
  },
  no: {
    locNotFound: 'Plassering ikke funnet',
    locSearching: 'Søker…',
    locSearch: 'Søk',
    locSearchPlaceholder: 'Skriv inn en adresse, f.eks. Oslo, Karl Johans gate 1',
    locClickHint: 'Klikk på kartet for å velge en plassering, eller skriv inn en adresse ovenfor.',
    regenTitle: 'Regenerer API-nøkkel',
    regenWarningTitle: 'Advarsel!',
    regenWarning:
      'Den gamle API-nøkkelen slutter å fungere umiddelbart etter at den nye er generert. Sørg for at du oppdaterer nøkkelen i alle applikasjoner som bruker den.',
    regenerating: 'Genererer…',
    regenBtn: 'Regenerer nøkkel',
    regenNewTitle: 'Ny API-nøkkel generert!',
    regenNewBody: 'Nøkkelen er tilgjengelig i kundeportalen.',
    newApiKey: 'Ny API-nøkkel',
    copied: 'Kopiert!',
    copy: 'Kopier',
    close: 'Lukk',
  },
  is: {
    locNotFound: 'Staðsetning fannst ekki',
    locSearching: 'Leita…',
    locSearch: 'Leita',
    locSearchPlaceholder: 'Sláðu inn heimilisfang, t.d. Reykjavík, Laugavegur 1',
    locClickHint: 'Smelltu á kortið til að velja staðsetningu eða sláðu inn heimilisfang að ofan.',
    regenTitle: 'Endurnýja API-lykil',
    regenWarningTitle: 'Aðvörun!',
    regenWarning:
      'Gamli API-lykillinn hættir að virka strax eftir að nýi er búinn til. Gakktu úr skugga um að þú uppfærir lykilinn í öllum forritum sem nota hann.',
    regenerating: 'Bý til…',
    regenBtn: 'Endurnýja lykil',
    regenNewTitle: 'Nýr API-lykill búinn til!',
    regenNewBody: 'Lykillinn er aðgengilegur í viðskiptavinagáttinni.',
    newApiKey: 'Nýr API-lykill',
    copied: 'Afritað!',
    copy: 'Afrita',
    close: 'Loka',
  },
  uk: {
    locNotFound: 'Розташування не знайдено',
    locSearching: 'Пошук…',
    locSearch: 'Пошук',
    locSearchPlaceholder: 'Введіть адресу, напр., Київ, Хрещатик 1',
    locClickHint: 'Натисніть на карту, щоб вибрати розташування, або введіть адресу вище.',
    regenTitle: 'Перегенерувати ключ API',
    regenWarningTitle: 'Увага!',
    regenWarning:
      'Старий ключ API припинить працювати одразу після генерації нового. Переконайтеся, що оновили ключ у всіх застосунках, які його використовують.',
    regenerating: 'Генерування…',
    regenBtn: 'Перегенерувати ключ',
    regenNewTitle: 'Новий ключ API згенеровано!',
    regenNewBody: 'Ключ доступний у порталі клієнта.',
    newApiKey: 'Новий ключ API',
    copied: 'Скопійовано!',
    copy: 'Копіювати',
    close: 'Закрити',
  },
  sr: {
    locNotFound: 'Lokacija nije pronađena',
    locSearching: 'Tražim…',
    locSearch: 'Traži',
    locSearchPlaceholder: 'Unesite adresu, npr. Beograd, Knez Mihailova 1',
    locClickHint: 'Kliknite na mapu da biste izabrali lokaciju ili unesite adresu iznad.',
    regenTitle: 'Ponovo generiši API ključ',
    regenWarningTitle: 'Upozorenje!',
    regenWarning:
      'Stari API ključ će prestati da radi odmah nakon generisanja novog. Postarajte se da ažurirate ključ u svim aplikacijama koje ga koriste.',
    regenerating: 'Generisanje…',
    regenBtn: 'Ponovo generiši ključ',
    regenNewTitle: 'Novi API ključ generisan!',
    regenNewBody: 'Ključ je dostupan u korisničkom portalu.',
    newApiKey: 'Novi API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    close: 'Zatvori',
  },
  sq: {
    locNotFound: 'Vendndodhja nuk u gjet',
    locSearching: 'Po kërkohet…',
    locSearch: 'Kërko',
    locSearchPlaceholder: 'Vendosni një adresë, p.sh. Tiranë, Rruga e Durrësit 1',
    locClickHint: 'Klikoni në hartë për të zgjedhur një vendndodhje ose vendosni një adresë sipër.',
    regenTitle: 'Rigjenero çelësin API',
    regenWarningTitle: 'Kujdes!',
    regenWarning:
      'Çelësi i vjetër API do të pushojë së punuari menjëherë pas gjenerimit të të riut. Sigurohuni që përditësoni çelësin në të gjitha aplikacionet që e përdorin.',
    regenerating: 'Po gjenerohet…',
    regenBtn: 'Rigjenero çelësin',
    regenNewTitle: 'Çelës i ri API u gjenerua!',
    regenNewBody: 'Çelësi është i disponueshëm në portalin e klientit.',
    newApiKey: 'Çelës i ri API',
    copied: 'U kopjua!',
    copy: 'Kopjo',
    close: 'Mbyll',
  },
  mk: {
    locNotFound: 'Локацијата не е пронајдена',
    locSearching: 'Се пребарува…',
    locSearch: 'Пребарај',
    locSearchPlaceholder: 'Внесете адреса, на пр. Скопје, ул. Македонија 1',
    locClickHint: 'Кликнете на картата за да изберете локација или внесете адреса погоре.',
    regenTitle: 'Регенерирај API клуч',
    regenWarningTitle: 'Внимание!',
    regenWarning:
      'Стариот API клуч ќе престане да работи веднаш по генерирањето на новиот. Осигурете се дека го ажурирате клучот во сите апликации што го користат.',
    regenerating: 'Генерирање…',
    regenBtn: 'Регенерирај клуч',
    regenNewTitle: 'Нов API клуч генериран!',
    regenNewBody: 'Клучот е достапен во клиентскиот портал.',
    newApiKey: 'Нов API клуч',
    copied: 'Копирано!',
    copy: 'Копирај',
    close: 'Затвори',
  },
  bs: {
    locNotFound: 'Lokacija nije pronađena',
    locSearching: 'Tražim…',
    locSearch: 'Traži',
    locSearchPlaceholder: 'Unesite adresu, npr. Sarajevo, Ferhadija 1',
    locClickHint: 'Kliknite na kartu da biste izabrali lokaciju ili unesite adresu iznad.',
    regenTitle: 'Ponovo generiši API ključ',
    regenWarningTitle: 'Upozorenje!',
    regenWarning:
      'Stari API ključ će prestati da radi odmah nakon generisanja novog. Pobrinite se da ažurirate ključ u svim aplikacijama koje ga koriste.',
    regenerating: 'Generisanje…',
    regenBtn: 'Ponovo generiši ključ',
    regenNewTitle: 'Novi API ključ generisan!',
    regenNewBody: 'Ključ je dostupan u korisničkom portalu.',
    newApiKey: 'Novi API ključ',
    copied: 'Kopirano!',
    copy: 'Kopiraj',
    close: 'Zatvori',
  },
};

/** Namespace 'perf' — SubmitProductionForm + PerformancePanel. */
const perfTr: Record<string, Dict> = {
  en: {
    spfTitle: 'Submit production data',
    spfDate: 'Date',
    spfTz: 'Time zone (UTC offset)',
    spfCsvUpload: 'Load data from a CSV file',
    spfHideInstr: 'Hide instructions',
    spfFileFormat: 'File format',
    spfCsvFormatTitle: 'Required CSV file format:',
    spfChooseFile: 'Choose a CSV file…',
    spfShowSolar: 'Show only daylight hours (5–20)',
    spfShowAll: 'Show all hours (0–23)',
    spfHourCol: 'Hour',
    spfProductionCol: 'Production (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Submit production data',
    spfSubmitting: 'Submitting…',
    spfNoData: 'Enter production data for at least one hour.',
    spfSaved: 'Production data has been saved.',
    spfCsvEmpty: 'The file is empty.',
    spfCsvErrorsTitle: 'Errors in the CSV file:',
    spfCsvLoadedPartial: 'Loaded {{loaded}} hours, but {{skipped}} rows were skipped.',
    spfCsvLoaded: 'Loaded CSV data: {{loaded}} hours, total {{sum}} Wh',
    ppTitle: 'Performance analysis',
    ppNoData:
      'Not enough data for analysis. Submit actual production data so the system can analyse installation performance.',
    ppDaysAnalyzed: '{{count}} days analysed',
    ppOverallEff: 'Overall efficiency',
    ppTotalForecast: 'Total forecast',
    ppTotalProduction: 'Total production',
    ppLostProduction: 'Lost production',
    ppWorksFine: 'Installation works correctly',
    ppWorksFineDesc: 'No issues with shading, degradation or failures detected.',
    ppHourlyAvg: 'Hourly average: forecast vs actual (Wh)',
    ppDailyEff: 'Daily efficiency (%)',
    ppEffPerHour: 'Efficiency per hour',
    ppForecast: 'Forecast',
    ppActual: 'Actual',
    ppEfficiency: 'Efficiency',
    pp100ideal: '100% = ideal efficiency',
    pp85threshold: '85% = degradation threshold',
    ppDays: '{{count}} days',
    ppShading: 'Shading',
    ppDegradation: 'Degradation',
    ppFailure: 'Failure',
    ppSevHigh: 'High',
    ppSevMedium: 'Medium',
    ppSevLow: 'Low',
  },
  pl: {
    spfTitle: 'Wprowadź dane produkcji',
    spfDate: 'Data',
    spfTz: 'Strefa czasowa (offset UTC)',
    spfCsvUpload: 'Załaduj dane z pliku CSV',
    spfHideInstr: 'Ukryj instrukcję',
    spfFileFormat: 'Format pliku',
    spfCsvFormatTitle: 'Wymagany format pliku CSV:',
    spfChooseFile: 'Wybierz plik CSV…',
    spfShowSolar: 'Pokaż tylko godziny słoneczne (5–20)',
    spfShowAll: 'Pokaż wszystkie godziny (0–23)',
    spfHourCol: 'Godz.',
    spfProductionCol: 'Produkcja (Wh)',
    spfTotal: 'Suma',
    spfSubmit: 'Wyślij dane produkcji',
    spfSubmitting: 'Wysyłanie…',
    spfNoData: 'Wprowadź dane produkcji dla przynajmniej jednej godziny.',
    spfSaved: 'Dane produkcji zostały zapisane.',
    spfCsvEmpty: 'Plik jest pusty.',
    spfCsvErrorsTitle: 'Błędy w pliku CSV:',
    spfCsvLoadedPartial: 'Załadowano {{loaded}} godzin, ale {{skipped}} wierszy pominięto.',
    spfCsvLoaded: 'Załadowano dane z CSV: {{loaded}} godzin, suma {{sum}} Wh',
    ppTitle: 'Analiza wydajności',
    ppNoData:
      'Brak wystarczających danych do analizy. Wprowadź dane produkcji rzeczywistej, aby system mógł przeanalizować wydajność instalacji.',
    ppDaysAnalyzed: '{{count}} dni analizy',
    ppOverallEff: 'Ogólna wydajność',
    ppTotalForecast: 'Prognoza łącznie',
    ppTotalProduction: 'Produkcja łącznie',
    ppLostProduction: 'Utracona produkcja',
    ppWorksFine: 'Instalacja działa prawidłowo',
    ppWorksFineDesc: 'Nie wykryto problemów z zacienieniem, degradacją ani awariami.',
    ppHourlyAvg: 'Średnia godzinowa: prognoza vs rzeczywistość (Wh)',
    ppDailyEff: 'Dzienna wydajność (%)',
    ppEffPerHour: 'Wydajność per godzina',
    ppForecast: 'Prognoza',
    ppActual: 'Rzeczywistość',
    ppEfficiency: 'Wydajność',
    pp100ideal: '100% = idealna wydajność',
    pp85threshold: '85% = próg degradacji',
    ppDays: '{{count}} dni',
    ppShading: 'Zacienienie',
    ppDegradation: 'Degradacja',
    ppFailure: 'Awaria',
    ppSevHigh: 'Wysoki',
    ppSevMedium: 'Średni',
    ppSevLow: 'Niski',
  },
  de: {
    spfTitle: 'Produktionsdaten übermitteln',
    spfDate: 'Datum',
    spfTz: 'Zeitzone (UTC-Offset)',
    spfCsvUpload: 'Daten aus CSV-Datei laden',
    spfHideInstr: 'Anleitung ausblenden',
    spfFileFormat: 'Dateiformat',
    spfCsvFormatTitle: 'Erforderliches CSV-Dateiformat:',
    spfChooseFile: 'CSV-Datei wählen…',
    spfShowSolar: 'Nur Tagstunden anzeigen (5–20)',
    spfShowAll: 'Alle Stunden anzeigen (0–23)',
    spfHourCol: 'Std.',
    spfProductionCol: 'Produktion (Wh)',
    spfTotal: 'Summe',
    spfSubmit: 'Produktionsdaten senden',
    spfSubmitting: 'Wird gesendet…',
    spfNoData: 'Geben Sie Produktionsdaten für mindestens eine Stunde ein.',
    spfSaved: 'Produktionsdaten wurden gespeichert.',
    spfCsvEmpty: 'Die Datei ist leer.',
    spfCsvErrorsTitle: 'Fehler in der CSV-Datei:',
    spfCsvLoadedPartial: '{{loaded}} Stunden geladen, aber {{skipped}} Zeilen übersprungen.',
    spfCsvLoaded: 'CSV-Daten geladen: {{loaded}} Stunden, Summe {{sum}} Wh',
    ppTitle: 'Leistungsanalyse',
    ppNoData:
      'Nicht genügend Daten für die Analyse. Übermitteln Sie tatsächliche Produktionsdaten, damit das System die Anlagenleistung analysieren kann.',
    ppDaysAnalyzed: '{{count}} Tage analysiert',
    ppOverallEff: 'Gesamtleistung',
    ppTotalForecast: 'Prognose gesamt',
    ppTotalProduction: 'Produktion gesamt',
    ppLostProduction: 'Verlorene Produktion',
    ppWorksFine: 'Anlage funktioniert korrekt',
    ppWorksFineDesc: 'Keine Probleme mit Verschattung, Degradation oder Ausfällen erkannt.',
    ppHourlyAvg: 'Stundenmittel: Prognose vs. Ist (Wh)',
    ppDailyEff: 'Tägliche Leistung (%)',
    ppEffPerHour: 'Leistung pro Stunde',
    ppForecast: 'Prognose',
    ppActual: 'Ist',
    ppEfficiency: 'Leistung',
    pp100ideal: '100% = ideale Leistung',
    pp85threshold: '85% = Degradationsschwelle',
    ppDays: '{{count}} Tage',
    ppShading: 'Verschattung',
    ppDegradation: 'Degradation',
    ppFailure: 'Ausfall',
    ppSevHigh: 'Hoch',
    ppSevMedium: 'Mittel',
    ppSevLow: 'Niedrig',
  },
  fr: {
    spfTitle: 'Soumettre les données de production',
    spfDate: 'Date',
    spfTz: 'Fuseau horaire (décalage UTC)',
    spfCsvUpload: 'Charger les données depuis un fichier CSV',
    spfHideInstr: 'Masquer les instructions',
    spfFileFormat: 'Format de fichier',
    spfCsvFormatTitle: 'Format de fichier CSV requis :',
    spfChooseFile: 'Choisir un fichier CSV…',
    spfShowSolar: 'Afficher uniquement les heures de jour (5–20)',
    spfShowAll: 'Afficher toutes les heures (0–23)',
    spfHourCol: 'Heure',
    spfProductionCol: 'Production (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Envoyer les données de production',
    spfSubmitting: 'Envoi…',
    spfNoData: 'Saisissez les données de production pour au moins une heure.',
    spfSaved: 'Les données de production ont été enregistrées.',
    spfCsvEmpty: 'Le fichier est vide.',
    spfCsvErrorsTitle: 'Erreurs dans le fichier CSV :',
    spfCsvLoadedPartial: '{{loaded}} heures chargées, mais {{skipped}} lignes ignorées.',
    spfCsvLoaded: 'Données CSV chargées : {{loaded}} heures, total {{sum}} Wh',
    ppTitle: 'Analyse de performance',
    ppNoData:
      "Données insuffisantes pour l'analyse. Soumettez des données de production réelles pour que le système puisse analyser la performance de l'installation.",
    ppDaysAnalyzed: '{{count}} jours analysés',
    ppOverallEff: 'Performance globale',
    ppTotalForecast: 'Prévision totale',
    ppTotalProduction: 'Production totale',
    ppLostProduction: 'Production perdue',
    ppWorksFine: "L'installation fonctionne correctement",
    ppWorksFineDesc: 'Aucun problème de masquage, de dégradation ou de panne détecté.',
    ppHourlyAvg: 'Moyenne horaire : prévision vs réel (Wh)',
    ppDailyEff: 'Performance quotidienne (%)',
    ppEffPerHour: 'Performance par heure',
    ppForecast: 'Prévision',
    ppActual: 'Réel',
    ppEfficiency: 'Performance',
    pp100ideal: '100% = performance idéale',
    pp85threshold: '85% = seuil de dégradation',
    ppDays: '{{count}} jours',
    ppShading: 'Masquage',
    ppDegradation: 'Dégradation',
    ppFailure: 'Panne',
    ppSevHigh: 'Élevé',
    ppSevMedium: 'Moyen',
    ppSevLow: 'Faible',
  },
  es: {
    spfTitle: 'Enviar datos de producción',
    spfDate: 'Fecha',
    spfTz: 'Zona horaria (offset UTC)',
    spfCsvUpload: 'Cargar datos desde un archivo CSV',
    spfHideInstr: 'Ocultar instrucciones',
    spfFileFormat: 'Formato de archivo',
    spfCsvFormatTitle: 'Formato de archivo CSV requerido:',
    spfChooseFile: 'Elegir un archivo CSV…',
    spfShowSolar: 'Mostrar solo horas de luz (5–20)',
    spfShowAll: 'Mostrar todas las horas (0–23)',
    spfHourCol: 'Hora',
    spfProductionCol: 'Producción (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Enviar datos de producción',
    spfSubmitting: 'Enviando…',
    spfNoData: 'Introduzca datos de producción para al menos una hora.',
    spfSaved: 'Los datos de producción se han guardado.',
    spfCsvEmpty: 'El archivo está vacío.',
    spfCsvErrorsTitle: 'Errores en el archivo CSV:',
    spfCsvLoadedPartial: 'Cargadas {{loaded}} horas, pero se omitieron {{skipped}} filas.',
    spfCsvLoaded: 'Datos CSV cargados: {{loaded}} horas, total {{sum}} Wh',
    ppTitle: 'Análisis de rendimiento',
    ppNoData:
      'No hay datos suficientes para el análisis. Envíe datos de producción reales para que el sistema pueda analizar el rendimiento de la instalación.',
    ppDaysAnalyzed: '{{count}} días analizados',
    ppOverallEff: 'Rendimiento general',
    ppTotalForecast: 'Previsión total',
    ppTotalProduction: 'Producción total',
    ppLostProduction: 'Producción perdida',
    ppWorksFine: 'La instalación funciona correctamente',
    ppWorksFineDesc: 'No se detectaron problemas de sombreado, degradación o fallos.',
    ppHourlyAvg: 'Media horaria: previsión vs real (Wh)',
    ppDailyEff: 'Rendimiento diario (%)',
    ppEffPerHour: 'Rendimiento por hora',
    ppForecast: 'Previsión',
    ppActual: 'Real',
    ppEfficiency: 'Rendimiento',
    pp100ideal: '100% = rendimiento ideal',
    pp85threshold: '85% = umbral de degradación',
    ppDays: '{{count}} días',
    ppShading: 'Sombreado',
    ppDegradation: 'Degradación',
    ppFailure: 'Fallo',
    ppSevHigh: 'Alto',
    ppSevMedium: 'Medio',
    ppSevLow: 'Bajo',
  },
  it: {
    spfTitle: 'Invia dati di produzione',
    spfDate: 'Data',
    spfTz: 'Fuso orario (offset UTC)',
    spfCsvUpload: 'Carica dati da un file CSV',
    spfHideInstr: 'Nascondi istruzioni',
    spfFileFormat: 'Formato file',
    spfCsvFormatTitle: 'Formato file CSV richiesto:',
    spfChooseFile: 'Scegli un file CSV…',
    spfShowSolar: 'Mostra solo ore diurne (5–20)',
    spfShowAll: 'Mostra tutte le ore (0–23)',
    spfHourCol: 'Ora',
    spfProductionCol: 'Produzione (Wh)',
    spfTotal: 'Totale',
    spfSubmit: 'Invia dati di produzione',
    spfSubmitting: 'Invio…',
    spfNoData: "Inserisci i dati di produzione per almeno un'ora.",
    spfSaved: 'I dati di produzione sono stati salvati.',
    spfCsvEmpty: 'Il file è vuoto.',
    spfCsvErrorsTitle: 'Errori nel file CSV:',
    spfCsvLoadedPartial: 'Caricate {{loaded}} ore, ma {{skipped}} righe sono state saltate.',
    spfCsvLoaded: 'Dati CSV caricati: {{loaded}} ore, totale {{sum}} Wh',
    ppTitle: 'Analisi delle prestazioni',
    ppNoData:
      "Dati insufficienti per l'analisi. Invia dati di produzione reali affinché il sistema possa analizzare le prestazioni dell'impianto.",
    ppDaysAnalyzed: '{{count}} giorni analizzati',
    ppOverallEff: 'Prestazioni complessive',
    ppTotalForecast: 'Previsione totale',
    ppTotalProduction: 'Produzione totale',
    ppLostProduction: 'Produzione persa',
    ppWorksFine: "L'impianto funziona correttamente",
    ppWorksFineDesc: 'Nessun problema di ombreggiamento, degrado o guasto rilevato.',
    ppHourlyAvg: 'Media oraria: previsione vs reale (Wh)',
    ppDailyEff: 'Prestazioni giornaliere (%)',
    ppEffPerHour: 'Prestazioni per ora',
    ppForecast: 'Previsione',
    ppActual: 'Reale',
    ppEfficiency: 'Prestazioni',
    pp100ideal: '100% = prestazioni ideali',
    pp85threshold: '85% = soglia di degrado',
    ppDays: '{{count}} giorni',
    ppShading: 'Ombreggiamento',
    ppDegradation: 'Degrado',
    ppFailure: 'Guasto',
    ppSevHigh: 'Alto',
    ppSevMedium: 'Medio',
    ppSevLow: 'Basso',
  },
  pt: {
    spfTitle: 'Submeter dados de produção',
    spfDate: 'Data',
    spfTz: 'Fuso horário (offset UTC)',
    spfCsvUpload: 'Carregar dados de um ficheiro CSV',
    spfHideInstr: 'Ocultar instruções',
    spfFileFormat: 'Formato do ficheiro',
    spfCsvFormatTitle: 'Formato de ficheiro CSV necessário:',
    spfChooseFile: 'Escolher um ficheiro CSV…',
    spfShowSolar: 'Mostrar apenas horas de luz (5–20)',
    spfShowAll: 'Mostrar todas as horas (0–23)',
    spfHourCol: 'Hora',
    spfProductionCol: 'Produção (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Enviar dados de produção',
    spfSubmitting: 'A enviar…',
    spfNoData: 'Introduza dados de produção para pelo menos uma hora.',
    spfSaved: 'Os dados de produção foram guardados.',
    spfCsvEmpty: 'O ficheiro está vazio.',
    spfCsvErrorsTitle: 'Erros no ficheiro CSV:',
    spfCsvLoadedPartial: 'Carregadas {{loaded}} horas, mas {{skipped}} linhas foram ignoradas.',
    spfCsvLoaded: 'Dados CSV carregados: {{loaded}} horas, total {{sum}} Wh',
    ppTitle: 'Análise de desempenho',
    ppNoData:
      'Dados insuficientes para a análise. Submeta dados de produção reais para que o sistema possa analisar o desempenho da instalação.',
    ppDaysAnalyzed: '{{count}} dias analisados',
    ppOverallEff: 'Desempenho global',
    ppTotalForecast: 'Previsão total',
    ppTotalProduction: 'Produção total',
    ppLostProduction: 'Produção perdida',
    ppWorksFine: 'A instalação funciona corretamente',
    ppWorksFineDesc: 'Não foram detetados problemas de sombreamento, degradação ou avarias.',
    ppHourlyAvg: 'Média horária: previsão vs real (Wh)',
    ppDailyEff: 'Desempenho diário (%)',
    ppEffPerHour: 'Desempenho por hora',
    ppForecast: 'Previsão',
    ppActual: 'Real',
    ppEfficiency: 'Desempenho',
    pp100ideal: '100% = desempenho ideal',
    pp85threshold: '85% = limiar de degradação',
    ppDays: '{{count}} dias',
    ppShading: 'Sombreamento',
    ppDegradation: 'Degradação',
    ppFailure: 'Avaria',
    ppSevHigh: 'Alto',
    ppSevMedium: 'Médio',
    ppSevLow: 'Baixo',
  },
  nl: {
    spfTitle: 'Productiegegevens indienen',
    spfDate: 'Datum',
    spfTz: 'Tijdzone (UTC-offset)',
    spfCsvUpload: 'Gegevens uit een CSV-bestand laden',
    spfHideInstr: 'Instructies verbergen',
    spfFileFormat: 'Bestandsformaat',
    spfCsvFormatTitle: 'Vereist CSV-bestandsformaat:',
    spfChooseFile: 'Kies een CSV-bestand…',
    spfShowSolar: 'Toon alleen daglichturen (5–20)',
    spfShowAll: 'Toon alle uren (0–23)',
    spfHourCol: 'Uur',
    spfProductionCol: 'Productie (Wh)',
    spfTotal: 'Totaal',
    spfSubmit: 'Productiegegevens verzenden',
    spfSubmitting: 'Verzenden…',
    spfNoData: 'Voer productiegegevens in voor ten minste één uur.',
    spfSaved: 'De productiegegevens zijn opgeslagen.',
    spfCsvEmpty: 'Het bestand is leeg.',
    spfCsvErrorsTitle: 'Fouten in het CSV-bestand:',
    spfCsvLoadedPartial: '{{loaded}} uren geladen, maar {{skipped}} rijen overgeslagen.',
    spfCsvLoaded: 'CSV-gegevens geladen: {{loaded}} uren, totaal {{sum}} Wh',
    ppTitle: 'Prestatieanalyse',
    ppNoData:
      'Onvoldoende gegevens voor analyse. Dien werkelijke productiegegevens in zodat het systeem de installatieprestaties kan analyseren.',
    ppDaysAnalyzed: '{{count}} dagen geanalyseerd',
    ppOverallEff: 'Algehele prestatie',
    ppTotalForecast: 'Prognose totaal',
    ppTotalProduction: 'Productie totaal',
    ppLostProduction: 'Verloren productie',
    ppWorksFine: 'De installatie werkt correct',
    ppWorksFineDesc: 'Geen problemen met schaduw, degradatie of storingen gedetecteerd.',
    ppHourlyAvg: 'Uurgemiddelde: prognose vs werkelijk (Wh)',
    ppDailyEff: 'Dagelijkse prestatie (%)',
    ppEffPerHour: 'Prestatie per uur',
    ppForecast: 'Prognose',
    ppActual: 'Werkelijk',
    ppEfficiency: 'Prestatie',
    pp100ideal: '100% = ideale prestatie',
    pp85threshold: '85% = degradatiedrempel',
    ppDays: '{{count}} dagen',
    ppShading: 'Schaduw',
    ppDegradation: 'Degradatie',
    ppFailure: 'Storing',
    ppSevHigh: 'Hoog',
    ppSevMedium: 'Gemiddeld',
    ppSevLow: 'Laag',
  },
  da: {
    spfTitle: 'Indsend produktionsdata',
    spfDate: 'Dato',
    spfTz: 'Tidszone (UTC-offset)',
    spfCsvUpload: 'Indlæs data fra en CSV-fil',
    spfHideInstr: 'Skjul instruktioner',
    spfFileFormat: 'Filformat',
    spfCsvFormatTitle: 'Påkrævet CSV-filformat:',
    spfChooseFile: 'Vælg en CSV-fil…',
    spfShowSolar: 'Vis kun dagslystimer (5–20)',
    spfShowAll: 'Vis alle timer (0–23)',
    spfHourCol: 'Time',
    spfProductionCol: 'Produktion (Wh)',
    spfTotal: 'I alt',
    spfSubmit: 'Send produktionsdata',
    spfSubmitting: 'Sender…',
    spfNoData: 'Indtast produktionsdata for mindst én time.',
    spfSaved: 'Produktionsdataene er gemt.',
    spfCsvEmpty: 'Filen er tom.',
    spfCsvErrorsTitle: 'Fejl i CSV-filen:',
    spfCsvLoadedPartial: '{{loaded}} timer indlæst, men {{skipped}} rækker blev sprunget over.',
    spfCsvLoaded: 'CSV-data indlæst: {{loaded}} timer, i alt {{sum}} Wh',
    ppTitle: 'Ydelsesanalyse',
    ppNoData:
      'Ikke nok data til analyse. Indsend faktiske produktionsdata, så systemet kan analysere anlæggets ydelse.',
    ppDaysAnalyzed: '{{count}} dage analyseret',
    ppOverallEff: 'Samlet ydelse',
    ppTotalForecast: 'Prognose i alt',
    ppTotalProduction: 'Produktion i alt',
    ppLostProduction: 'Tabt produktion',
    ppWorksFine: 'Anlægget fungerer korrekt',
    ppWorksFineDesc: 'Ingen problemer med skygge, degradering eller fejl registreret.',
    ppHourlyAvg: 'Timegennemsnit: prognose vs faktisk (Wh)',
    ppDailyEff: 'Daglig ydelse (%)',
    ppEffPerHour: 'Ydelse pr. time',
    ppForecast: 'Prognose',
    ppActual: 'Faktisk',
    ppEfficiency: 'Ydelse',
    pp100ideal: '100% = ideel ydelse',
    pp85threshold: '85% = degraderingstærskel',
    ppDays: '{{count}} dage',
    ppShading: 'Skygge',
    ppDegradation: 'Degradering',
    ppFailure: 'Fejl',
    ppSevHigh: 'Høj',
    ppSevMedium: 'Mellem',
    ppSevLow: 'Lav',
  },
  sv: {
    spfTitle: 'Skicka in produktionsdata',
    spfDate: 'Datum',
    spfTz: 'Tidszon (UTC-offset)',
    spfCsvUpload: 'Ladda data från en CSV-fil',
    spfHideInstr: 'Dölj instruktioner',
    spfFileFormat: 'Filformat',
    spfCsvFormatTitle: 'Obligatoriskt CSV-filformat:',
    spfChooseFile: 'Välj en CSV-fil…',
    spfShowSolar: 'Visa endast dagsljustimmar (5–20)',
    spfShowAll: 'Visa alla timmar (0–23)',
    spfHourCol: 'Tim.',
    spfProductionCol: 'Produktion (Wh)',
    spfTotal: 'Totalt',
    spfSubmit: 'Skicka produktionsdata',
    spfSubmitting: 'Skickar…',
    spfNoData: 'Ange produktionsdata för minst en timme.',
    spfSaved: 'Produktionsdata har sparats.',
    spfCsvEmpty: 'Filen är tom.',
    spfCsvErrorsTitle: 'Fel i CSV-filen:',
    spfCsvLoadedPartial: '{{loaded}} timmar laddade, men {{skipped}} rader hoppades över.',
    spfCsvLoaded: 'CSV-data laddade: {{loaded}} timmar, totalt {{sum}} Wh',
    ppTitle: 'Prestandaanalys',
    ppNoData:
      'Inte tillräckligt med data för analys. Skicka in faktiska produktionsdata så att systemet kan analysera anläggningens prestanda.',
    ppDaysAnalyzed: '{{count}} dagar analyserade',
    ppOverallEff: 'Total prestanda',
    ppTotalForecast: 'Prognos totalt',
    ppTotalProduction: 'Produktion totalt',
    ppLostProduction: 'Förlorad produktion',
    ppWorksFine: 'Anläggningen fungerar korrekt',
    ppWorksFineDesc: 'Inga problem med skuggning, degradering eller fel upptäckta.',
    ppHourlyAvg: 'Timgenomsnitt: prognos vs faktiskt (Wh)',
    ppDailyEff: 'Daglig prestanda (%)',
    ppEffPerHour: 'Prestanda per timme',
    ppForecast: 'Prognos',
    ppActual: 'Faktiskt',
    ppEfficiency: 'Prestanda',
    pp100ideal: '100% = ideal prestanda',
    pp85threshold: '85% = degraderingströskel',
    ppDays: '{{count}} dagar',
    ppShading: 'Skuggning',
    ppDegradation: 'Degradering',
    ppFailure: 'Fel',
    ppSevHigh: 'Hög',
    ppSevMedium: 'Medel',
    ppSevLow: 'Låg',
  },
  fi: {
    spfTitle: 'Lähetä tuotantotiedot',
    spfDate: 'Päivämäärä',
    spfTz: 'Aikavyöhyke (UTC-poikkeama)',
    spfCsvUpload: 'Lataa tiedot CSV-tiedostosta',
    spfHideInstr: 'Piilota ohjeet',
    spfFileFormat: 'Tiedostomuoto',
    spfCsvFormatTitle: 'Vaadittu CSV-tiedostomuoto:',
    spfChooseFile: 'Valitse CSV-tiedosto…',
    spfShowSolar: 'Näytä vain päivänvalotunnit (5–20)',
    spfShowAll: 'Näytä kaikki tunnit (0–23)',
    spfHourCol: 'Tunti',
    spfProductionCol: 'Tuotanto (Wh)',
    spfTotal: 'Yhteensä',
    spfSubmit: 'Lähetä tuotantotiedot',
    spfSubmitting: 'Lähetetään…',
    spfNoData: 'Anna tuotantotiedot vähintään yhdelle tunnille.',
    spfSaved: 'Tuotantotiedot on tallennettu.',
    spfCsvEmpty: 'Tiedosto on tyhjä.',
    spfCsvErrorsTitle: 'Virheitä CSV-tiedostossa:',
    spfCsvLoadedPartial: '{{loaded}} tuntia ladattu, mutta {{skipped}} riviä ohitettiin.',
    spfCsvLoaded: 'CSV-tiedot ladattu: {{loaded}} tuntia, yhteensä {{sum}} Wh',
    ppTitle: 'Suorituskykyanalyysi',
    ppNoData:
      'Ei tarpeeksi tietoja analyysiin. Lähetä todelliset tuotantotiedot, jotta järjestelmä voi analysoida laitoksen suorituskykyä.',
    ppDaysAnalyzed: '{{count}} päivää analysoitu',
    ppOverallEff: 'Kokonaissuorituskyky',
    ppTotalForecast: 'Ennuste yhteensä',
    ppTotalProduction: 'Tuotanto yhteensä',
    ppLostProduction: 'Menetetty tuotanto',
    ppWorksFine: 'Laitos toimii oikein',
    ppWorksFineDesc: 'Varjostus-, heikkenemis- tai vikaongelmia ei havaittu.',
    ppHourlyAvg: 'Tuntikeskiarvo: ennuste vs toteutunut (Wh)',
    ppDailyEff: 'Päivittäinen suorituskyky (%)',
    ppEffPerHour: 'Suorituskyky tunnissa',
    ppForecast: 'Ennuste',
    ppActual: 'Toteutunut',
    ppEfficiency: 'Suorituskyky',
    pp100ideal: '100% = ihanteellinen suorituskyky',
    pp85threshold: '85% = heikkenemiskynnys',
    ppDays: '{{count}} päivää',
    ppShading: 'Varjostus',
    ppDegradation: 'Heikkeneminen',
    ppFailure: 'Vika',
    ppSevHigh: 'Korkea',
    ppSevMedium: 'Keskitaso',
    ppSevLow: 'Matala',
  },
  cs: {
    spfTitle: 'Odeslat data výroby',
    spfDate: 'Datum',
    spfTz: 'Časové pásmo (offset UTC)',
    spfCsvUpload: 'Načíst data ze souboru CSV',
    spfHideInstr: 'Skrýt instrukce',
    spfFileFormat: 'Formát souboru',
    spfCsvFormatTitle: 'Požadovaný formát souboru CSV:',
    spfChooseFile: 'Vyberte soubor CSV…',
    spfShowSolar: 'Zobrazit pouze denní hodiny (5–20)',
    spfShowAll: 'Zobrazit všechny hodiny (0–23)',
    spfHourCol: 'Hod.',
    spfProductionCol: 'Výroba (Wh)',
    spfTotal: 'Celkem',
    spfSubmit: 'Odeslat data výroby',
    spfSubmitting: 'Odesílání…',
    spfNoData: 'Zadejte data výroby alespoň pro jednu hodinu.',
    spfSaved: 'Data výroby byla uložena.',
    spfCsvEmpty: 'Soubor je prázdný.',
    spfCsvErrorsTitle: 'Chyby v souboru CSV:',
    spfCsvLoadedPartial: 'Načteno {{loaded}} hodin, ale {{skipped}} řádků bylo přeskočeno.',
    spfCsvLoaded: 'Načtena data z CSV: {{loaded}} hodin, celkem {{sum}} Wh',
    ppTitle: 'Analýza výkonu',
    ppNoData:
      'Nedostatek dat pro analýzu. Zadejte data skutečné výroby, aby systém mohl analyzovat výkon instalace.',
    ppDaysAnalyzed: '{{count}} dní analýzy',
    ppOverallEff: 'Celkový výkon',
    ppTotalForecast: 'Předpověď celkem',
    ppTotalProduction: 'Výroba celkem',
    ppLostProduction: 'Ztracená výroba',
    ppWorksFine: 'Instalace funguje správně',
    ppWorksFineDesc: 'Nezjištěny problémy se zastíněním, degradací ani poruchami.',
    ppHourlyAvg: 'Hodinový průměr: předpověď vs skutečnost (Wh)',
    ppDailyEff: 'Denní výkon (%)',
    ppEffPerHour: 'Výkon za hodinu',
    ppForecast: 'Předpověď',
    ppActual: 'Skutečnost',
    ppEfficiency: 'Výkon',
    pp100ideal: '100% = ideální výkon',
    pp85threshold: '85% = práh degradace',
    ppDays: '{{count}} dní',
    ppShading: 'Zastínění',
    ppDegradation: 'Degradace',
    ppFailure: 'Porucha',
    ppSevHigh: 'Vysoký',
    ppSevMedium: 'Střední',
    ppSevLow: 'Nízký',
  },
  sk: {
    spfTitle: 'Odoslať dáta výroby',
    spfDate: 'Dátum',
    spfTz: 'Časové pásmo (offset UTC)',
    spfCsvUpload: 'Načítať dáta zo súboru CSV',
    spfHideInstr: 'Skryť inštrukcie',
    spfFileFormat: 'Formát súboru',
    spfCsvFormatTitle: 'Požadovaný formát súboru CSV:',
    spfChooseFile: 'Vyberte súbor CSV…',
    spfShowSolar: 'Zobraziť iba denné hodiny (5–20)',
    spfShowAll: 'Zobraziť všetky hodiny (0–23)',
    spfHourCol: 'Hod.',
    spfProductionCol: 'Výroba (Wh)',
    spfTotal: 'Spolu',
    spfSubmit: 'Odoslať dáta výroby',
    spfSubmitting: 'Odosielanie…',
    spfNoData: 'Zadajte dáta výroby aspoň pre jednu hodinu.',
    spfSaved: 'Dáta výroby boli uložené.',
    spfCsvEmpty: 'Súbor je prázdny.',
    spfCsvErrorsTitle: 'Chyby v súbore CSV:',
    spfCsvLoadedPartial: 'Načítaných {{loaded}} hodín, ale {{skipped}} riadkov bolo preskočených.',
    spfCsvLoaded: 'Načítané dáta z CSV: {{loaded}} hodín, spolu {{sum}} Wh',
    ppTitle: 'Analýza výkonu',
    ppNoData:
      'Nedostatok dát na analýzu. Zadajte dáta skutočnej výroby, aby systém mohol analyzovať výkon inštalácie.',
    ppDaysAnalyzed: '{{count}} dní analýzy',
    ppOverallEff: 'Celkový výkon',
    ppTotalForecast: 'Predpoveď spolu',
    ppTotalProduction: 'Výroba spolu',
    ppLostProduction: 'Stratená výroba',
    ppWorksFine: 'Inštalácia funguje správne',
    ppWorksFineDesc: 'Nezistené problémy so zatienením, degradáciou ani poruchami.',
    ppHourlyAvg: 'Hodinový priemer: predpoveď vs skutočnosť (Wh)',
    ppDailyEff: 'Denný výkon (%)',
    ppEffPerHour: 'Výkon za hodinu',
    ppForecast: 'Predpoveď',
    ppActual: 'Skutočnosť',
    ppEfficiency: 'Výkon',
    pp100ideal: '100% = ideálny výkon',
    pp85threshold: '85% = prah degradácie',
    ppDays: '{{count}} dní',
    ppShading: 'Zatienenie',
    ppDegradation: 'Degradácia',
    ppFailure: 'Porucha',
    ppSevHigh: 'Vysoký',
    ppSevMedium: 'Stredný',
    ppSevLow: 'Nízky',
  },
  sl: {
    spfTitle: 'Pošlji podatke o proizvodnji',
    spfDate: 'Datum',
    spfTz: 'Časovni pas (odmik UTC)',
    spfCsvUpload: 'Naloži podatke iz datoteke CSV',
    spfHideInstr: 'Skrij navodila',
    spfFileFormat: 'Format datoteke',
    spfCsvFormatTitle: 'Zahtevani format datoteke CSV:',
    spfChooseFile: 'Izberi datoteko CSV…',
    spfShowSolar: 'Prikaži samo dnevne ure (5–20)',
    spfShowAll: 'Prikaži vse ure (0–23)',
    spfHourCol: 'Ura',
    spfProductionCol: 'Proizvodnja (Wh)',
    spfTotal: 'Skupaj',
    spfSubmit: 'Pošlji podatke o proizvodnji',
    spfSubmitting: 'Pošiljanje…',
    spfNoData: 'Vnesite podatke o proizvodnji za vsaj eno uro.',
    spfSaved: 'Podatki o proizvodnji so bili shranjeni.',
    spfCsvEmpty: 'Datoteka je prazna.',
    spfCsvErrorsTitle: 'Napake v datoteki CSV:',
    spfCsvLoadedPartial: 'Naloženih {{loaded}} ur, vendar je bilo {{skipped}} vrstic preskočenih.',
    spfCsvLoaded: 'Naloženi podatki iz CSV: {{loaded}} ur, skupaj {{sum}} Wh',
    ppTitle: 'Analiza zmogljivosti',
    ppNoData:
      'Premalo podatkov za analizo. Pošljite dejanske podatke o proizvodnji, da bo sistem lahko analiziral zmogljivost naprave.',
    ppDaysAnalyzed: '{{count}} dni analize',
    ppOverallEff: 'Skupna zmogljivost',
    ppTotalForecast: 'Napoved skupaj',
    ppTotalProduction: 'Proizvodnja skupaj',
    ppLostProduction: 'Izgubljena proizvodnja',
    ppWorksFine: 'Naprava deluje pravilno',
    ppWorksFineDesc: 'Ni zaznanih težav s senčenjem, degradacijo ali okvarami.',
    ppHourlyAvg: 'Urno povprečje: napoved vs dejansko (Wh)',
    ppDailyEff: 'Dnevna zmogljivost (%)',
    ppEffPerHour: 'Zmogljivost na uro',
    ppForecast: 'Napoved',
    ppActual: 'Dejansko',
    ppEfficiency: 'Zmogljivost',
    pp100ideal: '100% = idealna zmogljivost',
    pp85threshold: '85% = prag degradacije',
    ppDays: '{{count}} dni',
    ppShading: 'Senčenje',
    ppDegradation: 'Degradacija',
    ppFailure: 'Okvara',
    ppSevHigh: 'Visoka',
    ppSevMedium: 'Srednja',
    ppSevLow: 'Nizka',
  },
  hr: {
    spfTitle: 'Pošalji podatke o proizvodnji',
    spfDate: 'Datum',
    spfTz: 'Vremenska zona (UTC odmak)',
    spfCsvUpload: 'Učitaj podatke iz CSV datoteke',
    spfHideInstr: 'Sakrij upute',
    spfFileFormat: 'Format datoteke',
    spfCsvFormatTitle: 'Potreban format CSV datoteke:',
    spfChooseFile: 'Odaberi CSV datoteku…',
    spfShowSolar: 'Prikaži samo dnevne sate (5–20)',
    spfShowAll: 'Prikaži sve sate (0–23)',
    spfHourCol: 'Sat',
    spfProductionCol: 'Proizvodnja (Wh)',
    spfTotal: 'Ukupno',
    spfSubmit: 'Pošalji podatke o proizvodnji',
    spfSubmitting: 'Slanje…',
    spfNoData: 'Unesite podatke o proizvodnji za barem jedan sat.',
    spfSaved: 'Podaci o proizvodnji su spremljeni.',
    spfCsvEmpty: 'Datoteka je prazna.',
    spfCsvErrorsTitle: 'Pogreške u CSV datoteci:',
    spfCsvLoadedPartial: 'Učitano {{loaded}} sati, ali {{skipped}} redaka je preskočeno.',
    spfCsvLoaded: 'Učitani podaci iz CSV: {{loaded}} sati, ukupno {{sum}} Wh',
    ppTitle: 'Analiza performansi',
    ppNoData:
      'Nedovoljno podataka za analizu. Pošaljite stvarne podatke o proizvodnji kako bi sustav mogao analizirati performanse postrojenja.',
    ppDaysAnalyzed: '{{count}} dana analize',
    ppOverallEff: 'Ukupne performanse',
    ppTotalForecast: 'Prognoza ukupno',
    ppTotalProduction: 'Proizvodnja ukupno',
    ppLostProduction: 'Izgubljena proizvodnja',
    ppWorksFine: 'Postrojenje radi ispravno',
    ppWorksFineDesc: 'Nisu otkriveni problemi sa sjenom, degradacijom ni kvarovima.',
    ppHourlyAvg: 'Satni prosjek: prognoza vs stvarno (Wh)',
    ppDailyEff: 'Dnevne performanse (%)',
    ppEffPerHour: 'Performanse po satu',
    ppForecast: 'Prognoza',
    ppActual: 'Stvarno',
    ppEfficiency: 'Performanse',
    pp100ideal: '100% = idealne performanse',
    pp85threshold: '85% = prag degradacije',
    ppDays: '{{count}} dana',
    ppShading: 'Sjenovitost',
    ppDegradation: 'Degradacija',
    ppFailure: 'Kvar',
    ppSevHigh: 'Visok',
    ppSevMedium: 'Srednji',
    ppSevLow: 'Nizak',
  },
  hu: {
    spfTitle: 'Termelési adatok beküldése',
    spfDate: 'Dátum',
    spfTz: 'Időzóna (UTC-eltolás)',
    spfCsvUpload: 'Adatok betöltése CSV-fájlból',
    spfHideInstr: 'Utasítások elrejtése',
    spfFileFormat: 'Fájlformátum',
    spfCsvFormatTitle: 'Szükséges CSV-fájlformátum:',
    spfChooseFile: 'Válasszon CSV-fájlt…',
    spfShowSolar: 'Csak nappali órák megjelenítése (5–20)',
    spfShowAll: 'Összes óra megjelenítése (0–23)',
    spfHourCol: 'Óra',
    spfProductionCol: 'Termelés (Wh)',
    spfTotal: 'Összesen',
    spfSubmit: 'Termelési adatok küldése',
    spfSubmitting: 'Küldés…',
    spfNoData: 'Adjon meg termelési adatokat legalább egy órára.',
    spfSaved: 'A termelési adatok mentésre kerültek.',
    spfCsvEmpty: 'A fájl üres.',
    spfCsvErrorsTitle: 'Hibák a CSV-fájlban:',
    spfCsvLoadedPartial: '{{loaded}} óra betöltve, de {{skipped}} sor kihagyva.',
    spfCsvLoaded: 'CSV-adatok betöltve: {{loaded}} óra, összesen {{sum}} Wh',
    ppTitle: 'Teljesítményelemzés',
    ppNoData:
      'Nincs elég adat az elemzéshez. Adjon meg tényleges termelési adatokat, hogy a rendszer elemezni tudja a telepítés teljesítményét.',
    ppDaysAnalyzed: '{{count}} nap elemezve',
    ppOverallEff: 'Általános teljesítmény',
    ppTotalForecast: 'Előrejelzés összesen',
    ppTotalProduction: 'Termelés összesen',
    ppLostProduction: 'Elveszett termelés',
    ppWorksFine: 'A telepítés megfelelően működik',
    ppWorksFineDesc: 'Nem észleltünk árnyékolási, degradációs vagy meghibásodási problémákat.',
    ppHourlyAvg: 'Óránkénti átlag: előrejelzés vs tényleges (Wh)',
    ppDailyEff: 'Napi teljesítmény (%)',
    ppEffPerHour: 'Teljesítmény óránként',
    ppForecast: 'Előrejelzés',
    ppActual: 'Tényleges',
    ppEfficiency: 'Teljesítmény',
    pp100ideal: '100% = ideális teljesítmény',
    pp85threshold: '85% = degradációs küszöb',
    ppDays: '{{count}} nap',
    ppShading: 'Árnyékolás',
    ppDegradation: 'Degradáció',
    ppFailure: 'Meghibásodás',
    ppSevHigh: 'Magas',
    ppSevMedium: 'Közepes',
    ppSevLow: 'Alacsony',
  },
  ro: {
    spfTitle: 'Trimite datele de producție',
    spfDate: 'Dată',
    spfTz: 'Fus orar (offset UTC)',
    spfCsvUpload: 'Încarcă datele dintr-un fișier CSV',
    spfHideInstr: 'Ascunde instrucțiunile',
    spfFileFormat: 'Format fișier',
    spfCsvFormatTitle: 'Format de fișier CSV necesar:',
    spfChooseFile: 'Alege un fișier CSV…',
    spfShowSolar: 'Afișează doar orele de zi (5–20)',
    spfShowAll: 'Afișează toate orele (0–23)',
    spfHourCol: 'Oră',
    spfProductionCol: 'Producție (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Trimite datele de producție',
    spfSubmitting: 'Se trimite…',
    spfNoData: 'Introduceți datele de producție pentru cel puțin o oră.',
    spfSaved: 'Datele de producție au fost salvate.',
    spfCsvEmpty: 'Fișierul este gol.',
    spfCsvErrorsTitle: 'Erori în fișierul CSV:',
    spfCsvLoadedPartial: 'Încărcate {{loaded}} ore, dar {{skipped}} rânduri au fost omise.',
    spfCsvLoaded: 'Date CSV încărcate: {{loaded}} ore, total {{sum}} Wh',
    ppTitle: 'Analiză de performanță',
    ppNoData:
      'Date insuficiente pentru analiză. Trimiteți date de producție reale pentru ca sistemul să poată analiza performanța instalației.',
    ppDaysAnalyzed: '{{count}} zile analizate',
    ppOverallEff: 'Performanță generală',
    ppTotalForecast: 'Prognoză totală',
    ppTotalProduction: 'Producție totală',
    ppLostProduction: 'Producție pierdută',
    ppWorksFine: 'Instalația funcționează corect',
    ppWorksFineDesc: 'Nu au fost detectate probleme de umbrire, degradare sau defecțiuni.',
    ppHourlyAvg: 'Media orară: prognoză vs real (Wh)',
    ppDailyEff: 'Performanță zilnică (%)',
    ppEffPerHour: 'Performanță pe oră',
    ppForecast: 'Prognoză',
    ppActual: 'Real',
    ppEfficiency: 'Performanță',
    pp100ideal: '100% = performanță ideală',
    pp85threshold: '85% = prag de degradare',
    ppDays: '{{count}} zile',
    ppShading: 'Umbrire',
    ppDegradation: 'Degradare',
    ppFailure: 'Defecțiune',
    ppSevHigh: 'Ridicat',
    ppSevMedium: 'Mediu',
    ppSevLow: 'Scăzut',
  },
  bg: {
    spfTitle: 'Изпрати данни за производството',
    spfDate: 'Дата',
    spfTz: 'Часова зона (UTC отместване)',
    spfCsvUpload: 'Зареди данни от CSV файл',
    spfHideInstr: 'Скрий инструкциите',
    spfFileFormat: 'Формат на файла',
    spfCsvFormatTitle: 'Необходим формат на CSV файла:',
    spfChooseFile: 'Изберете CSV файл…',
    spfShowSolar: 'Покажи само дневните часове (5–20)',
    spfShowAll: 'Покажи всички часове (0–23)',
    spfHourCol: 'Час',
    spfProductionCol: 'Производство (Wh)',
    spfTotal: 'Общо',
    spfSubmit: 'Изпрати данни за производството',
    spfSubmitting: 'Изпращане…',
    spfNoData: 'Въведете данни за производството за поне един час.',
    spfSaved: 'Данните за производството са запазени.',
    spfCsvEmpty: 'Файлът е празен.',
    spfCsvErrorsTitle: 'Грешки в CSV файла:',
    spfCsvLoadedPartial: 'Заредени {{loaded}} часа, но {{skipped}} реда са пропуснати.',
    spfCsvLoaded: 'Заредени данни от CSV: {{loaded}} часа, общо {{sum}} Wh',
    ppTitle: 'Анализ на производителността',
    ppNoData:
      'Недостатъчно данни за анализ. Изпратете реални данни за производството, за да може системата да анализира производителността на инсталацията.',
    ppDaysAnalyzed: '{{count}} дни анализ',
    ppOverallEff: 'Обща производителност',
    ppTotalForecast: 'Прогноза общо',
    ppTotalProduction: 'Производство общо',
    ppLostProduction: 'Загубено производство',
    ppWorksFine: 'Инсталацията работи правилно',
    ppWorksFineDesc: 'Не са открити проблеми със засенчване, деградация или повреди.',
    ppHourlyAvg: 'Средно почасово: прогноза vs реално (Wh)',
    ppDailyEff: 'Дневна производителност (%)',
    ppEffPerHour: 'Производителност на час',
    ppForecast: 'Прогноза',
    ppActual: 'Реално',
    ppEfficiency: 'Производителност',
    pp100ideal: '100% = идеална производителност',
    pp85threshold: '85% = праг на деградация',
    ppDays: '{{count}} дни',
    ppShading: 'Засенчване',
    ppDegradation: 'Деградация',
    ppFailure: 'Повреда',
    ppSevHigh: 'Висока',
    ppSevMedium: 'Средна',
    ppSevLow: 'Ниска',
  },
  el: {
    spfTitle: 'Υποβολή δεδομένων παραγωγής',
    spfDate: 'Ημερομηνία',
    spfTz: 'Ζώνη ώρας (μετατόπιση UTC)',
    spfCsvUpload: 'Φόρτωση δεδομένων από αρχείο CSV',
    spfHideInstr: 'Απόκρυψη οδηγιών',
    spfFileFormat: 'Μορφή αρχείου',
    spfCsvFormatTitle: 'Απαιτούμενη μορφή αρχείου CSV:',
    spfChooseFile: 'Επιλέξτε αρχείο CSV…',
    spfShowSolar: 'Εμφάνιση μόνο ωρών ημέρας (5–20)',
    spfShowAll: 'Εμφάνιση όλων των ωρών (0–23)',
    spfHourCol: 'Ώρα',
    spfProductionCol: 'Παραγωγή (Wh)',
    spfTotal: 'Σύνολο',
    spfSubmit: 'Υποβολή δεδομένων παραγωγής',
    spfSubmitting: 'Υποβολή…',
    spfNoData: 'Εισαγάγετε δεδομένα παραγωγής για τουλάχιστον μία ώρα.',
    spfSaved: 'Τα δεδομένα παραγωγής αποθηκεύτηκαν.',
    spfCsvEmpty: 'Το αρχείο είναι κενό.',
    spfCsvErrorsTitle: 'Σφάλματα στο αρχείο CSV:',
    spfCsvLoadedPartial: 'Φορτώθηκαν {{loaded}} ώρες, αλλά {{skipped}} γραμμές παραλείφθηκαν.',
    spfCsvLoaded: 'Φορτώθηκαν δεδομένα CSV: {{loaded}} ώρες, σύνολο {{sum}} Wh',
    ppTitle: 'Ανάλυση απόδοσης',
    ppNoData:
      'Ανεπαρκή δεδομένα για ανάλυση. Υποβάλετε πραγματικά δεδομένα παραγωγής ώστε το σύστημα να μπορεί να αναλύσει την απόδοση της εγκατάστασης.',
    ppDaysAnalyzed: '{{count}} ημέρες αναλύθηκαν',
    ppOverallEff: 'Συνολική απόδοση',
    ppTotalForecast: 'Σύνολο πρόβλεψης',
    ppTotalProduction: 'Σύνολο παραγωγής',
    ppLostProduction: 'Χαμένη παραγωγή',
    ppWorksFine: 'Η εγκατάσταση λειτουργεί σωστά',
    ppWorksFineDesc: 'Δεν εντοπίστηκαν προβλήματα σκίασης, υποβάθμισης ή βλαβών.',
    ppHourlyAvg: 'Ωριαίος μέσος όρος: πρόβλεψη vs πραγματικό (Wh)',
    ppDailyEff: 'Ημερήσια απόδοση (%)',
    ppEffPerHour: 'Απόδοση ανά ώρα',
    ppForecast: 'Πρόβλεψη',
    ppActual: 'Πραγματικό',
    ppEfficiency: 'Απόδοση',
    pp100ideal: '100% = ιδανική απόδοση',
    pp85threshold: '85% = όριο υποβάθμισης',
    ppDays: '{{count}} ημέρες',
    ppShading: 'Σκίαση',
    ppDegradation: 'Υποβάθμιση',
    ppFailure: 'Βλάβη',
    ppSevHigh: 'Υψηλή',
    ppSevMedium: 'Μέτρια',
    ppSevLow: 'Χαμηλή',
  },
  et: {
    spfTitle: 'Esita toodanguandmed',
    spfDate: 'Kuupäev',
    spfTz: 'Ajavöönd (UTC nihe)',
    spfCsvUpload: 'Laadi andmed CSV-failist',
    spfHideInstr: 'Peida juhised',
    spfFileFormat: 'Failivorming',
    spfCsvFormatTitle: 'Nõutav CSV-faili vorming:',
    spfChooseFile: 'Vali CSV-fail…',
    spfShowSolar: 'Näita ainult päevavalgustunde (5–20)',
    spfShowAll: 'Näita kõiki tunde (0–23)',
    spfHourCol: 'Tund',
    spfProductionCol: 'Toodang (Wh)',
    spfTotal: 'Kokku',
    spfSubmit: 'Saada toodanguandmed',
    spfSubmitting: 'Saatmine…',
    spfNoData: 'Sisestage toodanguandmed vähemalt ühe tunni kohta.',
    spfSaved: 'Toodanguandmed on salvestatud.',
    spfCsvEmpty: 'Fail on tühi.',
    spfCsvErrorsTitle: 'Vead CSV-failis:',
    spfCsvLoadedPartial: 'Laaditud {{loaded}} tundi, kuid {{skipped}} rida jäeti vahele.',
    spfCsvLoaded: 'CSV-andmed laaditud: {{loaded}} tundi, kokku {{sum}} Wh',
    ppTitle: 'Jõudluse analüüs',
    ppNoData:
      'Analüüsiks pole piisavalt andmeid. Esitage tegelikud toodanguandmed, et süsteem saaks paigaldise jõudlust analüüsida.',
    ppDaysAnalyzed: '{{count}} päeva analüüsitud',
    ppOverallEff: 'Üldine jõudlus',
    ppTotalForecast: 'Prognoos kokku',
    ppTotalProduction: 'Toodang kokku',
    ppLostProduction: 'Kaotatud toodang',
    ppWorksFine: 'Paigaldis töötab korrektselt',
    ppWorksFineDesc: 'Varjutamise, lagunemise või rikete probleeme ei tuvastatud.',
    ppHourlyAvg: 'Tunni keskmine: prognoos vs tegelik (Wh)',
    ppDailyEff: 'Päevane jõudlus (%)',
    ppEffPerHour: 'Jõudlus tunnis',
    ppForecast: 'Prognoos',
    ppActual: 'Tegelik',
    ppEfficiency: 'Jõudlus',
    pp100ideal: '100% = ideaalne jõudlus',
    pp85threshold: '85% = lagunemise lävi',
    ppDays: '{{count}} päeva',
    ppShading: 'Varjutamine',
    ppDegradation: 'Lagunemine',
    ppFailure: 'Rike',
    ppSevHigh: 'Kõrge',
    ppSevMedium: 'Keskmine',
    ppSevLow: 'Madal',
  },
  lv: {
    spfTitle: 'Iesniegt ražošanas datus',
    spfDate: 'Datums',
    spfTz: 'Laika josla (UTC nobīde)',
    spfCsvUpload: 'Ielādēt datus no CSV faila',
    spfHideInstr: 'Slēpt instrukcijas',
    spfFileFormat: 'Faila formāts',
    spfCsvFormatTitle: 'Nepieciešamais CSV faila formāts:',
    spfChooseFile: 'Izvēlieties CSV failu…',
    spfShowSolar: 'Rādīt tikai dienasgaismas stundas (5–20)',
    spfShowAll: 'Rādīt visas stundas (0–23)',
    spfHourCol: 'Stunda',
    spfProductionCol: 'Ražošana (Wh)',
    spfTotal: 'Kopā',
    spfSubmit: 'Sūtīt ražošanas datus',
    spfSubmitting: 'Sūta…',
    spfNoData: 'Ievadiet ražošanas datus vismaz vienai stundai.',
    spfSaved: 'Ražošanas dati ir saglabāti.',
    spfCsvEmpty: 'Fails ir tukšs.',
    spfCsvErrorsTitle: 'Kļūdas CSV failā:',
    spfCsvLoadedPartial: 'Ielādētas {{loaded}} stundas, bet {{skipped}} rindas tika izlaistas.',
    spfCsvLoaded: 'Ielādēti CSV dati: {{loaded}} stundas, kopā {{sum}} Wh',
    ppTitle: 'Veiktspējas analīze',
    ppNoData:
      'Analīzei nepietiek datu. Iesniedziet faktiskos ražošanas datus, lai sistēma varētu analizēt iekārtas veiktspēju.',
    ppDaysAnalyzed: '{{count}} dienas analizētas',
    ppOverallEff: 'Kopējā veiktspēja',
    ppTotalForecast: 'Prognoze kopā',
    ppTotalProduction: 'Ražošana kopā',
    ppLostProduction: 'Zaudētā ražošana',
    ppWorksFine: 'Iekārta darbojas pareizi',
    ppWorksFineDesc: 'Nav konstatētas ēnošanas, degradācijas vai bojājumu problēmas.',
    ppHourlyAvg: 'Stundas vidējais: prognoze vs faktiskais (Wh)',
    ppDailyEff: 'Dienas veiktspēja (%)',
    ppEffPerHour: 'Veiktspēja stundā',
    ppForecast: 'Prognoze',
    ppActual: 'Faktiskais',
    ppEfficiency: 'Veiktspēja',
    pp100ideal: '100% = ideāla veiktspēja',
    pp85threshold: '85% = degradācijas slieksnis',
    ppDays: '{{count}} dienas',
    ppShading: 'Ēnošana',
    ppDegradation: 'Degradācija',
    ppFailure: 'Bojājums',
    ppSevHigh: 'Augsts',
    ppSevMedium: 'Vidējs',
    ppSevLow: 'Zems',
  },
  lt: {
    spfTitle: 'Pateikti gamybos duomenis',
    spfDate: 'Data',
    spfTz: 'Laiko juosta (UTC poslinkis)',
    spfCsvUpload: 'Įkelti duomenis iš CSV failo',
    spfHideInstr: 'Slėpti instrukcijas',
    spfFileFormat: 'Failo formatas',
    spfCsvFormatTitle: 'Reikalingas CSV failo formatas:',
    spfChooseFile: 'Pasirinkite CSV failą…',
    spfShowSolar: 'Rodyti tik dienos šviesos valandas (5–20)',
    spfShowAll: 'Rodyti visas valandas (0–23)',
    spfHourCol: 'Val.',
    spfProductionCol: 'Gamyba (Wh)',
    spfTotal: 'Iš viso',
    spfSubmit: 'Siųsti gamybos duomenis',
    spfSubmitting: 'Siunčiama…',
    spfNoData: 'Įveskite gamybos duomenis bent vienai valandai.',
    spfSaved: 'Gamybos duomenys išsaugoti.',
    spfCsvEmpty: 'Failas tuščias.',
    spfCsvErrorsTitle: 'Klaidos CSV faile:',
    spfCsvLoadedPartial: 'Įkelta {{loaded}} valandų, bet {{skipped}} eilutės buvo praleistos.',
    spfCsvLoaded: 'Įkelti CSV duomenys: {{loaded}} valandų, iš viso {{sum}} Wh',
    ppTitle: 'Našumo analizė',
    ppNoData:
      'Nepakanka duomenų analizei. Pateikite faktinius gamybos duomenis, kad sistema galėtų analizuoti įrenginio našumą.',
    ppDaysAnalyzed: '{{count}} dienos analizuotos',
    ppOverallEff: 'Bendras našumas',
    ppTotalForecast: 'Prognozė iš viso',
    ppTotalProduction: 'Gamyba iš viso',
    ppLostProduction: 'Prarasta gamyba',
    ppWorksFine: 'Įrenginys veikia tinkamai',
    ppWorksFineDesc: 'Šešėliavimo, degradacijos ar gedimų problemų neaptikta.',
    ppHourlyAvg: 'Valandos vidurkis: prognozė vs faktinis (Wh)',
    ppDailyEff: 'Dienos našumas (%)',
    ppEffPerHour: 'Našumas per valandą',
    ppForecast: 'Prognozė',
    ppActual: 'Faktinis',
    ppEfficiency: 'Našumas',
    pp100ideal: '100% = idealus našumas',
    pp85threshold: '85% = degradacijos slenkstis',
    ppDays: '{{count}} dienos',
    ppShading: 'Šešėliavimas',
    ppDegradation: 'Degradacija',
    ppFailure: 'Gedimas',
    ppSevHigh: 'Aukštas',
    ppSevMedium: 'Vidutinis',
    ppSevLow: 'Žemas',
  },
  mt: {
    spfTitle: 'Issottometti dejta tal-produzzjoni',
    spfDate: 'Data',
    spfTz: 'Żona tal-ħin (offset UTC)',
    spfCsvUpload: 'Iċċarġja d-dejta minn fajl CSV',
    spfHideInstr: 'Aħbi l-istruzzjonijiet',
    spfFileFormat: 'Format tal-fajl',
    spfCsvFormatTitle: 'Format tal-fajl CSV meħtieġ:',
    spfChooseFile: 'Agħżel fajl CSV…',
    spfShowSolar: 'Uri biss is-sigħat tad-dawl tax-xemx (5–20)',
    spfShowAll: 'Uri s-sigħat kollha (0–23)',
    spfHourCol: 'Siegħa',
    spfProductionCol: 'Produzzjoni (Wh)',
    spfTotal: 'Total',
    spfSubmit: 'Ibgħat id-dejta tal-produzzjoni',
    spfSubmitting: 'Qed jibgħat…',
    spfNoData: 'Daħħal dejta tal-produzzjoni għal mill-inqas siegħa waħda.',
    spfSaved: 'Id-dejta tal-produzzjoni ġiet issejvjata.',
    spfCsvEmpty: 'Il-fajl huwa vojt.',
    spfCsvErrorsTitle: 'Żbalji fil-fajl CSV:',
    spfCsvLoadedPartial: 'Iċċarġjati {{loaded}} sigħat, iżda {{skipped}} ringieli ġew maqbuża.',
    spfCsvLoaded: 'Dejta CSV iċċarġjata: {{loaded}} sigħat, total {{sum}} Wh',
    ppTitle: 'Analiżi tal-prestazzjoni',
    ppNoData:
      "Dejta insuffiċjenti għall-analiżi. Issottometti dejta tal-produzzjoni reali biex is-sistema tkun tista' tanalizza l-prestazzjoni tal-installazzjoni.",
    ppDaysAnalyzed: '{{count}} jiem analizzati',
    ppOverallEff: 'Prestazzjoni ġenerali',
    ppTotalForecast: 'Previżjoni totali',
    ppTotalProduction: 'Produzzjoni totali',
    ppLostProduction: 'Produzzjoni mitlufa',
    ppWorksFine: "L-installazzjoni taħdem b'mod korrett",
    ppWorksFineDesc: "L-ebda problema ta' dell, degradazzjoni jew ħsarat ma ġiet identifikata.",
    ppHourlyAvg: 'Medja kull siegħa: previżjoni vs reali (Wh)',
    ppDailyEff: "Prestazzjoni ta' kuljum (%)",
    ppEffPerHour: 'Prestazzjoni kull siegħa',
    ppForecast: 'Previżjoni',
    ppActual: 'Reali',
    ppEfficiency: 'Prestazzjoni',
    pp100ideal: '100% = prestazzjoni ideali',
    pp85threshold: "85% = limitu ta' degradazzjoni",
    ppDays: '{{count}} jiem',
    ppShading: 'Dell',
    ppDegradation: 'Degradazzjoni',
    ppFailure: 'Ħsara',
    ppSevHigh: 'Għoli',
    ppSevMedium: 'Medju',
    ppSevLow: 'Baxx',
  },
  ga: {
    spfTitle: 'Cuir isteach sonraí táirgthe',
    spfDate: 'Dáta',
    spfTz: 'Crios ama (fritháireamh UTC)',
    spfCsvUpload: 'Lódáil sonraí ó chomhad CSV',
    spfHideInstr: 'Folaigh treoracha',
    spfFileFormat: 'Formáid chomhaid',
    spfCsvFormatTitle: 'Formáid chomhaid CSV riachtanach:',
    spfChooseFile: 'Roghnaigh comhad CSV…',
    spfShowSolar: 'Taispeáin uaireanta solas an lae amháin (5–20)',
    spfShowAll: 'Taispeáin gach uair (0–23)',
    spfHourCol: 'Uair',
    spfProductionCol: 'Táirgeadh (Wh)',
    spfTotal: 'Iomlán',
    spfSubmit: 'Seol sonraí táirgthe',
    spfSubmitting: 'Á sheoladh…',
    spfNoData: "Cuir isteach sonraí táirgthe d'uair amháin ar a laghad.",
    spfSaved: 'Sábháladh na sonraí táirgthe.',
    spfCsvEmpty: 'Tá an comhad folamh.',
    spfCsvErrorsTitle: 'Earráidí sa chomhad CSV:',
    spfCsvLoadedPartial: 'Lódáladh {{loaded}} uair, ach scipeáladh {{skipped}} ró.',
    spfCsvLoaded: 'Sonraí CSV lódáilte: {{loaded}} uair, iomlán {{sum}} Wh',
    ppTitle: 'Anailís feidhmíochta',
    ppNoData:
      'Níl go leor sonraí ann le haghaidh anailíse. Cuir isteach sonraí táirgthe iarbhír ionas gur féidir leis an gcóras feidhmíocht na suiteála a anailísiú.',
    ppDaysAnalyzed: '{{count}} lá anailísithe',
    ppOverallEff: 'Feidhmíocht iomlán',
    ppTotalForecast: 'Réamhaisnéis iomlán',
    ppTotalProduction: 'Táirgeadh iomlán',
    ppLostProduction: 'Táirgeadh caillte',
    ppWorksFine: 'Oibríonn an tsuiteáil i gceart',
    ppWorksFineDesc: 'Níor braitheadh aon fhadhbanna scáthaithe, díghrádaithe ná teipe.',
    ppHourlyAvg: 'Meán uaire: réamhaisnéis vs iarbhír (Wh)',
    ppDailyEff: 'Feidhmíocht laethúil (%)',
    ppEffPerHour: 'Feidhmíocht in aghaidh na huaire',
    ppForecast: 'Réamhaisnéis',
    ppActual: 'Iarbhír',
    ppEfficiency: 'Feidhmíocht',
    pp100ideal: '100% = feidhmíocht idéalach',
    pp85threshold: '85% = tairseach díghrádaithe',
    ppDays: '{{count}} lá',
    ppShading: 'Scáthú',
    ppDegradation: 'Díghrádú',
    ppFailure: 'Teip',
    ppSevHigh: 'Ard',
    ppSevMedium: 'Meánach',
    ppSevLow: 'Íseal',
  },
  no: {
    spfTitle: 'Send inn produksjonsdata',
    spfDate: 'Dato',
    spfTz: 'Tidssone (UTC-offset)',
    spfCsvUpload: 'Last inn data fra en CSV-fil',
    spfHideInstr: 'Skjul instruksjoner',
    spfFileFormat: 'Filformat',
    spfCsvFormatTitle: 'Påkrevd CSV-filformat:',
    spfChooseFile: 'Velg en CSV-fil…',
    spfShowSolar: 'Vis bare dagslystimer (5–20)',
    spfShowAll: 'Vis alle timer (0–23)',
    spfHourCol: 'Time',
    spfProductionCol: 'Produksjon (Wh)',
    spfTotal: 'Totalt',
    spfSubmit: 'Send produksjonsdata',
    spfSubmitting: 'Sender…',
    spfNoData: 'Skriv inn produksjonsdata for minst én time.',
    spfSaved: 'Produksjonsdataene er lagret.',
    spfCsvEmpty: 'Filen er tom.',
    spfCsvErrorsTitle: 'Feil i CSV-filen:',
    spfCsvLoadedPartial: '{{loaded}} timer lastet inn, men {{skipped}} rader ble hoppet over.',
    spfCsvLoaded: 'CSV-data lastet inn: {{loaded}} timer, totalt {{sum}} Wh',
    ppTitle: 'Ytelsesanalyse',
    ppNoData:
      'Ikke nok data til analyse. Send inn faktiske produksjonsdata slik at systemet kan analysere anleggets ytelse.',
    ppDaysAnalyzed: '{{count}} dager analysert',
    ppOverallEff: 'Samlet ytelse',
    ppTotalForecast: 'Prognose totalt',
    ppTotalProduction: 'Produksjon totalt',
    ppLostProduction: 'Tapt produksjon',
    ppWorksFine: 'Anlegget fungerer riktig',
    ppWorksFineDesc: 'Ingen problemer med skygge, degradering eller feil oppdaget.',
    ppHourlyAvg: 'Timegjennomsnitt: prognose vs faktisk (Wh)',
    ppDailyEff: 'Daglig ytelse (%)',
    ppEffPerHour: 'Ytelse per time',
    ppForecast: 'Prognose',
    ppActual: 'Faktisk',
    ppEfficiency: 'Ytelse',
    pp100ideal: '100% = ideell ytelse',
    pp85threshold: '85% = degraderingsterskel',
    ppDays: '{{count}} dager',
    ppShading: 'Skygge',
    ppDegradation: 'Degradering',
    ppFailure: 'Feil',
    ppSevHigh: 'Høy',
    ppSevMedium: 'Middels',
    ppSevLow: 'Lav',
  },
  is: {
    spfTitle: 'Senda framleiðslugögn',
    spfDate: 'Dagsetning',
    spfTz: 'Tímabelti (UTC-frávik)',
    spfCsvUpload: 'Hlaða gögnum úr CSV-skrá',
    spfHideInstr: 'Fela leiðbeiningar',
    spfFileFormat: 'Skráarsnið',
    spfCsvFormatTitle: 'Nauðsynlegt CSV-skráarsnið:',
    spfChooseFile: 'Veldu CSV-skrá…',
    spfShowSolar: 'Sýna aðeins dagsljóstíma (5–20)',
    spfShowAll: 'Sýna alla tíma (0–23)',
    spfHourCol: 'Klst.',
    spfProductionCol: 'Framleiðsla (Wh)',
    spfTotal: 'Samtals',
    spfSubmit: 'Senda framleiðslugögn',
    spfSubmitting: 'Sendir…',
    spfNoData: 'Sláðu inn framleiðslugögn fyrir að minnsta kosti einn klukkutíma.',
    spfSaved: 'Framleiðslugögnin hafa verið vistuð.',
    spfCsvEmpty: 'Skráin er tóm.',
    spfCsvErrorsTitle: 'Villur í CSV-skránni:',
    spfCsvLoadedPartial: '{{loaded}} klukkutímar hlaðnir, en {{skipped}} línum var sleppt.',
    spfCsvLoaded: 'CSV-gögn hlaðin: {{loaded}} klukkutímar, samtals {{sum}} Wh',
    ppTitle: 'Frammistöðugreining',
    ppNoData:
      'Ekki nóg gögn til greiningar. Sendu raunveruleg framleiðslugögn svo kerfið geti greint frammistöðu uppsetningarinnar.',
    ppDaysAnalyzed: '{{count}} dagar greindir',
    ppOverallEff: 'Heildarframmistaða',
    ppTotalForecast: 'Spá samtals',
    ppTotalProduction: 'Framleiðsla samtals',
    ppLostProduction: 'Töpuð framleiðsla',
    ppWorksFine: 'Uppsetningin virkar rétt',
    ppWorksFineDesc: 'Engin vandamál með skugga, niðurbrot eða bilanir greind.',
    ppHourlyAvg: 'Klukkutímameðaltal: spá vs raunveruleiki (Wh)',
    ppDailyEff: 'Dagleg frammistaða (%)',
    ppEffPerHour: 'Frammistaða á klukkutíma',
    ppForecast: 'Spá',
    ppActual: 'Raunveruleiki',
    ppEfficiency: 'Frammistaða',
    pp100ideal: '100% = fullkomin frammistaða',
    pp85threshold: '85% = niðurbrotsmörk',
    ppDays: '{{count}} dagar',
    ppShading: 'Skuggi',
    ppDegradation: 'Niðurbrot',
    ppFailure: 'Bilun',
    ppSevHigh: 'Hátt',
    ppSevMedium: 'Miðlungs',
    ppSevLow: 'Lágt',
  },
  uk: {
    spfTitle: 'Надіслати дані виробництва',
    spfDate: 'Дата',
    spfTz: 'Часовий пояс (зсув UTC)',
    spfCsvUpload: 'Завантажити дані з файлу CSV',
    spfHideInstr: 'Сховати інструкції',
    spfFileFormat: 'Формат файлу',
    spfCsvFormatTitle: 'Необхідний формат файлу CSV:',
    spfChooseFile: 'Виберіть файл CSV…',
    spfShowSolar: 'Показати лише денні години (5–20)',
    spfShowAll: 'Показати всі години (0–23)',
    spfHourCol: 'Год.',
    spfProductionCol: 'Виробництво (Wh)',
    spfTotal: 'Усього',
    spfSubmit: 'Надіслати дані виробництва',
    spfSubmitting: 'Надсилання…',
    spfNoData: 'Введіть дані виробництва принаймні для однієї години.',
    spfSaved: 'Дані виробництва збережено.',
    spfCsvEmpty: 'Файл порожній.',
    spfCsvErrorsTitle: 'Помилки у файлі CSV:',
    spfCsvLoadedPartial: 'Завантажено {{loaded}} годин, але {{skipped}} рядків пропущено.',
    spfCsvLoaded: 'Завантажено дані з CSV: {{loaded}} годин, усього {{sum}} Wh',
    ppTitle: 'Аналіз продуктивності',
    ppNoData:
      'Недостатньо даних для аналізу. Надішліть фактичні дані виробництва, щоб система могла проаналізувати продуктивність установки.',
    ppDaysAnalyzed: '{{count}} днів аналізу',
    ppOverallEff: 'Загальна продуктивність',
    ppTotalForecast: 'Прогноз загалом',
    ppTotalProduction: 'Виробництво загалом',
    ppLostProduction: 'Втрачене виробництво',
    ppWorksFine: 'Установка працює правильно',
    ppWorksFineDesc: 'Проблем із затіненням, деградацією чи несправностями не виявлено.',
    ppHourlyAvg: 'Погодинне середнє: прогноз vs фактичне (Wh)',
    ppDailyEff: 'Денна продуктивність (%)',
    ppEffPerHour: 'Продуктивність за годину',
    ppForecast: 'Прогноз',
    ppActual: 'Фактичне',
    ppEfficiency: 'Продуктивність',
    pp100ideal: '100% = ідеальна продуктивність',
    pp85threshold: '85% = поріг деградації',
    ppDays: '{{count}} днів',
    ppShading: 'Затінення',
    ppDegradation: 'Деградація',
    ppFailure: 'Несправність',
    ppSevHigh: 'Високий',
    ppSevMedium: 'Середній',
    ppSevLow: 'Низький',
  },
  sr: {
    spfTitle: 'Pošalji podatke o proizvodnji',
    spfDate: 'Datum',
    spfTz: 'Vremenska zona (UTC pomak)',
    spfCsvUpload: 'Učitaj podatke iz CSV datoteke',
    spfHideInstr: 'Sakrij uputstva',
    spfFileFormat: 'Format datoteke',
    spfCsvFormatTitle: 'Potreban format CSV datoteke:',
    spfChooseFile: 'Izaberi CSV datoteku…',
    spfShowSolar: 'Prikaži samo dnevne sate (5–20)',
    spfShowAll: 'Prikaži sve sate (0–23)',
    spfHourCol: 'Sat',
    spfProductionCol: 'Proizvodnja (Wh)',
    spfTotal: 'Ukupno',
    spfSubmit: 'Pošalji podatke o proizvodnji',
    spfSubmitting: 'Slanje…',
    spfNoData: 'Unesite podatke o proizvodnji za najmanje jedan sat.',
    spfSaved: 'Podaci o proizvodnji su sačuvani.',
    spfCsvEmpty: 'Datoteka je prazna.',
    spfCsvErrorsTitle: 'Greške u CSV datoteci:',
    spfCsvLoadedPartial: 'Učitano {{loaded}} sati, ali {{skipped}} redova je preskočeno.',
    spfCsvLoaded: 'Učitani podaci iz CSV: {{loaded}} sati, ukupno {{sum}} Wh',
    ppTitle: 'Analiza performansi',
    ppNoData:
      'Nedovoljno podataka za analizu. Pošaljite stvarne podatke o proizvodnji kako bi sistem mogao da analizira performanse postrojenja.',
    ppDaysAnalyzed: '{{count}} dana analize',
    ppOverallEff: 'Ukupne performanse',
    ppTotalForecast: 'Prognoza ukupno',
    ppTotalProduction: 'Proizvodnja ukupno',
    ppLostProduction: 'Izgubljena proizvodnja',
    ppWorksFine: 'Postrojenje radi ispravno',
    ppWorksFineDesc: 'Nisu otkriveni problemi sa senkom, degradacijom ni kvarovima.',
    ppHourlyAvg: 'Satni prosek: prognoza vs stvarno (Wh)',
    ppDailyEff: 'Dnevne performanse (%)',
    ppEffPerHour: 'Performanse po satu',
    ppForecast: 'Prognoza',
    ppActual: 'Stvarno',
    ppEfficiency: 'Performanse',
    pp100ideal: '100% = idealne performanse',
    pp85threshold: '85% = prag degradacije',
    ppDays: '{{count}} dana',
    ppShading: 'Senka',
    ppDegradation: 'Degradacija',
    ppFailure: 'Kvar',
    ppSevHigh: 'Visok',
    ppSevMedium: 'Srednji',
    ppSevLow: 'Nizak',
  },
  sq: {
    spfTitle: 'Dërgo të dhënat e prodhimit',
    spfDate: 'Data',
    spfTz: 'Zona orare (offset UTC)',
    spfCsvUpload: 'Ngarko të dhënat nga një skedar CSV',
    spfHideInstr: 'Fshih udhëzimet',
    spfFileFormat: 'Formati i skedarit',
    spfCsvFormatTitle: 'Formati i kërkuar i skedarit CSV:',
    spfChooseFile: 'Zgjidh një skedar CSV…',
    spfShowSolar: 'Shfaq vetëm orët e ditës (5–20)',
    spfShowAll: 'Shfaq të gjitha orët (0–23)',
    spfHourCol: 'Ora',
    spfProductionCol: 'Prodhimi (Wh)',
    spfTotal: 'Totali',
    spfSubmit: 'Dërgo të dhënat e prodhimit',
    spfSubmitting: 'Po dërgohet…',
    spfNoData: 'Vendosni të dhënat e prodhimit për të paktën një orë.',
    spfSaved: 'Të dhënat e prodhimit u ruajtën.',
    spfCsvEmpty: 'Skedari është bosh.',
    spfCsvErrorsTitle: 'Gabime në skedarin CSV:',
    spfCsvLoadedPartial: 'U ngarkuan {{loaded}} orë, por {{skipped}} rreshta u anashkaluan.',
    spfCsvLoaded: 'Të dhënat CSV u ngarkuan: {{loaded}} orë, totali {{sum}} Wh',
    ppTitle: 'Analiza e performancës',
    ppNoData:
      'Të dhëna të pamjaftueshme për analizë. Dërgoni të dhëna reale të prodhimit që sistemi të mund të analizojë performancën e instalimit.',
    ppDaysAnalyzed: '{{count}} ditë të analizuara',
    ppOverallEff: 'Performanca e përgjithshme',
    ppTotalForecast: 'Parashikimi total',
    ppTotalProduction: 'Prodhimi total',
    ppLostProduction: 'Prodhimi i humbur',
    ppWorksFine: 'Instalimi punon saktë',
    ppWorksFineDesc: 'Nuk u zbuluan probleme me hijëzim, degradim ose dështime.',
    ppHourlyAvg: 'Mesatarja orare: parashikim vs reale (Wh)',
    ppDailyEff: 'Performanca ditore (%)',
    ppEffPerHour: 'Performanca për orë',
    ppForecast: 'Parashikim',
    ppActual: 'Reale',
    ppEfficiency: 'Performanca',
    pp100ideal: '100% = performancë ideale',
    pp85threshold: '85% = prag i degradimit',
    ppDays: '{{count}} ditë',
    ppShading: 'Hijëzim',
    ppDegradation: 'Degradim',
    ppFailure: 'Dështim',
    ppSevHigh: 'I lartë',
    ppSevMedium: 'Mesatar',
    ppSevLow: 'I ulët',
  },
  mk: {
    spfTitle: 'Испрати податоци за производството',
    spfDate: 'Датум',
    spfTz: 'Временска зона (UTC поместување)',
    spfCsvUpload: 'Вчитај податоци од CSV датотека',
    spfHideInstr: 'Скриј инструкции',
    spfFileFormat: 'Формат на датотеката',
    spfCsvFormatTitle: 'Потребен формат на CSV датотеката:',
    spfChooseFile: 'Изберете CSV датотека…',
    spfShowSolar: 'Прикажи само дневни часови (5–20)',
    spfShowAll: 'Прикажи ги сите часови (0–23)',
    spfHourCol: 'Час',
    spfProductionCol: 'Производство (Wh)',
    spfTotal: 'Вкупно',
    spfSubmit: 'Испрати податоци за производството',
    spfSubmitting: 'Се испраќа…',
    spfNoData: 'Внесете податоци за производството за најмалку еден час.',
    spfSaved: 'Податоците за производството се зачувани.',
    spfCsvEmpty: 'Датотеката е празна.',
    spfCsvErrorsTitle: 'Грешки во CSV датотеката:',
    spfCsvLoadedPartial: 'Вчитани {{loaded}} часови, но {{skipped}} редови беа прескокнати.',
    spfCsvLoaded: 'Вчитани податоци од CSV: {{loaded}} часови, вкупно {{sum}} Wh',
    ppTitle: 'Анализа на перформанси',
    ppNoData:
      'Недоволно податоци за анализа. Испратете реални податоци за производството за да може системот да ги анализира перформансите на инсталацијата.',
    ppDaysAnalyzed: '{{count}} дена анализа',
    ppOverallEff: 'Вкупни перформанси',
    ppTotalForecast: 'Прогноза вкупно',
    ppTotalProduction: 'Производство вкупно',
    ppLostProduction: 'Изгубено производство',
    ppWorksFine: 'Инсталацијата работи правилно',
    ppWorksFineDesc: 'Не се откриени проблеми со засенчување, деградација ниту дефекти.',
    ppHourlyAvg: 'Часовен просек: прогноза vs реално (Wh)',
    ppDailyEff: 'Дневни перформанси (%)',
    ppEffPerHour: 'Перформанси по час',
    ppForecast: 'Прогноза',
    ppActual: 'Реално',
    ppEfficiency: 'Перформанси',
    pp100ideal: '100% = идеални перформанси',
    pp85threshold: '85% = праг на деградација',
    ppDays: '{{count}} дена',
    ppShading: 'Засенчување',
    ppDegradation: 'Деградација',
    ppFailure: 'Дефект',
    ppSevHigh: 'Висок',
    ppSevMedium: 'Среден',
    ppSevLow: 'Низок',
  },
  bs: {
    spfTitle: 'Pošalji podatke o proizvodnji',
    spfDate: 'Datum',
    spfTz: 'Vremenska zona (UTC pomak)',
    spfCsvUpload: 'Učitaj podatke iz CSV datoteke',
    spfHideInstr: 'Sakrij upute',
    spfFileFormat: 'Format datoteke',
    spfCsvFormatTitle: 'Potreban format CSV datoteke:',
    spfChooseFile: 'Odaberi CSV datoteku…',
    spfShowSolar: 'Prikaži samo dnevne sate (5–20)',
    spfShowAll: 'Prikaži sve sate (0–23)',
    spfHourCol: 'Sat',
    spfProductionCol: 'Proizvodnja (Wh)',
    spfTotal: 'Ukupno',
    spfSubmit: 'Pošalji podatke o proizvodnji',
    spfSubmitting: 'Slanje…',
    spfNoData: 'Unesite podatke o proizvodnji za najmanje jedan sat.',
    spfSaved: 'Podaci o proizvodnji su spremljeni.',
    spfCsvEmpty: 'Datoteka je prazna.',
    spfCsvErrorsTitle: 'Greške u CSV datoteci:',
    spfCsvLoadedPartial: 'Učitano {{loaded}} sati, ali {{skipped}} redova je preskočeno.',
    spfCsvLoaded: 'Učitani podaci iz CSV: {{loaded}} sati, ukupno {{sum}} Wh',
    ppTitle: 'Analiza performansi',
    ppNoData:
      'Nedovoljno podataka za analizu. Pošaljite stvarne podatke o proizvodnji kako bi sistem mogao analizirati performanse postrojenja.',
    ppDaysAnalyzed: '{{count}} dana analize',
    ppOverallEff: 'Ukupne performanse',
    ppTotalForecast: 'Prognoza ukupno',
    ppTotalProduction: 'Proizvodnja ukupno',
    ppLostProduction: 'Izgubljena proizvodnja',
    ppWorksFine: 'Postrojenje radi ispravno',
    ppWorksFineDesc: 'Nisu otkriveni problemi sa sjenom, degradacijom ni kvarovima.',
    ppHourlyAvg: 'Satni prosjek: prognoza vs stvarno (Wh)',
    ppDailyEff: 'Dnevne performanse (%)',
    ppEffPerHour: 'Performanse po satu',
    ppForecast: 'Prognoza',
    ppActual: 'Stvarno',
    ppEfficiency: 'Performanse',
    pp100ideal: '100% = idealne performanse',
    pp85threshold: '85% = prag degradacije',
    ppDays: '{{count}} dana',
    ppShading: 'Sjena',
    ppDegradation: 'Degradacija',
    ppFailure: 'Kvar',
    ppSevHigh: 'Visok',
    ppSevMedium: 'Srednji',
    ppSevLow: 'Nizak',
  },
};

/** Namespace 'forecast' — ForecastChart komponent. */
const forecastTr: Record<string, Dict> = {
  en: {
    loading: 'Loading forecast…',
    production: 'Production',
    peak: 'Peak',
    productionHours: 'Production hours',
    corrected: 'Corrected',
    correction: 'Correction',
    noData: 'No data',
    title: 'Production forecast',
    today: 'Today',
    tomorrow: 'Tomorrow',
    stringBreakdown: 'Per-string breakdown',
    noProdToday: 'No production data for today',
    noProdTomorrow: 'No production data for tomorrow',
    string: 'String',
  },
  pl: {
    loading: 'Ładowanie prognozy…',
    production: 'Produkcja',
    peak: 'Szczyt',
    productionHours: 'Godziny produkcji',
    corrected: 'Skorygowana',
    correction: 'Korekcja',
    noData: 'Brak danych',
    title: 'Prognoza produkcji',
    today: 'Dzisiaj',
    tomorrow: 'Jutro',
    stringBreakdown: 'Rozkład na stringi',
    noProdToday: 'Brak danych produkcyjnych na dzisiaj',
    noProdTomorrow: 'Brak danych produkcyjnych na jutro',
    string: 'String',
  },
  de: {
    loading: 'Prognose wird geladen…',
    production: 'Produktion',
    peak: 'Spitze',
    productionHours: 'Produktionsstunden',
    corrected: 'Korrigiert',
    correction: 'Korrektur',
    noData: 'Keine Daten',
    title: 'Produktionsprognose',
    today: 'Heute',
    tomorrow: 'Morgen',
    stringBreakdown: 'Aufteilung nach Strings',
    noProdToday: 'Keine Produktionsdaten für heute',
    noProdTomorrow: 'Keine Produktionsdaten für morgen',
    string: 'String',
  },
  fr: {
    loading: 'Chargement de la prévision…',
    production: 'Production',
    peak: 'Pic',
    productionHours: 'Heures de production',
    corrected: 'Corrigée',
    correction: 'Correction',
    noData: 'Aucune donnée',
    title: 'Prévision de production',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    stringBreakdown: 'Répartition par chaîne',
    noProdToday: "Aucune donnée de production pour aujourd'hui",
    noProdTomorrow: 'Aucune donnée de production pour demain',
    string: 'Chaîne',
  },
  es: {
    loading: 'Cargando previsión…',
    production: 'Producción',
    peak: 'Pico',
    productionHours: 'Horas de producción',
    corrected: 'Corregida',
    correction: 'Corrección',
    noData: 'Sin datos',
    title: 'Previsión de producción',
    today: 'Hoy',
    tomorrow: 'Mañana',
    stringBreakdown: 'Desglose por cadena',
    noProdToday: 'Sin datos de producción para hoy',
    noProdTomorrow: 'Sin datos de producción para mañana',
    string: 'Cadena',
  },
  it: {
    loading: 'Caricamento previsione…',
    production: 'Produzione',
    peak: 'Picco',
    productionHours: 'Ore di produzione',
    corrected: 'Corretta',
    correction: 'Correzione',
    noData: 'Nessun dato',
    title: 'Previsione di produzione',
    today: 'Oggi',
    tomorrow: 'Domani',
    stringBreakdown: 'Ripartizione per stringa',
    noProdToday: 'Nessun dato di produzione per oggi',
    noProdTomorrow: 'Nessun dato di produzione per domani',
    string: 'Stringa',
  },
  pt: {
    loading: 'A carregar previsão…',
    production: 'Produção',
    peak: 'Pico',
    productionHours: 'Horas de produção',
    corrected: 'Corrigida',
    correction: 'Correção',
    noData: 'Sem dados',
    title: 'Previsão de produção',
    today: 'Hoje',
    tomorrow: 'Amanhã',
    stringBreakdown: 'Repartição por string',
    noProdToday: 'Sem dados de produção para hoje',
    noProdTomorrow: 'Sem dados de produção para amanhã',
    string: 'String',
  },
  nl: {
    loading: 'Prognose laden…',
    production: 'Productie',
    peak: 'Piek',
    productionHours: 'Productie-uren',
    corrected: 'Gecorrigeerd',
    correction: 'Correctie',
    noData: 'Geen gegevens',
    title: 'Productieprognose',
    today: 'Vandaag',
    tomorrow: 'Morgen',
    stringBreakdown: 'Uitsplitsing per string',
    noProdToday: 'Geen productiegegevens voor vandaag',
    noProdTomorrow: 'Geen productiegegevens voor morgen',
    string: 'String',
  },
  da: {
    loading: 'Indlæser prognose…',
    production: 'Produktion',
    peak: 'Top',
    productionHours: 'Produktionstimer',
    corrected: 'Korrigeret',
    correction: 'Korrektion',
    noData: 'Ingen data',
    title: 'Produktionsprognose',
    today: 'I dag',
    tomorrow: 'I morgen',
    stringBreakdown: 'Opdeling pr. streng',
    noProdToday: 'Ingen produktionsdata for i dag',
    noProdTomorrow: 'Ingen produktionsdata for i morgen',
    string: 'Streng',
  },
  sv: {
    loading: 'Laddar prognos…',
    production: 'Produktion',
    peak: 'Topp',
    productionHours: 'Produktionstimmar',
    corrected: 'Korrigerad',
    correction: 'Korrigering',
    noData: 'Inga data',
    title: 'Produktionsprognos',
    today: 'Idag',
    tomorrow: 'Imorgon',
    stringBreakdown: 'Uppdelning per sträng',
    noProdToday: 'Inga produktionsdata för idag',
    noProdTomorrow: 'Inga produktionsdata för imorgon',
    string: 'Sträng',
  },
  fi: {
    loading: 'Ladataan ennustetta…',
    production: 'Tuotanto',
    peak: 'Huippu',
    productionHours: 'Tuotantotunnit',
    corrected: 'Korjattu',
    correction: 'Korjaus',
    noData: 'Ei tietoja',
    title: 'Tuotantoennuste',
    today: 'Tänään',
    tomorrow: 'Huomenna',
    stringBreakdown: 'Erittely sarjoittain',
    noProdToday: 'Ei tuotantotietoja tälle päivälle',
    noProdTomorrow: 'Ei tuotantotietoja huomiselle',
    string: 'Sarja',
  },
  cs: {
    loading: 'Načítání předpovědi…',
    production: 'Výroba',
    peak: 'Špička',
    productionHours: 'Hodiny výroby',
    corrected: 'Korigovaná',
    correction: 'Korekce',
    noData: 'Žádná data',
    title: 'Předpověď výroby',
    today: 'Dnes',
    tomorrow: 'Zítra',
    stringBreakdown: 'Rozdělení podle stringů',
    noProdToday: 'Žádná data výroby na dnes',
    noProdTomorrow: 'Žádná data výroby na zítra',
    string: 'String',
  },
  sk: {
    loading: 'Načítava sa predpoveď…',
    production: 'Výroba',
    peak: 'Špička',
    productionHours: 'Hodiny výroby',
    corrected: 'Korigovaná',
    correction: 'Korekcia',
    noData: 'Žiadne dáta',
    title: 'Predpoveď výroby',
    today: 'Dnes',
    tomorrow: 'Zajtra',
    stringBreakdown: 'Rozdelenie podľa stringov',
    noProdToday: 'Žiadne dáta výroby na dnes',
    noProdTomorrow: 'Žiadne dáta výroby na zajtra',
    string: 'String',
  },
  sl: {
    loading: 'Nalaganje napovedi…',
    production: 'Proizvodnja',
    peak: 'Vrh',
    productionHours: 'Ure proizvodnje',
    corrected: 'Popravljena',
    correction: 'Popravek',
    noData: 'Ni podatkov',
    title: 'Napoved proizvodnje',
    today: 'Danes',
    tomorrow: 'Jutri',
    stringBreakdown: 'Razčlenitev po nizih',
    noProdToday: 'Ni podatkov o proizvodnji za danes',
    noProdTomorrow: 'Ni podatkov o proizvodnji za jutri',
    string: 'Niz',
  },
  hr: {
    loading: 'Učitavanje prognoze…',
    production: 'Proizvodnja',
    peak: 'Vrhunac',
    productionHours: 'Sati proizvodnje',
    corrected: 'Ispravljena',
    correction: 'Ispravak',
    noData: 'Nema podataka',
    title: 'Prognoza proizvodnje',
    today: 'Danas',
    tomorrow: 'Sutra',
    stringBreakdown: 'Raspodjela po nizovima',
    noProdToday: 'Nema podataka o proizvodnji za danas',
    noProdTomorrow: 'Nema podataka o proizvodnji za sutra',
    string: 'Niz',
  },
  hu: {
    loading: 'Előrejelzés betöltése…',
    production: 'Termelés',
    peak: 'Csúcs',
    productionHours: 'Termelési órák',
    corrected: 'Korrigált',
    correction: 'Korrekció',
    noData: 'Nincs adat',
    title: 'Termelési előrejelzés',
    today: 'Ma',
    tomorrow: 'Holnap',
    stringBreakdown: 'Stringenkénti bontás',
    noProdToday: 'Nincs termelési adat mára',
    noProdTomorrow: 'Nincs termelési adat holnapra',
    string: 'String',
  },
  ro: {
    loading: 'Se încarcă prognoza…',
    production: 'Producție',
    peak: 'Vârf',
    productionHours: 'Ore de producție',
    corrected: 'Corectată',
    correction: 'Corecție',
    noData: 'Fără date',
    title: 'Prognoză de producție',
    today: 'Astăzi',
    tomorrow: 'Mâine',
    stringBreakdown: 'Defalcare pe string-uri',
    noProdToday: 'Fără date de producție pentru astăzi',
    noProdTomorrow: 'Fără date de producție pentru mâine',
    string: 'String',
  },
  bg: {
    loading: 'Зареждане на прогнозата…',
    production: 'Производство',
    peak: 'Пик',
    productionHours: 'Часове на производство',
    corrected: 'Коригирана',
    correction: 'Корекция',
    noData: 'Няма данни',
    title: 'Прогноза за производство',
    today: 'Днес',
    tomorrow: 'Утре',
    stringBreakdown: 'Разбивка по стрингове',
    noProdToday: 'Няма данни за производство за днес',
    noProdTomorrow: 'Няма данни за производство за утре',
    string: 'Стринг',
  },
  el: {
    loading: 'Φόρτωση πρόβλεψης…',
    production: 'Παραγωγή',
    peak: 'Αιχμή',
    productionHours: 'Ώρες παραγωγής',
    corrected: 'Διορθωμένη',
    correction: 'Διόρθωση',
    noData: 'Χωρίς δεδομένα',
    title: 'Πρόβλεψη παραγωγής',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
    stringBreakdown: 'Ανάλυση ανά string',
    noProdToday: 'Χωρίς δεδομένα παραγωγής για σήμερα',
    noProdTomorrow: 'Χωρίς δεδομένα παραγωγής για αύριο',
    string: 'String',
  },
  et: {
    loading: 'Prognoosi laadimine…',
    production: 'Toodang',
    peak: 'Tipp',
    productionHours: 'Tootmistunnid',
    corrected: 'Korrigeeritud',
    correction: 'Korrektsioon',
    noData: 'Andmed puuduvad',
    title: 'Toodanguprognoos',
    today: 'Täna',
    tomorrow: 'Homme',
    stringBreakdown: 'Jaotus stringide kaupa',
    noProdToday: 'Tänaseks pole toodanguandmeid',
    noProdTomorrow: 'Homseks pole toodanguandmeid',
    string: 'String',
  },
  lv: {
    loading: 'Notiek prognozes ielāde…',
    production: 'Ražošana',
    peak: 'Maksimums',
    productionHours: 'Ražošanas stundas',
    corrected: 'Koriģēta',
    correction: 'Korekcija',
    noData: 'Nav datu',
    title: 'Ražošanas prognoze',
    today: 'Šodien',
    tomorrow: 'Rīt',
    stringBreakdown: 'Sadalījums pa virknēm',
    noProdToday: 'Nav ražošanas datu šodienai',
    noProdTomorrow: 'Nav ražošanas datu rītdienai',
    string: 'Virkne',
  },
  lt: {
    loading: 'Įkeliama prognozė…',
    production: 'Gamyba',
    peak: 'Pikas',
    productionHours: 'Gamybos valandos',
    corrected: 'Pakoreguota',
    correction: 'Korekcija',
    noData: 'Nėra duomenų',
    title: 'Gamybos prognozė',
    today: 'Šiandien',
    tomorrow: 'Rytoj',
    stringBreakdown: 'Suskirstymas pagal eilutes',
    noProdToday: 'Šiandienai nėra gamybos duomenų',
    noProdTomorrow: 'Rytojui nėra gamybos duomenų',
    string: 'Eilutė',
  },
  mt: {
    loading: 'Qed jiġġarrab il-previżjoni…',
    production: 'Produzzjoni',
    peak: 'Quċċata',
    productionHours: "Sigħat ta' produzzjoni",
    corrected: 'Ikkoreġuta',
    correction: 'Korrezzjoni',
    noData: 'L-ebda dejta',
    title: 'Previżjoni tal-produzzjoni',
    today: 'Illum',
    tomorrow: 'Għada',
    stringBreakdown: 'Tqassim skont string',
    noProdToday: 'L-ebda dejta tal-produzzjoni għal-llum',
    noProdTomorrow: 'L-ebda dejta tal-produzzjoni għal għada',
    string: 'String',
  },
  ga: {
    loading: 'Ag lódáil réamhaisnéise…',
    production: 'Táirgeadh',
    peak: 'Buaic',
    productionHours: 'Uaireanta táirgthe',
    corrected: 'Ceartaithe',
    correction: 'Ceartúchán',
    noData: 'Gan sonraí',
    title: 'Réamhaisnéis táirgthe',
    today: 'Inniu',
    tomorrow: 'Amárach',
    stringBreakdown: 'Miondealú de réir slabhra',
    noProdToday: 'Gan sonraí táirgthe le haghaidh inniu',
    noProdTomorrow: 'Gan sonraí táirgthe le haghaidh amárach',
    string: 'Slabhra',
  },
  no: {
    loading: 'Laster prognose…',
    production: 'Produksjon',
    peak: 'Topp',
    productionHours: 'Produksjonstimer',
    corrected: 'Korrigert',
    correction: 'Korreksjon',
    noData: 'Ingen data',
    title: 'Produksjonsprognose',
    today: 'I dag',
    tomorrow: 'I morgen',
    stringBreakdown: 'Fordeling per streng',
    noProdToday: 'Ingen produksjonsdata for i dag',
    noProdTomorrow: 'Ingen produksjonsdata for i morgen',
    string: 'Streng',
  },
  is: {
    loading: 'Hleður spá…',
    production: 'Framleiðsla',
    peak: 'Hámark',
    productionHours: 'Framleiðslustundir',
    corrected: 'Leiðrétt',
    correction: 'Leiðrétting',
    noData: 'Engin gögn',
    title: 'Framleiðsluspá',
    today: 'Í dag',
    tomorrow: 'Á morgun',
    stringBreakdown: 'Sundurliðun eftir strengjum',
    noProdToday: 'Engin framleiðslugögn fyrir í dag',
    noProdTomorrow: 'Engin framleiðslugögn fyrir á morgun',
    string: 'Strengur',
  },
  uk: {
    loading: 'Завантаження прогнозу…',
    production: 'Виробництво',
    peak: 'Пік',
    productionHours: 'Години виробництва',
    corrected: 'Скоригований',
    correction: 'Корекція',
    noData: 'Немає даних',
    title: 'Прогноз виробництва',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    stringBreakdown: 'Розбивка за стрінгами',
    noProdToday: 'Немає даних виробництва на сьогодні',
    noProdTomorrow: 'Немає даних виробництва на завтра',
    string: 'Стрінг',
  },
  sr: {
    loading: 'Učitavanje prognoze…',
    production: 'Proizvodnja',
    peak: 'Vrhunac',
    productionHours: 'Sati proizvodnje',
    corrected: 'Ispravljena',
    correction: 'Ispravka',
    noData: 'Nema podataka',
    title: 'Prognoza proizvodnje',
    today: 'Danas',
    tomorrow: 'Sutra',
    stringBreakdown: 'Raspodela po nizovima',
    noProdToday: 'Nema podataka o proizvodnji za danas',
    noProdTomorrow: 'Nema podataka o proizvodnji za sutra',
    string: 'Niz',
  },
  sq: {
    loading: 'Po ngarkohet parashikimi…',
    production: 'Prodhimi',
    peak: 'Maja',
    productionHours: 'Orët e prodhimit',
    corrected: 'E korrigjuar',
    correction: 'Korrigjimi',
    noData: 'Nuk ka të dhëna',
    title: 'Parashikimi i prodhimit',
    today: 'Sot',
    tomorrow: 'Nesër',
    stringBreakdown: 'Ndarja sipas vargjeve',
    noProdToday: 'Nuk ka të dhëna prodhimi për sot',
    noProdTomorrow: 'Nuk ka të dhëna prodhimi për nesër',
    string: 'Varg',
  },
  mk: {
    loading: 'Се вчитува прогнозата…',
    production: 'Производство',
    peak: 'Врв',
    productionHours: 'Часови на производство',
    corrected: 'Коригирана',
    correction: 'Корекција',
    noData: 'Нема податоци',
    title: 'Прогноза за производство',
    today: 'Денес',
    tomorrow: 'Утре',
    stringBreakdown: 'Распределба по стрингови',
    noProdToday: 'Нема податоци за производство за денес',
    noProdTomorrow: 'Нема податоци за производство за утре',
    string: 'Стринг',
  },
  bs: {
    loading: 'Učitavanje prognoze…',
    production: 'Proizvodnja',
    peak: 'Vrhunac',
    productionHours: 'Sati proizvodnje',
    corrected: 'Ispravljena',
    correction: 'Ispravka',
    noData: 'Nema podataka',
    title: 'Prognoza proizvodnje',
    today: 'Danas',
    tomorrow: 'Sutra',
    stringBreakdown: 'Raspodjela po nizovima',
    noProdToday: 'Nema podataka o proizvodnji za danas',
    noProdTomorrow: 'Nema podataka o proizvodnji za sutra',
    string: 'Niz',
  },
};

/** Namespace 'viz' — InstallationVisualizer komponent (wizualizacja 3D). */
const vizTr: Record<string, Dict> = {
  en: {
    title: '3D installation visualization',
    sunElevation: 'Sun elevation',
    sunAzimuth: 'Sun azimuth',
    avgEfficiency: 'Average efficiency',
    sunriseSunset: 'Sunrise / Sunset',
    animateDay: 'Animate day',
    pause: 'Pause',
    now: 'Now',
    rotateRoof: 'Rotate roof 90°',
    rotateRoofTitle: "Swap roof ridge orientation (along / across the building's long axis)",
    refresh: 'Refresh',
    refreshTitle: 'Clear the view and refresh building data from OSM',
    mapAttribution: 'Map © OpenStreetMap',
    timeSlider: 'Time of day selector',
    string: 'String',
    tilt: 'Tilt',
    azimuth: 'Azimuth',
    panelCount: 'Number of panels',
    panelPower: 'Panel power',
    efficiency: 'Efficiency',
    illumination: 'Illumination',
    errTitle: 'Cannot render the 3D visualization',
    errBody: 'Your browser or device may not support WebGL.',
  },
  pl: {
    title: 'Wizualizacja 3D instalacji',
    sunElevation: 'Wysokość słońca',
    sunAzimuth: 'Azymut słońca',
    avgEfficiency: 'Średnia efektywność',
    sunriseSunset: 'Wschód / Zachód',
    animateDay: 'Animuj dzień',
    pause: 'Pauza',
    now: 'Teraz',
    rotateRoof: 'Obróć dach 90°',
    rotateRoofTitle: 'Zamień orientację kalenicy dachu (wzdłuż / w poprzek długiej osi budynku)',
    refresh: 'Odśwież',
    refreshTitle: 'Wyczyść widok i odśwież dane budynku z OSM',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Wybór pory dnia',
    string: 'String',
    tilt: 'Pochylenie',
    azimuth: 'Azymut',
    panelCount: 'Liczba paneli',
    panelPower: 'Moc panelu',
    efficiency: 'Efektywność',
    illumination: 'Oświetlenie',
    errTitle: 'Nie można wyrenderować wizualizacji 3D',
    errBody: 'Twoja przeglądarka lub urządzenie może nie obsługiwać WebGL.',
  },
  de: {
    title: '3D-Anlagenvisualisierung',
    sunElevation: 'Sonnenhöhe',
    sunAzimuth: 'Sonnenazimut',
    avgEfficiency: 'Durchschnittlicher Wirkungsgrad',
    sunriseSunset: 'Sonnenaufgang / Sonnenuntergang',
    animateDay: 'Tag animieren',
    pause: 'Pause',
    now: 'Jetzt',
    rotateRoof: 'Dach 90° drehen',
    rotateRoofTitle: 'Dachfirstausrichtung wechseln (längs / quer zur langen Gebäudeachse)',
    refresh: 'Aktualisieren',
    refreshTitle: 'Ansicht leeren und Gebäudedaten von OSM aktualisieren',
    mapAttribution: 'Karte © OpenStreetMap',
    timeSlider: 'Tageszeit-Auswahl',
    string: 'String',
    tilt: 'Neigung',
    azimuth: 'Azimut',
    panelCount: 'Anzahl Module',
    panelPower: 'Modulleistung',
    efficiency: 'Wirkungsgrad',
    illumination: 'Beleuchtung',
    errTitle: '3D-Visualisierung kann nicht gerendert werden',
    errBody: 'Ihr Browser oder Gerät unterstützt möglicherweise kein WebGL.',
  },
  fr: {
    title: "Visualisation 3D de l'installation",
    sunElevation: 'Hauteur du soleil',
    sunAzimuth: 'Azimut du soleil',
    avgEfficiency: 'Rendement moyen',
    sunriseSunset: 'Lever / Coucher du soleil',
    animateDay: 'Animer la journée',
    pause: 'Pause',
    now: 'Maintenant',
    rotateRoof: 'Tourner le toit de 90°',
    rotateRoofTitle:
      "Changer l'orientation du faîtage (le long / en travers du grand axe du bâtiment)",
    refresh: 'Actualiser',
    refreshTitle: 'Effacer la vue et actualiser les données du bâtiment depuis OSM',
    mapAttribution: 'Carte © OpenStreetMap',
    timeSlider: "Sélecteur d'heure de la journée",
    string: 'Chaîne',
    tilt: 'Inclinaison',
    azimuth: 'Azimut',
    panelCount: 'Nombre de panneaux',
    panelPower: 'Puissance du panneau',
    efficiency: 'Rendement',
    illumination: 'Éclairage',
    errTitle: 'Impossible de rendre la visualisation 3D',
    errBody: 'Votre navigateur ou appareil ne prend peut-être pas en charge WebGL.',
  },
  es: {
    title: 'Visualización 3D de la instalación',
    sunElevation: 'Altura del sol',
    sunAzimuth: 'Acimut del sol',
    avgEfficiency: 'Eficiencia media',
    sunriseSunset: 'Amanecer / Atardecer',
    animateDay: 'Animar el día',
    pause: 'Pausa',
    now: 'Ahora',
    rotateRoof: 'Girar el tejado 90°',
    rotateRoofTitle:
      'Cambiar la orientación de la cumbrera (a lo largo / a lo ancho del eje largo del edificio)',
    refresh: 'Actualizar',
    refreshTitle: 'Limpiar la vista y actualizar los datos del edificio desde OSM',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Selector de hora del día',
    string: 'Cadena',
    tilt: 'Inclinación',
    azimuth: 'Acimut',
    panelCount: 'Número de paneles',
    panelPower: 'Potencia del panel',
    efficiency: 'Eficiencia',
    illumination: 'Iluminación',
    errTitle: 'No se puede renderizar la visualización 3D',
    errBody: 'Su navegador o dispositivo puede no admitir WebGL.',
  },
  it: {
    title: "Visualizzazione 3D dell'impianto",
    sunElevation: 'Altezza del sole',
    sunAzimuth: 'Azimut del sole',
    avgEfficiency: 'Efficienza media',
    sunriseSunset: 'Alba / Tramonto',
    animateDay: 'Anima il giorno',
    pause: 'Pausa',
    now: 'Ora',
    rotateRoof: 'Ruota il tetto di 90°',
    rotateRoofTitle:
      "Cambia l'orientamento del colmo (lungo / attraverso l'asse lungo dell'edificio)",
    refresh: 'Aggiorna',
    refreshTitle: "Svuota la vista e aggiorna i dati dell'edificio da OSM",
    mapAttribution: 'Mappa © OpenStreetMap',
    timeSlider: 'Selettore ora del giorno',
    string: 'Stringa',
    tilt: 'Inclinazione',
    azimuth: 'Azimut',
    panelCount: 'Numero di pannelli',
    panelPower: 'Potenza del pannello',
    efficiency: 'Efficienza',
    illumination: 'Illuminazione',
    errTitle: 'Impossibile renderizzare la visualizzazione 3D',
    errBody: 'Il tuo browser o dispositivo potrebbe non supportare WebGL.',
  },
  pt: {
    title: 'Visualização 3D da instalação',
    sunElevation: 'Altura do sol',
    sunAzimuth: 'Azimute do sol',
    avgEfficiency: 'Eficiência média',
    sunriseSunset: 'Nascer / Pôr do sol',
    animateDay: 'Animar o dia',
    pause: 'Pausa',
    now: 'Agora',
    rotateRoof: 'Girar o telhado 90°',
    rotateRoofTitle:
      'Trocar a orientação da cumeeira (ao longo / através do eixo longo do edifício)',
    refresh: 'Atualizar',
    refreshTitle: 'Limpar a vista e atualizar os dados do edifício a partir do OSM',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Seletor de hora do dia',
    string: 'String',
    tilt: 'Inclinação',
    azimuth: 'Azimute',
    panelCount: 'Número de painéis',
    panelPower: 'Potência do painel',
    efficiency: 'Eficiência',
    illumination: 'Iluminação',
    errTitle: 'Não é possível renderizar a visualização 3D',
    errBody: 'O seu navegador ou dispositivo pode não suportar WebGL.',
  },
  nl: {
    title: '3D-installatievisualisatie',
    sunElevation: 'Zonshoogte',
    sunAzimuth: 'Zonsazimut',
    avgEfficiency: 'Gemiddeld rendement',
    sunriseSunset: 'Zonsopkomst / Zonsondergang',
    animateDay: 'Dag animeren',
    pause: 'Pauze',
    now: 'Nu',
    rotateRoof: 'Dak 90° draaien',
    rotateRoofTitle: 'Nokoriëntatie wisselen (langs / dwars op de lange as van het gebouw)',
    refresh: 'Vernieuwen',
    refreshTitle: 'Weergave wissen en gebouwgegevens vernieuwen vanuit OSM',
    mapAttribution: 'Kaart © OpenStreetMap',
    timeSlider: 'Tijdstipkiezer',
    string: 'String',
    tilt: 'Helling',
    azimuth: 'Azimut',
    panelCount: 'Aantal panelen',
    panelPower: 'Paneelvermogen',
    efficiency: 'Rendement',
    illumination: 'Belichting',
    errTitle: 'Kan de 3D-visualisatie niet weergeven',
    errBody: 'Uw browser of apparaat ondersteunt mogelijk geen WebGL.',
  },
  da: {
    title: '3D-anlægsvisualisering',
    sunElevation: 'Solens højde',
    sunAzimuth: 'Solens azimut',
    avgEfficiency: 'Gennemsnitlig virkningsgrad',
    sunriseSunset: 'Solopgang / Solnedgang',
    animateDay: 'Animér dagen',
    pause: 'Pause',
    now: 'Nu',
    rotateRoof: 'Drej taget 90°',
    rotateRoofTitle: 'Skift tagrygsorientering (langs / på tværs af bygningens lange akse)',
    refresh: 'Opdater',
    refreshTitle: 'Ryd visningen og opdater bygningsdata fra OSM',
    mapAttribution: 'Kort © OpenStreetMap',
    timeSlider: 'Vælger for tidspunkt på dagen',
    string: 'Streng',
    tilt: 'Hældning',
    azimuth: 'Azimut',
    panelCount: 'Antal paneler',
    panelPower: 'Paneleffekt',
    efficiency: 'Virkningsgrad',
    illumination: 'Belysning',
    errTitle: 'Kan ikke gengive 3D-visualiseringen',
    errBody: 'Din browser eller enhed understøtter muligvis ikke WebGL.',
  },
  sv: {
    title: '3D-anläggningsvisualisering',
    sunElevation: 'Solhöjd',
    sunAzimuth: 'Solazimut',
    avgEfficiency: 'Genomsnittlig verkningsgrad',
    sunriseSunset: 'Soluppgång / Solnedgång',
    animateDay: 'Animera dagen',
    pause: 'Paus',
    now: 'Nu',
    rotateRoof: 'Rotera taket 90°',
    rotateRoofTitle: 'Byt takryggens orientering (längs / tvärs byggnadens långa axel)',
    refresh: 'Uppdatera',
    refreshTitle: 'Rensa vyn och uppdatera byggnadsdata från OSM',
    mapAttribution: 'Karta © OpenStreetMap',
    timeSlider: 'Väljare för tid på dygnet',
    string: 'Sträng',
    tilt: 'Lutning',
    azimuth: 'Azimut',
    panelCount: 'Antal paneler',
    panelPower: 'Paneleffekt',
    efficiency: 'Verkningsgrad',
    illumination: 'Belysning',
    errTitle: 'Kan inte rendera 3D-visualiseringen',
    errBody: 'Din webbläsare eller enhet kanske inte stöder WebGL.',
  },
  fi: {
    title: 'Laitoksen 3D-visualisointi',
    sunElevation: 'Auringon korkeus',
    sunAzimuth: 'Auringon atsimuutti',
    avgEfficiency: 'Keskimääräinen hyötysuhde',
    sunriseSunset: 'Auringonnousu / Auringonlasku',
    animateDay: 'Animoi päivä',
    pause: 'Tauko',
    now: 'Nyt',
    rotateRoof: 'Käännä kattoa 90°',
    rotateRoofTitle:
      'Vaihda katon harjan suuntaa (rakennuksen pitkän akselin suuntaisesti / poikittain)',
    refresh: 'Päivitä',
    refreshTitle: 'Tyhjennä näkymä ja päivitä rakennustiedot OSM:stä',
    mapAttribution: 'Kartta © OpenStreetMap',
    timeSlider: 'Vuorokaudenajan valitsin',
    string: 'Sarja',
    tilt: 'Kallistus',
    azimuth: 'Atsimuutti',
    panelCount: 'Paneelien lukumäärä',
    panelPower: 'Paneelin teho',
    efficiency: 'Hyötysuhde',
    illumination: 'Valaistus',
    errTitle: '3D-visualisointia ei voi renderöidä',
    errBody: 'Selaimesi tai laitteesi ei ehkä tue WebGL:ää.',
  },
  cs: {
    title: '3D vizualizace instalace',
    sunElevation: 'Výška slunce',
    sunAzimuth: 'Azimut slunce',
    avgEfficiency: 'Průměrná účinnost',
    sunriseSunset: 'Východ / Západ slunce',
    animateDay: 'Animovat den',
    pause: 'Pauza',
    now: 'Nyní',
    rotateRoof: 'Otočit střechu o 90°',
    rotateRoofTitle: 'Změnit orientaci hřebene střechy (podél / napříč dlouhé osy budovy)',
    refresh: 'Obnovit',
    refreshTitle: 'Vyčistit zobrazení a obnovit data budovy z OSM',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Výběr denní doby',
    string: 'String',
    tilt: 'Sklon',
    azimuth: 'Azimut',
    panelCount: 'Počet panelů',
    panelPower: 'Výkon panelu',
    efficiency: 'Účinnost',
    illumination: 'Osvětlení',
    errTitle: 'Nelze vykreslit 3D vizualizaci',
    errBody: 'Váš prohlížeč nebo zařízení nemusí podporovat WebGL.',
  },
  sk: {
    title: '3D vizualizácia inštalácie',
    sunElevation: 'Výška slnka',
    sunAzimuth: 'Azimut slnka',
    avgEfficiency: 'Priemerná účinnosť',
    sunriseSunset: 'Východ / Západ slnka',
    animateDay: 'Animovať deň',
    pause: 'Pauza',
    now: 'Teraz',
    rotateRoof: 'Otočiť strechu o 90°',
    rotateRoofTitle: 'Zmeniť orientáciu hrebeňa strechy (pozdĺž / naprieč dlhej osi budovy)',
    refresh: 'Obnoviť',
    refreshTitle: 'Vyčistiť zobrazenie a obnoviť dáta budovy z OSM',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Výber dennej doby',
    string: 'String',
    tilt: 'Sklon',
    azimuth: 'Azimut',
    panelCount: 'Počet panelov',
    panelPower: 'Výkon panelu',
    efficiency: 'Účinnosť',
    illumination: 'Osvetlenie',
    errTitle: 'Nemožno vykresliť 3D vizualizáciu',
    errBody: 'Váš prehliadač alebo zariadenie nemusí podporovať WebGL.',
  },
  sl: {
    title: '3D vizualizacija naprave',
    sunElevation: 'Višina sonca',
    sunAzimuth: 'Azimut sonca',
    avgEfficiency: 'Povprečna učinkovitost',
    sunriseSunset: 'Sončni vzhod / zahod',
    animateDay: 'Animiraj dan',
    pause: 'Premor',
    now: 'Zdaj',
    rotateRoof: 'Zavrti streho za 90°',
    rotateRoofTitle: 'Zamenjaj usmeritev slemena strehe (vzdolž / prečno na dolgo os stavbe)',
    refresh: 'Osveži',
    refreshTitle: 'Počisti pogled in osveži podatke o stavbi iz OSM',
    mapAttribution: 'Zemljevid © OpenStreetMap',
    timeSlider: 'Izbirnik časa dneva',
    string: 'Niz',
    tilt: 'Naklon',
    azimuth: 'Azimut',
    panelCount: 'Število panelov',
    panelPower: 'Moč panela',
    efficiency: 'Učinkovitost',
    illumination: 'Osvetlitev',
    errTitle: 'Ni mogoče upodobiti 3D vizualizacije',
    errBody: 'Vaš brskalnik ali naprava morda ne podpira WebGL.',
  },
  hr: {
    title: '3D vizualizacija postrojenja',
    sunElevation: 'Visina sunca',
    sunAzimuth: 'Azimut sunca',
    avgEfficiency: 'Prosječna učinkovitost',
    sunriseSunset: 'Izlazak / Zalazak sunca',
    animateDay: 'Animiraj dan',
    pause: 'Pauza',
    now: 'Sada',
    rotateRoof: 'Zakreni krov za 90°',
    rotateRoofTitle: 'Zamijeni orijentaciju sljemena krova (uzduž / poprijeko duge osi zgrade)',
    refresh: 'Osvježi',
    refreshTitle: 'Očisti prikaz i osvježi podatke o zgradi iz OSM-a',
    mapAttribution: 'Karta © OpenStreetMap',
    timeSlider: 'Birač doba dana',
    string: 'Niz',
    tilt: 'Nagib',
    azimuth: 'Azimut',
    panelCount: 'Broj panela',
    panelPower: 'Snaga panela',
    efficiency: 'Učinkovitost',
    illumination: 'Osvjetljenje',
    errTitle: 'Nije moguće prikazati 3D vizualizaciju',
    errBody: 'Vaš preglednik ili uređaj možda ne podržava WebGL.',
  },
  hu: {
    title: '3D telepítésvizualizáció',
    sunElevation: 'Nap magassága',
    sunAzimuth: 'Nap azimutja',
    avgEfficiency: 'Átlagos hatásfok',
    sunriseSunset: 'Napkelte / Napnyugta',
    animateDay: 'Nap animálása',
    pause: 'Szünet',
    now: 'Most',
    rotateRoof: 'Tető elforgatása 90°-kal',
    rotateRoofTitle:
      'A tetőgerinc tájolásának cseréje (az épület hosszú tengelye mentén / arra merőlegesen)',
    refresh: 'Frissítés',
    refreshTitle: 'A nézet törlése és az épületadatok frissítése az OSM-ből',
    mapAttribution: 'Térkép © OpenStreetMap',
    timeSlider: 'Napszak választó',
    string: 'String',
    tilt: 'Dőlés',
    azimuth: 'Azimut',
    panelCount: 'Panelek száma',
    panelPower: 'Panel teljesítménye',
    efficiency: 'Hatásfok',
    illumination: 'Megvilágítás',
    errTitle: 'A 3D vizualizáció nem jeleníthető meg',
    errBody: 'Lehet, hogy a böngészője vagy eszköze nem támogatja a WebGL-t.',
  },
  ro: {
    title: 'Vizualizare 3D a instalației',
    sunElevation: 'Înălțimea soarelui',
    sunAzimuth: 'Azimutul soarelui',
    avgEfficiency: 'Eficiență medie',
    sunriseSunset: 'Răsărit / Apus',
    animateDay: 'Animează ziua',
    pause: 'Pauză',
    now: 'Acum',
    rotateRoof: 'Rotește acoperișul cu 90°',
    rotateRoofTitle:
      'Schimbă orientarea coamei acoperișului (de-a lungul / de-a latul axei lungi a clădirii)',
    refresh: 'Reîmprospătează',
    refreshTitle: 'Curăță vizualizarea și reîmprospătează datele clădirii din OSM',
    mapAttribution: 'Hartă © OpenStreetMap',
    timeSlider: 'Selector pentru ora zilei',
    string: 'String',
    tilt: 'Înclinare',
    azimuth: 'Azimut',
    panelCount: 'Număr de panouri',
    panelPower: 'Putere panou',
    efficiency: 'Eficiență',
    illumination: 'Iluminare',
    errTitle: 'Nu se poate reda vizualizarea 3D',
    errBody: 'Browserul sau dispozitivul dvs. ar putea să nu suporte WebGL.',
  },
  bg: {
    title: '3D визуализация на инсталацията',
    sunElevation: 'Височина на слънцето',
    sunAzimuth: 'Азимут на слънцето',
    avgEfficiency: 'Средна ефективност',
    sunriseSunset: 'Изгрев / Залез',
    animateDay: 'Анимирай деня',
    pause: 'Пауза',
    now: 'Сега',
    rotateRoof: 'Завърти покрива на 90°',
    rotateRoofTitle:
      'Смени ориентацията на билото на покрива (по дължина / напречно на дългата ос на сградата)',
    refresh: 'Обнови',
    refreshTitle: 'Изчисти изгледа и обнови данните за сградата от OSM',
    mapAttribution: 'Карта © OpenStreetMap',
    timeSlider: 'Избор на час от деня',
    string: 'Стринг',
    tilt: 'Наклон',
    azimuth: 'Азимут',
    panelCount: 'Брой панели',
    panelPower: 'Мощност на панела',
    efficiency: 'Ефективност',
    illumination: 'Осветеност',
    errTitle: 'Не може да се визуализира 3D визуализацията',
    errBody: 'Вашият браузър или устройство може да не поддържа WebGL.',
  },
  el: {
    title: '3D οπτικοποίηση εγκατάστασης',
    sunElevation: 'Ύψος ήλιου',
    sunAzimuth: 'Αζιμούθιο ήλιου',
    avgEfficiency: 'Μέση απόδοση',
    sunriseSunset: 'Ανατολή / Δύση',
    animateDay: 'Κίνηση ημέρας',
    pause: 'Παύση',
    now: 'Τώρα',
    rotateRoof: 'Περιστροφή στέγης 90°',
    rotateRoofTitle:
      'Αλλαγή προσανατολισμού κορυφογραμμής στέγης (κατά μήκος / κατά πλάτος του μακριού άξονα του κτιρίου)',
    refresh: 'Ανανέωση',
    refreshTitle: 'Καθαρισμός προβολής και ανανέωση δεδομένων κτιρίου από το OSM',
    mapAttribution: 'Χάρτης © OpenStreetMap',
    timeSlider: 'Επιλογέας ώρας ημέρας',
    string: 'String',
    tilt: 'Κλίση',
    azimuth: 'Αζιμούθιο',
    panelCount: 'Αριθμός πάνελ',
    panelPower: 'Ισχύς πάνελ',
    efficiency: 'Απόδοση',
    illumination: 'Φωτισμός',
    errTitle: 'Δεν είναι δυνατή η απόδοση της 3D οπτικοποίησης',
    errBody: 'Το πρόγραμμα περιήγησης ή η συσκευή σας ίσως δεν υποστηρίζει WebGL.',
  },
  et: {
    title: 'Paigaldise 3D-visualiseering',
    sunElevation: 'Päikese kõrgus',
    sunAzimuth: 'Päikese asimuut',
    avgEfficiency: 'Keskmine kasutegur',
    sunriseSunset: 'Päikesetõus / Päikeseloojang',
    animateDay: 'Animeeri päeva',
    pause: 'Paus',
    now: 'Praegu',
    rotateRoof: 'Pööra katust 90°',
    rotateRoofTitle: 'Vaheta katuseharja orientatsiooni (piki / risti hoone pikitelge)',
    refresh: 'Värskenda',
    refreshTitle: 'Tühjenda vaade ja värskenda hooneandmeid OSM-ist',
    mapAttribution: 'Kaart © OpenStreetMap',
    timeSlider: 'Päevaaja valija',
    string: 'String',
    tilt: 'Kalle',
    azimuth: 'Asimuut',
    panelCount: 'Paneelide arv',
    panelPower: 'Paneeli võimsus',
    efficiency: 'Kasutegur',
    illumination: 'Valgustus',
    errTitle: '3D-visualiseeringut ei saa renderdada',
    errBody: 'Teie brauser või seade ei pruugi WebGL-i toetada.',
  },
  lv: {
    title: 'Iekārtas 3D vizualizācija',
    sunElevation: 'Saules augstums',
    sunAzimuth: 'Saules azimuts',
    avgEfficiency: 'Vidējā efektivitāte',
    sunriseSunset: 'Saullēkts / Saulriets',
    animateDay: 'Animēt dienu',
    pause: 'Pauze',
    now: 'Tagad',
    rotateRoof: 'Pagriezt jumtu par 90°',
    rotateRoofTitle: 'Mainīt jumta kores orientāciju (gar / pāri ēkas garajai asij)',
    refresh: 'Atsvaidzināt',
    refreshTitle: 'Notīrīt skatu un atsvaidzināt ēkas datus no OSM',
    mapAttribution: 'Karte © OpenStreetMap',
    timeSlider: 'Diennakts laika izvēle',
    string: 'Virkne',
    tilt: 'Slīpums',
    azimuth: 'Azimuts',
    panelCount: 'Paneļu skaits',
    panelPower: 'Paneļa jauda',
    efficiency: 'Efektivitāte',
    illumination: 'Apgaismojums',
    errTitle: 'Nevar atveidot 3D vizualizāciju',
    errBody: 'Jūsu pārlūks vai ierīce, iespējams, neatbalsta WebGL.',
  },
  lt: {
    title: 'Įrenginio 3D vizualizacija',
    sunElevation: 'Saulės aukštis',
    sunAzimuth: 'Saulės azimutas',
    avgEfficiency: 'Vidutinis naudingumas',
    sunriseSunset: 'Saulėtekis / Saulėlydis',
    animateDay: 'Animuoti dieną',
    pause: 'Pauzė',
    now: 'Dabar',
    rotateRoof: 'Pasukti stogą 90°',
    rotateRoofTitle: 'Pakeisti stogo kraigo orientaciją (išilgai / skersai ilgosios pastato ašies)',
    refresh: 'Atnaujinti',
    refreshTitle: 'Išvalyti vaizdą ir atnaujinti pastato duomenis iš OSM',
    mapAttribution: 'Žemėlapis © OpenStreetMap',
    timeSlider: 'Paros laiko parinkiklis',
    string: 'Eilutė',
    tilt: 'Pasvirimas',
    azimuth: 'Azimutas',
    panelCount: 'Plokščių skaičius',
    panelPower: 'Plokštės galia',
    efficiency: 'Naudingumas',
    illumination: 'Apšvietimas',
    errTitle: 'Nepavyksta atvaizduoti 3D vizualizacijos',
    errBody: 'Jūsų naršyklė ar įrenginys gali nepalaikyti WebGL.',
  },
  mt: {
    title: 'Viżwalizzazzjoni 3D tal-installazzjoni',
    sunElevation: 'Għoli tax-xemx',
    sunAzimuth: 'Azimut tax-xemx',
    avgEfficiency: 'Effiċjenza medja',
    sunriseSunset: 'Tlugħ ix-xemx / Inżul ix-xemx',
    animateDay: 'Animar il-jum',
    pause: 'Pawża',
    now: 'Issa',
    rotateRoof: 'Dawwar is-saqaf 90°',
    rotateRoofTitle:
      'Ibdel l-orjentazzjoni tal-quċċata tas-saqaf (matul / mil-lat tal-assi twil tal-bini)',
    refresh: 'Aġġorna',
    refreshTitle: 'Naddaf il-veduta u aġġorna d-dejta tal-bini minn OSM',
    mapAttribution: 'Mappa © OpenStreetMap',
    timeSlider: 'Selettur tal-ħin tal-jum',
    string: 'String',
    tilt: 'Inklinazzjoni',
    azimuth: 'Azimut',
    panelCount: "Numru ta' pannelli",
    panelPower: 'Qawwa tal-pannell',
    efficiency: 'Effiċjenza',
    illumination: 'Illuminazzjoni',
    errTitle: 'Ma tistax tinforma l-viżwalizzazzjoni 3D',
    errBody: "Il-browser jew it-tagħmir tiegħek jista' ma jappoġġjax WebGL.",
  },
  ga: {
    title: 'Léirshamhlú 3D den tsuiteáil',
    sunElevation: 'Airde na gréine',
    sunAzimuth: 'Asaimit na gréine',
    avgEfficiency: 'Meánéifeachtúlacht',
    sunriseSunset: 'Éirí / Luí na gréine',
    animateDay: 'Beochan an lae',
    pause: 'Sos',
    now: 'Anois',
    rotateRoof: 'Rothlaigh an díon 90°',
    rotateRoofTitle: 'Athraigh treoshuíomh dhroim an dín (feadh / trasna ais fhada an fhoirgnimh)',
    refresh: 'Athnuaigh',
    refreshTitle: 'Glan an radharc agus athnuaigh sonraí an fhoirgnimh ó OSM',
    mapAttribution: 'Léarscáil © OpenStreetMap',
    timeSlider: 'Roghnóir am an lae',
    string: 'Slabhra',
    tilt: 'Claonadh',
    azimuth: 'Asaimit',
    panelCount: 'Líon na bpainéal',
    panelPower: 'Cumhacht an phainéil',
    efficiency: 'Éifeachtúlacht',
    illumination: 'Soilsiú',
    errTitle: 'Ní féidir an léirshamhlú 3D a sholáthar',
    errBody: "B'fhéidir nach dtacaíonn do bhrabhsálaí ná do ghléas le WebGL.",
  },
  no: {
    title: '3D-anleggsvisualisering',
    sunElevation: 'Solhøyde',
    sunAzimuth: 'Solazimut',
    avgEfficiency: 'Gjennomsnittlig virkningsgrad',
    sunriseSunset: 'Soloppgang / Solnedgang',
    animateDay: 'Animer dagen',
    pause: 'Pause',
    now: 'Nå',
    rotateRoof: 'Roter taket 90°',
    rotateRoofTitle: 'Bytt takryggens orientering (langs / på tvers av bygningens lange akse)',
    refresh: 'Oppdater',
    refreshTitle: 'Tøm visningen og oppdater bygningsdata fra OSM',
    mapAttribution: 'Kart © OpenStreetMap',
    timeSlider: 'Velger for tid på døgnet',
    string: 'Streng',
    tilt: 'Helning',
    azimuth: 'Asimut',
    panelCount: 'Antall paneler',
    panelPower: 'Paneleffekt',
    efficiency: 'Virkningsgrad',
    illumination: 'Belysning',
    errTitle: 'Kan ikke gjengi 3D-visualiseringen',
    errBody: 'Nettleseren eller enheten din støtter kanskje ikke WebGL.',
  },
  is: {
    title: '3D-myndræn framsetning uppsetningar',
    sunElevation: 'Hæð sólar',
    sunAzimuth: 'Áttarhorn sólar',
    avgEfficiency: 'Meðalnýtni',
    sunriseSunset: 'Sólarupprás / Sólsetur',
    animateDay: 'Hreyfa daginn',
    pause: 'Hlé',
    now: 'Núna',
    rotateRoof: 'Snúa þaki 90°',
    rotateRoofTitle: 'Skipta um stefnu þakkambs (eftir / þvert á langás byggingarinnar)',
    refresh: 'Endurnýja',
    refreshTitle: 'Hreinsa sýn og endurnýja byggingargögn frá OSM',
    mapAttribution: 'Kort © OpenStreetMap',
    timeSlider: 'Val á tíma dags',
    string: 'Strengur',
    tilt: 'Halli',
    azimuth: 'Áttarhorn',
    panelCount: 'Fjöldi spjalda',
    panelPower: 'Afl spjalds',
    efficiency: 'Nýtni',
    illumination: 'Lýsing',
    errTitle: 'Ekki tókst að birta 3D-framsetninguna',
    errBody: 'Vafrinn þinn eða tækið gæti ekki stutt WebGL.',
  },
  uk: {
    title: '3D-візуалізація установки',
    sunElevation: 'Висота сонця',
    sunAzimuth: 'Азимут сонця',
    avgEfficiency: 'Середня ефективність',
    sunriseSunset: 'Схід / Захід сонця',
    animateDay: 'Анімувати день',
    pause: 'Пауза',
    now: 'Зараз',
    rotateRoof: 'Повернути дах на 90°',
    rotateRoofTitle: 'Змінити орієнтацію коника даху (уздовж / поперек довгої осі будівлі)',
    refresh: 'Оновити',
    refreshTitle: 'Очистити вигляд і оновити дані будівлі з OSM',
    mapAttribution: 'Карта © OpenStreetMap',
    timeSlider: 'Вибір часу доби',
    string: 'Стрінг',
    tilt: 'Нахил',
    azimuth: 'Азимут',
    panelCount: 'Кількість панелей',
    panelPower: 'Потужність панелі',
    efficiency: 'Ефективність',
    illumination: 'Освітлення',
    errTitle: 'Не вдається відобразити 3D-візуалізацію',
    errBody: 'Ваш браузер або пристрій може не підтримувати WebGL.',
  },
  sr: {
    title: '3D vizuelizacija postrojenja',
    sunElevation: 'Visina sunca',
    sunAzimuth: 'Azimut sunca',
    avgEfficiency: 'Prosečna efikasnost',
    sunriseSunset: 'Izlazak / Zalazak sunca',
    animateDay: 'Animiraj dan',
    pause: 'Pauza',
    now: 'Sada',
    rotateRoof: 'Zaokreni krov za 90°',
    rotateRoofTitle: 'Zameni orijentaciju slemena krova (uzduž / poprečno duge ose zgrade)',
    refresh: 'Osveži',
    refreshTitle: 'Očisti prikaz i osveži podatke o zgradi iz OSM-a',
    mapAttribution: 'Mapa © OpenStreetMap',
    timeSlider: 'Birač doba dana',
    string: 'Niz',
    tilt: 'Nagib',
    azimuth: 'Azimut',
    panelCount: 'Broj panela',
    panelPower: 'Snaga panela',
    efficiency: 'Efikasnost',
    illumination: 'Osvetljenje',
    errTitle: 'Nije moguće prikazati 3D vizuelizaciju',
    errBody: 'Vaš pregledač ili uređaj možda ne podržava WebGL.',
  },
  sq: {
    title: 'Vizualizimi 3D i instalimit',
    sunElevation: 'Lartësia e diellit',
    sunAzimuth: 'Azimuti i diellit',
    avgEfficiency: 'Efikasiteti mesatar',
    sunriseSunset: 'Lindja / Perëndimi i diellit',
    animateDay: 'Animo ditën',
    pause: 'Pauzë',
    now: 'Tani',
    rotateRoof: 'Rrotullo çatinë 90°',
    rotateRoofTitle:
      'Ndërro orientimin e kreshtës së çatisë (përgjatë / tërthor boshtit të gjatë të ndërtesës)',
    refresh: 'Rifresko',
    refreshTitle: 'Pastro pamjen dhe rifresko të dhënat e ndërtesës nga OSM',
    mapAttribution: 'Harta © OpenStreetMap',
    timeSlider: 'Përzgjedhës i orës së ditës',
    string: 'Varg',
    tilt: 'Pjerrësia',
    azimuth: 'Azimuti',
    panelCount: 'Numri i paneleve',
    panelPower: 'Fuqia e panelit',
    efficiency: 'Efikasiteti',
    illumination: 'Ndriçimi',
    errTitle: 'Nuk mund të paraqitet vizualizimi 3D',
    errBody: 'Shfletuesi ose pajisja juaj mund të mos e mbështesë WebGL.',
  },
  mk: {
    title: '3D визуелизација на инсталацијата',
    sunElevation: 'Висина на сонцето',
    sunAzimuth: 'Азимут на сонцето',
    avgEfficiency: 'Просечна ефикасност',
    sunriseSunset: 'Изгрејсонце / Зајдисонце',
    animateDay: 'Анимирај го денот',
    pause: 'Пауза',
    now: 'Сега',
    rotateRoof: 'Заврти го кровот за 90°',
    rotateRoofTitle:
      'Замени ја ориентацијата на сртот на кровот (по должина / попречно на долгата оска на зградата)',
    refresh: 'Освежи',
    refreshTitle: 'Исчисти го приказот и освежи ги податоците за зградата од OSM',
    mapAttribution: 'Карта © OpenStreetMap',
    timeSlider: 'Избирач на време од денот',
    string: 'Стринг',
    tilt: 'Наклон',
    azimuth: 'Азимут',
    panelCount: 'Број на панели',
    panelPower: 'Моќност на панелот',
    efficiency: 'Ефикасност',
    illumination: 'Осветлување',
    errTitle: 'Не може да се прикаже 3D визуелизацијата',
    errBody: 'Вашиот прелистувач или уред можеби не поддржува WebGL.',
  },
  bs: {
    title: '3D vizualizacija postrojenja',
    sunElevation: 'Visina sunca',
    sunAzimuth: 'Azimut sunca',
    avgEfficiency: 'Prosječna efikasnost',
    sunriseSunset: 'Izlazak / Zalazak sunca',
    animateDay: 'Animiraj dan',
    pause: 'Pauza',
    now: 'Sada',
    rotateRoof: 'Zarotiraj krov za 90°',
    rotateRoofTitle: 'Zamijeni orijentaciju sljemena krova (uzduž / poprijeko duge ose zgrade)',
    refresh: 'Osvježi',
    refreshTitle: 'Očisti prikaz i osvježi podatke o zgradi iz OSM-a',
    mapAttribution: 'Karta © OpenStreetMap',
    timeSlider: 'Birač doba dana',
    string: 'Niz',
    tilt: 'Nagib',
    azimuth: 'Azimut',
    panelCount: 'Broj panela',
    panelPower: 'Snaga panela',
    efficiency: 'Efikasnost',
    illumination: 'Osvjetljenje',
    errTitle: 'Nije moguće prikazati 3D vizualizaciju',
    errBody: 'Vaš preglednik ili uređaj možda ne podržava WebGL.',
  },
};

/** Namespace 'quickLinks' — QuickLinks komponent (jak korzystać z API). */
const quickLinksTr: Record<string, Dict> = {
  en: {
    howToUse: 'How to use the API',
    reqWithInstallation: 'Request with a saved installation',
    reqWithInstallationDesc:
      'Installation parameters (location, strings) are taken automatically from your profile:',
    dayRequired: 'Parameter "day" is required: "today" or "tomorrow"',
    reqWithParams: 'Request with URL parameters',
    reqWithParamsDesc:
      'Full configuration passed as a query string (overrides the saved installation):',
    paramsHeading: 'Parameters',
    headers: 'Headers',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Authorization header',
    pRequired: 'required',
    pOptional: 'optional',
    pDay: 'required: today or tomorrow',
    pType: 'optional: standard (default) or chart',
    pLatLon: 'location and time zone',
    pAz1: 'azimuth, tilt, panels, area, efficiency (string 1)',
    pAz24: 'optional strings 2–4',
  },
  pl: {
    howToUse: 'Jak korzystać z API',
    reqWithInstallation: 'Zapytanie z zapisaną instalacją',
    reqWithInstallationDesc:
      'Parametry instalacji (lokalizacja, stringi) pobierane automatycznie z profilu:',
    dayRequired: 'Parametr „day" jest wymagany: „today" lub „tomorrow"',
    reqWithParams: 'Zapytanie z parametrami w URL',
    reqWithParamsDesc:
      'Pełna konfiguracja przekazana jako query string (nadpisuje zapisaną instalację):',
    paramsHeading: 'Parametry',
    headers: 'Nagłówki',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Nagłówek autoryzacji',
    pRequired: 'wymagany',
    pOptional: 'opcjonalny',
    pDay: 'wymagany: today lub tomorrow',
    pType: 'opcjonalny: standard (domyślny) lub chart',
    pLatLon: 'lokalizacja i strefa czasowa',
    pAz1: 'azymut, nachylenie, panele, powierzchnia, sprawność (string 1)',
    pAz24: 'opcjonalne stringi 2–4',
  },
  de: {
    howToUse: 'API-Nutzung',
    reqWithInstallation: 'Anfrage mit gespeicherter Anlage',
    reqWithInstallationDesc:
      'Anlagenparameter (Standort, Strings) werden automatisch aus Ihrem Profil übernommen:',
    dayRequired: 'Parameter „day" ist erforderlich: „today" oder „tomorrow"',
    reqWithParams: 'Anfrage mit URL-Parametern',
    reqWithParamsDesc:
      'Vollständige Konfiguration als Query-String übergeben (überschreibt die gespeicherte Anlage):',
    paramsHeading: 'Parameter',
    headers: 'Header',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Authorization-Header',
    pRequired: 'erforderlich',
    pOptional: 'optional',
    pDay: 'erforderlich: today oder tomorrow',
    pType: 'optional: standard (Standard) oder chart',
    pLatLon: 'Standort und Zeitzone',
    pAz1: 'Azimut, Neigung, Module, Fläche, Wirkungsgrad (String 1)',
    pAz24: 'optionale Strings 2–4',
  },
  fr: {
    howToUse: "Utilisation de l'API",
    reqWithInstallation: 'Requête avec une installation enregistrée',
    reqWithInstallationDesc:
      "Les paramètres de l'installation (emplacement, chaînes) sont repris automatiquement de votre profil :",
    dayRequired: 'Le paramètre « day » est requis : « today » ou « tomorrow »',
    reqWithParams: 'Requête avec des paramètres URL',
    reqWithParamsDesc:
      "Configuration complète passée en query string (remplace l'installation enregistrée) :",
    paramsHeading: 'Paramètres',
    headers: 'En-têtes',
    endpoint: 'Endpoint',
    authHeaderLabel: "En-tête d'autorisation",
    pRequired: 'requis',
    pOptional: 'optionnel',
    pDay: 'requis : today ou tomorrow',
    pType: 'optionnel : standard (par défaut) ou chart',
    pLatLon: 'emplacement et fuseau horaire',
    pAz1: 'azimut, inclinaison, panneaux, surface, rendement (chaîne 1)',
    pAz24: 'chaînes optionnelles 2–4',
  },
  es: {
    howToUse: 'Cómo usar la API',
    reqWithInstallation: 'Solicitud con una instalación guardada',
    reqWithInstallationDesc:
      'Los parámetros de la instalación (ubicación, cadenas) se toman automáticamente de su perfil:',
    dayRequired: 'El parámetro «day» es obligatorio: «today» o «tomorrow»',
    reqWithParams: 'Solicitud con parámetros en la URL',
    reqWithParamsDesc:
      'Configuración completa pasada como query string (sobrescribe la instalación guardada):',
    paramsHeading: 'Parámetros',
    headers: 'Cabeceras',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Cabecera de autorización',
    pRequired: 'obligatorio',
    pOptional: 'opcional',
    pDay: 'obligatorio: today o tomorrow',
    pType: 'opcional: standard (predeterminado) o chart',
    pLatLon: 'ubicación y zona horaria',
    pAz1: 'acimut, inclinación, paneles, superficie, eficiencia (cadena 1)',
    pAz24: 'cadenas opcionales 2–4',
  },
  it: {
    howToUse: "Come usare l'API",
    reqWithInstallation: 'Richiesta con un impianto salvato',
    reqWithInstallationDesc:
      "I parametri dell'impianto (posizione, stringhe) vengono presi automaticamente dal tuo profilo:",
    dayRequired: 'Il parametro «day» è obbligatorio: «today» o «tomorrow»',
    reqWithParams: "Richiesta con parametri nell'URL",
    reqWithParamsDesc:
      "Configurazione completa passata come query string (sovrascrive l'impianto salvato):",
    paramsHeading: 'Parametri',
    headers: 'Intestazioni',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Intestazione di autorizzazione',
    pRequired: 'obbligatorio',
    pOptional: 'facoltativo',
    pDay: 'obbligatorio: today o tomorrow',
    pType: 'facoltativo: standard (predefinito) o chart',
    pLatLon: 'posizione e fuso orario',
    pAz1: 'azimut, inclinazione, pannelli, superficie, efficienza (stringa 1)',
    pAz24: 'stringhe facoltative 2–4',
  },
  pt: {
    howToUse: 'Como usar a API',
    reqWithInstallation: 'Pedido com uma instalação guardada',
    reqWithInstallationDesc:
      'Os parâmetros da instalação (localização, strings) são obtidos automaticamente do seu perfil:',
    dayRequired: 'O parâmetro «day» é obrigatório: «today» ou «tomorrow»',
    reqWithParams: 'Pedido com parâmetros no URL',
    reqWithParamsDesc:
      'Configuração completa passada como query string (substitui a instalação guardada):',
    paramsHeading: 'Parâmetros',
    headers: 'Cabeçalhos',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Cabeçalho de autorização',
    pRequired: 'obrigatório',
    pOptional: 'opcional',
    pDay: 'obrigatório: today ou tomorrow',
    pType: 'opcional: standard (predefinido) ou chart',
    pLatLon: 'localização e fuso horário',
    pAz1: 'azimute, inclinação, painéis, área, eficiência (string 1)',
    pAz24: 'strings opcionais 2–4',
  },
  nl: {
    howToUse: 'Hoe de API te gebruiken',
    reqWithInstallation: 'Verzoek met een opgeslagen installatie',
    reqWithInstallationDesc:
      'Installatieparameters (locatie, strings) worden automatisch uit uw profiel overgenomen:',
    dayRequired: 'Parameter "day" is verplicht: "today" of "tomorrow"',
    reqWithParams: 'Verzoek met URL-parameters',
    reqWithParamsDesc:
      'Volledige configuratie doorgegeven als query string (overschrijft de opgeslagen installatie):',
    paramsHeading: 'Parameters',
    headers: 'Headers',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorisatieheader',
    pRequired: 'verplicht',
    pOptional: 'optioneel',
    pDay: 'verplicht: today of tomorrow',
    pType: 'optioneel: standard (standaard) of chart',
    pLatLon: 'locatie en tijdzone',
    pAz1: 'azimut, helling, panelen, oppervlak, rendement (string 1)',
    pAz24: 'optionele strings 2–4',
  },
  da: {
    howToUse: "Sådan bruger du API'en",
    reqWithInstallation: 'Anmodning med et gemt anlæg',
    reqWithInstallationDesc:
      'Anlægsparametre (placering, strenge) hentes automatisk fra din profil:',
    dayRequired: 'Parameteren "day" er påkrævet: "today" eller "tomorrow"',
    reqWithParams: 'Anmodning med URL-parametre',
    reqWithParamsDesc: 'Fuld konfiguration sendt som query string (overskriver det gemte anlæg):',
    paramsHeading: 'Parametre',
    headers: 'Headers',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorisationsheader',
    pRequired: 'påkrævet',
    pOptional: 'valgfri',
    pDay: 'påkrævet: today eller tomorrow',
    pType: 'valgfri: standard (standard) eller chart',
    pLatLon: 'placering og tidszone',
    pAz1: 'azimut, hældning, paneler, areal, virkningsgrad (streng 1)',
    pAz24: 'valgfrie strenge 2–4',
  },
  sv: {
    howToUse: 'Så använder du API:et',
    reqWithInstallation: 'Förfrågan med en sparad anläggning',
    reqWithInstallationDesc:
      'Anläggningsparametrar (plats, strängar) hämtas automatiskt från din profil:',
    dayRequired: 'Parametern "day" är obligatorisk: "today" eller "tomorrow"',
    reqWithParams: 'Förfrågan med URL-parametrar',
    reqWithParamsDesc:
      'Fullständig konfiguration skickad som query string (skriver över den sparade anläggningen):',
    paramsHeading: 'Parametrar',
    headers: 'Headers',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Auktoriseringsheader',
    pRequired: 'obligatorisk',
    pOptional: 'valfri',
    pDay: 'obligatorisk: today eller tomorrow',
    pType: 'valfri: standard (standard) eller chart',
    pLatLon: 'plats och tidszon',
    pAz1: 'azimut, lutning, paneler, yta, verkningsgrad (sträng 1)',
    pAz24: 'valfria strängar 2–4',
  },
  fi: {
    howToUse: 'API:n käyttö',
    reqWithInstallation: 'Pyyntö tallennetulla laitoksella',
    reqWithInstallationDesc:
      'Laitoksen parametrit (sijainti, sarjat) haetaan automaattisesti profiilistasi:',
    dayRequired: 'Parametri "day" on pakollinen: "today" tai "tomorrow"',
    reqWithParams: 'Pyyntö URL-parametreilla',
    reqWithParamsDesc:
      'Täydellinen kokoonpano välitettynä query-merkkijonona (korvaa tallennetun laitoksen):',
    paramsHeading: 'Parametrit',
    headers: 'Otsikot',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Valtuutusotsikko',
    pRequired: 'pakollinen',
    pOptional: 'valinnainen',
    pDay: 'pakollinen: today tai tomorrow',
    pType: 'valinnainen: standard (oletus) tai chart',
    pLatLon: 'sijainti ja aikavyöhyke',
    pAz1: 'atsimuutti, kallistus, paneelit, pinta-ala, hyötysuhde (sarja 1)',
    pAz24: 'valinnaiset sarjat 2–4',
  },
  cs: {
    howToUse: 'Jak používat API',
    reqWithInstallation: 'Požadavek s uloženou instalací',
    reqWithInstallationDesc:
      'Parametry instalace (poloha, stringy) se automaticky načítají z vašeho profilu:',
    dayRequired: 'Parametr „day" je povinný: „today" nebo „tomorrow"',
    reqWithParams: 'Požadavek s parametry v URL',
    reqWithParamsDesc: 'Úplná konfigurace předaná jako query string (přepíše uloženou instalaci):',
    paramsHeading: 'Parametry',
    headers: 'Hlavičky',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizační hlavička',
    pRequired: 'povinný',
    pOptional: 'volitelný',
    pDay: 'povinný: today nebo tomorrow',
    pType: 'volitelný: standard (výchozí) nebo chart',
    pLatLon: 'poloha a časové pásmo',
    pAz1: 'azimut, sklon, panely, plocha, účinnost (string 1)',
    pAz24: 'volitelné stringy 2–4',
  },
  sk: {
    howToUse: 'Ako používať API',
    reqWithInstallation: 'Požiadavka s uloženou inštaláciou',
    reqWithInstallationDesc:
      'Parametre inštalácie (poloha, stringy) sa automaticky načítavajú z vášho profilu:',
    dayRequired: 'Parameter „day" je povinný: „today" alebo „tomorrow"',
    reqWithParams: 'Požiadavka s parametrami v URL',
    reqWithParamsDesc:
      'Úplná konfigurácia odovzdaná ako query string (prepíše uloženú inštaláciu):',
    paramsHeading: 'Parametre',
    headers: 'Hlavičky',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizačná hlavička',
    pRequired: 'povinný',
    pOptional: 'voliteľný',
    pDay: 'povinný: today alebo tomorrow',
    pType: 'voliteľný: standard (predvolený) alebo chart',
    pLatLon: 'poloha a časové pásmo',
    pAz1: 'azimut, sklon, panely, plocha, účinnosť (string 1)',
    pAz24: 'voliteľné stringy 2–4',
  },
  sl: {
    howToUse: 'Kako uporabljati API',
    reqWithInstallation: 'Zahteva s shranjeno napravo',
    reqWithInstallationDesc:
      'Parametri naprave (lokacija, nizi) se samodejno pridobijo iz vašega profila:',
    dayRequired: 'Parameter »day« je obvezen: »today« ali »tomorrow«',
    reqWithParams: 'Zahteva s parametri v URL',
    reqWithParamsDesc:
      'Celotna konfiguracija posredovana kot poizvedbeni niz (prepiše shranjeno napravo):',
    paramsHeading: 'Parametri',
    headers: 'Glave',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Avtorizacijska glava',
    pRequired: 'obvezen',
    pOptional: 'neobvezen',
    pDay: 'obvezen: today ali tomorrow',
    pType: 'neobvezen: standard (privzeto) ali chart',
    pLatLon: 'lokacija in časovni pas',
    pAz1: 'azimut, naklon, paneli, površina, učinkovitost (niz 1)',
    pAz24: 'neobvezni nizi 2–4',
  },
  hr: {
    howToUse: 'Kako koristiti API',
    reqWithInstallation: 'Zahtjev sa spremljenim postrojenjem',
    reqWithInstallationDesc:
      'Parametri postrojenja (lokacija, nizovi) preuzimaju se automatski iz vašeg profila:',
    dayRequired: 'Parametar „day" je obavezan: „today" ili „tomorrow"',
    reqWithParams: 'Zahtjev s parametrima u URL-u',
    reqWithParamsDesc:
      'Potpuna konfiguracija proslijeđena kao query string (prepisuje spremljeno postrojenje):',
    paramsHeading: 'Parametri',
    headers: 'Zaglavlja',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizacijsko zaglavlje',
    pRequired: 'obavezan',
    pOptional: 'neobavezan',
    pDay: 'obavezan: today ili tomorrow',
    pType: 'neobavezan: standard (zadano) ili chart',
    pLatLon: 'lokacija i vremenska zona',
    pAz1: 'azimut, nagib, paneli, površina, učinkovitost (niz 1)',
    pAz24: 'neobavezni nizovi 2–4',
  },
  hu: {
    howToUse: 'Az API használata',
    reqWithInstallation: 'Kérés mentett telepítéssel',
    reqWithInstallationDesc:
      'A telepítés paraméterei (helyszín, stringek) automatikusan a profiljából származnak:',
    dayRequired: 'A „day" paraméter kötelező: „today" vagy „tomorrow"',
    reqWithParams: 'Kérés URL-paraméterekkel',
    reqWithParamsDesc:
      'Teljes konfiguráció query stringként átadva (felülírja a mentett telepítést):',
    paramsHeading: 'Paraméterek',
    headers: 'Fejlécek',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Engedélyezési fejléc',
    pRequired: 'kötelező',
    pOptional: 'opcionális',
    pDay: 'kötelező: today vagy tomorrow',
    pType: 'opcionális: standard (alapértelmezett) vagy chart',
    pLatLon: 'helyszín és időzóna',
    pAz1: 'azimut, dőlés, panelek, terület, hatásfok (string 1)',
    pAz24: 'opcionális stringek 2–4',
  },
  ro: {
    howToUse: 'Cum se folosește API-ul',
    reqWithInstallation: 'Cerere cu o instalație salvată',
    reqWithInstallationDesc:
      'Parametrii instalației (locație, string-uri) sunt preluați automat din profilul dvs.:',
    dayRequired: 'Parametrul „day" este obligatoriu: „today" sau „tomorrow"',
    reqWithParams: 'Cerere cu parametri în URL',
    reqWithParamsDesc:
      'Configurație completă transmisă ca query string (suprascrie instalația salvată):',
    paramsHeading: 'Parametri',
    headers: 'Anteturi',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Antet de autorizare',
    pRequired: 'obligatoriu',
    pOptional: 'opțional',
    pDay: 'obligatoriu: today sau tomorrow',
    pType: 'opțional: standard (implicit) sau chart',
    pLatLon: 'locație și fus orar',
    pAz1: 'azimut, înclinare, panouri, suprafață, eficiență (string 1)',
    pAz24: 'string-uri opționale 2–4',
  },
  bg: {
    howToUse: 'Как се използва API-то',
    reqWithInstallation: 'Заявка със запазена инсталация',
    reqWithInstallationDesc:
      'Параметрите на инсталацията (местоположение, стрингове) се вземат автоматично от вашия профил:',
    dayRequired: 'Параметърът „day" е задължителен: „today" или „tomorrow"',
    reqWithParams: 'Заявка с параметри в URL',
    reqWithParamsDesc:
      'Пълна конфигурация, предадена като query string (презаписва запазената инсталация):',
    paramsHeading: 'Параметри',
    headers: 'Заглавки',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Заглавка за оторизация',
    pRequired: 'задължителен',
    pOptional: 'незадължителен',
    pDay: 'задължителен: today или tomorrow',
    pType: 'незадължителен: standard (по подразбиране) или chart',
    pLatLon: 'местоположение и часова зона',
    pAz1: 'азимут, наклон, панели, площ, ефективност (стринг 1)',
    pAz24: 'незадължителни стрингове 2–4',
  },
  el: {
    howToUse: 'Πώς να χρησιμοποιήσετε το API',
    reqWithInstallation: 'Αίτημα με αποθηκευμένη εγκατάσταση',
    reqWithInstallationDesc:
      'Οι παράμετροι της εγκατάστασης (τοποθεσία, strings) λαμβάνονται αυτόματα από το προφίλ σας:',
    dayRequired: 'Η παράμετρος «day» απαιτείται: «today» ή «tomorrow»',
    reqWithParams: 'Αίτημα με παραμέτρους στο URL',
    reqWithParamsDesc:
      'Πλήρης διαμόρφωση που περνά ως query string (αντικαθιστά την αποθηκευμένη εγκατάσταση):',
    paramsHeading: 'Παράμετροι',
    headers: 'Κεφαλίδες',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Κεφαλίδα εξουσιοδότησης',
    pRequired: 'απαιτούμενο',
    pOptional: 'προαιρετικό',
    pDay: 'απαιτούμενο: today ή tomorrow',
    pType: 'προαιρετικό: standard (προεπιλογή) ή chart',
    pLatLon: 'τοποθεσία και ζώνη ώρας',
    pAz1: 'αζιμούθιο, κλίση, πάνελ, επιφάνεια, απόδοση (string 1)',
    pAz24: 'προαιρετικά strings 2–4',
  },
  et: {
    howToUse: 'Kuidas API-t kasutada',
    reqWithInstallation: 'Päring salvestatud paigaldisega',
    reqWithInstallationDesc:
      'Paigaldise parameetrid (asukoht, stringid) võetakse automaatselt teie profiilist:',
    dayRequired: 'Parameeter "day" on kohustuslik: "today" või "tomorrow"',
    reqWithParams: 'Päring URL-i parameetritega',
    reqWithParamsDesc:
      'Täielik konfiguratsioon edastatud päringustringina (kirjutab üle salvestatud paigaldise):',
    paramsHeading: 'Parameetrid',
    headers: 'Päised',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autoriseerimispäis',
    pRequired: 'kohustuslik',
    pOptional: 'valikuline',
    pDay: 'kohustuslik: today või tomorrow',
    pType: 'valikuline: standard (vaikimisi) või chart',
    pLatLon: 'asukoht ja ajavöönd',
    pAz1: 'asimuut, kalle, paneelid, pindala, kasutegur (string 1)',
    pAz24: 'valikulised stringid 2–4',
  },
  lv: {
    howToUse: 'Kā lietot API',
    reqWithInstallation: 'Pieprasījums ar saglabātu iekārtu',
    reqWithInstallationDesc:
      'Iekārtas parametri (atrašanās vieta, virknes) tiek automātiski iegūti no jūsu profila:',
    dayRequired: 'Parametrs "day" ir obligāts: "today" vai "tomorrow"',
    reqWithParams: 'Pieprasījums ar URL parametriem',
    reqWithParamsDesc:
      'Pilna konfigurācija nodota kā vaicājuma virkne (pārraksta saglabāto iekārtu):',
    paramsHeading: 'Parametri',
    headers: 'Galvenes',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizācijas galvene',
    pRequired: 'obligāts',
    pOptional: 'neobligāts',
    pDay: 'obligāts: today vai tomorrow',
    pType: 'neobligāts: standard (noklusējums) vai chart',
    pLatLon: 'atrašanās vieta un laika josla',
    pAz1: 'azimuts, slīpums, paneļi, laukums, efektivitāte (virkne 1)',
    pAz24: 'neobligātas virknes 2–4',
  },
  lt: {
    howToUse: 'Kaip naudoti API',
    reqWithInstallation: 'Užklausa su išsaugotu įrenginiu',
    reqWithInstallationDesc:
      'Įrenginio parametrai (vieta, eilutės) automatiškai paimami iš jūsų profilio:',
    dayRequired: 'Parametras „day" yra privalomas: „today" arba „tomorrow"',
    reqWithParams: 'Užklausa su URL parametrais',
    reqWithParamsDesc:
      'Visa konfigūracija perduota kaip užklausos eilutė (perrašo išsaugotą įrenginį):',
    paramsHeading: 'Parametrai',
    headers: 'Antraštės',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizacijos antraštė',
    pRequired: 'privalomas',
    pOptional: 'neprivalomas',
    pDay: 'privalomas: today arba tomorrow',
    pType: 'neprivalomas: standard (numatytasis) arba chart',
    pLatLon: 'vieta ir laiko juosta',
    pAz1: 'azimutas, pasvirimas, plokštės, plotas, naudingumas (eilutė 1)',
    pAz24: 'neprivalomos eilutės 2–4',
  },
  mt: {
    howToUse: 'Kif tuża l-API',
    reqWithInstallation: "Talba b'installazzjoni salvata",
    reqWithInstallationDesc:
      'Il-parametri tal-installazzjoni (post, strings) jittieħdu awtomatikament mill-profil tiegħek:',
    dayRequired: 'Il-parametru "day" huwa meħtieġ: "today" jew "tomorrow"',
    reqWithParams: 'Talba bil-parametri fl-URL',
    reqWithParamsDesc:
      'Konfigurazzjoni sħiħa mgħoddija bħala query string (tikteb fuq l-installazzjoni salvata):',
    paramsHeading: 'Parametri',
    headers: 'Headers',
    endpoint: 'Endpoint',
    authHeaderLabel: "Header ta' awtorizzazzjoni",
    pRequired: 'meħtieġ',
    pOptional: 'fakultattiv',
    pDay: 'meħtieġ: today jew tomorrow',
    pType: 'fakultattiv: standard (default) jew chart',
    pLatLon: 'post u żona tal-ħin',
    pAz1: 'azimut, inklinazzjoni, pannelli, erja, effiċjenza (string 1)',
    pAz24: 'strings fakultattivi 2–4',
  },
  ga: {
    howToUse: 'Conas an API a úsáid',
    reqWithInstallation: 'Iarratas le suiteáil shábháilte',
    reqWithInstallationDesc:
      'Tógtar paraiméadair na suiteála (suíomh, slabhraí) go huathoibríoch ó do phróifíl:',
    dayRequired: 'Tá an paraiméadar "day" riachtanach: "today" nó "tomorrow"',
    reqWithParams: 'Iarratas le paraiméadair URL',
    reqWithParamsDesc:
      'Cumraíocht iomlán curtha ar aghaidh mar theaghrán ceiste (forscríobhann an tsuiteáil shábháilte):',
    paramsHeading: 'Paraiméadair',
    headers: 'Ceanntásca',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Ceanntásc údaraithe',
    pRequired: 'riachtanach',
    pOptional: 'roghnach',
    pDay: 'riachtanach: today nó tomorrow',
    pType: 'roghnach: standard (réamhshocrú) nó chart',
    pLatLon: 'suíomh agus crios ama',
    pAz1: 'asaimit, claonadh, painéil, achar, éifeachtúlacht (slabhra 1)',
    pAz24: 'slabhraí roghnacha 2–4',
  },
  no: {
    howToUse: 'Slik bruker du API-et',
    reqWithInstallation: 'Forespørsel med et lagret anlegg',
    reqWithInstallationDesc:
      'Anleggsparametere (plassering, strenger) hentes automatisk fra profilen din:',
    dayRequired: 'Parameteren "day" er påkrevd: "today" eller "tomorrow"',
    reqWithParams: 'Forespørsel med URL-parametere',
    reqWithParamsDesc:
      'Full konfigurasjon sendt som query string (overskriver det lagrede anlegget):',
    paramsHeading: 'Parametere',
    headers: 'Headere',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorisasjonsheader',
    pRequired: 'påkrevd',
    pOptional: 'valgfri',
    pDay: 'påkrevd: today eller tomorrow',
    pType: 'valgfri: standard (standard) eller chart',
    pLatLon: 'plassering og tidssone',
    pAz1: 'asimut, helning, paneler, areal, virkningsgrad (streng 1)',
    pAz24: 'valgfrie strenger 2–4',
  },
  is: {
    howToUse: 'Hvernig á að nota API-ið',
    reqWithInstallation: 'Beiðni með vistaðri uppsetningu',
    reqWithInstallationDesc:
      'Færibreytur uppsetningar (staðsetning, strengir) eru sjálfkrafa teknar úr prófílnum þínum:',
    dayRequired: 'Færibreytan "day" er nauðsynleg: "today" eða "tomorrow"',
    reqWithParams: 'Beiðni með URL-færibreytum',
    reqWithParamsDesc:
      'Full stilling send sem fyrirspurnarstrengur (skrifar yfir vistuðu uppsetninguna):',
    paramsHeading: 'Færibreytur',
    headers: 'Hausar',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Heimildarhaus',
    pRequired: 'nauðsynleg',
    pOptional: 'valfrjáls',
    pDay: 'nauðsynleg: today eða tomorrow',
    pType: 'valfrjáls: standard (sjálfgefið) eða chart',
    pLatLon: 'staðsetning og tímabelti',
    pAz1: 'áttarhorn, halli, spjöld, flatarmál, nýtni (strengur 1)',
    pAz24: 'valfrjálsir strengir 2–4',
  },
  uk: {
    howToUse: 'Як користуватися API',
    reqWithInstallation: 'Запит зі збереженою установкою',
    reqWithInstallationDesc:
      'Параметри установки (розташування, стрінги) беруться автоматично з вашого профілю:',
    dayRequired: "Параметр «day» обов'язковий: «today» або «tomorrow»",
    reqWithParams: 'Запит з параметрами в URL',
    reqWithParamsDesc:
      'Повна конфігурація передається як рядок запиту (перезаписує збережену установку):',
    paramsHeading: 'Параметри',
    headers: 'Заголовки',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Заголовок авторизації',
    pRequired: "обов'язковий",
    pOptional: "необов'язковий",
    pDay: "обов'язковий: today або tomorrow",
    pType: "необов'язковий: standard (за замовчуванням) або chart",
    pLatLon: 'розташування і часовий пояс',
    pAz1: 'азимут, нахил, панелі, площа, ефективність (стрінг 1)',
    pAz24: "необов'язкові стрінги 2–4",
  },
  sr: {
    howToUse: 'Kako koristiti API',
    reqWithInstallation: 'Zahtev sa sačuvanim postrojenjem',
    reqWithInstallationDesc:
      'Parametri postrojenja (lokacija, nizovi) preuzimaju se automatski iz vašeg profila:',
    dayRequired: 'Parametar „day" je obavezan: „today" ili „tomorrow"',
    reqWithParams: 'Zahtev sa parametrima u URL-u',
    reqWithParamsDesc:
      'Potpuna konfiguracija prosleđena kao query string (prepisuje sačuvano postrojenje):',
    paramsHeading: 'Parametri',
    headers: 'Zaglavlja',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizaciono zaglavlje',
    pRequired: 'obavezan',
    pOptional: 'opcioni',
    pDay: 'obavezan: today ili tomorrow',
    pType: 'opcioni: standard (podrazumevano) ili chart',
    pLatLon: 'lokacija i vremenska zona',
    pAz1: 'azimut, nagib, paneli, površina, efikasnost (niz 1)',
    pAz24: 'opcioni nizovi 2–4',
  },
  sq: {
    howToUse: 'Si të përdorni API-në',
    reqWithInstallation: 'Kërkesë me një instalim të ruajtur',
    reqWithInstallationDesc:
      'Parametrat e instalimit (vendndodhja, vargjet) merren automatikisht nga profili juaj:',
    dayRequired: 'Parametri "day" është i detyrueshëm: "today" ose "tomorrow"',
    reqWithParams: 'Kërkesë me parametra në URL',
    reqWithParamsDesc:
      'Konfigurimi i plotë i kaluar si varg pyetjeje (mbishkruan instalimin e ruajtur):',
    paramsHeading: 'Parametrat',
    headers: 'Headerët',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Headeri i autorizimit',
    pRequired: 'i detyrueshëm',
    pOptional: 'opsional',
    pDay: 'i detyrueshëm: today ose tomorrow',
    pType: 'opsional: standard (parazgjedhur) ose chart',
    pLatLon: 'vendndodhja dhe zona orare',
    pAz1: 'azimuti, pjerrësia, panelet, sipërfaqja, efikasiteti (vargu 1)',
    pAz24: 'vargje opsionale 2–4',
  },
  mk: {
    howToUse: 'Како да го користите API-то',
    reqWithInstallation: 'Барање со зачувана инсталација',
    reqWithInstallationDesc:
      'Параметрите на инсталацијата (локација, стрингови) се земаат автоматски од вашиот профил:',
    dayRequired: 'Параметарот „day" е задолжителен: „today" или „tomorrow"',
    reqWithParams: 'Барање со параметри во URL',
    reqWithParamsDesc:
      'Целосна конфигурација предадена како query string (ја презапишува зачуваната инсталација):',
    paramsHeading: 'Параметри',
    headers: 'Заглавија',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Заглавие за авторизација',
    pRequired: 'задолжителен',
    pOptional: 'опционален',
    pDay: 'задолжителен: today или tomorrow',
    pType: 'опционален: standard (стандардно) или chart',
    pLatLon: 'локација и временска зона',
    pAz1: 'азимут, наклон, панели, површина, ефикасност (стринг 1)',
    pAz24: 'опционални стрингови 2–4',
  },
  bs: {
    howToUse: 'Kako koristiti API',
    reqWithInstallation: 'Zahtjev sa sačuvanim postrojenjem',
    reqWithInstallationDesc:
      'Parametri postrojenja (lokacija, nizovi) preuzimaju se automatski iz vašeg profila:',
    dayRequired: 'Parametar „day" je obavezan: „today" ili „tomorrow"',
    reqWithParams: 'Zahtjev sa parametrima u URL-u',
    reqWithParamsDesc:
      'Potpuna konfiguracija proslijeđena kao query string (prepisuje sačuvano postrojenje):',
    paramsHeading: 'Parametri',
    headers: 'Zaglavlja',
    endpoint: 'Endpoint',
    authHeaderLabel: 'Autorizacijsko zaglavlje',
    pRequired: 'obavezan',
    pOptional: 'opcioni',
    pDay: 'obavezan: today ili tomorrow',
    pType: 'opcioni: standard (podrazumijevano) ili chart',
    pLatLon: 'lokacija i vremenska zona',
    pAz1: 'azimut, nagib, paneli, površina, efikasnost (niz 1)',
    pAz24: 'opcioni nizovi 2–4',
  },
};

/** Namespace 'admin' — panel administracyjny (na razie: Sidebar + layout). */
const adminTr: Record<string, Dict> = {
  en: {
    adminPanel: 'Admin Panel',
    changePassword: 'Change password',
    dashboard: 'Dashboard',
    plans: 'Plans',
    apiKeys: 'API keys',
    clients: 'Clients',
    map: 'Map',
    activationCodes: 'Activation codes',
    graviteeImport: 'Gravitee import',
    admins: 'Administrators',
    auditLogs: 'Audit logs',
    modelAccuracy: 'Model accuracy',
    tokens: 'Access tokens',
    authHeaders: 'Auth headers',
  },
  pl: {
    adminPanel: 'Panel Administracyjny',
    changePassword: 'Zmień hasło',
    dashboard: 'Dashboard',
    plans: 'Plany',
    apiKeys: 'Klucze API',
    clients: 'Klienci',
    map: 'Mapa',
    activationCodes: 'Kody aktywacyjne',
    graviteeImport: 'Import Gravitee',
    admins: 'Administratorzy',
    auditLogs: 'Logi audytu',
    modelAccuracy: 'Dokładność modeli',
    tokens: 'Tokeny dostępowe',
    authHeaders: 'Nagłówki auth',
  },
  de: {
    adminPanel: 'Administrationsbereich',
    changePassword: 'Passwort ändern',
    dashboard: 'Dashboard',
    plans: 'Tarife',
    apiKeys: 'API-Schlüssel',
    clients: 'Kunden',
    map: 'Karte',
    activationCodes: 'Aktivierungscodes',
    graviteeImport: 'Gravitee-Import',
    admins: 'Administratoren',
    auditLogs: 'Audit-Protokolle',
    modelAccuracy: 'Modellgenauigkeit',
    tokens: 'Zugriffstokens',
    authHeaders: 'Auth-Header',
  },
  fr: {
    adminPanel: "Panneau d'administration",
    changePassword: 'Changer le mot de passe',
    dashboard: 'Tableau de bord',
    plans: 'Forfaits',
    apiKeys: 'Clés API',
    clients: 'Clients',
    map: 'Carte',
    activationCodes: "Codes d'activation",
    graviteeImport: 'Import Gravitee',
    admins: 'Administrateurs',
    auditLogs: "Journaux d'audit",
    modelAccuracy: 'Précision des modèles',
    tokens: "Jetons d'accès",
    authHeaders: "En-têtes d'auth",
  },
  es: {
    adminPanel: 'Panel de administración',
    changePassword: 'Cambiar contraseña',
    dashboard: 'Panel',
    plans: 'Planes',
    apiKeys: 'Claves API',
    clients: 'Clientes',
    map: 'Mapa',
    activationCodes: 'Códigos de activación',
    graviteeImport: 'Importación Gravitee',
    admins: 'Administradores',
    auditLogs: 'Registros de auditoría',
    modelAccuracy: 'Precisión de modelos',
    tokens: 'Tokens de acceso',
    authHeaders: 'Cabeceras de auth',
  },
  it: {
    adminPanel: 'Pannello di amministrazione',
    changePassword: 'Cambia password',
    dashboard: 'Pannello',
    plans: 'Piani',
    apiKeys: 'Chiavi API',
    clients: 'Clienti',
    map: 'Mappa',
    activationCodes: 'Codici di attivazione',
    graviteeImport: 'Import Gravitee',
    admins: 'Amministratori',
    auditLogs: 'Registri di audit',
    modelAccuracy: 'Precisione dei modelli',
    tokens: 'Token di accesso',
    authHeaders: 'Intestazioni di auth',
  },
  pt: {
    adminPanel: 'Painel de administração',
    changePassword: 'Alterar palavra-passe',
    dashboard: 'Painel',
    plans: 'Planos',
    apiKeys: 'Chaves API',
    clients: 'Clientes',
    map: 'Mapa',
    activationCodes: 'Códigos de ativação',
    graviteeImport: 'Importação Gravitee',
    admins: 'Administradores',
    auditLogs: 'Registos de auditoria',
    modelAccuracy: 'Precisão dos modelos',
    tokens: 'Tokens de acesso',
    authHeaders: 'Cabeçalhos de auth',
  },
  nl: {
    adminPanel: 'Beheerpaneel',
    changePassword: 'Wachtwoord wijzigen',
    dashboard: 'Dashboard',
    plans: 'Abonnementen',
    apiKeys: 'API-sleutels',
    clients: 'Klanten',
    map: 'Kaart',
    activationCodes: 'Activeringscodes',
    graviteeImport: 'Gravitee-import',
    admins: 'Beheerders',
    auditLogs: 'Auditlogboeken',
    modelAccuracy: 'Modelnauwkeurigheid',
    tokens: 'Toegangstokens',
    authHeaders: 'Auth-headers',
  },
  da: {
    adminPanel: 'Administrationspanel',
    changePassword: 'Skift adgangskode',
    dashboard: 'Dashboard',
    plans: 'Planer',
    apiKeys: 'API-nøgler',
    clients: 'Kunder',
    map: 'Kort',
    activationCodes: 'Aktiveringskoder',
    graviteeImport: 'Gravitee-import',
    admins: 'Administratorer',
    auditLogs: 'Revisionslogfiler',
    modelAccuracy: 'Modelnøjagtighed',
    tokens: 'Adgangstokens',
    authHeaders: 'Auth-headers',
  },
  sv: {
    adminPanel: 'Administrationspanel',
    changePassword: 'Byt lösenord',
    dashboard: 'Dashboard',
    plans: 'Planer',
    apiKeys: 'API-nycklar',
    clients: 'Kunder',
    map: 'Karta',
    activationCodes: 'Aktiveringskoder',
    graviteeImport: 'Gravitee-import',
    admins: 'Administratörer',
    auditLogs: 'Granskningsloggar',
    modelAccuracy: 'Modellprecision',
    tokens: 'Åtkomsttokens',
    authHeaders: 'Auth-headers',
  },
  fi: {
    adminPanel: 'Hallintapaneeli',
    changePassword: 'Vaihda salasana',
    dashboard: 'Koontinäyttö',
    plans: 'Suunnitelmat',
    apiKeys: 'API-avaimet',
    clients: 'Asiakkaat',
    map: 'Kartta',
    activationCodes: 'Aktivointikoodit',
    graviteeImport: 'Gravitee-tuonti',
    admins: 'Ylläpitäjät',
    auditLogs: 'Tarkastuslokit',
    modelAccuracy: 'Mallin tarkkuus',
    tokens: 'Käyttötunnukset',
    authHeaders: 'Auth-otsikot',
  },
  cs: {
    adminPanel: 'Administrační panel',
    changePassword: 'Změnit heslo',
    dashboard: 'Přehled',
    plans: 'Tarify',
    apiKeys: 'API klíče',
    clients: 'Klienti',
    map: 'Mapa',
    activationCodes: 'Aktivační kódy',
    graviteeImport: 'Import Gravitee',
    admins: 'Administrátoři',
    auditLogs: 'Auditní protokoly',
    modelAccuracy: 'Přesnost modelů',
    tokens: 'Přístupové tokeny',
    authHeaders: 'Auth hlavičky',
  },
  sk: {
    adminPanel: 'Administračný panel',
    changePassword: 'Zmeniť heslo',
    dashboard: 'Prehľad',
    plans: 'Tarify',
    apiKeys: 'API kľúče',
    clients: 'Klienti',
    map: 'Mapa',
    activationCodes: 'Aktivačné kódy',
    graviteeImport: 'Import Gravitee',
    admins: 'Administrátori',
    auditLogs: 'Auditné protokoly',
    modelAccuracy: 'Presnosť modelov',
    tokens: 'Prístupové tokeny',
    authHeaders: 'Auth hlavičky',
  },
  sl: {
    adminPanel: 'Skrbniška plošča',
    changePassword: 'Spremeni geslo',
    dashboard: 'Nadzorna plošča',
    plans: 'Paketi',
    apiKeys: 'API ključi',
    clients: 'Stranke',
    map: 'Zemljevid',
    activationCodes: 'Aktivacijske kode',
    graviteeImport: 'Uvoz Gravitee',
    admins: 'Skrbniki',
    auditLogs: 'Revizijski dnevniki',
    modelAccuracy: 'Natančnost modelov',
    tokens: 'Dostopni žetoni',
    authHeaders: 'Auth glave',
  },
  hr: {
    adminPanel: 'Administracijska ploča',
    changePassword: 'Promijeni lozinku',
    dashboard: 'Nadzorna ploča',
    plans: 'Planovi',
    apiKeys: 'API ključevi',
    clients: 'Klijenti',
    map: 'Karta',
    activationCodes: 'Aktivacijski kodovi',
    graviteeImport: 'Uvoz Gravitee',
    admins: 'Administratori',
    auditLogs: 'Revizijski zapisi',
    modelAccuracy: 'Točnost modela',
    tokens: 'Pristupni tokeni',
    authHeaders: 'Auth zaglavlja',
  },
  hu: {
    adminPanel: 'Adminisztrációs felület',
    changePassword: 'Jelszó módosítása',
    dashboard: 'Irányítópult',
    plans: 'Csomagok',
    apiKeys: 'API-kulcsok',
    clients: 'Ügyfelek',
    map: 'Térkép',
    activationCodes: 'Aktiválási kódok',
    graviteeImport: 'Gravitee import',
    admins: 'Adminisztrátorok',
    auditLogs: 'Auditnaplók',
    modelAccuracy: 'Modellpontosság',
    tokens: 'Hozzáférési tokenek',
    authHeaders: 'Auth fejlécek',
  },
  ro: {
    adminPanel: 'Panou de administrare',
    changePassword: 'Schimbă parola',
    dashboard: 'Tablou de bord',
    plans: 'Planuri',
    apiKeys: 'Chei API',
    clients: 'Clienți',
    map: 'Hartă',
    activationCodes: 'Coduri de activare',
    graviteeImport: 'Import Gravitee',
    admins: 'Administratori',
    auditLogs: 'Jurnale de audit',
    modelAccuracy: 'Acuratețea modelelor',
    tokens: 'Token-uri de acces',
    authHeaders: 'Anteturi de auth',
  },
  bg: {
    adminPanel: 'Административен панел',
    changePassword: 'Смяна на паролата',
    dashboard: 'Табло',
    plans: 'Планове',
    apiKeys: 'API ключове',
    clients: 'Клиенти',
    map: 'Карта',
    activationCodes: 'Кодове за активиране',
    graviteeImport: 'Импорт Gravitee',
    admins: 'Администратори',
    auditLogs: 'Одитни дневници',
    modelAccuracy: 'Точност на моделите',
    tokens: 'Токени за достъп',
    authHeaders: 'Auth заглавки',
  },
  el: {
    adminPanel: 'Πίνακας διαχείρισης',
    changePassword: 'Αλλαγή κωδικού',
    dashboard: 'Πίνακας',
    plans: 'Προγράμματα',
    apiKeys: 'Κλειδιά API',
    clients: 'Πελάτες',
    map: 'Χάρτης',
    activationCodes: 'Κωδικοί ενεργοποίησης',
    graviteeImport: 'Εισαγωγή Gravitee',
    admins: 'Διαχειριστές',
    auditLogs: 'Αρχεία ελέγχου',
    modelAccuracy: 'Ακρίβεια μοντέλων',
    tokens: 'Διακριτικά πρόσβασης',
    authHeaders: 'Κεφαλίδες auth',
  },
  et: {
    adminPanel: 'Halduspaneel',
    changePassword: 'Muuda parooli',
    dashboard: 'Töölaud',
    plans: 'Paketid',
    apiKeys: 'API-võtmed',
    clients: 'Kliendid',
    map: 'Kaart',
    activationCodes: 'Aktiveerimiskoodid',
    graviteeImport: 'Gravitee import',
    admins: 'Administraatorid',
    auditLogs: 'Auditilogid',
    modelAccuracy: 'Mudeli täpsus',
    tokens: 'Juurdepääsutokenid',
    authHeaders: 'Auth päised',
  },
  lv: {
    adminPanel: 'Administrēšanas panelis',
    changePassword: 'Mainīt paroli',
    dashboard: 'Panelis',
    plans: 'Plāni',
    apiKeys: 'API atslēgas',
    clients: 'Klienti',
    map: 'Karte',
    activationCodes: 'Aktivizācijas kodi',
    graviteeImport: 'Gravitee imports',
    admins: 'Administratori',
    auditLogs: 'Audita žurnāli',
    modelAccuracy: 'Modeļu precizitāte',
    tokens: 'Piekļuves marķieri',
    authHeaders: 'Auth galvenes',
  },
  lt: {
    adminPanel: 'Administravimo skydelis',
    changePassword: 'Keisti slaptažodį',
    dashboard: 'Skydelis',
    plans: 'Planai',
    apiKeys: 'API raktai',
    clients: 'Klientai',
    map: 'Žemėlapis',
    activationCodes: 'Aktyvavimo kodai',
    graviteeImport: 'Gravitee importas',
    admins: 'Administratoriai',
    auditLogs: 'Audito žurnalai',
    modelAccuracy: 'Modelių tikslumas',
    tokens: 'Prieigos prieigos raktai',
    authHeaders: 'Auth antraštės',
  },
  mt: {
    adminPanel: 'Pannell tal-amministrazzjoni',
    changePassword: 'Ibdel il-password',
    dashboard: 'Dashboard',
    plans: 'Pjanijiet',
    apiKeys: 'Ċwievet API',
    clients: 'Klijenti',
    map: 'Mappa',
    activationCodes: 'Kodiċijiet tal-attivazzjoni',
    graviteeImport: 'Importazzjoni Gravitee',
    admins: 'Amministraturi',
    auditLogs: 'Reġistri tal-awditjar',
    modelAccuracy: 'Preċiżjoni tal-mudelli',
    tokens: 'Tokens tal-aċċess',
    authHeaders: 'Headers tal-auth',
  },
  ga: {
    adminPanel: 'Painéal riaracháin',
    changePassword: 'Athraigh focal faire',
    dashboard: 'Deais',
    plans: 'Pleananna',
    apiKeys: 'Eochracha API',
    clients: 'Cliaint',
    map: 'Léarscáil',
    activationCodes: 'Cóid ghníomhachtaithe',
    graviteeImport: 'Iompórtáil Gravitee',
    admins: 'Riarthóirí',
    auditLogs: 'Logaí iniúchóireachta',
    modelAccuracy: 'Cruinneas múnlaí',
    tokens: 'Comharthaí rochtana',
    authHeaders: 'Ceanntásca auth',
  },
  no: {
    adminPanel: 'Administrasjonspanel',
    changePassword: 'Bytt passord',
    dashboard: 'Dashbord',
    plans: 'Planer',
    apiKeys: 'API-nøkler',
    clients: 'Kunder',
    map: 'Kart',
    activationCodes: 'Aktiveringskoder',
    graviteeImport: 'Gravitee-import',
    admins: 'Administratorer',
    auditLogs: 'Revisjonslogger',
    modelAccuracy: 'Modellnøyaktighet',
    tokens: 'Tilgangstokens',
    authHeaders: 'Auth-headere',
  },
  is: {
    adminPanel: 'Stjórnborð',
    changePassword: 'Breyta lykilorði',
    dashboard: 'Yfirlit',
    plans: 'Áskriftir',
    apiKeys: 'API-lyklar',
    clients: 'Viðskiptavinir',
    map: 'Kort',
    activationCodes: 'Virkjunarkóðar',
    graviteeImport: 'Gravitee innflutningur',
    admins: 'Stjórnendur',
    auditLogs: 'Endurskoðunarskrár',
    modelAccuracy: 'Nákvæmni líkana',
    tokens: 'Aðgangstóknar',
    authHeaders: 'Auth-hausar',
  },
  uk: {
    adminPanel: 'Панель адміністрування',
    changePassword: 'Змінити пароль',
    dashboard: 'Панель',
    plans: 'Плани',
    apiKeys: 'Ключі API',
    clients: 'Клієнти',
    map: 'Карта',
    activationCodes: 'Коди активації',
    graviteeImport: 'Імпорт Gravitee',
    admins: 'Адміністратори',
    auditLogs: 'Журнали аудиту',
    modelAccuracy: 'Точність моделей',
    tokens: 'Токени доступу',
    authHeaders: 'Auth-заголовки',
  },
  sr: {
    adminPanel: 'Administratorski panel',
    changePassword: 'Promeni lozinku',
    dashboard: 'Kontrolna tabla',
    plans: 'Planovi',
    apiKeys: 'API ključevi',
    clients: 'Klijenti',
    map: 'Mapa',
    activationCodes: 'Aktivacioni kodovi',
    graviteeImport: 'Uvoz Gravitee',
    admins: 'Administratori',
    auditLogs: 'Revizioni zapisi',
    modelAccuracy: 'Tačnost modela',
    tokens: 'Pristupni tokeni',
    authHeaders: 'Auth zaglavlja',
  },
  sq: {
    adminPanel: 'Paneli i administrimit',
    changePassword: 'Ndrysho fjalëkalimin',
    dashboard: 'Paneli',
    plans: 'Planet',
    apiKeys: 'Çelësat API',
    clients: 'Klientët',
    map: 'Harta',
    activationCodes: 'Kodet e aktivizimit',
    graviteeImport: 'Importi Gravitee',
    admins: 'Administratorët',
    auditLogs: 'Regjistrat e auditimit',
    modelAccuracy: 'Saktësia e modeleve',
    tokens: 'Tokenët e aksesit',
    authHeaders: 'Headerët e auth',
  },
  mk: {
    adminPanel: 'Администраторски панел',
    changePassword: 'Промени лозинка',
    dashboard: 'Контролна табла',
    plans: 'Планови',
    apiKeys: 'API клучеви',
    clients: 'Клиенти',
    map: 'Карта',
    activationCodes: 'Кодови за активирање',
    graviteeImport: 'Увоз Gravitee',
    admins: 'Администратори',
    auditLogs: 'Записи на ревизија',
    modelAccuracy: 'Точност на моделите',
    tokens: 'Токени за пристап',
    authHeaders: 'Auth заглавија',
  },
  bs: {
    adminPanel: 'Administratorski panel',
    changePassword: 'Promijeni lozinku',
    dashboard: 'Kontrolna ploča',
    plans: 'Planovi',
    apiKeys: 'API ključevi',
    clients: 'Klijenti',
    map: 'Karta',
    activationCodes: 'Aktivacijski kodovi',
    graviteeImport: 'Uvoz Gravitee',
    admins: 'Administratori',
    auditLogs: 'Revizijski zapisi',
    modelAccuracy: 'Tačnost modela',
    tokens: 'Pristupni tokeni',
    authHeaders: 'Auth zaglavlja',
  },
};

/** Namespace 'help' — strona pomocy / dokumentacji API. */
const helpTr: Record<string, Dict> = {
  en: {
    title: 'Help — API documentation',
    subtitle:
      'Complete Solar Forecast API documentation: modes, parameters, response formats and error codes.',
    toc: 'Table of contents',
    example: 'Example',
    exampleResponse: 'Example response',
    exampleError: 'Example error',
    s1Title: 'How to use the API',
    s1Intro:
      'The Forecast API supports two modes. In both, the API key must be passed in an HTTP header (the header name is defined in the plan — X-API-Key by default).',
    s1Mode1Title: 'Mode 1: Installation (simplified)',
    s1Mode1Desc:
      'All installation parameters (location, strings) are taken automatically from your profile. Just pass the "day" parameter.',
    s1Mode2Title: 'Mode 2: URL parameters (full control)',
    s1Mode2Desc:
      'You can pass all parameters directly in the query string. Requires a plan with the "allowQueryParams" option enabled. URL parameters override those from the saved installation.',
    s1Important:
      'Important: the "day" parameter is required in both modes. Allowed values: "today" or "tomorrow".',
    s2Title: 'Query parameters',
    s2Intro: 'Below is the full list of query string parameters for the GET {{endpoint}} endpoint.',
    thParam: 'Parameter',
    thType: 'Type',
    thRequired: 'Required',
    thDesc: 'Description',
    reqYes: 'yes',
    reqNo: 'no',
    dDay: 'Forecast day: "today" or "tomorrow"',
    dDate: 'Day key format: "relative" (default), "full" (YYYY-MM-DD) or "timestamp" (Unix seconds). With "full"/"timestamp" daily totals are returned in a "totals" map keyed by the same day keys. In the chart format only the totals change — hourly entries already carry full ts timestamps',
    dType: 'Response format: "standard" (default) or "chart"',
    dCorrection:
      'Production correction: "true" or "false". If omitted, the installation default is used.',
    dLat: 'Latitude (e.g. 52.23)',
    dLon: 'Longitude (e.g. 21.01)',
    dTz: 'UTC time zone offset (-12 to 12, e.g. 2 for CET summer)',
    dAz: 'Azimuth of string {{n}} in degrees (-180 to 180; 0=north, 180=south)',
    dTilt: 'Tilt of string {{n}} in degrees (0 to 90)',
    dPanels: 'Number of panels in string {{n}}',
    dPower: 'Panel power in kW in string {{n}} (e.g. 0.4 = 400W)',
    dEff: 'Panel efficiency of string {{n}} in % (e.g. 92)',
    s2Footnote:
      '* — Required in URL-parameter mode. In installation mode, location and string parameters are taken automatically. Location parameters (lat, lon, tz) and strings (az, t, n, s, e) require the "allowQueryParams" option in the plan.',
    s3Title: 'Response format',
    s3StdTitle: 'Standard response (type=standard)',
    s3StdDesc: 'Default format — hourly production in watt-hours (Wh) as a key-value object.',
    s3DateFmtTitle: 'Day keys as dates (date=full / date=timestamp)',
    s3DateFmtDesc: 'With date=full the day keys in result and strings become YYYY-MM-DD dates, and daily totals are returned in a "totals" map keyed by the same dates. date=timestamp works the same with Unix seconds (start of day in the tz timezone). Also applies to the "confidence" block with enriched=1. In the chart format only the totals change — hourly entries already carry full ts timestamps.',
    s3ChartTitle: 'Chart response (type=chart)',
    s3ChartDesc:
      'Format optimized for charts — an array of objects with an ISO timestamp and an energy value.',
    s3FieldsTitle: 'Field descriptions',
    thField: 'Field',
    fDate: 'Timestamp of response generation (ISO 8601)',
    fLatLon: 'Coordinates used in the request',
    fToday: 'Hourly production for today (key = hour, value = Wh)',
    fTomorrow: 'Hourly production for tomorrow',
    fTotals: 'Total production for the given day (Wh); with date=full/timestamp returned as a "totals" map keyed by day',
    fStrings: 'Array of per-string data (azimuth, efficiency, power, panel count, production)',
    fCorrApplied: 'Whether production correction was applied (true/false)',
    fProcTime: 'Request processing time in ms',
    fWeather:
      'Weather context: hourly cloud cover, air/panel temperature, wind, precipitation, snow, WMO code, UV, sunrise/sunset + per-day summary.',
    fWeatherAlerts: 'Active MeteoAlarm warnings for the location (empty array if none).',
    fProdAlerts: 'Forecast-derived production alerts: snow on panels, heavy clouds, strong gusts.',
    fAirQuality:
      'Air quality (PM10/PM2.5/dust/AOD) + panel-soiling assessment and cleaning recommendation.',
    fArchive:
      'Yearly site energy profile from the reanalysis archive: annual irradiation, monthly yield ranking, estimated optimal tilt/azimuth (best-effort, refreshed in the background).',
    fConfidence:
      'P10/P50/P90 production bands from the ensemble model spread (only when several models are configured).',
    s4Title: 'Error codes',
    s4Intro:
      'On error, the API returns a JSON object with an "error" field containing "code" and "message".',
    thCode: 'Code',
    thHttp: 'HTTP',
    eValidationError: 'Missing or invalid query parameters',
    eNoInstallation: 'No saved installation and no URL parameters',
    eApiKeyMissing: 'No API key in the header',
    eApiKeyInvalid: 'API key does not exist or is invalid',
    eApiKeyExpired: 'API key has expired',
    eApiKeyInactive: 'API key has been deactivated',
    ePlanLimitExceeded: 'Number of strings exceeds the plan limit',
    eQueryParamsNotAllowed: 'Plan does not allow dynamic URL parameters',
    eRateLimitExceeded: 'Request limit exceeded (rate limit)',
    eInternalError: 'Internal server error',
    eServiceError: 'Service error',
    s5Title: 'Production correction',
    s5Intro:
      'The correction feature improves forecast accuracy based on actual production data. The system compares forecast production with actual and computes per-hour (UTC) correction factors. Requires the "correctionEnabled" option in the plan.',
    s5SendTitle: 'Submitting actual data',
    s5SendDesc: 'Send hourly production for a given day to the endpoint:',
    s5DateD: 'date — date in YYYY-MM-DD format',
    s5TzD: 'tz — time zone (UTC offset) used when reading production',
    s5ProdD: 'production — object: key is the hour (string), value is production in Wh',
    s5EnableTitle: 'Enabling correction in the forecast',
    s5EnableDesc:
      'Add the "correction=true" parameter to the forecast request, or set the default correction in the client portal (Correction tab).',
    s5Note:
      'If the "correction" parameter is not provided, the system uses your installation default.',
    s6Title: 'Examples',
    ex1: 'Forecast for today (installation mode)',
    ex2: 'Forecast for tomorrow (installation mode)',
    ex3: 'Forecast with full configuration (2 strings)',
    ex4: 'Chart format (for charts)',
    ex5: 'Forecast with production correction',
    ex6: 'Handling the response (bash + jq)',
    ex7: 'Forecast with dates instead of today/tomorrow (date=full)',
  },
  pl: {
    title: 'Pomoc — dokumentacja API',
    subtitle:
      'Kompletna dokumentacja Solar Forecast API: tryby pracy, parametry, formaty odpowiedzi i kody błędów.',
    toc: 'Spis treści',
    example: 'Przykład',
    exampleResponse: 'Przykład odpowiedzi',
    exampleError: 'Przykład błędu',
    s1Title: 'Jak używać API',
    s1Intro:
      'Forecast API obsługuje dwa tryby pracy. W obu przypadkach klucz API należy przekazać w nagłówku HTTP (nazwa nagłówka jest zdefiniowana w planie — domyślnie X-API-Key).',
    s1Mode1Title: 'Tryb 1: Instalacja (uproszczony)',
    s1Mode1Desc:
      'Wszystkie parametry instalacji (lokalizacja, stringi) są pobierane automatycznie z Twojego profilu. Wystarczy podać parametr „day".',
    s1Mode2Title: 'Tryb 2: Parametry w URL (pełna kontrola)',
    s1Mode2Desc:
      'Możesz przekazać wszystkie parametry bezpośrednio w query stringu. Wymaga planu z włączoną opcją „allowQueryParams". Parametry z URL nadpisują te z zapisanej instalacji.',
    s1Important:
      'Ważne: parametr „day" jest wymagany w obu trybach. Dopuszczalne wartości: „today" lub „tomorrow".',
    s2Title: 'Parametry zapytania',
    s2Intro: 'Poniżej pełna lista parametrów query string dla endpointu GET {{endpoint}}.',
    thParam: 'Parametr',
    thType: 'Typ',
    thRequired: 'Wymagany',
    thDesc: 'Opis',
    reqYes: 'tak',
    reqNo: 'nie',
    dDay: 'Dzień prognozy: „today" lub „tomorrow"',
    dDate: 'Format kluczy dni: „relative" (domyślny), „full" (RRRR-MM-DD) lub „timestamp" (sekundy Unix). Przy „full"/„timestamp" sumy dzienne trafiają do mapy „totals" kluczowanej tak samo jak dni. W formacie chart zmieniają się tylko sumy — wpisy godzinowe mają już pełne znaczniki czasu ts',
    dType: 'Format odpowiedzi: „standard" (domyślny) lub „chart"',
    dCorrection:
      'Korekcja produkcji: „true" lub „false". Jeśli pominięty, stosowane jest ustawienie domyślne instalacji.',
    dLat: 'Szerokość geograficzna (np. 52.23)',
    dLon: 'Długość geograficzna (np. 21.01)',
    dTz: 'Przesunięcie strefy czasowej UTC (od -12 do 12, np. 2 dla CET lato)',
    dAz: 'Azymut stringu {{n}} w stopniach (-180 do 180; 0=północ, 180=południe)',
    dTilt: 'Nachylenie stringu {{n}} w stopniach (0 do 90)',
    dPanels: 'Liczba paneli w stringu {{n}}',
    dPower: 'Moc panelu w kW w stringu {{n}} (np. 0.4 = 400W)',
    dEff: 'Sprawność paneli stringu {{n}} w % (np. 92)',
    s2Footnote:
      '* — Wymagany w trybie z parametrami URL. W trybie instalacji parametry lokalizacji i stringów są pobierane automatycznie. Parametry lokalizacji (lat, lon, tz) oraz stringi (az, t, n, s, e) wymagają opcji „allowQueryParams" w planie.',
    s3Title: 'Format odpowiedzi',
    s3StdTitle: 'Odpowiedź standardowa (type=standard)',
    s3StdDesc:
      'Domyślny format — godzinowa produkcja w watogodzinach (Wh) jako obiekt klucz-wartość.',
    s3DateFmtTitle: 'Klucze dni jako daty (date=full / date=timestamp)',
    s3DateFmtDesc: 'Z parametrem date=full klucze dni w result i strings stają się datami RRRR-MM-DD, a sumy dzienne są zwracane w mapie „totals" kluczowanej tymi samymi datami. date=timestamp działa analogicznie z sekundami Unix (początek doby w strefie tz). Dotyczy też bloku „confidence" przy enriched=1. W formacie chart zmieniają się tylko sumy — wpisy godzinowe mają już pełne znaczniki czasu ts.',
    s3ChartTitle: 'Odpowiedź chart (type=chart)',
    s3ChartDesc:
      'Format zoptymalizowany pod wykresy — tablica obiektów z timestampem ISO i wartością energii.',
    s3FieldsTitle: 'Opis pól',
    thField: 'Pole',
    fDate: 'Timestamp wygenerowania odpowiedzi (ISO 8601)',
    fLatLon: 'Współrzędne użyte w zapytaniu',
    fToday: 'Godzinowa produkcja na dzisiaj (klucz = godzina, wartość = Wh)',
    fTomorrow: 'Godzinowa produkcja na jutro',
    fTotals: 'Suma produkcji na dany dzień (Wh); przy date=full/timestamp zwracane jako mapa „totals" kluczowana dniami',
    fStrings: 'Tablica z danymi per string (azymut, sprawność, moc, liczba paneli, produkcja)',
    fCorrApplied: 'Czy korekcja produkcji została zastosowana (true/false)',
    fProcTime: 'Czas przetwarzania zapytania w ms',
    fWeather:
      'Kontekst pogodowy: godzinowe zachmurzenie, temperatura powietrza/paneli, wiatr, opady, śnieg, kod WMO, UV, wschód/zachód + podsumowanie dzienne.',
    fWeatherAlerts: 'Aktywne ostrzeżenia MeteoAlarm dla lokalizacji (pusta tablica, gdy brak).',
    fProdAlerts:
      'Alerty produkcyjne z prognozy: śnieg na panelach, ciężkie zachmurzenie, silne porywy.',
    fAirQuality:
      'Jakość powietrza (PM10/PM2.5/pył/AOD) + ocena zabrudzenia paneli i rekomendacja mycia.',
    fArchive:
      'Roczny profil energetyczny lokalizacji z archiwum reanaliz: roczne nasłonecznienie, ranking miesięcy, szacowana optymalna konfiguracja (best-effort, odświeżany w tle).',
    fConfidence:
      'Przedziały produkcji P10/P50/P90 z rozrzutu modeli ensemble (tylko gdy skonfigurowano kilka modeli).',
    s4Title: 'Kody błędów',
    s4Intro:
      'W przypadku błędu API zwraca obiekt JSON z polem „error" zawierającym „code" i „message".',
    thCode: 'Kod',
    thHttp: 'HTTP',
    eValidationError: 'Brak lub nieprawidłowe parametry zapytania',
    eNoInstallation: 'Brak zapisanej instalacji i brak parametrów w URL',
    eApiKeyMissing: 'Brak klucza API w nagłówku',
    eApiKeyInvalid: 'Klucz API nie istnieje lub jest nieprawidłowy',
    eApiKeyExpired: 'Klucz API wygasł',
    eApiKeyInactive: 'Klucz API został dezaktywowany',
    ePlanLimitExceeded: 'Liczba stringów przekracza limit planu',
    eQueryParamsNotAllowed: 'Plan nie pozwala na dynamiczne parametry URL',
    eRateLimitExceeded: 'Przekroczony limit zapytań (rate limit)',
    eInternalError: 'Wewnętrzny błąd serwera',
    eServiceError: 'Błąd usługi',
    s5Title: 'Korekcja produkcji',
    s5Intro:
      'Funkcja korekcji pozwala poprawić dokładność prognoz na podstawie rzeczywistych danych produkcji. System porównuje prognozowaną produkcję z faktyczną i oblicza współczynniki korekcji per godzina (UTC). Wymaga włączonej opcji „correctionEnabled" w planie.',
    s5SendTitle: 'Wysyłanie danych rzeczywistych',
    s5SendDesc: 'Wyślij godzinową produkcję za dany dzień na endpoint:',
    s5DateD: 'date — data w formacie YYYY-MM-DD',
    s5TzD: 'tz — strefa czasowa (przesunięcie UTC) użyta przy odczycie produkcji',
    s5ProdD: 'production — obiekt: klucz to godzina (string), wartość to produkcja w Wh',
    s5EnableTitle: 'Włączanie korekcji w prognozie',
    s5EnableDesc:
      'Dodaj parametr „correction=true" do zapytania prognozy lub ustaw domyślne stosowanie korekcji w panelu klienta (zakładka Korekcja).',
    s5Note:
      'Jeśli parametr „correction" nie zostanie podany, system użyje domyślnego ustawienia Twojej instalacji.',
    s6Title: 'Przykłady',
    ex1: 'Prognoza na dzisiaj (tryb instalacji)',
    ex2: 'Prognoza na jutro (tryb instalacji)',
    ex3: 'Prognoza z pełną konfiguracją (2 stringi)',
    ex4: 'Format chart (do wykresów)',
    ex5: 'Prognoza z korekcją produkcji',
    ex6: 'Obsługa odpowiedzi (bash + jq)',
    ex7: 'Prognoza z datami zamiast today/tomorrow (date=full)',
  },
  de: {
    title: 'Hilfe — API-Dokumentation',
    subtitle:
      'Vollständige Solar Forecast API-Dokumentation: Modi, Parameter, Antwortformate und Fehlercodes.',
    toc: 'Inhaltsverzeichnis',
    example: 'Beispiel',
    exampleResponse: 'Beispielantwort',
    exampleError: 'Beispielfehler',
    s1Title: 'API-Nutzung',
    s1Intro:
      'Die Forecast API unterstützt zwei Modi. In beiden muss der API-Schlüssel in einem HTTP-Header übergeben werden (der Header-Name ist im Tarif definiert — standardmäßig X-API-Key).',
    s1Mode1Title: 'Modus 1: Anlage (vereinfacht)',
    s1Mode1Desc:
      'Alle Anlagenparameter (Standort, Strings) werden automatisch aus Ihrem Profil übernommen. Geben Sie nur den Parameter „day" an.',
    s1Mode2Title: 'Modus 2: URL-Parameter (volle Kontrolle)',
    s1Mode2Desc:
      'Sie können alle Parameter direkt im Query-String übergeben. Erfordert einen Tarif mit aktivierter Option „allowQueryParams". URL-Parameter überschreiben die der gespeicherten Anlage.',
    s1Important:
      'Wichtig: Der Parameter „day" ist in beiden Modi erforderlich. Zulässige Werte: „today" oder „tomorrow".',
    s2Title: 'Query-Parameter',
    s2Intro:
      'Nachfolgend die vollständige Liste der Query-String-Parameter für den Endpoint GET {{endpoint}}.',
    thParam: 'Parameter',
    thType: 'Typ',
    thRequired: 'Erforderlich',
    thDesc: 'Beschreibung',
    reqYes: 'ja',
    reqNo: 'nein',
    dDay: 'Prognosetag: „today" oder „tomorrow"',
    dDate: 'Format der Tagesschlüssel: „relative" (Standard), „full" (JJJJ-MM-TT) oder „timestamp" (Unix-Sekunden). Bei „full"/„timestamp" stehen die Tagessummen in einer „totals"-Map mit denselben Schlüsseln. Im Chart-Format ändern sich nur die Summen — die Stundeneinträge tragen bereits volle ts-Zeitstempel',
    dType: 'Antwortformat: „standard" (Standard) oder „chart"',
    dCorrection:
      'Produktionskorrektur: „true" oder „false". Wenn weggelassen, wird der Anlagenstandard verwendet.',
    dLat: 'Breitengrad (z. B. 52.23)',
    dLon: 'Längengrad (z. B. 21.01)',
    dTz: 'UTC-Zeitzonen-Offset (-12 bis 12, z. B. 2 für MEZ Sommer)',
    dAz: 'Azimut von String {{n}} in Grad (-180 bis 180; 0=Norden, 180=Süden)',
    dTilt: 'Neigung von String {{n}} in Grad (0 bis 90)',
    dPanels: 'Anzahl Module in String {{n}}',
    dPower: 'Modulleistung in kW in String {{n}} (z. B. 0.4 = 400W)',
    dEff: 'Modulwirkungsgrad von String {{n}} in % (z. B. 92)',
    s2Footnote:
      '* — Im URL-Parameter-Modus erforderlich. Im Anlagenmodus werden Standort- und String-Parameter automatisch übernommen. Standortparameter (lat, lon, tz) und Strings (az, t, n, s, e) erfordern die Option „allowQueryParams" im Tarif.',
    s3Title: 'Antwortformat',
    s3StdTitle: 'Standardantwort (type=standard)',
    s3StdDesc:
      'Standardformat — stündliche Produktion in Wattstunden (Wh) als Schlüssel-Wert-Objekt.',
    s3DateFmtTitle: 'Tagesschlüssel als Datum (date=full / date=timestamp)',
    s3DateFmtDesc: 'Mit date=full werden die Tagesschlüssel in result und strings zu JJJJ-MM-TT-Daten, und die Tagessummen werden in einer „totals"-Map mit denselben Daten zurückgegeben. date=timestamp funktioniert analog mit Unix-Sekunden (Tagesbeginn in der tz-Zeitzone). Gilt auch für den „confidence"-Block bei enriched=1. Im Chart-Format ändern sich nur die Summen — die Stundeneinträge tragen bereits volle ts-Zeitstempel.',
    s3ChartTitle: 'Chart-Antwort (type=chart)',
    s3ChartDesc:
      'Für Diagramme optimiertes Format — ein Array von Objekten mit ISO-Zeitstempel und Energiewert.',
    s3FieldsTitle: 'Feldbeschreibungen',
    thField: 'Feld',
    fDate: 'Zeitstempel der Antwortgenerierung (ISO 8601)',
    fLatLon: 'In der Anfrage verwendete Koordinaten',
    fToday: 'Stündliche Produktion für heute (Schlüssel = Stunde, Wert = Wh)',
    fTomorrow: 'Stündliche Produktion für morgen',
    fTotals: 'Gesamtproduktion für den jeweiligen Tag (Wh); bei date=full/timestamp als „totals"-Map mit Tagesschlüsseln zurückgegeben',
    fStrings:
      'Array mit Daten pro String (Azimut, Wirkungsgrad, Leistung, Modulanzahl, Produktion)',
    fCorrApplied: 'Ob die Produktionskorrektur angewendet wurde (true/false)',
    fProcTime: 'Verarbeitungszeit der Anfrage in ms',
    fWeather:
      'Wetterkontext: stündliche Bewölkung, Luft-/Modultemperatur, Wind, Niederschlag, Schnee, WMO-Code, UV, Sonnenauf-/-untergang + Tagesübersicht.',
    fWeatherAlerts: 'Aktive MeteoAlarm-Warnungen für den Standort (leeres Array, wenn keine).',
    fProdAlerts:
      'Aus der Vorhersage abgeleitete Produktionswarnungen: Schnee auf Modulen, starke Bewölkung, starke Böen.',
    fAirQuality:
      'Luftqualität (PM10/PM2.5/Staub/AOD) + Bewertung der Modulverschmutzung mit Reinigungsempfehlung.',
    fArchive:
      'Jährliches Standort-Energieprofil aus dem Reanalyse-Archiv: Jahreseinstrahlung, Monatsranking, geschätzte optimale Neigung/Azimut (Best-Effort, im Hintergrund aktualisiert).',
    fConfidence:
      'P10/P50/P90-Produktionsbänder aus der Streuung der Ensemble-Modelle (nur wenn mehrere Modelle konfiguriert sind).',
    s4Title: 'Fehlercodes',
    s4Intro:
      'Bei einem Fehler gibt die API ein JSON-Objekt mit einem Feld „error" zurück, das „code" und „message" enthält.',
    thCode: 'Code',
    thHttp: 'HTTP',
    eValidationError: 'Fehlende oder ungültige Query-Parameter',
    eNoInstallation: 'Keine gespeicherte Anlage und keine URL-Parameter',
    eApiKeyMissing: 'Kein API-Schlüssel im Header',
    eApiKeyInvalid: 'API-Schlüssel existiert nicht oder ist ungültig',
    eApiKeyExpired: 'API-Schlüssel ist abgelaufen',
    eApiKeyInactive: 'API-Schlüssel wurde deaktiviert',
    ePlanLimitExceeded: 'Anzahl der Strings überschreitet das Tariflimit',
    eQueryParamsNotAllowed: 'Tarif erlaubt keine dynamischen URL-Parameter',
    eRateLimitExceeded: 'Anfragelimit überschritten (Rate Limit)',
    eInternalError: 'Interner Serverfehler',
    eServiceError: 'Dienstfehler',
    s5Title: 'Produktionskorrektur',
    s5Intro:
      'Die Korrekturfunktion verbessert die Prognosegenauigkeit auf Basis tatsächlicher Produktionsdaten. Das System vergleicht die prognostizierte mit der tatsächlichen Produktion und berechnet Korrekturfaktoren pro Stunde (UTC). Erfordert die Option „correctionEnabled" im Tarif.',
    s5SendTitle: 'Übermittlung tatsächlicher Daten',
    s5SendDesc: 'Senden Sie die stündliche Produktion für einen Tag an den Endpoint:',
    s5DateD: 'date — Datum im Format YYYY-MM-DD',
    s5TzD: 'tz — Zeitzone (UTC-Offset), die beim Auslesen der Produktion verwendet wurde',
    s5ProdD:
      'production — Objekt: Schlüssel ist die Stunde (String), Wert ist die Produktion in Wh',
    s5EnableTitle: 'Korrektur in der Prognose aktivieren',
    s5EnableDesc:
      'Fügen Sie den Parameter „correction=true" zur Prognoseanfrage hinzu oder legen Sie die Standardkorrektur im Kundenportal fest (Reiter Korrektur).',
    s5Note:
      'Wenn der Parameter „correction" nicht angegeben wird, verwendet das System den Standard Ihrer Anlage.',
    s6Title: 'Beispiele',
    ex1: 'Prognose für heute (Anlagenmodus)',
    ex2: 'Prognose für morgen (Anlagenmodus)',
    ex3: 'Prognose mit vollständiger Konfiguration (2 Strings)',
    ex4: 'Chart-Format (für Diagramme)',
    ex5: 'Prognose mit Produktionskorrektur',
    ex6: 'Verarbeitung der Antwort (bash + jq)',
    ex7: 'Prognose mit Datum statt today/tomorrow (date=full)',
  },
  fr: {
    title: "Aide — documentation de l'API",
    subtitle:
      "Documentation complète de Solar Forecast API : modes, paramètres, formats de réponse et codes d'erreur.",
    toc: 'Table des matières',
    example: 'Exemple',
    exampleResponse: 'Exemple de réponse',
    exampleError: "Exemple d'erreur",
    s1Title: "Utilisation de l'API",
    s1Intro:
      "L'API Forecast prend en charge deux modes. Dans les deux cas, la clé API doit être transmise dans un en-tête HTTP (le nom de l'en-tête est défini dans le forfait — X-API-Key par défaut).",
    s1Mode1Title: 'Mode 1 : Installation (simplifié)',
    s1Mode1Desc:
      "Tous les paramètres de l'installation (emplacement, chaînes) sont repris automatiquement de votre profil. Il suffit de passer le paramètre « day ».",
    s1Mode2Title: 'Mode 2 : Paramètres URL (contrôle total)',
    s1Mode2Desc:
      "Vous pouvez passer tous les paramètres directement dans la query string. Nécessite un forfait avec l'option « allowQueryParams » activée. Les paramètres URL remplacent ceux de l'installation enregistrée.",
    s1Important:
      'Important : le paramètre « day » est requis dans les deux modes. Valeurs autorisées : « today » ou « tomorrow ».',
    s2Title: 'Paramètres de requête',
    s2Intro:
      "Voici la liste complète des paramètres de query string pour l'endpoint GET {{endpoint}}.",
    thParam: 'Paramètre',
    thType: 'Type',
    thRequired: 'Requis',
    thDesc: 'Description',
    reqYes: 'oui',
    reqNo: 'non',
    dDay: 'Jour de prévision : « today » ou « tomorrow »',
    dDate: 'Format des clés de jour : « relative » (défaut), « full » (AAAA-MM-JJ) ou « timestamp » (secondes Unix). Avec « full »/« timestamp », les totaux sont renvoyés dans une map « totals » avec les mêmes clés. Au format chart, seuls les totaux changent — les entrées horaires portent déjà des horodatages ts complets',
    dType: 'Format de réponse : « standard » (par défaut) ou « chart »',
    dCorrection:
      "Correction de production : « true » ou « false ». Si omis, la valeur par défaut de l'installation est utilisée.",
    dLat: 'Latitude (p. ex. 52.23)',
    dLon: 'Longitude (p. ex. 21.01)',
    dTz: 'Décalage de fuseau horaire UTC (-12 à 12, p. ex. 2 pour CET été)',
    dAz: 'Azimut de la chaîne {{n}} en degrés (-180 à 180 ; 0=nord, 180=sud)',
    dTilt: 'Inclinaison de la chaîne {{n}} en degrés (0 à 90)',
    dPanels: 'Nombre de panneaux dans la chaîne {{n}}',
    dPower: 'Puissance du panneau en kW dans la chaîne {{n}} (p. ex. 0.4 = 400W)',
    dEff: 'Rendement des panneaux de la chaîne {{n}} en % (p. ex. 92)',
    s2Footnote:
      "* — Requis en mode paramètres URL. En mode installation, les paramètres d'emplacement et de chaînes sont repris automatiquement. Les paramètres d'emplacement (lat, lon, tz) et les chaînes (az, t, n, s, e) nécessitent l'option « allowQueryParams » dans le forfait.",
    s3Title: 'Format de réponse',
    s3StdTitle: 'Réponse standard (type=standard)',
    s3StdDesc:
      "Format par défaut — production horaire en wattheures (Wh) sous forme d'objet clé-valeur.",
    s3DateFmtTitle: 'Clés de jour sous forme de dates (date=full / date=timestamp)',
    s3DateFmtDesc: 'Avec date=full, les clés de jour dans result et strings deviennent des dates AAAA-MM-JJ, et les totaux journaliers sont renvoyés dans une map « totals » indexée par les mêmes dates. date=timestamp fonctionne de même avec des secondes Unix (début de journée dans le fuseau tz). S applique aussi au bloc « confidence » avec enriched=1. Au format chart, seuls les totaux changent — les entrées horaires portent déjà des horodatages ts complets.',
    s3ChartTitle: 'Réponse chart (type=chart)',
    s3ChartDesc:
      "Format optimisé pour les graphiques — un tableau d'objets avec un horodatage ISO et une valeur d'énergie.",
    s3FieldsTitle: 'Description des champs',
    thField: 'Champ',
    fDate: 'Horodatage de génération de la réponse (ISO 8601)',
    fLatLon: 'Coordonnées utilisées dans la requête',
    fToday: "Production horaire pour aujourd'hui (clé = heure, valeur = Wh)",
    fTomorrow: 'Production horaire pour demain',
    fTotals: 'Production totale pour le jour donné (Wh) ; avec date=full/timestamp renvoyé sous forme de map « totals » indexée par jour',
    fStrings:
      'Tableau de données par chaîne (azimut, rendement, puissance, nombre de panneaux, production)',
    fCorrApplied: 'Si la correction de production a été appliquée (true/false)',
    fProcTime: 'Temps de traitement de la requête en ms',
    fWeather:
      'Contexte météo : couverture nuageuse horaire, température air/panneaux, vent, précipitations, neige, code WMO, UV, lever/coucher + résumé journalier.',
    fWeatherAlerts: 'Alertes météo MeteoAlarm actives pour le lieu (tableau vide si aucune).',
    fProdAlerts:
      'Alertes de production issues de la prévision : neige sur les panneaux, forte nébulosité, fortes rafales.',
    fAirQuality:
      'Qualité de l’air (PM10/PM2.5/poussière/AOD) + évaluation de l’encrassement des panneaux et recommandation de nettoyage.',
    fArchive:
      'Profil énergétique annuel du site issu de l’archive de réanalyse : irradiation annuelle, classement mensuel, inclinaison/azimut optimaux estimés (best-effort, rafraîchi en arrière-plan).',
    fConfidence:
      'Bandes de production P10/P50/P90 issues de la dispersion des modèles d’ensemble (seulement si plusieurs modèles configurés).',
    s4Title: "Codes d'erreur",
    s4Intro:
      "En cas d'erreur, l'API renvoie un objet JSON avec un champ « error » contenant « code » et « message ».",
    thCode: 'Code',
    thHttp: 'HTTP',
    eValidationError: 'Paramètres de requête manquants ou invalides',
    eNoInstallation: 'Aucune installation enregistrée et aucun paramètre URL',
    eApiKeyMissing: "Aucune clé API dans l'en-tête",
    eApiKeyInvalid: "La clé API n'existe pas ou est invalide",
    eApiKeyExpired: 'La clé API a expiré',
    eApiKeyInactive: 'La clé API a été désactivée',
    ePlanLimitExceeded: 'Le nombre de chaînes dépasse la limite du forfait',
    eQueryParamsNotAllowed: "Le forfait n'autorise pas les paramètres URL dynamiques",
    eRateLimitExceeded: 'Limite de requêtes dépassée (rate limit)',
    eInternalError: 'Erreur interne du serveur',
    eServiceError: 'Erreur de service',
    s5Title: 'Correction de production',
    s5Intro:
      "La fonction de correction améliore la précision des prévisions sur la base des données de production réelles. Le système compare la production prévue à la production réelle et calcule des facteurs de correction par heure (UTC). Nécessite l'option « correctionEnabled » dans le forfait.",
    s5SendTitle: 'Envoi des données réelles',
    s5SendDesc: "Envoyez la production horaire pour un jour donné à l'endpoint :",
    s5DateD: 'date — date au format YYYY-MM-DD',
    s5TzD: 'tz — fuseau horaire (décalage UTC) utilisé lors de la lecture de la production',
    s5ProdD: "production — objet : la clé est l'heure (string), la valeur est la production en Wh",
    s5EnableTitle: 'Activer la correction dans la prévision',
    s5EnableDesc:
      'Ajoutez le paramètre « correction=true » à la requête de prévision, ou définissez la correction par défaut dans le portail client (onglet Correction).',
    s5Note:
      "Si le paramètre « correction » n'est pas fourni, le système utilise la valeur par défaut de votre installation.",
    s6Title: 'Exemples',
    ex1: "Prévision pour aujourd'hui (mode installation)",
    ex2: 'Prévision pour demain (mode installation)',
    ex3: 'Prévision avec configuration complète (2 chaînes)',
    ex4: 'Format chart (pour les graphiques)',
    ex5: 'Prévision avec correction de production',
    ex6: 'Traitement de la réponse (bash + jq)',
    ex7: 'Prévision avec dates au lieu de today/tomorrow (date=full)',
  },
  es: {
    title: 'Ayuda — documentación de la API',
    subtitle:
      'Documentación completa de Solar Forecast API: modos, parámetros, formatos de respuesta y códigos de error.',
    toc: 'Tabla de contenidos',
    example: 'Ejemplo',
    exampleResponse: 'Ejemplo de respuesta',
    exampleError: 'Ejemplo de error',
    s1Title: 'Cómo usar la API',
    s1Intro:
      'La API Forecast admite dos modos. En ambos, la clave API debe pasarse en una cabecera HTTP (el nombre de la cabecera se define en el plan — X-API-Key por defecto).',
    s1Mode1Title: 'Modo 1: Instalación (simplificado)',
    s1Mode1Desc:
      'Todos los parámetros de la instalación (ubicación, cadenas) se toman automáticamente de su perfil. Solo hay que pasar el parámetro «day».',
    s1Mode2Title: 'Modo 2: Parámetros en la URL (control total)',
    s1Mode2Desc:
      'Puede pasar todos los parámetros directamente en la query string. Requiere un plan con la opción «allowQueryParams» habilitada. Los parámetros de la URL sobrescriben los de la instalación guardada.',
    s1Important:
      'Importante: el parámetro «day» es obligatorio en ambos modos. Valores permitidos: «today» o «tomorrow».',
    s2Title: 'Parámetros de consulta',
    s2Intro:
      'A continuación la lista completa de parámetros de query string para el endpoint GET {{endpoint}}.',
    thParam: 'Parámetro',
    thType: 'Tipo',
    thRequired: 'Obligatorio',
    thDesc: 'Descripción',
    reqYes: 'sí',
    reqNo: 'no',
    dDay: 'Día de previsión: «today» o «tomorrow»',
    dDate: 'Formato de las claves de día: «relative» (predeterminado), «full» (AAAA-MM-DD) o «timestamp» (segundos Unix). Con «full»/«timestamp», los totales se devuelven en un mapa «totals» con las mismas claves. En el formato chart solo cambian los totales — las entradas horarias ya llevan marcas de tiempo ts completas',
    dType: 'Formato de respuesta: «standard» (predeterminado) o «chart»',
    dCorrection:
      'Corrección de producción: «true» o «false». Si se omite, se usa el valor predeterminado de la instalación.',
    dLat: 'Latitud (p. ej. 52.23)',
    dLon: 'Longitud (p. ej. 21.01)',
    dTz: 'Desfase de zona horaria UTC (-12 a 12, p. ej. 2 para CET verano)',
    dAz: 'Acimut de la cadena {{n}} en grados (-180 a 180; 0=norte, 180=sur)',
    dTilt: 'Inclinación de la cadena {{n}} en grados (0 a 90)',
    dPanels: 'Número de paneles en la cadena {{n}}',
    dPower: 'Potencia del panel en kW en la cadena {{n}} (p. ej. 0.4 = 400W)',
    dEff: 'Eficiencia de los paneles de la cadena {{n}} en % (p. ej. 92)',
    s2Footnote:
      '* — Obligatorio en modo de parámetros URL. En modo instalación, los parámetros de ubicación y cadenas se toman automáticamente. Los parámetros de ubicación (lat, lon, tz) y cadenas (az, t, n, s, e) requieren la opción «allowQueryParams» en el plan.',
    s3Title: 'Formato de respuesta',
    s3StdTitle: 'Respuesta estándar (type=standard)',
    s3StdDesc:
      'Formato predeterminado — producción horaria en vatios-hora (Wh) como objeto clave-valor.',
    s3DateFmtTitle: 'Claves de día como fechas (date=full / date=timestamp)',
    s3DateFmtDesc: 'Con date=full, las claves de día en result y strings se convierten en fechas AAAA-MM-DD, y los totales diarios se devuelven en un mapa «totals» indexado por las mismas fechas. date=timestamp funciona igual con segundos Unix (inicio del día en la zona tz). También se aplica al bloque «confidence» con enriched=1. En el formato chart solo cambian los totales — las entradas horarias ya llevan marcas de tiempo ts completas.',
    s3ChartTitle: 'Respuesta chart (type=chart)',
    s3ChartDesc:
      'Formato optimizado para gráficos — un array de objetos con marca de tiempo ISO y valor de energía.',
    s3FieldsTitle: 'Descripción de los campos',
    thField: 'Campo',
    fDate: 'Marca de tiempo de generación de la respuesta (ISO 8601)',
    fLatLon: 'Coordenadas usadas en la solicitud',
    fToday: 'Producción horaria de hoy (clave = hora, valor = Wh)',
    fTomorrow: 'Producción horaria de mañana',
    fTotals: 'Producción total del día dado (Wh); con date=full/timestamp se devuelve como mapa «totals» indexado por día',
    fStrings:
      'Array con datos por cadena (acimut, eficiencia, potencia, número de paneles, producción)',
    fCorrApplied: 'Si se aplicó la corrección de producción (true/false)',
    fProcTime: 'Tiempo de procesamiento de la solicitud en ms',
    fWeather:
      'Contexto meteorológico: nubosidad horaria, temperatura aire/paneles, viento, precipitación, nieve, código WMO, UV, salida/puesta de sol + resumen diario.',
    fWeatherAlerts:
      'Avisos meteorológicos MeteoAlarm activos para la ubicación (array vacío si no hay).',
    fProdAlerts:
      'Alertas de producción derivadas del pronóstico: nieve en los paneles, nubosidad densa, ráfagas fuertes.',
    fAirQuality:
      'Calidad del aire (PM10/PM2.5/polvo/AOD) + evaluación de suciedad de los paneles y recomendación de limpieza.',
    fArchive:
      'Perfil energético anual del sitio desde el archivo de reanálisis: irradiación anual, ranking mensual, inclinación/azimut óptimos estimados (best-effort, actualizado en segundo plano).',
    fConfidence:
      'Bandas de producción P10/P50/P90 a partir de la dispersión de los modelos del ensemble (solo si hay varios modelos configurados).',
    s4Title: 'Códigos de error',
    s4Intro:
      'En caso de error, la API devuelve un objeto JSON con un campo «error» que contiene «code» y «message».',
    thCode: 'Código',
    thHttp: 'HTTP',
    eValidationError: 'Parámetros de consulta faltantes o inválidos',
    eNoInstallation: 'No hay instalación guardada ni parámetros en la URL',
    eApiKeyMissing: 'No hay clave API en la cabecera',
    eApiKeyInvalid: 'La clave API no existe o es inválida',
    eApiKeyExpired: 'La clave API ha caducado',
    eApiKeyInactive: 'La clave API ha sido desactivada',
    ePlanLimitExceeded: 'El número de cadenas supera el límite del plan',
    eQueryParamsNotAllowed: 'El plan no permite parámetros URL dinámicos',
    eRateLimitExceeded: 'Límite de solicitudes superado (rate limit)',
    eInternalError: 'Error interno del servidor',
    eServiceError: 'Error del servicio',
    s5Title: 'Corrección de producción',
    s5Intro:
      'La función de corrección mejora la precisión de las previsiones según los datos reales de producción. El sistema compara la producción prevista con la real y calcula factores de corrección por hora (UTC). Requiere la opción «correctionEnabled» en el plan.',
    s5SendTitle: 'Envío de datos reales',
    s5SendDesc: 'Envíe la producción horaria de un día al endpoint:',
    s5DateD: 'date — fecha en formato YYYY-MM-DD',
    s5TzD: 'tz — zona horaria (desfase UTC) usada al leer la producción',
    s5ProdD: 'production — objeto: la clave es la hora (string), el valor es la producción en Wh',
    s5EnableTitle: 'Activar la corrección en la previsión',
    s5EnableDesc:
      'Añada el parámetro «correction=true» a la solicitud de previsión, o establezca la corrección predeterminada en el portal del cliente (pestaña Corrección).',
    s5Note:
      'Si no se proporciona el parámetro «correction», el sistema usa el valor predeterminado de su instalación.',
    s6Title: 'Ejemplos',
    ex1: 'Previsión para hoy (modo instalación)',
    ex2: 'Previsión para mañana (modo instalación)',
    ex3: 'Previsión con configuración completa (2 cadenas)',
    ex4: 'Formato chart (para gráficos)',
    ex5: 'Previsión con corrección de producción',
    ex6: 'Manejo de la respuesta (bash + jq)',
    ex7: 'Previsión con fechas en lugar de today/tomorrow (date=full)',
  },
  it: {
    title: "Aiuto — documentazione dell'API",
    subtitle:
      'Documentazione completa di Solar Forecast API: modalità, parametri, formati di risposta e codici di errore.',
    toc: 'Indice',
    example: 'Esempio',
    exampleResponse: 'Esempio di risposta',
    exampleError: 'Esempio di errore',
    s1Title: "Come usare l'API",
    s1Intro:
      "L'API Forecast supporta due modalità. In entrambe, la chiave API deve essere passata in un'intestazione HTTP (il nome dell'intestazione è definito nel piano — X-API-Key per impostazione predefinita).",
    s1Mode1Title: 'Modalità 1: Impianto (semplificata)',
    s1Mode1Desc:
      "Tutti i parametri dell'impianto (posizione, stringhe) vengono presi automaticamente dal tuo profilo. Basta passare il parametro «day».",
    s1Mode2Title: 'Modalità 2: Parametri URL (controllo completo)',
    s1Mode2Desc:
      "Puoi passare tutti i parametri direttamente nella query string. Richiede un piano con l'opzione «allowQueryParams» abilitata. I parametri URL sovrascrivono quelli dell'impianto salvato.",
    s1Important:
      'Importante: il parametro «day» è obbligatorio in entrambe le modalità. Valori consentiti: «today» o «tomorrow».',
    s2Title: 'Parametri della richiesta',
    s2Intro:
      "Di seguito l'elenco completo dei parametri della query string per l'endpoint GET {{endpoint}}.",
    thParam: 'Parametro',
    thType: 'Tipo',
    thRequired: 'Obbligatorio',
    thDesc: 'Descrizione',
    reqYes: 'sì',
    reqNo: 'no',
    dDay: 'Giorno di previsione: «today» o «tomorrow»',
    dDate: 'Formato delle chiavi dei giorni: «relative» (predefinito), «full» (AAAA-MM-GG) o «timestamp» (secondi Unix). Con «full»/«timestamp» i totali sono restituiti nella mappa «totals» con le stesse chiavi. Nel formato chart cambiano solo i totali — le voci orarie hanno già timestamp ts completi',
    dType: 'Formato di risposta: «standard» (predefinito) o «chart»',
    dCorrection:
      "Correzione di produzione: «true» o «false». Se omesso, viene usato il valore predefinito dell'impianto.",
    dLat: 'Latitudine (es. 52.23)',
    dLon: 'Longitudine (es. 21.01)',
    dTz: 'Offset del fuso orario UTC (-12 a 12, es. 2 per CET estate)',
    dAz: 'Azimut della stringa {{n}} in gradi (-180 a 180; 0=nord, 180=sud)',
    dTilt: 'Inclinazione della stringa {{n}} in gradi (0 a 90)',
    dPanels: 'Numero di pannelli nella stringa {{n}}',
    dPower: 'Potenza del pannello in kW nella stringa {{n}} (es. 0.4 = 400W)',
    dEff: 'Efficienza dei pannelli della stringa {{n}} in % (es. 92)',
    s2Footnote:
      "* — Obbligatorio in modalità parametri URL. In modalità impianto, i parametri di posizione e stringhe vengono presi automaticamente. I parametri di posizione (lat, lon, tz) e le stringhe (az, t, n, s, e) richiedono l'opzione «allowQueryParams» nel piano.",
    s3Title: 'Formato di risposta',
    s3StdTitle: 'Risposta standard (type=standard)',
    s3StdDesc:
      'Formato predefinito — produzione oraria in wattora (Wh) come oggetto chiave-valore.',
    s3DateFmtTitle: 'Chiavi dei giorni come date (date=full / date=timestamp)',
    s3DateFmtDesc: 'Con date=full le chiavi dei giorni in result e strings diventano date AAAA-MM-GG e i totali giornalieri sono restituiti in una mappa «totals» con le stesse date. date=timestamp funziona allo stesso modo con secondi Unix (inizio giornata nel fuso tz). Vale anche per il blocco «confidence» con enriched=1. Nel formato chart cambiano solo i totali — le voci orarie hanno già timestamp ts completi.',
    s3ChartTitle: 'Risposta chart (type=chart)',
    s3ChartDesc:
      'Formato ottimizzato per i grafici — un array di oggetti con timestamp ISO e valore di energia.',
    s3FieldsTitle: 'Descrizione dei campi',
    thField: 'Campo',
    fDate: 'Timestamp di generazione della risposta (ISO 8601)',
    fLatLon: 'Coordinate usate nella richiesta',
    fToday: 'Produzione oraria di oggi (chiave = ora, valore = Wh)',
    fTomorrow: 'Produzione oraria di domani',
    fTotals: 'Produzione totale del giorno indicato (Wh); con date=full/timestamp restituito come mappa «totals» con chiavi dei giorni',
    fStrings:
      'Array con dati per stringa (azimut, efficienza, potenza, numero di pannelli, produzione)',
    fCorrApplied: 'Se la correzione di produzione è stata applicata (true/false)',
    fProcTime: 'Tempo di elaborazione della richiesta in ms',
    fWeather:
      'Contesto meteo: nuvolosità oraria, temperatura aria/pannelli, vento, precipitazioni, neve, codice WMO, UV, alba/tramonto + riepilogo giornaliero.',
    fWeatherAlerts: 'Avvisi meteo MeteoAlarm attivi per la località (array vuoto se assenti).',
    fProdAlerts:
      'Avvisi di produzione derivati dalla previsione: neve sui pannelli, forte nuvolosità, forti raffiche.',
    fAirQuality:
      'Qualità dell’aria (PM10/PM2.5/polvere/AOD) + valutazione dello sporcamento dei pannelli e raccomandazione di pulizia.',
    fArchive:
      'Profilo energetico annuo del sito dall’archivio di rianalisi: irradiazione annua, classifica mensile, inclinazione/azimut ottimali stimati (best-effort, aggiornato in background).',
    fConfidence:
      'Bande di produzione P10/P50/P90 dalla dispersione dei modelli ensemble (solo se sono configurati più modelli).',
    s4Title: 'Codici di errore',
    s4Intro:
      "In caso di errore, l'API restituisce un oggetto JSON con un campo «error» contenente «code» e «message».",
    thCode: 'Codice',
    thHttp: 'HTTP',
    eValidationError: 'Parametri della richiesta mancanti o non validi',
    eNoInstallation: 'Nessun impianto salvato e nessun parametro URL',
    eApiKeyMissing: "Nessuna chiave API nell'intestazione",
    eApiKeyInvalid: 'La chiave API non esiste o non è valida',
    eApiKeyExpired: 'La chiave API è scaduta',
    eApiKeyInactive: 'La chiave API è stata disattivata',
    ePlanLimitExceeded: 'Il numero di stringhe supera il limite del piano',
    eQueryParamsNotAllowed: 'Il piano non consente parametri URL dinamici',
    eRateLimitExceeded: 'Limite di richieste superato (rate limit)',
    eInternalError: 'Errore interno del server',
    eServiceError: 'Errore del servizio',
    s5Title: 'Correzione di produzione',
    s5Intro:
      "La funzione di correzione migliora la precisione delle previsioni in base ai dati di produzione reali. Il sistema confronta la produzione prevista con quella reale e calcola fattori di correzione per ora (UTC). Richiede l'opzione «correctionEnabled» nel piano.",
    s5SendTitle: 'Invio dei dati reali',
    s5SendDesc: "Invia la produzione oraria di un giorno all'endpoint:",
    s5DateD: 'date — data in formato YYYY-MM-DD',
    s5TzD: 'tz — fuso orario (offset UTC) usato durante la lettura della produzione',
    s5ProdD: "production — oggetto: la chiave è l'ora (string), il valore è la produzione in Wh",
    s5EnableTitle: 'Abilitare la correzione nella previsione',
    s5EnableDesc:
      'Aggiungi il parametro «correction=true» alla richiesta di previsione, o imposta la correzione predefinita nel portale cliente (scheda Correzione).',
    s5Note:
      'Se il parametro «correction» non viene fornito, il sistema usa il valore predefinito del tuo impianto.',
    s6Title: 'Esempi',
    ex1: 'Previsione per oggi (modalità impianto)',
    ex2: 'Previsione per domani (modalità impianto)',
    ex3: 'Previsione con configurazione completa (2 stringhe)',
    ex4: 'Formato chart (per i grafici)',
    ex5: 'Previsione con correzione di produzione',
    ex6: 'Gestione della risposta (bash + jq)',
    ex7: 'Previsione con date invece di today/tomorrow (date=full)',
  },
  pt: {
    title: 'Ajuda — documentação da API',
    subtitle:
      'Documentação completa da Solar Forecast API: modos, parâmetros, formatos de resposta e códigos de erro.',
    toc: 'Índice',
    example: 'Exemplo',
    exampleResponse: 'Exemplo de resposta',
    exampleError: 'Exemplo de erro',
    s1Title: 'Como usar a API',
    s1Intro:
      'A Forecast API suporta dois modos. Em ambos, a chave API deve ser passada num cabeçalho HTTP (o nome do cabeçalho é definido no plano — X-API-Key por predefinição).',
    s1Mode1Title: 'Modo 1: Instalação (simplificado)',
    s1Mode1Desc:
      'Todos os parâmetros da instalação (localização, strings) são obtidos automaticamente do seu perfil. Basta passar o parâmetro "day".',
    s1Mode2Title: 'Modo 2: Parâmetros de URL (controlo total)',
    s1Mode2Desc:
      'Pode passar todos os parâmetros diretamente na query string. Requer um plano com a opção "allowQueryParams" ativada. Os parâmetros de URL substituem os da instalação guardada.',
    s1Important:
      'Importante: o parâmetro "day" é obrigatório em ambos os modos. Valores permitidos: "today" ou "tomorrow".',
    s2Title: 'Parâmetros de query',
    s2Intro:
      'Abaixo está a lista completa de parâmetros da query string para o endpoint GET {{endpoint}}.',
    thParam: 'Parâmetro',
    thType: 'Tipo',
    thRequired: 'Obrigatório',
    thDesc: 'Descrição',
    reqYes: 'sim',
    reqNo: 'não',
    dDay: 'Dia da previsão: "today" ou "tomorrow"',
    dDate: 'Formato das chaves de dia: "relative" (padrão), "full" (AAAA-MM-DD) ou "timestamp" (segundos Unix). Com "full"/"timestamp", os totais são devolvidos num mapa "totals" com as mesmas chaves. No formato chart apenas os totais mudam — as entradas horárias já trazem timestamps ts completos',
    dType: 'Formato de resposta: "standard" (predefinido) ou "chart"',
    dCorrection:
      'Correção de produção: "true" ou "false". Se omitido, é usado o valor predefinido da instalação.',
    dLat: 'Latitude (ex. 52.23)',
    dLon: 'Longitude (ex. 21.01)',
    dTz: 'Diferença de fuso horário UTC (-12 a 12, ex. 2 para CET de verão)',
    dAz: 'Azimute da string {{n}} em graus (-180 a 180; 0=norte, 180=sul)',
    dTilt: 'Inclinação da string {{n}} em graus (0 a 90)',
    dPanels: 'Número de painéis na string {{n}}',
    dPower: 'Potência do painel em kW na string {{n}} (ex. 0.4 = 400W)',
    dEff: 'Eficiência do painel da string {{n}} em % (ex. 92)',
    s2Footnote:
      '* — Obrigatório no modo de parâmetros de URL. No modo de instalação, os parâmetros de localização e strings são obtidos automaticamente. Os parâmetros de localização (lat, lon, tz) e strings (az, t, n, s, e) requerem a opção "allowQueryParams" no plano.',
    s3Title: 'Formato de resposta',
    s3StdTitle: 'Resposta padrão (type=standard)',
    s3StdDesc: 'Formato predefinido — produção horária em watt-hora (Wh) como objeto chave-valor.',
    s3DateFmtTitle: 'Chaves de dia como datas (date=full / date=timestamp)',
    s3DateFmtDesc: 'Com date=full, as chaves de dia em result e strings tornam-se datas AAAA-MM-DD e os totais diários são devolvidos num mapa "totals" indexado pelas mesmas datas. date=timestamp funciona da mesma forma com segundos Unix (início do dia no fuso tz). Aplica-se também ao bloco "confidence" com enriched=1. No formato chart apenas os totais mudam — as entradas horárias já trazem timestamps ts completos.',
    s3ChartTitle: 'Resposta de gráfico (type=chart)',
    s3ChartDesc:
      'Formato otimizado para gráficos — um array de objetos com timestamp ISO e valor de energia.',
    s3FieldsTitle: 'Descrição dos campos',
    thField: 'Campo',
    fDate: 'Timestamp de geração da resposta (ISO 8601)',
    fLatLon: 'Coordenadas usadas no pedido',
    fToday: 'Produção horária de hoje (chave = hora, valor = Wh)',
    fTomorrow: 'Produção horária de amanhã',
    fTotals: 'Produção total para o dia indicado (Wh); com date=full/timestamp devolvido como mapa "totals" indexado por dia',
    fStrings:
      'Array de dados por string (azimute, eficiência, potência, número de painéis, produção)',
    fCorrApplied: 'Se a correção de produção foi aplicada (true/false)',
    fProcTime: 'Tempo de processamento do pedido em ms',
    fWeather:
      'Contexto meteorológico: nebulosidade horária, temperatura ar/painéis, vento, precipitação, neve, código WMO, UV, nascer/pôr do sol + resumo diário.',
    fWeatherAlerts: 'Avisos meteorológicos MeteoAlarm ativos para o local (array vazio se nenhum).',
    fProdAlerts:
      'Alertas de produção derivados da previsão: neve nos painéis, nebulosidade densa, rajadas fortes.',
    fAirQuality:
      'Qualidade do ar (PM10/PM2.5/poeira/AOD) + avaliação da sujidade dos painéis e recomendação de limpeza.',
    fArchive:
      'Perfil energético anual do local a partir do arquivo de reanálise: irradiação anual, ranking mensal, inclinação/azimute ótimos estimados (best-effort, atualizado em segundo plano).',
    fConfidence:
      'Bandas de produção P10/P50/P90 a partir da dispersão dos modelos do ensemble (apenas quando vários modelos configurados).',
    s4Title: 'Códigos de erro',
    s4Intro:
      'Em caso de erro, a API devolve um objeto JSON com um campo "error" contendo "code" e "message".',
    thCode: 'Código',
    thHttp: 'HTTP',
    eValidationError: 'Parâmetros de query em falta ou inválidos',
    eNoInstallation: 'Sem instalação guardada e sem parâmetros de URL',
    eApiKeyMissing: 'Sem chave API no cabeçalho',
    eApiKeyInvalid: 'A chave API não existe ou é inválida',
    eApiKeyExpired: 'A chave API expirou',
    eApiKeyInactive: 'A chave API foi desativada',
    ePlanLimitExceeded: 'O número de strings excede o limite do plano',
    eQueryParamsNotAllowed: 'O plano não permite parâmetros de URL dinâmicos',
    eRateLimitExceeded: 'Limite de pedidos excedido (rate limit)',
    eInternalError: 'Erro interno do servidor',
    eServiceError: 'Erro de serviço',
    s5Title: 'Correção de produção',
    s5Intro:
      'A funcionalidade de correção melhora a precisão da previsão com base em dados de produção reais. O sistema compara a produção prevista com a real e calcula fatores de correção por hora (UTC). Requer a opção "correctionEnabled" no plano.',
    s5SendTitle: 'Enviar dados reais',
    s5SendDesc: 'Envie a produção horária de um dado dia para o endpoint:',
    s5DateD: 'date — data no formato YYYY-MM-DD',
    s5TzD: 'tz — fuso horário (diferença UTC) usado ao ler a produção',
    s5ProdD: 'production — objeto: a chave é a hora (string), o valor é a produção em Wh',
    s5EnableTitle: 'Ativar a correção na previsão',
    s5EnableDesc:
      'Adicione o parâmetro "correction=true" ao pedido de previsão, ou defina a correção predefinida no portal do cliente (separador Correção).',
    s5Note:
      'Se o parâmetro "correction" não for fornecido, o sistema usa o valor predefinido da sua instalação.',
    s6Title: 'Exemplos',
    ex1: 'Previsão para hoje (modo de instalação)',
    ex2: 'Previsão para amanhã (modo de instalação)',
    ex3: 'Previsão com configuração completa (2 strings)',
    ex4: 'Formato de gráfico (para gráficos)',
    ex5: 'Previsão com correção de produção',
    ex6: 'Tratamento da resposta (bash + jq)',
    ex7: 'Previsão com datas em vez de today/tomorrow (date=full)',
  },
  nl: {
    title: 'Help — API-documentatie',
    subtitle:
      'Volledige Solar Forecast API-documentatie: modi, parameters, antwoordformaten en foutcodes.',
    toc: 'Inhoudsopgave',
    example: 'Voorbeeld',
    exampleResponse: 'Voorbeeldantwoord',
    exampleError: 'Voorbeeldfout',
    s1Title: 'De API gebruiken',
    s1Intro:
      'De Forecast API ondersteunt twee modi. In beide moet de API-sleutel in een HTTP-header worden meegegeven (de headernaam is gedefinieerd in het plan — standaard X-API-Key).',
    s1Mode1Title: 'Modus 1: Installatie (vereenvoudigd)',
    s1Mode1Desc:
      'Alle installatieparameters (locatie, strings) worden automatisch uit je profiel gehaald. Geef alleen de parameter "day" mee.',
    s1Mode2Title: 'Modus 2: URL-parameters (volledige controle)',
    s1Mode2Desc:
      'Je kunt alle parameters direct in de querystring meegeven. Vereist een plan met de optie "allowQueryParams" ingeschakeld. URL-parameters overschrijven die van de opgeslagen installatie.',
    s1Important:
      'Belangrijk: de parameter "day" is verplicht in beide modi. Toegestane waarden: "today" of "tomorrow".',
    s2Title: 'Queryparameters',
    s2Intro:
      'Hieronder staat de volledige lijst met querystringparameters voor het GET {{endpoint}}-endpoint.',
    thParam: 'Parameter',
    thType: 'Type',
    thRequired: 'Verplicht',
    thDesc: 'Beschrijving',
    reqYes: 'ja',
    reqNo: 'nee',
    dDay: 'Voorspellingsdag: "today" of "tomorrow"',
    dDate: 'Formaat van dagsleutels: "relative" (standaard), "full" (JJJJ-MM-DD) of "timestamp" (Unix-seconden). Bij "full"/"timestamp" staan de dagtotalen in een "totals"-map met dezelfde sleutels. In het chart-formaat veranderen alleen de totalen — uurvermeldingen hebben al volledige ts-tijdstempels',
    dType: 'Antwoordformaat: "standard" (standaard) of "chart"',
    dCorrection:
      'Productiecorrectie: "true" of "false". Indien weggelaten wordt de standaardwaarde van de installatie gebruikt.',
    dLat: 'Breedtegraad (bijv. 52.23)',
    dLon: 'Lengtegraad (bijv. 21.01)',
    dTz: 'UTC-tijdzoneverschuiving (-12 tot 12, bijv. 2 voor CET-zomertijd)',
    dAz: 'Azimut van string {{n}} in graden (-180 tot 180; 0=noord, 180=zuid)',
    dTilt: 'Helling van string {{n}} in graden (0 tot 90)',
    dPanels: 'Aantal panelen in string {{n}}',
    dPower: 'Paneelvermogen in kW in string {{n}} (bijv. 0.4 = 400W)',
    dEff: 'Paneelrendement van string {{n}} in % (bijv. 92)',
    s2Footnote:
      '* — Verplicht in URL-parametermodus. In installatiemodus worden locatie- en stringparameters automatisch overgenomen. Locatieparameters (lat, lon, tz) en strings (az, t, n, s, e) vereisen de optie "allowQueryParams" in het plan.',
    s3Title: 'Antwoordformaat',
    s3StdTitle: 'Standaardantwoord (type=standard)',
    s3StdDesc: 'Standaardformaat — uurlijkse productie in wattuur (Wh) als sleutel-waardeobject.',
    s3DateFmtTitle: 'Dagsleutels als datums (date=full / date=timestamp)',
    s3DateFmtDesc: 'Met date=full worden de dagsleutels in result en strings JJJJ-MM-DD-datums, en dagtotalen staan in een "totals"-map met dezelfde datums. date=timestamp werkt hetzelfde met Unix-seconden (begin van de dag in de tz-tijdzone). Geldt ook voor het "confidence"-blok bij enriched=1. In het chart-formaat veranderen alleen de totalen — uurvermeldingen hebben al volledige ts-tijdstempels.',
    s3ChartTitle: 'Grafiekantwoord (type=chart)',
    s3ChartDesc:
      'Formaat geoptimaliseerd voor grafieken — een array van objecten met een ISO-tijdstempel en een energiewaarde.',
    s3FieldsTitle: 'Veldbeschrijvingen',
    thField: 'Veld',
    fDate: 'Tijdstempel van antwoordgeneratie (ISO 8601)',
    fLatLon: 'Coördinaten gebruikt in het verzoek',
    fToday: 'Uurlijkse productie voor vandaag (sleutel = uur, waarde = Wh)',
    fTomorrow: 'Uurlijkse productie voor morgen',
    fTotals: 'Totale productie voor de opgegeven dag (Wh); bij date=full/timestamp teruggegeven als "totals"-map met dagsleutels',
    fStrings:
      'Array met gegevens per string (azimut, rendement, vermogen, aantal panelen, productie)',
    fCorrApplied: 'Of productiecorrectie is toegepast (true/false)',
    fProcTime: 'Verwerkingstijd van het verzoek in ms',
    fWeather:
      'Weercontext: uurlijkse bewolking, lucht-/paneeltemperatuur, wind, neerslag, sneeuw, WMO-code, UV, zonsop-/ondergang + dagsamenvatting.',
    fWeatherAlerts: 'Actieve MeteoAlarm-waarschuwingen voor de locatie (lege array indien geen).',
    fProdAlerts:
      'Uit de voorspelling afgeleide productiewaarschuwingen: sneeuw op panelen, dichte bewolking, sterke windstoten.',
    fAirQuality:
      'Luchtkwaliteit (PM10/PM2.5/stof/AOD) + beoordeling van paneelvervuiling met reinigingsadvies.',
    fArchive:
      'Jaarlijks energieprofiel van de locatie uit het heranalysearchief: jaarinstraling, maandranglijst, geschatte optimale helling/azimut (best-effort, op de achtergrond ververst).',
    fConfidence:
      'P10/P50/P90-productiebanden uit de spreiding van de ensemblemodellen (alleen als meerdere modellen geconfigureerd zijn).',
    s4Title: 'Foutcodes',
    s4Intro:
      'Bij een fout retourneert de API een JSON-object met een veld "error" dat "code" en "message" bevat.',
    thCode: 'Code',
    thHttp: 'HTTP',
    eValidationError: 'Ontbrekende of ongeldige queryparameters',
    eNoInstallation: 'Geen opgeslagen installatie en geen URL-parameters',
    eApiKeyMissing: 'Geen API-sleutel in de header',
    eApiKeyInvalid: 'API-sleutel bestaat niet of is ongeldig',
    eApiKeyExpired: 'API-sleutel is verlopen',
    eApiKeyInactive: 'API-sleutel is gedeactiveerd',
    ePlanLimitExceeded: 'Aantal strings overschrijdt de planlimiet',
    eQueryParamsNotAllowed: 'Plan staat geen dynamische URL-parameters toe',
    eRateLimitExceeded: 'Verzoeklimiet overschreden (rate limit)',
    eInternalError: 'Interne serverfout',
    eServiceError: 'Servicefout',
    s5Title: 'Productiecorrectie',
    s5Intro:
      'De correctiefunctie verbetert de nauwkeurigheid van de voorspelling op basis van werkelijke productiegegevens. Het systeem vergelijkt de voorspelde productie met de werkelijke en berekent correctiefactoren per uur (UTC). Vereist de optie "correctionEnabled" in het plan.',
    s5SendTitle: 'Werkelijke gegevens indienen',
    s5SendDesc: 'Stuur de uurlijkse productie voor een bepaalde dag naar het endpoint:',
    s5DateD: 'date — datum in YYYY-MM-DD-formaat',
    s5TzD: 'tz — tijdzone (UTC-verschuiving) gebruikt bij het lezen van de productie',
    s5ProdD: 'production — object: de sleutel is het uur (string), de waarde is de productie in Wh',
    s5EnableTitle: 'Correctie inschakelen in de voorspelling',
    s5EnableDesc:
      'Voeg de parameter "correction=true" toe aan het voorspellingsverzoek, of stel de standaardcorrectie in in het klantportaal (tabblad Correctie).',
    s5Note:
      'Als de parameter "correction" niet wordt opgegeven, gebruikt het systeem de standaardwaarde van je installatie.',
    s6Title: 'Voorbeelden',
    ex1: 'Voorspelling voor vandaag (installatiemodus)',
    ex2: 'Voorspelling voor morgen (installatiemodus)',
    ex3: 'Voorspelling met volledige configuratie (2 strings)',
    ex4: 'Grafiekformaat (voor grafieken)',
    ex5: 'Voorspelling met productiecorrectie',
    ex6: 'Het antwoord verwerken (bash + jq)',
    ex7: 'Voorspelling met datums in plaats van today/tomorrow (date=full)',
  },
  da: {
    title: 'Hjælp — API-dokumentation',
    subtitle:
      'Komplet Solar Forecast API-dokumentation: tilstande, parametre, svarformater og fejlkoder.',
    toc: 'Indholdsfortegnelse',
    example: 'Eksempel',
    exampleResponse: 'Eksempelsvar',
    exampleError: 'Eksempelfejl',
    s1Title: "Sådan bruges API'et",
    s1Intro:
      'Forecast API understøtter to tilstande. I begge skal API-nøglen sendes i en HTTP-header (headernavnet defineres i planen — X-API-Key som standard).',
    s1Mode1Title: 'Tilstand 1: Anlæg (forenklet)',
    s1Mode1Desc:
      'Alle anlægsparametre (placering, strings) hentes automatisk fra din profil. Send blot parameteren "day".',
    s1Mode2Title: 'Tilstand 2: URL-parametre (fuld kontrol)',
    s1Mode2Desc:
      'Du kan sende alle parametre direkte i query-strengen. Kræver en plan med "allowQueryParams"-indstillingen aktiveret. URL-parametre tilsidesætter dem fra det gemte anlæg.',
    s1Important:
      'Vigtigt: parameteren "day" er påkrævet i begge tilstande. Tilladte værdier: "today" eller "tomorrow".',
    s2Title: 'Query-parametre',
    s2Intro:
      'Nedenfor er den fulde liste over query-streng-parametre for GET {{endpoint}}-endpointet.',
    thParam: 'Parameter',
    thType: 'Type',
    thRequired: 'Påkrævet',
    thDesc: 'Beskrivelse',
    reqYes: 'ja',
    reqNo: 'nej',
    dDay: 'Prognosedag: "today" eller "tomorrow"',
    dDate: 'Format for dagsnøgler: "relative" (standard), "full" (ÅÅÅÅ-MM-DD) eller "timestamp" (Unix-sekunder). Med "full"/"timestamp" returneres dagstotaler i et "totals"-map med samme nøgler. I chart-formatet ændres kun totalerne — timeposterne bærer allerede fulde ts-tidsstempler',
    dType: 'Svarformat: "standard" (standard) eller "chart"',
    dCorrection:
      'Produktionskorrektion: "true" eller "false". Hvis udeladt bruges anlæggets standardværdi.',
    dLat: 'Breddegrad (f.eks. 52.23)',
    dLon: 'Længdegrad (f.eks. 21.01)',
    dTz: 'UTC-tidszoneforskydning (-12 til 12, f.eks. 2 for CET sommertid)',
    dAz: 'Azimut for string {{n}} i grader (-180 til 180; 0=nord, 180=syd)',
    dTilt: 'Hældning for string {{n}} i grader (0 til 90)',
    dPanels: 'Antal paneler i string {{n}}',
    dPower: 'Paneleffekt i kW i string {{n}} (f.eks. 0.4 = 400W)',
    dEff: 'Panelvirkningsgrad for string {{n}} i % (f.eks. 92)',
    s2Footnote:
      '* — Påkrævet i URL-parametertilstand. I anlægstilstand hentes placerings- og string-parametre automatisk. Placeringsparametre (lat, lon, tz) og strings (az, t, n, s, e) kræver "allowQueryParams"-indstillingen i planen.',
    s3Title: 'Svarformat',
    s3StdTitle: 'Standardsvar (type=standard)',
    s3StdDesc: 'Standardformat — timeproduktion i watt-timer (Wh) som et nøgle-værdi-objekt.',
    s3DateFmtTitle: 'Dagsnøgler som datoer (date=full / date=timestamp)',
    s3DateFmtDesc: 'Med date=full bliver dagsnøglerne i result og strings til ÅÅÅÅ-MM-DD-datoer, og dagstotaler returneres i et "totals"-map med samme datoer. date=timestamp fungerer tilsvarende med Unix-sekunder (dagens start i tz-tidszonen). Gælder også "confidence"-blokken med enriched=1. I chart-formatet ændres kun totalerne — timeposterne bærer allerede fulde ts-tidsstempler.',
    s3ChartTitle: 'Diagramsvar (type=chart)',
    s3ChartDesc:
      'Format optimeret til diagrammer — et array af objekter med et ISO-tidsstempel og en energiværdi.',
    s3FieldsTitle: 'Feltbeskrivelser',
    thField: 'Felt',
    fDate: 'Tidsstempel for svargenerering (ISO 8601)',
    fLatLon: 'Koordinater brugt i anmodningen',
    fToday: 'Timeproduktion for i dag (nøgle = time, værdi = Wh)',
    fTomorrow: 'Timeproduktion for i morgen',
    fTotals: 'Samlet produktion for den angivne dag (Wh); med date=full/timestamp returneret som "totals"-map med dagsnøgler',
    fStrings:
      'Array med data pr. string (azimut, virkningsgrad, effekt, antal paneler, produktion)',
    fCorrApplied: 'Om produktionskorrektion blev anvendt (true/false)',
    fProcTime: 'Behandlingstid for anmodningen i ms',
    fWeather:
      'Vejrkontekst: timebaseret skydække, luft-/paneltemperatur, vind, nedbør, sne, WMO-kode, UV, solop-/nedgang + dagsoversigt.',
    fWeatherAlerts: 'Aktive MeteoAlarm-advarsler for lokationen (tomt array hvis ingen).',
    fProdAlerts:
      'Produktionsadvarsler udledt af prognosen: sne på paneler, tæt skydække, kraftige vindstød.',
    fAirQuality:
      'Luftkvalitet (PM10/PM2.5/støv/AOD) + vurdering af paneltilsmudsning med rengøringsanbefaling.',
    fArchive:
      'Årlig energiprofil for lokationen fra reanalysearkivet: årlig indstråling, månedsrangering, anslået optimal hældning/azimut (best-effort, opdateres i baggrunden).',
    fConfidence:
      'P10/P50/P90-produktionsbånd ud fra spredningen i ensemble-modellerne (kun når flere modeller er konfigureret).',
    s4Title: 'Fejlkoder',
    s4Intro:
      'Ved fejl returnerer API\'et et JSON-objekt med et "error"-felt, der indeholder "code" og "message".',
    thCode: 'Kode',
    thHttp: 'HTTP',
    eValidationError: 'Manglende eller ugyldige query-parametre',
    eNoInstallation: 'Intet gemt anlæg og ingen URL-parametre',
    eApiKeyMissing: 'Ingen API-nøgle i headeren',
    eApiKeyInvalid: 'API-nøglen findes ikke eller er ugyldig',
    eApiKeyExpired: 'API-nøglen er udløbet',
    eApiKeyInactive: 'API-nøglen er deaktiveret',
    ePlanLimitExceeded: 'Antallet af strings overstiger planens grænse',
    eQueryParamsNotAllowed: 'Planen tillader ikke dynamiske URL-parametre',
    eRateLimitExceeded: 'Anmodningsgrænse overskredet (rate limit)',
    eInternalError: 'Intern serverfejl',
    eServiceError: 'Servicefejl',
    s5Title: 'Produktionskorrektion',
    s5Intro:
      'Korrektionsfunktionen forbedrer prognosens nøjagtighed baseret på faktiske produktionsdata. Systemet sammenligner den forventede produktion med den faktiske og beregner korrektionsfaktorer pr. time (UTC). Kræver "correctionEnabled"-indstillingen i planen.',
    s5SendTitle: 'Indsendelse af faktiske data',
    s5SendDesc: 'Send timeproduktionen for en given dag til endpointet:',
    s5DateD: 'date — dato i formatet YYYY-MM-DD',
    s5TzD: 'tz — tidszone (UTC-forskydning) brugt ved læsning af produktion',
    s5ProdD: 'production — objekt: nøglen er timen (streng), værdien er produktionen i Wh',
    s5EnableTitle: 'Aktivering af korrektion i prognosen',
    s5EnableDesc:
      'Tilføj parameteren "correction=true" til prognoseanmodningen, eller indstil standardkorrektionen i kundeportalen (fanen Korrektion).',
    s5Note: 'Hvis parameteren "correction" ikke angives, bruger systemet dit anlægs standardværdi.',
    s6Title: 'Eksempler',
    ex1: 'Prognose for i dag (anlægstilstand)',
    ex2: 'Prognose for i morgen (anlægstilstand)',
    ex3: 'Prognose med fuld konfiguration (2 strings)',
    ex4: 'Diagramformat (til diagrammer)',
    ex5: 'Prognose med produktionskorrektion',
    ex6: 'Håndtering af svaret (bash + jq)',
    ex7: 'Prognose med datoer i stedet for today/tomorrow (date=full)',
  },
  sv: {
    title: 'Hjälp — API-dokumentation',
    subtitle:
      'Komplett Solar Forecast API-dokumentation: lägen, parametrar, svarsformat och felkoder.',
    toc: 'Innehållsförteckning',
    example: 'Exempel',
    exampleResponse: 'Exempelsvar',
    exampleError: 'Exempelfel',
    s1Title: 'Så använder du API:et',
    s1Intro:
      'Forecast API stöder två lägen. I båda måste API-nyckeln skickas i en HTTP-header (headernamnet definieras i planen — X-API-Key som standard).',
    s1Mode1Title: 'Läge 1: Anläggning (förenklat)',
    s1Mode1Desc:
      'Alla anläggningsparametrar (plats, strings) hämtas automatiskt från din profil. Skicka bara parametern "day".',
    s1Mode2Title: 'Läge 2: URL-parametrar (full kontroll)',
    s1Mode2Desc:
      'Du kan skicka alla parametrar direkt i query-strängen. Kräver en plan med alternativet "allowQueryParams" aktiverat. URL-parametrar åsidosätter de från den sparade anläggningen.',
    s1Important:
      'Viktigt: parametern "day" är obligatorisk i båda lägena. Tillåtna värden: "today" eller "tomorrow".',
    s2Title: 'Query-parametrar',
    s2Intro: 'Nedan finns hela listan med query-strängparametrar för GET {{endpoint}}-endpointen.',
    thParam: 'Parameter',
    thType: 'Typ',
    thRequired: 'Obligatorisk',
    thDesc: 'Beskrivning',
    reqYes: 'ja',
    reqNo: 'nej',
    dDay: 'Prognosdag: "today" eller "tomorrow"',
    dDate: 'Format för dagnycklar: "relative" (standard), "full" (ÅÅÅÅ-MM-DD) eller "timestamp" (Unix-sekunder). Med "full"/"timestamp" returneras dagssummor i en "totals"-karta med samma nycklar. I chart-formatet ändras bara summorna — timposterna bär redan fullständiga ts-tidsstämplar',
    dType: 'Svarsformat: "standard" (standard) eller "chart"',
    dCorrection:
      'Produktionskorrigering: "true" eller "false". Om utelämnad används anläggningens standardvärde.',
    dLat: 'Latitud (t.ex. 52.23)',
    dLon: 'Longitud (t.ex. 21.01)',
    dTz: 'UTC-tidszonsförskjutning (-12 till 12, t.ex. 2 för CET sommartid)',
    dAz: 'Azimut för string {{n}} i grader (-180 till 180; 0=norr, 180=söder)',
    dTilt: 'Lutning för string {{n}} i grader (0 till 90)',
    dPanels: 'Antal paneler i string {{n}}',
    dPower: 'Paneleffekt i kW i string {{n}} (t.ex. 0.4 = 400W)',
    dEff: 'Panelverkningsgrad för string {{n}} i % (t.ex. 92)',
    s2Footnote:
      '* — Obligatorisk i URL-parameterläge. I anläggningsläge hämtas plats- och string-parametrar automatiskt. Platsparametrar (lat, lon, tz) och strings (az, t, n, s, e) kräver alternativet "allowQueryParams" i planen.',
    s3Title: 'Svarsformat',
    s3StdTitle: 'Standardsvar (type=standard)',
    s3StdDesc: 'Standardformat — timproduktion i wattimmar (Wh) som ett nyckel-värde-objekt.',
    s3DateFmtTitle: 'Dagnycklar som datum (date=full / date=timestamp)',
    s3DateFmtDesc: 'Med date=full blir dagnycklarna i result och strings ÅÅÅÅ-MM-DD-datum, och dagssummor returneras i en "totals"-karta med samma datum. date=timestamp fungerar likadant med Unix-sekunder (dagens början i tz-tidszonen). Gäller även "confidence"-blocket med enriched=1. I chart-formatet ändras bara summorna — timposterna bär redan fullständiga ts-tidsstämplar.',
    s3ChartTitle: 'Diagramsvar (type=chart)',
    s3ChartDesc:
      'Format optimerat för diagram — en array av objekt med en ISO-tidsstämpel och ett energivärde.',
    s3FieldsTitle: 'Fältbeskrivningar',
    thField: 'Fält',
    fDate: 'Tidsstämpel för svarsgenerering (ISO 8601)',
    fLatLon: 'Koordinater som användes i begäran',
    fToday: 'Timproduktion för idag (nyckel = timme, värde = Wh)',
    fTomorrow: 'Timproduktion för imorgon',
    fTotals: 'Total produktion för den angivna dagen (Wh); med date=full/timestamp returneras som "totals"-karta med dagnycklar',
    fStrings:
      'Array med data per string (azimut, verkningsgrad, effekt, antal paneler, produktion)',
    fCorrApplied: 'Om produktionskorrigering tillämpades (true/false)',
    fProcTime: 'Behandlingstid för begäran i ms',
    fWeather:
      'Väderkontext: timvis molntäcke, luft-/paneltemperatur, vind, nederbörd, snö, WMO-kod, UV, soluppgång/-nedgång + dagssammanfattning.',
    fWeatherAlerts: 'Aktiva MeteoAlarm-varningar för platsen (tom array om inga).',
    fProdAlerts:
      'Produktionsvarningar härledda från prognosen: snö på paneler, tätt molntäcke, kraftiga vindbyar.',
    fAirQuality:
      'Luftkvalitet (PM10/PM2.5/damm/AOD) + bedömning av panelnedsmutsning med rengöringsrekommendation.',
    fArchive:
      'Årlig energiprofil för platsen från reanalysarkivet: årlig instrålning, månadsrankning, uppskattad optimal lutning/azimut (best-effort, uppdateras i bakgrunden).',
    fConfidence:
      'P10/P50/P90-produktionsband från spridningen i ensemblemodellerna (endast när flera modeller är konfigurerade).',
    s4Title: 'Felkoder',
    s4Intro:
      'Vid fel returnerar API:et ett JSON-objekt med ett "error"-fält som innehåller "code" och "message".',
    thCode: 'Kod',
    thHttp: 'HTTP',
    eValidationError: 'Saknade eller ogiltiga query-parametrar',
    eNoInstallation: 'Ingen sparad anläggning och inga URL-parametrar',
    eApiKeyMissing: 'Ingen API-nyckel i headern',
    eApiKeyInvalid: 'API-nyckeln finns inte eller är ogiltig',
    eApiKeyExpired: 'API-nyckeln har gått ut',
    eApiKeyInactive: 'API-nyckeln har inaktiverats',
    ePlanLimitExceeded: 'Antalet strings överstiger planens gräns',
    eQueryParamsNotAllowed: 'Planen tillåter inte dynamiska URL-parametrar',
    eRateLimitExceeded: 'Begäransgräns överskriden (rate limit)',
    eInternalError: 'Internt serverfel',
    eServiceError: 'Tjänstfel',
    s5Title: 'Produktionskorrigering',
    s5Intro:
      'Korrigeringsfunktionen förbättrar prognosens noggrannhet baserat på faktiska produktionsdata. Systemet jämför den prognostiserade produktionen med den faktiska och beräknar korrigeringsfaktorer per timme (UTC). Kräver alternativet "correctionEnabled" i planen.',
    s5SendTitle: 'Skicka in faktiska data',
    s5SendDesc: 'Skicka timproduktionen för en viss dag till endpointen:',
    s5DateD: 'date — datum i formatet YYYY-MM-DD',
    s5TzD: 'tz — tidszon (UTC-förskjutning) som används vid läsning av produktion',
    s5ProdD: 'production — objekt: nyckeln är timmen (sträng), värdet är produktionen i Wh',
    s5EnableTitle: 'Aktivera korrigering i prognosen',
    s5EnableDesc:
      'Lägg till parametern "correction=true" i prognosbegäran, eller ställ in standardkorrigeringen i kundportalen (fliken Korrigering).',
    s5Note:
      'Om parametern "correction" inte anges använder systemet din anläggnings standardvärde.',
    s6Title: 'Exempel',
    ex1: 'Prognos för idag (anläggningsläge)',
    ex2: 'Prognos för imorgon (anläggningsläge)',
    ex3: 'Prognos med fullständig konfiguration (2 strings)',
    ex4: 'Diagramformat (för diagram)',
    ex5: 'Prognos med produktionskorrigering',
    ex6: 'Hantera svaret (bash + jq)',
    ex7: 'Prognos med datum i stället för today/tomorrow (date=full)',
  },
  fi: {
    title: 'Ohje — API-dokumentaatio',
    subtitle:
      'Täydellinen Solar Forecast API -dokumentaatio: tilat, parametrit, vastausmuodot ja virhekoodit.',
    toc: 'Sisällysluettelo',
    example: 'Esimerkki',
    exampleResponse: 'Esimerkkivastaus',
    exampleError: 'Esimerkkivirhe',
    s1Title: 'API:n käyttö',
    s1Intro:
      'Forecast API tukee kahta tilaa. Molemmissa API-avain on välitettävä HTTP-otsikossa (otsikon nimi määritetään suunnitelmassa — oletuksena X-API-Key).',
    s1Mode1Title: 'Tila 1: Laitos (yksinkertaistettu)',
    s1Mode1Desc:
      'Kaikki laitoksen parametrit (sijainti, stringit) haetaan automaattisesti profiilistasi. Välitä vain "day"-parametri.',
    s1Mode2Title: 'Tila 2: URL-parametrit (täysi hallinta)',
    s1Mode2Desc:
      'Voit välittää kaikki parametrit suoraan kyselymerkkijonossa. Vaatii suunnitelman, jossa "allowQueryParams"-asetus on käytössä. URL-parametrit ohittavat tallennetun laitoksen parametrit.',
    s1Important:
      'Tärkeää: "day"-parametri on pakollinen molemmissa tiloissa. Sallitut arvot: "today" tai "tomorrow".',
    s2Title: 'Kyselyparametrit',
    s2Intro:
      'Alla on täydellinen luettelo kyselymerkkijonon parametreista GET {{endpoint}} -päätepisteelle.',
    thParam: 'Parametri',
    thType: 'Tyyppi',
    thRequired: 'Pakollinen',
    thDesc: 'Kuvaus',
    reqYes: 'kyllä',
    reqNo: 'ei',
    dDay: 'Ennustepäivä: "today" tai "tomorrow"',
    dDate: 'Päiväavainten muoto: "relative" (oletus), "full" (VVVV-KK-PP) tai "timestamp" (Unix-sekunnit). Muodoilla "full"/"timestamp" päiväsummat palautetaan "totals"-kartassa samoilla avaimilla. Chart-muodossa vain summat muuttuvat — tuntimerkinnöissä on jo täydet ts-aikaleimat',
    dType: 'Vastausmuoto: "standard" (oletus) tai "chart"',
    dCorrection:
      'Tuotannon korjaus: "true" tai "false". Jos jätetään pois, käytetään laitoksen oletusarvoa.',
    dLat: 'Leveysaste (esim. 52.23)',
    dLon: 'Pituusaste (esim. 21.01)',
    dTz: 'UTC-aikavyöhykkeen poikkeama (-12–12, esim. 2 CET-kesäaika)',
    dAz: 'Stringin {{n}} atsimuutti asteina (-180–180; 0=pohjoinen, 180=etelä)',
    dTilt: 'Stringin {{n}} kallistus asteina (0–90)',
    dPanels: 'Paneelien määrä stringissä {{n}}',
    dPower: 'Paneelin teho kW stringissä {{n}} (esim. 0.4 = 400W)',
    dEff: 'Stringin {{n}} paneelin hyötysuhde % (esim. 92)',
    s2Footnote:
      '* — Pakollinen URL-parametritilassa. Laitostilassa sijainti- ja string-parametrit haetaan automaattisesti. Sijaintiparametrit (lat, lon, tz) ja stringit (az, t, n, s, e) vaativat "allowQueryParams"-asetuksen suunnitelmassa.',
    s3Title: 'Vastausmuoto',
    s3StdTitle: 'Vakiovastaus (type=standard)',
    s3StdDesc: 'Oletusmuoto — tuntikohtainen tuotanto wattitunteina (Wh) avain-arvo-objektina.',
    s3DateFmtTitle: 'Päiväavaimet päivämäärinä (date=full / date=timestamp)',
    s3DateFmtDesc: 'Muodolla date=full result- ja strings-osien päiväavaimet muuttuvat VVVV-KK-PP-päivämääriksi ja päiväsummat palautetaan "totals"-kartassa samoilla päivämäärillä. date=timestamp toimii vastaavasti Unix-sekunneilla (vuorokauden alku tz-vyöhykkeellä). Koskee myös "confidence"-lohkoa, kun enriched=1. Chart-muodossa vain summat muuttuvat — tuntimerkinnöissä on jo täydet ts-aikaleimat.',
    s3ChartTitle: 'Kaaviovastaus (type=chart)',
    s3ChartDesc:
      'Kaavioille optimoitu muoto — taulukko objekteja, joissa on ISO-aikaleima ja energia-arvo.',
    s3FieldsTitle: 'Kenttien kuvaukset',
    thField: 'Kenttä',
    fDate: 'Vastauksen luonnin aikaleima (ISO 8601)',
    fLatLon: 'Pyynnössä käytetyt koordinaatit',
    fToday: 'Tämän päivän tuntikohtainen tuotanto (avain = tunti, arvo = Wh)',
    fTomorrow: 'Huomisen tuntikohtainen tuotanto',
    fTotals: 'Kokonaistuotanto annetulle päivälle (Wh); date=full/timestamp palauttaa "totals"-kartan päiväavaimilla',
    fStrings:
      'Taulukko string-kohtaisia tietoja (atsimuutti, hyötysuhde, teho, paneelien määrä, tuotanto)',
    fCorrApplied: 'Sovellettiinko tuotannon korjausta (true/false)',
    fProcTime: 'Pyynnön käsittelyaika ms',
    fWeather:
      'Sääkonteksti: tunneittainen pilvisyys, ilman/paneelin lämpötila, tuuli, sade, lumi, WMO-koodi, UV, auringonnousu/-lasku + päiväyhteenveto.',
    fWeatherAlerts: 'Sijainnin aktiiviset MeteoAlarm-varoitukset (tyhjä taulukko, jos ei ole).',
    fProdAlerts:
      'Ennusteesta johdetut tuotantohälytykset: lumi paneeleilla, voimakas pilvisyys, kovat puuskat.',
    fAirQuality:
      'Ilmanlaatu (PM10/PM2.5/pöly/AOD) + paneelien likaantumisen arvio ja puhdistussuositus.',
    fArchive:
      'Sijainnin vuotuinen energiaprofiili uudelleenanalyysiarkistosta: vuotuinen säteily, kuukausijärjestys, arvioitu optimaalinen kallistus/atsimuutti (best-effort, päivitetään taustalla).',
    fConfidence:
      'P10/P50/P90-tuotantokaistat ensemble-mallien hajonnasta (vain kun useita malleja on määritetty).',
    s4Title: 'Virhekoodit',
    s4Intro:
      'Virheen sattuessa API palauttaa JSON-objektin, jossa on "error"-kenttä, joka sisältää "code"- ja "message"-kentät.',
    thCode: 'Koodi',
    thHttp: 'HTTP',
    eValidationError: 'Puuttuvia tai virheellisiä kyselyparametreja',
    eNoInstallation: 'Ei tallennettua laitosta eikä URL-parametreja',
    eApiKeyMissing: 'Ei API-avainta otsikossa',
    eApiKeyInvalid: 'API-avainta ei ole olemassa tai se on virheellinen',
    eApiKeyExpired: 'API-avain on vanhentunut',
    eApiKeyInactive: 'API-avain on poistettu käytöstä',
    ePlanLimitExceeded: 'Stringien määrä ylittää suunnitelman rajan',
    eQueryParamsNotAllowed: 'Suunnitelma ei salli dynaamisia URL-parametreja',
    eRateLimitExceeded: 'Pyyntöraja ylitetty (rate limit)',
    eInternalError: 'Sisäinen palvelinvirhe',
    eServiceError: 'Palveluvirhe',
    s5Title: 'Tuotannon korjaus',
    s5Intro:
      'Korjaustoiminto parantaa ennusteen tarkkuutta todellisten tuotantotietojen perusteella. Järjestelmä vertaa ennustettua tuotantoa todelliseen ja laskee tuntikohtaiset (UTC) korjauskertoimet. Vaatii "correctionEnabled"-asetuksen suunnitelmassa.',
    s5SendTitle: 'Todellisten tietojen lähettäminen',
    s5SendDesc: 'Lähetä tietyn päivän tuntikohtainen tuotanto päätepisteeseen:',
    s5DateD: 'date — päivämäärä muodossa YYYY-MM-DD',
    s5TzD: 'tz — aikavyöhyke (UTC-poikkeama), jota käytetään tuotantoa luettaessa',
    s5ProdD: 'production — objekti: avain on tunti (merkkijono), arvo on tuotanto wattitunteina',
    s5EnableTitle: 'Korjauksen ottaminen käyttöön ennusteessa',
    s5EnableDesc:
      'Lisää "correction=true"-parametri ennustepyyntöön tai aseta oletuskorjaus asiakasportaalissa (Korjaus-välilehti).',
    s5Note: 'Jos "correction"-parametria ei anneta, järjestelmä käyttää laitoksesi oletusarvoa.',
    s6Title: 'Esimerkit',
    ex1: 'Ennuste tälle päivälle (laitostila)',
    ex2: 'Ennuste huomiselle (laitostila)',
    ex3: 'Ennuste täydellä konfiguraatiolla (2 stringiä)',
    ex4: 'Kaaviomuoto (kaavioille)',
    ex5: 'Ennuste tuotannon korjauksella',
    ex6: 'Vastauksen käsittely (bash + jq)',
    ex7: 'Ennuste päivämäärillä today/tomorrow-avainten sijaan (date=full)',
  },
  cs: {
    title: 'Nápověda — dokumentace API',
    subtitle:
      'Kompletní dokumentace Solar Forecast API: režimy, parametry, formáty odpovědí a kódy chyb.',
    toc: 'Obsah',
    example: 'Příklad',
    exampleResponse: 'Příklad odpovědi',
    exampleError: 'Příklad chyby',
    s1Title: 'Jak používat API',
    s1Intro:
      'Forecast API podporuje dva režimy. V obou musí být API klíč předán v HTTP hlavičce (název hlavičky je definován v plánu — výchozí X-API-Key).',
    s1Mode1Title: 'Režim 1: Instalace (zjednodušený)',
    s1Mode1Desc:
      'Všechny parametry instalace (poloha, stringy) se automaticky přebírají z vašeho profilu. Stačí předat parametr "day".',
    s1Mode2Title: 'Režim 2: URL parametry (plná kontrola)',
    s1Mode2Desc:
      'Všechny parametry můžete předat přímo v dotazovacím řetězci. Vyžaduje plán s povolenou možností "allowQueryParams". URL parametry přepisují ty z uložené instalace.',
    s1Important:
      'Důležité: parametr "day" je povinný v obou režimech. Povolené hodnoty: "today" nebo "tomorrow".',
    s2Title: 'Parametry dotazu',
    s2Intro: 'Níže je úplný seznam parametrů dotazovacího řetězce pro endpoint GET {{endpoint}}.',
    thParam: 'Parametr',
    thType: 'Typ',
    thRequired: 'Povinný',
    thDesc: 'Popis',
    reqYes: 'ano',
    reqNo: 'ne',
    dDay: 'Den předpovědi: "today" nebo "tomorrow"',
    dDate: 'Formát klíčů dnů: "relative" (výchozí), "full" (RRRR-MM-DD) nebo "timestamp" (unixové sekundy). Při "full"/"timestamp" jsou denní součty vráceny v mapě "totals" se stejnými klíči. Ve formátu chart se mění jen součty — hodinové záznamy už nesou plná časová razítka ts',
    dType: 'Formát odpovědi: "standard" (výchozí) nebo "chart"',
    dCorrection:
      'Korekce produkce: "true" nebo "false". Pokud se vynechá, použije se výchozí hodnota instalace.',
    dLat: 'Zeměpisná šířka (např. 52.23)',
    dLon: 'Zeměpisná délka (např. 21.01)',
    dTz: 'Posun časového pásma UTC (-12 až 12, např. 2 pro letní SEČ)',
    dAz: 'Azimut stringu {{n}} ve stupních (-180 až 180; 0=sever, 180=jih)',
    dTilt: 'Sklon stringu {{n}} ve stupních (0 až 90)',
    dPanels: 'Počet panelů ve stringu {{n}}',
    dPower: 'Výkon panelu v kW ve stringu {{n}} (např. 0.4 = 400W)',
    dEff: 'Účinnost panelu stringu {{n}} v % (např. 92)',
    s2Footnote:
      '* — Povinné v režimu URL parametrů. V režimu instalace se parametry polohy a stringů přebírají automaticky. Parametry polohy (lat, lon, tz) a stringy (az, t, n, s, e) vyžadují možnost "allowQueryParams" v plánu.',
    s3Title: 'Formát odpovědi',
    s3StdTitle: 'Standardní odpověď (type=standard)',
    s3StdDesc: 'Výchozí formát — hodinová produkce ve watthodinách (Wh) jako objekt klíč-hodnota.',
    s3DateFmtTitle: 'Klíče dnů jako data (date=full / date=timestamp)',
    s3DateFmtDesc: 'S date=full se klíče dnů v result a strings stanou daty RRRR-MM-DD a denní součty jsou vráceny v mapě "totals" se stejnými daty. date=timestamp funguje obdobně s unixovými sekundami (začátek dne v zóně tz). Platí i pro blok "confidence" při enriched=1. Ve formátu chart se mění jen součty — hodinové záznamy už nesou plná časová razítka ts.',
    s3ChartTitle: 'Odpověď pro graf (type=chart)',
    s3ChartDesc:
      'Formát optimalizovaný pro grafy — pole objektů s ISO časovou značkou a hodnotou energie.',
    s3FieldsTitle: 'Popis polí',
    thField: 'Pole',
    fDate: 'Časová značka generování odpovědi (ISO 8601)',
    fLatLon: 'Souřadnice použité v požadavku',
    fToday: 'Hodinová produkce pro dnešek (klíč = hodina, hodnota = Wh)',
    fTomorrow: 'Hodinová produkce pro zítřek',
    fTotals: 'Celková produkce pro daný den (Wh); při date=full/timestamp vráceno jako mapa "totals" s klíči dnů',
    fStrings: 'Pole dat pro jednotlivé stringy (azimut, účinnost, výkon, počet panelů, produkce)',
    fCorrApplied: 'Zda byla použita korekce produkce (true/false)',
    fProcTime: 'Doba zpracování požadavku v ms',
    fWeather:
      'Meteorologický kontext: hodinová oblačnost, teplota vzduchu/panelů, vítr, srážky, sníh, kód WMO, UV, východ/západ slunce + denní souhrn.',
    fWeatherAlerts: 'Aktivní výstrahy MeteoAlarm pro lokalitu (prázdné pole, pokud žádné).',
    fProdAlerts:
      'Produkční výstrahy odvozené z předpovědi: sníh na panelech, hustá oblačnost, silné nárazy větru.',
    fAirQuality:
      'Kvalita ovzduší (PM10/PM2.5/prach/AOD) + posouzení znečištění panelů a doporučení k čištění.',
    fArchive:
      'Roční energetický profil lokality z archivu reanalýz: roční ozáření, žebříček měsíců, odhadovaný optimální sklon/azimut (best-effort, obnovováno na pozadí).',
    fConfidence:
      'Produkční pásma P10/P50/P90 z rozptylu ensemble modelů (pouze když je nakonfigurováno více modelů).',
    s4Title: 'Kódy chyb',
    s4Intro: 'Při chybě API vrací JSON objekt s polem "error" obsahujícím "code" a "message".',
    thCode: 'Kód',
    thHttp: 'HTTP',
    eValidationError: 'Chybějící nebo neplatné parametry dotazu',
    eNoInstallation: 'Žádná uložená instalace a žádné URL parametry',
    eApiKeyMissing: 'Žádný API klíč v hlavičce',
    eApiKeyInvalid: 'API klíč neexistuje nebo je neplatný',
    eApiKeyExpired: 'API klíč vypršel',
    eApiKeyInactive: 'API klíč byl deaktivován',
    ePlanLimitExceeded: 'Počet stringů překračuje limit plánu',
    eQueryParamsNotAllowed: 'Plán neumožňuje dynamické URL parametry',
    eRateLimitExceeded: 'Překročen limit požadavků (rate limit)',
    eInternalError: 'Vnitřní chyba serveru',
    eServiceError: 'Chyba služby',
    s5Title: 'Korekce produkce',
    s5Intro:
      'Funkce korekce zlepšuje přesnost předpovědi na základě skutečných produkčních dat. Systém porovnává předpovězenou produkci se skutečnou a počítá korekční faktory po hodinách (UTC). Vyžaduje možnost "correctionEnabled" v plánu.',
    s5SendTitle: 'Odesílání skutečných dat',
    s5SendDesc: 'Odešlete hodinovou produkci pro daný den na endpoint:',
    s5DateD: 'date — datum ve formátu YYYY-MM-DD',
    s5TzD: 'tz — časové pásmo (posun UTC) použité při čtení produkce',
    s5ProdD: 'production — objekt: klíč je hodina (řetězec), hodnota je produkce ve Wh',
    s5EnableTitle: 'Zapnutí korekce v předpovědi',
    s5EnableDesc:
      'Přidejte parametr "correction=true" k požadavku na předpověď, nebo nastavte výchozí korekci v klientském portálu (záložka Korekce).',
    s5Note:
      'Pokud parametr "correction" není uveden, systém použije výchozí hodnotu vaší instalace.',
    s6Title: 'Příklady',
    ex1: 'Předpověď pro dnešek (režim instalace)',
    ex2: 'Předpověď pro zítřek (režim instalace)',
    ex3: 'Předpověď s úplnou konfigurací (2 stringy)',
    ex4: 'Formát grafu (pro grafy)',
    ex5: 'Předpověď s korekcí produkce',
    ex6: 'Zpracování odpovědi (bash + jq)',
    ex7: 'Předpověď s daty místo today/tomorrow (date=full)',
  },
  sk: {
    title: 'Pomoc — dokumentácia API',
    subtitle:
      'Kompletná dokumentácia Solar Forecast API: režimy, parametre, formáty odpovedí a kódy chýb.',
    toc: 'Obsah',
    example: 'Príklad',
    exampleResponse: 'Príklad odpovede',
    exampleError: 'Príklad chyby',
    s1Title: 'Ako používať API',
    s1Intro:
      'Forecast API podporuje dva režimy. V oboch musí byť API kľúč odovzdaný v HTTP hlavičke (názov hlavičky je definovaný v pláne — predvolené X-API-Key).',
    s1Mode1Title: 'Režim 1: Inštalácia (zjednodušený)',
    s1Mode1Desc:
      'Všetky parametre inštalácie (poloha, stringy) sa automaticky preberajú z vášho profilu. Stačí odovzdať parameter "day".',
    s1Mode2Title: 'Režim 2: URL parametre (plná kontrola)',
    s1Mode2Desc:
      'Všetky parametre môžete odovzdať priamo v dopytovacom reťazci. Vyžaduje plán s povolenou možnosťou "allowQueryParams". URL parametre prepisujú tie z uloženej inštalácie.',
    s1Important:
      'Dôležité: parameter "day" je povinný v oboch režimoch. Povolené hodnoty: "today" alebo "tomorrow".',
    s2Title: 'Parametre dopytu',
    s2Intro:
      'Nižšie je úplný zoznam parametrov dopytovacieho reťazca pre endpoint GET {{endpoint}}.',
    thParam: 'Parameter',
    thType: 'Typ',
    thRequired: 'Povinný',
    thDesc: 'Popis',
    reqYes: 'áno',
    reqNo: 'nie',
    dDay: 'Deň predpovede: "today" alebo "tomorrow"',
    dDate: 'Formát kľúčov dní: "relative" (predvolený), "full" (RRRR-MM-DD) alebo "timestamp" (unixové sekundy). Pri "full"/"timestamp" sú denné súčty vrátené v mape "totals" s rovnakými kľúčmi. Vo formáte chart sa menia len súčty — hodinové záznamy už nesú plné časové pečiatky ts',
    dType: 'Formát odpovede: "standard" (predvolený) alebo "chart"',
    dCorrection:
      'Korekcia produkcie: "true" alebo "false". Ak sa vynechá, použije sa predvolená hodnota inštalácie.',
    dLat: 'Zemepisná šírka (napr. 52.23)',
    dLon: 'Zemepisná dĺžka (napr. 21.01)',
    dTz: 'Posun časového pásma UTC (-12 až 12, napr. 2 pre letný SEČ)',
    dAz: 'Azimut stringu {{n}} v stupňoch (-180 až 180; 0=sever, 180=juh)',
    dTilt: 'Sklon stringu {{n}} v stupňoch (0 až 90)',
    dPanels: 'Počet panelov v stringu {{n}}',
    dPower: 'Výkon panelu v kW v stringu {{n}} (napr. 0.4 = 400W)',
    dEff: 'Účinnosť panelu stringu {{n}} v % (napr. 92)',
    s2Footnote:
      '* — Povinné v režime URL parametrov. V režime inštalácie sa parametre polohy a stringov preberajú automaticky. Parametre polohy (lat, lon, tz) a stringy (az, t, n, s, e) vyžadujú možnosť "allowQueryParams" v pláne.',
    s3Title: 'Formát odpovede',
    s3StdTitle: 'Štandardná odpoveď (type=standard)',
    s3StdDesc:
      'Predvolený formát — hodinová produkcia vo watthodinách (Wh) ako objekt kľúč-hodnota.',
    s3DateFmtTitle: 'Kľúče dní ako dátumy (date=full / date=timestamp)',
    s3DateFmtDesc: 'S date=full sa kľúče dní v result a strings stanú dátumami RRRR-MM-DD a denné súčty sú vrátené v mape "totals" s rovnakými dátumami. date=timestamp funguje obdobne s unixovými sekundami (začiatok dňa v zóne tz). Platí aj pre blok "confidence" pri enriched=1. Vo formáte chart sa menia len súčty — hodinové záznamy už nesú plné časové pečiatky ts.',
    s3ChartTitle: 'Odpoveď pre graf (type=chart)',
    s3ChartDesc:
      'Formát optimalizovaný pre grafy — pole objektov s ISO časovou značkou a hodnotou energie.',
    s3FieldsTitle: 'Popis polí',
    thField: 'Pole',
    fDate: 'Časová značka generovania odpovede (ISO 8601)',
    fLatLon: 'Súradnice použité v požiadavke',
    fToday: 'Hodinová produkcia pre dnešok (kľúč = hodina, hodnota = Wh)',
    fTomorrow: 'Hodinová produkcia pre zajtrajšok',
    fTotals: 'Celková produkcia pre daný deň (Wh); pri date=full/timestamp vrátené ako mapa "totals" s kľúčmi dní',
    fStrings: 'Pole dát pre jednotlivé stringy (azimut, účinnosť, výkon, počet panelov, produkcia)',
    fCorrApplied: 'Či bola použitá korekcia produkcie (true/false)',
    fProcTime: 'Čas spracovania požiadavky v ms',
    fWeather:
      'Meteorologický kontext: hodinová oblačnosť, teplota vzduchu/panelov, vietor, zrážky, sneh, kód WMO, UV, východ/západ slnka + denný súhrn.',
    fWeatherAlerts: 'Aktívne výstrahy MeteoAlarm pre lokalitu (prázdne pole, ak žiadne).',
    fProdAlerts:
      'Produkčné výstrahy odvodené z predpovede: sneh na paneloch, hustá oblačnosť, silné nárazy vetra.',
    fAirQuality:
      'Kvalita ovzdušia (PM10/PM2.5/prach/AOD) + posúdenie znečistenia panelov a odporúčanie na čistenie.',
    fArchive:
      'Ročný energetický profil lokality z archívu reanalýz: ročné ožiarenie, rebríček mesiacov, odhadovaný optimálny sklon/azimut (best-effort, obnovované na pozadí).',
    fConfidence:
      'Produkčné pásma P10/P50/P90 z rozptylu ensemble modelov (iba keď je nakonfigurovaných viac modelov).',
    s4Title: 'Kódy chýb',
    s4Intro: 'Pri chybe API vracia JSON objekt s poľom "error" obsahujúcim "code" a "message".',
    thCode: 'Kód',
    thHttp: 'HTTP',
    eValidationError: 'Chýbajúce alebo neplatné parametre dopytu',
    eNoInstallation: 'Žiadna uložená inštalácia a žiadne URL parametre',
    eApiKeyMissing: 'Žiadny API kľúč v hlavičke',
    eApiKeyInvalid: 'API kľúč neexistuje alebo je neplatný',
    eApiKeyExpired: 'API kľúč vypršal',
    eApiKeyInactive: 'API kľúč bol deaktivovaný',
    ePlanLimitExceeded: 'Počet stringov prekračuje limit plánu',
    eQueryParamsNotAllowed: 'Plán neumožňuje dynamické URL parametre',
    eRateLimitExceeded: 'Prekročený limit požiadaviek (rate limit)',
    eInternalError: 'Vnútorná chyba servera',
    eServiceError: 'Chyba služby',
    s5Title: 'Korekcia produkcie',
    s5Intro:
      'Funkcia korekcie zlepšuje presnosť predpovede na základe skutočných produkčných dát. Systém porovnáva predpovedanú produkciu so skutočnou a počíta korekčné faktory po hodinách (UTC). Vyžaduje možnosť "correctionEnabled" v pláne.',
    s5SendTitle: 'Odosielanie skutočných dát',
    s5SendDesc: 'Odošlite hodinovú produkciu pre daný deň na endpoint:',
    s5DateD: 'date — dátum vo formáte YYYY-MM-DD',
    s5TzD: 'tz — časové pásmo (posun UTC) použité pri čítaní produkcie',
    s5ProdD: 'production — objekt: kľúč je hodina (reťazec), hodnota je produkcia vo Wh',
    s5EnableTitle: 'Zapnutie korekcie v predpovedi',
    s5EnableDesc:
      'Pridajte parameter "correction=true" k požiadavke na predpoveď, alebo nastavte predvolenú korekciu v klientskom portáli (záložka Korekcia).',
    s5Note:
      'Ak parameter "correction" nie je uvedený, systém použije predvolenú hodnotu vašej inštalácie.',
    s6Title: 'Príklady',
    ex1: 'Predpoveď pre dnešok (režim inštalácie)',
    ex2: 'Predpoveď pre zajtrajšok (režim inštalácie)',
    ex3: 'Predpoveď s úplnou konfiguráciou (2 stringy)',
    ex4: 'Formát grafu (pre grafy)',
    ex5: 'Predpoveď s korekciou produkcie',
    ex6: 'Spracovanie odpovede (bash + jq)',
    ex7: 'Predpoveď s dátumami namiesto today/tomorrow (date=full)',
  },
  sl: {
    title: 'Pomoč — dokumentacija API',
    subtitle:
      'Popolna dokumentacija Solar Forecast API: načini, parametri, formati odgovorov in kode napak.',
    toc: 'Kazalo vsebine',
    example: 'Primer',
    exampleResponse: 'Primer odgovora',
    exampleError: 'Primer napake',
    s1Title: 'Kako uporabljati API',
    s1Intro:
      'Forecast API podpira dva načina. V obeh mora biti ključ API posredovan v glavi HTTP (ime glave je določeno v načrtu — privzeto X-API-Key).',
    s1Mode1Title: 'Način 1: Naprava (poenostavljeno)',
    s1Mode1Desc:
      'Vsi parametri naprave (lokacija, nizi) se samodejno prevzamejo iz vašega profila. Posredujte le parameter "day".',
    s1Mode2Title: 'Način 2: Parametri URL (popoln nadzor)',
    s1Mode2Desc:
      'Vse parametre lahko posredujete neposredno v poizvedbenem nizu. Zahteva načrt z omogočeno možnostjo "allowQueryParams". Parametri URL prepišejo tiste iz shranjene naprave.',
    s1Important:
      'Pomembno: parameter "day" je obvezen v obeh načinih. Dovoljene vrednosti: "today" ali "tomorrow".',
    s2Title: 'Parametri poizvedbe',
    s2Intro:
      'Spodaj je celoten seznam parametrov poizvedbenega niza za končno točko GET {{endpoint}}.',
    thParam: 'Parameter',
    thType: 'Vrsta',
    thRequired: 'Obvezno',
    thDesc: 'Opis',
    reqYes: 'da',
    reqNo: 'ne',
    dDay: 'Dan napovedi: "today" ali "tomorrow"',
    dDate: 'Oblika ključev dni: "relative" (privzeto), "full" (LLLL-MM-DD) ali "timestamp" (sekunde Unix). Pri "full"/"timestamp" so dnevne vsote vrnjene v mapi "totals" z enakimi ključi. V formatu chart se spremenijo le vsote — urni vnosi že nosijo polne časovne žige ts',
    dType: 'Format odgovora: "standard" (privzeto) ali "chart"',
    dCorrection:
      'Popravek proizvodnje: "true" ali "false". Če je izpuščeno, se uporabi privzeta vrednost naprave.',
    dLat: 'Zemljepisna širina (npr. 52.23)',
    dLon: 'Zemljepisna dolžina (npr. 21.01)',
    dTz: 'Odmik časovnega pasu UTC (-12 do 12, npr. 2 za poletni CET)',
    dAz: 'Azimut niza {{n}} v stopinjah (-180 do 180; 0=sever, 180=jug)',
    dTilt: 'Naklon niza {{n}} v stopinjah (0 do 90)',
    dPanels: 'Število panelov v nizu {{n}}',
    dPower: 'Moč panela v kW v nizu {{n}} (npr. 0.4 = 400W)',
    dEff: 'Učinkovitost panela niza {{n}} v % (npr. 92)',
    s2Footnote:
      '* — Obvezno v načinu parametrov URL. V načinu naprave se parametri lokacije in nizov prevzamejo samodejno. Parametri lokacije (lat, lon, tz) in nizi (az, t, n, s, e) zahtevajo možnost "allowQueryParams" v načrtu.',
    s3Title: 'Format odgovora',
    s3StdTitle: 'Standardni odgovor (type=standard)',
    s3StdDesc: 'Privzeti format — urna proizvodnja v vatnih urah (Wh) kot objekt ključ-vrednost.',
    s3DateFmtTitle: 'Ključi dni kot datumi (date=full / date=timestamp)',
    s3DateFmtDesc: 'Z date=full postanejo ključi dni v result in strings datumi LLLL-MM-DD, dnevne vsote pa so vrnjene v mapi "totals" z istimi datumi. date=timestamp deluje enako s sekundami Unix (začetek dneva v časovnem pasu tz). Velja tudi za blok "confidence" pri enriched=1. V formatu chart se spremenijo le vsote — urni vnosi že nosijo polne časovne žige ts.',
    s3ChartTitle: 'Odgovor za graf (type=chart)',
    s3ChartDesc:
      'Format, optimiziran za grafe — polje objektov s časovnim žigom ISO in vrednostjo energije.',
    s3FieldsTitle: 'Opisi polj',
    thField: 'Polje',
    fDate: 'Časovni žig generiranja odgovora (ISO 8601)',
    fLatLon: 'Koordinate, uporabljene v zahtevi',
    fToday: 'Urna proizvodnja za danes (ključ = ura, vrednost = Wh)',
    fTomorrow: 'Urna proizvodnja za jutri',
    fTotals: 'Skupna proizvodnja za dani dan (Wh); pri date=full/timestamp vrnjeno kot mapa "totals" s ključi dni',
    fStrings:
      'Polje podatkov za posamezni niz (azimut, učinkovitost, moč, število panelov, proizvodnja)',
    fCorrApplied: 'Ali je bil uporabljen popravek proizvodnje (true/false)',
    fProcTime: 'Čas obdelave zahteve v ms',
    fWeather:
      'Vremenski kontekst: urna oblačnost, temperatura zraka/panelov, veter, padavine, sneg, koda WMO, UV, vzhod/zahod sonca + dnevni povzetek.',
    fWeatherAlerts: 'Aktivna opozorila MeteoAlarm za lokacijo (prazno polje, če jih ni).',
    fProdAlerts:
      'Proizvodna opozorila iz napovedi: sneg na panelih, gosta oblačnost, močni sunki vetra.',
    fAirQuality:
      'Kakovost zraka (PM10/PM2.5/prah/AOD) + ocena umazanije panelov in priporočilo za čiščenje.',
    fArchive:
      'Letni energetski profil lokacije iz arhiva reanaliz: letno obsevanje, lestvica mesecev, ocenjeni optimalni naklon/azimut (best-effort, osveženo v ozadju).',
    fConfidence:
      'Proizvodni pasovi P10/P50/P90 iz razpršenosti ansambelskih modelov (samo ko je nastavljenih več modelov).',
    s4Title: 'Kode napak',
    s4Intro: 'Ob napaki API vrne objekt JSON s poljem "error", ki vsebuje "code" in "message".',
    thCode: 'Koda',
    thHttp: 'HTTP',
    eValidationError: 'Manjkajoči ali neveljavni parametri poizvedbe',
    eNoInstallation: 'Ni shranjene naprave in ni parametrov URL',
    eApiKeyMissing: 'Ni ključa API v glavi',
    eApiKeyInvalid: 'Ključ API ne obstaja ali je neveljaven',
    eApiKeyExpired: 'Ključ API je potekel',
    eApiKeyInactive: 'Ključ API je bil deaktiviran',
    ePlanLimitExceeded: 'Število nizov presega omejitev načrta',
    eQueryParamsNotAllowed: 'Načrt ne dovoljuje dinamičnih parametrov URL',
    eRateLimitExceeded: 'Presežena omejitev zahtev (rate limit)',
    eInternalError: 'Notranja napaka strežnika',
    eServiceError: 'Napaka storitve',
    s5Title: 'Popravek proizvodnje',
    s5Intro:
      'Funkcija popravka izboljša natančnost napovedi na podlagi dejanskih proizvodnih podatkov. Sistem primerja napovedano proizvodnjo z dejansko in izračuna korekcijske faktorje po urah (UTC). Zahteva možnost "correctionEnabled" v načrtu.',
    s5SendTitle: 'Pošiljanje dejanskih podatkov',
    s5SendDesc: 'Pošljite urno proizvodnjo za dani dan na končno točko:',
    s5DateD: 'date — datum v formatu YYYY-MM-DD',
    s5TzD: 'tz — časovni pas (odmik UTC), uporabljen pri branju proizvodnje',
    s5ProdD: 'production — objekt: ključ je ura (niz), vrednost je proizvodnja v Wh',
    s5EnableTitle: 'Omogočanje popravka v napovedi',
    s5EnableDesc:
      'Dodajte parameter "correction=true" v zahtevo za napoved ali nastavite privzeti popravek na portalu za stranke (zavihek Popravek).',
    s5Note: 'Če parameter "correction" ni naveden, sistem uporabi privzeto vrednost vaše naprave.',
    s6Title: 'Primeri',
    ex1: 'Napoved za danes (način naprave)',
    ex2: 'Napoved za jutri (način naprave)',
    ex3: 'Napoved s polno konfiguracijo (2 niza)',
    ex4: 'Format grafa (za grafe)',
    ex5: 'Napoved s popravkom proizvodnje',
    ex6: 'Obravnava odgovora (bash + jq)',
    ex7: 'Napoved z datumi namesto today/tomorrow (date=full)',
  },
  hr: {
    title: 'Pomoć — dokumentacija API-ja',
    subtitle:
      'Potpuna dokumentacija Solar Forecast API-ja: načini rada, parametri, formati odgovora i kodovi grešaka.',
    toc: 'Sadržaj',
    example: 'Primjer',
    exampleResponse: 'Primjer odgovora',
    exampleError: 'Primjer greške',
    s1Title: 'Kako koristiti API',
    s1Intro:
      'Forecast API podržava dva načina rada. U oba se API ključ mora proslijediti u HTTP zaglavlju (naziv zaglavlja definiran je u planu — zadano X-API-Key).',
    s1Mode1Title: 'Način 1: Postrojenje (pojednostavljeno)',
    s1Mode1Desc:
      'Svi parametri postrojenja (lokacija, nizovi) automatski se preuzimaju iz vašeg profila. Samo proslijedite parametar "day".',
    s1Mode2Title: 'Način 2: URL parametri (potpuna kontrola)',
    s1Mode2Desc:
      'Sve parametre možete proslijediti izravno u upitni niz. Zahtijeva plan s omogućenom opcijom "allowQueryParams". URL parametri nadjačavaju one iz spremljenog postrojenja.',
    s1Important:
      'Važno: parametar "day" je obavezan u oba načina rada. Dopuštene vrijednosti: "today" ili "tomorrow".',
    s2Title: 'Parametri upita',
    s2Intro: 'Ispod je potpuni popis parametara upitnog niza za krajnju točku GET {{endpoint}}.',
    thParam: 'Parametar',
    thType: 'Vrsta',
    thRequired: 'Obavezno',
    thDesc: 'Opis',
    reqYes: 'da',
    reqNo: 'ne',
    dDay: 'Dan prognoze: "today" ili "tomorrow"',
    dDate: 'Format ključeva dana: "relative" (zadano), "full" (GGGG-MM-DD) ili "timestamp" (Unix sekunde). Uz "full"/"timestamp" dnevni zbrojevi vraćaju se u mapi "totals" s istim ključevima. U formatu chart mijenjaju se samo zbrojevi — satni unosi već nose pune vremenske oznake ts',
    dType: 'Format odgovora: "standard" (zadano) ili "chart"',
    dCorrection:
      'Korekcija proizvodnje: "true" ili "false". Ako se izostavi, koristi se zadana vrijednost postrojenja.',
    dLat: 'Zemljopisna širina (npr. 52.23)',
    dLon: 'Zemljopisna dužina (npr. 21.01)',
    dTz: 'Pomak vremenske zone UTC (-12 do 12, npr. 2 za ljetno CET)',
    dAz: 'Azimut niza {{n}} u stupnjevima (-180 do 180; 0=sjever, 180=jug)',
    dTilt: 'Nagib niza {{n}} u stupnjevima (0 do 90)',
    dPanels: 'Broj panela u nizu {{n}}',
    dPower: 'Snaga panela u kW u nizu {{n}} (npr. 0.4 = 400W)',
    dEff: 'Učinkovitost panela niza {{n}} u % (npr. 92)',
    s2Footnote:
      '* — Obavezno u načinu URL parametara. U načinu postrojenja parametri lokacije i nizova preuzimaju se automatski. Parametri lokacije (lat, lon, tz) i nizovi (az, t, n, s, e) zahtijevaju opciju "allowQueryParams" u planu.',
    s3Title: 'Format odgovora',
    s3StdTitle: 'Standardni odgovor (type=standard)',
    s3StdDesc: 'Zadani format — satna proizvodnja u vat-satima (Wh) kao objekt ključ-vrijednost.',
    s3DateFmtTitle: 'Ključevi dana kao datumi (date=full / date=timestamp)',
    s3DateFmtDesc: 'Uz date=full ključevi dana u result i strings postaju datumi GGGG-MM-DD, a dnevni zbrojevi vraćaju se u mapi "totals" s istim datumima. date=timestamp radi jednako s Unix sekundama (početak dana u zoni tz). Vrijedi i za blok "confidence" uz enriched=1. U formatu chart mijenjaju se samo zbrojevi — satni unosi već nose pune vremenske oznake ts.',
    s3ChartTitle: 'Odgovor za grafikon (type=chart)',
    s3ChartDesc:
      'Format optimiziran za grafikone — polje objekata s ISO vremenskom oznakom i vrijednošću energije.',
    s3FieldsTitle: 'Opisi polja',
    thField: 'Polje',
    fDate: 'Vremenska oznaka generiranja odgovora (ISO 8601)',
    fLatLon: 'Koordinate korištene u zahtjevu',
    fToday: 'Satna proizvodnja za danas (ključ = sat, vrijednost = Wh)',
    fTomorrow: 'Satna proizvodnja za sutra',
    fTotals: 'Ukupna proizvodnja za dani dan (Wh); uz date=full/timestamp vraća se kao mapa "totals" s ključevima dana',
    fStrings: 'Polje podataka po nizu (azimut, učinkovitost, snaga, broj panela, proizvodnja)',
    fCorrApplied: 'Je li primijenjena korekcija proizvodnje (true/false)',
    fProcTime: 'Vrijeme obrade zahtjeva u ms',
    fWeather:
      'Vremenski kontekst: satna naoblaka, temperatura zraka/panela, vjetar, oborine, snijeg, WMO kod, UV, izlazak/zalazak sunca + dnevni sažetak.',
    fWeatherAlerts: 'Aktivna upozorenja MeteoAlarm za lokaciju (prazan niz ako ih nema).',
    fProdAlerts:
      'Proizvodna upozorenja izvedena iz prognoze: snijeg na panelima, gusta naoblaka, jaki udari vjetra.',
    fAirQuality:
      'Kvaliteta zraka (PM10/PM2.5/prašina/AOD) + procjena onečišćenja panela i preporuka za čišćenje.',
    fArchive:
      'Godišnji energetski profil lokacije iz arhiva reanaliza: godišnje ozračenje, poredak mjeseci, procijenjeni optimalni nagib/azimut (best-effort, osvježava se u pozadini).',
    fConfidence:
      'Proizvodni pojasevi P10/P50/P90 iz raspršenosti ansambl modela (samo kad je konfigurirano više modela).',
    s4Title: 'Kodovi grešaka',
    s4Intro:
      'U slučaju greške API vraća JSON objekt s poljem "error" koje sadrži "code" i "message".',
    thCode: 'Kod',
    thHttp: 'HTTP',
    eValidationError: 'Nedostajući ili nevažeći parametri upita',
    eNoInstallation: 'Nema spremljenog postrojenja i nema URL parametara',
    eApiKeyMissing: 'Nema API ključa u zaglavlju',
    eApiKeyInvalid: 'API ključ ne postoji ili je nevažeći',
    eApiKeyExpired: 'API ključ je istekao',
    eApiKeyInactive: 'API ključ je deaktiviran',
    ePlanLimitExceeded: 'Broj nizova premašuje ograničenje plana',
    eQueryParamsNotAllowed: 'Plan ne dopušta dinamičke URL parametre',
    eRateLimitExceeded: 'Premašeno ograničenje zahtjeva (rate limit)',
    eInternalError: 'Interna greška poslužitelja',
    eServiceError: 'Greška usluge',
    s5Title: 'Korekcija proizvodnje',
    s5Intro:
      'Funkcija korekcije poboljšava točnost prognoze na temelju stvarnih proizvodnih podataka. Sustav uspoređuje prognoziranu proizvodnju sa stvarnom i izračunava korekcijske faktore po satu (UTC). Zahtijeva opciju "correctionEnabled" u planu.',
    s5SendTitle: 'Slanje stvarnih podataka',
    s5SendDesc: 'Pošaljite satnu proizvodnju za dani dan na krajnju točku:',
    s5DateD: 'date — datum u formatu YYYY-MM-DD',
    s5TzD: 'tz — vremenska zona (pomak UTC) korištena pri čitanju proizvodnje',
    s5ProdD: 'production — objekt: ključ je sat (niz), vrijednost je proizvodnja u Wh',
    s5EnableTitle: 'Omogućavanje korekcije u prognozi',
    s5EnableDesc:
      'Dodajte parametar "correction=true" zahtjevu za prognozu ili postavite zadanu korekciju u korisničkom portalu (kartica Korekcija).',
    s5Note:
      'Ako parametar "correction" nije naveden, sustav koristi zadanu vrijednost vašeg postrojenja.',
    s6Title: 'Primjeri',
    ex1: 'Prognoza za danas (način postrojenja)',
    ex2: 'Prognoza za sutra (način postrojenja)',
    ex3: 'Prognoza s potpunom konfiguracijom (2 niza)',
    ex4: 'Format grafikona (za grafikone)',
    ex5: 'Prognoza s korekcijom proizvodnje',
    ex6: 'Obrada odgovora (bash + jq)',
    ex7: 'Prognoza s datumima umjesto today/tomorrow (date=full)',
  },
  hu: {
    title: 'Súgó — API-dokumentáció',
    subtitle:
      'Teljes Solar Forecast API-dokumentáció: módok, paraméterek, válaszformátumok és hibakódok.',
    toc: 'Tartalomjegyzék',
    example: 'Példa',
    exampleResponse: 'Példaválasz',
    exampleError: 'Példahiba',
    s1Title: 'Az API használata',
    s1Intro:
      'A Forecast API két módot támogat. Mindkettőben az API-kulcsot HTTP-fejlécben kell átadni (a fejléc neve a csomagban van meghatározva — alapértelmezetten X-API-Key).',
    s1Mode1Title: '1. mód: Létesítmény (egyszerűsített)',
    s1Mode1Desc:
      'Az összes létesítményparaméter (hely, stringek) automatikusan a profilodból kerül átvételre. Csak a "day" paramétert add át.',
    s1Mode2Title: '2. mód: URL-paraméterek (teljes kontroll)',
    s1Mode2Desc:
      'Az összes paramétert közvetlenül a lekérdezési karakterláncban átadhatod. Olyan csomagot igényel, ahol az "allowQueryParams" opció engedélyezve van. Az URL-paraméterek felülírják a mentett létesítmény paramétereit.',
    s1Important:
      'Fontos: a "day" paraméter mindkét módban kötelező. Megengedett értékek: "today" vagy "tomorrow".',
    s2Title: 'Lekérdezési paraméterek',
    s2Intro:
      'Alább a GET {{endpoint}} végpont lekérdezési karakterlánc-paramétereinek teljes listája.',
    thParam: 'Paraméter',
    thType: 'Típus',
    thRequired: 'Kötelező',
    thDesc: 'Leírás',
    reqYes: 'igen',
    reqNo: 'nem',
    dDay: 'Előrejelzés napja: "today" vagy "tomorrow"',
    dDate: 'Napkulcsok formátuma: "relative" (alapértelmezett), "full" (ÉÉÉÉ-HH-NN) vagy "timestamp" (Unix másodpercek). "full"/"timestamp" esetén a napi összegek a "totals" térképben térnek vissza azonos kulcsokkal. A chart formátumban csak az összegek változnak — az órás bejegyzések már teljes ts időbélyegeket hordoznak',
    dType: 'Válaszformátum: "standard" (alapértelmezett) vagy "chart"',
    dCorrection:
      'Termeléskorrekció: "true" vagy "false". Ha kihagyod, a létesítmény alapértéke kerül felhasználásra.',
    dLat: 'Szélesség (pl. 52.23)',
    dLon: 'Hosszúság (pl. 21.01)',
    dTz: 'UTC-időzóna-eltolás (-12 és 12 között, pl. 2 a nyári CET-hez)',
    dAz: 'A(z) {{n}}. string azimutja fokban (-180 és 180 között; 0=észak, 180=dél)',
    dTilt: 'A(z) {{n}}. string dőlésszöge fokban (0 és 90 között)',
    dPanels: 'Panelek száma a(z) {{n}}. stringben',
    dPower: 'Panelteljesítmény kW-ban a(z) {{n}}. stringben (pl. 0.4 = 400W)',
    dEff: 'A(z) {{n}}. string panelhatásfoka %-ban (pl. 92)',
    s2Footnote:
      '* — Kötelező URL-paraméter módban. Létesítmény módban a hely- és string-paraméterek automatikusan átvételre kerülnek. A helyparaméterek (lat, lon, tz) és stringek (az, t, n, s, e) az "allowQueryParams" opciót igénylik a csomagban.',
    s3Title: 'Válaszformátum',
    s3StdTitle: 'Szabványos válasz (type=standard)',
    s3StdDesc:
      'Alapértelmezett formátum — óránkénti termelés wattórában (Wh) kulcs-érték objektumként.',
    s3DateFmtTitle: 'Napkulcsok dátumként (date=full / date=timestamp)',
    s3DateFmtDesc: 'date=full esetén a result és strings napkulcsai ÉÉÉÉ-HH-NN dátumokká válnak, a napi összegek pedig a "totals" térképben térnek vissza ugyanazokkal a dátumokkal. A date=timestamp ugyanígy működik Unix másodpercekkel (a nap kezdete a tz időzónában). Az enriched=1 melletti "confidence" blokkra is vonatkozik. A chart formátumban csak az összegek változnak — az órás bejegyzések már teljes ts időbélyegeket hordoznak.',
    s3ChartTitle: 'Diagram válasz (type=chart)',
    s3ChartDesc:
      'Diagramokra optimalizált formátum — objektumok tömbje ISO időbélyeggel és energiaértékkel.',
    s3FieldsTitle: 'Mezőleírások',
    thField: 'Mező',
    fDate: 'A válasz generálásának időbélyege (ISO 8601)',
    fLatLon: 'A kérésben használt koordináták',
    fToday: 'Mai óránkénti termelés (kulcs = óra, érték = Wh)',
    fTomorrow: 'Holnapi óránkénti termelés',
    fTotals: 'Teljes termelés az adott napra (Wh); date=full/timestamp esetén "totals" térképként tér vissza napkulcsokkal',
    fStrings:
      'Stringenkénti adatok tömbje (azimut, hatásfok, teljesítmény, panelek száma, termelés)',
    fCorrApplied: 'Alkalmazták-e a termeléskorrekciót (true/false)',
    fProcTime: 'A kérés feldolgozási ideje ms-ban',
    fWeather:
      'Időjárási környezet: óránkénti felhőzet, levegő-/panelhőmérséklet, szél, csapadék, hó, WMO-kód, UV, napkelte/napnyugta + napi összegzés.',
    fWeatherAlerts: 'A helyszín aktív MeteoAlarm figyelmeztetései (üres tömb, ha nincs).',
    fProdAlerts:
      'Az előrejelzésből származó termelési riasztások: hó a paneleken, sűrű felhőzet, erős széllökések.',
    fAirQuality:
      'Levegőminőség (PM10/PM2.5/por/AOD) + a panelek szennyeződésének értékelése és tisztítási ajánlás.',
    fArchive:
      'A helyszín éves energiaprofilja az újraelemzési archívumból: éves besugárzás, havi rangsor, becsült optimális dőlés/azimut (best-effort, háttérben frissül).',
    fConfidence:
      'P10/P50/P90 termelési sávok az együttes modellek szórásából (csak ha több modell van konfigurálva).',
    s4Title: 'Hibakódok',
    s4Intro:
      'Hiba esetén az API egy JSON-objektumot ad vissza egy "error" mezővel, amely "code" és "message" mezőket tartalmaz.',
    thCode: 'Kód',
    thHttp: 'HTTP',
    eValidationError: 'Hiányzó vagy érvénytelen lekérdezési paraméterek',
    eNoInstallation: 'Nincs mentett létesítmény és nincsenek URL-paraméterek',
    eApiKeyMissing: 'Nincs API-kulcs a fejlécben',
    eApiKeyInvalid: 'Az API-kulcs nem létezik vagy érvénytelen',
    eApiKeyExpired: 'Az API-kulcs lejárt',
    eApiKeyInactive: 'Az API-kulcsot deaktiválták',
    ePlanLimitExceeded: 'A stringek száma meghaladja a csomag korlátját',
    eQueryParamsNotAllowed: 'A csomag nem enged dinamikus URL-paramétereket',
    eRateLimitExceeded: 'Kéréskorlát túllépve (rate limit)',
    eInternalError: 'Belső szerverhiba',
    eServiceError: 'Szolgáltatási hiba',
    s5Title: 'Termeléskorrekció',
    s5Intro:
      'A korrekciós funkció a tényleges termelési adatok alapján javítja az előrejelzés pontosságát. A rendszer összehasonlítja az előrejelzett termelést a tényleges termeléssel, és óránkénti (UTC) korrekciós tényezőket számít. Az "correctionEnabled" opciót igényli a csomagban.',
    s5SendTitle: 'Tényleges adatok beküldése',
    s5SendDesc: 'Küldd el egy adott nap óránkénti termelését a végpontra:',
    s5DateD: 'date — dátum YYYY-MM-DD formátumban',
    s5TzD: 'tz — időzóna (UTC-eltolás), amelyet a termelés olvasásakor használnak',
    s5ProdD: 'production — objektum: a kulcs az óra (string), az érték a termelés Wh-ban',
    s5EnableTitle: 'A korrekció engedélyezése az előrejelzésben',
    s5EnableDesc:
      'Add hozzá a "correction=true" paramétert az előrejelzési kéréshez, vagy állítsd be az alapértelmezett korrekciót az ügyfélportálon (Korrekció fül).',
    s5Note:
      'Ha a "correction" paramétert nem adod meg, a rendszer a létesítményed alapértékét használja.',
    s6Title: 'Példák',
    ex1: 'Mai előrejelzés (létesítmény mód)',
    ex2: 'Holnapi előrejelzés (létesítmény mód)',
    ex3: 'Előrejelzés teljes konfigurációval (2 string)',
    ex4: 'Diagramformátum (diagramokhoz)',
    ex5: 'Előrejelzés termeléskorrekcióval',
    ex6: 'A válasz kezelése (bash + jq)',
    ex7: 'Előrejelzés dátumokkal a today/tomorrow helyett (date=full)',
  },
  ro: {
    title: 'Ajutor — documentația API',
    subtitle:
      'Documentație completă Solar Forecast API: moduri, parametri, formate de răspuns și coduri de eroare.',
    toc: 'Cuprins',
    example: 'Exemplu',
    exampleResponse: 'Exemplu de răspuns',
    exampleError: 'Exemplu de eroare',
    s1Title: 'Cum se folosește API-ul',
    s1Intro:
      'Forecast API acceptă două moduri. În ambele, cheia API trebuie transmisă într-un antet HTTP (numele antetului este definit în plan — implicit X-API-Key).',
    s1Mode1Title: 'Modul 1: Instalație (simplificat)',
    s1Mode1Desc:
      'Toți parametrii instalației (locație, stringuri) sunt preluați automat din profilul tău. Transmite doar parametrul "day".',
    s1Mode2Title: 'Modul 2: Parametri URL (control total)',
    s1Mode2Desc:
      'Poți transmite toți parametrii direct în șirul de interogare. Necesită un plan cu opțiunea "allowQueryParams" activată. Parametrii URL îi suprascriu pe cei din instalația salvată.',
    s1Important:
      'Important: parametrul "day" este obligatoriu în ambele moduri. Valori permise: "today" sau "tomorrow".',
    s2Title: 'Parametri de interogare',
    s2Intro:
      'Mai jos este lista completă a parametrilor șirului de interogare pentru endpoint-ul GET {{endpoint}}.',
    thParam: 'Parametru',
    thType: 'Tip',
    thRequired: 'Obligatoriu',
    thDesc: 'Descriere',
    reqYes: 'da',
    reqNo: 'nu',
    dDay: 'Ziua prognozei: "today" sau "tomorrow"',
    dDate: 'Formatul cheilor de zi: "relative" (implicit), "full" (AAAA-LL-ZZ) sau "timestamp" (secunde Unix). Cu "full"/"timestamp", totalurile zilnice sunt returnate într-o hartă "totals" cu aceleași chei. În formatul chart se schimbă doar totalurile — intrările orare poartă deja timestampuri ts complete',
    dType: 'Format de răspuns: "standard" (implicit) sau "chart"',
    dCorrection:
      'Corecția producției: "true" sau "false". Dacă este omis, se folosește valoarea implicită a instalației.',
    dLat: 'Latitudine (ex. 52.23)',
    dLon: 'Longitudine (ex. 21.01)',
    dTz: 'Decalajul fusului orar UTC (-12 la 12, ex. 2 pentru CET de vară)',
    dAz: 'Azimutul stringului {{n}} în grade (-180 la 180; 0=nord, 180=sud)',
    dTilt: 'Înclinarea stringului {{n}} în grade (0 la 90)',
    dPanels: 'Numărul de panouri din stringul {{n}}',
    dPower: 'Puterea panoului în kW în stringul {{n}} (ex. 0.4 = 400W)',
    dEff: 'Eficiența panoului stringului {{n}} în % (ex. 92)',
    s2Footnote:
      '* — Obligatoriu în modul parametri URL. În modul instalație, parametrii de locație și stringuri sunt preluați automat. Parametrii de locație (lat, lon, tz) și stringurile (az, t, n, s, e) necesită opțiunea "allowQueryParams" în plan.',
    s3Title: 'Format de răspuns',
    s3StdTitle: 'Răspuns standard (type=standard)',
    s3StdDesc: 'Format implicit — producție orară în wați-oră (Wh) ca obiect cheie-valoare.',
    s3DateFmtTitle: 'Chei de zi ca date calendaristice (date=full / date=timestamp)',
    s3DateFmtDesc: 'Cu date=full, cheile de zi din result și strings devin date AAAA-LL-ZZ, iar totalurile zilnice sunt returnate într-o hartă "totals" cu aceleași date. date=timestamp funcționează la fel cu secunde Unix (începutul zilei în fusul tz). Se aplică și blocului "confidence" cu enriched=1. În formatul chart se schimbă doar totalurile — intrările orare poartă deja timestampuri ts complete.',
    s3ChartTitle: 'Răspuns pentru grafic (type=chart)',
    s3ChartDesc:
      'Format optimizat pentru grafice — un array de obiecte cu marcaj de timp ISO și o valoare de energie.',
    s3FieldsTitle: 'Descrierea câmpurilor',
    thField: 'Câmp',
    fDate: 'Marcaj de timp al generării răspunsului (ISO 8601)',
    fLatLon: 'Coordonatele folosite în cerere',
    fToday: 'Producție orară pentru azi (cheie = oră, valoare = Wh)',
    fTomorrow: 'Producție orară pentru mâine',
    fTotals: 'Producția totală pentru ziua dată (Wh); cu date=full/timestamp returnat ca hartă "totals" cu chei de zi',
    fStrings: 'Array de date per string (azimut, eficiență, putere, număr de panouri, producție)',
    fCorrApplied: 'Dacă s-a aplicat corecția producției (true/false)',
    fProcTime: 'Timpul de procesare a cererii în ms',
    fWeather:
      'Context meteo: nebulozitate orară, temperatură aer/panouri, vânt, precipitații, zăpadă, cod WMO, UV, răsărit/apus + rezumat zilnic.',
    fWeatherAlerts:
      'Avertizări meteo MeteoAlarm active pentru locație (matrice goală dacă nu există).',
    fProdAlerts:
      'Alerte de producție derivate din prognoză: zăpadă pe panouri, nebulozitate densă, rafale puternice.',
    fAirQuality:
      'Calitatea aerului (PM10/PM2.5/praf/AOD) + evaluarea murdăririi panourilor și recomandare de curățare.',
    fArchive:
      'Profilul energetic anual al locației din arhiva de reanaliză: iradiere anuală, clasament lunar, înclinare/azimut optime estimate (best-effort, reîmprospătat în fundal).',
    fConfidence:
      'Benzi de producție P10/P50/P90 din dispersia modelelor de ansamblu (doar când sunt configurate mai multe modele).',
    s4Title: 'Coduri de eroare',
    s4Intro:
      'În caz de eroare, API-ul returnează un obiect JSON cu un câmp "error" care conține "code" și "message".',
    thCode: 'Cod',
    thHttp: 'HTTP',
    eValidationError: 'Parametri de interogare lipsă sau nevalizi',
    eNoInstallation: 'Nicio instalație salvată și niciun parametru URL',
    eApiKeyMissing: 'Nicio cheie API în antet',
    eApiKeyInvalid: 'Cheia API nu există sau este nevalidă',
    eApiKeyExpired: 'Cheia API a expirat',
    eApiKeyInactive: 'Cheia API a fost dezactivată',
    ePlanLimitExceeded: 'Numărul de stringuri depășește limita planului',
    eQueryParamsNotAllowed: 'Planul nu permite parametri URL dinamici',
    eRateLimitExceeded: 'Limită de cereri depășită (rate limit)',
    eInternalError: 'Eroare internă de server',
    eServiceError: 'Eroare de serviciu',
    s5Title: 'Corecția producției',
    s5Intro:
      'Funcția de corecție îmbunătățește precizia prognozei pe baza datelor reale de producție. Sistemul compară producția prognozată cu cea reală și calculează factori de corecție pe oră (UTC). Necesită opțiunea "correctionEnabled" în plan.',
    s5SendTitle: 'Trimiterea datelor reale',
    s5SendDesc: 'Trimite producția orară pentru o anumită zi la endpoint:',
    s5DateD: 'date — data în format YYYY-MM-DD',
    s5TzD: 'tz — fusul orar (decalaj UTC) folosit la citirea producției',
    s5ProdD: 'production — obiect: cheia este ora (string), valoarea este producția în Wh',
    s5EnableTitle: 'Activarea corecției în prognoză',
    s5EnableDesc:
      'Adaugă parametrul "correction=true" la cererea de prognoză sau setează corecția implicită în portalul clientului (fila Corecție).',
    s5Note:
      'Dacă parametrul "correction" nu este furnizat, sistemul folosește valoarea implicită a instalației tale.',
    s6Title: 'Exemple',
    ex1: 'Prognoză pentru azi (mod instalație)',
    ex2: 'Prognoză pentru mâine (mod instalație)',
    ex3: 'Prognoză cu configurație completă (2 stringuri)',
    ex4: 'Format grafic (pentru grafice)',
    ex5: 'Prognoză cu corecția producției',
    ex6: 'Gestionarea răspunsului (bash + jq)',
    ex7: 'Prognoză cu date în loc de today/tomorrow (date=full)',
  },
  bg: {
    title: 'Помощ — документация на API',
    subtitle:
      'Пълна документация на Solar Forecast API: режими, параметри, формати на отговор и кодове на грешки.',
    toc: 'Съдържание',
    example: 'Пример',
    exampleResponse: 'Примерен отговор',
    exampleError: 'Примерна грешка',
    s1Title: 'Как се използва API',
    s1Intro:
      'Forecast API поддържа два режима. И в двата API ключът трябва да се подаде в HTTP заглавка (името на заглавката е дефинирано в плана — по подразбиране X-API-Key).',
    s1Mode1Title: 'Режим 1: Инсталация (опростен)',
    s1Mode1Desc:
      'Всички параметри на инсталацията (местоположение, стрингове) се вземат автоматично от профила ви. Просто подайте параметъра "day".',
    s1Mode2Title: 'Режим 2: URL параметри (пълен контрол)',
    s1Mode2Desc:
      'Можете да подадете всички параметри директно в заявката. Изисква план с активирана опция "allowQueryParams". URL параметрите заменят тези от запазената инсталация.',
    s1Important:
      'Важно: параметърът "day" е задължителен и в двата режима. Разрешени стойности: "today" или "tomorrow".',
    s2Title: 'Параметри на заявката',
    s2Intro: 'По-долу е пълният списък с параметри на заявката за крайната точка GET {{endpoint}}.',
    thParam: 'Параметър',
    thType: 'Тип',
    thRequired: 'Задължителен',
    thDesc: 'Описание',
    reqYes: 'да',
    reqNo: 'не',
    dDay: 'Ден на прогнозата: "today" или "tomorrow"',
    dDate: 'Формат на ключовете за дни: "relative" (по подразбиране), "full" (ГГГГ-ММ-ДД) или "timestamp" (Unix секунди). При "full"/"timestamp" дневните суми се връщат в карта "totals" със същите ключове. Във формат chart се променят само сумите — часовите записи вече носят пълни ts времеви печати',
    dType: 'Формат на отговора: "standard" (по подразбиране) или "chart"',
    dCorrection:
      'Корекция на производството: "true" или "false". Ако е пропуснато, се използва стойността по подразбиране на инсталацията.',
    dLat: 'Географска ширина (напр. 52.23)',
    dLon: 'Географска дължина (напр. 21.01)',
    dTz: 'Отместване на часовата зона UTC (-12 до 12, напр. 2 за лятно CET)',
    dAz: 'Азимут на стринг {{n}} в градуси (-180 до 180; 0=север, 180=юг)',
    dTilt: 'Наклон на стринг {{n}} в градуси (0 до 90)',
    dPanels: 'Брой панели в стринг {{n}}',
    dPower: 'Мощност на панела в kW в стринг {{n}} (напр. 0.4 = 400W)',
    dEff: 'Ефективност на панела на стринг {{n}} в % (напр. 92)',
    s2Footnote:
      '* — Задължителен в режим на URL параметри. В режим на инсталация параметрите за местоположение и стрингове се вземат автоматично. Параметрите за местоположение (lat, lon, tz) и стрингове (az, t, n, s, e) изискват опцията "allowQueryParams" в плана.',
    s3Title: 'Формат на отговора',
    s3StdTitle: 'Стандартен отговор (type=standard)',
    s3StdDesc:
      'Формат по подразбиране — почасово производство във ватчасове (Wh) като обект ключ-стойност.',
    s3DateFmtTitle: 'Ключове за дни като дати (date=full / date=timestamp)',
    s3DateFmtDesc: 'С date=full ключовете за дни в result и strings стават дати ГГГГ-ММ-ДД, а дневните суми се връщат в карта "totals" със същите дати. date=timestamp работи по същия начин с Unix секунди (начало на деня в зоната tz). Важи и за блока "confidence" при enriched=1. Във формат chart се променят само сумите — часовите записи вече носят пълни ts времеви печати.',
    s3ChartTitle: 'Отговор за графика (type=chart)',
    s3ChartDesc:
      'Формат, оптимизиран за графики — масив от обекти с ISO времеви маркер и стойност на енергията.',
    s3FieldsTitle: 'Описание на полетата',
    thField: 'Поле',
    fDate: 'Времеви маркер на генерирането на отговора (ISO 8601)',
    fLatLon: 'Координати, използвани в заявката',
    fToday: 'Почасово производство за днес (ключ = час, стойност = Wh)',
    fTomorrow: 'Почасово производство за утре',
    fTotals: 'Общо производство за дадения ден (Wh); при date=full/timestamp се връща като карта "totals" с ключове за дни',
    fStrings:
      'Масив от данни за всеки стринг (азимут, ефективност, мощност, брой панели, производство)',
    fCorrApplied: 'Дали е приложена корекция на производството (true/false)',
    fProcTime: 'Време за обработка на заявката в ms',
    fWeather:
      'Метеорологичен контекст: почасова облачност, температура въздух/панели, вятър, валежи, сняг, код WMO, UV, изгрев/залез + дневно обобщение.',
    fWeatherAlerts: 'Активни предупреждения MeteoAlarm за локацията (празен масив, ако няма).',
    fProdAlerts:
      'Производствени сигнали от прогнозата: сняг по панелите, гъста облачност, силни пориви.',
    fAirQuality:
      'Качество на въздуха (PM10/PM2.5/прах/AOD) + оценка на замърсяването на панелите и препоръка за почистване.',
    fArchive:
      'Годишен енергиен профил на локацията от архива на реанализи: годишно облъчване, класиране по месеци, прогнозни оптимални наклон/азимут (best-effort, обновява се във фонов режим).',
    fConfidence:
      'Производствени ленти P10/P50/P90 от разсейването на ансамбловите модели (само когато са конфигурирани няколко модела).',
    s4Title: 'Кодове на грешки',
    s4Intro: 'При грешка API връща JSON обект с поле "error", съдържащо "code" и "message".',
    thCode: 'Код',
    thHttp: 'HTTP',
    eValidationError: 'Липсващи или невалидни параметри на заявката',
    eNoInstallation: 'Няма запазена инсталация и няма URL параметри',
    eApiKeyMissing: 'Няма API ключ в заглавката',
    eApiKeyInvalid: 'API ключът не съществува или е невалиден',
    eApiKeyExpired: 'API ключът е изтекъл',
    eApiKeyInactive: 'API ключът е деактивиран',
    ePlanLimitExceeded: 'Броят на стринговете надвишава лимита на плана',
    eQueryParamsNotAllowed: 'Планът не позволява динамични URL параметри',
    eRateLimitExceeded: 'Превишен лимит на заявки (rate limit)',
    eInternalError: 'Вътрешна грешка на сървъра',
    eServiceError: 'Грешка в услугата',
    s5Title: 'Корекция на производството',
    s5Intro:
      'Функцията за корекция подобрява точността на прогнозата въз основа на реални производствени данни. Системата сравнява прогнозираното производство с реалното и изчислява почасови (UTC) корекционни коефициенти. Изисква опцията "correctionEnabled" в плана.',
    s5SendTitle: 'Изпращане на реални данни',
    s5SendDesc: 'Изпратете почасовото производство за даден ден към крайната точка:',
    s5DateD: 'date — дата във формат YYYY-MM-DD',
    s5TzD: 'tz — часова зона (отместване UTC), използвана при четене на производството',
    s5ProdD: 'production — обект: ключът е часът (низ), стойността е производството в Wh',
    s5EnableTitle: 'Активиране на корекцията в прогнозата',
    s5EnableDesc:
      'Добавете параметъра "correction=true" към заявката за прогноза или задайте корекцията по подразбиране в клиентския портал (раздел Корекция).',
    s5Note:
      'Ако параметърът "correction" не е предоставен, системата използва стойността по подразбиране на вашата инсталация.',
    s6Title: 'Примери',
    ex1: 'Прогноза за днес (режим инсталация)',
    ex2: 'Прогноза за утре (режим инсталация)',
    ex3: 'Прогноза с пълна конфигурация (2 стринга)',
    ex4: 'Формат графика (за графики)',
    ex5: 'Прогноза с корекция на производството',
    ex6: 'Обработка на отговора (bash + jq)',
    ex7: 'Прогноза с дати вместо today/tomorrow (date=full)',
  },
  el: {
    title: 'Βοήθεια — τεκμηρίωση API',
    subtitle:
      'Πλήρης τεκμηρίωση Solar Forecast API: λειτουργίες, παράμετροι, μορφές απάντησης και κωδικοί σφαλμάτων.',
    toc: 'Πίνακας περιεχομένων',
    example: 'Παράδειγμα',
    exampleResponse: 'Παράδειγμα απάντησης',
    exampleError: 'Παράδειγμα σφάλματος',
    s1Title: 'Πώς να χρησιμοποιήσετε το API',
    s1Intro:
      'Το Forecast API υποστηρίζει δύο λειτουργίες. Και στις δύο, το κλειδί API πρέπει να περαστεί σε κεφαλίδα HTTP (το όνομα της κεφαλίδας ορίζεται στο πρόγραμμα — προεπιλογή X-API-Key).',
    s1Mode1Title: 'Λειτουργία 1: Εγκατάσταση (απλοποιημένη)',
    s1Mode1Desc:
      'Όλες οι παράμετροι της εγκατάστασης (τοποθεσία, strings) λαμβάνονται αυτόματα από το προφίλ σας. Απλώς περάστε την παράμετρο "day".',
    s1Mode2Title: 'Λειτουργία 2: Παράμετροι URL (πλήρης έλεγχος)',
    s1Mode2Desc:
      'Μπορείτε να περάσετε όλες τις παραμέτρους απευθείας στο query string. Απαιτεί πρόγραμμα με ενεργοποιημένη την επιλογή "allowQueryParams". Οι παράμετροι URL υπερισχύουν εκείνων από την αποθηκευμένη εγκατάσταση.',
    s1Important:
      'Σημαντικό: η παράμετρος "day" είναι υποχρεωτική και στις δύο λειτουργίες. Επιτρεπόμενες τιμές: "today" ή "tomorrow".',
    s2Title: 'Παράμετροι ερωτήματος',
    s2Intro:
      'Παρακάτω είναι η πλήρης λίστα των παραμέτρων του query string για το endpoint GET {{endpoint}}.',
    thParam: 'Παράμετρος',
    thType: 'Τύπος',
    thRequired: 'Υποχρεωτικό',
    thDesc: 'Περιγραφή',
    reqYes: 'ναι',
    reqNo: 'όχι',
    dDay: 'Ημέρα πρόβλεψης: "today" ή "tomorrow"',
    dDate: 'Μορφή κλειδιών ημερών: "relative" (προεπιλογή), "full" (ΕΕΕΕ-ΜΜ-ΗΗ) ή "timestamp" (δευτερόλεπτα Unix). Με "full"/"timestamp" τα ημερήσια σύνολα επιστρέφονται σε χάρτη "totals" με τα ίδια κλειδιά. Στη μορφή chart αλλάζουν μόνο τα σύνολα — οι ωριαίες εγγραφές φέρουν ήδη πλήρεις χρονοσφραγίδες ts',
    dType: 'Μορφή απάντησης: "standard" (προεπιλογή) ή "chart"',
    dCorrection:
      'Διόρθωση παραγωγής: "true" ή "false". Αν παραλειφθεί, χρησιμοποιείται η προεπιλεγμένη τιμή της εγκατάστασης.',
    dLat: 'Γεωγραφικό πλάτος (π.χ. 52.23)',
    dLon: 'Γεωγραφικό μήκος (π.χ. 21.01)',
    dTz: 'Μετατόπιση ζώνης ώρας UTC (-12 έως 12, π.χ. 2 για θερινή CET)',
    dAz: 'Αζιμούθιο του string {{n}} σε μοίρες (-180 έως 180· 0=βορράς, 180=νότος)',
    dTilt: 'Κλίση του string {{n}} σε μοίρες (0 έως 90)',
    dPanels: 'Αριθμός πάνελ στο string {{n}}',
    dPower: 'Ισχύς πάνελ σε kW στο string {{n}} (π.χ. 0.4 = 400W)',
    dEff: 'Απόδοση πάνελ του string {{n}} σε % (π.χ. 92)',
    s2Footnote:
      '* — Υποχρεωτικό σε λειτουργία παραμέτρων URL. Σε λειτουργία εγκατάστασης, οι παράμετροι τοποθεσίας και strings λαμβάνονται αυτόματα. Οι παράμετροι τοποθεσίας (lat, lon, tz) και τα strings (az, t, n, s, e) απαιτούν την επιλογή "allowQueryParams" στο πρόγραμμα.',
    s3Title: 'Μορφή απάντησης',
    s3StdTitle: 'Τυπική απάντηση (type=standard)',
    s3StdDesc:
      'Προεπιλεγμένη μορφή — ωριαία παραγωγή σε βατώρες (Wh) ως αντικείμενο κλειδιού-τιμής.',
    s3DateFmtTitle: 'Κλειδιά ημερών ως ημερομηνίες (date=full / date=timestamp)',
    s3DateFmtDesc: 'Με date=full τα κλειδιά ημερών στα result και strings γίνονται ημερομηνίες ΕΕΕΕ-ΜΜ-ΗΗ και τα ημερήσια σύνολα επιστρέφονται σε χάρτη "totals" με τις ίδιες ημερομηνίες. Το date=timestamp λειτουργεί αντίστοιχα με δευτερόλεπτα Unix (αρχή ημέρας στη ζώνη tz). Ισχύει και για το μπλοκ "confidence" με enriched=1. Στη μορφή chart αλλάζουν μόνο τα σύνολα — οι ωριαίες εγγραφές φέρουν ήδη πλήρεις χρονοσφραγίδες ts.',
    s3ChartTitle: 'Απάντηση γραφήματος (type=chart)',
    s3ChartDesc:
      'Μορφή βελτιστοποιημένη για γραφήματα — ένας πίνακας αντικειμένων με χρονοσφραγίδα ISO και τιμή ενέργειας.',
    s3FieldsTitle: 'Περιγραφές πεδίων',
    thField: 'Πεδίο',
    fDate: 'Χρονοσφραγίδα δημιουργίας απάντησης (ISO 8601)',
    fLatLon: 'Συντεταγμένες που χρησιμοποιήθηκαν στο αίτημα',
    fToday: 'Ωριαία παραγωγή για σήμερα (κλειδί = ώρα, τιμή = Wh)',
    fTomorrow: 'Ωριαία παραγωγή για αύριο',
    fTotals: 'Συνολική παραγωγή για τη δεδομένη ημέρα (Wh); με date=full/timestamp επιστρέφεται ως χάρτης "totals" με κλειδιά ημερών',
    fStrings: 'Πίνακας δεδομένων ανά string (αζιμούθιο, απόδοση, ισχύς, αριθμός πάνελ, παραγωγή)',
    fCorrApplied: 'Αν εφαρμόστηκε διόρθωση παραγωγής (true/false)',
    fProcTime: 'Χρόνος επεξεργασίας αιτήματος σε ms',
    fWeather:
      'Μετεωρολογικό πλαίσιο: ωριαία νέφωση, θερμοκρασία αέρα/πάνελ, άνεμος, βροχόπτωση, χιόνι, κωδικός WMO, UV, ανατολή/δύση + ημερήσια σύνοψη.',
    fWeatherAlerts:
      'Ενεργές προειδοποιήσεις MeteoAlarm για την τοποθεσία (κενός πίνακας αν δεν υπάρχουν).',
    fProdAlerts:
      'Ειδοποιήσεις παραγωγής από την πρόγνωση: χιόνι στα πάνελ, πυκνή νέφωση, ισχυρές ριπές.',
    fAirQuality:
      'Ποιότητα αέρα (PM10/PM2.5/σκόνη/AOD) + αξιολόγηση ρύπανσης πάνελ και σύσταση καθαρισμού.',
    fArchive:
      'Ετήσιο ενεργειακό προφίλ της τοποθεσίας από το αρχείο επανανάλυσης: ετήσια ακτινοβολία, μηνιαία κατάταξη, εκτιμώμενη βέλτιστη κλίση/αζιμούθιο (best-effort, ανανεώνεται στο παρασκήνιο).',
    fConfidence:
      'Ζώνες παραγωγής P10/P50/P90 από τη διασπορά των μοντέλων ensemble (μόνο όταν έχουν ρυθμιστεί πολλά μοντέλα).',
    s4Title: 'Κωδικοί σφαλμάτων',
    s4Intro:
      'Σε περίπτωση σφάλματος, το API επιστρέφει ένα αντικείμενο JSON με ένα πεδίο "error" που περιέχει "code" και "message".',
    thCode: 'Κωδικός',
    thHttp: 'HTTP',
    eValidationError: 'Παράμετροι ερωτήματος που λείπουν ή είναι μη έγκυρες',
    eNoInstallation: 'Δεν υπάρχει αποθηκευμένη εγκατάσταση ούτε παράμετροι URL',
    eApiKeyMissing: 'Δεν υπάρχει κλειδί API στην κεφαλίδα',
    eApiKeyInvalid: 'Το κλειδί API δεν υπάρχει ή είναι μη έγκυρο',
    eApiKeyExpired: 'Το κλειδί API έχει λήξει',
    eApiKeyInactive: 'Το κλειδί API έχει απενεργοποιηθεί',
    ePlanLimitExceeded: 'Ο αριθμός των strings υπερβαίνει το όριο του προγράμματος',
    eQueryParamsNotAllowed: 'Το πρόγραμμα δεν επιτρέπει δυναμικές παραμέτρους URL',
    eRateLimitExceeded: 'Υπέρβαση ορίου αιτημάτων (rate limit)',
    eInternalError: 'Εσωτερικό σφάλμα διακομιστή',
    eServiceError: 'Σφάλμα υπηρεσίας',
    s5Title: 'Διόρθωση παραγωγής',
    s5Intro:
      'Η λειτουργία διόρθωσης βελτιώνει την ακρίβεια της πρόβλεψης με βάση πραγματικά δεδομένα παραγωγής. Το σύστημα συγκρίνει την προβλεπόμενη παραγωγή με την πραγματική και υπολογίζει συντελεστές διόρθωσης ανά ώρα (UTC). Απαιτεί την επιλογή "correctionEnabled" στο πρόγραμμα.',
    s5SendTitle: 'Υποβολή πραγματικών δεδομένων',
    s5SendDesc: 'Στείλτε την ωριαία παραγωγή για μια δεδομένη ημέρα στο endpoint:',
    s5DateD: 'date — ημερομηνία σε μορφή YYYY-MM-DD',
    s5TzD: 'tz — ζώνη ώρας (μετατόπιση UTC) που χρησιμοποιείται κατά την ανάγνωση της παραγωγής',
    s5ProdD:
      'production — αντικείμενο: το κλειδί είναι η ώρα (συμβολοσειρά), η τιμή είναι η παραγωγή σε Wh',
    s5EnableTitle: 'Ενεργοποίηση της διόρθωσης στην πρόβλεψη',
    s5EnableDesc:
      'Προσθέστε την παράμετρο "correction=true" στο αίτημα πρόβλεψης ή ορίστε την προεπιλεγμένη διόρθωση στην πύλη πελάτη (καρτέλα Διόρθωση).',
    s5Note:
      'Αν η παράμετρος "correction" δεν παρέχεται, το σύστημα χρησιμοποιεί την προεπιλεγμένη τιμή της εγκατάστασής σας.',
    s6Title: 'Παραδείγματα',
    ex1: 'Πρόβλεψη για σήμερα (λειτουργία εγκατάστασης)',
    ex2: 'Πρόβλεψη για αύριο (λειτουργία εγκατάστασης)',
    ex3: 'Πρόβλεψη με πλήρη διαμόρφωση (2 strings)',
    ex4: 'Μορφή γραφήματος (για γραφήματα)',
    ex5: 'Πρόβλεψη με διόρθωση παραγωγής',
    ex6: 'Διαχείριση της απάντησης (bash + jq)',
    ex7: 'Πρόβλεψη με ημερομηνίες αντί για today/tomorrow (date=full)',
  },
  et: {
    title: 'Abi — API dokumentatsioon',
    subtitle:
      'Solar Forecast API täielik dokumentatsioon: režiimid, parameetrid, vastusvormingud ja veakoodid.',
    toc: 'Sisukord',
    example: 'Näide',
    exampleResponse: 'Näidisvastus',
    exampleError: 'Näidisviga',
    s1Title: 'Kuidas API-t kasutada',
    s1Intro:
      'Forecast API toetab kahte režiimi. Mõlemas tuleb API-võti edastada HTTP-päises (päise nimi on määratud paketis — vaikimisi X-API-Key).',
    s1Mode1Title: 'Režiim 1: Paigaldis (lihtsustatud)',
    s1Mode1Desc:
      'Kõik paigaldise parameetrid (asukoht, stringid) võetakse automaatselt sinu profiilist. Edasta lihtsalt parameeter "day".',
    s1Mode2Title: 'Režiim 2: URL-parameetrid (täielik kontroll)',
    s1Mode2Desc:
      'Kõik parameetrid saab edastada otse päringustringis. Nõuab paketti, kus on lubatud valik "allowQueryParams". URL-parameetrid kirjutavad üle salvestatud paigaldise omad.',
    s1Important:
      'Tähtis: parameeter "day" on mõlemas režiimis kohustuslik. Lubatud väärtused: "today" või "tomorrow".',
    s2Title: 'Päringuparameetrid',
    s2Intro: 'Allpool on täielik loend GET {{endpoint}} lõpp-punkti päringustringi parameetritest.',
    thParam: 'Parameeter',
    thType: 'Tüüp',
    thRequired: 'Kohustuslik',
    thDesc: 'Kirjeldus',
    reqYes: 'jah',
    reqNo: 'ei',
    dDay: 'Prognoosi päev: "today" või "tomorrow"',
    dDate: 'Päevavõtmete vorming: "relative" (vaikimisi), "full" (AAAA-KK-PP) või "timestamp" (Unixi sekundid). "full"/"timestamp" korral tagastatakse päevasummad "totals" kaardis samade võtmetega. Chart-vormingus muutuvad ainult summad — tunnikirjed kannavad juba täielikke ts-ajatempleid',
    dType: 'Vastusvorming: "standard" (vaikimisi) või "chart"',
    dCorrection:
      'Tootmise korrektsioon: "true" või "false". Kui välja jätta, kasutatakse paigaldise vaikeväärtust.',
    dLat: 'Laiuskraad (nt 52.23)',
    dLon: 'Pikkuskraad (nt 21.01)',
    dTz: 'UTC ajavööndi nihe (-12 kuni 12, nt 2 suvise CET jaoks)',
    dAz: 'Stringi {{n}} asimuut kraadides (-180 kuni 180; 0=põhi, 180=lõuna)',
    dTilt: 'Stringi {{n}} kalle kraadides (0 kuni 90)',
    dPanels: 'Paneelide arv stringis {{n}}',
    dPower: 'Paneeli võimsus kW stringis {{n}} (nt 0.4 = 400W)',
    dEff: 'Stringi {{n}} paneeli efektiivsus % (nt 92)',
    s2Footnote:
      '* — Kohustuslik URL-parameetrite režiimis. Paigaldise režiimis võetakse asukoha ja stringi parameetrid automaatselt. Asukoha parameetrid (lat, lon, tz) ja stringid (az, t, n, s, e) nõuavad paketis valikut "allowQueryParams".',
    s3Title: 'Vastusvorming',
    s3StdTitle: 'Standardvastus (type=standard)',
    s3StdDesc: 'Vaikevorming — tunnipõhine tootmine vatt-tundides (Wh) võti-väärtus objektina.',
    s3DateFmtTitle: 'Päevavõtmed kuupäevadena (date=full / date=timestamp)',
    s3DateFmtDesc: 'date=full korral muutuvad result ja strings päevavõtmed AAAA-KK-PP kuupäevadeks ning päevasummad tagastatakse "totals" kaardis samade kuupäevadega. date=timestamp töötab samamoodi Unixi sekunditega (päeva algus tz vööndis). Kehtib ka "confidence" ploki kohta, kui enriched=1. Chart-vormingus muutuvad ainult summad — tunnikirjed kannavad juba täielikke ts-ajatempleid.',
    s3ChartTitle: 'Graafiku vastus (type=chart)',
    s3ChartDesc:
      'Graafikutele optimeeritud vorming — objektide massiiv ISO ajatempli ja energiaväärtusega.',
    s3FieldsTitle: 'Väljade kirjeldused',
    thField: 'Väli',
    fDate: 'Vastuse genereerimise ajatempel (ISO 8601)',
    fLatLon: 'Päringus kasutatud koordinaadid',
    fToday: 'Tänane tunnipõhine tootmine (võti = tund, väärtus = Wh)',
    fTomorrow: 'Homne tunnipõhine tootmine',
    fTotals: 'Antud päeva kogutootmine (Wh); date=full/timestamp korral tagastatakse "totals" kaardina päevavõtmetega',
    fStrings:
      'Stringipõhiste andmete massiiv (asimuut, efektiivsus, võimsus, paneelide arv, tootmine)',
    fCorrApplied: 'Kas tootmise korrektsioon rakendati (true/false)',
    fProcTime: 'Päringu töötlemise aeg ms',
    fWeather:
      'Ilmakontekst: tunnipõhine pilvisus, õhu-/paneelitemperatuur, tuul, sademed, lumi, WMO-kood, UV, päikesetõus/-loojang + päevakokkuvõte.',
    fWeatherAlerts: 'Asukoha aktiivsed MeteoAlarmi hoiatused (tühi massiiv, kui pole).',
    fProdAlerts:
      'Prognoosist tuletatud tootmishoiatused: lumi paneelidel, tihe pilvisus, tugevad puhangud.',
    fAirQuality:
      'Õhukvaliteet (PM10/PM2.5/tolm/AOD) + paneelide määrdumise hinnang ja puhastussoovitus.',
    fArchive:
      'Asukoha aastane energiaprofiil reanalüüsi arhiivist: aastane kiirgus, kuude pingerida, hinnanguline optimaalne kalle/asimuut (best-effort, värskendatakse taustal).',
    fConfidence:
      'Tootmisvahemikud P10/P50/P90 ansamblimudelite hajuvusest (ainult kui on seadistatud mitu mudelit).',
    s4Title: 'Veakoodid',
    s4Intro:
      'Vea korral tagastab API JSON-objekti väljaga "error", mis sisaldab välju "code" ja "message".',
    thCode: 'Kood',
    thHttp: 'HTTP',
    eValidationError: 'Puuduvad või vigased päringuparameetrid',
    eNoInstallation: 'Pole salvestatud paigaldist ega URL-parameetreid',
    eApiKeyMissing: 'Päises pole API-võtit',
    eApiKeyInvalid: 'API-võtit ei ole olemas või see on vigane',
    eApiKeyExpired: 'API-võti on aegunud',
    eApiKeyInactive: 'API-võti on deaktiveeritud',
    ePlanLimitExceeded: 'Stringide arv ületab paketi piiri',
    eQueryParamsNotAllowed: 'Pakett ei luba dünaamilisi URL-parameetreid',
    eRateLimitExceeded: 'Päringupiir ületatud (rate limit)',
    eInternalError: 'Sisemine serveriviga',
    eServiceError: 'Teenuse viga',
    s5Title: 'Tootmise korrektsioon',
    s5Intro:
      'Korrektsioonifunktsioon parandab prognoosi täpsust tegelike tootmisandmete põhjal. Süsteem võrdleb prognoositud tootmist tegelikuga ja arvutab tunnipõhised (UTC) korrektsioonitegurid. Nõuab paketis valikut "correctionEnabled".',
    s5SendTitle: 'Tegelike andmete esitamine',
    s5SendDesc: 'Saada antud päeva tunnipõhine tootmine lõpp-punkti:',
    s5DateD: 'date — kuupäev formaadis YYYY-MM-DD',
    s5TzD: 'tz — ajavöönd (UTC nihe), mida kasutatakse tootmise lugemisel',
    s5ProdD: 'production — objekt: võti on tund (string), väärtus on tootmine Wh-des',
    s5EnableTitle: 'Korrektsiooni lubamine prognoosis',
    s5EnableDesc:
      'Lisa parameeter "correction=true" prognoosipäringusse või määra vaikekorrektsioon kliendiportaalis (Korrektsiooni vahekaart).',
    s5Note:
      'Kui parameetrit "correction" ei esitata, kasutab süsteem sinu paigaldise vaikeväärtust.',
    s6Title: 'Näited',
    ex1: 'Tänane prognoos (paigaldise režiim)',
    ex2: 'Homne prognoos (paigaldise režiim)',
    ex3: 'Prognoos täieliku konfiguratsiooniga (2 stringi)',
    ex4: 'Graafiku vorming (graafikute jaoks)',
    ex5: 'Prognoos tootmise korrektsiooniga',
    ex6: 'Vastuse käsitlemine (bash + jq)',
    ex7: 'Prognoos kuupäevadega today/tomorrow asemel (date=full)',
  },
  lv: {
    title: 'Palīdzība — API dokumentācija',
    subtitle:
      'Pilna Solar Forecast API dokumentācija: režīmi, parametri, atbildes formāti un kļūdu kodi.',
    toc: 'Saturs',
    example: 'Piemērs',
    exampleResponse: 'Atbildes piemērs',
    exampleError: 'Kļūdas piemērs',
    s1Title: 'Kā lietot API',
    s1Intro:
      'Forecast API atbalsta divus režīmus. Abos API atslēga jānodod HTTP galvenē (galvenes nosaukums ir definēts plānā — pēc noklusējuma X-API-Key).',
    s1Mode1Title: '1. režīms: Iekārta (vienkāršots)',
    s1Mode1Desc:
      'Visi iekārtas parametri (atrašanās vieta, virknes) tiek automātiski ņemti no jūsu profila. Vienkārši nododiet parametru "day".',
    s1Mode2Title: '2. režīms: URL parametri (pilna kontrole)',
    s1Mode2Desc:
      'Visus parametrus var nodot tieši vaicājuma virknē. Nepieciešams plāns ar iespējotu opciju "allowQueryParams". URL parametri pārraksta saglabātās iekārtas parametrus.',
    s1Important:
      'Svarīgi: parametrs "day" ir obligāts abos režīmos. Atļautās vērtības: "today" vai "tomorrow".',
    s2Title: 'Vaicājuma parametri',
    s2Intro: 'Zemāk ir pilns GET {{endpoint}} galapunkta vaicājuma virknes parametru saraksts.',
    thParam: 'Parametrs',
    thType: 'Tips',
    thRequired: 'Obligāts',
    thDesc: 'Apraksts',
    reqYes: 'jā',
    reqNo: 'nē',
    dDay: 'Prognozes diena: "today" vai "tomorrow"',
    dDate: 'Dienu atslēgu formāts: "relative" (noklusējums), "full" (GGGG-MM-DD) vai "timestamp" (Unix sekundes). Ar "full"/"timestamp" dienas kopsummas tiek atgrieztas kartē "totals" ar tām pašām atslēgām. Chart formātā mainās tikai kopsummas — stundu ieraksti jau satur pilnus ts laika zīmogus',
    dType: 'Atbildes formāts: "standard" (noklusējums) vai "chart"',
    dCorrection:
      'Ražošanas korekcija: "true" vai "false". Ja izlaists, tiek izmantota iekārtas noklusējuma vērtība.',
    dLat: 'Platums (piem., 52.23)',
    dLon: 'Garums (piem., 21.01)',
    dTz: 'UTC laika joslas nobīde (-12 līdz 12, piem., 2 vasaras CET)',
    dAz: 'Virknes {{n}} azimuts grādos (-180 līdz 180; 0=ziemeļi, 180=dienvidi)',
    dTilt: 'Virknes {{n}} slīpums grādos (0 līdz 90)',
    dPanels: 'Paneļu skaits virknē {{n}}',
    dPower: 'Paneļa jauda kW virknē {{n}} (piem., 0.4 = 400W)',
    dEff: 'Virknes {{n}} paneļa efektivitāte % (piem., 92)',
    s2Footnote:
      '* — Obligāts URL parametru režīmā. Iekārtas režīmā atrašanās vietas un virkņu parametri tiek ņemti automātiski. Atrašanās vietas parametri (lat, lon, tz) un virknes (az, t, n, s, e) prasa opciju "allowQueryParams" plānā.',
    s3Title: 'Atbildes formāts',
    s3StdTitle: 'Standarta atbilde (type=standard)',
    s3StdDesc: 'Noklusējuma formāts — stundas ražošana vatstundās (Wh) kā atslēga-vērtība objekts.',
    s3DateFmtTitle: 'Dienu atslēgas kā datumi (date=full / date=timestamp)',
    s3DateFmtDesc: 'Ar date=full dienu atslēgas result un strings kļūst par GGGG-MM-DD datumiem, un dienas kopsummas tiek atgrieztas kartē "totals" ar tiem pašiem datumiem. date=timestamp darbojas tāpat ar Unix sekundēm (dienas sākums tz joslā). Attiecas arī uz "confidence" bloku ar enriched=1. Chart formātā mainās tikai kopsummas — stundu ieraksti jau satur pilnus ts laika zīmogus.',
    s3ChartTitle: 'Diagrammas atbilde (type=chart)',
    s3ChartDesc:
      'Formāts, kas optimizēts diagrammām — objektu masīvs ar ISO laika zīmogu un enerģijas vērtību.',
    s3FieldsTitle: 'Lauku apraksti',
    thField: 'Lauks',
    fDate: 'Atbildes ģenerēšanas laika zīmogs (ISO 8601)',
    fLatLon: 'Pieprasījumā izmantotās koordinātes',
    fToday: 'Šodienas stundas ražošana (atslēga = stunda, vērtība = Wh)',
    fTomorrow: 'Rītdienas stundas ražošana',
    fTotals: 'Kopējā ražošana attiecīgajā dienā (Wh); ar date=full/timestamp tiek atgriezts kā karte "totals" ar dienu atslēgām',
    fStrings: 'Datu masīvs katrai virknei (azimuts, efektivitāte, jauda, paneļu skaits, ražošana)',
    fCorrApplied: 'Vai tika piemērota ražošanas korekcija (true/false)',
    fProcTime: 'Pieprasījuma apstrādes laiks ms',
    fWeather:
      'Laikapstākļu konteksts: stundu mākoņainība, gaisa/paneļu temperatūra, vējš, nokrišņi, sniegs, WMO kods, UV, saullēkts/saulriets + dienas kopsavilkums.',
    fWeatherAlerts: 'Atrašanās vietas aktīvie MeteoAlarm brīdinājumi (tukšs masīvs, ja nav).',
    fProdAlerts:
      'No prognozes atvasinātie ražošanas brīdinājumi: sniegs uz paneļiem, bieza mākoņainība, spēcīgas brāzmas.',
    fAirQuality:
      'Gaisa kvalitāte (PM10/PM2.5/putekļi/AOD) + paneļu piesārņojuma novērtējums un tīrīšanas ieteikums.',
    fArchive:
      'Atrašanās vietas gada enerģijas profils no reanalīzes arhīva: gada starojums, mēnešu reitings, aptuvenais optimālais slīpums/azimuts (best-effort, atjaunina fonā).',
    fConfidence:
      'Ražošanas joslas P10/P50/P90 no ansambļa modeļu izkliedes (tikai tad, ja konfigurēti vairāki modeļi).',
    s4Title: 'Kļūdu kodi',
    s4Intro:
      'Kļūdas gadījumā API atgriež JSON objektu ar lauku "error", kas satur "code" un "message".',
    thCode: 'Kods',
    thHttp: 'HTTP',
    eValidationError: 'Trūkstoši vai nederīgi vaicājuma parametri',
    eNoInstallation: 'Nav saglabātas iekārtas un nav URL parametru',
    eApiKeyMissing: 'Galvenē nav API atslēgas',
    eApiKeyInvalid: 'API atslēga neeksistē vai ir nederīga',
    eApiKeyExpired: 'API atslēgai beidzies derīguma termiņš',
    eApiKeyInactive: 'API atslēga ir deaktivizēta',
    ePlanLimitExceeded: 'Virkņu skaits pārsniedz plāna ierobežojumu',
    eQueryParamsNotAllowed: 'Plāns neatļauj dinamiskus URL parametrus',
    eRateLimitExceeded: 'Pārsniegts pieprasījumu ierobežojums (rate limit)',
    eInternalError: 'Iekšēja servera kļūda',
    eServiceError: 'Pakalpojuma kļūda',
    s5Title: 'Ražošanas korekcija',
    s5Intro:
      'Korekcijas funkcija uzlabo prognozes precizitāti, pamatojoties uz faktiskajiem ražošanas datiem. Sistēma salīdzina prognozēto ražošanu ar faktisko un aprēķina korekcijas koeficientus pa stundām (UTC). Nepieciešama opcija "correctionEnabled" plānā.',
    s5SendTitle: 'Faktisko datu iesniegšana',
    s5SendDesc: 'Nosūtiet attiecīgās dienas stundas ražošanu uz galapunktu:',
    s5DateD: 'date — datums formātā YYYY-MM-DD',
    s5TzD: 'tz — laika josla (UTC nobīde), ko izmanto, lasot ražošanu',
    s5ProdD: 'production — objekts: atslēga ir stunda (virkne), vērtība ir ražošana Wh',
    s5EnableTitle: 'Korekcijas iespējošana prognozē',
    s5EnableDesc:
      'Pievienojiet parametru "correction=true" prognozes pieprasījumam vai iestatiet noklusējuma korekciju klienta portālā (cilne Korekcija).',
    s5Note:
      'Ja parametrs "correction" nav norādīts, sistēma izmanto jūsu iekārtas noklusējuma vērtību.',
    s6Title: 'Piemēri',
    ex1: 'Šodienas prognoze (iekārtas režīms)',
    ex2: 'Rītdienas prognoze (iekārtas režīms)',
    ex3: 'Prognoze ar pilnu konfigurāciju (2 virknes)',
    ex4: 'Diagrammas formāts (diagrammām)',
    ex5: 'Prognoze ar ražošanas korekciju',
    ex6: 'Atbildes apstrāde (bash + jq)',
    ex7: 'Prognoze ar datumiem today/tomorrow vietā (date=full)',
  },
  lt: {
    title: 'Pagalba — API dokumentacija',
    subtitle:
      'Visa Solar Forecast API dokumentacija: režimai, parametrai, atsako formatai ir klaidų kodai.',
    toc: 'Turinys',
    example: 'Pavyzdys',
    exampleResponse: 'Atsako pavyzdys',
    exampleError: 'Klaidos pavyzdys',
    s1Title: 'Kaip naudoti API',
    s1Intro:
      'Forecast API palaiko du režimus. Abiejuose API raktas turi būti perduodamas HTTP antraštėje (antraštės pavadinimas apibrėžtas plane — numatytuoju X-API-Key).',
    s1Mode1Title: '1 režimas: Įrenginys (supaprastintas)',
    s1Mode1Desc:
      'Visi įrenginio parametrai (vieta, stringai) automatiškai paimami iš jūsų profilio. Tiesiog perduokite parametrą "day".',
    s1Mode2Title: '2 režimas: URL parametrai (visiška kontrolė)',
    s1Mode2Desc:
      'Visus parametrus galite perduoti tiesiogiai užklausos eilutėje. Reikalingas planas su įjungta parinktimi "allowQueryParams". URL parametrai pakeičia įrašyto įrenginio parametrus.',
    s1Important:
      'Svarbu: parametras "day" privalomas abiejuose režimuose. Leistinos reikšmės: "today" arba "tomorrow".',
    s2Title: 'Užklausos parametrai',
    s2Intro:
      'Žemiau pateikiamas visas GET {{endpoint}} galutinio taško užklausos eilutės parametrų sąrašas.',
    thParam: 'Parametras',
    thType: 'Tipas',
    thRequired: 'Privalomas',
    thDesc: 'Aprašymas',
    reqYes: 'taip',
    reqNo: 'ne',
    dDay: 'Prognozės diena: "today" arba "tomorrow"',
    dDate: 'Dienų raktų formatas: "relative" (numatytasis), "full" (YYYY-MM-DD) arba "timestamp" (Unix sekundės). Su "full"/"timestamp" dienos sumos grąžinamos žemėlapyje "totals" su tais pačiais raktais. Chart formate keičiasi tik sumos — valandiniai įrašai jau turi pilnas ts laiko žymas',
    dType: 'Atsako formatas: "standard" (numatytasis) arba "chart"',
    dCorrection:
      'Gamybos korekcija: "true" arba "false". Jei praleista, naudojama įrenginio numatytoji reikšmė.',
    dLat: 'Platuma (pvz., 52.23)',
    dLon: 'Ilguma (pvz., 21.01)',
    dTz: 'UTC laiko juostos poslinkis (-12 iki 12, pvz., 2 vasaros CET)',
    dAz: 'Stringo {{n}} azimutas laipsniais (-180 iki 180; 0=šiaurė, 180=pietūs)',
    dTilt: 'Stringo {{n}} pakrypimas laipsniais (0 iki 90)',
    dPanels: 'Plokščių skaičius stringe {{n}}',
    dPower: 'Plokštės galia kW stringe {{n}} (pvz., 0.4 = 400W)',
    dEff: 'Stringo {{n}} plokštės efektyvumas % (pvz., 92)',
    s2Footnote:
      '* — Privalomas URL parametrų režime. Įrenginio režime vietos ir stringo parametrai paimami automatiškai. Vietos parametrai (lat, lon, tz) ir stringai (az, t, n, s, e) reikalauja parinkties "allowQueryParams" plane.',
    s3Title: 'Atsako formatas',
    s3StdTitle: 'Standartinis atsakas (type=standard)',
    s3StdDesc:
      'Numatytasis formatas — valandinė gamyba vatvalandėmis (Wh) kaip raktas-reikšmė objektas.',
    s3DateFmtTitle: 'Dienų raktai kaip datos (date=full / date=timestamp)',
    s3DateFmtDesc: 'Su date=full dienų raktai result ir strings tampa YYYY-MM-DD datomis, o dienos sumos grąžinamos žemėlapyje "totals" su tomis pačiomis datomis. date=timestamp veikia taip pat su Unix sekundėmis (paros pradžia tz juostoje). Taip pat taikoma "confidence" blokui su enriched=1. Chart formate keičiasi tik sumos — valandiniai įrašai jau turi pilnas ts laiko žymas.',
    s3ChartTitle: 'Diagramos atsakas (type=chart)',
    s3ChartDesc:
      'Diagramoms optimizuotas formatas — objektų masyvas su ISO laiko žyma ir energijos reikšme.',
    s3FieldsTitle: 'Laukų aprašymai',
    thField: 'Laukas',
    fDate: 'Atsako generavimo laiko žyma (ISO 8601)',
    fLatLon: 'Užklausoje naudotos koordinatės',
    fToday: 'Šios dienos valandinė gamyba (raktas = valanda, reikšmė = Wh)',
    fTomorrow: 'Rytojaus valandinė gamyba',
    fTotals: 'Bendra gamyba nurodytą dieną (Wh); su date=full/timestamp grąžinama kaip žemėlapis "totals" su dienų raktais',
    fStrings:
      'Duomenų masyvas kiekvienam stringui (azimutas, efektyvumas, galia, plokščių skaičius, gamyba)',
    fCorrApplied: 'Ar buvo taikoma gamybos korekcija (true/false)',
    fProcTime: 'Užklausos apdorojimo laikas ms',
    fWeather:
      'Orų kontekstas: valandinis debesuotumas, oro/plokščių temperatūra, vėjas, krituliai, sniegas, WMO kodas, UV, saulėtekis/saulėlydis + dienos santrauka.',
    fWeatherAlerts: 'Vietos aktyvūs MeteoAlarm įspėjimai (tuščias masyvas, jei nėra).',
    fProdAlerts:
      'Iš prognozės gauti gamybos įspėjimai: sniegas ant plokščių, tankus debesuotumas, stiprūs gūsiai.',
    fAirQuality:
      'Oro kokybė (PM10/PM2.5/dulkės/AOD) + plokščių užterštumo vertinimas ir valymo rekomendacija.',
    fArchive:
      'Vietos metinis energijos profilis iš pakartotinės analizės archyvo: metinė apšvieta, mėnesių reitingas, apytikslis optimalus pasvirimas/azimutas (best-effort, atnaujinama fone).',
    fConfidence:
      'Gamybos juostos P10/P50/P90 iš ansamblio modelių sklaidos (tik kai sukonfigūruoti keli modeliai).',
    s4Title: 'Klaidų kodai',
    s4Intro:
      'Įvykus klaidai, API grąžina JSON objektą su lauku "error", kuriame yra "code" ir "message".',
    thCode: 'Kodas',
    thHttp: 'HTTP',
    eValidationError: 'Trūksta arba netinkami užklausos parametrai',
    eNoInstallation: 'Nėra įrašyto įrenginio ir nėra URL parametrų',
    eApiKeyMissing: 'Antraštėje nėra API rakto',
    eApiKeyInvalid: 'API raktas neegzistuoja arba yra netinkamas',
    eApiKeyExpired: 'API rakto galiojimas pasibaigė',
    eApiKeyInactive: 'API raktas buvo deaktyvuotas',
    ePlanLimitExceeded: 'Stringų skaičius viršija plano ribą',
    eQueryParamsNotAllowed: 'Planas neleidžia dinaminių URL parametrų',
    eRateLimitExceeded: 'Viršyta užklausų riba (rate limit)',
    eInternalError: 'Vidinė serverio klaida',
    eServiceError: 'Paslaugos klaida',
    s5Title: 'Gamybos korekcija',
    s5Intro:
      'Korekcijos funkcija pagerina prognozės tikslumą remiantis faktiniais gamybos duomenimis. Sistema lygina prognozuotą gamybą su faktine ir apskaičiuoja korekcijos koeficientus pagal valandą (UTC). Reikalauja parinkties "correctionEnabled" plane.',
    s5SendTitle: 'Faktinių duomenų pateikimas',
    s5SendDesc: 'Siųskite nurodytos dienos valandinę gamybą į galutinį tašką:',
    s5DateD: 'date — data formatu YYYY-MM-DD',
    s5TzD: 'tz — laiko juosta (UTC poslinkis), naudojama skaitant gamybą',
    s5ProdD: 'production — objektas: raktas yra valanda (eilutė), reikšmė yra gamyba Wh',
    s5EnableTitle: 'Korekcijos įjungimas prognozėje',
    s5EnableDesc:
      'Pridėkite parametrą "correction=true" prie prognozės užklausos arba nustatykite numatytąją korekciją kliento portale (kortelė Korekcija).',
    s5Note:
      'Jei parametras "correction" nepateiktas, sistema naudoja jūsų įrenginio numatytąją reikšmę.',
    s6Title: 'Pavyzdžiai',
    ex1: 'Šios dienos prognozė (įrenginio režimas)',
    ex2: 'Rytojaus prognozė (įrenginio režimas)',
    ex3: 'Prognozė su pilna konfigūracija (2 stringai)',
    ex4: 'Diagramos formatas (diagramoms)',
    ex5: 'Prognozė su gamybos korekcija',
    ex6: 'Atsako tvarkymas (bash + jq)',
    ex7: 'Prognozė su datomis vietoj today/tomorrow (date=full)',
  },
  mt: {
    title: 'Għajnuna — dokumentazzjoni API',
    subtitle:
      "Dokumentazzjoni sħiħa ta' Solar Forecast API: modi, parametri, formati ta' tweġiba u kodiċijiet ta' żball.",
    toc: 'Werrej',
    example: 'Eżempju',
    exampleResponse: "Eżempju ta' tweġiba",
    exampleError: "Eżempju ta' żball",
    s1Title: 'Kif tuża l-API',
    s1Intro:
      "L-API Forecast jappoġġja żewġ modi. Fit-tnejn, iċ-ċavetta API trid tingħadda f'header HTTP (l-isem tal-header huwa definit fil-pjan — X-API-Key b'mod default).",
    s1Mode1Title: 'Mod 1: Installazzjoni (simplifikat)',
    s1Mode1Desc:
      'Il-parametri kollha tal-installazzjoni (post, strings) jittieħdu awtomatikament mill-profil tiegħek. Sempliċement għaddi l-parametru "day".',
    s1Mode2Title: 'Mod 2: Parametri URL (kontroll sħiħ)',
    s1Mode2Desc:
      'Tista\' tgħaddi l-parametri kollha direttament fil-query string. Jeħtieġ pjan bl-għażla "allowQueryParams" attivata. Il-parametri URL jegħlbu dawk mill-installazzjoni ssejvjata.',
    s1Important:
      'Importanti: il-parametru "day" huwa obbligatorju fiż-żewġ modi. Valuri permessi: "today" jew "tomorrow".',
    s2Title: 'Parametri tal-query',
    s2Intro:
      'Hawn taħt hawn il-lista sħiħa tal-parametri tal-query string għall-endpoint GET {{endpoint}}.',
    thParam: 'Parametru',
    thType: 'Tip',
    thRequired: 'Obbligatorju',
    thDesc: 'Deskrizzjoni',
    reqYes: 'iva',
    reqNo: 'le',
    dDay: 'Jum tat-tbassir: "today" jew "tomorrow"',
    dDate: 'Format taċ-ċwievet tal-jiem: "relative" (default), "full" (SSSS-XX-JJ) jew "timestamp" (sekondi Unix). Bil-"full"/"timestamp" it-totali tal-jum jiġu rritornati fil-mappa "totals" bl-istess ċwievet. Fil-format chart jinbidlu biss it-totali — l-entrati fis-siegħa diġà għandhom timestamps ts sħaħ',
    dType: 'Format ta\' tweġiba: "standard" (default) jew "chart"',
    dCorrection:
      'Korrezzjoni tal-produzzjoni: "true" jew "false". Jekk titħalla barra, jintuża l-valur default tal-installazzjoni.',
    dLat: 'Latitudni (eż. 52.23)',
    dLon: 'Lonġitudni (eż. 21.01)',
    dTz: 'Offset taż-żona tal-ħin UTC (-12 sa 12, eż. 2 għal CET tas-sajf)',
    dAz: 'Azimut tal-istring {{n}} fi gradi (-180 sa 180; 0=tramuntana, 180=nofsinhar)',
    dTilt: 'Inklinazzjoni tal-istring {{n}} fi gradi (0 sa 90)',
    dPanels: "Numru ta' pannelli fl-istring {{n}}",
    dPower: "Qawwa tal-pannell f'kW fl-istring {{n}} (eż. 0.4 = 400W)",
    dEff: "Effiċjenza tal-pannell tal-istring {{n}} f'% (eż. 92)",
    s2Footnote:
      '* — Obbligatorju fil-mod tal-parametri URL. Fil-mod tal-installazzjoni, il-parametri tal-post u tal-istrings jittieħdu awtomatikament. Il-parametri tal-post (lat, lon, tz) u l-istrings (az, t, n, s, e) jeħtieġu l-għażla "allowQueryParams" fil-pjan.',
    s3Title: "Format ta' tweġiba",
    s3StdTitle: 'Tweġiba standard (type=standard)',
    s3StdDesc:
      "Format default — produzzjoni bis-siegħa f'watt-sigħat (Wh) bħala oġġett ċavetta-valur.",
    s3DateFmtTitle: 'Ċwievet tal-jiem bħala dati (date=full / date=timestamp)',
    s3DateFmtDesc: 'Bil-date=full iċ-ċwievet tal-jiem f result u strings isiru dati SSSS-XX-JJ, u t-totali tal-jum jiġu rritornati fil-mappa "totals" bl-istess dati. date=timestamp jaħdem bl-istess mod b sekondi Unix (bidu tal-jum fiż-żona tz). Japplika wkoll għall-blokk "confidence" b enriched=1. Fil-format chart jinbidlu biss it-totali — l-entrati fis-siegħa diġà għandhom timestamps ts sħaħ.',
    s3ChartTitle: "Tweġiba ta' grafika (type=chart)",
    s3ChartDesc:
      "Format ottimizzat għall-grafiki — array ta' oġġetti b'timestamp ISO u valur ta' enerġija.",
    s3FieldsTitle: 'Deskrizzjoni tal-kampi',
    thField: 'Kamp',
    fDate: 'Timestamp tal-ġenerazzjoni tat-tweġiba (ISO 8601)',
    fLatLon: 'Koordinati użati fit-talba',
    fToday: 'Produzzjoni bis-siegħa għal-llum (ċavetta = siegħa, valur = Wh)',
    fTomorrow: 'Produzzjoni bis-siegħa għal għada',
    fTotals: 'Produzzjoni totali għall-jum mogħti (Wh); bil-date=full/timestamp jiġi rritornat bħala mappa "totals" biċ-ċwievet tal-jiem',
    fStrings:
      "Array ta' data għal kull string (azimut, effiċjenza, qawwa, numru ta' pannelli, produzzjoni)",
    fCorrApplied: 'Jekk ġiet applikata l-korrezzjoni tal-produzzjoni (true/false)',
    fProcTime: "Ħin tal-ipproċessar tat-talba f'ms",
    fWeather:
      'Kuntest tat-temp: sħab kull siegħa, temperatura arja/pannelli, riħ, preċipitazzjoni, silġ, kodiċi WMO, UV, tlugħ/inżul tax-xemx + sommarju ta’ kuljum.',
    fWeatherAlerts: 'Twissijiet attivi tal-MeteoAlarm għall-post (array vojt jekk m’hemm xejn).',
    fProdAlerts:
      'Twissijiet tal-produzzjoni mit-tbassir: silġ fuq il-pannelli, sħab dens, raffigħat qawwija.',
    fAirQuality:
      'Kwalità tal-arja (PM10/PM2.5/trab/AOD) + valutazzjoni tal-ħmieġ tal-pannelli u rakkomandazzjoni ta’ tindif.',
    fArchive:
      'Profil annwali tal-enerġija tas-sit mill-arkivju tal-analiżi mill-ġdid: irradjazzjoni annwali, klassifika ta’ kull xahar, inklinazzjoni/ażimut ottimali stmati (best-effort, jiġġedded fl-isfond).',
    fConfidence:
      'Faxxi tal-produzzjoni P10/P50/P90 mill-firxa tal-mudelli ensemble (biss meta jkunu kkonfigurati diversi mudelli).',
    s4Title: "Kodiċijiet ta' żball",
    s4Intro:
      'F\'każ ta\' żball, l-API jirritorna oġġett JSON b\'kamp "error" li fih "code" u "message".',
    thCode: 'Kodiċi',
    thHttp: 'HTTP',
    eValidationError: 'Parametri tal-query nieqsa jew invalidi',
    eNoInstallation: 'Ebda installazzjoni ssejvjata u ebda parametru URL',
    eApiKeyMissing: 'Ebda ċavetta API fil-header',
    eApiKeyInvalid: 'Iċ-ċavetta API ma teżistix jew hija invalida',
    eApiKeyExpired: 'Iċ-ċavetta API skadiet',
    eApiKeyInactive: 'Iċ-ċavetta API ġiet diżattivata',
    ePlanLimitExceeded: "In-numru ta' strings jaqbeż il-limitu tal-pjan",
    eQueryParamsNotAllowed: 'Il-pjan ma jippermettix parametri URL dinamiċi',
    eRateLimitExceeded: "Limitu ta' talbiet maqbuż (rate limit)",
    eInternalError: 'Żball intern tas-server',
    eServiceError: 'Żball tas-servizz',
    s5Title: 'Korrezzjoni tal-produzzjoni',
    s5Intro:
      "Il-funzjoni ta' korrezzjoni ttejjeb il-preċiżjoni tat-tbassir ibbażata fuq data tal-produzzjoni reali. Is-sistema tqabbel il-produzzjoni mbassra ma' dik reali u tikkalkula fatturi ta' korrezzjoni kull siegħa (UTC). Jeħtieġ l-għażla \"correctionEnabled\" fil-pjan.",
    s5SendTitle: "Sottomissjoni ta' data reali",
    s5SendDesc: 'Ibgħat il-produzzjoni bis-siegħa għal jum mogħti lill-endpoint:',
    s5DateD: 'date — data fil-format YYYY-MM-DD',
    s5TzD: 'tz — żona tal-ħin (offset UTC) użata meta tinqara l-produzzjoni',
    s5ProdD:
      "production — oġġett: iċ-ċavetta hija s-siegħa (string), il-valur huwa l-produzzjoni f'Wh",
    s5EnableTitle: 'Attivazzjoni tal-korrezzjoni fit-tbassir',
    s5EnableDesc:
      'Żid il-parametru "correction=true" mat-talba tat-tbassir, jew issettja l-korrezzjoni default fil-portal tal-klijent (tab Korrezzjoni).',
    s5Note:
      'Jekk il-parametru "correction" ma jingħatax, is-sistema tuża l-valur default tal-installazzjoni tiegħek.',
    s6Title: 'Eżempji',
    ex1: 'Tbassir għal-llum (mod installazzjoni)',
    ex2: 'Tbassir għal għada (mod installazzjoni)',
    ex3: "Tbassir b'konfigurazzjoni sħiħa (2 strings)",
    ex4: "Format ta' grafika (għall-grafiki)",
    ex5: "Tbassir b'korrezzjoni tal-produzzjoni",
    ex6: 'Trattament tat-tweġiba (bash + jq)',
    ex7: 'Tbassir b dati minflok today/tomorrow (date=full)',
  },
  ga: {
    title: 'Cabhair — doiciméadúchán API',
    subtitle:
      'Doiciméadúchán iomlán Solar Forecast API: móid, paraiméadair, formáidí freagartha agus cóid earráide.',
    toc: 'Clár ábhair',
    example: 'Sampla',
    exampleResponse: 'Sampla freagartha',
    exampleError: 'Sampla earráide',
    s1Title: 'Conas an API a úsáid',
    s1Intro:
      'Tacaíonn an Forecast API le dhá mhód. Sa dá cheann, caithfear an eochair API a sheoladh i gceanntásc HTTP (sainmhínítear ainm an cheanntáisc sa phlean — X-API-Key de réir réamhshocraithe).',
    s1Mode1Title: 'Mód 1: Suiteáil (simplithe)',
    s1Mode1Desc:
      'Tógtar gach paraiméadar suiteála (suíomh, strings) go huathoibríoch ó do phróifíl. Seol an paraiméadar "day" amháin.',
    s1Mode2Title: 'Mód 2: Paraiméadair URL (rialú iomlán)',
    s1Mode2Desc:
      'Is féidir leat gach paraiméadar a sheoladh go díreach sa teaghrán fiosrúcháin. Éilíonn sé plean leis an rogha "allowQueryParams" cumasaithe. Sáraíonn paraiméadair URL iad siúd ón suiteáil shábháilte.',
    s1Important:
      'Tábhachtach: tá an paraiméadar "day" éigeantach sa dá mhód. Luachanna ceadaithe: "today" nó "tomorrow".',
    s2Title: 'Paraiméadair fiosrúcháin',
    s2Intro:
      'Anseo thíos tá an liosta iomlán de pharaiméadair an teaghráin fiosrúcháin don chríochphointe GET {{endpoint}}.',
    thParam: 'Paraiméadar',
    thType: 'Cineál',
    thRequired: 'Éigeantach',
    thDesc: 'Cur síos',
    reqYes: 'tá',
    reqNo: 'níl',
    dDay: 'Lá na réamhaisnéise: "today" nó "tomorrow"',
    dDate: 'Formáid eochracha lae: "relative" (réamhshocrú), "full" (BBBB-MM-LL) nó "timestamp" (soicindí Unix). Le "full"/"timestamp" cuirtear iomláin laethúla ar ais i mapa "totals" leis na heochracha céanna. San fhormáid chart ní athraíonn ach na hiomláin — tá lánstampaí ama ts cheana féin ar na hiontrálacha in aghaidh na huaire',
    dType: 'Formáid freagartha: "standard" (réamhshocrú) nó "chart"',
    dCorrection:
      'Ceartú táirgthe: "true" nó "false". Má fhágtar ar lár é, úsáidtear luach réamhshocraithe na suiteála.',
    dLat: 'Domhanleithead (m.sh. 52.23)',
    dLon: 'Domhanfhad (m.sh. 21.01)',
    dTz: 'Fritháireamh chrios ama UTC (-12 go 12, m.sh. 2 do CET an tsamhraidh)',
    dAz: 'Asamat an string {{n}} i gcéimeanna (-180 go 180; 0=tuaisceart, 180=deisceart)',
    dTilt: 'Claonadh an string {{n}} i gcéimeanna (0 go 90)',
    dPanels: 'Líon na bpainéal sa string {{n}}',
    dPower: 'Cumhacht an phainéil i kW sa string {{n}} (m.sh. 0.4 = 400W)',
    dEff: 'Éifeachtúlacht an phainéil de string {{n}} i % (m.sh. 92)',
    s2Footnote:
      '* — Éigeantach i mód paraiméadair URL. I mód suiteála, tógtar paraiméadair suímh agus string go huathoibríoch. Éilíonn paraiméadair suímh (lat, lon, tz) agus strings (az, t, n, s, e) an rogha "allowQueryParams" sa phlean.',
    s3Title: 'Formáid freagartha',
    s3StdTitle: 'Freagra caighdeánach (type=standard)',
    s3StdDesc:
      'Formáid réamhshocraithe — táirgeadh in aghaidh na huaire i vatuaireanta (Wh) mar réad eochair-luach.',
    s3DateFmtTitle: 'Eochracha lae mar dhátaí (date=full / date=timestamp)',
    s3DateFmtDesc: 'Le date=full déantar dátaí BBBB-MM-LL de na heochracha lae in result agus strings, agus cuirtear iomláin laethúla ar ais i mapa "totals" leis na dátaí céanna. Oibríonn date=timestamp mar an gcéanna le soicindí Unix (tús an lae i gcrios tz). Baineann sé freisin leis an mbloc "confidence" le enriched=1. San fhormáid chart ní athraíonn ach na hiomláin — tá lánstampaí ama ts cheana féin ar na hiontrálacha in aghaidh na huaire.',
    s3ChartTitle: 'Freagra cairte (type=chart)',
    s3ChartDesc:
      'Formáid optamaithe do chairteacha — eagar réad le stampa ama ISO agus luach fuinnimh.',
    s3FieldsTitle: 'Cur síos ar na réimsí',
    thField: 'Réimse',
    fDate: 'Stampa ama ghiniúint an fhreagra (ISO 8601)',
    fLatLon: 'Comhordanáidí a úsáideadh san iarratas',
    fToday: 'Táirgeadh in aghaidh na huaire don lá inniu (eochair = uair, luach = Wh)',
    fTomorrow: 'Táirgeadh in aghaidh na huaire amárach',
    fTotals: 'Táirgeadh iomlán don lá tugtha (Wh); le date=full/timestamp cuirtear ar ais mar mhapa "totals" le heochracha lae',
    fStrings:
      'Eagar sonraí in aghaidh an string (asamat, éifeachtúlacht, cumhacht, líon painéal, táirgeadh)',
    fCorrApplied: 'Cibé an cuireadh ceartú táirgthe i bhfeidhm (true/false)',
    fProcTime: 'Am próiseála an iarratais i ms',
    fWeather:
      'Comhthéacs aimsire: scamallú in aghaidh na huaire, teocht aeir/painéil, gaoth, deascadh, sneachta, cód WMO, UV, éirí/luí na gréine + achoimre laethúil.',
    fWeatherAlerts:
      'Rabhaidh MeteoAlarm gníomhacha don suíomh (eagar folamh mura bhfuil aon cheann ann).',
    fProdAlerts:
      'Foláirimh táirgeachta díorthaithe ón réamhaisnéis: sneachta ar phainéil, scamallú trom, séideáin láidre.',
    fAirQuality:
      'Cáilíocht an aeir (PM10/PM2.5/deannach/AOD) + measúnú ar shalú na bpainéal agus moladh glanta.',
    fArchive:
      'Próifíl fuinnimh bhliantúil an láithreáin ón gcartlann athanailíse: ionradaíocht bhliantúil, rangú míosúil, claonadh/asamat optamach measta (best-effort, athnuaite sa chúlra).',
    fConfidence:
      'Bandaí táirgthe P10/P50/P90 ó scaipeadh na samhlacha ensemble (nuair atá iliomad samhlacha cumraithe amháin).',
    s4Title: 'Cóid earráide',
    s4Intro:
      'I gcás earráide, filleann an API réad JSON le réimse "error" ina bhfuil "code" agus "message".',
    thCode: 'Cód',
    thHttp: 'HTTP',
    eValidationError: 'Paraiméadair fiosrúcháin in easnamh nó neamhbhailí',
    eNoInstallation: 'Gan suiteáil shábháilte agus gan paraiméadair URL',
    eApiKeyMissing: 'Gan eochair API sa cheanntásc',
    eApiKeyInvalid: 'Níl an eochair API ann nó tá sí neamhbhailí',
    eApiKeyExpired: 'Tá an eochair API imithe in éag',
    eApiKeyInactive: 'Díghníomhachtaíodh an eochair API',
    ePlanLimitExceeded: 'Sáraíonn líon na strings teorainn an phlean',
    eQueryParamsNotAllowed: 'Ní cheadaíonn an plean paraiméadair URL dhinimiciúla',
    eRateLimitExceeded: 'Sáraíodh teorainn na n-iarratas (rate limit)',
    eInternalError: 'Earráid inmheánach freastalaí',
    eServiceError: 'Earráid seirbhíse',
    s5Title: 'Ceartú táirgthe',
    s5Intro:
      'Feabhsaíonn an ghné cheartaithe cruinneas na réamhaisnéise bunaithe ar shonraí táirgthe iarbhír. Déanann an córas comparáid idir an táirgeadh réamhaisnéise agus an táirgeadh iarbhír agus ríomhann sé fachtóirí ceartaithe in aghaidh na huaire (UTC). Éilíonn sé an rogha "correctionEnabled" sa phlean.',
    s5SendTitle: 'Sonraí iarbhír a chur isteach',
    s5SendDesc: 'Seol an táirgeadh in aghaidh na huaire do lá ar leith chuig an gcríochphointe:',
    s5DateD: 'date — dáta i bhformáid YYYY-MM-DD',
    s5TzD: 'tz — crios ama (fritháireamh UTC) a úsáidtear agus an táirgeadh á léamh',
    s5ProdD:
      'production — réad: is í an eochair an uair (teaghrán), is é an luach an táirgeadh i Wh',
    s5EnableTitle: 'An ceartú a chumasú sa réamhaisnéis',
    s5EnableDesc:
      'Cuir an paraiméadar "correction=true" leis an iarratas réamhaisnéise, nó socraigh an ceartú réamhshocraithe i bpunann an chliaint (cluaisín Ceartú).',
    s5Note:
      'Mura gcuirtear an paraiméadar "correction" ar fáil, úsáideann an córas luach réamhshocraithe do shuiteála.',
    s6Title: 'Samplaí',
    ex1: 'Réamhaisnéis don lá inniu (mód suiteála)',
    ex2: 'Réamhaisnéis don lá amárach (mód suiteála)',
    ex3: 'Réamhaisnéis le cumraíocht iomlán (2 string)',
    ex4: 'Formáid cairte (do chairteacha)',
    ex5: 'Réamhaisnéis le ceartú táirgthe',
    ex6: 'An freagra a láimhseáil (bash + jq)',
    ex7: 'Réamhaisnéis le dátaí in ionad today/tomorrow (date=full)',
  },
  no: {
    title: 'Hjelp — API-dokumentasjon',
    subtitle:
      'Komplett Solar Forecast API-dokumentasjon: moduser, parametere, svarformater og feilkoder.',
    toc: 'Innholdsfortegnelse',
    example: 'Eksempel',
    exampleResponse: 'Eksempelsvar',
    exampleError: 'Eksempelfeil',
    s1Title: 'Slik bruker du API-et',
    s1Intro:
      'Forecast API støtter to moduser. I begge må API-nøkkelen sendes i en HTTP-header (headernavnet defineres i planen — X-API-Key som standard).',
    s1Mode1Title: 'Modus 1: Anlegg (forenklet)',
    s1Mode1Desc:
      'Alle anleggsparametere (plassering, strings) hentes automatisk fra profilen din. Bare send parameteren "day".',
    s1Mode2Title: 'Modus 2: URL-parametere (full kontroll)',
    s1Mode2Desc:
      'Du kan sende alle parametere direkte i spørringsstrengen. Krever en plan med alternativet "allowQueryParams" aktivert. URL-parametere overstyrer dem fra det lagrede anlegget.',
    s1Important:
      'Viktig: parameteren "day" er obligatorisk i begge modusene. Tillatte verdier: "today" eller "tomorrow".',
    s2Title: 'Spørringsparametere',
    s2Intro:
      'Nedenfor er den fullstendige listen over spørringsstrengparametere for GET {{endpoint}}-endepunktet.',
    thParam: 'Parameter',
    thType: 'Type',
    thRequired: 'Obligatorisk',
    thDesc: 'Beskrivelse',
    reqYes: 'ja',
    reqNo: 'nei',
    dDay: 'Prognosedag: "today" eller "tomorrow"',
    dDate: 'Format for dagsnøkler: "relative" (standard), "full" (ÅÅÅÅ-MM-DD) eller "timestamp" (Unix-sekunder). Med "full"/"timestamp" returneres dagssummer i et "totals"-kart med samme nøkler. I chart-formatet endres bare summene — timeoppføringene bærer allerede fulle ts-tidsstempler',
    dType: 'Svarformat: "standard" (standard) eller "chart"',
    dCorrection:
      'Produksjonskorreksjon: "true" eller "false". Hvis utelatt brukes anleggets standardverdi.',
    dLat: 'Breddegrad (f.eks. 52.23)',
    dLon: 'Lengdegrad (f.eks. 21.01)',
    dTz: 'UTC-tidssoneforskyvning (-12 til 12, f.eks. 2 for CET sommertid)',
    dAz: 'Asimut for string {{n}} i grader (-180 til 180; 0=nord, 180=sør)',
    dTilt: 'Helning for string {{n}} i grader (0 til 90)',
    dPanels: 'Antall paneler i string {{n}}',
    dPower: 'Paneleffekt i kW i string {{n}} (f.eks. 0.4 = 400W)',
    dEff: 'Panelvirkningsgrad for string {{n}} i % (f.eks. 92)',
    s2Footnote:
      '* — Obligatorisk i URL-parametermodus. I anleggsmodus hentes plasserings- og string-parametere automatisk. Plasseringsparametere (lat, lon, tz) og strings (az, t, n, s, e) krever alternativet "allowQueryParams" i planen.',
    s3Title: 'Svarformat',
    s3StdTitle: 'Standardsvar (type=standard)',
    s3StdDesc: 'Standardformat — timeproduksjon i wattimer (Wh) som et nøkkel-verdi-objekt.',
    s3DateFmtTitle: 'Dagsnøkler som datoer (date=full / date=timestamp)',
    s3DateFmtDesc: 'Med date=full blir dagsnøklene i result og strings til ÅÅÅÅ-MM-DD-datoer, og dagssummer returneres i et "totals"-kart med samme datoer. date=timestamp fungerer tilsvarende med Unix-sekunder (dagens start i tz-tidssonen). Gjelder også "confidence"-blokken med enriched=1. I chart-formatet endres bare summene — timeoppføringene bærer allerede fulle ts-tidsstempler.',
    s3ChartTitle: 'Diagramsvar (type=chart)',
    s3ChartDesc:
      'Format optimalisert for diagrammer — en matrise av objekter med et ISO-tidsstempel og en energiverdi.',
    s3FieldsTitle: 'Feltbeskrivelser',
    thField: 'Felt',
    fDate: 'Tidsstempel for svargenerering (ISO 8601)',
    fLatLon: 'Koordinater brukt i forespørselen',
    fToday: 'Timeproduksjon for i dag (nøkkel = time, verdi = Wh)',
    fTomorrow: 'Timeproduksjon for i morgen',
    fTotals: 'Total produksjon for den gitte dagen (Wh); med date=full/timestamp returneres som "totals"-kart med dagsnøkler',
    fStrings:
      'Matrise med data per string (asimut, virkningsgrad, effekt, antall paneler, produksjon)',
    fCorrApplied: 'Om produksjonskorreksjon ble brukt (true/false)',
    fProcTime: 'Behandlingstid for forespørselen i ms',
    fWeather:
      'Værkontekst: timesvis skydekke, luft-/paneltemperatur, vind, nedbør, snø, WMO-kode, UV, soloppgang/-nedgang + dagsoversikt.',
    fWeatherAlerts: 'Aktive MeteoAlarm-varsler for stedet (tom matrise hvis ingen).',
    fProdAlerts:
      'Produksjonsvarsler utledet fra prognosen: snø på paneler, tett skydekke, kraftige vindkast.',
    fAirQuality:
      'Luftkvalitet (PM10/PM2.5/støv/AOD) + vurdering av paneltilsmussing med rengjøringsanbefaling.',
    fArchive:
      'Årlig energiprofil for stedet fra reanalysearkivet: årlig innstråling, månedsrangering, anslått optimal helning/asimut (best-effort, oppdateres i bakgrunnen).',
    fConfidence:
      'P10/P50/P90-produksjonsbånd fra spredningen i ensemblemodellene (kun når flere modeller er konfigurert).',
    s4Title: 'Feilkoder',
    s4Intro:
      'Ved feil returnerer API-et et JSON-objekt med et "error"-felt som inneholder "code" og "message".',
    thCode: 'Kode',
    thHttp: 'HTTP',
    eValidationError: 'Manglende eller ugyldige spørringsparametere',
    eNoInstallation: 'Ingen lagret anlegg og ingen URL-parametere',
    eApiKeyMissing: 'Ingen API-nøkkel i headeren',
    eApiKeyInvalid: 'API-nøkkelen finnes ikke eller er ugyldig',
    eApiKeyExpired: 'API-nøkkelen har utløpt',
    eApiKeyInactive: 'API-nøkkelen er deaktivert',
    ePlanLimitExceeded: 'Antall strings overstiger planens grense',
    eQueryParamsNotAllowed: 'Planen tillater ikke dynamiske URL-parametere',
    eRateLimitExceeded: 'Forespørselsgrense overskredet (rate limit)',
    eInternalError: 'Intern serverfeil',
    eServiceError: 'Tjenestefeil',
    s5Title: 'Produksjonskorreksjon',
    s5Intro:
      'Korreksjonsfunksjonen forbedrer prognosens nøyaktighet basert på faktiske produksjonsdata. Systemet sammenligner den prognostiserte produksjonen med den faktiske og beregner korreksjonsfaktorer per time (UTC). Krever alternativet "correctionEnabled" i planen.',
    s5SendTitle: 'Innsending av faktiske data',
    s5SendDesc: 'Send timeproduksjonen for en gitt dag til endepunktet:',
    s5DateD: 'date — dato i formatet YYYY-MM-DD',
    s5TzD: 'tz — tidssone (UTC-forskyvning) brukt ved lesing av produksjon',
    s5ProdD: 'production — objekt: nøkkelen er timen (streng), verdien er produksjonen i Wh',
    s5EnableTitle: 'Aktivere korreksjon i prognosen',
    s5EnableDesc:
      'Legg til parameteren "correction=true" i prognoseforespørselen, eller angi standardkorreksjonen i kundeportalen (fanen Korreksjon).',
    s5Note: 'Hvis parameteren "correction" ikke angis, bruker systemet anleggets standardverdi.',
    s6Title: 'Eksempler',
    ex1: 'Prognose for i dag (anleggsmodus)',
    ex2: 'Prognose for i morgen (anleggsmodus)',
    ex3: 'Prognose med full konfigurasjon (2 strings)',
    ex4: 'Diagramformat (for diagrammer)',
    ex5: 'Prognose med produksjonskorreksjon',
    ex6: 'Håndtere svaret (bash + jq)',
    ex7: 'Prognose med datoer i stedet for today/tomorrow (date=full)',
  },
  is: {
    title: 'Hjálp — API-skjölun',
    subtitle: 'Heildarskjölun Solar Forecast API: hamir, færibreytur, svarsnið og villukóðar.',
    toc: 'Efnisyfirlit',
    example: 'Dæmi',
    exampleResponse: 'Dæmi um svar',
    exampleError: 'Dæmi um villu',
    s1Title: 'Hvernig á að nota API-ið',
    s1Intro:
      'Forecast API styður tvo hami. Í báðum verður að senda API-lykilinn í HTTP-haus (heiti haussins er skilgreint í áætluninni — X-API-Key sjálfgefið).',
    s1Mode1Title: 'Hamur 1: Stöð (einfaldað)',
    s1Mode1Desc:
      'Allar færibreytur stöðvarinnar (staðsetning, strings) eru sóttar sjálfkrafa úr prófílnum þínum. Sendu bara færibreytuna "day".',
    s1Mode2Title: 'Hamur 2: URL-færibreytur (full stjórn)',
    s1Mode2Desc:
      'Þú getur sent allar færibreytur beint í fyrirspurnastrenginn. Krefst áætlunar með valkostinn "allowQueryParams" virkan. URL-færibreytur hnekkja þeim úr vistuðu stöðinni.',
    s1Important:
      'Mikilvægt: færibreytan "day" er skylda í báðum hömum. Leyfileg gildi: "today" eða "tomorrow".',
    s2Title: 'Fyrirspurnafæribreytur',
    s2Intro:
      'Hér að neðan er heildarlisti yfir fyrirspurnastrengsfæribreytur fyrir GET {{endpoint}} endapunktinn.',
    thParam: 'Færibreyta',
    thType: 'Tegund',
    thRequired: 'Skylda',
    thDesc: 'Lýsing',
    reqYes: 'já',
    reqNo: 'nei',
    dDay: 'Dagur spár: "today" eða "tomorrow"',
    dDate: 'Snið dagalykla: "relative" (sjálfgefið), "full" (ÁÁÁÁ-MM-DD) eða "timestamp" (Unix-sekúndur). Með "full"/"timestamp" eru dagssummur skilaðar í "totals"-korti með sömu lyklum. Í chart-sniðinu breytast aðeins summurnar — klukkustundafærslur bera nú þegar full ts-tímamerki',
    dType: 'Svarsnið: "standard" (sjálfgefið) eða "chart"',
    dCorrection:
      'Framleiðsluleiðrétting: "true" eða "false". Ef sleppt er notað sjálfgefið gildi stöðvarinnar.',
    dLat: 'Breiddargráða (t.d. 52.23)',
    dLon: 'Lengdargráða (t.d. 21.01)',
    dTz: 'UTC tímabeltisfærsla (-12 til 12, t.d. 2 fyrir sumar-CET)',
    dAz: 'Áttarhorn strings {{n}} í gráðum (-180 til 180; 0=norður, 180=suður)',
    dTilt: 'Halli strings {{n}} í gráðum (0 til 90)',
    dPanels: 'Fjöldi spjalda í string {{n}}',
    dPower: 'Afl spjalds í kW í string {{n}} (t.d. 0.4 = 400W)',
    dEff: 'Nýtni spjalds strings {{n}} í % (t.d. 92)',
    s2Footnote:
      '* — Skylda í URL-færibreytuham. Í stöðvarham eru staðsetningar- og string-færibreytur sóttar sjálfkrafa. Staðsetningarfæribreytur (lat, lon, tz) og strings (az, t, n, s, e) krefjast valkostsins "allowQueryParams" í áætluninni.',
    s3Title: 'Svarsnið',
    s3StdTitle: 'Staðlað svar (type=standard)',
    s3StdDesc:
      'Sjálfgefið snið — klukkustundarframleiðsla í vattstundum (Wh) sem lykill-gildi hlutur.',
    s3DateFmtTitle: 'Dagalyklar sem dagsetningar (date=full / date=timestamp)',
    s3DateFmtDesc: 'Með date=full verða dagalyklar í result og strings að ÁÁÁÁ-MM-DD dagsetningum og dagssummur eru skilaðar í "totals"-korti með sömu dagsetningum. date=timestamp virkar eins með Unix-sekúndum (upphaf dags á tz-tímabelti). Gildir einnig um "confidence"-blokkina með enriched=1. Í chart-sniðinu breytast aðeins summurnar — klukkustundafærslur bera nú þegar full ts-tímamerki.',
    s3ChartTitle: 'Línuritssvar (type=chart)',
    s3ChartDesc: 'Snið fínstillt fyrir línurit — fylki hluta með ISO tímastimpli og orkugildi.',
    s3FieldsTitle: 'Lýsing reita',
    thField: 'Reitur',
    fDate: 'Tímastimpill svargerðar (ISO 8601)',
    fLatLon: 'Hnit sem notuð voru í beiðninni',
    fToday: 'Klukkustundarframleiðsla fyrir í dag (lykill = klukkustund, gildi = Wh)',
    fTomorrow: 'Klukkustundarframleiðsla fyrir á morgun',
    fTotals: 'Heildarframleiðsla fyrir gefinn dag (Wh); með date=full/timestamp skilað sem "totals"-korti með dagalyklum',
    fStrings: 'Fylki gagna fyrir hvern string (áttarhorn, nýtni, afl, fjöldi spjalda, framleiðsla)',
    fCorrApplied: 'Hvort framleiðsluleiðrétting var beitt (true/false)',
    fProcTime: 'Vinnslutími beiðninnar í ms',
    fWeather:
      'Veðursamhengi: skýjahula á klukkustund, loft-/spjaldhiti, vindur, úrkoma, snjór, WMO-kóði, UV, sólarupprás/-setur + dagleg samantekt.',
    fWeatherAlerts: 'Virkar MeteoAlarm-viðvaranir fyrir staðsetninguna (tómt fylki ef engar).',
    fProdAlerts:
      'Framleiðsluviðvaranir leiddar af spánni: snjór á spjöldum, þétt skýjahula, sterkar vindhviður.',
    fAirQuality:
      'Loftgæði (PM10/PM2.5/ryk/AOD) + mat á óhreinindum á spjöldum og tillaga um hreinsun.',
    fArchive:
      'Árleg orkusnið staðarins úr endurgreiningasafninu: árleg geislun, mánaðarröðun, áætluð kjörhalli/áttarhorn (best-effort, uppfært í bakgrunni).',
    fConfidence:
      'P10/P50/P90 framleiðslubönd út frá dreifingu safnlíkana (aðeins þegar mörg líkön eru stillt).',
    s4Title: 'Villukóðar',
    s4Intro:
      'Við villu skilar API-ið JSON-hlut með "error"-reit sem inniheldur "code" og "message".',
    thCode: 'Kóði',
    thHttp: 'HTTP',
    eValidationError: 'Vantar eða ógildar fyrirspurnafæribreytur',
    eNoInstallation: 'Engin vistuð stöð og engar URL-færibreytur',
    eApiKeyMissing: 'Enginn API-lykill í hausnum',
    eApiKeyInvalid: 'API-lykillinn er ekki til eða er ógildur',
    eApiKeyExpired: 'API-lykillinn er útrunninn',
    eApiKeyInactive: 'API-lykillinn hefur verið gerður óvirkur',
    ePlanLimitExceeded: 'Fjöldi strings fer yfir takmörk áætlunarinnar',
    eQueryParamsNotAllowed: 'Áætlunin leyfir ekki kvikar URL-færibreytur',
    eRateLimitExceeded: 'Beiðnatakmörk farin yfir (rate limit)',
    eInternalError: 'Innri þjónsvilla',
    eServiceError: 'Þjónustuvilla',
    s5Title: 'Framleiðsluleiðrétting',
    s5Intro:
      'Leiðréttingaraðgerðin bætir nákvæmni spárinnar út frá raunverulegum framleiðslugögnum. Kerfið ber saman spáða framleiðslu við raunverulega og reiknar leiðréttingarstuðla eftir klukkustundum (UTC). Krefst valkostsins "correctionEnabled" í áætluninni.',
    s5SendTitle: 'Sending raungagna',
    s5SendDesc: 'Sendu klukkustundarframleiðslu fyrir gefinn dag á endapunktinn:',
    s5DateD: 'date — dagsetning á sniðinu YYYY-MM-DD',
    s5TzD: 'tz — tímabelti (UTC-færsla) notað við lestur framleiðslu',
    s5ProdD:
      'production — hlutur: lykillinn er klukkustundin (strengur), gildið er framleiðslan í Wh',
    s5EnableTitle: 'Virkja leiðréttingu í spánni',
    s5EnableDesc:
      'Bættu færibreytunni "correction=true" við spárbeiðnina, eða stilltu sjálfgefnu leiðréttinguna í viðskiptavinagáttinni (flipinn Leiðrétting).',
    s5Note:
      'Ef færibreytan "correction" er ekki gefin notar kerfið sjálfgefið gildi stöðvarinnar þinnar.',
    s6Title: 'Dæmi',
    ex1: 'Spá fyrir í dag (stöðvarhamur)',
    ex2: 'Spá fyrir á morgun (stöðvarhamur)',
    ex3: 'Spá með fullri uppsetningu (2 strings)',
    ex4: 'Línuritssnið (fyrir línurit)',
    ex5: 'Spá með framleiðsluleiðréttingu',
    ex6: 'Meðhöndlun svarsins (bash + jq)',
    ex7: 'Spá með dagsetningum í stað today/tomorrow (date=full)',
  },
  uk: {
    title: 'Довідка — документація API',
    subtitle:
      'Повна документація Solar Forecast API: режими, параметри, формати відповіді та коди помилок.',
    toc: 'Зміст',
    example: 'Приклад',
    exampleResponse: 'Приклад відповіді',
    exampleError: 'Приклад помилки',
    s1Title: 'Як використовувати API',
    s1Intro:
      'Forecast API підтримує два режими. В обох ключ API має передаватися в заголовку HTTP (назву заголовка визначено в плані — за замовчуванням X-API-Key).',
    s1Mode1Title: 'Режим 1: Установка (спрощений)',
    s1Mode1Desc:
      'Усі параметри установки (розташування, стрінги) беруться автоматично з вашого профілю. Просто передайте параметр "day".',
    s1Mode2Title: 'Режим 2: Параметри URL (повний контроль)',
    s1Mode2Desc:
      'Усі параметри можна передати безпосередньо в рядку запиту. Потребує плану з увімкненою опцією "allowQueryParams". Параметри URL перевизначають ті, що зі збереженої установки.',
    s1Important:
      'Важливо: параметр "day" обов\'язковий в обох режимах. Дозволені значення: "today" або "tomorrow".',
    s2Title: 'Параметри запиту',
    s2Intro:
      'Нижче наведено повний список параметрів рядка запиту для кінцевої точки GET {{endpoint}}.',
    thParam: 'Параметр',
    thType: 'Тип',
    thRequired: "Обов'язковий",
    thDesc: 'Опис',
    reqYes: 'так',
    reqNo: 'ні',
    dDay: 'День прогнозу: "today" або "tomorrow"',
    dDate: 'Формат ключів днів: "relative" (за замовчуванням), "full" (РРРР-ММ-ДД) або "timestamp" (секунди Unix). З "full"/"timestamp" денні суми повертаються в мапі "totals" з тими самими ключами. У форматі chart змінюються лише суми — погодинні записи вже містять повні часові мітки ts',
    dType: 'Формат відповіді: "standard" (за замовчуванням) або "chart"',
    dCorrection:
      'Корекція виробництва: "true" або "false". Якщо пропущено, використовується значення установки за замовчуванням.',
    dLat: 'Широта (напр. 52.23)',
    dLon: 'Довгота (напр. 21.01)',
    dTz: 'Зсув часового поясу UTC (-12 до 12, напр. 2 для літнього CET)',
    dAz: 'Азимут стрінга {{n}} у градусах (-180 до 180; 0=північ, 180=південь)',
    dTilt: 'Нахил стрінга {{n}} у градусах (0 до 90)',
    dPanels: 'Кількість панелей у стрінгу {{n}}',
    dPower: 'Потужність панелі в кВт у стрінгу {{n}} (напр. 0.4 = 400W)',
    dEff: 'Ефективність панелі стрінга {{n}} у % (напр. 92)',
    s2Footnote:
      '* — Обов\'язковий у режимі параметрів URL. У режимі установки параметри розташування та стрінгів беруться автоматично. Параметри розташування (lat, lon, tz) і стрінги (az, t, n, s, e) потребують опції "allowQueryParams" у плані.',
    s3Title: 'Формат відповіді',
    s3StdTitle: 'Стандартна відповідь (type=standard)',
    s3StdDesc:
      "Формат за замовчуванням — погодинне виробництво у ватт-годинах (Wh) як об'єкт ключ-значення.",
    s3DateFmtTitle: 'Ключі днів як дати (date=full / date=timestamp)',
    s3DateFmtDesc: 'З date=full ключі днів у result і strings стають датами РРРР-ММ-ДД, а денні суми повертаються в мапі "totals" з тими самими датами. date=timestamp працює так само з секундами Unix (початок доби в зоні tz). Стосується також блоку "confidence" з enriched=1. У форматі chart змінюються лише суми — погодинні записи вже містять повні часові мітки ts.',
    s3ChartTitle: 'Відповідь для графіка (type=chart)',
    s3ChartDesc:
      "Формат, оптимізований для графіків — масив об'єктів із міткою часу ISO та значенням енергії.",
    s3FieldsTitle: 'Опис полів',
    thField: 'Поле',
    fDate: 'Мітка часу генерації відповіді (ISO 8601)',
    fLatLon: 'Координати, використані в запиті',
    fToday: 'Погодинне виробництво на сьогодні (ключ = година, значення = Wh)',
    fTomorrow: 'Погодинне виробництво на завтра',
    fTotals: 'Загальне виробництво за вказаний день (Wh); з date=full/timestamp повертається як мапа "totals" з ключами днів',
    fStrings:
      'Масив даних по кожному стрінгу (азимут, ефективність, потужність, кількість панелей, виробництво)',
    fCorrApplied: 'Чи застосовано корекцію виробництва (true/false)',
    fProcTime: 'Час обробки запиту в мс',
    fWeather:
      'Погодний контекст: погодинна хмарність, температура повітря/панелей, вітер, опади, сніг, код WMO, UV, схід/захід сонця + денне зведення.',
    fWeatherAlerts: 'Активні попередження MeteoAlarm для локації (порожній масив, якщо немає).',
    fProdAlerts:
      'Виробничі сповіщення з прогнозу: сніг на панелях, густа хмарність, сильні пориви.',
    fAirQuality:
      'Якість повітря (PM10/PM2.5/пил/AOD) + оцінка забруднення панелей і рекомендація щодо чищення.',
    fArchive:
      'Річний енергетичний профіль локації з архіву реаналізів: річне опромінення, рейтинг місяців, орієнтовні оптимальні нахил/азимут (best-effort, оновлюється у фоні).',
    fConfidence:
      'Виробничі смуги P10/P50/P90 з розкиду ансамблевих моделей (лише коли налаштовано кілька моделей).',
    s4Title: 'Коди помилок',
    s4Intro:
      'У разі помилки API повертає об\'єкт JSON з полем "error", що містить "code" і "message".',
    thCode: 'Код',
    thHttp: 'HTTP',
    eValidationError: 'Відсутні або недійсні параметри запиту',
    eNoInstallation: 'Немає збереженої установки і немає параметрів URL',
    eApiKeyMissing: 'Немає ключа API в заголовку',
    eApiKeyInvalid: 'Ключ API не існує або недійсний',
    eApiKeyExpired: 'Термін дії ключа API минув',
    eApiKeyInactive: 'Ключ API деактивовано',
    ePlanLimitExceeded: 'Кількість стрінгів перевищує ліміт плану',
    eQueryParamsNotAllowed: 'План не дозволяє динамічні параметри URL',
    eRateLimitExceeded: 'Перевищено ліміт запитів (rate limit)',
    eInternalError: 'Внутрішня помилка сервера',
    eServiceError: 'Помилка сервісу',
    s5Title: 'Корекція виробництва',
    s5Intro:
      'Функція корекції покращує точність прогнозу на основі фактичних даних виробництва. Система порівнює прогнозоване виробництво з фактичним і обчислює погодинні (UTC) коефіцієнти корекції. Потребує опції "correctionEnabled" у плані.',
    s5SendTitle: 'Надсилання фактичних даних',
    s5SendDesc: 'Надішліть погодинне виробництво за вказаний день на кінцеву точку:',
    s5DateD: 'date — дата у форматі YYYY-MM-DD',
    s5TzD: 'tz — часовий пояс (зсув UTC), що використовується під час читання виробництва',
    s5ProdD: "production — об'єкт: ключ — це година (рядок), значення — виробництво у Wh",
    s5EnableTitle: 'Увімкнення корекції в прогнозі',
    s5EnableDesc:
      'Додайте параметр "correction=true" до запиту прогнозу або встановіть корекцію за замовчуванням у порталі клієнта (вкладка Корекція).',
    s5Note:
      'Якщо параметр "correction" не надано, система використовує значення вашої установки за замовчуванням.',
    s6Title: 'Приклади',
    ex1: 'Прогноз на сьогодні (режим установки)',
    ex2: 'Прогноз на завтра (режим установки)',
    ex3: 'Прогноз з повною конфігурацією (2 стрінги)',
    ex4: 'Формат графіка (для графіків)',
    ex5: 'Прогноз з корекцією виробництва',
    ex6: 'Обробка відповіді (bash + jq)',
    ex7: 'Прогноз із датами замість today/tomorrow (date=full)',
  },
  sr: {
    title: 'Помоћ — документација API-ja',
    subtitle:
      'Потпуна документација Solar Forecast API-ja: режими, параметри, формати одговора и кодови грешака.',
    toc: 'Садржај',
    example: 'Пример',
    exampleResponse: 'Пример одговора',
    exampleError: 'Пример грешке',
    s1Title: 'Како користити API',
    s1Intro:
      'Forecast API подржава два режима. У оба, API кључ мора бити прослеђен у HTTP заглављу (име заглавља је дефинисано у плану — подразумевано X-API-Key).',
    s1Mode1Title: 'Режим 1: Постројење (поједностављено)',
    s1Mode1Desc:
      'Сви параметри постројења (локација, стрингови) се аутоматски преузимају из вашег профила. Само проследите параметар "day".',
    s1Mode2Title: 'Режим 2: URL параметри (потпуна контрола)',
    s1Mode2Desc:
      'Све параметре можете проследити директно у упитном низу. Захтева план са омогућеном опцијом "allowQueryParams". URL параметри замењују оне из сачуваног постројења.',
    s1Important:
      'Важно: параметар "day" је обавезан у оба режима. Дозвољене вредности: "today" или "tomorrow".',
    s2Title: 'Параметри упита',
    s2Intro: 'Испод је потпуна листа параметара упитног низа за крајњу тачку GET {{endpoint}}.',
    thParam: 'Параметар',
    thType: 'Тип',
    thRequired: 'Обавезно',
    thDesc: 'Опис',
    reqYes: 'да',
    reqNo: 'не',
    dDay: 'Дан прогнозе: "today" или "tomorrow"',
    dDate: 'Формат кључева дана: "relative" (подразумевано), "full" (ГГГГ-ММ-ДД) или "timestamp" (Unix секунде). Уз "full"/"timestamp" дневни збирови се враћају у мапи "totals" са истим кључевима. У формату chart мењају се само збирови — сатни уноси већ носе пуне временске ознаке ts',
    dType: 'Формат одговора: "standard" (подразумевано) или "chart"',
    dCorrection:
      'Корекција производње: "true" или "false". Ако се изостави, користи се подразумевана вредност постројења.',
    dLat: 'Географска ширина (нпр. 52.23)',
    dLon: 'Географска дужина (нпр. 21.01)',
    dTz: 'Помак временске зоне UTC (-12 до 12, нпр. 2 за летње CET)',
    dAz: 'Азимут стринга {{n}} у степенима (-180 до 180; 0=север, 180=југ)',
    dTilt: 'Нагиб стринга {{n}} у степенима (0 до 90)',
    dPanels: 'Број панела у стрингу {{n}}',
    dPower: 'Снага панела у kW у стрингу {{n}} (нпр. 0.4 = 400W)',
    dEff: 'Ефикасност панела стринга {{n}} у % (нпр. 92)',
    s2Footnote:
      '* — Обавезно у режиму URL параметара. У режиму постројења параметри локације и стрингова се преузимају аутоматски. Параметри локације (lat, lon, tz) и стрингови (az, t, n, s, e) захтевају опцију "allowQueryParams" у плану.',
    s3Title: 'Формат одговора',
    s3StdTitle: 'Стандардни одговор (type=standard)',
    s3StdDesc:
      'Подразумевани формат — сатна производња у ват-сатима (Wh) као објекат кључ-вредност.',
    s3DateFmtTitle: 'Кључеви дана као датуми (date=full / date=timestamp)',
    s3DateFmtDesc: 'Уз date=full кључеви дана у result и strings постају датуми ГГГГ-ММ-ДД, а дневни збирови се враћају у мапи "totals" са истим датумима. date=timestamp ради исто са Unix секундама (почетак дана у зони tz). Важи и за блок "confidence" уз enriched=1. У формату chart мењају се само збирови — сатни уноси већ носе пуне временске ознаке ts.',
    s3ChartTitle: 'Одговор за графикон (type=chart)',
    s3ChartDesc:
      'Формат оптимизован за графиконе — низ објеката са ISO временском ознаком и вредношћу енергије.',
    s3FieldsTitle: 'Опис поља',
    thField: 'Поље',
    fDate: 'Временска ознака генерисања одговора (ISO 8601)',
    fLatLon: 'Координате коришћене у захтеву',
    fToday: 'Сатна производња за данас (кључ = сат, вредност = Wh)',
    fTomorrow: 'Сатна производња за сутра',
    fTotals: 'Укупна производња за дати дан (Wh); уз date=full/timestamp враћа се као мапа "totals" са кључевима дана',
    fStrings: 'Низ података по стрингу (азимут, ефикасност, снага, број панела, производња)',
    fCorrApplied: 'Да ли је примењена корекција производње (true/false)',
    fProcTime: 'Време обраде захтева у ms',
    fWeather:
      'Временски контекст: сатна наоблака, температура ваздуха/панела, ветар, падавине, снег, WMO код, UV, излазак/залазак сунца + дневни преглед.',
    fWeatherAlerts: 'Активна упозорења MeteoAlarm за локацију (празан низ ако их нема).',
    fProdAlerts:
      'Производна упозорења изведена из прогнозе: снег на панелима, густа наоблака, јаки удари ветра.',
    fAirQuality:
      'Квалитет ваздуха (PM10/PM2.5/прашина/AOD) + процена запрљања панела и препорука за чишћење.',
    fArchive:
      'Годишњи енергетски профил локације из архиве реанализа: годишње зрачење, ранг месеци, процењени оптимални нагиб/азимут (best-effort, освежава се у позадини).',
    fConfidence:
      'Производни појасеви P10/P50/P90 из расипања ансамбл модела (само када је конфигурисано више модела).',
    s4Title: 'Кодови грешака',
    s4Intro:
      'У случају грешке, API враћа JSON објекат са пољем "error" које садржи "code" и "message".',
    thCode: 'Код',
    thHttp: 'HTTP',
    eValidationError: 'Недостајући или неважећи параметри упита',
    eNoInstallation: 'Нема сачуваног постројења и нема URL параметара',
    eApiKeyMissing: 'Нема API кључа у заглављу',
    eApiKeyInvalid: 'API кључ не постоји или је неважећи',
    eApiKeyExpired: 'API кључ је истекао',
    eApiKeyInactive: 'API кључ је деактивиран',
    ePlanLimitExceeded: 'Број стрингова премашује ограничење плана',
    eQueryParamsNotAllowed: 'План не дозвољава динамичке URL параметре',
    eRateLimitExceeded: 'Премашено ограничење захтева (rate limit)',
    eInternalError: 'Интерна грешка сервера',
    eServiceError: 'Грешка сервиса',
    s5Title: 'Корекција производње',
    s5Intro:
      'Функција корекције побољшава тачност прогнозе на основу стварних производних података. Систем упоређује прогнозирану производњу са стварном и израчунава корекционе факторе по сату (UTC). Захтева опцију "correctionEnabled" у плану.',
    s5SendTitle: 'Слање стварних података',
    s5SendDesc: 'Пошаљите сатну производњу за дати дан на крајњу тачку:',
    s5DateD: 'date — датум у формату YYYY-MM-DD',
    s5TzD: 'tz — временска зона (помак UTC) коришћена при читању производње',
    s5ProdD: 'production — објекат: кључ је сат (стринг), вредност је производња у Wh',
    s5EnableTitle: 'Омогућавање корекције у прогнози',
    s5EnableDesc:
      'Додајте параметар "correction=true" захтеву за прогнозу или поставите подразумевану корекцију у клијентском порталу (картица Корекција).',
    s5Note:
      'Ако параметар "correction" није наведен, систем користи подразумевану вредност вашег постројења.',
    s6Title: 'Примери',
    ex1: 'Прогноза за данас (режим постројења)',
    ex2: 'Прогноза за сутра (режим постројења)',
    ex3: 'Прогноза са потпуном конфигурацијом (2 стринга)',
    ex4: 'Формат графикона (за графиконе)',
    ex5: 'Прогноза са корекцијом производње',
    ex6: 'Обрада одговора (bash + jq)',
    ex7: 'Прогноза са датумима уместо today/tomorrow (date=full)',
  },
  sq: {
    title: 'Ndihmë — dokumentacioni i API-së',
    subtitle:
      'Dokumentacioni i plotë i Solar Forecast API: modet, parametrat, formatet e përgjigjes dhe kodet e gabimit.',
    toc: 'Tabela e përmbajtjes',
    example: 'Shembull',
    exampleResponse: 'Shembull përgjigjeje',
    exampleError: 'Shembull gabimi',
    s1Title: 'Si të përdoret API-ja',
    s1Intro:
      'Forecast API mbështet dy mode. Në të dyja, çelësi API duhet të kalohet në një header HTTP (emri i header-it përcaktohet në plan — X-API-Key si parazgjedhje).',
    s1Mode1Title: 'Modi 1: Instalimi (i thjeshtuar)',
    s1Mode1Desc:
      'Të gjithë parametrat e instalimit (vendndodhja, strings) merren automatikisht nga profili yt. Thjesht kalo parametrin "day".',
    s1Mode2Title: 'Modi 2: Parametrat e URL-së (kontroll i plotë)',
    s1Mode2Desc:
      'Mund t\'i kalosh të gjithë parametrat direkt në query string. Kërkon një plan me opsionin "allowQueryParams" të aktivizuar. Parametrat e URL-së mbishkruajnë ata nga instalimi i ruajtur.',
    s1Important:
      'E rëndësishme: parametri "day" është i detyrueshëm në të dyja modet. Vlerat e lejuara: "today" ose "tomorrow".',
    s2Title: 'Parametrat e query-së',
    s2Intro:
      'Më poshtë është lista e plotë e parametrave të query string për endpoint-in GET {{endpoint}}.',
    thParam: 'Parametri',
    thType: 'Lloji',
    thRequired: 'I detyrueshëm',
    thDesc: 'Përshkrimi',
    reqYes: 'po',
    reqNo: 'jo',
    dDay: 'Dita e parashikimit: "today" ose "tomorrow"',
    dDate: 'Formati i çelësave të ditëve: "relative" (parazgjedhur), "full" (VVVV-MM-DD) ose "timestamp" (sekonda Unix). Me "full"/"timestamp" totalet ditore kthehen në hartën "totals" me të njëjtët çelësa. Në formatin chart ndryshojnë vetëm totalet — hyrjet orare tashmë mbajnë vula kohore ts të plota',
    dType: 'Formati i përgjigjes: "standard" (parazgjedhje) ose "chart"',
    dCorrection:
      'Korrigjimi i prodhimit: "true" ose "false". Nëse hiqet, përdoret vlera e parazgjedhur e instalimit.',
    dLat: 'Gjerësia gjeografike (p.sh. 52.23)',
    dLon: 'Gjatësia gjeografike (p.sh. 21.01)',
    dTz: 'Zhvendosja e zonës kohore UTC (-12 deri 12, p.sh. 2 për CET verore)',
    dAz: 'Azimuti i string-ut {{n}} në gradë (-180 deri 180; 0=veri, 180=jug)',
    dTilt: 'Pjerrësia e string-ut {{n}} në gradë (0 deri 90)',
    dPanels: 'Numri i paneleve në string-un {{n}}',
    dPower: 'Fuqia e panelit në kW në string-un {{n}} (p.sh. 0.4 = 400W)',
    dEff: 'Efikasiteti i panelit të string-ut {{n}} në % (p.sh. 92)',
    s2Footnote:
      '* — I detyrueshëm në modin e parametrave të URL-së. Në modin e instalimit, parametrat e vendndodhjes dhe string-ut merren automatikisht. Parametrat e vendndodhjes (lat, lon, tz) dhe strings (az, t, n, s, e) kërkojnë opsionin "allowQueryParams" në plan.',
    s3Title: 'Formati i përgjigjes',
    s3StdTitle: 'Përgjigje standarde (type=standard)',
    s3StdDesc: 'Formati parazgjedhje — prodhimi orar në vat-orë (Wh) si objekt çelës-vlerë.',
    s3DateFmtTitle: 'Çelësat e ditëve si data (date=full / date=timestamp)',
    s3DateFmtDesc: 'Me date=full çelësat e ditëve në result dhe strings bëhen data VVVV-MM-DD, dhe totalet ditore kthehen në hartën "totals" me të njëjtat data. date=timestamp punon njësoj me sekonda Unix (fillimi i ditës në zonën tz). Vlen edhe për bllokun "confidence" me enriched=1. Në formatin chart ndryshojnë vetëm totalet — hyrjet orare tashmë mbajnë vula kohore ts të plota.',
    s3ChartTitle: 'Përgjigje grafiku (type=chart)',
    s3ChartDesc:
      'Format i optimizuar për grafikë — një varg objektesh me një timestamp ISO dhe një vlerë energjie.',
    s3FieldsTitle: 'Përshkrimi i fushave',
    thField: 'Fusha',
    fDate: 'Timestamp i gjenerimit të përgjigjes (ISO 8601)',
    fLatLon: 'Koordinatat e përdorura në kërkesë',
    fToday: 'Prodhimi orar për sot (çelësi = ora, vlera = Wh)',
    fTomorrow: 'Prodhimi orar për nesër',
    fTotals: 'Prodhimi total për ditën e dhënë (Wh); me date=full/timestamp kthehet si hartë "totals" me çelësa ditësh',
    fStrings: 'Varg të dhënash për çdo string (azimut, efikasitet, fuqi, numër panelesh, prodhim)',
    fCorrApplied: 'Nëse u aplikua korrigjimi i prodhimit (true/false)',
    fProcTime: 'Koha e përpunimit të kërkesës në ms',
    fWeather:
      'Konteksti i motit: vranësira çdo orë, temperatura ajër/panele, era, reshjet, bora, kodi WMO, UV, lindja/perëndimi i diellit + përmbledhje ditore.',
    fWeatherAlerts: 'Paralajmërimet aktive MeteoAlarm për vendndodhjen (varg bosh nëse asnjë).',
    fProdAlerts:
      'Sinjalizime prodhimi nga parashikimi: borë mbi panele, vranësira të dendura, shpërthime të forta ere.',
    fAirQuality:
      'Cilësia e ajrit (PM10/PM2.5/pluhur/AOD) + vlerësimi i ndotjes së paneleve dhe rekomandim pastrimi.',
    fArchive:
      'Profili energjetik vjetor i vendndodhjes nga arkivi i rianalizës: rrezatimi vjetor, renditja mujore, pjerrësia/azimuti optimal i vlerësuar (best-effort, rifreskohet në sfond).',
    fConfidence:
      'Brezat e prodhimit P10/P50/P90 nga shpërndarja e modeleve ensemble (vetëm kur janë konfiguruar disa modele).',
    s4Title: 'Kodet e gabimit',
    s4Intro:
      'Në rast gabimi, API-ja kthen një objekt JSON me një fushë "error" që përmban "code" dhe "message".',
    thCode: 'Kodi',
    thHttp: 'HTTP',
    eValidationError: 'Parametra query-je që mungojnë ose janë të pavlefshëm',
    eNoInstallation: 'Asnjë instalim i ruajtur dhe asnjë parametër URL',
    eApiKeyMissing: 'Asnjë çelës API në header',
    eApiKeyInvalid: 'Çelësi API nuk ekziston ose është i pavlefshëm',
    eApiKeyExpired: 'Çelësi API ka skaduar',
    eApiKeyInactive: 'Çelësi API është çaktivizuar',
    ePlanLimitExceeded: 'Numri i strings tejkalon limitin e planit',
    eQueryParamsNotAllowed: 'Plani nuk lejon parametra URL dinamikë',
    eRateLimitExceeded: 'Limiti i kërkesave i tejkaluar (rate limit)',
    eInternalError: 'Gabim i brendshëm i serverit',
    eServiceError: 'Gabim shërbimi',
    s5Title: 'Korrigjimi i prodhimit',
    s5Intro:
      'Funksioni i korrigjimit përmirëson saktësinë e parashikimit bazuar në të dhënat reale të prodhimit. Sistemi krahason prodhimin e parashikuar me atë real dhe llogarit faktorë korrigjimi për orë (UTC). Kërkon opsionin "correctionEnabled" në plan.',
    s5SendTitle: 'Dërgimi i të dhënave reale',
    s5SendDesc: 'Dërgo prodhimin orar për një ditë të dhënë te endpoint-i:',
    s5DateD: 'date — data në formatin YYYY-MM-DD',
    s5TzD: 'tz — zona kohore (zhvendosja UTC) e përdorur gjatë leximit të prodhimit',
    s5ProdD: 'production — objekt: çelësi është ora (string), vlera është prodhimi në Wh',
    s5EnableTitle: 'Aktivizimi i korrigjimit në parashikim',
    s5EnableDesc:
      'Shto parametrin "correction=true" te kërkesa e parashikimit, ose vendos korrigjimin e parazgjedhur në portalin e klientit (skeda Korrigjimi).',
    s5Note:
      'Nëse parametri "correction" nuk jepet, sistemi përdor vlerën e parazgjedhur të instalimit tënd.',
    s6Title: 'Shembuj',
    ex1: 'Parashikim për sot (modi i instalimit)',
    ex2: 'Parashikim për nesër (modi i instalimit)',
    ex3: 'Parashikim me konfigurim të plotë (2 strings)',
    ex4: 'Formati i grafikut (për grafikë)',
    ex5: 'Parashikim me korrigjim prodhimi',
    ex6: 'Trajtimi i përgjigjes (bash + jq)',
    ex7: 'Parashikim me data në vend të today/tomorrow (date=full)',
  },
  mk: {
    title: 'Помош — документација на API',
    subtitle:
      'Целосна документација на Solar Forecast API: режими, параметри, формати на одговор и кодови на грешки.',
    toc: 'Содржина',
    example: 'Пример',
    exampleResponse: 'Пример на одговор',
    exampleError: 'Пример на грешка',
    s1Title: 'Како да се користи API-то',
    s1Intro:
      'Forecast API поддржува два режима. Во двата, API клучот мора да се проследи во HTTP заглавие (името на заглавието е дефинирано во планот — стандардно X-API-Key).',
    s1Mode1Title: 'Режим 1: Инсталација (поедноставено)',
    s1Mode1Desc:
      'Сите параметри на инсталацијата (локација, стрингови) се земаат автоматски од вашиот профил. Само проследете го параметарот "day".',
    s1Mode2Title: 'Режим 2: URL параметри (целосна контрола)',
    s1Mode2Desc:
      'Можете да ги проследите сите параметри директно во низата за барање. Бара план со овозможена опција "allowQueryParams". URL параметрите ги заменуваат оние од зачуваната инсталација.',
    s1Important:
      'Важно: параметарот "day" е задолжителен во двата режима. Дозволени вредности: "today" или "tomorrow".',
    s2Title: 'Параметри на барањето',
    s2Intro:
      'Подолу е целосната листа на параметри на низата за барање за крајната точка GET {{endpoint}}.',
    thParam: 'Параметар',
    thType: 'Тип',
    thRequired: 'Задолжителен',
    thDesc: 'Опис',
    reqYes: 'да',
    reqNo: 'не',
    dDay: 'Ден на прогнозата: "today" или "tomorrow"',
    dDate: 'Формат на клучевите за денови: "relative" (стандардно), "full" (ГГГГ-ММ-ДД) или "timestamp" (Unix секунди). Со "full"/"timestamp" дневните збирови се враќаат во мапа "totals" со истите клучеви. Во форматот chart се менуваат само збировите — часовните записи веќе носат целосни временски ознаки ts',
    dType: 'Формат на одговорот: "standard" (стандардно) или "chart"',
    dCorrection:
      'Корекција на производството: "true" или "false". Ако се изостави, се користи стандардната вредност на инсталацијата.',
    dLat: 'Географска ширина (пр. 52.23)',
    dLon: 'Географска должина (пр. 21.01)',
    dTz: 'Поместување на временската зона UTC (-12 до 12, пр. 2 за летно CET)',
    dAz: 'Азимут на стрингот {{n}} во степени (-180 до 180; 0=север, 180=југ)',
    dTilt: 'Наклон на стрингот {{n}} во степени (0 до 90)',
    dPanels: 'Број на панели во стрингот {{n}}',
    dPower: 'Моќност на панелот во kW во стрингот {{n}} (пр. 0.4 = 400W)',
    dEff: 'Ефикасност на панелот на стрингот {{n}} во % (пр. 92)',
    s2Footnote:
      '* — Задолжителен во режим на URL параметри. Во режим на инсталација, параметрите за локација и стрингови се земаат автоматски. Параметрите за локација (lat, lon, tz) и стринговите (az, t, n, s, e) бараат опција "allowQueryParams" во планот.',
    s3Title: 'Формат на одговорот',
    s3StdTitle: 'Стандарден одговор (type=standard)',
    s3StdDesc:
      'Стандарден формат — часовно производство во ват-часови (Wh) како објект клуч-вредност.',
    s3DateFmtTitle: 'Клучеви за денови како датуми (date=full / date=timestamp)',
    s3DateFmtDesc: 'Со date=full клучевите за денови во result и strings стануваат датуми ГГГГ-ММ-ДД, а дневните збирови се враќаат во мапа "totals" со истите датуми. date=timestamp работи исто со Unix секунди (почеток на денот во зоната tz). Важи и за блокот "confidence" со enriched=1. Во форматот chart се менуваат само збировите — часовните записи веќе носат целосни временски ознаки ts.',
    s3ChartTitle: 'Одговор за графикон (type=chart)',
    s3ChartDesc:
      'Формат оптимизиран за графикони — низа од објекти со ISO временска ознака и вредност на енергија.',
    s3FieldsTitle: 'Опис на полињата',
    thField: 'Поле',
    fDate: 'Временска ознака на генерирањето на одговорот (ISO 8601)',
    fLatLon: 'Координати користени во барањето',
    fToday: 'Часовно производство за денес (клуч = час, вредност = Wh)',
    fTomorrow: 'Часовно производство за утре',
    fTotals: 'Вкупно производство за дадениот ден (Wh); со date=full/timestamp се враќа како мапа "totals" со клучеви за денови',
    fStrings:
      'Низа на податоци по стринг (азимут, ефикасност, моќност, број на панели, производство)',
    fCorrApplied: 'Дали е применета корекција на производството (true/false)',
    fProcTime: 'Време на обработка на барањето во ms',
    fWeather:
      'Временски контекст: часовна облачност, температура воздух/панели, ветер, врнежи, снег, WMO код, UV, изгрев/зајдисонце + дневен преглед.',
    fWeatherAlerts: 'Активни предупредувања MeteoAlarm за локацијата (празна низа ако нема).',
    fProdAlerts:
      'Производни предупредувања изведени од прогнозата: снег на панелите, густа облачност, силни налети.',
    fAirQuality:
      'Квалитет на воздухот (PM10/PM2.5/прашина/AOD) + проценка на извалкувањето на панелите и препорака за чистење.',
    fArchive:
      'Годишен енергетски профил на локацијата од архивата на реанализи: годишно зрачење, рангирање по месеци, проценети оптимални наклон/азимут (best-effort, се освежува во позадина).',
    fConfidence:
      'Производни појаси P10/P50/P90 од расејувањето на ансамбл моделите (само кога се конфигурирани повеќе модели).',
    s4Title: 'Кодови на грешки',
    s4Intro:
      'Во случај на грешка, API-то враќа JSON објект со поле "error" што содржи "code" и "message".',
    thCode: 'Код',
    thHttp: 'HTTP',
    eValidationError: 'Параметри на барањето што недостасуваат или се невалидни',
    eNoInstallation: 'Нема зачувана инсталација и нема URL параметри',
    eApiKeyMissing: 'Нема API клуч во заглавието',
    eApiKeyInvalid: 'API клучот не постои или е невалиден',
    eApiKeyExpired: 'API клучот истече',
    eApiKeyInactive: 'API клучот е деактивиран',
    ePlanLimitExceeded: 'Бројот на стрингови го надминува лимитот на планот',
    eQueryParamsNotAllowed: 'Планот не дозволува динамички URL параметри',
    eRateLimitExceeded: 'Надминат лимит на барања (rate limit)',
    eInternalError: 'Внатрешна грешка на серверот',
    eServiceError: 'Грешка во услугата',
    s5Title: 'Корекција на производството',
    s5Intro:
      'Функцијата за корекција ја подобрува точноста на прогнозата врз основа на реални производствени податоци. Системот ја споредува прогнозираното производство со реалното и пресметува корекциски фактори по час (UTC). Бара опција "correctionEnabled" во планот.',
    s5SendTitle: 'Испраќање на реални податоци',
    s5SendDesc: 'Испратете го часовното производство за даден ден до крајната точка:',
    s5DateD: 'date — датум во формат YYYY-MM-DD',
    s5TzD: 'tz — временска зона (поместување UTC) што се користи при читање на производството',
    s5ProdD: 'production — објект: клучот е часот (стринг), вредноста е производството во Wh',
    s5EnableTitle: 'Овозможување на корекцијата во прогнозата',
    s5EnableDesc:
      'Додадете го параметарот "correction=true" на барањето за прогноза или поставете ја стандардната корекција во клиентскиот портал (картичка Корекција).',
    s5Note:
      'Ако параметарот "correction" не е даден, системот ја користи стандардната вредност на вашата инсталација.',
    s6Title: 'Примери',
    ex1: 'Прогноза за денес (режим на инсталација)',
    ex2: 'Прогноза за утре (режим на инсталација)',
    ex3: 'Прогноза со целосна конфигурација (2 стринга)',
    ex4: 'Формат на графикон (за графикони)',
    ex5: 'Прогноза со корекција на производството',
    ex6: 'Обработка на одговорот (bash + jq)',
    ex7: 'Прогноза со датуми наместо today/tomorrow (date=full)',
  },
  bs: {
    title: 'Pomoć — dokumentacija API-ja',
    subtitle:
      'Potpuna dokumentacija Solar Forecast API-ja: režimi, parametri, formati odgovora i kodovi grešaka.',
    toc: 'Sadržaj',
    example: 'Primjer',
    exampleResponse: 'Primjer odgovora',
    exampleError: 'Primjer greške',
    s1Title: 'Kako koristiti API',
    s1Intro:
      'Forecast API podržava dva režima. U oba se API ključ mora proslijediti u HTTP zaglavlju (naziv zaglavlja je definisan u planu — zadano X-API-Key).',
    s1Mode1Title: 'Režim 1: Postrojenje (pojednostavljeno)',
    s1Mode1Desc:
      'Svi parametri postrojenja (lokacija, stringovi) automatski se preuzimaju iz vašeg profila. Samo proslijedite parametar "day".',
    s1Mode2Title: 'Režim 2: URL parametri (potpuna kontrola)',
    s1Mode2Desc:
      'Sve parametre možete proslijediti direktno u upitnom nizu. Zahtijeva plan s omogućenom opcijom "allowQueryParams". URL parametri nadjačavaju one iz spremljenog postrojenja.',
    s1Important:
      'Važno: parametar "day" je obavezan u oba režima. Dozvoljene vrijednosti: "today" ili "tomorrow".',
    s2Title: 'Parametri upita',
    s2Intro: 'Ispod je potpun popis parametara upitnog niza za krajnju tačku GET {{endpoint}}.',
    thParam: 'Parametar',
    thType: 'Vrsta',
    thRequired: 'Obavezno',
    thDesc: 'Opis',
    reqYes: 'da',
    reqNo: 'ne',
    dDay: 'Dan prognoze: "today" ili "tomorrow"',
    dDate: 'Format ključeva dana: "relative" (zadano), "full" (GGGG-MM-DD) ili "timestamp" (Unix sekunde). Uz "full"/"timestamp" dnevni zbirovi se vraćaju u mapi "totals" s istim ključevima. U formatu chart mijenjaju se samo zbirovi — satni unosi već nose pune vremenske oznake ts',
    dType: 'Format odgovora: "standard" (zadano) ili "chart"',
    dCorrection:
      'Korekcija proizvodnje: "true" ili "false". Ako se izostavi, koristi se zadana vrijednost postrojenja.',
    dLat: 'Geografska širina (npr. 52.23)',
    dLon: 'Geografska dužina (npr. 21.01)',
    dTz: 'Pomak vremenske zone UTC (-12 do 12, npr. 2 za ljetno CET)',
    dAz: 'Azimut stringa {{n}} u stepenima (-180 do 180; 0=sjever, 180=jug)',
    dTilt: 'Nagib stringa {{n}} u stepenima (0 do 90)',
    dPanels: 'Broj panela u stringu {{n}}',
    dPower: 'Snaga panela u kW u stringu {{n}} (npr. 0.4 = 400W)',
    dEff: 'Efikasnost panela stringa {{n}} u % (npr. 92)',
    s2Footnote:
      '* — Obavezno u režimu URL parametara. U režimu postrojenja parametri lokacije i stringova preuzimaju se automatski. Parametri lokacije (lat, lon, tz) i stringovi (az, t, n, s, e) zahtijevaju opciju "allowQueryParams" u planu.',
    s3Title: 'Format odgovora',
    s3StdTitle: 'Standardni odgovor (type=standard)',
    s3StdDesc: 'Zadani format — satna proizvodnja u vat-satima (Wh) kao objekt ključ-vrijednost.',
    s3DateFmtTitle: 'Ključevi dana kao datumi (date=full / date=timestamp)',
    s3DateFmtDesc: 'Uz date=full ključevi dana u result i strings postaju datumi GGGG-MM-DD, a dnevni zbirovi se vraćaju u mapi "totals" s istim datumima. date=timestamp radi isto s Unix sekundama (početak dana u zoni tz). Vrijedi i za blok "confidence" uz enriched=1. U formatu chart mijenjaju se samo zbirovi — satni unosi već nose pune vremenske oznake ts.',
    s3ChartTitle: 'Odgovor za grafikon (type=chart)',
    s3ChartDesc:
      'Format optimizovan za grafikone — niz objekata s ISO vremenskom oznakom i vrijednošću energije.',
    s3FieldsTitle: 'Opis polja',
    thField: 'Polje',
    fDate: 'Vremenska oznaka generisanja odgovora (ISO 8601)',
    fLatLon: 'Koordinate korištene u zahtjevu',
    fToday: 'Satna proizvodnja za danas (ključ = sat, vrijednost = Wh)',
    fTomorrow: 'Satna proizvodnja za sutra',
    fTotals: 'Ukupna proizvodnja za dati dan (Wh); uz date=full/timestamp vraća se kao mapa "totals" s ključevima dana',
    fStrings: 'Niz podataka po stringu (azimut, efikasnost, snaga, broj panela, proizvodnja)',
    fCorrApplied: 'Da li je primijenjena korekcija proizvodnje (true/false)',
    fProcTime: 'Vrijeme obrade zahtjeva u ms',
    fWeather:
      'Vremenski kontekst: satna naoblaka, temperatura zraka/panela, vjetar, padavine, snijeg, WMO kod, UV, izlazak/zalazak sunca + dnevni pregled.',
    fWeatherAlerts: 'Aktivna upozorenja MeteoAlarm za lokaciju (prazan niz ako ih nema).',
    fProdAlerts:
      'Proizvodna upozorenja izvedena iz prognoze: snijeg na panelima, gusta naoblaka, jaki udari vjetra.',
    fAirQuality:
      'Kvalitet zraka (PM10/PM2.5/prašina/AOD) + procjena zaprljanja panela i preporuka za čišćenje.',
    fArchive:
      'Godišnji energetski profil lokacije iz arhiva reanaliza: godišnje zračenje, poredak mjeseci, procijenjeni optimalni nagib/azimut (best-effort, osvježava se u pozadini).',
    fConfidence:
      'Proizvodni pojasevi P10/P50/P90 iz raspršenosti ansambl modela (samo kada je konfigurisano više modela).',
    s4Title: 'Kodovi grešaka',
    s4Intro:
      'U slučaju greške, API vraća JSON objekt s poljem "error" koje sadrži "code" i "message".',
    thCode: 'Kod',
    thHttp: 'HTTP',
    eValidationError: 'Nedostajući ili nevažeći parametri upita',
    eNoInstallation: 'Nema spremljenog postrojenja i nema URL parametara',
    eApiKeyMissing: 'Nema API ključa u zaglavlju',
    eApiKeyInvalid: 'API ključ ne postoji ili je nevažeći',
    eApiKeyExpired: 'API ključ je istekao',
    eApiKeyInactive: 'API ključ je deaktiviran',
    ePlanLimitExceeded: 'Broj stringova premašuje ograničenje plana',
    eQueryParamsNotAllowed: 'Plan ne dozvoljava dinamičke URL parametre',
    eRateLimitExceeded: 'Premašeno ograničenje zahtjeva (rate limit)',
    eInternalError: 'Interna greška servera',
    eServiceError: 'Greška usluge',
    s5Title: 'Korekcija proizvodnje',
    s5Intro:
      'Funkcija korekcije poboljšava tačnost prognoze na osnovu stvarnih proizvodnih podataka. Sistem upoređuje prognoziranu proizvodnju sa stvarnom i izračunava korekcijske faktore po satu (UTC). Zahtijeva opciju "correctionEnabled" u planu.',
    s5SendTitle: 'Slanje stvarnih podataka',
    s5SendDesc: 'Pošaljite satnu proizvodnju za dati dan na krajnju tačku:',
    s5DateD: 'date — datum u formatu YYYY-MM-DD',
    s5TzD: 'tz — vremenska zona (pomak UTC) korištena pri čitanju proizvodnje',
    s5ProdD: 'production — objekt: ključ je sat (string), vrijednost je proizvodnja u Wh',
    s5EnableTitle: 'Omogućavanje korekcije u prognozi',
    s5EnableDesc:
      'Dodajte parametar "correction=true" zahtjevu za prognozu ili postavite zadanu korekciju u korisničkom portalu (kartica Korekcija).',
    s5Note:
      'Ako parametar "correction" nije naveden, sistem koristi zadanu vrijednost vašeg postrojenja.',
    s6Title: 'Primjeri',
    ex1: 'Prognoza za danas (režim postrojenja)',
    ex2: 'Prognoza za sutra (režim postrojenja)',
    ex3: 'Prognoza s potpunom konfiguracijom (2 stringa)',
    ex4: 'Format grafikona (za grafikone)',
    ex5: 'Prognoza s korekcijom proizvodnje',
    ex6: 'Obrada odgovora (bash + jq)',
    ex7: 'Prognoza s datumima umjesto today/tomorrow (date=full)',
  },
};

/** Namespace 'admin' — dodatkowe klucze (strona Mapy). */
const adminTr2: Record<string, Dict> = {
  en: {
    mapLoadFailed: 'Failed to load the client map',
    mapLoading: 'Loading map…',
    mapTitle: 'Installations map',
    mapSubtitle: '{{clients}} clients with {{installations}} installations',
    popupEmail: 'E-mail',
    popupLocation: 'Location',
    popupStringCount: 'String count',
    popupTotalPanels: 'Total panels',
  },
  pl: {
    mapLoadFailed: 'Nie udało się załadować mapy klientów',
    mapLoading: 'Wczytywanie mapy…',
    mapTitle: 'Mapa instalacji',
    mapSubtitle: '{{clients}} klientów z {{installations}} instalacjami',
    popupEmail: 'E-mail',
    popupLocation: 'Lokalizacja',
    popupStringCount: 'Liczba stringów',
    popupTotalPanels: 'Całkowita liczba paneli',
  },
  de: {
    mapLoadFailed: 'Kundenkarte konnte nicht geladen werden',
    mapLoading: 'Karte wird geladen…',
    mapTitle: 'Anlagenkarte',
    mapSubtitle: '{{clients}} Kunden mit {{installations}} Anlagen',
    popupEmail: 'E-Mail',
    popupLocation: 'Standort',
    popupStringCount: 'Anzahl Strings',
    popupTotalPanels: 'Module gesamt',
  },
  fr: {
    mapLoadFailed: 'Échec du chargement de la carte des clients',
    mapLoading: 'Chargement de la carte…',
    mapTitle: 'Carte des installations',
    mapSubtitle: '{{clients}} clients avec {{installations}} installations',
    popupEmail: 'E-mail',
    popupLocation: 'Emplacement',
    popupStringCount: 'Nombre de chaînes',
    popupTotalPanels: 'Panneaux au total',
  },
  es: {
    mapLoadFailed: 'No se pudo cargar el mapa de clientes',
    mapLoading: 'Cargando mapa…',
    mapTitle: 'Mapa de instalaciones',
    mapSubtitle: '{{clients}} clientes con {{installations}} instalaciones',
    popupEmail: 'Correo electrónico',
    popupLocation: 'Ubicación',
    popupStringCount: 'Número de cadenas',
    popupTotalPanels: 'Paneles en total',
  },
  it: {
    mapLoadFailed: 'Impossibile caricare la mappa dei clienti',
    mapLoading: 'Caricamento mappa…',
    mapTitle: 'Mappa degli impianti',
    mapSubtitle: '{{clients}} clienti con {{installations}} impianti',
    popupEmail: 'E-mail',
    popupLocation: 'Posizione',
    popupStringCount: 'Numero di stringhe',
    popupTotalPanels: 'Pannelli totali',
  },
  pt: {
    mapLoadFailed: 'Não foi possível carregar o mapa de clientes',
    mapLoading: 'A carregar mapa…',
    mapTitle: 'Mapa de instalações',
    mapSubtitle: '{{clients}} clientes com {{installations}} instalações',
    popupEmail: 'E-mail',
    popupLocation: 'Localização',
    popupStringCount: 'Número de strings',
    popupTotalPanels: 'Painéis no total',
  },
  nl: {
    mapLoadFailed: 'Kon klantenkaart niet laden',
    mapLoading: 'Kaart laden…',
    mapTitle: 'Installatiekaart',
    mapSubtitle: '{{clients}} klanten met {{installations}} installaties',
    popupEmail: 'E-mail',
    popupLocation: 'Locatie',
    popupStringCount: 'Aantal strings',
    popupTotalPanels: 'Panelen totaal',
  },
  da: {
    mapLoadFailed: 'Kunne ikke indlæse kundekortet',
    mapLoading: 'Indlæser kort…',
    mapTitle: 'Anlægskort',
    mapSubtitle: '{{clients}} kunder med {{installations}} anlæg',
    popupEmail: 'E-mail',
    popupLocation: 'Placering',
    popupStringCount: 'Antal strenge',
    popupTotalPanels: 'Paneler i alt',
  },
  sv: {
    mapLoadFailed: 'Kunde inte ladda kundkartan',
    mapLoading: 'Laddar karta…',
    mapTitle: 'Anläggningskarta',
    mapSubtitle: '{{clients}} kunder med {{installations}} anläggningar',
    popupEmail: 'E-post',
    popupLocation: 'Plats',
    popupStringCount: 'Antal strängar',
    popupTotalPanels: 'Paneler totalt',
  },
  fi: {
    mapLoadFailed: 'Asiakaskartan lataaminen epäonnistui',
    mapLoading: 'Ladataan karttaa…',
    mapTitle: 'Laitoskartta',
    mapSubtitle: '{{clients}} asiakasta, {{installations}} laitosta',
    popupEmail: 'Sähköposti',
    popupLocation: 'Sijainti',
    popupStringCount: 'Sarjojen määrä',
    popupTotalPanels: 'Paneeleja yhteensä',
  },
  cs: {
    mapLoadFailed: 'Nepodařilo se načíst mapu klientů',
    mapLoading: 'Načítání mapy…',
    mapTitle: 'Mapa instalací',
    mapSubtitle: '{{clients}} klientů s {{installations}} instalacemi',
    popupEmail: 'E-mail',
    popupLocation: 'Poloha',
    popupStringCount: 'Počet stringů',
    popupTotalPanels: 'Panelů celkem',
  },
  sk: {
    mapLoadFailed: 'Nepodarilo sa načítať mapu klientov',
    mapLoading: 'Načítava sa mapa…',
    mapTitle: 'Mapa inštalácií',
    mapSubtitle: '{{clients}} klientov s {{installations}} inštaláciami',
    popupEmail: 'E-mail',
    popupLocation: 'Poloha',
    popupStringCount: 'Počet stringov',
    popupTotalPanels: 'Panelov celkom',
  },
  sl: {
    mapLoadFailed: 'Zemljevida strank ni bilo mogoče naložiti',
    mapLoading: 'Nalaganje zemljevida…',
    mapTitle: 'Zemljevid naprav',
    mapSubtitle: '{{clients}} strank z {{installations}} napravami',
    popupEmail: 'E-pošta',
    popupLocation: 'Lokacija',
    popupStringCount: 'Število nizov',
    popupTotalPanels: 'Panelov skupaj',
  },
  hr: {
    mapLoadFailed: 'Nije moguće učitati kartu klijenata',
    mapLoading: 'Učitavanje karte…',
    mapTitle: 'Karta postrojenja',
    mapSubtitle: '{{clients}} klijenata s {{installations}} postrojenja',
    popupEmail: 'E-mail',
    popupLocation: 'Lokacija',
    popupStringCount: 'Broj nizova',
    popupTotalPanels: 'Panela ukupno',
  },
  hu: {
    mapLoadFailed: 'Az ügyféltérkép betöltése sikertelen',
    mapLoading: 'Térkép betöltése…',
    mapTitle: 'Telepítések térképe',
    mapSubtitle: '{{clients}} ügyfél {{installations}} telepítéssel',
    popupEmail: 'E-mail',
    popupLocation: 'Helyszín',
    popupStringCount: 'Stringek száma',
    popupTotalPanels: 'Panelek összesen',
  },
  ro: {
    mapLoadFailed: 'Nu s-a putut încărca harta clienților',
    mapLoading: 'Se încarcă harta…',
    mapTitle: 'Harta instalațiilor',
    mapSubtitle: '{{clients}} clienți cu {{installations}} instalații',
    popupEmail: 'E-mail',
    popupLocation: 'Locație',
    popupStringCount: 'Număr de string-uri',
    popupTotalPanels: 'Panouri în total',
  },
  bg: {
    mapLoadFailed: 'Неуспешно зареждане на картата на клиентите',
    mapLoading: 'Зареждане на картата…',
    mapTitle: 'Карта на инсталациите',
    mapSubtitle: '{{clients}} клиенти с {{installations}} инсталации',
    popupEmail: 'Имейл',
    popupLocation: 'Местоположение',
    popupStringCount: 'Брой стрингове',
    popupTotalPanels: 'Панели общо',
  },
  el: {
    mapLoadFailed: 'Αποτυχία φόρτωσης του χάρτη πελατών',
    mapLoading: 'Φόρτωση χάρτη…',
    mapTitle: 'Χάρτης εγκαταστάσεων',
    mapSubtitle: '{{clients}} πελάτες με {{installations}} εγκαταστάσεις',
    popupEmail: 'E-mail',
    popupLocation: 'Τοποθεσία',
    popupStringCount: 'Αριθμός string',
    popupTotalPanels: 'Σύνολο πάνελ',
  },
  et: {
    mapLoadFailed: 'Kliendikaardi laadimine ebaõnnestus',
    mapLoading: 'Kaardi laadimine…',
    mapTitle: 'Paigaldiste kaart',
    mapSubtitle: '{{clients}} klienti, {{installations}} paigaldist',
    popupEmail: 'E-post',
    popupLocation: 'Asukoht',
    popupStringCount: 'Stringide arv',
    popupTotalPanels: 'Paneele kokku',
  },
  lv: {
    mapLoadFailed: 'Neizdevās ielādēt klientu karti',
    mapLoading: 'Notiek kartes ielāde…',
    mapTitle: 'Iekārtu karte',
    mapSubtitle: '{{clients}} klienti ar {{installations}} iekārtām',
    popupEmail: 'E-pasts',
    popupLocation: 'Atrašanās vieta',
    popupStringCount: 'Virkņu skaits',
    popupTotalPanels: 'Paneļi kopā',
  },
  lt: {
    mapLoadFailed: 'Nepavyko įkelti klientų žemėlapio',
    mapLoading: 'Įkeliamas žemėlapis…',
    mapTitle: 'Įrenginių žemėlapis',
    mapSubtitle: '{{clients}} klientų su {{installations}} įrenginiais',
    popupEmail: 'El. paštas',
    popupLocation: 'Vieta',
    popupStringCount: 'Eilučių skaičius',
    popupTotalPanels: 'Plokščių iš viso',
  },
  mt: {
    mapLoadFailed: 'Ma setax jiġi mgħobbi l-mappa tal-klijenti',
    mapLoading: 'Qed tiġi mgħobbija l-mappa…',
    mapTitle: 'Mappa tal-installazzjonijiet',
    mapSubtitle: "{{clients}} klijenti b'{{installations}} installazzjonijiet",
    popupEmail: 'E-mail',
    popupLocation: 'Post',
    popupStringCount: "Numru ta' strings",
    popupTotalPanels: 'Pannelli totali',
  },
  ga: {
    mapLoadFailed: 'Theip ar lódáil léarscáil na gcliant',
    mapLoading: 'Léarscáil á lódáil…',
    mapTitle: 'Léarscáil suiteálacha',
    mapSubtitle: '{{clients}} cliant le {{installations}} suiteáil',
    popupEmail: 'Ríomhphost',
    popupLocation: 'Suíomh',
    popupStringCount: 'Líon slabhraí',
    popupTotalPanels: 'Painéil san iomlán',
  },
  no: {
    mapLoadFailed: 'Kunne ikke laste kundekartet',
    mapLoading: 'Laster kart…',
    mapTitle: 'Anleggskart',
    mapSubtitle: '{{clients}} kunder med {{installations}} anlegg',
    popupEmail: 'E-post',
    popupLocation: 'Plassering',
    popupStringCount: 'Antall strenger',
    popupTotalPanels: 'Paneler totalt',
  },
  is: {
    mapLoadFailed: 'Ekki tókst að hlaða viðskiptavinakorti',
    mapLoading: 'Hleður korti…',
    mapTitle: 'Kort uppsetninga',
    mapSubtitle: '{{clients}} viðskiptavinir með {{installations}} uppsetningar',
    popupEmail: 'Netfang',
    popupLocation: 'Staðsetning',
    popupStringCount: 'Fjöldi strengja',
    popupTotalPanels: 'Spjöld samtals',
  },
  uk: {
    mapLoadFailed: 'Не вдалося завантажити карту клієнтів',
    mapLoading: 'Завантаження карти…',
    mapTitle: 'Карта установок',
    mapSubtitle: '{{clients}} клієнтів з {{installations}} установками',
    popupEmail: 'Електронна пошта',
    popupLocation: 'Розташування',
    popupStringCount: 'Кількість стрінгів',
    popupTotalPanels: 'Панелей загалом',
  },
  sr: {
    mapLoadFailed: 'Nije moguće učitati mapu klijenata',
    mapLoading: 'Učitavanje mape…',
    mapTitle: 'Mapa postrojenja',
    mapSubtitle: '{{clients}} klijenata sa {{installations}} postrojenja',
    popupEmail: 'E-pošta',
    popupLocation: 'Lokacija',
    popupStringCount: 'Broj nizova',
    popupTotalPanels: 'Panela ukupno',
  },
  sq: {
    mapLoadFailed: 'Harta e klientëve nuk u ngarkua',
    mapLoading: 'Po ngarkohet harta…',
    mapTitle: 'Harta e instalimeve',
    mapSubtitle: '{{clients}} klientë me {{installations}} instalime',
    popupEmail: 'E-mail',
    popupLocation: 'Vendndodhja',
    popupStringCount: 'Numri i vargjeve',
    popupTotalPanels: 'Panele në total',
  },
  mk: {
    mapLoadFailed: 'Не може да се вчита картата на клиентите',
    mapLoading: 'Се вчитува картата…',
    mapTitle: 'Карта на инсталациите',
    mapSubtitle: '{{clients}} клиенти со {{installations}} инсталации',
    popupEmail: 'Е-пошта',
    popupLocation: 'Локација',
    popupStringCount: 'Број на стрингови',
    popupTotalPanels: 'Панели вкупно',
  },
  bs: {
    mapLoadFailed: 'Nije moguće učitati mapu klijenata',
    mapLoading: 'Učitavanje mape…',
    mapTitle: 'Mapa postrojenja',
    mapSubtitle: '{{clients}} klijenata sa {{installations}} postrojenja',
    popupEmail: 'E-pošta',
    popupLocation: 'Lokacija',
    popupStringCount: 'Broj nizova',
    popupTotalPanels: 'Panela ukupno',
  },
};

const modelAccTr: Record<string, Dict> = {
  en: {
    title: 'Model accuracy & backtest',
    installation: 'Installation',
    selectOption: '— select —',
    periodDays: 'Period (days)',
    loading: 'Loading…',
    fetch: 'Fetch',
    enterInstId: 'Enter Installation ID',
    backtestTitle: 'Backtest (ensemble forecast vs actual)',
    samples: 'Samples',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Daily sums (kWh)',
    date: 'Date',
    forecast: 'Forecast',
    actual: 'Actual',
    deviation: 'Deviation',
    perModelTitle: 'Accuracy per model (sorted best first)',
    noModelData:
      'No data. Make sure the installation has stored ActualProduction and ModelForecastCache.',
    model: 'Model',
  },
  pl: {
    title: 'Dokładność modeli i backtest',
    installation: 'Instalacja',
    selectOption: '— wybierz —',
    periodDays: 'Okres (dni)',
    loading: 'Ładowanie…',
    fetch: 'Pobierz',
    enterInstId: 'Podaj Installation ID',
    backtestTitle: 'Backtest (prognoza ensemble vs rzeczywista)',
    samples: 'Próbki',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dzienne sumy (kWh)',
    date: 'Data',
    forecast: 'Prognoza',
    actual: 'Rzeczywista',
    deviation: 'Odchylenie',
    perModelTitle: 'Dokładność per model (sortowane od najlepszego)',
    noModelData:
      'Brak danych. Upewnij się, że instalacja ma zapisaną ActualProduction oraz ModelForecastCache.',
    model: 'Model',
  },
  de: {
    title: 'Modellgenauigkeit & Backtest',
    installation: 'Anlage',
    selectOption: '— auswählen —',
    periodDays: 'Zeitraum (Tage)',
    loading: 'Wird geladen…',
    fetch: 'Abrufen',
    enterInstId: 'Installations-ID eingeben',
    backtestTitle: 'Backtest (Ensemble-Prognose vs. Ist)',
    samples: 'Stichproben',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Tagessummen (kWh)',
    date: 'Datum',
    forecast: 'Prognose',
    actual: 'Ist',
    deviation: 'Abweichung',
    perModelTitle: 'Genauigkeit pro Modell (bestes zuerst)',
    noModelData:
      'Keine Daten. Stellen Sie sicher, dass die Anlage ActualProduction und ModelForecastCache gespeichert hat.',
    model: 'Modell',
  },
  fr: {
    title: 'Précision des modèles & backtest',
    installation: 'Installation',
    selectOption: '— choisir —',
    periodDays: 'Période (jours)',
    loading: 'Chargement…',
    fetch: 'Récupérer',
    enterInstId: "Saisir l'ID d'installation",
    backtestTitle: 'Backtest (prévision ensemble vs réel)',
    samples: 'Échantillons',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Biais [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Sommes quotidiennes (kWh)',
    date: 'Date',
    forecast: 'Prévision',
    actual: 'Réel',
    deviation: 'Écart',
    perModelTitle: 'Précision par modèle (meilleur en premier)',
    noModelData:
      "Aucune donnée. Assurez-vous que l'installation a enregistré ActualProduction et ModelForecastCache.",
    model: 'Modèle',
  },
  es: {
    title: 'Precisión de modelos y backtest',
    installation: 'Instalación',
    selectOption: '— seleccionar —',
    periodDays: 'Período (días)',
    loading: 'Cargando…',
    fetch: 'Obtener',
    enterInstId: 'Introduce el ID de instalación',
    backtestTitle: 'Backtest (pronóstico ensemble vs real)',
    samples: 'Muestras',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Sesgo [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Sumas diarias (kWh)',
    date: 'Fecha',
    forecast: 'Pronóstico',
    actual: 'Real',
    deviation: 'Desviación',
    perModelTitle: 'Precisión por modelo (mejor primero)',
    noModelData:
      'Sin datos. Asegúrate de que la instalación tenga guardado ActualProduction y ModelForecastCache.',
    model: 'Modelo',
  },
  it: {
    title: 'Precisione dei modelli e backtest',
    installation: 'Impianto',
    selectOption: '— seleziona —',
    periodDays: 'Periodo (giorni)',
    loading: 'Caricamento…',
    fetch: 'Recupera',
    enterInstId: "Inserisci l'ID impianto",
    backtestTitle: 'Backtest (previsione ensemble vs reale)',
    samples: 'Campioni',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Somme giornaliere (kWh)',
    date: 'Data',
    forecast: 'Previsione',
    actual: 'Reale',
    deviation: 'Scostamento',
    perModelTitle: 'Precisione per modello (migliore per primo)',
    noModelData:
      "Nessun dato. Assicurati che l'impianto abbia salvato ActualProduction e ModelForecastCache.",
    model: 'Modello',
  },
  pt: {
    title: 'Precisão dos modelos e backtest',
    installation: 'Instalação',
    selectOption: '— selecionar —',
    periodDays: 'Período (dias)',
    loading: 'A carregar…',
    fetch: 'Obter',
    enterInstId: 'Insira o ID da instalação',
    backtestTitle: 'Backtest (previsão ensemble vs real)',
    samples: 'Amostras',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Viés [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Somas diárias (kWh)',
    date: 'Data',
    forecast: 'Previsão',
    actual: 'Real',
    deviation: 'Desvio',
    perModelTitle: 'Precisão por modelo (melhor primeiro)',
    noModelData:
      'Sem dados. Garanta que a instalação tem ActualProduction e ModelForecastCache guardados.',
    model: 'Modelo',
  },
  nl: {
    title: 'Modelnauwkeurigheid & backtest',
    installation: 'Installatie',
    selectOption: '— kiezen —',
    periodDays: 'Periode (dagen)',
    loading: 'Laden…',
    fetch: 'Ophalen',
    enterInstId: 'Voer installatie-ID in',
    backtestTitle: 'Backtest (ensemble-prognose vs werkelijk)',
    samples: 'Monsters',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dagelijkse sommen (kWh)',
    date: 'Datum',
    forecast: 'Prognose',
    actual: 'Werkelijk',
    deviation: 'Afwijking',
    perModelTitle: 'Nauwkeurigheid per model (beste eerst)',
    noModelData:
      'Geen data. Zorg dat de installatie ActualProduction en ModelForecastCache heeft opgeslagen.',
    model: 'Model',
  },
  da: {
    title: 'Modelnøjagtighed & backtest',
    installation: 'Anlæg',
    selectOption: '— vælg —',
    periodDays: 'Periode (dage)',
    loading: 'Indlæser…',
    fetch: 'Hent',
    enterInstId: 'Indtast anlægs-ID',
    backtestTitle: 'Backtest (ensemble-prognose vs faktisk)',
    samples: 'Stikprøver',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Daglige summer (kWh)',
    date: 'Dato',
    forecast: 'Prognose',
    actual: 'Faktisk',
    deviation: 'Afvigelse',
    perModelTitle: 'Nøjagtighed pr. model (bedste først)',
    noModelData:
      'Ingen data. Sørg for, at anlægget har gemt ActualProduction og ModelForecastCache.',
    model: 'Model',
  },
  sv: {
    title: 'Modellprecision & backtest',
    installation: 'Anläggning',
    selectOption: '— välj —',
    periodDays: 'Period (dagar)',
    loading: 'Laddar…',
    fetch: 'Hämta',
    enterInstId: 'Ange anläggnings-ID',
    backtestTitle: 'Backtest (ensembleprognos vs faktiskt)',
    samples: 'Stickprov',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dagliga summor (kWh)',
    date: 'Datum',
    forecast: 'Prognos',
    actual: 'Faktiskt',
    deviation: 'Avvikelse',
    perModelTitle: 'Precision per modell (bästa först)',
    noModelData:
      'Inga data. Se till att anläggningen har sparat ActualProduction och ModelForecastCache.',
    model: 'Modell',
  },
  fi: {
    title: 'Mallien tarkkuus ja backtest',
    installation: 'Laitos',
    selectOption: '— valitse —',
    periodDays: 'Jakso (päivää)',
    loading: 'Ladataan…',
    fetch: 'Hae',
    enterInstId: 'Anna laitoksen tunnus',
    backtestTitle: 'Backtest (ensemble-ennuste vs toteuma)',
    samples: 'Näytteet',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Päivittäiset summat (kWh)',
    date: 'Päivä',
    forecast: 'Ennuste',
    actual: 'Toteuma',
    deviation: 'Poikkeama',
    perModelTitle: 'Tarkkuus malleittain (paras ensin)',
    noModelData:
      'Ei dataa. Varmista, että laitoksella on tallennettuna ActualProduction ja ModelForecastCache.',
    model: 'Malli',
  },
  cs: {
    title: 'Přesnost modelů a backtest',
    installation: 'Instalace',
    selectOption: '— vyberte —',
    periodDays: 'Období (dny)',
    loading: 'Načítání…',
    fetch: 'Načíst',
    enterInstId: 'Zadejte ID instalace',
    backtestTitle: 'Backtest (ensemble předpověď vs skutečnost)',
    samples: 'Vzorky',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Denní součty (kWh)',
    date: 'Datum',
    forecast: 'Předpověď',
    actual: 'Skutečnost',
    deviation: 'Odchylka',
    perModelTitle: 'Přesnost podle modelu (nejlepší první)',
    noModelData:
      'Žádná data. Ujistěte se, že instalace má uloženou ActualProduction a ModelForecastCache.',
    model: 'Model',
  },
  sk: {
    title: 'Presnosť modelov a backtest',
    installation: 'Inštalácia',
    selectOption: '— vyberte —',
    periodDays: 'Obdobie (dni)',
    loading: 'Načítava sa…',
    fetch: 'Načítať',
    enterInstId: 'Zadajte ID inštalácie',
    backtestTitle: 'Backtest (ensemble predpoveď vs skutočnosť)',
    samples: 'Vzorky',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Denné súčty (kWh)',
    date: 'Dátum',
    forecast: 'Predpoveď',
    actual: 'Skutočnosť',
    deviation: 'Odchýlka',
    perModelTitle: 'Presnosť podľa modelu (najlepší prvý)',
    noModelData:
      'Žiadne dáta. Uistite sa, že inštalácia má uloženú ActualProduction a ModelForecastCache.',
    model: 'Model',
  },
  sl: {
    title: 'Natančnost modelov in backtest',
    installation: 'Naprava',
    selectOption: '— izberite —',
    periodDays: 'Obdobje (dni)',
    loading: 'Nalaganje…',
    fetch: 'Pridobi',
    enterInstId: 'Vnesite ID naprave',
    backtestTitle: 'Backtest (napoved ansambla vs dejansko)',
    samples: 'Vzorci',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dnevne vsote (kWh)',
    date: 'Datum',
    forecast: 'Napoved',
    actual: 'Dejansko',
    deviation: 'Odstopanje',
    perModelTitle: 'Natančnost po modelu (najboljši prvi)',
    noModelData:
      'Ni podatkov. Poskrbite, da ima naprava shranjena ActualProduction in ModelForecastCache.',
    model: 'Model',
  },
  hr: {
    title: 'Točnost modela i backtest',
    installation: 'Postrojenje',
    selectOption: '— odaberite —',
    periodDays: 'Razdoblje (dani)',
    loading: 'Učitavanje…',
    fetch: 'Dohvati',
    enterInstId: 'Unesite ID postrojenja',
    backtestTitle: 'Backtest (ansambl prognoza vs stvarno)',
    samples: 'Uzorci',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dnevne sume (kWh)',
    date: 'Datum',
    forecast: 'Prognoza',
    actual: 'Stvarno',
    deviation: 'Odstupanje',
    perModelTitle: 'Točnost po modelu (najbolji prvi)',
    noModelData:
      'Nema podataka. Provjerite ima li postrojenje spremljene ActualProduction i ModelForecastCache.',
    model: 'Model',
  },
  hu: {
    title: 'Modellpontosság és backteszt',
    installation: 'Létesítmény',
    selectOption: '— válasszon —',
    periodDays: 'Időszak (nap)',
    loading: 'Betöltés…',
    fetch: 'Lekérés',
    enterInstId: 'Adja meg a létesítmény azonosítóját',
    backtestTitle: 'Backteszt (ensemble előrejelzés vs tény)',
    samples: 'Minták',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Torzítás [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Napi összegek (kWh)',
    date: 'Dátum',
    forecast: 'Előrejelzés',
    actual: 'Tény',
    deviation: 'Eltérés',
    perModelTitle: 'Pontosság modellenként (legjobb elöl)',
    noModelData:
      'Nincs adat. Győződjön meg róla, hogy a létesítményhez el van mentve az ActualProduction és a ModelForecastCache.',
    model: 'Modell',
  },
  ro: {
    title: 'Acuratețea modelelor și backtest',
    installation: 'Instalație',
    selectOption: '— selectați —',
    periodDays: 'Perioadă (zile)',
    loading: 'Se încarcă…',
    fetch: 'Preia',
    enterInstId: 'Introduceți ID-ul instalației',
    backtestTitle: 'Backtest (prognoză ensemble vs real)',
    samples: 'Eșantioane',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Sume zilnice (kWh)',
    date: 'Data',
    forecast: 'Prognoză',
    actual: 'Real',
    deviation: 'Abatere',
    perModelTitle: 'Acuratețe pe model (cel mai bun primul)',
    noModelData:
      'Fără date. Asigurați-vă că instalația are salvate ActualProduction și ModelForecastCache.',
    model: 'Model',
  },
  bg: {
    title: 'Точност на моделите и бектест',
    installation: 'Инсталация',
    selectOption: '— изберете —',
    periodDays: 'Период (дни)',
    loading: 'Зареждане…',
    fetch: 'Извличане',
    enterInstId: 'Въведете ID на инсталацията',
    backtestTitle: 'Бектест (ансамблова прогноза vs реално)',
    samples: 'Извадки',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Дневни суми (kWh)',
    date: 'Дата',
    forecast: 'Прогноза',
    actual: 'Реално',
    deviation: 'Отклонение',
    perModelTitle: 'Точност по модел (най-добрият първи)',
    noModelData:
      'Няма данни. Уверете се, че инсталацията има записани ActualProduction и ModelForecastCache.',
    model: 'Модел',
  },
  el: {
    title: 'Ακρίβεια μοντέλων & backtest',
    installation: 'Εγκατάσταση',
    selectOption: '— επιλέξτε —',
    periodDays: 'Περίοδος (ημέρες)',
    loading: 'Φόρτωση…',
    fetch: 'Ανάκτηση',
    enterInstId: 'Εισαγάγετε το ID εγκατάστασης',
    backtestTitle: 'Backtest (πρόβλεψη ensemble vs πραγματικό)',
    samples: 'Δείγματα',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Ημερήσια σύνολα (kWh)',
    date: 'Ημερομηνία',
    forecast: 'Πρόβλεψη',
    actual: 'Πραγματικό',
    deviation: 'Απόκλιση',
    perModelTitle: 'Ακρίβεια ανά μοντέλο (καλύτερο πρώτο)',
    noModelData:
      'Δεν υπάρχουν δεδομένα. Βεβαιωθείτε ότι η εγκατάσταση έχει αποθηκευμένα ActualProduction και ModelForecastCache.',
    model: 'Μοντέλο',
  },
  et: {
    title: 'Mudelite täpsus ja backtest',
    installation: 'Paigaldis',
    selectOption: '— vali —',
    periodDays: 'Periood (päeva)',
    loading: 'Laadimine…',
    fetch: 'Hangi',
    enterInstId: 'Sisesta paigaldise ID',
    backtestTitle: 'Backtest (ansambli prognoos vs tegelik)',
    samples: 'Valimid',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Päevased summad (kWh)',
    date: 'Kuupäev',
    forecast: 'Prognoos',
    actual: 'Tegelik',
    deviation: 'Hälve',
    perModelTitle: 'Täpsus mudeli kaupa (parim esimesena)',
    noModelData:
      'Andmed puuduvad. Veendu, et paigaldisel on salvestatud ActualProduction ja ModelForecastCache.',
    model: 'Mudel',
  },
  lv: {
    title: 'Modeļu precizitāte un backtest',
    installation: 'Iekārta',
    selectOption: '— izvēlieties —',
    periodDays: 'Periods (dienas)',
    loading: 'Notiek ielāde…',
    fetch: 'Iegūt',
    enterInstId: 'Ievadiet iekārtas ID',
    backtestTitle: 'Backtest (ansambļa prognoze vs faktiskais)',
    samples: 'Paraugi',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dienas summas (kWh)',
    date: 'Datums',
    forecast: 'Prognoze',
    actual: 'Faktiskais',
    deviation: 'Novirze',
    perModelTitle: 'Precizitāte pa modeļiem (labākais pirmais)',
    noModelData:
      'Nav datu. Pārliecinieties, ka iekārtai ir saglabāti ActualProduction un ModelForecastCache.',
    model: 'Modelis',
  },
  lt: {
    title: 'Modelių tikslumas ir backtest',
    installation: 'Įrenginys',
    selectOption: '— pasirinkite —',
    periodDays: 'Laikotarpis (dienos)',
    loading: 'Įkeliama…',
    fetch: 'Gauti',
    enterInstId: 'Įveskite įrenginio ID',
    backtestTitle: 'Backtest (ansamblio prognozė vs faktinė)',
    samples: 'Imtys',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dienos sumos (kWh)',
    date: 'Data',
    forecast: 'Prognozė',
    actual: 'Faktinė',
    deviation: 'Nuokrypis',
    perModelTitle: 'Tikslumas pagal modelį (geriausias pirmas)',
    noModelData:
      'Nėra duomenų. Įsitikinkite, kad įrenginys turi išsaugotus ActualProduction ir ModelForecastCache.',
    model: 'Modelis',
  },
  mt: {
    title: 'Preċiżjoni tal-mudelli u backtest',
    installation: 'Installazzjoni',
    selectOption: '— agħżel —',
    periodDays: 'Perjodu (jiem)',
    loading: 'Qed jitgħabba…',
    fetch: 'Iġbed',
    enterInstId: 'Daħħal l-ID tal-installazzjoni',
    backtestTitle: 'Backtest (tbassir ensemble vs reali)',
    samples: 'Kampjuni',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: "Total ta' kuljum (kWh)",
    date: 'Data',
    forecast: 'Tbassir',
    actual: 'Reali',
    deviation: 'Devjazzjoni',
    perModelTitle: 'Preċiżjoni skont mudell (l-aħjar l-ewwel)',
    noModelData:
      'Ebda data. Aċċerta li l-installazzjoni għandha ActualProduction u ModelForecastCache maħżuna.',
    model: 'Mudell',
  },
  ga: {
    title: 'Cruinneas múnlaí & backtest',
    installation: 'Suiteáil',
    selectOption: '— roghnaigh —',
    periodDays: 'Tréimhse (laethanta)',
    loading: 'Á luchtú…',
    fetch: 'Faigh',
    enterInstId: 'Cuir isteach ID na suiteála',
    backtestTitle: 'Backtest (réamhaisnéis ensemble vs fíor)',
    samples: 'Samplaí',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Suimeanna laethúla (kWh)',
    date: 'Dáta',
    forecast: 'Réamhaisnéis',
    actual: 'Fíor',
    deviation: 'Diall',
    perModelTitle: 'Cruinneas de réir múnla (an ceann is fearr ar dtús)',
    noModelData:
      'Gan sonraí. Cinntigh go bhfuil ActualProduction agus ModelForecastCache sábháilte ag an tsuiteáil.',
    model: 'Múnla',
  },
  no: {
    title: 'Modellnøyaktighet & backtest',
    installation: 'Anlegg',
    selectOption: '— velg —',
    periodDays: 'Periode (dager)',
    loading: 'Laster…',
    fetch: 'Hent',
    enterInstId: 'Skriv inn anleggs-ID',
    backtestTitle: 'Backtest (ensemble-prognose vs faktisk)',
    samples: 'Prøver',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Daglige summer (kWh)',
    date: 'Dato',
    forecast: 'Prognose',
    actual: 'Faktisk',
    deviation: 'Avvik',
    perModelTitle: 'Nøyaktighet per modell (beste først)',
    noModelData:
      'Ingen data. Sørg for at anlegget har lagret ActualProduction og ModelForecastCache.',
    model: 'Modell',
  },
  is: {
    title: 'Nákvæmni líkana & backtest',
    installation: 'Stöð',
    selectOption: '— veldu —',
    periodDays: 'Tímabil (dagar)',
    loading: 'Hleður…',
    fetch: 'Sækja',
    enterInstId: 'Sláðu inn auðkenni stöðvar',
    backtestTitle: 'Backtest (ensemble-spá vs raun)',
    samples: 'Sýni',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dagleg samtölur (kWh)',
    date: 'Dagsetning',
    forecast: 'Spá',
    actual: 'Raun',
    deviation: 'Frávik',
    perModelTitle: 'Nákvæmni eftir líkani (besta fyrst)',
    noModelData:
      'Engin gögn. Gakktu úr skugga um að stöðin hafi vistuð ActualProduction og ModelForecastCache.',
    model: 'Líkan',
  },
  uk: {
    title: 'Точність моделей і бектест',
    installation: 'Установка',
    selectOption: '— виберіть —',
    periodDays: 'Період (дні)',
    loading: 'Завантаження…',
    fetch: 'Отримати',
    enterInstId: 'Введіть ID установки',
    backtestTitle: 'Бектест (ансамблевий прогноз vs фактичне)',
    samples: 'Вибірки',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Денні суми (kWh)',
    date: 'Дата',
    forecast: 'Прогноз',
    actual: 'Фактичне',
    deviation: 'Відхилення',
    perModelTitle: 'Точність за моделлю (найкраща перша)',
    noModelData:
      'Немає даних. Переконайтеся, що установка має збережені ActualProduction і ModelForecastCache.',
    model: 'Модель',
  },
  sr: {
    title: 'Тачност модела и бектест',
    installation: 'Постројење',
    selectOption: '— изаберите —',
    periodDays: 'Период (дани)',
    loading: 'Учитавање…',
    fetch: 'Преузми',
    enterInstId: 'Унесите ИД постројења',
    backtestTitle: 'Бектест (ансамбл прогноза vs стварно)',
    samples: 'Узорци',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Дневне суме (kWh)',
    date: 'Датум',
    forecast: 'Прогноза',
    actual: 'Стварно',
    deviation: 'Одступање',
    perModelTitle: 'Тачност по моделу (најбољи први)',
    noModelData:
      'Нема података. Проверите да ли постројење има сачуване ActualProduction и ModelForecastCache.',
    model: 'Модел',
  },
  sq: {
    title: 'Saktësia e modeleve & backtest',
    installation: 'Instalimi',
    selectOption: '— zgjidh —',
    periodDays: 'Periudha (ditë)',
    loading: 'Po ngarkohet…',
    fetch: 'Merr',
    enterInstId: 'Fut ID-në e instalimit',
    backtestTitle: 'Backtest (parashikim ensemble vs real)',
    samples: 'Mostra',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Shuma ditore (kWh)',
    date: 'Data',
    forecast: 'Parashikim',
    actual: 'Real',
    deviation: 'Devijim',
    perModelTitle: 'Saktësia sipas modelit (më i miri i pari)',
    noModelData:
      'Nuk ka të dhëna. Sigurohu që instalimi ka të ruajtura ActualProduction dhe ModelForecastCache.',
    model: 'Modeli',
  },
  mk: {
    title: 'Точност на моделите и бектест',
    installation: 'Инсталација',
    selectOption: '— изберете —',
    periodDays: 'Период (денови)',
    loading: 'Се вчитува…',
    fetch: 'Преземи',
    enterInstId: 'Внесете ID на инсталацијата',
    backtestTitle: 'Бектест (ансамбл прогноза vs реално)',
    samples: 'Примероци',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Дневни суми (kWh)',
    date: 'Датум',
    forecast: 'Прогноза',
    actual: 'Реално',
    deviation: 'Отстапување',
    perModelTitle: 'Точност по модел (најдобар прв)',
    noModelData:
      'Нема податоци. Проверете дали инсталацијата има зачувано ActualProduction и ModelForecastCache.',
    model: 'Модел',
  },
  bs: {
    title: 'Tačnost modela i backtest',
    installation: 'Postrojenje',
    selectOption: '— odaberite —',
    periodDays: 'Period (dani)',
    loading: 'Učitavanje…',
    fetch: 'Dohvati',
    enterInstId: 'Unesite ID postrojenja',
    backtestTitle: 'Backtest (ansambl prognoza vs stvarno)',
    samples: 'Uzorci',
    rmseWh: 'RMSE [Wh]',
    maeWh: 'MAE [Wh]',
    biasWh: 'Bias [Wh]',
    nrmsePct: 'NRMSE %',
    dailySums: 'Dnevne sume (kWh)',
    date: 'Datum',
    forecast: 'Prognoza',
    actual: 'Stvarno',
    deviation: 'Odstupanje',
    perModelTitle: 'Tačnost po modelu (najbolji prvi)',
    noModelData:
      'Nema podataka. Provjerite ima li postrojenje sačuvane ActualProduction i ModelForecastCache.',
    model: 'Model',
  },
};

const carbonTr: Record<string, Dict> = {
  en: {
    title: 'Avoided CO₂',
    today: 'Today',
    tomorrow: 'Tomorrow',
    carEquiv: '≈ {{km}} km by car',
    intensity: 'Grid intensity',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'avg estimate',
  },
  pl: {
    title: 'Uniknione CO₂',
    today: 'Dziś',
    tomorrow: 'Jutro',
    carEquiv: '≈ {{km}} km autem',
    intensity: 'Emisje sieci',
    sourceLive: 'na żywo (Electricity Maps)',
    sourceStatic: 'średnia szac.',
  },
  de: {
    title: 'Vermiedenes CO₂',
    today: 'Heute',
    tomorrow: 'Morgen',
    carEquiv: '≈ {{km}} km mit dem Auto',
    intensity: 'Netzintensität',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'Schätzwert',
  },
  fr: {
    title: 'CO₂ évité',
    today: 'Aujourd’hui',
    tomorrow: 'Demain',
    carEquiv: '≈ {{km}} km en voiture',
    intensity: 'Intensité réseau',
    sourceLive: 'en direct (Electricity Maps)',
    sourceStatic: 'estimation moyenne',
  },
  es: {
    title: 'CO₂ evitado',
    today: 'Hoy',
    tomorrow: 'Mañana',
    carEquiv: '≈ {{km}} km en coche',
    intensity: 'Intensidad de red',
    sourceLive: 'en vivo (Electricity Maps)',
    sourceStatic: 'estimación media',
  },
  it: {
    title: 'CO₂ evitato',
    today: 'Oggi',
    tomorrow: 'Domani',
    carEquiv: '≈ {{km}} km in auto',
    intensity: 'Intensità di rete',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'stima media',
  },
  pt: {
    title: 'CO₂ evitado',
    today: 'Hoje',
    tomorrow: 'Amanhã',
    carEquiv: '≈ {{km}} km de carro',
    intensity: 'Intensidade da rede',
    sourceLive: 'em direto (Electricity Maps)',
    sourceStatic: 'estimativa média',
  },
  nl: {
    title: 'Vermeden CO₂',
    today: 'Vandaag',
    tomorrow: 'Morgen',
    carEquiv: '≈ {{km}} km met de auto',
    intensity: 'Netintensiteit',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'gemiddelde schatting',
  },
  da: {
    title: 'Undgået CO₂',
    today: 'I dag',
    tomorrow: 'I morgen',
    carEquiv: '≈ {{km}} km i bil',
    intensity: 'Netintensitet',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'gennemsnitsestimat',
  },
  sv: {
    title: 'Undvikt CO₂',
    today: 'I dag',
    tomorrow: 'I morgon',
    carEquiv: '≈ {{km}} km med bil',
    intensity: 'Elnätsintensitet',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'genomsnittlig uppskattning',
  },
  fi: {
    title: 'Vältetty CO₂',
    today: 'Tänään',
    tomorrow: 'Huomenna',
    carEquiv: '≈ {{km}} km autolla',
    intensity: 'Verkon päästö-intensiteetti',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'keskim. arvio',
  },
  cs: {
    title: 'Ušetřené CO₂',
    today: 'Dnes',
    tomorrow: 'Zítra',
    carEquiv: '≈ {{km}} km autem',
    intensity: 'Intenzita sítě',
    sourceLive: 'živě (Electricity Maps)',
    sourceStatic: 'průměr. odhad',
  },
  sk: {
    title: 'Ušetrené CO₂',
    today: 'Dnes',
    tomorrow: 'Zajtra',
    carEquiv: '≈ {{km}} km autom',
    intensity: 'Intenzita siete',
    sourceLive: 'naživo (Electricity Maps)',
    sourceStatic: 'priemer. odhad',
  },
  sl: {
    title: 'Privarčevano CO₂',
    today: 'Danes',
    tomorrow: 'Jutri',
    carEquiv: '≈ {{km}} km z avtom',
    intensity: 'Intenzivnost omrežja',
    sourceLive: 'v živo (Electricity Maps)',
    sourceStatic: 'povp. ocena',
  },
  hr: {
    title: 'Izbjegnuti CO₂',
    today: 'Danas',
    tomorrow: 'Sutra',
    carEquiv: '≈ {{km}} km autom',
    intensity: 'Intenzitet mreže',
    sourceLive: 'uživo (Electricity Maps)',
    sourceStatic: 'prosj. procjena',
  },
  hu: {
    title: 'Megtakarított CO₂',
    today: 'Ma',
    tomorrow: 'Holnap',
    carEquiv: '≈ {{km}} km autóval',
    intensity: 'Hálózati intenzitás',
    sourceLive: 'élő (Electricity Maps)',
    sourceStatic: 'átlagos becslés',
  },
  ro: {
    title: 'CO₂ evitat',
    today: 'Azi',
    tomorrow: 'Mâine',
    carEquiv: '≈ {{km}} km cu mașina',
    intensity: 'Intensitatea rețelei',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'estimare medie',
  },
  bg: {
    title: 'Спестен CO₂',
    today: 'Днес',
    tomorrow: 'Утре',
    carEquiv: '≈ {{km}} km с автомобил',
    intensity: 'Интензитет на мрежата',
    sourceLive: 'на живо (Electricity Maps)',
    sourceStatic: 'средна оценка',
  },
  el: {
    title: 'Αποφευχθέν CO₂',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
    carEquiv: '≈ {{km}} km με αυτοκίνητο',
    intensity: 'Ένταση δικτύου',
    sourceLive: 'ζωντανά (Electricity Maps)',
    sourceStatic: 'μέση εκτίμηση',
  },
  et: {
    title: 'Välditud CO₂',
    today: 'Täna',
    tomorrow: 'Homme',
    carEquiv: '≈ {{km}} km autoga',
    intensity: 'Võrgu intensiivsus',
    sourceLive: 'reaalajas (Electricity Maps)',
    sourceStatic: 'keskm. hinnang',
  },
  lv: {
    title: 'Novērstais CO₂',
    today: 'Šodien',
    tomorrow: 'Rīt',
    carEquiv: '≈ {{km}} km ar auto',
    intensity: 'Tīkla intensitāte',
    sourceLive: 'tiešraidē (Electricity Maps)',
    sourceStatic: 'vid. novērtējums',
  },
  lt: {
    title: 'Išvengtas CO₂',
    today: 'Šiandien',
    tomorrow: 'Rytoj',
    carEquiv: '≈ {{km}} km automobiliu',
    intensity: 'Tinklo intensyvumas',
    sourceLive: 'tiesiogiai (Electricity Maps)',
    sourceStatic: 'vid. įvertis',
  },
  mt: {
    title: 'CO₂ evitat',
    today: 'Illum',
    tomorrow: 'Għada',
    carEquiv: '≈ {{km}} km bil-karozza',
    intensity: 'Intensità tax-xibka',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'stima medja',
  },
  ga: {
    title: 'CO₂ seachanta',
    today: 'Inniu',
    tomorrow: 'Amárach',
    carEquiv: '≈ {{km}} km le carr',
    intensity: 'Déine eangaí',
    sourceLive: 'beo (Electricity Maps)',
    sourceStatic: 'meastachán meánach',
  },
  no: {
    title: 'Unngått CO₂',
    today: 'I dag',
    tomorrow: 'I morgen',
    carEquiv: '≈ {{km}} km med bil',
    intensity: 'Nettintensitet',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'gjennomsnittsestimat',
  },
  is: {
    title: 'Forðað CO₂',
    today: 'Í dag',
    tomorrow: 'Á morgun',
    carEquiv: '≈ {{km}} km á bíl',
    intensity: 'Netskerpa',
    sourceLive: 'í beinni (Electricity Maps)',
    sourceStatic: 'meðaltalsmat',
  },
  uk: {
    title: 'Уникнуті CO₂',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    carEquiv: '≈ {{km}} км на авто',
    intensity: 'Інтенсивність мережі',
    sourceLive: 'наживо (Electricity Maps)',
    sourceStatic: 'середня оцінка',
  },
  sr: {
    title: 'Избегнут CO₂',
    today: 'Данас',
    tomorrow: 'Сутра',
    carEquiv: '≈ {{km}} km аутом',
    intensity: 'Интензитет мреже',
    sourceLive: 'уживо (Electricity Maps)',
    sourceStatic: 'просечна процена',
  },
  sq: {
    title: 'CO₂ i shmangur',
    today: 'Sot',
    tomorrow: 'Nesër',
    carEquiv: '≈ {{km}} km me makinë',
    intensity: 'Intensiteti i rrjetit',
    sourceLive: 'live (Electricity Maps)',
    sourceStatic: 'vlerësim mesatar',
  },
  mk: {
    title: 'Избегнат CO₂',
    today: 'Денес',
    tomorrow: 'Утре',
    carEquiv: '≈ {{km}} km со автомобил',
    intensity: 'Интензитет на мрежата',
    sourceLive: 'во живо (Electricity Maps)',
    sourceStatic: 'просечна проценка',
  },
  bs: {
    title: 'Izbjegnuti CO₂',
    today: 'Danas',
    tomorrow: 'Sutra',
    carEquiv: '≈ {{km}} km automobilom',
    intensity: 'Intenzitet mreže',
    sourceLive: 'uživo (Electricity Maps)',
    sourceStatic: 'prosj. procjena',
  },
};

const horizonTr: Record<string, Dict> = {
  en: {
    title: 'Horizon shading',
    solarAvg: 'Avg horizon (S band)',
    solarMax: 'Max horizon (S band)',
    annualLoss: 'Est. annual loss',
    siteElevation: 'Site elevation',
    degrees: '°',
    noShading: 'No significant horizon shading detected.',
  },
  pl: {
    title: 'Zacienienie z horyzontu',
    solarAvg: 'Średnia (pas S)',
    solarMax: 'Maks. (pas S)',
    annualLoss: 'Szac. strata roczna',
    siteElevation: 'Wysokość terenu',
    degrees: '°',
    noShading: 'Brak znaczącego zacienienia z horyzontu.',
  },
  de: {
    title: 'Horizontverschattung',
    solarAvg: 'Mittelw. (S-Bereich)',
    solarMax: 'Max. (S-Bereich)',
    annualLoss: 'Geschätzter Jahresverlust',
    siteElevation: 'Geländehöhe',
    degrees: '°',
    noShading: 'Keine signifikante Horizontverschattung erkannt.',
  },
  fr: {
    title: 'Ombrage de l’horizon',
    solarAvg: 'Moyenne (zone S)',
    solarMax: 'Max (zone S)',
    annualLoss: 'Perte annuelle estimée',
    siteElevation: 'Altitude du site',
    degrees: '°',
    noShading: 'Pas d’ombrage significatif détecté.',
  },
  es: {
    title: 'Sombreado del horizonte',
    solarAvg: 'Media (zona S)',
    solarMax: 'Máx (zona S)',
    annualLoss: 'Pérdida anual est.',
    siteElevation: 'Altitud del sitio',
    degrees: '°',
    noShading: 'No se detecta sombreado significativo.',
  },
  it: {
    title: 'Ombreggiatura dell’orizzonte',
    solarAvg: 'Media (banda S)',
    solarMax: 'Max (banda S)',
    annualLoss: 'Perdita annua stimata',
    siteElevation: 'Quota del sito',
    degrees: '°',
    noShading: 'Nessuna ombreggiatura significativa rilevata.',
  },
  pt: {
    title: 'Sombreamento do horizonte',
    solarAvg: 'Média (banda S)',
    solarMax: 'Máx (banda S)',
    annualLoss: 'Perda anual estimada',
    siteElevation: 'Altitude do local',
    degrees: '°',
    noShading: 'Sem sombreamento significativo detectado.',
  },
  nl: {
    title: 'Horizonschaduw',
    solarAvg: 'Gem. (Z-band)',
    solarMax: 'Max (Z-band)',
    annualLoss: 'Geschat jaarverlies',
    siteElevation: 'Locatiehoogte',
    degrees: '°',
    noShading: 'Geen significante horizonschaduw gedetecteerd.',
  },
  da: {
    title: 'Horisontskygge',
    solarAvg: 'Gns. (S-bånd)',
    solarMax: 'Maks. (S-bånd)',
    annualLoss: 'Anslået årligt tab',
    siteElevation: 'Stedhøjde',
    degrees: '°',
    noShading: 'Ingen væsentlig horisontskygge fundet.',
  },
  sv: {
    title: 'Horisontskugga',
    solarAvg: 'Snitt (S-bandet)',
    solarMax: 'Max (S-bandet)',
    annualLoss: 'Uppskattat årligt bortfall',
    siteElevation: 'Höjd över havet',
    degrees: '°',
    noShading: 'Ingen betydande horisontskugga upptäckt.',
  },
  fi: {
    title: 'Horisonttivarjostus',
    solarAvg: 'Keskim. (E-vyö)',
    solarMax: 'Maks. (E-vyö)',
    annualLoss: 'Arvio vuotuisesta hävikistä',
    siteElevation: 'Maaston korkeus',
    degrees: '°',
    noShading: 'Merkittävää horisonttivarjostusta ei havaittu.',
  },
  cs: {
    title: 'Stínění horizontem',
    solarAvg: 'Průměr (pásmo J)',
    solarMax: 'Max. (pásmo J)',
    annualLoss: 'Odhad ročních ztrát',
    siteElevation: 'Nadm. výška',
    degrees: '°',
    noShading: 'Nezjištěno významné stínění horizontem.',
  },
  sk: {
    title: 'Tienenie horizontom',
    solarAvg: 'Priem. (pásmo J)',
    solarMax: 'Max. (pásmo J)',
    annualLoss: 'Odhad ročných strát',
    siteElevation: 'Nadm. výška',
    degrees: '°',
    noShading: 'Nezistené významné tienenie horizontom.',
  },
  sl: {
    title: 'Senčenje obzorja',
    solarAvg: 'Povp. (pas J)',
    solarMax: 'Najv. (pas J)',
    annualLoss: 'Oc. letne izgube',
    siteElevation: 'Nadm. višina',
    degrees: '°',
    noShading: 'Pomembnejše senčenje obzorja ni zaznano.',
  },
  hr: {
    title: 'Sjenčanje horizontom',
    solarAvg: 'Prosj. (pojas J)',
    solarMax: 'Maks. (pojas J)',
    annualLoss: 'Procj. godišnji gubitak',
    siteElevation: 'Nadmorska visina',
    degrees: '°',
    noShading: 'Nije otkriveno značajno sjenčanje horizontom.',
  },
  hu: {
    title: 'Horizontárnyékolás',
    solarAvg: 'Átlag (D-sáv)',
    solarMax: 'Max. (D-sáv)',
    annualLoss: 'Becsült éves veszteség',
    siteElevation: 'Tengerszint feletti magasság',
    degrees: '°',
    noShading: 'Nincs jelentős horizontárnyékolás.',
  },
  ro: {
    title: 'Umbrire de la orizont',
    solarAvg: 'Medie (banda S)',
    solarMax: 'Max. (banda S)',
    annualLoss: 'Pierdere anuală estimată',
    siteElevation: 'Altitudine sit',
    degrees: '°',
    noShading: 'Nu s-a detectat umbrire semnificativă.',
  },
  bg: {
    title: 'Засенчване от хоризонта',
    solarAvg: 'Средно (зона Ю)',
    solarMax: 'Макс. (зона Ю)',
    annualLoss: 'Прибл. годишна загуба',
    siteElevation: 'Надм. височина',
    degrees: '°',
    noShading: 'Не е открито значимо засенчване.',
  },
  el: {
    title: 'Σκίαση από τον ορίζοντα',
    solarAvg: 'Μέσος (ζώνη Ν)',
    solarMax: 'Μέγ. (ζώνη Ν)',
    annualLoss: 'Εκτιμώμενη ετήσια απώλεια',
    siteElevation: 'Υψόμετρο',
    degrees: '°',
    noShading: 'Δεν εντοπίστηκε σημαντική σκίαση.',
  },
  et: {
    title: 'Horisondi varjutus',
    solarAvg: 'Keskmine (L-vöönd)',
    solarMax: 'Maks. (L-vöönd)',
    annualLoss: 'Hinnanguline aastane kadu',
    siteElevation: 'Asukoha kõrgus',
    degrees: '°',
    noShading: 'Olulist horisondi varjutust ei tuvastatud.',
  },
  lv: {
    title: 'Horizonta noēnojums',
    solarAvg: 'Vid. (D josla)',
    solarMax: 'Maks. (D josla)',
    annualLoss: 'Apt. gada zaudējumi',
    siteElevation: 'Vietas augstums',
    degrees: '°',
    noShading: 'Nav konstatēts būtisks horizonta noēnojums.',
  },
  lt: {
    title: 'Horizonto šešėliavimas',
    solarAvg: 'Vid. (P juosta)',
    solarMax: 'Maks. (P juosta)',
    annualLoss: 'Numatomi metiniai nuostoliai',
    siteElevation: 'Vietos aukštis',
    degrees: '°',
    noShading: 'Reikšmingo horizonto šešėliavimo nerasta.',
  },
  mt: {
    title: 'Tidlim mill-orizzont',
    solarAvg: 'Medja (medda N)',
    solarMax: 'Massimu (medda N)',
    annualLoss: 'Telf annwali stmat',
    siteElevation: 'Għoli tas-sit',
    degrees: '°',
    noShading: 'L-ebda tidlim sinifikanti.',
  },
  ga: {
    title: 'Scáthú ón léaslíne',
    solarAvg: 'Meán (banda T)',
    solarMax: 'Uas. (banda T)',
    annualLoss: 'Caillteanas bliantúil measta',
    siteElevation: 'Airde an láithreáin',
    degrees: '°',
    noShading: 'Níor aimsíodh aon scáthú suntasach.',
  },
  no: {
    title: 'Horisontskygge',
    solarAvg: 'Snitt (S-bånd)',
    solarMax: 'Maks. (S-bånd)',
    annualLoss: 'Anslått årlig tap',
    siteElevation: 'Stedshøyde',
    degrees: '°',
    noShading: 'Ingen betydelig horisontskygge funnet.',
  },
  is: {
    title: 'Skuggar af sjóndeildarhring',
    solarAvg: 'Meðaltal (S-belti)',
    solarMax: 'Hám. (S-belti)',
    annualLoss: 'Áætlað árstap',
    siteElevation: 'Hæð staðar',
    degrees: '°',
    noShading: 'Engin marktæk skugga frá sjóndeildarhring.',
  },
  uk: {
    title: 'Затінення горизонтом',
    solarAvg: 'Сер. (пд. зона)',
    solarMax: 'Макс. (пд. зона)',
    annualLoss: 'Орієнт. річні втрати',
    siteElevation: 'Висота над рівнем моря',
    degrees: '°',
    noShading: 'Суттєвого затінення не виявлено.',
  },
  sr: {
    title: 'Сенчење од хоризонта',
    solarAvg: 'Прос. (појас Ј)',
    solarMax: 'Макс. (појас Ј)',
    annualLoss: 'Проц. годишњи губитак',
    siteElevation: 'Надм. висина',
    degrees: '°',
    noShading: 'Није откривено значајно сенчење.',
  },
  sq: {
    title: 'Hijëzim nga horizonti',
    solarAvg: 'Mesatare (brezi J)',
    solarMax: 'Maks. (brezi J)',
    annualLoss: 'Humbja vjetore e vlerësuar',
    siteElevation: 'Lartësia e vendit',
    degrees: '°',
    noShading: 'Nuk u zbulua hijëzim i ndjeshëm.',
  },
  mk: {
    title: 'Засенчување од хоризонтот',
    solarAvg: 'Прос. (појас Ј)',
    solarMax: 'Макс. (појас Ј)',
    annualLoss: 'Проц. годишен губиток',
    siteElevation: 'Надм. височина',
    degrees: '°',
    noShading: 'Не е откриено значајно засенчување.',
  },
  bs: {
    title: 'Sjenčanje od horizonta',
    solarAvg: 'Pros. (pojas J)',
    solarMax: 'Maks. (pojas J)',
    annualLoss: 'Proc. godišnji gubitak',
    siteElevation: 'Nadm. visina',
    degrees: '°',
    noShading: 'Nije otkriveno značajno sjenčanje od horizonta.',
  },
};

const revenueChartTr: Record<string, Dict> = {
  en: {
    title: 'Production × PSE day-ahead price',
    dayTotal: 'Day total',
    peakHour: 'Peak revenue hour',
    bestPrice: 'Best price hour',
    hourRevenue: 'Hourly revenue',
    hourlyRevenue: 'Hourly revenue (PLN)',
    formula: 'Formula',
  },
  pl: {
    title: 'Produkcja × cena PSE RCE (day-ahead)',
    dayTotal: 'Suma dzienna',
    peakHour: 'Szczyt przychodu',
    bestPrice: 'Najlepsza cena',
    hourRevenue: 'Przychód godzinowy',
    hourlyRevenue: 'Godzinowy przychód (PLN)',
    formula: 'Wzór',
  },
  de: {
    title: 'Produktion × PSE-Day-Ahead-Preis',
    dayTotal: 'Tagessumme',
    peakHour: 'Peak-Ertragsstunde',
    bestPrice: 'Bester Preis',
    hourRevenue: 'Stundenerlös',
    hourlyRevenue: 'Stundenerlös (PLN)',
    formula: 'Formel',
  },
  fr: {
    title: 'Production × prix PSE day-ahead',
    dayTotal: 'Total du jour',
    peakHour: 'Heure de pic de revenu',
    bestPrice: 'Meilleur prix',
    hourRevenue: 'Revenu horaire',
    hourlyRevenue: 'Revenu horaire (PLN)',
    formula: 'Formule',
  },
  es: {
    title: 'Producción × precio PSE day-ahead',
    dayTotal: 'Total del día',
    peakHour: 'Hora pico de ingresos',
    bestPrice: 'Mejor precio',
    hourRevenue: 'Ingreso horario',
    hourlyRevenue: 'Ingreso horario (PLN)',
    formula: 'Fórmula',
  },
  it: {
    title: 'Produzione × prezzo PSE day-ahead',
    dayTotal: 'Totale giornaliero',
    peakHour: 'Ora di picco',
    bestPrice: 'Miglior prezzo',
    hourRevenue: 'Ricavo orario',
    hourlyRevenue: 'Ricavo orario (PLN)',
    formula: 'Formula',
  },
  pt: {
    title: 'Produção × preço PSE day-ahead',
    dayTotal: 'Total diário',
    peakHour: 'Hora de pico',
    bestPrice: 'Melhor preço',
    hourRevenue: 'Receita horária',
    hourlyRevenue: 'Receita horária (PLN)',
    formula: 'Fórmula',
  },
  nl: {
    title: 'Productie × PSE day-ahead-prijs',
    dayTotal: 'Dagtotaal',
    peakHour: 'Piek-uur',
    bestPrice: 'Beste prijs',
    hourRevenue: 'Uurinkomen',
    hourlyRevenue: 'Uurinkomen (PLN)',
    formula: 'Formule',
  },
  da: {
    title: 'Produktion × PSE day-ahead-pris',
    dayTotal: 'Dagstotal',
    peakHour: 'Toptime',
    bestPrice: 'Bedste pris',
    hourRevenue: 'Timesindkomst',
    hourlyRevenue: 'Timesindkomst (PLN)',
    formula: 'Formel',
  },
  sv: {
    title: 'Produktion × PSE day-ahead-pris',
    dayTotal: 'Dagstotal',
    peakHour: 'Topptimme',
    bestPrice: 'Bästa pris',
    hourRevenue: 'Timinkomst',
    hourlyRevenue: 'Timinkomst (PLN)',
    formula: 'Formel',
  },
  fi: {
    title: 'Tuotanto × PSE day-ahead-hinta',
    dayTotal: 'Päivän summa',
    peakHour: 'Huipputunti',
    bestPrice: 'Paras hinta',
    hourRevenue: 'Tuntitulot',
    hourlyRevenue: 'Tuntitulot (PLN)',
    formula: 'Kaava',
  },
  cs: {
    title: 'Výroba × cena PSE day-ahead',
    dayTotal: 'Denní suma',
    peakHour: 'Špičková hodina',
    bestPrice: 'Nejlepší cena',
    hourRevenue: 'Hodinový výnos',
    hourlyRevenue: 'Hodinový výnos (PLN)',
    formula: 'Vzorec',
  },
  sk: {
    title: 'Výroba × cena PSE day-ahead',
    dayTotal: 'Denná suma',
    peakHour: 'Špičková hodina',
    bestPrice: 'Najlepšia cena',
    hourRevenue: 'Hodinový výnos',
    hourlyRevenue: 'Hodinový výnos (PLN)',
    formula: 'Vzorec',
  },
  sl: {
    title: 'Proizvodnja × cena PSE day-ahead',
    dayTotal: 'Dnevni seštevek',
    peakHour: 'Vrh prihodka',
    bestPrice: 'Najboljša cena',
    hourRevenue: 'Urni prihodek',
    hourlyRevenue: 'Urni prihodek (PLN)',
    formula: 'Formula',
  },
  hr: {
    title: 'Proizvodnja × cijena PSE day-ahead',
    dayTotal: 'Dnevni zbroj',
    peakHour: 'Vrhunski sat',
    bestPrice: 'Najbolja cijena',
    hourRevenue: 'Satni prihod',
    hourlyRevenue: 'Satni prihod (PLN)',
    formula: 'Formula',
  },
  hu: {
    title: 'Termelés × PSE day-ahead ár',
    dayTotal: 'Napi összeg',
    peakHour: 'Csúcs óra',
    bestPrice: 'Legjobb ár',
    hourRevenue: 'Óránkénti bevétel',
    hourlyRevenue: 'Óránkénti bevétel (PLN)',
    formula: 'Képlet',
  },
  ro: {
    title: 'Producție × preț PSE day-ahead',
    dayTotal: 'Total zilnic',
    peakHour: 'Ora de vârf',
    bestPrice: 'Cel mai bun preț',
    hourRevenue: 'Venit orar',
    hourlyRevenue: 'Venit orar (PLN)',
    formula: 'Formulă',
  },
  bg: {
    title: 'Производство × цена PSE day-ahead',
    dayTotal: 'Дневен общ',
    peakHour: 'Пиков час',
    bestPrice: 'Най-добра цена',
    hourRevenue: 'Часов приход',
    hourlyRevenue: 'Часов приход (PLN)',
    formula: 'Формула',
  },
  el: {
    title: 'Παραγωγή × τιμή PSE day-ahead',
    dayTotal: 'Ημερήσιο σύνολο',
    peakHour: 'Ώρα αιχμής',
    bestPrice: 'Καλύτερη τιμή',
    hourRevenue: 'Ωριαίο έσοδο',
    hourlyRevenue: 'Ωριαίο έσοδο (PLN)',
    formula: 'Τύπος',
  },
  et: {
    title: 'Tootmine × PSE day-ahead hind',
    dayTotal: 'Päeva kokku',
    peakHour: 'Tipptund',
    bestPrice: 'Parim hind',
    hourRevenue: 'Tunni tulu',
    hourlyRevenue: 'Tunni tulu (PLN)',
    formula: 'Valem',
  },
  lv: {
    title: 'Ražošana × PSE day-ahead cena',
    dayTotal: 'Dienas kopā',
    peakHour: 'Pīķa stunda',
    bestPrice: 'Labākā cena',
    hourRevenue: 'Stundas ieņēmumi',
    hourlyRevenue: 'Stundas ieņēmumi (PLN)',
    formula: 'Formula',
  },
  lt: {
    title: 'Gamyba × PSE day-ahead kaina',
    dayTotal: 'Dienos suma',
    peakHour: 'Piko valanda',
    bestPrice: 'Geriausia kaina',
    hourRevenue: 'Valandinės pajamos',
    hourlyRevenue: 'Valandinės pajamos (PLN)',
    formula: 'Formulė',
  },
  mt: {
    title: 'Produzzjoni × prezz PSE day-ahead',
    dayTotal: 'Total tal-ġurnata',
    peakHour: 'Siegħa tal-quċċata',
    bestPrice: 'L-aħjar prezz',
    hourRevenue: 'Dħul kull siegħa',
    hourlyRevenue: 'Dħul kull siegħa (PLN)',
    formula: 'Formula',
  },
  ga: {
    title: 'Táirgeacht × praghas PSE day-ahead',
    dayTotal: 'Iomlán an lae',
    peakHour: 'Buaicuair',
    bestPrice: 'An praghas is fearr',
    hourRevenue: 'Ioncam in aghaidh na huaire',
    hourlyRevenue: 'Ioncam in aghaidh na huaire (PLN)',
    formula: 'Foirmle',
  },
  no: {
    title: 'Produksjon × PSE day-ahead-pris',
    dayTotal: 'Dagstotal',
    peakHour: 'Topptime',
    bestPrice: 'Beste pris',
    hourRevenue: 'Timeinntekt',
    hourlyRevenue: 'Timeinntekt (PLN)',
    formula: 'Formel',
  },
  is: {
    title: 'Framleiðsla × PSE day-ahead-verð',
    dayTotal: 'Dagsheild',
    peakHour: 'Hámarkstími',
    bestPrice: 'Besta verð',
    hourRevenue: 'Tímatekjur',
    hourlyRevenue: 'Tímatekjur (PLN)',
    formula: 'Formúla',
  },
  uk: {
    title: 'Виробництво × ціна PSE day-ahead',
    dayTotal: 'Сума за добу',
    peakHour: 'Пікова година',
    bestPrice: 'Найкраща ціна',
    hourRevenue: 'Погодинний дохід',
    hourlyRevenue: 'Погодинний дохід (PLN)',
    formula: 'Формула',
  },
  sr: {
    title: 'Производња × цена PSE day-ahead',
    dayTotal: 'Дневни збир',
    peakHour: 'Врхунски сат',
    bestPrice: 'Најбоља цена',
    hourRevenue: 'Сатни приход',
    hourlyRevenue: 'Сатни приход (PLN)',
    formula: 'Формула',
  },
  sq: {
    title: 'Prodhimi × çmimi PSE day-ahead',
    dayTotal: 'Totali ditor',
    peakHour: 'Ora e pikut',
    bestPrice: 'Çmimi më i mirë',
    hourRevenue: 'Të ardhura orare',
    hourlyRevenue: 'Të ardhura orare (PLN)',
    formula: 'Formula',
  },
  mk: {
    title: 'Производство × цена PSE day-ahead',
    dayTotal: 'Дневна сума',
    peakHour: 'Врвен час',
    bestPrice: 'Најдобра цена',
    hourRevenue: 'Часовен приход',
    hourlyRevenue: 'Часовен приход (PLN)',
    formula: 'Формула',
  },
  bs: {
    title: 'Proizvodnja × cijena PSE day-ahead',
    dayTotal: 'Dnevni zbir',
    peakHour: 'Vršni sat',
    bestPrice: 'Najbolja cijena',
    hourRevenue: 'Satni prihod',
    hourlyRevenue: 'Satni prihod (PLN)',
    formula: 'Formula',
  },
};

const revenueTr: Record<string, Dict> = {
  en: {
    title: 'Revenue estimate (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Avg price',
    minMax: 'Min / Max',
    source: 'Source: PSE rynek dnia następnego',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'No prices for this location (PSE covers Poland only).',
  },
  pl: {
    title: 'Szacowany przychód (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Średnia cena',
    minMax: 'Min / Max',
    source: 'Źródło: PSE rynek dnia następnego',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Brak cen dla tej lokalizacji (PSE obsługuje tylko Polskę).',
  },
  de: {
    title: 'Erlösschätzung (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Ø-Preis',
    minMax: 'Min / Max',
    source: 'Quelle: PSE Day-Ahead-Markt',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Keine Preise für diesen Standort (PSE deckt nur Polen ab).',
  },
  fr: {
    title: 'Estimation de revenu (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Prix moyen',
    minMax: 'Min / Max',
    source: 'Source : marché day-ahead PSE',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Pas de prix pour ce lieu (PSE couvre uniquement la Pologne).',
  },
  es: {
    title: 'Ingresos estimados (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Precio medio',
    minMax: 'Mín / Máx',
    source: 'Fuente: mercado day-ahead PSE',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Sin precios para esta ubicación (PSE solo cubre Polonia).',
  },
  it: {
    title: 'Ricavo stimato (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Prezzo medio',
    minMax: 'Min / Max',
    source: 'Fonte: mercato day-ahead PSE',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nessun prezzo per questa località (PSE copre solo la Polonia).',
  },
  pt: {
    title: 'Receita estimada (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Preço médio',
    minMax: 'Mín / Máx',
    source: 'Fonte: mercado day-ahead PSE',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Sem preços para esta localização (PSE cobre apenas a Polónia).',
  },
  nl: {
    title: 'Geschat inkomen (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Gem. prijs',
    minMax: 'Min / Max',
    source: 'Bron: PSE day-aheadmarkt',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Geen prijzen voor deze locatie (PSE dekt alleen Polen).',
  },
  da: {
    title: 'Estimeret indtægt (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Gns. pris',
    minMax: 'Min / Maks',
    source: 'Kilde: PSE day-ahead-marked',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Ingen priser for denne lokation (PSE dækker kun Polen).',
  },
  sv: {
    title: 'Beräknad intäkt (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Snittpris',
    minMax: 'Min / Max',
    source: 'Källa: PSE day-ahead-marknad',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Inga priser för denna plats (PSE täcker endast Polen).',
  },
  fi: {
    title: 'Tuottoarvio (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Keskihinta',
    minMax: 'Min / Max',
    source: 'Lähde: PSE day-ahead-markkina',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Ei hintoja tälle sijainnille (PSE kattaa vain Puolan).',
  },
  cs: {
    title: 'Odhad výnosu (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Prům. cena',
    minMax: 'Min / Max',
    source: 'Zdroj: PSE trh dne ahead',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Pro tuto lokalitu nejsou ceny (PSE pokrývá pouze Polsko).',
  },
  sk: {
    title: 'Odhad výnosu (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Priem. cena',
    minMax: 'Min / Max',
    source: 'Zdroj: PSE trh dňa vopred',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Pre túto lokalitu nie sú ceny (PSE pokrýva iba Poľsko).',
  },
  sl: {
    title: 'Ocena prihodka (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Povp. cena',
    minMax: 'Min / Maks',
    source: 'Vir: trg PSE dan vnaprej',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Za to lokacijo ni cen (PSE pokriva samo Poljsko).',
  },
  hr: {
    title: 'Procjena prihoda (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Prosj. cijena',
    minMax: 'Min / Maks',
    source: 'Izvor: PSE day-ahead tržište',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nema cijena za ovu lokaciju (PSE pokriva samo Poljsku).',
  },
  hu: {
    title: 'Becsült bevétel (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Átlagár',
    minMax: 'Min / Max',
    source: 'Forrás: PSE másnapi piac',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Erre a helyre nincsenek árak (a PSE csak Lengyelországot fedi).',
  },
  ro: {
    title: 'Venit estimat (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Preț mediu',
    minMax: 'Min / Max',
    source: 'Sursă: piață PSE pentru ziua următoare',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nu există prețuri pentru această locație (PSE acoperă doar Polonia).',
  },
  bg: {
    title: 'Прогноза за приход (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Средна цена',
    minMax: 'Мин / Макс',
    source: 'Източник: PSE пазар за следващия ден',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Няма цени за тази локация (PSE покрива само Полша).',
  },
  el: {
    title: 'Εκτίμηση εσόδων (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Μέση τιμή',
    minMax: 'Ελάχ. / Μέγ.',
    source: 'Πηγή: αγορά PSE επόμενης ημέρας',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Δεν υπάρχουν τιμές για αυτή την τοποθεσία (η PSE καλύπτει μόνο την Πολωνία).',
  },
  et: {
    title: 'Tulu hinnang (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Keskm. hind',
    minMax: 'Min / Maks',
    source: 'Allikas: PSE päev-ette turg',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Selle asukoha kohta hindu pole (PSE katab ainult Poola).',
  },
  lv: {
    title: 'Ieņēmumu novērtējums (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Vid. cena',
    minMax: 'Min / Maks',
    source: 'Avots: PSE nākamās dienas tirgus',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nav cenu šai atrašanās vietai (PSE aptver tikai Poliju).',
  },
  lt: {
    title: 'Pajamų prognozė (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Vid. kaina',
    minMax: 'Min / Maks',
    source: 'Šaltinis: PSE kitos dienos rinka',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nėra kainų šiai vietai (PSE apima tik Lenkiją).',
  },
  mt: {
    title: "Stima ta' dħul (PSE day-ahead)",
    currency: 'PLN',
    avgPrice: 'Prezz medju',
    minMax: 'Min / Maks',
    source: 'Sors: suq PSE għall-ġurnata li jmiss',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Ebda prezzijiet għal din il-pożizzjoni (PSE tkopri biss il-Polonja).',
  },
  ga: {
    title: 'Meastachán ioncaim (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Meánphraghas',
    minMax: 'Min / Uas',
    source: 'Foinse: margadh PSE don lá amárach',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Níl praghsanna ar fáil don suíomh seo (ní chlúdaíonn PSE ach an Pholainn).',
  },
  no: {
    title: 'Estimert inntekt (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Snittpris',
    minMax: 'Min / Maks',
    source: 'Kilde: PSE day-ahead-marked',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Ingen priser for denne plasseringen (PSE dekker bare Polen).',
  },
  is: {
    title: 'Áætlaður tekjur (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Meðalverð',
    minMax: 'Lágm. / Hám.',
    source: 'Heimild: PSE morgundagsmarkaður',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Engin verð fyrir þessa staðsetningu (PSE nær aðeins yfir Pólland).',
  },
  uk: {
    title: 'Орієнтовний дохід (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Сер. ціна',
    minMax: 'Мін / Макс',
    source: 'Джерело: ринок PSE на наступну добу',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Немає цін для цієї локації (PSE охоплює лише Польщу).',
  },
  sr: {
    title: 'Процена прихода (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Прос. цена',
    minMax: 'Мин / Макс',
    source: 'Извор: PSE тржиште следећег дана',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Нема цена за ову локацију (PSE покрива само Пољску).',
  },
  sq: {
    title: 'Vlerësim i të ardhurave (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Çmimi mes.',
    minMax: 'Min / Maks',
    source: 'Burimi: tregu PSE për ditën tjetër',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nuk ka çmime për këtë vendndodhje (PSE mbulon vetëm Poloninë).',
  },
  mk: {
    title: 'Проценета добивка (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Прос. цена',
    minMax: 'Мин / Макс',
    source: 'Извор: PSE пазар за следниот ден',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Нема цени за оваа локација (PSE покрива само Полска).',
  },
  bs: {
    title: 'Procjena prihoda (PSE day-ahead)',
    currency: 'PLN',
    avgPrice: 'Prosj. cijena',
    minMax: 'Min / Maks',
    source: 'Izvor: PSE day-ahead tržište',
    plnPerMwh: 'PLN/MWh',
    noPrices: 'Nema cijena za ovu lokaciju (PSE pokriva samo Poljsku).',
  },
};

const archiveTr: Record<string, Dict> = {
  en: {
    title: 'Yearly solar potential',
    period: 'Reanalysis {{from}} – {{to}}',
    annualYield: 'Annual irradiation',
    tiltGain: 'Tilt bonus vs flat mount',
    monthly: 'Monthly distribution',
    bestMonths: 'Best months',
    optimalSetup: 'Est. optimal: {{tilt}}° tilt / {{az}}° azimuth',
    optimalGain: '+{{gain}}% per year vs your config',
    alreadyOptimal: 'Your config is already near the estimated optimum',
    unit: 'kWh/m²',
  },
  pl: {
    title: 'Roczny potencjał słoneczny',
    period: 'Reanaliza {{from}} – {{to}}',
    annualYield: 'Roczne nasłonecznienie',
    tiltGain: 'Zysk z pochylenia vs montaż płaski',
    monthly: 'Rozkład miesięczny',
    bestMonths: 'Najlepsze miesiące',
    optimalSetup: 'Szac. optimum: {{tilt}}° pochylenia / {{az}}° azymutu',
    optimalGain: '+{{gain}}% rocznie vs Twoja konfiguracja',
    alreadyOptimal: 'Twoja konfiguracja jest już blisko szacowanego optimum',
    unit: 'kWh/m²',
  },
  de: {
    title: 'Jährliches Solarpotenzial',
    period: 'Reanalyse {{from}} – {{to}}',
    annualYield: 'Jährliche Einstrahlung',
    tiltGain: 'Neigungsbonus vs. flache Montage',
    monthly: 'Monatsverteilung',
    bestMonths: 'Beste Monate',
    optimalSetup: 'Geschätztes Optimum: {{tilt}}° Neigung / {{az}}° Azimut',
    optimalGain: '+{{gain}}% pro Jahr vs. Ihre Konfiguration',
    alreadyOptimal: 'Ihre Konfiguration ist bereits nahe am geschätzten Optimum',
    unit: 'kWh/m²',
  },
  fr: {
    title: 'Potentiel solaire annuel',
    period: 'Réanalyse {{from}} – {{to}}',
    annualYield: 'Irradiation annuelle',
    tiltGain: 'Gain d’inclinaison vs pose à plat',
    monthly: 'Répartition mensuelle',
    bestMonths: 'Meilleurs mois',
    optimalSetup: 'Optimum estimé : {{tilt}}° d’inclinaison / {{az}}° d’azimut',
    optimalGain: '+{{gain}}% par an vs votre configuration',
    alreadyOptimal: 'Votre configuration est déjà proche de l’optimum estimé',
    unit: 'kWh/m²',
  },
  es: {
    title: 'Potencial solar anual',
    period: 'Reanálisis {{from}} – {{to}}',
    annualYield: 'Irradiación anual',
    tiltGain: 'Bono por inclinación vs montaje plano',
    monthly: 'Distribución mensual',
    bestMonths: 'Mejores meses',
    optimalSetup: 'Óptimo estimado: {{tilt}}° de inclinación / {{az}}° de azimut',
    optimalGain: '+{{gain}}% al año vs tu configuración',
    alreadyOptimal: 'Tu configuración ya está cerca del óptimo estimado',
    unit: 'kWh/m²',
  },
  it: {
    title: 'Potenziale solare annuo',
    period: 'Rianalisi {{from}} – {{to}}',
    annualYield: 'Irradiazione annua',
    tiltGain: 'Bonus inclinazione vs montaggio piatto',
    monthly: 'Distribuzione mensile',
    bestMonths: 'Mesi migliori',
    optimalSetup: 'Ottimo stimato: {{tilt}}° inclinazione / {{az}}° azimut',
    optimalGain: '+{{gain}}% all’anno vs la tua configurazione',
    alreadyOptimal: 'La tua configurazione è già vicina all’ottimo stimato',
    unit: 'kWh/m²',
  },
  pt: {
    title: 'Potencial solar anual',
    period: 'Reanálise {{from}} – {{to}}',
    annualYield: 'Irradiação anual',
    tiltGain: 'Bónus de inclinação vs montagem plana',
    monthly: 'Distribuição mensal',
    bestMonths: 'Melhores meses',
    optimalSetup: 'Ótimo estimado: {{tilt}}° de inclinação / {{az}}° de azimute',
    optimalGain: '+{{gain}}% por ano vs a sua configuração',
    alreadyOptimal: 'A sua configuração já está perto do ótimo estimado',
    unit: 'kWh/m²',
  },
  nl: {
    title: 'Jaarlijks zonnepotentieel',
    period: 'Heranalyse {{from}} – {{to}}',
    annualYield: 'Jaarlijkse instraling',
    tiltGain: 'Hellingbonus vs platte montage',
    monthly: 'Maandverdeling',
    bestMonths: 'Beste maanden',
    optimalSetup: 'Geschat optimum: {{tilt}}° helling / {{az}}° azimut',
    optimalGain: '+{{gain}}% per jaar vs jouw configuratie',
    alreadyOptimal: 'Jouw configuratie zit al dicht bij het geschatte optimum',
    unit: 'kWh/m²',
  },
  da: {
    title: 'Årligt solpotentiale',
    period: 'Reanalyse {{from}} – {{to}}',
    annualYield: 'Årlig indstråling',
    tiltGain: 'Hældningsbonus vs flad montering',
    monthly: 'Månedsfordeling',
    bestMonths: 'Bedste måneder',
    optimalSetup: 'Anslået optimum: {{tilt}}° hældning / {{az}}° azimut',
    optimalGain: '+{{gain}}% om året vs din konfiguration',
    alreadyOptimal: 'Din konfiguration er allerede tæt på det anslåede optimum',
    unit: 'kWh/m²',
  },
  sv: {
    title: 'Årlig solpotential',
    period: 'Reanalys {{from}} – {{to}}',
    annualYield: 'Årlig instrålning',
    tiltGain: 'Lutningsbonus vs platt montering',
    monthly: 'Månadsfördelning',
    bestMonths: 'Bästa månaderna',
    optimalSetup: 'Uppskattat optimum: {{tilt}}° lutning / {{az}}° azimut',
    optimalGain: '+{{gain}}% per år vs din konfiguration',
    alreadyOptimal: 'Din konfiguration är redan nära det uppskattade optimumet',
    unit: 'kWh/m²',
  },
  fi: {
    title: 'Vuotuinen aurinkopotentiaali',
    period: 'Uudelleenanalyysi {{from}} – {{to}}',
    annualYield: 'Vuotuinen säteily',
    tiltGain: 'Kallistusbonus vs tasoasennus',
    monthly: 'Kuukausijakauma',
    bestMonths: 'Parhaat kuukaudet',
    optimalSetup: 'Arvioitu optimi: {{tilt}}° kallistus / {{az}}° atsimuutti',
    optimalGain: '+{{gain}}% vuodessa vs konfiguraatiosi',
    alreadyOptimal: 'Konfiguraatiosi on jo lähellä arvioitua optimia',
    unit: 'kWh/m²',
  },
  cs: {
    title: 'Roční solární potenciál',
    period: 'Reanalýza {{from}} – {{to}}',
    annualYield: 'Roční ozáření',
    tiltGain: 'Bonus za sklon vs ploché uložení',
    monthly: 'Měsíční rozdělení',
    bestMonths: 'Nejlepší měsíce',
    optimalSetup: 'Odhad. optimum: {{tilt}}° sklon / {{az}}° azimut',
    optimalGain: '+{{gain}}% ročně vs vaše konfigurace',
    alreadyOptimal: 'Vaše konfigurace je už blízko odhadovaného optima',
    unit: 'kWh/m²',
  },
  sk: {
    title: 'Ročný solárny potenciál',
    period: 'Reanalýza {{from}} – {{to}}',
    annualYield: 'Ročné ožiarenie',
    tiltGain: 'Bonus za sklon vs ploché uloženie',
    monthly: 'Mesačné rozdelenie',
    bestMonths: 'Najlepšie mesiace',
    optimalSetup: 'Odhad. optimum: {{tilt}}° sklon / {{az}}° azimut',
    optimalGain: '+{{gain}}% ročne vs vaša konfigurácia',
    alreadyOptimal: 'Vaša konfigurácia je už blízko odhadovaného optima',
    unit: 'kWh/m²',
  },
  sl: {
    title: 'Letni sončni potencial',
    period: 'Reanaliza {{from}} – {{to}}',
    annualYield: 'Letno obsevanje',
    tiltGain: 'Bonus naklona vs ravna montaža',
    monthly: 'Mesečna porazdelitev',
    bestMonths: 'Najboljši meseci',
    optimalSetup: 'Ocenjeni optimum: {{tilt}}° naklon / {{az}}° azimut',
    optimalGain: '+{{gain}}% letno vs vaša konfiguracija',
    alreadyOptimal: 'Vaša konfiguracija je že blizu ocenjenega optimuma',
    unit: 'kWh/m²',
  },
  hr: {
    title: 'Godišnji solarni potencijal',
    period: 'Reanaliza {{from}} – {{to}}',
    annualYield: 'Godišnje ozračenje',
    tiltGain: 'Bonus nagiba vs ravna montaža',
    monthly: 'Mjesečna raspodjela',
    bestMonths: 'Najbolji mjeseci',
    optimalSetup: 'Procij. optimum: {{tilt}}° nagib / {{az}}° azimut',
    optimalGain: '+{{gain}}% godišnje vs vaša konfiguracija',
    alreadyOptimal: 'Vaša konfiguracija je već blizu procijenjenog optimuma',
    unit: 'kWh/m²',
  },
  hu: {
    title: 'Éves napenergia-potenciál',
    period: 'Újraelemzés {{from}} – {{to}}',
    annualYield: 'Éves besugárzás',
    tiltGain: 'Dőlésszög-bónusz vs sík rögzítés',
    monthly: 'Havi eloszlás',
    bestMonths: 'Legjobb hónapok',
    optimalSetup: 'Becsült optimum: {{tilt}}° dőlés / {{az}}° azimut',
    optimalGain: '+{{gain}}% évente vs az Ön konfigurációja',
    alreadyOptimal: 'Az Ön konfigurációja már közel van a becsült optimumhoz',
    unit: 'kWh/m²',
  },
  ro: {
    title: 'Potențial solar anual',
    period: 'Reanaliză {{from}} – {{to}}',
    annualYield: 'Iradiere anuală',
    tiltGain: 'Bonus de înclinare vs montaj plat',
    monthly: 'Distribuție lunară',
    bestMonths: 'Cele mai bune luni',
    optimalSetup: 'Optim estimat: {{tilt}}° înclinare / {{az}}° azimut',
    optimalGain: '+{{gain}}% pe an vs configurația ta',
    alreadyOptimal: 'Configurația ta este deja aproape de optimul estimat',
    unit: 'kWh/m²',
  },
  bg: {
    title: 'Годишен соларен потенциал',
    period: 'Реанализ {{from}} – {{to}}',
    annualYield: 'Годишно облъчване',
    tiltGain: 'Бонус от наклона спрямо плосък монтаж',
    monthly: 'Месечно разпределение',
    bestMonths: 'Най-добри месеци',
    optimalSetup: 'Прибл. оптимум: {{tilt}}° наклон / {{az}}° азимут',
    optimalGain: '+{{gain}}% годишно спрямо вашата конфигурация',
    alreadyOptimal: 'Вашата конфигурация вече е близо до прогнозния оптимум',
    unit: 'kWh/m²',
  },
  el: {
    title: 'Ετήσιο ηλιακό δυναμικό',
    period: 'Επανανάλυση {{from}} – {{to}}',
    annualYield: 'Ετήσια ακτινοβολία',
    tiltGain: 'Μπόνους κλίσης έναντι επίπεδης τοποθέτησης',
    monthly: 'Μηνιαία κατανομή',
    bestMonths: 'Καλύτεροι μήνες',
    optimalSetup: 'Εκτ. βέλτιστο: {{tilt}}° κλίση / {{az}}° αζιμούθιο',
    optimalGain: '+{{gain}}% ετησίως έναντι της διαμόρφωσής σας',
    alreadyOptimal: 'Η διαμόρφωσή σας είναι ήδη κοντά στο εκτιμώμενο βέλτιστο',
    unit: 'kWh/m²',
  },
  et: {
    title: 'Aastane päikeseenergia potentsiaal',
    period: 'Reanalüüs {{from}} – {{to}}',
    annualYield: 'Aastane kiirgus',
    tiltGain: 'Kaldenurga boonus vs lameda paigaldusega',
    monthly: 'Kuude jaotus',
    bestMonths: 'Parimad kuud',
    optimalSetup: 'Hinnang. optimum: {{tilt}}° kalle / {{az}}° asimuut',
    optimalGain: '+{{gain}}% aastas vs sinu konfiguratsioon',
    alreadyOptimal: 'Sinu konfiguratsioon on juba hinnangulise optimumi lähedal',
    unit: 'kWh/m²',
  },
  lv: {
    title: 'Gada saules potenciāls',
    period: 'Reanalīze {{from}} – {{to}}',
    annualYield: 'Gada starojums',
    tiltGain: 'Slīpuma bonuss pret plakanu montāžu',
    monthly: 'Mēnešu sadalījums',
    bestMonths: 'Labākie mēneši',
    optimalSetup: 'Aptuv. optimums: {{tilt}}° slīpums / {{az}}° azimuts',
    optimalGain: '+{{gain}}% gadā pret jūsu konfigurāciju',
    alreadyOptimal: 'Jūsu konfigurācija jau ir tuvu aptuvenajam optimumam',
    unit: 'kWh/m²',
  },
  lt: {
    title: 'Metinis saulės potencialas',
    period: 'Pakartotinė analizė {{from}} – {{to}}',
    annualYield: 'Metinė apšvieta',
    tiltGain: 'Pasvirimo priedas vs plokščias montavimas',
    monthly: 'Mėnesinis pasiskirstymas',
    bestMonths: 'Geriausi mėnesiai',
    optimalSetup: 'Apytiksl. optimumas: {{tilt}}° pasvirimas / {{az}}° azimutas',
    optimalGain: '+{{gain}}% per metus vs jūsų konfigūracija',
    alreadyOptimal: 'Jūsų konfigūracija jau arti apytikrio optimumo',
    unit: 'kWh/m²',
  },
  mt: {
    title: 'Potenzjal solari annwali',
    period: 'Analiżi mill-ġdid {{from}} – {{to}}',
    annualYield: 'Irradjazzjoni annwali',
    tiltGain: 'Bonus tal-inklinazzjoni vs immuntar ċatt',
    monthly: 'Distribuzzjoni ta’ kull xahar',
    bestMonths: 'L-aħjar xhur',
    optimalSetup: 'Optimum stmat: {{tilt}}° inklinazzjoni / {{az}}° ażimut',
    optimalGain: '+{{gain}}% fis-sena vs il-konfigurazzjoni tiegħek',
    alreadyOptimal: 'Il-konfigurazzjoni tiegħek diġà qrib l-optimum stmat',
    unit: 'kWh/m²',
  },
  ga: {
    title: 'Acmhainn ghréine bhliantúil',
    period: 'Athanailís {{from}} – {{to}}',
    annualYield: 'Ionradaíocht bhliantúil',
    tiltGain: 'Bónas claonta vs feistiú comhréidh',
    monthly: 'Dáileadh míosúil',
    bestMonths: 'Na míonna is fearr',
    optimalSetup: 'Optamam measta: {{tilt}}° claonadh / {{az}}° asamat',
    optimalGain: '+{{gain}}% sa bhliain vs do chumraíocht',
    alreadyOptimal: 'Tá do chumraíocht gar don optamam measta cheana féin',
    unit: 'kWh/m²',
  },
  no: {
    title: 'Årlig solpotensial',
    period: 'Reanalyse {{from}} – {{to}}',
    annualYield: 'Årlig innstråling',
    tiltGain: 'Helningsbonus vs flat montering',
    monthly: 'Månedsfordeling',
    bestMonths: 'Beste måneder',
    optimalSetup: 'Anslått optimum: {{tilt}}° helning / {{az}}° asimut',
    optimalGain: '+{{gain}}% per år vs din konfigurasjon',
    alreadyOptimal: 'Konfigurasjonen din er allerede nær det anslåtte optimumet',
    unit: 'kWh/m²',
  },
  is: {
    title: 'Árleg sólarorka',
    period: 'Endurgreining {{from}} – {{to}}',
    annualYield: 'Árleg geislun',
    tiltGain: 'Hallabónus vs flöt uppsetning',
    monthly: 'Mánaðardreifing',
    bestMonths: 'Bestu mánuðirnir',
    optimalSetup: 'Áætlað kjörgildi: {{tilt}}° halli / {{az}}° áttarhorn',
    optimalGain: '+{{gain}}% á ári vs stillingar þínar',
    alreadyOptimal: 'Stillingar þínar eru þegar nálægt áætluðu kjörgildi',
    unit: 'kWh/m²',
  },
  uk: {
    title: 'Річний сонячний потенціал',
    period: 'Реаналіз {{from}} – {{to}}',
    annualYield: 'Річне опромінення',
    tiltGain: 'Бонус від нахилу vs плоский монтаж',
    monthly: 'Розподіл за місяцями',
    bestMonths: 'Найкращі місяці',
    optimalSetup: 'Орієнт. оптимум: {{tilt}}° нахил / {{az}}° азимут',
    optimalGain: '+{{gain}}% на рік vs ваша конфігурація',
    alreadyOptimal: 'Ваша конфігурація вже близька до орієнтовного оптимуму',
    unit: 'kWh/m²',
  },
  sr: {
    title: 'Годишњи соларни потенцијал',
    period: 'Реанализа {{from}} – {{to}}',
    annualYield: 'Годишње зрачење',
    tiltGain: 'Бонус нагиба vs равна монтажа',
    monthly: 'Месечна расподела',
    bestMonths: 'Најбољи месеци',
    optimalSetup: 'Проц. оптимум: {{tilt}}° нагиб / {{az}}° азимут',
    optimalGain: '+{{gain}}% годишње vs ваша конфигурација',
    alreadyOptimal: 'Ваша конфигурација је већ близу процењеног оптимума',
    unit: 'kWh/m²',
  },
  sq: {
    title: 'Potenciali diellor vjetor',
    period: 'Rianalizë {{from}} – {{to}}',
    annualYield: 'Rrezatimi vjetor',
    tiltGain: 'Bonus i pjerrësisë vs montim i sheshtë',
    monthly: 'Shpërndarja mujore',
    bestMonths: 'Muajt më të mirë',
    optimalSetup: 'Optimum i vlerësuar: {{tilt}}° pjerrësi / {{az}}° azimut',
    optimalGain: '+{{gain}}% në vit vs konfigurimi yt',
    alreadyOptimal: 'Konfigurimi yt është tashmë afër optimumit të vlerësuar',
    unit: 'kWh/m²',
  },
  mk: {
    title: 'Годишен соларен потенцијал',
    period: 'Реанализа {{from}} – {{to}}',
    annualYield: 'Годишно зрачење',
    tiltGain: 'Бонус од наклон vs рамна монтажа',
    monthly: 'Месечна распределба',
    bestMonths: 'Најдобри месеци',
    optimalSetup: 'Проц. оптимум: {{tilt}}° наклон / {{az}}° азимут',
    optimalGain: '+{{gain}}% годишно vs вашата конфигурација',
    alreadyOptimal: 'Вашата конфигурација е веќе блиску до проценетиот оптимум',
    unit: 'kWh/m²',
  },
  bs: {
    title: 'Godišnji solarni potencijal',
    period: 'Reanaliza {{from}} – {{to}}',
    annualYield: 'Godišnje zračenje',
    tiltGain: 'Bonus nagiba vs ravna montaža',
    monthly: 'Mjesečna raspodjela',
    bestMonths: 'Najbolji mjeseci',
    optimalSetup: 'Proc. optimum: {{tilt}}° nagib / {{az}}° azimut',
    optimalGain: '+{{gain}}% godišnje vs vaša konfiguracija',
    alreadyOptimal: 'Vaša konfiguracija je već blizu procijenjenog optimuma',
    unit: 'kWh/m²',
  },
};

const confidenceTr: Record<string, Dict> = {
  en: { bandName: 'Spread (P10–P90)', tooltipRange: 'Range: {{lo}}–{{hi}} Wh' },
  pl: { bandName: 'Rozrzut (P10–P90)', tooltipRange: 'Zakres: {{lo}}–{{hi}} Wh' },
  de: { bandName: 'Streuung (P10–P90)', tooltipRange: 'Bereich: {{lo}}–{{hi}} Wh' },
  fr: { bandName: 'Dispersion (P10–P90)', tooltipRange: 'Plage : {{lo}}–{{hi}} Wh' },
  es: { bandName: 'Dispersión (P10–P90)', tooltipRange: 'Rango: {{lo}}–{{hi}} Wh' },
  it: { bandName: 'Dispersione (P10–P90)', tooltipRange: 'Intervallo: {{lo}}–{{hi}} Wh' },
  pt: { bandName: 'Dispersão (P10–P90)', tooltipRange: 'Intervalo: {{lo}}–{{hi}} Wh' },
  nl: { bandName: 'Spreiding (P10–P90)', tooltipRange: 'Bereik: {{lo}}–{{hi}} Wh' },
  da: { bandName: 'Spredning (P10–P90)', tooltipRange: 'Interval: {{lo}}–{{hi}} Wh' },
  sv: { bandName: 'Spridning (P10–P90)', tooltipRange: 'Intervall: {{lo}}–{{hi}} Wh' },
  fi: { bandName: 'Hajonta (P10–P90)', tooltipRange: 'Vaihteluväli: {{lo}}–{{hi}} Wh' },
  cs: { bandName: 'Rozptyl (P10–P90)', tooltipRange: 'Rozsah: {{lo}}–{{hi}} Wh' },
  sk: { bandName: 'Rozptyl (P10–P90)', tooltipRange: 'Rozsah: {{lo}}–{{hi}} Wh' },
  sl: { bandName: 'Razpršenost (P10–P90)', tooltipRange: 'Obseg: {{lo}}–{{hi}} Wh' },
  hr: { bandName: 'Raspršenost (P10–P90)', tooltipRange: 'Raspon: {{lo}}–{{hi}} Wh' },
  hu: { bandName: 'Szórás (P10–P90)', tooltipRange: 'Tartomány: {{lo}}–{{hi}} Wh' },
  ro: { bandName: 'Dispersie (P10–P90)', tooltipRange: 'Interval: {{lo}}–{{hi}} Wh' },
  bg: { bandName: 'Разсейване (P10–P90)', tooltipRange: 'Диапазон: {{lo}}–{{hi}} Wh' },
  el: { bandName: 'Διασπορά (P10–P90)', tooltipRange: 'Εύρος: {{lo}}–{{hi}} Wh' },
  et: { bandName: 'Hajuvus (P10–P90)', tooltipRange: 'Vahemik: {{lo}}–{{hi}} Wh' },
  lv: { bandName: 'Izkliede (P10–P90)', tooltipRange: 'Diapazons: {{lo}}–{{hi}} Wh' },
  lt: { bandName: 'Sklaida (P10–P90)', tooltipRange: 'Intervalas: {{lo}}–{{hi}} Wh' },
  mt: { bandName: 'Firxa (P10–P90)', tooltipRange: 'Medda: {{lo}}–{{hi}} Wh' },
  ga: { bandName: 'Scaipeadh (P10–P90)', tooltipRange: 'Raon: {{lo}}–{{hi}} Wh' },
  no: { bandName: 'Spredning (P10–P90)', tooltipRange: 'Område: {{lo}}–{{hi}} Wh' },
  is: { bandName: 'Dreifing (P10–P90)', tooltipRange: 'Bil: {{lo}}–{{hi}} Wh' },
  uk: { bandName: 'Розкид (P10–P90)', tooltipRange: 'Діапазон: {{lo}}–{{hi}} Wh' },
  sr: { bandName: 'Расипање (P10–P90)', tooltipRange: 'Опсег: {{lo}}–{{hi}} Wh' },
  sq: { bandName: 'Shpërndarja (P10–P90)', tooltipRange: 'Intervali: {{lo}}–{{hi}} Wh' },
  mk: { bandName: 'Расејување (P10–P90)', tooltipRange: 'Опсег: {{lo}}–{{hi}} Wh' },
  bs: { bandName: 'Raspršenost (P10–P90)', tooltipRange: 'Raspon: {{lo}}–{{hi}} Wh' },
};

const airQualityTr: Record<string, Dict> = {
  en: {
    title: 'Air quality',
    soilingRisk: 'Soiling risk',
    riskLow: 'Low',
    riskModerate: 'Moderate',
    riskHigh: 'High',
    dustLoad: 'Dust load',
    particulates: 'Particulates (PM10/PM2.5)',
    dust: 'Mineral dust',
    aod: 'Aerosol (AOD)',
    recNone: 'Panels are clean — no action needed',
    recMonitor: 'Some dust building up — keep an eye on output',
    recPlanCleaning: 'Noticeable soiling — plan a cleaning',
    recCleanNow: 'Heavy soiling — cleaning recommended now',
    recRainWillClean: 'Rain in the forecast — it will rinse the panels',
    fetchedAt: 'Updated',
  },
  pl: {
    title: 'Jakość powietrza',
    soilingRisk: 'Ryzyko zabrudzenia',
    riskLow: 'Niskie',
    riskModerate: 'Umiarkowane',
    riskHigh: 'Wysokie',
    dustLoad: 'Obciążenie pyłem',
    particulates: 'Pył zawieszony (PM10/PM2.5)',
    dust: 'Pył mineralny',
    aod: 'Aerozol (AOD)',
    recNone: 'Panele są czyste — nic nie trzeba robić',
    recMonitor: 'Trochę kurzu się odkłada — obserwuj uzysk',
    recPlanCleaning: 'Zauważalne zabrudzenie — zaplanuj mycie',
    recCleanNow: 'Silne zabrudzenie — zalecane mycie',
    recRainWillClean: 'W prognozie deszcz — opłucze panele',
    fetchedAt: 'Zaktualizowano',
  },
  de: {
    title: 'Luftqualität',
    soilingRisk: 'Verschmutzungsrisiko',
    riskLow: 'Gering',
    riskModerate: 'Mäßig',
    riskHigh: 'Hoch',
    dustLoad: 'Staubbelastung',
    particulates: 'Feinstaub (PM10/PM2.5)',
    dust: 'Mineralstaub',
    aod: 'Aerosol (AOD)',
    recNone: 'Module sind sauber — nichts zu tun',
    recMonitor: 'Etwas Staub setzt sich ab — Ertrag im Auge behalten',
    recPlanCleaning: 'Spürbare Verschmutzung — Reinigung einplanen',
    recCleanNow: 'Starke Verschmutzung — Reinigung jetzt empfohlen',
    recRainWillClean: 'Regen in der Vorhersage — er spült die Module ab',
    fetchedAt: 'Aktualisiert',
  },
  fr: {
    title: "Qualité de l'air",
    soilingRisk: "Risque d'encrassement",
    riskLow: 'Faible',
    riskModerate: 'Modéré',
    riskHigh: 'Élevé',
    dustLoad: 'Charge de poussière',
    particulates: 'Particules (PM10/PM2.5)',
    dust: 'Poussière minérale',
    aod: 'Aérosol (AOD)',
    recNone: 'Les panneaux sont propres — rien à faire',
    recMonitor: "Un peu de poussière s'accumule — surveillez la production",
    recPlanCleaning: 'Encrassement notable — planifiez un nettoyage',
    recCleanNow: 'Fort encrassement — nettoyage recommandé maintenant',
    recRainWillClean: 'Pluie prévue — elle rincera les panneaux',
    fetchedAt: 'Mis à jour',
  },
  es: {
    title: 'Calidad del aire',
    soilingRisk: 'Riesgo de suciedad',
    riskLow: 'Bajo',
    riskModerate: 'Moderado',
    riskHigh: 'Alto',
    dustLoad: 'Carga de polvo',
    particulates: 'Partículas (PM10/PM2.5)',
    dust: 'Polvo mineral',
    aod: 'Aerosol (AOD)',
    recNone: 'Los paneles están limpios — no hace falta nada',
    recMonitor: 'Se acumula algo de polvo — vigila la producción',
    recPlanCleaning: 'Suciedad apreciable — planifica una limpieza',
    recCleanNow: 'Suciedad intensa — se recomienda limpiar ahora',
    recRainWillClean: 'Lluvia en el pronóstico — enjuagará los paneles',
    fetchedAt: 'Actualizado',
  },
  it: {
    title: "Qualità dell'aria",
    soilingRisk: 'Rischio di sporcamento',
    riskLow: 'Basso',
    riskModerate: 'Moderato',
    riskHigh: 'Alto',
    dustLoad: 'Carico di polvere',
    particulates: 'Particolato (PM10/PM2.5)',
    dust: 'Polvere minerale',
    aod: 'Aerosol (AOD)',
    recNone: 'I pannelli sono puliti — nulla da fare',
    recMonitor: "Si accumula un po' di polvere — tieni d'occhio la produzione",
    recPlanCleaning: 'Sporco evidente — pianifica una pulizia',
    recCleanNow: 'Sporco intenso — pulizia consigliata ora',
    recRainWillClean: 'Pioggia in arrivo — risciacquerà i pannelli',
    fetchedAt: 'Aggiornato',
  },
  pt: {
    title: 'Qualidade do ar',
    soilingRisk: 'Risco de sujidade',
    riskLow: 'Baixo',
    riskModerate: 'Moderado',
    riskHigh: 'Alto',
    dustLoad: 'Carga de poeira',
    particulates: 'Partículas (PM10/PM2.5)',
    dust: 'Poeira mineral',
    aod: 'Aerossol (AOD)',
    recNone: 'Os painéis estão limpos — nada a fazer',
    recMonitor: 'Alguma poeira a acumular — fique de olho na produção',
    recPlanCleaning: 'Sujidade percetível — planeie uma limpeza',
    recCleanNow: 'Sujidade intensa — limpeza recomendada agora',
    recRainWillClean: 'Chuva na previsão — vai enxaguar os painéis',
    fetchedAt: 'Atualizado',
  },
  nl: {
    title: 'Luchtkwaliteit',
    soilingRisk: 'Vervuilingsrisico',
    riskLow: 'Laag',
    riskModerate: 'Matig',
    riskHigh: 'Hoog',
    dustLoad: 'Stofbelasting',
    particulates: 'Fijnstof (PM10/PM2.5)',
    dust: 'Mineraal stof',
    aod: 'Aerosol (AOD)',
    recNone: 'Panelen zijn schoon — niets te doen',
    recMonitor: 'Er hoopt wat stof op — houd de opbrengst in de gaten',
    recPlanCleaning: 'Merkbare vervuiling — plan een reiniging',
    recCleanNow: 'Sterke vervuiling — reiniging nu aanbevolen',
    recRainWillClean: 'Regen in de verwachting — spoelt de panelen schoon',
    fetchedAt: 'Bijgewerkt',
  },
  da: {
    title: 'Luftkvalitet',
    soilingRisk: 'Risiko for tilsmudsning',
    riskLow: 'Lav',
    riskModerate: 'Moderat',
    riskHigh: 'Høj',
    dustLoad: 'Støvbelastning',
    particulates: 'Partikler (PM10/PM2.5)',
    dust: 'Mineralstøv',
    aod: 'Aerosol (AOD)',
    recNone: 'Panelerne er rene — intet at gøre',
    recMonitor: 'Lidt støv samler sig — hold øje med produktionen',
    recPlanCleaning: 'Mærkbar tilsmudsning — planlæg en rengøring',
    recCleanNow: 'Kraftig tilsmudsning — rengøring anbefales nu',
    recRainWillClean: 'Regn i vejrudsigten — den skyller panelerne',
    fetchedAt: 'Opdateret',
  },
  sv: {
    title: 'Luftkvalitet',
    soilingRisk: 'Risk för nedsmutsning',
    riskLow: 'Låg',
    riskModerate: 'Måttlig',
    riskHigh: 'Hög',
    dustLoad: 'Dammbelastning',
    particulates: 'Partiklar (PM10/PM2.5)',
    dust: 'Mineraldamm',
    aod: 'Aerosol (AOD)',
    recNone: 'Panelerna är rena — inget att göra',
    recMonitor: 'Lite damm samlas — håll koll på produktionen',
    recPlanCleaning: 'Märkbar nedsmutsning — planera en rengöring',
    recCleanNow: 'Kraftig nedsmutsning — rengöring rekommenderas nu',
    recRainWillClean: 'Regn i prognosen — det sköljer panelerna',
    fetchedAt: 'Uppdaterat',
  },
  fi: {
    title: 'Ilmanlaatu',
    soilingRisk: 'Likaantumisriski',
    riskLow: 'Matala',
    riskModerate: 'Kohtalainen',
    riskHigh: 'Korkea',
    dustLoad: 'Pölykuorma',
    particulates: 'Hiukkaset (PM10/PM2.5)',
    dust: 'Mineraalipöly',
    aod: 'Aerosoli (AOD)',
    recNone: 'Paneelit ovat puhtaat — ei toimenpiteitä',
    recMonitor: 'Pölyä kertyy hieman — seuraa tuottoa',
    recPlanCleaning: 'Havaittavaa likaantumista — suunnittele puhdistus',
    recCleanNow: 'Voimakasta likaantumista — puhdistus suositeltavaa nyt',
    recRainWillClean: 'Sade tulossa — se huuhtoo paneelit',
    fetchedAt: 'Päivitetty',
  },
  cs: {
    title: 'Kvalita ovzduší',
    soilingRisk: 'Riziko znečištění',
    riskLow: 'Nízké',
    riskModerate: 'Střední',
    riskHigh: 'Vysoké',
    dustLoad: 'Zatížení prachem',
    particulates: 'Prachové částice (PM10/PM2.5)',
    dust: 'Minerální prach',
    aod: 'Aerosol (AOD)',
    recNone: 'Panely jsou čisté — není třeba nic dělat',
    recMonitor: 'Trochu se usazuje prach — sledujte výrobu',
    recPlanCleaning: 'Znatelné znečištění — naplánujte čištění',
    recCleanNow: 'Silné znečištění — čištění doporučeno nyní',
    recRainWillClean: 'V předpovědi déšť — opláchne panely',
    fetchedAt: 'Aktualizováno',
  },
  sk: {
    title: 'Kvalita ovzdušia',
    soilingRisk: 'Riziko znečistenia',
    riskLow: 'Nízke',
    riskModerate: 'Stredné',
    riskHigh: 'Vysoké',
    dustLoad: 'Zaťaženie prachom',
    particulates: 'Prachové častice (PM10/PM2.5)',
    dust: 'Minerálny prach',
    aod: 'Aerosól (AOD)',
    recNone: 'Panely sú čisté — netreba nič robiť',
    recMonitor: 'Trochu sa usádza prach — sledujte výrobu',
    recPlanCleaning: 'Badateľné znečistenie — naplánujte čistenie',
    recCleanNow: 'Silné znečistenie — čistenie odporúčané teraz',
    recRainWillClean: 'V predpovedi dážď — opláchne panely',
    fetchedAt: 'Aktualizované',
  },
  sl: {
    title: 'Kakovost zraka',
    soilingRisk: 'Tveganje umazanije',
    riskLow: 'Nizko',
    riskModerate: 'Zmerno',
    riskHigh: 'Visoko',
    dustLoad: 'Obremenitev s prahom',
    particulates: 'Delci (PM10/PM2.5)',
    dust: 'Mineralni prah',
    aod: 'Aerosol (AOD)',
    recNone: 'Paneli so čisti — ni treba ukrepati',
    recMonitor: 'Nabira se nekaj prahu — spremljajte proizvodnjo',
    recPlanCleaning: 'Opazna umazanija — načrtujte čiščenje',
    recCleanNow: 'Močna umazanija — čiščenje priporočljivo zdaj',
    recRainWillClean: 'V napovedi dež — splaknil bo panele',
    fetchedAt: 'Posodobljeno',
  },
  hr: {
    title: 'Kvaliteta zraka',
    soilingRisk: 'Rizik od onečišćenja',
    riskLow: 'Nizak',
    riskModerate: 'Umjeren',
    riskHigh: 'Visok',
    dustLoad: 'Opterećenje prašinom',
    particulates: 'Lebdeće čestice (PM10/PM2.5)',
    dust: 'Mineralna prašina',
    aod: 'Aerosol (AOD)',
    recNone: 'Paneli su čisti — nije potrebno ništa',
    recMonitor: 'Nakuplja se nešto prašine — pratite proizvodnju',
    recPlanCleaning: 'Primjetno onečišćenje — planirajte čišćenje',
    recCleanNow: 'Jako onečišćenje — preporučuje se čišćenje sada',
    recRainWillClean: 'U prognozi kiša — isprat će panele',
    fetchedAt: 'Ažurirano',
  },
  hu: {
    title: 'Levegőminőség',
    soilingRisk: 'Szennyeződés kockázata',
    riskLow: 'Alacsony',
    riskModerate: 'Mérsékelt',
    riskHigh: 'Magas',
    dustLoad: 'Porterhelés',
    particulates: 'Szállópor (PM10/PM2.5)',
    dust: 'Ásványi por',
    aod: 'Aeroszol (AOD)',
    recNone: 'A panelek tiszták — nincs teendő',
    recMonitor: 'Kevés por rakódik le — figyelje a termelést',
    recPlanCleaning: 'Észrevehető szennyeződés — tervezzen tisztítást',
    recCleanNow: 'Erős szennyeződés — tisztítás most ajánlott',
    recRainWillClean: 'Eső várható — leöblíti a paneleket',
    fetchedAt: 'Frissítve',
  },
  ro: {
    title: 'Calitatea aerului',
    soilingRisk: 'Risc de murdărire',
    riskLow: 'Scăzut',
    riskModerate: 'Moderat',
    riskHigh: 'Ridicat',
    dustLoad: 'Încărcare cu praf',
    particulates: 'Particule (PM10/PM2.5)',
    dust: 'Praf mineral',
    aod: 'Aerosol (AOD)',
    recNone: 'Panourile sunt curate — nimic de făcut',
    recMonitor: 'Se depune puțin praf — urmăriți producția',
    recPlanCleaning: 'Murdărire vizibilă — planificați o curățare',
    recCleanNow: 'Murdărire puternică — curățare recomandată acum',
    recRainWillClean: 'Ploaie în prognoză — va clăti panourile',
    fetchedAt: 'Actualizat',
  },
  bg: {
    title: 'Качество на въздуха',
    soilingRisk: 'Риск от замърсяване',
    riskLow: 'Нисък',
    riskModerate: 'Умерен',
    riskHigh: 'Висок',
    dustLoad: 'Натоварване с прах',
    particulates: 'Фини прахови частици (PM10/PM2.5)',
    dust: 'Минерален прах',
    aod: 'Аерозол (AOD)',
    recNone: 'Панелите са чисти — няма нужда от действие',
    recMonitor: 'Натрупва се малко прах — следете производството',
    recPlanCleaning: 'Забележимо замърсяване — планирайте почистване',
    recCleanNow: 'Силно замърсяване — препоръчва се почистване сега',
    recRainWillClean: 'В прогнозата има дъжд — ще изплакне панелите',
    fetchedAt: 'Актуализирано',
  },
  el: {
    title: 'Ποιότητα αέρα',
    soilingRisk: 'Κίνδυνος ρύπανσης',
    riskLow: 'Χαμηλός',
    riskModerate: 'Μέτριος',
    riskHigh: 'Υψηλός',
    dustLoad: 'Φορτίο σκόνης',
    particulates: 'Αιωρούμενα σωματίδια (PM10/PM2.5)',
    dust: 'Ορυκτή σκόνη',
    aod: 'Αερόλυμα (AOD)',
    recNone: 'Τα πάνελ είναι καθαρά — δεν χρειάζεται ενέργεια',
    recMonitor: 'Συσσωρεύεται λίγη σκόνη — παρακολουθήστε την παραγωγή',
    recPlanCleaning: 'Αισθητή ρύπανση — προγραμματίστε καθαρισμό',
    recCleanNow: 'Έντονη ρύπανση — συνιστάται καθαρισμός τώρα',
    recRainWillClean: 'Βροχή στην πρόγνωση — θα ξεπλύνει τα πάνελ',
    fetchedAt: 'Ενημερώθηκε',
  },
  et: {
    title: 'Õhukvaliteet',
    soilingRisk: 'Määrdumise risk',
    riskLow: 'Madal',
    riskModerate: 'Mõõdukas',
    riskHigh: 'Kõrge',
    dustLoad: 'Tolmukoormus',
    particulates: 'Peenosakesed (PM10/PM2.5)',
    dust: 'Mineraaltolm',
    aod: 'Aerosool (AOD)',
    recNone: 'Paneelid on puhtad — pole vaja midagi teha',
    recMonitor: 'Veidi tolmu koguneb — jälgi toodangut',
    recPlanCleaning: 'Märgatav määrdumine — planeeri puhastus',
    recCleanNow: 'Tugev määrdumine — puhastus on nüüd soovitatav',
    recRainWillClean: 'Prognoosis on vihm — see loputab paneelid',
    fetchedAt: 'Värskendatud',
  },
  lv: {
    title: 'Gaisa kvalitāte',
    soilingRisk: 'Piesārņojuma risks',
    riskLow: 'Zems',
    riskModerate: 'Mērens',
    riskHigh: 'Augsts',
    dustLoad: 'Putekļu slodze',
    particulates: 'Cietās daļiņas (PM10/PM2.5)',
    dust: 'Minerālie putekļi',
    aod: 'Aerosols (AOD)',
    recNone: 'Paneļi ir tīri — nekas nav jādara',
    recMonitor: 'Uzkrājas nedaudz putekļu — sekojiet ražošanai',
    recPlanCleaning: 'Manāms piesārņojums — ieplānojiet tīrīšanu',
    recCleanNow: 'Spēcīgs piesārņojums — tīrīšana ieteicama tagad',
    recRainWillClean: 'Prognozē lietus — tas noskalos paneļus',
    fetchedAt: 'Atjaunināts',
  },
  lt: {
    title: 'Oro kokybė',
    soilingRisk: 'Užteršimo rizika',
    riskLow: 'Maža',
    riskModerate: 'Vidutinė',
    riskHigh: 'Didelė',
    dustLoad: 'Dulkių apkrova',
    particulates: 'Kietosios dalelės (PM10/PM2.5)',
    dust: 'Mineralinės dulkės',
    aod: 'Aerozolis (AOD)',
    recNone: 'Plokštės švarios — nieko daryti nereikia',
    recMonitor: 'Kaupiasi šiek tiek dulkių — stebėkite gamybą',
    recPlanCleaning: 'Pastebimas užteršimas — suplanuokite valymą',
    recCleanNow: 'Stiprus užteršimas — dabar rekomenduojama valyti',
    recRainWillClean: 'Prognozėje lietus — jis nuplaus plokštes',
    fetchedAt: 'Atnaujinta',
  },
  mt: {
    title: 'Kwalità tal-arja',
    soilingRisk: "Riskju ta' ħmieġ",
    riskLow: 'Baxx',
    riskModerate: 'Moderat',
    riskHigh: 'Għoli',
    dustLoad: "Tagħbija ta' trab",
    particulates: 'Partiċelli (PM10/PM2.5)',
    dust: 'Trab minerali',
    aod: 'Aerosol (AOD)',
    recNone: "Il-pannelli huma nodfa — m'hemm xejn x'tagħmel",
    recMonitor: 'Ftit trab qed jinġabar — żomm għajnejk fuq il-produzzjoni',
    recPlanCleaning: 'Ħmieġ notevoli — ippjana tindif',
    recCleanNow: 'Ħmieġ qawwi — tindif irrakkomandat issa',
    recRainWillClean: 'Xita fil-previżjoni — ser tlaħlaħ il-pannelli',
    fetchedAt: 'Aġġornat',
  },
  ga: {
    title: 'Cáilíocht an aeir',
    soilingRisk: 'Riosca salaithe',
    riskLow: 'Íseal',
    riskModerate: 'Measartha',
    riskHigh: 'Ard',
    dustLoad: 'Lód deannaigh',
    particulates: 'Cáithníní (PM10/PM2.5)',
    dust: 'Deannach mianrach',
    aod: 'Aerasól (AOD)',
    recNone: 'Tá na painéil glan — níl aon rud le déanamh',
    recMonitor: 'Tá roinnt deannaigh ag carnadh — coinnigh súil ar an aschur',
    recPlanCleaning: 'Salú suntasach — pleanáil glanadh',
    recCleanNow: 'Salú trom — moltar glanadh anois',
    recRainWillClean: 'Báisteach sa réamhaisnéis — sruthlóidh sí na painéil',
    fetchedAt: 'Nuashonraithe',
  },
  no: {
    title: 'Luftkvalitet',
    soilingRisk: 'Risiko for tilsmussing',
    riskLow: 'Lav',
    riskModerate: 'Moderat',
    riskHigh: 'Høy',
    dustLoad: 'Støvbelastning',
    particulates: 'Svevestøv (PM10/PM2.5)',
    dust: 'Mineralstøv',
    aod: 'Aerosol (AOD)',
    recNone: 'Panelene er rene — ingenting å gjøre',
    recMonitor: 'Litt støv samler seg — følg med på produksjonen',
    recPlanCleaning: 'Merkbar tilsmussing — planlegg en rengjøring',
    recCleanNow: 'Kraftig tilsmussing — rengjøring anbefales nå',
    recRainWillClean: 'Regn i værmeldingen — det skyller panelene',
    fetchedAt: 'Oppdatert',
  },
  is: {
    title: 'Loftgæði',
    soilingRisk: 'Hætta á óhreinindum',
    riskLow: 'Lítil',
    riskModerate: 'Miðlungs',
    riskHigh: 'Mikil',
    dustLoad: 'Rykálag',
    particulates: 'Svifryk (PM10/PM2.5)',
    dust: 'Steinefnaryk',
    aod: 'Úði (AOD)',
    recNone: 'Spjöldin eru hrein — ekkert að gera',
    recMonitor: 'Smá ryk safnast — fylgstu með framleiðslunni',
    recPlanCleaning: 'Greinileg óhreinindi — skipuleggðu hreinsun',
    recCleanNow: 'Mikil óhreinindi — mælt með hreinsun núna',
    recRainWillClean: 'Rigning í spánni — hún skolar spjöldin',
    fetchedAt: 'Uppfært',
  },
  uk: {
    title: 'Якість повітря',
    soilingRisk: 'Ризик забруднення',
    riskLow: 'Низький',
    riskModerate: 'Помірний',
    riskHigh: 'Високий',
    dustLoad: 'Пилове навантаження',
    particulates: 'Тверді частинки (PM10/PM2.5)',
    dust: 'Мінеральний пил',
    aod: 'Аерозоль (AOD)',
    recNone: 'Панелі чисті — нічого робити не потрібно',
    recMonitor: 'Накопичується трохи пилу — стежте за виробленням',
    recPlanCleaning: 'Помітне забруднення — заплануйте чищення',
    recCleanNow: 'Сильне забруднення — рекомендовано почистити зараз',
    recRainWillClean: 'У прогнозі дощ — він обмиє панелі',
    fetchedAt: 'Оновлено',
  },
  sr: {
    title: 'Квалитет ваздуха',
    soilingRisk: 'Ризик од запрљања',
    riskLow: 'Низак',
    riskModerate: 'Умерен',
    riskHigh: 'Висок',
    dustLoad: 'Оптерећење прашином',
    particulates: 'Честице (PM10/PM2.5)',
    dust: 'Минерална прашина',
    aod: 'Аеросол (AOD)',
    recNone: 'Панели су чисти — ништа не треба радити',
    recMonitor: 'Накупља се мало прашине — пратите производњу',
    recPlanCleaning: 'Приметно запрљање — испланирајте чишћење',
    recCleanNow: 'Јако запрљање — препоручује се чишћење сада',
    recRainWillClean: 'У прогнози киша — испраће панеле',
    fetchedAt: 'Ажурирано',
  },
  sq: {
    title: 'Cilësia e ajrit',
    soilingRisk: 'Rrezik ndotjeje',
    riskLow: 'I ulët',
    riskModerate: 'I moderuar',
    riskHigh: 'I lartë',
    dustLoad: 'Ngarkesa e pluhurit',
    particulates: 'Grimcat (PM10/PM2.5)',
    dust: 'Pluhur mineral',
    aod: 'Aerosol (AOD)',
    recNone: "Panelet janë të pastra — s'ka nevojë për veprim",
    recMonitor: 'Po grumbullohet pak pluhur — mbaj nën sy prodhimin',
    recPlanCleaning: 'Ndotje e dukshme — planifiko një pastrim',
    recCleanNow: 'Ndotje e fortë — rekomandohet pastrim tani',
    recRainWillClean: "Shi në parashikim — do t'i shpëlajë panelet",
    fetchedAt: 'Përditësuar',
  },
  mk: {
    title: 'Квалитет на воздухот',
    soilingRisk: 'Ризик од извалкување',
    riskLow: 'Низок',
    riskModerate: 'Умерен',
    riskHigh: 'Висок',
    dustLoad: 'Оптоварување со прашина',
    particulates: 'Честички (PM10/PM2.5)',
    dust: 'Минерална прашина',
    aod: 'Аеросол (AOD)',
    recNone: 'Панелите се чисти — нема потреба од дејство',
    recMonitor: 'Се собира малку прашина — следете го производството',
    recPlanCleaning: 'Забележливо извалкување — планирајте чистење',
    recCleanNow: 'Силно извалкување — се препорачува чистење сега',
    recRainWillClean: 'Во прогнозата има дожд — ќе ги исплакне панелите',
    fetchedAt: 'Ажурирано',
  },
  bs: {
    title: 'Kvalitet zraka',
    soilingRisk: 'Rizik od zaprljanja',
    riskLow: 'Nizak',
    riskModerate: 'Umjeren',
    riskHigh: 'Visok',
    dustLoad: 'Opterećenje prašinom',
    particulates: 'Čestice (PM10/PM2.5)',
    dust: 'Mineralna prašina',
    aod: 'Aerosol (AOD)',
    recNone: 'Paneli su čisti — nije potrebno ništa',
    recMonitor: 'Nakuplja se malo prašine — pratite proizvodnju',
    recPlanCleaning: 'Primjetno zaprljanje — isplanirajte čišćenje',
    recCleanNow: 'Jako zaprljanje — preporučuje se čišćenje sada',
    recRainWillClean: 'U prognozi kiša — isprat će panele',
    fetchedAt: 'Ažurirano',
  },
};

const prodAlertsTr: Record<string, Dict> = {
  en: {
    title: 'Production alerts',
    snowOnPanels: 'Possible snow on panels — production may drop to near zero',
    heavyClouds: 'Heavy cloud cover — much lower production expected today',
    strongGusts: 'Strong wind gusts (>70 km/h) — check the mounting',
  },
  pl: {
    title: 'Alerty produkcyjne',
    snowOnPanels: 'Możliwy śnieg na panelach — produkcja może spaść niemal do zera',
    heavyClouds: 'Ciężkie zachmurzenie — dziś spodziewana znacznie niższa produkcja',
    strongGusts: 'Silne porywy wiatru (>70 km/h) — sprawdź mocowanie instalacji',
  },
  de: {
    title: 'Produktionswarnungen',
    snowOnPanels: 'Möglicherweise Schnee auf den Modulen — Produktion kann fast auf null fallen',
    heavyClouds: 'Starke Bewölkung — heute deutlich geringere Produktion erwartet',
    strongGusts: 'Starke Windböen (>70 km/h) — Befestigung prüfen',
  },
  fr: {
    title: 'Alertes de production',
    snowOnPanels: 'Neige possible sur les panneaux — la production peut tomber à près de zéro',
    heavyClouds: "Couverture nuageuse dense — production bien plus faible attendue aujourd'hui",
    strongGusts: 'Fortes rafales (>70 km/h) — vérifiez la fixation',
  },
  es: {
    title: 'Alertas de producción',
    snowOnPanels: 'Posible nieve en los paneles — la producción puede caer casi a cero',
    heavyClouds: 'Nubosidad densa — se espera una producción mucho menor hoy',
    strongGusts: 'Ráfagas fuertes (>70 km/h) — revisa la sujeción',
  },
  it: {
    title: 'Avvisi di produzione',
    snowOnPanels: 'Possibile neve sui pannelli — la produzione può scendere quasi a zero',
    heavyClouds: 'Nuvolosità densa — oggi prevista produzione molto inferiore',
    strongGusts: 'Forti raffiche (>70 km/h) — controlla il fissaggio',
  },
  pt: {
    title: 'Alertas de produção',
    snowOnPanels: 'Possível neve nos painéis — a produção pode cair para quase zero',
    heavyClouds: 'Nebulosidade densa — hoje espera-se uma produção muito menor',
    strongGusts: 'Rajadas fortes (>70 km/h) — verifique a fixação',
  },
  nl: {
    title: 'Productiewaarschuwingen',
    snowOnPanels: 'Mogelijk sneeuw op de panelen — productie kan tot bijna nul dalen',
    heavyClouds: 'Dichte bewolking — vandaag veel lagere productie verwacht',
    strongGusts: 'Sterke windstoten (>70 km/u) — controleer de bevestiging',
  },
  da: {
    title: 'Produktionsadvarsler',
    snowOnPanels: 'Muligvis sne på panelerne — produktionen kan falde til næsten nul',
    heavyClouds: 'Tæt skydække — meget lavere produktion forventet i dag',
    strongGusts: 'Kraftige vindstød (>70 km/t) — tjek monteringen',
  },
  sv: {
    title: 'Produktionsvarningar',
    snowOnPanels: 'Möjligen snö på panelerna — produktionen kan sjunka till nästan noll',
    heavyClouds: 'Tätt molntäcke — mycket lägre produktion förväntas idag',
    strongGusts: 'Kraftiga vindbyar (>70 km/h) — kontrollera infästningen',
  },
  fi: {
    title: 'Tuotantovaroitukset',
    snowOnPanels: 'Mahdollisesti lunta paneeleilla — tuotanto voi pudota lähes nollaan',
    heavyClouds: 'Tiivis pilvisyys — tänään odotettavissa paljon pienempi tuotanto',
    strongGusts: 'Voimakkaita puuskia (>70 km/h) — tarkista kiinnitys',
  },
  cs: {
    title: 'Produkční upozornění',
    snowOnPanels: 'Možný sníh na panelech — produkce může klesnout téměř na nulu',
    heavyClouds: 'Hustá oblačnost — dnes se očekává mnohem nižší produkce',
    strongGusts: 'Silné nárazy větru (>70 km/h) — zkontrolujte uchycení',
  },
  sk: {
    title: 'Produkčné upozornenia',
    snowOnPanels: 'Možný sneh na paneloch — produkcia môže klesnúť takmer na nulu',
    heavyClouds: 'Hustá oblačnosť — dnes sa očakáva oveľa nižšia produkcia',
    strongGusts: 'Silné nárazy vetra (>70 km/h) — skontrolujte uchytenie',
  },
  sl: {
    title: 'Opozorila o proizvodnji',
    snowOnPanels: 'Možen sneg na panelih — proizvodnja lahko pade skoraj na nič',
    heavyClouds: 'Gosta oblačnost — danes pričakovana veliko nižja proizvodnja',
    strongGusts: 'Močni sunki vetra (>70 km/h) — preverite pritrditev',
  },
  hr: {
    title: 'Upozorenja o proizvodnji',
    snowOnPanels: 'Mogući snijeg na panelima — proizvodnja može pasti gotovo na nulu',
    heavyClouds: 'Gusta naoblaka — danas se očekuje znatno niža proizvodnja',
    strongGusts: 'Jaki udari vjetra (>70 km/h) — provjerite pričvršćenje',
  },
  hu: {
    title: 'Termelési riasztások',
    snowOnPanels: 'Lehetséges hó a paneleken — a termelés szinte nullára eshet',
    heavyClouds: 'Sűrű felhőzet — ma sokkal alacsonyabb termelés várható',
    strongGusts: 'Erős széllökések (>70 km/h) — ellenőrizze a rögzítést',
  },
  ro: {
    title: 'Alerte de producție',
    snowOnPanels: 'Posibilă zăpadă pe panouri — producția poate scădea aproape la zero',
    heavyClouds: 'Nebulozitate densă — astăzi se așteaptă o producție mult mai mică',
    strongGusts: 'Rafale puternice (>70 km/h) — verificați prinderea',
  },
  bg: {
    title: 'Производствени предупреждения',
    snowOnPanels: 'Възможен сняг върху панелите — производството може да падне почти до нула',
    heavyClouds: 'Гъста облачност — днес се очаква значително по-ниско производство',
    strongGusts: 'Силни пориви на вятъра (>70 km/h) — проверете закрепването',
  },
  el: {
    title: 'Ειδοποιήσεις παραγωγής',
    snowOnPanels: 'Πιθανό χιόνι στα πάνελ — η παραγωγή μπορεί να πέσει σχεδόν στο μηδέν',
    heavyClouds: 'Πυκνή νεφοκάλυψη — αναμένεται πολύ χαμηλότερη παραγωγή σήμερα',
    strongGusts: 'Ισχυρές ριπές ανέμου (>70 km/h) — ελέγξτε τη στερέωση',
  },
  et: {
    title: 'Tootmishoiatused',
    snowOnPanels: 'Võimalik lumi paneelidel — tootmine võib langeda peaaegu nullini',
    heavyClouds: 'Tihe pilvkate — täna oodata palju väiksemat tootmist',
    strongGusts: 'Tugevad tuuleiilid (>70 km/h) — kontrolli kinnitust',
  },
  lv: {
    title: 'Ražošanas brīdinājumi',
    snowOnPanels: 'Iespējams sniegs uz paneļiem — ražošana var nokristies gandrīz līdz nullei',
    heavyClouds: 'Bieza mākoņainība — šodien gaidāma daudz zemāka ražošana',
    strongGusts: 'Spēcīgas vēja brāzmas (>70 km/h) — pārbaudiet stiprinājumu',
  },
  lt: {
    title: 'Gamybos įspėjimai',
    snowOnPanels: 'Galimas sniegas ant plokščių — gamyba gali nukristi beveik iki nulio',
    heavyClouds: 'Tankūs debesys — šiandien tikimasi daug mažesnės gamybos',
    strongGusts: 'Stiprūs vėjo gūsiai (>70 km/h) — patikrinkite tvirtinimą',
  },
  mt: {
    title: "Twissijiet ta' produzzjoni",
    snowOnPanels: "Borra possibbli fuq il-pannelli — il-produzzjoni tista' tinżel kważi għal żero",
    heavyClouds: 'Sħab dens — illum mistennija produzzjoni ferm aktar baxxa',
    strongGusts: 'Buffuri qawwija tar-riħ (>70 km/h) — iċċekkja l-immuntar',
  },
  ga: {
    title: 'Foláirimh táirgthe',
    snowOnPanels:
      "Sneachta féideartha ar na painéil — d'fhéadfadh an táirgeadh titim go dtí beagnach nialas",
    heavyClouds: 'Clúdach trom scamall — táirgeadh i bhfad níos ísle inniu',
    strongGusts: 'Séideáin láidre gaoithe (>70 km/u) — seiceáil an feistiú',
  },
  no: {
    title: 'Produksjonsvarsler',
    snowOnPanels: 'Mulig snø på panelene — produksjonen kan falle til nesten null',
    heavyClouds: 'Tett skydekke — mye lavere produksjon forventet i dag',
    strongGusts: 'Sterke vindkast (>70 km/t) — sjekk festet',
  },
  is: {
    title: 'Framleiðsluviðvaranir',
    snowOnPanels: 'Hugsanlega snjór á spjöldunum — framleiðsla getur fallið niður í næstum núll',
    heavyClouds: 'Þétt skýjahula — búast má við mun minni framleiðslu í dag',
    strongGusts: 'Sterkar vindhviður (>70 km/klst) — athugaðu festinguna',
  },
  uk: {
    title: 'Виробничі сповіщення',
    snowOnPanels: 'Можливий сніг на панелях — виробництво може впасти майже до нуля',
    heavyClouds: 'Щільна хмарність — сьогодні очікується значно менше виробництво',
    strongGusts: 'Сильні пориви вітру (>70 км/год) — перевірте кріплення',
  },
  sr: {
    title: 'Производна упозорења',
    snowOnPanels: 'Могућ снег на панелима — производња може пасти готово на нулу',
    heavyClouds: 'Густа облачност — данас се очекује знатно мања производња',
    strongGusts: 'Јаки удари ветра (>70 km/h) — проверите причвршћење',
  },
  sq: {
    title: 'Sinjalizime prodhimi',
    snowOnPanels: 'Borë e mundshme në panele — prodhimi mund të bjerë pothuajse në zero',
    heavyClouds: 'Vranësira të dendura — sot pritet prodhim shumë më i ulët',
    strongGusts: 'Shtërngata të forta ere (>70 km/h) — kontrollo montimin',
  },
  mk: {
    title: 'Производни известувања',
    snowOnPanels: 'Можен снег на панелите — производството може да падне речиси на нула',
    heavyClouds: 'Густа облачност — денес се очекува многу пониско производство',
    strongGusts: 'Силни удари на ветер (>70 km/h) — проверете го прицврстувањето',
  },
  bs: {
    title: 'Proizvodna upozorenja',
    snowOnPanels: 'Mogući snijeg na panelima — proizvodnja može pasti gotovo na nulu',
    heavyClouds: 'Gusta naoblačenost — danas se očekuje znatno niža proizvodnja',
    strongGusts: 'Jaki udari vjetra (>70 km/h) — provjerite pričvršćenje',
  },
};

const weatherTr: Record<string, Dict> = {
  en: {
    title: 'Weather',
    today: 'Today',
    tomorrow: 'Tomorrow',
    noData: 'No weather data',
    wmoClear: 'Clear sky',
    wmoMostlyClear: 'Mostly clear',
    wmoPartlyCloudy: 'Partly cloudy',
    wmoOvercast: 'Overcast',
    wmoFog: 'Fog',
    wmoDrizzle: 'Drizzle',
    wmoRain: 'Rain',
    wmoShowers: 'Showers',
    wmoSnow: 'Snow',
    wmoThunderstorm: 'Thunderstorm',
    wmoUnknown: 'Weather',
    tempRange: 'Temperature',
    cloudCover: 'Cloud cover',
    sunrise: 'Sunrise',
    sunset: 'Sunset',
    daylight: 'Daylight',
    sunshine: 'Sunshine hours',
    uvIndex: 'UV index',
    precip: 'Precipitation',
    snowfall: 'Snowfall',
    windGust: 'Wind gusts',
    bestWindow: 'Best production window',
    performanceRatio: 'Performance (vs. clear sky)',
    panelTemp: 'Panel temp.',
    thermalDerate: 'Temperature effect',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  pl: {
    title: 'Pogoda',
    today: 'Dziś',
    tomorrow: 'Jutro',
    noData: 'Brak danych pogodowych',
    wmoClear: 'Bezchmurnie',
    wmoMostlyClear: 'Przeważnie bezchmurnie',
    wmoPartlyCloudy: 'Częściowe zachmurzenie',
    wmoOvercast: 'Pełne zachmurzenie',
    wmoFog: 'Mgła',
    wmoDrizzle: 'Mżawka',
    wmoRain: 'Deszcz',
    wmoShowers: 'Przelotne opady',
    wmoSnow: 'Śnieg',
    wmoThunderstorm: 'Burza',
    wmoUnknown: 'Pogoda',
    tempRange: 'Temperatura',
    cloudCover: 'Zachmurzenie',
    sunrise: 'Wschód słońca',
    sunset: 'Zachód słońca',
    daylight: 'Długość dnia',
    sunshine: 'Godziny słoneczne',
    uvIndex: 'Indeks UV',
    precip: 'Opady',
    snowfall: 'Opad śniegu',
    windGust: 'Porywy wiatru',
    bestWindow: 'Najlepsze okno produkcji',
    performanceRatio: 'Wydajność (vs. niebo bezchmurne)',
    panelTemp: 'Temp. paneli',
    thermalDerate: 'Wpływ temperatury',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  de: {
    title: 'Wetter',
    today: 'Heute',
    tomorrow: 'Morgen',
    noData: 'Keine Wetterdaten',
    wmoClear: 'Klarer Himmel',
    wmoMostlyClear: 'Überwiegend klar',
    wmoPartlyCloudy: 'Teilweise bewölkt',
    wmoOvercast: 'Bedeckt',
    wmoFog: 'Nebel',
    wmoDrizzle: 'Nieselregen',
    wmoRain: 'Regen',
    wmoShowers: 'Schauer',
    wmoSnow: 'Schnee',
    wmoThunderstorm: 'Gewitter',
    wmoUnknown: 'Wetter',
    tempRange: 'Temperatur',
    cloudCover: 'Bewölkung',
    sunrise: 'Sonnenaufgang',
    sunset: 'Sonnenuntergang',
    daylight: 'Tageslicht',
    sunshine: 'Sonnenstunden',
    uvIndex: 'UV-Index',
    precip: 'Niederschlag',
    snowfall: 'Schneefall',
    windGust: 'Windböen',
    bestWindow: 'Bestes Produktionsfenster',
    performanceRatio: 'Leistung (vs. klarer Himmel)',
    panelTemp: 'Modultemp.',
    thermalDerate: 'Temperatureinfluss',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  fr: {
    title: 'Météo',
    today: "Aujourd'hui",
    tomorrow: 'Demain',
    noData: 'Aucune donnée météo',
    wmoClear: 'Ciel dégagé',
    wmoMostlyClear: 'Plutôt dégagé',
    wmoPartlyCloudy: 'Partiellement nuageux',
    wmoOvercast: 'Couvert',
    wmoFog: 'Brouillard',
    wmoDrizzle: 'Bruine',
    wmoRain: 'Pluie',
    wmoShowers: 'Averses',
    wmoSnow: 'Neige',
    wmoThunderstorm: 'Orage',
    wmoUnknown: 'Météo',
    tempRange: 'Température',
    cloudCover: 'Couverture nuageuse',
    sunrise: 'Lever du soleil',
    sunset: 'Coucher du soleil',
    daylight: 'Durée du jour',
    sunshine: "Heures d'ensoleillement",
    uvIndex: 'Indice UV',
    precip: 'Précipitations',
    snowfall: 'Chute de neige',
    windGust: 'Rafales de vent',
    bestWindow: 'Meilleure fenêtre de production',
    performanceRatio: 'Performance (vs ciel clair)',
    panelTemp: 'Temp. panneaux',
    thermalDerate: 'Effet de la température',
    hours: '{{n}} h',
    windowFmt: '{{from}}h–{{to}}h',
  },
  es: {
    title: 'Tiempo',
    today: 'Hoy',
    tomorrow: 'Mañana',
    noData: 'Sin datos meteorológicos',
    wmoClear: 'Cielo despejado',
    wmoMostlyClear: 'Mayormente despejado',
    wmoPartlyCloudy: 'Parcialmente nublado',
    wmoOvercast: 'Cubierto',
    wmoFog: 'Niebla',
    wmoDrizzle: 'Llovizna',
    wmoRain: 'Lluvia',
    wmoShowers: 'Chubascos',
    wmoSnow: 'Nieve',
    wmoThunderstorm: 'Tormenta',
    wmoUnknown: 'Tiempo',
    tempRange: 'Temperatura',
    cloudCover: 'Nubosidad',
    sunrise: 'Amanecer',
    sunset: 'Atardecer',
    daylight: 'Horas de luz',
    sunshine: 'Horas de sol',
    uvIndex: 'Índice UV',
    precip: 'Precipitación',
    snowfall: 'Nevada',
    windGust: 'Ráfagas de viento',
    bestWindow: 'Mejor ventana de producción',
    performanceRatio: 'Rendimiento (vs. cielo despejado)',
    panelTemp: 'Temp. paneles',
    thermalDerate: 'Efecto de la temperatura',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  it: {
    title: 'Meteo',
    today: 'Oggi',
    tomorrow: 'Domani',
    noData: 'Nessun dato meteo',
    wmoClear: 'Cielo sereno',
    wmoMostlyClear: 'Per lo più sereno',
    wmoPartlyCloudy: 'Parzialmente nuvoloso',
    wmoOvercast: 'Coperto',
    wmoFog: 'Nebbia',
    wmoDrizzle: 'Pioviggine',
    wmoRain: 'Pioggia',
    wmoShowers: 'Rovesci',
    wmoSnow: 'Neve',
    wmoThunderstorm: 'Temporale',
    wmoUnknown: 'Meteo',
    tempRange: 'Temperatura',
    cloudCover: 'Nuvolosità',
    sunrise: 'Alba',
    sunset: 'Tramonto',
    daylight: 'Ore di luce',
    sunshine: 'Ore di sole',
    uvIndex: 'Indice UV',
    precip: 'Precipitazioni',
    snowfall: 'Nevicata',
    windGust: 'Raffiche di vento',
    bestWindow: 'Migliore finestra di produzione',
    performanceRatio: 'Rendimento (vs. cielo sereno)',
    panelTemp: 'Temp. pannelli',
    thermalDerate: 'Effetto della temperatura',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  pt: {
    title: 'Tempo',
    today: 'Hoje',
    tomorrow: 'Amanhã',
    noData: 'Sem dados meteorológicos',
    wmoClear: 'Céu limpo',
    wmoMostlyClear: 'Maioritariamente limpo',
    wmoPartlyCloudy: 'Parcialmente nublado',
    wmoOvercast: 'Encoberto',
    wmoFog: 'Nevoeiro',
    wmoDrizzle: 'Chuvisco',
    wmoRain: 'Chuva',
    wmoShowers: 'Aguaceiros',
    wmoSnow: 'Neve',
    wmoThunderstorm: 'Trovoada',
    wmoUnknown: 'Tempo',
    tempRange: 'Temperatura',
    cloudCover: 'Nebulosidade',
    sunrise: 'Nascer do sol',
    sunset: 'Pôr do sol',
    daylight: 'Horas de luz',
    sunshine: 'Horas de sol',
    uvIndex: 'Índice UV',
    precip: 'Precipitação',
    snowfall: 'Queda de neve',
    windGust: 'Rajadas de vento',
    bestWindow: 'Melhor janela de produção',
    performanceRatio: 'Desempenho (vs. céu limpo)',
    panelTemp: 'Temp. painéis',
    thermalDerate: 'Efeito da temperatura',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  nl: {
    title: 'Weer',
    today: 'Vandaag',
    tomorrow: 'Morgen',
    noData: 'Geen weergegevens',
    wmoClear: 'Heldere hemel',
    wmoMostlyClear: 'Overwegend helder',
    wmoPartlyCloudy: 'Half bewolkt',
    wmoOvercast: 'Bewolkt',
    wmoFog: 'Mist',
    wmoDrizzle: 'Motregen',
    wmoRain: 'Regen',
    wmoShowers: 'Buien',
    wmoSnow: 'Sneeuw',
    wmoThunderstorm: 'Onweer',
    wmoUnknown: 'Weer',
    tempRange: 'Temperatuur',
    cloudCover: 'Bewolking',
    sunrise: 'Zonsopkomst',
    sunset: 'Zonsondergang',
    daylight: 'Daglicht',
    sunshine: 'Zonuren',
    uvIndex: 'UV-index',
    precip: 'Neerslag',
    snowfall: 'Sneeuwval',
    windGust: 'Windstoten',
    bestWindow: 'Beste productievenster',
    performanceRatio: 'Prestatie (vs. heldere hemel)',
    panelTemp: 'Paneeltemp.',
    thermalDerate: 'Temperatuureffect',
    hours: '{{n}} u',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  da: {
    title: 'Vejr',
    today: 'I dag',
    tomorrow: 'I morgen',
    noData: 'Ingen vejrdata',
    wmoClear: 'Klar himmel',
    wmoMostlyClear: 'Overvejende klart',
    wmoPartlyCloudy: 'Delvist skyet',
    wmoOvercast: 'Overskyet',
    wmoFog: 'Tåge',
    wmoDrizzle: 'Støvregn',
    wmoRain: 'Regn',
    wmoShowers: 'Byger',
    wmoSnow: 'Sne',
    wmoThunderstorm: 'Tordenvejr',
    wmoUnknown: 'Vejr',
    tempRange: 'Temperatur',
    cloudCover: 'Skydække',
    sunrise: 'Solopgang',
    sunset: 'Solnedgang',
    daylight: 'Dagslys',
    sunshine: 'Soltimer',
    uvIndex: 'UV-indeks',
    precip: 'Nedbør',
    snowfall: 'Snefald',
    windGust: 'Vindstød',
    bestWindow: 'Bedste produktionsvindue',
    performanceRatio: 'Ydeevne (vs. klar himmel)',
    panelTemp: 'Paneltemp.',
    thermalDerate: 'Temperatureffekt',
    hours: '{{n}} t',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  sv: {
    title: 'Väder',
    today: 'Idag',
    tomorrow: 'Imorgon',
    noData: 'Inga väderdata',
    wmoClear: 'Klar himmel',
    wmoMostlyClear: 'Mestadels klart',
    wmoPartlyCloudy: 'Delvis molnigt',
    wmoOvercast: 'Mulet',
    wmoFog: 'Dimma',
    wmoDrizzle: 'Duggregn',
    wmoRain: 'Regn',
    wmoShowers: 'Skurar',
    wmoSnow: 'Snö',
    wmoThunderstorm: 'Åskväder',
    wmoUnknown: 'Väder',
    tempRange: 'Temperatur',
    cloudCover: 'Molntäcke',
    sunrise: 'Soluppgång',
    sunset: 'Solnedgång',
    daylight: 'Dagsljus',
    sunshine: 'Soltimmar',
    uvIndex: 'UV-index',
    precip: 'Nederbörd',
    snowfall: 'Snöfall',
    windGust: 'Vindbyar',
    bestWindow: 'Bästa produktionsfönster',
    performanceRatio: 'Prestanda (vs. klar himmel)',
    panelTemp: 'Paneltemp.',
    thermalDerate: 'Temperatureffekt',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  fi: {
    title: 'Sää',
    today: 'Tänään',
    tomorrow: 'Huomenna',
    noData: 'Ei säätietoja',
    wmoClear: 'Selkeää',
    wmoMostlyClear: 'Enimmäkseen selkeää',
    wmoPartlyCloudy: 'Puolipilvistä',
    wmoOvercast: 'Pilvistä',
    wmoFog: 'Sumua',
    wmoDrizzle: 'Tihkusadetta',
    wmoRain: 'Sadetta',
    wmoShowers: 'Sadekuuroja',
    wmoSnow: 'Lunta',
    wmoThunderstorm: 'Ukkosta',
    wmoUnknown: 'Sää',
    tempRange: 'Lämpötila',
    cloudCover: 'Pilvisyys',
    sunrise: 'Auringonnousu',
    sunset: 'Auringonlasku',
    daylight: 'Päivänvalo',
    sunshine: 'Aurinkotunnit',
    uvIndex: 'UV-indeksi',
    precip: 'Sademäärä',
    snowfall: 'Lumisade',
    windGust: 'Tuulenpuuskat',
    bestWindow: 'Paras tuotantoikkuna',
    performanceRatio: 'Suorituskyky (vs. selkeä taivas)',
    panelTemp: 'Paneelien lämpö',
    thermalDerate: 'Lämpötilan vaikutus',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  cs: {
    title: 'Počasí',
    today: 'Dnes',
    tomorrow: 'Zítra',
    noData: 'Žádná data o počasí',
    wmoClear: 'Jasno',
    wmoMostlyClear: 'Převážně jasno',
    wmoPartlyCloudy: 'Polojasno',
    wmoOvercast: 'Zataženo',
    wmoFog: 'Mlha',
    wmoDrizzle: 'Mrholení',
    wmoRain: 'Déšť',
    wmoShowers: 'Přeháňky',
    wmoSnow: 'Sníh',
    wmoThunderstorm: 'Bouřka',
    wmoUnknown: 'Počasí',
    tempRange: 'Teplota',
    cloudCover: 'Oblačnost',
    sunrise: 'Východ slunce',
    sunset: 'Západ slunce',
    daylight: 'Délka dne',
    sunshine: 'Hodiny slunce',
    uvIndex: 'UV index',
    precip: 'Srážky',
    snowfall: 'Sněžení',
    windGust: 'Nárazy větru',
    bestWindow: 'Nejlepší okno produkce',
    performanceRatio: 'Výkon (vs. jasná obloha)',
    panelTemp: 'Teplota panelů',
    thermalDerate: 'Vliv teploty',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  sk: {
    title: 'Počasie',
    today: 'Dnes',
    tomorrow: 'Zajtra',
    noData: 'Žiadne údaje o počasí',
    wmoClear: 'Jasno',
    wmoMostlyClear: 'Prevažne jasno',
    wmoPartlyCloudy: 'Polojasno',
    wmoOvercast: 'Zamračené',
    wmoFog: 'Hmla',
    wmoDrizzle: 'Mrholenie',
    wmoRain: 'Dážď',
    wmoShowers: 'Prehánky',
    wmoSnow: 'Sneh',
    wmoThunderstorm: 'Búrka',
    wmoUnknown: 'Počasie',
    tempRange: 'Teplota',
    cloudCover: 'Oblačnosť',
    sunrise: 'Východ slnka',
    sunset: 'Západ slnka',
    daylight: 'Dĺžka dňa',
    sunshine: 'Hodiny slnka',
    uvIndex: 'UV index',
    precip: 'Zrážky',
    snowfall: 'Sneženie',
    windGust: 'Nárazy vetra',
    bestWindow: 'Najlepšie okno produkcie',
    performanceRatio: 'Výkon (vs. jasná obloha)',
    panelTemp: 'Teplota panelov',
    thermalDerate: 'Vplyv teploty',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  sl: {
    title: 'Vreme',
    today: 'Danes',
    tomorrow: 'Jutri',
    noData: 'Ni podatkov o vremenu',
    wmoClear: 'Jasno',
    wmoMostlyClear: 'Pretežno jasno',
    wmoPartlyCloudy: 'Delno oblačno',
    wmoOvercast: 'Oblačno',
    wmoFog: 'Megla',
    wmoDrizzle: 'Rosenje',
    wmoRain: 'Dež',
    wmoShowers: 'Plohe',
    wmoSnow: 'Sneg',
    wmoThunderstorm: 'Nevihta',
    wmoUnknown: 'Vreme',
    tempRange: 'Temperatura',
    cloudCover: 'Oblačnost',
    sunrise: 'Sončni vzhod',
    sunset: 'Sončni zahod',
    daylight: 'Dolžina dneva',
    sunshine: 'Sončne ure',
    uvIndex: 'UV indeks',
    precip: 'Padavine',
    snowfall: 'Sneženje',
    windGust: 'Sunki vetra',
    bestWindow: 'Najboljše okno proizvodnje',
    performanceRatio: 'Zmogljivost (vs. jasno nebo)',
    panelTemp: 'Temp. panelov',
    thermalDerate: 'Vpliv temperature',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  hr: {
    title: 'Vrijeme',
    today: 'Danas',
    tomorrow: 'Sutra',
    noData: 'Nema podataka o vremenu',
    wmoClear: 'Vedro',
    wmoMostlyClear: 'Pretežno vedro',
    wmoPartlyCloudy: 'Djelomično oblačno',
    wmoOvercast: 'Oblačno',
    wmoFog: 'Magla',
    wmoDrizzle: 'Rosulja',
    wmoRain: 'Kiša',
    wmoShowers: 'Pljuskovi',
    wmoSnow: 'Snijeg',
    wmoThunderstorm: 'Grmljavinsko nevrijeme',
    wmoUnknown: 'Vrijeme',
    tempRange: 'Temperatura',
    cloudCover: 'Naoblaka',
    sunrise: 'Izlazak sunca',
    sunset: 'Zalazak sunca',
    daylight: 'Trajanje dana',
    sunshine: 'Sati sunca',
    uvIndex: 'UV indeks',
    precip: 'Oborine',
    snowfall: 'Snježne padaline',
    windGust: 'Udari vjetra',
    bestWindow: 'Najbolji prozor proizvodnje',
    performanceRatio: 'Učinkovitost (vs. vedro nebo)',
    panelTemp: 'Temp. panela',
    thermalDerate: 'Utjecaj temperature',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  hu: {
    title: 'Időjárás',
    today: 'Ma',
    tomorrow: 'Holnap',
    noData: 'Nincsenek időjárási adatok',
    wmoClear: 'Derült',
    wmoMostlyClear: 'Többnyire derült',
    wmoPartlyCloudy: 'Részben felhős',
    wmoOvercast: 'Borult',
    wmoFog: 'Köd',
    wmoDrizzle: 'Szitálás',
    wmoRain: 'Eső',
    wmoShowers: 'Zápor',
    wmoSnow: 'Hó',
    wmoThunderstorm: 'Zivatar',
    wmoUnknown: 'Időjárás',
    tempRange: 'Hőmérséklet',
    cloudCover: 'Felhőzet',
    sunrise: 'Napkelte',
    sunset: 'Napnyugta',
    daylight: 'Nappal hossza',
    sunshine: 'Napsütéses órák',
    uvIndex: 'UV-index',
    precip: 'Csapadék',
    snowfall: 'Havazás',
    windGust: 'Széllökések',
    bestWindow: 'Legjobb termelési ablak',
    performanceRatio: 'Teljesítmény (vs. tiszta ég)',
    panelTemp: 'Panel hőm.',
    thermalDerate: 'Hőmérséklet hatása',
    hours: '{{n}} ó',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  ro: {
    title: 'Vreme',
    today: 'Azi',
    tomorrow: 'Mâine',
    noData: 'Fără date meteo',
    wmoClear: 'Cer senin',
    wmoMostlyClear: 'Predominant senin',
    wmoPartlyCloudy: 'Parțial înnorat',
    wmoOvercast: 'Acoperit',
    wmoFog: 'Ceață',
    wmoDrizzle: 'Burniță',
    wmoRain: 'Ploaie',
    wmoShowers: 'Averse',
    wmoSnow: 'Ninsoare',
    wmoThunderstorm: 'Furtună',
    wmoUnknown: 'Vreme',
    tempRange: 'Temperatură',
    cloudCover: 'Nebulozitate',
    sunrise: 'Răsărit',
    sunset: 'Apus',
    daylight: 'Durata zilei',
    sunshine: 'Ore de soare',
    uvIndex: 'Indice UV',
    precip: 'Precipitații',
    snowfall: 'Ninsoare',
    windGust: 'Rafale de vânt',
    bestWindow: 'Cea mai bună fereastră de producție',
    performanceRatio: 'Performanță (vs. cer senin)',
    panelTemp: 'Temp. panouri',
    thermalDerate: 'Efectul temperaturii',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  bg: {
    title: 'Време',
    today: 'Днес',
    tomorrow: 'Утре',
    noData: 'Няма метеоданни',
    wmoClear: 'Ясно',
    wmoMostlyClear: 'Предимно ясно',
    wmoPartlyCloudy: 'Частично облачно',
    wmoOvercast: 'Облачно',
    wmoFog: 'Мъгла',
    wmoDrizzle: 'Ръмеж',
    wmoRain: 'Дъжд',
    wmoShowers: 'Превалявания',
    wmoSnow: 'Сняг',
    wmoThunderstorm: 'Гръмотевична буря',
    wmoUnknown: 'Време',
    tempRange: 'Температура',
    cloudCover: 'Облачност',
    sunrise: 'Изгрев',
    sunset: 'Залез',
    daylight: 'Дължина на деня',
    sunshine: 'Слънчеви часове',
    uvIndex: 'UV индекс',
    precip: 'Валежи',
    snowfall: 'Снеговалеж',
    windGust: 'Пориви на вятъра',
    bestWindow: 'Най-добър прозорец за производство',
    performanceRatio: 'Производителност (спрямо ясно небе)',
    panelTemp: 'Темп. на панелите',
    thermalDerate: 'Влияние на температурата',
    hours: '{{n}} ч',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  el: {
    title: 'Καιρός',
    today: 'Σήμερα',
    tomorrow: 'Αύριο',
    noData: 'Δεν υπάρχουν δεδομένα καιρού',
    wmoClear: 'Αίθριος',
    wmoMostlyClear: 'Κυρίως αίθριος',
    wmoPartlyCloudy: 'Μερικώς νεφελώδης',
    wmoOvercast: 'Συννεφιά',
    wmoFog: 'Ομίχλη',
    wmoDrizzle: 'Ψιχάλα',
    wmoRain: 'Βροχή',
    wmoShowers: 'Μπόρες',
    wmoSnow: 'Χιόνι',
    wmoThunderstorm: 'Καταιγίδα',
    wmoUnknown: 'Καιρός',
    tempRange: 'Θερμοκρασία',
    cloudCover: 'Νεφοκάλυψη',
    sunrise: 'Ανατολή',
    sunset: 'Δύση',
    daylight: 'Διάρκεια ημέρας',
    sunshine: 'Ώρες ηλιοφάνειας',
    uvIndex: 'Δείκτης UV',
    precip: 'Βροχόπτωση',
    snowfall: 'Χιονόπτωση',
    windGust: 'Ριπές ανέμου',
    bestWindow: 'Καλύτερο παράθυρο παραγωγής',
    performanceRatio: 'Απόδοση (vs. αίθριος ουρανός)',
    panelTemp: 'Θερμ. πάνελ',
    thermalDerate: 'Επίδραση θερμοκρασίας',
    hours: '{{n}} ώ',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  et: {
    title: 'Ilm',
    today: 'Täna',
    tomorrow: 'Homme',
    noData: 'Ilmaandmed puuduvad',
    wmoClear: 'Selge',
    wmoMostlyClear: 'Enamasti selge',
    wmoPartlyCloudy: 'Osaliselt pilves',
    wmoOvercast: 'Pilves',
    wmoFog: 'Udu',
    wmoDrizzle: 'Uduvihm',
    wmoRain: 'Vihm',
    wmoShowers: 'Hoovihmad',
    wmoSnow: 'Lumi',
    wmoThunderstorm: 'Äike',
    wmoUnknown: 'Ilm',
    tempRange: 'Temperatuur',
    cloudCover: 'Pilvisus',
    sunrise: 'Päikesetõus',
    sunset: 'Päikeseloojang',
    daylight: 'Päevavalgus',
    sunshine: 'Päikesetunnid',
    uvIndex: 'UV-indeks',
    precip: 'Sademed',
    snowfall: 'Lumesadu',
    windGust: 'Tuuleiilid',
    bestWindow: 'Parim tootmisaken',
    performanceRatio: 'Jõudlus (vs. selge taevas)',
    panelTemp: 'Paneeli temp.',
    thermalDerate: 'Temperatuuri mõju',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  lv: {
    title: 'Laikapstākļi',
    today: 'Šodien',
    tomorrow: 'Rīt',
    noData: 'Nav laikapstākļu datu',
    wmoClear: 'Skaidrs',
    wmoMostlyClear: 'Galvenokārt skaidrs',
    wmoPartlyCloudy: 'Daļēji mākoņains',
    wmoOvercast: 'Apmācies',
    wmoFog: 'Migla',
    wmoDrizzle: 'Smidzina',
    wmoRain: 'Lietus',
    wmoShowers: 'Lietusgāzes',
    wmoSnow: 'Sniegs',
    wmoThunderstorm: 'Pērkona negaiss',
    wmoUnknown: 'Laikapstākļi',
    tempRange: 'Temperatūra',
    cloudCover: 'Mākoņainība',
    sunrise: 'Saullēkts',
    sunset: 'Saulriets',
    daylight: 'Dienas garums',
    sunshine: 'Saulainās stundas',
    uvIndex: 'UV indekss',
    precip: 'Nokrišņi',
    snowfall: 'Snigšana',
    windGust: 'Vēja brāzmas',
    bestWindow: 'Labākais ražošanas logs',
    performanceRatio: 'Veiktspēja (pret skaidrām debesīm)',
    panelTemp: 'Paneļu temp.',
    thermalDerate: 'Temperatūras ietekme',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  lt: {
    title: 'Orai',
    today: 'Šiandien',
    tomorrow: 'Rytoj',
    noData: 'Nėra orų duomenų',
    wmoClear: 'Giedra',
    wmoMostlyClear: 'Daugiausia giedra',
    wmoPartlyCloudy: 'Mažai debesuota',
    wmoOvercast: 'Debesuota',
    wmoFog: 'Rūkas',
    wmoDrizzle: 'Dulksna',
    wmoRain: 'Lietus',
    wmoShowers: 'Liūtys',
    wmoSnow: 'Sniegas',
    wmoThunderstorm: 'Perkūnija',
    wmoUnknown: 'Orai',
    tempRange: 'Temperatūra',
    cloudCover: 'Debesuotumas',
    sunrise: 'Saulėtekis',
    sunset: 'Saulėlydis',
    daylight: 'Dienos ilgumas',
    sunshine: 'Saulėtos valandos',
    uvIndex: 'UV indeksas',
    precip: 'Krituliai',
    snowfall: 'Snigimas',
    windGust: 'Vėjo gūsiai',
    bestWindow: 'Geriausias gamybos langas',
    performanceRatio: 'Našumas (vs. giedras dangus)',
    panelTemp: 'Plokščių temp.',
    thermalDerate: 'Temperatūros poveikis',
    hours: '{{n}} val.',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  mt: {
    title: 'It-temp',
    today: 'Illum',
    tomorrow: 'Għada',
    noData: 'Ebda data tat-temp',
    wmoClear: 'Bnazzi',
    wmoMostlyClear: 'Fil-biċċa l-kbira bnazzi',
    wmoPartlyCloudy: 'Parzjalment imsaħħab',
    wmoOvercast: 'Imsaħħab',
    wmoFog: 'Ċpar',
    wmoDrizzle: 'Rożoż',
    wmoRain: 'Xita',
    wmoShowers: 'Ħalbiet',
    wmoSnow: 'Borra',
    wmoThunderstorm: 'Maltempata bir-ragħad',
    wmoUnknown: 'It-temp',
    tempRange: 'Temperatura',
    cloudCover: 'Sħab',
    sunrise: 'Tlugħ ix-xemx',
    sunset: 'Nżul ix-xemx',
    daylight: 'Dawl tal-jum',
    sunshine: "Sigħat ta' xemx",
    uvIndex: 'Indiċi UV',
    precip: 'Preċipitazzjoni',
    snowfall: 'Borra',
    windGust: 'Buffuri tar-riħ',
    bestWindow: "L-aħjar tieqa ta' produzzjoni",
    performanceRatio: 'Prestazzjoni (vs. sema bnazzi)',
    panelTemp: 'Temp. tal-pannelli',
    thermalDerate: 'Effett tat-temperatura',
    hours: '{{n}} sg',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  ga: {
    title: 'Aimsir',
    today: 'Inniu',
    tomorrow: 'Amárach',
    noData: 'Gan sonraí aimsire',
    wmoClear: 'Spéir ghlan',
    wmoMostlyClear: 'Glan den chuid is mó',
    wmoPartlyCloudy: 'Scamallach go páirteach',
    wmoOvercast: 'Modartha',
    wmoFog: 'Ceo',
    wmoDrizzle: 'Ceobhrán',
    wmoRain: 'Báisteach',
    wmoShowers: 'Ceathanna',
    wmoSnow: 'Sneachta',
    wmoThunderstorm: 'Stoirm thoirní',
    wmoUnknown: 'Aimsir',
    tempRange: 'Teocht',
    cloudCover: 'Clúdach scamall',
    sunrise: 'Éirí na gréine',
    sunset: 'Luí na gréine',
    daylight: 'Solas an lae',
    sunshine: 'Uaireanta gréine',
    uvIndex: 'Innéacs UV',
    precip: 'Deascadh',
    snowfall: 'Titim shneachta',
    windGust: 'Séideáin ghaoithe',
    bestWindow: 'An fhuinneog táirgthe is fearr',
    performanceRatio: 'Feidhmíocht (vs. spéir ghlan)',
    panelTemp: 'Teocht na bpainéal',
    thermalDerate: 'Éifeacht na teochta',
    hours: '{{n}} u',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  no: {
    title: 'Vær',
    today: 'I dag',
    tomorrow: 'I morgen',
    noData: 'Ingen værdata',
    wmoClear: 'Klar himmel',
    wmoMostlyClear: 'Stort sett klart',
    wmoPartlyCloudy: 'Delvis skyet',
    wmoOvercast: 'Overskyet',
    wmoFog: 'Tåke',
    wmoDrizzle: 'Yr',
    wmoRain: 'Regn',
    wmoShowers: 'Byger',
    wmoSnow: 'Snø',
    wmoThunderstorm: 'Tordenvær',
    wmoUnknown: 'Vær',
    tempRange: 'Temperatur',
    cloudCover: 'Skydekke',
    sunrise: 'Soloppgang',
    sunset: 'Solnedgang',
    daylight: 'Dagslys',
    sunshine: 'Soltimer',
    uvIndex: 'UV-indeks',
    precip: 'Nedbør',
    snowfall: 'Snøfall',
    windGust: 'Vindkast',
    bestWindow: 'Beste produksjonsvindu',
    performanceRatio: 'Ytelse (vs. klar himmel)',
    panelTemp: 'Paneltemp.',
    thermalDerate: 'Temperatureffekt',
    hours: '{{n}} t',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  is: {
    title: 'Veður',
    today: 'Í dag',
    tomorrow: 'Á morgun',
    noData: 'Engin veðurgögn',
    wmoClear: 'Heiðskírt',
    wmoMostlyClear: 'Að mestu heiðskírt',
    wmoPartlyCloudy: 'Skýjað með köflum',
    wmoOvercast: 'Alskýjað',
    wmoFog: 'Þoka',
    wmoDrizzle: 'Súld',
    wmoRain: 'Rigning',
    wmoShowers: 'Skúrir',
    wmoSnow: 'Snjókoma',
    wmoThunderstorm: 'Þrumuveður',
    wmoUnknown: 'Veður',
    tempRange: 'Hitastig',
    cloudCover: 'Skýjahula',
    sunrise: 'Sólarupprás',
    sunset: 'Sólsetur',
    daylight: 'Dagsbirta',
    sunshine: 'Sólskinsstundir',
    uvIndex: 'UV-stuðull',
    precip: 'Úrkoma',
    snowfall: 'Snjókoma',
    windGust: 'Vindhviður',
    bestWindow: 'Besti framleiðslugluggi',
    performanceRatio: 'Afköst (m.v. heiðskíran himin)',
    panelTemp: 'Hiti spjalda',
    thermalDerate: 'Áhrif hitastigs',
    hours: '{{n}} klst',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  uk: {
    title: 'Погода',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    noData: 'Немає даних про погоду',
    wmoClear: 'Ясно',
    wmoMostlyClear: 'Переважно ясно',
    wmoPartlyCloudy: 'Мінлива хмарність',
    wmoOvercast: 'Хмарно',
    wmoFog: 'Туман',
    wmoDrizzle: 'Мряка',
    wmoRain: 'Дощ',
    wmoShowers: 'Зливи',
    wmoSnow: 'Сніг',
    wmoThunderstorm: 'Гроза',
    wmoUnknown: 'Погода',
    tempRange: 'Температура',
    cloudCover: 'Хмарність',
    sunrise: 'Схід сонця',
    sunset: 'Захід сонця',
    daylight: 'Тривалість дня',
    sunshine: 'Сонячні години',
    uvIndex: 'УФ-індекс',
    precip: 'Опади',
    snowfall: 'Снігопад',
    windGust: 'Пориви вітру',
    bestWindow: 'Найкраще вікно виробництва',
    performanceRatio: 'Продуктивність (vs. ясне небо)',
    panelTemp: 'Темп. панелей',
    thermalDerate: 'Вплив температури',
    hours: '{{n}} год',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  sr: {
    title: 'Време',
    today: 'Данас',
    tomorrow: 'Сутра',
    noData: 'Нема података о времену',
    wmoClear: 'Ведро',
    wmoMostlyClear: 'Претежно ведро',
    wmoPartlyCloudy: 'Делимично облачно',
    wmoOvercast: 'Облачно',
    wmoFog: 'Магла',
    wmoDrizzle: 'Ромиња',
    wmoRain: 'Киша',
    wmoShowers: 'Пљускови',
    wmoSnow: 'Снег',
    wmoThunderstorm: 'Грмљавинско невреме',
    wmoUnknown: 'Време',
    tempRange: 'Температура',
    cloudCover: 'Облачност',
    sunrise: 'Излазак сунца',
    sunset: 'Залазак сунца',
    daylight: 'Дужина дана',
    sunshine: 'Сунчани сати',
    uvIndex: 'УВ индекс',
    precip: 'Падавине',
    snowfall: 'Снежне падавине',
    windGust: 'Удари ветра',
    bestWindow: 'Најбољи прозор производње',
    performanceRatio: 'Учинак (у односу на ведро небо)',
    panelTemp: 'Темп. панела',
    thermalDerate: 'Утицај температуре',
    hours: '{{n}} ч',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  sq: {
    title: 'Moti',
    today: 'Sot',
    tomorrow: 'Nesër',
    noData: 'Asnjë e dhënë moti',
    wmoClear: 'Qiell i kthjellët',
    wmoMostlyClear: 'Kryesisht i kthjellët',
    wmoPartlyCloudy: 'Pjesërisht me re',
    wmoOvercast: 'I vranët',
    wmoFog: 'Mjegull',
    wmoDrizzle: 'Shtrëngatë e imët',
    wmoRain: 'Shi',
    wmoShowers: 'Rrebeshe',
    wmoSnow: 'Borë',
    wmoThunderstorm: 'Stuhi me bubullimë',
    wmoUnknown: 'Moti',
    tempRange: 'Temperatura',
    cloudCover: 'Vranësira',
    sunrise: 'Lindja e diellit',
    sunset: 'Perëndimi i diellit',
    daylight: 'Kohëzgjatja e ditës',
    sunshine: 'Orë me diell',
    uvIndex: 'Indeksi UV',
    precip: 'Reshje',
    snowfall: 'Reshje bore',
    windGust: 'Shtërngata ere',
    bestWindow: 'Dritarja më e mirë e prodhimit',
    performanceRatio: 'Performanca (vs. qiell i kthjellët)',
    panelTemp: 'Temp. e paneleve',
    thermalDerate: 'Efekti i temperaturës',
    hours: '{{n}} orë',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  mk: {
    title: 'Време',
    today: 'Денес',
    tomorrow: 'Утре',
    noData: 'Нема податоци за времето',
    wmoClear: 'Ведро',
    wmoMostlyClear: 'Претежно ведро',
    wmoPartlyCloudy: 'Делумно облачно',
    wmoOvercast: 'Облачно',
    wmoFog: 'Магла',
    wmoDrizzle: 'Ситен дожд',
    wmoRain: 'Дожд',
    wmoShowers: 'Пороен дожд',
    wmoSnow: 'Снег',
    wmoThunderstorm: 'Грмежи',
    wmoUnknown: 'Време',
    tempRange: 'Температура',
    cloudCover: 'Облачност',
    sunrise: 'Изгрев на сонцето',
    sunset: 'Зајдисонце',
    daylight: 'Должина на денот',
    sunshine: 'Сончеви часови',
    uvIndex: 'УВ индекс',
    precip: 'Врнежи',
    snowfall: 'Снежни врнежи',
    windGust: 'Удари на ветер',
    bestWindow: 'Најдобар прозорец за производство',
    performanceRatio: 'Перформанси (vs. ведро небо)',
    panelTemp: 'Темп. на панелите',
    thermalDerate: 'Влијание на температурата',
    hours: '{{n}} ч',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
  bs: {
    title: 'Vrijeme',
    today: 'Danas',
    tomorrow: 'Sutra',
    noData: 'Nema podataka o vremenu',
    wmoClear: 'Vedro',
    wmoMostlyClear: 'Pretežno vedro',
    wmoPartlyCloudy: 'Djelimično oblačno',
    wmoOvercast: 'Oblačno',
    wmoFog: 'Magla',
    wmoDrizzle: 'Rosulja',
    wmoRain: 'Kiša',
    wmoShowers: 'Pljuskovi',
    wmoSnow: 'Snijeg',
    wmoThunderstorm: 'Grmljavinsko nevrijeme',
    wmoUnknown: 'Vrijeme',
    tempRange: 'Temperatura',
    cloudCover: 'Naoblačenost',
    sunrise: 'Izlazak sunca',
    sunset: 'Zalazak sunca',
    daylight: 'Trajanje dana',
    sunshine: 'Sunčani sati',
    uvIndex: 'UV indeks',
    precip: 'Padavine',
    snowfall: 'Snježne padavine',
    windGust: 'Udari vjetra',
    bestWindow: 'Najbolji prozor proizvodnje',
    performanceRatio: 'Učinak (vs. vedro nebo)',
    panelTemp: 'Temp. panela',
    thermalDerate: 'Utjecaj temperature',
    hours: '{{n}} h',
    windowFmt: '{{from}}:00–{{to}}:00',
  },
};

const clientsTr: Record<string, Dict> = {
  en: {
    title: 'Client management',
    subtitle: 'Overview and management of all clients',
    searchPlaceholder: 'Search by name or e-mail…',
    exportBtn: 'Export clients',
    loadFailed: 'Failed to load the client list',
    loadingClients: 'Loading clients…',
    emptyClients: 'No clients',
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {{cur}} of {{total}}',
    colApiKeys: 'API keys',
    colInstallations: 'Installations',
    colRequests: 'Requests',
    colCreatedAt: 'Created at',
    colActions: 'Actions',
    actView: 'Details',
    actEdit: 'Edit',
    actStats: 'Statistics',
    actResetPw: 'Reset password',
    actDelete: 'Delete',
    lblName: 'Name',
    lblEmail: 'E-mail',
    lblPhone: 'Phone',
    lblCompany: 'Company',
    lblNip: 'Tax ID',
    lblStreet: 'Street',
    lblCity: 'City',
    lblPostalCode: 'Postal code',
    lblCountry: 'Country',
    secPersonal: 'Personal data',
    secCompany: 'Company data',
    secAddress: 'Address',
    secDates: 'Dates',
    lblRegDate: 'Registration date',
    lblLastLogin: 'Last login',
    lblLastUpdate: 'Last update',
    tabInfo: 'Client data',
    tabInstallations: 'Installations',
    tabApiKeys: 'API keys',
    tabCodes: 'Activation codes',
    tabStats: 'Statistics',
    noInstallations: 'No installations',
    installationFallback: 'Installation',
    stringsLabel: 'string(s)',
    lblUpdated: 'Updated',
    lblLatitude: 'Latitude:',
    lblLongitude: 'Longitude:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panels, az={{az}}, tilt={{tilt}}, power={{ps}}W, eff={{eff}}%',
    noApiKeys: 'No API keys',
    thKey: 'Key',
    thPlan: 'Plan',
    thInstallation: 'Installation',
    thStatus: 'Status',
    thExpires: 'Expires',
    thCreated: 'Created',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusExpired: 'Expired',
    noCodes: 'No used activation codes',
    thCode: 'Code',
    thUsedAt: 'Used at',
    thCreatedAt: 'Created at',
    statTotalRequests: 'Total requests',
    statActiveKeys: 'Active API keys',
    statAllKeys: 'All keys',
    statInstallations: 'Installations',
    chartTitle30d: 'Requests in the last 30 days',
    noRequestData: 'No request data',
    modalClientTitle: 'Client: {{name}}',
    loading: 'Loading…',
    loadingClientData: 'Loading client data…',
    editTitle: 'Edit client',
    cancel: 'Cancel',
    save: 'Save',
    statsTitle: 'Client statistics',
    statsRequestsOverTime: 'Requests over time',
    deleteTitle: 'Delete client',
    deleteConfirm:
      'Are you sure you want to delete client "{{name}}"? This operation will also delete all API keys and installations of this client.',
    deleteBtn: 'Delete',
    resetLinkTitle: 'Password reset link',
    resetLinkInfo: 'Give the link below to the client. The link is valid for 1 hour.',
    copyBtn: 'Copy',
  },
  pl: {
    title: 'Zarządzanie klientami',
    subtitle: 'Przegląd i zarządzanie wszystkimi klientami',
    searchPlaceholder: 'Szukaj po nazwie lub e-mailu…',
    exportBtn: 'Eksportuj klientów',
    loadFailed: 'Nie udało się załadować listy klientów',
    loadingClients: 'Wczytywanie klientów…',
    emptyClients: 'Brak klientów',
    prev: 'Poprzednia',
    next: 'Następna',
    pageOf: 'Strona {{cur}} z {{total}}',
    colApiKeys: 'Klucze API',
    colInstallations: 'Instalacje',
    colRequests: 'Żądania',
    colCreatedAt: 'Data utworzenia',
    colActions: 'Akcje',
    actView: 'Szczegóły',
    actEdit: 'Edytuj',
    actStats: 'Statystyki',
    actResetPw: 'Resetuj hasło',
    actDelete: 'Usuń',
    lblName: 'Nazwa',
    lblEmail: 'E-mail',
    lblPhone: 'Telefon',
    lblCompany: 'Firma',
    lblNip: 'NIP',
    lblStreet: 'Ulica',
    lblCity: 'Miasto',
    lblPostalCode: 'Kod pocztowy',
    lblCountry: 'Kraj',
    secPersonal: 'Dane osobowe',
    secCompany: 'Dane firmy',
    secAddress: 'Adres',
    secDates: 'Daty',
    lblRegDate: 'Data rejestracji',
    lblLastLogin: 'Ostatnie logowanie',
    lblLastUpdate: 'Ostatnia aktualizacja',
    tabInfo: 'Dane klienta',
    tabInstallations: 'Instalacje',
    tabApiKeys: 'Klucze API',
    tabCodes: 'Kody aktywacyjne',
    tabStats: 'Statystyki',
    noInstallations: 'Brak instalacji',
    installationFallback: 'Instalacja',
    stringsLabel: 'string(ów)',
    lblUpdated: 'Aktualizacja',
    lblLatitude: 'Szerokość:',
    lblLongitude: 'Długość:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneli, az={{az}}, tilt={{tilt}}, moc={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Brak kluczy API',
    thKey: 'Klucz',
    thPlan: 'Plan',
    thInstallation: 'Instalacja',
    thStatus: 'Status',
    thExpires: 'Wygasa',
    thCreated: 'Utworzono',
    statusActive: 'Aktywny',
    statusInactive: 'Nieaktywny',
    statusExpired: 'Wygasły',
    noCodes: 'Brak wykorzystanych kodów aktywacyjnych',
    thCode: 'Kod',
    thUsedAt: 'Data użycia',
    thCreatedAt: 'Data utworzenia',
    statTotalRequests: 'Całkowita liczba żądań',
    statActiveKeys: 'Aktywne klucze API',
    statAllKeys: 'Wszystkie klucze',
    statInstallations: 'Instalacje',
    chartTitle30d: 'Żądania w ostatnich 30 dniach',
    noRequestData: 'Brak danych o żądaniach',
    modalClientTitle: 'Klient: {{name}}',
    loading: 'Wczytywanie…',
    loadingClientData: 'Wczytywanie danych klienta…',
    editTitle: 'Edytuj klienta',
    cancel: 'Anuluj',
    save: 'Zapisz',
    statsTitle: 'Statystyki klienta',
    statsRequestsOverTime: 'Żądania w czasie',
    deleteTitle: 'Usuń klienta',
    deleteConfirm:
      'Czy na pewno chcesz usunąć klienta „{{name}}”? Ta operacja usunie również wszystkie klucze API i instalacje tego klienta.',
    deleteBtn: 'Usuń',
    resetLinkTitle: 'Link resetowania hasła',
    resetLinkInfo: 'Przekaż poniższy link klientowi. Link jest ważny przez 1 godzinę.',
    copyBtn: 'Skopiuj',
  },
  de: {
    title: 'Kundenverwaltung',
    subtitle: 'Übersicht und Verwaltung aller Kunden',
    searchPlaceholder: 'Nach Name oder E-Mail suchen…',
    exportBtn: 'Kunden exportieren',
    loadFailed: 'Kundenliste konnte nicht geladen werden',
    loadingClients: 'Kunden werden geladen…',
    emptyClients: 'Keine Kunden',
    prev: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {{cur}} von {{total}}',
    colApiKeys: 'API-Schlüssel',
    colInstallations: 'Anlagen',
    colRequests: 'Anfragen',
    colCreatedAt: 'Erstellt am',
    colActions: 'Aktionen',
    actView: 'Details',
    actEdit: 'Bearbeiten',
    actStats: 'Statistiken',
    actResetPw: 'Passwort zurücksetzen',
    actDelete: 'Löschen',
    lblName: 'Name',
    lblEmail: 'E-Mail',
    lblPhone: 'Telefon',
    lblCompany: 'Firma',
    lblNip: 'USt-IdNr.',
    lblStreet: 'Straße',
    lblCity: 'Stadt',
    lblPostalCode: 'Postleitzahl',
    lblCountry: 'Land',
    secPersonal: 'Persönliche Daten',
    secCompany: 'Firmendaten',
    secAddress: 'Adresse',
    secDates: 'Daten',
    lblRegDate: 'Registrierungsdatum',
    lblLastLogin: 'Letzte Anmeldung',
    lblLastUpdate: 'Letzte Aktualisierung',
    tabInfo: 'Kundendaten',
    tabInstallations: 'Anlagen',
    tabApiKeys: 'API-Schlüssel',
    tabCodes: 'Aktivierungscodes',
    tabStats: 'Statistiken',
    noInstallations: 'Keine Anlagen',
    installationFallback: 'Anlage',
    stringsLabel: 'String(s)',
    lblUpdated: 'Aktualisiert',
    lblLatitude: 'Breitengrad:',
    lblLongitude: 'Längengrad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} Module, az={{az}}, tilt={{tilt}}, Leistung={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Keine API-Schlüssel',
    thKey: 'Schlüssel',
    thPlan: 'Plan',
    thInstallation: 'Anlage',
    thStatus: 'Status',
    thExpires: 'Läuft ab',
    thCreated: 'Erstellt',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    statusExpired: 'Abgelaufen',
    noCodes: 'Keine verwendeten Aktivierungscodes',
    thCode: 'Code',
    thUsedAt: 'Verwendet am',
    thCreatedAt: 'Erstellt am',
    statTotalRequests: 'Anfragen gesamt',
    statActiveKeys: 'Aktive API-Schlüssel',
    statAllKeys: 'Alle Schlüssel',
    statInstallations: 'Anlagen',
    chartTitle30d: 'Anfragen in den letzten 30 Tagen',
    noRequestData: 'Keine Anfragedaten',
    modalClientTitle: 'Kunde: {{name}}',
    loading: 'Wird geladen…',
    loadingClientData: 'Kundendaten werden geladen…',
    editTitle: 'Kunde bearbeiten',
    cancel: 'Abbrechen',
    save: 'Speichern',
    statsTitle: 'Kundenstatistiken',
    statsRequestsOverTime: 'Anfragen im Zeitverlauf',
    deleteTitle: 'Kunde löschen',
    deleteConfirm:
      'Möchten Sie den Kunden „{{name}}“ wirklich löschen? Dieser Vorgang löscht auch alle API-Schlüssel und Anlagen dieses Kunden.',
    deleteBtn: 'Löschen',
    resetLinkTitle: 'Link zum Zurücksetzen des Passworts',
    resetLinkInfo:
      'Geben Sie den untenstehenden Link an den Kunden weiter. Der Link ist 1 Stunde gültig.',
    copyBtn: 'Kopieren',
  },
  fr: {
    title: 'Gestion des clients',
    subtitle: "Vue d'ensemble et gestion de tous les clients",
    searchPlaceholder: 'Rechercher par nom ou e-mail…',
    exportBtn: 'Exporter les clients',
    loadFailed: 'Échec du chargement de la liste des clients',
    loadingClients: 'Chargement des clients…',
    emptyClients: 'Aucun client',
    prev: 'Précédent',
    next: 'Suivant',
    pageOf: 'Page {{cur}} sur {{total}}',
    colApiKeys: 'Clés API',
    colInstallations: 'Installations',
    colRequests: 'Requêtes',
    colCreatedAt: 'Créé le',
    colActions: 'Actions',
    actView: 'Détails',
    actEdit: 'Modifier',
    actStats: 'Statistiques',
    actResetPw: 'Réinitialiser le mot de passe',
    actDelete: 'Supprimer',
    lblName: 'Nom',
    lblEmail: 'E-mail',
    lblPhone: 'Téléphone',
    lblCompany: 'Société',
    lblNip: 'N° de TVA',
    lblStreet: 'Rue',
    lblCity: 'Ville',
    lblPostalCode: 'Code postal',
    lblCountry: 'Pays',
    secPersonal: 'Données personnelles',
    secCompany: 'Données de la société',
    secAddress: 'Adresse',
    secDates: 'Dates',
    lblRegDate: "Date d'inscription",
    lblLastLogin: 'Dernière connexion',
    lblLastUpdate: 'Dernière mise à jour',
    tabInfo: 'Données du client',
    tabInstallations: 'Installations',
    tabApiKeys: 'Clés API',
    tabCodes: "Codes d'activation",
    tabStats: 'Statistiques',
    noInstallations: 'Aucune installation',
    installationFallback: 'Installation',
    stringsLabel: 'string(s)',
    lblUpdated: 'Mis à jour',
    lblLatitude: 'Latitude :',
    lblLongitude: 'Longitude :',
    stringNLabel: 'String {{n}} :',
    stringConfigDetail:
      '{{pc}} panneaux, az={{az}}, tilt={{tilt}}, puissance={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Aucune clé API',
    thKey: 'Clé',
    thPlan: 'Plan',
    thInstallation: 'Installation',
    thStatus: 'Statut',
    thExpires: 'Expire',
    thCreated: 'Créé',
    statusActive: 'Actif',
    statusInactive: 'Inactif',
    statusExpired: 'Expiré',
    noCodes: "Aucun code d'activation utilisé",
    thCode: 'Code',
    thUsedAt: 'Utilisé le',
    thCreatedAt: 'Créé le',
    statTotalRequests: 'Total des requêtes',
    statActiveKeys: 'Clés API actives',
    statAllKeys: 'Toutes les clés',
    statInstallations: 'Installations',
    chartTitle30d: 'Requêtes des 30 derniers jours',
    noRequestData: 'Aucune donnée de requête',
    modalClientTitle: 'Client : {{name}}',
    loading: 'Chargement…',
    loadingClientData: 'Chargement des données du client…',
    editTitle: 'Modifier le client',
    cancel: 'Annuler',
    save: 'Enregistrer',
    statsTitle: 'Statistiques du client',
    statsRequestsOverTime: 'Requêtes au fil du temps',
    deleteTitle: 'Supprimer le client',
    deleteConfirm:
      'Voulez-vous vraiment supprimer le client « {{name}} » ? Cette opération supprimera également toutes les clés API et installations de ce client.',
    deleteBtn: 'Supprimer',
    resetLinkTitle: 'Lien de réinitialisation du mot de passe',
    resetLinkInfo: 'Transmettez le lien ci-dessous au client. Le lien est valable 1 heure.',
    copyBtn: 'Copier',
  },
  es: {
    title: 'Gestión de clientes',
    subtitle: 'Resumen y gestión de todos los clientes',
    searchPlaceholder: 'Buscar por nombre o correo…',
    exportBtn: 'Exportar clientes',
    loadFailed: 'No se pudo cargar la lista de clientes',
    loadingClients: 'Cargando clientes…',
    emptyClients: 'Sin clientes',
    prev: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {{cur}} de {{total}}',
    colApiKeys: 'Claves API',
    colInstallations: 'Instalaciones',
    colRequests: 'Solicitudes',
    colCreatedAt: 'Creado el',
    colActions: 'Acciones',
    actView: 'Detalles',
    actEdit: 'Editar',
    actStats: 'Estadísticas',
    actResetPw: 'Restablecer contraseña',
    actDelete: 'Eliminar',
    lblName: 'Nombre',
    lblEmail: 'Correo electrónico',
    lblPhone: 'Teléfono',
    lblCompany: 'Empresa',
    lblNip: 'NIF/CIF',
    lblStreet: 'Calle',
    lblCity: 'Ciudad',
    lblPostalCode: 'Código postal',
    lblCountry: 'País',
    secPersonal: 'Datos personales',
    secCompany: 'Datos de la empresa',
    secAddress: 'Dirección',
    secDates: 'Fechas',
    lblRegDate: 'Fecha de registro',
    lblLastLogin: 'Último inicio de sesión',
    lblLastUpdate: 'Última actualización',
    tabInfo: 'Datos del cliente',
    tabInstallations: 'Instalaciones',
    tabApiKeys: 'Claves API',
    tabCodes: 'Códigos de activación',
    tabStats: 'Estadísticas',
    noInstallations: 'Sin instalaciones',
    installationFallback: 'Instalación',
    stringsLabel: 'string(s)',
    lblUpdated: 'Actualizado',
    lblLatitude: 'Latitud:',
    lblLongitude: 'Longitud:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneles, az={{az}}, tilt={{tilt}}, potencia={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Sin claves API',
    thKey: 'Clave',
    thPlan: 'Plan',
    thInstallation: 'Instalación',
    thStatus: 'Estado',
    thExpires: 'Caduca',
    thCreated: 'Creado',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    statusExpired: 'Caducado',
    noCodes: 'Sin códigos de activación usados',
    thCode: 'Código',
    thUsedAt: 'Usado el',
    thCreatedAt: 'Creado el',
    statTotalRequests: 'Total de solicitudes',
    statActiveKeys: 'Claves API activas',
    statAllKeys: 'Todas las claves',
    statInstallations: 'Instalaciones',
    chartTitle30d: 'Solicitudes en los últimos 30 días',
    noRequestData: 'Sin datos de solicitudes',
    modalClientTitle: 'Cliente: {{name}}',
    loading: 'Cargando…',
    loadingClientData: 'Cargando datos del cliente…',
    editTitle: 'Editar cliente',
    cancel: 'Cancelar',
    save: 'Guardar',
    statsTitle: 'Estadísticas del cliente',
    statsRequestsOverTime: 'Solicitudes a lo largo del tiempo',
    deleteTitle: 'Eliminar cliente',
    deleteConfirm:
      '¿Seguro que quieres eliminar al cliente «{{name}}»? Esta operación también eliminará todas las claves API e instalaciones de este cliente.',
    deleteBtn: 'Eliminar',
    resetLinkTitle: 'Enlace para restablecer la contraseña',
    resetLinkInfo: 'Pasa el siguiente enlace al cliente. El enlace es válido durante 1 hora.',
    copyBtn: 'Copiar',
  },
  it: {
    title: 'Gestione dei clienti',
    subtitle: 'Panoramica e gestione di tutti i clienti',
    searchPlaceholder: 'Cerca per nome o e-mail…',
    exportBtn: 'Esporta clienti',
    loadFailed: "Impossibile caricare l'elenco dei clienti",
    loadingClients: 'Caricamento clienti…',
    emptyClients: 'Nessun cliente',
    prev: 'Precedente',
    next: 'Successiva',
    pageOf: 'Pagina {{cur}} di {{total}}',
    colApiKeys: 'Chiavi API',
    colInstallations: 'Impianti',
    colRequests: 'Richieste',
    colCreatedAt: 'Creato il',
    colActions: 'Azioni',
    actView: 'Dettagli',
    actEdit: 'Modifica',
    actStats: 'Statistiche',
    actResetPw: 'Reimposta password',
    actDelete: 'Elimina',
    lblName: 'Nome',
    lblEmail: 'E-mail',
    lblPhone: 'Telefono',
    lblCompany: 'Azienda',
    lblNip: 'P.IVA',
    lblStreet: 'Via',
    lblCity: 'Città',
    lblPostalCode: 'CAP',
    lblCountry: 'Paese',
    secPersonal: 'Dati personali',
    secCompany: "Dati dell'azienda",
    secAddress: 'Indirizzo',
    secDates: 'Date',
    lblRegDate: 'Data di registrazione',
    lblLastLogin: 'Ultimo accesso',
    lblLastUpdate: 'Ultimo aggiornamento',
    tabInfo: 'Dati del cliente',
    tabInstallations: 'Impianti',
    tabApiKeys: 'Chiavi API',
    tabCodes: 'Codici di attivazione',
    tabStats: 'Statistiche',
    noInstallations: 'Nessun impianto',
    installationFallback: 'Impianto',
    stringsLabel: 'string(he)',
    lblUpdated: 'Aggiornato',
    lblLatitude: 'Latitudine:',
    lblLongitude: 'Longitudine:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} pannelli, az={{az}}, tilt={{tilt}}, potenza={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nessuna chiave API',
    thKey: 'Chiave',
    thPlan: 'Piano',
    thInstallation: 'Impianto',
    thStatus: 'Stato',
    thExpires: 'Scade',
    thCreated: 'Creato',
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    statusExpired: 'Scaduto',
    noCodes: 'Nessun codice di attivazione utilizzato',
    thCode: 'Codice',
    thUsedAt: 'Usato il',
    thCreatedAt: 'Creato il',
    statTotalRequests: 'Totale richieste',
    statActiveKeys: 'Chiavi API attive',
    statAllKeys: 'Tutte le chiavi',
    statInstallations: 'Impianti',
    chartTitle30d: 'Richieste negli ultimi 30 giorni',
    noRequestData: 'Nessun dato sulle richieste',
    modalClientTitle: 'Cliente: {{name}}',
    loading: 'Caricamento…',
    loadingClientData: 'Caricamento dati del cliente…',
    editTitle: 'Modifica cliente',
    cancel: 'Annulla',
    save: 'Salva',
    statsTitle: 'Statistiche del cliente',
    statsRequestsOverTime: 'Richieste nel tempo',
    deleteTitle: 'Elimina cliente',
    deleteConfirm:
      'Vuoi davvero eliminare il cliente «{{name}}»? Questa operazione eliminerà anche tutte le chiavi API e gli impianti di questo cliente.',
    deleteBtn: 'Elimina',
    resetLinkTitle: 'Link per reimpostare la password',
    resetLinkInfo: 'Trasmetti il link qui sotto al cliente. Il link è valido per 1 ora.',
    copyBtn: 'Copia',
  },
  pt: {
    title: 'Gestão de clientes',
    subtitle: 'Visão geral e gestão de todos os clientes',
    searchPlaceholder: 'Pesquisar por nome ou e-mail…',
    exportBtn: 'Exportar clientes',
    loadFailed: 'Não foi possível carregar a lista de clientes',
    loadingClients: 'A carregar clientes…',
    emptyClients: 'Sem clientes',
    prev: 'Anterior',
    next: 'Seguinte',
    pageOf: 'Página {{cur}} de {{total}}',
    colApiKeys: 'Chaves API',
    colInstallations: 'Instalações',
    colRequests: 'Pedidos',
    colCreatedAt: 'Criado em',
    colActions: 'Ações',
    actView: 'Detalhes',
    actEdit: 'Editar',
    actStats: 'Estatísticas',
    actResetPw: 'Repor palavra-passe',
    actDelete: 'Eliminar',
    lblName: 'Nome',
    lblEmail: 'E-mail',
    lblPhone: 'Telefone',
    lblCompany: 'Empresa',
    lblNip: 'NIF',
    lblStreet: 'Rua',
    lblCity: 'Cidade',
    lblPostalCode: 'Código postal',
    lblCountry: 'País',
    secPersonal: 'Dados pessoais',
    secCompany: 'Dados da empresa',
    secAddress: 'Morada',
    secDates: 'Datas',
    lblRegDate: 'Data de registo',
    lblLastLogin: 'Último início de sessão',
    lblLastUpdate: 'Última atualização',
    tabInfo: 'Dados do cliente',
    tabInstallations: 'Instalações',
    tabApiKeys: 'Chaves API',
    tabCodes: 'Códigos de ativação',
    tabStats: 'Estatísticas',
    noInstallations: 'Sem instalações',
    installationFallback: 'Instalação',
    stringsLabel: 'string(s)',
    lblUpdated: 'Atualizado',
    lblLatitude: 'Latitude:',
    lblLongitude: 'Longitude:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} painéis, az={{az}}, tilt={{tilt}}, potência={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Sem chaves API',
    thKey: 'Chave',
    thPlan: 'Plano',
    thInstallation: 'Instalação',
    thStatus: 'Estado',
    thExpires: 'Expira',
    thCreated: 'Criado',
    statusActive: 'Ativo',
    statusInactive: 'Inativo',
    statusExpired: 'Expirado',
    noCodes: 'Sem códigos de ativação usados',
    thCode: 'Código',
    thUsedAt: 'Usado em',
    thCreatedAt: 'Criado em',
    statTotalRequests: 'Total de pedidos',
    statActiveKeys: 'Chaves API ativas',
    statAllKeys: 'Todas as chaves',
    statInstallations: 'Instalações',
    chartTitle30d: 'Pedidos nos últimos 30 dias',
    noRequestData: 'Sem dados de pedidos',
    modalClientTitle: 'Cliente: {{name}}',
    loading: 'A carregar…',
    loadingClientData: 'A carregar dados do cliente…',
    editTitle: 'Editar cliente',
    cancel: 'Cancelar',
    save: 'Guardar',
    statsTitle: 'Estatísticas do cliente',
    statsRequestsOverTime: 'Pedidos ao longo do tempo',
    deleteTitle: 'Eliminar cliente',
    deleteConfirm:
      'Tem a certeza de que quer eliminar o cliente «{{name}}»? Esta operação eliminará também todas as chaves API e instalações deste cliente.',
    deleteBtn: 'Eliminar',
    resetLinkTitle: 'Link para repor a palavra-passe',
    resetLinkInfo: 'Passe o link abaixo ao cliente. O link é válido durante 1 hora.',
    copyBtn: 'Copiar',
  },
  nl: {
    title: 'Klantbeheer',
    subtitle: 'Overzicht en beheer van alle klanten',
    searchPlaceholder: 'Zoeken op naam of e-mail…',
    exportBtn: 'Klanten exporteren',
    loadFailed: 'Kan klantenlijst niet laden',
    loadingClients: 'Klanten laden…',
    emptyClients: 'Geen klanten',
    prev: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {{cur}} van {{total}}',
    colApiKeys: 'API-sleutels',
    colInstallations: 'Installaties',
    colRequests: 'Verzoeken',
    colCreatedAt: 'Aangemaakt op',
    colActions: 'Acties',
    actView: 'Details',
    actEdit: 'Bewerken',
    actStats: 'Statistieken',
    actResetPw: 'Wachtwoord opnieuw instellen',
    actDelete: 'Verwijderen',
    lblName: 'Naam',
    lblEmail: 'E-mail',
    lblPhone: 'Telefoon',
    lblCompany: 'Bedrijf',
    lblNip: 'Btw-nummer',
    lblStreet: 'Straat',
    lblCity: 'Stad',
    lblPostalCode: 'Postcode',
    lblCountry: 'Land',
    secPersonal: 'Persoonlijke gegevens',
    secCompany: 'Bedrijfsgegevens',
    secAddress: 'Adres',
    secDates: 'Datums',
    lblRegDate: 'Registratiedatum',
    lblLastLogin: 'Laatste aanmelding',
    lblLastUpdate: 'Laatste update',
    tabInfo: 'Klantgegevens',
    tabInstallations: 'Installaties',
    tabApiKeys: 'API-sleutels',
    tabCodes: 'Activeringscodes',
    tabStats: 'Statistieken',
    noInstallations: 'Geen installaties',
    installationFallback: 'Installatie',
    stringsLabel: 'string(s)',
    lblUpdated: 'Bijgewerkt',
    lblLatitude: 'Breedtegraad:',
    lblLongitude: 'Lengtegraad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panelen, az={{az}}, tilt={{tilt}}, vermogen={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Geen API-sleutels',
    thKey: 'Sleutel',
    thPlan: 'Plan',
    thInstallation: 'Installatie',
    thStatus: 'Status',
    thExpires: 'Verloopt',
    thCreated: 'Aangemaakt',
    statusActive: 'Actief',
    statusInactive: 'Inactief',
    statusExpired: 'Verlopen',
    noCodes: 'Geen gebruikte activeringscodes',
    thCode: 'Code',
    thUsedAt: 'Gebruikt op',
    thCreatedAt: 'Aangemaakt op',
    statTotalRequests: 'Totaal verzoeken',
    statActiveKeys: 'Actieve API-sleutels',
    statAllKeys: 'Alle sleutels',
    statInstallations: 'Installaties',
    chartTitle30d: 'Verzoeken in de laatste 30 dagen',
    noRequestData: 'Geen verzoekgegevens',
    modalClientTitle: 'Klant: {{name}}',
    loading: 'Laden…',
    loadingClientData: 'Klantgegevens laden…',
    editTitle: 'Klant bewerken',
    cancel: 'Annuleren',
    save: 'Opslaan',
    statsTitle: 'Klantstatistieken',
    statsRequestsOverTime: 'Verzoeken in de tijd',
    deleteTitle: 'Klant verwijderen',
    deleteConfirm:
      'Weet je zeker dat je klant "{{name}}" wilt verwijderen? Deze bewerking verwijdert ook alle API-sleutels en installaties van deze klant.',
    deleteBtn: 'Verwijderen',
    resetLinkTitle: 'Link voor wachtwoordherstel',
    resetLinkInfo: 'Geef de onderstaande link aan de klant. De link is 1 uur geldig.',
    copyBtn: 'Kopiëren',
  },
  da: {
    title: 'Kundeadministration',
    subtitle: 'Oversigt og administration af alle kunder',
    searchPlaceholder: 'Søg efter navn eller e-mail…',
    exportBtn: 'Eksportér kunder',
    loadFailed: 'Kunne ikke indlæse kundelisten',
    loadingClients: 'Indlæser kunder…',
    emptyClients: 'Ingen kunder',
    prev: 'Forrige',
    next: 'Næste',
    pageOf: 'Side {{cur}} af {{total}}',
    colApiKeys: 'API-nøgler',
    colInstallations: 'Anlæg',
    colRequests: 'Anmodninger',
    colCreatedAt: 'Oprettet den',
    colActions: 'Handlinger',
    actView: 'Detaljer',
    actEdit: 'Rediger',
    actStats: 'Statistik',
    actResetPw: 'Nulstil adgangskode',
    actDelete: 'Slet',
    lblName: 'Navn',
    lblEmail: 'E-mail',
    lblPhone: 'Telefon',
    lblCompany: 'Firma',
    lblNip: 'CVR-nr.',
    lblStreet: 'Gade',
    lblCity: 'By',
    lblPostalCode: 'Postnummer',
    lblCountry: 'Land',
    secPersonal: 'Personlige data',
    secCompany: 'Firmadata',
    secAddress: 'Adresse',
    secDates: 'Datoer',
    lblRegDate: 'Registreringsdato',
    lblLastLogin: 'Seneste login',
    lblLastUpdate: 'Seneste opdatering',
    tabInfo: 'Kundedata',
    tabInstallations: 'Anlæg',
    tabApiKeys: 'API-nøgler',
    tabCodes: 'Aktiveringskoder',
    tabStats: 'Statistik',
    noInstallations: 'Ingen anlæg',
    installationFallback: 'Anlæg',
    stringsLabel: 'string(s)',
    lblUpdated: 'Opdateret',
    lblLatitude: 'Breddegrad:',
    lblLongitude: 'Længdegrad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneler, az={{az}}, tilt={{tilt}}, effekt={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Ingen API-nøgler',
    thKey: 'Nøgle',
    thPlan: 'Plan',
    thInstallation: 'Anlæg',
    thStatus: 'Status',
    thExpires: 'Udløber',
    thCreated: 'Oprettet',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    statusExpired: 'Udløbet',
    noCodes: 'Ingen brugte aktiveringskoder',
    thCode: 'Kode',
    thUsedAt: 'Brugt den',
    thCreatedAt: 'Oprettet den',
    statTotalRequests: 'Anmodninger i alt',
    statActiveKeys: 'Aktive API-nøgler',
    statAllKeys: 'Alle nøgler',
    statInstallations: 'Anlæg',
    chartTitle30d: 'Anmodninger i de seneste 30 dage',
    noRequestData: 'Ingen anmodningsdata',
    modalClientTitle: 'Kunde: {{name}}',
    loading: 'Indlæser…',
    loadingClientData: 'Indlæser kundedata…',
    editTitle: 'Rediger kunde',
    cancel: 'Annuller',
    save: 'Gem',
    statsTitle: 'Kundestatistik',
    statsRequestsOverTime: 'Anmodninger over tid',
    deleteTitle: 'Slet kunde',
    deleteConfirm:
      'Er du sikker på, at du vil slette kunden "{{name}}"? Denne handling sletter også alle API-nøgler og anlæg for denne kunde.',
    deleteBtn: 'Slet',
    resetLinkTitle: 'Link til nulstilling af adgangskode',
    resetLinkInfo: 'Giv linket nedenfor til kunden. Linket er gyldigt i 1 time.',
    copyBtn: 'Kopiér',
  },
  sv: {
    title: 'Kundhantering',
    subtitle: 'Översikt och hantering av alla kunder',
    searchPlaceholder: 'Sök efter namn eller e-post…',
    exportBtn: 'Exportera kunder',
    loadFailed: 'Det gick inte att läsa in kundlistan',
    loadingClients: 'Läser in kunder…',
    emptyClients: 'Inga kunder',
    prev: 'Föregående',
    next: 'Nästa',
    pageOf: 'Sida {{cur}} av {{total}}',
    colApiKeys: 'API-nycklar',
    colInstallations: 'Anläggningar',
    colRequests: 'Förfrågningar',
    colCreatedAt: 'Skapad den',
    colActions: 'Åtgärder',
    actView: 'Detaljer',
    actEdit: 'Redigera',
    actStats: 'Statistik',
    actResetPw: 'Återställ lösenord',
    actDelete: 'Ta bort',
    lblName: 'Namn',
    lblEmail: 'E-post',
    lblPhone: 'Telefon',
    lblCompany: 'Företag',
    lblNip: 'Org.nr',
    lblStreet: 'Gata',
    lblCity: 'Stad',
    lblPostalCode: 'Postnummer',
    lblCountry: 'Land',
    secPersonal: 'Personuppgifter',
    secCompany: 'Företagsuppgifter',
    secAddress: 'Adress',
    secDates: 'Datum',
    lblRegDate: 'Registreringsdatum',
    lblLastLogin: 'Senaste inloggning',
    lblLastUpdate: 'Senaste uppdatering',
    tabInfo: 'Kunduppgifter',
    tabInstallations: 'Anläggningar',
    tabApiKeys: 'API-nycklar',
    tabCodes: 'Aktiveringskoder',
    tabStats: 'Statistik',
    noInstallations: 'Inga anläggningar',
    installationFallback: 'Anläggning',
    stringsLabel: 'string(s)',
    lblUpdated: 'Uppdaterad',
    lblLatitude: 'Latitud:',
    lblLongitude: 'Longitud:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneler, az={{az}}, tilt={{tilt}}, effekt={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Inga API-nycklar',
    thKey: 'Nyckel',
    thPlan: 'Plan',
    thInstallation: 'Anläggning',
    thStatus: 'Status',
    thExpires: 'Upphör',
    thCreated: 'Skapad',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    statusExpired: 'Utgången',
    noCodes: 'Inga använda aktiveringskoder',
    thCode: 'Kod',
    thUsedAt: 'Använd den',
    thCreatedAt: 'Skapad den',
    statTotalRequests: 'Totalt antal förfrågningar',
    statActiveKeys: 'Aktiva API-nycklar',
    statAllKeys: 'Alla nycklar',
    statInstallations: 'Anläggningar',
    chartTitle30d: 'Förfrågningar de senaste 30 dagarna',
    noRequestData: 'Inga förfrågningsdata',
    modalClientTitle: 'Kund: {{name}}',
    loading: 'Läser in…',
    loadingClientData: 'Läser in kunddata…',
    editTitle: 'Redigera kund',
    cancel: 'Avbryt',
    save: 'Spara',
    statsTitle: 'Kundstatistik',
    statsRequestsOverTime: 'Förfrågningar över tid',
    deleteTitle: 'Ta bort kund',
    deleteConfirm:
      'Är du säker på att du vill ta bort kunden "{{name}}"? Denna åtgärd tar även bort alla API-nycklar och anläggningar för denna kund.',
    deleteBtn: 'Ta bort',
    resetLinkTitle: 'Länk för återställning av lösenord',
    resetLinkInfo: 'Ge länken nedan till kunden. Länken är giltig i 1 timme.',
    copyBtn: 'Kopiera',
  },
  fi: {
    title: 'Asiakkaiden hallinta',
    subtitle: 'Kaikkien asiakkaiden yleiskatsaus ja hallinta',
    searchPlaceholder: 'Hae nimellä tai sähköpostilla…',
    exportBtn: 'Vie asiakkaat',
    loadFailed: 'Asiakaslistan lataaminen epäonnistui',
    loadingClients: 'Ladataan asiakkaita…',
    emptyClients: 'Ei asiakkaita',
    prev: 'Edellinen',
    next: 'Seuraava',
    pageOf: 'Sivu {{cur}} / {{total}}',
    colApiKeys: 'API-avaimet',
    colInstallations: 'Laitokset',
    colRequests: 'Pyynnöt',
    colCreatedAt: 'Luotu',
    colActions: 'Toiminnot',
    actView: 'Tiedot',
    actEdit: 'Muokkaa',
    actStats: 'Tilastot',
    actResetPw: 'Nollaa salasana',
    actDelete: 'Poista',
    lblName: 'Nimi',
    lblEmail: 'Sähköposti',
    lblPhone: 'Puhelin',
    lblCompany: 'Yritys',
    lblNip: 'Y-tunnus',
    lblStreet: 'Katu',
    lblCity: 'Kaupunki',
    lblPostalCode: 'Postinumero',
    lblCountry: 'Maa',
    secPersonal: 'Henkilötiedot',
    secCompany: 'Yritystiedot',
    secAddress: 'Osoite',
    secDates: 'Päivämäärät',
    lblRegDate: 'Rekisteröitymispäivä',
    lblLastLogin: 'Viimeisin kirjautuminen',
    lblLastUpdate: 'Viimeisin päivitys',
    tabInfo: 'Asiakkaan tiedot',
    tabInstallations: 'Laitokset',
    tabApiKeys: 'API-avaimet',
    tabCodes: 'Aktivointikoodit',
    tabStats: 'Tilastot',
    noInstallations: 'Ei laitoksia',
    installationFallback: 'Laitos',
    stringsLabel: 'string(iä)',
    lblUpdated: 'Päivitetty',
    lblLatitude: 'Leveysaste:',
    lblLongitude: 'Pituusaste:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneelia, az={{az}}, tilt={{tilt}}, teho={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Ei API-avaimia',
    thKey: 'Avain',
    thPlan: 'Suunnitelma',
    thInstallation: 'Laitos',
    thStatus: 'Tila',
    thExpires: 'Vanhenee',
    thCreated: 'Luotu',
    statusActive: 'Aktiivinen',
    statusInactive: 'Ei-aktiivinen',
    statusExpired: 'Vanhentunut',
    noCodes: 'Ei käytettyjä aktivointikoodeja',
    thCode: 'Koodi',
    thUsedAt: 'Käytetty',
    thCreatedAt: 'Luotu',
    statTotalRequests: 'Pyyntöjä yhteensä',
    statActiveKeys: 'Aktiiviset API-avaimet',
    statAllKeys: 'Kaikki avaimet',
    statInstallations: 'Laitokset',
    chartTitle30d: 'Pyynnöt viimeisten 30 päivän aikana',
    noRequestData: 'Ei pyyntötietoja',
    modalClientTitle: 'Asiakas: {{name}}',
    loading: 'Ladataan…',
    loadingClientData: 'Ladataan asiakkaan tietoja…',
    editTitle: 'Muokkaa asiakasta',
    cancel: 'Peruuta',
    save: 'Tallenna',
    statsTitle: 'Asiakkaan tilastot',
    statsRequestsOverTime: 'Pyynnöt ajan myötä',
    deleteTitle: 'Poista asiakas',
    deleteConfirm:
      'Haluatko varmasti poistaa asiakkaan "{{name}}"? Tämä toiminto poistaa myös kaikki tämän asiakkaan API-avaimet ja laitokset.',
    deleteBtn: 'Poista',
    resetLinkTitle: 'Salasanan nollauslinkki',
    resetLinkInfo: 'Välitä alla oleva linkki asiakkaalle. Linkki on voimassa 1 tunnin.',
    copyBtn: 'Kopioi',
  },
  cs: {
    title: 'Správa klientů',
    subtitle: 'Přehled a správa všech klientů',
    searchPlaceholder: 'Hledat podle názvu nebo e-mailu…',
    exportBtn: 'Exportovat klienty',
    loadFailed: 'Nepodařilo se načíst seznam klientů',
    loadingClients: 'Načítání klientů…',
    emptyClients: 'Žádní klienti',
    prev: 'Předchozí',
    next: 'Další',
    pageOf: 'Stránka {{cur}} z {{total}}',
    colApiKeys: 'API klíče',
    colInstallations: 'Instalace',
    colRequests: 'Požadavky',
    colCreatedAt: 'Vytvořeno dne',
    colActions: 'Akce',
    actView: 'Podrobnosti',
    actEdit: 'Upravit',
    actStats: 'Statistiky',
    actResetPw: 'Resetovat heslo',
    actDelete: 'Smazat',
    lblName: 'Název',
    lblEmail: 'E-mail',
    lblPhone: 'Telefon',
    lblCompany: 'Společnost',
    lblNip: 'DIČ',
    lblStreet: 'Ulice',
    lblCity: 'Město',
    lblPostalCode: 'PSČ',
    lblCountry: 'Země',
    secPersonal: 'Osobní údaje',
    secCompany: 'Údaje o společnosti',
    secAddress: 'Adresa',
    secDates: 'Data',
    lblRegDate: 'Datum registrace',
    lblLastLogin: 'Poslední přihlášení',
    lblLastUpdate: 'Poslední aktualizace',
    tabInfo: 'Údaje klienta',
    tabInstallations: 'Instalace',
    tabApiKeys: 'API klíče',
    tabCodes: 'Aktivační kódy',
    tabStats: 'Statistiky',
    noInstallations: 'Žádné instalace',
    installationFallback: 'Instalace',
    stringsLabel: 'stringů',
    lblUpdated: 'Aktualizováno',
    lblLatitude: 'Zeměpisná šířka:',
    lblLongitude: 'Zeměpisná délka:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panelů, az={{az}}, tilt={{tilt}}, výkon={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Žádné API klíče',
    thKey: 'Klíč',
    thPlan: 'Plán',
    thInstallation: 'Instalace',
    thStatus: 'Stav',
    thExpires: 'Vyprší',
    thCreated: 'Vytvořeno',
    statusActive: 'Aktivní',
    statusInactive: 'Neaktivní',
    statusExpired: 'Vypršelo',
    noCodes: 'Žádné použité aktivační kódy',
    thCode: 'Kód',
    thUsedAt: 'Použito dne',
    thCreatedAt: 'Vytvořeno dne',
    statTotalRequests: 'Celkem požadavků',
    statActiveKeys: 'Aktivní API klíče',
    statAllKeys: 'Všechny klíče',
    statInstallations: 'Instalace',
    chartTitle30d: 'Požadavky za posledních 30 dní',
    noRequestData: 'Žádná data o požadavcích',
    modalClientTitle: 'Klient: {{name}}',
    loading: 'Načítání…',
    loadingClientData: 'Načítání údajů klienta…',
    editTitle: 'Upravit klienta',
    cancel: 'Zrušit',
    save: 'Uložit',
    statsTitle: 'Statistiky klienta',
    statsRequestsOverTime: 'Požadavky v čase',
    deleteTitle: 'Smazat klienta',
    deleteConfirm:
      'Opravdu chcete smazat klienta „{{name}}“? Tato operace také smaže všechny API klíče a instalace tohoto klienta.',
    deleteBtn: 'Smazat',
    resetLinkTitle: 'Odkaz pro reset hesla',
    resetLinkInfo: 'Předejte níže uvedený odkaz klientovi. Odkaz je platný 1 hodinu.',
    copyBtn: 'Kopírovat',
  },
  sk: {
    title: 'Správa klientov',
    subtitle: 'Prehľad a správa všetkých klientov',
    searchPlaceholder: 'Hľadať podľa názvu alebo e-mailu…',
    exportBtn: 'Exportovať klientov',
    loadFailed: 'Nepodarilo sa načítať zoznam klientov',
    loadingClients: 'Načítavajú sa klienti…',
    emptyClients: 'Žiadni klienti',
    prev: 'Predchádzajúca',
    next: 'Ďalšia',
    pageOf: 'Stránka {{cur}} z {{total}}',
    colApiKeys: 'API kľúče',
    colInstallations: 'Inštalácie',
    colRequests: 'Požiadavky',
    colCreatedAt: 'Vytvorené dňa',
    colActions: 'Akcie',
    actView: 'Podrobnosti',
    actEdit: 'Upraviť',
    actStats: 'Štatistiky',
    actResetPw: 'Resetovať heslo',
    actDelete: 'Zmazať',
    lblName: 'Názov',
    lblEmail: 'E-mail',
    lblPhone: 'Telefón',
    lblCompany: 'Spoločnosť',
    lblNip: 'IČ DPH',
    lblStreet: 'Ulica',
    lblCity: 'Mesto',
    lblPostalCode: 'PSČ',
    lblCountry: 'Krajina',
    secPersonal: 'Osobné údaje',
    secCompany: 'Údaje o spoločnosti',
    secAddress: 'Adresa',
    secDates: 'Dátumy',
    lblRegDate: 'Dátum registrácie',
    lblLastLogin: 'Posledné prihlásenie',
    lblLastUpdate: 'Posledná aktualizácia',
    tabInfo: 'Údaje klienta',
    tabInstallations: 'Inštalácie',
    tabApiKeys: 'API kľúče',
    tabCodes: 'Aktivačné kódy',
    tabStats: 'Štatistiky',
    noInstallations: 'Žiadne inštalácie',
    installationFallback: 'Inštalácia',
    stringsLabel: 'stringov',
    lblUpdated: 'Aktualizované',
    lblLatitude: 'Zemepisná šírka:',
    lblLongitude: 'Zemepisná dĺžka:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panelov, az={{az}}, tilt={{tilt}}, výkon={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Žiadne API kľúče',
    thKey: 'Kľúč',
    thPlan: 'Plán',
    thInstallation: 'Inštalácia',
    thStatus: 'Stav',
    thExpires: 'Vyprší',
    thCreated: 'Vytvorené',
    statusActive: 'Aktívny',
    statusInactive: 'Neaktívny',
    statusExpired: 'Vypršané',
    noCodes: 'Žiadne použité aktivačné kódy',
    thCode: 'Kód',
    thUsedAt: 'Použité dňa',
    thCreatedAt: 'Vytvorené dňa',
    statTotalRequests: 'Celkom požiadaviek',
    statActiveKeys: 'Aktívne API kľúče',
    statAllKeys: 'Všetky kľúče',
    statInstallations: 'Inštalácie',
    chartTitle30d: 'Požiadavky za posledných 30 dní',
    noRequestData: 'Žiadne údaje o požiadavkách',
    modalClientTitle: 'Klient: {{name}}',
    loading: 'Načítava sa…',
    loadingClientData: 'Načítavajú sa údaje klienta…',
    editTitle: 'Upraviť klienta',
    cancel: 'Zrušiť',
    save: 'Uložiť',
    statsTitle: 'Štatistiky klienta',
    statsRequestsOverTime: 'Požiadavky v čase',
    deleteTitle: 'Zmazať klienta',
    deleteConfirm:
      'Naozaj chcete zmazať klienta „{{name}}“? Táto operácia tiež zmaže všetky API kľúče a inštalácie tohto klienta.',
    deleteBtn: 'Zmazať',
    resetLinkTitle: 'Odkaz na reset hesla',
    resetLinkInfo: 'Odovzdajte nižšie uvedený odkaz klientovi. Odkaz je platný 1 hodinu.',
    copyBtn: 'Kopírovať',
  },
  sl: {
    title: 'Upravljanje strank',
    subtitle: 'Pregled in upravljanje vseh strank',
    searchPlaceholder: 'Iskanje po imenu ali e-pošti…',
    exportBtn: 'Izvozi stranke',
    loadFailed: 'Seznama strank ni bilo mogoče naložiti',
    loadingClients: 'Nalaganje strank…',
    emptyClients: 'Ni strank',
    prev: 'Prejšnja',
    next: 'Naslednja',
    pageOf: 'Stran {{cur}} od {{total}}',
    colApiKeys: 'Ključi API',
    colInstallations: 'Naprave',
    colRequests: 'Zahteve',
    colCreatedAt: 'Ustvarjeno dne',
    colActions: 'Dejanja',
    actView: 'Podrobnosti',
    actEdit: 'Uredi',
    actStats: 'Statistika',
    actResetPw: 'Ponastavi geslo',
    actDelete: 'Izbriši',
    lblName: 'Ime',
    lblEmail: 'E-pošta',
    lblPhone: 'Telefon',
    lblCompany: 'Podjetje',
    lblNip: 'Davčna št.',
    lblStreet: 'Ulica',
    lblCity: 'Mesto',
    lblPostalCode: 'Poštna številka',
    lblCountry: 'Država',
    secPersonal: 'Osebni podatki',
    secCompany: 'Podatki o podjetju',
    secAddress: 'Naslov',
    secDates: 'Datumi',
    lblRegDate: 'Datum registracije',
    lblLastLogin: 'Zadnja prijava',
    lblLastUpdate: 'Zadnja posodobitev',
    tabInfo: 'Podatki stranke',
    tabInstallations: 'Naprave',
    tabApiKeys: 'Ključi API',
    tabCodes: 'Aktivacijske kode',
    tabStats: 'Statistika',
    noInstallations: 'Ni naprav',
    installationFallback: 'Naprava',
    stringsLabel: 'nizov',
    lblUpdated: 'Posodobljeno',
    lblLatitude: 'Zemljepisna širina:',
    lblLongitude: 'Zemljepisna dolžina:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panelov, az={{az}}, tilt={{tilt}}, moč={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Ni ključev API',
    thKey: 'Ključ',
    thPlan: 'Načrt',
    thInstallation: 'Naprava',
    thStatus: 'Stanje',
    thExpires: 'Poteče',
    thCreated: 'Ustvarjeno',
    statusActive: 'Aktiven',
    statusInactive: 'Neaktiven',
    statusExpired: 'Poteklo',
    noCodes: 'Ni uporabljenih aktivacijskih kod',
    thCode: 'Koda',
    thUsedAt: 'Uporabljeno dne',
    thCreatedAt: 'Ustvarjeno dne',
    statTotalRequests: 'Skupaj zahtev',
    statActiveKeys: 'Aktivni ključi API',
    statAllKeys: 'Vsi ključi',
    statInstallations: 'Naprave',
    chartTitle30d: 'Zahteve v zadnjih 30 dneh',
    noRequestData: 'Ni podatkov o zahtevah',
    modalClientTitle: 'Stranka: {{name}}',
    loading: 'Nalaganje…',
    loadingClientData: 'Nalaganje podatkov stranke…',
    editTitle: 'Uredi stranko',
    cancel: 'Prekliči',
    save: 'Shrani',
    statsTitle: 'Statistika stranke',
    statsRequestsOverTime: 'Zahteve skozi čas',
    deleteTitle: 'Izbriši stranko',
    deleteConfirm:
      'Ali ste prepričani, da želite izbrisati stranko »{{name}}«? Ta operacija bo izbrisala tudi vse ključe API in naprave te stranke.',
    deleteBtn: 'Izbriši',
    resetLinkTitle: 'Povezava za ponastavitev gesla',
    resetLinkInfo: 'Posredujte spodnjo povezavo stranki. Povezava je veljavna 1 uro.',
    copyBtn: 'Kopiraj',
  },
  hr: {
    title: 'Upravljanje klijentima',
    subtitle: 'Pregled i upravljanje svim klijentima',
    searchPlaceholder: 'Traži po nazivu ili e-pošti…',
    exportBtn: 'Izvezi klijente',
    loadFailed: 'Nije moguće učitati popis klijenata',
    loadingClients: 'Učitavanje klijenata…',
    emptyClients: 'Nema klijenata',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    colApiKeys: 'API ključevi',
    colInstallations: 'Postrojenja',
    colRequests: 'Zahtjevi',
    colCreatedAt: 'Stvoreno dana',
    colActions: 'Radnje',
    actView: 'Detalji',
    actEdit: 'Uredi',
    actStats: 'Statistika',
    actResetPw: 'Resetiraj lozinku',
    actDelete: 'Obriši',
    lblName: 'Naziv',
    lblEmail: 'E-pošta',
    lblPhone: 'Telefon',
    lblCompany: 'Tvrtka',
    lblNip: 'OIB',
    lblStreet: 'Ulica',
    lblCity: 'Grad',
    lblPostalCode: 'Poštanski broj',
    lblCountry: 'Država',
    secPersonal: 'Osobni podaci',
    secCompany: 'Podaci o tvrtki',
    secAddress: 'Adresa',
    secDates: 'Datumi',
    lblRegDate: 'Datum registracije',
    lblLastLogin: 'Zadnja prijava',
    lblLastUpdate: 'Zadnja izmjena',
    tabInfo: 'Podaci klijenta',
    tabInstallations: 'Postrojenja',
    tabApiKeys: 'API ključevi',
    tabCodes: 'Aktivacijski kodovi',
    tabStats: 'Statistika',
    noInstallations: 'Nema postrojenja',
    installationFallback: 'Postrojenje',
    stringsLabel: 'nizova',
    lblUpdated: 'Ažurirano',
    lblLatitude: 'Zemljopisna širina:',
    lblLongitude: 'Zemljopisna dužina:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panela, az={{az}}, tilt={{tilt}}, snaga={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nema API ključeva',
    thKey: 'Ključ',
    thPlan: 'Plan',
    thInstallation: 'Postrojenje',
    thStatus: 'Status',
    thExpires: 'Istječe',
    thCreated: 'Stvoreno',
    statusActive: 'Aktivan',
    statusInactive: 'Neaktivan',
    statusExpired: 'Isteklo',
    noCodes: 'Nema iskorištenih aktivacijskih kodova',
    thCode: 'Kod',
    thUsedAt: 'Iskorišteno dana',
    thCreatedAt: 'Stvoreno dana',
    statTotalRequests: 'Ukupno zahtjeva',
    statActiveKeys: 'Aktivni API ključevi',
    statAllKeys: 'Svi ključevi',
    statInstallations: 'Postrojenja',
    chartTitle30d: 'Zahtjevi u zadnjih 30 dana',
    noRequestData: 'Nema podataka o zahtjevima',
    modalClientTitle: 'Klijent: {{name}}',
    loading: 'Učitavanje…',
    loadingClientData: 'Učitavanje podataka klijenta…',
    editTitle: 'Uredi klijenta',
    cancel: 'Odustani',
    save: 'Spremi',
    statsTitle: 'Statistika klijenta',
    statsRequestsOverTime: 'Zahtjevi tijekom vremena',
    deleteTitle: 'Obriši klijenta',
    deleteConfirm:
      'Jeste li sigurni da želite obrisati klijenta „{{name}}“? Ova operacija će također obrisati sve API ključeve i postrojenja ovog klijenta.',
    deleteBtn: 'Obriši',
    resetLinkTitle: 'Poveznica za resetiranje lozinke',
    resetLinkInfo: 'Proslijedite poveznicu ispod klijentu. Poveznica vrijedi 1 sat.',
    copyBtn: 'Kopiraj',
  },
  hu: {
    title: 'Ügyfélkezelés',
    subtitle: 'Az összes ügyfél áttekintése és kezelése',
    searchPlaceholder: 'Keresés név vagy e-mail alapján…',
    exportBtn: 'Ügyfelek exportálása',
    loadFailed: 'Nem sikerült betölteni az ügyféllistát',
    loadingClients: 'Ügyfelek betöltése…',
    emptyClients: 'Nincsenek ügyfelek',
    prev: 'Előző',
    next: 'Következő',
    pageOf: '{{cur}} / {{total}} oldal',
    colApiKeys: 'API-kulcsok',
    colInstallations: 'Létesítmények',
    colRequests: 'Kérések',
    colCreatedAt: 'Létrehozva',
    colActions: 'Műveletek',
    actView: 'Részletek',
    actEdit: 'Szerkesztés',
    actStats: 'Statisztikák',
    actResetPw: 'Jelszó visszaállítása',
    actDelete: 'Törlés',
    lblName: 'Név',
    lblEmail: 'E-mail',
    lblPhone: 'Telefon',
    lblCompany: 'Cég',
    lblNip: 'Adószám',
    lblStreet: 'Utca',
    lblCity: 'Város',
    lblPostalCode: 'Irányítószám',
    lblCountry: 'Ország',
    secPersonal: 'Személyes adatok',
    secCompany: 'Céges adatok',
    secAddress: 'Cím',
    secDates: 'Dátumok',
    lblRegDate: 'Regisztráció dátuma',
    lblLastLogin: 'Utolsó bejelentkezés',
    lblLastUpdate: 'Utolsó frissítés',
    tabInfo: 'Ügyfél adatai',
    tabInstallations: 'Létesítmények',
    tabApiKeys: 'API-kulcsok',
    tabCodes: 'Aktiváló kódok',
    tabStats: 'Statisztikák',
    noInstallations: 'Nincsenek létesítmények',
    installationFallback: 'Létesítmény',
    stringsLabel: 'string',
    lblUpdated: 'Frissítve',
    lblLatitude: 'Szélesség:',
    lblLongitude: 'Hosszúság:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail:
      '{{pc}} panel, az={{az}}, tilt={{tilt}}, teljesítmény={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nincsenek API-kulcsok',
    thKey: 'Kulcs',
    thPlan: 'Csomag',
    thInstallation: 'Létesítmény',
    thStatus: 'Állapot',
    thExpires: 'Lejár',
    thCreated: 'Létrehozva',
    statusActive: 'Aktív',
    statusInactive: 'Inaktív',
    statusExpired: 'Lejárt',
    noCodes: 'Nincsenek felhasznált aktiváló kódok',
    thCode: 'Kód',
    thUsedAt: 'Felhasználva',
    thCreatedAt: 'Létrehozva',
    statTotalRequests: 'Összes kérés',
    statActiveKeys: 'Aktív API-kulcsok',
    statAllKeys: 'Összes kulcs',
    statInstallations: 'Létesítmények',
    chartTitle30d: 'Kérések az elmúlt 30 napban',
    noRequestData: 'Nincsenek kérési adatok',
    modalClientTitle: 'Ügyfél: {{name}}',
    loading: 'Betöltés…',
    loadingClientData: 'Ügyféladatok betöltése…',
    editTitle: 'Ügyfél szerkesztése',
    cancel: 'Mégse',
    save: 'Mentés',
    statsTitle: 'Ügyfél statisztikái',
    statsRequestsOverTime: 'Kérések az idő függvényében',
    deleteTitle: 'Ügyfél törlése',
    deleteConfirm:
      'Biztosan törölni szeretné a(z) „{{name}}” ügyfelet? Ez a művelet törli az ügyfél összes API-kulcsát és létesítményét is.',
    deleteBtn: 'Törlés',
    resetLinkTitle: 'Jelszó-visszaállítási link',
    resetLinkInfo: 'Adja át az alábbi linket az ügyfélnek. A link 1 órán át érvényes.',
    copyBtn: 'Másolás',
  },
  ro: {
    title: 'Gestionarea clienților',
    subtitle: 'Prezentare generală și gestionarea tuturor clienților',
    searchPlaceholder: 'Căutați după nume sau e-mail…',
    exportBtn: 'Exportă clienții',
    loadFailed: 'Nu s-a putut încărca lista de clienți',
    loadingClients: 'Se încarcă clienții…',
    emptyClients: 'Niciun client',
    prev: 'Anterioară',
    next: 'Următoarea',
    pageOf: 'Pagina {{cur}} din {{total}}',
    colApiKeys: 'Chei API',
    colInstallations: 'Instalații',
    colRequests: 'Cereri',
    colCreatedAt: 'Creat la',
    colActions: 'Acțiuni',
    actView: 'Detalii',
    actEdit: 'Editează',
    actStats: 'Statistici',
    actResetPw: 'Resetează parola',
    actDelete: 'Șterge',
    lblName: 'Nume',
    lblEmail: 'E-mail',
    lblPhone: 'Telefon',
    lblCompany: 'Companie',
    lblNip: 'CUI',
    lblStreet: 'Stradă',
    lblCity: 'Oraș',
    lblPostalCode: 'Cod poștal',
    lblCountry: 'Țară',
    secPersonal: 'Date personale',
    secCompany: 'Date despre companie',
    secAddress: 'Adresă',
    secDates: 'Date',
    lblRegDate: 'Data înregistrării',
    lblLastLogin: 'Ultima autentificare',
    lblLastUpdate: 'Ultima actualizare',
    tabInfo: 'Datele clientului',
    tabInstallations: 'Instalații',
    tabApiKeys: 'Chei API',
    tabCodes: 'Coduri de activare',
    tabStats: 'Statistici',
    noInstallations: 'Nicio instalație',
    installationFallback: 'Instalație',
    stringsLabel: 'stringuri',
    lblUpdated: 'Actualizat',
    lblLatitude: 'Latitudine:',
    lblLongitude: 'Longitudine:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panouri, az={{az}}, tilt={{tilt}}, putere={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nicio cheie API',
    thKey: 'Cheie',
    thPlan: 'Plan',
    thInstallation: 'Instalație',
    thStatus: 'Stare',
    thExpires: 'Expiră',
    thCreated: 'Creat',
    statusActive: 'Activ',
    statusInactive: 'Inactiv',
    statusExpired: 'Expirat',
    noCodes: 'Niciun cod de activare folosit',
    thCode: 'Cod',
    thUsedAt: 'Folosit la',
    thCreatedAt: 'Creat la',
    statTotalRequests: 'Total cereri',
    statActiveKeys: 'Chei API active',
    statAllKeys: 'Toate cheile',
    statInstallations: 'Instalații',
    chartTitle30d: 'Cereri în ultimele 30 de zile',
    noRequestData: 'Nicio dată despre cereri',
    modalClientTitle: 'Client: {{name}}',
    loading: 'Se încarcă…',
    loadingClientData: 'Se încarcă datele clientului…',
    editTitle: 'Editează clientul',
    cancel: 'Anulează',
    save: 'Salvează',
    statsTitle: 'Statisticile clientului',
    statsRequestsOverTime: 'Cereri în timp',
    deleteTitle: 'Șterge clientul',
    deleteConfirm:
      'Sigur doriți să ștergeți clientul „{{name}}”? Această operație va șterge și toate cheile API și instalațiile acestui client.',
    deleteBtn: 'Șterge',
    resetLinkTitle: 'Link de resetare a parolei',
    resetLinkInfo: 'Transmiteți linkul de mai jos clientului. Linkul este valabil 1 oră.',
    copyBtn: 'Copiază',
  },
  bg: {
    title: 'Управление на клиенти',
    subtitle: 'Преглед и управление на всички клиенти',
    searchPlaceholder: 'Търсене по име или имейл…',
    exportBtn: 'Експортиране на клиенти',
    loadFailed: 'Неуспешно зареждане на списъка с клиенти',
    loadingClients: 'Зареждане на клиенти…',
    emptyClients: 'Няма клиенти',
    prev: 'Предишна',
    next: 'Следваща',
    pageOf: 'Страница {{cur}} от {{total}}',
    colApiKeys: 'API ключове',
    colInstallations: 'Инсталации',
    colRequests: 'Заявки',
    colCreatedAt: 'Създаден на',
    colActions: 'Действия',
    actView: 'Детайли',
    actEdit: 'Редактиране',
    actStats: 'Статистика',
    actResetPw: 'Нулиране на парола',
    actDelete: 'Изтриване',
    lblName: 'Име',
    lblEmail: 'Имейл',
    lblPhone: 'Телефон',
    lblCompany: 'Фирма',
    lblNip: 'ЕИК/ДДС №',
    lblStreet: 'Улица',
    lblCity: 'Град',
    lblPostalCode: 'Пощенски код',
    lblCountry: 'Държава',
    secPersonal: 'Лични данни',
    secCompany: 'Данни на фирмата',
    secAddress: 'Адрес',
    secDates: 'Дати',
    lblRegDate: 'Дата на регистрация',
    lblLastLogin: 'Последен вход',
    lblLastUpdate: 'Последна актуализация',
    tabInfo: 'Данни на клиента',
    tabInstallations: 'Инсталации',
    tabApiKeys: 'API ключове',
    tabCodes: 'Кодове за активиране',
    tabStats: 'Статистика',
    noInstallations: 'Няма инсталации',
    installationFallback: 'Инсталация',
    stringsLabel: 'стринга',
    lblUpdated: 'Актуализирано',
    lblLatitude: 'Географска ширина:',
    lblLongitude: 'Географска дължина:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} панела, az={{az}}, tilt={{tilt}}, мощност={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Няма API ключове',
    thKey: 'Ключ',
    thPlan: 'План',
    thInstallation: 'Инсталация',
    thStatus: 'Статус',
    thExpires: 'Изтича',
    thCreated: 'Създаден',
    statusActive: 'Активен',
    statusInactive: 'Неактивен',
    statusExpired: 'Изтекъл',
    noCodes: 'Няма използвани кодове за активиране',
    thCode: 'Код',
    thUsedAt: 'Използван на',
    thCreatedAt: 'Създаден на',
    statTotalRequests: 'Общо заявки',
    statActiveKeys: 'Активни API ключове',
    statAllKeys: 'Всички ключове',
    statInstallations: 'Инсталации',
    chartTitle30d: 'Заявки през последните 30 дни',
    noRequestData: 'Няма данни за заявки',
    modalClientTitle: 'Клиент: {{name}}',
    loading: 'Зареждане…',
    loadingClientData: 'Зареждане на данни на клиента…',
    editTitle: 'Редактиране на клиент',
    cancel: 'Отказ',
    save: 'Запази',
    statsTitle: 'Статистика на клиента',
    statsRequestsOverTime: 'Заявки във времето',
    deleteTitle: 'Изтриване на клиент',
    deleteConfirm:
      'Сигурни ли сте, че искате да изтриете клиента „{{name}}“? Тази операция ще изтрие също всички API ключове и инсталации на този клиент.',
    deleteBtn: 'Изтрий',
    resetLinkTitle: 'Връзка за нулиране на парола',
    resetLinkInfo: 'Предайте връзката по-долу на клиента. Връзката е валидна 1 час.',
    copyBtn: 'Копирай',
  },
  el: {
    title: 'Διαχείριση πελατών',
    subtitle: 'Επισκόπηση και διαχείριση όλων των πελατών',
    searchPlaceholder: 'Αναζήτηση κατά όνομα ή e-mail…',
    exportBtn: 'Εξαγωγή πελατών',
    loadFailed: 'Αποτυχία φόρτωσης της λίστας πελατών',
    loadingClients: 'Φόρτωση πελατών…',
    emptyClients: 'Δεν υπάρχουν πελάτες',
    prev: 'Προηγούμενη',
    next: 'Επόμενη',
    pageOf: 'Σελίδα {{cur}} από {{total}}',
    colApiKeys: 'Κλειδιά API',
    colInstallations: 'Εγκαταστάσεις',
    colRequests: 'Αιτήματα',
    colCreatedAt: 'Δημιουργήθηκε στις',
    colActions: 'Ενέργειες',
    actView: 'Λεπτομέρειες',
    actEdit: 'Επεξεργασία',
    actStats: 'Στατιστικά',
    actResetPw: 'Επαναφορά κωδικού',
    actDelete: 'Διαγραφή',
    lblName: 'Όνομα',
    lblEmail: 'E-mail',
    lblPhone: 'Τηλέφωνο',
    lblCompany: 'Εταιρεία',
    lblNip: 'ΑΦΜ',
    lblStreet: 'Οδός',
    lblCity: 'Πόλη',
    lblPostalCode: 'Ταχ. κώδικας',
    lblCountry: 'Χώρα',
    secPersonal: 'Προσωπικά στοιχεία',
    secCompany: 'Στοιχεία εταιρείας',
    secAddress: 'Διεύθυνση',
    secDates: 'Ημερομηνίες',
    lblRegDate: 'Ημερομηνία εγγραφής',
    lblLastLogin: 'Τελευταία σύνδεση',
    lblLastUpdate: 'Τελευταία ενημέρωση',
    tabInfo: 'Στοιχεία πελάτη',
    tabInstallations: 'Εγκαταστάσεις',
    tabApiKeys: 'Κλειδιά API',
    tabCodes: 'Κωδικοί ενεργοποίησης',
    tabStats: 'Στατιστικά',
    noInstallations: 'Δεν υπάρχουν εγκαταστάσεις',
    installationFallback: 'Εγκατάσταση',
    stringsLabel: 'strings',
    lblUpdated: 'Ενημερώθηκε',
    lblLatitude: 'Γεωγραφικό πλάτος:',
    lblLongitude: 'Γεωγραφικό μήκος:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} πάνελ, az={{az}}, tilt={{tilt}}, ισχύς={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Δεν υπάρχουν κλειδιά API',
    thKey: 'Κλειδί',
    thPlan: 'Πρόγραμμα',
    thInstallation: 'Εγκατάσταση',
    thStatus: 'Κατάσταση',
    thExpires: 'Λήγει',
    thCreated: 'Δημιουργήθηκε',
    statusActive: 'Ενεργό',
    statusInactive: 'Ανενεργό',
    statusExpired: 'Έληξε',
    noCodes: 'Δεν υπάρχουν χρησιμοποιημένοι κωδικοί ενεργοποίησης',
    thCode: 'Κωδικός',
    thUsedAt: 'Χρησιμοποιήθηκε στις',
    thCreatedAt: 'Δημιουργήθηκε στις',
    statTotalRequests: 'Σύνολο αιτημάτων',
    statActiveKeys: 'Ενεργά κλειδιά API',
    statAllKeys: 'Όλα τα κλειδιά',
    statInstallations: 'Εγκαταστάσεις',
    chartTitle30d: 'Αιτήματα τις τελευταίες 30 ημέρες',
    noRequestData: 'Δεν υπάρχουν δεδομένα αιτημάτων',
    modalClientTitle: 'Πελάτης: {{name}}',
    loading: 'Φόρτωση…',
    loadingClientData: 'Φόρτωση δεδομένων πελάτη…',
    editTitle: 'Επεξεργασία πελάτη',
    cancel: 'Άκυρο',
    save: 'Αποθήκευση',
    statsTitle: 'Στατιστικά πελάτη',
    statsRequestsOverTime: 'Αιτήματα με την πάροδο του χρόνου',
    deleteTitle: 'Διαγραφή πελάτη',
    deleteConfirm:
      'Είστε σίγουροι ότι θέλετε να διαγράψετε τον πελάτη «{{name}}»; Αυτή η ενέργεια θα διαγράψει επίσης όλα τα κλειδιά API και τις εγκαταστάσεις αυτού του πελάτη.',
    deleteBtn: 'Διαγραφή',
    resetLinkTitle: 'Σύνδεσμος επαναφοράς κωδικού',
    resetLinkInfo: 'Δώστε τον παρακάτω σύνδεσμο στον πελάτη. Ο σύνδεσμος ισχύει για 1 ώρα.',
    copyBtn: 'Αντιγραφή',
  },
  et: {
    title: 'Klientide haldamine',
    subtitle: 'Kõigi klientide ülevaade ja haldamine',
    searchPlaceholder: 'Otsi nime või e-posti järgi…',
    exportBtn: 'Ekspordi kliendid',
    loadFailed: 'Klientide loendi laadimine ebaõnnestus',
    loadingClients: 'Klientide laadimine…',
    emptyClients: 'Kliente pole',
    prev: 'Eelmine',
    next: 'Järgmine',
    pageOf: 'Lehekülg {{cur}} / {{total}}',
    colApiKeys: 'API-võtmed',
    colInstallations: 'Paigaldised',
    colRequests: 'Päringud',
    colCreatedAt: 'Loodud',
    colActions: 'Toimingud',
    actView: 'Üksikasjad',
    actEdit: 'Muuda',
    actStats: 'Statistika',
    actResetPw: 'Lähtesta parool',
    actDelete: 'Kustuta',
    lblName: 'Nimi',
    lblEmail: 'E-post',
    lblPhone: 'Telefon',
    lblCompany: 'Ettevõte',
    lblNip: 'KMKR nr',
    lblStreet: 'Tänav',
    lblCity: 'Linn',
    lblPostalCode: 'Sihtnumber',
    lblCountry: 'Riik',
    secPersonal: 'Isikuandmed',
    secCompany: 'Ettevõtte andmed',
    secAddress: 'Aadress',
    secDates: 'Kuupäevad',
    lblRegDate: 'Registreerimise kuupäev',
    lblLastLogin: 'Viimane sisselogimine',
    lblLastUpdate: 'Viimane uuendus',
    tabInfo: 'Kliendi andmed',
    tabInstallations: 'Paigaldised',
    tabApiKeys: 'API-võtmed',
    tabCodes: 'Aktiveerimiskoodid',
    tabStats: 'Statistika',
    noInstallations: 'Paigaldisi pole',
    installationFallback: 'Paigaldis',
    stringsLabel: 'stringi',
    lblUpdated: 'Uuendatud',
    lblLatitude: 'Laiuskraad:',
    lblLongitude: 'Pikkuskraad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneeli, az={{az}}, tilt={{tilt}}, võimsus={{ps}}W, eff={{eff}}%',
    noApiKeys: 'API-võtmeid pole',
    thKey: 'Võti',
    thPlan: 'Pakett',
    thInstallation: 'Paigaldis',
    thStatus: 'Olek',
    thExpires: 'Aegub',
    thCreated: 'Loodud',
    statusActive: 'Aktiivne',
    statusInactive: 'Mitteaktiivne',
    statusExpired: 'Aegunud',
    noCodes: 'Kasutatud aktiveerimiskoode pole',
    thCode: 'Kood',
    thUsedAt: 'Kasutatud',
    thCreatedAt: 'Loodud',
    statTotalRequests: 'Päringuid kokku',
    statActiveKeys: 'Aktiivsed API-võtmed',
    statAllKeys: 'Kõik võtmed',
    statInstallations: 'Paigaldised',
    chartTitle30d: 'Päringud viimase 30 päeva jooksul',
    noRequestData: 'Päringuandmed puuduvad',
    modalClientTitle: 'Klient: {{name}}',
    loading: 'Laadimine…',
    loadingClientData: 'Kliendi andmete laadimine…',
    editTitle: 'Muuda klienti',
    cancel: 'Tühista',
    save: 'Salvesta',
    statsTitle: 'Kliendi statistika',
    statsRequestsOverTime: 'Päringud aja jooksul',
    deleteTitle: 'Kustuta klient',
    deleteConfirm:
      'Kas oled kindel, et soovid kustutada kliendi "{{name}}"? See toiming kustutab ka kõik selle kliendi API-võtmed ja paigaldised.',
    deleteBtn: 'Kustuta',
    resetLinkTitle: 'Parooli lähtestamise link',
    resetLinkInfo: 'Edasta allolev link kliendile. Link kehtib 1 tund.',
    copyBtn: 'Kopeeri',
  },
  lv: {
    title: 'Klientu pārvaldība',
    subtitle: 'Visu klientu pārskats un pārvaldība',
    searchPlaceholder: 'Meklēt pēc nosaukuma vai e-pasta…',
    exportBtn: 'Eksportēt klientus',
    loadFailed: 'Neizdevās ielādēt klientu sarakstu',
    loadingClients: 'Notiek klientu ielāde…',
    emptyClients: 'Nav klientu',
    prev: 'Iepriekšējā',
    next: 'Nākamā',
    pageOf: 'Lapa {{cur}} no {{total}}',
    colApiKeys: 'API atslēgas',
    colInstallations: 'Iekārtas',
    colRequests: 'Pieprasījumi',
    colCreatedAt: 'Izveidots',
    colActions: 'Darbības',
    actView: 'Detaļas',
    actEdit: 'Rediģēt',
    actStats: 'Statistika',
    actResetPw: 'Atiestatīt paroli',
    actDelete: 'Dzēst',
    lblName: 'Nosaukums',
    lblEmail: 'E-pasts',
    lblPhone: 'Tālrunis',
    lblCompany: 'Uzņēmums',
    lblNip: 'PVN reģ. nr.',
    lblStreet: 'Iela',
    lblCity: 'Pilsēta',
    lblPostalCode: 'Pasta indekss',
    lblCountry: 'Valsts',
    secPersonal: 'Personas dati',
    secCompany: 'Uzņēmuma dati',
    secAddress: 'Adrese',
    secDates: 'Datumi',
    lblRegDate: 'Reģistrācijas datums',
    lblLastLogin: 'Pēdējā pieteikšanās',
    lblLastUpdate: 'Pēdējā atjaunināšana',
    tabInfo: 'Klienta dati',
    tabInstallations: 'Iekārtas',
    tabApiKeys: 'API atslēgas',
    tabCodes: 'Aktivizācijas kodi',
    tabStats: 'Statistika',
    noInstallations: 'Nav iekārtu',
    installationFallback: 'Iekārta',
    stringsLabel: 'virknes',
    lblUpdated: 'Atjaunināts',
    lblLatitude: 'Platums:',
    lblLongitude: 'Garums:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneļi, az={{az}}, tilt={{tilt}}, jauda={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nav API atslēgu',
    thKey: 'Atslēga',
    thPlan: 'Plāns',
    thInstallation: 'Iekārta',
    thStatus: 'Statuss',
    thExpires: 'Beidzas',
    thCreated: 'Izveidots',
    statusActive: 'Aktīvs',
    statusInactive: 'Neaktīvs',
    statusExpired: 'Beidzies',
    noCodes: 'Nav izmantotu aktivizācijas kodu',
    thCode: 'Kods',
    thUsedAt: 'Izmantots',
    thCreatedAt: 'Izveidots',
    statTotalRequests: 'Kopā pieprasījumu',
    statActiveKeys: 'Aktīvās API atslēgas',
    statAllKeys: 'Visas atslēgas',
    statInstallations: 'Iekārtas',
    chartTitle30d: 'Pieprasījumi pēdējās 30 dienās',
    noRequestData: 'Nav datu par pieprasījumiem',
    modalClientTitle: 'Klients: {{name}}',
    loading: 'Notiek ielāde…',
    loadingClientData: 'Notiek klienta datu ielāde…',
    editTitle: 'Rediģēt klientu',
    cancel: 'Atcelt',
    save: 'Saglabāt',
    statsTitle: 'Klienta statistika',
    statsRequestsOverTime: 'Pieprasījumi laika gaitā',
    deleteTitle: 'Dzēst klientu',
    deleteConfirm:
      'Vai tiešām vēlaties dzēst klientu "{{name}}"? Šī darbība dzēsīs arī visas šī klienta API atslēgas un iekārtas.',
    deleteBtn: 'Dzēst',
    resetLinkTitle: 'Paroles atiestatīšanas saite',
    resetLinkInfo: 'Nododiet zemāk esošo saiti klientam. Saite ir derīga 1 stundu.',
    copyBtn: 'Kopēt',
  },
  lt: {
    title: 'Klientų valdymas',
    subtitle: 'Visų klientų apžvalga ir valdymas',
    searchPlaceholder: 'Ieškoti pagal pavadinimą arba el. paštą…',
    exportBtn: 'Eksportuoti klientus',
    loadFailed: 'Nepavyko įkelti klientų sąrašo',
    loadingClients: 'Įkeliami klientai…',
    emptyClients: 'Nėra klientų',
    prev: 'Ankstesnis',
    next: 'Kitas',
    pageOf: 'Puslapis {{cur}} iš {{total}}',
    colApiKeys: 'API raktai',
    colInstallations: 'Įrenginiai',
    colRequests: 'Užklausos',
    colCreatedAt: 'Sukurta',
    colActions: 'Veiksmai',
    actView: 'Išsamiau',
    actEdit: 'Redaguoti',
    actStats: 'Statistika',
    actResetPw: 'Atstatyti slaptažodį',
    actDelete: 'Ištrinti',
    lblName: 'Pavadinimas',
    lblEmail: 'El. paštas',
    lblPhone: 'Telefonas',
    lblCompany: 'Įmonė',
    lblNip: 'PVM kodas',
    lblStreet: 'Gatvė',
    lblCity: 'Miestas',
    lblPostalCode: 'Pašto kodas',
    lblCountry: 'Šalis',
    secPersonal: 'Asmeniniai duomenys',
    secCompany: 'Įmonės duomenys',
    secAddress: 'Adresas',
    secDates: 'Datos',
    lblRegDate: 'Registracijos data',
    lblLastLogin: 'Paskutinis prisijungimas',
    lblLastUpdate: 'Paskutinis atnaujinimas',
    tabInfo: 'Kliento duomenys',
    tabInstallations: 'Įrenginiai',
    tabApiKeys: 'API raktai',
    tabCodes: 'Aktyvavimo kodai',
    tabStats: 'Statistika',
    noInstallations: 'Nėra įrenginių',
    installationFallback: 'Įrenginys',
    stringsLabel: 'stringų',
    lblUpdated: 'Atnaujinta',
    lblLatitude: 'Platuma:',
    lblLongitude: 'Ilguma:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} plokščių, az={{az}}, tilt={{tilt}}, galia={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nėra API raktų',
    thKey: 'Raktas',
    thPlan: 'Planas',
    thInstallation: 'Įrenginys',
    thStatus: 'Būsena',
    thExpires: 'Baigiasi',
    thCreated: 'Sukurta',
    statusActive: 'Aktyvus',
    statusInactive: 'Neaktyvus',
    statusExpired: 'Pasibaigęs',
    noCodes: 'Nėra panaudotų aktyvavimo kodų',
    thCode: 'Kodas',
    thUsedAt: 'Panaudota',
    thCreatedAt: 'Sukurta',
    statTotalRequests: 'Iš viso užklausų',
    statActiveKeys: 'Aktyvūs API raktai',
    statAllKeys: 'Visi raktai',
    statInstallations: 'Įrenginiai',
    chartTitle30d: 'Užklausos per paskutines 30 dienų',
    noRequestData: 'Nėra duomenų apie užklausas',
    modalClientTitle: 'Klientas: {{name}}',
    loading: 'Įkeliama…',
    loadingClientData: 'Įkeliami kliento duomenys…',
    editTitle: 'Redaguoti klientą',
    cancel: 'Atšaukti',
    save: 'Išsaugoti',
    statsTitle: 'Kliento statistika',
    statsRequestsOverTime: 'Užklausos laikui bėgant',
    deleteTitle: 'Ištrinti klientą',
    deleteConfirm:
      'Ar tikrai norite ištrinti klientą "{{name}}"? Šis veiksmas taip pat ištrins visus šio kliento API raktus ir įrenginius.',
    deleteBtn: 'Ištrinti',
    resetLinkTitle: 'Slaptažodžio atstatymo nuoroda',
    resetLinkInfo: 'Perduokite žemiau esančią nuorodą klientui. Nuoroda galioja 1 valandą.',
    copyBtn: 'Kopijuoti',
  },
  mt: {
    title: 'Ġestjoni tal-klijenti',
    subtitle: 'Ħarsa ġenerali u ġestjoni tal-klijenti kollha',
    searchPlaceholder: 'Fittex skont l-isem jew l-email…',
    exportBtn: 'Esporta l-klijenti',
    loadFailed: 'Ma rnexxiex jiġi mgħobbi l-lista tal-klijenti',
    loadingClients: 'Qed jitgħabbew il-klijenti…',
    emptyClients: 'Ebda klijent',
    prev: 'Preċedenti',
    next: 'Li jmiss',
    pageOf: 'Paġna {{cur}} minn {{total}}',
    colApiKeys: 'Ċwievet API',
    colInstallations: 'Installazzjonijiet',
    colRequests: 'Talbiet',
    colCreatedAt: 'Maħluq fl-',
    colActions: 'Azzjonijiet',
    actView: 'Dettalji',
    actEdit: 'Editja',
    actStats: 'Statistika',
    actResetPw: 'Irrisettja l-password',
    actDelete: 'Ħassar',
    lblName: 'Isem',
    lblEmail: 'Email',
    lblPhone: 'Telefon',
    lblCompany: 'Kumpanija',
    lblNip: 'Nru VAT',
    lblStreet: 'Triq',
    lblCity: 'Belt',
    lblPostalCode: 'Kodiċi postali',
    lblCountry: 'Pajjiż',
    secPersonal: 'Dejta personali',
    secCompany: 'Dejta tal-kumpanija',
    secAddress: 'Indirizz',
    secDates: 'Dati',
    lblRegDate: 'Data tar-reġistrazzjoni',
    lblLastLogin: 'L-aħħar login',
    lblLastUpdate: 'L-aħħar aġġornament',
    tabInfo: 'Dejta tal-klijent',
    tabInstallations: 'Installazzjonijiet',
    tabApiKeys: 'Ċwievet API',
    tabCodes: "Kodiċijiet ta' attivazzjoni",
    tabStats: 'Statistika',
    noInstallations: 'Ebda installazzjoni',
    installationFallback: 'Installazzjoni',
    stringsLabel: 'strings',
    lblUpdated: 'Aġġornat',
    lblLatitude: 'Latitudni:',
    lblLongitude: 'Lonġitudni:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} pannelli, az={{az}}, tilt={{tilt}}, qawwa={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Ebda ċavetta API',
    thKey: 'Ċavetta',
    thPlan: 'Pjan',
    thInstallation: 'Installazzjoni',
    thStatus: 'Status',
    thExpires: 'Tiskadi',
    thCreated: 'Maħluq',
    statusActive: 'Attiv',
    statusInactive: 'Inattiv',
    statusExpired: 'Skadut',
    noCodes: "Ebda kodiċi ta' attivazzjoni użat",
    thCode: 'Kodiċi',
    thUsedAt: 'Użat fl-',
    thCreatedAt: 'Maħluq fl-',
    statTotalRequests: 'Total talbiet',
    statActiveKeys: 'Ċwievet API attivi',
    statAllKeys: 'Iċ-ċwievet kollha',
    statInstallations: 'Installazzjonijiet',
    chartTitle30d: 'Talbiet fl-aħħar 30 jum',
    noRequestData: 'Ebda data dwar it-talbiet',
    modalClientTitle: 'Klijent: {{name}}',
    loading: 'Qed jitgħabba…',
    loadingClientData: 'Qed titgħabba d-dejta tal-klijent…',
    editTitle: 'Editja l-klijent',
    cancel: 'Ikkanċella',
    save: 'Issejvja',
    statsTitle: 'Statistika tal-klijent',
    statsRequestsOverTime: 'Talbiet maż-żmien',
    deleteTitle: 'Ħassar il-klijent',
    deleteConfirm:
      'Żgur trid tħassar il-klijent "{{name}}"? Din l-operazzjoni se tħassar ukoll iċ-ċwievet API u l-installazzjonijiet kollha ta\' dan il-klijent.',
    deleteBtn: 'Ħassar',
    resetLinkTitle: 'Link għar-reset tal-password',
    resetLinkInfo: 'Għaddi l-link hawn taħt lill-klijent. Il-link huwa validu għal siegħa.',
    copyBtn: 'Ikkopja',
  },
  ga: {
    title: 'Bainistíocht cliant',
    subtitle: 'Forléargas agus bainistíocht ar gach cliant',
    searchPlaceholder: 'Cuardaigh de réir ainm nó ríomhphoist…',
    exportBtn: 'Easpórtáil cliaint',
    loadFailed: 'Theip ar liosta na gcliant a luchtú',
    loadingClients: 'Cliaint á luchtú…',
    emptyClients: 'Gan cliaint',
    prev: 'Roimhe seo',
    next: 'Ar aghaidh',
    pageOf: 'Leathanach {{cur}} as {{total}}',
    colApiKeys: 'Eochracha API',
    colInstallations: 'Suiteálacha',
    colRequests: 'Iarratais',
    colCreatedAt: 'Cruthaithe ar',
    colActions: 'Gníomhartha',
    actView: 'Sonraí',
    actEdit: 'Cuir in eagar',
    actStats: 'Staitisticí',
    actResetPw: 'Athshocraigh focal faire',
    actDelete: 'Scrios',
    lblName: 'Ainm',
    lblEmail: 'Ríomhphost',
    lblPhone: 'Fón',
    lblCompany: 'Cuideachta',
    lblNip: 'Uimh. CBL',
    lblStreet: 'Sráid',
    lblCity: 'Cathair',
    lblPostalCode: 'Cód poist',
    lblCountry: 'Tír',
    secPersonal: 'Sonraí pearsanta',
    secCompany: 'Sonraí na cuideachta',
    secAddress: 'Seoladh',
    secDates: 'Dátaí',
    lblRegDate: 'Dáta cláraithe',
    lblLastLogin: 'Logáil isteach is déanaí',
    lblLastUpdate: 'Nuashonrú is déanaí',
    tabInfo: 'Sonraí an chliaint',
    tabInstallations: 'Suiteálacha',
    tabApiKeys: 'Eochracha API',
    tabCodes: 'Cóid ghníomhachtaithe',
    tabStats: 'Staitisticí',
    noInstallations: 'Gan suiteálacha',
    installationFallback: 'Suiteáil',
    stringsLabel: 'strings',
    lblUpdated: 'Nuashonraithe',
    lblLatitude: 'Domhanleithead:',
    lblLongitude: 'Domhanfhad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} painéil, az={{az}}, tilt={{tilt}}, cumhacht={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Gan eochracha API',
    thKey: 'Eochair',
    thPlan: 'Plean',
    thInstallation: 'Suiteáil',
    thStatus: 'Stádas',
    thExpires: 'Éagann',
    thCreated: 'Cruthaithe',
    statusActive: 'Gníomhach',
    statusInactive: 'Neamhghníomhach',
    statusExpired: 'Éagtha',
    noCodes: 'Gan cóid ghníomhachtaithe úsáidte',
    thCode: 'Cód',
    thUsedAt: 'Úsáidte ar',
    thCreatedAt: 'Cruthaithe ar',
    statTotalRequests: 'Iarratais san iomlán',
    statActiveKeys: 'Eochracha API gníomhacha',
    statAllKeys: 'Gach eochair',
    statInstallations: 'Suiteálacha',
    chartTitle30d: 'Iarratais sna 30 lá deireanacha',
    noRequestData: 'Gan sonraí iarratais',
    modalClientTitle: 'Cliant: {{name}}',
    loading: 'Á luchtú…',
    loadingClientData: 'Sonraí an chliaint á luchtú…',
    editTitle: 'Cuir an cliant in eagar',
    cancel: 'Cealaigh',
    save: 'Sábháil',
    statsTitle: 'Staitisticí an chliaint',
    statsRequestsOverTime: 'Iarratais le himeacht ama',
    deleteTitle: 'Scrios cliant',
    deleteConfirm:
      'An bhfuil tú cinnte gur mhaith leat an cliant "{{name}}" a scriosadh? Scriosfaidh an oibríocht seo gach eochair API agus suiteáil de chuid an chliaint seo freisin.',
    deleteBtn: 'Scrios',
    resetLinkTitle: 'Nasc athshocraithe focal faire',
    resetLinkInfo: 'Tabhair an nasc thíos don chliant. Tá an nasc bailí ar feadh uair an chloig.',
    copyBtn: 'Cóipeáil',
  },
  no: {
    title: 'Kundeadministrasjon',
    subtitle: 'Oversikt og administrasjon av alle kunder',
    searchPlaceholder: 'Søk etter navn eller e-post…',
    exportBtn: 'Eksporter kunder',
    loadFailed: 'Kunne ikke laste kundelisten',
    loadingClients: 'Laster kunder…',
    emptyClients: 'Ingen kunder',
    prev: 'Forrige',
    next: 'Neste',
    pageOf: 'Side {{cur}} av {{total}}',
    colApiKeys: 'API-nøkler',
    colInstallations: 'Anlegg',
    colRequests: 'Forespørsler',
    colCreatedAt: 'Opprettet den',
    colActions: 'Handlinger',
    actView: 'Detaljer',
    actEdit: 'Rediger',
    actStats: 'Statistikk',
    actResetPw: 'Tilbakestill passord',
    actDelete: 'Slett',
    lblName: 'Navn',
    lblEmail: 'E-post',
    lblPhone: 'Telefon',
    lblCompany: 'Firma',
    lblNip: 'Org.nr',
    lblStreet: 'Gate',
    lblCity: 'By',
    lblPostalCode: 'Postnummer',
    lblCountry: 'Land',
    secPersonal: 'Personlige data',
    secCompany: 'Firmadata',
    secAddress: 'Adresse',
    secDates: 'Datoer',
    lblRegDate: 'Registreringsdato',
    lblLastLogin: 'Siste innlogging',
    lblLastUpdate: 'Siste oppdatering',
    tabInfo: 'Kundedata',
    tabInstallations: 'Anlegg',
    tabApiKeys: 'API-nøkler',
    tabCodes: 'Aktiveringskoder',
    tabStats: 'Statistikk',
    noInstallations: 'Ingen anlegg',
    installationFallback: 'Anlegg',
    stringsLabel: 'string(s)',
    lblUpdated: 'Oppdatert',
    lblLatitude: 'Breddegrad:',
    lblLongitude: 'Lengdegrad:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} paneler, az={{az}}, tilt={{tilt}}, effekt={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Ingen API-nøkler',
    thKey: 'Nøkkel',
    thPlan: 'Plan',
    thInstallation: 'Anlegg',
    thStatus: 'Status',
    thExpires: 'Utløper',
    thCreated: 'Opprettet',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    statusExpired: 'Utløpt',
    noCodes: 'Ingen brukte aktiveringskoder',
    thCode: 'Kode',
    thUsedAt: 'Brukt den',
    thCreatedAt: 'Opprettet den',
    statTotalRequests: 'Totalt antall forespørsler',
    statActiveKeys: 'Aktive API-nøkler',
    statAllKeys: 'Alle nøkler',
    statInstallations: 'Anlegg',
    chartTitle30d: 'Forespørsler de siste 30 dagene',
    noRequestData: 'Ingen forespørselsdata',
    modalClientTitle: 'Kunde: {{name}}',
    loading: 'Laster…',
    loadingClientData: 'Laster kundedata…',
    editTitle: 'Rediger kunde',
    cancel: 'Avbryt',
    save: 'Lagre',
    statsTitle: 'Kundestatistikk',
    statsRequestsOverTime: 'Forespørsler over tid',
    deleteTitle: 'Slett kunde',
    deleteConfirm:
      'Er du sikker på at du vil slette kunden "{{name}}"? Denne handlingen sletter også alle API-nøkler og anlegg for denne kunden.',
    deleteBtn: 'Slett',
    resetLinkTitle: 'Lenke for tilbakestilling av passord',
    resetLinkInfo: 'Gi lenken nedenfor til kunden. Lenken er gyldig i 1 time.',
    copyBtn: 'Kopier',
  },
  is: {
    title: 'Viðskiptavinastjórnun',
    subtitle: 'Yfirlit og stjórnun allra viðskiptavina',
    searchPlaceholder: 'Leita eftir nafni eða tölvupósti…',
    exportBtn: 'Flytja út viðskiptavini',
    loadFailed: 'Ekki tókst að hlaða lista yfir viðskiptavini',
    loadingClients: 'Hleð viðskiptavini…',
    emptyClients: 'Engir viðskiptavinir',
    prev: 'Fyrri',
    next: 'Næsta',
    pageOf: 'Síða {{cur}} af {{total}}',
    colApiKeys: 'API-lyklar',
    colInstallations: 'Stöðvar',
    colRequests: 'Beiðnir',
    colCreatedAt: 'Búið til þann',
    colActions: 'Aðgerðir',
    actView: 'Nánar',
    actEdit: 'Breyta',
    actStats: 'Tölfræði',
    actResetPw: 'Endurstilla lykilorð',
    actDelete: 'Eyða',
    lblName: 'Nafn',
    lblEmail: 'Tölvupóstur',
    lblPhone: 'Sími',
    lblCompany: 'Fyrirtæki',
    lblNip: 'VSK-nr.',
    lblStreet: 'Gata',
    lblCity: 'Borg',
    lblPostalCode: 'Póstnúmer',
    lblCountry: 'Land',
    secPersonal: 'Persónuupplýsingar',
    secCompany: 'Fyrirtækjaupplýsingar',
    secAddress: 'Heimilisfang',
    secDates: 'Dagsetningar',
    lblRegDate: 'Skráningardagsetning',
    lblLastLogin: 'Síðasta innskráning',
    lblLastUpdate: 'Síðasta uppfærsla',
    tabInfo: 'Upplýsingar viðskiptavinar',
    tabInstallations: 'Stöðvar',
    tabApiKeys: 'API-lyklar',
    tabCodes: 'Virkjunarkóðar',
    tabStats: 'Tölfræði',
    noInstallations: 'Engar stöðvar',
    installationFallback: 'Stöð',
    stringsLabel: 'strings',
    lblUpdated: 'Uppfært',
    lblLatitude: 'Breiddargráða:',
    lblLongitude: 'Lengdargráða:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} spjöld, az={{az}}, tilt={{tilt}}, afl={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Engir API-lyklar',
    thKey: 'Lykill',
    thPlan: 'Áætlun',
    thInstallation: 'Stöð',
    thStatus: 'Staða',
    thExpires: 'Rennur út',
    thCreated: 'Búið til',
    statusActive: 'Virkur',
    statusInactive: 'Óvirkur',
    statusExpired: 'Útrunninn',
    noCodes: 'Engir notaðir virkjunarkóðar',
    thCode: 'Kóði',
    thUsedAt: 'Notaður þann',
    thCreatedAt: 'Búið til þann',
    statTotalRequests: 'Beiðnir alls',
    statActiveKeys: 'Virkir API-lyklar',
    statAllKeys: 'Allir lyklar',
    statInstallations: 'Stöðvar',
    chartTitle30d: 'Beiðnir síðustu 30 daga',
    noRequestData: 'Engin beiðnagögn',
    modalClientTitle: 'Viðskiptavinur: {{name}}',
    loading: 'Hleð…',
    loadingClientData: 'Hleð gögn viðskiptavinar…',
    editTitle: 'Breyta viðskiptavini',
    cancel: 'Hætta við',
    save: 'Vista',
    statsTitle: 'Tölfræði viðskiptavinar',
    statsRequestsOverTime: 'Beiðnir yfir tíma',
    deleteTitle: 'Eyða viðskiptavini',
    deleteConfirm:
      'Ertu viss um að þú viljir eyða viðskiptavininum "{{name}}"? Þessi aðgerð mun einnig eyða öllum API-lyklum og stöðvum þessa viðskiptavinar.',
    deleteBtn: 'Eyða',
    resetLinkTitle: 'Tengill til að endurstilla lykilorð',
    resetLinkInfo:
      'Komdu tenglinum hér að neðan til viðskiptavinarins. Tengillinn gildir í 1 klukkustund.',
    copyBtn: 'Afrita',
  },
  uk: {
    title: 'Керування клієнтами',
    subtitle: 'Огляд і керування всіма клієнтами',
    searchPlaceholder: 'Пошук за назвою або e-mail…',
    exportBtn: 'Експортувати клієнтів',
    loadFailed: 'Не вдалося завантажити список клієнтів',
    loadingClients: 'Завантаження клієнтів…',
    emptyClients: 'Немає клієнтів',
    prev: 'Попередня',
    next: 'Наступна',
    pageOf: 'Сторінка {{cur}} з {{total}}',
    colApiKeys: 'Ключі API',
    colInstallations: 'Установки',
    colRequests: 'Запити',
    colCreatedAt: 'Створено',
    colActions: 'Дії',
    actView: 'Деталі',
    actEdit: 'Редагувати',
    actStats: 'Статистика',
    actResetPw: 'Скинути пароль',
    actDelete: 'Видалити',
    lblName: 'Назва',
    lblEmail: 'E-mail',
    lblPhone: 'Телефон',
    lblCompany: 'Компанія',
    lblNip: 'Податковий номер',
    lblStreet: 'Вулиця',
    lblCity: 'Місто',
    lblPostalCode: 'Поштовий індекс',
    lblCountry: 'Країна',
    secPersonal: 'Особисті дані',
    secCompany: 'Дані компанії',
    secAddress: 'Адреса',
    secDates: 'Дати',
    lblRegDate: 'Дата реєстрації',
    lblLastLogin: 'Останній вхід',
    lblLastUpdate: 'Останнє оновлення',
    tabInfo: 'Дані клієнта',
    tabInstallations: 'Установки',
    tabApiKeys: 'Ключі API',
    tabCodes: 'Коди активації',
    tabStats: 'Статистика',
    noInstallations: 'Немає установок',
    installationFallback: 'Установка',
    stringsLabel: 'стрінгів',
    lblUpdated: 'Оновлено',
    lblLatitude: 'Широта:',
    lblLongitude: 'Довгота:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail:
      '{{pc}} панелей, az={{az}}, tilt={{tilt}}, потужність={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Немає ключів API',
    thKey: 'Ключ',
    thPlan: 'План',
    thInstallation: 'Установка',
    thStatus: 'Статус',
    thExpires: 'Закінчується',
    thCreated: 'Створено',
    statusActive: 'Активний',
    statusInactive: 'Неактивний',
    statusExpired: 'Закінчився',
    noCodes: 'Немає використаних кодів активації',
    thCode: 'Код',
    thUsedAt: 'Використано',
    thCreatedAt: 'Створено',
    statTotalRequests: 'Усього запитів',
    statActiveKeys: 'Активні ключі API',
    statAllKeys: 'Усі ключі',
    statInstallations: 'Установки',
    chartTitle30d: 'Запити за останні 30 днів',
    noRequestData: 'Немає даних про запити',
    modalClientTitle: 'Клієнт: {{name}}',
    loading: 'Завантаження…',
    loadingClientData: 'Завантаження даних клієнта…',
    editTitle: 'Редагувати клієнта',
    cancel: 'Скасувати',
    save: 'Зберегти',
    statsTitle: 'Статистика клієнта',
    statsRequestsOverTime: 'Запити з часом',
    deleteTitle: 'Видалити клієнта',
    deleteConfirm:
      'Ви впевнені, що хочете видалити клієнта «{{name}}»? Ця операція також видалить усі ключі API та установки цього клієнта.',
    deleteBtn: 'Видалити',
    resetLinkTitle: 'Посилання для скидання пароля',
    resetLinkInfo: 'Передайте посилання нижче клієнту. Посилання дійсне протягом 1 години.',
    copyBtn: 'Копіювати',
  },
  sr: {
    title: 'Управљање клијентима',
    subtitle: 'Преглед и управљање свим клијентима',
    searchPlaceholder: 'Претрага по називу или имејлу…',
    exportBtn: 'Извези клијенте',
    loadFailed: 'Није могуће учитати листу клијената',
    loadingClients: 'Учитавање клијената…',
    emptyClients: 'Нема клијената',
    prev: 'Претходна',
    next: 'Следећа',
    pageOf: 'Страница {{cur}} од {{total}}',
    colApiKeys: 'API кључеви',
    colInstallations: 'Постројења',
    colRequests: 'Захтеви',
    colCreatedAt: 'Креирано дана',
    colActions: 'Радње',
    actView: 'Детаљи',
    actEdit: 'Уреди',
    actStats: 'Статистика',
    actResetPw: 'Ресетуј лозинку',
    actDelete: 'Обриши',
    lblName: 'Назив',
    lblEmail: 'Имејл',
    lblPhone: 'Телефон',
    lblCompany: 'Компанија',
    lblNip: 'ПИБ',
    lblStreet: 'Улица',
    lblCity: 'Град',
    lblPostalCode: 'Поштански број',
    lblCountry: 'Држава',
    secPersonal: 'Лични подаци',
    secCompany: 'Подаци о компанији',
    secAddress: 'Адреса',
    secDates: 'Датуми',
    lblRegDate: 'Датум регистрације',
    lblLastLogin: 'Последња пријава',
    lblLastUpdate: 'Последња измена',
    tabInfo: 'Подаци клијента',
    tabInstallations: 'Постројења',
    tabApiKeys: 'API кључеви',
    tabCodes: 'Активациони кодови',
    tabStats: 'Статистика',
    noInstallations: 'Нема постројења',
    installationFallback: 'Постројење',
    stringsLabel: 'стрингова',
    lblUpdated: 'Ажурирано',
    lblLatitude: 'Географска ширина:',
    lblLongitude: 'Географска дужина:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} панела, az={{az}}, tilt={{tilt}}, снага={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Нема API кључева',
    thKey: 'Кључ',
    thPlan: 'План',
    thInstallation: 'Постројење',
    thStatus: 'Статус',
    thExpires: 'Истиче',
    thCreated: 'Креирано',
    statusActive: 'Активан',
    statusInactive: 'Неактиван',
    statusExpired: 'Истекао',
    noCodes: 'Нема искоришћених активационих кодова',
    thCode: 'Код',
    thUsedAt: 'Искоришћено дана',
    thCreatedAt: 'Креирано дана',
    statTotalRequests: 'Укупно захтева',
    statActiveKeys: 'Активни API кључеви',
    statAllKeys: 'Сви кључеви',
    statInstallations: 'Постројења',
    chartTitle30d: 'Захтеви у последњих 30 дана',
    noRequestData: 'Нема података о захтевима',
    modalClientTitle: 'Клијент: {{name}}',
    loading: 'Учитавање…',
    loadingClientData: 'Учитавање података клијента…',
    editTitle: 'Уреди клијента',
    cancel: 'Откажи',
    save: 'Сачувај',
    statsTitle: 'Статистика клијента',
    statsRequestsOverTime: 'Захтеви током времена',
    deleteTitle: 'Обриши клијента',
    deleteConfirm:
      'Да ли сте сигурни да желите да обришете клијента „{{name}}“? Ова операција ће такође обрисати све API кључеве и постројења овог клијента.',
    deleteBtn: 'Обриши',
    resetLinkTitle: 'Веза за ресетовање лозинке',
    resetLinkInfo: 'Проследите везу испод клијенту. Веза важи 1 сат.',
    copyBtn: 'Копирај',
  },
  sq: {
    title: 'Menaxhimi i klientëve',
    subtitle: 'Pamje e përgjithshme dhe menaxhimi i të gjithë klientëve',
    searchPlaceholder: 'Kërko sipas emrit ose e-mail-it…',
    exportBtn: 'Eksporto klientët',
    loadFailed: 'Dështoi ngarkimi i listës së klientëve',
    loadingClients: 'Po ngarkohen klientët…',
    emptyClients: 'Asnjë klient',
    prev: 'Mëparshme',
    next: 'Tjetra',
    pageOf: 'Faqja {{cur}} nga {{total}}',
    colApiKeys: 'Çelësat API',
    colInstallations: 'Instalimet',
    colRequests: 'Kërkesat',
    colCreatedAt: 'Krijuar më',
    colActions: 'Veprime',
    actView: 'Detaje',
    actEdit: 'Modifiko',
    actStats: 'Statistika',
    actResetPw: 'Rivendos fjalëkalimin',
    actDelete: 'Fshi',
    lblName: 'Emri',
    lblEmail: 'E-mail',
    lblPhone: 'Telefoni',
    lblCompany: 'Kompania',
    lblNip: 'Nr. TVSH',
    lblStreet: 'Rruga',
    lblCity: 'Qyteti',
    lblPostalCode: 'Kodi postar',
    lblCountry: 'Shteti',
    secPersonal: 'Të dhëna personale',
    secCompany: 'Të dhënat e kompanisë',
    secAddress: 'Adresa',
    secDates: 'Datat',
    lblRegDate: 'Data e regjistrimit',
    lblLastLogin: 'Hyrja e fundit',
    lblLastUpdate: 'Përditësimi i fundit',
    tabInfo: 'Të dhënat e klientit',
    tabInstallations: 'Instalimet',
    tabApiKeys: 'Çelësat API',
    tabCodes: 'Kodet e aktivizimit',
    tabStats: 'Statistika',
    noInstallations: 'Asnjë instalim',
    installationFallback: 'Instalim',
    stringsLabel: 'strings',
    lblUpdated: 'Përditësuar',
    lblLatitude: 'Gjerësia gjeografike:',
    lblLongitude: 'Gjatësia gjeografike:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panele, az={{az}}, tilt={{tilt}}, fuqia={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Asnjë çelës API',
    thKey: 'Çelësi',
    thPlan: 'Plani',
    thInstallation: 'Instalimi',
    thStatus: 'Statusi',
    thExpires: 'Skadon',
    thCreated: 'Krijuar',
    statusActive: 'Aktiv',
    statusInactive: 'Joaktiv',
    statusExpired: 'I skaduar',
    noCodes: 'Asnjë kod aktivizimi i përdorur',
    thCode: 'Kodi',
    thUsedAt: 'Përdorur më',
    thCreatedAt: 'Krijuar më',
    statTotalRequests: 'Gjithsej kërkesa',
    statActiveKeys: 'Çelësat API aktivë',
    statAllKeys: 'Të gjithë çelësat',
    statInstallations: 'Instalimet',
    chartTitle30d: 'Kërkesat në 30 ditët e fundit',
    noRequestData: 'Asnjë e dhënë për kërkesat',
    modalClientTitle: 'Klienti: {{name}}',
    loading: 'Po ngarkohet…',
    loadingClientData: 'Po ngarkohen të dhënat e klientit…',
    editTitle: 'Modifiko klientin',
    cancel: 'Anulo',
    save: 'Ruaj',
    statsTitle: 'Statistika e klientit',
    statsRequestsOverTime: 'Kërkesat me kalimin e kohës',
    deleteTitle: 'Fshi klientin',
    deleteConfirm:
      'Je i sigurt që dëshiron të fshish klientin "{{name}}"? Ky veprim do të fshijë gjithashtu të gjithë çelësat API dhe instalimet e këtij klienti.',
    deleteBtn: 'Fshi',
    resetLinkTitle: 'Lidhja për rivendosjen e fjalëkalimit',
    resetLinkInfo: 'Jepi klientit lidhjen më poshtë. Lidhja është e vlefshme për 1 orë.',
    copyBtn: 'Kopjo',
  },
  mk: {
    title: 'Управување со клиенти',
    subtitle: 'Преглед и управување со сите клиенти',
    searchPlaceholder: 'Пребарувај по име или е-пошта…',
    exportBtn: 'Извези клиенти',
    loadFailed: 'Не може да се вчита списокот на клиенти',
    loadingClients: 'Се вчитуваат клиенти…',
    emptyClients: 'Нема клиенти',
    prev: 'Претходна',
    next: 'Следна',
    pageOf: 'Страница {{cur}} од {{total}}',
    colApiKeys: 'API клучеви',
    colInstallations: 'Инсталации',
    colRequests: 'Барања',
    colCreatedAt: 'Создаден на',
    colActions: 'Дејства',
    actView: 'Детали',
    actEdit: 'Уреди',
    actStats: 'Статистика',
    actResetPw: 'Ресетирај лозинка',
    actDelete: 'Избриши',
    lblName: 'Име',
    lblEmail: 'Е-пошта',
    lblPhone: 'Телефон',
    lblCompany: 'Компанија',
    lblNip: 'ЕДБ',
    lblStreet: 'Улица',
    lblCity: 'Град',
    lblPostalCode: 'Поштенски код',
    lblCountry: 'Земја',
    secPersonal: 'Лични податоци',
    secCompany: 'Податоци за компанијата',
    secAddress: 'Адреса',
    secDates: 'Датуми',
    lblRegDate: 'Датум на регистрација',
    lblLastLogin: 'Последна најава',
    lblLastUpdate: 'Последно ажурирање',
    tabInfo: 'Податоци за клиентот',
    tabInstallations: 'Инсталации',
    tabApiKeys: 'API клучеви',
    tabCodes: 'Кодови за активација',
    tabStats: 'Статистика',
    noInstallations: 'Нема инсталации',
    installationFallback: 'Инсталација',
    stringsLabel: 'стрингови',
    lblUpdated: 'Ажурирано',
    lblLatitude: 'Географска ширина:',
    lblLongitude: 'Географска должина:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} панели, az={{az}}, tilt={{tilt}}, моќност={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Нема API клучеви',
    thKey: 'Клуч',
    thPlan: 'План',
    thInstallation: 'Инсталација',
    thStatus: 'Статус',
    thExpires: 'Истекува',
    thCreated: 'Создаден',
    statusActive: 'Активен',
    statusInactive: 'Неактивен',
    statusExpired: 'Истечен',
    noCodes: 'Нема искористени кодови за активација',
    thCode: 'Код',
    thUsedAt: 'Искористен на',
    thCreatedAt: 'Создаден на',
    statTotalRequests: 'Вкупно барања',
    statActiveKeys: 'Активни API клучеви',
    statAllKeys: 'Сите клучеви',
    statInstallations: 'Инсталации',
    chartTitle30d: 'Барања во последните 30 дена',
    noRequestData: 'Нема податоци за барања',
    modalClientTitle: 'Клиент: {{name}}',
    loading: 'Се вчитува…',
    loadingClientData: 'Се вчитуваат податоците за клиентот…',
    editTitle: 'Уреди клиент',
    cancel: 'Откажи',
    save: 'Зачувај',
    statsTitle: 'Статистика за клиентот',
    statsRequestsOverTime: 'Барања низ времето',
    deleteTitle: 'Избриши клиент',
    deleteConfirm:
      'Дали сте сигурни дека сакате да го избришете клиентот „{{name}}“? Оваа операција исто така ќе ги избрише сите API клучеви и инсталации на овој клиент.',
    deleteBtn: 'Избриши',
    resetLinkTitle: 'Врска за ресетирање на лозинка',
    resetLinkInfo: 'Дадете му ја врската подолу на клиентот. Врската важи 1 час.',
    copyBtn: 'Копирај',
  },
  bs: {
    title: 'Upravljanje klijentima',
    subtitle: 'Pregled i upravljanje svim klijentima',
    searchPlaceholder: 'Traži po nazivu ili e-pošti…',
    exportBtn: 'Izvezi klijente',
    loadFailed: 'Nije moguće učitati listu klijenata',
    loadingClients: 'Učitavanje klijenata…',
    emptyClients: 'Nema klijenata',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    colApiKeys: 'API ključevi',
    colInstallations: 'Postrojenja',
    colRequests: 'Zahtjevi',
    colCreatedAt: 'Kreirano dana',
    colActions: 'Radnje',
    actView: 'Detalji',
    actEdit: 'Uredi',
    actStats: 'Statistika',
    actResetPw: 'Resetuj lozinku',
    actDelete: 'Obriši',
    lblName: 'Naziv',
    lblEmail: 'E-pošta',
    lblPhone: 'Telefon',
    lblCompany: 'Kompanija',
    lblNip: 'PDV broj',
    lblStreet: 'Ulica',
    lblCity: 'Grad',
    lblPostalCode: 'Poštanski broj',
    lblCountry: 'Država',
    secPersonal: 'Lični podaci',
    secCompany: 'Podaci o kompaniji',
    secAddress: 'Adresa',
    secDates: 'Datumi',
    lblRegDate: 'Datum registracije',
    lblLastLogin: 'Zadnja prijava',
    lblLastUpdate: 'Zadnja izmjena',
    tabInfo: 'Podaci klijenta',
    tabInstallations: 'Postrojenja',
    tabApiKeys: 'API ključevi',
    tabCodes: 'Aktivacijski kodovi',
    tabStats: 'Statistika',
    noInstallations: 'Nema postrojenja',
    installationFallback: 'Postrojenje',
    stringsLabel: 'nizova',
    lblUpdated: 'Ažurirano',
    lblLatitude: 'Geografska širina:',
    lblLongitude: 'Geografska dužina:',
    stringNLabel: 'String {{n}}:',
    stringConfigDetail: '{{pc}} panela, az={{az}}, tilt={{tilt}}, snaga={{ps}}W, eff={{eff}}%',
    noApiKeys: 'Nema API ključeva',
    thKey: 'Ključ',
    thPlan: 'Plan',
    thInstallation: 'Postrojenje',
    thStatus: 'Status',
    thExpires: 'Ističe',
    thCreated: 'Kreirano',
    statusActive: 'Aktivan',
    statusInactive: 'Neaktivan',
    statusExpired: 'Istekao',
    noCodes: 'Nema iskorištenih aktivacijskih kodova',
    thCode: 'Kod',
    thUsedAt: 'Iskorišteno dana',
    thCreatedAt: 'Kreirano dana',
    statTotalRequests: 'Ukupno zahtjeva',
    statActiveKeys: 'Aktivni API ključevi',
    statAllKeys: 'Svi ključevi',
    statInstallations: 'Postrojenja',
    chartTitle30d: 'Zahtjevi u zadnjih 30 dana',
    noRequestData: 'Nema podataka o zahtjevima',
    modalClientTitle: 'Klijent: {{name}}',
    loading: 'Učitavanje…',
    loadingClientData: 'Učitavanje podataka klijenta…',
    editTitle: 'Uredi klijenta',
    cancel: 'Otkaži',
    save: 'Spremi',
    statsTitle: 'Statistika klijenta',
    statsRequestsOverTime: 'Zahtjevi tokom vremena',
    deleteTitle: 'Obriši klijenta',
    deleteConfirm:
      'Jeste li sigurni da želite obrisati klijenta "{{name}}"? Ova operacija će također obrisati sve API ključeve i postrojenja ovog klijenta.',
    deleteBtn: 'Obriši',
    resetLinkTitle: 'Link za resetovanje lozinke',
    resetLinkInfo: 'Proslijedite link ispod klijentu. Link vrijedi 1 sat.',
    copyBtn: 'Kopiraj',
  },
};

const graviteeTr: Record<string, Dict> = {
  en: {
    title: 'Gravitee import',
    subtitle: 'Import subscriptions and API keys from Gravitee',
    modeApi: 'Sync with Gravitee API',
    modeFile: 'Import from JSON file',
    loadPlansFailed: 'Failed to load plans',
    fileMustBeJson: 'The file must be in JSON format',
    fileInvalid: 'Invalid file',
    allPlansMustBeMapped: 'All Gravitee plans must be mapped',
    selectApiToSync: 'Select an API to sync',
    step1Api: '1. Sync with Gravitee',
    step1ApiSub: 'Fetch data directly from the Gravitee API',
    connectionStatus: 'Connection status',
    testConnection: 'Test connection',
    selectApiLabel: 'Select an API to sync',
    apiIdPlaceholder: 'Gravitee API ID',
    defaultApiHint: 'Default: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Fetch preview from Gravitee',
    step1File: '1. Load JSON file',
    step1FileSub: 'Choose the export file from Gravitee',
    clickToSelectFile: 'Click to select a JSON file',
    validate: 'Validate',
    step2: '2. Import preview',
    step2Sub: 'Review the import statistics',
    subsToImport: 'Subscriptions to import',
    subsToSkip: 'Subscriptions to skip',
    keysToImport: 'Keys to import',
    clientsToCreate: 'Clients to create',
    warnings: 'Warnings:',
    step3: '3. Plan mapping',
    step3Sub: 'Map Gravitee plans to local plans',
    graviteePlanLabel: 'Gravitee plan:',
    planUnlimited: 'unlimited',
    planOptionFmt: '{{name}} (max: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Select a local plan',
    skipDuplicates: 'Skip duplicates',
    skipInactive: 'Skip inactive',
    cancel: 'Cancel',
    syncAndImport: 'Sync and import',
    importBtn: 'Import',
    step4: '4. Import complete',
    importSuccess: 'Import completed successfully!',
    importedKeys: 'Imported keys',
    skippedKeys: 'Skipped keys',
    failedKeys: 'Failed keys',
    createdClients: 'Created clients',
    errors: 'Errors:',
    newImport: 'New import',
  },
  pl: {
    title: 'Import Gravitee',
    subtitle: 'Importuj subskrypcje i klucze API z Gravitee',
    modeApi: 'Synchronizuj z Gravitee API',
    modeFile: 'Importuj z pliku JSON',
    loadPlansFailed: 'Nie udało się załadować planów',
    fileMustBeJson: 'Plik musi być w formacie JSON',
    fileInvalid: 'Plik nieprawidłowy',
    allPlansMustBeMapped: 'Wszystkie plany Gravitee muszą być zmapowane',
    selectApiToSync: 'Wybierz API do synchronizacji',
    step1Api: '1. Synchronizuj z Gravitee',
    step1ApiSub: 'Pobierz dane bezpośrednio z Gravitee API',
    connectionStatus: 'Status połączenia',
    testConnection: 'Testuj połączenie',
    selectApiLabel: 'Wybierz API do synchronizacji',
    apiIdPlaceholder: 'ID API w Gravitee',
    defaultApiHint: 'Domyślnie: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Pobierz podgląd z Gravitee',
    step1File: '1. Wczytaj plik JSON',
    step1FileSub: 'Wybierz plik eksportu z Gravitee',
    clickToSelectFile: 'Kliknij, aby wybrać plik JSON',
    validate: 'Waliduj',
    step2: '2. Podgląd importu',
    step2Sub: 'Sprawdź statystyki importu',
    subsToImport: 'Subskrypcje do importu',
    subsToSkip: 'Subskrypcje do pominięcia',
    keysToImport: 'Klucze do importu',
    clientsToCreate: 'Klienci do utworzenia',
    warnings: 'Ostrzeżenia:',
    step3: '3. Mapowanie planów',
    step3Sub: 'Przypisz plany Gravitee do lokalnych planów',
    graviteePlanLabel: 'Plan Gravitee:',
    planUnlimited: 'nielimitowane',
    planOptionFmt: '{{name}} (max: {{max}} stringów, {{rate}}/min)',
    selectLocalPlan: 'Wybierz plan lokalny',
    skipDuplicates: 'Pomiń duplikaty',
    skipInactive: 'Pomiń nieaktywne',
    cancel: 'Anuluj',
    syncAndImport: 'Synchronizuj i importuj',
    importBtn: 'Importuj',
    step4: '4. Import zakończony',
    importSuccess: 'Import zakończony pomyślnie!',
    importedKeys: 'Zaimportowane klucze',
    skippedKeys: 'Pominięte klucze',
    failedKeys: 'Nieudane klucze',
    createdClients: 'Utworzeni klienci',
    errors: 'Błędy:',
    newImport: 'Nowy import',
  },
  de: {
    title: 'Gravitee-Import',
    subtitle: 'Abonnements und API-Schlüssel aus Gravitee importieren',
    modeApi: 'Mit Gravitee-API synchronisieren',
    modeFile: 'Aus JSON-Datei importieren',
    loadPlansFailed: 'Pläne konnten nicht geladen werden',
    fileMustBeJson: 'Die Datei muss im JSON-Format sein',
    fileInvalid: 'Ungültige Datei',
    allPlansMustBeMapped: 'Alle Gravitee-Pläne müssen zugeordnet werden',
    selectApiToSync: 'API zum Synchronisieren auswählen',
    step1Api: '1. Mit Gravitee synchronisieren',
    step1ApiSub: 'Daten direkt von der Gravitee-API abrufen',
    connectionStatus: 'Verbindungsstatus',
    testConnection: 'Verbindung testen',
    selectApiLabel: 'API zum Synchronisieren auswählen',
    apiIdPlaceholder: 'Gravitee-API-ID',
    defaultApiHint: 'Standard: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Vorschau von Gravitee abrufen',
    step1File: '1. JSON-Datei laden',
    step1FileSub: 'Die Exportdatei von Gravitee auswählen',
    clickToSelectFile: 'Klicken, um eine JSON-Datei auszuwählen',
    validate: 'Validieren',
    step2: '2. Import-Vorschau',
    step2Sub: 'Die Importstatistiken überprüfen',
    subsToImport: 'Zu importierende Abonnements',
    subsToSkip: 'Zu überspringende Abonnements',
    keysToImport: 'Zu importierende Schlüssel',
    clientsToCreate: 'Zu erstellende Kunden',
    warnings: 'Warnungen:',
    step3: '3. Planzuordnung',
    step3Sub: 'Gravitee-Pläne lokalen Plänen zuordnen',
    graviteePlanLabel: 'Gravitee-Plan:',
    planUnlimited: 'unbegrenzt',
    planOptionFmt: '{{name}} (max.: {{max}} Strings, {{rate}}/min)',
    selectLocalPlan: 'Lokalen Plan auswählen',
    skipDuplicates: 'Duplikate überspringen',
    skipInactive: 'Inaktive überspringen',
    cancel: 'Abbrechen',
    syncAndImport: 'Synchronisieren und importieren',
    importBtn: 'Importieren',
    step4: '4. Import abgeschlossen',
    importSuccess: 'Import erfolgreich abgeschlossen!',
    importedKeys: 'Importierte Schlüssel',
    skippedKeys: 'Übersprungene Schlüssel',
    failedKeys: 'Fehlgeschlagene Schlüssel',
    createdClients: 'Erstellte Kunden',
    errors: 'Fehler:',
    newImport: 'Neuer Import',
  },
  fr: {
    title: 'Import Gravitee',
    subtitle: 'Importer les abonnements et les clés API depuis Gravitee',
    modeApi: "Synchroniser avec l'API Gravitee",
    modeFile: 'Importer depuis un fichier JSON',
    loadPlansFailed: 'Échec du chargement des plans',
    fileMustBeJson: 'Le fichier doit être au format JSON',
    fileInvalid: 'Fichier non valide',
    allPlansMustBeMapped: 'Tous les plans Gravitee doivent être mappés',
    selectApiToSync: 'Sélectionnez une API à synchroniser',
    step1Api: '1. Synchroniser avec Gravitee',
    step1ApiSub: "Récupérer les données directement depuis l'API Gravitee",
    connectionStatus: 'État de la connexion',
    testConnection: 'Tester la connexion',
    selectApiLabel: 'Sélectionnez une API à synchroniser',
    apiIdPlaceholder: "ID de l'API Gravitee",
    defaultApiHint: 'Par défaut : e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Récupérer un aperçu depuis Gravitee',
    step1File: '1. Charger le fichier JSON',
    step1FileSub: "Choisissez le fichier d'export de Gravitee",
    clickToSelectFile: 'Cliquez pour sélectionner un fichier JSON',
    validate: 'Valider',
    step2: "2. Aperçu de l'import",
    step2Sub: "Vérifiez les statistiques de l'import",
    subsToImport: 'Abonnements à importer',
    subsToSkip: 'Abonnements à ignorer',
    keysToImport: 'Clés à importer',
    clientsToCreate: 'Clients à créer',
    warnings: 'Avertissements :',
    step3: '3. Mappage des plans',
    step3Sub: 'Associer les plans Gravitee aux plans locaux',
    graviteePlanLabel: 'Plan Gravitee :',
    planUnlimited: 'illimité',
    planOptionFmt: '{{name}} (max : {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Sélectionnez un plan local',
    skipDuplicates: 'Ignorer les doublons',
    skipInactive: 'Ignorer les inactifs',
    cancel: 'Annuler',
    syncAndImport: 'Synchroniser et importer',
    importBtn: 'Importer',
    step4: '4. Import terminé',
    importSuccess: 'Import terminé avec succès !',
    importedKeys: 'Clés importées',
    skippedKeys: 'Clés ignorées',
    failedKeys: 'Clés échouées',
    createdClients: 'Clients créés',
    errors: 'Erreurs :',
    newImport: 'Nouvel import',
  },
  es: {
    title: 'Importación de Gravitee',
    subtitle: 'Importar suscripciones y claves API desde Gravitee',
    modeApi: 'Sincronizar con la API de Gravitee',
    modeFile: 'Importar desde un archivo JSON',
    loadPlansFailed: 'No se pudieron cargar los planes',
    fileMustBeJson: 'El archivo debe estar en formato JSON',
    fileInvalid: 'Archivo no válido',
    allPlansMustBeMapped: 'Todos los planes de Gravitee deben estar asignados',
    selectApiToSync: 'Selecciona una API para sincronizar',
    step1Api: '1. Sincronizar con Gravitee',
    step1ApiSub: 'Obtener datos directamente de la API de Gravitee',
    connectionStatus: 'Estado de la conexión',
    testConnection: 'Probar conexión',
    selectApiLabel: 'Selecciona una API para sincronizar',
    apiIdPlaceholder: 'ID de la API de Gravitee',
    defaultApiHint: 'Por defecto: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Obtener vista previa de Gravitee',
    step1File: '1. Cargar archivo JSON',
    step1FileSub: 'Elige el archivo de exportación de Gravitee',
    clickToSelectFile: 'Haz clic para seleccionar un archivo JSON',
    validate: 'Validar',
    step2: '2. Vista previa de la importación',
    step2Sub: 'Revisa las estadísticas de la importación',
    subsToImport: 'Suscripciones a importar',
    subsToSkip: 'Suscripciones a omitir',
    keysToImport: 'Claves a importar',
    clientsToCreate: 'Clientes a crear',
    warnings: 'Advertencias:',
    step3: '3. Asignación de planes',
    step3Sub: 'Asignar planes de Gravitee a planes locales',
    graviteePlanLabel: 'Plan de Gravitee:',
    planUnlimited: 'ilimitado',
    planOptionFmt: '{{name}} (máx.: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Selecciona un plan local',
    skipDuplicates: 'Omitir duplicados',
    skipInactive: 'Omitir inactivos',
    cancel: 'Cancelar',
    syncAndImport: 'Sincronizar e importar',
    importBtn: 'Importar',
    step4: '4. Importación completada',
    importSuccess: '¡Importación completada con éxito!',
    importedKeys: 'Claves importadas',
    skippedKeys: 'Claves omitidas',
    failedKeys: 'Claves fallidas',
    createdClients: 'Clientes creados',
    errors: 'Errores:',
    newImport: 'Nueva importación',
  },
  it: {
    title: 'Importazione Gravitee',
    subtitle: 'Importa abbonamenti e chiavi API da Gravitee',
    modeApi: "Sincronizza con l'API Gravitee",
    modeFile: 'Importa da file JSON',
    loadPlansFailed: 'Impossibile caricare i piani',
    fileMustBeJson: 'Il file deve essere in formato JSON',
    fileInvalid: 'File non valido',
    allPlansMustBeMapped: 'Tutti i piani Gravitee devono essere mappati',
    selectApiToSync: "Seleziona un'API da sincronizzare",
    step1Api: '1. Sincronizza con Gravitee',
    step1ApiSub: "Recupera i dati direttamente dall'API Gravitee",
    connectionStatus: 'Stato della connessione',
    testConnection: 'Testa connessione',
    selectApiLabel: "Seleziona un'API da sincronizzare",
    apiIdPlaceholder: "ID dell'API Gravitee",
    defaultApiHint: 'Predefinito: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Recupera anteprima da Gravitee',
    step1File: '1. Carica file JSON',
    step1FileSub: 'Scegli il file di esportazione da Gravitee',
    clickToSelectFile: 'Clicca per selezionare un file JSON',
    validate: 'Convalida',
    step2: "2. Anteprima dell'importazione",
    step2Sub: "Controlla le statistiche dell'importazione",
    subsToImport: 'Abbonamenti da importare',
    subsToSkip: 'Abbonamenti da saltare',
    keysToImport: 'Chiavi da importare',
    clientsToCreate: 'Clienti da creare',
    warnings: 'Avvisi:',
    step3: '3. Mappatura dei piani',
    step3Sub: 'Associa i piani Gravitee ai piani locali',
    graviteePlanLabel: 'Piano Gravitee:',
    planUnlimited: 'illimitato',
    planOptionFmt: '{{name}} (max: {{max}} stringhe, {{rate}}/min)',
    selectLocalPlan: 'Seleziona un piano locale',
    skipDuplicates: 'Salta i duplicati',
    skipInactive: 'Salta gli inattivi',
    cancel: 'Annulla',
    syncAndImport: 'Sincronizza e importa',
    importBtn: 'Importa',
    step4: '4. Importazione completata',
    importSuccess: 'Importazione completata con successo!',
    importedKeys: 'Chiavi importate',
    skippedKeys: 'Chiavi saltate',
    failedKeys: 'Chiavi non riuscite',
    createdClients: 'Clienti creati',
    errors: 'Errori:',
    newImport: 'Nuova importazione',
  },
  pt: {
    title: 'Importação do Gravitee',
    subtitle: 'Importar subscrições e chaves API do Gravitee',
    modeApi: 'Sincronizar com a API do Gravitee',
    modeFile: 'Importar de um ficheiro JSON',
    loadPlansFailed: 'Não foi possível carregar os planos',
    fileMustBeJson: 'O ficheiro deve estar em formato JSON',
    fileInvalid: 'Ficheiro inválido',
    allPlansMustBeMapped: 'Todos os planos do Gravitee devem estar mapeados',
    selectApiToSync: 'Selecione uma API para sincronizar',
    step1Api: '1. Sincronizar com o Gravitee',
    step1ApiSub: 'Obter dados diretamente da API do Gravitee',
    connectionStatus: 'Estado da ligação',
    testConnection: 'Testar ligação',
    selectApiLabel: 'Selecione uma API para sincronizar',
    apiIdPlaceholder: 'ID da API do Gravitee',
    defaultApiHint: 'Predefinição: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Obter pré-visualização do Gravitee',
    step1File: '1. Carregar ficheiro JSON',
    step1FileSub: 'Escolha o ficheiro de exportação do Gravitee',
    clickToSelectFile: 'Clique para selecionar um ficheiro JSON',
    validate: 'Validar',
    step2: '2. Pré-visualização da importação',
    step2Sub: 'Reveja as estatísticas da importação',
    subsToImport: 'Subscrições a importar',
    subsToSkip: 'Subscrições a ignorar',
    keysToImport: 'Chaves a importar',
    clientsToCreate: 'Clientes a criar',
    warnings: 'Avisos:',
    step3: '3. Mapeamento de planos',
    step3Sub: 'Associar planos do Gravitee a planos locais',
    graviteePlanLabel: 'Plano do Gravitee:',
    planUnlimited: 'ilimitado',
    planOptionFmt: '{{name}} (máx.: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Selecione um plano local',
    skipDuplicates: 'Ignorar duplicados',
    skipInactive: 'Ignorar inativos',
    cancel: 'Cancelar',
    syncAndImport: 'Sincronizar e importar',
    importBtn: 'Importar',
    step4: '4. Importação concluída',
    importSuccess: 'Importação concluída com sucesso!',
    importedKeys: 'Chaves importadas',
    skippedKeys: 'Chaves ignoradas',
    failedKeys: 'Chaves falhadas',
    createdClients: 'Clientes criados',
    errors: 'Erros:',
    newImport: 'Nova importação',
  },
  nl: {
    title: 'Gravitee-import',
    subtitle: 'Abonnementen en API-sleutels uit Gravitee importeren',
    modeApi: 'Synchroniseren met de Gravitee-API',
    modeFile: 'Importeren uit JSON-bestand',
    loadPlansFailed: 'Kan plannen niet laden',
    fileMustBeJson: 'Het bestand moet in JSON-formaat zijn',
    fileInvalid: 'Ongeldig bestand',
    allPlansMustBeMapped: 'Alle Gravitee-plannen moeten worden toegewezen',
    selectApiToSync: 'Selecteer een API om te synchroniseren',
    step1Api: '1. Synchroniseren met Gravitee',
    step1ApiSub: 'Gegevens rechtstreeks van de Gravitee-API ophalen',
    connectionStatus: 'Verbindingsstatus',
    testConnection: 'Verbinding testen',
    selectApiLabel: 'Selecteer een API om te synchroniseren',
    apiIdPlaceholder: 'Gravitee-API-ID',
    defaultApiHint: 'Standaard: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Voorbeeld ophalen uit Gravitee',
    step1File: '1. JSON-bestand laden',
    step1FileSub: 'Kies het exportbestand van Gravitee',
    clickToSelectFile: 'Klik om een JSON-bestand te selecteren',
    validate: 'Valideren',
    step2: '2. Importvoorbeeld',
    step2Sub: 'Bekijk de importstatistieken',
    subsToImport: 'Te importeren abonnementen',
    subsToSkip: 'Over te slaan abonnementen',
    keysToImport: 'Te importeren sleutels',
    clientsToCreate: 'Aan te maken klanten',
    warnings: 'Waarschuwingen:',
    step3: '3. Plantoewijzing',
    step3Sub: 'Gravitee-plannen toewijzen aan lokale plannen',
    graviteePlanLabel: 'Gravitee-plan:',
    planUnlimited: 'onbeperkt',
    planOptionFmt: '{{name}} (max: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Selecteer een lokaal plan',
    skipDuplicates: 'Duplicaten overslaan',
    skipInactive: 'Inactieve overslaan',
    cancel: 'Annuleren',
    syncAndImport: 'Synchroniseren en importeren',
    importBtn: 'Importeren',
    step4: '4. Import voltooid',
    importSuccess: 'Import succesvol voltooid!',
    importedKeys: 'Geïmporteerde sleutels',
    skippedKeys: 'Overgeslagen sleutels',
    failedKeys: 'Mislukte sleutels',
    createdClients: 'Aangemaakte klanten',
    errors: 'Fouten:',
    newImport: 'Nieuwe import',
  },
  da: {
    title: 'Gravitee-import',
    subtitle: 'Importér abonnementer og API-nøgler fra Gravitee',
    modeApi: 'Synkronisér med Gravitee-API',
    modeFile: 'Importér fra JSON-fil',
    loadPlansFailed: 'Kunne ikke indlæse planer',
    fileMustBeJson: 'Filen skal være i JSON-format',
    fileInvalid: 'Ugyldig fil',
    allPlansMustBeMapped: 'Alle Gravitee-planer skal være tilknyttet',
    selectApiToSync: 'Vælg en API at synkronisere',
    step1Api: '1. Synkronisér med Gravitee',
    step1ApiSub: 'Hent data direkte fra Gravitee-API',
    connectionStatus: 'Forbindelsesstatus',
    testConnection: 'Test forbindelse',
    selectApiLabel: 'Vælg en API at synkronisere',
    apiIdPlaceholder: 'Gravitee-API-ID',
    defaultApiHint: 'Standard: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Hent forhåndsvisning fra Gravitee',
    step1File: '1. Indlæs JSON-fil',
    step1FileSub: 'Vælg eksportfilen fra Gravitee',
    clickToSelectFile: 'Klik for at vælge en JSON-fil',
    validate: 'Validér',
    step2: '2. Forhåndsvisning af import',
    step2Sub: 'Gennemgå importstatistikkerne',
    subsToImport: 'Abonnementer at importere',
    subsToSkip: 'Abonnementer at springe over',
    keysToImport: 'Nøgler at importere',
    clientsToCreate: 'Kunder at oprette',
    warnings: 'Advarsler:',
    step3: '3. Plantilknytning',
    step3Sub: 'Tilknyt Gravitee-planer til lokale planer',
    graviteePlanLabel: 'Gravitee-plan:',
    planUnlimited: 'ubegrænset',
    planOptionFmt: '{{name}} (maks.: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Vælg en lokal plan',
    skipDuplicates: 'Spring duplikater over',
    skipInactive: 'Spring inaktive over',
    cancel: 'Annuller',
    syncAndImport: 'Synkronisér og importér',
    importBtn: 'Importér',
    step4: '4. Import fuldført',
    importSuccess: 'Import fuldført!',
    importedKeys: 'Importerede nøgler',
    skippedKeys: 'Oversprungne nøgler',
    failedKeys: 'Mislykkede nøgler',
    createdClients: 'Oprettede kunder',
    errors: 'Fejl:',
    newImport: 'Ny import',
  },
  sv: {
    title: 'Gravitee-import',
    subtitle: 'Importera prenumerationer och API-nycklar från Gravitee',
    modeApi: 'Synkronisera med Gravitee-API',
    modeFile: 'Importera från JSON-fil',
    loadPlansFailed: 'Det gick inte att läsa in planer',
    fileMustBeJson: 'Filen måste vara i JSON-format',
    fileInvalid: 'Ogiltig fil',
    allPlansMustBeMapped: 'Alla Gravitee-planer måste vara mappade',
    selectApiToSync: 'Välj ett API att synkronisera',
    step1Api: '1. Synkronisera med Gravitee',
    step1ApiSub: 'Hämta data direkt från Gravitee-API:et',
    connectionStatus: 'Anslutningsstatus',
    testConnection: 'Testa anslutning',
    selectApiLabel: 'Välj ett API att synkronisera',
    apiIdPlaceholder: 'Gravitee-API-ID',
    defaultApiHint: 'Standard: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Hämta förhandsvisning från Gravitee',
    step1File: '1. Läs in JSON-fil',
    step1FileSub: 'Välj exportfilen från Gravitee',
    clickToSelectFile: 'Klicka för att välja en JSON-fil',
    validate: 'Validera',
    step2: '2. Förhandsvisning av import',
    step2Sub: 'Granska importstatistiken',
    subsToImport: 'Prenumerationer att importera',
    subsToSkip: 'Prenumerationer att hoppa över',
    keysToImport: 'Nycklar att importera',
    clientsToCreate: 'Kunder att skapa',
    warnings: 'Varningar:',
    step3: '3. Planmappning',
    step3Sub: 'Mappa Gravitee-planer till lokala planer',
    graviteePlanLabel: 'Gravitee-plan:',
    planUnlimited: 'obegränsat',
    planOptionFmt: '{{name}} (max: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Välj en lokal plan',
    skipDuplicates: 'Hoppa över dubbletter',
    skipInactive: 'Hoppa över inaktiva',
    cancel: 'Avbryt',
    syncAndImport: 'Synkronisera och importera',
    importBtn: 'Importera',
    step4: '4. Import slutförd',
    importSuccess: 'Import slutförd!',
    importedKeys: 'Importerade nycklar',
    skippedKeys: 'Överhoppade nycklar',
    failedKeys: 'Misslyckade nycklar',
    createdClients: 'Skapade kunder',
    errors: 'Fel:',
    newImport: 'Ny import',
  },
  fi: {
    title: 'Gravitee-tuonti',
    subtitle: 'Tuo tilaukset ja API-avaimet Graviteesta',
    modeApi: 'Synkronoi Gravitee-API:n kanssa',
    modeFile: 'Tuo JSON-tiedostosta',
    loadPlansFailed: 'Suunnitelmien lataaminen epäonnistui',
    fileMustBeJson: 'Tiedoston on oltava JSON-muodossa',
    fileInvalid: 'Virheellinen tiedosto',
    allPlansMustBeMapped: 'Kaikki Gravitee-suunnitelmat on liitettävä',
    selectApiToSync: 'Valitse synkronoitava API',
    step1Api: '1. Synkronoi Graviteen kanssa',
    step1ApiSub: 'Hae tiedot suoraan Gravitee-API:sta',
    connectionStatus: 'Yhteyden tila',
    testConnection: 'Testaa yhteys',
    selectApiLabel: 'Valitse synkronoitava API',
    apiIdPlaceholder: 'Gravitee-API:n tunnus',
    defaultApiHint: 'Oletus: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Hae esikatselu Graviteesta',
    step1File: '1. Lataa JSON-tiedosto',
    step1FileSub: 'Valitse Graviteen vientitiedosto',
    clickToSelectFile: 'Valitse JSON-tiedosto napsauttamalla',
    validate: 'Vahvista',
    step2: '2. Tuonnin esikatselu',
    step2Sub: 'Tarkista tuonnin tilastot',
    subsToImport: 'Tuotavat tilaukset',
    subsToSkip: 'Ohitettavat tilaukset',
    keysToImport: 'Tuotavat avaimet',
    clientsToCreate: 'Luotavat asiakkaat',
    warnings: 'Varoitukset:',
    step3: '3. Suunnitelmien liittäminen',
    step3Sub: 'Liitä Gravitee-suunnitelmat paikallisiin suunnitelmiin',
    graviteePlanLabel: 'Gravitee-suunnitelma:',
    planUnlimited: 'rajoittamaton',
    planOptionFmt: '{{name}} (enint.: {{max}} stringiä, {{rate}}/min)',
    selectLocalPlan: 'Valitse paikallinen suunnitelma',
    skipDuplicates: 'Ohita duplikaatit',
    skipInactive: 'Ohita ei-aktiiviset',
    cancel: 'Peruuta',
    syncAndImport: 'Synkronoi ja tuo',
    importBtn: 'Tuo',
    step4: '4. Tuonti valmis',
    importSuccess: 'Tuonti suoritettu onnistuneesti!',
    importedKeys: 'Tuodut avaimet',
    skippedKeys: 'Ohitetut avaimet',
    failedKeys: 'Epäonnistuneet avaimet',
    createdClients: 'Luodut asiakkaat',
    errors: 'Virheet:',
    newImport: 'Uusi tuonti',
  },
  cs: {
    title: 'Import Gravitee',
    subtitle: 'Importujte předplatná a API klíče z Gravitee',
    modeApi: 'Synchronizovat s Gravitee API',
    modeFile: 'Importovat ze souboru JSON',
    loadPlansFailed: 'Nepodařilo se načíst plány',
    fileMustBeJson: 'Soubor musí být ve formátu JSON',
    fileInvalid: 'Neplatný soubor',
    allPlansMustBeMapped: 'Všechny plány Gravitee musí být namapovány',
    selectApiToSync: 'Vyberte API k synchronizaci',
    step1Api: '1. Synchronizovat s Gravitee',
    step1ApiSub: 'Načíst data přímo z Gravitee API',
    connectionStatus: 'Stav připojení',
    testConnection: 'Otestovat připojení',
    selectApiLabel: 'Vyberte API k synchronizaci',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'Výchozí: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Načíst náhled z Gravitee',
    step1File: '1. Načíst soubor JSON',
    step1FileSub: 'Vyberte exportní soubor z Gravitee',
    clickToSelectFile: 'Klikněte pro výběr souboru JSON',
    validate: 'Ověřit',
    step2: '2. Náhled importu',
    step2Sub: 'Zkontrolujte statistiky importu',
    subsToImport: 'Předplatná k importu',
    subsToSkip: 'Předplatná k přeskočení',
    keysToImport: 'Klíče k importu',
    clientsToCreate: 'Klienti k vytvoření',
    warnings: 'Upozornění:',
    step3: '3. Mapování plánů',
    step3Sub: 'Přiřaďte plány Gravitee k místním plánům',
    graviteePlanLabel: 'Plán Gravitee:',
    planUnlimited: 'neomezené',
    planOptionFmt: '{{name}} (max: {{max}} stringů, {{rate}}/min)',
    selectLocalPlan: 'Vyberte místní plán',
    skipDuplicates: 'Přeskočit duplikáty',
    skipInactive: 'Přeskočit neaktivní',
    cancel: 'Zrušit',
    syncAndImport: 'Synchronizovat a importovat',
    importBtn: 'Importovat',
    step4: '4. Import dokončen',
    importSuccess: 'Import úspěšně dokončen!',
    importedKeys: 'Importované klíče',
    skippedKeys: 'Přeskočené klíče',
    failedKeys: 'Neúspěšné klíče',
    createdClients: 'Vytvoření klienti',
    errors: 'Chyby:',
    newImport: 'Nový import',
  },
  sk: {
    title: 'Import Gravitee',
    subtitle: 'Importujte predplatné a API kľúče z Gravitee',
    modeApi: 'Synchronizovať s Gravitee API',
    modeFile: 'Importovať zo súboru JSON',
    loadPlansFailed: 'Nepodarilo sa načítať plány',
    fileMustBeJson: 'Súbor musí byť vo formáte JSON',
    fileInvalid: 'Neplatný súbor',
    allPlansMustBeMapped: 'Všetky plány Gravitee musia byť namapované',
    selectApiToSync: 'Vyberte API na synchronizáciu',
    step1Api: '1. Synchronizovať s Gravitee',
    step1ApiSub: 'Načítať údaje priamo z Gravitee API',
    connectionStatus: 'Stav pripojenia',
    testConnection: 'Otestovať pripojenie',
    selectApiLabel: 'Vyberte API na synchronizáciu',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'Predvolené: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Načítať náhľad z Gravitee',
    step1File: '1. Načítať súbor JSON',
    step1FileSub: 'Vyberte exportný súbor z Gravitee',
    clickToSelectFile: 'Kliknite pre výber súboru JSON',
    validate: 'Overiť',
    step2: '2. Náhľad importu',
    step2Sub: 'Skontrolujte štatistiky importu',
    subsToImport: 'Predplatné na import',
    subsToSkip: 'Predplatné na preskočenie',
    keysToImport: 'Kľúče na import',
    clientsToCreate: 'Klienti na vytvorenie',
    warnings: 'Upozornenia:',
    step3: '3. Mapovanie plánov',
    step3Sub: 'Priraďte plány Gravitee k miestnym plánom',
    graviteePlanLabel: 'Plán Gravitee:',
    planUnlimited: 'neobmedzené',
    planOptionFmt: '{{name}} (max: {{max}} stringov, {{rate}}/min)',
    selectLocalPlan: 'Vyberte miestny plán',
    skipDuplicates: 'Preskočiť duplikáty',
    skipInactive: 'Preskočiť neaktívne',
    cancel: 'Zrušiť',
    syncAndImport: 'Synchronizovať a importovať',
    importBtn: 'Importovať',
    step4: '4. Import dokončený',
    importSuccess: 'Import úspešne dokončený!',
    importedKeys: 'Importované kľúče',
    skippedKeys: 'Preskočené kľúče',
    failedKeys: 'Neúspešné kľúče',
    createdClients: 'Vytvorení klienti',
    errors: 'Chyby:',
    newImport: 'Nový import',
  },
  sl: {
    title: 'Uvoz Gravitee',
    subtitle: 'Uvozite naročnine in ključe API iz Gravitee',
    modeApi: 'Sinhroniziraj z Gravitee API',
    modeFile: 'Uvozi iz datoteke JSON',
    loadPlansFailed: 'Načrtov ni bilo mogoče naložiti',
    fileMustBeJson: 'Datoteka mora biti v formatu JSON',
    fileInvalid: 'Neveljavna datoteka',
    allPlansMustBeMapped: 'Vsi načrti Gravitee morajo biti preslikani',
    selectApiToSync: 'Izberite API za sinhronizacijo',
    step1Api: '1. Sinhroniziraj z Gravitee',
    step1ApiSub: 'Pridobi podatke neposredno iz Gravitee API',
    connectionStatus: 'Stanje povezave',
    testConnection: 'Preizkusi povezavo',
    selectApiLabel: 'Izberite API za sinhronizacijo',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'Privzeto: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Pridobi predogled iz Gravitee',
    step1File: '1. Naloži datoteko JSON',
    step1FileSub: 'Izberite izvozno datoteko iz Gravitee',
    clickToSelectFile: 'Kliknite za izbiro datoteke JSON',
    validate: 'Preveri',
    step2: '2. Predogled uvoza',
    step2Sub: 'Preglejte statistiko uvoza',
    subsToImport: 'Naročnine za uvoz',
    subsToSkip: 'Naročnine za preskok',
    keysToImport: 'Ključi za uvoz',
    clientsToCreate: 'Stranke za ustvarjanje',
    warnings: 'Opozorila:',
    step3: '3. Preslikava načrtov',
    step3Sub: 'Preslikajte načrte Gravitee na lokalne načrte',
    graviteePlanLabel: 'Načrt Gravitee:',
    planUnlimited: 'neomejeno',
    planOptionFmt: '{{name}} (najv.: {{max}} nizov, {{rate}}/min)',
    selectLocalPlan: 'Izberite lokalni načrt',
    skipDuplicates: 'Preskoči dvojnike',
    skipInactive: 'Preskoči neaktivne',
    cancel: 'Prekliči',
    syncAndImport: 'Sinhroniziraj in uvozi',
    importBtn: 'Uvozi',
    step4: '4. Uvoz dokončan',
    importSuccess: 'Uvoz uspešno dokončan!',
    importedKeys: 'Uvoženi ključi',
    skippedKeys: 'Preskočeni ključi',
    failedKeys: 'Neuspeli ključi',
    createdClients: 'Ustvarjene stranke',
    errors: 'Napake:',
    newImport: 'Nov uvoz',
  },
  hr: {
    title: 'Uvoz Gravitee',
    subtitle: 'Uvezite pretplate i API ključeve iz Gravitee',
    modeApi: 'Sinkroniziraj s Gravitee API',
    modeFile: 'Uvezi iz JSON datoteke',
    loadPlansFailed: 'Nije moguće učitati planove',
    fileMustBeJson: 'Datoteka mora biti u JSON formatu',
    fileInvalid: 'Nevažeća datoteka',
    allPlansMustBeMapped: 'Svi planovi Gravitee moraju biti mapirani',
    selectApiToSync: 'Odaberite API za sinkronizaciju',
    step1Api: '1. Sinkroniziraj s Gravitee',
    step1ApiSub: 'Dohvati podatke izravno iz Gravitee API',
    connectionStatus: 'Status veze',
    testConnection: 'Testiraj vezu',
    selectApiLabel: 'Odaberite API za sinkronizaciju',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'Zadano: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Dohvati pregled iz Gravitee',
    step1File: '1. Učitaj JSON datoteku',
    step1FileSub: 'Odaberite izvoznu datoteku iz Gravitee',
    clickToSelectFile: 'Kliknite za odabir JSON datoteke',
    validate: 'Provjeri',
    step2: '2. Pregled uvoza',
    step2Sub: 'Pregledajte statistiku uvoza',
    subsToImport: 'Pretplate za uvoz',
    subsToSkip: 'Pretplate za preskakanje',
    keysToImport: 'Ključevi za uvoz',
    clientsToCreate: 'Klijenti za stvaranje',
    warnings: 'Upozorenja:',
    step3: '3. Mapiranje planova',
    step3Sub: 'Mapirajte planove Gravitee na lokalne planove',
    graviteePlanLabel: 'Plan Gravitee:',
    planUnlimited: 'neograničeno',
    planOptionFmt: '{{name}} (najv.: {{max}} nizova, {{rate}}/min)',
    selectLocalPlan: 'Odaberite lokalni plan',
    skipDuplicates: 'Preskoči duplikate',
    skipInactive: 'Preskoči neaktivne',
    cancel: 'Odustani',
    syncAndImport: 'Sinkroniziraj i uvezi',
    importBtn: 'Uvezi',
    step4: '4. Uvoz dovršen',
    importSuccess: 'Uvoz uspješno dovršen!',
    importedKeys: 'Uvezeni ključevi',
    skippedKeys: 'Preskočeni ključevi',
    failedKeys: 'Neuspjeli ključevi',
    createdClients: 'Stvoreni klijenti',
    errors: 'Greške:',
    newImport: 'Novi uvoz',
  },
  hu: {
    title: 'Gravitee-import',
    subtitle: 'Előfizetések és API-kulcsok importálása a Gravitee-ből',
    modeApi: 'Szinkronizálás a Gravitee API-val',
    modeFile: 'Importálás JSON-fájlból',
    loadPlansFailed: 'Nem sikerült betölteni a csomagokat',
    fileMustBeJson: 'A fájlnak JSON formátumúnak kell lennie',
    fileInvalid: 'Érvénytelen fájl',
    allPlansMustBeMapped: 'Minden Gravitee-csomagot le kell képezni',
    selectApiToSync: 'Válasszon egy API-t a szinkronizáláshoz',
    step1Api: '1. Szinkronizálás a Gravitee-vel',
    step1ApiSub: 'Adatok lekérése közvetlenül a Gravitee API-ból',
    connectionStatus: 'Kapcsolat állapota',
    testConnection: 'Kapcsolat tesztelése',
    selectApiLabel: 'Válasszon egy API-t a szinkronizáláshoz',
    apiIdPlaceholder: 'Gravitee API azonosító',
    defaultApiHint: 'Alapértelmezett: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Előnézet lekérése a Gravitee-ből',
    step1File: '1. JSON-fájl betöltése',
    step1FileSub: 'Válassza ki a Gravitee exportfájlját',
    clickToSelectFile: 'Kattintson egy JSON-fájl kiválasztásához',
    validate: 'Ellenőrzés',
    step2: '2. Import előnézete',
    step2Sub: 'Tekintse át az import statisztikáit',
    subsToImport: 'Importálandó előfizetések',
    subsToSkip: 'Kihagyandó előfizetések',
    keysToImport: 'Importálandó kulcsok',
    clientsToCreate: 'Létrehozandó ügyfelek',
    warnings: 'Figyelmeztetések:',
    step3: '3. Csomagok leképezése',
    step3Sub: 'Gravitee-csomagok hozzárendelése helyi csomagokhoz',
    graviteePlanLabel: 'Gravitee-csomag:',
    planUnlimited: 'korlátlan',
    planOptionFmt: '{{name}} (max.: {{max}} string, {{rate}}/perc)',
    selectLocalPlan: 'Válasszon helyi csomagot',
    skipDuplicates: 'Duplikátumok kihagyása',
    skipInactive: 'Inaktívak kihagyása',
    cancel: 'Mégse',
    syncAndImport: 'Szinkronizálás és importálás',
    importBtn: 'Importálás',
    step4: '4. Import befejezve',
    importSuccess: 'Az import sikeresen befejeződött!',
    importedKeys: 'Importált kulcsok',
    skippedKeys: 'Kihagyott kulcsok',
    failedKeys: 'Sikertelen kulcsok',
    createdClients: 'Létrehozott ügyfelek',
    errors: 'Hibák:',
    newImport: 'Új import',
  },
  ro: {
    title: 'Import Gravitee',
    subtitle: 'Importați abonamente și chei API din Gravitee',
    modeApi: 'Sincronizează cu API-ul Gravitee',
    modeFile: 'Importă dintr-un fișier JSON',
    loadPlansFailed: 'Nu s-au putut încărca planurile',
    fileMustBeJson: 'Fișierul trebuie să fie în format JSON',
    fileInvalid: 'Fișier nevalid',
    allPlansMustBeMapped: 'Toate planurile Gravitee trebuie mapate',
    selectApiToSync: 'Selectați un API de sincronizat',
    step1Api: '1. Sincronizează cu Gravitee',
    step1ApiSub: 'Preia datele direct din API-ul Gravitee',
    connectionStatus: 'Stare conexiune',
    testConnection: 'Testează conexiunea',
    selectApiLabel: 'Selectați un API de sincronizat',
    apiIdPlaceholder: 'ID-ul API-ului Gravitee',
    defaultApiHint: 'Implicit: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Preia previzualizarea din Gravitee',
    step1File: '1. Încarcă fișierul JSON',
    step1FileSub: 'Alegeți fișierul de export din Gravitee',
    clickToSelectFile: 'Faceți clic pentru a selecta un fișier JSON',
    validate: 'Validează',
    step2: '2. Previzualizare import',
    step2Sub: 'Verificați statisticile importului',
    subsToImport: 'Abonamente de importat',
    subsToSkip: 'Abonamente de omis',
    keysToImport: 'Chei de importat',
    clientsToCreate: 'Clienți de creat',
    warnings: 'Avertismente:',
    step3: '3. Maparea planurilor',
    step3Sub: 'Asociați planurile Gravitee cu planuri locale',
    graviteePlanLabel: 'Plan Gravitee:',
    planUnlimited: 'nelimitat',
    planOptionFmt: '{{name}} (max: {{max}} stringuri, {{rate}}/min)',
    selectLocalPlan: 'Selectați un plan local',
    skipDuplicates: 'Omite duplicatele',
    skipInactive: 'Omite inactivele',
    cancel: 'Anulează',
    syncAndImport: 'Sincronizează și importă',
    importBtn: 'Importă',
    step4: '4. Import finalizat',
    importSuccess: 'Import finalizat cu succes!',
    importedKeys: 'Chei importate',
    skippedKeys: 'Chei omise',
    failedKeys: 'Chei eșuate',
    createdClients: 'Clienți creați',
    errors: 'Erori:',
    newImport: 'Import nou',
  },
  bg: {
    title: 'Импорт от Gravitee',
    subtitle: 'Импортирайте абонаменти и API ключове от Gravitee',
    modeApi: 'Синхронизирай с Gravitee API',
    modeFile: 'Импортирай от JSON файл',
    loadPlansFailed: 'Неуспешно зареждане на плановете',
    fileMustBeJson: 'Файлът трябва да е във формат JSON',
    fileInvalid: 'Невалиден файл',
    allPlansMustBeMapped: 'Всички планове на Gravitee трябва да са съпоставени',
    selectApiToSync: 'Изберете API за синхронизиране',
    step1Api: '1. Синхронизирай с Gravitee',
    step1ApiSub: 'Извлечи данни директно от Gravitee API',
    connectionStatus: 'Състояние на връзката',
    testConnection: 'Тествай връзката',
    selectApiLabel: 'Изберете API за синхронизиране',
    apiIdPlaceholder: 'ID на Gravitee API',
    defaultApiHint: 'По подразбиране: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Извлечи преглед от Gravitee',
    step1File: '1. Зареди JSON файл',
    step1FileSub: 'Изберете файла за експорт от Gravitee',
    clickToSelectFile: 'Кликнете, за да изберете JSON файл',
    validate: 'Валидирай',
    step2: '2. Преглед на импорта',
    step2Sub: 'Прегледайте статистиката на импорта',
    subsToImport: 'Абонаменти за импортиране',
    subsToSkip: 'Абонаменти за пропускане',
    keysToImport: 'Ключове за импортиране',
    clientsToCreate: 'Клиенти за създаване',
    warnings: 'Предупреждения:',
    step3: '3. Съпоставяне на планове',
    step3Sub: 'Съпоставете плановете на Gravitee с локалните планове',
    graviteePlanLabel: 'План на Gravitee:',
    planUnlimited: 'неограничено',
    planOptionFmt: '{{name}} (макс.: {{max}} стринга, {{rate}}/мин)',
    selectLocalPlan: 'Изберете локален план',
    skipDuplicates: 'Пропусни дубликатите',
    skipInactive: 'Пропусни неактивните',
    cancel: 'Отказ',
    syncAndImport: 'Синхронизирай и импортирай',
    importBtn: 'Импортирай',
    step4: '4. Импортът е завършен',
    importSuccess: 'Импортът е завършен успешно!',
    importedKeys: 'Импортирани ключове',
    skippedKeys: 'Пропуснати ключове',
    failedKeys: 'Неуспешни ключове',
    createdClients: 'Създадени клиенти',
    errors: 'Грешки:',
    newImport: 'Нов импорт',
  },
  el: {
    title: 'Εισαγωγή Gravitee',
    subtitle: 'Εισαγάγετε συνδρομές και κλειδιά API από το Gravitee',
    modeApi: 'Συγχρονισμός με το API του Gravitee',
    modeFile: 'Εισαγωγή από αρχείο JSON',
    loadPlansFailed: 'Αποτυχία φόρτωσης των προγραμμάτων',
    fileMustBeJson: 'Το αρχείο πρέπει να είναι σε μορφή JSON',
    fileInvalid: 'Μη έγκυρο αρχείο',
    allPlansMustBeMapped: 'Όλα τα προγράμματα Gravitee πρέπει να αντιστοιχιστούν',
    selectApiToSync: 'Επιλέξτε ένα API για συγχρονισμό',
    step1Api: '1. Συγχρονισμός με το Gravitee',
    step1ApiSub: 'Ανάκτηση δεδομένων απευθείας από το API του Gravitee',
    connectionStatus: 'Κατάσταση σύνδεσης',
    testConnection: 'Δοκιμή σύνδεσης',
    selectApiLabel: 'Επιλέξτε ένα API για συγχρονισμό',
    apiIdPlaceholder: 'ID του API του Gravitee',
    defaultApiHint: 'Προεπιλογή: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Ανάκτηση προεπισκόπησης από το Gravitee',
    step1File: '1. Φόρτωση αρχείου JSON',
    step1FileSub: 'Επιλέξτε το αρχείο εξαγωγής από το Gravitee',
    clickToSelectFile: 'Κάντε κλικ για να επιλέξετε ένα αρχείο JSON',
    validate: 'Επικύρωση',
    step2: '2. Προεπισκόπηση εισαγωγής',
    step2Sub: 'Ελέγξτε τα στατιστικά της εισαγωγής',
    subsToImport: 'Συνδρομές προς εισαγωγή',
    subsToSkip: 'Συνδρομές προς παράλειψη',
    keysToImport: 'Κλειδιά προς εισαγωγή',
    clientsToCreate: 'Πελάτες προς δημιουργία',
    warnings: 'Προειδοποιήσεις:',
    step3: '3. Αντιστοίχιση προγραμμάτων',
    step3Sub: 'Αντιστοιχίστε τα προγράμματα Gravitee με τοπικά προγράμματα',
    graviteePlanLabel: 'Πρόγραμμα Gravitee:',
    planUnlimited: 'απεριόριστο',
    planOptionFmt: '{{name}} (μέγ.: {{max}} strings, {{rate}}/λεπτό)',
    selectLocalPlan: 'Επιλέξτε ένα τοπικό πρόγραμμα',
    skipDuplicates: 'Παράλειψη διπλότυπων',
    skipInactive: 'Παράλειψη ανενεργών',
    cancel: 'Άκυρο',
    syncAndImport: 'Συγχρονισμός και εισαγωγή',
    importBtn: 'Εισαγωγή',
    step4: '4. Η εισαγωγή ολοκληρώθηκε',
    importSuccess: 'Η εισαγωγή ολοκληρώθηκε με επιτυχία!',
    importedKeys: 'Εισαγμένα κλειδιά',
    skippedKeys: 'Παραλειφθέντα κλειδιά',
    failedKeys: 'Αποτυχημένα κλειδιά',
    createdClients: 'Δημιουργημένοι πελάτες',
    errors: 'Σφάλματα:',
    newImport: 'Νέα εισαγωγή',
  },
  et: {
    title: 'Gravitee import',
    subtitle: 'Impordi tellimused ja API-võtmed Gravitee-st',
    modeApi: 'Sünkrooni Gravitee API-ga',
    modeFile: 'Impordi JSON-failist',
    loadPlansFailed: 'Pakettide laadimine ebaõnnestus',
    fileMustBeJson: 'Fail peab olema JSON-vormingus',
    fileInvalid: 'Vigane fail',
    allPlansMustBeMapped: 'Kõik Gravitee paketid peavad olema vastendatud',
    selectApiToSync: 'Vali sünkroonitav API',
    step1Api: '1. Sünkrooni Gravitee-ga',
    step1ApiSub: 'Hangi andmed otse Gravitee API-st',
    connectionStatus: 'Ühenduse olek',
    testConnection: 'Testi ühendust',
    selectApiLabel: 'Vali sünkroonitav API',
    apiIdPlaceholder: 'Gravitee API ID',
    defaultApiHint: 'Vaikimisi: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Hangi eelvaade Gravitee-st',
    step1File: '1. Laadi JSON-fail',
    step1FileSub: 'Vali Gravitee ekspordifail',
    clickToSelectFile: 'Klõpsa JSON-faili valimiseks',
    validate: 'Valideeri',
    step2: '2. Impordi eelvaade',
    step2Sub: 'Vaata impordi statistikat',
    subsToImport: 'Imporditavad tellimused',
    subsToSkip: 'Vahelejäetavad tellimused',
    keysToImport: 'Imporditavad võtmed',
    clientsToCreate: 'Loodavad kliendid',
    warnings: 'Hoiatused:',
    step3: '3. Pakettide vastendamine',
    step3Sub: 'Vastenda Gravitee paketid kohalike pakettidega',
    graviteePlanLabel: 'Gravitee pakett:',
    planUnlimited: 'piiramatu',
    planOptionFmt: '{{name}} (max: {{max}} stringi, {{rate}}/min)',
    selectLocalPlan: 'Vali kohalik pakett',
    skipDuplicates: 'Jäta duplikaadid vahele',
    skipInactive: 'Jäta mitteaktiivsed vahele',
    cancel: 'Tühista',
    syncAndImport: 'Sünkrooni ja impordi',
    importBtn: 'Impordi',
    step4: '4. Import lõpetatud',
    importSuccess: 'Import edukalt lõpetatud!',
    importedKeys: 'Imporditud võtmed',
    skippedKeys: 'Vahelejäetud võtmed',
    failedKeys: 'Ebaõnnestunud võtmed',
    createdClients: 'Loodud kliendid',
    errors: 'Vead:',
    newImport: 'Uus import',
  },
  lv: {
    title: 'Gravitee imports',
    subtitle: 'Importējiet abonementus un API atslēgas no Gravitee',
    modeApi: 'Sinhronizēt ar Gravitee API',
    modeFile: 'Importēt no JSON faila',
    loadPlansFailed: 'Neizdevās ielādēt plānus',
    fileMustBeJson: 'Failam jābūt JSON formātā',
    fileInvalid: 'Nederīgs fails',
    allPlansMustBeMapped: 'Visiem Gravitee plāniem jābūt kartētiem',
    selectApiToSync: 'Atlasiet API sinhronizācijai',
    step1Api: '1. Sinhronizēt ar Gravitee',
    step1ApiSub: 'Iegūt datus tieši no Gravitee API',
    connectionStatus: 'Savienojuma statuss',
    testConnection: 'Pārbaudīt savienojumu',
    selectApiLabel: 'Atlasiet API sinhronizācijai',
    apiIdPlaceholder: 'Gravitee API ID',
    defaultApiHint: 'Noklusējums: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Iegūt priekšskatījumu no Gravitee',
    step1File: '1. Ielādēt JSON failu',
    step1FileSub: 'Izvēlieties Gravitee eksporta failu',
    clickToSelectFile: 'Noklikšķiniet, lai atlasītu JSON failu',
    validate: 'Validēt',
    step2: '2. Importa priekšskatījums',
    step2Sub: 'Pārskatiet importa statistiku',
    subsToImport: 'Importējamie abonementi',
    subsToSkip: 'Izlaižamie abonementi',
    keysToImport: 'Importējamās atslēgas',
    clientsToCreate: 'Izveidojamie klienti',
    warnings: 'Brīdinājumi:',
    step3: '3. Plānu kartēšana',
    step3Sub: 'Kartējiet Gravitee plānus uz vietējiem plāniem',
    graviteePlanLabel: 'Gravitee plāns:',
    planUnlimited: 'neierobežots',
    planOptionFmt: '{{name}} (maks.: {{max}} virknes, {{rate}}/min)',
    selectLocalPlan: 'Atlasiet vietējo plānu',
    skipDuplicates: 'Izlaist dublikātus',
    skipInactive: 'Izlaist neaktīvos',
    cancel: 'Atcelt',
    syncAndImport: 'Sinhronizēt un importēt',
    importBtn: 'Importēt',
    step4: '4. Imports pabeigts',
    importSuccess: 'Imports veiksmīgi pabeigts!',
    importedKeys: 'Importētās atslēgas',
    skippedKeys: 'Izlaistās atslēgas',
    failedKeys: 'Neizdevušās atslēgas',
    createdClients: 'Izveidotie klienti',
    errors: 'Kļūdas:',
    newImport: 'Jauns imports',
  },
  lt: {
    title: 'Gravitee importas',
    subtitle: 'Importuokite prenumeratas ir API raktus iš Gravitee',
    modeApi: 'Sinchronizuoti su Gravitee API',
    modeFile: 'Importuoti iš JSON failo',
    loadPlansFailed: 'Nepavyko įkelti planų',
    fileMustBeJson: 'Failas turi būti JSON formato',
    fileInvalid: 'Netinkamas failas',
    allPlansMustBeMapped: 'Visi Gravitee planai turi būti susieti',
    selectApiToSync: 'Pasirinkite API sinchronizavimui',
    step1Api: '1. Sinchronizuoti su Gravitee',
    step1ApiSub: 'Gauti duomenis tiesiogiai iš Gravitee API',
    connectionStatus: 'Ryšio būsena',
    testConnection: 'Tikrinti ryšį',
    selectApiLabel: 'Pasirinkite API sinchronizavimui',
    apiIdPlaceholder: 'Gravitee API ID',
    defaultApiHint: 'Numatytasis: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Gauti peržiūrą iš Gravitee',
    step1File: '1. Įkelti JSON failą',
    step1FileSub: 'Pasirinkite Gravitee eksporto failą',
    clickToSelectFile: 'Spustelėkite, kad pasirinktumėte JSON failą',
    validate: 'Patvirtinti',
    step2: '2. Importo peržiūra',
    step2Sub: 'Peržiūrėkite importo statistiką',
    subsToImport: 'Importuojamos prenumeratos',
    subsToSkip: 'Praleidžiamos prenumeratos',
    keysToImport: 'Importuojami raktai',
    clientsToCreate: 'Kuriami klientai',
    warnings: 'Įspėjimai:',
    step3: '3. Planų susiejimas',
    step3Sub: 'Susiekite Gravitee planus su vietiniais planais',
    graviteePlanLabel: 'Gravitee planas:',
    planUnlimited: 'neribota',
    planOptionFmt: '{{name}} (daugiausia: {{max}} stringų, {{rate}}/min)',
    selectLocalPlan: 'Pasirinkite vietinį planą',
    skipDuplicates: 'Praleisti dublikatus',
    skipInactive: 'Praleisti neaktyvius',
    cancel: 'Atšaukti',
    syncAndImport: 'Sinchronizuoti ir importuoti',
    importBtn: 'Importuoti',
    step4: '4. Importas baigtas',
    importSuccess: 'Importas sėkmingai baigtas!',
    importedKeys: 'Importuoti raktai',
    skippedKeys: 'Praleisti raktai',
    failedKeys: 'Nepavykę raktai',
    createdClients: 'Sukurti klientai',
    errors: 'Klaidos:',
    newImport: 'Naujas importas',
  },
  mt: {
    title: 'Importazzjoni Gravitee',
    subtitle: 'Importa abbonamenti u ċwievet API minn Gravitee',
    modeApi: "Issinkronizza mal-API ta' Gravitee",
    modeFile: 'Importa minn fajl JSON',
    loadPlansFailed: 'Ma rnexxiex jiġu mgħobbija l-pjanijiet',
    fileMustBeJson: 'Il-fajl irid ikun fil-format JSON',
    fileInvalid: 'Fajl invalidu',
    allPlansMustBeMapped: "Il-pjanijiet kollha ta' Gravitee għandhom jiġu mmappjati",
    selectApiToSync: 'Agħżel API biex tissinkronizza',
    step1Api: "1. Issinkronizza ma' Gravitee",
    step1ApiSub: "Iġbed id-data direttament mill-API ta' Gravitee",
    connectionStatus: 'Status tal-konnessjoni',
    testConnection: 'Ittestja l-konnessjoni',
    selectApiLabel: 'Agħżel API biex tissinkronizza',
    apiIdPlaceholder: "ID tal-API ta' Gravitee",
    defaultApiHint: 'Default: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Iġbed previżjoni minn Gravitee',
    step1File: '1. Għabbi l-fajl JSON',
    step1FileSub: 'Agħżel il-fajl tal-esportazzjoni minn Gravitee',
    clickToSelectFile: 'Ikklikkja biex tagħżel fajl JSON',
    validate: 'Ivvalida',
    step2: '2. Previżjoni tal-importazzjoni',
    step2Sub: 'Iċċekkja l-istatistika tal-importazzjoni',
    subsToImport: 'Abbonamenti biex jiġu importati',
    subsToSkip: 'Abbonamenti biex jinqabżu',
    keysToImport: 'Ċwievet biex jiġu importati',
    clientsToCreate: 'Klijenti biex jinħolqu',
    warnings: 'Twissijiet:',
    step3: '3. Immappjar tal-pjanijiet',
    step3Sub: "Immappja l-pjanijiet ta' Gravitee mal-pjanijiet lokali",
    graviteePlanLabel: "Pjan ta' Gravitee:",
    planUnlimited: 'bla limitu',
    planOptionFmt: '{{name}} (massimu: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Agħżel pjan lokali',
    skipDuplicates: 'Aqbeż id-duplikati',
    skipInactive: 'Aqbeż l-inattivi',
    cancel: 'Ikkanċella',
    syncAndImport: 'Issinkronizza u importa',
    importBtn: 'Importa',
    step4: '4. Importazzjoni kompluta',
    importSuccess: "L-importazzjoni tlestiet b'suċċess!",
    importedKeys: 'Ċwievet importati',
    skippedKeys: 'Ċwievet maqbuża',
    failedKeys: 'Ċwievet falluti',
    createdClients: 'Klijenti maħluqa',
    errors: 'Żbalji:',
    newImport: 'Importazzjoni ġdida',
  },
  ga: {
    title: 'Iompórtáil Gravitee',
    subtitle: 'Iompórtáil síntiúis agus eochracha API ó Gravitee',
    modeApi: 'Sioncronaigh le API Gravitee',
    modeFile: 'Iompórtáil ó chomhad JSON',
    loadPlansFailed: 'Theip ar na pleananna a luchtú',
    fileMustBeJson: 'Caithfidh an comhad a bheith i bhformáid JSON',
    fileInvalid: 'Comhad neamhbhailí',
    allPlansMustBeMapped: 'Caithfear gach plean Gravitee a mhapáil',
    selectApiToSync: 'Roghnaigh API le sioncronú',
    step1Api: '1. Sioncronaigh le Gravitee',
    step1ApiSub: 'Faigh sonraí go díreach ó API Gravitee',
    connectionStatus: 'Stádas an cheangail',
    testConnection: 'Tástáil an ceangal',
    selectApiLabel: 'Roghnaigh API le sioncronú',
    apiIdPlaceholder: 'ID API Gravitee',
    defaultApiHint: 'Réamhshocrú: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Faigh réamhamharc ó Gravitee',
    step1File: '1. Luchtaigh comhad JSON',
    step1FileSub: 'Roghnaigh an comhad easpórtála ó Gravitee',
    clickToSelectFile: 'Cliceáil chun comhad JSON a roghnú',
    validate: 'Bailíochtaigh',
    step2: '2. Réamhamharc iompórtála',
    step2Sub: 'Athbhreithnigh staitisticí na hiompórtála',
    subsToImport: 'Síntiúis le hiompórtáil',
    subsToSkip: 'Síntiúis le scipeáil',
    keysToImport: 'Eochracha le hiompórtáil',
    clientsToCreate: 'Cliaint le cruthú',
    warnings: 'Rabhaidh:',
    step3: '3. Mapáil pleananna',
    step3Sub: 'Mapáil pleananna Gravitee go pleananna áitiúla',
    graviteePlanLabel: 'Plean Gravitee:',
    planUnlimited: 'gan teorainn',
    planOptionFmt: '{{name}} (uas.: {{max}} strings, {{rate}}/nóim)',
    selectLocalPlan: 'Roghnaigh plean áitiúil',
    skipDuplicates: 'Scipeáil dúblaigh',
    skipInactive: 'Scipeáil cinn neamhghníomhacha',
    cancel: 'Cealaigh',
    syncAndImport: 'Sioncronaigh agus iompórtáil',
    importBtn: 'Iompórtáil',
    step4: '4. Iompórtáil críochnaithe',
    importSuccess: "D'éirigh leis an iompórtáil!",
    importedKeys: 'Eochracha iompórtáilte',
    skippedKeys: 'Eochracha scipeáilte',
    failedKeys: 'Eochracha teipthe',
    createdClients: 'Cliaint chruthaithe',
    errors: 'Earráidí:',
    newImport: 'Iompórtáil nua',
  },
  no: {
    title: 'Gravitee-import',
    subtitle: 'Importer abonnementer og API-nøkler fra Gravitee',
    modeApi: 'Synkroniser med Gravitee-API',
    modeFile: 'Importer fra JSON-fil',
    loadPlansFailed: 'Kunne ikke laste planer',
    fileMustBeJson: 'Filen må være i JSON-format',
    fileInvalid: 'Ugyldig fil',
    allPlansMustBeMapped: 'Alle Gravitee-planer må være tilordnet',
    selectApiToSync: 'Velg en API å synkronisere',
    step1Api: '1. Synkroniser med Gravitee',
    step1ApiSub: 'Hent data direkte fra Gravitee-API',
    connectionStatus: 'Tilkoblingsstatus',
    testConnection: 'Test tilkobling',
    selectApiLabel: 'Velg en API å synkronisere',
    apiIdPlaceholder: 'Gravitee-API-ID',
    defaultApiHint: 'Standard: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Hent forhåndsvisning fra Gravitee',
    step1File: '1. Last inn JSON-fil',
    step1FileSub: 'Velg eksportfilen fra Gravitee',
    clickToSelectFile: 'Klikk for å velge en JSON-fil',
    validate: 'Valider',
    step2: '2. Forhåndsvisning av import',
    step2Sub: 'Gå gjennom importstatistikken',
    subsToImport: 'Abonnementer å importere',
    subsToSkip: 'Abonnementer å hoppe over',
    keysToImport: 'Nøkler å importere',
    clientsToCreate: 'Kunder å opprette',
    warnings: 'Advarsler:',
    step3: '3. Plantilordning',
    step3Sub: 'Tilordne Gravitee-planer til lokale planer',
    graviteePlanLabel: 'Gravitee-plan:',
    planUnlimited: 'ubegrenset',
    planOptionFmt: '{{name}} (maks: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Velg en lokal plan',
    skipDuplicates: 'Hopp over duplikater',
    skipInactive: 'Hopp over inaktive',
    cancel: 'Avbryt',
    syncAndImport: 'Synkroniser og importer',
    importBtn: 'Importer',
    step4: '4. Import fullført',
    importSuccess: 'Import fullført!',
    importedKeys: 'Importerte nøkler',
    skippedKeys: 'Hoppet over nøkler',
    failedKeys: 'Mislykkede nøkler',
    createdClients: 'Opprettede kunder',
    errors: 'Feil:',
    newImport: 'Ny import',
  },
  is: {
    title: 'Gravitee-innflutningur',
    subtitle: 'Flyttu inn áskriftir og API-lykla úr Gravitee',
    modeApi: 'Samstilla við Gravitee-API',
    modeFile: 'Flytja inn úr JSON-skrá',
    loadPlansFailed: 'Ekki tókst að hlaða áætlanir',
    fileMustBeJson: 'Skráin verður að vera á JSON-sniði',
    fileInvalid: 'Ógild skrá',
    allPlansMustBeMapped: 'Allar Gravitee-áætlanir verður að varpa',
    selectApiToSync: 'Veldu API til að samstilla',
    step1Api: '1. Samstilla við Gravitee',
    step1ApiSub: 'Sækja gögn beint úr Gravitee-API',
    connectionStatus: 'Staða tengingar',
    testConnection: 'Prófa tengingu',
    selectApiLabel: 'Veldu API til að samstilla',
    apiIdPlaceholder: 'Auðkenni Gravitee-API',
    defaultApiHint: 'Sjálfgefið: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Sækja forskoðun úr Gravitee',
    step1File: '1. Hlaða JSON-skrá',
    step1FileSub: 'Veldu útflutningsskrána úr Gravitee',
    clickToSelectFile: 'Smelltu til að velja JSON-skrá',
    validate: 'Staðfesta',
    step2: '2. Forskoðun innflutnings',
    step2Sub: 'Skoðaðu tölfræði innflutnings',
    subsToImport: 'Áskriftir til innflutnings',
    subsToSkip: 'Áskriftir til að sleppa',
    keysToImport: 'Lyklar til innflutnings',
    clientsToCreate: 'Viðskiptavinir til að búa til',
    warnings: 'Viðvaranir:',
    step3: '3. Vörpun áætlana',
    step3Sub: 'Vörpaðu Gravitee-áætlunum á staðbundnar áætlanir',
    graviteePlanLabel: 'Gravitee-áætlun:',
    planUnlimited: 'ótakmarkað',
    planOptionFmt: '{{name}} (hám.: {{max}} strings, {{rate}}/mín)',
    selectLocalPlan: 'Veldu staðbundna áætlun',
    skipDuplicates: 'Sleppa tvíteknum',
    skipInactive: 'Sleppa óvirkum',
    cancel: 'Hætta við',
    syncAndImport: 'Samstilla og flytja inn',
    importBtn: 'Flytja inn',
    step4: '4. Innflutningi lokið',
    importSuccess: 'Innflutningi lokið!',
    importedKeys: 'Innfluttir lyklar',
    skippedKeys: 'Slepptir lyklar',
    failedKeys: 'Misheppnaðir lyklar',
    createdClients: 'Stofnaðir viðskiptavinir',
    errors: 'Villur:',
    newImport: 'Nýr innflutningur',
  },
  uk: {
    title: 'Імпорт із Gravitee',
    subtitle: 'Імпортуйте підписки та ключі API з Gravitee',
    modeApi: 'Синхронізувати з Gravitee API',
    modeFile: 'Імпортувати з файлу JSON',
    loadPlansFailed: 'Не вдалося завантажити плани',
    fileMustBeJson: 'Файл має бути у форматі JSON',
    fileInvalid: 'Недійсний файл',
    allPlansMustBeMapped: 'Усі плани Gravitee мають бути зіставлені',
    selectApiToSync: 'Виберіть API для синхронізації',
    step1Api: '1. Синхронізувати з Gravitee',
    step1ApiSub: 'Отримати дані безпосередньо з Gravitee API',
    connectionStatus: "Стан з'єднання",
    testConnection: "Перевірити з'єднання",
    selectApiLabel: 'Виберіть API для синхронізації',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'За замовчуванням: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Отримати попередній перегляд з Gravitee',
    step1File: '1. Завантажити файл JSON',
    step1FileSub: 'Виберіть файл експорту з Gravitee',
    clickToSelectFile: 'Натисніть, щоб вибрати файл JSON',
    validate: 'Перевірити',
    step2: '2. Попередній перегляд імпорту',
    step2Sub: 'Перегляньте статистику імпорту',
    subsToImport: 'Підписки для імпорту',
    subsToSkip: 'Підписки для пропуску',
    keysToImport: 'Ключі для імпорту',
    clientsToCreate: 'Клієнти для створення',
    warnings: 'Попередження:',
    step3: '3. Зіставлення планів',
    step3Sub: 'Зіставте плани Gravitee з локальними планами',
    graviteePlanLabel: 'План Gravitee:',
    planUnlimited: 'без обмежень',
    planOptionFmt: '{{name}} (макс.: {{max}} стрінгів, {{rate}}/хв)',
    selectLocalPlan: 'Виберіть локальний план',
    skipDuplicates: 'Пропустити дублікати',
    skipInactive: 'Пропустити неактивні',
    cancel: 'Скасувати',
    syncAndImport: 'Синхронізувати та імпортувати',
    importBtn: 'Імпортувати',
    step4: '4. Імпорт завершено',
    importSuccess: 'Імпорт успішно завершено!',
    importedKeys: 'Імпортовані ключі',
    skippedKeys: 'Пропущені ключі',
    failedKeys: 'Невдалі ключі',
    createdClients: 'Створені клієнти',
    errors: 'Помилки:',
    newImport: 'Новий імпорт',
  },
  sr: {
    title: 'Увоз из Gravitee',
    subtitle: 'Увезите претплате и API кључеве из Gravitee',
    modeApi: 'Синхронизуј са Gravitee API',
    modeFile: 'Увези из JSON датотеке',
    loadPlansFailed: 'Није могуће учитати планове',
    fileMustBeJson: 'Датотека мора бити у JSON формату',
    fileInvalid: 'Неважећа датотека',
    allPlansMustBeMapped: 'Сви Gravitee планови морају бити мапирани',
    selectApiToSync: 'Изаберите API за синхронизацију',
    step1Api: '1. Синхронизуј са Gravitee',
    step1ApiSub: 'Преузми податке директно из Gravitee API',
    connectionStatus: 'Статус везе',
    testConnection: 'Тестирај везу',
    selectApiLabel: 'Изаберите API за синхронизацију',
    apiIdPlaceholder: 'ИД Gravitee API',
    defaultApiHint: 'Подразумевано: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Преузми преглед из Gravitee',
    step1File: '1. Учитај JSON датотеку',
    step1FileSub: 'Изаберите датотеку извоза из Gravitee',
    clickToSelectFile: 'Кликните да изаберете JSON датотеку',
    validate: 'Провери',
    step2: '2. Преглед увоза',
    step2Sub: 'Прегледајте статистику увоза',
    subsToImport: 'Претплате за увоз',
    subsToSkip: 'Претплате за прескакање',
    keysToImport: 'Кључеви за увоз',
    clientsToCreate: 'Клијенти за креирање',
    warnings: 'Упозорења:',
    step3: '3. Мапирање планова',
    step3Sub: 'Мапирајте Gravitee планове на локалне планове',
    graviteePlanLabel: 'Gravitee план:',
    planUnlimited: 'неограничено',
    planOptionFmt: '{{name}} (макс.: {{max}} стрингова, {{rate}}/мин)',
    selectLocalPlan: 'Изаберите локални план',
    skipDuplicates: 'Прескочи дупликате',
    skipInactive: 'Прескочи неактивне',
    cancel: 'Откажи',
    syncAndImport: 'Синхронизуј и увези',
    importBtn: 'Увези',
    step4: '4. Увоз завршен',
    importSuccess: 'Увоз успешно завршен!',
    importedKeys: 'Увезени кључеви',
    skippedKeys: 'Прескочени кључеви',
    failedKeys: 'Неуспели кључеви',
    createdClients: 'Креирани клијенти',
    errors: 'Грешке:',
    newImport: 'Нови увоз',
  },
  sq: {
    title: 'Importi nga Gravitee',
    subtitle: 'Importo abonimet dhe çelësat API nga Gravitee',
    modeApi: 'Sinkronizo me API-në e Gravitee',
    modeFile: 'Importo nga një skedar JSON',
    loadPlansFailed: 'Dështoi ngarkimi i planeve',
    fileMustBeJson: 'Skedari duhet të jetë në formatin JSON',
    fileInvalid: 'Skedar i pavlefshëm',
    allPlansMustBeMapped: 'Të gjitha planet e Gravitee duhet të hartëzohen',
    selectApiToSync: 'Zgjidh një API për të sinkronizuar',
    step1Api: '1. Sinkronizo me Gravitee',
    step1ApiSub: 'Merr të dhënat direkt nga API-ja e Gravitee',
    connectionStatus: 'Statusi i lidhjes',
    testConnection: 'Testo lidhjen',
    selectApiLabel: 'Zgjidh një API për të sinkronizuar',
    apiIdPlaceholder: 'ID-ja e API-së së Gravitee',
    defaultApiHint: 'Parazgjedhja: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Merr paraafishimin nga Gravitee',
    step1File: '1. Ngarko skedarin JSON',
    step1FileSub: 'Zgjidh skedarin e eksportit nga Gravitee',
    clickToSelectFile: 'Kliko për të zgjedhur një skedar JSON',
    validate: 'Validizo',
    step2: '2. Paraafishimi i importit',
    step2Sub: 'Shqyrto statistikat e importit',
    subsToImport: "Abonime për t'u importuar",
    subsToSkip: "Abonime për t'u anashkaluar",
    keysToImport: "Çelësa për t'u importuar",
    clientsToCreate: "Klientë për t'u krijuar",
    warnings: 'Paralajmërime:',
    step3: '3. Hartëzimi i planeve',
    step3Sub: 'Hartëzo planet e Gravitee me planet lokale',
    graviteePlanLabel: 'Plani i Gravitee:',
    planUnlimited: 'i pakufizuar',
    planOptionFmt: '{{name}} (maks.: {{max}} strings, {{rate}}/min)',
    selectLocalPlan: 'Zgjidh një plan lokal',
    skipDuplicates: 'Anashkalo dublikatat',
    skipInactive: 'Anashkalo joaktivët',
    cancel: 'Anulo',
    syncAndImport: 'Sinkronizo dhe importo',
    importBtn: 'Importo',
    step4: '4. Importi u përfundua',
    importSuccess: 'Importi u përfundua me sukses!',
    importedKeys: 'Çelësat e importuar',
    skippedKeys: 'Çelësat e anashkaluar',
    failedKeys: 'Çelësat e dështuar',
    createdClients: 'Klientët e krijuar',
    errors: 'Gabime:',
    newImport: 'Import i ri',
  },
  mk: {
    title: 'Увоз од Gravitee',
    subtitle: 'Увезете претплати и API клучеви од Gravitee',
    modeApi: 'Синхронизирај со Gravitee API',
    modeFile: 'Увези од JSON датотека',
    loadPlansFailed: 'Не може да се вчитаат плановите',
    fileMustBeJson: 'Датотеката мора да биде во JSON формат',
    fileInvalid: 'Неважечка датотека',
    allPlansMustBeMapped: 'Сите Gravitee планови мора да бидат мапирани',
    selectApiToSync: 'Изберете API за синхронизирање',
    step1Api: '1. Синхронизирај со Gravitee',
    step1ApiSub: 'Преземи податоци директно од Gravitee API',
    connectionStatus: 'Статус на врската',
    testConnection: 'Тестирај ја врската',
    selectApiLabel: 'Изберете API за синхронизирање',
    apiIdPlaceholder: 'ID на Gravitee API',
    defaultApiHint: 'Стандардно: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Преземи преглед од Gravitee',
    step1File: '1. Вчитај JSON датотека',
    step1FileSub: 'Изберете ја датотеката за извоз од Gravitee',
    clickToSelectFile: 'Кликнете за да изберете JSON датотека',
    validate: 'Провери',
    step2: '2. Преглед на увозот',
    step2Sub: 'Прегледајте ја статистиката на увозот',
    subsToImport: 'Претплати за увоз',
    subsToSkip: 'Претплати за прескокнување',
    keysToImport: 'Клучеви за увоз',
    clientsToCreate: 'Клиенти за создавање',
    warnings: 'Предупредувања:',
    step3: '3. Мапирање на планови',
    step3Sub: 'Мапирајте ги Gravitee плановите на локални планови',
    graviteePlanLabel: 'Gravitee план:',
    planUnlimited: 'неограничено',
    planOptionFmt: '{{name}} (макс.: {{max}} стрингови, {{rate}}/мин)',
    selectLocalPlan: 'Изберете локален план',
    skipDuplicates: 'Прескокни дупликати',
    skipInactive: 'Прескокни неактивни',
    cancel: 'Откажи',
    syncAndImport: 'Синхронизирај и увези',
    importBtn: 'Увези',
    step4: '4. Увозот е завршен',
    importSuccess: 'Увозот е завршен успешно!',
    importedKeys: 'Увезени клучеви',
    skippedKeys: 'Прескокнати клучеви',
    failedKeys: 'Неуспешни клучеви',
    createdClients: 'Создадени клиенти',
    errors: 'Грешки:',
    newImport: 'Нов увоз',
  },
  bs: {
    title: 'Uvoz iz Gravitee',
    subtitle: 'Uvezite pretplate i API ključeve iz Gravitee',
    modeApi: 'Sinhronizuj sa Gravitee API',
    modeFile: 'Uvezi iz JSON datoteke',
    loadPlansFailed: 'Nije moguće učitati planove',
    fileMustBeJson: 'Datoteka mora biti u JSON formatu',
    fileInvalid: 'Nevažeća datoteka',
    allPlansMustBeMapped: 'Svi Gravitee planovi moraju biti mapirani',
    selectApiToSync: 'Odaberite API za sinhronizaciju',
    step1Api: '1. Sinhronizuj sa Gravitee',
    step1ApiSub: 'Dohvati podatke direktno iz Gravitee API',
    connectionStatus: 'Status veze',
    testConnection: 'Testiraj vezu',
    selectApiLabel: 'Odaberite API za sinhronizaciju',
    apiIdPlaceholder: 'ID Gravitee API',
    defaultApiHint: 'Zadano: e333a72a-472d-4607-b3a7-2a472df60739 (Forecast API)',
    fetchPreview: 'Dohvati pregled iz Gravitee',
    step1File: '1. Učitaj JSON datoteku',
    step1FileSub: 'Odaberite datoteku izvoza iz Gravitee',
    clickToSelectFile: 'Kliknite za odabir JSON datoteke',
    validate: 'Provjeri',
    step2: '2. Pregled uvoza',
    step2Sub: 'Pregledajte statistiku uvoza',
    subsToImport: 'Pretplate za uvoz',
    subsToSkip: 'Pretplate za preskakanje',
    keysToImport: 'Ključevi za uvoz',
    clientsToCreate: 'Klijenti za kreiranje',
    warnings: 'Upozorenja:',
    step3: '3. Mapiranje planova',
    step3Sub: 'Mapirajte Gravitee planove na lokalne planove',
    graviteePlanLabel: 'Gravitee plan:',
    planUnlimited: 'neograničeno',
    planOptionFmt: '{{name}} (najv.: {{max}} nizova, {{rate}}/min)',
    selectLocalPlan: 'Odaberite lokalni plan',
    skipDuplicates: 'Preskoči duplikate',
    skipInactive: 'Preskoči neaktivne',
    cancel: 'Otkaži',
    syncAndImport: 'Sinhronizuj i uvezi',
    importBtn: 'Uvezi',
    step4: '4. Uvoz dovršen',
    importSuccess: 'Uvoz uspješno dovršen!',
    importedKeys: 'Uvezeni ključevi',
    skippedKeys: 'Preskočeni ključevi',
    failedKeys: 'Neuspjeli ključevi',
    createdClients: 'Kreirani klijenti',
    errors: 'Greške:',
    newImport: 'Novi uvoz',
  },
};

const cpwTr: Record<string, Dict> = {
  en: {
    title: 'Change password',
    cancel: 'Cancel',
    changeBtn: 'Change password',
    mismatch: 'The new password and confirmation do not match.',
    tooShort: 'The new password must be at least 8 characters.',
    success: 'The password has been changed successfully.',
    currentLabel: 'Current password',
    currentPh: 'Enter your current password',
    newLabel: 'New password',
    newPh: 'Enter a new password (min. 8 characters)',
    newHelp: 'The password must be at least 8 characters',
    confirmLabel: 'Confirm new password',
    confirmPh: 'Re-enter the new password',
  },
  pl: {
    title: 'Zmień hasło',
    cancel: 'Anuluj',
    changeBtn: 'Zmień hasło',
    mismatch: 'Nowe hasło i potwierdzenie hasła nie są identyczne.',
    tooShort: 'Nowe hasło musi mieć co najmniej 8 znaków.',
    success: 'Hasło zostało pomyślnie zmienione.',
    currentLabel: 'Obecne hasło',
    currentPh: 'Wpisz obecne hasło',
    newLabel: 'Nowe hasło',
    newPh: 'Wpisz nowe hasło (min. 8 znaków)',
    newHelp: 'Hasło musi mieć co najmniej 8 znaków',
    confirmLabel: 'Potwierdź nowe hasło',
    confirmPh: 'Wpisz ponownie nowe hasło',
  },
  de: {
    title: 'Passwort ändern',
    cancel: 'Abbrechen',
    changeBtn: 'Passwort ändern',
    mismatch: 'Das neue Passwort und die Bestätigung stimmen nicht überein.',
    tooShort: 'Das neue Passwort muss mindestens 8 Zeichen lang sein.',
    success: 'Das Passwort wurde erfolgreich geändert.',
    currentLabel: 'Aktuelles Passwort',
    currentPh: 'Geben Sie Ihr aktuelles Passwort ein',
    newLabel: 'Neues Passwort',
    newPh: 'Geben Sie ein neues Passwort ein (min. 8 Zeichen)',
    newHelp: 'Das Passwort muss mindestens 8 Zeichen lang sein',
    confirmLabel: 'Neues Passwort bestätigen',
    confirmPh: 'Geben Sie das neue Passwort erneut ein',
  },
  fr: {
    title: 'Changer le mot de passe',
    cancel: 'Annuler',
    changeBtn: 'Changer le mot de passe',
    mismatch: 'Le nouveau mot de passe et la confirmation ne correspondent pas.',
    tooShort: 'Le nouveau mot de passe doit comporter au moins 8 caractères.',
    success: 'Le mot de passe a été changé avec succès.',
    currentLabel: 'Mot de passe actuel',
    currentPh: 'Saisissez votre mot de passe actuel',
    newLabel: 'Nouveau mot de passe',
    newPh: 'Saisissez un nouveau mot de passe (min. 8 caractères)',
    newHelp: 'Le mot de passe doit comporter au moins 8 caractères',
    confirmLabel: 'Confirmer le nouveau mot de passe',
    confirmPh: 'Saisissez à nouveau le nouveau mot de passe',
  },
  es: {
    title: 'Cambiar contraseña',
    cancel: 'Cancelar',
    changeBtn: 'Cambiar contraseña',
    mismatch: 'La nueva contraseña y la confirmación no coinciden.',
    tooShort: 'La nueva contraseña debe tener al menos 8 caracteres.',
    success: 'La contraseña se ha cambiado correctamente.',
    currentLabel: 'Contraseña actual',
    currentPh: 'Introduce tu contraseña actual',
    newLabel: 'Nueva contraseña',
    newPh: 'Introduce una nueva contraseña (mín. 8 caracteres)',
    newHelp: 'La contraseña debe tener al menos 8 caracteres',
    confirmLabel: 'Confirmar nueva contraseña',
    confirmPh: 'Vuelve a introducir la nueva contraseña',
  },
  it: {
    title: 'Cambia password',
    cancel: 'Annulla',
    changeBtn: 'Cambia password',
    mismatch: 'La nuova password e la conferma non corrispondono.',
    tooShort: 'La nuova password deve contenere almeno 8 caratteri.',
    success: 'La password è stata cambiata con successo.',
    currentLabel: 'Password attuale',
    currentPh: 'Inserisci la tua password attuale',
    newLabel: 'Nuova password',
    newPh: 'Inserisci una nuova password (min. 8 caratteri)',
    newHelp: 'La password deve contenere almeno 8 caratteri',
    confirmLabel: 'Conferma nuova password',
    confirmPh: 'Reinserisci la nuova password',
  },
  pt: {
    title: 'Alterar palavra-passe',
    cancel: 'Cancelar',
    changeBtn: 'Alterar palavra-passe',
    mismatch: 'A nova palavra-passe e a confirmação não coincidem.',
    tooShort: 'A nova palavra-passe deve ter pelo menos 8 caracteres.',
    success: 'A palavra-passe foi alterada com sucesso.',
    currentLabel: 'Palavra-passe atual',
    currentPh: 'Insira a sua palavra-passe atual',
    newLabel: 'Nova palavra-passe',
    newPh: 'Insira uma nova palavra-passe (mín. 8 caracteres)',
    newHelp: 'A palavra-passe deve ter pelo menos 8 caracteres',
    confirmLabel: 'Confirmar nova palavra-passe',
    confirmPh: 'Insira novamente a nova palavra-passe',
  },
  nl: {
    title: 'Wachtwoord wijzigen',
    cancel: 'Annuleren',
    changeBtn: 'Wachtwoord wijzigen',
    mismatch: 'Het nieuwe wachtwoord en de bevestiging komen niet overeen.',
    tooShort: 'Het nieuwe wachtwoord moet minstens 8 tekens bevatten.',
    success: 'Het wachtwoord is succesvol gewijzigd.',
    currentLabel: 'Huidig wachtwoord',
    currentPh: 'Voer je huidige wachtwoord in',
    newLabel: 'Nieuw wachtwoord',
    newPh: 'Voer een nieuw wachtwoord in (min. 8 tekens)',
    newHelp: 'Het wachtwoord moet minstens 8 tekens bevatten',
    confirmLabel: 'Nieuw wachtwoord bevestigen',
    confirmPh: 'Voer het nieuwe wachtwoord opnieuw in',
  },
  da: {
    title: 'Skift adgangskode',
    cancel: 'Annuller',
    changeBtn: 'Skift adgangskode',
    mismatch: 'Den nye adgangskode og bekræftelsen stemmer ikke overens.',
    tooShort: 'Den nye adgangskode skal være mindst 8 tegn.',
    success: 'Adgangskoden er blevet ændret.',
    currentLabel: 'Nuværende adgangskode',
    currentPh: 'Indtast din nuværende adgangskode',
    newLabel: 'Ny adgangskode',
    newPh: 'Indtast en ny adgangskode (min. 8 tegn)',
    newHelp: 'Adgangskoden skal være mindst 8 tegn',
    confirmLabel: 'Bekræft ny adgangskode',
    confirmPh: 'Indtast den nye adgangskode igen',
  },
  sv: {
    title: 'Byt lösenord',
    cancel: 'Avbryt',
    changeBtn: 'Byt lösenord',
    mismatch: 'Det nya lösenordet och bekräftelsen matchar inte.',
    tooShort: 'Det nya lösenordet måste vara minst 8 tecken.',
    success: 'Lösenordet har ändrats.',
    currentLabel: 'Nuvarande lösenord',
    currentPh: 'Ange ditt nuvarande lösenord',
    newLabel: 'Nytt lösenord',
    newPh: 'Ange ett nytt lösenord (min. 8 tecken)',
    newHelp: 'Lösenordet måste vara minst 8 tecken',
    confirmLabel: 'Bekräfta nytt lösenord',
    confirmPh: 'Ange det nya lösenordet igen',
  },
  fi: {
    title: 'Vaihda salasana',
    cancel: 'Peruuta',
    changeBtn: 'Vaihda salasana',
    mismatch: 'Uusi salasana ja vahvistus eivät täsmää.',
    tooShort: 'Uuden salasanan on oltava vähintään 8 merkkiä.',
    success: 'Salasana on vaihdettu onnistuneesti.',
    currentLabel: 'Nykyinen salasana',
    currentPh: 'Anna nykyinen salasanasi',
    newLabel: 'Uusi salasana',
    newPh: 'Anna uusi salasana (väh. 8 merkkiä)',
    newHelp: 'Salasanan on oltava vähintään 8 merkkiä',
    confirmLabel: 'Vahvista uusi salasana',
    confirmPh: 'Anna uusi salasana uudelleen',
  },
  cs: {
    title: 'Změnit heslo',
    cancel: 'Zrušit',
    changeBtn: 'Změnit heslo',
    mismatch: 'Nové heslo a potvrzení se neshodují.',
    tooShort: 'Nové heslo musí mít alespoň 8 znaků.',
    success: 'Heslo bylo úspěšně změněno.',
    currentLabel: 'Současné heslo',
    currentPh: 'Zadejte své současné heslo',
    newLabel: 'Nové heslo',
    newPh: 'Zadejte nové heslo (min. 8 znaků)',
    newHelp: 'Heslo musí mít alespoň 8 znaků',
    confirmLabel: 'Potvrďte nové heslo',
    confirmPh: 'Zadejte nové heslo znovu',
  },
  sk: {
    title: 'Zmeniť heslo',
    cancel: 'Zrušiť',
    changeBtn: 'Zmeniť heslo',
    mismatch: 'Nové heslo a potvrdenie sa nezhodujú.',
    tooShort: 'Nové heslo musí mať aspoň 8 znakov.',
    success: 'Heslo bolo úspešne zmenené.',
    currentLabel: 'Aktuálne heslo',
    currentPh: 'Zadajte svoje aktuálne heslo',
    newLabel: 'Nové heslo',
    newPh: 'Zadajte nové heslo (min. 8 znakov)',
    newHelp: 'Heslo musí mať aspoň 8 znakov',
    confirmLabel: 'Potvrďte nové heslo',
    confirmPh: 'Zadajte nové heslo znova',
  },
  sl: {
    title: 'Spremeni geslo',
    cancel: 'Prekliči',
    changeBtn: 'Spremeni geslo',
    mismatch: 'Novo geslo in potrditev se ne ujemata.',
    tooShort: 'Novo geslo mora vsebovati vsaj 8 znakov.',
    success: 'Geslo je bilo uspešno spremenjeno.',
    currentLabel: 'Trenutno geslo',
    currentPh: 'Vnesite svoje trenutno geslo',
    newLabel: 'Novo geslo',
    newPh: 'Vnesite novo geslo (najmanj 8 znakov)',
    newHelp: 'Geslo mora vsebovati vsaj 8 znakov',
    confirmLabel: 'Potrdite novo geslo',
    confirmPh: 'Znova vnesite novo geslo',
  },
  hr: {
    title: 'Promijeni lozinku',
    cancel: 'Odustani',
    changeBtn: 'Promijeni lozinku',
    mismatch: 'Nova lozinka i potvrda se ne podudaraju.',
    tooShort: 'Nova lozinka mora imati najmanje 8 znakova.',
    success: 'Lozinka je uspješno promijenjena.',
    currentLabel: 'Trenutna lozinka',
    currentPh: 'Unesite trenutnu lozinku',
    newLabel: 'Nova lozinka',
    newPh: 'Unesite novu lozinku (najmanje 8 znakova)',
    newHelp: 'Lozinka mora imati najmanje 8 znakova',
    confirmLabel: 'Potvrdite novu lozinku',
    confirmPh: 'Ponovno unesite novu lozinku',
  },
  hu: {
    title: 'Jelszó módosítása',
    cancel: 'Mégse',
    changeBtn: 'Jelszó módosítása',
    mismatch: 'Az új jelszó és a megerősítés nem egyezik.',
    tooShort: 'Az új jelszónak legalább 8 karakterből kell állnia.',
    success: 'A jelszó sikeresen módosítva.',
    currentLabel: 'Jelenlegi jelszó',
    currentPh: 'Adja meg a jelenlegi jelszavát',
    newLabel: 'Új jelszó',
    newPh: 'Adjon meg egy új jelszót (min. 8 karakter)',
    newHelp: 'A jelszónak legalább 8 karakterből kell állnia',
    confirmLabel: 'Új jelszó megerősítése',
    confirmPh: 'Adja meg újra az új jelszót',
  },
  ro: {
    title: 'Schimbă parola',
    cancel: 'Anulează',
    changeBtn: 'Schimbă parola',
    mismatch: 'Parola nouă și confirmarea nu se potrivesc.',
    tooShort: 'Parola nouă trebuie să aibă cel puțin 8 caractere.',
    success: 'Parola a fost schimbată cu succes.',
    currentLabel: 'Parola actuală',
    currentPh: 'Introduceți parola actuală',
    newLabel: 'Parolă nouă',
    newPh: 'Introduceți o parolă nouă (min. 8 caractere)',
    newHelp: 'Parola trebuie să aibă cel puțin 8 caractere',
    confirmLabel: 'Confirmați parola nouă',
    confirmPh: 'Reintroduceți parola nouă',
  },
  bg: {
    title: 'Смяна на парола',
    cancel: 'Отказ',
    changeBtn: 'Смяна на парола',
    mismatch: 'Новата парола и потвърждението не съвпадат.',
    tooShort: 'Новата парола трябва да е поне 8 знака.',
    success: 'Паролата беше успешно сменена.',
    currentLabel: 'Текуща парола',
    currentPh: 'Въведете текущата си парола',
    newLabel: 'Нова парола',
    newPh: 'Въведете нова парола (мин. 8 знака)',
    newHelp: 'Паролата трябва да е поне 8 знака',
    confirmLabel: 'Потвърдете новата парола',
    confirmPh: 'Въведете отново новата парола',
  },
  el: {
    title: 'Αλλαγή κωδικού',
    cancel: 'Άκυρο',
    changeBtn: 'Αλλαγή κωδικού',
    mismatch: 'Ο νέος κωδικός και η επιβεβαίωση δεν ταιριάζουν.',
    tooShort: 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.',
    success: 'Ο κωδικός άλλαξε με επιτυχία.',
    currentLabel: 'Τρέχων κωδικός',
    currentPh: 'Εισαγάγετε τον τρέχοντα κωδικό σας',
    newLabel: 'Νέος κωδικός',
    newPh: 'Εισαγάγετε νέο κωδικό (τουλάχ. 8 χαρακτήρες)',
    newHelp: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες',
    confirmLabel: 'Επιβεβαίωση νέου κωδικού',
    confirmPh: 'Εισαγάγετε ξανά τον νέο κωδικό',
  },
  et: {
    title: 'Muuda parooli',
    cancel: 'Tühista',
    changeBtn: 'Muuda parooli',
    mismatch: 'Uus parool ja kinnitus ei ühti.',
    tooShort: 'Uus parool peab olema vähemalt 8 tähemärki.',
    success: 'Parool on edukalt muudetud.',
    currentLabel: 'Praegune parool',
    currentPh: 'Sisesta oma praegune parool',
    newLabel: 'Uus parool',
    newPh: 'Sisesta uus parool (vähemalt 8 tähemärki)',
    newHelp: 'Parool peab olema vähemalt 8 tähemärki',
    confirmLabel: 'Kinnita uus parool',
    confirmPh: 'Sisesta uus parool uuesti',
  },
  lv: {
    title: 'Mainīt paroli',
    cancel: 'Atcelt',
    changeBtn: 'Mainīt paroli',
    mismatch: 'Jaunā parole un apstiprinājums nesakrīt.',
    tooShort: 'Jaunajai parolei jābūt vismaz 8 rakstzīmēm.',
    success: 'Parole tika veiksmīgi nomainīta.',
    currentLabel: 'Pašreizējā parole',
    currentPh: 'Ievadiet savu pašreizējo paroli',
    newLabel: 'Jauna parole',
    newPh: 'Ievadiet jaunu paroli (vismaz 8 rakstzīmes)',
    newHelp: 'Parolei jābūt vismaz 8 rakstzīmēm',
    confirmLabel: 'Apstipriniet jauno paroli',
    confirmPh: 'Ievadiet jauno paroli vēlreiz',
  },
  lt: {
    title: 'Keisti slaptažodį',
    cancel: 'Atšaukti',
    changeBtn: 'Keisti slaptažodį',
    mismatch: 'Naujas slaptažodis ir patvirtinimas nesutampa.',
    tooShort: 'Naują slaptažodį turi sudaryti bent 8 simboliai.',
    success: 'Slaptažodis sėkmingai pakeistas.',
    currentLabel: 'Dabartinis slaptažodis',
    currentPh: 'Įveskite savo dabartinį slaptažodį',
    newLabel: 'Naujas slaptažodis',
    newPh: 'Įveskite naują slaptažodį (bent 8 simboliai)',
    newHelp: 'Slaptažodį turi sudaryti bent 8 simboliai',
    confirmLabel: 'Patvirtinkite naują slaptažodį',
    confirmPh: 'Įveskite naują slaptažodį dar kartą',
  },
  mt: {
    title: 'Ibdel il-password',
    cancel: 'Ikkanċella',
    changeBtn: 'Ibdel il-password',
    mismatch: 'Il-password il-ġdida u l-konferma ma jaqblux.',
    tooShort: 'Il-password il-ġdida trid tkun mill-inqas 8 karattri.',
    success: "Il-password inbidlet b'suċċess.",
    currentLabel: 'Il-password attwali',
    currentPh: 'Daħħal il-password attwali tiegħek',
    newLabel: 'Password ġdida',
    newPh: 'Daħħal password ġdida (mill-inqas 8 karattri)',
    newHelp: 'Il-password trid tkun mill-inqas 8 karattri',
    confirmLabel: 'Ikkonferma l-password il-ġdida',
    confirmPh: 'Daħħal mill-ġdid il-password il-ġdida',
  },
  ga: {
    title: 'Athraigh focal faire',
    cancel: 'Cealaigh',
    changeBtn: 'Athraigh focal faire',
    mismatch: 'Ní mheaitseálann an focal faire nua agus an deimhniú.',
    tooShort: 'Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire nua.',
    success: 'Athraíodh an focal faire go rathúil.',
    currentLabel: 'Focal faire reatha',
    currentPh: 'Cuir isteach do fhocal faire reatha',
    newLabel: 'Focal faire nua',
    newPh: 'Cuir isteach focal faire nua (8 gcarachtar ar a laghad)',
    newHelp: 'Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire',
    confirmLabel: 'Deimhnigh an focal faire nua',
    confirmPh: 'Cuir isteach an focal faire nua arís',
  },
  no: {
    title: 'Endre passord',
    cancel: 'Avbryt',
    changeBtn: 'Endre passord',
    mismatch: 'Det nye passordet og bekreftelsen samsvarer ikke.',
    tooShort: 'Det nye passordet må være minst 8 tegn.',
    success: 'Passordet er endret.',
    currentLabel: 'Nåværende passord',
    currentPh: 'Skriv inn ditt nåværende passord',
    newLabel: 'Nytt passord',
    newPh: 'Skriv inn et nytt passord (min. 8 tegn)',
    newHelp: 'Passordet må være minst 8 tegn',
    confirmLabel: 'Bekreft nytt passord',
    confirmPh: 'Skriv inn det nye passordet på nytt',
  },
  is: {
    title: 'Breyta lykilorði',
    cancel: 'Hætta við',
    changeBtn: 'Breyta lykilorði',
    mismatch: 'Nýja lykilorðið og staðfestingin passa ekki saman.',
    tooShort: 'Nýja lykilorðið verður að vera að minnsta kosti 8 stafir.',
    success: 'Lykilorðinu var breytt.',
    currentLabel: 'Núverandi lykilorð',
    currentPh: 'Sláðu inn núverandi lykilorð þitt',
    newLabel: 'Nýtt lykilorð',
    newPh: 'Sláðu inn nýtt lykilorð (lágmark 8 stafir)',
    newHelp: 'Lykilorðið verður að vera að minnsta kosti 8 stafir',
    confirmLabel: 'Staðfestu nýtt lykilorð',
    confirmPh: 'Sláðu inn nýja lykilorðið aftur',
  },
  uk: {
    title: 'Змінити пароль',
    cancel: 'Скасувати',
    changeBtn: 'Змінити пароль',
    mismatch: 'Новий пароль і підтвердження не збігаються.',
    tooShort: 'Новий пароль має містити щонайменше 8 символів.',
    success: 'Пароль успішно змінено.',
    currentLabel: 'Поточний пароль',
    currentPh: 'Введіть свій поточний пароль',
    newLabel: 'Новий пароль',
    newPh: 'Введіть новий пароль (мін. 8 символів)',
    newHelp: 'Пароль має містити щонайменше 8 символів',
    confirmLabel: 'Підтвердіть новий пароль',
    confirmPh: 'Введіть новий пароль ще раз',
  },
  sr: {
    title: 'Промени лозинку',
    cancel: 'Откажи',
    changeBtn: 'Промени лозинку',
    mismatch: 'Нова лозинка и потврда се не подударају.',
    tooShort: 'Нова лозинка мора имати најмање 8 знакова.',
    success: 'Лозинка је успешно промењена.',
    currentLabel: 'Тренутна лозинка',
    currentPh: 'Унесите тренутну лозинку',
    newLabel: 'Нова лозинка',
    newPh: 'Унесите нову лозинку (најмање 8 знакова)',
    newHelp: 'Лозинка мора имати најмање 8 знакова',
    confirmLabel: 'Потврдите нову лозинку',
    confirmPh: 'Поново унесите нову лозинку',
  },
  sq: {
    title: 'Ndrysho fjalëkalimin',
    cancel: 'Anulo',
    changeBtn: 'Ndrysho fjalëkalimin',
    mismatch: 'Fjalëkalimi i ri dhe konfirmimi nuk përputhen.',
    tooShort: 'Fjalëkalimi i ri duhet të ketë të paktën 8 karaktere.',
    success: 'Fjalëkalimi u ndryshua me sukses.',
    currentLabel: 'Fjalëkalimi aktual',
    currentPh: 'Fut fjalëkalimin tënd aktual',
    newLabel: 'Fjalëkalim i ri',
    newPh: 'Fut një fjalëkalim të ri (min. 8 karaktere)',
    newHelp: 'Fjalëkalimi duhet të ketë të paktën 8 karaktere',
    confirmLabel: 'Konfirmo fjalëkalimin e ri',
    confirmPh: 'Fut sërish fjalëkalimin e ri',
  },
  mk: {
    title: 'Промени лозинка',
    cancel: 'Откажи',
    changeBtn: 'Промени лозинка',
    mismatch: 'Новата лозинка и потврдата не се совпаѓаат.',
    tooShort: 'Новата лозинка мора да има најмалку 8 знаци.',
    success: 'Лозинката е успешно променета.',
    currentLabel: 'Тековна лозинка',
    currentPh: 'Внесете ја вашата тековна лозинка',
    newLabel: 'Нова лозинка',
    newPh: 'Внесете нова лозинка (најмалку 8 знаци)',
    newHelp: 'Лозинката мора да има најмалку 8 знаци',
    confirmLabel: 'Потврдете ја новата лозинка',
    confirmPh: 'Внесете ја новата лозинка повторно',
  },
  bs: {
    title: 'Promijeni lozinku',
    cancel: 'Otkaži',
    changeBtn: 'Promijeni lozinku',
    mismatch: 'Nova lozinka i potvrda se ne podudaraju.',
    tooShort: 'Nova lozinka mora imati najmanje 8 znakova.',
    success: 'Lozinka je uspješno promijenjena.',
    currentLabel: 'Trenutna lozinka',
    currentPh: 'Unesite trenutnu lozinku',
    newLabel: 'Nova lozinka',
    newPh: 'Unesite novu lozinku (najmanje 8 znakova)',
    newHelp: 'Lozinka mora imati najmanje 8 znakova',
    confirmLabel: 'Potvrdite novu lozinku',
    confirmPh: 'Ponovo unesite novu lozinku',
  },
};

const apiKeysTr: Record<string, Dict> = {
  en: {
    title: 'API key management',
    subtitle: 'Overview and management of all API keys',
    loadFailed: 'Failed to load API keys',
    selectAtLeastOne: 'Select at least one key',
    bulkRevokeTitle: 'Revoke keys',
    bulkRevokeConfirm: 'Are you sure you want to revoke {{count}} keys?',
    bulkOpError: 'Error while performing the operation',
    keyCopied: 'Key copied to clipboard',
    keyExtended: 'Key extended by {{days}} days',
    extendError: 'Error while extending the key',
    revokeTitle: 'Revoke API key',
    revokeConfirm: 'Are you sure you want to revoke the key for {{name}}?',
    revoke: 'Revoke',
    cancel: 'Cancel',
    keyRevoked: 'The key has been revoked',
    revokeError: 'Error while revoking the key',
    keyReactivated: 'The key has been reactivated',
    reactivateError: 'Error while reactivating the key',
    regenerateTitle: 'Regenerate API key',
    regenerateConfirm:
      'Are you sure you want to generate a new key for {{name}}? The old key will stop working.',
    regenerate: 'Regenerate',
    keyRegenerated: 'New key generated and copied to clipboard',
    regenerateError: 'Error while regenerating the key',
    colKey: 'API key',
    colClient: 'Client',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Expires',
    colActions: 'Actions',
    standaloneName: 'Standalone key',
    standaloneDesc1: ' — weather key with no client assignment. Plan: ',
    standaloneDesc2: ' (max 4 strings, dynamic parameters in REST).',
    standaloneCreated: 'Standalone key created. Copy the value now — we will not show it again:',
    errorWord: 'Error',
    addStandaloneBtn: '+ Standalone key',
    syncResult:
      'Sync Gravitee: fetched {{fetched}} keys, added {{imported}}, updated {{updated}}, skipped {{skipped}}. Time: {{ms}}ms',
    syncGraviteeTitle: 'Fetch keys from Gravitee and import as standalone keys (with dedup)',
    syncGraviteeBtn: '⟲ Sync Gravitee',
    searchPlaceholder: 'Search by client name or key…',
    allStatuses: 'All statuses',
    statusActiveOpt: 'Active',
    statusInactiveOpt: 'Inactive',
    statusExpiredOpt: 'Expired',
    exportBtn: 'Export',
    selectedKeys: 'Selected keys: {{count}}',
    chooseAction: 'Choose an action…',
    actExtend: 'Extend validity',
    actActivate: 'Activate',
    clearSelection: 'Clear selection',
    loadingKeys: 'Loading keys…',
    empty: 'No API keys',
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {{cur}} of {{total}}',
    extendModalTitle: 'Extend key validity',
    extendBtn: 'Extend',
    extendFor: 'Extend key validity for:',
    numDays: 'Number of days',
    bulkExtendTitle: 'Extend key validity',
    bulkActivateTitle: 'Activate keys',
    execute: 'Execute',
    selectedN: 'Selected {{count}} keys',
    numDaysExtend: 'Number of days to extend',
    activateInfo: 'The selected keys will be activated.',
    revokeWarn: 'Warning: the selected keys will be revoked and will stop working.',
  },
  pl: {
    title: 'Zarządzanie kluczami API',
    subtitle: 'Przegląd i zarządzanie wszystkimi kluczami API',
    loadFailed: 'Nie udało się załadować kluczy API',
    selectAtLeastOne: 'Wybierz co najmniej jeden klucz',
    bulkRevokeTitle: 'Unieważnij klucze',
    bulkRevokeConfirm: 'Czy na pewno chcesz unieważnić {{count}} kluczy?',
    bulkOpError: 'Błąd podczas wykonywania operacji',
    keyCopied: 'Klucz skopiowany do schowka',
    keyExtended: 'Klucz przedłużony o {{days}} dni',
    extendError: 'Błąd podczas przedłużania klucza',
    revokeTitle: 'Unieważnij klucz API',
    revokeConfirm: 'Czy na pewno chcesz unieważnić klucz dla {{name}}?',
    revoke: 'Unieważnij',
    cancel: 'Anuluj',
    keyRevoked: 'Klucz został unieważniony',
    revokeError: 'Błąd podczas unieważniania klucza',
    keyReactivated: 'Klucz został reaktywowany',
    reactivateError: 'Błąd podczas reaktywacji klucza',
    regenerateTitle: 'Regeneruj klucz API',
    regenerateConfirm:
      'Czy na pewno chcesz wygenerować nowy klucz dla {{name}}? Stary klucz przestanie działać.',
    regenerate: 'Regeneruj',
    keyRegenerated: 'Nowy klucz wygenerowany i skopiowany do schowka',
    regenerateError: 'Błąd podczas regeneracji klucza',
    colKey: 'Klucz API',
    colClient: 'Klient',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Wygasa',
    colActions: 'Akcje',
    standaloneName: 'Standalone key',
    standaloneDesc1: ' — klucz pogodowy bez przypisania do klienta. Plan: ',
    standaloneDesc2: ' (max 4 stringi, parametry dynamiczne w REST).',
    standaloneCreated: 'Standalone key utworzony. Skopiuj wartość teraz — nie pokażemy ponownie:',
    errorWord: 'Błąd',
    addStandaloneBtn: '+ Standalone key',
    syncResult:
      'Sync Gravitee: pobrano {{fetched}} kluczy, dodano {{imported}}, zaktualizowano {{updated}}, pominięto {{skipped}}. Czas: {{ms}}ms',
    syncGraviteeTitle: 'Pobierz klucze z Gravitee i zaimportuj jako standalone keys (z dedup)',
    syncGraviteeBtn: '⟲ Synchronizuj Gravitee',
    searchPlaceholder: 'Szukaj po nazwie klienta lub kluczu…',
    allStatuses: 'Wszystkie statusy',
    statusActiveOpt: 'Aktywne',
    statusInactiveOpt: 'Nieaktywne',
    statusExpiredOpt: 'Wygasłe',
    exportBtn: 'Eksportuj',
    selectedKeys: 'Zaznaczono kluczy: {{count}}',
    chooseAction: 'Wybierz akcję…',
    actExtend: 'Przedłuż ważność',
    actActivate: 'Aktywuj',
    clearSelection: 'Wyczyść zaznaczenie',
    loadingKeys: 'Wczytywanie kluczy…',
    empty: 'Brak kluczy API',
    prev: 'Poprzednia',
    next: 'Następna',
    pageOf: 'Strona {{cur}} z {{total}}',
    extendModalTitle: 'Przedłuż ważność klucza',
    extendBtn: 'Przedłuż',
    extendFor: 'Przedłuż ważność klucza dla:',
    numDays: 'Liczba dni',
    bulkExtendTitle: 'Przedłuż ważność kluczy',
    bulkActivateTitle: 'Aktywuj klucze',
    execute: 'Wykonaj',
    selectedN: 'Wybrano kluczy: {{count}}',
    numDaysExtend: 'Liczba dni przedłużenia',
    activateInfo: 'Wybrane klucze zostaną aktywowane.',
    revokeWarn: 'Uwaga: Wybrane klucze zostaną unieważnione i przestaną działać.',
  },
  de: {
    title: 'API-Schlüssel-Verwaltung',
    subtitle: 'Übersicht und Verwaltung aller API-Schlüssel',
    loadFailed: 'API-Schlüssel konnten nicht geladen werden',
    selectAtLeastOne: 'Wählen Sie mindestens einen Schlüssel aus',
    bulkRevokeTitle: 'Schlüssel widerrufen',
    bulkRevokeConfirm: 'Möchten Sie wirklich {{count}} Schlüssel widerrufen?',
    bulkOpError: 'Fehler beim Ausführen des Vorgangs',
    keyCopied: 'Schlüssel in die Zwischenablage kopiert',
    keyExtended: 'Schlüssel um {{days}} Tage verlängert',
    extendError: 'Fehler beim Verlängern des Schlüssels',
    revokeTitle: 'API-Schlüssel widerrufen',
    revokeConfirm: 'Möchten Sie den Schlüssel für {{name}} wirklich widerrufen?',
    revoke: 'Widerrufen',
    cancel: 'Abbrechen',
    keyRevoked: 'Der Schlüssel wurde widerrufen',
    revokeError: 'Fehler beim Widerrufen des Schlüssels',
    keyReactivated: 'Der Schlüssel wurde reaktiviert',
    reactivateError: 'Fehler beim Reaktivieren des Schlüssels',
    regenerateTitle: 'API-Schlüssel neu generieren',
    regenerateConfirm:
      'Möchten Sie wirklich einen neuen Schlüssel für {{name}} generieren? Der alte Schlüssel funktioniert dann nicht mehr.',
    regenerate: 'Neu generieren',
    keyRegenerated: 'Neuer Schlüssel generiert und in die Zwischenablage kopiert',
    regenerateError: 'Fehler beim Neugenerieren des Schlüssels',
    colKey: 'API-Schlüssel',
    colClient: 'Kunde',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Läuft ab',
    colActions: 'Aktionen',
    standaloneName: 'Standalone-Schlüssel',
    standaloneDesc1: ' — Wetter-Schlüssel ohne Kundenzuordnung. Plan: ',
    standaloneDesc2: ' (max. 4 Strings, dynamische Parameter im REST).',
    standaloneCreated:
      'Standalone-Schlüssel erstellt. Kopieren Sie den Wert jetzt — wir zeigen ihn nicht erneut an:',
    errorWord: 'Fehler',
    addStandaloneBtn: '+ Standalone-Schlüssel',
    syncResult:
      'Sync Gravitee: {{fetched}} Schlüssel abgerufen, {{imported}} hinzugefügt, {{updated}} aktualisiert, {{skipped}} übersprungen. Zeit: {{ms}}ms',
    syncGraviteeTitle:
      'Schlüssel von Gravitee abrufen und als Standalone-Schlüssel importieren (mit Dedup)',
    syncGraviteeBtn: '⟲ Gravitee synchronisieren',
    searchPlaceholder: 'Nach Kundenname oder Schlüssel suchen…',
    allStatuses: 'Alle Status',
    statusActiveOpt: 'Aktiv',
    statusInactiveOpt: 'Inaktiv',
    statusExpiredOpt: 'Abgelaufen',
    exportBtn: 'Exportieren',
    selectedKeys: 'Ausgewählte Schlüssel: {{count}}',
    chooseAction: 'Aktion auswählen…',
    actExtend: 'Gültigkeit verlängern',
    actActivate: 'Aktivieren',
    clearSelection: 'Auswahl aufheben',
    loadingKeys: 'Schlüssel werden geladen…',
    empty: 'Keine API-Schlüssel',
    prev: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {{cur}} von {{total}}',
    extendModalTitle: 'Schlüsselgültigkeit verlängern',
    extendBtn: 'Verlängern',
    extendFor: 'Schlüsselgültigkeit verlängern für:',
    numDays: 'Anzahl der Tage',
    bulkExtendTitle: 'Schlüsselgültigkeit verlängern',
    bulkActivateTitle: 'Schlüssel aktivieren',
    execute: 'Ausführen',
    selectedN: '{{count}} Schlüssel ausgewählt',
    numDaysExtend: 'Anzahl der Verlängerungstage',
    activateInfo: 'Die ausgewählten Schlüssel werden aktiviert.',
    revokeWarn:
      'Achtung: Die ausgewählten Schlüssel werden widerrufen und funktionieren nicht mehr.',
  },
  fr: {
    title: 'Gestion des clés API',
    subtitle: "Vue d'ensemble et gestion de toutes les clés API",
    loadFailed: 'Échec du chargement des clés API',
    selectAtLeastOne: 'Sélectionnez au moins une clé',
    bulkRevokeTitle: 'Révoquer les clés',
    bulkRevokeConfirm: 'Voulez-vous vraiment révoquer {{count}} clés ?',
    bulkOpError: "Erreur lors de l'exécution de l'opération",
    keyCopied: 'Clé copiée dans le presse-papiers',
    keyExtended: 'Clé prolongée de {{days}} jours',
    extendError: 'Erreur lors de la prolongation de la clé',
    revokeTitle: 'Révoquer la clé API',
    revokeConfirm: 'Voulez-vous vraiment révoquer la clé de {{name}} ?',
    revoke: 'Révoquer',
    cancel: 'Annuler',
    keyRevoked: 'La clé a été révoquée',
    revokeError: 'Erreur lors de la révocation de la clé',
    keyReactivated: 'La clé a été réactivée',
    reactivateError: 'Erreur lors de la réactivation de la clé',
    regenerateTitle: 'Régénérer la clé API',
    regenerateConfirm:
      "Voulez-vous vraiment générer une nouvelle clé pour {{name}} ? L'ancienne clé cessera de fonctionner.",
    regenerate: 'Régénérer',
    keyRegenerated: 'Nouvelle clé générée et copiée dans le presse-papiers',
    regenerateError: 'Erreur lors de la régénération de la clé',
    colKey: 'Clé API',
    colClient: 'Client',
    colPlan: 'Plan',
    colStatus: 'Statut',
    colExpires: 'Expire',
    colActions: 'Actions',
    standaloneName: 'Clé autonome',
    standaloneDesc1: ' — clé météo sans affectation à un client. Plan : ',
    standaloneDesc2: ' (max 4 strings, paramètres dynamiques en REST).',
    standaloneCreated:
      "Clé autonome créée. Copiez la valeur maintenant — nous ne l'afficherons plus :",
    errorWord: 'Erreur',
    addStandaloneBtn: '+ Clé autonome',
    syncResult:
      'Sync Gravitee : {{fetched}} clés récupérées, {{imported}} ajoutées, {{updated}} mises à jour, {{skipped}} ignorées. Temps : {{ms}}ms',
    syncGraviteeTitle:
      'Récupérer les clés de Gravitee et les importer comme clés autonomes (avec dédoublonnage)',
    syncGraviteeBtn: '⟲ Synchroniser Gravitee',
    searchPlaceholder: 'Rechercher par nom de client ou clé…',
    allStatuses: 'Tous les statuts',
    statusActiveOpt: 'Actives',
    statusInactiveOpt: 'Inactives',
    statusExpiredOpt: 'Expirées',
    exportBtn: 'Exporter',
    selectedKeys: 'Clés sélectionnées : {{count}}',
    chooseAction: 'Choisir une action…',
    actExtend: 'Prolonger la validité',
    actActivate: 'Activer',
    clearSelection: 'Effacer la sélection',
    loadingKeys: 'Chargement des clés…',
    empty: 'Aucune clé API',
    prev: 'Précédent',
    next: 'Suivant',
    pageOf: 'Page {{cur}} sur {{total}}',
    extendModalTitle: 'Prolonger la validité de la clé',
    extendBtn: 'Prolonger',
    extendFor: 'Prolonger la validité de la clé pour :',
    numDays: 'Nombre de jours',
    bulkExtendTitle: 'Prolonger la validité des clés',
    bulkActivateTitle: 'Activer les clés',
    execute: 'Exécuter',
    selectedN: '{{count}} clés sélectionnées',
    numDaysExtend: 'Nombre de jours de prolongation',
    activateInfo: 'Les clés sélectionnées seront activées.',
    revokeWarn: 'Attention : les clés sélectionnées seront révoquées et cesseront de fonctionner.',
  },
  es: {
    title: 'Gestión de claves API',
    subtitle: 'Resumen y gestión de todas las claves API',
    loadFailed: 'No se pudieron cargar las claves API',
    selectAtLeastOne: 'Selecciona al menos una clave',
    bulkRevokeTitle: 'Revocar claves',
    bulkRevokeConfirm: '¿Seguro que quieres revocar {{count}} claves?',
    bulkOpError: 'Error al realizar la operación',
    keyCopied: 'Clave copiada al portapapeles',
    keyExtended: 'Clave ampliada {{days}} días',
    extendError: 'Error al ampliar la clave',
    revokeTitle: 'Revocar clave API',
    revokeConfirm: '¿Seguro que quieres revocar la clave de {{name}}?',
    revoke: 'Revocar',
    cancel: 'Cancelar',
    keyRevoked: 'La clave ha sido revocada',
    revokeError: 'Error al revocar la clave',
    keyReactivated: 'La clave ha sido reactivada',
    reactivateError: 'Error al reactivar la clave',
    regenerateTitle: 'Regenerar clave API',
    regenerateConfirm:
      '¿Seguro que quieres generar una nueva clave para {{name}}? La clave antigua dejará de funcionar.',
    regenerate: 'Regenerar',
    keyRegenerated: 'Nueva clave generada y copiada al portapapeles',
    regenerateError: 'Error al regenerar la clave',
    colKey: 'Clave API',
    colClient: 'Cliente',
    colPlan: 'Plan',
    colStatus: 'Estado',
    colExpires: 'Caduca',
    colActions: 'Acciones',
    standaloneName: 'Clave independiente',
    standaloneDesc1: ' — clave meteorológica sin asignación a cliente. Plan: ',
    standaloneDesc2: ' (máx. 4 strings, parámetros dinámicos en REST).',
    standaloneCreated:
      'Clave independiente creada. Copia el valor ahora — no lo mostraremos de nuevo:',
    errorWord: 'Error',
    addStandaloneBtn: '+ Clave independiente',
    syncResult:
      'Sync Gravitee: {{fetched}} claves obtenidas, {{imported}} añadidas, {{updated}} actualizadas, {{skipped}} omitidas. Tiempo: {{ms}}ms',
    syncGraviteeTitle:
      'Obtener claves de Gravitee e importarlas como claves independientes (con deduplicación)',
    syncGraviteeBtn: '⟲ Sincronizar Gravitee',
    searchPlaceholder: 'Buscar por nombre de cliente o clave…',
    allStatuses: 'Todos los estados',
    statusActiveOpt: 'Activas',
    statusInactiveOpt: 'Inactivas',
    statusExpiredOpt: 'Caducadas',
    exportBtn: 'Exportar',
    selectedKeys: 'Claves seleccionadas: {{count}}',
    chooseAction: 'Elige una acción…',
    actExtend: 'Ampliar validez',
    actActivate: 'Activar',
    clearSelection: 'Borrar selección',
    loadingKeys: 'Cargando claves…',
    empty: 'Sin claves API',
    prev: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {{cur}} de {{total}}',
    extendModalTitle: 'Ampliar validez de la clave',
    extendBtn: 'Ampliar',
    extendFor: 'Ampliar validez de la clave para:',
    numDays: 'Número de días',
    bulkExtendTitle: 'Ampliar validez de las claves',
    bulkActivateTitle: 'Activar claves',
    execute: 'Ejecutar',
    selectedN: '{{count}} claves seleccionadas',
    numDaysExtend: 'Número de días de ampliación',
    activateInfo: 'Las claves seleccionadas se activarán.',
    revokeWarn: 'Atención: las claves seleccionadas se revocarán y dejarán de funcionar.',
  },
  it: {
    title: 'Gestione delle chiavi API',
    subtitle: 'Panoramica e gestione di tutte le chiavi API',
    loadFailed: 'Impossibile caricare le chiavi API',
    selectAtLeastOne: 'Seleziona almeno una chiave',
    bulkRevokeTitle: 'Revoca chiavi',
    bulkRevokeConfirm: 'Vuoi davvero revocare {{count}} chiavi?',
    bulkOpError: "Errore durante l'esecuzione dell'operazione",
    keyCopied: 'Chiave copiata negli appunti',
    keyExtended: 'Chiave prolungata di {{days}} giorni',
    extendError: 'Errore durante il prolungamento della chiave',
    revokeTitle: 'Revoca chiave API',
    revokeConfirm: 'Vuoi davvero revocare la chiave per {{name}}?',
    revoke: 'Revoca',
    cancel: 'Annulla',
    keyRevoked: 'La chiave è stata revocata',
    revokeError: 'Errore durante la revoca della chiave',
    keyReactivated: 'La chiave è stata riattivata',
    reactivateError: 'Errore durante la riattivazione della chiave',
    regenerateTitle: 'Rigenera chiave API',
    regenerateConfirm:
      'Vuoi davvero generare una nuova chiave per {{name}}? La vecchia chiave smetterà di funzionare.',
    regenerate: 'Rigenera',
    keyRegenerated: 'Nuova chiave generata e copiata negli appunti',
    regenerateError: 'Errore durante la rigenerazione della chiave',
    colKey: 'Chiave API',
    colClient: 'Cliente',
    colPlan: 'Piano',
    colStatus: 'Stato',
    colExpires: 'Scade',
    colActions: 'Azioni',
    standaloneName: 'Chiave standalone',
    standaloneDesc1: ' — chiave meteo senza assegnazione a un cliente. Piano: ',
    standaloneDesc2: ' (max 4 stringhe, parametri dinamici in REST).',
    standaloneCreated:
      'Chiave standalone creata. Copia il valore ora — non lo mostreremo di nuovo:',
    errorWord: 'Errore',
    addStandaloneBtn: '+ Chiave standalone',
    syncResult:
      'Sync Gravitee: {{fetched}} chiavi recuperate, {{imported}} aggiunte, {{updated}} aggiornate, {{skipped}} saltate. Tempo: {{ms}}ms',
    syncGraviteeTitle:
      'Recupera chiavi da Gravitee e importale come chiavi standalone (con deduplica)',
    syncGraviteeBtn: '⟲ Sincronizza Gravitee',
    searchPlaceholder: 'Cerca per nome cliente o chiave…',
    allStatuses: 'Tutti gli stati',
    statusActiveOpt: 'Attive',
    statusInactiveOpt: 'Inattive',
    statusExpiredOpt: 'Scadute',
    exportBtn: 'Esporta',
    selectedKeys: 'Chiavi selezionate: {{count}}',
    chooseAction: "Scegli un'azione…",
    actExtend: 'Prolunga validità',
    actActivate: 'Attiva',
    clearSelection: 'Cancella selezione',
    loadingKeys: 'Caricamento chiavi…',
    empty: 'Nessuna chiave API',
    prev: 'Precedente',
    next: 'Successiva',
    pageOf: 'Pagina {{cur}} di {{total}}',
    extendModalTitle: 'Prolunga validità della chiave',
    extendBtn: 'Prolunga',
    extendFor: 'Prolunga validità della chiave per:',
    numDays: 'Numero di giorni',
    bulkExtendTitle: 'Prolunga validità delle chiavi',
    bulkActivateTitle: 'Attiva chiavi',
    execute: 'Esegui',
    selectedN: '{{count}} chiavi selezionate',
    numDaysExtend: 'Numero di giorni di prolungamento',
    activateInfo: 'Le chiavi selezionate verranno attivate.',
    revokeWarn: 'Attenzione: le chiavi selezionate verranno revocate e smetteranno di funzionare.',
  },
  pt: {
    title: 'Gestão de chaves API',
    subtitle: 'Visão geral e gestão de todas as chaves API',
    loadFailed: 'Não foi possível carregar as chaves API',
    selectAtLeastOne: 'Selecione pelo menos uma chave',
    bulkRevokeTitle: 'Revogar chaves',
    bulkRevokeConfirm: 'Tem a certeza de que quer revogar {{count}} chaves?',
    bulkOpError: 'Erro ao executar a operação',
    keyCopied: 'Chave copiada para a área de transferência',
    keyExtended: 'Chave prolongada por {{days}} dias',
    extendError: 'Erro ao prolongar a chave',
    revokeTitle: 'Revogar chave API',
    revokeConfirm: 'Tem a certeza de que quer revogar a chave de {{name}}?',
    revoke: 'Revogar',
    cancel: 'Cancelar',
    keyRevoked: 'A chave foi revogada',
    revokeError: 'Erro ao revogar a chave',
    keyReactivated: 'A chave foi reativada',
    reactivateError: 'Erro ao reativar a chave',
    regenerateTitle: 'Regenerar chave API',
    regenerateConfirm:
      'Tem a certeza de que quer gerar uma nova chave para {{name}}? A chave antiga deixará de funcionar.',
    regenerate: 'Regenerar',
    keyRegenerated: 'Nova chave gerada e copiada para a área de transferência',
    regenerateError: 'Erro ao regenerar a chave',
    colKey: 'Chave API',
    colClient: 'Cliente',
    colPlan: 'Plano',
    colStatus: 'Estado',
    colExpires: 'Expira',
    colActions: 'Ações',
    standaloneName: 'Chave autónoma',
    standaloneDesc1: ' — chave meteorológica sem atribuição a cliente. Plano: ',
    standaloneDesc2: ' (máx. 4 strings, parâmetros dinâmicos em REST).',
    standaloneCreated: 'Chave autónoma criada. Copie o valor agora — não o mostraremos novamente:',
    errorWord: 'Erro',
    addStandaloneBtn: '+ Chave autónoma',
    syncResult:
      'Sync Gravitee: {{fetched}} chaves obtidas, {{imported}} adicionadas, {{updated}} atualizadas, {{skipped}} ignoradas. Tempo: {{ms}}ms',
    syncGraviteeTitle:
      'Obter chaves do Gravitee e importar como chaves autónomas (com deduplicação)',
    syncGraviteeBtn: '⟲ Sincronizar Gravitee',
    searchPlaceholder: 'Pesquisar por nome do cliente ou chave…',
    allStatuses: 'Todos os estados',
    statusActiveOpt: 'Ativas',
    statusInactiveOpt: 'Inativas',
    statusExpiredOpt: 'Expiradas',
    exportBtn: 'Exportar',
    selectedKeys: 'Chaves selecionadas: {{count}}',
    chooseAction: 'Escolha uma ação…',
    actExtend: 'Prolongar validade',
    actActivate: 'Ativar',
    clearSelection: 'Limpar seleção',
    loadingKeys: 'A carregar chaves…',
    empty: 'Sem chaves API',
    prev: 'Anterior',
    next: 'Seguinte',
    pageOf: 'Página {{cur}} de {{total}}',
    extendModalTitle: 'Prolongar validade da chave',
    extendBtn: 'Prolongar',
    extendFor: 'Prolongar validade da chave para:',
    numDays: 'Número de dias',
    bulkExtendTitle: 'Prolongar validade das chaves',
    bulkActivateTitle: 'Ativar chaves',
    execute: 'Executar',
    selectedN: '{{count}} chaves selecionadas',
    numDaysExtend: 'Número de dias de prolongamento',
    activateInfo: 'As chaves selecionadas serão ativadas.',
    revokeWarn: 'Atenção: as chaves selecionadas serão revogadas e deixarão de funcionar.',
  },
  nl: {
    title: 'API-sleutelbeheer',
    subtitle: 'Overzicht en beheer van alle API-sleutels',
    loadFailed: 'Kan API-sleutels niet laden',
    selectAtLeastOne: 'Selecteer ten minste één sleutel',
    bulkRevokeTitle: 'Sleutels intrekken',
    bulkRevokeConfirm: 'Weet je zeker dat je {{count}} sleutels wilt intrekken?',
    bulkOpError: 'Fout bij het uitvoeren van de bewerking',
    keyCopied: 'Sleutel naar klembord gekopieerd',
    keyExtended: 'Sleutel verlengd met {{days}} dagen',
    extendError: 'Fout bij het verlengen van de sleutel',
    revokeTitle: 'API-sleutel intrekken',
    revokeConfirm: 'Weet je zeker dat je de sleutel voor {{name}} wilt intrekken?',
    revoke: 'Intrekken',
    cancel: 'Annuleren',
    keyRevoked: 'De sleutel is ingetrokken',
    revokeError: 'Fout bij het intrekken van de sleutel',
    keyReactivated: 'De sleutel is opnieuw geactiveerd',
    reactivateError: 'Fout bij het opnieuw activeren van de sleutel',
    regenerateTitle: 'API-sleutel opnieuw genereren',
    regenerateConfirm:
      'Weet je zeker dat je een nieuwe sleutel voor {{name}} wilt genereren? De oude sleutel werkt dan niet meer.',
    regenerate: 'Opnieuw genereren',
    keyRegenerated: 'Nieuwe sleutel gegenereerd en naar klembord gekopieerd',
    regenerateError: 'Fout bij het opnieuw genereren van de sleutel',
    colKey: 'API-sleutel',
    colClient: 'Klant',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Verloopt',
    colActions: 'Acties',
    standaloneName: 'Standalone-sleutel',
    standaloneDesc1: ' — weersleutel zonder klanttoewijzing. Plan: ',
    standaloneDesc2: ' (max 4 strings, dynamische parameters in REST).',
    standaloneCreated:
      'Standalone-sleutel aangemaakt. Kopieer de waarde nu — we tonen hem niet opnieuw:',
    errorWord: 'Fout',
    addStandaloneBtn: '+ Standalone-sleutel',
    syncResult:
      'Sync Gravitee: {{fetched}} sleutels opgehaald, {{imported}} toegevoegd, {{updated}} bijgewerkt, {{skipped}} overgeslagen. Tijd: {{ms}}ms',
    syncGraviteeTitle:
      'Sleutels van Gravitee ophalen en importeren als standalone-sleutels (met dedup)',
    syncGraviteeBtn: '⟲ Gravitee synchroniseren',
    searchPlaceholder: 'Zoeken op klantnaam of sleutel…',
    allStatuses: 'Alle statussen',
    statusActiveOpt: 'Actief',
    statusInactiveOpt: 'Inactief',
    statusExpiredOpt: 'Verlopen',
    exportBtn: 'Exporteren',
    selectedKeys: 'Geselecteerde sleutels: {{count}}',
    chooseAction: 'Kies een actie…',
    actExtend: 'Geldigheid verlengen',
    actActivate: 'Activeren',
    clearSelection: 'Selectie wissen',
    loadingKeys: 'Sleutels laden…',
    empty: 'Geen API-sleutels',
    prev: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {{cur}} van {{total}}',
    extendModalTitle: 'Sleutelgeldigheid verlengen',
    extendBtn: 'Verlengen',
    extendFor: 'Sleutelgeldigheid verlengen voor:',
    numDays: 'Aantal dagen',
    bulkExtendTitle: 'Sleutelgeldigheid verlengen',
    bulkActivateTitle: 'Sleutels activeren',
    execute: 'Uitvoeren',
    selectedN: '{{count}} sleutels geselecteerd',
    numDaysExtend: 'Aantal dagen verlenging',
    activateInfo: 'De geselecteerde sleutels worden geactiveerd.',
    revokeWarn: 'Waarschuwing: de geselecteerde sleutels worden ingetrokken en werken niet meer.',
  },
  da: {
    title: 'Administration af API-nøgler',
    subtitle: 'Oversigt og administration af alle API-nøgler',
    loadFailed: 'Kunne ikke indlæse API-nøgler',
    selectAtLeastOne: 'Vælg mindst én nøgle',
    bulkRevokeTitle: 'Tilbagekald nøgler',
    bulkRevokeConfirm: 'Er du sikker på, at du vil tilbagekalde {{count}} nøgler?',
    bulkOpError: 'Fejl under udførelse af handlingen',
    keyCopied: 'Nøgle kopieret til udklipsholder',
    keyExtended: 'Nøgle forlænget med {{days}} dage',
    extendError: 'Fejl under forlængelse af nøglen',
    revokeTitle: 'Tilbagekald API-nøgle',
    revokeConfirm: 'Er du sikker på, at du vil tilbagekalde nøglen for {{name}}?',
    revoke: 'Tilbagekald',
    cancel: 'Annuller',
    keyRevoked: 'Nøglen er blevet tilbagekaldt',
    revokeError: 'Fejl under tilbagekaldelse af nøglen',
    keyReactivated: 'Nøglen er blevet genaktiveret',
    reactivateError: 'Fejl under genaktivering af nøglen',
    regenerateTitle: 'Regenerér API-nøgle',
    regenerateConfirm:
      'Er du sikker på, at du vil generere en ny nøgle for {{name}}? Den gamle nøgle holder op med at virke.',
    regenerate: 'Regenerér',
    keyRegenerated: 'Ny nøgle genereret og kopieret til udklipsholder',
    regenerateError: 'Fejl under regenerering af nøglen',
    colKey: 'API-nøgle',
    colClient: 'Kunde',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Udløber',
    colActions: 'Handlinger',
    standaloneName: 'Standalone-nøgle',
    standaloneDesc1: ' — vejrnøgle uden kundetilknytning. Plan: ',
    standaloneDesc2: ' (maks. 4 strings, dynamiske parametre i REST).',
    standaloneCreated: 'Standalone-nøgle oprettet. Kopiér værdien nu — vi viser den ikke igen:',
    errorWord: 'Fejl',
    addStandaloneBtn: '+ Standalone-nøgle',
    syncResult:
      'Sync Gravitee: {{fetched}} nøgler hentet, {{imported}} tilføjet, {{updated}} opdateret, {{skipped}} sprunget over. Tid: {{ms}}ms',
    syncGraviteeTitle: 'Hent nøgler fra Gravitee og importér som standalone-nøgler (med dedup)',
    syncGraviteeBtn: '⟲ Synkronisér Gravitee',
    searchPlaceholder: 'Søg efter kundenavn eller nøgle…',
    allStatuses: 'Alle statusser',
    statusActiveOpt: 'Aktive',
    statusInactiveOpt: 'Inaktive',
    statusExpiredOpt: 'Udløbne',
    exportBtn: 'Eksportér',
    selectedKeys: 'Valgte nøgler: {{count}}',
    chooseAction: 'Vælg en handling…',
    actExtend: 'Forlæng gyldighed',
    actActivate: 'Aktivér',
    clearSelection: 'Ryd valg',
    loadingKeys: 'Indlæser nøgler…',
    empty: 'Ingen API-nøgler',
    prev: 'Forrige',
    next: 'Næste',
    pageOf: 'Side {{cur}} af {{total}}',
    extendModalTitle: 'Forlæng nøglens gyldighed',
    extendBtn: 'Forlæng',
    extendFor: 'Forlæng nøglens gyldighed for:',
    numDays: 'Antal dage',
    bulkExtendTitle: 'Forlæng nøglernes gyldighed',
    bulkActivateTitle: 'Aktivér nøgler',
    execute: 'Udfør',
    selectedN: '{{count}} nøgler valgt',
    numDaysExtend: 'Antal dage til forlængelse',
    activateInfo: 'De valgte nøgler bliver aktiveret.',
    revokeWarn: 'Advarsel: de valgte nøgler bliver tilbagekaldt og holder op med at virke.',
  },
  sv: {
    title: 'Hantering av API-nycklar',
    subtitle: 'Översikt och hantering av alla API-nycklar',
    loadFailed: 'Det gick inte att läsa in API-nycklar',
    selectAtLeastOne: 'Välj minst en nyckel',
    bulkRevokeTitle: 'Återkalla nycklar',
    bulkRevokeConfirm: 'Är du säker på att du vill återkalla {{count}} nycklar?',
    bulkOpError: 'Fel vid utförande av åtgärden',
    keyCopied: 'Nyckel kopierad till urklipp',
    keyExtended: 'Nyckel förlängd med {{days}} dagar',
    extendError: 'Fel vid förlängning av nyckeln',
    revokeTitle: 'Återkalla API-nyckel',
    revokeConfirm: 'Är du säker på att du vill återkalla nyckeln för {{name}}?',
    revoke: 'Återkalla',
    cancel: 'Avbryt',
    keyRevoked: 'Nyckeln har återkallats',
    revokeError: 'Fel vid återkallelse av nyckeln',
    keyReactivated: 'Nyckeln har återaktiverats',
    reactivateError: 'Fel vid återaktivering av nyckeln',
    regenerateTitle: 'Återskapa API-nyckel',
    regenerateConfirm:
      'Är du säker på att du vill generera en ny nyckel för {{name}}? Den gamla nyckeln slutar fungera.',
    regenerate: 'Återskapa',
    keyRegenerated: 'Ny nyckel genererad och kopierad till urklipp',
    regenerateError: 'Fel vid återskapande av nyckeln',
    colKey: 'API-nyckel',
    colClient: 'Kund',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Upphör',
    colActions: 'Åtgärder',
    standaloneName: 'Fristående nyckel',
    standaloneDesc1: ' — vädernyckel utan kundkoppling. Plan: ',
    standaloneDesc2: ' (max 4 strings, dynamiska parametrar i REST).',
    standaloneCreated: 'Fristående nyckel skapad. Kopiera värdet nu — vi visar det inte igen:',
    errorWord: 'Fel',
    addStandaloneBtn: '+ Fristående nyckel',
    syncResult:
      'Sync Gravitee: {{fetched}} nycklar hämtade, {{imported}} tillagda, {{updated}} uppdaterade, {{skipped}} överhoppade. Tid: {{ms}}ms',
    syncGraviteeTitle:
      'Hämta nycklar från Gravitee och importera som fristående nycklar (med dedup)',
    syncGraviteeBtn: '⟲ Synkronisera Gravitee',
    searchPlaceholder: 'Sök efter kundnamn eller nyckel…',
    allStatuses: 'Alla statusar',
    statusActiveOpt: 'Aktiva',
    statusInactiveOpt: 'Inaktiva',
    statusExpiredOpt: 'Utgångna',
    exportBtn: 'Exportera',
    selectedKeys: 'Valda nycklar: {{count}}',
    chooseAction: 'Välj en åtgärd…',
    actExtend: 'Förläng giltighet',
    actActivate: 'Aktivera',
    clearSelection: 'Rensa val',
    loadingKeys: 'Läser in nycklar…',
    empty: 'Inga API-nycklar',
    prev: 'Föregående',
    next: 'Nästa',
    pageOf: 'Sida {{cur}} av {{total}}',
    extendModalTitle: 'Förläng nyckelns giltighet',
    extendBtn: 'Förläng',
    extendFor: 'Förläng nyckelns giltighet för:',
    numDays: 'Antal dagar',
    bulkExtendTitle: 'Förläng nycklarnas giltighet',
    bulkActivateTitle: 'Aktivera nycklar',
    execute: 'Utför',
    selectedN: '{{count}} nycklar valda',
    numDaysExtend: 'Antal dagar att förlänga',
    activateInfo: 'De valda nycklarna aktiveras.',
    revokeWarn: 'Varning: de valda nycklarna återkallas och slutar fungera.',
  },
  fi: {
    title: 'API-avainten hallinta',
    subtitle: 'Kaikkien API-avainten yleiskatsaus ja hallinta',
    loadFailed: 'API-avainten lataaminen epäonnistui',
    selectAtLeastOne: 'Valitse vähintään yksi avain',
    bulkRevokeTitle: 'Peruuta avaimet',
    bulkRevokeConfirm: 'Haluatko varmasti peruuttaa {{count}} avainta?',
    bulkOpError: 'Virhe toimintoa suoritettaessa',
    keyCopied: 'Avain kopioitu leikepöydälle',
    keyExtended: 'Avainta jatkettu {{days}} päivällä',
    extendError: 'Virhe avaimen jatkamisessa',
    revokeTitle: 'Peruuta API-avain',
    revokeConfirm: 'Haluatko varmasti peruuttaa avaimen asiakkaalle {{name}}?',
    revoke: 'Peruuta',
    cancel: 'Peruuta',
    keyRevoked: 'Avain on peruutettu',
    revokeError: 'Virhe avaimen peruuttamisessa',
    keyReactivated: 'Avain on aktivoitu uudelleen',
    reactivateError: 'Virhe avaimen uudelleenaktivoinnissa',
    regenerateTitle: 'Luo API-avain uudelleen',
    regenerateConfirm:
      'Haluatko varmasti luoda uuden avaimen asiakkaalle {{name}}? Vanha avain lakkaa toimimasta.',
    regenerate: 'Luo uudelleen',
    keyRegenerated: 'Uusi avain luotu ja kopioitu leikepöydälle',
    regenerateError: 'Virhe avaimen uudelleenluonnissa',
    colKey: 'API-avain',
    colClient: 'Asiakas',
    colPlan: 'Suunnitelma',
    colStatus: 'Tila',
    colExpires: 'Vanhenee',
    colActions: 'Toiminnot',
    standaloneName: 'Itsenäinen avain',
    standaloneDesc1: ' — sääavain ilman asiakasyhdistystä. Suunnitelma: ',
    standaloneDesc2: ' (enint. 4 stringiä, dynaamiset parametrit REST:ssä).',
    standaloneCreated: 'Itsenäinen avain luotu. Kopioi arvo nyt — emme näytä sitä uudelleen:',
    errorWord: 'Virhe',
    addStandaloneBtn: '+ Itsenäinen avain',
    syncResult:
      'Sync Gravitee: haettu {{fetched}} avainta, lisätty {{imported}}, päivitetty {{updated}}, ohitettu {{skipped}}. Aika: {{ms}}ms',
    syncGraviteeTitle: 'Hae avaimet Graviteesta ja tuo itsenäisinä avaimina (deduplikoinnilla)',
    syncGraviteeBtn: '⟲ Synkronoi Gravitee',
    searchPlaceholder: 'Hae asiakkaan nimellä tai avaimella…',
    allStatuses: 'Kaikki tilat',
    statusActiveOpt: 'Aktiiviset',
    statusInactiveOpt: 'Ei-aktiiviset',
    statusExpiredOpt: 'Vanhentuneet',
    exportBtn: 'Vie',
    selectedKeys: 'Valitut avaimet: {{count}}',
    chooseAction: 'Valitse toiminto…',
    actExtend: 'Jatka voimassaoloa',
    actActivate: 'Aktivoi',
    clearSelection: 'Tyhjennä valinta',
    loadingKeys: 'Ladataan avaimia…',
    empty: 'Ei API-avaimia',
    prev: 'Edellinen',
    next: 'Seuraava',
    pageOf: 'Sivu {{cur}} / {{total}}',
    extendModalTitle: 'Jatka avaimen voimassaoloa',
    extendBtn: 'Jatka',
    extendFor: 'Jatka avaimen voimassaoloa asiakkaalle:',
    numDays: 'Päivien määrä',
    bulkExtendTitle: 'Jatka avainten voimassaoloa',
    bulkActivateTitle: 'Aktivoi avaimet',
    execute: 'Suorita',
    selectedN: 'Valittu {{count}} avainta',
    numDaysExtend: 'Jatkopäivien määrä',
    activateInfo: 'Valitut avaimet aktivoidaan.',
    revokeWarn: 'Varoitus: valitut avaimet peruutetaan ja ne lakkaavat toimimasta.',
  },
  cs: {
    title: 'Správa API klíčů',
    subtitle: 'Přehled a správa všech API klíčů',
    loadFailed: 'Nepodařilo se načíst API klíče',
    selectAtLeastOne: 'Vyberte alespoň jeden klíč',
    bulkRevokeTitle: 'Zneplatnit klíče',
    bulkRevokeConfirm: 'Opravdu chcete zneplatnit {{count}} klíčů?',
    bulkOpError: 'Chyba při provádění operace',
    keyCopied: 'Klíč zkopírován do schránky',
    keyExtended: 'Klíč prodloužen o {{days}} dní',
    extendError: 'Chyba při prodlužování klíče',
    revokeTitle: 'Zneplatnit API klíč',
    revokeConfirm: 'Opravdu chcete zneplatnit klíč pro {{name}}?',
    revoke: 'Zneplatnit',
    cancel: 'Zrušit',
    keyRevoked: 'Klíč byl zneplatněn',
    revokeError: 'Chyba při zneplatňování klíče',
    keyReactivated: 'Klíč byl reaktivován',
    reactivateError: 'Chyba při reaktivaci klíče',
    regenerateTitle: 'Regenerovat API klíč',
    regenerateConfirm:
      'Opravdu chcete vygenerovat nový klíč pro {{name}}? Starý klíč přestane fungovat.',
    regenerate: 'Regenerovat',
    keyRegenerated: 'Nový klíč vygenerován a zkopírován do schránky',
    regenerateError: 'Chyba při regeneraci klíče',
    colKey: 'API klíč',
    colClient: 'Klient',
    colPlan: 'Plán',
    colStatus: 'Stav',
    colExpires: 'Vyprší',
    colActions: 'Akce',
    standaloneName: 'Samostatný klíč',
    standaloneDesc1: ' — meteorologický klíč bez přiřazení ke klientovi. Plán: ',
    standaloneDesc2: ' (max 4 stringy, dynamické parametry v REST).',
    standaloneCreated: 'Samostatný klíč vytvořen. Zkopírujte hodnotu nyní — znovu ji nezobrazíme:',
    errorWord: 'Chyba',
    addStandaloneBtn: '+ Samostatný klíč',
    syncResult:
      'Sync Gravitee: načteno {{fetched}} klíčů, přidáno {{imported}}, aktualizováno {{updated}}, přeskočeno {{skipped}}. Čas: {{ms}}ms',
    syncGraviteeTitle: 'Načíst klíče z Gravitee a importovat jako samostatné klíče (s deduplikací)',
    syncGraviteeBtn: '⟲ Synchronizovat Gravitee',
    searchPlaceholder: 'Hledat podle názvu klienta nebo klíče…',
    allStatuses: 'Všechny stavy',
    statusActiveOpt: 'Aktivní',
    statusInactiveOpt: 'Neaktivní',
    statusExpiredOpt: 'Vypršené',
    exportBtn: 'Exportovat',
    selectedKeys: 'Vybrané klíče: {{count}}',
    chooseAction: 'Vyberte akci…',
    actExtend: 'Prodloužit platnost',
    actActivate: 'Aktivovat',
    clearSelection: 'Zrušit výběr',
    loadingKeys: 'Načítání klíčů…',
    empty: 'Žádné API klíče',
    prev: 'Předchozí',
    next: 'Další',
    pageOf: 'Stránka {{cur}} z {{total}}',
    extendModalTitle: 'Prodloužit platnost klíče',
    extendBtn: 'Prodloužit',
    extendFor: 'Prodloužit platnost klíče pro:',
    numDays: 'Počet dní',
    bulkExtendTitle: 'Prodloužit platnost klíčů',
    bulkActivateTitle: 'Aktivovat klíče',
    execute: 'Provést',
    selectedN: 'Vybráno {{count}} klíčů',
    numDaysExtend: 'Počet dní prodloužení',
    activateInfo: 'Vybrané klíče budou aktivovány.',
    revokeWarn: 'Pozor: vybrané klíče budou zneplatněny a přestanou fungovat.',
  },
  sk: {
    title: 'Správa API kľúčov',
    subtitle: 'Prehľad a správa všetkých API kľúčov',
    loadFailed: 'Nepodarilo sa načítať API kľúče',
    selectAtLeastOne: 'Vyberte aspoň jeden kľúč',
    bulkRevokeTitle: 'Zneplatniť kľúče',
    bulkRevokeConfirm: 'Naozaj chcete zneplatniť {{count}} kľúčov?',
    bulkOpError: 'Chyba pri vykonávaní operácie',
    keyCopied: 'Kľúč skopírovaný do schránky',
    keyExtended: 'Kľúč predĺžený o {{days}} dní',
    extendError: 'Chyba pri predlžovaní kľúča',
    revokeTitle: 'Zneplatniť API kľúč',
    revokeConfirm: 'Naozaj chcete zneplatniť kľúč pre {{name}}?',
    revoke: 'Zneplatniť',
    cancel: 'Zrušiť',
    keyRevoked: 'Kľúč bol zneplatnený',
    revokeError: 'Chyba pri zneplatňovaní kľúča',
    keyReactivated: 'Kľúč bol reaktivovaný',
    reactivateError: 'Chyba pri reaktivácii kľúča',
    regenerateTitle: 'Regenerovať API kľúč',
    regenerateConfirm:
      'Naozaj chcete vygenerovať nový kľúč pre {{name}}? Starý kľúč prestane fungovať.',
    regenerate: 'Regenerovať',
    keyRegenerated: 'Nový kľúč vygenerovaný a skopírovaný do schránky',
    regenerateError: 'Chyba pri regenerácii kľúča',
    colKey: 'API kľúč',
    colClient: 'Klient',
    colPlan: 'Plán',
    colStatus: 'Stav',
    colExpires: 'Vyprší',
    colActions: 'Akcie',
    standaloneName: 'Samostatný kľúč',
    standaloneDesc1: ' — meteorologický kľúč bez priradenia ku klientovi. Plán: ',
    standaloneDesc2: ' (max 4 stringy, dynamické parametre v REST).',
    standaloneCreated:
      'Samostatný kľúč vytvorený. Skopírujte hodnotu teraz — znova ju nezobrazíme:',
    errorWord: 'Chyba',
    addStandaloneBtn: '+ Samostatný kľúč',
    syncResult:
      'Sync Gravitee: načítaných {{fetched}} kľúčov, pridaných {{imported}}, aktualizovaných {{updated}}, preskočených {{skipped}}. Čas: {{ms}}ms',
    syncGraviteeTitle:
      'Načítať kľúče z Gravitee a importovať ako samostatné kľúče (s deduplikáciou)',
    syncGraviteeBtn: '⟲ Synchronizovať Gravitee',
    searchPlaceholder: 'Hľadať podľa názvu klienta alebo kľúča…',
    allStatuses: 'Všetky stavy',
    statusActiveOpt: 'Aktívne',
    statusInactiveOpt: 'Neaktívne',
    statusExpiredOpt: 'Vypršané',
    exportBtn: 'Exportovať',
    selectedKeys: 'Vybrané kľúče: {{count}}',
    chooseAction: 'Vyberte akciu…',
    actExtend: 'Predĺžiť platnosť',
    actActivate: 'Aktivovať',
    clearSelection: 'Zrušiť výber',
    loadingKeys: 'Načítavajú sa kľúče…',
    empty: 'Žiadne API kľúče',
    prev: 'Predchádzajúca',
    next: 'Ďalšia',
    pageOf: 'Stránka {{cur}} z {{total}}',
    extendModalTitle: 'Predĺžiť platnosť kľúča',
    extendBtn: 'Predĺžiť',
    extendFor: 'Predĺžiť platnosť kľúča pre:',
    numDays: 'Počet dní',
    bulkExtendTitle: 'Predĺžiť platnosť kľúčov',
    bulkActivateTitle: 'Aktivovať kľúče',
    execute: 'Vykonať',
    selectedN: 'Vybraných {{count}} kľúčov',
    numDaysExtend: 'Počet dní predĺženia',
    activateInfo: 'Vybrané kľúče budú aktivované.',
    revokeWarn: 'Pozor: vybrané kľúče budú zneplatnené a prestanú fungovať.',
  },
  sl: {
    title: 'Upravljanje ključev API',
    subtitle: 'Pregled in upravljanje vseh ključev API',
    loadFailed: 'Ključev API ni bilo mogoče naložiti',
    selectAtLeastOne: 'Izberite vsaj en ključ',
    bulkRevokeTitle: 'Prekliči ključe',
    bulkRevokeConfirm: 'Ali ste prepričani, da želite preklicati {{count}} ključev?',
    bulkOpError: 'Napaka pri izvajanju operacije',
    keyCopied: 'Ključ kopiran v odložišče',
    keyExtended: 'Ključ podaljšan za {{days}} dni',
    extendError: 'Napaka pri podaljševanju ključa',
    revokeTitle: 'Prekliči ključ API',
    revokeConfirm: 'Ali ste prepričani, da želite preklicati ključ za {{name}}?',
    revoke: 'Prekliči',
    cancel: 'Prekliči',
    keyRevoked: 'Ključ je bil preklican',
    revokeError: 'Napaka pri preklicu ključa',
    keyReactivated: 'Ključ je bil ponovno aktiviran',
    reactivateError: 'Napaka pri ponovnem aktiviranju ključa',
    regenerateTitle: 'Ponovno ustvari ključ API',
    regenerateConfirm:
      'Ali ste prepričani, da želite ustvariti nov ključ za {{name}}? Stari ključ bo prenehal delovati.',
    regenerate: 'Ponovno ustvari',
    keyRegenerated: 'Nov ključ ustvarjen in kopiran v odložišče',
    regenerateError: 'Napaka pri ponovnem ustvarjanju ključa',
    colKey: 'Ključ API',
    colClient: 'Stranka',
    colPlan: 'Načrt',
    colStatus: 'Stanje',
    colExpires: 'Poteče',
    colActions: 'Dejanja',
    standaloneName: 'Samostojni ključ',
    standaloneDesc1: ' — vremenski ključ brez dodelitve stranki. Načrt: ',
    standaloneDesc2: ' (največ 4 nizi, dinamični parametri v REST).',
    standaloneCreated:
      'Samostojni ključ ustvarjen. Vrednost kopirajte zdaj — ne bomo je več prikazali:',
    errorWord: 'Napaka',
    addStandaloneBtn: '+ Samostojni ključ',
    syncResult:
      'Sync Gravitee: pridobljenih {{fetched}} ključev, dodanih {{imported}}, posodobljenih {{updated}}, preskočenih {{skipped}}. Čas: {{ms}}ms',
    syncGraviteeTitle:
      'Pridobi ključe iz Gravitee in uvozi kot samostojne ključe (z deduplikacijo)',
    syncGraviteeBtn: '⟲ Sinhroniziraj Gravitee',
    searchPlaceholder: 'Iskanje po imenu stranke ali ključu…',
    allStatuses: 'Vsa stanja',
    statusActiveOpt: 'Aktivni',
    statusInactiveOpt: 'Neaktivni',
    statusExpiredOpt: 'Potekli',
    exportBtn: 'Izvozi',
    selectedKeys: 'Izbrani ključi: {{count}}',
    chooseAction: 'Izberite dejanje…',
    actExtend: 'Podaljšaj veljavnost',
    actActivate: 'Aktiviraj',
    clearSelection: 'Počisti izbor',
    loadingKeys: 'Nalaganje ključev…',
    empty: 'Ni ključev API',
    prev: 'Prejšnja',
    next: 'Naslednja',
    pageOf: 'Stran {{cur}} od {{total}}',
    extendModalTitle: 'Podaljšaj veljavnost ključa',
    extendBtn: 'Podaljšaj',
    extendFor: 'Podaljšaj veljavnost ključa za:',
    numDays: 'Število dni',
    bulkExtendTitle: 'Podaljšaj veljavnost ključev',
    bulkActivateTitle: 'Aktiviraj ključe',
    execute: 'Izvedi',
    selectedN: 'Izbranih {{count}} ključev',
    numDaysExtend: 'Število dni podaljšanja',
    activateInfo: 'Izbrani ključi bodo aktivirani.',
    revokeWarn: 'Opozorilo: izbrani ključi bodo preklicani in bodo prenehali delovati.',
  },
  hr: {
    title: 'Upravljanje API ključevima',
    subtitle: 'Pregled i upravljanje svim API ključevima',
    loadFailed: 'Nije moguće učitati API ključeve',
    selectAtLeastOne: 'Odaberite barem jedan ključ',
    bulkRevokeTitle: 'Opozovi ključeve',
    bulkRevokeConfirm: 'Jeste li sigurni da želite opozvati {{count}} ključeva?',
    bulkOpError: 'Greška pri izvođenju operacije',
    keyCopied: 'Ključ kopiran u međuspremnik',
    keyExtended: 'Ključ produžen za {{days}} dana',
    extendError: 'Greška pri produženju ključa',
    revokeTitle: 'Opozovi API ključ',
    revokeConfirm: 'Jeste li sigurni da želite opozvati ključ za {{name}}?',
    revoke: 'Opozovi',
    cancel: 'Odustani',
    keyRevoked: 'Ključ je opozvan',
    revokeError: 'Greška pri opozivu ključa',
    keyReactivated: 'Ključ je ponovno aktiviran',
    reactivateError: 'Greška pri ponovnoj aktivaciji ključa',
    regenerateTitle: 'Regeneriraj API ključ',
    regenerateConfirm:
      'Jeste li sigurni da želite generirati novi ključ za {{name}}? Stari ključ će prestati raditi.',
    regenerate: 'Regeneriraj',
    keyRegenerated: 'Novi ključ generiran i kopiran u međuspremnik',
    regenerateError: 'Greška pri regeneraciji ključa',
    colKey: 'API ključ',
    colClient: 'Klijent',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Istječe',
    colActions: 'Radnje',
    standaloneName: 'Samostalni ključ',
    standaloneDesc1: ' — vremenski ključ bez dodjele klijentu. Plan: ',
    standaloneDesc2: ' (najviše 4 niza, dinamički parametri u REST).',
    standaloneCreated:
      'Samostalni ključ stvoren. Kopirajte vrijednost sada — nećemo je ponovno prikazati:',
    errorWord: 'Greška',
    addStandaloneBtn: '+ Samostalni ključ',
    syncResult:
      'Sync Gravitee: dohvaćeno {{fetched}} ključeva, dodano {{imported}}, ažurirano {{updated}}, preskočeno {{skipped}}. Vrijeme: {{ms}}ms',
    syncGraviteeTitle:
      'Dohvati ključeve iz Gravitee i uvezi kao samostalne ključeve (s deduplikacijom)',
    syncGraviteeBtn: '⟲ Sinkroniziraj Gravitee',
    searchPlaceholder: 'Traži po nazivu klijenta ili ključu…',
    allStatuses: 'Svi statusi',
    statusActiveOpt: 'Aktivni',
    statusInactiveOpt: 'Neaktivni',
    statusExpiredOpt: 'Istekli',
    exportBtn: 'Izvezi',
    selectedKeys: 'Odabrani ključevi: {{count}}',
    chooseAction: 'Odaberite radnju…',
    actExtend: 'Produži valjanost',
    actActivate: 'Aktiviraj',
    clearSelection: 'Očisti odabir',
    loadingKeys: 'Učitavanje ključeva…',
    empty: 'Nema API ključeva',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    extendModalTitle: 'Produži valjanost ključa',
    extendBtn: 'Produži',
    extendFor: 'Produži valjanost ključa za:',
    numDays: 'Broj dana',
    bulkExtendTitle: 'Produži valjanost ključeva',
    bulkActivateTitle: 'Aktiviraj ključeve',
    execute: 'Izvrši',
    selectedN: 'Odabrano {{count}} ključeva',
    numDaysExtend: 'Broj dana produženja',
    activateInfo: 'Odabrani ključevi bit će aktivirani.',
    revokeWarn: 'Upozorenje: odabrani ključevi bit će opozvani i prestat će raditi.',
  },
  hu: {
    title: 'API-kulcsok kezelése',
    subtitle: 'Az összes API-kulcs áttekintése és kezelése',
    loadFailed: 'Nem sikerült betölteni az API-kulcsokat',
    selectAtLeastOne: 'Válasszon legalább egy kulcsot',
    bulkRevokeTitle: 'Kulcsok visszavonása',
    bulkRevokeConfirm: 'Biztosan vissza szeretne vonni {{count}} kulcsot?',
    bulkOpError: 'Hiba a művelet végrehajtása közben',
    keyCopied: 'Kulcs a vágólapra másolva',
    keyExtended: 'Kulcs meghosszabbítva {{days}} nappal',
    extendError: 'Hiba a kulcs meghosszabbítása közben',
    revokeTitle: 'API-kulcs visszavonása',
    revokeConfirm: 'Biztosan vissza szeretné vonni a(z) {{name}} kulcsát?',
    revoke: 'Visszavonás',
    cancel: 'Mégse',
    keyRevoked: 'A kulcs visszavonva',
    revokeError: 'Hiba a kulcs visszavonása közben',
    keyReactivated: 'A kulcs újraaktiválva',
    reactivateError: 'Hiba a kulcs újraaktiválása közben',
    regenerateTitle: 'API-kulcs újragenerálása',
    regenerateConfirm:
      'Biztosan új kulcsot szeretne generálni a(z) {{name}} számára? A régi kulcs nem fog többé működni.',
    regenerate: 'Újragenerálás',
    keyRegenerated: 'Új kulcs generálva és a vágólapra másolva',
    regenerateError: 'Hiba a kulcs újragenerálása közben',
    colKey: 'API-kulcs',
    colClient: 'Ügyfél',
    colPlan: 'Csomag',
    colStatus: 'Állapot',
    colExpires: 'Lejár',
    colActions: 'Műveletek',
    standaloneName: 'Önálló kulcs',
    standaloneDesc1: ' — időjárási kulcs ügyfél-hozzárendelés nélkül. Csomag: ',
    standaloneDesc2: ' (max. 4 string, dinamikus paraméterek a REST-ben).',
    standaloneCreated:
      'Önálló kulcs létrehozva. Másolja ki az értéket most — többé nem mutatjuk meg:',
    errorWord: 'Hiba',
    addStandaloneBtn: '+ Önálló kulcs',
    syncResult:
      'Sync Gravitee: {{fetched}} kulcs lekérve, {{imported}} hozzáadva, {{updated}} frissítve, {{skipped}} kihagyva. Idő: {{ms}}ms',
    syncGraviteeTitle:
      'Kulcsok lekérése a Gravitee-ből és importálás önálló kulcsként (deduplikációval)',
    syncGraviteeBtn: '⟲ Gravitee szinkronizálása',
    searchPlaceholder: 'Keresés ügyfélnév vagy kulcs alapján…',
    allStatuses: 'Minden állapot',
    statusActiveOpt: 'Aktívak',
    statusInactiveOpt: 'Inaktívak',
    statusExpiredOpt: 'Lejártak',
    exportBtn: 'Exportálás',
    selectedKeys: 'Kijelölt kulcsok: {{count}}',
    chooseAction: 'Válasszon műveletet…',
    actExtend: 'Érvényesség meghosszabbítása',
    actActivate: 'Aktiválás',
    clearSelection: 'Kijelölés törlése',
    loadingKeys: 'Kulcsok betöltése…',
    empty: 'Nincsenek API-kulcsok',
    prev: 'Előző',
    next: 'Következő',
    pageOf: '{{cur}} / {{total}} oldal',
    extendModalTitle: 'Kulcs érvényességének meghosszabbítása',
    extendBtn: 'Meghosszabbítás',
    extendFor: 'Kulcs érvényességének meghosszabbítása a következőnek:',
    numDays: 'Napok száma',
    bulkExtendTitle: 'Kulcsok érvényességének meghosszabbítása',
    bulkActivateTitle: 'Kulcsok aktiválása',
    execute: 'Végrehajtás',
    selectedN: '{{count}} kulcs kijelölve',
    numDaysExtend: 'Meghosszabbítási napok száma',
    activateInfo: 'A kijelölt kulcsok aktiválva lesznek.',
    revokeWarn: 'Figyelem: a kijelölt kulcsok visszavonásra kerülnek, és nem fognak többé működni.',
  },
  ro: {
    title: 'Gestionarea cheilor API',
    subtitle: 'Prezentare generală și gestionarea tuturor cheilor API',
    loadFailed: 'Nu s-au putut încărca cheile API',
    selectAtLeastOne: 'Selectați cel puțin o cheie',
    bulkRevokeTitle: 'Revocă cheile',
    bulkRevokeConfirm: 'Sigur doriți să revocați {{count}} chei?',
    bulkOpError: 'Eroare la executarea operației',
    keyCopied: 'Cheie copiată în clipboard',
    keyExtended: 'Cheie prelungită cu {{days}} zile',
    extendError: 'Eroare la prelungirea cheii',
    revokeTitle: 'Revocă cheia API',
    revokeConfirm: 'Sigur doriți să revocați cheia pentru {{name}}?',
    revoke: 'Revocă',
    cancel: 'Anulează',
    keyRevoked: 'Cheia a fost revocată',
    revokeError: 'Eroare la revocarea cheii',
    keyReactivated: 'Cheia a fost reactivată',
    reactivateError: 'Eroare la reactivarea cheii',
    regenerateTitle: 'Regenerează cheia API',
    regenerateConfirm:
      'Sigur doriți să generați o cheie nouă pentru {{name}}? Cheia veche va înceta să funcționeze.',
    regenerate: 'Regenerează',
    keyRegenerated: 'Cheie nouă generată și copiată în clipboard',
    regenerateError: 'Eroare la regenerarea cheii',
    colKey: 'Cheie API',
    colClient: 'Client',
    colPlan: 'Plan',
    colStatus: 'Stare',
    colExpires: 'Expiră',
    colActions: 'Acțiuni',
    standaloneName: 'Cheie independentă',
    standaloneDesc1: ' — cheie meteo fără atribuire la un client. Plan: ',
    standaloneDesc2: ' (max 4 stringuri, parametri dinamici în REST).',
    standaloneCreated: 'Cheie independentă creată. Copiați valoarea acum — nu o vom mai afișa:',
    errorWord: 'Eroare',
    addStandaloneBtn: '+ Cheie independentă',
    syncResult:
      'Sync Gravitee: {{fetched}} chei preluate, {{imported}} adăugate, {{updated}} actualizate, {{skipped}} omise. Timp: {{ms}}ms',
    syncGraviteeTitle:
      'Preia cheile din Gravitee și importă-le ca chei independente (cu deduplicare)',
    syncGraviteeBtn: '⟲ Sincronizează Gravitee',
    searchPlaceholder: 'Căutați după numele clientului sau cheie…',
    allStatuses: 'Toate stările',
    statusActiveOpt: 'Active',
    statusInactiveOpt: 'Inactive',
    statusExpiredOpt: 'Expirate',
    exportBtn: 'Exportă',
    selectedKeys: 'Chei selectate: {{count}}',
    chooseAction: 'Alegeți o acțiune…',
    actExtend: 'Prelungește valabilitatea',
    actActivate: 'Activează',
    clearSelection: 'Șterge selecția',
    loadingKeys: 'Se încarcă cheile…',
    empty: 'Nicio cheie API',
    prev: 'Anterioară',
    next: 'Următoarea',
    pageOf: 'Pagina {{cur}} din {{total}}',
    extendModalTitle: 'Prelungește valabilitatea cheii',
    extendBtn: 'Prelungește',
    extendFor: 'Prelungește valabilitatea cheii pentru:',
    numDays: 'Număr de zile',
    bulkExtendTitle: 'Prelungește valabilitatea cheilor',
    bulkActivateTitle: 'Activează cheile',
    execute: 'Execută',
    selectedN: '{{count}} chei selectate',
    numDaysExtend: 'Număr de zile de prelungire',
    activateInfo: 'Cheile selectate vor fi activate.',
    revokeWarn: 'Atenție: cheile selectate vor fi revocate și vor înceta să funcționeze.',
  },
  bg: {
    title: 'Управление на API ключове',
    subtitle: 'Преглед и управление на всички API ключове',
    loadFailed: 'Неуспешно зареждане на API ключовете',
    selectAtLeastOne: 'Изберете поне един ключ',
    bulkRevokeTitle: 'Анулиране на ключове',
    bulkRevokeConfirm: 'Сигурни ли сте, че искате да анулирате {{count}} ключа?',
    bulkOpError: 'Грешка при изпълнение на операцията',
    keyCopied: 'Ключът е копиран в клипборда',
    keyExtended: 'Ключът е удължен с {{days}} дни',
    extendError: 'Грешка при удължаване на ключа',
    revokeTitle: 'Анулиране на API ключ',
    revokeConfirm: 'Сигурни ли сте, че искате да анулирате ключа за {{name}}?',
    revoke: 'Анулирай',
    cancel: 'Отказ',
    keyRevoked: 'Ключът беше анулиран',
    revokeError: 'Грешка при анулиране на ключа',
    keyReactivated: 'Ключът беше реактивиран',
    reactivateError: 'Грешка при реактивиране на ключа',
    regenerateTitle: 'Регенериране на API ключ',
    regenerateConfirm:
      'Сигурни ли сте, че искате да генерирате нов ключ за {{name}}? Старият ключ ще спре да работи.',
    regenerate: 'Регенерирай',
    keyRegenerated: 'Нов ключ е генериран и копиран в клипборда',
    regenerateError: 'Грешка при регенериране на ключа',
    colKey: 'API ключ',
    colClient: 'Клиент',
    colPlan: 'План',
    colStatus: 'Статус',
    colExpires: 'Изтича',
    colActions: 'Действия',
    standaloneName: 'Самостоятелен ключ',
    standaloneDesc1: ' — метеорологичен ключ без присвояване към клиент. План: ',
    standaloneDesc2: ' (макс. 4 стринга, динамични параметри в REST).',
    standaloneCreated:
      'Самостоятелен ключ е създаден. Копирайте стойността сега — няма да я покажем отново:',
    errorWord: 'Грешка',
    addStandaloneBtn: '+ Самостоятелен ключ',
    syncResult:
      'Sync Gravitee: извлечени {{fetched}} ключа, добавени {{imported}}, актуализирани {{updated}}, пропуснати {{skipped}}. Време: {{ms}}ms',
    syncGraviteeTitle:
      'Извлечи ключове от Gravitee и ги импортирай като самостоятелни ключове (с дедупликация)',
    syncGraviteeBtn: '⟲ Синхронизирай Gravitee',
    searchPlaceholder: 'Търсене по име на клиент или ключ…',
    allStatuses: 'Всички статуси',
    statusActiveOpt: 'Активни',
    statusInactiveOpt: 'Неактивни',
    statusExpiredOpt: 'Изтекли',
    exportBtn: 'Експортиране',
    selectedKeys: 'Избрани ключове: {{count}}',
    chooseAction: 'Изберете действие…',
    actExtend: 'Удължи валидност',
    actActivate: 'Активирай',
    clearSelection: 'Изчисти избора',
    loadingKeys: 'Зареждане на ключове…',
    empty: 'Няма API ключове',
    prev: 'Предишна',
    next: 'Следваща',
    pageOf: 'Страница {{cur}} от {{total}}',
    extendModalTitle: 'Удължи валидността на ключа',
    extendBtn: 'Удължи',
    extendFor: 'Удължи валидността на ключа за:',
    numDays: 'Брой дни',
    bulkExtendTitle: 'Удължи валидността на ключовете',
    bulkActivateTitle: 'Активирай ключовете',
    execute: 'Изпълни',
    selectedN: 'Избрани {{count}} ключа',
    numDaysExtend: 'Брой дни за удължаване',
    activateInfo: 'Избраните ключове ще бъдат активирани.',
    revokeWarn: 'Внимание: избраните ключове ще бъдат анулирани и ще спрат да работят.',
  },
  el: {
    title: 'Διαχείριση κλειδιών API',
    subtitle: 'Επισκόπηση και διαχείριση όλων των κλειδιών API',
    loadFailed: 'Αποτυχία φόρτωσης των κλειδιών API',
    selectAtLeastOne: 'Επιλέξτε τουλάχιστον ένα κλειδί',
    bulkRevokeTitle: 'Ανάκληση κλειδιών',
    bulkRevokeConfirm: 'Είστε σίγουροι ότι θέλετε να ανακαλέσετε {{count}} κλειδιά;',
    bulkOpError: 'Σφάλμα κατά την εκτέλεση της λειτουργίας',
    keyCopied: 'Το κλειδί αντιγράφηκε στο πρόχειρο',
    keyExtended: 'Το κλειδί παρατάθηκε κατά {{days}} ημέρες',
    extendError: 'Σφάλμα κατά την παράταση του κλειδιού',
    revokeTitle: 'Ανάκληση κλειδιού API',
    revokeConfirm: 'Είστε σίγουροι ότι θέλετε να ανακαλέσετε το κλειδί για τον/την {{name}};',
    revoke: 'Ανάκληση',
    cancel: 'Άκυρο',
    keyRevoked: 'Το κλειδί ανακλήθηκε',
    revokeError: 'Σφάλμα κατά την ανάκληση του κλειδιού',
    keyReactivated: 'Το κλειδί επανενεργοποιήθηκε',
    reactivateError: 'Σφάλμα κατά την επανενεργοποίηση του κλειδιού',
    regenerateTitle: 'Αναδημιουργία κλειδιού API',
    regenerateConfirm:
      'Είστε σίγουροι ότι θέλετε να δημιουργήσετε νέο κλειδί για τον/την {{name}}; Το παλιό κλειδί θα σταματήσει να λειτουργεί.',
    regenerate: 'Αναδημιουργία',
    keyRegenerated: 'Νέο κλειδί δημιουργήθηκε και αντιγράφηκε στο πρόχειρο',
    regenerateError: 'Σφάλμα κατά την αναδημιουργία του κλειδιού',
    colKey: 'Κλειδί API',
    colClient: 'Πελάτης',
    colPlan: 'Πρόγραμμα',
    colStatus: 'Κατάσταση',
    colExpires: 'Λήγει',
    colActions: 'Ενέργειες',
    standaloneName: 'Αυτόνομο κλειδί',
    standaloneDesc1: ' — κλειδί καιρού χωρίς ανάθεση σε πελάτη. Πρόγραμμα: ',
    standaloneDesc2: ' (μέγ. 4 strings, δυναμικές παράμετροι σε REST).',
    standaloneCreated:
      'Δημιουργήθηκε αυτόνομο κλειδί. Αντιγράψτε την τιμή τώρα — δεν θα την εμφανίσουμε ξανά:',
    errorWord: 'Σφάλμα',
    addStandaloneBtn: '+ Αυτόνομο κλειδί',
    syncResult:
      'Sync Gravitee: ανακτήθηκαν {{fetched}} κλειδιά, προστέθηκαν {{imported}}, ενημερώθηκαν {{updated}}, παραλείφθηκαν {{skipped}}. Χρόνος: {{ms}}ms',
    syncGraviteeTitle:
      'Ανάκτηση κλειδιών από το Gravitee και εισαγωγή ως αυτόνομα κλειδιά (με αφαίρεση διπλοτύπων)',
    syncGraviteeBtn: '⟲ Συγχρονισμός Gravitee',
    searchPlaceholder: 'Αναζήτηση κατά όνομα πελάτη ή κλειδί…',
    allStatuses: 'Όλες οι καταστάσεις',
    statusActiveOpt: 'Ενεργά',
    statusInactiveOpt: 'Ανενεργά',
    statusExpiredOpt: 'Ληγμένα',
    exportBtn: 'Εξαγωγή',
    selectedKeys: 'Επιλεγμένα κλειδιά: {{count}}',
    chooseAction: 'Επιλέξτε ενέργεια…',
    actExtend: 'Παράταση ισχύος',
    actActivate: 'Ενεργοποίηση',
    clearSelection: 'Εκκαθάριση επιλογής',
    loadingKeys: 'Φόρτωση κλειδιών…',
    empty: 'Δεν υπάρχουν κλειδιά API',
    prev: 'Προηγούμενη',
    next: 'Επόμενη',
    pageOf: 'Σελίδα {{cur}} από {{total}}',
    extendModalTitle: 'Παράταση ισχύος κλειδιού',
    extendBtn: 'Παράταση',
    extendFor: 'Παράταση ισχύος κλειδιού για:',
    numDays: 'Αριθμός ημερών',
    bulkExtendTitle: 'Παράταση ισχύος κλειδιών',
    bulkActivateTitle: 'Ενεργοποίηση κλειδιών',
    execute: 'Εκτέλεση',
    selectedN: 'Επιλέχθηκαν {{count}} κλειδιά',
    numDaysExtend: 'Αριθμός ημερών παράτασης',
    activateInfo: 'Τα επιλεγμένα κλειδιά θα ενεργοποιηθούν.',
    revokeWarn: 'Προσοχή: τα επιλεγμένα κλειδιά θα ανακληθούν και θα σταματήσουν να λειτουργούν.',
  },
  et: {
    title: 'API-võtmete haldamine',
    subtitle: 'Kõigi API-võtmete ülevaade ja haldamine',
    loadFailed: 'API-võtmete laadimine ebaõnnestus',
    selectAtLeastOne: 'Vali vähemalt üks võti',
    bulkRevokeTitle: 'Tühista võtmed',
    bulkRevokeConfirm: 'Kas oled kindel, et soovid tühistada {{count}} võtit?',
    bulkOpError: 'Viga toimingu tegemisel',
    keyCopied: 'Võti kopeeriti lõikelauale',
    keyExtended: 'Võtit pikendati {{days}} päeva võrra',
    extendError: 'Viga võtme pikendamisel',
    revokeTitle: 'Tühista API-võti',
    revokeConfirm: 'Kas oled kindel, et soovid tühistada võtme kliendile {{name}}?',
    revoke: 'Tühista',
    cancel: 'Tühista',
    keyRevoked: 'Võti on tühistatud',
    revokeError: 'Viga võtme tühistamisel',
    keyReactivated: 'Võti on uuesti aktiveeritud',
    reactivateError: 'Viga võtme uuesti aktiveerimisel',
    regenerateTitle: 'Genereeri API-võti uuesti',
    regenerateConfirm:
      'Kas oled kindel, et soovid genereerida uue võtme kliendile {{name}}? Vana võti lakkab töötamast.',
    regenerate: 'Genereeri uuesti',
    keyRegenerated: 'Uus võti genereeritud ja kopeeriti lõikelauale',
    regenerateError: 'Viga võtme uuesti genereerimisel',
    colKey: 'API-võti',
    colClient: 'Klient',
    colPlan: 'Pakett',
    colStatus: 'Olek',
    colExpires: 'Aegub',
    colActions: 'Toimingud',
    standaloneName: 'Iseseisev võti',
    standaloneDesc1: ' — ilmavõti ilma kliendi määramiseta. Pakett: ',
    standaloneDesc2: ' (kuni 4 stringi, dünaamilised parameetrid REST-is).',
    standaloneCreated: 'Iseseisev võti loodud. Kopeeri väärtus kohe — me ei näita seda uuesti:',
    errorWord: 'Viga',
    addStandaloneBtn: '+ Iseseisev võti',
    syncResult:
      'Sync Gravitee: hangiti {{fetched}} võtit, lisati {{imported}}, uuendati {{updated}}, jäeti vahele {{skipped}}. Aeg: {{ms}}ms',
    syncGraviteeTitle: 'Hangi võtmed Gravitee-st ja impordi iseseisvate võtmetena (dedupiga)',
    syncGraviteeBtn: '⟲ Sünkrooni Gravitee',
    searchPlaceholder: 'Otsi kliendi nime või võtme järgi…',
    allStatuses: 'Kõik olekud',
    statusActiveOpt: 'Aktiivsed',
    statusInactiveOpt: 'Mitteaktiivsed',
    statusExpiredOpt: 'Aegunud',
    exportBtn: 'Ekspordi',
    selectedKeys: 'Valitud võtmed: {{count}}',
    chooseAction: 'Vali toiming…',
    actExtend: 'Pikenda kehtivust',
    actActivate: 'Aktiveeri',
    clearSelection: 'Tühjenda valik',
    loadingKeys: 'Võtmete laadimine…',
    empty: 'API-võtmeid pole',
    prev: 'Eelmine',
    next: 'Järgmine',
    pageOf: 'Lehekülg {{cur}} / {{total}}',
    extendModalTitle: 'Pikenda võtme kehtivust',
    extendBtn: 'Pikenda',
    extendFor: 'Pikenda võtme kehtivust kliendile:',
    numDays: 'Päevade arv',
    bulkExtendTitle: 'Pikenda võtmete kehtivust',
    bulkActivateTitle: 'Aktiveeri võtmed',
    execute: 'Käivita',
    selectedN: 'Valitud {{count}} võtit',
    numDaysExtend: 'Pikendamispäevade arv',
    activateInfo: 'Valitud võtmed aktiveeritakse.',
    revokeWarn: 'Hoiatus: valitud võtmed tühistatakse ja lakkavad töötamast.',
  },
  lv: {
    title: 'API atslēgu pārvaldība',
    subtitle: 'Visu API atslēgu pārskats un pārvaldība',
    loadFailed: 'Neizdevās ielādēt API atslēgas',
    selectAtLeastOne: 'Atlasiet vismaz vienu atslēgu',
    bulkRevokeTitle: 'Atsaukt atslēgas',
    bulkRevokeConfirm: 'Vai tiešām vēlaties atsaukt {{count}} atslēgas?',
    bulkOpError: 'Kļūda, izpildot darbību',
    keyCopied: 'Atslēga nokopēta starpliktuvē',
    keyExtended: 'Atslēga pagarināta par {{days}} dienām',
    extendError: 'Kļūda, pagarinot atslēgu',
    revokeTitle: 'Atsaukt API atslēgu',
    revokeConfirm: 'Vai tiešām vēlaties atsaukt atslēgu klientam {{name}}?',
    revoke: 'Atsaukt',
    cancel: 'Atcelt',
    keyRevoked: 'Atslēga tika atsaukta',
    revokeError: 'Kļūda, atsaucot atslēgu',
    keyReactivated: 'Atslēga tika atkārtoti aktivizēta',
    reactivateError: 'Kļūda, atkārtoti aktivizējot atslēgu',
    regenerateTitle: 'Atjaunot API atslēgu',
    regenerateConfirm:
      'Vai tiešām vēlaties ģenerēt jaunu atslēgu klientam {{name}}? Vecā atslēga pārstās darboties.',
    regenerate: 'Atjaunot',
    keyRegenerated: 'Jauna atslēga ģenerēta un nokopēta starpliktuvē',
    regenerateError: 'Kļūda, atjaunojot atslēgu',
    colKey: 'API atslēga',
    colClient: 'Klients',
    colPlan: 'Plāns',
    colStatus: 'Statuss',
    colExpires: 'Beidzas',
    colActions: 'Darbības',
    standaloneName: 'Atsevišķa atslēga',
    standaloneDesc1: ' — laikapstākļu atslēga bez piesaistes klientam. Plāns: ',
    standaloneDesc2: ' (maks. 4 virknes, dinamiski parametri REST).',
    standaloneCreated:
      'Atsevišķa atslēga izveidota. Nokopējiet vērtību tagad — vairs to neparādīsim:',
    errorWord: 'Kļūda',
    addStandaloneBtn: '+ Atsevišķa atslēga',
    syncResult:
      'Sync Gravitee: iegūtas {{fetched}} atslēgas, pievienotas {{imported}}, atjauninātas {{updated}}, izlaistas {{skipped}}. Laiks: {{ms}}ms',
    syncGraviteeTitle:
      'Iegūt atslēgas no Gravitee un importēt kā atsevišķas atslēgas (ar dublikātu noņemšanu)',
    syncGraviteeBtn: '⟲ Sinhronizēt Gravitee',
    searchPlaceholder: 'Meklēt pēc klienta nosaukuma vai atslēgas…',
    allStatuses: 'Visi statusi',
    statusActiveOpt: 'Aktīvas',
    statusInactiveOpt: 'Neaktīvas',
    statusExpiredOpt: 'Beigušās',
    exportBtn: 'Eksportēt',
    selectedKeys: 'Atlasītās atslēgas: {{count}}',
    chooseAction: 'Izvēlieties darbību…',
    actExtend: 'Pagarināt derīgumu',
    actActivate: 'Aktivizēt',
    clearSelection: 'Notīrīt atlasi',
    loadingKeys: 'Notiek atslēgu ielāde…',
    empty: 'Nav API atslēgu',
    prev: 'Iepriekšējā',
    next: 'Nākamā',
    pageOf: 'Lapa {{cur}} no {{total}}',
    extendModalTitle: 'Pagarināt atslēgas derīgumu',
    extendBtn: 'Pagarināt',
    extendFor: 'Pagarināt atslēgas derīgumu klientam:',
    numDays: 'Dienu skaits',
    bulkExtendTitle: 'Pagarināt atslēgu derīgumu',
    bulkActivateTitle: 'Aktivizēt atslēgas',
    execute: 'Izpildīt',
    selectedN: 'Atlasītas {{count}} atslēgas',
    numDaysExtend: 'Pagarinājuma dienu skaits',
    activateInfo: 'Atlasītās atslēgas tiks aktivizētas.',
    revokeWarn: 'Brīdinājums: atlasītās atslēgas tiks atsauktas un pārstās darboties.',
  },
  lt: {
    title: 'API raktų valdymas',
    subtitle: 'Visų API raktų apžvalga ir valdymas',
    loadFailed: 'Nepavyko įkelti API raktų',
    selectAtLeastOne: 'Pasirinkite bent vieną raktą',
    bulkRevokeTitle: 'Atšaukti raktus',
    bulkRevokeConfirm: 'Ar tikrai norite atšaukti {{count}} raktų?',
    bulkOpError: 'Klaida vykdant operaciją',
    keyCopied: 'Raktas nukopijuotas į iškarpinę',
    keyExtended: 'Raktas pratęstas {{days}} dienomis',
    extendError: 'Klaida pratęsiant raktą',
    revokeTitle: 'Atšaukti API raktą',
    revokeConfirm: 'Ar tikrai norite atšaukti raktą klientui {{name}}?',
    revoke: 'Atšaukti',
    cancel: 'Atšaukti',
    keyRevoked: 'Raktas buvo atšauktas',
    revokeError: 'Klaida atšaukiant raktą',
    keyReactivated: 'Raktas buvo pakartotinai aktyvuotas',
    reactivateError: 'Klaida pakartotinai aktyvuojant raktą',
    regenerateTitle: 'Regeneruoti API raktą',
    regenerateConfirm:
      'Ar tikrai norite sugeneruoti naują raktą klientui {{name}}? Senas raktas nustos veikti.',
    regenerate: 'Regeneruoti',
    keyRegenerated: 'Naujas raktas sugeneruotas ir nukopijuotas į iškarpinę',
    regenerateError: 'Klaida regeneruojant raktą',
    colKey: 'API raktas',
    colClient: 'Klientas',
    colPlan: 'Planas',
    colStatus: 'Būsena',
    colExpires: 'Baigiasi',
    colActions: 'Veiksmai',
    standaloneName: 'Atskiras raktas',
    standaloneDesc1: ' — orų raktas be priskyrimo klientui. Planas: ',
    standaloneDesc2: ' (daugiausia 4 stringai, dinaminiai parametrai REST).',
    standaloneCreated:
      'Atskiras raktas sukurtas. Nukopijuokite reikšmę dabar — daugiau jos neberodysime:',
    errorWord: 'Klaida',
    addStandaloneBtn: '+ Atskiras raktas',
    syncResult:
      'Sync Gravitee: gauta {{fetched}} raktų, pridėta {{imported}}, atnaujinta {{updated}}, praleista {{skipped}}. Laikas: {{ms}}ms',
    syncGraviteeTitle:
      'Gauti raktus iš Gravitee ir importuoti kaip atskirus raktus (su dublikatų pašalinimu)',
    syncGraviteeBtn: '⟲ Sinchronizuoti Gravitee',
    searchPlaceholder: 'Ieškoti pagal kliento pavadinimą arba raktą…',
    allStatuses: 'Visos būsenos',
    statusActiveOpt: 'Aktyvūs',
    statusInactiveOpt: 'Neaktyvūs',
    statusExpiredOpt: 'Pasibaigę',
    exportBtn: 'Eksportuoti',
    selectedKeys: 'Pasirinkti raktai: {{count}}',
    chooseAction: 'Pasirinkite veiksmą…',
    actExtend: 'Pratęsti galiojimą',
    actActivate: 'Aktyvuoti',
    clearSelection: 'Išvalyti pasirinkimą',
    loadingKeys: 'Įkeliami raktai…',
    empty: 'Nėra API raktų',
    prev: 'Ankstesnis',
    next: 'Kitas',
    pageOf: 'Puslapis {{cur}} iš {{total}}',
    extendModalTitle: 'Pratęsti rakto galiojimą',
    extendBtn: 'Pratęsti',
    extendFor: 'Pratęsti rakto galiojimą klientui:',
    numDays: 'Dienų skaičius',
    bulkExtendTitle: 'Pratęsti raktų galiojimą',
    bulkActivateTitle: 'Aktyvuoti raktus',
    execute: 'Vykdyti',
    selectedN: 'Pasirinkta {{count}} raktų',
    numDaysExtend: 'Pratęsimo dienų skaičius',
    activateInfo: 'Pasirinkti raktai bus aktyvuoti.',
    revokeWarn: 'Įspėjimas: pasirinkti raktai bus atšaukti ir nustos veikti.',
  },
  mt: {
    title: 'Ġestjoni taċ-ċwievet API',
    subtitle: 'Ħarsa ġenerali u ġestjoni taċ-ċwievet API kollha',
    loadFailed: 'Ma rnexxiex jiġu mgħobbija ċ-ċwievet API',
    selectAtLeastOne: 'Agħżel mill-inqas ċavetta waħda',
    bulkRevokeTitle: 'Revoka ċwievet',
    bulkRevokeConfirm: 'Żgur trid tirrevoka {{count}} ċwievet?',
    bulkOpError: 'Żball waqt l-eżekuzzjoni tal-operazzjoni',
    keyCopied: 'Iċ-ċavetta ġiet ikkopjata fil-clipboard',
    keyExtended: "Iċ-ċavetta ġiet estiża b'{{days}} ijiem",
    extendError: 'Żball waqt l-estensjoni taċ-ċavetta',
    revokeTitle: 'Revoka ċ-ċavetta API',
    revokeConfirm: 'Żgur trid tirrevoka ċ-ċavetta għal {{name}}?',
    revoke: 'Revoka',
    cancel: 'Ikkanċella',
    keyRevoked: 'Iċ-ċavetta ġiet revokata',
    revokeError: 'Żball waqt ir-revoka taċ-ċavetta',
    keyReactivated: 'Iċ-ċavetta ġiet riattivata',
    reactivateError: 'Żball waqt ir-riattivazzjoni taċ-ċavetta',
    regenerateTitle: 'Iġġenera mill-ġdid iċ-ċavetta API',
    regenerateConfirm:
      'Żgur trid tiġġenera ċavetta ġdida għal {{name}}? Iċ-ċavetta l-qadima se tieqaf taħdem.',
    regenerate: 'Iġġenera mill-ġdid',
    keyRegenerated: 'Ċavetta ġdida ġġenerata u kkopjata fil-clipboard',
    regenerateError: 'Żball waqt il-ġenerazzjoni mill-ġdid taċ-ċavetta',
    colKey: 'Ċavetta API',
    colClient: 'Klijent',
    colPlan: 'Pjan',
    colStatus: 'Status',
    colExpires: 'Tiskadi',
    colActions: 'Azzjonijiet',
    standaloneName: 'Ċavetta standalone',
    standaloneDesc1: ' — ċavetta tat-temp mingħajr assenjazzjoni lil klijent. Pjan: ',
    standaloneDesc2: " (massimu 4 strings, parametri dinamiċi f'REST).",
    standaloneCreated:
      'Ċavetta standalone maħluqa. Ikkopja l-valur issa — mhux se nuruh mill-ġdid:',
    errorWord: 'Żball',
    addStandaloneBtn: '+ Ċavetta standalone',
    syncResult:
      'Sync Gravitee: inġabu {{fetched}} ċwievet, miżjuda {{imported}}, aġġornati {{updated}}, maqbuża {{skipped}}. Ħin: {{ms}}ms',
    syncGraviteeTitle:
      "Iġbed iċ-ċwievet minn Gravitee u importahom bħala ċwievet standalone (b'dedup)",
    syncGraviteeBtn: '⟲ Issinkronizza Gravitee',
    searchPlaceholder: 'Fittex skont isem il-klijent jew iċ-ċavetta…',
    allStatuses: 'L-istatusijiet kollha',
    statusActiveOpt: 'Attivi',
    statusInactiveOpt: 'Inattivi',
    statusExpiredOpt: 'Skaduti',
    exportBtn: 'Esporta',
    selectedKeys: 'Ċwievet magħżula: {{count}}',
    chooseAction: 'Agħżel azzjoni…',
    actExtend: 'Estendi l-validità',
    actActivate: 'Attiva',
    clearSelection: 'Naddaf l-għażla',
    loadingKeys: 'Qed jitgħabbew iċ-ċwievet…',
    empty: 'Ebda ċavetta API',
    prev: 'Preċedenti',
    next: 'Li jmiss',
    pageOf: 'Paġna {{cur}} minn {{total}}',
    extendModalTitle: 'Estendi l-validità taċ-ċavetta',
    extendBtn: 'Estendi',
    extendFor: 'Estendi l-validità taċ-ċavetta għal:',
    numDays: "Numru ta' jiem",
    bulkExtendTitle: 'Estendi l-validità taċ-ċwievet',
    bulkActivateTitle: 'Attiva ċ-ċwievet',
    execute: 'Esegwi',
    selectedN: '{{count}} ċwievet magħżula',
    numDaysExtend: "Numru ta' jiem ta' estensjoni",
    activateInfo: 'Iċ-ċwievet magħżula se jiġu attivati.',
    revokeWarn: 'Attenzjoni: iċ-ċwievet magħżula se jiġu revokati u jieqfu jaħdmu.',
  },
  ga: {
    title: 'Bainistíocht eochracha API',
    subtitle: 'Forléargas agus bainistíocht ar gach eochair API',
    loadFailed: 'Theip ar na heochracha API a luchtú',
    selectAtLeastOne: 'Roghnaigh eochair amháin ar a laghad',
    bulkRevokeTitle: 'Cúlghair eochracha',
    bulkRevokeConfirm: 'An bhfuil tú cinnte gur mhaith leat {{count}} eochair a chúlghairm?',
    bulkOpError: 'Earráid agus an oibríocht á cur i bhfeidhm',
    keyCopied: 'Eochair cóipeáilte chuig an ngearrthaisce',
    keyExtended: 'Eochair sínte {{days}} lá',
    extendError: 'Earráid agus an eochair á síneadh',
    revokeTitle: 'Cúlghair eochair API',
    revokeConfirm: 'An bhfuil tú cinnte gur mhaith leat an eochair do {{name}} a chúlghairm?',
    revoke: 'Cúlghair',
    cancel: 'Cealaigh',
    keyRevoked: 'Cúlghaireadh an eochair',
    revokeError: 'Earráid agus an eochair á cúlghairm',
    keyReactivated: 'Athghníomhachtaíodh an eochair',
    reactivateError: 'Earráid agus an eochair á hathghníomhachtú',
    regenerateTitle: 'Athghin eochair API',
    regenerateConfirm:
      'An bhfuil tú cinnte gur mhaith leat eochair nua a ghiniúint do {{name}}? Stopfaidh an seaneochair ag obair.',
    regenerate: 'Athghin',
    keyRegenerated: 'Eochair nua ginte agus cóipeáilte chuig an ngearrthaisce',
    regenerateError: 'Earráid agus an eochair á hathghiniúint',
    colKey: 'Eochair API',
    colClient: 'Cliant',
    colPlan: 'Plean',
    colStatus: 'Stádas',
    colExpires: 'Éagann',
    colActions: 'Gníomhartha',
    standaloneName: 'Eochair neamhspleách',
    standaloneDesc1: ' — eochair aimsire gan sannadh do chliant. Plean: ',
    standaloneDesc2: ' (uasmhéid 4 strings, paraiméadair dhinimiciúla in REST).',
    standaloneCreated:
      'Eochair neamhspleách cruthaithe. Cóipeáil an luach anois — ní thaispeánfaimid arís é:',
    errorWord: 'Earráid',
    addStandaloneBtn: '+ Eochair neamhspleách',
    syncResult:
      'Sync Gravitee: fuarthas {{fetched}} eochair, cuireadh {{imported}} leis, nuashonraíodh {{updated}}, scipeáladh {{skipped}}. Am: {{ms}}ms',
    syncGraviteeTitle:
      'Faigh eochracha ó Gravitee agus iompórtáil iad mar eochracha neamhspleácha (le dí-dhúbailt)',
    syncGraviteeBtn: '⟲ Sioncronaigh Gravitee',
    searchPlaceholder: 'Cuardaigh de réir ainm an chliaint nó eochair…',
    allStatuses: 'Gach stádas',
    statusActiveOpt: 'Gníomhach',
    statusInactiveOpt: 'Neamhghníomhach',
    statusExpiredOpt: 'Éagtha',
    exportBtn: 'Easpórtáil',
    selectedKeys: 'Eochracha roghnaithe: {{count}}',
    chooseAction: 'Roghnaigh gníomh…',
    actExtend: 'Sín an bhailíocht',
    actActivate: 'Gníomhachtaigh',
    clearSelection: 'Glan an roghnúchán',
    loadingKeys: 'Eochracha á luchtú…',
    empty: 'Gan eochracha API',
    prev: 'Roimhe seo',
    next: 'Ar aghaidh',
    pageOf: 'Leathanach {{cur}} as {{total}}',
    extendModalTitle: 'Sín bailíocht na heochrach',
    extendBtn: 'Sín',
    extendFor: 'Sín bailíocht na heochrach do:',
    numDays: 'Líon laethanta',
    bulkExtendTitle: 'Sín bailíocht na n-eochracha',
    bulkActivateTitle: 'Gníomhachtaigh eochracha',
    execute: 'Cuir i bhfeidhm',
    selectedN: '{{count}} eochair roghnaithe',
    numDaysExtend: 'Líon laethanta le síneadh',
    activateInfo: 'Gníomhachtófar na heochracha roghnaithe.',
    revokeWarn: 'Rabhadh: cúlghairfear na heochracha roghnaithe agus stopfaidh siad ag obair.',
  },
  no: {
    title: 'Administrasjon av API-nøkler',
    subtitle: 'Oversikt og administrasjon av alle API-nøkler',
    loadFailed: 'Kunne ikke laste API-nøkler',
    selectAtLeastOne: 'Velg minst én nøkkel',
    bulkRevokeTitle: 'Tilbakekall nøkler',
    bulkRevokeConfirm: 'Er du sikker på at du vil tilbakekalle {{count}} nøkler?',
    bulkOpError: 'Feil under utføring av handlingen',
    keyCopied: 'Nøkkel kopiert til utklippstavlen',
    keyExtended: 'Nøkkel forlenget med {{days}} dager',
    extendError: 'Feil under forlengelse av nøkkelen',
    revokeTitle: 'Tilbakekall API-nøkkel',
    revokeConfirm: 'Er du sikker på at du vil tilbakekalle nøkkelen for {{name}}?',
    revoke: 'Tilbakekall',
    cancel: 'Avbryt',
    keyRevoked: 'Nøkkelen er tilbakekalt',
    revokeError: 'Feil under tilbakekalling av nøkkelen',
    keyReactivated: 'Nøkkelen er reaktivert',
    reactivateError: 'Feil under reaktivering av nøkkelen',
    regenerateTitle: 'Regenerer API-nøkkel',
    regenerateConfirm:
      'Er du sikker på at du vil generere en ny nøkkel for {{name}}? Den gamle nøkkelen slutter å fungere.',
    regenerate: 'Regenerer',
    keyRegenerated: 'Ny nøkkel generert og kopiert til utklippstavlen',
    regenerateError: 'Feil under regenerering av nøkkelen',
    colKey: 'API-nøkkel',
    colClient: 'Kunde',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Utløper',
    colActions: 'Handlinger',
    standaloneName: 'Frittstående nøkkel',
    standaloneDesc1: ' — værnøkkel uten kundetilknytning. Plan: ',
    standaloneDesc2: ' (maks 4 strings, dynamiske parametere i REST).',
    standaloneCreated:
      'Frittstående nøkkel opprettet. Kopier verdien nå — vi viser den ikke igjen:',
    errorWord: 'Feil',
    addStandaloneBtn: '+ Frittstående nøkkel',
    syncResult:
      'Sync Gravitee: hentet {{fetched}} nøkler, lagt til {{imported}}, oppdatert {{updated}}, hoppet over {{skipped}}. Tid: {{ms}}ms',
    syncGraviteeTitle: 'Hent nøkler fra Gravitee og importer som frittstående nøkler (med dedup)',
    syncGraviteeBtn: '⟲ Synkroniser Gravitee',
    searchPlaceholder: 'Søk etter kundenavn eller nøkkel…',
    allStatuses: 'Alle statuser',
    statusActiveOpt: 'Aktive',
    statusInactiveOpt: 'Inaktive',
    statusExpiredOpt: 'Utløpte',
    exportBtn: 'Eksporter',
    selectedKeys: 'Valgte nøkler: {{count}}',
    chooseAction: 'Velg en handling…',
    actExtend: 'Forleng gyldighet',
    actActivate: 'Aktiver',
    clearSelection: 'Tøm valg',
    loadingKeys: 'Laster nøkler…',
    empty: 'Ingen API-nøkler',
    prev: 'Forrige',
    next: 'Neste',
    pageOf: 'Side {{cur}} av {{total}}',
    extendModalTitle: 'Forleng nøkkelens gyldighet',
    extendBtn: 'Forleng',
    extendFor: 'Forleng nøkkelens gyldighet for:',
    numDays: 'Antall dager',
    bulkExtendTitle: 'Forleng nøklenes gyldighet',
    bulkActivateTitle: 'Aktiver nøkler',
    execute: 'Utfør',
    selectedN: '{{count}} nøkler valgt',
    numDaysExtend: 'Antall dager å forlenge',
    activateInfo: 'De valgte nøklene blir aktivert.',
    revokeWarn: 'Advarsel: de valgte nøklene blir tilbakekalt og slutter å fungere.',
  },
  is: {
    title: 'Stjórnun API-lykla',
    subtitle: 'Yfirlit og stjórnun allra API-lykla',
    loadFailed: 'Ekki tókst að hlaða API-lykla',
    selectAtLeastOne: 'Veldu að minnsta kosti einn lykil',
    bulkRevokeTitle: 'Afturkalla lykla',
    bulkRevokeConfirm: 'Ertu viss um að þú viljir afturkalla {{count}} lykla?',
    bulkOpError: 'Villa við að framkvæma aðgerðina',
    keyCopied: 'Lykill afritaður á klippiborð',
    keyExtended: 'Lykill framlengdur um {{days}} daga',
    extendError: 'Villa við að framlengja lykilinn',
    revokeTitle: 'Afturkalla API-lykil',
    revokeConfirm: 'Ertu viss um að þú viljir afturkalla lykilinn fyrir {{name}}?',
    revoke: 'Afturkalla',
    cancel: 'Hætta við',
    keyRevoked: 'Lykillinn hefur verið afturkallaður',
    revokeError: 'Villa við að afturkalla lykilinn',
    keyReactivated: 'Lykillinn hefur verið endurvirkjaður',
    reactivateError: 'Villa við að endurvirkja lykilinn',
    regenerateTitle: 'Endurmynda API-lykil',
    regenerateConfirm:
      'Ertu viss um að þú viljir mynda nýjan lykil fyrir {{name}}? Gamli lykillinn hættir að virka.',
    regenerate: 'Endurmynda',
    keyRegenerated: 'Nýr lykill myndaður og afritaður á klippiborð',
    regenerateError: 'Villa við að endurmynda lykilinn',
    colKey: 'API-lykill',
    colClient: 'Viðskiptavinur',
    colPlan: 'Áætlun',
    colStatus: 'Staða',
    colExpires: 'Rennur út',
    colActions: 'Aðgerðir',
    standaloneName: 'Sjálfstæður lykill',
    standaloneDesc1: ' — veðurlykill án úthlutunar til viðskiptavinar. Áætlun: ',
    standaloneDesc2: ' (að hámarki 4 strings, kvik færibreytur í REST).',
    standaloneCreated:
      'Sjálfstæður lykill stofnaður. Afritaðu gildið núna — við sýnum það ekki aftur:',
    errorWord: 'Villa',
    addStandaloneBtn: '+ Sjálfstæður lykill',
    syncResult:
      'Sync Gravitee: sótti {{fetched}} lykla, bætti við {{imported}}, uppfærði {{updated}}, sleppti {{skipped}}. Tími: {{ms}}ms',
    syncGraviteeTitle: 'Sækja lykla frá Gravitee og flytja inn sem sjálfstæða lykla (með afdælun)',
    syncGraviteeBtn: '⟲ Samstilla Gravitee',
    searchPlaceholder: 'Leita eftir nafni viðskiptavinar eða lykli…',
    allStatuses: 'Allar stöður',
    statusActiveOpt: 'Virkir',
    statusInactiveOpt: 'Óvirkir',
    statusExpiredOpt: 'Útrunnir',
    exportBtn: 'Flytja út',
    selectedKeys: 'Valdir lyklar: {{count}}',
    chooseAction: 'Veldu aðgerð…',
    actExtend: 'Framlengja gildistíma',
    actActivate: 'Virkja',
    clearSelection: 'Hreinsa val',
    loadingKeys: 'Hleð lykla…',
    empty: 'Engir API-lyklar',
    prev: 'Fyrri',
    next: 'Næsta',
    pageOf: 'Síða {{cur}} af {{total}}',
    extendModalTitle: 'Framlengja gildistíma lykils',
    extendBtn: 'Framlengja',
    extendFor: 'Framlengja gildistíma lykils fyrir:',
    numDays: 'Fjöldi daga',
    bulkExtendTitle: 'Framlengja gildistíma lykla',
    bulkActivateTitle: 'Virkja lykla',
    execute: 'Framkvæma',
    selectedN: '{{count}} lyklar valdir',
    numDaysExtend: 'Fjöldi daga til framlengingar',
    activateInfo: 'Völdu lyklarnir verða virkjaðir.',
    revokeWarn: 'Viðvörun: völdu lyklarnir verða afturkallaðir og hætta að virka.',
  },
  uk: {
    title: 'Керування ключами API',
    subtitle: 'Огляд і керування всіма ключами API',
    loadFailed: 'Не вдалося завантажити ключі API',
    selectAtLeastOne: 'Виберіть принаймні один ключ',
    bulkRevokeTitle: 'Анулювати ключі',
    bulkRevokeConfirm: 'Ви впевнені, що хочете анулювати {{count}} ключів?',
    bulkOpError: 'Помилка під час виконання операції',
    keyCopied: 'Ключ скопійовано в буфер обміну',
    keyExtended: 'Ключ продовжено на {{days}} днів',
    extendError: 'Помилка під час продовження ключа',
    revokeTitle: 'Анулювати ключ API',
    revokeConfirm: 'Ви впевнені, що хочете анулювати ключ для {{name}}?',
    revoke: 'Анулювати',
    cancel: 'Скасувати',
    keyRevoked: 'Ключ анульовано',
    revokeError: 'Помилка під час анулювання ключа',
    keyReactivated: 'Ключ повторно активовано',
    reactivateError: 'Помилка під час повторної активації ключа',
    regenerateTitle: 'Регенерувати ключ API',
    regenerateConfirm:
      'Ви впевнені, що хочете згенерувати новий ключ для {{name}}? Старий ключ перестане працювати.',
    regenerate: 'Регенерувати',
    keyRegenerated: 'Новий ключ згенеровано і скопійовано в буфер обміну',
    regenerateError: 'Помилка під час регенерації ключа',
    colKey: 'Ключ API',
    colClient: 'Клієнт',
    colPlan: 'План',
    colStatus: 'Статус',
    colExpires: 'Закінчується',
    colActions: 'Дії',
    standaloneName: 'Окремий ключ',
    standaloneDesc1: " — погодний ключ без прив'язки до клієнта. План: ",
    standaloneDesc2: ' (макс. 4 стрінги, динамічні параметри в REST).',
    standaloneCreated:
      'Окремий ключ створено. Скопіюйте значення зараз — ми більше його не покажемо:',
    errorWord: 'Помилка',
    addStandaloneBtn: '+ Окремий ключ',
    syncResult:
      'Sync Gravitee: отримано {{fetched}} ключів, додано {{imported}}, оновлено {{updated}}, пропущено {{skipped}}. Час: {{ms}}мс',
    syncGraviteeTitle: 'Отримати ключі з Gravitee і імпортувати як окремі ключі (з дедуплікацією)',
    syncGraviteeBtn: '⟲ Синхронізувати Gravitee',
    searchPlaceholder: 'Пошук за назвою клієнта або ключем…',
    allStatuses: 'Усі статуси',
    statusActiveOpt: 'Активні',
    statusInactiveOpt: 'Неактивні',
    statusExpiredOpt: 'Закінчилися',
    exportBtn: 'Експортувати',
    selectedKeys: 'Вибрані ключі: {{count}}',
    chooseAction: 'Виберіть дію…',
    actExtend: 'Продовжити дію',
    actActivate: 'Активувати',
    clearSelection: 'Очистити вибір',
    loadingKeys: 'Завантаження ключів…',
    empty: 'Немає ключів API',
    prev: 'Попередня',
    next: 'Наступна',
    pageOf: 'Сторінка {{cur}} з {{total}}',
    extendModalTitle: 'Продовжити дію ключа',
    extendBtn: 'Продовжити',
    extendFor: 'Продовжити дію ключа для:',
    numDays: 'Кількість днів',
    bulkExtendTitle: 'Продовжити дію ключів',
    bulkActivateTitle: 'Активувати ключі',
    execute: 'Виконати',
    selectedN: 'Вибрано {{count}} ключів',
    numDaysExtend: 'Кількість днів продовження',
    activateInfo: 'Вибрані ключі буде активовано.',
    revokeWarn: 'Увага: вибрані ключі буде анульовано, і вони перестануть працювати.',
  },
  sr: {
    title: 'Управљање API кључевима',
    subtitle: 'Преглед и управљање свим API кључевима',
    loadFailed: 'Није могуће учитати API кључеве',
    selectAtLeastOne: 'Изаберите барем један кључ',
    bulkRevokeTitle: 'Опозови кључеве',
    bulkRevokeConfirm: 'Да ли сте сигурни да желите да опозовете {{count}} кључева?',
    bulkOpError: 'Грешка при извршавању операције',
    keyCopied: 'Кључ копиран у клипборд',
    keyExtended: 'Кључ продужен за {{days}} дана',
    extendError: 'Грешка при продужавању кључа',
    revokeTitle: 'Опозови API кључ',
    revokeConfirm: 'Да ли сте сигурни да желите да опозовете кључ за {{name}}?',
    revoke: 'Опозови',
    cancel: 'Откажи',
    keyRevoked: 'Кључ је опозван',
    revokeError: 'Грешка при опозиву кључа',
    keyReactivated: 'Кључ је поново активиран',
    reactivateError: 'Грешка при поновној активацији кључа',
    regenerateTitle: 'Регенериши API кључ',
    regenerateConfirm:
      'Да ли сте сигурни да желите да генеришете нови кључ за {{name}}? Стари кључ ће престати да ради.',
    regenerate: 'Регенериши',
    keyRegenerated: 'Нови кључ генерисан и копиран у клипборд',
    regenerateError: 'Грешка при регенерацији кључа',
    colKey: 'API кључ',
    colClient: 'Клијент',
    colPlan: 'План',
    colStatus: 'Статус',
    colExpires: 'Истиче',
    colActions: 'Радње',
    standaloneName: 'Самостални кључ',
    standaloneDesc1: ' — временски кључ без додељивања клијенту. План: ',
    standaloneDesc2: ' (највише 4 стринга, динамички параметри у REST).',
    standaloneCreated:
      'Самостални кључ креиран. Копирајте вредност сада — нећемо је поново приказати:',
    errorWord: 'Грешка',
    addStandaloneBtn: '+ Самостални кључ',
    syncResult:
      'Sync Gravitee: преузето {{fetched}} кључева, додато {{imported}}, ажурирано {{updated}}, прескочено {{skipped}}. Време: {{ms}}ms',
    syncGraviteeTitle:
      'Преузми кључеве из Gravitee и увези као самосталне кључеве (са дедупликацијом)',
    syncGraviteeBtn: '⟲ Синхронизуј Gravitee',
    searchPlaceholder: 'Претрага по имену клијента или кључу…',
    allStatuses: 'Сви статуси',
    statusActiveOpt: 'Активни',
    statusInactiveOpt: 'Неактивни',
    statusExpiredOpt: 'Истекли',
    exportBtn: 'Извези',
    selectedKeys: 'Изабрани кључеви: {{count}}',
    chooseAction: 'Изаберите радњу…',
    actExtend: 'Продужи важност',
    actActivate: 'Активирај',
    clearSelection: 'Очисти избор',
    loadingKeys: 'Учитавање кључева…',
    empty: 'Нема API кључева',
    prev: 'Претходна',
    next: 'Следећа',
    pageOf: 'Страница {{cur}} од {{total}}',
    extendModalTitle: 'Продужи важност кључа',
    extendBtn: 'Продужи',
    extendFor: 'Продужи важност кључа за:',
    numDays: 'Број дана',
    bulkExtendTitle: 'Продужи важност кључева',
    bulkActivateTitle: 'Активирај кључеве',
    execute: 'Изврши',
    selectedN: 'Изабрано {{count}} кључева',
    numDaysExtend: 'Број дана продужења',
    activateInfo: 'Изабрани кључеви ће бити активирани.',
    revokeWarn: 'Упозорење: изабрани кључеви ће бити опозвани и престаће да раде.',
  },
  sq: {
    title: 'Menaxhimi i çelësave API',
    subtitle: 'Pamje e përgjithshme dhe menaxhimi i të gjithë çelësave API',
    loadFailed: 'Dështoi ngarkimi i çelësave API',
    selectAtLeastOne: 'Zgjidh të paktën një çelës',
    bulkRevokeTitle: 'Revoko çelësat',
    bulkRevokeConfirm: 'Je i sigurt që dëshiron të revokosh {{count}} çelësa?',
    bulkOpError: 'Gabim gjatë ekzekutimit të operacionit',
    keyCopied: 'Çelësi u kopjua në clipboard',
    keyExtended: 'Çelësi u zgjat me {{days}} ditë',
    extendError: 'Gabim gjatë zgjatjes së çelësit',
    revokeTitle: 'Revoko çelësin API',
    revokeConfirm: 'Je i sigurt që dëshiron të revokosh çelësin për {{name}}?',
    revoke: 'Revoko',
    cancel: 'Anulo',
    keyRevoked: 'Çelësi u revokua',
    revokeError: 'Gabim gjatë revokimit të çelësit',
    keyReactivated: 'Çelësi u riaktivizua',
    reactivateError: 'Gabim gjatë riaktivizimit të çelësit',
    regenerateTitle: 'Rigjenero çelësin API',
    regenerateConfirm:
      'Je i sigurt që dëshiron të gjenerosh një çelës të ri për {{name}}? Çelësi i vjetër do të ndalojë së punuari.',
    regenerate: 'Rigjenero',
    keyRegenerated: 'Çelës i ri u gjenerua dhe u kopjua në clipboard',
    regenerateError: 'Gabim gjatë rigjenerimit të çelësit',
    colKey: 'Çelësi API',
    colClient: 'Klienti',
    colPlan: 'Plani',
    colStatus: 'Statusi',
    colExpires: 'Skadon',
    colActions: 'Veprime',
    standaloneName: 'Çelës i pavarur',
    standaloneDesc1: ' — çelës moti pa caktim te një klient. Plani: ',
    standaloneDesc2: ' (maks. 4 strings, parametra dinamikë në REST).',
    standaloneCreated: 'Çelës i pavarur u krijua. Kopjo vlerën tani — nuk do ta shfaqim sërish:',
    errorWord: 'Gabim',
    addStandaloneBtn: '+ Çelës i pavarur',
    syncResult:
      'Sync Gravitee: u morën {{fetched}} çelësa, u shtuan {{imported}}, u përditësuan {{updated}}, u anashkaluan {{skipped}}. Koha: {{ms}}ms',
    syncGraviteeTitle:
      'Merr çelësat nga Gravitee dhe importoji si çelësa të pavarur (me deduplikim)',
    syncGraviteeBtn: '⟲ Sinkronizo Gravitee',
    searchPlaceholder: 'Kërko sipas emrit të klientit ose çelësit…',
    allStatuses: 'Të gjitha statuset',
    statusActiveOpt: 'Aktivë',
    statusInactiveOpt: 'Joaktivë',
    statusExpiredOpt: 'Të skaduar',
    exportBtn: 'Eksporto',
    selectedKeys: 'Çelësat e zgjedhur: {{count}}',
    chooseAction: 'Zgjidh një veprim…',
    actExtend: 'Zgjat vlefshmërinë',
    actActivate: 'Aktivizo',
    clearSelection: 'Pastro zgjedhjen',
    loadingKeys: 'Po ngarkohen çelësat…',
    empty: 'Asnjë çelës API',
    prev: 'Mëparshme',
    next: 'Tjetra',
    pageOf: 'Faqja {{cur}} nga {{total}}',
    extendModalTitle: 'Zgjat vlefshmërinë e çelësit',
    extendBtn: 'Zgjat',
    extendFor: 'Zgjat vlefshmërinë e çelësit për:',
    numDays: 'Numri i ditëve',
    bulkExtendTitle: 'Zgjat vlefshmërinë e çelësave',
    bulkActivateTitle: 'Aktivizo çelësat',
    execute: 'Ekzekuto',
    selectedN: 'U zgjodhën {{count}} çelësa',
    numDaysExtend: 'Numri i ditëve të zgjatjes',
    activateInfo: 'Çelësat e zgjedhur do të aktivizohen.',
    revokeWarn: 'Kujdes: çelësat e zgjedhur do të revokohen dhe do të ndalojnë së punuari.',
  },
  mk: {
    title: 'Управување со API клучеви',
    subtitle: 'Преглед и управување со сите API клучеви',
    loadFailed: 'Не може да се вчитаат API клучевите',
    selectAtLeastOne: 'Изберете барем еден клуч',
    bulkRevokeTitle: 'Отповикај клучеви',
    bulkRevokeConfirm: 'Дали сте сигурни дека сакате да отповикате {{count}} клучеви?',
    bulkOpError: 'Грешка при извршување на операцијата',
    keyCopied: 'Клучот е копиран во клипбордот',
    keyExtended: 'Клучот е продолжен за {{days}} дена',
    extendError: 'Грешка при продолжување на клучот',
    revokeTitle: 'Отповикај API клуч',
    revokeConfirm: 'Дали сте сигурни дека сакате да го отповикате клучот за {{name}}?',
    revoke: 'Отповикај',
    cancel: 'Откажи',
    keyRevoked: 'Клучот е отповикан',
    revokeError: 'Грешка при отповикување на клучот',
    keyReactivated: 'Клучот е повторно активиран',
    reactivateError: 'Грешка при повторно активирање на клучот',
    regenerateTitle: 'Регенерирај API клуч',
    regenerateConfirm:
      'Дали сте сигурни дека сакате да генерирате нов клуч за {{name}}? Стариот клуч ќе престане да работи.',
    regenerate: 'Регенерирај',
    keyRegenerated: 'Нов клуч е генериран и копиран во клипбордот',
    regenerateError: 'Грешка при регенерирање на клучот',
    colKey: 'API клуч',
    colClient: 'Клиент',
    colPlan: 'План',
    colStatus: 'Статус',
    colExpires: 'Истекува',
    colActions: 'Дејства',
    standaloneName: 'Самостоен клуч',
    standaloneDesc1: ' — временски клуч без доделување на клиент. План: ',
    standaloneDesc2: ' (најмногу 4 стринга, динамички параметри во REST).',
    standaloneCreated:
      'Самостоен клуч е создаден. Копирајте ја вредноста сега — нема да ја прикажеме повторно:',
    errorWord: 'Грешка',
    addStandaloneBtn: '+ Самостоен клуч',
    syncResult:
      'Sync Gravitee: преземени {{fetched}} клучеви, додадени {{imported}}, ажурирани {{updated}}, прескокнати {{skipped}}. Време: {{ms}}ms',
    syncGraviteeTitle:
      'Преземи клучеви од Gravitee и увези ги како самостојни клучеви (со дедупликација)',
    syncGraviteeBtn: '⟲ Синхронизирај Gravitee',
    searchPlaceholder: 'Пребарувај по име на клиент или клуч…',
    allStatuses: 'Сите статуси',
    statusActiveOpt: 'Активни',
    statusInactiveOpt: 'Неактивни',
    statusExpiredOpt: 'Истечени',
    exportBtn: 'Извези',
    selectedKeys: 'Избрани клучеви: {{count}}',
    chooseAction: 'Изберете дејство…',
    actExtend: 'Продолжи важност',
    actActivate: 'Активирај',
    clearSelection: 'Исчисти избор',
    loadingKeys: 'Се вчитуваат клучеви…',
    empty: 'Нема API клучеви',
    prev: 'Претходна',
    next: 'Следна',
    pageOf: 'Страница {{cur}} од {{total}}',
    extendModalTitle: 'Продолжи важност на клучот',
    extendBtn: 'Продолжи',
    extendFor: 'Продолжи важност на клучот за:',
    numDays: 'Број денови',
    bulkExtendTitle: 'Продолжи важност на клучевите',
    bulkActivateTitle: 'Активирај клучеви',
    execute: 'Изврши',
    selectedN: 'Избрани {{count}} клучеви',
    numDaysExtend: 'Број денови за продолжување',
    activateInfo: 'Избраните клучеви ќе бидат активирани.',
    revokeWarn: 'Внимание: избраните клучеви ќе бидат отповикани и ќе престанат да работат.',
  },
  bs: {
    title: 'Upravljanje API ključevima',
    subtitle: 'Pregled i upravljanje svim API ključevima',
    loadFailed: 'Nije moguće učitati API ključeve',
    selectAtLeastOne: 'Odaberite barem jedan ključ',
    bulkRevokeTitle: 'Opozovi ključeve',
    bulkRevokeConfirm: 'Jeste li sigurni da želite opozvati {{count}} ključeva?',
    bulkOpError: 'Greška pri izvođenju operacije',
    keyCopied: 'Ključ kopiran u međuspremnik',
    keyExtended: 'Ključ produžen za {{days}} dana',
    extendError: 'Greška pri produženju ključa',
    revokeTitle: 'Opozovi API ključ',
    revokeConfirm: 'Jeste li sigurni da želite opozvati ključ za {{name}}?',
    revoke: 'Opozovi',
    cancel: 'Otkaži',
    keyRevoked: 'Ključ je opozvan',
    revokeError: 'Greška pri opozivu ključa',
    keyReactivated: 'Ključ je ponovo aktiviran',
    reactivateError: 'Greška pri ponovnoj aktivaciji ključa',
    regenerateTitle: 'Regeneriši API ključ',
    regenerateConfirm:
      'Jeste li sigurni da želite generisati novi ključ za {{name}}? Stari ključ će prestati raditi.',
    regenerate: 'Regeneriši',
    keyRegenerated: 'Novi ključ generisan i kopiran u međuspremnik',
    regenerateError: 'Greška pri regeneraciji ključa',
    colKey: 'API ključ',
    colClient: 'Klijent',
    colPlan: 'Plan',
    colStatus: 'Status',
    colExpires: 'Ističe',
    colActions: 'Radnje',
    standaloneName: 'Samostalni ključ',
    standaloneDesc1: ' — vremenski ključ bez dodjele klijentu. Plan: ',
    standaloneDesc2: ' (najviše 4 niza, dinamički parametri u REST).',
    standaloneCreated:
      'Samostalni ključ kreiran. Kopirajte vrijednost sada — nećemo je ponovo prikazati:',
    errorWord: 'Greška',
    addStandaloneBtn: '+ Samostalni ključ',
    syncResult:
      'Sync Gravitee: dohvaćeno {{fetched}} ključeva, dodano {{imported}}, ažurirano {{updated}}, preskočeno {{skipped}}. Vrijeme: {{ms}}ms',
    syncGraviteeTitle:
      'Dohvati ključeve iz Gravitee i uvezi kao samostalne ključeve (s deduplikacijom)',
    syncGraviteeBtn: '⟲ Sinhronizuj Gravitee',
    searchPlaceholder: 'Traži po nazivu klijenta ili ključu…',
    allStatuses: 'Svi statusi',
    statusActiveOpt: 'Aktivni',
    statusInactiveOpt: 'Neaktivni',
    statusExpiredOpt: 'Istekli',
    exportBtn: 'Izvezi',
    selectedKeys: 'Odabrani ključevi: {{count}}',
    chooseAction: 'Odaberite radnju…',
    actExtend: 'Produži valjanost',
    actActivate: 'Aktiviraj',
    clearSelection: 'Očisti odabir',
    loadingKeys: 'Učitavanje ključeva…',
    empty: 'Nema API ključeva',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    extendModalTitle: 'Produži valjanost ključa',
    extendBtn: 'Produži',
    extendFor: 'Produži valjanost ključa za:',
    numDays: 'Broj dana',
    bulkExtendTitle: 'Produži valjanost ključeva',
    bulkActivateTitle: 'Aktiviraj ključeve',
    execute: 'Izvrši',
    selectedN: 'Odabrano {{count}} ključeva',
    numDaysExtend: 'Broj dana produženja',
    activateInfo: 'Odabrani ključevi će biti aktivirani.',
    revokeWarn: 'Upozorenje: odabrani ključevi će biti opozvani i prestat će raditi.',
  },
};

const adminsTr: Record<string, Dict> = {
  en: {
    loadFailed: 'Failed to load administrators',
    pwMin8: 'Password must be at least 8 characters',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Read only',
    colName: 'Name',
    colRole: 'Role',
    colStatus: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    colLastLogin: 'Last login',
    never: 'Never',
    colCreatedAt: 'Created at',
    colActions: 'Actions',
    edit: 'Edit',
    changePassword: 'Change password',
    delete: 'Delete',
    title: 'Administrators',
    subtitle: 'Manage system administrator accounts',
    addAdmin: 'Add administrator',
    modalEdit: 'Edit administrator',
    modalAdd: 'Add administrator',
    email: 'Email',
    fullName: 'Full name',
    password: 'Password',
    min8chars: 'Minimum 8 characters',
    role: 'Role',
    accountActive: 'Account active',
    cancel: 'Cancel',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    createAdmin: 'Create administrator',
    pwModalTitle: 'Change password: {{name}}',
    newPassword: 'New password',
    changePasswordBtn: 'Change password',
    deleteTitle: 'Delete administrator',
    deleteConfirm:
      'Are you sure you want to delete administrator "{{name}}"? This operation is irreversible.',
    deleteBtn: 'Delete',
  },
  pl: {
    loadFailed: 'Nie udało się załadować administratorów',
    pwMin8: 'Hasło musi mieć minimum 8 znaków',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Tylko odczyt',
    colName: 'Imię',
    colRole: 'Rola',
    colStatus: 'Status',
    statusActive: 'Aktywny',
    statusInactive: 'Nieaktywny',
    colLastLogin: 'Ostatnie logowanie',
    never: 'Nigdy',
    colCreatedAt: 'Data utworzenia',
    colActions: 'Akcje',
    edit: 'Edytuj',
    changePassword: 'Zmień hasło',
    delete: 'Usuń',
    title: 'Administratorzy',
    subtitle: 'Zarządzanie kontami administratorów systemu',
    addAdmin: 'Dodaj administratora',
    modalEdit: 'Edytuj administratora',
    modalAdd: 'Dodaj administratora',
    email: 'Email',
    fullName: 'Imię i nazwisko',
    password: 'Hasło',
    min8chars: 'Minimum 8 znaków',
    role: 'Rola',
    accountActive: 'Konto aktywne',
    cancel: 'Anuluj',
    saving: 'Zapisywanie…',
    saveChanges: 'Zapisz zmiany',
    createAdmin: 'Utwórz administratora',
    pwModalTitle: 'Zmiana hasła: {{name}}',
    newPassword: 'Nowe hasło',
    changePasswordBtn: 'Zmień hasło',
    deleteTitle: 'Usuń administratora',
    deleteConfirm:
      'Czy na pewno chcesz usunąć administratora „{{name}}”? Ta operacja jest nieodwracalna.',
    deleteBtn: 'Usuń',
  },
  de: {
    loadFailed: 'Administratoren konnten nicht geladen werden',
    pwMin8: 'Das Passwort muss mindestens 8 Zeichen lang sein',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Nur Lesen',
    colName: 'Name',
    colRole: 'Rolle',
    colStatus: 'Status',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    colLastLogin: 'Letzte Anmeldung',
    never: 'Nie',
    colCreatedAt: 'Erstellt am',
    colActions: 'Aktionen',
    edit: 'Bearbeiten',
    changePassword: 'Passwort ändern',
    delete: 'Löschen',
    title: 'Administratoren',
    subtitle: 'Verwaltung der System-Administratorkonten',
    addAdmin: 'Administrator hinzufügen',
    modalEdit: 'Administrator bearbeiten',
    modalAdd: 'Administrator hinzufügen',
    email: 'E-Mail',
    fullName: 'Vor- und Nachname',
    password: 'Passwort',
    min8chars: 'Mindestens 8 Zeichen',
    role: 'Rolle',
    accountActive: 'Konto aktiv',
    cancel: 'Abbrechen',
    saving: 'Wird gespeichert…',
    saveChanges: 'Änderungen speichern',
    createAdmin: 'Administrator erstellen',
    pwModalTitle: 'Passwort ändern: {{name}}',
    newPassword: 'Neues Passwort',
    changePasswordBtn: 'Passwort ändern',
    deleteTitle: 'Administrator löschen',
    deleteConfirm:
      'Möchten Sie den Administrator „{{name}}“ wirklich löschen? Dieser Vorgang ist unwiderruflich.',
    deleteBtn: 'Löschen',
  },
  fr: {
    loadFailed: 'Échec du chargement des administrateurs',
    pwMin8: 'Le mot de passe doit comporter au moins 8 caractères',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Lecture seule',
    colName: 'Nom',
    colRole: 'Rôle',
    colStatus: 'Statut',
    statusActive: 'Actif',
    statusInactive: 'Inactif',
    colLastLogin: 'Dernière connexion',
    never: 'Jamais',
    colCreatedAt: 'Créé le',
    colActions: 'Actions',
    edit: 'Modifier',
    changePassword: 'Changer le mot de passe',
    delete: 'Supprimer',
    title: 'Administrateurs',
    subtitle: 'Gérer les comptes administrateur du système',
    addAdmin: 'Ajouter un administrateur',
    modalEdit: "Modifier l'administrateur",
    modalAdd: 'Ajouter un administrateur',
    email: 'E-mail',
    fullName: 'Nom complet',
    password: 'Mot de passe',
    min8chars: 'Minimum 8 caractères',
    role: 'Rôle',
    accountActive: 'Compte actif',
    cancel: 'Annuler',
    saving: 'Enregistrement…',
    saveChanges: 'Enregistrer les modifications',
    createAdmin: "Créer l'administrateur",
    pwModalTitle: 'Changer le mot de passe : {{name}}',
    newPassword: 'Nouveau mot de passe',
    changePasswordBtn: 'Changer le mot de passe',
    deleteTitle: "Supprimer l'administrateur",
    deleteConfirm:
      "Voulez-vous vraiment supprimer l'administrateur « {{name}} » ? Cette opération est irréversible.",
    deleteBtn: 'Supprimer',
  },
  es: {
    loadFailed: 'No se pudieron cargar los administradores',
    pwMin8: 'La contraseña debe tener al menos 8 caracteres',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Solo lectura',
    colName: 'Nombre',
    colRole: 'Rol',
    colStatus: 'Estado',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    colLastLogin: 'Último inicio de sesión',
    never: 'Nunca',
    colCreatedAt: 'Creado el',
    colActions: 'Acciones',
    edit: 'Editar',
    changePassword: 'Cambiar contraseña',
    delete: 'Eliminar',
    title: 'Administradores',
    subtitle: 'Gestionar las cuentas de administrador del sistema',
    addAdmin: 'Añadir administrador',
    modalEdit: 'Editar administrador',
    modalAdd: 'Añadir administrador',
    email: 'Correo electrónico',
    fullName: 'Nombre completo',
    password: 'Contraseña',
    min8chars: 'Mínimo 8 caracteres',
    role: 'Rol',
    accountActive: 'Cuenta activa',
    cancel: 'Cancelar',
    saving: 'Guardando…',
    saveChanges: 'Guardar cambios',
    createAdmin: 'Crear administrador',
    pwModalTitle: 'Cambiar contraseña: {{name}}',
    newPassword: 'Nueva contraseña',
    changePasswordBtn: 'Cambiar contraseña',
    deleteTitle: 'Eliminar administrador',
    deleteConfirm:
      '¿Seguro que quieres eliminar al administrador «{{name}}»? Esta operación es irreversible.',
    deleteBtn: 'Eliminar',
  },
  it: {
    loadFailed: 'Impossibile caricare gli amministratori',
    pwMin8: 'La password deve contenere almeno 8 caratteri',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Solo lettura',
    colName: 'Nome',
    colRole: 'Ruolo',
    colStatus: 'Stato',
    statusActive: 'Attivo',
    statusInactive: 'Inattivo',
    colLastLogin: 'Ultimo accesso',
    never: 'Mai',
    colCreatedAt: 'Creato il',
    colActions: 'Azioni',
    edit: 'Modifica',
    changePassword: 'Cambia password',
    delete: 'Elimina',
    title: 'Amministratori',
    subtitle: 'Gestisci gli account amministratore del sistema',
    addAdmin: 'Aggiungi amministratore',
    modalEdit: 'Modifica amministratore',
    modalAdd: 'Aggiungi amministratore',
    email: 'Email',
    fullName: 'Nome e cognome',
    password: 'Password',
    min8chars: 'Minimo 8 caratteri',
    role: 'Ruolo',
    accountActive: 'Account attivo',
    cancel: 'Annulla',
    saving: 'Salvataggio…',
    saveChanges: 'Salva modifiche',
    createAdmin: 'Crea amministratore',
    pwModalTitle: 'Cambio password: {{name}}',
    newPassword: 'Nuova password',
    changePasswordBtn: 'Cambia password',
    deleteTitle: 'Elimina amministratore',
    deleteConfirm:
      "Vuoi davvero eliminare l'amministratore «{{name}}»? Questa operazione è irreversibile.",
    deleteBtn: 'Elimina',
  },
  pt: {
    loadFailed: 'Não foi possível carregar os administradores',
    pwMin8: 'A palavra-passe deve ter pelo menos 8 caracteres',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Apenas leitura',
    colName: 'Nome',
    colRole: 'Função',
    colStatus: 'Estado',
    statusActive: 'Ativo',
    statusInactive: 'Inativo',
    colLastLogin: 'Último início de sessão',
    never: 'Nunca',
    colCreatedAt: 'Criado em',
    colActions: 'Ações',
    edit: 'Editar',
    changePassword: 'Alterar palavra-passe',
    delete: 'Eliminar',
    title: 'Administradores',
    subtitle: 'Gerir as contas de administrador do sistema',
    addAdmin: 'Adicionar administrador',
    modalEdit: 'Editar administrador',
    modalAdd: 'Adicionar administrador',
    email: 'E-mail',
    fullName: 'Nome completo',
    password: 'Palavra-passe',
    min8chars: 'Mínimo 8 caracteres',
    role: 'Função',
    accountActive: 'Conta ativa',
    cancel: 'Cancelar',
    saving: 'A guardar…',
    saveChanges: 'Guardar alterações',
    createAdmin: 'Criar administrador',
    pwModalTitle: 'Alterar palavra-passe: {{name}}',
    newPassword: 'Nova palavra-passe',
    changePasswordBtn: 'Alterar palavra-passe',
    deleteTitle: 'Eliminar administrador',
    deleteConfirm:
      'Tem a certeza de que quer eliminar o administrador «{{name}}»? Esta operação é irreversível.',
    deleteBtn: 'Eliminar',
  },
  nl: {
    loadFailed: 'Kan beheerders niet laden',
    pwMin8: 'Het wachtwoord moet minstens 8 tekens bevatten',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Alleen lezen',
    colName: 'Naam',
    colRole: 'Rol',
    colStatus: 'Status',
    statusActive: 'Actief',
    statusInactive: 'Inactief',
    colLastLogin: 'Laatste aanmelding',
    never: 'Nooit',
    colCreatedAt: 'Aangemaakt op',
    colActions: 'Acties',
    edit: 'Bewerken',
    changePassword: 'Wachtwoord wijzigen',
    delete: 'Verwijderen',
    title: 'Beheerders',
    subtitle: 'Beheer systeembeheerdersaccounts',
    addAdmin: 'Beheerder toevoegen',
    modalEdit: 'Beheerder bewerken',
    modalAdd: 'Beheerder toevoegen',
    email: 'E-mail',
    fullName: 'Volledige naam',
    password: 'Wachtwoord',
    min8chars: 'Minimaal 8 tekens',
    role: 'Rol',
    accountActive: 'Account actief',
    cancel: 'Annuleren',
    saving: 'Opslaan…',
    saveChanges: 'Wijzigingen opslaan',
    createAdmin: 'Beheerder aanmaken',
    pwModalTitle: 'Wachtwoord wijzigen: {{name}}',
    newPassword: 'Nieuw wachtwoord',
    changePasswordBtn: 'Wachtwoord wijzigen',
    deleteTitle: 'Beheerder verwijderen',
    deleteConfirm:
      'Weet je zeker dat je beheerder "{{name}}" wilt verwijderen? Deze bewerking is onomkeerbaar.',
    deleteBtn: 'Verwijderen',
  },
  da: {
    loadFailed: 'Kunne ikke indlæse administratorer',
    pwMin8: 'Adgangskoden skal være mindst 8 tegn',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Kun læsning',
    colName: 'Navn',
    colRole: 'Rolle',
    colStatus: 'Status',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    colLastLogin: 'Seneste login',
    never: 'Aldrig',
    colCreatedAt: 'Oprettet den',
    colActions: 'Handlinger',
    edit: 'Rediger',
    changePassword: 'Skift adgangskode',
    delete: 'Slet',
    title: 'Administratorer',
    subtitle: 'Administrer systemets administratorkonti',
    addAdmin: 'Tilføj administrator',
    modalEdit: 'Rediger administrator',
    modalAdd: 'Tilføj administrator',
    email: 'E-mail',
    fullName: 'Fulde navn',
    password: 'Adgangskode',
    min8chars: 'Mindst 8 tegn',
    role: 'Rolle',
    accountActive: 'Konto aktiv',
    cancel: 'Annuller',
    saving: 'Gemmer…',
    saveChanges: 'Gem ændringer',
    createAdmin: 'Opret administrator',
    pwModalTitle: 'Skift adgangskode: {{name}}',
    newPassword: 'Ny adgangskode',
    changePasswordBtn: 'Skift adgangskode',
    deleteTitle: 'Slet administrator',
    deleteConfirm:
      'Er du sikker på, at du vil slette administratoren "{{name}}"? Denne handling kan ikke fortrydes.',
    deleteBtn: 'Slet',
  },
  sv: {
    loadFailed: 'Det gick inte att läsa in administratörer',
    pwMin8: 'Lösenordet måste vara minst 8 tecken',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Endast läsning',
    colName: 'Namn',
    colRole: 'Roll',
    colStatus: 'Status',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    colLastLogin: 'Senaste inloggning',
    never: 'Aldrig',
    colCreatedAt: 'Skapad den',
    colActions: 'Åtgärder',
    edit: 'Redigera',
    changePassword: 'Byt lösenord',
    delete: 'Ta bort',
    title: 'Administratörer',
    subtitle: 'Hantera systemets administratörskonton',
    addAdmin: 'Lägg till administratör',
    modalEdit: 'Redigera administratör',
    modalAdd: 'Lägg till administratör',
    email: 'E-post',
    fullName: 'Fullständigt namn',
    password: 'Lösenord',
    min8chars: 'Minst 8 tecken',
    role: 'Roll',
    accountActive: 'Konto aktivt',
    cancel: 'Avbryt',
    saving: 'Sparar…',
    saveChanges: 'Spara ändringar',
    createAdmin: 'Skapa administratör',
    pwModalTitle: 'Byt lösenord: {{name}}',
    newPassword: 'Nytt lösenord',
    changePasswordBtn: 'Byt lösenord',
    deleteTitle: 'Ta bort administratör',
    deleteConfirm:
      'Är du säker på att du vill ta bort administratören "{{name}}"? Den här åtgärden kan inte ångras.',
    deleteBtn: 'Ta bort',
  },
  fi: {
    loadFailed: 'Ylläpitäjien lataaminen epäonnistui',
    pwMin8: 'Salasanan on oltava vähintään 8 merkkiä',
    roleSuperAdmin: 'Pääylläpitäjä',
    roleAdmin: 'Ylläpitäjä',
    roleReadOnly: 'Vain luku',
    colName: 'Nimi',
    colRole: 'Rooli',
    colStatus: 'Tila',
    statusActive: 'Aktiivinen',
    statusInactive: 'Ei-aktiivinen',
    colLastLogin: 'Viimeisin kirjautuminen',
    never: 'Ei koskaan',
    colCreatedAt: 'Luotu',
    colActions: 'Toiminnot',
    edit: 'Muokkaa',
    changePassword: 'Vaihda salasana',
    delete: 'Poista',
    title: 'Ylläpitäjät',
    subtitle: 'Hallitse järjestelmän ylläpitäjätilejä',
    addAdmin: 'Lisää ylläpitäjä',
    modalEdit: 'Muokkaa ylläpitäjää',
    modalAdd: 'Lisää ylläpitäjä',
    email: 'Sähköposti',
    fullName: 'Koko nimi',
    password: 'Salasana',
    min8chars: 'Vähintään 8 merkkiä',
    role: 'Rooli',
    accountActive: 'Tili aktiivinen',
    cancel: 'Peruuta',
    saving: 'Tallennetaan…',
    saveChanges: 'Tallenna muutokset',
    createAdmin: 'Luo ylläpitäjä',
    pwModalTitle: 'Vaihda salasana: {{name}}',
    newPassword: 'Uusi salasana',
    changePasswordBtn: 'Vaihda salasana',
    deleteTitle: 'Poista ylläpitäjä',
    deleteConfirm:
      'Haluatko varmasti poistaa ylläpitäjän "{{name}}"? Tätä toimintoa ei voi peruuttaa.',
    deleteBtn: 'Poista',
  },
  cs: {
    loadFailed: 'Nepodařilo se načíst administrátory',
    pwMin8: 'Heslo musí mít alespoň 8 znaků',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Pouze čtení',
    colName: 'Jméno',
    colRole: 'Role',
    colStatus: 'Stav',
    statusActive: 'Aktivní',
    statusInactive: 'Neaktivní',
    colLastLogin: 'Poslední přihlášení',
    never: 'Nikdy',
    colCreatedAt: 'Vytvořeno dne',
    colActions: 'Akce',
    edit: 'Upravit',
    changePassword: 'Změnit heslo',
    delete: 'Smazat',
    title: 'Administrátoři',
    subtitle: 'Správa účtů administrátorů systému',
    addAdmin: 'Přidat administrátora',
    modalEdit: 'Upravit administrátora',
    modalAdd: 'Přidat administrátora',
    email: 'E-mail',
    fullName: 'Jméno a příjmení',
    password: 'Heslo',
    min8chars: 'Minimálně 8 znaků',
    role: 'Role',
    accountActive: 'Účet aktivní',
    cancel: 'Zrušit',
    saving: 'Ukládání…',
    saveChanges: 'Uložit změny',
    createAdmin: 'Vytvořit administrátora',
    pwModalTitle: 'Změna hesla: {{name}}',
    newPassword: 'Nové heslo',
    changePasswordBtn: 'Změnit heslo',
    deleteTitle: 'Smazat administrátora',
    deleteConfirm: 'Opravdu chcete smazat administrátora „{{name}}“? Tato operace je nevratná.',
    deleteBtn: 'Smazat',
  },
  sk: {
    loadFailed: 'Nepodarilo sa načítať administrátorov',
    pwMin8: 'Heslo musí mať aspoň 8 znakov',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Iba čítanie',
    colName: 'Meno',
    colRole: 'Rola',
    colStatus: 'Stav',
    statusActive: 'Aktívny',
    statusInactive: 'Neaktívny',
    colLastLogin: 'Posledné prihlásenie',
    never: 'Nikdy',
    colCreatedAt: 'Vytvorené dňa',
    colActions: 'Akcie',
    edit: 'Upraviť',
    changePassword: 'Zmeniť heslo',
    delete: 'Zmazať',
    title: 'Administrátori',
    subtitle: 'Správa účtov administrátorov systému',
    addAdmin: 'Pridať administrátora',
    modalEdit: 'Upraviť administrátora',
    modalAdd: 'Pridať administrátora',
    email: 'E-mail',
    fullName: 'Meno a priezvisko',
    password: 'Heslo',
    min8chars: 'Minimálne 8 znakov',
    role: 'Rola',
    accountActive: 'Účet aktívny',
    cancel: 'Zrušiť',
    saving: 'Ukladá sa…',
    saveChanges: 'Uložiť zmeny',
    createAdmin: 'Vytvoriť administrátora',
    pwModalTitle: 'Zmena hesla: {{name}}',
    newPassword: 'Nové heslo',
    changePasswordBtn: 'Zmeniť heslo',
    deleteTitle: 'Zmazať administrátora',
    deleteConfirm: 'Naozaj chcete zmazať administrátora „{{name}}“? Táto operácia je nezvratná.',
    deleteBtn: 'Zmazať',
  },
  sl: {
    loadFailed: 'Skrbnikov ni bilo mogoče naložiti',
    pwMin8: 'Geslo mora vsebovati vsaj 8 znakov',
    roleSuperAdmin: 'Super skrbnik',
    roleAdmin: 'Skrbnik',
    roleReadOnly: 'Samo branje',
    colName: 'Ime',
    colRole: 'Vloga',
    colStatus: 'Stanje',
    statusActive: 'Aktiven',
    statusInactive: 'Neaktiven',
    colLastLogin: 'Zadnja prijava',
    never: 'Nikoli',
    colCreatedAt: 'Ustvarjeno dne',
    colActions: 'Dejanja',
    edit: 'Uredi',
    changePassword: 'Spremeni geslo',
    delete: 'Izbriši',
    title: 'Skrbniki',
    subtitle: 'Upravljanje skrbniških računov sistema',
    addAdmin: 'Dodaj skrbnika',
    modalEdit: 'Uredi skrbnika',
    modalAdd: 'Dodaj skrbnika',
    email: 'E-pošta',
    fullName: 'Polno ime',
    password: 'Geslo',
    min8chars: 'Najmanj 8 znakov',
    role: 'Vloga',
    accountActive: 'Račun aktiven',
    cancel: 'Prekliči',
    saving: 'Shranjevanje…',
    saveChanges: 'Shrani spremembe',
    createAdmin: 'Ustvari skrbnika',
    pwModalTitle: 'Sprememba gesla: {{name}}',
    newPassword: 'Novo geslo',
    changePasswordBtn: 'Spremeni geslo',
    deleteTitle: 'Izbriši skrbnika',
    deleteConfirm:
      'Ali ste prepričani, da želite izbrisati skrbnika »{{name}}«? Te operacije ni mogoče razveljaviti.',
    deleteBtn: 'Izbriši',
  },
  hr: {
    loadFailed: 'Nije moguće učitati administratore',
    pwMin8: 'Lozinka mora imati najmanje 8 znakova',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Samo čitanje',
    colName: 'Ime',
    colRole: 'Uloga',
    colStatus: 'Status',
    statusActive: 'Aktivan',
    statusInactive: 'Neaktivan',
    colLastLogin: 'Zadnja prijava',
    never: 'Nikada',
    colCreatedAt: 'Stvoreno dana',
    colActions: 'Radnje',
    edit: 'Uredi',
    changePassword: 'Promijeni lozinku',
    delete: 'Obriši',
    title: 'Administratori',
    subtitle: 'Upravljanje administratorskim računima sustava',
    addAdmin: 'Dodaj administratora',
    modalEdit: 'Uredi administratora',
    modalAdd: 'Dodaj administratora',
    email: 'E-pošta',
    fullName: 'Puno ime',
    password: 'Lozinka',
    min8chars: 'Najmanje 8 znakova',
    role: 'Uloga',
    accountActive: 'Račun aktivan',
    cancel: 'Odustani',
    saving: 'Spremanje…',
    saveChanges: 'Spremi promjene',
    createAdmin: 'Stvori administratora',
    pwModalTitle: 'Promjena lozinke: {{name}}',
    newPassword: 'Nova lozinka',
    changePasswordBtn: 'Promijeni lozinku',
    deleteTitle: 'Obriši administratora',
    deleteConfirm:
      'Jeste li sigurni da želite obrisati administratora „{{name}}“? Ova operacija je nepovratna.',
    deleteBtn: 'Obriši',
  },
  hu: {
    loadFailed: 'Nem sikerült betölteni az adminisztrátorokat',
    pwMin8: 'A jelszónak legalább 8 karakterből kell állnia',
    roleSuperAdmin: 'Szuper admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Csak olvasás',
    colName: 'Név',
    colRole: 'Szerepkör',
    colStatus: 'Állapot',
    statusActive: 'Aktív',
    statusInactive: 'Inaktív',
    colLastLogin: 'Utolsó bejelentkezés',
    never: 'Soha',
    colCreatedAt: 'Létrehozva',
    colActions: 'Műveletek',
    edit: 'Szerkesztés',
    changePassword: 'Jelszó módosítása',
    delete: 'Törlés',
    title: 'Adminisztrátorok',
    subtitle: 'A rendszer adminisztrátori fiókjainak kezelése',
    addAdmin: 'Adminisztrátor hozzáadása',
    modalEdit: 'Adminisztrátor szerkesztése',
    modalAdd: 'Adminisztrátor hozzáadása',
    email: 'E-mail',
    fullName: 'Teljes név',
    password: 'Jelszó',
    min8chars: 'Legalább 8 karakter',
    role: 'Szerepkör',
    accountActive: 'Fiók aktív',
    cancel: 'Mégse',
    saving: 'Mentés…',
    saveChanges: 'Módosítások mentése',
    createAdmin: 'Adminisztrátor létrehozása',
    pwModalTitle: 'Jelszó módosítása: {{name}}',
    newPassword: 'Új jelszó',
    changePasswordBtn: 'Jelszó módosítása',
    deleteTitle: 'Adminisztrátor törlése',
    deleteConfirm:
      'Biztosan törölni szeretné a(z) „{{name}}” adminisztrátort? Ez a művelet visszafordíthatatlan.',
    deleteBtn: 'Törlés',
  },
  ro: {
    loadFailed: 'Nu s-au putut încărca administratorii',
    pwMin8: 'Parola trebuie să aibă cel puțin 8 caractere',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Doar citire',
    colName: 'Nume',
    colRole: 'Rol',
    colStatus: 'Stare',
    statusActive: 'Activ',
    statusInactive: 'Inactiv',
    colLastLogin: 'Ultima autentificare',
    never: 'Niciodată',
    colCreatedAt: 'Creat la',
    colActions: 'Acțiuni',
    edit: 'Editează',
    changePassword: 'Schimbă parola',
    delete: 'Șterge',
    title: 'Administratori',
    subtitle: 'Gestionarea conturilor de administrator ale sistemului',
    addAdmin: 'Adaugă administrator',
    modalEdit: 'Editează administrator',
    modalAdd: 'Adaugă administrator',
    email: 'E-mail',
    fullName: 'Nume complet',
    password: 'Parolă',
    min8chars: 'Minimum 8 caractere',
    role: 'Rol',
    accountActive: 'Cont activ',
    cancel: 'Anulează',
    saving: 'Se salvează…',
    saveChanges: 'Salvează modificările',
    createAdmin: 'Creează administrator',
    pwModalTitle: 'Schimbare parolă: {{name}}',
    newPassword: 'Parolă nouă',
    changePasswordBtn: 'Schimbă parola',
    deleteTitle: 'Șterge administrator',
    deleteConfirm:
      'Sigur doriți să ștergeți administratorul „{{name}}”? Această operație este ireversibilă.',
    deleteBtn: 'Șterge',
  },
  bg: {
    loadFailed: 'Неуспешно зареждане на администраторите',
    pwMin8: 'Паролата трябва да е поне 8 знака',
    roleSuperAdmin: 'Супер админ',
    roleAdmin: 'Админ',
    roleReadOnly: 'Само четене',
    colName: 'Име',
    colRole: 'Роля',
    colStatus: 'Статус',
    statusActive: 'Активен',
    statusInactive: 'Неактивен',
    colLastLogin: 'Последен вход',
    never: 'Никога',
    colCreatedAt: 'Създаден на',
    colActions: 'Действия',
    edit: 'Редактиране',
    changePassword: 'Смяна на парола',
    delete: 'Изтриване',
    title: 'Администратори',
    subtitle: 'Управление на администраторските акаунти на системата',
    addAdmin: 'Добавяне на администратор',
    modalEdit: 'Редактиране на администратор',
    modalAdd: 'Добавяне на администратор',
    email: 'Имейл',
    fullName: 'Пълно име',
    password: 'Парола',
    min8chars: 'Минимум 8 знака',
    role: 'Роля',
    accountActive: 'Акаунтът е активен',
    cancel: 'Отказ',
    saving: 'Запазване…',
    saveChanges: 'Запазване на промените',
    createAdmin: 'Създаване на администратор',
    pwModalTitle: 'Смяна на парола: {{name}}',
    newPassword: 'Нова парола',
    changePasswordBtn: 'Смяна на парола',
    deleteTitle: 'Изтриване на администратор',
    deleteConfirm:
      'Сигурни ли сте, че искате да изтриете администратора „{{name}}“? Тази операция е необратима.',
    deleteBtn: 'Изтрий',
  },
  el: {
    loadFailed: 'Αποτυχία φόρτωσης των διαχειριστών',
    pwMin8: 'Ο κωδικός πρόσβασης πρέπει να έχει τουλάχιστον 8 χαρακτήρες',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Μόνο ανάγνωση',
    colName: 'Όνομα',
    colRole: 'Ρόλος',
    colStatus: 'Κατάσταση',
    statusActive: 'Ενεργός',
    statusInactive: 'Ανενεργός',
    colLastLogin: 'Τελευταία σύνδεση',
    never: 'Ποτέ',
    colCreatedAt: 'Δημιουργήθηκε στις',
    colActions: 'Ενέργειες',
    edit: 'Επεξεργασία',
    changePassword: 'Αλλαγή κωδικού',
    delete: 'Διαγραφή',
    title: 'Διαχειριστές',
    subtitle: 'Διαχείριση των λογαριασμών διαχειριστή του συστήματος',
    addAdmin: 'Προσθήκη διαχειριστή',
    modalEdit: 'Επεξεργασία διαχειριστή',
    modalAdd: 'Προσθήκη διαχειριστή',
    email: 'Email',
    fullName: 'Πλήρες όνομα',
    password: 'Κωδικός πρόσβασης',
    min8chars: 'Τουλάχιστον 8 χαρακτήρες',
    role: 'Ρόλος',
    accountActive: 'Λογαριασμός ενεργός',
    cancel: 'Άκυρο',
    saving: 'Αποθήκευση…',
    saveChanges: 'Αποθήκευση αλλαγών',
    createAdmin: 'Δημιουργία διαχειριστή',
    pwModalTitle: 'Αλλαγή κωδικού: {{name}}',
    newPassword: 'Νέος κωδικός πρόσβασης',
    changePasswordBtn: 'Αλλαγή κωδικού',
    deleteTitle: 'Διαγραφή διαχειριστή',
    deleteConfirm:
      'Είστε σίγουροι ότι θέλετε να διαγράψετε τον διαχειριστή «{{name}}»; Αυτή η ενέργεια είναι μη αναστρέψιμη.',
    deleteBtn: 'Διαγραφή',
  },
  et: {
    loadFailed: 'Administraatorite laadimine ebaõnnestus',
    pwMin8: 'Parool peab olema vähemalt 8 tähemärki',
    roleSuperAdmin: 'Superadministraator',
    roleAdmin: 'Administraator',
    roleReadOnly: 'Ainult lugemine',
    colName: 'Nimi',
    colRole: 'Roll',
    colStatus: 'Olek',
    statusActive: 'Aktiivne',
    statusInactive: 'Mitteaktiivne',
    colLastLogin: 'Viimane sisselogimine',
    never: 'Mitte kunagi',
    colCreatedAt: 'Loodud',
    colActions: 'Toimingud',
    edit: 'Muuda',
    changePassword: 'Muuda parooli',
    delete: 'Kustuta',
    title: 'Administraatorid',
    subtitle: 'Süsteemi administraatorite kontode haldamine',
    addAdmin: 'Lisa administraator',
    modalEdit: 'Muuda administraatorit',
    modalAdd: 'Lisa administraator',
    email: 'E-post',
    fullName: 'Täisnimi',
    password: 'Parool',
    min8chars: 'Vähemalt 8 tähemärki',
    role: 'Roll',
    accountActive: 'Konto aktiivne',
    cancel: 'Tühista',
    saving: 'Salvestamine…',
    saveChanges: 'Salvesta muudatused',
    createAdmin: 'Loo administraator',
    pwModalTitle: 'Parooli muutmine: {{name}}',
    newPassword: 'Uus parool',
    changePasswordBtn: 'Muuda parooli',
    deleteTitle: 'Kustuta administraator',
    deleteConfirm:
      'Kas oled kindel, et soovid kustutada administraatori "{{name}}"? See toiming on pöördumatu.',
    deleteBtn: 'Kustuta',
  },
  lv: {
    loadFailed: 'Neizdevās ielādēt administratorus',
    pwMin8: 'Parolei jābūt vismaz 8 rakstzīmēm',
    roleSuperAdmin: 'Superadministrators',
    roleAdmin: 'Administrators',
    roleReadOnly: 'Tikai lasīšana',
    colName: 'Vārds',
    colRole: 'Loma',
    colStatus: 'Statuss',
    statusActive: 'Aktīvs',
    statusInactive: 'Neaktīvs',
    colLastLogin: 'Pēdējā pieteikšanās',
    never: 'Nekad',
    colCreatedAt: 'Izveidots',
    colActions: 'Darbības',
    edit: 'Rediģēt',
    changePassword: 'Mainīt paroli',
    delete: 'Dzēst',
    title: 'Administratori',
    subtitle: 'Sistēmas administratoru kontu pārvaldība',
    addAdmin: 'Pievienot administratoru',
    modalEdit: 'Rediģēt administratoru',
    modalAdd: 'Pievienot administratoru',
    email: 'E-pasts',
    fullName: 'Pilns vārds',
    password: 'Parole',
    min8chars: 'Vismaz 8 rakstzīmes',
    role: 'Loma',
    accountActive: 'Konts aktīvs',
    cancel: 'Atcelt',
    saving: 'Saglabā…',
    saveChanges: 'Saglabāt izmaiņas',
    createAdmin: 'Izveidot administratoru',
    pwModalTitle: 'Paroles maiņa: {{name}}',
    newPassword: 'Jauna parole',
    changePasswordBtn: 'Mainīt paroli',
    deleteTitle: 'Dzēst administratoru',
    deleteConfirm:
      'Vai tiešām vēlaties dzēst administratoru "{{name}}"? Šī darbība ir neatgriezeniska.',
    deleteBtn: 'Dzēst',
  },
  lt: {
    loadFailed: 'Nepavyko įkelti administratorių',
    pwMin8: 'Slaptažodį turi sudaryti bent 8 simboliai',
    roleSuperAdmin: 'Superadministratorius',
    roleAdmin: 'Administratorius',
    roleReadOnly: 'Tik skaitymas',
    colName: 'Vardas',
    colRole: 'Vaidmuo',
    colStatus: 'Būsena',
    statusActive: 'Aktyvus',
    statusInactive: 'Neaktyvus',
    colLastLogin: 'Paskutinis prisijungimas',
    never: 'Niekada',
    colCreatedAt: 'Sukurta',
    colActions: 'Veiksmai',
    edit: 'Redaguoti',
    changePassword: 'Keisti slaptažodį',
    delete: 'Ištrinti',
    title: 'Administratoriai',
    subtitle: 'Sistemos administratorių paskyrų valdymas',
    addAdmin: 'Pridėti administratorių',
    modalEdit: 'Redaguoti administratorių',
    modalAdd: 'Pridėti administratorių',
    email: 'El. paštas',
    fullName: 'Visas vardas',
    password: 'Slaptažodis',
    min8chars: 'Bent 8 simboliai',
    role: 'Vaidmuo',
    accountActive: 'Paskyra aktyvi',
    cancel: 'Atšaukti',
    saving: 'Įrašoma…',
    saveChanges: 'Įrašyti pakeitimus',
    createAdmin: 'Sukurti administratorių',
    pwModalTitle: 'Slaptažodžio keitimas: {{name}}',
    newPassword: 'Naujas slaptažodis',
    changePasswordBtn: 'Keisti slaptažodį',
    deleteTitle: 'Ištrinti administratorių',
    deleteConfirm:
      'Ar tikrai norite ištrinti administratorių "{{name}}"? Šis veiksmas negrįžtamas.',
    deleteBtn: 'Ištrinti',
  },
  mt: {
    loadFailed: 'Ma rnexxiex jiġu mgħobbija l-amministraturi',
    pwMin8: 'Il-password trid tkun mill-inqas 8 karattri',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Qari biss',
    colName: 'Isem',
    colRole: 'Rwol',
    colStatus: 'Status',
    statusActive: 'Attiv',
    statusInactive: 'Inattiv',
    colLastLogin: 'L-aħħar login',
    never: 'Qatt',
    colCreatedAt: 'Maħluq fl-',
    colActions: 'Azzjonijiet',
    edit: 'Editja',
    changePassword: 'Ibdel il-password',
    delete: 'Ħassar',
    title: 'Amministraturi',
    subtitle: 'Ġestjoni tal-kontijiet tal-amministraturi tas-sistema',
    addAdmin: 'Żid amministratur',
    modalEdit: 'Editja l-amministratur',
    modalAdd: 'Żid amministratur',
    email: 'Email',
    fullName: 'Isem sħiħ',
    password: 'Password',
    min8chars: 'Minimu 8 karattri',
    role: 'Rwol',
    accountActive: 'Kont attiv',
    cancel: 'Ikkanċella',
    saving: 'Qed jiġi ssejvjat…',
    saveChanges: 'Issejvja l-bidliet',
    createAdmin: 'Oħloq amministratur',
    pwModalTitle: 'Bidla tal-password: {{name}}',
    newPassword: 'Password ġdida',
    changePasswordBtn: 'Ibdel il-password',
    deleteTitle: 'Ħassar l-amministratur',
    deleteConfirm:
      'Żgur trid tħassar l-amministratur "{{name}}"? Din l-operazzjoni hija irreversibbli.',
    deleteBtn: 'Ħassar',
  },
  ga: {
    loadFailed: 'Theip ar na riarthóirí a luchtú',
    pwMin8: 'Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire',
    roleSuperAdmin: 'Sár-Riarthóir',
    roleAdmin: 'Riarthóir',
    roleReadOnly: 'Léamh amháin',
    colName: 'Ainm',
    colRole: 'Ról',
    colStatus: 'Stádas',
    statusActive: 'Gníomhach',
    statusInactive: 'Neamhghníomhach',
    colLastLogin: 'Logáil isteach is déanaí',
    never: 'Riamh',
    colCreatedAt: 'Cruthaithe ar',
    colActions: 'Gníomhartha',
    edit: 'Cuir in eagar',
    changePassword: 'Athraigh focal faire',
    delete: 'Scrios',
    title: 'Riarthóirí',
    subtitle: 'Bainistigh cuntais riarthóra an chórais',
    addAdmin: 'Cuir riarthóir leis',
    modalEdit: 'Cuir an riarthóir in eagar',
    modalAdd: 'Cuir riarthóir leis',
    email: 'Ríomhphost',
    fullName: 'Ainm iomlán',
    password: 'Focal faire',
    min8chars: 'Íosmhéid 8 gcarachtar',
    role: 'Ról',
    accountActive: 'Cuntas gníomhach',
    cancel: 'Cealaigh',
    saving: 'Á shábháil…',
    saveChanges: 'Sábháil athruithe',
    createAdmin: 'Cruthaigh riarthóir',
    pwModalTitle: 'Athrú focal faire: {{name}}',
    newPassword: 'Focal faire nua',
    changePasswordBtn: 'Athraigh focal faire',
    deleteTitle: 'Scrios riarthóir',
    deleteConfirm:
      'An bhfuil tú cinnte gur mhaith leat an riarthóir "{{name}}" a scriosadh? Tá an oibríocht seo do-aisghabhála.',
    deleteBtn: 'Scrios',
  },
  no: {
    loadFailed: 'Kunne ikke laste administratorer',
    pwMin8: 'Passordet må være minst 8 tegn',
    roleSuperAdmin: 'Superadministrator',
    roleAdmin: 'Administrator',
    roleReadOnly: 'Kun lesing',
    colName: 'Navn',
    colRole: 'Rolle',
    colStatus: 'Status',
    statusActive: 'Aktiv',
    statusInactive: 'Inaktiv',
    colLastLogin: 'Siste innlogging',
    never: 'Aldri',
    colCreatedAt: 'Opprettet den',
    colActions: 'Handlinger',
    edit: 'Rediger',
    changePassword: 'Endre passord',
    delete: 'Slett',
    title: 'Administratorer',
    subtitle: 'Administrer systemets administratorkontoer',
    addAdmin: 'Legg til administrator',
    modalEdit: 'Rediger administrator',
    modalAdd: 'Legg til administrator',
    email: 'E-post',
    fullName: 'Fullt navn',
    password: 'Passord',
    min8chars: 'Minst 8 tegn',
    role: 'Rolle',
    accountActive: 'Konto aktiv',
    cancel: 'Avbryt',
    saving: 'Lagrer…',
    saveChanges: 'Lagre endringer',
    createAdmin: 'Opprett administrator',
    pwModalTitle: 'Endre passord: {{name}}',
    newPassword: 'Nytt passord',
    changePasswordBtn: 'Endre passord',
    deleteTitle: 'Slett administrator',
    deleteConfirm:
      'Er du sikker på at du vil slette administratoren "{{name}}"? Denne handlingen kan ikke angres.',
    deleteBtn: 'Slett',
  },
  is: {
    loadFailed: 'Ekki tókst að hlaða stjórnendur',
    pwMin8: 'Lykilorðið verður að vera að minnsta kosti 8 stafir',
    roleSuperAdmin: 'Yfirstjórnandi',
    roleAdmin: 'Stjórnandi',
    roleReadOnly: 'Aðeins lestur',
    colName: 'Nafn',
    colRole: 'Hlutverk',
    colStatus: 'Staða',
    statusActive: 'Virkur',
    statusInactive: 'Óvirkur',
    colLastLogin: 'Síðasta innskráning',
    never: 'Aldrei',
    colCreatedAt: 'Búið til þann',
    colActions: 'Aðgerðir',
    edit: 'Breyta',
    changePassword: 'Breyta lykilorði',
    delete: 'Eyða',
    title: 'Stjórnendur',
    subtitle: 'Stjórna stjórnendareikningum kerfisins',
    addAdmin: 'Bæta við stjórnanda',
    modalEdit: 'Breyta stjórnanda',
    modalAdd: 'Bæta við stjórnanda',
    email: 'Tölvupóstur',
    fullName: 'Fullt nafn',
    password: 'Lykilorð',
    min8chars: 'Lágmark 8 stafir',
    role: 'Hlutverk',
    accountActive: 'Reikningur virkur',
    cancel: 'Hætta við',
    saving: 'Vista…',
    saveChanges: 'Vista breytingar',
    createAdmin: 'Búa til stjórnanda',
    pwModalTitle: 'Breyta lykilorði: {{name}}',
    newPassword: 'Nýtt lykilorð',
    changePasswordBtn: 'Breyta lykilorði',
    deleteTitle: 'Eyða stjórnanda',
    deleteConfirm:
      'Ertu viss um að þú viljir eyða stjórnandanum "{{name}}"? Þessi aðgerð er óafturkræf.',
    deleteBtn: 'Eyða',
  },
  uk: {
    loadFailed: 'Не вдалося завантажити адміністраторів',
    pwMin8: 'Пароль має містити щонайменше 8 символів',
    roleSuperAdmin: 'Суперадміністратор',
    roleAdmin: 'Адміністратор',
    roleReadOnly: 'Лише читання',
    colName: "Ім'я",
    colRole: 'Роль',
    colStatus: 'Статус',
    statusActive: 'Активний',
    statusInactive: 'Неактивний',
    colLastLogin: 'Останній вхід',
    never: 'Ніколи',
    colCreatedAt: 'Створено',
    colActions: 'Дії',
    edit: 'Редагувати',
    changePassword: 'Змінити пароль',
    delete: 'Видалити',
    title: 'Адміністратори',
    subtitle: 'Керування обліковими записами адміністраторів системи',
    addAdmin: 'Додати адміністратора',
    modalEdit: 'Редагувати адміністратора',
    modalAdd: 'Додати адміністратора',
    email: 'Електронна пошта',
    fullName: "Повне ім'я",
    password: 'Пароль',
    min8chars: 'Мінімум 8 символів',
    role: 'Роль',
    accountActive: 'Обліковий запис активний',
    cancel: 'Скасувати',
    saving: 'Збереження…',
    saveChanges: 'Зберегти зміни',
    createAdmin: 'Створити адміністратора',
    pwModalTitle: 'Зміна пароля: {{name}}',
    newPassword: 'Новий пароль',
    changePasswordBtn: 'Змінити пароль',
    deleteTitle: 'Видалити адміністратора',
    deleteConfirm:
      'Ви впевнені, що хочете видалити адміністратора «{{name}}»? Ця операція незворотна.',
    deleteBtn: 'Видалити',
  },
  sr: {
    loadFailed: 'Није могуће учитати администраторе',
    pwMin8: 'Лозинка мора имати најмање 8 знакова',
    roleSuperAdmin: 'Супер админ',
    roleAdmin: 'Админ',
    roleReadOnly: 'Само читање',
    colName: 'Име',
    colRole: 'Улога',
    colStatus: 'Статус',
    statusActive: 'Активан',
    statusInactive: 'Неактиван',
    colLastLogin: 'Последња пријава',
    never: 'Никада',
    colCreatedAt: 'Креиран дана',
    colActions: 'Радње',
    edit: 'Уреди',
    changePassword: 'Промени лозинку',
    delete: 'Обриши',
    title: 'Администратори',
    subtitle: 'Управљање администраторским налозима система',
    addAdmin: 'Додај администратора',
    modalEdit: 'Уреди администратора',
    modalAdd: 'Додај администратора',
    email: 'Имејл',
    fullName: 'Пуно име',
    password: 'Лозинка',
    min8chars: 'Најмање 8 знакова',
    role: 'Улога',
    accountActive: 'Налог активан',
    cancel: 'Откажи',
    saving: 'Чување…',
    saveChanges: 'Сачувај измене',
    createAdmin: 'Креирај администратора',
    pwModalTitle: 'Промена лозинке: {{name}}',
    newPassword: 'Нова лозинка',
    changePasswordBtn: 'Промени лозинку',
    deleteTitle: 'Обриши администратора',
    deleteConfirm:
      'Да ли сте сигурни да желите да обришете администратора „{{name}}“? Ова операција је неповратна.',
    deleteBtn: 'Обриши',
  },
  sq: {
    loadFailed: 'Dështoi ngarkimi i administratorëve',
    pwMin8: 'Fjalëkalimi duhet të ketë të paktën 8 karaktere',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Vetëm lexim',
    colName: 'Emri',
    colRole: 'Roli',
    colStatus: 'Statusi',
    statusActive: 'Aktiv',
    statusInactive: 'Joaktiv',
    colLastLogin: 'Hyrja e fundit',
    never: 'Asnjëherë',
    colCreatedAt: 'Krijuar më',
    colActions: 'Veprime',
    edit: 'Modifiko',
    changePassword: 'Ndrysho fjalëkalimin',
    delete: 'Fshi',
    title: 'Administratorët',
    subtitle: 'Menaxho llogaritë e administratorëve të sistemit',
    addAdmin: 'Shto administrator',
    modalEdit: 'Modifiko administratorin',
    modalAdd: 'Shto administrator',
    email: 'Email',
    fullName: 'Emri i plotë',
    password: 'Fjalëkalimi',
    min8chars: 'Minimumi 8 karaktere',
    role: 'Roli',
    accountActive: 'Llogaria aktive',
    cancel: 'Anulo',
    saving: 'Po ruhet…',
    saveChanges: 'Ruaj ndryshimet',
    createAdmin: 'Krijo administrator',
    pwModalTitle: 'Ndryshimi i fjalëkalimit: {{name}}',
    newPassword: 'Fjalëkalim i ri',
    changePasswordBtn: 'Ndrysho fjalëkalimin',
    deleteTitle: 'Fshi administratorin',
    deleteConfirm:
      'Je i sigurt që dëshiron të fshish administratorin "{{name}}"? Ky veprim është i pakthyeshëm.',
    deleteBtn: 'Fshi',
  },
  mk: {
    loadFailed: 'Не може да се вчитаат администраторите',
    pwMin8: 'Лозинката мора да има најмалку 8 знаци',
    roleSuperAdmin: 'Супер админ',
    roleAdmin: 'Админ',
    roleReadOnly: 'Само читање',
    colName: 'Име',
    colRole: 'Улога',
    colStatus: 'Статус',
    statusActive: 'Активен',
    statusInactive: 'Неактивен',
    colLastLogin: 'Последна најава',
    never: 'Никогаш',
    colCreatedAt: 'Создаден на',
    colActions: 'Дејства',
    edit: 'Уреди',
    changePassword: 'Промени лозинка',
    delete: 'Избриши',
    title: 'Администратори',
    subtitle: 'Управување со администраторските сметки на системот',
    addAdmin: 'Додај администратор',
    modalEdit: 'Уреди администратор',
    modalAdd: 'Додај администратор',
    email: 'Е-пошта',
    fullName: 'Полно име',
    password: 'Лозинка',
    min8chars: 'Минимум 8 знаци',
    role: 'Улога',
    accountActive: 'Сметката е активна',
    cancel: 'Откажи',
    saving: 'Се зачувува…',
    saveChanges: 'Зачувај промени',
    createAdmin: 'Создади администратор',
    pwModalTitle: 'Промена на лозинка: {{name}}',
    newPassword: 'Нова лозинка',
    changePasswordBtn: 'Промени лозинка',
    deleteTitle: 'Избриши администратор',
    deleteConfirm:
      'Дали сте сигурни дека сакате да го избришете администраторот „{{name}}“? Оваа операција е неповратна.',
    deleteBtn: 'Избриши',
  },
  bs: {
    loadFailed: 'Nije moguće učitati administratore',
    pwMin8: 'Lozinka mora imati najmanje 8 znakova',
    roleSuperAdmin: 'Super Admin',
    roleAdmin: 'Admin',
    roleReadOnly: 'Samo čitanje',
    colName: 'Ime',
    colRole: 'Uloga',
    colStatus: 'Status',
    statusActive: 'Aktivan',
    statusInactive: 'Neaktivan',
    colLastLogin: 'Zadnja prijava',
    never: 'Nikada',
    colCreatedAt: 'Kreiran dana',
    colActions: 'Radnje',
    edit: 'Uredi',
    changePassword: 'Promijeni lozinku',
    delete: 'Obriši',
    title: 'Administratori',
    subtitle: 'Upravljanje administratorskim računima sistema',
    addAdmin: 'Dodaj administratora',
    modalEdit: 'Uredi administratora',
    modalAdd: 'Dodaj administratora',
    email: 'E-pošta',
    fullName: 'Puno ime',
    password: 'Lozinka',
    min8chars: 'Najmanje 8 znakova',
    role: 'Uloga',
    accountActive: 'Račun aktivan',
    cancel: 'Otkaži',
    saving: 'Spremanje…',
    saveChanges: 'Spremi promjene',
    createAdmin: 'Kreiraj administratora',
    pwModalTitle: 'Promjena lozinke: {{name}}',
    newPassword: 'Nova lozinka',
    changePasswordBtn: 'Promijeni lozinku',
    deleteTitle: 'Obriši administratora',
    deleteConfirm:
      'Jeste li sigurni da želite obrisati administratora "{{name}}"? Ova operacija je nepovratna.',
    deleteBtn: 'Obriši',
  },
};

const plansTr: Record<string, Dict> = {
  en: {
    title: 'Plan management',
    subtitle: 'Subscription plan configuration',
    addPlan: 'Add plan',
    loadFailed: 'Failed to load plans',
    deleteSuccess: 'The plan was successfully deleted along with all related items.',
    colName: 'Plan name',
    colMaxStrings: 'Max. strings',
    unlimited: 'Unlimited',
    colRateLimit: 'Requests/min limit',
    colDescription: 'Description',
    colCorrection: 'Correction',
    correctionYes: 'Yes ({{days}} days)',
    correctionNo: 'No',
    colCreatedAt: 'Created at',
    colActions: 'Actions',
    edit: 'Edit',
    delete: 'Delete',
    loadingPlans: 'Loading plans…',
    empty: 'No plans',
    modalEdit: 'Edit plan',
    modalAdd: 'Add a new plan',
    cancel: 'Cancel',
    save: 'Save',
    fieldName: 'Plan name',
    fieldNamePh: 'e.g. Basic, Premium',
    fieldMaxStrings: 'Maximum number of strings',
    fieldMaxStringsHelp: '-1 means an unlimited number of strings',
    fieldRateLimit: 'Request limit (per minute)',
    fieldRateLimitHelp: 'Maximum number of API requests per minute',
    fieldDescription: 'Description (optional)',
    fieldDescriptionPh: 'e.g. Basic plan for small installations',
    fieldAuthHeader: 'Authorization header (optional)',
    fieldAuthHeaderPh: 'e.g. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Custom HTTP header name for the API key. If empty, the ?apikey URL parameter is used.',
    allowQueryParams: 'Dynamic query parameters',
    allowQueryParamsHelp:
      'Allows clients to pass location and string configuration directly in the URL. Without this option the client must configure the installation in the panel and use only the day and type parameters.',
    correctionEnabled: 'Production correction',
    correctionEnabledHelp:
      'Enables forecast correction based on actual production data submitted by the client.',
    fieldCorrectionDays: 'Days for calculations (3-90)',
    fieldCorrectionDaysHelp: 'Number of recent days used to compute the correction coefficients',
    deleteModalTitle: 'Delete plan: {{name}}',
    deleteAll: 'Delete everything',
    loadingDetails: 'Loading details…',
    warnDelete: 'Warning! This operation will delete the plan and all related items:',
    apiKeysCount: 'API keys ({{count}})',
    activeLabel: 'Active',
    inactiveLabel: 'Inactive',
    expires: 'Expires',
    actCodesCount: 'Activation codes ({{count}})',
    usedBy: 'Used by',
    statusUsed: 'Used',
    statusUnused: 'Unused',
    noRelated: 'The plan has no related items. You can safely delete it.',
    confirmDeleteQ: 'Are you sure you want to delete this plan along with all the items above?',
    irreversible: 'This operation is irreversible.',
  },
  pl: {
    title: 'Zarządzanie planami',
    subtitle: 'Konfiguracja planów subskrypcyjnych',
    addPlan: 'Dodaj plan',
    loadFailed: 'Nie udało się załadować planów',
    deleteSuccess: 'Plan został pomyślnie usunięty wraz ze wszystkimi powiązanymi elementami.',
    colName: 'Nazwa planu',
    colMaxStrings: 'Maks. stringów',
    unlimited: 'Nielimitowane',
    colRateLimit: 'Limit żądań/min',
    colDescription: 'Opis',
    colCorrection: 'Korekcja',
    correctionYes: 'Tak ({{days}} dni)',
    correctionNo: 'Nie',
    colCreatedAt: 'Data utworzenia',
    colActions: 'Akcje',
    edit: 'Edytuj',
    delete: 'Usuń',
    loadingPlans: 'Wczytywanie planów…',
    empty: 'Brak planów',
    modalEdit: 'Edytuj plan',
    modalAdd: 'Dodaj nowy plan',
    cancel: 'Anuluj',
    save: 'Zapisz',
    fieldName: 'Nazwa planu',
    fieldNamePh: 'np. Basic, Premium',
    fieldMaxStrings: 'Maksymalna liczba stringów',
    fieldMaxStringsHelp: '-1 oznacza nielimitowaną liczbę stringów',
    fieldRateLimit: 'Limit żądań (na minutę)',
    fieldRateLimitHelp: 'Maksymalna liczba żądań API na minutę',
    fieldDescription: 'Opis (opcjonalnie)',
    fieldDescriptionPh: 'np. Plan podstawowy dla małych instalacji',
    fieldAuthHeader: 'Nagłówek autoryzacji (opcjonalnie)',
    fieldAuthHeaderPh: 'np. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Niestandardowa nazwa nagłówka HTTP dla klucza API. Jeśli puste, używany będzie parametr ?apikey w URL.',
    allowQueryParams: 'Dynamiczne parametry w zapytaniu',
    allowQueryParamsHelp:
      'Pozwala klientom na przekazywanie lokalizacji i konfiguracji stringów bezpośrednio w URL. Bez tej opcji klient musi skonfigurować instalację w panelu i używać tylko parametrów day i type.',
    correctionEnabled: 'Korekcja produkcji',
    correctionEnabledHelp:
      'Włącza korektę prognoz na podstawie rzeczywistych danych produkcyjnych przesyłanych przez klienta.',
    fieldCorrectionDays: 'Dni do obliczeń (3-90)',
    fieldCorrectionDaysHelp: 'Liczba ostatnich dni używanych do obliczenia współczynników korekcji',
    deleteModalTitle: 'Usuń plan: {{name}}',
    deleteAll: 'Usuń wszystko',
    loadingDetails: 'Ładowanie szczegółów…',
    warnDelete: 'Uwaga! Ta operacja usunie plan oraz wszystkie powiązane elementy:',
    apiKeysCount: 'Klucze API ({{count}})',
    activeLabel: 'Aktywny',
    inactiveLabel: 'Nieaktywny',
    expires: 'Wygasa',
    actCodesCount: 'Kody aktywacyjne ({{count}})',
    usedBy: 'Użyty przez',
    statusUsed: 'Użyty',
    statusUnused: 'Nieużyty',
    noRelated: 'Plan nie ma żadnych powiązanych elementów. Możesz go bezpiecznie usunąć.',
    confirmDeleteQ: 'Czy na pewno chcesz usunąć ten plan wraz ze wszystkimi powyższymi elementami?',
    irreversible: 'Ta operacja jest nieodwracalna.',
  },
  de: {
    title: 'Planverwaltung',
    subtitle: 'Konfiguration der Abonnement-Pläne',
    addPlan: 'Plan hinzufügen',
    loadFailed: 'Pläne konnten nicht geladen werden',
    deleteSuccess: 'Der Plan wurde erfolgreich zusammen mit allen verknüpften Elementen gelöscht.',
    colName: 'Planname',
    colMaxStrings: 'Max. Strings',
    unlimited: 'Unbegrenzt',
    colRateLimit: 'Anfragen/min-Limit',
    colDescription: 'Beschreibung',
    colCorrection: 'Korrektur',
    correctionYes: 'Ja ({{days}} Tage)',
    correctionNo: 'Nein',
    colCreatedAt: 'Erstellt am',
    colActions: 'Aktionen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    loadingPlans: 'Pläne werden geladen…',
    empty: 'Keine Pläne',
    modalEdit: 'Plan bearbeiten',
    modalAdd: 'Neuen Plan hinzufügen',
    cancel: 'Abbrechen',
    save: 'Speichern',
    fieldName: 'Planname',
    fieldNamePh: 'z. B. Basic, Premium',
    fieldMaxStrings: 'Maximale Anzahl an Strings',
    fieldMaxStringsHelp: '-1 bedeutet eine unbegrenzte Anzahl an Strings',
    fieldRateLimit: 'Anfragelimit (pro Minute)',
    fieldRateLimitHelp: 'Maximale Anzahl an API-Anfragen pro Minute',
    fieldDescription: 'Beschreibung (optional)',
    fieldDescriptionPh: 'z. B. Basisplan für kleine Anlagen',
    fieldAuthHeader: 'Autorisierungs-Header (optional)',
    fieldAuthHeaderPh: 'z. B. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Benutzerdefinierter HTTP-Headername für den API-Schlüssel. Wenn leer, wird der URL-Parameter ?apikey verwendet.',
    allowQueryParams: 'Dynamische Abfrageparameter',
    allowQueryParamsHelp:
      'Erlaubt Clients, Standort- und String-Konfiguration direkt in der URL zu übergeben. Ohne diese Option muss der Client die Anlage im Panel konfigurieren und nur die Parameter day und type verwenden.',
    correctionEnabled: 'Produktionskorrektur',
    correctionEnabledHelp:
      'Aktiviert die Prognosekorrektur basierend auf den vom Client übermittelten Ist-Produktionsdaten.',
    fieldCorrectionDays: 'Tage für Berechnungen (3-90)',
    fieldCorrectionDaysHelp:
      'Anzahl der letzten Tage, die zur Berechnung der Korrekturkoeffizienten verwendet werden',
    deleteModalTitle: 'Plan löschen: {{name}}',
    deleteAll: 'Alles löschen',
    loadingDetails: 'Details werden geladen…',
    warnDelete: 'Achtung! Dieser Vorgang löscht den Plan und alle verknüpften Elemente:',
    apiKeysCount: 'API-Schlüssel ({{count}})',
    activeLabel: 'Aktiv',
    inactiveLabel: 'Inaktiv',
    expires: 'Läuft ab',
    actCodesCount: 'Aktivierungscodes ({{count}})',
    usedBy: 'Verwendet von',
    statusUsed: 'Verwendet',
    statusUnused: 'Unbenutzt',
    noRelated: 'Der Plan hat keine verknüpften Elemente. Sie können ihn sicher löschen.',
    confirmDeleteQ:
      'Möchten Sie diesen Plan wirklich zusammen mit allen oben genannten Elementen löschen?',
    irreversible: 'Dieser Vorgang ist unwiderruflich.',
  },
  fr: {
    title: 'Gestion des plans',
    subtitle: "Configuration des plans d'abonnement",
    addPlan: 'Ajouter un plan',
    loadFailed: 'Échec du chargement des plans',
    deleteSuccess: 'Le plan a été supprimé avec succès ainsi que tous les éléments associés.',
    colName: 'Nom du plan',
    colMaxStrings: 'Strings max.',
    unlimited: 'Illimité',
    colRateLimit: 'Limite de requêtes/min',
    colDescription: 'Description',
    colCorrection: 'Correction',
    correctionYes: 'Oui ({{days}} jours)',
    correctionNo: 'Non',
    colCreatedAt: 'Créé le',
    colActions: 'Actions',
    edit: 'Modifier',
    delete: 'Supprimer',
    loadingPlans: 'Chargement des plans…',
    empty: 'Aucun plan',
    modalEdit: 'Modifier le plan',
    modalAdd: 'Ajouter un nouveau plan',
    cancel: 'Annuler',
    save: 'Enregistrer',
    fieldName: 'Nom du plan',
    fieldNamePh: 'ex. Basic, Premium',
    fieldMaxStrings: 'Nombre maximum de strings',
    fieldMaxStringsHelp: '-1 signifie un nombre illimité de strings',
    fieldRateLimit: 'Limite de requêtes (par minute)',
    fieldRateLimitHelp: 'Nombre maximum de requêtes API par minute',
    fieldDescription: 'Description (facultatif)',
    fieldDescriptionPh: 'ex. Plan de base pour petites installations',
    fieldAuthHeader: "En-tête d'autorisation (facultatif)",
    fieldAuthHeaderPh: 'ex. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      "Nom d'en-tête HTTP personnalisé pour la clé API. Si vide, le paramètre d'URL ?apikey est utilisé.",
    allowQueryParams: 'Paramètres de requête dynamiques',
    allowQueryParamsHelp:
      "Permet aux clients de transmettre l'emplacement et la configuration des strings directement dans l'URL. Sans cette option, le client doit configurer l'installation dans le panneau et n'utiliser que les paramètres day et type.",
    correctionEnabled: 'Correction de production',
    correctionEnabledHelp:
      'Active la correction des prévisions en fonction des données de production réelles soumises par le client.',
    fieldCorrectionDays: 'Jours pour les calculs (3-90)',
    fieldCorrectionDaysHelp:
      'Nombre de jours récents utilisés pour calculer les coefficients de correction',
    deleteModalTitle: 'Supprimer le plan : {{name}}',
    deleteAll: 'Tout supprimer',
    loadingDetails: 'Chargement des détails…',
    warnDelete: 'Attention ! Cette opération supprimera le plan et tous les éléments associés :',
    apiKeysCount: 'Clés API ({{count}})',
    activeLabel: 'Actif',
    inactiveLabel: 'Inactif',
    expires: 'Expire',
    actCodesCount: "Codes d'activation ({{count}})",
    usedBy: 'Utilisé par',
    statusUsed: 'Utilisé',
    statusUnused: 'Inutilisé',
    noRelated: "Le plan n'a aucun élément associé. Vous pouvez le supprimer en toute sécurité.",
    confirmDeleteQ:
      'Voulez-vous vraiment supprimer ce plan ainsi que tous les éléments ci-dessus ?',
    irreversible: 'Cette opération est irréversible.',
  },
  es: {
    title: 'Gestión de planes',
    subtitle: 'Configuración de planes de suscripción',
    addPlan: 'Añadir plan',
    loadFailed: 'No se pudieron cargar los planes',
    deleteSuccess: 'El plan se eliminó correctamente junto con todos los elementos relacionados.',
    colName: 'Nombre del plan',
    colMaxStrings: 'Máx. strings',
    unlimited: 'Ilimitado',
    colRateLimit: 'Límite de solicitudes/min',
    colDescription: 'Descripción',
    colCorrection: 'Corrección',
    correctionYes: 'Sí ({{days}} días)',
    correctionNo: 'No',
    colCreatedAt: 'Creado el',
    colActions: 'Acciones',
    edit: 'Editar',
    delete: 'Eliminar',
    loadingPlans: 'Cargando planes…',
    empty: 'Sin planes',
    modalEdit: 'Editar plan',
    modalAdd: 'Añadir un nuevo plan',
    cancel: 'Cancelar',
    save: 'Guardar',
    fieldName: 'Nombre del plan',
    fieldNamePh: 'p. ej. Basic, Premium',
    fieldMaxStrings: 'Número máximo de strings',
    fieldMaxStringsHelp: '-1 significa un número ilimitado de strings',
    fieldRateLimit: 'Límite de solicitudes (por minuto)',
    fieldRateLimitHelp: 'Número máximo de solicitudes API por minuto',
    fieldDescription: 'Descripción (opcional)',
    fieldDescriptionPh: 'p. ej. Plan básico para instalaciones pequeñas',
    fieldAuthHeader: 'Encabezado de autorización (opcional)',
    fieldAuthHeaderPh: 'p. ej. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Nombre de encabezado HTTP personalizado para la clave API. Si está vacío, se usa el parámetro de URL ?apikey.',
    allowQueryParams: 'Parámetros de consulta dinámicos',
    allowQueryParamsHelp:
      'Permite a los clientes pasar la ubicación y la configuración de strings directamente en la URL. Sin esta opción, el cliente debe configurar la instalación en el panel y usar solo los parámetros day y type.',
    correctionEnabled: 'Corrección de producción',
    correctionEnabledHelp:
      'Activa la corrección de pronósticos según los datos de producción reales enviados por el cliente.',
    fieldCorrectionDays: 'Días para cálculos (3-90)',
    fieldCorrectionDaysHelp:
      'Número de días recientes usados para calcular los coeficientes de corrección',
    deleteModalTitle: 'Eliminar plan: {{name}}',
    deleteAll: 'Eliminar todo',
    loadingDetails: 'Cargando detalles…',
    warnDelete: '¡Atención! Esta operación eliminará el plan y todos los elementos relacionados:',
    apiKeysCount: 'Claves API ({{count}})',
    activeLabel: 'Activa',
    inactiveLabel: 'Inactiva',
    expires: 'Caduca',
    actCodesCount: 'Códigos de activación ({{count}})',
    usedBy: 'Usado por',
    statusUsed: 'Usado',
    statusUnused: 'Sin usar',
    noRelated: 'El plan no tiene elementos relacionados. Puedes eliminarlo de forma segura.',
    confirmDeleteQ:
      '¿Seguro que quieres eliminar este plan junto con todos los elementos anteriores?',
    irreversible: 'Esta operación es irreversible.',
  },
  it: {
    title: 'Gestione dei piani',
    subtitle: 'Configurazione dei piani di abbonamento',
    addPlan: 'Aggiungi piano',
    loadFailed: 'Impossibile caricare i piani',
    deleteSuccess:
      'Il piano è stato eliminato con successo insieme a tutti gli elementi correlati.',
    colName: 'Nome del piano',
    colMaxStrings: 'Max. stringhe',
    unlimited: 'Illimitato',
    colRateLimit: 'Limite richieste/min',
    colDescription: 'Descrizione',
    colCorrection: 'Correzione',
    correctionYes: 'Sì ({{days}} giorni)',
    correctionNo: 'No',
    colCreatedAt: 'Creato il',
    colActions: 'Azioni',
    edit: 'Modifica',
    delete: 'Elimina',
    loadingPlans: 'Caricamento piani…',
    empty: 'Nessun piano',
    modalEdit: 'Modifica piano',
    modalAdd: 'Aggiungi un nuovo piano',
    cancel: 'Annulla',
    save: 'Salva',
    fieldName: 'Nome del piano',
    fieldNamePh: 'es. Basic, Premium',
    fieldMaxStrings: 'Numero massimo di stringhe',
    fieldMaxStringsHelp: '-1 significa un numero illimitato di stringhe',
    fieldRateLimit: 'Limite di richieste (al minuto)',
    fieldRateLimitHelp: 'Numero massimo di richieste API al minuto',
    fieldDescription: 'Descrizione (opzionale)',
    fieldDescriptionPh: 'es. Piano base per piccoli impianti',
    fieldAuthHeader: 'Header di autorizzazione (opzionale)',
    fieldAuthHeaderPh: 'es. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      "Nome dell'header HTTP personalizzato per la chiave API. Se vuoto, viene usato il parametro URL ?apikey.",
    allowQueryParams: 'Parametri di query dinamici',
    allowQueryParamsHelp:
      "Consente ai client di passare posizione e configurazione delle stringhe direttamente nell'URL. Senza questa opzione il client deve configurare l'impianto nel pannello e usare solo i parametri day e type.",
    correctionEnabled: 'Correzione produzione',
    correctionEnabledHelp:
      'Abilita la correzione delle previsioni in base ai dati di produzione reali inviati dal client.',
    fieldCorrectionDays: 'Giorni per i calcoli (3-90)',
    fieldCorrectionDaysHelp:
      'Numero di giorni recenti usati per calcolare i coefficienti di correzione',
    deleteModalTitle: 'Elimina piano: {{name}}',
    deleteAll: 'Elimina tutto',
    loadingDetails: 'Caricamento dettagli…',
    warnDelete: 'Attenzione! Questa operazione eliminerà il piano e tutti gli elementi correlati:',
    apiKeysCount: 'Chiavi API ({{count}})',
    activeLabel: 'Attiva',
    inactiveLabel: 'Inattiva',
    expires: 'Scade',
    actCodesCount: 'Codici di attivazione ({{count}})',
    usedBy: 'Usato da',
    statusUsed: 'Usato',
    statusUnused: 'Non usato',
    noRelated: 'Il piano non ha elementi correlati. Puoi eliminarlo in sicurezza.',
    confirmDeleteQ: 'Vuoi davvero eliminare questo piano insieme a tutti gli elementi sopra?',
    irreversible: 'Questa operazione è irreversibile.',
  },
  pt: {
    title: 'Gestão de planos',
    subtitle: 'Configuração dos planos de subscrição',
    addPlan: 'Adicionar plano',
    loadFailed: 'Não foi possível carregar os planos',
    deleteSuccess: 'O plano foi eliminado com sucesso juntamente com todos os itens relacionados.',
    colName: 'Nome do plano',
    colMaxStrings: 'Máx. strings',
    unlimited: 'Ilimitado',
    colRateLimit: 'Limite de pedidos/min',
    colDescription: 'Descrição',
    colCorrection: 'Correção',
    correctionYes: 'Sim ({{days}} dias)',
    correctionNo: 'Não',
    colCreatedAt: 'Criado em',
    colActions: 'Ações',
    edit: 'Editar',
    delete: 'Eliminar',
    loadingPlans: 'A carregar planos…',
    empty: 'Sem planos',
    modalEdit: 'Editar plano',
    modalAdd: 'Adicionar um novo plano',
    cancel: 'Cancelar',
    save: 'Guardar',
    fieldName: 'Nome do plano',
    fieldNamePh: 'ex. Basic, Premium',
    fieldMaxStrings: 'Número máximo de strings',
    fieldMaxStringsHelp: '-1 significa um número ilimitado de strings',
    fieldRateLimit: 'Limite de pedidos (por minuto)',
    fieldRateLimitHelp: 'Número máximo de pedidos API por minuto',
    fieldDescription: 'Descrição (opcional)',
    fieldDescriptionPh: 'ex. Plano básico para pequenas instalações',
    fieldAuthHeader: 'Cabeçalho de autorização (opcional)',
    fieldAuthHeaderPh: 'ex. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Nome de cabeçalho HTTP personalizado para a chave API. Se vazio, é usado o parâmetro de URL ?apikey.',
    allowQueryParams: 'Parâmetros de consulta dinâmicos',
    allowQueryParamsHelp:
      'Permite aos clientes passar a localização e a configuração de strings diretamente no URL. Sem esta opção, o cliente deve configurar a instalação no painel e usar apenas os parâmetros day e type.',
    correctionEnabled: 'Correção de produção',
    correctionEnabledHelp:
      'Ativa a correção de previsões com base nos dados de produção reais enviados pelo cliente.',
    fieldCorrectionDays: 'Dias para cálculos (3-90)',
    fieldCorrectionDaysHelp:
      'Número de dias recentes usados para calcular os coeficientes de correção',
    deleteModalTitle: 'Eliminar plano: {{name}}',
    deleteAll: 'Eliminar tudo',
    loadingDetails: 'A carregar detalhes…',
    warnDelete: 'Atenção! Esta operação eliminará o plano e todos os itens relacionados:',
    apiKeysCount: 'Chaves API ({{count}})',
    activeLabel: 'Ativa',
    inactiveLabel: 'Inativa',
    expires: 'Expira',
    actCodesCount: 'Códigos de ativação ({{count}})',
    usedBy: 'Usado por',
    statusUsed: 'Usado',
    statusUnused: 'Não usado',
    noRelated: 'O plano não tem itens relacionados. Pode eliminá-lo com segurança.',
    confirmDeleteQ:
      'Tem a certeza de que quer eliminar este plano juntamente com todos os itens acima?',
    irreversible: 'Esta operação é irreversível.',
  },
  nl: {
    title: 'Planbeheer',
    subtitle: 'Configuratie van abonnementsplannen',
    addPlan: 'Plan toevoegen',
    loadFailed: 'Kan plannen niet laden',
    deleteSuccess: 'Het plan is succesvol verwijderd samen met alle gerelateerde items.',
    colName: 'Plannaam',
    colMaxStrings: 'Max. strings',
    unlimited: 'Onbeperkt',
    colRateLimit: 'Limiet verzoeken/min',
    colDescription: 'Beschrijving',
    colCorrection: 'Correctie',
    correctionYes: 'Ja ({{days}} dagen)',
    correctionNo: 'Nee',
    colCreatedAt: 'Aangemaakt op',
    colActions: 'Acties',
    edit: 'Bewerken',
    delete: 'Verwijderen',
    loadingPlans: 'Plannen laden…',
    empty: 'Geen plannen',
    modalEdit: 'Plan bewerken',
    modalAdd: 'Een nieuw plan toevoegen',
    cancel: 'Annuleren',
    save: 'Opslaan',
    fieldName: 'Plannaam',
    fieldNamePh: 'bijv. Basic, Premium',
    fieldMaxStrings: 'Maximaal aantal strings',
    fieldMaxStringsHelp: '-1 betekent een onbeperkt aantal strings',
    fieldRateLimit: 'Verzoeklimiet (per minuut)',
    fieldRateLimitHelp: 'Maximaal aantal API-verzoeken per minuut',
    fieldDescription: 'Beschrijving (optioneel)',
    fieldDescriptionPh: 'bijv. Basisplan voor kleine installaties',
    fieldAuthHeader: 'Autorisatie-header (optioneel)',
    fieldAuthHeaderPh: 'bijv. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Aangepaste HTTP-headernaam voor de API-sleutel. Indien leeg, wordt de URL-parameter ?apikey gebruikt.',
    allowQueryParams: 'Dynamische queryparameters',
    allowQueryParamsHelp:
      'Staat clients toe locatie en stringconfiguratie rechtstreeks in de URL door te geven. Zonder deze optie moet de client de installatie in het paneel configureren en alleen de parameters day en type gebruiken.',
    correctionEnabled: 'Productiecorrectie',
    correctionEnabledHelp:
      'Schakelt prognosecorrectie in op basis van werkelijke productiegegevens die door de client zijn ingediend.',
    fieldCorrectionDays: 'Dagen voor berekeningen (3-90)',
    fieldCorrectionDaysHelp:
      'Aantal recente dagen dat wordt gebruikt om de correctiecoëfficiënten te berekenen',
    deleteModalTitle: 'Plan verwijderen: {{name}}',
    deleteAll: 'Alles verwijderen',
    loadingDetails: 'Details laden…',
    warnDelete: 'Waarschuwing! Deze bewerking verwijdert het plan en alle gerelateerde items:',
    apiKeysCount: 'API-sleutels ({{count}})',
    activeLabel: 'Actief',
    inactiveLabel: 'Inactief',
    expires: 'Verloopt',
    actCodesCount: 'Activeringscodes ({{count}})',
    usedBy: 'Gebruikt door',
    statusUsed: 'Gebruikt',
    statusUnused: 'Ongebruikt',
    noRelated: 'Het plan heeft geen gerelateerde items. Je kunt het veilig verwijderen.',
    confirmDeleteQ:
      'Weet je zeker dat je dit plan samen met alle bovenstaande items wilt verwijderen?',
    irreversible: 'Deze bewerking is onomkeerbaar.',
  },
  da: {
    title: 'Planadministration',
    subtitle: 'Konfiguration af abonnementsplaner',
    addPlan: 'Tilføj plan',
    loadFailed: 'Kunne ikke indlæse planer',
    deleteSuccess: 'Planen blev slettet sammen med alle relaterede elementer.',
    colName: 'Plannavn',
    colMaxStrings: 'Maks. strings',
    unlimited: 'Ubegrænset',
    colRateLimit: 'Grænse anmodninger/min',
    colDescription: 'Beskrivelse',
    colCorrection: 'Korrektion',
    correctionYes: 'Ja ({{days}} dage)',
    correctionNo: 'Nej',
    colCreatedAt: 'Oprettet den',
    colActions: 'Handlinger',
    edit: 'Rediger',
    delete: 'Slet',
    loadingPlans: 'Indlæser planer…',
    empty: 'Ingen planer',
    modalEdit: 'Rediger plan',
    modalAdd: 'Tilføj en ny plan',
    cancel: 'Annuller',
    save: 'Gem',
    fieldName: 'Plannavn',
    fieldNamePh: 'f.eks. Basic, Premium',
    fieldMaxStrings: 'Maksimalt antal strings',
    fieldMaxStringsHelp: '-1 betyder et ubegrænset antal strings',
    fieldRateLimit: 'Anmodningsgrænse (pr. minut)',
    fieldRateLimitHelp: 'Maksimalt antal API-anmodninger pr. minut',
    fieldDescription: 'Beskrivelse (valgfrit)',
    fieldDescriptionPh: 'f.eks. Basisplan til små anlæg',
    fieldAuthHeader: 'Autorisations-header (valgfrit)',
    fieldAuthHeaderPh: 'f.eks. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Brugerdefineret HTTP-headernavn til API-nøglen. Hvis tom, bruges URL-parameteren ?apikey.',
    allowQueryParams: 'Dynamiske forespørgselsparametre',
    allowQueryParamsHelp:
      "Tillader klienter at sende placering og string-konfiguration direkte i URL'en. Uden denne mulighed skal klienten konfigurere anlægget i panelet og kun bruge parametrene day og type.",
    correctionEnabled: 'Produktionskorrektion',
    correctionEnabledHelp:
      'Aktiverer prognosekorrektion baseret på faktiske produktionsdata indsendt af klienten.',
    fieldCorrectionDays: 'Dage til beregninger (3-90)',
    fieldCorrectionDaysHelp:
      'Antal seneste dage, der bruges til at beregne korrektionskoefficienterne',
    deleteModalTitle: 'Slet plan: {{name}}',
    deleteAll: 'Slet alt',
    loadingDetails: 'Indlæser detaljer…',
    warnDelete: 'Advarsel! Denne handling sletter planen og alle relaterede elementer:',
    apiKeysCount: 'API-nøgler ({{count}})',
    activeLabel: 'Aktiv',
    inactiveLabel: 'Inaktiv',
    expires: 'Udløber',
    actCodesCount: 'Aktiveringskoder ({{count}})',
    usedBy: 'Brugt af',
    statusUsed: 'Brugt',
    statusUnused: 'Ubrugt',
    noRelated: 'Planen har ingen relaterede elementer. Du kan trygt slette den.',
    confirmDeleteQ:
      'Er du sikker på, at du vil slette denne plan sammen med alle elementerne ovenfor?',
    irreversible: 'Denne handling kan ikke fortrydes.',
  },
  sv: {
    title: 'Planhantering',
    subtitle: 'Konfiguration av prenumerationsplaner',
    addPlan: 'Lägg till plan',
    loadFailed: 'Det gick inte att läsa in planer',
    deleteSuccess: 'Planen togs bort tillsammans med alla relaterade objekt.',
    colName: 'Plannamn',
    colMaxStrings: 'Max. strings',
    unlimited: 'Obegränsat',
    colRateLimit: 'Gräns förfrågningar/min',
    colDescription: 'Beskrivning',
    colCorrection: 'Korrigering',
    correctionYes: 'Ja ({{days}} dagar)',
    correctionNo: 'Nej',
    colCreatedAt: 'Skapad den',
    colActions: 'Åtgärder',
    edit: 'Redigera',
    delete: 'Ta bort',
    loadingPlans: 'Läser in planer…',
    empty: 'Inga planer',
    modalEdit: 'Redigera plan',
    modalAdd: 'Lägg till en ny plan',
    cancel: 'Avbryt',
    save: 'Spara',
    fieldName: 'Plannamn',
    fieldNamePh: 't.ex. Basic, Premium',
    fieldMaxStrings: 'Maximalt antal strings',
    fieldMaxStringsHelp: '-1 betyder ett obegränsat antal strings',
    fieldRateLimit: 'Förfrågningsgräns (per minut)',
    fieldRateLimitHelp: 'Maximalt antal API-förfrågningar per minut',
    fieldDescription: 'Beskrivning (valfritt)',
    fieldDescriptionPh: 't.ex. Basplan för små anläggningar',
    fieldAuthHeader: 'Auktoriseringsheader (valfritt)',
    fieldAuthHeaderPh: 't.ex. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Anpassat HTTP-headernamn för API-nyckeln. Om tomt används URL-parametern ?apikey.',
    allowQueryParams: 'Dynamiska frågeparametrar',
    allowQueryParamsHelp:
      'Tillåter klienter att skicka plats och string-konfiguration direkt i URL:en. Utan detta alternativ måste klienten konfigurera anläggningen i panelen och endast använda parametrarna day och type.',
    correctionEnabled: 'Produktionskorrigering',
    correctionEnabledHelp:
      'Aktiverar prognoskorrigering baserat på faktiska produktionsdata som skickats av klienten.',
    fieldCorrectionDays: 'Dagar för beräkningar (3-90)',
    fieldCorrectionDaysHelp:
      'Antal senaste dagar som används för att beräkna korrigeringskoefficienterna',
    deleteModalTitle: 'Ta bort plan: {{name}}',
    deleteAll: 'Ta bort allt',
    loadingDetails: 'Läser in detaljer…',
    warnDelete: 'Varning! Den här åtgärden tar bort planen och alla relaterade objekt:',
    apiKeysCount: 'API-nycklar ({{count}})',
    activeLabel: 'Aktiv',
    inactiveLabel: 'Inaktiv',
    expires: 'Upphör',
    actCodesCount: 'Aktiveringskoder ({{count}})',
    usedBy: 'Använd av',
    statusUsed: 'Använd',
    statusUnused: 'Oanvänd',
    noRelated: 'Planen har inga relaterade objekt. Du kan ta bort den utan risk.',
    confirmDeleteQ:
      'Är du säker på att du vill ta bort denna plan tillsammans med alla objekt ovan?',
    irreversible: 'Den här åtgärden kan inte ångras.',
  },
  fi: {
    title: 'Suunnitelmien hallinta',
    subtitle: 'Tilaussuunnitelmien määritys',
    addPlan: 'Lisää suunnitelma',
    loadFailed: 'Suunnitelmien lataaminen epäonnistui',
    deleteSuccess:
      'Suunnitelma poistettiin onnistuneesti kaikkien siihen liittyvien kohteiden kanssa.',
    colName: 'Suunnitelman nimi',
    colMaxStrings: 'Maks. stringit',
    unlimited: 'Rajoittamaton',
    colRateLimit: 'Pyyntöjen raja/min',
    colDescription: 'Kuvaus',
    colCorrection: 'Korjaus',
    correctionYes: 'Kyllä ({{days}} päivää)',
    correctionNo: 'Ei',
    colCreatedAt: 'Luotu',
    colActions: 'Toiminnot',
    edit: 'Muokkaa',
    delete: 'Poista',
    loadingPlans: 'Ladataan suunnitelmia…',
    empty: 'Ei suunnitelmia',
    modalEdit: 'Muokkaa suunnitelmaa',
    modalAdd: 'Lisää uusi suunnitelma',
    cancel: 'Peruuta',
    save: 'Tallenna',
    fieldName: 'Suunnitelman nimi',
    fieldNamePh: 'esim. Basic, Premium',
    fieldMaxStrings: 'Stringien enimmäismäärä',
    fieldMaxStringsHelp: '-1 tarkoittaa rajoittamatonta määrää stringejä',
    fieldRateLimit: 'Pyyntöraja (minuutissa)',
    fieldRateLimitHelp: 'API-pyyntöjen enimmäismäärä minuutissa',
    fieldDescription: 'Kuvaus (valinnainen)',
    fieldDescriptionPh: 'esim. Perussuunnitelma pienille laitoksille',
    fieldAuthHeader: 'Valtuutusotsikko (valinnainen)',
    fieldAuthHeaderPh: 'esim. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Mukautettu HTTP-otsikon nimi API-avaimelle. Jos tyhjä, käytetään URL-parametria ?apikey.',
    allowQueryParams: 'Dynaamiset kyselyparametrit',
    allowQueryParamsHelp:
      'Antaa asiakkaiden välittää sijainnin ja stringien määrityksen suoraan URL-osoitteessa. Ilman tätä asetusta asiakkaan on määritettävä laitos paneelissa ja käytettävä vain parametreja day ja type.',
    correctionEnabled: 'Tuotannon korjaus',
    correctionEnabledHelp:
      'Ottaa käyttöön ennusteiden korjauksen asiakkaan lähettämien todellisten tuotantotietojen perusteella.',
    fieldCorrectionDays: 'Päivät laskelmiin (3-90)',
    fieldCorrectionDaysHelp: 'Korjauskertoimien laskemiseen käytettävien viimeisten päivien määrä',
    deleteModalTitle: 'Poista suunnitelma: {{name}}',
    deleteAll: 'Poista kaikki',
    loadingDetails: 'Ladataan tietoja…',
    warnDelete: 'Varoitus! Tämä toiminto poistaa suunnitelman ja kaikki siihen liittyvät kohteet:',
    apiKeysCount: 'API-avaimet ({{count}})',
    activeLabel: 'Aktiivinen',
    inactiveLabel: 'Ei-aktiivinen',
    expires: 'Vanhenee',
    actCodesCount: 'Aktivointikoodit ({{count}})',
    usedBy: 'Käyttäjä',
    statusUsed: 'Käytetty',
    statusUnused: 'Käyttämätön',
    noRelated: 'Suunnitelmalla ei ole siihen liittyviä kohteita. Voit poistaa sen turvallisesti.',
    confirmDeleteQ:
      'Haluatko varmasti poistaa tämän suunnitelman kaikkien yllä olevien kohteiden kanssa?',
    irreversible: 'Tätä toimintoa ei voi peruuttaa.',
  },
  cs: {
    title: 'Správa plánů',
    subtitle: 'Konfigurace předplatných plánů',
    addPlan: 'Přidat plán',
    loadFailed: 'Nepodařilo se načíst plány',
    deleteSuccess: 'Plán byl úspěšně smazán spolu se všemi souvisejícími položkami.',
    colName: 'Název plánu',
    colMaxStrings: 'Max. stringů',
    unlimited: 'Neomezeně',
    colRateLimit: 'Limit požadavků/min',
    colDescription: 'Popis',
    colCorrection: 'Korekce',
    correctionYes: 'Ano ({{days}} dní)',
    correctionNo: 'Ne',
    colCreatedAt: 'Vytvořeno dne',
    colActions: 'Akce',
    edit: 'Upravit',
    delete: 'Smazat',
    loadingPlans: 'Načítání plánů…',
    empty: 'Žádné plány',
    modalEdit: 'Upravit plán',
    modalAdd: 'Přidat nový plán',
    cancel: 'Zrušit',
    save: 'Uložit',
    fieldName: 'Název plánu',
    fieldNamePh: 'např. Basic, Premium',
    fieldMaxStrings: 'Maximální počet stringů',
    fieldMaxStringsHelp: '-1 znamená neomezený počet stringů',
    fieldRateLimit: 'Limit požadavků (za minutu)',
    fieldRateLimitHelp: 'Maximální počet API požadavků za minutu',
    fieldDescription: 'Popis (volitelné)',
    fieldDescriptionPh: 'např. Základní plán pro malé instalace',
    fieldAuthHeader: 'Autorizační hlavička (volitelné)',
    fieldAuthHeaderPh: 'např. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Vlastní název HTTP hlavičky pro API klíč. Pokud je prázdný, použije se URL parametr ?apikey.',
    allowQueryParams: 'Dynamické parametry dotazu',
    allowQueryParamsHelp:
      'Umožňuje klientům předávat polohu a konfiguraci stringů přímo v URL. Bez této možnosti musí klient nakonfigurovat instalaci v panelu a používat pouze parametry day a type.',
    correctionEnabled: 'Korekce produkce',
    correctionEnabledHelp:
      'Povolí korekci předpovědí na základě skutečných produkčních dat zaslaných klientem.',
    fieldCorrectionDays: 'Dny pro výpočty (3-90)',
    fieldCorrectionDaysHelp: 'Počet posledních dní použitých k výpočtu korekčních koeficientů',
    deleteModalTitle: 'Smazat plán: {{name}}',
    deleteAll: 'Smazat vše',
    loadingDetails: 'Načítání detailů…',
    warnDelete: 'Pozor! Tato operace smaže plán a všechny související položky:',
    apiKeysCount: 'API klíče ({{count}})',
    activeLabel: 'Aktivní',
    inactiveLabel: 'Neaktivní',
    expires: 'Vyprší',
    actCodesCount: 'Aktivační kódy ({{count}})',
    usedBy: 'Použil',
    statusUsed: 'Použitý',
    statusUnused: 'Nepoužitý',
    noRelated: 'Plán nemá žádné související položky. Můžete jej bezpečně smazat.',
    confirmDeleteQ: 'Opravdu chcete smazat tento plán spolu se všemi výše uvedenými položkami?',
    irreversible: 'Tato operace je nevratná.',
  },
  sk: {
    title: 'Správa plánov',
    subtitle: 'Konfigurácia predplatných plánov',
    addPlan: 'Pridať plán',
    loadFailed: 'Nepodarilo sa načítať plány',
    deleteSuccess: 'Plán bol úspešne zmazaný spolu so všetkými súvisiacimi položkami.',
    colName: 'Názov plánu',
    colMaxStrings: 'Max. stringov',
    unlimited: 'Neobmedzene',
    colRateLimit: 'Limit požiadaviek/min',
    colDescription: 'Popis',
    colCorrection: 'Korekcia',
    correctionYes: 'Áno ({{days}} dní)',
    correctionNo: 'Nie',
    colCreatedAt: 'Vytvorené dňa',
    colActions: 'Akcie',
    edit: 'Upraviť',
    delete: 'Zmazať',
    loadingPlans: 'Načítavajú sa plány…',
    empty: 'Žiadne plány',
    modalEdit: 'Upraviť plán',
    modalAdd: 'Pridať nový plán',
    cancel: 'Zrušiť',
    save: 'Uložiť',
    fieldName: 'Názov plánu',
    fieldNamePh: 'napr. Basic, Premium',
    fieldMaxStrings: 'Maximálny počet stringov',
    fieldMaxStringsHelp: '-1 znamená neobmedzený počet stringov',
    fieldRateLimit: 'Limit požiadaviek (za minútu)',
    fieldRateLimitHelp: 'Maximálny počet API požiadaviek za minútu',
    fieldDescription: 'Popis (voliteľné)',
    fieldDescriptionPh: 'napr. Základný plán pre malé inštalácie',
    fieldAuthHeader: 'Autorizačná hlavička (voliteľné)',
    fieldAuthHeaderPh: 'napr. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Vlastný názov HTTP hlavičky pre API kľúč. Ak je prázdny, použije sa URL parameter ?apikey.',
    allowQueryParams: 'Dynamické parametre dotazu',
    allowQueryParamsHelp:
      'Umožňuje klientom odovzdávať polohu a konfiguráciu stringov priamo v URL. Bez tejto možnosti musí klient nakonfigurovať inštaláciu v paneli a používať iba parametre day a type.',
    correctionEnabled: 'Korekcia produkcie',
    correctionEnabledHelp:
      'Povolí korekciu predpovedí na základe skutočných produkčných údajov zaslaných klientom.',
    fieldCorrectionDays: 'Dni na výpočty (3-90)',
    fieldCorrectionDaysHelp: 'Počet posledných dní použitých na výpočet korekčných koeficientov',
    deleteModalTitle: 'Zmazať plán: {{name}}',
    deleteAll: 'Zmazať všetko',
    loadingDetails: 'Načítavajú sa detaily…',
    warnDelete: 'Pozor! Táto operácia zmaže plán a všetky súvisiace položky:',
    apiKeysCount: 'API kľúče ({{count}})',
    activeLabel: 'Aktívny',
    inactiveLabel: 'Neaktívny',
    expires: 'Vyprší',
    actCodesCount: 'Aktivačné kódy ({{count}})',
    usedBy: 'Použil',
    statusUsed: 'Použitý',
    statusUnused: 'Nepoužitý',
    noRelated: 'Plán nemá žiadne súvisiace položky. Môžete ho bezpečne zmazať.',
    confirmDeleteQ: 'Naozaj chcete zmazať tento plán spolu so všetkými vyššie uvedenými položkami?',
    irreversible: 'Táto operácia je nezvratná.',
  },
  sl: {
    title: 'Upravljanje načrtov',
    subtitle: 'Konfiguracija naročniških načrtov',
    addPlan: 'Dodaj načrt',
    loadFailed: 'Načrtov ni bilo mogoče naložiti',
    deleteSuccess: 'Načrt je bil uspešno izbrisan skupaj z vsemi povezanimi elementi.',
    colName: 'Ime načrta',
    colMaxStrings: 'Najv. nizov',
    unlimited: 'Neomejeno',
    colRateLimit: 'Omejitev zahtev/min',
    colDescription: 'Opis',
    colCorrection: 'Popravek',
    correctionYes: 'Da ({{days}} dni)',
    correctionNo: 'Ne',
    colCreatedAt: 'Ustvarjeno dne',
    colActions: 'Dejanja',
    edit: 'Uredi',
    delete: 'Izbriši',
    loadingPlans: 'Nalaganje načrtov…',
    empty: 'Ni načrtov',
    modalEdit: 'Uredi načrt',
    modalAdd: 'Dodaj nov načrt',
    cancel: 'Prekliči',
    save: 'Shrani',
    fieldName: 'Ime načrta',
    fieldNamePh: 'npr. Basic, Premium',
    fieldMaxStrings: 'Največje število nizov',
    fieldMaxStringsHelp: '-1 pomeni neomejeno število nizov',
    fieldRateLimit: 'Omejitev zahtev (na minuto)',
    fieldRateLimitHelp: 'Največje število zahtev API na minuto',
    fieldDescription: 'Opis (neobvezno)',
    fieldDescriptionPh: 'npr. Osnovni načrt za majhne naprave',
    fieldAuthHeader: 'Avtorizacijska glava (neobvezno)',
    fieldAuthHeaderPh: 'npr. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Ime glave HTTP po meri za ključ API. Če je prazno, se uporabi parameter URL ?apikey.',
    allowQueryParams: 'Dinamični parametri poizvedbe',
    allowQueryParamsHelp:
      'Odjemalcem omogoča posredovanje lokacije in konfiguracije nizov neposredno v URL-ju. Brez te možnosti mora odjemalec napravo nastaviti v plošči in uporabljati samo parametra day in type.',
    correctionEnabled: 'Popravek proizvodnje',
    correctionEnabledHelp:
      'Omogoči popravek napovedi na podlagi dejanskih proizvodnih podatkov, ki jih pošlje odjemalec.',
    fieldCorrectionDays: 'Dnevi za izračune (3-90)',
    fieldCorrectionDaysHelp:
      'Število zadnjih dni, uporabljenih za izračun korekcijskih koeficientov',
    deleteModalTitle: 'Izbriši načrt: {{name}}',
    deleteAll: 'Izbriši vse',
    loadingDetails: 'Nalaganje podrobnosti…',
    warnDelete: 'Opozorilo! Ta operacija bo izbrisala načrt in vse povezane elemente:',
    apiKeysCount: 'Ključi API ({{count}})',
    activeLabel: 'Aktiven',
    inactiveLabel: 'Neaktiven',
    expires: 'Poteče',
    actCodesCount: 'Aktivacijske kode ({{count}})',
    usedBy: 'Uporabil',
    statusUsed: 'Uporabljena',
    statusUnused: 'Neuporabljena',
    noRelated: 'Načrt nima povezanih elementov. Lahko ga varno izbrišete.',
    confirmDeleteQ:
      'Ali ste prepričani, da želite izbrisati ta načrt skupaj z vsemi zgornjimi elementi?',
    irreversible: 'Te operacije ni mogoče razveljaviti.',
  },
  hr: {
    title: 'Upravljanje planovima',
    subtitle: 'Konfiguracija pretplatničkih planova',
    addPlan: 'Dodaj plan',
    loadFailed: 'Nije moguće učitati planove',
    deleteSuccess: 'Plan je uspješno obrisan zajedno sa svim povezanim stavkama.',
    colName: 'Naziv plana',
    colMaxStrings: 'Maks. nizova',
    unlimited: 'Neograničeno',
    colRateLimit: 'Ograničenje zahtjeva/min',
    colDescription: 'Opis',
    colCorrection: 'Korekcija',
    correctionYes: 'Da ({{days}} dana)',
    correctionNo: 'Ne',
    colCreatedAt: 'Stvoreno dana',
    colActions: 'Radnje',
    edit: 'Uredi',
    delete: 'Obriši',
    loadingPlans: 'Učitavanje planova…',
    empty: 'Nema planova',
    modalEdit: 'Uredi plan',
    modalAdd: 'Dodaj novi plan',
    cancel: 'Odustani',
    save: 'Spremi',
    fieldName: 'Naziv plana',
    fieldNamePh: 'npr. Basic, Premium',
    fieldMaxStrings: 'Najveći broj nizova',
    fieldMaxStringsHelp: '-1 znači neograničen broj nizova',
    fieldRateLimit: 'Ograničenje zahtjeva (po minuti)',
    fieldRateLimitHelp: 'Najveći broj API zahtjeva po minuti',
    fieldDescription: 'Opis (neobavezno)',
    fieldDescriptionPh: 'npr. Osnovni plan za male instalacije',
    fieldAuthHeader: 'Autorizacijsko zaglavlje (neobavezno)',
    fieldAuthHeaderPh: 'npr. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Prilagođeni naziv HTTP zaglavlja za API ključ. Ako je prazno, koristi se URL parametar ?apikey.',
    allowQueryParams: 'Dinamički parametri upita',
    allowQueryParamsHelp:
      'Omogućuje klijentima slanje lokacije i konfiguracije nizova izravno u URL-u. Bez ove opcije klijent mora konfigurirati instalaciju u ploči i koristiti samo parametre day i type.',
    correctionEnabled: 'Korekcija proizvodnje',
    correctionEnabledHelp:
      'Omogućuje korekciju prognoza na temelju stvarnih proizvodnih podataka koje šalje klijent.',
    fieldCorrectionDays: 'Dani za izračune (3-90)',
    fieldCorrectionDaysHelp: 'Broj zadnjih dana korištenih za izračun korekcijskih koeficijenata',
    deleteModalTitle: 'Obriši plan: {{name}}',
    deleteAll: 'Obriši sve',
    loadingDetails: 'Učitavanje detalja…',
    warnDelete: 'Upozorenje! Ova operacija obrisat će plan i sve povezane stavke:',
    apiKeysCount: 'API ključevi ({{count}})',
    activeLabel: 'Aktivan',
    inactiveLabel: 'Neaktivan',
    expires: 'Istječe',
    actCodesCount: 'Aktivacijski kodovi ({{count}})',
    usedBy: 'Iskoristio',
    statusUsed: 'Iskorišten',
    statusUnused: 'Neiskorišten',
    noRelated: 'Plan nema povezanih stavki. Možete ga sigurno obrisati.',
    confirmDeleteQ:
      'Jeste li sigurni da želite obrisati ovaj plan zajedno sa svim gornjim stavkama?',
    irreversible: 'Ova operacija je nepovratna.',
  },
  hu: {
    title: 'Csomagkezelés',
    subtitle: 'Előfizetési csomagok konfigurációja',
    addPlan: 'Csomag hozzáadása',
    loadFailed: 'Nem sikerült betölteni a csomagokat',
    deleteSuccess: 'A csomag az összes kapcsolódó elemmel együtt sikeresen törölve.',
    colName: 'Csomag neve',
    colMaxStrings: 'Max. stringek',
    unlimited: 'Korlátlan',
    colRateLimit: 'Kérések/perc korlát',
    colDescription: 'Leírás',
    colCorrection: 'Korrekció',
    correctionYes: 'Igen ({{days}} nap)',
    correctionNo: 'Nem',
    colCreatedAt: 'Létrehozva',
    colActions: 'Műveletek',
    edit: 'Szerkesztés',
    delete: 'Törlés',
    loadingPlans: 'Csomagok betöltése…',
    empty: 'Nincsenek csomagok',
    modalEdit: 'Csomag szerkesztése',
    modalAdd: 'Új csomag hozzáadása',
    cancel: 'Mégse',
    save: 'Mentés',
    fieldName: 'Csomag neve',
    fieldNamePh: 'pl. Basic, Premium',
    fieldMaxStrings: 'Stringek maximális száma',
    fieldMaxStringsHelp: 'A -1 korlátlan számú stringet jelent',
    fieldRateLimit: 'Kéréskorlát (percenként)',
    fieldRateLimitHelp: 'API-kérések maximális száma percenként',
    fieldDescription: 'Leírás (opcionális)',
    fieldDescriptionPh: 'pl. Alapcsomag kis létesítményekhez',
    fieldAuthHeader: 'Engedélyezési fejléc (opcionális)',
    fieldAuthHeaderPh: 'pl. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Egyéni HTTP-fejlécnév az API-kulcshoz. Ha üres, a ?apikey URL-paraméter használatos.',
    allowQueryParams: 'Dinamikus lekérdezési paraméterek',
    allowQueryParamsHelp:
      'Lehetővé teszi az ügyfeleknek, hogy a helyet és a stringkonfigurációt közvetlenül az URL-ben adják át. E nélkül az opció nélkül az ügyfélnek a panelen kell konfigurálnia a létesítményt, és csak a day és type paramétereket használhatja.',
    correctionEnabled: 'Termeléskorrekció',
    correctionEnabledHelp:
      'Engedélyezi az előrejelzések korrekcióját az ügyfél által beküldött tényleges termelési adatok alapján.',
    fieldCorrectionDays: 'Napok a számításokhoz (3-90)',
    fieldCorrectionDaysHelp: 'A korrekciós együtthatók kiszámításához használt utóbbi napok száma',
    deleteModalTitle: 'Csomag törlése: {{name}}',
    deleteAll: 'Minden törlése',
    loadingDetails: 'Részletek betöltése…',
    warnDelete: 'Figyelem! Ez a művelet törli a csomagot és az összes kapcsolódó elemet:',
    apiKeysCount: 'API-kulcsok ({{count}})',
    activeLabel: 'Aktív',
    inactiveLabel: 'Inaktív',
    expires: 'Lejár',
    actCodesCount: 'Aktiváló kódok ({{count}})',
    usedBy: 'Felhasználta',
    statusUsed: 'Felhasznált',
    statusUnused: 'Fel nem használt',
    noRelated: 'A csomaghoz nincsenek kapcsolódó elemek. Biztonságosan törölheti.',
    confirmDeleteQ: 'Biztosan törölni szeretné ezt a csomagot az összes fenti elemmel együtt?',
    irreversible: 'Ez a művelet visszafordíthatatlan.',
  },
  ro: {
    title: 'Gestionarea planurilor',
    subtitle: 'Configurarea planurilor de abonament',
    addPlan: 'Adaugă plan',
    loadFailed: 'Nu s-au putut încărca planurile',
    deleteSuccess: 'Planul a fost șters cu succes împreună cu toate elementele asociate.',
    colName: 'Nume plan',
    colMaxStrings: 'Max. stringuri',
    unlimited: 'Nelimitat',
    colRateLimit: 'Limită cereri/min',
    colDescription: 'Descriere',
    colCorrection: 'Corecție',
    correctionYes: 'Da ({{days}} zile)',
    correctionNo: 'Nu',
    colCreatedAt: 'Creat la',
    colActions: 'Acțiuni',
    edit: 'Editează',
    delete: 'Șterge',
    loadingPlans: 'Se încarcă planurile…',
    empty: 'Niciun plan',
    modalEdit: 'Editează planul',
    modalAdd: 'Adaugă un plan nou',
    cancel: 'Anulează',
    save: 'Salvează',
    fieldName: 'Nume plan',
    fieldNamePh: 'ex. Basic, Premium',
    fieldMaxStrings: 'Numărul maxim de stringuri',
    fieldMaxStringsHelp: '-1 înseamnă un număr nelimitat de stringuri',
    fieldRateLimit: 'Limită de cereri (pe minut)',
    fieldRateLimitHelp: 'Numărul maxim de cereri API pe minut',
    fieldDescription: 'Descriere (opțional)',
    fieldDescriptionPh: 'ex. Plan de bază pentru instalații mici',
    fieldAuthHeader: 'Antet de autorizare (opțional)',
    fieldAuthHeaderPh: 'ex. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Nume de antet HTTP personalizat pentru cheia API. Dacă este gol, se folosește parametrul URL ?apikey.',
    allowQueryParams: 'Parametri de interogare dinamici',
    allowQueryParamsHelp:
      'Permite clienților să transmită locația și configurația stringurilor direct în URL. Fără această opțiune, clientul trebuie să configureze instalația în panou și să folosească doar parametrii day și type.',
    correctionEnabled: 'Corecția producției',
    correctionEnabledHelp:
      'Activează corecția prognozelor pe baza datelor reale de producție trimise de client.',
    fieldCorrectionDays: 'Zile pentru calcule (3-90)',
    fieldCorrectionDaysHelp:
      'Numărul de zile recente folosite pentru calcularea coeficienților de corecție',
    deleteModalTitle: 'Șterge planul: {{name}}',
    deleteAll: 'Șterge tot',
    loadingDetails: 'Se încarcă detaliile…',
    warnDelete: 'Atenție! Această operație va șterge planul și toate elementele asociate:',
    apiKeysCount: 'Chei API ({{count}})',
    activeLabel: 'Activă',
    inactiveLabel: 'Inactivă',
    expires: 'Expiră',
    actCodesCount: 'Coduri de activare ({{count}})',
    usedBy: 'Utilizat de',
    statusUsed: 'Utilizat',
    statusUnused: 'Neutilizat',
    noRelated: 'Planul nu are elemente asociate. Îl puteți șterge în siguranță.',
    confirmDeleteQ: 'Sigur doriți să ștergeți acest plan împreună cu toate elementele de mai sus?',
    irreversible: 'Această operație este ireversibilă.',
  },
  bg: {
    title: 'Управление на планове',
    subtitle: 'Конфигурация на абонаментни планове',
    addPlan: 'Добавяне на план',
    loadFailed: 'Неуспешно зареждане на плановете',
    deleteSuccess: 'Планът беше успешно изтрит заедно с всички свързани елементи.',
    colName: 'Име на плана',
    colMaxStrings: 'Макс. стрингове',
    unlimited: 'Неограничено',
    colRateLimit: 'Лимит заявки/мин',
    colDescription: 'Описание',
    colCorrection: 'Корекция',
    correctionYes: 'Да ({{days}} дни)',
    correctionNo: 'Не',
    colCreatedAt: 'Създаден на',
    colActions: 'Действия',
    edit: 'Редактиране',
    delete: 'Изтриване',
    loadingPlans: 'Зареждане на плановете…',
    empty: 'Няма планове',
    modalEdit: 'Редактиране на план',
    modalAdd: 'Добавяне на нов план',
    cancel: 'Отказ',
    save: 'Запазване',
    fieldName: 'Име на плана',
    fieldNamePh: 'напр. Basic, Premium',
    fieldMaxStrings: 'Максимален брой стрингове',
    fieldMaxStringsHelp: '-1 означава неограничен брой стрингове',
    fieldRateLimit: 'Лимит на заявки (на минута)',
    fieldRateLimitHelp: 'Максимален брой API заявки на минута',
    fieldDescription: 'Описание (по избор)',
    fieldDescriptionPh: 'напр. Базов план за малки инсталации',
    fieldAuthHeader: 'Заглавка за оторизация (по избор)',
    fieldAuthHeaderPh: 'напр. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Персонализирано име на HTTP заглавка за API ключа. Ако е празно, се използва URL параметърът ?apikey.',
    allowQueryParams: 'Динамични параметри на заявката',
    allowQueryParamsHelp:
      'Позволява на клиентите да предават местоположение и конфигурация на стрингове директно в URL адреса. Без тази опция клиентът трябва да конфигурира инсталацията в панела и да използва само параметрите day и type.',
    correctionEnabled: 'Корекция на производството',
    correctionEnabledHelp:
      'Активира корекция на прогнозите въз основа на реални производствени данни, изпратени от клиента.',
    fieldCorrectionDays: 'Дни за изчисления (3-90)',
    fieldCorrectionDaysHelp:
      'Брой последни дни, използвани за изчисляване на коефициентите за корекция',
    deleteModalTitle: 'Изтриване на план: {{name}}',
    deleteAll: 'Изтриване на всичко',
    loadingDetails: 'Зареждане на детайли…',
    warnDelete: 'Внимание! Тази операция ще изтрие плана и всички свързани елементи:',
    apiKeysCount: 'API ключове ({{count}})',
    activeLabel: 'Активен',
    inactiveLabel: 'Неактивен',
    expires: 'Изтича',
    actCodesCount: 'Кодове за активиране ({{count}})',
    usedBy: 'Използван от',
    statusUsed: 'Използван',
    statusUnused: 'Неизползван',
    noRelated: 'Планът няма свързани елементи. Можете да го изтриете безопасно.',
    confirmDeleteQ:
      'Сигурни ли сте, че искате да изтриете този план заедно с всички елементи по-горе?',
    irreversible: 'Тази операция е необратима.',
  },
  el: {
    title: 'Διαχείριση προγραμμάτων',
    subtitle: 'Διαμόρφωση προγραμμάτων συνδρομής',
    addPlan: 'Προσθήκη προγράμματος',
    loadFailed: 'Αποτυχία φόρτωσης των προγραμμάτων',
    deleteSuccess: 'Το πρόγραμμα διαγράφηκε με επιτυχία μαζί με όλα τα σχετικά στοιχεία.',
    colName: 'Όνομα προγράμματος',
    colMaxStrings: 'Μέγ. strings',
    unlimited: 'Απεριόριστο',
    colRateLimit: 'Όριο αιτημάτων/λεπτό',
    colDescription: 'Περιγραφή',
    colCorrection: 'Διόρθωση',
    correctionYes: 'Ναι ({{days}} ημέρες)',
    correctionNo: 'Όχι',
    colCreatedAt: 'Δημιουργήθηκε στις',
    colActions: 'Ενέργειες',
    edit: 'Επεξεργασία',
    delete: 'Διαγραφή',
    loadingPlans: 'Φόρτωση προγραμμάτων…',
    empty: 'Δεν υπάρχουν προγράμματα',
    modalEdit: 'Επεξεργασία προγράμματος',
    modalAdd: 'Προσθήκη νέου προγράμματος',
    cancel: 'Άκυρο',
    save: 'Αποθήκευση',
    fieldName: 'Όνομα προγράμματος',
    fieldNamePh: 'π.χ. Basic, Premium',
    fieldMaxStrings: 'Μέγιστος αριθμός strings',
    fieldMaxStringsHelp: 'Το -1 σημαίνει απεριόριστος αριθμός strings',
    fieldRateLimit: 'Όριο αιτημάτων (ανά λεπτό)',
    fieldRateLimitHelp: 'Μέγιστος αριθμός αιτημάτων API ανά λεπτό',
    fieldDescription: 'Περιγραφή (προαιρετικό)',
    fieldDescriptionPh: 'π.χ. Βασικό πρόγραμμα για μικρές εγκαταστάσεις',
    fieldAuthHeader: 'Κεφαλίδα εξουσιοδότησης (προαιρετικό)',
    fieldAuthHeaderPh: 'π.χ. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Προσαρμοσμένο όνομα κεφαλίδας HTTP για το κλειδί API. Εάν είναι κενό, χρησιμοποιείται η παράμετρος URL ?apikey.',
    allowQueryParams: 'Δυναμικές παράμετροι ερωτήματος',
    allowQueryParamsHelp:
      'Επιτρέπει στους πελάτες να περνούν την τοποθεσία και τη διαμόρφωση των strings απευθείας στο URL. Χωρίς αυτήν την επιλογή, ο πελάτης πρέπει να διαμορφώσει την εγκατάσταση στον πίνακα και να χρησιμοποιεί μόνο τις παραμέτρους day και type.',
    correctionEnabled: 'Διόρθωση παραγωγής',
    correctionEnabledHelp:
      'Ενεργοποιεί τη διόρθωση προβλέψεων με βάση τα πραγματικά δεδομένα παραγωγής που υποβάλλει ο πελάτης.',
    fieldCorrectionDays: 'Ημέρες για υπολογισμούς (3-90)',
    fieldCorrectionDaysHelp:
      'Αριθμός πρόσφατων ημερών που χρησιμοποιούνται για τον υπολογισμό των συντελεστών διόρθωσης',
    deleteModalTitle: 'Διαγραφή προγράμματος: {{name}}',
    deleteAll: 'Διαγραφή όλων',
    loadingDetails: 'Φόρτωση λεπτομερειών…',
    warnDelete: 'Προσοχή! Αυτή η ενέργεια θα διαγράψει το πρόγραμμα και όλα τα σχετικά στοιχεία:',
    apiKeysCount: 'Κλειδιά API ({{count}})',
    activeLabel: 'Ενεργό',
    inactiveLabel: 'Ανενεργό',
    expires: 'Λήγει',
    actCodesCount: 'Κωδικοί ενεργοποίησης ({{count}})',
    usedBy: 'Χρησιμοποιήθηκε από',
    statusUsed: 'Χρησιμοποιημένος',
    statusUnused: 'Αχρησιμοποίητος',
    noRelated: 'Το πρόγραμμα δεν έχει σχετικά στοιχεία. Μπορείτε να το διαγράψετε με ασφάλεια.',
    confirmDeleteQ:
      'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το πρόγραμμα μαζί με όλα τα παραπάνω στοιχεία;',
    irreversible: 'Αυτή η ενέργεια είναι μη αναστρέψιμη.',
  },
  et: {
    title: 'Pakettide haldamine',
    subtitle: 'Tellimispakettide konfiguratsioon',
    addPlan: 'Lisa pakett',
    loadFailed: 'Pakettide laadimine ebaõnnestus',
    deleteSuccess: 'Pakett kustutati edukalt koos kõigi seotud üksustega.',
    colName: 'Paketi nimi',
    colMaxStrings: 'Maks. stringe',
    unlimited: 'Piiramatu',
    colRateLimit: 'Päringute piir/min',
    colDescription: 'Kirjeldus',
    colCorrection: 'Korrektsioon',
    correctionYes: 'Jah ({{days}} päeva)',
    correctionNo: 'Ei',
    colCreatedAt: 'Loodud',
    colActions: 'Toimingud',
    edit: 'Muuda',
    delete: 'Kustuta',
    loadingPlans: 'Pakettide laadimine…',
    empty: 'Pakette pole',
    modalEdit: 'Muuda paketti',
    modalAdd: 'Lisa uus pakett',
    cancel: 'Tühista',
    save: 'Salvesta',
    fieldName: 'Paketi nimi',
    fieldNamePh: 'nt Basic, Premium',
    fieldMaxStrings: 'Stringide maksimaalne arv',
    fieldMaxStringsHelp: '-1 tähendab piiramatut arvu stringe',
    fieldRateLimit: 'Päringute piir (minutis)',
    fieldRateLimitHelp: 'API-päringute maksimaalne arv minutis',
    fieldDescription: 'Kirjeldus (valikuline)',
    fieldDescriptionPh: 'nt Põhipakett väikestele paigaldistele',
    fieldAuthHeader: 'Autoriseerimispäis (valikuline)',
    fieldAuthHeaderPh: 'nt X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Kohandatud HTTP-päise nimi API-võtmele. Kui tühi, kasutatakse URL-i parameetrit ?apikey.',
    allowQueryParams: 'Dünaamilised päringuparameetrid',
    allowQueryParamsHelp:
      'Lubab klientidel edastada asukoha ja stringide konfiguratsiooni otse URL-is. Ilma selle valikuta peab klient seadistama paigaldise paneelil ja kasutama ainult parameetreid day ja type.',
    correctionEnabled: 'Tootmise korrektsioon',
    correctionEnabledHelp:
      'Lubab prognooside korrektsiooni kliendi esitatud tegelike tootmisandmete põhjal.',
    fieldCorrectionDays: 'Päevad arvutusteks (3-90)',
    fieldCorrectionDaysHelp:
      'Korrektsioonikoefitsientide arvutamiseks kasutatavate viimaste päevade arv',
    deleteModalTitle: 'Kustuta pakett: {{name}}',
    deleteAll: 'Kustuta kõik',
    loadingDetails: 'Üksikasjade laadimine…',
    warnDelete: 'Hoiatus! See toiming kustutab paketi ja kõik seotud üksused:',
    apiKeysCount: 'API-võtmed ({{count}})',
    activeLabel: 'Aktiivne',
    inactiveLabel: 'Mitteaktiivne',
    expires: 'Aegub',
    actCodesCount: 'Aktiveerimiskoodid ({{count}})',
    usedBy: 'Kasutas',
    statusUsed: 'Kasutatud',
    statusUnused: 'Kasutamata',
    noRelated: 'Paketil pole seotud üksusi. Saate selle ohutult kustutada.',
    confirmDeleteQ:
      'Kas oled kindel, et soovid selle paketi koos kõigi ülaltoodud üksustega kustutada?',
    irreversible: 'See toiming on pöördumatu.',
  },
  lv: {
    title: 'Plānu pārvaldība',
    subtitle: 'Abonementu plānu konfigurācija',
    addPlan: 'Pievienot plānu',
    loadFailed: 'Neizdevās ielādēt plānus',
    deleteSuccess: 'Plāns tika veiksmīgi izdzēsts kopā ar visiem saistītajiem elementiem.',
    colName: 'Plāna nosaukums',
    colMaxStrings: 'Maks. virkņu',
    unlimited: 'Neierobežoti',
    colRateLimit: 'Pieprasījumu limits/min',
    colDescription: 'Apraksts',
    colCorrection: 'Korekcija',
    correctionYes: 'Jā ({{days}} dienas)',
    correctionNo: 'Nē',
    colCreatedAt: 'Izveidots',
    colActions: 'Darbības',
    edit: 'Rediģēt',
    delete: 'Dzēst',
    loadingPlans: 'Notiek plānu ielāde…',
    empty: 'Nav plānu',
    modalEdit: 'Rediģēt plānu',
    modalAdd: 'Pievienot jaunu plānu',
    cancel: 'Atcelt',
    save: 'Saglabāt',
    fieldName: 'Plāna nosaukums',
    fieldNamePh: 'piem., Basic, Premium',
    fieldMaxStrings: 'Maksimālais virkņu skaits',
    fieldMaxStringsHelp: '-1 nozīmē neierobežotu virkņu skaitu',
    fieldRateLimit: 'Pieprasījumu limits (minūtē)',
    fieldRateLimitHelp: 'Maksimālais API pieprasījumu skaits minūtē',
    fieldDescription: 'Apraksts (neobligāts)',
    fieldDescriptionPh: 'piem., Pamata plāns mazām iekārtām',
    fieldAuthHeader: 'Autorizācijas galvene (neobligāta)',
    fieldAuthHeaderPh: 'piem., X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Pielāgots HTTP galvenes nosaukums API atslēgai. Ja tukšs, tiek izmantots URL parametrs ?apikey.',
    allowQueryParams: 'Dinamiski vaicājuma parametri',
    allowQueryParamsHelp:
      'Ļauj klientiem nodot atrašanās vietu un virkņu konfigurāciju tieši URL. Bez šīs opcijas klientam jākonfigurē iekārta panelī un jāizmanto tikai parametri day un type.',
    correctionEnabled: 'Ražošanas korekcija',
    correctionEnabledHelp:
      'Iespējo prognožu korekciju, pamatojoties uz klienta iesniegtajiem faktiskajiem ražošanas datiem.',
    fieldCorrectionDays: 'Dienas aprēķiniem (3-90)',
    fieldCorrectionDaysHelp:
      'Pēdējo dienu skaits, kas izmantots korekcijas koeficientu aprēķināšanai',
    deleteModalTitle: 'Dzēst plānu: {{name}}',
    deleteAll: 'Dzēst visu',
    loadingDetails: 'Notiek detaļu ielāde…',
    warnDelete: 'Uzmanību! Šī darbība dzēsīs plānu un visus saistītos elementus:',
    apiKeysCount: 'API atslēgas ({{count}})',
    activeLabel: 'Aktīva',
    inactiveLabel: 'Neaktīva',
    expires: 'Beidzas',
    actCodesCount: 'Aktivizācijas kodi ({{count}})',
    usedBy: 'Izmantoja',
    statusUsed: 'Izmantots',
    statusUnused: 'Neizmantots',
    noRelated: 'Plānam nav saistītu elementu. Varat to droši dzēst.',
    confirmDeleteQ:
      'Vai tiešām vēlaties dzēst šo plānu kopā ar visiem iepriekš minētajiem elementiem?',
    irreversible: 'Šī darbība ir neatgriezeniska.',
  },
  lt: {
    title: 'Planų valdymas',
    subtitle: 'Prenumeratos planų konfigūracija',
    addPlan: 'Pridėti planą',
    loadFailed: 'Nepavyko įkelti planų',
    deleteSuccess: 'Planas sėkmingai ištrintas kartu su visais susijusiais elementais.',
    colName: 'Plano pavadinimas',
    colMaxStrings: 'Maks. stringų',
    unlimited: 'Neribota',
    colRateLimit: 'Užklausų limitas/min',
    colDescription: 'Aprašymas',
    colCorrection: 'Korekcija',
    correctionYes: 'Taip ({{days}} d.)',
    correctionNo: 'Ne',
    colCreatedAt: 'Sukurta',
    colActions: 'Veiksmai',
    edit: 'Redaguoti',
    delete: 'Ištrinti',
    loadingPlans: 'Įkeliami planai…',
    empty: 'Nėra planų',
    modalEdit: 'Redaguoti planą',
    modalAdd: 'Pridėti naują planą',
    cancel: 'Atšaukti',
    save: 'Išsaugoti',
    fieldName: 'Plano pavadinimas',
    fieldNamePh: 'pvz., Basic, Premium',
    fieldMaxStrings: 'Maksimalus stringų skaičius',
    fieldMaxStringsHelp: '-1 reiškia neribotą stringų skaičių',
    fieldRateLimit: 'Užklausų limitas (per minutę)',
    fieldRateLimitHelp: 'Maksimalus API užklausų skaičius per minutę',
    fieldDescription: 'Aprašymas (neprivaloma)',
    fieldDescriptionPh: 'pvz., Bazinis planas mažoms instaliacijoms',
    fieldAuthHeader: 'Autorizacijos antraštė (neprivaloma)',
    fieldAuthHeaderPh: 'pvz., X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Pasirinktinis HTTP antraštės pavadinimas API raktui. Jei tuščia, naudojamas URL parametras ?apikey.',
    allowQueryParams: 'Dinaminiai užklausos parametrai',
    allowQueryParamsHelp:
      'Leidžia klientams perduoti vietą ir stringų konfigūraciją tiesiogiai URL. Be šios parinkties klientas turi sukonfigūruoti įrenginį skydelyje ir naudoti tik parametrus day ir type.',
    correctionEnabled: 'Gamybos korekcija',
    correctionEnabledHelp:
      'Įjungia prognozių korekciją pagal kliento pateiktus faktinius gamybos duomenis.',
    fieldCorrectionDays: 'Dienos skaičiavimams (3-90)',
    fieldCorrectionDaysHelp:
      'Paskutinių dienų skaičius, naudojamas korekcijos koeficientams apskaičiuoti',
    deleteModalTitle: 'Ištrinti planą: {{name}}',
    deleteAll: 'Ištrinti viską',
    loadingDetails: 'Įkeliama išsami informacija…',
    warnDelete: 'Dėmesio! Ši operacija ištrins planą ir visus susijusius elementus:',
    apiKeysCount: 'API raktai ({{count}})',
    activeLabel: 'Aktyvus',
    inactiveLabel: 'Neaktyvus',
    expires: 'Baigiasi',
    actCodesCount: 'Aktyvavimo kodai ({{count}})',
    usedBy: 'Panaudojo',
    statusUsed: 'Panaudotas',
    statusUnused: 'Nepanaudotas',
    noRelated: 'Planas neturi susijusių elementų. Galite jį saugiai ištrinti.',
    confirmDeleteQ:
      'Ar tikrai norite ištrinti šį planą kartu su visais aukščiau pateiktais elementais?',
    irreversible: 'Ši operacija negrįžtama.',
  },
  mt: {
    title: 'Ġestjoni tal-pjanijiet',
    subtitle: "Konfigurazzjoni tal-pjanijiet ta' abbonament",
    addPlan: 'Żid pjan',
    loadFailed: 'Ma rnexxiex jiġu mgħobbija l-pjanijiet',
    deleteSuccess: "Il-pjan tħassar b'suċċess flimkien mal-elementi relatati kollha.",
    colName: 'Isem il-pjan',
    colMaxStrings: 'Maks. strings',
    unlimited: 'Bla limitu',
    colRateLimit: 'Limitu talbiet/min',
    colDescription: 'Deskrizzjoni',
    colCorrection: 'Korrezzjoni',
    correctionYes: 'Iva ({{days}} ijiem)',
    correctionNo: 'Le',
    colCreatedAt: 'Maħluq fl-',
    colActions: 'Azzjonijiet',
    edit: 'Editja',
    delete: 'Ħassar',
    loadingPlans: 'Qed jitgħabbew il-pjanijiet…',
    empty: 'Ebda pjan',
    modalEdit: 'Editja l-pjan',
    modalAdd: 'Żid pjan ġdid',
    cancel: 'Ikkanċella',
    save: 'Issejvja',
    fieldName: 'Isem il-pjan',
    fieldNamePh: 'eż. Basic, Premium',
    fieldMaxStrings: "Numru massimu ta' strings",
    fieldMaxStringsHelp: "-1 ifisser numru bla limitu ta' strings",
    fieldRateLimit: "Limitu ta' talbiet (fil-minuta)",
    fieldRateLimitHelp: "Numru massimu ta' talbiet API fil-minuta",
    fieldDescription: 'Deskrizzjoni (mhux obbligatorja)',
    fieldDescriptionPh: 'eż. Pjan bażiku għal installazzjonijiet żgħar',
    fieldAuthHeader: "Header ta' awtorizzazzjoni (mhux obbligatorju)",
    fieldAuthHeaderPh: 'eż. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      "Isem ta' header HTTP personalizzat għaċ-ċavetta API. Jekk vojt, jintuża l-parametru tal-URL ?apikey.",
    allowQueryParams: 'Parametri tal-mistoqsija dinamiċi',
    allowQueryParamsHelp:
      'Jippermetti lill-klijenti jgħaddu l-pożizzjoni u l-konfigurazzjoni tal-strings direttament fl-URL. Mingħajr din l-għażla, il-klijent irid jikkonfigura l-installazzjoni fil-pannell u juża biss il-parametri day u type.',
    correctionEnabled: 'Korrezzjoni tal-produzzjoni',
    correctionEnabledHelp:
      'Tippermetti l-korrezzjoni tat-tbassir ibbażata fuq data tal-produzzjoni reali mibgħuta mill-klijent.',
    fieldCorrectionDays: 'Jiem għall-kalkoli (3-90)',
    fieldCorrectionDaysHelp:
      "In-numru ta' jiem reċenti użati biex jiġu kkalkulati l-koeffiċjenti ta' korrezzjoni",
    deleteModalTitle: 'Ħassar il-pjan: {{name}}',
    deleteAll: 'Ħassar kollox',
    loadingDetails: 'Qed jitgħabbew id-dettalji…',
    warnDelete: 'Attenzjoni! Din l-operazzjoni se tħassar il-pjan u l-elementi relatati kollha:',
    apiKeysCount: 'Ċwievet API ({{count}})',
    activeLabel: 'Attiv',
    inactiveLabel: 'Inattiv',
    expires: 'Jiskadi',
    actCodesCount: "Kodiċijiet ta' attivazzjoni ({{count}})",
    usedBy: 'Użat minn',
    statusUsed: 'Użat',
    statusUnused: 'Mhux użat',
    noRelated: "Il-pjan m'għandux elementi relatati. Tista' tħassru b'mod sikur.",
    confirmDeleteQ: 'Żgur trid tħassar dan il-pjan flimkien mal-elementi kollha hawn fuq?',
    irreversible: 'Din l-operazzjoni hija irreversibbli.',
  },
  ga: {
    title: 'Bainistíocht pleananna',
    subtitle: 'Cumraíocht pleananna síntiúis',
    addPlan: 'Cuir plean leis',
    loadFailed: 'Theip ar na pleananna a luchtú',
    deleteSuccess: 'Scriosadh an plean go rathúil mar aon leis na míreanna gaolmhara go léir.',
    colName: 'Ainm an phlean',
    colMaxStrings: 'Uas. strings',
    unlimited: 'Gan teorainn',
    colRateLimit: 'Teorainn iarratais/nóim',
    colDescription: 'Cur síos',
    colCorrection: 'Ceartú',
    correctionYes: 'Tá ({{days}} lá)',
    correctionNo: 'Níl',
    colCreatedAt: 'Cruthaithe ar',
    colActions: 'Gníomhartha',
    edit: 'Cuir in eagar',
    delete: 'Scrios',
    loadingPlans: 'Pleananna á luchtú…',
    empty: 'Gan pleananna',
    modalEdit: 'Cuir an plean in eagar',
    modalAdd: 'Cuir plean nua leis',
    cancel: 'Cealaigh',
    save: 'Sábháil',
    fieldName: 'Ainm an phlean',
    fieldNamePh: 'm.sh. Basic, Premium',
    fieldMaxStrings: 'Uaslíon strings',
    fieldMaxStringsHelp: 'Ciallaíonn -1 líon neamhtheoranta strings',
    fieldRateLimit: 'Teorainn iarratas (in aghaidh an nóiméid)',
    fieldRateLimitHelp: 'Uaslíon iarratas API in aghaidh an nóiméid',
    fieldDescription: 'Cur síos (roghnach)',
    fieldDescriptionPh: 'm.sh. Bunphlean do shuiteálacha beaga',
    fieldAuthHeader: 'Ceanntásc údaraithe (roghnach)',
    fieldAuthHeaderPh: 'm.sh. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Ainm ceanntásc HTTP saincheaptha don eochair API. Má tá sé folamh, úsáidtear an paraiméadar URL ?apikey.',
    allowQueryParams: 'Paraiméadair fiosrúcháin dhinimiciúla',
    allowQueryParamsHelp:
      'Ligeann sé do chliaint suíomh agus cumraíocht na strings a sheoladh go díreach san URL. Gan an rogha seo, caithfidh an cliant an tsuiteáil a chumrú sa phainéal agus na paraiméadair day agus type amháin a úsáid.',
    correctionEnabled: 'Ceartú táirgthe',
    correctionEnabledHelp:
      'Cumasaíonn sé ceartú réamhaisnéise bunaithe ar shonraí táirgthe iarbhír a chuir an cliant isteach.',
    fieldCorrectionDays: 'Laethanta le haghaidh ríomhanna (3-90)',
    fieldCorrectionDaysHelp:
      'Líon na laethanta le déanaí a úsáidtear chun na comhéifeachtaí ceartaithe a ríomh',
    deleteModalTitle: 'Scrios plean: {{name}}',
    deleteAll: 'Scrios gach rud',
    loadingDetails: 'Sonraí á luchtú…',
    warnDelete:
      'Rabhadh! Scriosfaidh an oibríocht seo an plean agus na míreanna gaolmhara go léir:',
    apiKeysCount: 'Eochracha API ({{count}})',
    activeLabel: 'Gníomhach',
    inactiveLabel: 'Neamhghníomhach',
    expires: 'Éagann',
    actCodesCount: 'Cóid ghníomhachtaithe ({{count}})',
    usedBy: 'Úsáidte ag',
    statusUsed: 'Úsáidte',
    statusUnused: 'Gan úsáid',
    noRelated: 'Níl míreanna gaolmhara ag an bplean. Is féidir leat é a scriosadh go sábháilte.',
    confirmDeleteQ:
      'An bhfuil tú cinnte gur mhaith leat an plean seo a scriosadh mar aon leis na míreanna thuas go léir?',
    irreversible: 'Tá an oibríocht seo do-aisghabhála.',
  },
  no: {
    title: 'Planadministrasjon',
    subtitle: 'Konfigurasjon av abonnementsplaner',
    addPlan: 'Legg til plan',
    loadFailed: 'Kunne ikke laste planer',
    deleteSuccess: 'Planen ble slettet sammen med alle relaterte elementer.',
    colName: 'Plannavn',
    colMaxStrings: 'Maks. strings',
    unlimited: 'Ubegrenset',
    colRateLimit: 'Grense forespørsler/min',
    colDescription: 'Beskrivelse',
    colCorrection: 'Korreksjon',
    correctionYes: 'Ja ({{days}} dager)',
    correctionNo: 'Nei',
    colCreatedAt: 'Opprettet den',
    colActions: 'Handlinger',
    edit: 'Rediger',
    delete: 'Slett',
    loadingPlans: 'Laster planer…',
    empty: 'Ingen planer',
    modalEdit: 'Rediger plan',
    modalAdd: 'Legg til en ny plan',
    cancel: 'Avbryt',
    save: 'Lagre',
    fieldName: 'Plannavn',
    fieldNamePh: 'f.eks. Basic, Premium',
    fieldMaxStrings: 'Maksimalt antall strings',
    fieldMaxStringsHelp: '-1 betyr et ubegrenset antall strings',
    fieldRateLimit: 'Forespørselsgrense (per minutt)',
    fieldRateLimitHelp: 'Maksimalt antall API-forespørsler per minutt',
    fieldDescription: 'Beskrivelse (valgfritt)',
    fieldDescriptionPh: 'f.eks. Basisplan for små anlegg',
    fieldAuthHeader: 'Autorisasjonshode (valgfritt)',
    fieldAuthHeaderPh: 'f.eks. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Egendefinert HTTP-hodenavn for API-nøkkelen. Hvis tomt, brukes URL-parameteren ?apikey.',
    allowQueryParams: 'Dynamiske spørringsparametere',
    allowQueryParamsHelp:
      'Lar klienter sende plassering og string-konfigurasjon direkte i URL-en. Uten dette alternativet må klienten konfigurere anlegget i panelet og bare bruke parameterne day og type.',
    correctionEnabled: 'Produksjonskorreksjon',
    correctionEnabledHelp:
      'Aktiverer prognosekorreksjon basert på faktiske produksjonsdata sendt inn av klienten.',
    fieldCorrectionDays: 'Dager for beregninger (3-90)',
    fieldCorrectionDaysHelp:
      'Antall siste dager som brukes til å beregne korreksjonskoeffisientene',
    deleteModalTitle: 'Slett plan: {{name}}',
    deleteAll: 'Slett alt',
    loadingDetails: 'Laster detaljer…',
    warnDelete: 'Advarsel! Denne handlingen sletter planen og alle relaterte elementer:',
    apiKeysCount: 'API-nøkler ({{count}})',
    activeLabel: 'Aktiv',
    inactiveLabel: 'Inaktiv',
    expires: 'Utløper',
    actCodesCount: 'Aktiveringskoder ({{count}})',
    usedBy: 'Brukt av',
    statusUsed: 'Brukt',
    statusUnused: 'Ubrukt',
    noRelated: 'Planen har ingen relaterte elementer. Du kan trygt slette den.',
    confirmDeleteQ:
      'Er du sikker på at du vil slette denne planen sammen med alle elementene over?',
    irreversible: 'Denne handlingen kan ikke angres.',
  },
  is: {
    title: 'Áætlanastjórnun',
    subtitle: 'Stillingar áskriftaráætlana',
    addPlan: 'Bæta við áætlun',
    loadFailed: 'Ekki tókst að hlaða áætlanir',
    deleteSuccess: 'Áætluninni var eytt ásamt öllum tengdum atriðum.',
    colName: 'Heiti áætlunar',
    colMaxStrings: 'Hám. strings',
    unlimited: 'Ótakmarkað',
    colRateLimit: 'Mörk beiðna/mín',
    colDescription: 'Lýsing',
    colCorrection: 'Leiðrétting',
    correctionYes: 'Já ({{days}} dagar)',
    correctionNo: 'Nei',
    colCreatedAt: 'Búið til þann',
    colActions: 'Aðgerðir',
    edit: 'Breyta',
    delete: 'Eyða',
    loadingPlans: 'Hleð áætlanir…',
    empty: 'Engar áætlanir',
    modalEdit: 'Breyta áætlun',
    modalAdd: 'Bæta við nýrri áætlun',
    cancel: 'Hætta við',
    save: 'Vista',
    fieldName: 'Heiti áætlunar',
    fieldNamePh: 't.d. Basic, Premium',
    fieldMaxStrings: 'Hámarksfjöldi strings',
    fieldMaxStringsHelp: '-1 þýðir ótakmarkaður fjöldi strings',
    fieldRateLimit: 'Beiðnamörk (á mínútu)',
    fieldRateLimitHelp: 'Hámarksfjöldi API-beiðna á mínútu',
    fieldDescription: 'Lýsing (valfrjálst)',
    fieldDescriptionPh: 't.d. Grunnáætlun fyrir litlar uppsetningar',
    fieldAuthHeader: 'Heimildarhaus (valfrjálst)',
    fieldAuthHeaderPh: 't.d. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Sérsniðið heiti HTTP-hauss fyrir API-lykilinn. Ef tómt er notuð slóðarbreytan ?apikey.',
    allowQueryParams: 'Kraftmiklar fyrirspurnarbreytur',
    allowQueryParamsHelp:
      'Leyfir viðskiptavinum að senda staðsetningu og strings-stillingar beint í slóðinni. Án þessa valkostar verður viðskiptavinurinn að stilla uppsetninguna í stjórnborðinu og nota aðeins breyturnar day og type.',
    correctionEnabled: 'Framleiðsluleiðrétting',
    correctionEnabledHelp:
      'Virkjar leiðréttingu spáa byggða á raunverulegum framleiðslugögnum sem viðskiptavinurinn sendir inn.',
    fieldCorrectionDays: 'Dagar fyrir útreikninga (3-90)',
    fieldCorrectionDaysHelp:
      'Fjöldi nýlegra daga sem notaðir eru til að reikna leiðréttingarstuðlana',
    deleteModalTitle: 'Eyða áætlun: {{name}}',
    deleteAll: 'Eyða öllu',
    loadingDetails: 'Hleð upplýsingar…',
    warnDelete: 'Viðvörun! Þessi aðgerð eyðir áætluninni og öllum tengdum atriðum:',
    apiKeysCount: 'API-lyklar ({{count}})',
    activeLabel: 'Virkt',
    inactiveLabel: 'Óvirkt',
    expires: 'Rennur út',
    actCodesCount: 'Virkjunarkóðar ({{count}})',
    usedBy: 'Notað af',
    statusUsed: 'Notað',
    statusUnused: 'Ónotað',
    noRelated: 'Áætlunin hefur engin tengd atriði. Þú getur eytt henni á öruggan hátt.',
    confirmDeleteQ:
      'Ertu viss um að þú viljir eyða þessari áætlun ásamt öllum atriðunum hér að ofan?',
    irreversible: 'Þessi aðgerð er óafturkræf.',
  },
  uk: {
    title: 'Керування планами',
    subtitle: 'Налаштування планів підписки',
    addPlan: 'Додати план',
    loadFailed: 'Не вдалося завантажити плани',
    deleteSuccess: "План успішно видалено разом з усіма пов'язаними елементами.",
    colName: 'Назва плану',
    colMaxStrings: 'Макс. стрінгів',
    unlimited: 'Без обмежень',
    colRateLimit: 'Ліміт запитів/хв',
    colDescription: 'Опис',
    colCorrection: 'Корекція',
    correctionYes: 'Так ({{days}} днів)',
    correctionNo: 'Ні',
    colCreatedAt: 'Створено',
    colActions: 'Дії',
    edit: 'Редагувати',
    delete: 'Видалити',
    loadingPlans: 'Завантаження планів…',
    empty: 'Немає планів',
    modalEdit: 'Редагувати план',
    modalAdd: 'Додати новий план',
    cancel: 'Скасувати',
    save: 'Зберегти',
    fieldName: 'Назва плану',
    fieldNamePh: 'напр. Basic, Premium',
    fieldMaxStrings: 'Максимальна кількість стрінгів',
    fieldMaxStringsHelp: '-1 означає необмежену кількість стрінгів',
    fieldRateLimit: 'Ліміт запитів (на хвилину)',
    fieldRateLimitHelp: 'Максимальна кількість запитів API на хвилину',
    fieldDescription: "Опис (необов'язково)",
    fieldDescriptionPh: 'напр. Базовий план для малих установок',
    fieldAuthHeader: "Заголовок авторизації (необов'язково)",
    fieldAuthHeaderPh: 'напр. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Користувацька назва HTTP-заголовка для ключа API. Якщо порожньо, використовується параметр URL ?apikey.',
    allowQueryParams: 'Динамічні параметри запиту',
    allowQueryParamsHelp:
      'Дозволяє клієнтам передавати розташування та конфігурацію стрінгів безпосередньо в URL. Без цього параметра клієнт повинен налаштувати установку в панелі та використовувати лише параметри day і type.',
    correctionEnabled: 'Корекція виробництва',
    correctionEnabledHelp:
      'Вмикає корекцію прогнозів на основі фактичних виробничих даних, надісланих клієнтом.',
    fieldCorrectionDays: 'Дні для обчислень (3-90)',
    fieldCorrectionDaysHelp:
      'Кількість останніх днів, що використовуються для обчислення коефіцієнтів корекції',
    deleteModalTitle: 'Видалити план: {{name}}',
    deleteAll: 'Видалити все',
    loadingDetails: 'Завантаження деталей…',
    warnDelete: "Увага! Ця операція видалить план та всі пов'язані елементи:",
    apiKeysCount: 'Ключі API ({{count}})',
    activeLabel: 'Активний',
    inactiveLabel: 'Неактивний',
    expires: 'Закінчується',
    actCodesCount: 'Коди активації ({{count}})',
    usedBy: 'Використав',
    statusUsed: 'Використаний',
    statusUnused: 'Невикористаний',
    noRelated: "План не має пов'язаних елементів. Ви можете безпечно його видалити.",
    confirmDeleteQ: 'Ви впевнені, що хочете видалити цей план разом з усіма елементами вище?',
    irreversible: 'Ця операція незворотна.',
  },
  sr: {
    title: 'Управљање плановима',
    subtitle: 'Конфигурација претплатничких планова',
    addPlan: 'Додај план',
    loadFailed: 'Није могуће учитати планове',
    deleteSuccess: 'План је успешно обрисан заједно са свим повезаним ставкама.',
    colName: 'Назив плана',
    colMaxStrings: 'Макс. стрингова',
    unlimited: 'Неограничено',
    colRateLimit: 'Ограничење захтева/мин',
    colDescription: 'Опис',
    colCorrection: 'Корекција',
    correctionYes: 'Да ({{days}} дана)',
    correctionNo: 'Не',
    colCreatedAt: 'Креиран дана',
    colActions: 'Радње',
    edit: 'Уреди',
    delete: 'Обриши',
    loadingPlans: 'Учитавање планова…',
    empty: 'Нема планова',
    modalEdit: 'Уреди план',
    modalAdd: 'Додај нови план',
    cancel: 'Откажи',
    save: 'Сачувај',
    fieldName: 'Назив плана',
    fieldNamePh: 'нпр. Basic, Premium',
    fieldMaxStrings: 'Максималан број стрингова',
    fieldMaxStringsHelp: '-1 значи неограничен број стрингова',
    fieldRateLimit: 'Ограничење захтева (по минути)',
    fieldRateLimitHelp: 'Максималан број API захтева по минути',
    fieldDescription: 'Опис (опционо)',
    fieldDescriptionPh: 'нпр. Основни план за мале инсталације',
    fieldAuthHeader: 'Ауторизационо заглавље (опционо)',
    fieldAuthHeaderPh: 'нпр. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Прилагођени назив HTTP заглавља за API кључ. Ако је празно, користи се URL параметар ?apikey.',
    allowQueryParams: 'Динамички параметри упита',
    allowQueryParamsHelp:
      'Омогућава клијентима да прослеђују локацију и конфигурацију стрингова директно у URL-у. Без ове опције клијент мора да конфигурише инсталацију у панелу и да користи само параметре day и type.',
    correctionEnabled: 'Корекција производње',
    correctionEnabledHelp:
      'Омогућава корекцију прогноза на основу стварних производних података које шаље клијент.',
    fieldCorrectionDays: 'Дани за прорачуне (3-90)',
    fieldCorrectionDaysHelp:
      'Број последњих дана коришћених за израчунавање коефицијената корекције',
    deleteModalTitle: 'Обриши план: {{name}}',
    deleteAll: 'Обриши све',
    loadingDetails: 'Учитавање детаља…',
    warnDelete: 'Упозорење! Ова операција ће обрисати план и све повезане ставке:',
    apiKeysCount: 'API кључеви ({{count}})',
    activeLabel: 'Активан',
    inactiveLabel: 'Неактиван',
    expires: 'Истиче',
    actCodesCount: 'Активациони кодови ({{count}})',
    usedBy: 'Искористио',
    statusUsed: 'Искоришћен',
    statusUnused: 'Неискоришћен',
    noRelated: 'План нема повезаних ставки. Можете га безбедно обрисати.',
    confirmDeleteQ:
      'Да ли сте сигурни да желите да обришете овај план заједно са свим горњим ставкама?',
    irreversible: 'Ова операција је неповратна.',
  },
  sq: {
    title: 'Menaxhimi i planeve',
    subtitle: 'Konfigurimi i planeve të abonimit',
    addPlan: 'Shto plan',
    loadFailed: 'Dështoi ngarkimi i planeve',
    deleteSuccess: 'Plani u fshi me sukses së bashku me të gjitha artikujt e lidhur.',
    colName: 'Emri i planit',
    colMaxStrings: 'Maks. strings',
    unlimited: 'I pakufizuar',
    colRateLimit: 'Limiti kërkesa/min',
    colDescription: 'Përshkrimi',
    colCorrection: 'Korrigjimi',
    correctionYes: 'Po ({{days}} ditë)',
    correctionNo: 'Jo',
    colCreatedAt: 'Krijuar më',
    colActions: 'Veprime',
    edit: 'Modifiko',
    delete: 'Fshi',
    loadingPlans: 'Po ngarkohen planet…',
    empty: 'Asnjë plan',
    modalEdit: 'Modifiko planin',
    modalAdd: 'Shto një plan të ri',
    cancel: 'Anulo',
    save: 'Ruaj',
    fieldName: 'Emri i planit',
    fieldNamePh: 'p.sh. Basic, Premium',
    fieldMaxStrings: 'Numri maksimal i strings',
    fieldMaxStringsHelp: '-1 do të thotë numër i pakufizuar strings',
    fieldRateLimit: 'Limiti i kërkesave (në minutë)',
    fieldRateLimitHelp: 'Numri maksimal i kërkesave API në minutë',
    fieldDescription: 'Përshkrimi (opsional)',
    fieldDescriptionPh: 'p.sh. Plan bazë për instalime të vogla',
    fieldAuthHeader: 'Header autorizimi (opsional)',
    fieldAuthHeaderPh: 'p.sh. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Emër header HTTP i personalizuar për çelësin API. Nëse bosh, përdoret parametri i URL ?apikey.',
    allowQueryParams: 'Parametra dinamikë të kërkesës',
    allowQueryParamsHelp:
      'Lejon klientët të kalojnë vendndodhjen dhe konfigurimin e strings direkt në URL. Pa këtë opsion klienti duhet të konfigurojë instalimin në panel dhe të përdorë vetëm parametrat day dhe type.',
    correctionEnabled: 'Korrigjimi i prodhimit',
    correctionEnabledHelp:
      'Aktivizon korrigjimin e parashikimeve bazuar në të dhënat reale të prodhimit të dërguara nga klienti.',
    fieldCorrectionDays: 'Ditë për llogaritjet (3-90)',
    fieldCorrectionDaysHelp:
      'Numri i ditëve të fundit të përdorura për të llogaritur koeficientët e korrigjimit',
    deleteModalTitle: 'Fshi planin: {{name}}',
    deleteAll: 'Fshi gjithçka',
    loadingDetails: 'Po ngarkohen detajet…',
    warnDelete: 'Kujdes! Ky veprim do të fshijë planin dhe të gjitha artikujt e lidhur:',
    apiKeysCount: 'Çelësat API ({{count}})',
    activeLabel: 'Aktiv',
    inactiveLabel: 'Joaktiv',
    expires: 'Skadon',
    actCodesCount: 'Kodet e aktivizimit ({{count}})',
    usedBy: 'Përdorur nga',
    statusUsed: 'I përdorur',
    statusUnused: 'I papërdorur',
    noRelated: 'Plani nuk ka artikuj të lidhur. Mund ta fshish në mënyrë të sigurt.',
    confirmDeleteQ:
      'Je i sigurt që dëshiron të fshish këtë plan së bashku me të gjitha artikujt e mësipërm?',
    irreversible: 'Ky veprim është i pakthyeshëm.',
  },
  mk: {
    title: 'Управување со планови',
    subtitle: 'Конфигурација на претплатнички планови',
    addPlan: 'Додај план',
    loadFailed: 'Не може да се вчитаат плановите',
    deleteSuccess: 'Планот е успешно избришан заедно со сите поврзани ставки.',
    colName: 'Име на планот',
    colMaxStrings: 'Макс. стрингови',
    unlimited: 'Неограничено',
    colRateLimit: 'Лимит барања/мин',
    colDescription: 'Опис',
    colCorrection: 'Корекција',
    correctionYes: 'Да ({{days}} дена)',
    correctionNo: 'Не',
    colCreatedAt: 'Создаден на',
    colActions: 'Дејства',
    edit: 'Уреди',
    delete: 'Избриши',
    loadingPlans: 'Се вчитуваат планови…',
    empty: 'Нема планови',
    modalEdit: 'Уреди план',
    modalAdd: 'Додај нов план',
    cancel: 'Откажи',
    save: 'Зачувај',
    fieldName: 'Име на планот',
    fieldNamePh: 'пр. Basic, Premium',
    fieldMaxStrings: 'Максимален број стрингови',
    fieldMaxStringsHelp: '-1 значи неограничен број стрингови',
    fieldRateLimit: 'Лимит на барања (по минута)',
    fieldRateLimitHelp: 'Максимален број API барања по минута',
    fieldDescription: 'Опис (опционално)',
    fieldDescriptionPh: 'пр. Основен план за мали инсталации',
    fieldAuthHeader: 'Заглавие за авторизација (опционално)',
    fieldAuthHeaderPh: 'пр. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Прилагодено име на HTTP заглавие за API клучот. Ако е празно, се користи URL параметарот ?apikey.',
    allowQueryParams: 'Динамички параметри на барање',
    allowQueryParamsHelp:
      'Им овозможува на клиентите да ја пренесуваат локацијата и конфигурацијата на стринговите директно во URL. Без оваа опција клиентот мора да ја конфигурира инсталацијата во панелот и да ги користи само параметрите day и type.',
    correctionEnabled: 'Корекција на производството',
    correctionEnabledHelp:
      'Овозможува корекција на прогнозите врз основа на реални производствени податоци испратени од клиентот.',
    fieldCorrectionDays: 'Денови за пресметки (3-90)',
    fieldCorrectionDaysHelp:
      'Број на последни денови што се користат за пресметка на коефициентите за корекција',
    deleteModalTitle: 'Избриши план: {{name}}',
    deleteAll: 'Избриши сè',
    loadingDetails: 'Се вчитуваат детали…',
    warnDelete: 'Внимание! Оваа операција ќе го избрише планот и сите поврзани ставки:',
    apiKeysCount: 'API клучеви ({{count}})',
    activeLabel: 'Активен',
    inactiveLabel: 'Неактивен',
    expires: 'Истекува',
    actCodesCount: 'Кодови за активација ({{count}})',
    usedBy: 'Искористил',
    statusUsed: 'Искористен',
    statusUnused: 'Неискористен',
    noRelated: 'Планот нема поврзани ставки. Можете безбедно да го избришете.',
    confirmDeleteQ:
      'Дали сте сигурни дека сакате да го избришете овој план заедно со сите горенаведени ставки?',
    irreversible: 'Оваа операција е неповратна.',
  },
  bs: {
    title: 'Upravljanje planovima',
    subtitle: 'Konfiguracija pretplatničkih planova',
    addPlan: 'Dodaj plan',
    loadFailed: 'Nije moguće učitati planove',
    deleteSuccess: 'Plan je uspješno obrisan zajedno sa svim povezanim stavkama.',
    colName: 'Naziv plana',
    colMaxStrings: 'Maks. stringova',
    unlimited: 'Neograničeno',
    colRateLimit: 'Ograničenje zahtjeva/min',
    colDescription: 'Opis',
    colCorrection: 'Korekcija',
    correctionYes: 'Da ({{days}} dana)',
    correctionNo: 'Ne',
    colCreatedAt: 'Kreiran dana',
    colActions: 'Radnje',
    edit: 'Uredi',
    delete: 'Obriši',
    loadingPlans: 'Učitavanje planova…',
    empty: 'Nema planova',
    modalEdit: 'Uredi plan',
    modalAdd: 'Dodaj novi plan',
    cancel: 'Otkaži',
    save: 'Spremi',
    fieldName: 'Naziv plana',
    fieldNamePh: 'npr. Basic, Premium',
    fieldMaxStrings: 'Najveći broj stringova',
    fieldMaxStringsHelp: '-1 znači neograničen broj stringova',
    fieldRateLimit: 'Ograničenje zahtjeva (po minuti)',
    fieldRateLimitHelp: 'Najveći broj API zahtjeva po minuti',
    fieldDescription: 'Opis (opcionalno)',
    fieldDescriptionPh: 'npr. Osnovni plan za male instalacije',
    fieldAuthHeader: 'Autorizacijsko zaglavlje (opcionalno)',
    fieldAuthHeaderPh: 'npr. X-API-Key, Authorization',
    fieldAuthHeaderHelp:
      'Prilagođeni naziv HTTP zaglavlja za API ključ. Ako je prazno, koristi se URL parametar ?apikey.',
    allowQueryParams: 'Dinamički parametri upita',
    allowQueryParamsHelp:
      'Omogućava klijentima da šalju lokaciju i konfiguraciju stringova direktno u URL-u. Bez ove opcije klijent mora konfigurisati instalaciju u panelu i koristiti samo parametre day i type.',
    correctionEnabled: 'Korekcija proizvodnje',
    correctionEnabledHelp:
      'Omogućava korekciju prognoza na osnovu stvarnih proizvodnih podataka koje šalje klijent.',
    fieldCorrectionDays: 'Dani za izračune (3-90)',
    fieldCorrectionDaysHelp: 'Broj zadnjih dana korištenih za izračun korekcijskih koeficijenata',
    deleteModalTitle: 'Obriši plan: {{name}}',
    deleteAll: 'Obriši sve',
    loadingDetails: 'Učitavanje detalja…',
    warnDelete: 'Upozorenje! Ova operacija će obrisati plan i sve povezane stavke:',
    apiKeysCount: 'API ključevi ({{count}})',
    activeLabel: 'Aktivan',
    inactiveLabel: 'Neaktivan',
    expires: 'Ističe',
    actCodesCount: 'Aktivacijski kodovi ({{count}})',
    usedBy: 'Iskoristio',
    statusUsed: 'Iskorišten',
    statusUnused: 'Neiskorišten',
    noRelated: 'Plan nema povezanih stavki. Možete ga sigurno obrisati.',
    confirmDeleteQ:
      'Jeste li sigurni da želite obrisati ovaj plan zajedno sa svim gornjim stavkama?',
    irreversible: 'Ova operacija je nepovratna.',
  },
};

const actCodesTr: Record<string, Dict> = {
  en: {
    title: 'Activation codes',
    subtitle: 'Manage activation codes',
    generateBtn: 'Generate codes',
    loadPlansFailed: 'Failed to load plans',
    loadCodesFailed: 'Failed to load activation codes',
    selectAtLeastOne: 'Select at least one code',
    deleteTitle: 'Delete activation codes',
    deleteConfirm: 'Are you sure you want to delete {{count}} codes?',
    deleteBtn: 'Delete',
    cancel: 'Cancel',
    close: 'Close',
    deleteError: 'Error while deleting codes',
    generatedN: 'Generated {{count}} activation codes',
    copyCode: 'Copy code',
    copied: 'Copied!',
    copyFailed: 'Failed to copy the code. Try again or copy manually.',
    allCopied: 'All codes copied to clipboard',
    copyAllFailed: 'Failed to copy the codes. Try again.',
    colCode: 'Code',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'used',
    statusUnused: 'unused',
    colUsedBy: 'Used by',
    colUsedAt: 'Used at',
    colCreatedAt: 'Created at',
    allStatuses: 'All statuses',
    statusFilterUnused: 'Unused',
    statusFilterUsed: 'Used',
    allPlans: 'All plans',
    selectedCodes: 'Selected codes: {{count}}',
    deleteSelected: 'Delete selected',
    clearSelection: 'Clear selection',
    loadingCodes: 'Loading codes…',
    empty: 'No activation codes',
    prev: 'Previous',
    next: 'Next',
    pageOf: 'Page {{cur}} of {{total}}',
    modalTitle: 'Generate activation codes',
    planLabel: 'Plan',
    selectPlan: 'Select a plan',
    quantityLabel: 'Number of codes',
    quantityHelp: 'You can generate from 1 to 100 codes at once',
    generatedCount: 'Generated {{count}} codes',
    copyAll: 'Copy all',
  },
  pl: {
    title: 'Kody aktywacyjne',
    subtitle: 'Zarządzanie kodami aktywacyjnymi',
    generateBtn: 'Generuj kody',
    loadPlansFailed: 'Nie udało się załadować planów',
    loadCodesFailed: 'Nie udało się załadować kodów aktywacyjnych',
    selectAtLeastOne: 'Wybierz co najmniej jeden kod',
    deleteTitle: 'Usuń kody aktywacyjne',
    deleteConfirm: 'Czy na pewno chcesz usunąć {{count}} kodów?',
    deleteBtn: 'Usuń',
    cancel: 'Anuluj',
    close: 'Zamknij',
    deleteError: 'Błąd podczas usuwania kodów',
    generatedN: 'Wygenerowano {{count}} kodów aktywacyjnych',
    copyCode: 'Kopiuj kod',
    copied: 'Skopiowano!',
    copyFailed: 'Nie udało się skopiować kodu. Spróbuj ponownie lub skopiuj ręcznie.',
    allCopied: 'Wszystkie kody skopiowane do schowka',
    copyAllFailed: 'Nie udało się skopiować kodów. Spróbuj ponownie.',
    colCode: 'Kod',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'użyty',
    statusUnused: 'nieużywany',
    colUsedBy: 'Użyty przez',
    colUsedAt: 'Data użycia',
    colCreatedAt: 'Data utworzenia',
    allStatuses: 'Wszystkie statusy',
    statusFilterUnused: 'Nieużywane',
    statusFilterUsed: 'Użyte',
    allPlans: 'Wszystkie plany',
    selectedCodes: 'Zaznaczono kodów: {{count}}',
    deleteSelected: 'Usuń zaznaczone',
    clearSelection: 'Wyczyść zaznaczenie',
    loadingCodes: 'Wczytywanie kodów…',
    empty: 'Brak kodów aktywacyjnych',
    prev: 'Poprzednia',
    next: 'Następna',
    pageOf: 'Strona {{cur}} z {{total}}',
    modalTitle: 'Generuj kody aktywacyjne',
    planLabel: 'Plan',
    selectPlan: 'Wybierz plan',
    quantityLabel: 'Liczba kodów',
    quantityHelp: 'Możesz wygenerować od 1 do 100 kodów jednocześnie',
    generatedCount: 'Wygenerowano {{count}} kodów',
    copyAll: 'Kopiuj wszystkie',
  },
  de: {
    title: 'Aktivierungscodes',
    subtitle: 'Aktivierungscodes verwalten',
    generateBtn: 'Codes generieren',
    loadPlansFailed: 'Pläne konnten nicht geladen werden',
    loadCodesFailed: 'Aktivierungscodes konnten nicht geladen werden',
    selectAtLeastOne: 'Wählen Sie mindestens einen Code aus',
    deleteTitle: 'Aktivierungscodes löschen',
    deleteConfirm: 'Möchten Sie wirklich {{count}} Codes löschen?',
    deleteBtn: 'Löschen',
    cancel: 'Abbrechen',
    close: 'Schließen',
    deleteError: 'Fehler beim Löschen der Codes',
    generatedN: '{{count}} Aktivierungscodes generiert',
    copyCode: 'Code kopieren',
    copied: 'Kopiert!',
    copyFailed:
      'Code konnte nicht kopiert werden. Versuchen Sie es erneut oder kopieren Sie manuell.',
    allCopied: 'Alle Codes in die Zwischenablage kopiert',
    copyAllFailed: 'Codes konnten nicht kopiert werden. Versuchen Sie es erneut.',
    colCode: 'Code',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'verwendet',
    statusUnused: 'unbenutzt',
    colUsedBy: 'Verwendet von',
    colUsedAt: 'Verwendet am',
    colCreatedAt: 'Erstellt am',
    allStatuses: 'Alle Status',
    statusFilterUnused: 'Unbenutzt',
    statusFilterUsed: 'Verwendet',
    allPlans: 'Alle Pläne',
    selectedCodes: 'Ausgewählte Codes: {{count}}',
    deleteSelected: 'Ausgewählte löschen',
    clearSelection: 'Auswahl aufheben',
    loadingCodes: 'Codes werden geladen…',
    empty: 'Keine Aktivierungscodes',
    prev: 'Zurück',
    next: 'Weiter',
    pageOf: 'Seite {{cur}} von {{total}}',
    modalTitle: 'Aktivierungscodes generieren',
    planLabel: 'Plan',
    selectPlan: 'Plan auswählen',
    quantityLabel: 'Anzahl der Codes',
    quantityHelp: 'Sie können 1 bis 100 Codes gleichzeitig generieren',
    generatedCount: '{{count}} Codes generiert',
    copyAll: 'Alle kopieren',
  },
  fr: {
    title: "Codes d'activation",
    subtitle: "Gérer les codes d'activation",
    generateBtn: 'Générer des codes',
    loadPlansFailed: 'Échec du chargement des plans',
    loadCodesFailed: "Échec du chargement des codes d'activation",
    selectAtLeastOne: 'Sélectionnez au moins un code',
    deleteTitle: "Supprimer les codes d'activation",
    deleteConfirm: 'Voulez-vous vraiment supprimer {{count}} codes ?',
    deleteBtn: 'Supprimer',
    cancel: 'Annuler',
    close: 'Fermer',
    deleteError: 'Erreur lors de la suppression des codes',
    generatedN: "{{count}} codes d'activation générés",
    copyCode: 'Copier le code',
    copied: 'Copié !',
    copyFailed: 'Échec de la copie du code. Réessayez ou copiez manuellement.',
    allCopied: 'Tous les codes copiés dans le presse-papiers',
    copyAllFailed: 'Échec de la copie des codes. Réessayez.',
    colCode: 'Code',
    colPlan: 'Plan',
    colStatus: 'Statut',
    statusUsed: 'utilisé',
    statusUnused: 'inutilisé',
    colUsedBy: 'Utilisé par',
    colUsedAt: 'Utilisé le',
    colCreatedAt: 'Créé le',
    allStatuses: 'Tous les statuts',
    statusFilterUnused: 'Inutilisés',
    statusFilterUsed: 'Utilisés',
    allPlans: 'Tous les plans',
    selectedCodes: 'Codes sélectionnés : {{count}}',
    deleteSelected: 'Supprimer la sélection',
    clearSelection: 'Effacer la sélection',
    loadingCodes: 'Chargement des codes…',
    empty: "Aucun code d'activation",
    prev: 'Précédent',
    next: 'Suivant',
    pageOf: 'Page {{cur}} sur {{total}}',
    modalTitle: "Générer des codes d'activation",
    planLabel: 'Plan',
    selectPlan: 'Sélectionnez un plan',
    quantityLabel: 'Nombre de codes',
    quantityHelp: 'Vous pouvez générer de 1 à 100 codes à la fois',
    generatedCount: '{{count}} codes générés',
    copyAll: 'Tout copier',
  },
  es: {
    title: 'Códigos de activación',
    subtitle: 'Gestionar códigos de activación',
    generateBtn: 'Generar códigos',
    loadPlansFailed: 'No se pudieron cargar los planes',
    loadCodesFailed: 'No se pudieron cargar los códigos de activación',
    selectAtLeastOne: 'Selecciona al menos un código',
    deleteTitle: 'Eliminar códigos de activación',
    deleteConfirm: '¿Seguro que quieres eliminar {{count}} códigos?',
    deleteBtn: 'Eliminar',
    cancel: 'Cancelar',
    close: 'Cerrar',
    deleteError: 'Error al eliminar los códigos',
    generatedN: '{{count}} códigos de activación generados',
    copyCode: 'Copiar código',
    copied: '¡Copiado!',
    copyFailed: 'No se pudo copiar el código. Inténtalo de nuevo o cópialo manualmente.',
    allCopied: 'Todos los códigos copiados al portapapeles',
    copyAllFailed: 'No se pudieron copiar los códigos. Inténtalo de nuevo.',
    colCode: 'Código',
    colPlan: 'Plan',
    colStatus: 'Estado',
    statusUsed: 'usado',
    statusUnused: 'sin usar',
    colUsedBy: 'Usado por',
    colUsedAt: 'Usado el',
    colCreatedAt: 'Creado el',
    allStatuses: 'Todos los estados',
    statusFilterUnused: 'Sin usar',
    statusFilterUsed: 'Usados',
    allPlans: 'Todos los planes',
    selectedCodes: 'Códigos seleccionados: {{count}}',
    deleteSelected: 'Eliminar seleccionados',
    clearSelection: 'Borrar selección',
    loadingCodes: 'Cargando códigos…',
    empty: 'Sin códigos de activación',
    prev: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {{cur}} de {{total}}',
    modalTitle: 'Generar códigos de activación',
    planLabel: 'Plan',
    selectPlan: 'Selecciona un plan',
    quantityLabel: 'Número de códigos',
    quantityHelp: 'Puedes generar de 1 a 100 códigos a la vez',
    generatedCount: '{{count}} códigos generados',
    copyAll: 'Copiar todos',
  },
  it: {
    title: 'Codici di attivazione',
    subtitle: 'Gestisci i codici di attivazione',
    generateBtn: 'Genera codici',
    loadPlansFailed: 'Impossibile caricare i piani',
    loadCodesFailed: 'Impossibile caricare i codici di attivazione',
    selectAtLeastOne: 'Seleziona almeno un codice',
    deleteTitle: 'Elimina codici di attivazione',
    deleteConfirm: 'Vuoi davvero eliminare {{count}} codici?',
    deleteBtn: 'Elimina',
    cancel: 'Annulla',
    close: 'Chiudi',
    deleteError: "Errore durante l'eliminazione dei codici",
    generatedN: '{{count}} codici di attivazione generati',
    copyCode: 'Copia codice',
    copied: 'Copiato!',
    copyFailed: 'Impossibile copiare il codice. Riprova o copia manualmente.',
    allCopied: 'Tutti i codici copiati negli appunti',
    copyAllFailed: 'Impossibile copiare i codici. Riprova.',
    colCode: 'Codice',
    colPlan: 'Piano',
    colStatus: 'Stato',
    statusUsed: 'usato',
    statusUnused: 'non usato',
    colUsedBy: 'Usato da',
    colUsedAt: 'Usato il',
    colCreatedAt: 'Creato il',
    allStatuses: 'Tutti gli stati',
    statusFilterUnused: 'Non usati',
    statusFilterUsed: 'Usati',
    allPlans: 'Tutti i piani',
    selectedCodes: 'Codici selezionati: {{count}}',
    deleteSelected: 'Elimina selezionati',
    clearSelection: 'Cancella selezione',
    loadingCodes: 'Caricamento codici…',
    empty: 'Nessun codice di attivazione',
    prev: 'Precedente',
    next: 'Successiva',
    pageOf: 'Pagina {{cur}} di {{total}}',
    modalTitle: 'Genera codici di attivazione',
    planLabel: 'Piano',
    selectPlan: 'Seleziona un piano',
    quantityLabel: 'Numero di codici',
    quantityHelp: 'Puoi generare da 1 a 100 codici alla volta',
    generatedCount: '{{count}} codici generati',
    copyAll: 'Copia tutti',
  },
  pt: {
    title: 'Códigos de ativação',
    subtitle: 'Gerir códigos de ativação',
    generateBtn: 'Gerar códigos',
    loadPlansFailed: 'Não foi possível carregar os planos',
    loadCodesFailed: 'Não foi possível carregar os códigos de ativação',
    selectAtLeastOne: 'Selecione pelo menos um código',
    deleteTitle: 'Eliminar códigos de ativação',
    deleteConfirm: 'Tem a certeza de que quer eliminar {{count}} códigos?',
    deleteBtn: 'Eliminar',
    cancel: 'Cancelar',
    close: 'Fechar',
    deleteError: 'Erro ao eliminar os códigos',
    generatedN: '{{count}} códigos de ativação gerados',
    copyCode: 'Copiar código',
    copied: 'Copiado!',
    copyFailed: 'Não foi possível copiar o código. Tente novamente ou copie manualmente.',
    allCopied: 'Todos os códigos copiados para a área de transferência',
    copyAllFailed: 'Não foi possível copiar os códigos. Tente novamente.',
    colCode: 'Código',
    colPlan: 'Plano',
    colStatus: 'Estado',
    statusUsed: 'usado',
    statusUnused: 'não usado',
    colUsedBy: 'Usado por',
    colUsedAt: 'Usado em',
    colCreatedAt: 'Criado em',
    allStatuses: 'Todos os estados',
    statusFilterUnused: 'Não usados',
    statusFilterUsed: 'Usados',
    allPlans: 'Todos os planos',
    selectedCodes: 'Códigos selecionados: {{count}}',
    deleteSelected: 'Eliminar selecionados',
    clearSelection: 'Limpar seleção',
    loadingCodes: 'A carregar códigos…',
    empty: 'Sem códigos de ativação',
    prev: 'Anterior',
    next: 'Seguinte',
    pageOf: 'Página {{cur}} de {{total}}',
    modalTitle: 'Gerar códigos de ativação',
    planLabel: 'Plano',
    selectPlan: 'Selecione um plano',
    quantityLabel: 'Número de códigos',
    quantityHelp: 'Pode gerar de 1 a 100 códigos de cada vez',
    generatedCount: '{{count}} códigos gerados',
    copyAll: 'Copiar todos',
  },
  nl: {
    title: 'Activeringscodes',
    subtitle: 'Activeringscodes beheren',
    generateBtn: 'Codes genereren',
    loadPlansFailed: 'Kan plannen niet laden',
    loadCodesFailed: 'Kan activeringscodes niet laden',
    selectAtLeastOne: 'Selecteer ten minste één code',
    deleteTitle: 'Activeringscodes verwijderen',
    deleteConfirm: 'Weet je zeker dat je {{count}} codes wilt verwijderen?',
    deleteBtn: 'Verwijderen',
    cancel: 'Annuleren',
    close: 'Sluiten',
    deleteError: 'Fout bij het verwijderen van codes',
    generatedN: '{{count}} activeringscodes gegenereerd',
    copyCode: 'Code kopiëren',
    copied: 'Gekopieerd!',
    copyFailed: 'Kan de code niet kopiëren. Probeer het opnieuw of kopieer handmatig.',
    allCopied: 'Alle codes naar klembord gekopieerd',
    copyAllFailed: 'Kan de codes niet kopiëren. Probeer het opnieuw.',
    colCode: 'Code',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'gebruikt',
    statusUnused: 'ongebruikt',
    colUsedBy: 'Gebruikt door',
    colUsedAt: 'Gebruikt op',
    colCreatedAt: 'Aangemaakt op',
    allStatuses: 'Alle statussen',
    statusFilterUnused: 'Ongebruikt',
    statusFilterUsed: 'Gebruikt',
    allPlans: 'Alle plannen',
    selectedCodes: 'Geselecteerde codes: {{count}}',
    deleteSelected: 'Geselecteerde verwijderen',
    clearSelection: 'Selectie wissen',
    loadingCodes: 'Codes laden…',
    empty: 'Geen activeringscodes',
    prev: 'Vorige',
    next: 'Volgende',
    pageOf: 'Pagina {{cur}} van {{total}}',
    modalTitle: 'Activeringscodes genereren',
    planLabel: 'Plan',
    selectPlan: 'Selecteer een plan',
    quantityLabel: 'Aantal codes',
    quantityHelp: 'Je kunt 1 tot 100 codes tegelijk genereren',
    generatedCount: '{{count}} codes gegenereerd',
    copyAll: 'Alles kopiëren',
  },
  da: {
    title: 'Aktiveringskoder',
    subtitle: 'Administrer aktiveringskoder',
    generateBtn: 'Generér koder',
    loadPlansFailed: 'Kunne ikke indlæse planer',
    loadCodesFailed: 'Kunne ikke indlæse aktiveringskoder',
    selectAtLeastOne: 'Vælg mindst én kode',
    deleteTitle: 'Slet aktiveringskoder',
    deleteConfirm: 'Er du sikker på, at du vil slette {{count}} koder?',
    deleteBtn: 'Slet',
    cancel: 'Annuller',
    close: 'Luk',
    deleteError: 'Fejl ved sletning af koder',
    generatedN: '{{count}} aktiveringskoder genereret',
    copyCode: 'Kopiér kode',
    copied: 'Kopieret!',
    copyFailed: 'Kunne ikke kopiere koden. Prøv igen, eller kopiér manuelt.',
    allCopied: 'Alle koder kopieret til udklipsholder',
    copyAllFailed: 'Kunne ikke kopiere koderne. Prøv igen.',
    colCode: 'Kode',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'brugt',
    statusUnused: 'ubrugt',
    colUsedBy: 'Brugt af',
    colUsedAt: 'Brugt den',
    colCreatedAt: 'Oprettet den',
    allStatuses: 'Alle statusser',
    statusFilterUnused: 'Ubrugte',
    statusFilterUsed: 'Brugte',
    allPlans: 'Alle planer',
    selectedCodes: 'Valgte koder: {{count}}',
    deleteSelected: 'Slet valgte',
    clearSelection: 'Ryd valg',
    loadingCodes: 'Indlæser koder…',
    empty: 'Ingen aktiveringskoder',
    prev: 'Forrige',
    next: 'Næste',
    pageOf: 'Side {{cur}} af {{total}}',
    modalTitle: 'Generér aktiveringskoder',
    planLabel: 'Plan',
    selectPlan: 'Vælg en plan',
    quantityLabel: 'Antal koder',
    quantityHelp: 'Du kan generere fra 1 til 100 koder ad gangen',
    generatedCount: '{{count}} koder genereret',
    copyAll: 'Kopiér alle',
  },
  sv: {
    title: 'Aktiveringskoder',
    subtitle: 'Hantera aktiveringskoder',
    generateBtn: 'Generera koder',
    loadPlansFailed: 'Det gick inte att läsa in planer',
    loadCodesFailed: 'Det gick inte att läsa in aktiveringskoder',
    selectAtLeastOne: 'Välj minst en kod',
    deleteTitle: 'Ta bort aktiveringskoder',
    deleteConfirm: 'Är du säker på att du vill ta bort {{count}} koder?',
    deleteBtn: 'Ta bort',
    cancel: 'Avbryt',
    close: 'Stäng',
    deleteError: 'Fel vid borttagning av koder',
    generatedN: '{{count}} aktiveringskoder genererade',
    copyCode: 'Kopiera kod',
    copied: 'Kopierad!',
    copyFailed: 'Det gick inte att kopiera koden. Försök igen eller kopiera manuellt.',
    allCopied: 'Alla koder kopierade till urklipp',
    copyAllFailed: 'Det gick inte att kopiera koderna. Försök igen.',
    colCode: 'Kod',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'använd',
    statusUnused: 'oanvänd',
    colUsedBy: 'Använd av',
    colUsedAt: 'Använd den',
    colCreatedAt: 'Skapad den',
    allStatuses: 'Alla statusar',
    statusFilterUnused: 'Oanvända',
    statusFilterUsed: 'Använda',
    allPlans: 'Alla planer',
    selectedCodes: 'Valda koder: {{count}}',
    deleteSelected: 'Ta bort valda',
    clearSelection: 'Rensa val',
    loadingCodes: 'Läser in koder…',
    empty: 'Inga aktiveringskoder',
    prev: 'Föregående',
    next: 'Nästa',
    pageOf: 'Sida {{cur}} av {{total}}',
    modalTitle: 'Generera aktiveringskoder',
    planLabel: 'Plan',
    selectPlan: 'Välj en plan',
    quantityLabel: 'Antal koder',
    quantityHelp: 'Du kan generera 1 till 100 koder åt gången',
    generatedCount: '{{count}} koder genererade',
    copyAll: 'Kopiera alla',
  },
  fi: {
    title: 'Aktivointikoodit',
    subtitle: 'Hallitse aktivointikoodeja',
    generateBtn: 'Luo koodeja',
    loadPlansFailed: 'Suunnitelmien lataaminen epäonnistui',
    loadCodesFailed: 'Aktivointikoodien lataaminen epäonnistui',
    selectAtLeastOne: 'Valitse vähintään yksi koodi',
    deleteTitle: 'Poista aktivointikoodit',
    deleteConfirm: 'Haluatko varmasti poistaa {{count}} koodia?',
    deleteBtn: 'Poista',
    cancel: 'Peruuta',
    close: 'Sulje',
    deleteError: 'Virhe koodeja poistettaessa',
    generatedN: '{{count}} aktivointikoodia luotu',
    copyCode: 'Kopioi koodi',
    copied: 'Kopioitu!',
    copyFailed: 'Koodin kopiointi epäonnistui. Yritä uudelleen tai kopioi manuaalisesti.',
    allCopied: 'Kaikki koodit kopioitu leikepöydälle',
    copyAllFailed: 'Koodien kopiointi epäonnistui. Yritä uudelleen.',
    colCode: 'Koodi',
    colPlan: 'Suunnitelma',
    colStatus: 'Tila',
    statusUsed: 'käytetty',
    statusUnused: 'käyttämätön',
    colUsedBy: 'Käyttäjä',
    colUsedAt: 'Käytetty',
    colCreatedAt: 'Luotu',
    allStatuses: 'Kaikki tilat',
    statusFilterUnused: 'Käyttämättömät',
    statusFilterUsed: 'Käytetyt',
    allPlans: 'Kaikki suunnitelmat',
    selectedCodes: 'Valitut koodit: {{count}}',
    deleteSelected: 'Poista valitut',
    clearSelection: 'Tyhjennä valinta',
    loadingCodes: 'Ladataan koodeja…',
    empty: 'Ei aktivointikoodeja',
    prev: 'Edellinen',
    next: 'Seuraava',
    pageOf: 'Sivu {{cur}} / {{total}}',
    modalTitle: 'Luo aktivointikoodeja',
    planLabel: 'Suunnitelma',
    selectPlan: 'Valitse suunnitelma',
    quantityLabel: 'Koodien määrä',
    quantityHelp: 'Voit luoda 1–100 koodia kerralla',
    generatedCount: '{{count}} koodia luotu',
    copyAll: 'Kopioi kaikki',
  },
  cs: {
    title: 'Aktivační kódy',
    subtitle: 'Správa aktivačních kódů',
    generateBtn: 'Generovat kódy',
    loadPlansFailed: 'Nepodařilo se načíst plány',
    loadCodesFailed: 'Nepodařilo se načíst aktivační kódy',
    selectAtLeastOne: 'Vyberte alespoň jeden kód',
    deleteTitle: 'Smazat aktivační kódy',
    deleteConfirm: 'Opravdu chcete smazat {{count}} kódů?',
    deleteBtn: 'Smazat',
    cancel: 'Zrušit',
    close: 'Zavřít',
    deleteError: 'Chyba při mazání kódů',
    generatedN: 'Vygenerováno {{count}} aktivačních kódů',
    copyCode: 'Kopírovat kód',
    copied: 'Zkopírováno!',
    copyFailed: 'Nepodařilo se zkopírovat kód. Zkuste to znovu nebo zkopírujte ručně.',
    allCopied: 'Všechny kódy zkopírovány do schránky',
    copyAllFailed: 'Nepodařilo se zkopírovat kódy. Zkuste to znovu.',
    colCode: 'Kód',
    colPlan: 'Plán',
    colStatus: 'Stav',
    statusUsed: 'použitý',
    statusUnused: 'nepoužitý',
    colUsedBy: 'Použil',
    colUsedAt: 'Použito dne',
    colCreatedAt: 'Vytvořeno dne',
    allStatuses: 'Všechny stavy',
    statusFilterUnused: 'Nepoužité',
    statusFilterUsed: 'Použité',
    allPlans: 'Všechny plány',
    selectedCodes: 'Vybrané kódy: {{count}}',
    deleteSelected: 'Smazat vybrané',
    clearSelection: 'Zrušit výběr',
    loadingCodes: 'Načítání kódů…',
    empty: 'Žádné aktivační kódy',
    prev: 'Předchozí',
    next: 'Další',
    pageOf: 'Stránka {{cur}} z {{total}}',
    modalTitle: 'Generovat aktivační kódy',
    planLabel: 'Plán',
    selectPlan: 'Vyberte plán',
    quantityLabel: 'Počet kódů',
    quantityHelp: 'Najednou můžete vygenerovat 1 až 100 kódů',
    generatedCount: 'Vygenerováno {{count}} kódů',
    copyAll: 'Kopírovat vše',
  },
  sk: {
    title: 'Aktivačné kódy',
    subtitle: 'Správa aktivačných kódov',
    generateBtn: 'Generovať kódy',
    loadPlansFailed: 'Nepodarilo sa načítať plány',
    loadCodesFailed: 'Nepodarilo sa načítať aktivačné kódy',
    selectAtLeastOne: 'Vyberte aspoň jeden kód',
    deleteTitle: 'Zmazať aktivačné kódy',
    deleteConfirm: 'Naozaj chcete zmazať {{count}} kódov?',
    deleteBtn: 'Zmazať',
    cancel: 'Zrušiť',
    close: 'Zavrieť',
    deleteError: 'Chyba pri mazaní kódov',
    generatedN: 'Vygenerovaných {{count}} aktivačných kódov',
    copyCode: 'Kopírovať kód',
    copied: 'Skopírované!',
    copyFailed: 'Nepodarilo sa skopírovať kód. Skúste to znova alebo skopírujte ručne.',
    allCopied: 'Všetky kódy skopírované do schránky',
    copyAllFailed: 'Nepodarilo sa skopírovať kódy. Skúste to znova.',
    colCode: 'Kód',
    colPlan: 'Plán',
    colStatus: 'Stav',
    statusUsed: 'použitý',
    statusUnused: 'nepoužitý',
    colUsedBy: 'Použil',
    colUsedAt: 'Použité dňa',
    colCreatedAt: 'Vytvorené dňa',
    allStatuses: 'Všetky stavy',
    statusFilterUnused: 'Nepoužité',
    statusFilterUsed: 'Použité',
    allPlans: 'Všetky plány',
    selectedCodes: 'Vybrané kódy: {{count}}',
    deleteSelected: 'Zmazať vybrané',
    clearSelection: 'Zrušiť výber',
    loadingCodes: 'Načítavajú sa kódy…',
    empty: 'Žiadne aktivačné kódy',
    prev: 'Predchádzajúca',
    next: 'Ďalšia',
    pageOf: 'Stránka {{cur}} z {{total}}',
    modalTitle: 'Generovať aktivačné kódy',
    planLabel: 'Plán',
    selectPlan: 'Vyberte plán',
    quantityLabel: 'Počet kódov',
    quantityHelp: 'Naraz môžete vygenerovať 1 až 100 kódov',
    generatedCount: 'Vygenerovaných {{count}} kódov',
    copyAll: 'Kopírovať všetko',
  },
  sl: {
    title: 'Aktivacijske kode',
    subtitle: 'Upravljanje aktivacijskih kod',
    generateBtn: 'Ustvari kode',
    loadPlansFailed: 'Načrtov ni bilo mogoče naložiti',
    loadCodesFailed: 'Aktivacijskih kod ni bilo mogoče naložiti',
    selectAtLeastOne: 'Izberite vsaj eno kodo',
    deleteTitle: 'Izbriši aktivacijske kode',
    deleteConfirm: 'Ali ste prepričani, da želite izbrisati {{count}} kod?',
    deleteBtn: 'Izbriši',
    cancel: 'Prekliči',
    close: 'Zapri',
    deleteError: 'Napaka pri brisanju kod',
    generatedN: 'Ustvarjenih {{count}} aktivacijskih kod',
    copyCode: 'Kopiraj kodo',
    copied: 'Kopirano!',
    copyFailed: 'Kode ni bilo mogoče kopirati. Poskusite znova ali kopirajte ročno.',
    allCopied: 'Vse kode kopirane v odložišče',
    copyAllFailed: 'Kod ni bilo mogoče kopirati. Poskusite znova.',
    colCode: 'Koda',
    colPlan: 'Načrt',
    colStatus: 'Stanje',
    statusUsed: 'uporabljena',
    statusUnused: 'neuporabljena',
    colUsedBy: 'Uporabil',
    colUsedAt: 'Uporabljeno dne',
    colCreatedAt: 'Ustvarjeno dne',
    allStatuses: 'Vsa stanja',
    statusFilterUnused: 'Neuporabljene',
    statusFilterUsed: 'Uporabljene',
    allPlans: 'Vsi načrti',
    selectedCodes: 'Izbrane kode: {{count}}',
    deleteSelected: 'Izbriši izbrane',
    clearSelection: 'Počisti izbor',
    loadingCodes: 'Nalaganje kod…',
    empty: 'Ni aktivacijskih kod',
    prev: 'Prejšnja',
    next: 'Naslednja',
    pageOf: 'Stran {{cur}} od {{total}}',
    modalTitle: 'Ustvari aktivacijske kode',
    planLabel: 'Načrt',
    selectPlan: 'Izberite načrt',
    quantityLabel: 'Število kod',
    quantityHelp: 'Naenkrat lahko ustvarite od 1 do 100 kod',
    generatedCount: 'Ustvarjenih {{count}} kod',
    copyAll: 'Kopiraj vse',
  },
  hr: {
    title: 'Aktivacijski kodovi',
    subtitle: 'Upravljanje aktivacijskim kodovima',
    generateBtn: 'Generiraj kodove',
    loadPlansFailed: 'Nije moguće učitati planove',
    loadCodesFailed: 'Nije moguće učitati aktivacijske kodove',
    selectAtLeastOne: 'Odaberite barem jedan kod',
    deleteTitle: 'Obriši aktivacijske kodove',
    deleteConfirm: 'Jeste li sigurni da želite obrisati {{count}} kodova?',
    deleteBtn: 'Obriši',
    cancel: 'Odustani',
    close: 'Zatvori',
    deleteError: 'Greška pri brisanju kodova',
    generatedN: 'Generirano {{count}} aktivacijskih kodova',
    copyCode: 'Kopiraj kod',
    copied: 'Kopirano!',
    copyFailed: 'Nije moguće kopirati kod. Pokušajte ponovno ili kopirajte ručno.',
    allCopied: 'Svi kodovi kopirani u međuspremnik',
    copyAllFailed: 'Nije moguće kopirati kodove. Pokušajte ponovno.',
    colCode: 'Kod',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'iskorišten',
    statusUnused: 'neiskorišten',
    colUsedBy: 'Iskoristio',
    colUsedAt: 'Iskorišten dana',
    colCreatedAt: 'Stvoreno dana',
    allStatuses: 'Svi statusi',
    statusFilterUnused: 'Neiskorišteni',
    statusFilterUsed: 'Iskorišteni',
    allPlans: 'Svi planovi',
    selectedCodes: 'Odabrani kodovi: {{count}}',
    deleteSelected: 'Obriši odabrane',
    clearSelection: 'Očisti odabir',
    loadingCodes: 'Učitavanje kodova…',
    empty: 'Nema aktivacijskih kodova',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    modalTitle: 'Generiraj aktivacijske kodove',
    planLabel: 'Plan',
    selectPlan: 'Odaberite plan',
    quantityLabel: 'Broj kodova',
    quantityHelp: 'Možete generirati od 1 do 100 kodova odjednom',
    generatedCount: 'Generirano {{count}} kodova',
    copyAll: 'Kopiraj sve',
  },
  hu: {
    title: 'Aktiváló kódok',
    subtitle: 'Aktiváló kódok kezelése',
    generateBtn: 'Kódok generálása',
    loadPlansFailed: 'Nem sikerült betölteni a csomagokat',
    loadCodesFailed: 'Nem sikerült betölteni az aktiváló kódokat',
    selectAtLeastOne: 'Válasszon legalább egy kódot',
    deleteTitle: 'Aktiváló kódok törlése',
    deleteConfirm: 'Biztosan törölni szeretne {{count}} kódot?',
    deleteBtn: 'Törlés',
    cancel: 'Mégse',
    close: 'Bezárás',
    deleteError: 'Hiba a kódok törlése közben',
    generatedN: '{{count}} aktiváló kód generálva',
    copyCode: 'Kód másolása',
    copied: 'Másolva!',
    copyFailed: 'Nem sikerült másolni a kódot. Próbálja újra, vagy másolja kézzel.',
    allCopied: 'Az összes kód a vágólapra másolva',
    copyAllFailed: 'Nem sikerült másolni a kódokat. Próbálja újra.',
    colCode: 'Kód',
    colPlan: 'Csomag',
    colStatus: 'Állapot',
    statusUsed: 'felhasznált',
    statusUnused: 'fel nem használt',
    colUsedBy: 'Felhasználta',
    colUsedAt: 'Felhasználva',
    colCreatedAt: 'Létrehozva',
    allStatuses: 'Minden állapot',
    statusFilterUnused: 'Fel nem használtak',
    statusFilterUsed: 'Felhasználtak',
    allPlans: 'Minden csomag',
    selectedCodes: 'Kijelölt kódok: {{count}}',
    deleteSelected: 'Kijelöltek törlése',
    clearSelection: 'Kijelölés törlése',
    loadingCodes: 'Kódok betöltése…',
    empty: 'Nincsenek aktiváló kódok',
    prev: 'Előző',
    next: 'Következő',
    pageOf: '{{cur}} / {{total}} oldal',
    modalTitle: 'Aktiváló kódok generálása',
    planLabel: 'Csomag',
    selectPlan: 'Válasszon csomagot',
    quantityLabel: 'Kódok száma',
    quantityHelp: 'Egyszerre 1–100 kódot generálhat',
    generatedCount: '{{count}} kód generálva',
    copyAll: 'Összes másolása',
  },
  ro: {
    title: 'Coduri de activare',
    subtitle: 'Gestionarea codurilor de activare',
    generateBtn: 'Generează coduri',
    loadPlansFailed: 'Nu s-au putut încărca planurile',
    loadCodesFailed: 'Nu s-au putut încărca codurile de activare',
    selectAtLeastOne: 'Selectați cel puțin un cod',
    deleteTitle: 'Șterge codurile de activare',
    deleteConfirm: 'Sigur doriți să ștergeți {{count}} coduri?',
    deleteBtn: 'Șterge',
    cancel: 'Anulează',
    close: 'Închide',
    deleteError: 'Eroare la ștergerea codurilor',
    generatedN: '{{count}} coduri de activare generate',
    copyCode: 'Copiază codul',
    copied: 'Copiat!',
    copyFailed: 'Nu s-a putut copia codul. Încercați din nou sau copiați manual.',
    allCopied: 'Toate codurile copiate în clipboard',
    copyAllFailed: 'Nu s-au putut copia codurile. Încercați din nou.',
    colCode: 'Cod',
    colPlan: 'Plan',
    colStatus: 'Stare',
    statusUsed: 'utilizat',
    statusUnused: 'neutilizat',
    colUsedBy: 'Utilizat de',
    colUsedAt: 'Utilizat la',
    colCreatedAt: 'Creat la',
    allStatuses: 'Toate stările',
    statusFilterUnused: 'Neutilizate',
    statusFilterUsed: 'Utilizate',
    allPlans: 'Toate planurile',
    selectedCodes: 'Coduri selectate: {{count}}',
    deleteSelected: 'Șterge selectate',
    clearSelection: 'Șterge selecția',
    loadingCodes: 'Se încarcă codurile…',
    empty: 'Niciun cod de activare',
    prev: 'Anterioară',
    next: 'Următoarea',
    pageOf: 'Pagina {{cur}} din {{total}}',
    modalTitle: 'Generează coduri de activare',
    planLabel: 'Plan',
    selectPlan: 'Selectați un plan',
    quantityLabel: 'Număr de coduri',
    quantityHelp: 'Puteți genera de la 1 la 100 de coduri deodată',
    generatedCount: '{{count}} coduri generate',
    copyAll: 'Copiază tot',
  },
  bg: {
    title: 'Кодове за активиране',
    subtitle: 'Управление на кодове за активиране',
    generateBtn: 'Генериране на кодове',
    loadPlansFailed: 'Неуспешно зареждане на плановете',
    loadCodesFailed: 'Неуспешно зареждане на кодовете за активиране',
    selectAtLeastOne: 'Изберете поне един код',
    deleteTitle: 'Изтриване на кодове за активиране',
    deleteConfirm: 'Сигурни ли сте, че искате да изтриете {{count}} кода?',
    deleteBtn: 'Изтрий',
    cancel: 'Отказ',
    close: 'Затвори',
    deleteError: 'Грешка при изтриване на кодовете',
    generatedN: 'Генерирани са {{count}} кода за активиране',
    copyCode: 'Копирай кода',
    copied: 'Копирано!',
    copyFailed: 'Неуспешно копиране на кода. Опитайте отново или копирайте ръчно.',
    allCopied: 'Всички кодове са копирани в клипборда',
    copyAllFailed: 'Неуспешно копиране на кодовете. Опитайте отново.',
    colCode: 'Код',
    colPlan: 'План',
    colStatus: 'Статус',
    statusUsed: 'използван',
    statusUnused: 'неизползван',
    colUsedBy: 'Използван от',
    colUsedAt: 'Използван на',
    colCreatedAt: 'Създаден на',
    allStatuses: 'Всички статуси',
    statusFilterUnused: 'Неизползвани',
    statusFilterUsed: 'Използвани',
    allPlans: 'Всички планове',
    selectedCodes: 'Избрани кодове: {{count}}',
    deleteSelected: 'Изтрий избраните',
    clearSelection: 'Изчисти избора',
    loadingCodes: 'Зареждане на кодове…',
    empty: 'Няма кодове за активиране',
    prev: 'Предишна',
    next: 'Следваща',
    pageOf: 'Страница {{cur}} от {{total}}',
    modalTitle: 'Генериране на кодове за активиране',
    planLabel: 'План',
    selectPlan: 'Изберете план',
    quantityLabel: 'Брой кодове',
    quantityHelp: 'Можете да генерирате от 1 до 100 кода наведнъж',
    generatedCount: 'Генерирани са {{count}} кода',
    copyAll: 'Копирай всички',
  },
  el: {
    title: 'Κωδικοί ενεργοποίησης',
    subtitle: 'Διαχείριση κωδικών ενεργοποίησης',
    generateBtn: 'Δημιουργία κωδικών',
    loadPlansFailed: 'Αποτυχία φόρτωσης των προγραμμάτων',
    loadCodesFailed: 'Αποτυχία φόρτωσης των κωδικών ενεργοποίησης',
    selectAtLeastOne: 'Επιλέξτε τουλάχιστον έναν κωδικό',
    deleteTitle: 'Διαγραφή κωδικών ενεργοποίησης',
    deleteConfirm: 'Είστε σίγουροι ότι θέλετε να διαγράψετε {{count}} κωδικούς;',
    deleteBtn: 'Διαγραφή',
    cancel: 'Άκυρο',
    close: 'Κλείσιμο',
    deleteError: 'Σφάλμα κατά τη διαγραφή των κωδικών',
    generatedN: 'Δημιουργήθηκαν {{count}} κωδικοί ενεργοποίησης',
    copyCode: 'Αντιγραφή κωδικού',
    copied: 'Αντιγράφηκε!',
    copyFailed: 'Αποτυχία αντιγραφής του κωδικού. Δοκιμάστε ξανά ή αντιγράψτε χειροκίνητα.',
    allCopied: 'Όλοι οι κωδικοί αντιγράφηκαν στο πρόχειρο',
    copyAllFailed: 'Αποτυχία αντιγραφής των κωδικών. Δοκιμάστε ξανά.',
    colCode: 'Κωδικός',
    colPlan: 'Πρόγραμμα',
    colStatus: 'Κατάσταση',
    statusUsed: 'χρησιμοποιημένος',
    statusUnused: 'αχρησιμοποίητος',
    colUsedBy: 'Χρησιμοποιήθηκε από',
    colUsedAt: 'Χρησιμοποιήθηκε στις',
    colCreatedAt: 'Δημιουργήθηκε στις',
    allStatuses: 'Όλες οι καταστάσεις',
    statusFilterUnused: 'Αχρησιμοποίητοι',
    statusFilterUsed: 'Χρησιμοποιημένοι',
    allPlans: 'Όλα τα προγράμματα',
    selectedCodes: 'Επιλεγμένοι κωδικοί: {{count}}',
    deleteSelected: 'Διαγραφή επιλεγμένων',
    clearSelection: 'Εκκαθάριση επιλογής',
    loadingCodes: 'Φόρτωση κωδικών…',
    empty: 'Δεν υπάρχουν κωδικοί ενεργοποίησης',
    prev: 'Προηγούμενη',
    next: 'Επόμενη',
    pageOf: 'Σελίδα {{cur}} από {{total}}',
    modalTitle: 'Δημιουργία κωδικών ενεργοποίησης',
    planLabel: 'Πρόγραμμα',
    selectPlan: 'Επιλέξτε πρόγραμμα',
    quantityLabel: 'Αριθμός κωδικών',
    quantityHelp: 'Μπορείτε να δημιουργήσετε 1 έως 100 κωδικούς ταυτόχρονα',
    generatedCount: 'Δημιουργήθηκαν {{count}} κωδικοί',
    copyAll: 'Αντιγραφή όλων',
  },
  et: {
    title: 'Aktiveerimiskoodid',
    subtitle: 'Aktiveerimiskoodide haldamine',
    generateBtn: 'Genereeri koodid',
    loadPlansFailed: 'Pakettide laadimine ebaõnnestus',
    loadCodesFailed: 'Aktiveerimiskoodide laadimine ebaõnnestus',
    selectAtLeastOne: 'Vali vähemalt üks kood',
    deleteTitle: 'Kustuta aktiveerimiskoodid',
    deleteConfirm: 'Kas oled kindel, et soovid kustutada {{count}} koodi?',
    deleteBtn: 'Kustuta',
    cancel: 'Tühista',
    close: 'Sulge',
    deleteError: 'Viga koodide kustutamisel',
    generatedN: 'Genereeritud {{count}} aktiveerimiskoodi',
    copyCode: 'Kopeeri kood',
    copied: 'Kopeeritud!',
    copyFailed: 'Koodi ei õnnestunud kopeerida. Proovi uuesti või kopeeri käsitsi.',
    allCopied: 'Kõik koodid kopeeritud lõikelauale',
    copyAllFailed: 'Koode ei õnnestunud kopeerida. Proovi uuesti.',
    colCode: 'Kood',
    colPlan: 'Pakett',
    colStatus: 'Olek',
    statusUsed: 'kasutatud',
    statusUnused: 'kasutamata',
    colUsedBy: 'Kasutas',
    colUsedAt: 'Kasutatud',
    colCreatedAt: 'Loodud',
    allStatuses: 'Kõik olekud',
    statusFilterUnused: 'Kasutamata',
    statusFilterUsed: 'Kasutatud',
    allPlans: 'Kõik paketid',
    selectedCodes: 'Valitud koodid: {{count}}',
    deleteSelected: 'Kustuta valitud',
    clearSelection: 'Tühjenda valik',
    loadingCodes: 'Koodide laadimine…',
    empty: 'Aktiveerimiskoode pole',
    prev: 'Eelmine',
    next: 'Järgmine',
    pageOf: 'Lehekülg {{cur}} / {{total}}',
    modalTitle: 'Genereeri aktiveerimiskoodid',
    planLabel: 'Pakett',
    selectPlan: 'Vali pakett',
    quantityLabel: 'Koodide arv',
    quantityHelp: 'Korraga saab genereerida 1 kuni 100 koodi',
    generatedCount: 'Genereeritud {{count}} koodi',
    copyAll: 'Kopeeri kõik',
  },
  lv: {
    title: 'Aktivizācijas kodi',
    subtitle: 'Aktivizācijas kodu pārvaldība',
    generateBtn: 'Ģenerēt kodus',
    loadPlansFailed: 'Neizdevās ielādēt plānus',
    loadCodesFailed: 'Neizdevās ielādēt aktivizācijas kodus',
    selectAtLeastOne: 'Atlasiet vismaz vienu kodu',
    deleteTitle: 'Dzēst aktivizācijas kodus',
    deleteConfirm: 'Vai tiešām vēlaties dzēst {{count}} kodus?',
    deleteBtn: 'Dzēst',
    cancel: 'Atcelt',
    close: 'Aizvērt',
    deleteError: 'Kļūda, dzēšot kodus',
    generatedN: 'Ģenerēti {{count}} aktivizācijas kodi',
    copyCode: 'Kopēt kodu',
    copied: 'Nokopēts!',
    copyFailed: 'Neizdevās nokopēt kodu. Mēģiniet vēlreiz vai kopējiet manuāli.',
    allCopied: 'Visi kodi nokopēti starpliktuvē',
    copyAllFailed: 'Neizdevās nokopēt kodus. Mēģiniet vēlreiz.',
    colCode: 'Kods',
    colPlan: 'Plāns',
    colStatus: 'Statuss',
    statusUsed: 'izmantots',
    statusUnused: 'neizmantots',
    colUsedBy: 'Izmantoja',
    colUsedAt: 'Izmantots',
    colCreatedAt: 'Izveidots',
    allStatuses: 'Visi statusi',
    statusFilterUnused: 'Neizmantotie',
    statusFilterUsed: 'Izmantotie',
    allPlans: 'Visi plāni',
    selectedCodes: 'Atlasītie kodi: {{count}}',
    deleteSelected: 'Dzēst atlasītos',
    clearSelection: 'Notīrīt atlasi',
    loadingCodes: 'Notiek kodu ielāde…',
    empty: 'Nav aktivizācijas kodu',
    prev: 'Iepriekšējā',
    next: 'Nākamā',
    pageOf: 'Lapa {{cur}} no {{total}}',
    modalTitle: 'Ģenerēt aktivizācijas kodus',
    planLabel: 'Plāns',
    selectPlan: 'Atlasiet plānu',
    quantityLabel: 'Kodu skaits',
    quantityHelp: 'Vienlaikus var ģenerēt no 1 līdz 100 kodiem',
    generatedCount: 'Ģenerēti {{count}} kodi',
    copyAll: 'Kopēt visu',
  },
  lt: {
    title: 'Aktyvavimo kodai',
    subtitle: 'Aktyvavimo kodų valdymas',
    generateBtn: 'Generuoti kodus',
    loadPlansFailed: 'Nepavyko įkelti planų',
    loadCodesFailed: 'Nepavyko įkelti aktyvavimo kodų',
    selectAtLeastOne: 'Pasirinkite bent vieną kodą',
    deleteTitle: 'Ištrinti aktyvavimo kodus',
    deleteConfirm: 'Ar tikrai norite ištrinti {{count}} kodų?',
    deleteBtn: 'Ištrinti',
    cancel: 'Atšaukti',
    close: 'Uždaryti',
    deleteError: 'Klaida trinant kodus',
    generatedN: 'Sugeneruota {{count}} aktyvavimo kodų',
    copyCode: 'Kopijuoti kodą',
    copied: 'Nukopijuota!',
    copyFailed: 'Nepavyko nukopijuoti kodo. Bandykite dar kartą arba nukopijuokite rankiniu būdu.',
    allCopied: 'Visi kodai nukopijuoti į iškarpinę',
    copyAllFailed: 'Nepavyko nukopijuoti kodų. Bandykite dar kartą.',
    colCode: 'Kodas',
    colPlan: 'Planas',
    colStatus: 'Būsena',
    statusUsed: 'panaudotas',
    statusUnused: 'nepanaudotas',
    colUsedBy: 'Panaudojo',
    colUsedAt: 'Panaudota',
    colCreatedAt: 'Sukurta',
    allStatuses: 'Visos būsenos',
    statusFilterUnused: 'Nepanaudoti',
    statusFilterUsed: 'Panaudoti',
    allPlans: 'Visi planai',
    selectedCodes: 'Pasirinkti kodai: {{count}}',
    deleteSelected: 'Ištrinti pasirinktus',
    clearSelection: 'Išvalyti pasirinkimą',
    loadingCodes: 'Įkeliami kodai…',
    empty: 'Nėra aktyvavimo kodų',
    prev: 'Ankstesnis',
    next: 'Kitas',
    pageOf: 'Puslapis {{cur}} iš {{total}}',
    modalTitle: 'Generuoti aktyvavimo kodus',
    planLabel: 'Planas',
    selectPlan: 'Pasirinkite planą',
    quantityLabel: 'Kodų skaičius',
    quantityHelp: 'Vienu metu galite sugeneruoti nuo 1 iki 100 kodų',
    generatedCount: 'Sugeneruota {{count}} kodų',
    copyAll: 'Kopijuoti viską',
  },
  mt: {
    title: "Kodiċijiet ta' attivazzjoni",
    subtitle: "Ġestjoni tal-kodiċijiet ta' attivazzjoni",
    generateBtn: 'Iġġenera kodiċijiet',
    loadPlansFailed: 'Ma rnexxiex jiġu mgħobbija l-pjanijiet',
    loadCodesFailed: "Ma rnexxiex jiġu mgħobbija l-kodiċijiet ta' attivazzjoni",
    selectAtLeastOne: 'Agħżel mill-inqas kodiċi wieħed',
    deleteTitle: "Ħassar il-kodiċijiet ta' attivazzjoni",
    deleteConfirm: 'Żgur trid tħassar {{count}} kodiċijiet?',
    deleteBtn: 'Ħassar',
    cancel: 'Ikkanċella',
    close: 'Agħlaq',
    deleteError: 'Żball waqt it-tħassir tal-kodiċijiet',
    generatedN: "Ġġenerati {{count}} kodiċijiet ta' attivazzjoni",
    copyCode: 'Ikkopja l-kodiċi',
    copied: 'Ikkopjat!',
    copyFailed: "Ma rnexxiex jiġi kkopjat il-kodiċi. Erġa' pprova jew ikkopja manwalment.",
    allCopied: 'Il-kodiċijiet kollha kkopjati fil-clipboard',
    copyAllFailed: "Ma rnexxiex jiġu kkopjati l-kodiċijiet. Erġa' pprova.",
    colCode: 'Kodiċi',
    colPlan: 'Pjan',
    colStatus: 'Status',
    statusUsed: 'użat',
    statusUnused: 'mhux użat',
    colUsedBy: 'Użat minn',
    colUsedAt: 'Użat fl-',
    colCreatedAt: 'Maħluq fl-',
    allStatuses: 'L-istatusijiet kollha',
    statusFilterUnused: 'Mhux użati',
    statusFilterUsed: 'Użati',
    allPlans: 'Il-pjanijiet kollha',
    selectedCodes: 'Kodiċijiet magħżula: {{count}}',
    deleteSelected: 'Ħassar magħżula',
    clearSelection: 'Naddaf l-għażla',
    loadingCodes: 'Qed jitgħabbew il-kodiċijiet…',
    empty: "Ebda kodiċi ta' attivazzjoni",
    prev: 'Preċedenti',
    next: 'Li jmiss',
    pageOf: 'Paġna {{cur}} minn {{total}}',
    modalTitle: "Iġġenera kodiċijiet ta' attivazzjoni",
    planLabel: 'Pjan',
    selectPlan: 'Agħżel pjan',
    quantityLabel: "Numru ta' kodiċijiet",
    quantityHelp: "Tista' tiġġenera minn 1 sa 100 kodiċi f'daqqa",
    generatedCount: 'Ġġenerati {{count}} kodiċijiet',
    copyAll: 'Ikkopja kollox',
  },
  ga: {
    title: 'Cóid ghníomhachtaithe',
    subtitle: 'Bainistigh cóid ghníomhachtaithe',
    generateBtn: 'Gin cóid',
    loadPlansFailed: 'Theip ar na pleananna a luchtú',
    loadCodesFailed: 'Theip ar na cóid ghníomhachtaithe a luchtú',
    selectAtLeastOne: 'Roghnaigh cód amháin ar a laghad',
    deleteTitle: 'Scrios cóid ghníomhachtaithe',
    deleteConfirm: 'An bhfuil tú cinnte gur mhaith leat {{count}} cód a scriosadh?',
    deleteBtn: 'Scrios',
    cancel: 'Cealaigh',
    close: 'Dún',
    deleteError: 'Earráid agus cóid á scriosadh',
    generatedN: 'Ginadh {{count}} cód gníomhachtaithe',
    copyCode: 'Cóipeáil cód',
    copied: 'Cóipeáilte!',
    copyFailed: 'Theip ar an gcód a chóipeáil. Bain triail eile as nó cóipeáil de láimh.',
    allCopied: 'Na cóid go léir cóipeáilte chuig an ngearrthaisce',
    copyAllFailed: 'Theip ar na cóid a chóipeáil. Bain triail eile as.',
    colCode: 'Cód',
    colPlan: 'Plean',
    colStatus: 'Stádas',
    statusUsed: 'úsáidte',
    statusUnused: 'gan úsáid',
    colUsedBy: 'Úsáidte ag',
    colUsedAt: 'Úsáidte ar',
    colCreatedAt: 'Cruthaithe ar',
    allStatuses: 'Gach stádas',
    statusFilterUnused: 'Gan úsáid',
    statusFilterUsed: 'Úsáidte',
    allPlans: 'Gach plean',
    selectedCodes: 'Cóid roghnaithe: {{count}}',
    deleteSelected: 'Scrios na cinn roghnaithe',
    clearSelection: 'Glan an roghnúchán',
    loadingCodes: 'Cóid á luchtú…',
    empty: 'Gan cóid ghníomhachtaithe',
    prev: 'Roimhe seo',
    next: 'Ar aghaidh',
    pageOf: 'Leathanach {{cur}} as {{total}}',
    modalTitle: 'Gin cóid ghníomhachtaithe',
    planLabel: 'Plean',
    selectPlan: 'Roghnaigh plean',
    quantityLabel: 'Líon na gcód',
    quantityHelp: 'Is féidir leat 1 go 100 cód a ghiniúint ag an am céanna',
    generatedCount: 'Ginadh {{count}} cód',
    copyAll: 'Cóipeáil gach rud',
  },
  no: {
    title: 'Aktiveringskoder',
    subtitle: 'Administrer aktiveringskoder',
    generateBtn: 'Generer koder',
    loadPlansFailed: 'Kunne ikke laste planer',
    loadCodesFailed: 'Kunne ikke laste aktiveringskoder',
    selectAtLeastOne: 'Velg minst én kode',
    deleteTitle: 'Slett aktiveringskoder',
    deleteConfirm: 'Er du sikker på at du vil slette {{count}} koder?',
    deleteBtn: 'Slett',
    cancel: 'Avbryt',
    close: 'Lukk',
    deleteError: 'Feil ved sletting av koder',
    generatedN: '{{count}} aktiveringskoder generert',
    copyCode: 'Kopier kode',
    copied: 'Kopiert!',
    copyFailed: 'Kunne ikke kopiere koden. Prøv igjen eller kopier manuelt.',
    allCopied: 'Alle koder kopiert til utklippstavlen',
    copyAllFailed: 'Kunne ikke kopiere kodene. Prøv igjen.',
    colCode: 'Kode',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'brukt',
    statusUnused: 'ubrukt',
    colUsedBy: 'Brukt av',
    colUsedAt: 'Brukt den',
    colCreatedAt: 'Opprettet den',
    allStatuses: 'Alle statuser',
    statusFilterUnused: 'Ubrukte',
    statusFilterUsed: 'Brukte',
    allPlans: 'Alle planer',
    selectedCodes: 'Valgte koder: {{count}}',
    deleteSelected: 'Slett valgte',
    clearSelection: 'Tøm valg',
    loadingCodes: 'Laster koder…',
    empty: 'Ingen aktiveringskoder',
    prev: 'Forrige',
    next: 'Neste',
    pageOf: 'Side {{cur}} av {{total}}',
    modalTitle: 'Generer aktiveringskoder',
    planLabel: 'Plan',
    selectPlan: 'Velg en plan',
    quantityLabel: 'Antall koder',
    quantityHelp: 'Du kan generere fra 1 til 100 koder om gangen',
    generatedCount: '{{count}} koder generert',
    copyAll: 'Kopier alle',
  },
  is: {
    title: 'Virkjunarkóðar',
    subtitle: 'Stjórna virkjunarkóðum',
    generateBtn: 'Búa til kóða',
    loadPlansFailed: 'Ekki tókst að hlaða áætlanir',
    loadCodesFailed: 'Ekki tókst að hlaða virkjunarkóða',
    selectAtLeastOne: 'Veldu að minnsta kosti einn kóða',
    deleteTitle: 'Eyða virkjunarkóðum',
    deleteConfirm: 'Ertu viss um að þú viljir eyða {{count}} kóðum?',
    deleteBtn: 'Eyða',
    cancel: 'Hætta við',
    close: 'Loka',
    deleteError: 'Villa við að eyða kóðum',
    generatedN: 'Bjó til {{count}} virkjunarkóða',
    copyCode: 'Afrita kóða',
    copied: 'Afritað!',
    copyFailed: 'Ekki tókst að afrita kóðann. Reyndu aftur eða afritaðu handvirkt.',
    allCopied: 'Allir kóðar afritaðir á klippiborð',
    copyAllFailed: 'Ekki tókst að afrita kóðana. Reyndu aftur.',
    colCode: 'Kóði',
    colPlan: 'Áætlun',
    colStatus: 'Staða',
    statusUsed: 'notaður',
    statusUnused: 'ónotaður',
    colUsedBy: 'Notaður af',
    colUsedAt: 'Notaður þann',
    colCreatedAt: 'Búinn til þann',
    allStatuses: 'Allar stöður',
    statusFilterUnused: 'Ónotaðir',
    statusFilterUsed: 'Notaðir',
    allPlans: 'Allar áætlanir',
    selectedCodes: 'Valdir kóðar: {{count}}',
    deleteSelected: 'Eyða völdum',
    clearSelection: 'Hreinsa val',
    loadingCodes: 'Hleð kóða…',
    empty: 'Engir virkjunarkóðar',
    prev: 'Fyrri',
    next: 'Næsta',
    pageOf: 'Síða {{cur}} af {{total}}',
    modalTitle: 'Búa til virkjunarkóða',
    planLabel: 'Áætlun',
    selectPlan: 'Veldu áætlun',
    quantityLabel: 'Fjöldi kóða',
    quantityHelp: 'Þú getur búið til 1 til 100 kóða í einu',
    generatedCount: 'Bjó til {{count}} kóða',
    copyAll: 'Afrita allt',
  },
  uk: {
    title: 'Коди активації',
    subtitle: 'Керування кодами активації',
    generateBtn: 'Згенерувати коди',
    loadPlansFailed: 'Не вдалося завантажити плани',
    loadCodesFailed: 'Не вдалося завантажити коди активації',
    selectAtLeastOne: 'Виберіть принаймні один код',
    deleteTitle: 'Видалити коди активації',
    deleteConfirm: 'Ви впевнені, що хочете видалити {{count}} кодів?',
    deleteBtn: 'Видалити',
    cancel: 'Скасувати',
    close: 'Закрити',
    deleteError: 'Помилка під час видалення кодів',
    generatedN: 'Згенеровано {{count}} кодів активації',
    copyCode: 'Копіювати код',
    copied: 'Скопійовано!',
    copyFailed: 'Не вдалося скопіювати код. Спробуйте ще раз або скопіюйте вручну.',
    allCopied: 'Усі коди скопійовано в буфер обміну',
    copyAllFailed: 'Не вдалося скопіювати коди. Спробуйте ще раз.',
    colCode: 'Код',
    colPlan: 'План',
    colStatus: 'Статус',
    statusUsed: 'використаний',
    statusUnused: 'невикористаний',
    colUsedBy: 'Використав',
    colUsedAt: 'Використано',
    colCreatedAt: 'Створено',
    allStatuses: 'Усі статуси',
    statusFilterUnused: 'Невикористані',
    statusFilterUsed: 'Використані',
    allPlans: 'Усі плани',
    selectedCodes: 'Вибрані коди: {{count}}',
    deleteSelected: 'Видалити вибрані',
    clearSelection: 'Очистити вибір',
    loadingCodes: 'Завантаження кодів…',
    empty: 'Немає кодів активації',
    prev: 'Попередня',
    next: 'Наступна',
    pageOf: 'Сторінка {{cur}} з {{total}}',
    modalTitle: 'Згенерувати коди активації',
    planLabel: 'План',
    selectPlan: 'Виберіть план',
    quantityLabel: 'Кількість кодів',
    quantityHelp: 'Ви можете згенерувати від 1 до 100 кодів за раз',
    generatedCount: 'Згенеровано {{count}} кодів',
    copyAll: 'Копіювати все',
  },
  sr: {
    title: 'Кодови за активацију',
    subtitle: 'Управљање кодовима за активацију',
    generateBtn: 'Генериши кодове',
    loadPlansFailed: 'Није могуће учитати планове',
    loadCodesFailed: 'Није могуће учитати кодове за активацију',
    selectAtLeastOne: 'Изаберите барем један код',
    deleteTitle: 'Обриши кодове за активацију',
    deleteConfirm: 'Да ли сте сигурни да желите да обришете {{count}} кодова?',
    deleteBtn: 'Обриши',
    cancel: 'Откажи',
    close: 'Затвори',
    deleteError: 'Грешка при брисању кодова',
    generatedN: 'Генерисано {{count}} кодова за активацију',
    copyCode: 'Копирај код',
    copied: 'Копирано!',
    copyFailed: 'Није могуће копирати код. Покушајте поново или копирајте ручно.',
    allCopied: 'Сви кодови копирани у клипборд',
    copyAllFailed: 'Није могуће копирати кодове. Покушајте поново.',
    colCode: 'Код',
    colPlan: 'План',
    colStatus: 'Статус',
    statusUsed: 'искоришћен',
    statusUnused: 'неискоришћен',
    colUsedBy: 'Искористио',
    colUsedAt: 'Искоришћен дана',
    colCreatedAt: 'Креиран дана',
    allStatuses: 'Сви статуси',
    statusFilterUnused: 'Неискоришћени',
    statusFilterUsed: 'Искоришћени',
    allPlans: 'Сви планови',
    selectedCodes: 'Изабрани кодови: {{count}}',
    deleteSelected: 'Обриши изабране',
    clearSelection: 'Очисти избор',
    loadingCodes: 'Учитавање кодова…',
    empty: 'Нема кодова за активацију',
    prev: 'Претходна',
    next: 'Следећа',
    pageOf: 'Страница {{cur}} од {{total}}',
    modalTitle: 'Генериши кодове за активацију',
    planLabel: 'План',
    selectPlan: 'Изаберите план',
    quantityLabel: 'Број кодова',
    quantityHelp: 'Можете генерисати од 1 до 100 кодова одједном',
    generatedCount: 'Генерисано {{count}} кодова',
    copyAll: 'Копирај све',
  },
  sq: {
    title: 'Kodet e aktivizimit',
    subtitle: 'Menaxho kodet e aktivizimit',
    generateBtn: 'Gjenero kode',
    loadPlansFailed: 'Dështoi ngarkimi i planeve',
    loadCodesFailed: 'Dështoi ngarkimi i kodeve të aktivizimit',
    selectAtLeastOne: 'Zgjidh të paktën një kod',
    deleteTitle: 'Fshi kodet e aktivizimit',
    deleteConfirm: 'Je i sigurt që dëshiron të fshish {{count}} kode?',
    deleteBtn: 'Fshi',
    cancel: 'Anulo',
    close: 'Mbyll',
    deleteError: 'Gabim gjatë fshirjes së kodeve',
    generatedN: 'U gjeneruan {{count}} kode aktivizimi',
    copyCode: 'Kopjo kodin',
    copied: 'U kopjua!',
    copyFailed: 'Dështoi kopjimi i kodit. Provo përsëri ose kopjoje manualisht.',
    allCopied: 'Të gjitha kodet u kopjuan në clipboard',
    copyAllFailed: 'Dështoi kopjimi i kodeve. Provo përsëri.',
    colCode: 'Kodi',
    colPlan: 'Plani',
    colStatus: 'Statusi',
    statusUsed: 'i përdorur',
    statusUnused: 'i papërdorur',
    colUsedBy: 'Përdorur nga',
    colUsedAt: 'Përdorur më',
    colCreatedAt: 'Krijuar më',
    allStatuses: 'Të gjitha statuset',
    statusFilterUnused: 'Të papërdorur',
    statusFilterUsed: 'Të përdorur',
    allPlans: 'Të gjitha planet',
    selectedCodes: 'Kode të zgjedhura: {{count}}',
    deleteSelected: 'Fshi të zgjedhurat',
    clearSelection: 'Pastro zgjedhjen',
    loadingCodes: 'Po ngarkohen kodet…',
    empty: 'Asnjë kod aktivizimi',
    prev: 'Mëparshme',
    next: 'Tjetra',
    pageOf: 'Faqja {{cur}} nga {{total}}',
    modalTitle: 'Gjenero kode aktivizimi',
    planLabel: 'Plani',
    selectPlan: 'Zgjidh një plan',
    quantityLabel: 'Numri i kodeve',
    quantityHelp: 'Mund të gjenerosh nga 1 deri në 100 kode njëherësh',
    generatedCount: 'U gjeneruan {{count}} kode',
    copyAll: 'Kopjo të gjitha',
  },
  mk: {
    title: 'Кодови за активација',
    subtitle: 'Управување со кодови за активација',
    generateBtn: 'Генерирај кодови',
    loadPlansFailed: 'Не може да се вчитаат плановите',
    loadCodesFailed: 'Не може да се вчитаат кодовите за активација',
    selectAtLeastOne: 'Изберете барем еден код',
    deleteTitle: 'Избриши кодови за активација',
    deleteConfirm: 'Дали сте сигурни дека сакате да избришете {{count}} кодови?',
    deleteBtn: 'Избриши',
    cancel: 'Откажи',
    close: 'Затвори',
    deleteError: 'Грешка при бришење на кодовите',
    generatedN: 'Генерирани се {{count}} кодови за активација',
    copyCode: 'Копирај код',
    copied: 'Копирано!',
    copyFailed: 'Не може да се копира кодот. Обидете се повторно или копирајте рачно.',
    allCopied: 'Сите кодови се копирани во клипбордот',
    copyAllFailed: 'Не може да се копираат кодовите. Обидете се повторно.',
    colCode: 'Код',
    colPlan: 'План',
    colStatus: 'Статус',
    statusUsed: 'искористен',
    statusUnused: 'неискористен',
    colUsedBy: 'Искористил',
    colUsedAt: 'Искористен на',
    colCreatedAt: 'Создаден на',
    allStatuses: 'Сите статуси',
    statusFilterUnused: 'Неискористени',
    statusFilterUsed: 'Искористени',
    allPlans: 'Сите планови',
    selectedCodes: 'Избрани кодови: {{count}}',
    deleteSelected: 'Избриши избрани',
    clearSelection: 'Исчисти избор',
    loadingCodes: 'Се вчитуваат кодови…',
    empty: 'Нема кодови за активација',
    prev: 'Претходна',
    next: 'Следна',
    pageOf: 'Страница {{cur}} од {{total}}',
    modalTitle: 'Генерирај кодови за активација',
    planLabel: 'План',
    selectPlan: 'Изберете план',
    quantityLabel: 'Број кодови',
    quantityHelp: 'Можете да генерирате од 1 до 100 кодови одеднаш',
    generatedCount: 'Генерирани се {{count}} кодови',
    copyAll: 'Копирај сè',
  },
  bs: {
    title: 'Aktivacijski kodovi',
    subtitle: 'Upravljanje aktivacijskim kodovima',
    generateBtn: 'Generiši kodove',
    loadPlansFailed: 'Nije moguće učitati planove',
    loadCodesFailed: 'Nije moguće učitati aktivacijske kodove',
    selectAtLeastOne: 'Odaberite barem jedan kod',
    deleteTitle: 'Obriši aktivacijske kodove',
    deleteConfirm: 'Jeste li sigurni da želite obrisati {{count}} kodova?',
    deleteBtn: 'Obriši',
    cancel: 'Otkaži',
    close: 'Zatvori',
    deleteError: 'Greška pri brisanju kodova',
    generatedN: 'Generisano {{count}} aktivacijskih kodova',
    copyCode: 'Kopiraj kod',
    copied: 'Kopirano!',
    copyFailed: 'Nije moguće kopirati kod. Pokušajte ponovo ili kopirajte ručno.',
    allCopied: 'Svi kodovi kopirani u međuspremnik',
    copyAllFailed: 'Nije moguće kopirati kodove. Pokušajte ponovo.',
    colCode: 'Kod',
    colPlan: 'Plan',
    colStatus: 'Status',
    statusUsed: 'iskorišten',
    statusUnused: 'neiskorišten',
    colUsedBy: 'Iskoristio',
    colUsedAt: 'Iskorišten dana',
    colCreatedAt: 'Kreiran dana',
    allStatuses: 'Svi statusi',
    statusFilterUnused: 'Neiskorišteni',
    statusFilterUsed: 'Iskorišteni',
    allPlans: 'Svi planovi',
    selectedCodes: 'Odabrani kodovi: {{count}}',
    deleteSelected: 'Obriši odabrane',
    clearSelection: 'Očisti odabir',
    loadingCodes: 'Učitavanje kodova…',
    empty: 'Nema aktivacijskih kodova',
    prev: 'Prethodna',
    next: 'Sljedeća',
    pageOf: 'Stranica {{cur}} od {{total}}',
    modalTitle: 'Generiši aktivacijske kodove',
    planLabel: 'Plan',
    selectPlan: 'Odaberite plan',
    quantityLabel: 'Broj kodova',
    quantityHelp: 'Možete generisati od 1 do 100 kodova odjednom',
    generatedCount: 'Generisano {{count}} kodova',
    copyAll: 'Kopiraj sve',
  },
};

const auditTr: Record<string, Dict> = {
  en: {
    title: 'Audit logs',
    subtitle: 'History of all actions performed by administrators',
    loadFailed: 'Failed to load audit logs',
    filtersTitle: 'Filters',
    exportBtn: 'Export',
    showFilters: 'Show filters',
    hideFilters: 'Hide filters',
    clearFilters: 'Clear filters',
    filterAction: 'Action',
    filterEntity: 'Entity type',
    filterDateFrom: 'Date from',
    filterDateTo: 'Date to',
    allOption: 'All',
    colDateTime: 'Date & time',
    colAdmin: 'Administrator',
    colAction: 'Action',
    colEntityType: 'Entity type',
    colEntityId: 'Entity ID',
    colChanges: 'Changes',
    colIp: 'IP',
    viewDetails: 'View details',
    pageOf: 'Page {{cur}} of {{total}}',
    prev: 'Previous',
    next: 'Next',
    emptyTitle: 'No audit logs',
    emptyBody: 'No logs matching the search criteria were found.',
    entityApiKey: 'API key',
    entityClient: 'Client',
    entityPlan: 'Plan',
    entityActivationCode: 'Activation code',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Login',
    actLOGOUT: 'Logout',
    actLOGIN_FAILED: 'Failed login',
    actEXTEND_KEY: 'Key extension',
    actREVOKE_KEY: 'Key revocation',
    actREACTIVATE_KEY: 'Key reactivation',
    actREGENERATE_KEY: 'Key regeneration',
    actCREATE_SANDBOX_KEY: 'Sandbox key creation',
    actCREATE_CLIENT: 'Client creation',
    actUPDATE_CLIENT: 'Client update',
    actDELETE_CLIENT: 'Client deletion',
    actCREATE_PLAN: 'Plan creation',
    actUPDATE_PLAN: 'Plan update',
    actDELETE_PLAN: 'Plan deletion',
    actCREATE_ACTIVATION_CODE: 'Activation code creation',
    actDELETE_ACTIVATION_CODE: 'Activation code deletion',
    actCREATE_ADMIN: 'Administrator creation',
    actUPDATE_ADMIN: 'Administrator update',
    actDELETE_ADMIN: 'Administrator deletion',
    actDEACTIVATE_ADMIN: 'Administrator deactivation',
    actACTIVATE_ADMIN: 'Administrator activation',
    actGRAVITEE_IMPORT: 'Gravitee import',
    actBULK_EXTEND_KEYS: 'Bulk key extension',
    actBULK_CHANGE_PLAN: 'Bulk plan change',
    actBULK_DEACTIVATE_KEYS: 'Bulk key deactivation',
  },
  pl: {
    title: 'Logi audytu',
    subtitle: 'Historia wszystkich akcji wykonanych przez administratorów',
    loadFailed: 'Nie udało się załadować logów audytu',
    filtersTitle: 'Filtry',
    exportBtn: 'Eksportuj',
    showFilters: 'Pokaż filtry',
    hideFilters: 'Ukryj filtry',
    clearFilters: 'Wyczyść filtry',
    filterAction: 'Akcja',
    filterEntity: 'Typ encji',
    filterDateFrom: 'Data od',
    filterDateTo: 'Data do',
    allOption: 'Wszystkie',
    colDateTime: 'Data i czas',
    colAdmin: 'Administrator',
    colAction: 'Akcja',
    colEntityType: 'Typ encji',
    colEntityId: 'ID encji',
    colChanges: 'Zmiany',
    colIp: 'IP',
    viewDetails: 'Zobacz szczegóły',
    pageOf: 'Strona {{cur}} z {{total}}',
    prev: 'Poprzednia',
    next: 'Następna',
    emptyTitle: 'Brak logów audytu',
    emptyBody: 'Nie znaleziono żadnych logów spełniających kryteria wyszukiwania.',
    entityApiKey: 'Klucz API',
    entityClient: 'Klient',
    entityPlan: 'Plan',
    entityActivationCode: 'Kod aktywacyjny',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Logowanie',
    actLOGOUT: 'Wylogowanie',
    actLOGIN_FAILED: 'Nieudane logowanie',
    actEXTEND_KEY: 'Przedłużenie klucza',
    actREVOKE_KEY: 'Odwołanie klucza',
    actREACTIVATE_KEY: 'Reaktywacja klucza',
    actREGENERATE_KEY: 'Regeneracja klucza',
    actCREATE_SANDBOX_KEY: 'Utworzenie klucza sandbox',
    actCREATE_CLIENT: 'Utworzenie klienta',
    actUPDATE_CLIENT: 'Aktualizacja klienta',
    actDELETE_CLIENT: 'Usunięcie klienta',
    actCREATE_PLAN: 'Utworzenie planu',
    actUPDATE_PLAN: 'Aktualizacja planu',
    actDELETE_PLAN: 'Usunięcie planu',
    actCREATE_ACTIVATION_CODE: 'Utworzenie kodu aktywacyjnego',
    actDELETE_ACTIVATION_CODE: 'Usunięcie kodu aktywacyjnego',
    actCREATE_ADMIN: 'Utworzenie administratora',
    actUPDATE_ADMIN: 'Aktualizacja administratora',
    actDELETE_ADMIN: 'Usunięcie administratora',
    actDEACTIVATE_ADMIN: 'Deaktywacja administratora',
    actACTIVATE_ADMIN: 'Aktywacja administratora',
    actGRAVITEE_IMPORT: 'Import Gravitee',
    actBULK_EXTEND_KEYS: 'Zbiorcze przedłużenie kluczy',
    actBULK_CHANGE_PLAN: 'Zbiorcza zmiana planu',
    actBULK_DEACTIVATE_KEYS: 'Zbiorcza deaktywacja kluczy',
  },
  de: {
    title: 'Audit-Protokolle',
    subtitle: 'Verlauf aller von Administratoren durchgeführten Aktionen',
    loadFailed: 'Audit-Protokolle konnten nicht geladen werden',
    filtersTitle: 'Filter',
    exportBtn: 'Exportieren',
    showFilters: 'Filter anzeigen',
    hideFilters: 'Filter ausblenden',
    clearFilters: 'Filter zurücksetzen',
    filterAction: 'Aktion',
    filterEntity: 'Entitätstyp',
    filterDateFrom: 'Datum von',
    filterDateTo: 'Datum bis',
    allOption: 'Alle',
    colDateTime: 'Datum & Zeit',
    colAdmin: 'Administrator',
    colAction: 'Aktion',
    colEntityType: 'Entitätstyp',
    colEntityId: 'Entitäts-ID',
    colChanges: 'Änderungen',
    colIp: 'IP',
    viewDetails: 'Details anzeigen',
    pageOf: 'Seite {{cur}} von {{total}}',
    prev: 'Zurück',
    next: 'Weiter',
    emptyTitle: 'Keine Audit-Protokolle',
    emptyBody: 'Es wurden keine Protokolle gefunden, die den Suchkriterien entsprechen.',
    entityApiKey: 'API-Schlüssel',
    entityClient: 'Kunde',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktivierungscode',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Anmeldung',
    actLOGOUT: 'Abmeldung',
    actLOGIN_FAILED: 'Fehlgeschlagene Anmeldung',
    actEXTEND_KEY: 'Schlüsselverlängerung',
    actREVOKE_KEY: 'Schlüsselwiderruf',
    actREACTIVATE_KEY: 'Schlüsselreaktivierung',
    actREGENERATE_KEY: 'Schlüsselneugenerierung',
    actCREATE_SANDBOX_KEY: 'Sandbox-Schlüssel erstellt',
    actCREATE_CLIENT: 'Kunde erstellt',
    actUPDATE_CLIENT: 'Kunde aktualisiert',
    actDELETE_CLIENT: 'Kunde gelöscht',
    actCREATE_PLAN: 'Plan erstellt',
    actUPDATE_PLAN: 'Plan aktualisiert',
    actDELETE_PLAN: 'Plan gelöscht',
    actCREATE_ACTIVATION_CODE: 'Aktivierungscode erstellt',
    actDELETE_ACTIVATION_CODE: 'Aktivierungscode gelöscht',
    actCREATE_ADMIN: 'Administrator erstellt',
    actUPDATE_ADMIN: 'Administrator aktualisiert',
    actDELETE_ADMIN: 'Administrator gelöscht',
    actDEACTIVATE_ADMIN: 'Administrator deaktiviert',
    actACTIVATE_ADMIN: 'Administrator aktiviert',
    actGRAVITEE_IMPORT: 'Gravitee-Import',
    actBULK_EXTEND_KEYS: 'Massen-Schlüsselverlängerung',
    actBULK_CHANGE_PLAN: 'Massen-Planänderung',
    actBULK_DEACTIVATE_KEYS: 'Massen-Schlüsseldeaktivierung',
  },
  fr: {
    title: "Journaux d'audit",
    subtitle: 'Historique de toutes les actions effectuées par les administrateurs',
    loadFailed: "Échec du chargement des journaux d'audit",
    filtersTitle: 'Filtres',
    exportBtn: 'Exporter',
    showFilters: 'Afficher les filtres',
    hideFilters: 'Masquer les filtres',
    clearFilters: 'Effacer les filtres',
    filterAction: 'Action',
    filterEntity: "Type d'entité",
    filterDateFrom: 'Date de début',
    filterDateTo: 'Date de fin',
    allOption: 'Toutes',
    colDateTime: 'Date et heure',
    colAdmin: 'Administrateur',
    colAction: 'Action',
    colEntityType: "Type d'entité",
    colEntityId: "ID d'entité",
    colChanges: 'Modifications',
    colIp: 'IP',
    viewDetails: 'Voir les détails',
    pageOf: 'Page {{cur}} sur {{total}}',
    prev: 'Précédent',
    next: 'Suivant',
    emptyTitle: "Aucun journal d'audit",
    emptyBody: "Aucun journal correspondant aux critères de recherche n'a été trouvé.",
    entityApiKey: 'Clé API',
    entityClient: 'Client',
    entityPlan: 'Plan',
    entityActivationCode: "Code d'activation",
    entityAdmin: 'Administrateur',
    entityImport: 'Import',
    actLOGIN: 'Connexion',
    actLOGOUT: 'Déconnexion',
    actLOGIN_FAILED: 'Échec de connexion',
    actEXTEND_KEY: 'Prolongation de clé',
    actREVOKE_KEY: 'Révocation de clé',
    actREACTIVATE_KEY: 'Réactivation de clé',
    actREGENERATE_KEY: 'Régénération de clé',
    actCREATE_SANDBOX_KEY: 'Création de clé sandbox',
    actCREATE_CLIENT: 'Création de client',
    actUPDATE_CLIENT: 'Mise à jour de client',
    actDELETE_CLIENT: 'Suppression de client',
    actCREATE_PLAN: 'Création de plan',
    actUPDATE_PLAN: 'Mise à jour de plan',
    actDELETE_PLAN: 'Suppression de plan',
    actCREATE_ACTIVATION_CODE: "Création de code d'activation",
    actDELETE_ACTIVATION_CODE: "Suppression de code d'activation",
    actCREATE_ADMIN: "Création d'administrateur",
    actUPDATE_ADMIN: "Mise à jour d'administrateur",
    actDELETE_ADMIN: "Suppression d'administrateur",
    actDEACTIVATE_ADMIN: "Désactivation d'administrateur",
    actACTIVATE_ADMIN: "Activation d'administrateur",
    actGRAVITEE_IMPORT: 'Import Gravitee',
    actBULK_EXTEND_KEYS: 'Prolongation groupée de clés',
    actBULK_CHANGE_PLAN: 'Changement groupé de plan',
    actBULK_DEACTIVATE_KEYS: 'Désactivation groupée de clés',
  },
  es: {
    title: 'Registros de auditoría',
    subtitle: 'Historial de todas las acciones realizadas por los administradores',
    loadFailed: 'No se pudieron cargar los registros de auditoría',
    filtersTitle: 'Filtros',
    exportBtn: 'Exportar',
    showFilters: 'Mostrar filtros',
    hideFilters: 'Ocultar filtros',
    clearFilters: 'Limpiar filtros',
    filterAction: 'Acción',
    filterEntity: 'Tipo de entidad',
    filterDateFrom: 'Fecha desde',
    filterDateTo: 'Fecha hasta',
    allOption: 'Todas',
    colDateTime: 'Fecha y hora',
    colAdmin: 'Administrador',
    colAction: 'Acción',
    colEntityType: 'Tipo de entidad',
    colEntityId: 'ID de entidad',
    colChanges: 'Cambios',
    colIp: 'IP',
    viewDetails: 'Ver detalles',
    pageOf: 'Página {{cur}} de {{total}}',
    prev: 'Anterior',
    next: 'Siguiente',
    emptyTitle: 'Sin registros de auditoría',
    emptyBody: 'No se encontraron registros que coincidan con los criterios de búsqueda.',
    entityApiKey: 'Clave API',
    entityClient: 'Cliente',
    entityPlan: 'Plan',
    entityActivationCode: 'Código de activación',
    entityAdmin: 'Administrador',
    entityImport: 'Importación',
    actLOGIN: 'Inicio de sesión',
    actLOGOUT: 'Cierre de sesión',
    actLOGIN_FAILED: 'Inicio de sesión fallido',
    actEXTEND_KEY: 'Extensión de clave',
    actREVOKE_KEY: 'Revocación de clave',
    actREACTIVATE_KEY: 'Reactivación de clave',
    actREGENERATE_KEY: 'Regeneración de clave',
    actCREATE_SANDBOX_KEY: 'Creación de clave sandbox',
    actCREATE_CLIENT: 'Creación de cliente',
    actUPDATE_CLIENT: 'Actualización de cliente',
    actDELETE_CLIENT: 'Eliminación de cliente',
    actCREATE_PLAN: 'Creación de plan',
    actUPDATE_PLAN: 'Actualización de plan',
    actDELETE_PLAN: 'Eliminación de plan',
    actCREATE_ACTIVATION_CODE: 'Creación de código de activación',
    actDELETE_ACTIVATION_CODE: 'Eliminación de código de activación',
    actCREATE_ADMIN: 'Creación de administrador',
    actUPDATE_ADMIN: 'Actualización de administrador',
    actDELETE_ADMIN: 'Eliminación de administrador',
    actDEACTIVATE_ADMIN: 'Desactivación de administrador',
    actACTIVATE_ADMIN: 'Activación de administrador',
    actGRAVITEE_IMPORT: 'Importación Gravitee',
    actBULK_EXTEND_KEYS: 'Extensión masiva de claves',
    actBULK_CHANGE_PLAN: 'Cambio masivo de plan',
    actBULK_DEACTIVATE_KEYS: 'Desactivación masiva de claves',
  },
  it: {
    title: 'Registri di audit',
    subtitle: 'Cronologia di tutte le azioni eseguite dagli amministratori',
    loadFailed: 'Impossibile caricare i registri di audit',
    filtersTitle: 'Filtri',
    exportBtn: 'Esporta',
    showFilters: 'Mostra filtri',
    hideFilters: 'Nascondi filtri',
    clearFilters: 'Cancella filtri',
    filterAction: 'Azione',
    filterEntity: 'Tipo di entità',
    filterDateFrom: 'Data da',
    filterDateTo: 'Data a',
    allOption: 'Tutte',
    colDateTime: 'Data e ora',
    colAdmin: 'Amministratore',
    colAction: 'Azione',
    colEntityType: 'Tipo di entità',
    colEntityId: 'ID entità',
    colChanges: 'Modifiche',
    colIp: 'IP',
    viewDetails: 'Vedi dettagli',
    pageOf: 'Pagina {{cur}} di {{total}}',
    prev: 'Precedente',
    next: 'Successiva',
    emptyTitle: 'Nessun registro di audit',
    emptyBody: 'Non è stato trovato alcun registro corrispondente ai criteri di ricerca.',
    entityApiKey: 'Chiave API',
    entityClient: 'Cliente',
    entityPlan: 'Piano',
    entityActivationCode: 'Codice di attivazione',
    entityAdmin: 'Amministratore',
    entityImport: 'Importazione',
    actLOGIN: 'Accesso',
    actLOGOUT: 'Disconnessione',
    actLOGIN_FAILED: 'Accesso fallito',
    actEXTEND_KEY: 'Estensione chiave',
    actREVOKE_KEY: 'Revoca chiave',
    actREACTIVATE_KEY: 'Riattivazione chiave',
    actREGENERATE_KEY: 'Rigenerazione chiave',
    actCREATE_SANDBOX_KEY: 'Creazione chiave sandbox',
    actCREATE_CLIENT: 'Creazione cliente',
    actUPDATE_CLIENT: 'Aggiornamento cliente',
    actDELETE_CLIENT: 'Eliminazione cliente',
    actCREATE_PLAN: 'Creazione piano',
    actUPDATE_PLAN: 'Aggiornamento piano',
    actDELETE_PLAN: 'Eliminazione piano',
    actCREATE_ACTIVATION_CODE: 'Creazione codice di attivazione',
    actDELETE_ACTIVATION_CODE: 'Eliminazione codice di attivazione',
    actCREATE_ADMIN: 'Creazione amministratore',
    actUPDATE_ADMIN: 'Aggiornamento amministratore',
    actDELETE_ADMIN: 'Eliminazione amministratore',
    actDEACTIVATE_ADMIN: 'Disattivazione amministratore',
    actACTIVATE_ADMIN: 'Attivazione amministratore',
    actGRAVITEE_IMPORT: 'Importazione Gravitee',
    actBULK_EXTEND_KEYS: 'Estensione massiva chiavi',
    actBULK_CHANGE_PLAN: 'Cambio massivo piano',
    actBULK_DEACTIVATE_KEYS: 'Disattivazione massiva chiavi',
  },
  pt: {
    title: 'Registos de auditoria',
    subtitle: 'Histórico de todas as ações executadas pelos administradores',
    loadFailed: 'Não foi possível carregar os registos de auditoria',
    filtersTitle: 'Filtros',
    exportBtn: 'Exportar',
    showFilters: 'Mostrar filtros',
    hideFilters: 'Ocultar filtros',
    clearFilters: 'Limpar filtros',
    filterAction: 'Ação',
    filterEntity: 'Tipo de entidade',
    filterDateFrom: 'Data de',
    filterDateTo: 'Data até',
    allOption: 'Todas',
    colDateTime: 'Data e hora',
    colAdmin: 'Administrador',
    colAction: 'Ação',
    colEntityType: 'Tipo de entidade',
    colEntityId: 'ID da entidade',
    colChanges: 'Alterações',
    colIp: 'IP',
    viewDetails: 'Ver detalhes',
    pageOf: 'Página {{cur}} de {{total}}',
    prev: 'Anterior',
    next: 'Seguinte',
    emptyTitle: 'Sem registos de auditoria',
    emptyBody: 'Não foram encontrados registos que correspondam aos critérios de pesquisa.',
    entityApiKey: 'Chave API',
    entityClient: 'Cliente',
    entityPlan: 'Plano',
    entityActivationCode: 'Código de ativação',
    entityAdmin: 'Administrador',
    entityImport: 'Importação',
    actLOGIN: 'Início de sessão',
    actLOGOUT: 'Fim de sessão',
    actLOGIN_FAILED: 'Início de sessão falhado',
    actEXTEND_KEY: 'Extensão de chave',
    actREVOKE_KEY: 'Revogação de chave',
    actREACTIVATE_KEY: 'Reativação de chave',
    actREGENERATE_KEY: 'Regeneração de chave',
    actCREATE_SANDBOX_KEY: 'Criação de chave sandbox',
    actCREATE_CLIENT: 'Criação de cliente',
    actUPDATE_CLIENT: 'Atualização de cliente',
    actDELETE_CLIENT: 'Eliminação de cliente',
    actCREATE_PLAN: 'Criação de plano',
    actUPDATE_PLAN: 'Atualização de plano',
    actDELETE_PLAN: 'Eliminação de plano',
    actCREATE_ACTIVATION_CODE: 'Criação de código de ativação',
    actDELETE_ACTIVATION_CODE: 'Eliminação de código de ativação',
    actCREATE_ADMIN: 'Criação de administrador',
    actUPDATE_ADMIN: 'Atualização de administrador',
    actDELETE_ADMIN: 'Eliminação de administrador',
    actDEACTIVATE_ADMIN: 'Desativação de administrador',
    actACTIVATE_ADMIN: 'Ativação de administrador',
    actGRAVITEE_IMPORT: 'Importação Gravitee',
    actBULK_EXTEND_KEYS: 'Extensão em massa de chaves',
    actBULK_CHANGE_PLAN: 'Alteração em massa de plano',
    actBULK_DEACTIVATE_KEYS: 'Desativação em massa de chaves',
  },
  nl: {
    title: 'Auditlogboeken',
    subtitle: 'Geschiedenis van alle door beheerders uitgevoerde acties',
    loadFailed: 'Kan auditlogboeken niet laden',
    filtersTitle: 'Filters',
    exportBtn: 'Exporteren',
    showFilters: 'Filters tonen',
    hideFilters: 'Filters verbergen',
    clearFilters: 'Filters wissen',
    filterAction: 'Actie',
    filterEntity: 'Entiteitstype',
    filterDateFrom: 'Datum vanaf',
    filterDateTo: 'Datum tot',
    allOption: 'Alle',
    colDateTime: 'Datum en tijd',
    colAdmin: 'Beheerder',
    colAction: 'Actie',
    colEntityType: 'Entiteitstype',
    colEntityId: 'Entiteit-ID',
    colChanges: 'Wijzigingen',
    colIp: 'IP',
    viewDetails: 'Details bekijken',
    pageOf: 'Pagina {{cur}} van {{total}}',
    prev: 'Vorige',
    next: 'Volgende',
    emptyTitle: 'Geen auditlogboeken',
    emptyBody: 'Er zijn geen logboeken gevonden die aan de zoekcriteria voldoen.',
    entityApiKey: 'API-sleutel',
    entityClient: 'Klant',
    entityPlan: 'Plan',
    entityActivationCode: 'Activeringscode',
    entityAdmin: 'Beheerder',
    entityImport: 'Import',
    actLOGIN: 'Inloggen',
    actLOGOUT: 'Uitloggen',
    actLOGIN_FAILED: 'Mislukte inlogpoging',
    actEXTEND_KEY: 'Sleutelverlenging',
    actREVOKE_KEY: 'Sleutelintrekking',
    actREACTIVATE_KEY: 'Sleutelreactivering',
    actREGENERATE_KEY: 'Sleutelhergeneratie',
    actCREATE_SANDBOX_KEY: 'Sandbox-sleutel aangemaakt',
    actCREATE_CLIENT: 'Klant aangemaakt',
    actUPDATE_CLIENT: 'Klant bijgewerkt',
    actDELETE_CLIENT: 'Klant verwijderd',
    actCREATE_PLAN: 'Plan aangemaakt',
    actUPDATE_PLAN: 'Plan bijgewerkt',
    actDELETE_PLAN: 'Plan verwijderd',
    actCREATE_ACTIVATION_CODE: 'Activeringscode aangemaakt',
    actDELETE_ACTIVATION_CODE: 'Activeringscode verwijderd',
    actCREATE_ADMIN: 'Beheerder aangemaakt',
    actUPDATE_ADMIN: 'Beheerder bijgewerkt',
    actDELETE_ADMIN: 'Beheerder verwijderd',
    actDEACTIVATE_ADMIN: 'Beheerder gedeactiveerd',
    actACTIVATE_ADMIN: 'Beheerder geactiveerd',
    actGRAVITEE_IMPORT: 'Gravitee-import',
    actBULK_EXTEND_KEYS: 'Bulk-sleutelverlenging',
    actBULK_CHANGE_PLAN: 'Bulk-planwijziging',
    actBULK_DEACTIVATE_KEYS: 'Bulk-sleuteldeactivering',
  },
  da: {
    title: 'Revisionslogfiler',
    subtitle: 'Historik over alle handlinger udført af administratorer',
    loadFailed: 'Kunne ikke indlæse revisionslogfiler',
    filtersTitle: 'Filtre',
    exportBtn: 'Eksportér',
    showFilters: 'Vis filtre',
    hideFilters: 'Skjul filtre',
    clearFilters: 'Ryd filtre',
    filterAction: 'Handling',
    filterEntity: 'Entitetstype',
    filterDateFrom: 'Dato fra',
    filterDateTo: 'Dato til',
    allOption: 'Alle',
    colDateTime: 'Dato og tid',
    colAdmin: 'Administrator',
    colAction: 'Handling',
    colEntityType: 'Entitetstype',
    colEntityId: 'Entitets-ID',
    colChanges: 'Ændringer',
    colIp: 'IP',
    viewDetails: 'Se detaljer',
    pageOf: 'Side {{cur}} af {{total}}',
    prev: 'Forrige',
    next: 'Næste',
    emptyTitle: 'Ingen revisionslogfiler',
    emptyBody: 'Der blev ikke fundet nogen logfiler, der matcher søgekriterierne.',
    entityApiKey: 'API-nøgle',
    entityClient: 'Kunde',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktiveringskode',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Login',
    actLOGOUT: 'Logout',
    actLOGIN_FAILED: 'Mislykket login',
    actEXTEND_KEY: 'Nøgleforlængelse',
    actREVOKE_KEY: 'Nøgletilbagekaldelse',
    actREACTIVATE_KEY: 'Nøglereaktivering',
    actREGENERATE_KEY: 'Nøglegenerering',
    actCREATE_SANDBOX_KEY: 'Sandbox-nøgle oprettet',
    actCREATE_CLIENT: 'Kunde oprettet',
    actUPDATE_CLIENT: 'Kunde opdateret',
    actDELETE_CLIENT: 'Kunde slettet',
    actCREATE_PLAN: 'Plan oprettet',
    actUPDATE_PLAN: 'Plan opdateret',
    actDELETE_PLAN: 'Plan slettet',
    actCREATE_ACTIVATION_CODE: 'Aktiveringskode oprettet',
    actDELETE_ACTIVATION_CODE: 'Aktiveringskode slettet',
    actCREATE_ADMIN: 'Administrator oprettet',
    actUPDATE_ADMIN: 'Administrator opdateret',
    actDELETE_ADMIN: 'Administrator slettet',
    actDEACTIVATE_ADMIN: 'Administrator deaktiveret',
    actACTIVATE_ADMIN: 'Administrator aktiveret',
    actGRAVITEE_IMPORT: 'Gravitee-import',
    actBULK_EXTEND_KEYS: 'Masseforlængelse af nøgler',
    actBULK_CHANGE_PLAN: 'Masseændring af plan',
    actBULK_DEACTIVATE_KEYS: 'Massedeaktivering af nøgler',
  },
  sv: {
    title: 'Granskningsloggar',
    subtitle: 'Historik över alla åtgärder utförda av administratörer',
    loadFailed: 'Det gick inte att läsa in granskningsloggarna',
    filtersTitle: 'Filter',
    exportBtn: 'Exportera',
    showFilters: 'Visa filter',
    hideFilters: 'Dölj filter',
    clearFilters: 'Rensa filter',
    filterAction: 'Åtgärd',
    filterEntity: 'Entitetstyp',
    filterDateFrom: 'Datum från',
    filterDateTo: 'Datum till',
    allOption: 'Alla',
    colDateTime: 'Datum och tid',
    colAdmin: 'Administratör',
    colAction: 'Åtgärd',
    colEntityType: 'Entitetstyp',
    colEntityId: 'Entitets-ID',
    colChanges: 'Ändringar',
    colIp: 'IP',
    viewDetails: 'Visa detaljer',
    pageOf: 'Sida {{cur}} av {{total}}',
    prev: 'Föregående',
    next: 'Nästa',
    emptyTitle: 'Inga granskningsloggar',
    emptyBody: 'Inga loggar som matchar sökkriterierna hittades.',
    entityApiKey: 'API-nyckel',
    entityClient: 'Kund',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktiveringskod',
    entityAdmin: 'Administratör',
    entityImport: 'Import',
    actLOGIN: 'Inloggning',
    actLOGOUT: 'Utloggning',
    actLOGIN_FAILED: 'Misslyckad inloggning',
    actEXTEND_KEY: 'Nyckelförlängning',
    actREVOKE_KEY: 'Nyckelåterkallelse',
    actREACTIVATE_KEY: 'Nyckelåteraktivering',
    actREGENERATE_KEY: 'Nyckelregenerering',
    actCREATE_SANDBOX_KEY: 'Sandbox-nyckel skapad',
    actCREATE_CLIENT: 'Kund skapad',
    actUPDATE_CLIENT: 'Kund uppdaterad',
    actDELETE_CLIENT: 'Kund borttagen',
    actCREATE_PLAN: 'Plan skapad',
    actUPDATE_PLAN: 'Plan uppdaterad',
    actDELETE_PLAN: 'Plan borttagen',
    actCREATE_ACTIVATION_CODE: 'Aktiveringskod skapad',
    actDELETE_ACTIVATION_CODE: 'Aktiveringskod borttagen',
    actCREATE_ADMIN: 'Administratör skapad',
    actUPDATE_ADMIN: 'Administratör uppdaterad',
    actDELETE_ADMIN: 'Administratör borttagen',
    actDEACTIVATE_ADMIN: 'Administratör inaktiverad',
    actACTIVATE_ADMIN: 'Administratör aktiverad',
    actGRAVITEE_IMPORT: 'Gravitee-import',
    actBULK_EXTEND_KEYS: 'Massförlängning av nycklar',
    actBULK_CHANGE_PLAN: 'Massändring av plan',
    actBULK_DEACTIVATE_KEYS: 'Massinaktivering av nycklar',
  },
  fi: {
    title: 'Tarkastuslokit',
    subtitle: 'Kaikkien ylläpitäjien suorittamien toimien historia',
    loadFailed: 'Tarkastuslokien lataaminen epäonnistui',
    filtersTitle: 'Suodattimet',
    exportBtn: 'Vie',
    showFilters: 'Näytä suodattimet',
    hideFilters: 'Piilota suodattimet',
    clearFilters: 'Tyhjennä suodattimet',
    filterAction: 'Toiminto',
    filterEntity: 'Entiteettityyppi',
    filterDateFrom: 'Päivämäärä alkaen',
    filterDateTo: 'Päivämäärä saakka',
    allOption: 'Kaikki',
    colDateTime: 'Päivä ja aika',
    colAdmin: 'Ylläpitäjä',
    colAction: 'Toiminto',
    colEntityType: 'Entiteettityyppi',
    colEntityId: 'Entiteetin tunnus',
    colChanges: 'Muutokset',
    colIp: 'IP',
    viewDetails: 'Näytä tiedot',
    pageOf: 'Sivu {{cur}} / {{total}}',
    prev: 'Edellinen',
    next: 'Seuraava',
    emptyTitle: 'Ei tarkastuslokeja',
    emptyBody: 'Hakuehtoja vastaavia lokeja ei löytynyt.',
    entityApiKey: 'API-avain',
    entityClient: 'Asiakas',
    entityPlan: 'Suunnitelma',
    entityActivationCode: 'Aktivointikoodi',
    entityAdmin: 'Ylläpitäjä',
    entityImport: 'Tuonti',
    actLOGIN: 'Kirjautuminen',
    actLOGOUT: 'Uloskirjautuminen',
    actLOGIN_FAILED: 'Epäonnistunut kirjautuminen',
    actEXTEND_KEY: 'Avaimen jatkaminen',
    actREVOKE_KEY: 'Avaimen peruutus',
    actREACTIVATE_KEY: 'Avaimen uudelleenaktivointi',
    actREGENERATE_KEY: 'Avaimen uudelleenluonti',
    actCREATE_SANDBOX_KEY: 'Hiekkalaatikkoavain luotu',
    actCREATE_CLIENT: 'Asiakas luotu',
    actUPDATE_CLIENT: 'Asiakas päivitetty',
    actDELETE_CLIENT: 'Asiakas poistettu',
    actCREATE_PLAN: 'Suunnitelma luotu',
    actUPDATE_PLAN: 'Suunnitelma päivitetty',
    actDELETE_PLAN: 'Suunnitelma poistettu',
    actCREATE_ACTIVATION_CODE: 'Aktivointikoodi luotu',
    actDELETE_ACTIVATION_CODE: 'Aktivointikoodi poistettu',
    actCREATE_ADMIN: 'Ylläpitäjä luotu',
    actUPDATE_ADMIN: 'Ylläpitäjä päivitetty',
    actDELETE_ADMIN: 'Ylläpitäjä poistettu',
    actDEACTIVATE_ADMIN: 'Ylläpitäjä deaktivoitu',
    actACTIVATE_ADMIN: 'Ylläpitäjä aktivoitu',
    actGRAVITEE_IMPORT: 'Gravitee-tuonti',
    actBULK_EXTEND_KEYS: 'Avainten joukkojatko',
    actBULK_CHANGE_PLAN: 'Suunnitelman joukkomuutos',
    actBULK_DEACTIVATE_KEYS: 'Avainten joukkodeaktivointi',
  },
  cs: {
    title: 'Auditní logy',
    subtitle: 'Historie všech akcí provedených administrátory',
    loadFailed: 'Nepodařilo se načíst auditní logy',
    filtersTitle: 'Filtry',
    exportBtn: 'Exportovat',
    showFilters: 'Zobrazit filtry',
    hideFilters: 'Skrýt filtry',
    clearFilters: 'Vymazat filtry',
    filterAction: 'Akce',
    filterEntity: 'Typ entity',
    filterDateFrom: 'Datum od',
    filterDateTo: 'Datum do',
    allOption: 'Vše',
    colDateTime: 'Datum a čas',
    colAdmin: 'Administrátor',
    colAction: 'Akce',
    colEntityType: 'Typ entity',
    colEntityId: 'ID entity',
    colChanges: 'Změny',
    colIp: 'IP',
    viewDetails: 'Zobrazit detaily',
    pageOf: 'Stránka {{cur}} z {{total}}',
    prev: 'Předchozí',
    next: 'Další',
    emptyTitle: 'Žádné auditní logy',
    emptyBody: 'Nebyly nalezeny žádné logy odpovídající kritériím vyhledávání.',
    entityApiKey: 'API klíč',
    entityClient: 'Klient',
    entityPlan: 'Plán',
    entityActivationCode: 'Aktivační kód',
    entityAdmin: 'Administrátor',
    entityImport: 'Import',
    actLOGIN: 'Přihlášení',
    actLOGOUT: 'Odhlášení',
    actLOGIN_FAILED: 'Neúspěšné přihlášení',
    actEXTEND_KEY: 'Prodloužení klíče',
    actREVOKE_KEY: 'Odvolání klíče',
    actREACTIVATE_KEY: 'Reaktivace klíče',
    actREGENERATE_KEY: 'Regenerace klíče',
    actCREATE_SANDBOX_KEY: 'Vytvoření sandbox klíče',
    actCREATE_CLIENT: 'Vytvoření klienta',
    actUPDATE_CLIENT: 'Aktualizace klienta',
    actDELETE_CLIENT: 'Smazání klienta',
    actCREATE_PLAN: 'Vytvoření plánu',
    actUPDATE_PLAN: 'Aktualizace plánu',
    actDELETE_PLAN: 'Smazání plánu',
    actCREATE_ACTIVATION_CODE: 'Vytvoření aktivačního kódu',
    actDELETE_ACTIVATION_CODE: 'Smazání aktivačního kódu',
    actCREATE_ADMIN: 'Vytvoření administrátora',
    actUPDATE_ADMIN: 'Aktualizace administrátora',
    actDELETE_ADMIN: 'Smazání administrátora',
    actDEACTIVATE_ADMIN: 'Deaktivace administrátora',
    actACTIVATE_ADMIN: 'Aktivace administrátora',
    actGRAVITEE_IMPORT: 'Import Gravitee',
    actBULK_EXTEND_KEYS: 'Hromadné prodloužení klíčů',
    actBULK_CHANGE_PLAN: 'Hromadná změna plánu',
    actBULK_DEACTIVATE_KEYS: 'Hromadná deaktivace klíčů',
  },
  sk: {
    title: 'Auditné logy',
    subtitle: 'História všetkých akcií vykonaných administrátormi',
    loadFailed: 'Nepodarilo sa načítať auditné logy',
    filtersTitle: 'Filtre',
    exportBtn: 'Exportovať',
    showFilters: 'Zobraziť filtre',
    hideFilters: 'Skryť filtre',
    clearFilters: 'Vymazať filtre',
    filterAction: 'Akcia',
    filterEntity: 'Typ entity',
    filterDateFrom: 'Dátum od',
    filterDateTo: 'Dátum do',
    allOption: 'Všetko',
    colDateTime: 'Dátum a čas',
    colAdmin: 'Administrátor',
    colAction: 'Akcia',
    colEntityType: 'Typ entity',
    colEntityId: 'ID entity',
    colChanges: 'Zmeny',
    colIp: 'IP',
    viewDetails: 'Zobraziť detaily',
    pageOf: 'Stránka {{cur}} z {{total}}',
    prev: 'Predchádzajúca',
    next: 'Ďalšia',
    emptyTitle: 'Žiadne auditné logy',
    emptyBody: 'Nenašli sa žiadne logy zodpovedajúce kritériám vyhľadávania.',
    entityApiKey: 'API kľúč',
    entityClient: 'Klient',
    entityPlan: 'Plán',
    entityActivationCode: 'Aktivačný kód',
    entityAdmin: 'Administrátor',
    entityImport: 'Import',
    actLOGIN: 'Prihlásenie',
    actLOGOUT: 'Odhlásenie',
    actLOGIN_FAILED: 'Neúspešné prihlásenie',
    actEXTEND_KEY: 'Predĺženie kľúča',
    actREVOKE_KEY: 'Odvolanie kľúča',
    actREACTIVATE_KEY: 'Reaktivácia kľúča',
    actREGENERATE_KEY: 'Regenerácia kľúča',
    actCREATE_SANDBOX_KEY: 'Vytvorenie sandbox kľúča',
    actCREATE_CLIENT: 'Vytvorenie klienta',
    actUPDATE_CLIENT: 'Aktualizácia klienta',
    actDELETE_CLIENT: 'Zmazanie klienta',
    actCREATE_PLAN: 'Vytvorenie plánu',
    actUPDATE_PLAN: 'Aktualizácia plánu',
    actDELETE_PLAN: 'Zmazanie plánu',
    actCREATE_ACTIVATION_CODE: 'Vytvorenie aktivačného kódu',
    actDELETE_ACTIVATION_CODE: 'Zmazanie aktivačného kódu',
    actCREATE_ADMIN: 'Vytvorenie administrátora',
    actUPDATE_ADMIN: 'Aktualizácia administrátora',
    actDELETE_ADMIN: 'Zmazanie administrátora',
    actDEACTIVATE_ADMIN: 'Deaktivácia administrátora',
    actACTIVATE_ADMIN: 'Aktivácia administrátora',
    actGRAVITEE_IMPORT: 'Import Gravitee',
    actBULK_EXTEND_KEYS: 'Hromadné predĺženie kľúčov',
    actBULK_CHANGE_PLAN: 'Hromadná zmena plánu',
    actBULK_DEACTIVATE_KEYS: 'Hromadná deaktivácia kľúčov',
  },
  sl: {
    title: 'Revizijski dnevniki',
    subtitle: 'Zgodovina vseh dejanj, ki so jih izvedli skrbniki',
    loadFailed: 'Revizijskih dnevnikov ni bilo mogoče naložiti',
    filtersTitle: 'Filtri',
    exportBtn: 'Izvozi',
    showFilters: 'Pokaži filtre',
    hideFilters: 'Skrij filtre',
    clearFilters: 'Počisti filtre',
    filterAction: 'Dejanje',
    filterEntity: 'Vrsta entitete',
    filterDateFrom: 'Datum od',
    filterDateTo: 'Datum do',
    allOption: 'Vse',
    colDateTime: 'Datum in čas',
    colAdmin: 'Skrbnik',
    colAction: 'Dejanje',
    colEntityType: 'Vrsta entitete',
    colEntityId: 'ID entitete',
    colChanges: 'Spremembe',
    colIp: 'IP',
    viewDetails: 'Prikaži podrobnosti',
    pageOf: 'Stran {{cur}} od {{total}}',
    prev: 'Prejšnja',
    next: 'Naslednja',
    emptyTitle: 'Ni revizijskih dnevnikov',
    emptyBody: 'Ni bilo najdenih dnevnikov, ki bi ustrezali iskalnim merilom.',
    entityApiKey: 'Ključ API',
    entityClient: 'Stranka',
    entityPlan: 'Načrt',
    entityActivationCode: 'Aktivacijska koda',
    entityAdmin: 'Skrbnik',
    entityImport: 'Uvoz',
    actLOGIN: 'Prijava',
    actLOGOUT: 'Odjava',
    actLOGIN_FAILED: 'Neuspešna prijava',
    actEXTEND_KEY: 'Podaljšanje ključa',
    actREVOKE_KEY: 'Preklic ključa',
    actREACTIVATE_KEY: 'Ponovna aktivacija ključa',
    actREGENERATE_KEY: 'Ponovno generiranje ključa',
    actCREATE_SANDBOX_KEY: 'Ustvarjanje peskovniškega ključa',
    actCREATE_CLIENT: 'Ustvarjanje stranke',
    actUPDATE_CLIENT: 'Posodobitev stranke',
    actDELETE_CLIENT: 'Izbris stranke',
    actCREATE_PLAN: 'Ustvarjanje načrta',
    actUPDATE_PLAN: 'Posodobitev načrta',
    actDELETE_PLAN: 'Izbris načrta',
    actCREATE_ACTIVATION_CODE: 'Ustvarjanje aktivacijske kode',
    actDELETE_ACTIVATION_CODE: 'Izbris aktivacijske kode',
    actCREATE_ADMIN: 'Ustvarjanje skrbnika',
    actUPDATE_ADMIN: 'Posodobitev skrbnika',
    actDELETE_ADMIN: 'Izbris skrbnika',
    actDEACTIVATE_ADMIN: 'Deaktivacija skrbnika',
    actACTIVATE_ADMIN: 'Aktivacija skrbnika',
    actGRAVITEE_IMPORT: 'Uvoz Gravitee',
    actBULK_EXTEND_KEYS: 'Množično podaljšanje ključev',
    actBULK_CHANGE_PLAN: 'Množična sprememba načrta',
    actBULK_DEACTIVATE_KEYS: 'Množična deaktivacija ključev',
  },
  hr: {
    title: 'Revizijski zapisi',
    subtitle: 'Povijest svih radnji koje su izvršili administratori',
    loadFailed: 'Nije moguće učitati revizijske zapise',
    filtersTitle: 'Filtri',
    exportBtn: 'Izvezi',
    showFilters: 'Prikaži filtre',
    hideFilters: 'Sakrij filtre',
    clearFilters: 'Očisti filtre',
    filterAction: 'Radnja',
    filterEntity: 'Vrsta entiteta',
    filterDateFrom: 'Datum od',
    filterDateTo: 'Datum do',
    allOption: 'Sve',
    colDateTime: 'Datum i vrijeme',
    colAdmin: 'Administrator',
    colAction: 'Radnja',
    colEntityType: 'Vrsta entiteta',
    colEntityId: 'ID entiteta',
    colChanges: 'Promjene',
    colIp: 'IP',
    viewDetails: 'Prikaži detalje',
    pageOf: 'Stranica {{cur}} od {{total}}',
    prev: 'Prethodna',
    next: 'Sljedeća',
    emptyTitle: 'Nema revizijskih zapisa',
    emptyBody: 'Nisu pronađeni zapisi koji odgovaraju kriterijima pretraživanja.',
    entityApiKey: 'API ključ',
    entityClient: 'Klijent',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktivacijski kod',
    entityAdmin: 'Administrator',
    entityImport: 'Uvoz',
    actLOGIN: 'Prijava',
    actLOGOUT: 'Odjava',
    actLOGIN_FAILED: 'Neuspješna prijava',
    actEXTEND_KEY: 'Produženje ključa',
    actREVOKE_KEY: 'Opoziv ključa',
    actREACTIVATE_KEY: 'Ponovna aktivacija ključa',
    actREGENERATE_KEY: 'Regeneracija ključa',
    actCREATE_SANDBOX_KEY: 'Stvaranje sandbox ključa',
    actCREATE_CLIENT: 'Stvaranje klijenta',
    actUPDATE_CLIENT: 'Ažuriranje klijenta',
    actDELETE_CLIENT: 'Brisanje klijenta',
    actCREATE_PLAN: 'Stvaranje plana',
    actUPDATE_PLAN: 'Ažuriranje plana',
    actDELETE_PLAN: 'Brisanje plana',
    actCREATE_ACTIVATION_CODE: 'Stvaranje aktivacijskog koda',
    actDELETE_ACTIVATION_CODE: 'Brisanje aktivacijskog koda',
    actCREATE_ADMIN: 'Stvaranje administratora',
    actUPDATE_ADMIN: 'Ažuriranje administratora',
    actDELETE_ADMIN: 'Brisanje administratora',
    actDEACTIVATE_ADMIN: 'Deaktivacija administratora',
    actACTIVATE_ADMIN: 'Aktivacija administratora',
    actGRAVITEE_IMPORT: 'Uvoz Gravitee',
    actBULK_EXTEND_KEYS: 'Skupno produženje ključeva',
    actBULK_CHANGE_PLAN: 'Skupna promjena plana',
    actBULK_DEACTIVATE_KEYS: 'Skupna deaktivacija ključeva',
  },
  hu: {
    title: 'Auditnaplók',
    subtitle: 'Az adminisztrátorok által végrehajtott összes művelet előzményei',
    loadFailed: 'Nem sikerült betölteni az auditnaplókat',
    filtersTitle: 'Szűrők',
    exportBtn: 'Exportálás',
    showFilters: 'Szűrők megjelenítése',
    hideFilters: 'Szűrők elrejtése',
    clearFilters: 'Szűrők törlése',
    filterAction: 'Művelet',
    filterEntity: 'Entitástípus',
    filterDateFrom: 'Dátum tól',
    filterDateTo: 'Dátum ig',
    allOption: 'Összes',
    colDateTime: 'Dátum és idő',
    colAdmin: 'Adminisztrátor',
    colAction: 'Művelet',
    colEntityType: 'Entitástípus',
    colEntityId: 'Entitásazonosító',
    colChanges: 'Változások',
    colIp: 'IP',
    viewDetails: 'Részletek megtekintése',
    pageOf: '{{cur}} / {{total}} oldal',
    prev: 'Előző',
    next: 'Következő',
    emptyTitle: 'Nincsenek auditnaplók',
    emptyBody: 'Nem találhatók a keresési feltételeknek megfelelő naplók.',
    entityApiKey: 'API-kulcs',
    entityClient: 'Ügyfél',
    entityPlan: 'Csomag',
    entityActivationCode: 'Aktiváló kód',
    entityAdmin: 'Adminisztrátor',
    entityImport: 'Importálás',
    actLOGIN: 'Bejelentkezés',
    actLOGOUT: 'Kijelentkezés',
    actLOGIN_FAILED: 'Sikertelen bejelentkezés',
    actEXTEND_KEY: 'Kulcshosszabbítás',
    actREVOKE_KEY: 'Kulcsvisszavonás',
    actREACTIVATE_KEY: 'Kulcs újraaktiválása',
    actREGENERATE_KEY: 'Kulcs újragenerálása',
    actCREATE_SANDBOX_KEY: 'Sandbox-kulcs létrehozása',
    actCREATE_CLIENT: 'Ügyfél létrehozása',
    actUPDATE_CLIENT: 'Ügyfél frissítése',
    actDELETE_CLIENT: 'Ügyfél törlése',
    actCREATE_PLAN: 'Csomag létrehozása',
    actUPDATE_PLAN: 'Csomag frissítése',
    actDELETE_PLAN: 'Csomag törlése',
    actCREATE_ACTIVATION_CODE: 'Aktiváló kód létrehozása',
    actDELETE_ACTIVATION_CODE: 'Aktiváló kód törlése',
    actCREATE_ADMIN: 'Adminisztrátor létrehozása',
    actUPDATE_ADMIN: 'Adminisztrátor frissítése',
    actDELETE_ADMIN: 'Adminisztrátor törlése',
    actDEACTIVATE_ADMIN: 'Adminisztrátor deaktiválása',
    actACTIVATE_ADMIN: 'Adminisztrátor aktiválása',
    actGRAVITEE_IMPORT: 'Gravitee-import',
    actBULK_EXTEND_KEYS: 'Kulcsok tömeges hosszabbítása',
    actBULK_CHANGE_PLAN: 'Csomag tömeges módosítása',
    actBULK_DEACTIVATE_KEYS: 'Kulcsok tömeges deaktiválása',
  },
  ro: {
    title: 'Jurnale de audit',
    subtitle: 'Istoricul tuturor acțiunilor efectuate de administratori',
    loadFailed: 'Nu s-au putut încărca jurnalele de audit',
    filtersTitle: 'Filtre',
    exportBtn: 'Exportă',
    showFilters: 'Arată filtrele',
    hideFilters: 'Ascunde filtrele',
    clearFilters: 'Șterge filtrele',
    filterAction: 'Acțiune',
    filterEntity: 'Tip entitate',
    filterDateFrom: 'Data de la',
    filterDateTo: 'Data până la',
    allOption: 'Toate',
    colDateTime: 'Data și ora',
    colAdmin: 'Administrator',
    colAction: 'Acțiune',
    colEntityType: 'Tip entitate',
    colEntityId: 'ID entitate',
    colChanges: 'Modificări',
    colIp: 'IP',
    viewDetails: 'Vezi detalii',
    pageOf: 'Pagina {{cur}} din {{total}}',
    prev: 'Anterioară',
    next: 'Următoarea',
    emptyTitle: 'Niciun jurnal de audit',
    emptyBody: 'Nu au fost găsite jurnale care să corespundă criteriilor de căutare.',
    entityApiKey: 'Cheie API',
    entityClient: 'Client',
    entityPlan: 'Plan',
    entityActivationCode: 'Cod de activare',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Autentificare',
    actLOGOUT: 'Deconectare',
    actLOGIN_FAILED: 'Autentificare eșuată',
    actEXTEND_KEY: 'Prelungire cheie',
    actREVOKE_KEY: 'Revocare cheie',
    actREACTIVATE_KEY: 'Reactivare cheie',
    actREGENERATE_KEY: 'Regenerare cheie',
    actCREATE_SANDBOX_KEY: 'Creare cheie sandbox',
    actCREATE_CLIENT: 'Creare client',
    actUPDATE_CLIENT: 'Actualizare client',
    actDELETE_CLIENT: 'Ștergere client',
    actCREATE_PLAN: 'Creare plan',
    actUPDATE_PLAN: 'Actualizare plan',
    actDELETE_PLAN: 'Ștergere plan',
    actCREATE_ACTIVATION_CODE: 'Creare cod de activare',
    actDELETE_ACTIVATION_CODE: 'Ștergere cod de activare',
    actCREATE_ADMIN: 'Creare administrator',
    actUPDATE_ADMIN: 'Actualizare administrator',
    actDELETE_ADMIN: 'Ștergere administrator',
    actDEACTIVATE_ADMIN: 'Dezactivare administrator',
    actACTIVATE_ADMIN: 'Activare administrator',
    actGRAVITEE_IMPORT: 'Import Gravitee',
    actBULK_EXTEND_KEYS: 'Prelungire în masă a cheilor',
    actBULK_CHANGE_PLAN: 'Schimbare în masă a planului',
    actBULK_DEACTIVATE_KEYS: 'Dezactivare în masă a cheilor',
  },
  bg: {
    title: 'Одитни логове',
    subtitle: 'История на всички действия, извършени от администраторите',
    loadFailed: 'Неуспешно зареждане на одитните логове',
    filtersTitle: 'Филтри',
    exportBtn: 'Експортиране',
    showFilters: 'Показване на филтрите',
    hideFilters: 'Скриване на филтрите',
    clearFilters: 'Изчистване на филтрите',
    filterAction: 'Действие',
    filterEntity: 'Тип същност',
    filterDateFrom: 'Дата от',
    filterDateTo: 'Дата до',
    allOption: 'Всички',
    colDateTime: 'Дата и час',
    colAdmin: 'Администратор',
    colAction: 'Действие',
    colEntityType: 'Тип същност',
    colEntityId: 'ID на същност',
    colChanges: 'Промени',
    colIp: 'IP',
    viewDetails: 'Преглед на детайли',
    pageOf: 'Страница {{cur}} от {{total}}',
    prev: 'Предишна',
    next: 'Следваща',
    emptyTitle: 'Няма одитни логове',
    emptyBody: 'Не са намерени логове, отговарящи на критериите за търсене.',
    entityApiKey: 'API ключ',
    entityClient: 'Клиент',
    entityPlan: 'План',
    entityActivationCode: 'Код за активиране',
    entityAdmin: 'Администратор',
    entityImport: 'Импортиране',
    actLOGIN: 'Вход',
    actLOGOUT: 'Изход',
    actLOGIN_FAILED: 'Неуспешен вход',
    actEXTEND_KEY: 'Удължаване на ключ',
    actREVOKE_KEY: 'Отмяна на ключ',
    actREACTIVATE_KEY: 'Реактивиране на ключ',
    actREGENERATE_KEY: 'Регенериране на ключ',
    actCREATE_SANDBOX_KEY: 'Създаване на sandbox ключ',
    actCREATE_CLIENT: 'Създаване на клиент',
    actUPDATE_CLIENT: 'Актуализиране на клиент',
    actDELETE_CLIENT: 'Изтриване на клиент',
    actCREATE_PLAN: 'Създаване на план',
    actUPDATE_PLAN: 'Актуализиране на план',
    actDELETE_PLAN: 'Изтриване на план',
    actCREATE_ACTIVATION_CODE: 'Създаване на код за активиране',
    actDELETE_ACTIVATION_CODE: 'Изтриване на код за активиране',
    actCREATE_ADMIN: 'Създаване на администратор',
    actUPDATE_ADMIN: 'Актуализиране на администратор',
    actDELETE_ADMIN: 'Изтриване на администратор',
    actDEACTIVATE_ADMIN: 'Деактивиране на администратор',
    actACTIVATE_ADMIN: 'Активиране на администратор',
    actGRAVITEE_IMPORT: 'Импортиране от Gravitee',
    actBULK_EXTEND_KEYS: 'Масово удължаване на ключове',
    actBULK_CHANGE_PLAN: 'Масова смяна на план',
    actBULK_DEACTIVATE_KEYS: 'Масово деактивиране на ключове',
  },
  el: {
    title: 'Αρχεία καταγραφής ελέγχου',
    subtitle: 'Ιστορικό όλων των ενεργειών που εκτελέστηκαν από διαχειριστές',
    loadFailed: 'Αποτυχία φόρτωσης των αρχείων καταγραφής ελέγχου',
    filtersTitle: 'Φίλτρα',
    exportBtn: 'Εξαγωγή',
    showFilters: 'Εμφάνιση φίλτρων',
    hideFilters: 'Απόκρυψη φίλτρων',
    clearFilters: 'Εκκαθάριση φίλτρων',
    filterAction: 'Ενέργεια',
    filterEntity: 'Τύπος οντότητας',
    filterDateFrom: 'Ημερομηνία από',
    filterDateTo: 'Ημερομηνία έως',
    allOption: 'Όλες',
    colDateTime: 'Ημερομηνία και ώρα',
    colAdmin: 'Διαχειριστής',
    colAction: 'Ενέργεια',
    colEntityType: 'Τύπος οντότητας',
    colEntityId: 'ID οντότητας',
    colChanges: 'Αλλαγές',
    colIp: 'IP',
    viewDetails: 'Προβολή λεπτομερειών',
    pageOf: 'Σελίδα {{cur}} από {{total}}',
    prev: 'Προηγούμενη',
    next: 'Επόμενη',
    emptyTitle: 'Δεν υπάρχουν αρχεία καταγραφής ελέγχου',
    emptyBody: 'Δεν βρέθηκαν αρχεία καταγραφής που να ταιριάζουν στα κριτήρια αναζήτησης.',
    entityApiKey: 'Κλειδί API',
    entityClient: 'Πελάτης',
    entityPlan: 'Πρόγραμμα',
    entityActivationCode: 'Κωδικός ενεργοποίησης',
    entityAdmin: 'Διαχειριστής',
    entityImport: 'Εισαγωγή',
    actLOGIN: 'Σύνδεση',
    actLOGOUT: 'Αποσύνδεση',
    actLOGIN_FAILED: 'Αποτυχημένη σύνδεση',
    actEXTEND_KEY: 'Επέκταση κλειδιού',
    actREVOKE_KEY: 'Ανάκληση κλειδιού',
    actREACTIVATE_KEY: 'Επανενεργοποίηση κλειδιού',
    actREGENERATE_KEY: 'Αναδημιουργία κλειδιού',
    actCREATE_SANDBOX_KEY: 'Δημιουργία κλειδιού sandbox',
    actCREATE_CLIENT: 'Δημιουργία πελάτη',
    actUPDATE_CLIENT: 'Ενημέρωση πελάτη',
    actDELETE_CLIENT: 'Διαγραφή πελάτη',
    actCREATE_PLAN: 'Δημιουργία προγράμματος',
    actUPDATE_PLAN: 'Ενημέρωση προγράμματος',
    actDELETE_PLAN: 'Διαγραφή προγράμματος',
    actCREATE_ACTIVATION_CODE: 'Δημιουργία κωδικού ενεργοποίησης',
    actDELETE_ACTIVATION_CODE: 'Διαγραφή κωδικού ενεργοποίησης',
    actCREATE_ADMIN: 'Δημιουργία διαχειριστή',
    actUPDATE_ADMIN: 'Ενημέρωση διαχειριστή',
    actDELETE_ADMIN: 'Διαγραφή διαχειριστή',
    actDEACTIVATE_ADMIN: 'Απενεργοποίηση διαχειριστή',
    actACTIVATE_ADMIN: 'Ενεργοποίηση διαχειριστή',
    actGRAVITEE_IMPORT: 'Εισαγωγή Gravitee',
    actBULK_EXTEND_KEYS: 'Μαζική επέκταση κλειδιών',
    actBULK_CHANGE_PLAN: 'Μαζική αλλαγή προγράμματος',
    actBULK_DEACTIVATE_KEYS: 'Μαζική απενεργοποίηση κλειδιών',
  },
  et: {
    title: 'Auditilogid',
    subtitle: 'Kõigi administraatorite tehtud toimingute ajalugu',
    loadFailed: 'Auditilogide laadimine ebaõnnestus',
    filtersTitle: 'Filtrid',
    exportBtn: 'Ekspordi',
    showFilters: 'Näita filtreid',
    hideFilters: 'Peida filtrid',
    clearFilters: 'Tühjenda filtrid',
    filterAction: 'Toiming',
    filterEntity: 'Olemi tüüp',
    filterDateFrom: 'Kuupäev alates',
    filterDateTo: 'Kuupäev kuni',
    allOption: 'Kõik',
    colDateTime: 'Kuupäev ja kellaaeg',
    colAdmin: 'Administraator',
    colAction: 'Toiming',
    colEntityType: 'Olemi tüüp',
    colEntityId: 'Olemi ID',
    colChanges: 'Muudatused',
    colIp: 'IP',
    viewDetails: 'Vaata üksikasju',
    pageOf: 'Lehekülg {{cur}} / {{total}}',
    prev: 'Eelmine',
    next: 'Järgmine',
    emptyTitle: 'Auditilogisid pole',
    emptyBody: 'Otsingukriteeriumidele vastavaid logisid ei leitud.',
    entityApiKey: 'API-võti',
    entityClient: 'Klient',
    entityPlan: 'Pakett',
    entityActivationCode: 'Aktiveerimiskood',
    entityAdmin: 'Administraator',
    entityImport: 'Import',
    actLOGIN: 'Sisselogimine',
    actLOGOUT: 'Väljalogimine',
    actLOGIN_FAILED: 'Ebaõnnestunud sisselogimine',
    actEXTEND_KEY: 'Võtme pikendamine',
    actREVOKE_KEY: 'Võtme tühistamine',
    actREACTIVATE_KEY: 'Võtme taasaktiveerimine',
    actREGENERATE_KEY: 'Võtme uuesti genereerimine',
    actCREATE_SANDBOX_KEY: 'Liivakastivõtme loomine',
    actCREATE_CLIENT: 'Kliendi loomine',
    actUPDATE_CLIENT: 'Kliendi uuendamine',
    actDELETE_CLIENT: 'Kliendi kustutamine',
    actCREATE_PLAN: 'Paketi loomine',
    actUPDATE_PLAN: 'Paketi uuendamine',
    actDELETE_PLAN: 'Paketi kustutamine',
    actCREATE_ACTIVATION_CODE: 'Aktiveerimiskoodi loomine',
    actDELETE_ACTIVATION_CODE: 'Aktiveerimiskoodi kustutamine',
    actCREATE_ADMIN: 'Administraatori loomine',
    actUPDATE_ADMIN: 'Administraatori uuendamine',
    actDELETE_ADMIN: 'Administraatori kustutamine',
    actDEACTIVATE_ADMIN: 'Administraatori deaktiveerimine',
    actACTIVATE_ADMIN: 'Administraatori aktiveerimine',
    actGRAVITEE_IMPORT: 'Gravitee import',
    actBULK_EXTEND_KEYS: 'Võtmete masspikendamine',
    actBULK_CHANGE_PLAN: 'Paketi massmuutmine',
    actBULK_DEACTIVATE_KEYS: 'Võtmete massdeaktiveerimine',
  },
  lv: {
    title: 'Audita žurnāli',
    subtitle: 'Visu administratoru veikto darbību vēsture',
    loadFailed: 'Neizdevās ielādēt audita žurnālus',
    filtersTitle: 'Filtri',
    exportBtn: 'Eksportēt',
    showFilters: 'Rādīt filtrus',
    hideFilters: 'Slēpt filtrus',
    clearFilters: 'Notīrīt filtrus',
    filterAction: 'Darbība',
    filterEntity: 'Entītijas tips',
    filterDateFrom: 'Datums no',
    filterDateTo: 'Datums līdz',
    allOption: 'Visi',
    colDateTime: 'Datums un laiks',
    colAdmin: 'Administrators',
    colAction: 'Darbība',
    colEntityType: 'Entītijas tips',
    colEntityId: 'Entītijas ID',
    colChanges: 'Izmaiņas',
    colIp: 'IP',
    viewDetails: 'Skatīt detaļas',
    pageOf: 'Lapa {{cur}} no {{total}}',
    prev: 'Iepriekšējā',
    next: 'Nākamā',
    emptyTitle: 'Nav audita žurnālu',
    emptyBody: 'Netika atrasti žurnāli, kas atbilstu meklēšanas kritērijiem.',
    entityApiKey: 'API atslēga',
    entityClient: 'Klients',
    entityPlan: 'Plāns',
    entityActivationCode: 'Aktivizācijas kods',
    entityAdmin: 'Administrators',
    entityImport: 'Imports',
    actLOGIN: 'Pieteikšanās',
    actLOGOUT: 'Atteikšanās',
    actLOGIN_FAILED: 'Neveiksmīga pieteikšanās',
    actEXTEND_KEY: 'Atslēgas pagarināšana',
    actREVOKE_KEY: 'Atslēgas atsaukšana',
    actREACTIVATE_KEY: 'Atslēgas atkārtota aktivizēšana',
    actREGENERATE_KEY: 'Atslēgas atjaunošana',
    actCREATE_SANDBOX_KEY: 'Smilšu kastes atslēgas izveide',
    actCREATE_CLIENT: 'Klienta izveide',
    actUPDATE_CLIENT: 'Klienta atjaunināšana',
    actDELETE_CLIENT: 'Klienta dzēšana',
    actCREATE_PLAN: 'Plāna izveide',
    actUPDATE_PLAN: 'Plāna atjaunināšana',
    actDELETE_PLAN: 'Plāna dzēšana',
    actCREATE_ACTIVATION_CODE: 'Aktivizācijas koda izveide',
    actDELETE_ACTIVATION_CODE: 'Aktivizācijas koda dzēšana',
    actCREATE_ADMIN: 'Administratora izveide',
    actUPDATE_ADMIN: 'Administratora atjaunināšana',
    actDELETE_ADMIN: 'Administratora dzēšana',
    actDEACTIVATE_ADMIN: 'Administratora deaktivizēšana',
    actACTIVATE_ADMIN: 'Administratora aktivizēšana',
    actGRAVITEE_IMPORT: 'Gravitee imports',
    actBULK_EXTEND_KEYS: 'Atslēgu masveida pagarināšana',
    actBULK_CHANGE_PLAN: 'Plāna masveida maiņa',
    actBULK_DEACTIVATE_KEYS: 'Atslēgu masveida deaktivizēšana',
  },
  lt: {
    title: 'Audito žurnalai',
    subtitle: 'Visų administratorių atliktų veiksmų istorija',
    loadFailed: 'Nepavyko įkelti audito žurnalų',
    filtersTitle: 'Filtrai',
    exportBtn: 'Eksportuoti',
    showFilters: 'Rodyti filtrus',
    hideFilters: 'Slėpti filtrus',
    clearFilters: 'Išvalyti filtrus',
    filterAction: 'Veiksmas',
    filterEntity: 'Esybės tipas',
    filterDateFrom: 'Data nuo',
    filterDateTo: 'Data iki',
    allOption: 'Visi',
    colDateTime: 'Data ir laikas',
    colAdmin: 'Administratorius',
    colAction: 'Veiksmas',
    colEntityType: 'Esybės tipas',
    colEntityId: 'Esybės ID',
    colChanges: 'Pakeitimai',
    colIp: 'IP',
    viewDetails: 'Peržiūrėti išsamią informaciją',
    pageOf: 'Puslapis {{cur}} iš {{total}}',
    prev: 'Ankstesnis',
    next: 'Kitas',
    emptyTitle: 'Nėra audito žurnalų',
    emptyBody: 'Nerasta žurnalų, atitinkančių paieškos kriterijus.',
    entityApiKey: 'API raktas',
    entityClient: 'Klientas',
    entityPlan: 'Planas',
    entityActivationCode: 'Aktyvavimo kodas',
    entityAdmin: 'Administratorius',
    entityImport: 'Importas',
    actLOGIN: 'Prisijungimas',
    actLOGOUT: 'Atsijungimas',
    actLOGIN_FAILED: 'Nepavykęs prisijungimas',
    actEXTEND_KEY: 'Rakto pratęsimas',
    actREVOKE_KEY: 'Rakto atšaukimas',
    actREACTIVATE_KEY: 'Rakto pakartotinis aktyvavimas',
    actREGENERATE_KEY: 'Rakto regeneravimas',
    actCREATE_SANDBOX_KEY: 'Smėlio dėžės rakto sukūrimas',
    actCREATE_CLIENT: 'Kliento sukūrimas',
    actUPDATE_CLIENT: 'Kliento atnaujinimas',
    actDELETE_CLIENT: 'Kliento ištrynimas',
    actCREATE_PLAN: 'Plano sukūrimas',
    actUPDATE_PLAN: 'Plano atnaujinimas',
    actDELETE_PLAN: 'Plano ištrynimas',
    actCREATE_ACTIVATION_CODE: 'Aktyvavimo kodo sukūrimas',
    actDELETE_ACTIVATION_CODE: 'Aktyvavimo kodo ištrynimas',
    actCREATE_ADMIN: 'Administratoriaus sukūrimas',
    actUPDATE_ADMIN: 'Administratoriaus atnaujinimas',
    actDELETE_ADMIN: 'Administratoriaus ištrynimas',
    actDEACTIVATE_ADMIN: 'Administratoriaus deaktyvavimas',
    actACTIVATE_ADMIN: 'Administratoriaus aktyvavimas',
    actGRAVITEE_IMPORT: 'Gravitee importas',
    actBULK_EXTEND_KEYS: 'Masinis raktų pratęsimas',
    actBULK_CHANGE_PLAN: 'Masinis plano keitimas',
    actBULK_DEACTIVATE_KEYS: 'Masinis raktų deaktyvavimas',
  },
  mt: {
    title: 'Reġistri tal-awditjar',
    subtitle: 'Storja tal-azzjonijiet kollha mwettqa mill-amministraturi',
    loadFailed: 'Ma rnexxiex jiġu mgħobbija r-reġistri tal-awditjar',
    filtersTitle: 'Filtri',
    exportBtn: 'Esporta',
    showFilters: 'Uri l-filtri',
    hideFilters: 'Aħbi l-filtri',
    clearFilters: 'Naddaf il-filtri',
    filterAction: 'Azzjoni',
    filterEntity: "Tip ta' entità",
    filterDateFrom: 'Data minn',
    filterDateTo: 'Data sa',
    allOption: 'Kollha',
    colDateTime: 'Data u ħin',
    colAdmin: 'Amministratur',
    colAction: 'Azzjoni',
    colEntityType: "Tip ta' entità",
    colEntityId: 'ID tal-entità',
    colChanges: 'Bidliet',
    colIp: 'IP',
    viewDetails: 'Ara d-dettalji',
    pageOf: 'Paġna {{cur}} minn {{total}}',
    prev: 'Preċedenti',
    next: 'Li jmiss',
    emptyTitle: 'Ebda reġistru tal-awditjar',
    emptyBody: 'Ma nstabu ebda reġistri li jaqblu mal-kriterji tat-tfittxija.',
    entityApiKey: 'Ċavetta API',
    entityClient: 'Klijent',
    entityPlan: 'Pjan',
    entityActivationCode: "Kodiċi ta' attivazzjoni",
    entityAdmin: 'Amministratur',
    entityImport: 'Importazzjoni',
    actLOGIN: 'Login',
    actLOGOUT: 'Logout',
    actLOGIN_FAILED: 'Login falluta',
    actEXTEND_KEY: 'Estensjoni taċ-ċavetta',
    actREVOKE_KEY: 'Revoka taċ-ċavetta',
    actREACTIVATE_KEY: 'Riattivazzjoni taċ-ċavetta',
    actREGENERATE_KEY: 'Riġenerazzjoni taċ-ċavetta',
    actCREATE_SANDBOX_KEY: "Ħolqien ta' ċavetta sandbox",
    actCREATE_CLIENT: "Ħolqien ta' klijent",
    actUPDATE_CLIENT: "Aġġornament ta' klijent",
    actDELETE_CLIENT: "Tħassir ta' klijent",
    actCREATE_PLAN: "Ħolqien ta' pjan",
    actUPDATE_PLAN: "Aġġornament ta' pjan",
    actDELETE_PLAN: "Tħassir ta' pjan",
    actCREATE_ACTIVATION_CODE: "Ħolqien ta' kodiċi ta' attivazzjoni",
    actDELETE_ACTIVATION_CODE: "Tħassir ta' kodiċi ta' attivazzjoni",
    actCREATE_ADMIN: "Ħolqien ta' amministratur",
    actUPDATE_ADMIN: "Aġġornament ta' amministratur",
    actDELETE_ADMIN: "Tħassir ta' amministratur",
    actDEACTIVATE_ADMIN: "Diżattivazzjoni ta' amministratur",
    actACTIVATE_ADMIN: "Attivazzjoni ta' amministratur",
    actGRAVITEE_IMPORT: 'Importazzjoni Gravitee',
    actBULK_EXTEND_KEYS: 'Estensjoni bl-ingrossa taċ-ċwievet',
    actBULK_CHANGE_PLAN: 'Bidla bl-ingrossa tal-pjan',
    actBULK_DEACTIVATE_KEYS: 'Diżattivazzjoni bl-ingrossa taċ-ċwievet',
  },
  ga: {
    title: 'Logaí iniúchta',
    subtitle: 'Stair na ngníomhartha go léir a rinne riarthóirí',
    loadFailed: 'Theip ar na logaí iniúchta a luchtú',
    filtersTitle: 'Scagairí',
    exportBtn: 'Easpórtáil',
    showFilters: 'Taispeáin scagairí',
    hideFilters: 'Folaigh scagairí',
    clearFilters: 'Glan scagairí',
    filterAction: 'Gníomh',
    filterEntity: 'Cineál aonáin',
    filterDateFrom: 'Dáta ó',
    filterDateTo: 'Dáta go',
    allOption: 'Gach ceann',
    colDateTime: 'Dáta agus am',
    colAdmin: 'Riarthóir',
    colAction: 'Gníomh',
    colEntityType: 'Cineál aonáin',
    colEntityId: 'ID aonáin',
    colChanges: 'Athruithe',
    colIp: 'IP',
    viewDetails: 'Féach sonraí',
    pageOf: 'Leathanach {{cur}} as {{total}}',
    prev: 'Roimhe seo',
    next: 'Ar aghaidh',
    emptyTitle: 'Gan logaí iniúchta',
    emptyBody: 'Níor aimsíodh aon logaí a mheaitseáil leis na critéir chuardaigh.',
    entityApiKey: 'Eochair API',
    entityClient: 'Cliant',
    entityPlan: 'Plean',
    entityActivationCode: 'Cód gníomhachtaithe',
    entityAdmin: 'Riarthóir',
    entityImport: 'Iompórtáil',
    actLOGIN: 'Logáil isteach',
    actLOGOUT: 'Logáil amach',
    actLOGIN_FAILED: 'Logáil isteach teipthe',
    actEXTEND_KEY: 'Síneadh eochrach',
    actREVOKE_KEY: 'Cúlghairm eochrach',
    actREACTIVATE_KEY: 'Athghníomhachtú eochrach',
    actREGENERATE_KEY: 'Athghiniúint eochrach',
    actCREATE_SANDBOX_KEY: 'Cruthú eochrach sandbox',
    actCREATE_CLIENT: 'Cruthú cliaint',
    actUPDATE_CLIENT: 'Nuashonrú cliaint',
    actDELETE_CLIENT: 'Scriosadh cliaint',
    actCREATE_PLAN: 'Cruthú plean',
    actUPDATE_PLAN: 'Nuashonrú plean',
    actDELETE_PLAN: 'Scriosadh plean',
    actCREATE_ACTIVATION_CODE: 'Cruthú cód gníomhachtaithe',
    actDELETE_ACTIVATION_CODE: 'Scriosadh cód gníomhachtaithe',
    actCREATE_ADMIN: 'Cruthú riarthóra',
    actUPDATE_ADMIN: 'Nuashonrú riarthóra',
    actDELETE_ADMIN: 'Scriosadh riarthóra',
    actDEACTIVATE_ADMIN: 'Díghníomhachtú riarthóra',
    actACTIVATE_ADMIN: 'Gníomhachtú riarthóra',
    actGRAVITEE_IMPORT: 'Iompórtáil Gravitee',
    actBULK_EXTEND_KEYS: 'Síneadh ollmhór eochracha',
    actBULK_CHANGE_PLAN: 'Athrú ollmhór plean',
    actBULK_DEACTIVATE_KEYS: 'Díghníomhachtú ollmhór eochracha',
  },
  no: {
    title: 'Revisjonslogger',
    subtitle: 'Historikk over alle handlinger utført av administratorer',
    loadFailed: 'Kunne ikke laste revisjonsloggene',
    filtersTitle: 'Filtre',
    exportBtn: 'Eksporter',
    showFilters: 'Vis filtre',
    hideFilters: 'Skjul filtre',
    clearFilters: 'Tøm filtre',
    filterAction: 'Handling',
    filterEntity: 'Entitetstype',
    filterDateFrom: 'Dato fra',
    filterDateTo: 'Dato til',
    allOption: 'Alle',
    colDateTime: 'Dato og tid',
    colAdmin: 'Administrator',
    colAction: 'Handling',
    colEntityType: 'Entitetstype',
    colEntityId: 'Entitets-ID',
    colChanges: 'Endringer',
    colIp: 'IP',
    viewDetails: 'Vis detaljer',
    pageOf: 'Side {{cur}} av {{total}}',
    prev: 'Forrige',
    next: 'Neste',
    emptyTitle: 'Ingen revisjonslogger',
    emptyBody: 'Ingen logger som samsvarer med søkekriteriene ble funnet.',
    entityApiKey: 'API-nøkkel',
    entityClient: 'Kunde',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktiveringskode',
    entityAdmin: 'Administrator',
    entityImport: 'Import',
    actLOGIN: 'Innlogging',
    actLOGOUT: 'Utlogging',
    actLOGIN_FAILED: 'Mislykket innlogging',
    actEXTEND_KEY: 'Nøkkelforlengelse',
    actREVOKE_KEY: 'Nøkkeltilbakekalling',
    actREACTIVATE_KEY: 'Nøkkelreaktivering',
    actREGENERATE_KEY: 'Nøkkelregenerering',
    actCREATE_SANDBOX_KEY: 'Opprettelse av sandkassenøkkel',
    actCREATE_CLIENT: 'Opprettelse av kunde',
    actUPDATE_CLIENT: 'Oppdatering av kunde',
    actDELETE_CLIENT: 'Sletting av kunde',
    actCREATE_PLAN: 'Opprettelse av plan',
    actUPDATE_PLAN: 'Oppdatering av plan',
    actDELETE_PLAN: 'Sletting av plan',
    actCREATE_ACTIVATION_CODE: 'Opprettelse av aktiveringskode',
    actDELETE_ACTIVATION_CODE: 'Sletting av aktiveringskode',
    actCREATE_ADMIN: 'Opprettelse av administrator',
    actUPDATE_ADMIN: 'Oppdatering av administrator',
    actDELETE_ADMIN: 'Sletting av administrator',
    actDEACTIVATE_ADMIN: 'Deaktivering av administrator',
    actACTIVATE_ADMIN: 'Aktivering av administrator',
    actGRAVITEE_IMPORT: 'Gravitee-import',
    actBULK_EXTEND_KEYS: 'Masseforlengelse av nøkler',
    actBULK_CHANGE_PLAN: 'Masseendring av plan',
    actBULK_DEACTIVATE_KEYS: 'Massedeaktivering av nøkler',
  },
  is: {
    title: 'Endurskoðunarskrár',
    subtitle: 'Saga allra aðgerða sem stjórnendur framkvæmdu',
    loadFailed: 'Ekki tókst að hlaða endurskoðunarskrár',
    filtersTitle: 'Síur',
    exportBtn: 'Flytja út',
    showFilters: 'Sýna síur',
    hideFilters: 'Fela síur',
    clearFilters: 'Hreinsa síur',
    filterAction: 'Aðgerð',
    filterEntity: 'Tegund einingar',
    filterDateFrom: 'Dagsetning frá',
    filterDateTo: 'Dagsetning til',
    allOption: 'Allar',
    colDateTime: 'Dagsetning og tími',
    colAdmin: 'Stjórnandi',
    colAction: 'Aðgerð',
    colEntityType: 'Tegund einingar',
    colEntityId: 'Auðkenni einingar',
    colChanges: 'Breytingar',
    colIp: 'IP',
    viewDetails: 'Skoða nánar',
    pageOf: 'Síða {{cur}} af {{total}}',
    prev: 'Fyrri',
    next: 'Næsta',
    emptyTitle: 'Engar endurskoðunarskrár',
    emptyBody: 'Engar skrár fundust sem passa við leitarskilyrðin.',
    entityApiKey: 'API-lykill',
    entityClient: 'Viðskiptavinur',
    entityPlan: 'Áætlun',
    entityActivationCode: 'Virkjunarkóði',
    entityAdmin: 'Stjórnandi',
    entityImport: 'Innflutningur',
    actLOGIN: 'Innskráning',
    actLOGOUT: 'Útskráning',
    actLOGIN_FAILED: 'Misheppnuð innskráning',
    actEXTEND_KEY: 'Lengja lykil',
    actREVOKE_KEY: 'Afturkalla lykil',
    actREACTIVATE_KEY: 'Endurvirkja lykil',
    actREGENERATE_KEY: 'Endurmynda lykil',
    actCREATE_SANDBOX_KEY: 'Búa til sandkassalykil',
    actCREATE_CLIENT: 'Búa til viðskiptavin',
    actUPDATE_CLIENT: 'Uppfæra viðskiptavin',
    actDELETE_CLIENT: 'Eyða viðskiptavini',
    actCREATE_PLAN: 'Búa til áætlun',
    actUPDATE_PLAN: 'Uppfæra áætlun',
    actDELETE_PLAN: 'Eyða áætlun',
    actCREATE_ACTIVATION_CODE: 'Búa til virkjunarkóða',
    actDELETE_ACTIVATION_CODE: 'Eyða virkjunarkóða',
    actCREATE_ADMIN: 'Búa til stjórnanda',
    actUPDATE_ADMIN: 'Uppfæra stjórnanda',
    actDELETE_ADMIN: 'Eyða stjórnanda',
    actDEACTIVATE_ADMIN: 'Afvirkja stjórnanda',
    actACTIVATE_ADMIN: 'Virkja stjórnanda',
    actGRAVITEE_IMPORT: 'Gravitee-innflutningur',
    actBULK_EXTEND_KEYS: 'Fjöldalenging lykla',
    actBULK_CHANGE_PLAN: 'Fjöldabreyting áætlunar',
    actBULK_DEACTIVATE_KEYS: 'Fjöldaafvirkjun lykla',
  },
  uk: {
    title: 'Журнали аудиту',
    subtitle: 'Історія всіх дій, виконаних адміністраторами',
    loadFailed: 'Не вдалося завантажити журнали аудиту',
    filtersTitle: 'Фільтри',
    exportBtn: 'Експортувати',
    showFilters: 'Показати фільтри',
    hideFilters: 'Сховати фільтри',
    clearFilters: 'Очистити фільтри',
    filterAction: 'Дія',
    filterEntity: 'Тип сутності',
    filterDateFrom: 'Дата від',
    filterDateTo: 'Дата до',
    allOption: 'Усі',
    colDateTime: 'Дата і час',
    colAdmin: 'Адміністратор',
    colAction: 'Дія',
    colEntityType: 'Тип сутності',
    colEntityId: 'ID сутності',
    colChanges: 'Зміни',
    colIp: 'IP',
    viewDetails: 'Переглянути деталі',
    pageOf: 'Сторінка {{cur}} з {{total}}',
    prev: 'Попередня',
    next: 'Наступна',
    emptyTitle: 'Немає журналів аудиту',
    emptyBody: 'Не знайдено журналів, що відповідають критеріям пошуку.',
    entityApiKey: 'Ключ API',
    entityClient: 'Клієнт',
    entityPlan: 'План',
    entityActivationCode: 'Код активації',
    entityAdmin: 'Адміністратор',
    entityImport: 'Імпорт',
    actLOGIN: 'Вхід',
    actLOGOUT: 'Вихід',
    actLOGIN_FAILED: 'Невдалий вхід',
    actEXTEND_KEY: 'Продовження ключа',
    actREVOKE_KEY: 'Відкликання ключа',
    actREACTIVATE_KEY: 'Повторна активація ключа',
    actREGENERATE_KEY: 'Регенерація ключа',
    actCREATE_SANDBOX_KEY: 'Створення ключа sandbox',
    actCREATE_CLIENT: 'Створення клієнта',
    actUPDATE_CLIENT: 'Оновлення клієнта',
    actDELETE_CLIENT: 'Видалення клієнта',
    actCREATE_PLAN: 'Створення плану',
    actUPDATE_PLAN: 'Оновлення плану',
    actDELETE_PLAN: 'Видалення плану',
    actCREATE_ACTIVATION_CODE: 'Створення коду активації',
    actDELETE_ACTIVATION_CODE: 'Видалення коду активації',
    actCREATE_ADMIN: 'Створення адміністратора',
    actUPDATE_ADMIN: 'Оновлення адміністратора',
    actDELETE_ADMIN: 'Видалення адміністратора',
    actDEACTIVATE_ADMIN: 'Деактивація адміністратора',
    actACTIVATE_ADMIN: 'Активація адміністратора',
    actGRAVITEE_IMPORT: 'Імпорт Gravitee',
    actBULK_EXTEND_KEYS: 'Масове продовження ключів',
    actBULK_CHANGE_PLAN: 'Масова зміна плану',
    actBULK_DEACTIVATE_KEYS: 'Масова деактивація ключів',
  },
  sr: {
    title: 'Ревизиони логови',
    subtitle: 'Историја свих радњи које су извршили администратори',
    loadFailed: 'Није могуће учитати ревизионе логове',
    filtersTitle: 'Филтери',
    exportBtn: 'Извези',
    showFilters: 'Прикажи филтере',
    hideFilters: 'Сакриј филтере',
    clearFilters: 'Очисти филтере',
    filterAction: 'Радња',
    filterEntity: 'Тип ентитета',
    filterDateFrom: 'Датум од',
    filterDateTo: 'Датум до',
    allOption: 'Све',
    colDateTime: 'Датум и време',
    colAdmin: 'Администратор',
    colAction: 'Радња',
    colEntityType: 'Тип ентитета',
    colEntityId: 'ИД ентитета',
    colChanges: 'Измене',
    colIp: 'IP',
    viewDetails: 'Прикажи детаље',
    pageOf: 'Страница {{cur}} од {{total}}',
    prev: 'Претходна',
    next: 'Следећа',
    emptyTitle: 'Нема ревизионих логова',
    emptyBody: 'Нису пронађени логови који одговарају критеријумима претраге.',
    entityApiKey: 'API кључ',
    entityClient: 'Клијент',
    entityPlan: 'План',
    entityActivationCode: 'Активациони код',
    entityAdmin: 'Администратор',
    entityImport: 'Увоз',
    actLOGIN: 'Пријава',
    actLOGOUT: 'Одјава',
    actLOGIN_FAILED: 'Неуспела пријава',
    actEXTEND_KEY: 'Продужење кључа',
    actREVOKE_KEY: 'Опозив кључа',
    actREACTIVATE_KEY: 'Поновна активација кључа',
    actREGENERATE_KEY: 'Регенерација кључа',
    actCREATE_SANDBOX_KEY: 'Креирање sandbox кључа',
    actCREATE_CLIENT: 'Креирање клијента',
    actUPDATE_CLIENT: 'Ажурирање клијента',
    actDELETE_CLIENT: 'Брисање клијента',
    actCREATE_PLAN: 'Креирање плана',
    actUPDATE_PLAN: 'Ажурирање плана',
    actDELETE_PLAN: 'Брисање плана',
    actCREATE_ACTIVATION_CODE: 'Креирање активационог кода',
    actDELETE_ACTIVATION_CODE: 'Брисање активационог кода',
    actCREATE_ADMIN: 'Креирање администратора',
    actUPDATE_ADMIN: 'Ажурирање администратора',
    actDELETE_ADMIN: 'Брисање администратора',
    actDEACTIVATE_ADMIN: 'Деактивација администратора',
    actACTIVATE_ADMIN: 'Активација администратора',
    actGRAVITEE_IMPORT: 'Увоз Gravitee',
    actBULK_EXTEND_KEYS: 'Масовно продужење кључева',
    actBULK_CHANGE_PLAN: 'Масовна промена плана',
    actBULK_DEACTIVATE_KEYS: 'Масовна деактивација кључева',
  },
  sq: {
    title: 'Regjistrat e auditimit',
    subtitle: 'Historiku i të gjitha veprimeve të kryera nga administratorët',
    loadFailed: 'Dështoi ngarkimi i regjistrave të auditimit',
    filtersTitle: 'Filtrat',
    exportBtn: 'Eksporto',
    showFilters: 'Shfaq filtrat',
    hideFilters: 'Fshih filtrat',
    clearFilters: 'Pastro filtrat',
    filterAction: 'Veprimi',
    filterEntity: 'Lloji i entitetit',
    filterDateFrom: 'Data nga',
    filterDateTo: 'Data deri',
    allOption: 'Të gjitha',
    colDateTime: 'Data dhe ora',
    colAdmin: 'Administratori',
    colAction: 'Veprimi',
    colEntityType: 'Lloji i entitetit',
    colEntityId: 'ID e entitetit',
    colChanges: 'Ndryshimet',
    colIp: 'IP',
    viewDetails: 'Shiko detajet',
    pageOf: 'Faqja {{cur}} nga {{total}}',
    prev: 'Mëparshme',
    next: 'Tjetra',
    emptyTitle: 'Asnjë regjistër auditimi',
    emptyBody: 'Nuk u gjetën regjistra që përputhen me kriteret e kërkimit.',
    entityApiKey: 'Çelësi API',
    entityClient: 'Klienti',
    entityPlan: 'Plani',
    entityActivationCode: 'Kodi i aktivizimit',
    entityAdmin: 'Administratori',
    entityImport: 'Importimi',
    actLOGIN: 'Hyrje',
    actLOGOUT: 'Dalje',
    actLOGIN_FAILED: 'Hyrje e dështuar',
    actEXTEND_KEY: 'Zgjatje e çelësit',
    actREVOKE_KEY: 'Revokim i çelësit',
    actREACTIVATE_KEY: 'Riaktivizim i çelësit',
    actREGENERATE_KEY: 'Rigjenerim i çelësit',
    actCREATE_SANDBOX_KEY: 'Krijim i çelësit sandbox',
    actCREATE_CLIENT: 'Krijim klienti',
    actUPDATE_CLIENT: 'Përditësim klienti',
    actDELETE_CLIENT: 'Fshirje klienti',
    actCREATE_PLAN: 'Krijim plani',
    actUPDATE_PLAN: 'Përditësim plani',
    actDELETE_PLAN: 'Fshirje plani',
    actCREATE_ACTIVATION_CODE: 'Krijim i kodit të aktivizimit',
    actDELETE_ACTIVATION_CODE: 'Fshirje e kodit të aktivizimit',
    actCREATE_ADMIN: 'Krijim administratori',
    actUPDATE_ADMIN: 'Përditësim administratori',
    actDELETE_ADMIN: 'Fshirje administratori',
    actDEACTIVATE_ADMIN: 'Çaktivizim administratori',
    actACTIVATE_ADMIN: 'Aktivizim administratori',
    actGRAVITEE_IMPORT: 'Importim Gravitee',
    actBULK_EXTEND_KEYS: 'Zgjatje masive e çelësave',
    actBULK_CHANGE_PLAN: 'Ndryshim masiv i planit',
    actBULK_DEACTIVATE_KEYS: 'Çaktivizim masiv i çelësave',
  },
  mk: {
    title: 'Логови за ревизија',
    subtitle: 'Историја на сите дејства извршени од администраторите',
    loadFailed: 'Не може да се вчитаат логовите за ревизија',
    filtersTitle: 'Филтри',
    exportBtn: 'Извези',
    showFilters: 'Прикажи филтри',
    hideFilters: 'Сокриј филтри',
    clearFilters: 'Исчисти филтри',
    filterAction: 'Дејство',
    filterEntity: 'Тип ентитет',
    filterDateFrom: 'Датум од',
    filterDateTo: 'Датум до',
    allOption: 'Сите',
    colDateTime: 'Датум и време',
    colAdmin: 'Администратор',
    colAction: 'Дејство',
    colEntityType: 'Тип ентитет',
    colEntityId: 'ID на ентитет',
    colChanges: 'Промени',
    colIp: 'IP',
    viewDetails: 'Прикажи детали',
    pageOf: 'Страница {{cur}} од {{total}}',
    prev: 'Претходна',
    next: 'Следна',
    emptyTitle: 'Нема логови за ревизија',
    emptyBody: 'Не се пронајдени логови што одговараат на критериумите за пребарување.',
    entityApiKey: 'API клуч',
    entityClient: 'Клиент',
    entityPlan: 'План',
    entityActivationCode: 'Код за активирање',
    entityAdmin: 'Администратор',
    entityImport: 'Увоз',
    actLOGIN: 'Најава',
    actLOGOUT: 'Одјава',
    actLOGIN_FAILED: 'Неуспешна најава',
    actEXTEND_KEY: 'Продолжување на клуч',
    actREVOKE_KEY: 'Отповикување на клуч',
    actREACTIVATE_KEY: 'Повторно активирање на клуч',
    actREGENERATE_KEY: 'Регенерирање на клуч',
    actCREATE_SANDBOX_KEY: 'Создавање на sandbox клуч',
    actCREATE_CLIENT: 'Создавање на клиент',
    actUPDATE_CLIENT: 'Ажурирање на клиент',
    actDELETE_CLIENT: 'Бришење на клиент',
    actCREATE_PLAN: 'Создавање на план',
    actUPDATE_PLAN: 'Ажурирање на план',
    actDELETE_PLAN: 'Бришење на план',
    actCREATE_ACTIVATION_CODE: 'Создавање на код за активирање',
    actDELETE_ACTIVATION_CODE: 'Бришење на код за активирање',
    actCREATE_ADMIN: 'Создавање на администратор',
    actUPDATE_ADMIN: 'Ажурирање на администратор',
    actDELETE_ADMIN: 'Бришење на администратор',
    actDEACTIVATE_ADMIN: 'Деактивирање на администратор',
    actACTIVATE_ADMIN: 'Активирање на администратор',
    actGRAVITEE_IMPORT: 'Увоз од Gravitee',
    actBULK_EXTEND_KEYS: 'Масовно продолжување на клучеви',
    actBULK_CHANGE_PLAN: 'Масовна промена на план',
    actBULK_DEACTIVATE_KEYS: 'Масовно деактивирање на клучеви',
  },
  bs: {
    title: 'Revizijski zapisi',
    subtitle: 'Historija svih radnji koje su izvršili administratori',
    loadFailed: 'Nije moguće učitati revizijske zapise',
    filtersTitle: 'Filteri',
    exportBtn: 'Izvezi',
    showFilters: 'Prikaži filtere',
    hideFilters: 'Sakrij filtere',
    clearFilters: 'Očisti filtere',
    filterAction: 'Radnja',
    filterEntity: 'Vrsta entiteta',
    filterDateFrom: 'Datum od',
    filterDateTo: 'Datum do',
    allOption: 'Sve',
    colDateTime: 'Datum i vrijeme',
    colAdmin: 'Administrator',
    colAction: 'Radnja',
    colEntityType: 'Vrsta entiteta',
    colEntityId: 'ID entiteta',
    colChanges: 'Promjene',
    colIp: 'IP',
    viewDetails: 'Prikaži detalje',
    pageOf: 'Stranica {{cur}} od {{total}}',
    prev: 'Prethodna',
    next: 'Sljedeća',
    emptyTitle: 'Nema revizijskih zapisa',
    emptyBody: 'Nisu pronađeni zapisi koji odgovaraju kriterijima pretrage.',
    entityApiKey: 'API ključ',
    entityClient: 'Klijent',
    entityPlan: 'Plan',
    entityActivationCode: 'Aktivacijski kod',
    entityAdmin: 'Administrator',
    entityImport: 'Uvoz',
    actLOGIN: 'Prijava',
    actLOGOUT: 'Odjava',
    actLOGIN_FAILED: 'Neuspjela prijava',
    actEXTEND_KEY: 'Produženje ključa',
    actREVOKE_KEY: 'Opoziv ključa',
    actREACTIVATE_KEY: 'Ponovna aktivacija ključa',
    actREGENERATE_KEY: 'Regeneracija ključa',
    actCREATE_SANDBOX_KEY: 'Kreiranje sandbox ključa',
    actCREATE_CLIENT: 'Kreiranje klijenta',
    actUPDATE_CLIENT: 'Ažuriranje klijenta',
    actDELETE_CLIENT: 'Brisanje klijenta',
    actCREATE_PLAN: 'Kreiranje plana',
    actUPDATE_PLAN: 'Ažuriranje plana',
    actDELETE_PLAN: 'Brisanje plana',
    actCREATE_ACTIVATION_CODE: 'Kreiranje aktivacijskog koda',
    actDELETE_ACTIVATION_CODE: 'Brisanje aktivacijskog koda',
    actCREATE_ADMIN: 'Kreiranje administratora',
    actUPDATE_ADMIN: 'Ažuriranje administratora',
    actDELETE_ADMIN: 'Brisanje administratora',
    actDEACTIVATE_ADMIN: 'Deaktivacija administratora',
    actACTIVATE_ADMIN: 'Aktivacija administratora',
    actGRAVITEE_IMPORT: 'Uvoz Gravitee',
    actBULK_EXTEND_KEYS: 'Masovno produženje ključeva',
    actBULK_CHANGE_PLAN: 'Masovna promjena plana',
    actBULK_DEACTIVATE_KEYS: 'Masovna deaktivacija ključeva',
  },
};

const adminDashTr: Record<string, Dict> = {
  en: {
    title: 'Admin panel',
    subtitle: 'Solar Forecast API system overview',
    loadFailed: 'Failed to load the dashboard',
    loading: 'Loading data…',
    totalClients: 'Total clients',
    activeApiKeys: 'Active API keys',
    totalSuffix: '{{n}} total',
    requestsToday: 'Requests today',
    totalRequests: 'Total requests',
    requestsOverTime: 'Requests over time',
    lastRange: 'Last {{range}} ({{gran}})',
    h24: '24 hours',
    daysShort: '{{n}} days',
    granAuto: 'Auto',
    granMinute: 'Minutes',
    granHour: 'Hours',
    granDay: 'Days',
    granWeek: 'Weeks',
    rangeLabel: 'Range:',
    scaleLabel: 'Scale:',
    topClients: 'Top clients',
    byRequestCount: 'By request count',
    lastDaysParen: '(last {{n}} days)',
    allTimeParen: '(all time)',
    periodLabel: 'Period:',
    allTime: 'All time',
    recentActivity: 'Recent activity',
    recentActivitySubtitle: 'Last 20 API requests',
    noActivity: 'No activity',
    colClient: 'Client',
    colLocation: 'Location',
    colStrings: 'Strings',
    colTimeMs: 'Time (ms)',
    colDate: 'Date',
  },
  pl: {
    title: 'Panel administracyjny',
    subtitle: 'Przegląd systemu Solar Forecast API',
    loadFailed: 'Nie udało się załadować dashboardu',
    loading: 'Wczytywanie danych…',
    totalClients: 'Całkowita liczba klientów',
    activeApiKeys: 'Aktywne klucze API',
    totalSuffix: '{{n}} łącznie',
    requestsToday: 'Żądania dzisiaj',
    totalRequests: 'Całkowita liczba żądań',
    requestsOverTime: 'Żądania w czasie',
    lastRange: 'Ostatnie {{range}} ({{gran}})',
    h24: '24 godziny',
    daysShort: '{{n}} dni',
    granAuto: 'Auto',
    granMinute: 'Minuty',
    granHour: 'Godziny',
    granDay: 'Dni',
    granWeek: 'Tygodnie',
    rangeLabel: 'Zakres:',
    scaleLabel: 'Skala:',
    topClients: 'Top klienci',
    byRequestCount: 'Według liczby żądań',
    lastDaysParen: '(ostatnie {{n}} dni)',
    allTimeParen: '(cały czas)',
    periodLabel: 'Okres:',
    allTime: 'Cały czas',
    recentActivity: 'Ostatnia aktywność',
    recentActivitySubtitle: '20 ostatnich żądań API',
    noActivity: 'Brak aktywności',
    colClient: 'Klient',
    colLocation: 'Lokalizacja',
    colStrings: 'Stringi',
    colTimeMs: 'Czas (ms)',
    colDate: 'Data',
  },
  de: {
    title: 'Admin-Panel',
    subtitle: 'Systemübersicht der Solar Forecast API',
    loadFailed: 'Dashboard konnte nicht geladen werden',
    loading: 'Daten werden geladen…',
    totalClients: 'Kunden gesamt',
    activeApiKeys: 'Aktive API-Schlüssel',
    totalSuffix: '{{n}} gesamt',
    requestsToday: 'Anfragen heute',
    totalRequests: 'Anfragen gesamt',
    requestsOverTime: 'Anfragen im Zeitverlauf',
    lastRange: 'Letzte {{range}} ({{gran}})',
    h24: '24 Stunden',
    daysShort: '{{n}} Tage',
    granAuto: 'Auto',
    granMinute: 'Minuten',
    granHour: 'Stunden',
    granDay: 'Tage',
    granWeek: 'Wochen',
    rangeLabel: 'Bereich:',
    scaleLabel: 'Skala:',
    topClients: 'Top-Kunden',
    byRequestCount: 'Nach Anfragenanzahl',
    lastDaysParen: '(letzte {{n}} Tage)',
    allTimeParen: '(gesamter Zeitraum)',
    periodLabel: 'Zeitraum:',
    allTime: 'Gesamter Zeitraum',
    recentActivity: 'Letzte Aktivität',
    recentActivitySubtitle: 'Letzte 20 API-Anfragen',
    noActivity: 'Keine Aktivität',
    colClient: 'Kunde',
    colLocation: 'Standort',
    colStrings: 'Strings',
    colTimeMs: 'Zeit (ms)',
    colDate: 'Datum',
  },
  fr: {
    title: "Panneau d'administration",
    subtitle: "Vue d'ensemble du système Solar Forecast API",
    loadFailed: 'Échec du chargement du tableau de bord',
    loading: 'Chargement des données…',
    totalClients: 'Total des clients',
    activeApiKeys: 'Clés API actives',
    totalSuffix: '{{n}} au total',
    requestsToday: "Requêtes aujourd'hui",
    totalRequests: 'Total des requêtes',
    requestsOverTime: 'Requêtes au fil du temps',
    lastRange: '{{range}} dernières ({{gran}})',
    h24: '24 heures',
    daysShort: '{{n}} jours',
    granAuto: 'Auto',
    granMinute: 'Minutes',
    granHour: 'Heures',
    granDay: 'Jours',
    granWeek: 'Semaines',
    rangeLabel: 'Plage :',
    scaleLabel: 'Échelle :',
    topClients: 'Meilleurs clients',
    byRequestCount: 'Par nombre de requêtes',
    lastDaysParen: '({{n}} derniers jours)',
    allTimeParen: '(tout le temps)',
    periodLabel: 'Période :',
    allTime: 'Tout le temps',
    recentActivity: 'Activité récente',
    recentActivitySubtitle: '20 dernières requêtes API',
    noActivity: 'Aucune activité',
    colClient: 'Client',
    colLocation: 'Emplacement',
    colStrings: 'Strings',
    colTimeMs: 'Temps (ms)',
    colDate: 'Date',
  },
  es: {
    title: 'Panel de administración',
    subtitle: 'Resumen del sistema Solar Forecast API',
    loadFailed: 'No se pudo cargar el panel',
    loading: 'Cargando datos…',
    totalClients: 'Total de clientes',
    activeApiKeys: 'Claves API activas',
    totalSuffix: '{{n}} en total',
    requestsToday: 'Solicitudes hoy',
    totalRequests: 'Total de solicitudes',
    requestsOverTime: 'Solicitudes a lo largo del tiempo',
    lastRange: 'Últimas {{range}} ({{gran}})',
    h24: '24 horas',
    daysShort: '{{n}} días',
    granAuto: 'Auto',
    granMinute: 'Minutos',
    granHour: 'Horas',
    granDay: 'Días',
    granWeek: 'Semanas',
    rangeLabel: 'Rango:',
    scaleLabel: 'Escala:',
    topClients: 'Mejores clientes',
    byRequestCount: 'Por número de solicitudes',
    lastDaysParen: '(últimos {{n}} días)',
    allTimeParen: '(todo el tiempo)',
    periodLabel: 'Período:',
    allTime: 'Todo el tiempo',
    recentActivity: 'Actividad reciente',
    recentActivitySubtitle: 'Últimas 20 solicitudes API',
    noActivity: 'Sin actividad',
    colClient: 'Cliente',
    colLocation: 'Ubicación',
    colStrings: 'Strings',
    colTimeMs: 'Tiempo (ms)',
    colDate: 'Fecha',
  },
  it: {
    title: 'Pannello di amministrazione',
    subtitle: 'Panoramica del sistema Solar Forecast API',
    loadFailed: 'Impossibile caricare la dashboard',
    loading: 'Caricamento dati…',
    totalClients: 'Totale clienti',
    activeApiKeys: 'Chiavi API attive',
    totalSuffix: '{{n}} in totale',
    requestsToday: 'Richieste oggi',
    totalRequests: 'Totale richieste',
    requestsOverTime: 'Richieste nel tempo',
    lastRange: 'Ultime {{range}} ({{gran}})',
    h24: '24 ore',
    daysShort: '{{n}} giorni',
    granAuto: 'Auto',
    granMinute: 'Minuti',
    granHour: 'Ore',
    granDay: 'Giorni',
    granWeek: 'Settimane',
    rangeLabel: 'Intervallo:',
    scaleLabel: 'Scala:',
    topClients: 'Migliori clienti',
    byRequestCount: 'Per numero di richieste',
    lastDaysParen: '(ultimi {{n}} giorni)',
    allTimeParen: '(sempre)',
    periodLabel: 'Periodo:',
    allTime: 'Sempre',
    recentActivity: 'Attività recente',
    recentActivitySubtitle: 'Ultime 20 richieste API',
    noActivity: 'Nessuna attività',
    colClient: 'Cliente',
    colLocation: 'Posizione',
    colStrings: 'Strings',
    colTimeMs: 'Tempo (ms)',
    colDate: 'Data',
  },
  pt: {
    title: 'Painel de administração',
    subtitle: 'Visão geral do sistema Solar Forecast API',
    loadFailed: 'Não foi possível carregar o painel',
    loading: 'A carregar dados…',
    totalClients: 'Total de clientes',
    activeApiKeys: 'Chaves API ativas',
    totalSuffix: '{{n}} no total',
    requestsToday: 'Pedidos hoje',
    totalRequests: 'Total de pedidos',
    requestsOverTime: 'Pedidos ao longo do tempo',
    lastRange: 'Últimos {{range}} ({{gran}})',
    h24: '24 horas',
    daysShort: '{{n}} dias',
    granAuto: 'Auto',
    granMinute: 'Minutos',
    granHour: 'Horas',
    granDay: 'Dias',
    granWeek: 'Semanas',
    rangeLabel: 'Intervalo:',
    scaleLabel: 'Escala:',
    topClients: 'Melhores clientes',
    byRequestCount: 'Por número de pedidos',
    lastDaysParen: '(últimos {{n}} dias)',
    allTimeParen: '(sempre)',
    periodLabel: 'Período:',
    allTime: 'Sempre',
    recentActivity: 'Atividade recente',
    recentActivitySubtitle: 'Últimos 20 pedidos API',
    noActivity: 'Sem atividade',
    colClient: 'Cliente',
    colLocation: 'Localização',
    colStrings: 'Strings',
    colTimeMs: 'Tempo (ms)',
    colDate: 'Data',
  },
  nl: {
    title: 'Beheerpaneel',
    subtitle: 'Systeemoverzicht Solar Forecast API',
    loadFailed: 'Kan dashboard niet laden',
    loading: 'Gegevens laden…',
    totalClients: 'Totaal klanten',
    activeApiKeys: 'Actieve API-sleutels',
    totalSuffix: '{{n}} totaal',
    requestsToday: 'Verzoeken vandaag',
    totalRequests: 'Totaal verzoeken',
    requestsOverTime: 'Verzoeken in de tijd',
    lastRange: 'Laatste {{range}} ({{gran}})',
    h24: '24 uur',
    daysShort: '{{n}} dagen',
    granAuto: 'Auto',
    granMinute: 'Minuten',
    granHour: 'Uren',
    granDay: 'Dagen',
    granWeek: 'Weken',
    rangeLabel: 'Bereik:',
    scaleLabel: 'Schaal:',
    topClients: 'Topklanten',
    byRequestCount: 'Op aantal verzoeken',
    lastDaysParen: '(laatste {{n}} dagen)',
    allTimeParen: '(altijd)',
    periodLabel: 'Periode:',
    allTime: 'Altijd',
    recentActivity: 'Recente activiteit',
    recentActivitySubtitle: 'Laatste 20 API-verzoeken',
    noActivity: 'Geen activiteit',
    colClient: 'Klant',
    colLocation: 'Locatie',
    colStrings: 'Strings',
    colTimeMs: 'Tijd (ms)',
    colDate: 'Datum',
  },
  da: {
    title: 'Adminpanel',
    subtitle: 'Systemoversigt for Solar Forecast API',
    loadFailed: 'Kunne ikke indlæse dashboardet',
    loading: 'Indlæser data…',
    totalClients: 'Kunder i alt',
    activeApiKeys: 'Aktive API-nøgler',
    totalSuffix: '{{n}} i alt',
    requestsToday: 'Anmodninger i dag',
    totalRequests: 'Anmodninger i alt',
    requestsOverTime: 'Anmodninger over tid',
    lastRange: 'Seneste {{range}} ({{gran}})',
    h24: '24 timer',
    daysShort: '{{n}} dage',
    granAuto: 'Auto',
    granMinute: 'Minutter',
    granHour: 'Timer',
    granDay: 'Dage',
    granWeek: 'Uger',
    rangeLabel: 'Interval:',
    scaleLabel: 'Skala:',
    topClients: 'Topkunder',
    byRequestCount: 'Efter antal anmodninger',
    lastDaysParen: '(seneste {{n}} dage)',
    allTimeParen: '(hele tiden)',
    periodLabel: 'Periode:',
    allTime: 'Hele tiden',
    recentActivity: 'Seneste aktivitet',
    recentActivitySubtitle: 'Seneste 20 API-anmodninger',
    noActivity: 'Ingen aktivitet',
    colClient: 'Kunde',
    colLocation: 'Placering',
    colStrings: 'Strings',
    colTimeMs: 'Tid (ms)',
    colDate: 'Dato',
  },
  sv: {
    title: 'Adminpanel',
    subtitle: 'Systemöversikt för Solar Forecast API',
    loadFailed: 'Kunde inte ladda instrumentpanelen',
    loading: 'Laddar data…',
    totalClients: 'Totalt antal kunder',
    activeApiKeys: 'Aktiva API-nycklar',
    totalSuffix: '{{n}} totalt',
    requestsToday: 'Förfrågningar idag',
    totalRequests: 'Totalt antal förfrågningar',
    requestsOverTime: 'Förfrågningar över tid',
    lastRange: 'Senaste {{range}} ({{gran}})',
    h24: '24 timmar',
    daysShort: '{{n}} dagar',
    granAuto: 'Auto',
    granMinute: 'Minuter',
    granHour: 'Timmar',
    granDay: 'Dagar',
    granWeek: 'Veckor',
    rangeLabel: 'Intervall:',
    scaleLabel: 'Skala:',
    topClients: 'Toppkunder',
    byRequestCount: 'Efter antal förfrågningar',
    lastDaysParen: '(senaste {{n}} dagarna)',
    allTimeParen: '(hela tiden)',
    periodLabel: 'Period:',
    allTime: 'Hela tiden',
    recentActivity: 'Senaste aktivitet',
    recentActivitySubtitle: 'Senaste 20 API-förfrågningarna',
    noActivity: 'Ingen aktivitet',
    colClient: 'Kund',
    colLocation: 'Plats',
    colStrings: 'Strings',
    colTimeMs: 'Tid (ms)',
    colDate: 'Datum',
  },
  fi: {
    title: 'Hallintapaneeli',
    subtitle: 'Solar Forecast API -järjestelmän yleiskatsaus',
    loadFailed: 'Hallintapaneelin lataaminen epäonnistui',
    loading: 'Ladataan tietoja…',
    totalClients: 'Asiakkaita yhteensä',
    activeApiKeys: 'Aktiiviset API-avaimet',
    totalSuffix: '{{n}} yhteensä',
    requestsToday: 'Pyyntöjä tänään',
    totalRequests: 'Pyyntöjä yhteensä',
    requestsOverTime: 'Pyynnöt ajan myötä',
    lastRange: 'Viimeiset {{range}} ({{gran}})',
    h24: '24 tuntia',
    daysShort: '{{n}} päivää',
    granAuto: 'Auto',
    granMinute: 'Minuutit',
    granHour: 'Tunnit',
    granDay: 'Päivät',
    granWeek: 'Viikot',
    rangeLabel: 'Alue:',
    scaleLabel: 'Asteikko:',
    topClients: 'Suurimmat asiakkaat',
    byRequestCount: 'Pyyntöjen määrän mukaan',
    lastDaysParen: '(viimeiset {{n}} päivää)',
    allTimeParen: '(koko ajalta)',
    periodLabel: 'Jakso:',
    allTime: 'Koko ajalta',
    recentActivity: 'Viimeaikainen toiminta',
    recentActivitySubtitle: 'Viimeiset 20 API-pyyntöä',
    noActivity: 'Ei toimintaa',
    colClient: 'Asiakas',
    colLocation: 'Sijainti',
    colStrings: 'Stringit',
    colTimeMs: 'Aika (ms)',
    colDate: 'Päivä',
  },
  cs: {
    title: 'Administrační panel',
    subtitle: 'Přehled systému Solar Forecast API',
    loadFailed: 'Nepodařilo se načíst dashboard',
    loading: 'Načítání dat…',
    totalClients: 'Celkem klientů',
    activeApiKeys: 'Aktivní API klíče',
    totalSuffix: '{{n}} celkem',
    requestsToday: 'Požadavky dnes',
    totalRequests: 'Celkem požadavků',
    requestsOverTime: 'Požadavky v čase',
    lastRange: 'Posledních {{range}} ({{gran}})',
    h24: '24 hodin',
    daysShort: '{{n}} dní',
    granAuto: 'Auto',
    granMinute: 'Minuty',
    granHour: 'Hodiny',
    granDay: 'Dny',
    granWeek: 'Týdny',
    rangeLabel: 'Rozsah:',
    scaleLabel: 'Měřítko:',
    topClients: 'Top klienti',
    byRequestCount: 'Podle počtu požadavků',
    lastDaysParen: '(posledních {{n}} dní)',
    allTimeParen: '(celá doba)',
    periodLabel: 'Období:',
    allTime: 'Celá doba',
    recentActivity: 'Nedávná aktivita',
    recentActivitySubtitle: 'Posledních 20 API požadavků',
    noActivity: 'Žádná aktivita',
    colClient: 'Klient',
    colLocation: 'Poloha',
    colStrings: 'Stringy',
    colTimeMs: 'Čas (ms)',
    colDate: 'Datum',
  },
  sk: {
    title: 'Administračný panel',
    subtitle: 'Prehľad systému Solar Forecast API',
    loadFailed: 'Nepodarilo sa načítať dashboard',
    loading: 'Načítavanie údajov…',
    totalClients: 'Celkom klientov',
    activeApiKeys: 'Aktívne API kľúče',
    totalSuffix: '{{n}} celkom',
    requestsToday: 'Požiadavky dnes',
    totalRequests: 'Celkom požiadaviek',
    requestsOverTime: 'Požiadavky v čase',
    lastRange: 'Posledných {{range}} ({{gran}})',
    h24: '24 hodín',
    daysShort: '{{n}} dní',
    granAuto: 'Auto',
    granMinute: 'Minúty',
    granHour: 'Hodiny',
    granDay: 'Dni',
    granWeek: 'Týždne',
    rangeLabel: 'Rozsah:',
    scaleLabel: 'Mierka:',
    topClients: 'Top klienti',
    byRequestCount: 'Podľa počtu požiadaviek',
    lastDaysParen: '(posledných {{n}} dní)',
    allTimeParen: '(celý čas)',
    periodLabel: 'Obdobie:',
    allTime: 'Celý čas',
    recentActivity: 'Nedávna aktivita',
    recentActivitySubtitle: 'Posledných 20 API požiadaviek',
    noActivity: 'Žiadna aktivita',
    colClient: 'Klient',
    colLocation: 'Poloha',
    colStrings: 'Stringy',
    colTimeMs: 'Čas (ms)',
    colDate: 'Dátum',
  },
  sl: {
    title: 'Skrbniška plošča',
    subtitle: 'Pregled sistema Solar Forecast API',
    loadFailed: 'Nadzorne plošče ni bilo mogoče naložiti',
    loading: 'Nalaganje podatkov…',
    totalClients: 'Skupaj strank',
    activeApiKeys: 'Aktivni ključi API',
    totalSuffix: '{{n}} skupaj',
    requestsToday: 'Zahteve danes',
    totalRequests: 'Skupaj zahtev',
    requestsOverTime: 'Zahteve skozi čas',
    lastRange: 'Zadnjih {{range}} ({{gran}})',
    h24: '24 ur',
    daysShort: '{{n}} dni',
    granAuto: 'Samodejno',
    granMinute: 'Minute',
    granHour: 'Ure',
    granDay: 'Dnevi',
    granWeek: 'Tedni',
    rangeLabel: 'Obseg:',
    scaleLabel: 'Merilo:',
    topClients: 'Najboljše stranke',
    byRequestCount: 'Po številu zahtev',
    lastDaysParen: '(zadnjih {{n}} dni)',
    allTimeParen: '(ves čas)',
    periodLabel: 'Obdobje:',
    allTime: 'Ves čas',
    recentActivity: 'Nedavna dejavnost',
    recentActivitySubtitle: 'Zadnjih 20 zahtev API',
    noActivity: 'Ni dejavnosti',
    colClient: 'Stranka',
    colLocation: 'Lokacija',
    colStrings: 'Stringi',
    colTimeMs: 'Čas (ms)',
    colDate: 'Datum',
  },
  hr: {
    title: 'Administracijska ploča',
    subtitle: 'Pregled sustava Solar Forecast API',
    loadFailed: 'Nije moguće učitati nadzornu ploču',
    loading: 'Učitavanje podataka…',
    totalClients: 'Ukupno klijenata',
    activeApiKeys: 'Aktivni API ključevi',
    totalSuffix: '{{n}} ukupno',
    requestsToday: 'Zahtjevi danas',
    totalRequests: 'Ukupno zahtjeva',
    requestsOverTime: 'Zahtjevi tijekom vremena',
    lastRange: 'Zadnjih {{range}} ({{gran}})',
    h24: '24 sata',
    daysShort: '{{n}} dana',
    granAuto: 'Automatski',
    granMinute: 'Minute',
    granHour: 'Sati',
    granDay: 'Dani',
    granWeek: 'Tjedni',
    rangeLabel: 'Raspon:',
    scaleLabel: 'Mjerilo:',
    topClients: 'Najbolji klijenti',
    byRequestCount: 'Po broju zahtjeva',
    lastDaysParen: '(zadnjih {{n}} dana)',
    allTimeParen: '(cijelo vrijeme)',
    periodLabel: 'Razdoblje:',
    allTime: 'Cijelo vrijeme',
    recentActivity: 'Nedavna aktivnost',
    recentActivitySubtitle: 'Zadnjih 20 API zahtjeva',
    noActivity: 'Nema aktivnosti',
    colClient: 'Klijent',
    colLocation: 'Lokacija',
    colStrings: 'Stringovi',
    colTimeMs: 'Vrijeme (ms)',
    colDate: 'Datum',
  },
  hu: {
    title: 'Adminisztrációs panel',
    subtitle: 'A Solar Forecast API rendszer áttekintése',
    loadFailed: 'Nem sikerült betölteni az irányítópultot',
    loading: 'Adatok betöltése…',
    totalClients: 'Összes ügyfél',
    activeApiKeys: 'Aktív API-kulcsok',
    totalSuffix: '{{n}} összesen',
    requestsToday: 'Mai kérések',
    totalRequests: 'Összes kérés',
    requestsOverTime: 'Kérések az idő függvényében',
    lastRange: 'Utolsó {{range}} ({{gran}})',
    h24: '24 óra',
    daysShort: '{{n}} nap',
    granAuto: 'Auto',
    granMinute: 'Percek',
    granHour: 'Órák',
    granDay: 'Napok',
    granWeek: 'Hetek',
    rangeLabel: 'Tartomány:',
    scaleLabel: 'Skála:',
    topClients: 'Top ügyfelek',
    byRequestCount: 'Kérések száma szerint',
    lastDaysParen: '(utolsó {{n}} nap)',
    allTimeParen: '(teljes időszak)',
    periodLabel: 'Időszak:',
    allTime: 'Teljes időszak',
    recentActivity: 'Legutóbbi tevékenység',
    recentActivitySubtitle: 'Utolsó 20 API-kérés',
    noActivity: 'Nincs tevékenység',
    colClient: 'Ügyfél',
    colLocation: 'Hely',
    colStrings: 'Stringek',
    colTimeMs: 'Idő (ms)',
    colDate: 'Dátum',
  },
  ro: {
    title: 'Panou de administrare',
    subtitle: 'Prezentare generală a sistemului Solar Forecast API',
    loadFailed: 'Nu s-a putut încărca panoul',
    loading: 'Se încarcă datele…',
    totalClients: 'Total clienți',
    activeApiKeys: 'Chei API active',
    totalSuffix: '{{n}} în total',
    requestsToday: 'Cereri astăzi',
    totalRequests: 'Total cereri',
    requestsOverTime: 'Cereri în timp',
    lastRange: 'Ultimele {{range}} ({{gran}})',
    h24: '24 de ore',
    daysShort: '{{n}} zile',
    granAuto: 'Auto',
    granMinute: 'Minute',
    granHour: 'Ore',
    granDay: 'Zile',
    granWeek: 'Săptămâni',
    rangeLabel: 'Interval:',
    scaleLabel: 'Scară:',
    topClients: 'Cei mai buni clienți',
    byRequestCount: 'După numărul de cereri',
    lastDaysParen: '(ultimele {{n}} zile)',
    allTimeParen: '(tot timpul)',
    periodLabel: 'Perioadă:',
    allTime: 'Tot timpul',
    recentActivity: 'Activitate recentă',
    recentActivitySubtitle: 'Ultimele 20 de cereri API',
    noActivity: 'Nicio activitate',
    colClient: 'Client',
    colLocation: 'Locație',
    colStrings: 'Stringuri',
    colTimeMs: 'Timp (ms)',
    colDate: 'Data',
  },
  bg: {
    title: 'Администраторски панел',
    subtitle: 'Преглед на системата Solar Forecast API',
    loadFailed: 'Неуспешно зареждане на таблото',
    loading: 'Зареждане на данни…',
    totalClients: 'Общо клиенти',
    activeApiKeys: 'Активни API ключове',
    totalSuffix: '{{n}} общо',
    requestsToday: 'Заявки днес',
    totalRequests: 'Общо заявки',
    requestsOverTime: 'Заявки във времето',
    lastRange: 'Последните {{range}} ({{gran}})',
    h24: '24 часа',
    daysShort: '{{n}} дни',
    granAuto: 'Авто',
    granMinute: 'Минути',
    granHour: 'Часове',
    granDay: 'Дни',
    granWeek: 'Седмици',
    rangeLabel: 'Диапазон:',
    scaleLabel: 'Мащаб:',
    topClients: 'Топ клиенти',
    byRequestCount: 'По брой заявки',
    lastDaysParen: '(последните {{n}} дни)',
    allTimeParen: '(за цялото време)',
    periodLabel: 'Период:',
    allTime: 'За цялото време',
    recentActivity: 'Скорошна активност',
    recentActivitySubtitle: 'Последните 20 API заявки',
    noActivity: 'Няма активност',
    colClient: 'Клиент',
    colLocation: 'Местоположение',
    colStrings: 'Стрингове',
    colTimeMs: 'Време (ms)',
    colDate: 'Дата',
  },
  el: {
    title: 'Πίνακας διαχείρισης',
    subtitle: 'Επισκόπηση συστήματος Solar Forecast API',
    loadFailed: 'Αποτυχία φόρτωσης του πίνακα ελέγχου',
    loading: 'Φόρτωση δεδομένων…',
    totalClients: 'Σύνολο πελατών',
    activeApiKeys: 'Ενεργά κλειδιά API',
    totalSuffix: '{{n}} συνολικά',
    requestsToday: 'Αιτήματα σήμερα',
    totalRequests: 'Σύνολο αιτημάτων',
    requestsOverTime: 'Αιτήματα με την πάροδο του χρόνου',
    lastRange: 'Τελευταίες {{range}} ({{gran}})',
    h24: '24 ώρες',
    daysShort: '{{n}} ημέρες',
    granAuto: 'Αυτόματο',
    granMinute: 'Λεπτά',
    granHour: 'Ώρες',
    granDay: 'Ημέρες',
    granWeek: 'Εβδομάδες',
    rangeLabel: 'Εύρος:',
    scaleLabel: 'Κλίμακα:',
    topClients: 'Κορυφαίοι πελάτες',
    byRequestCount: 'Κατά αριθμό αιτημάτων',
    lastDaysParen: '(τελευταίες {{n}} ημέρες)',
    allTimeParen: '(όλος ο χρόνος)',
    periodLabel: 'Περίοδος:',
    allTime: 'Όλος ο χρόνος',
    recentActivity: 'Πρόσφατη δραστηριότητα',
    recentActivitySubtitle: 'Τελευταία 20 αιτήματα API',
    noActivity: 'Καμία δραστηριότητα',
    colClient: 'Πελάτης',
    colLocation: 'Τοποθεσία',
    colStrings: 'Strings',
    colTimeMs: 'Χρόνος (ms)',
    colDate: 'Ημερομηνία',
  },
  et: {
    title: 'Halduspaneel',
    subtitle: 'Solar Forecast API süsteemi ülevaade',
    loadFailed: 'Töölaua laadimine ebaõnnestus',
    loading: 'Andmete laadimine…',
    totalClients: 'Kliente kokku',
    activeApiKeys: 'Aktiivsed API-võtmed',
    totalSuffix: '{{n}} kokku',
    requestsToday: 'Päringuid täna',
    totalRequests: 'Päringuid kokku',
    requestsOverTime: 'Päringud aja jooksul',
    lastRange: 'Viimased {{range}} ({{gran}})',
    h24: '24 tundi',
    daysShort: '{{n}} päeva',
    granAuto: 'Automaatne',
    granMinute: 'Minutid',
    granHour: 'Tunnid',
    granDay: 'Päevad',
    granWeek: 'Nädalad',
    rangeLabel: 'Vahemik:',
    scaleLabel: 'Skaala:',
    topClients: 'Tippkliendid',
    byRequestCount: 'Päringute arvu järgi',
    lastDaysParen: '(viimased {{n}} päeva)',
    allTimeParen: '(kogu aeg)',
    periodLabel: 'Periood:',
    allTime: 'Kogu aeg',
    recentActivity: 'Hiljutine tegevus',
    recentActivitySubtitle: 'Viimased 20 API-päringut',
    noActivity: 'Tegevus puudub',
    colClient: 'Klient',
    colLocation: 'Asukoht',
    colStrings: 'Stringid',
    colTimeMs: 'Aeg (ms)',
    colDate: 'Kuupäev',
  },
  lv: {
    title: 'Administrēšanas panelis',
    subtitle: 'Solar Forecast API sistēmas pārskats',
    loadFailed: 'Neizdevās ielādēt informācijas paneli',
    loading: 'Ielādē datus…',
    totalClients: 'Kopā klientu',
    activeApiKeys: 'Aktīvās API atslēgas',
    totalSuffix: '{{n}} kopā',
    requestsToday: 'Pieprasījumi šodien',
    totalRequests: 'Kopā pieprasījumu',
    requestsOverTime: 'Pieprasījumi laika gaitā',
    lastRange: 'Pēdējās {{range}} ({{gran}})',
    h24: '24 stundas',
    daysShort: '{{n}} dienas',
    granAuto: 'Automātiski',
    granMinute: 'Minūtes',
    granHour: 'Stundas',
    granDay: 'Dienas',
    granWeek: 'Nedēļas',
    rangeLabel: 'Diapazons:',
    scaleLabel: 'Mērogs:',
    topClients: 'Labākie klienti',
    byRequestCount: 'Pēc pieprasījumu skaita',
    lastDaysParen: '(pēdējās {{n}} dienas)',
    allTimeParen: '(visu laiku)',
    periodLabel: 'Periods:',
    allTime: 'Visu laiku',
    recentActivity: 'Nesenā aktivitāte',
    recentActivitySubtitle: 'Pēdējie 20 API pieprasījumi',
    noActivity: 'Nav aktivitātes',
    colClient: 'Klients',
    colLocation: 'Atrašanās vieta',
    colStrings: 'Stringi',
    colTimeMs: 'Laiks (ms)',
    colDate: 'Datums',
  },
  lt: {
    title: 'Administravimo skydelis',
    subtitle: 'Solar Forecast API sistemos apžvalga',
    loadFailed: 'Nepavyko įkelti skydelio',
    loading: 'Įkeliami duomenys…',
    totalClients: 'Iš viso klientų',
    activeApiKeys: 'Aktyvūs API raktai',
    totalSuffix: '{{n}} iš viso',
    requestsToday: 'Užklausos šiandien',
    totalRequests: 'Iš viso užklausų',
    requestsOverTime: 'Užklausos laikui bėgant',
    lastRange: 'Paskutinės {{range}} ({{gran}})',
    h24: '24 valandos',
    daysShort: '{{n}} d.',
    granAuto: 'Automatinis',
    granMinute: 'Minutės',
    granHour: 'Valandos',
    granDay: 'Dienos',
    granWeek: 'Savaitės',
    rangeLabel: 'Intervalas:',
    scaleLabel: 'Mastelis:',
    topClients: 'Geriausi klientai',
    byRequestCount: 'Pagal užklausų skaičių',
    lastDaysParen: '(paskutinės {{n}} d.)',
    allTimeParen: '(visą laiką)',
    periodLabel: 'Laikotarpis:',
    allTime: 'Visą laiką',
    recentActivity: 'Naujausia veikla',
    recentActivitySubtitle: 'Paskutinės 20 API užklausų',
    noActivity: 'Veiklos nėra',
    colClient: 'Klientas',
    colLocation: 'Vieta',
    colStrings: 'Stringai',
    colTimeMs: 'Laikas (ms)',
    colDate: 'Data',
  },
  mt: {
    title: 'Panell tal-amministrazzjoni',
    subtitle: 'Ħarsa ġenerali tas-sistema Solar Forecast API',
    loadFailed: 'Ma rnexxiex jiġi mgħobbi d-dashboard',
    loading: 'Qed jitgħabbew id-data…',
    totalClients: 'Total klijenti',
    activeApiKeys: 'Ċwievet API attivi',
    totalSuffix: "{{n}} b'kollox",
    requestsToday: 'Talbiet illum',
    totalRequests: 'Total talbiet',
    requestsOverTime: 'Talbiet maż-żmien',
    lastRange: 'L-aħħar {{range}} ({{gran}})',
    h24: '24 siegħa',
    daysShort: '{{n}} ijiem',
    granAuto: 'Awto',
    granMinute: 'Minuti',
    granHour: 'Sigħat',
    granDay: 'Ijiem',
    granWeek: 'Ġimgħat',
    rangeLabel: 'Firxa:',
    scaleLabel: 'Skala:',
    topClients: 'L-aqwa klijenti',
    byRequestCount: "Skont l-għadd ta' talbiet",
    lastDaysParen: '(l-aħħar {{n}} ijiem)',
    allTimeParen: '(il-ħin kollu)',
    periodLabel: 'Perjodu:',
    allTime: 'Il-ħin kollu',
    recentActivity: 'Attività reċenti',
    recentActivitySubtitle: 'L-aħħar 20 talba API',
    noActivity: 'Ebda attività',
    colClient: 'Klijent',
    colLocation: 'Post',
    colStrings: 'Strings',
    colTimeMs: 'Ħin (ms)',
    colDate: 'Data',
  },
  ga: {
    title: 'Painéal riaracháin',
    subtitle: 'Forléargas ar an gcóras Solar Forecast API',
    loadFailed: 'Theip ar an bpainéal a luchtú',
    loading: 'Sonraí á luchtú…',
    totalClients: 'Cliaint san iomlán',
    activeApiKeys: 'Eochracha API gníomhacha',
    totalSuffix: '{{n}} san iomlán',
    requestsToday: 'Iarratais inniu',
    totalRequests: 'Iarratais san iomlán',
    requestsOverTime: 'Iarratais le himeacht ama',
    lastRange: '{{range}} dheireanacha ({{gran}})',
    h24: '24 uair',
    daysShort: '{{n}} lá',
    granAuto: 'Uathoibríoch',
    granMinute: 'Nóiméid',
    granHour: 'Uaireanta',
    granDay: 'Laethanta',
    granWeek: 'Seachtainí',
    rangeLabel: 'Raon:',
    scaleLabel: 'Scála:',
    topClients: 'Príomhchliaint',
    byRequestCount: 'De réir líon na n-iarratas',
    lastDaysParen: '({{n}} lá dheireanacha)',
    allTimeParen: '(an t-am ar fad)',
    periodLabel: 'Tréimhse:',
    allTime: 'An t-am ar fad',
    recentActivity: 'Gníomhaíocht le déanaí',
    recentActivitySubtitle: 'Na 20 iarratas API is déanaí',
    noActivity: 'Gan ghníomhaíocht',
    colClient: 'Cliant',
    colLocation: 'Suíomh',
    colStrings: 'Strings',
    colTimeMs: 'Am (ms)',
    colDate: 'Dáta',
  },
  no: {
    title: 'Administrasjonspanel',
    subtitle: 'Systemoversikt for Solar Forecast API',
    loadFailed: 'Kunne ikke laste dashbordet',
    loading: 'Laster data…',
    totalClients: 'Totalt antall kunder',
    activeApiKeys: 'Aktive API-nøkler',
    totalSuffix: '{{n}} totalt',
    requestsToday: 'Forespørsler i dag',
    totalRequests: 'Totalt antall forespørsler',
    requestsOverTime: 'Forespørsler over tid',
    lastRange: 'Siste {{range}} ({{gran}})',
    h24: '24 timer',
    daysShort: '{{n}} dager',
    granAuto: 'Auto',
    granMinute: 'Minutter',
    granHour: 'Timer',
    granDay: 'Dager',
    granWeek: 'Uker',
    rangeLabel: 'Intervall:',
    scaleLabel: 'Skala:',
    topClients: 'Toppkunder',
    byRequestCount: 'Etter antall forespørsler',
    lastDaysParen: '(siste {{n}} dager)',
    allTimeParen: '(hele tiden)',
    periodLabel: 'Periode:',
    allTime: 'Hele tiden',
    recentActivity: 'Nylig aktivitet',
    recentActivitySubtitle: 'Siste 20 API-forespørsler',
    noActivity: 'Ingen aktivitet',
    colClient: 'Kunde',
    colLocation: 'Plassering',
    colStrings: 'Strings',
    colTimeMs: 'Tid (ms)',
    colDate: 'Dato',
  },
  is: {
    title: 'Stjórnborð',
    subtitle: 'Yfirlit yfir Solar Forecast API kerfið',
    loadFailed: 'Ekki tókst að hlaða stjórnborðið',
    loading: 'Hleð gögn…',
    totalClients: 'Viðskiptavinir alls',
    activeApiKeys: 'Virkir API-lyklar',
    totalSuffix: '{{n}} alls',
    requestsToday: 'Beiðnir í dag',
    totalRequests: 'Beiðnir alls',
    requestsOverTime: 'Beiðnir yfir tíma',
    lastRange: 'Síðustu {{range}} ({{gran}})',
    h24: '24 klukkustundir',
    daysShort: '{{n}} dagar',
    granAuto: 'Sjálfvirkt',
    granMinute: 'Mínútur',
    granHour: 'Klukkustundir',
    granDay: 'Dagar',
    granWeek: 'Vikur',
    rangeLabel: 'Svið:',
    scaleLabel: 'Kvarði:',
    topClients: 'Helstu viðskiptavinir',
    byRequestCount: 'Eftir fjölda beiðna',
    lastDaysParen: '(síðustu {{n}} dagar)',
    allTimeParen: '(allan tímann)',
    periodLabel: 'Tímabil:',
    allTime: 'Allan tímann',
    recentActivity: 'Nýleg virkni',
    recentActivitySubtitle: 'Síðustu 20 API-beiðnir',
    noActivity: 'Engin virkni',
    colClient: 'Viðskiptavinur',
    colLocation: 'Staðsetning',
    colStrings: 'Strings',
    colTimeMs: 'Tími (ms)',
    colDate: 'Dagsetning',
  },
  uk: {
    title: 'Адміністративна панель',
    subtitle: 'Огляд системи Solar Forecast API',
    loadFailed: 'Не вдалося завантажити панель',
    loading: 'Завантаження даних…',
    totalClients: 'Усього клієнтів',
    activeApiKeys: 'Активні ключі API',
    totalSuffix: '{{n}} разом',
    requestsToday: 'Запити сьогодні',
    totalRequests: 'Усього запитів',
    requestsOverTime: 'Запити з часом',
    lastRange: 'Останні {{range}} ({{gran}})',
    h24: '24 години',
    daysShort: '{{n}} днів',
    granAuto: 'Авто',
    granMinute: 'Хвилини',
    granHour: 'Години',
    granDay: 'Дні',
    granWeek: 'Тижні',
    rangeLabel: 'Діапазон:',
    scaleLabel: 'Масштаб:',
    topClients: 'Топ клієнти',
    byRequestCount: 'За кількістю запитів',
    lastDaysParen: '(останні {{n}} днів)',
    allTimeParen: '(увесь час)',
    periodLabel: 'Період:',
    allTime: 'Увесь час',
    recentActivity: 'Нещодавня активність',
    recentActivitySubtitle: 'Останні 20 запитів API',
    noActivity: 'Немає активності',
    colClient: 'Клієнт',
    colLocation: 'Розташування',
    colStrings: 'Стрінги',
    colTimeMs: 'Час (ms)',
    colDate: 'Дата',
  },
  sr: {
    title: 'Администраторски панел',
    subtitle: 'Преглед система Solar Forecast API',
    loadFailed: 'Није могуће учитати контролну таблу',
    loading: 'Учитавање података…',
    totalClients: 'Укупно клијената',
    activeApiKeys: 'Активни API кључеви',
    totalSuffix: '{{n}} укупно',
    requestsToday: 'Захтеви данас',
    totalRequests: 'Укупно захтева',
    requestsOverTime: 'Захтеви током времена',
    lastRange: 'Последњих {{range}} ({{gran}})',
    h24: '24 сата',
    daysShort: '{{n}} дана',
    granAuto: 'Аутоматски',
    granMinute: 'Минути',
    granHour: 'Сати',
    granDay: 'Дани',
    granWeek: 'Недеље',
    rangeLabel: 'Опсег:',
    scaleLabel: 'Размера:',
    topClients: 'Најбољи клијенти',
    byRequestCount: 'По броју захтева',
    lastDaysParen: '(последњих {{n}} дана)',
    allTimeParen: '(све време)',
    periodLabel: 'Период:',
    allTime: 'Све време',
    recentActivity: 'Недавна активност',
    recentActivitySubtitle: 'Последњих 20 API захтева',
    noActivity: 'Нема активности',
    colClient: 'Клијент',
    colLocation: 'Локација',
    colStrings: 'Стрингови',
    colTimeMs: 'Време (ms)',
    colDate: 'Датум',
  },
  sq: {
    title: 'Paneli i administrimit',
    subtitle: 'Pamje e përgjithshme e sistemit Solar Forecast API',
    loadFailed: 'Dështoi ngarkimi i panelit',
    loading: 'Po ngarkohen të dhënat…',
    totalClients: 'Gjithsej klientë',
    activeApiKeys: 'Çelësat API aktivë',
    totalSuffix: '{{n}} gjithsej',
    requestsToday: 'Kërkesa sot',
    totalRequests: 'Gjithsej kërkesa',
    requestsOverTime: 'Kërkesat me kalimin e kohës',
    lastRange: '{{range}} e fundit ({{gran}})',
    h24: '24 orë',
    daysShort: '{{n}} ditë',
    granAuto: 'Auto',
    granMinute: 'Minuta',
    granHour: 'Orë',
    granDay: 'Ditë',
    granWeek: 'Javë',
    rangeLabel: 'Diapazoni:',
    scaleLabel: 'Shkalla:',
    topClients: 'Klientët kryesorë',
    byRequestCount: 'Sipas numrit të kërkesave',
    lastDaysParen: '({{n}} ditët e fundit)',
    allTimeParen: '(gjithë kohën)',
    periodLabel: 'Periudha:',
    allTime: 'Gjithë kohën',
    recentActivity: 'Aktiviteti i fundit',
    recentActivitySubtitle: '20 kërkesat e fundit API',
    noActivity: 'Asnjë aktivitet',
    colClient: 'Klienti',
    colLocation: 'Vendndodhja',
    colStrings: 'Strings',
    colTimeMs: 'Koha (ms)',
    colDate: 'Data',
  },
  mk: {
    title: 'Администраторски панел',
    subtitle: 'Преглед на системот Solar Forecast API',
    loadFailed: 'Не може да се вчита контролната табла',
    loading: 'Се вчитуваат податоци…',
    totalClients: 'Вкупно клиенти',
    activeApiKeys: 'Активни API клучеви',
    totalSuffix: '{{n}} вкупно',
    requestsToday: 'Барања денес',
    totalRequests: 'Вкупно барања',
    requestsOverTime: 'Барања низ времето',
    lastRange: 'Последни {{range}} ({{gran}})',
    h24: '24 часа',
    daysShort: '{{n}} дена',
    granAuto: 'Авто',
    granMinute: 'Минути',
    granHour: 'Часови',
    granDay: 'Дена',
    granWeek: 'Недели',
    rangeLabel: 'Опсег:',
    scaleLabel: 'Размер:',
    topClients: 'Топ клиенти',
    byRequestCount: 'По број на барања',
    lastDaysParen: '(последни {{n}} дена)',
    allTimeParen: '(сето време)',
    periodLabel: 'Период:',
    allTime: 'Сето време',
    recentActivity: 'Неодамнешна активност',
    recentActivitySubtitle: 'Последни 20 API барања',
    noActivity: 'Нема активност',
    colClient: 'Клиент',
    colLocation: 'Локација',
    colStrings: 'Стрингови',
    colTimeMs: 'Време (ms)',
    colDate: 'Датум',
  },
  bs: {
    title: 'Administracijski panel',
    subtitle: 'Pregled sistema Solar Forecast API',
    loadFailed: 'Nije moguće učitati kontrolnu tablu',
    loading: 'Učitavanje podataka…',
    totalClients: 'Ukupno klijenata',
    activeApiKeys: 'Aktivni API ključevi',
    totalSuffix: '{{n}} ukupno',
    requestsToday: 'Zahtjevi danas',
    totalRequests: 'Ukupno zahtjeva',
    requestsOverTime: 'Zahtjevi tokom vremena',
    lastRange: 'Zadnjih {{range}} ({{gran}})',
    h24: '24 sata',
    daysShort: '{{n}} dana',
    granAuto: 'Automatski',
    granMinute: 'Minute',
    granHour: 'Sati',
    granDay: 'Dani',
    granWeek: 'Sedmice',
    rangeLabel: 'Raspon:',
    scaleLabel: 'Razmjera:',
    topClients: 'Najbolji klijenti',
    byRequestCount: 'Po broju zahtjeva',
    lastDaysParen: '(zadnjih {{n}} dana)',
    allTimeParen: '(cijelo vrijeme)',
    periodLabel: 'Period:',
    allTime: 'Cijelo vrijeme',
    recentActivity: 'Nedavna aktivnost',
    recentActivitySubtitle: 'Zadnjih 20 API zahtjeva',
    noActivity: 'Nema aktivnosti',
    colClient: 'Klijent',
    colLocation: 'Lokacija',
    colStrings: 'Stringovi',
    colTimeMs: 'Vrijeme (ms)',
    colDate: 'Datum',
  },
};

const adminTokensTr: Record<string, Dict> = {
  en: {
    title: 'Access tokens',
    intro1: 'Tokens for the Management API (',
    intro2: '). Use the header ',
    intro3: '. The raw value is shown ',
    introOnce: 'only once',
    intro4: ' on creation.',
    saveNow: "Save the token now — we won't show it again:",
    okSaved: 'OK, saved',
    createNew: 'Create a new token',
    namePh: 'Token name (e.g. deploy-script, integration-x)',
    creating: 'Creating…',
    createBtn: 'Create token',
    existing: 'Existing tokens',
    loading: 'Loading…',
    empty: 'No tokens created.',
    colName: 'Name',
    colCreatedBy: 'Created by',
    colCreated: 'Created',
    colLastUsed: 'Last used',
    colStatus: 'Status',
    save: 'Save',
    cancel: 'Cancel',
    statusRevoked: 'revoked',
    statusActive: 'active',
    rename: 'Rename',
    revokeBtn: 'Revoke',
    confirmRevoke:
      'Revoke token "{{name}}"? Revoked tokens are not deletable, only marked as inactive.',
  },
  pl: {
    title: 'Tokeny dostępowe',
    intro1: 'Tokeny do Management API (',
    intro2: '). Używaj nagłówka ',
    intro3: '. Surowa wartość pokazana ',
    introOnce: 'jeden raz',
    intro4: ' przy tworzeniu.',
    saveNow: 'Zapisz token teraz — nie pokażemy go ponownie:',
    okSaved: 'OK, zapisałem',
    createNew: 'Utwórz nowy token',
    namePh: 'Nazwa tokenu (np. deploy-script, integration-x)',
    creating: 'Tworzę…',
    createBtn: 'Utwórz token',
    existing: 'Istniejące tokeny',
    loading: 'Ładowanie…',
    empty: 'Brak utworzonych tokenów.',
    colName: 'Nazwa',
    colCreatedBy: 'Utworzył',
    colCreated: 'Utworzono',
    colLastUsed: 'Ostatnio użyty',
    colStatus: 'Status',
    save: 'Zapisz',
    cancel: 'Anuluj',
    statusRevoked: 'wycofany',
    statusActive: 'aktywny',
    rename: 'Zmień nazwę',
    revokeBtn: 'Wycofaj',
    confirmRevoke:
      'Cofnąć token „{{name}}”? Wycofane tokeny nie są usuwalne, jedynie oznaczane jako nieaktywne.',
  },
  de: {
    title: 'Zugriffstoken',
    intro1: 'Token für die Management-API (',
    intro2: '). Verwenden Sie den Header ',
    intro3: '. Der Rohwert wird ',
    introOnce: 'nur einmal',
    intro4: ' bei der Erstellung angezeigt.',
    saveNow: 'Speichern Sie das Token jetzt — wir zeigen es nicht erneut an:',
    okSaved: 'OK, gespeichert',
    createNew: 'Neues Token erstellen',
    namePh: 'Token-Name (z. B. deploy-script, integration-x)',
    creating: 'Wird erstellt…',
    createBtn: 'Token erstellen',
    existing: 'Vorhandene Token',
    loading: 'Wird geladen…',
    empty: 'Keine Token erstellt.',
    colName: 'Name',
    colCreatedBy: 'Erstellt von',
    colCreated: 'Erstellt',
    colLastUsed: 'Zuletzt verwendet',
    colStatus: 'Status',
    save: 'Speichern',
    cancel: 'Abbrechen',
    statusRevoked: 'widerrufen',
    statusActive: 'aktiv',
    rename: 'Umbenennen',
    revokeBtn: 'Widerrufen',
    confirmRevoke:
      'Token „{{name}}“ widerrufen? Widerrufene Token sind nicht löschbar, sondern werden nur als inaktiv markiert.',
  },
  fr: {
    title: "Jetons d'accès",
    intro1: 'Jetons pour la Management API (',
    intro2: "). Utilisez l'en-tête ",
    intro3: '. La valeur brute est affichée ',
    introOnce: 'une seule fois',
    intro4: ' à la création.',
    saveNow: "Enregistrez le jeton maintenant — nous ne l'afficherons plus :",
    okSaved: "OK, c'est enregistré",
    createNew: 'Créer un nouveau jeton',
    namePh: 'Nom du jeton (ex. deploy-script, integration-x)',
    creating: 'Création…',
    createBtn: 'Créer le jeton',
    existing: 'Jetons existants',
    loading: 'Chargement…',
    empty: 'Aucun jeton créé.',
    colName: 'Nom',
    colCreatedBy: 'Créé par',
    colCreated: 'Créé',
    colLastUsed: 'Dernière utilisation',
    colStatus: 'Statut',
    save: 'Enregistrer',
    cancel: 'Annuler',
    statusRevoked: 'révoqué',
    statusActive: 'actif',
    rename: 'Renommer',
    revokeBtn: 'Révoquer',
    confirmRevoke:
      'Révoquer le jeton « {{name}} » ? Les jetons révoqués ne sont pas supprimables, seulement marqués comme inactifs.',
  },
  es: {
    title: 'Tokens de acceso',
    intro1: 'Tokens para la Management API (',
    intro2: '). Usa el encabezado ',
    intro3: '. El valor en bruto se muestra ',
    introOnce: 'una sola vez',
    intro4: ' al crearlo.',
    saveNow: 'Guarda el token ahora — no volveremos a mostrarlo:',
    okSaved: 'OK, lo guardé',
    createNew: 'Crear un nuevo token',
    namePh: 'Nombre del token (p. ej. deploy-script, integration-x)',
    creating: 'Creando…',
    createBtn: 'Crear token',
    existing: 'Tokens existentes',
    loading: 'Cargando…',
    empty: 'No se han creado tokens.',
    colName: 'Nombre',
    colCreatedBy: 'Creado por',
    colCreated: 'Creado',
    colLastUsed: 'Último uso',
    colStatus: 'Estado',
    save: 'Guardar',
    cancel: 'Cancelar',
    statusRevoked: 'revocado',
    statusActive: 'activo',
    rename: 'Renombrar',
    revokeBtn: 'Revocar',
    confirmRevoke:
      '¿Revocar el token «{{name}}»? Los tokens revocados no se pueden eliminar, solo se marcan como inactivos.',
  },
  it: {
    title: 'Token di accesso',
    intro1: 'Token per la Management API (',
    intro2: "). Usa l'header ",
    intro3: '. Il valore grezzo viene mostrato ',
    introOnce: 'una sola volta',
    intro4: ' alla creazione.',
    saveNow: 'Salva il token ora — non lo mostreremo di nuovo:',
    okSaved: 'OK, salvato',
    createNew: 'Crea un nuovo token',
    namePh: 'Nome del token (es. deploy-script, integration-x)',
    creating: 'Creazione…',
    createBtn: 'Crea token',
    existing: 'Token esistenti',
    loading: 'Caricamento…',
    empty: 'Nessun token creato.',
    colName: 'Nome',
    colCreatedBy: 'Creato da',
    colCreated: 'Creato',
    colLastUsed: 'Ultimo utilizzo',
    colStatus: 'Stato',
    save: 'Salva',
    cancel: 'Annulla',
    statusRevoked: 'revocato',
    statusActive: 'attivo',
    rename: 'Rinomina',
    revokeBtn: 'Revoca',
    confirmRevoke:
      'Revocare il token «{{name}}»? I token revocati non sono eliminabili, solo contrassegnati come inattivi.',
  },
  pt: {
    title: 'Tokens de acesso',
    intro1: 'Tokens para a Management API (',
    intro2: '). Use o cabeçalho ',
    intro3: '. O valor em bruto é mostrado ',
    introOnce: 'apenas uma vez',
    intro4: ' na criação.',
    saveNow: 'Guarde o token agora — não o mostraremos novamente:',
    okSaved: 'OK, guardei',
    createNew: 'Criar um novo token',
    namePh: 'Nome do token (ex. deploy-script, integration-x)',
    creating: 'A criar…',
    createBtn: 'Criar token',
    existing: 'Tokens existentes',
    loading: 'A carregar…',
    empty: 'Nenhum token criado.',
    colName: 'Nome',
    colCreatedBy: 'Criado por',
    colCreated: 'Criado',
    colLastUsed: 'Último uso',
    colStatus: 'Estado',
    save: 'Guardar',
    cancel: 'Cancelar',
    statusRevoked: 'revogado',
    statusActive: 'ativo',
    rename: 'Renomear',
    revokeBtn: 'Revogar',
    confirmRevoke:
      'Revogar o token «{{name}}»? Os tokens revogados não são elimináveis, apenas marcados como inativos.',
  },
  nl: {
    title: 'Toegangstokens',
    intro1: 'Tokens voor de Management API (',
    intro2: '). Gebruik de header ',
    intro3: '. De ruwe waarde wordt ',
    introOnce: 'slechts één keer',
    intro4: ' bij het aanmaken getoond.',
    saveNow: 'Sla het token nu op — we tonen het niet opnieuw:',
    okSaved: 'OK, opgeslagen',
    createNew: 'Een nieuw token aanmaken',
    namePh: 'Tokennaam (bijv. deploy-script, integration-x)',
    creating: 'Aanmaken…',
    createBtn: 'Token aanmaken',
    existing: 'Bestaande tokens',
    loading: 'Laden…',
    empty: 'Geen tokens aangemaakt.',
    colName: 'Naam',
    colCreatedBy: 'Aangemaakt door',
    colCreated: 'Aangemaakt',
    colLastUsed: 'Laatst gebruikt',
    colStatus: 'Status',
    save: 'Opslaan',
    cancel: 'Annuleren',
    statusRevoked: 'ingetrokken',
    statusActive: 'actief',
    rename: 'Naam wijzigen',
    revokeBtn: 'Intrekken',
    confirmRevoke:
      'Token "{{name}}" intrekken? Ingetrokken tokens zijn niet verwijderbaar, alleen als inactief gemarkeerd.',
  },
  da: {
    title: 'Adgangstokens',
    intro1: 'Tokens til Management API (',
    intro2: '). Brug headeren ',
    intro3: '. Den rå værdi vises ',
    introOnce: 'kun én gang',
    intro4: ' ved oprettelse.',
    saveNow: 'Gem tokenet nu — vi viser det ikke igen:',
    okSaved: 'OK, gemt',
    createNew: 'Opret et nyt token',
    namePh: 'Tokennavn (f.eks. deploy-script, integration-x)',
    creating: 'Opretter…',
    createBtn: 'Opret token',
    existing: 'Eksisterende tokens',
    loading: 'Indlæser…',
    empty: 'Ingen tokens oprettet.',
    colName: 'Navn',
    colCreatedBy: 'Oprettet af',
    colCreated: 'Oprettet',
    colLastUsed: 'Sidst brugt',
    colStatus: 'Status',
    save: 'Gem',
    cancel: 'Annuller',
    statusRevoked: 'tilbagekaldt',
    statusActive: 'aktiv',
    rename: 'Omdøb',
    revokeBtn: 'Tilbagekald',
    confirmRevoke:
      'Tilbagekald token "{{name}}"? Tilbagekaldte tokens kan ikke slettes, kun markeres som inaktive.',
  },
  sv: {
    title: 'Åtkomsttokens',
    intro1: 'Tokens för Management API (',
    intro2: '). Använd headern ',
    intro3: '. Råvärdet visas ',
    introOnce: 'endast en gång',
    intro4: ' vid skapandet.',
    saveNow: 'Spara token nu — vi visar det inte igen:',
    okSaved: 'OK, sparat',
    createNew: 'Skapa ett nytt token',
    namePh: 'Tokennamn (t.ex. deploy-script, integration-x)',
    creating: 'Skapar…',
    createBtn: 'Skapa token',
    existing: 'Befintliga tokens',
    loading: 'Laddar…',
    empty: 'Inga tokens skapade.',
    colName: 'Namn',
    colCreatedBy: 'Skapad av',
    colCreated: 'Skapad',
    colLastUsed: 'Senast använd',
    colStatus: 'Status',
    save: 'Spara',
    cancel: 'Avbryt',
    statusRevoked: 'återkallad',
    statusActive: 'aktiv',
    rename: 'Byt namn',
    revokeBtn: 'Återkalla',
    confirmRevoke:
      'Återkalla token "{{name}}"? Återkallade tokens kan inte tas bort, bara markeras som inaktiva.',
  },
  fi: {
    title: 'Käyttötunnukset',
    intro1: 'Management API:n tunnukset (',
    intro2: '). Käytä otsikkoa ',
    intro3: '. Raaka-arvo näytetään ',
    introOnce: 'vain kerran',
    intro4: ' luonnin yhteydessä.',
    saveNow: 'Tallenna tunnus nyt — emme näytä sitä uudelleen:',
    okSaved: 'OK, tallennettu',
    createNew: 'Luo uusi tunnus',
    namePh: 'Tunnuksen nimi (esim. deploy-script, integration-x)',
    creating: 'Luodaan…',
    createBtn: 'Luo tunnus',
    existing: 'Olemassa olevat tunnukset',
    loading: 'Ladataan…',
    empty: 'Tunnuksia ei ole luotu.',
    colName: 'Nimi',
    colCreatedBy: 'Luonut',
    colCreated: 'Luotu',
    colLastUsed: 'Viimeksi käytetty',
    colStatus: 'Tila',
    save: 'Tallenna',
    cancel: 'Peruuta',
    statusRevoked: 'peruutettu',
    statusActive: 'aktiivinen',
    rename: 'Nimeä uudelleen',
    revokeBtn: 'Peruuta',
    confirmRevoke:
      'Peruutetaanko tunnus "{{name}}"? Peruutettuja tunnuksia ei voi poistaa, ne vain merkitään ei-aktiivisiksi.',
  },
  cs: {
    title: 'Přístupové tokeny',
    intro1: 'Tokeny pro Management API (',
    intro2: '). Použijte hlavičku ',
    intro3: '. Surová hodnota se zobrazí ',
    introOnce: 'pouze jednou',
    intro4: ' při vytvoření.',
    saveNow: 'Uložte token hned — znovu jej nezobrazíme:',
    okSaved: 'OK, uloženo',
    createNew: 'Vytvořit nový token',
    namePh: 'Název tokenu (např. deploy-script, integration-x)',
    creating: 'Vytváření…',
    createBtn: 'Vytvořit token',
    existing: 'Existující tokeny',
    loading: 'Načítání…',
    empty: 'Žádné tokeny nevytvořeny.',
    colName: 'Název',
    colCreatedBy: 'Vytvořil',
    colCreated: 'Vytvořeno',
    colLastUsed: 'Naposledy použit',
    colStatus: 'Stav',
    save: 'Uložit',
    cancel: 'Zrušit',
    statusRevoked: 'odvolán',
    statusActive: 'aktivní',
    rename: 'Přejmenovat',
    revokeBtn: 'Odvolat',
    confirmRevoke:
      'Odvolat token „{{name}}“? Odvolané tokeny nelze smazat, pouze označit jako neaktivní.',
  },
  sk: {
    title: 'Prístupové tokeny',
    intro1: 'Tokeny pre Management API (',
    intro2: '). Použite hlavičku ',
    intro3: '. Surová hodnota sa zobrazí ',
    introOnce: 'iba raz',
    intro4: ' pri vytvorení.',
    saveNow: 'Uložte token hneď — znova ho nezobrazíme:',
    okSaved: 'OK, uložené',
    createNew: 'Vytvoriť nový token',
    namePh: 'Názov tokenu (napr. deploy-script, integration-x)',
    creating: 'Vytváranie…',
    createBtn: 'Vytvoriť token',
    existing: 'Existujúce tokeny',
    loading: 'Načítava sa…',
    empty: 'Žiadne tokeny nevytvorené.',
    colName: 'Názov',
    colCreatedBy: 'Vytvoril',
    colCreated: 'Vytvorené',
    colLastUsed: 'Naposledy použitý',
    colStatus: 'Stav',
    save: 'Uložiť',
    cancel: 'Zrušiť',
    statusRevoked: 'odvolaný',
    statusActive: 'aktívny',
    rename: 'Premenovať',
    revokeBtn: 'Odvolať',
    confirmRevoke:
      'Odvolať token „{{name}}“? Odvolané tokeny nie je možné zmazať, len označiť ako neaktívne.',
  },
  sl: {
    title: 'Dostopni žetoni',
    intro1: 'Žetoni za Management API (',
    intro2: '). Uporabite glavo ',
    intro3: '. Surova vrednost je prikazana ',
    introOnce: 'samo enkrat',
    intro4: ' ob ustvarjanju.',
    saveNow: 'Žeton shranite zdaj — ne bomo ga več prikazali:',
    okSaved: 'V redu, shranjeno',
    createNew: 'Ustvari nov žeton',
    namePh: 'Ime žetona (npr. deploy-script, integration-x)',
    creating: 'Ustvarjanje…',
    createBtn: 'Ustvari žeton',
    existing: 'Obstoječi žetoni',
    loading: 'Nalaganje…',
    empty: 'Ustvarjenih ni žetonov.',
    colName: 'Ime',
    colCreatedBy: 'Ustvaril',
    colCreated: 'Ustvarjeno',
    colLastUsed: 'Nazadnje uporabljen',
    colStatus: 'Stanje',
    save: 'Shrani',
    cancel: 'Prekliči',
    statusRevoked: 'preklican',
    statusActive: 'aktiven',
    rename: 'Preimenuj',
    revokeBtn: 'Prekliči',
    confirmRevoke:
      'Prekličem žeton „{{name}}“? Preklicanih žetonov ni mogoče izbrisati, le označiti kot neaktivne.',
  },
  hr: {
    title: 'Pristupni tokeni',
    intro1: 'Tokeni za Management API (',
    intro2: '). Koristite zaglavlje ',
    intro3: '. Sirova vrijednost prikazuje se ',
    introOnce: 'samo jednom',
    intro4: ' pri stvaranju.',
    saveNow: 'Spremite token sada — nećemo ga ponovno prikazati:',
    okSaved: 'U redu, spremljeno',
    createNew: 'Stvori novi token',
    namePh: 'Naziv tokena (npr. deploy-script, integration-x)',
    creating: 'Stvaranje…',
    createBtn: 'Stvori token',
    existing: 'Postojeći tokeni',
    loading: 'Učitavanje…',
    empty: 'Nema stvorenih tokena.',
    colName: 'Naziv',
    colCreatedBy: 'Stvorio',
    colCreated: 'Stvoreno',
    colLastUsed: 'Zadnji put korišten',
    colStatus: 'Status',
    save: 'Spremi',
    cancel: 'Odustani',
    statusRevoked: 'opozvan',
    statusActive: 'aktivan',
    rename: 'Preimenuj',
    revokeBtn: 'Opozovi',
    confirmRevoke:
      'Opozvati token „{{name}}“? Opozvani tokeni ne mogu se izbrisati, samo označiti kao neaktivni.',
  },
  hu: {
    title: 'Hozzáférési tokenek',
    intro1: 'Tokenek a Management API-hoz (',
    intro2: '). Használja a fejlécet ',
    intro3: '. A nyers érték ',
    introOnce: 'csak egyszer',
    intro4: ' jelenik meg a létrehozáskor.',
    saveNow: 'Mentse el a tokent most — többé nem mutatjuk meg:',
    okSaved: 'OK, elmentettem',
    createNew: 'Új token létrehozása',
    namePh: 'Token neve (pl. deploy-script, integration-x)',
    creating: 'Létrehozás…',
    createBtn: 'Token létrehozása',
    existing: 'Meglévő tokenek',
    loading: 'Betöltés…',
    empty: 'Nincsenek létrehozott tokenek.',
    colName: 'Név',
    colCreatedBy: 'Létrehozta',
    colCreated: 'Létrehozva',
    colLastUsed: 'Utoljára használva',
    colStatus: 'Állapot',
    save: 'Mentés',
    cancel: 'Mégse',
    statusRevoked: 'visszavonva',
    statusActive: 'aktív',
    rename: 'Átnevezés',
    revokeBtn: 'Visszavonás',
    confirmRevoke:
      'Visszavonja a(z) „{{name}}” tokent? A visszavont tokenek nem törölhetők, csak inaktívként jelölhetők.',
  },
  ro: {
    title: 'Token-uri de acces',
    intro1: 'Token-uri pentru Management API (',
    intro2: '). Folosiți antetul ',
    intro3: '. Valoarea brută este afișată ',
    introOnce: 'o singură dată',
    intro4: ' la creare.',
    saveNow: 'Salvați token-ul acum — nu îl vom mai afișa:',
    okSaved: 'OK, am salvat',
    createNew: 'Creează un token nou',
    namePh: 'Numele token-ului (ex. deploy-script, integration-x)',
    creating: 'Se creează…',
    createBtn: 'Creează token',
    existing: 'Token-uri existente',
    loading: 'Se încarcă…',
    empty: 'Niciun token creat.',
    colName: 'Nume',
    colCreatedBy: 'Creat de',
    colCreated: 'Creat',
    colLastUsed: 'Ultima utilizare',
    colStatus: 'Stare',
    save: 'Salvează',
    cancel: 'Anulează',
    statusRevoked: 'revocat',
    statusActive: 'activ',
    rename: 'Redenumește',
    revokeBtn: 'Revocă',
    confirmRevoke:
      'Revocați token-ul „{{name}}”? Token-urile revocate nu pot fi șterse, doar marcate ca inactive.',
  },
  bg: {
    title: 'Токени за достъп',
    intro1: 'Токени за Management API (',
    intro2: '). Използвайте заглавката ',
    intro3: '. Суровата стойност се показва ',
    introOnce: 'само веднъж',
    intro4: ' при създаване.',
    saveNow: 'Запазете токена сега — няма да го покажем отново:',
    okSaved: 'ОК, запазих',
    createNew: 'Създаване на нов токен',
    namePh: 'Име на токена (напр. deploy-script, integration-x)',
    creating: 'Създаване…',
    createBtn: 'Създай токен',
    existing: 'Съществуващи токени',
    loading: 'Зареждане…',
    empty: 'Няма създадени токени.',
    colName: 'Име',
    colCreatedBy: 'Създаден от',
    colCreated: 'Създаден',
    colLastUsed: 'Последно използван',
    colStatus: 'Статус',
    save: 'Запази',
    cancel: 'Отказ',
    statusRevoked: 'отменен',
    statusActive: 'активен',
    rename: 'Преименуване',
    revokeBtn: 'Отмени',
    confirmRevoke:
      'Да се отмени ли токенът „{{name}}“? Отменените токени не могат да се изтриват, само се маркират като неактивни.',
  },
  el: {
    title: 'Διακριτικά πρόσβασης',
    intro1: 'Διακριτικά για το Management API (',
    intro2: '). Χρησιμοποιήστε την κεφαλίδα ',
    intro3: '. Η ακατέργαστη τιμή εμφανίζεται ',
    introOnce: 'μόνο μία φορά',
    intro4: ' κατά τη δημιουργία.',
    saveNow: 'Αποθηκεύστε το διακριτικό τώρα — δεν θα το εμφανίσουμε ξανά:',
    okSaved: 'Εντάξει, αποθηκεύτηκε',
    createNew: 'Δημιουργία νέου διακριτικού',
    namePh: 'Όνομα διακριτικού (π.χ. deploy-script, integration-x)',
    creating: 'Δημιουργία…',
    createBtn: 'Δημιουργία διακριτικού',
    existing: 'Υπάρχοντα διακριτικά',
    loading: 'Φόρτωση…',
    empty: 'Δεν δημιουργήθηκαν διακριτικά.',
    colName: 'Όνομα',
    colCreatedBy: 'Δημιουργήθηκε από',
    colCreated: 'Δημιουργήθηκε',
    colLastUsed: 'Τελευταία χρήση',
    colStatus: 'Κατάσταση',
    save: 'Αποθήκευση',
    cancel: 'Άκυρο',
    statusRevoked: 'ανακλήθηκε',
    statusActive: 'ενεργό',
    rename: 'Μετονομασία',
    revokeBtn: 'Ανάκληση',
    confirmRevoke:
      'Ανάκληση του διακριτικού «{{name}}»; Τα ανακληθέντα διακριτικά δεν διαγράφονται, μόνο επισημαίνονται ως ανενεργά.',
  },
  et: {
    title: 'Juurdepääsutokenid',
    intro1: 'Management API tokenid (',
    intro2: '). Kasuta päist ',
    intro3: '. Toorväärtus kuvatakse ',
    introOnce: 'ainult üks kord',
    intro4: ' loomisel.',
    saveNow: 'Salvesta token kohe — me ei näita seda uuesti:',
    okSaved: 'OK, salvestatud',
    createNew: 'Loo uus token',
    namePh: 'Tokeni nimi (nt deploy-script, integration-x)',
    creating: 'Loomine…',
    createBtn: 'Loo token',
    existing: 'Olemasolevad tokenid',
    loading: 'Laadimine…',
    empty: 'Tokeneid pole loodud.',
    colName: 'Nimi',
    colCreatedBy: 'Looja',
    colCreated: 'Loodud',
    colLastUsed: 'Viimati kasutatud',
    colStatus: 'Olek',
    save: 'Salvesta',
    cancel: 'Tühista',
    statusRevoked: 'tühistatud',
    statusActive: 'aktiivne',
    rename: 'Nimeta ümber',
    revokeBtn: 'Tühista',
    confirmRevoke:
      'Tühistada token "{{name}}"? Tühistatud tokeneid ei saa kustutada, ainult mitteaktiivseks märkida.',
  },
  lv: {
    title: 'Piekļuves marķieri',
    intro1: 'Management API marķieri (',
    intro2: '). Izmantojiet galveni ',
    intro3: '. Neapstrādātā vērtība tiek parādīta ',
    introOnce: 'tikai vienu reizi',
    intro4: ' izveides laikā.',
    saveNow: 'Saglabājiet marķieri tagad — vairs to neparādīsim:',
    okSaved: 'Labi, saglabāts',
    createNew: 'Izveidot jaunu marķieri',
    namePh: 'Marķiera nosaukums (piem., deploy-script, integration-x)',
    creating: 'Izveido…',
    createBtn: 'Izveidot marķieri',
    existing: 'Esošie marķieri',
    loading: 'Notiek ielāde…',
    empty: 'Nav izveidotu marķieru.',
    colName: 'Nosaukums',
    colCreatedBy: 'Izveidoja',
    colCreated: 'Izveidots',
    colLastUsed: 'Pēdējoreiz lietots',
    colStatus: 'Statuss',
    save: 'Saglabāt',
    cancel: 'Atcelt',
    statusRevoked: 'atsaukts',
    statusActive: 'aktīvs',
    rename: 'Pārdēvēt',
    revokeBtn: 'Atsaukt',
    confirmRevoke:
      'Atsaukt marķieri "{{name}}"? Atsauktos marķierus nevar dzēst, tikai atzīmēt kā neaktīvus.',
  },
  lt: {
    title: 'Prieigos prieigos raktai',
    intro1: 'Management API raktai (',
    intro2: '). Naudokite antraštę ',
    intro3: '. Neapdorota reikšmė rodoma ',
    introOnce: 'tik vieną kartą',
    intro4: ' kuriant.',
    saveNow: 'Išsaugokite raktą dabar — daugiau jo neberodysime:',
    okSaved: 'Gerai, išsaugojau',
    createNew: 'Sukurti naują raktą',
    namePh: 'Rakto pavadinimas (pvz., deploy-script, integration-x)',
    creating: 'Kuriama…',
    createBtn: 'Sukurti raktą',
    existing: 'Esami raktai',
    loading: 'Įkeliama…',
    empty: 'Nesukurta jokių raktų.',
    colName: 'Pavadinimas',
    colCreatedBy: 'Sukūrė',
    colCreated: 'Sukurta',
    colLastUsed: 'Paskutinį kartą naudotas',
    colStatus: 'Būsena',
    save: 'Išsaugoti',
    cancel: 'Atšaukti',
    statusRevoked: 'atšauktas',
    statusActive: 'aktyvus',
    rename: 'Pervadinti',
    revokeBtn: 'Atšaukti',
    confirmRevoke:
      'Atšaukti raktą "{{name}}"? Atšauktų raktų negalima ištrinti, tik pažymėti kaip neaktyvius.',
  },
  mt: {
    title: "Tokens ta' aċċess",
    intro1: 'Tokens għall-Management API (',
    intro2: '). Uża l-header ',
    intro3: '. Il-valur mhux ipproċessat jintwera ',
    introOnce: 'darba biss',
    intro4: ' meta jinħoloq.',
    saveNow: 'Issejvja t-token issa — mhux se nuruh mill-ġdid:',
    okSaved: 'OK, issejvjajt',
    createNew: 'Oħloq token ġdid',
    namePh: 'Isem it-token (eż. deploy-script, integration-x)',
    creating: 'Qed jinħoloq…',
    createBtn: 'Oħloq token',
    existing: 'Tokens eżistenti',
    loading: 'Qed jitgħabba…',
    empty: 'Ebda token maħluq.',
    colName: 'Isem',
    colCreatedBy: 'Maħluq minn',
    colCreated: 'Maħluq',
    colLastUsed: 'L-aħħar użu',
    colStatus: 'Status',
    save: 'Issejvja',
    cancel: 'Ikkanċella',
    statusRevoked: 'revokat',
    statusActive: 'attiv',
    rename: 'Bidel l-isem',
    revokeBtn: 'Revoka',
    confirmRevoke:
      'Tirrevoka t-token "{{name}}"? It-tokens revokati ma jistgħux jitħassru, jiġu biss immarkati bħala inattivi.',
  },
  ga: {
    title: 'Comharthaí rochtana',
    intro1: 'Comharthaí don Management API (',
    intro2: '). Úsáid an ceanntásc ',
    intro3: '. Taispeántar an luach amh ',
    introOnce: 'aon uair amháin',
    intro4: ' agus é á chruthú.',
    saveNow: 'Sábháil an comhartha anois — ní thaispeánfaimid arís é:',
    okSaved: 'Ceart go leor, sábháilte',
    createNew: 'Cruthaigh comhartha nua',
    namePh: 'Ainm an chomhartha (m.sh. deploy-script, integration-x)',
    creating: 'Á chruthú…',
    createBtn: 'Cruthaigh comhartha',
    existing: 'Comharthaí atá ann',
    loading: 'Á luchtú…',
    empty: 'Níor cruthaíodh aon chomhartha.',
    colName: 'Ainm',
    colCreatedBy: 'Cruthaithe ag',
    colCreated: 'Cruthaithe',
    colLastUsed: 'Úsáidte go deireanach',
    colStatus: 'Stádas',
    save: 'Sábháil',
    cancel: 'Cealaigh',
    statusRevoked: 'cúlghairthe',
    statusActive: 'gníomhach',
    rename: 'Athainmnigh',
    revokeBtn: 'Cúlghair',
    confirmRevoke:
      'Cúlghair an comhartha "{{name}}"? Ní féidir comharthaí cúlghairthe a scriosadh, ní dhéantar ach iad a mharcáil mar neamhghníomhach.',
  },
  no: {
    title: 'Tilgangstokener',
    intro1: 'Tokener for Management API (',
    intro2: '). Bruk headeren ',
    intro3: '. Råverdien vises ',
    introOnce: 'bare én gang',
    intro4: ' ved opprettelse.',
    saveNow: 'Lagre tokenet nå — vi viser det ikke igjen:',
    okSaved: 'OK, lagret',
    createNew: 'Opprett et nytt token',
    namePh: 'Tokennavn (f.eks. deploy-script, integration-x)',
    creating: 'Oppretter…',
    createBtn: 'Opprett token',
    existing: 'Eksisterende tokener',
    loading: 'Laster…',
    empty: 'Ingen tokener opprettet.',
    colName: 'Navn',
    colCreatedBy: 'Opprettet av',
    colCreated: 'Opprettet',
    colLastUsed: 'Sist brukt',
    colStatus: 'Status',
    save: 'Lagre',
    cancel: 'Avbryt',
    statusRevoked: 'tilbakekalt',
    statusActive: 'aktiv',
    rename: 'Gi nytt navn',
    revokeBtn: 'Tilbakekall',
    confirmRevoke:
      'Tilbakekall token "{{name}}"? Tilbakekalte tokener kan ikke slettes, bare merkes som inaktive.',
  },
  is: {
    title: 'Aðgangstóken',
    intro1: 'Tóken fyrir Management API (',
    intro2: '). Notaðu hausinn ',
    intro3: '. Hráa gildið er sýnt ',
    introOnce: 'aðeins einu sinni',
    intro4: ' við stofnun.',
    saveNow: 'Vistaðu tókenið núna — við sýnum það ekki aftur:',
    okSaved: 'Í lagi, vistað',
    createNew: 'Stofna nýtt tóken',
    namePh: 'Heiti tókens (t.d. deploy-script, integration-x)',
    creating: 'Stofnar…',
    createBtn: 'Stofna tóken',
    existing: 'Núverandi tóken',
    loading: 'Hleður…',
    empty: 'Engin tóken stofnuð.',
    colName: 'Heiti',
    colCreatedBy: 'Stofnað af',
    colCreated: 'Stofnað',
    colLastUsed: 'Síðast notað',
    colStatus: 'Staða',
    save: 'Vista',
    cancel: 'Hætta við',
    statusRevoked: 'afturkallað',
    statusActive: 'virkt',
    rename: 'Endurnefna',
    revokeBtn: 'Afturkalla',
    confirmRevoke:
      'Afturkalla tókenið "{{name}}"? Afturkölluð tóken er ekki hægt að eyða, aðeins merkja sem óvirk.',
  },
  uk: {
    title: 'Токени доступу',
    intro1: 'Токени для Management API (',
    intro2: '). Використовуйте заголовок ',
    intro3: '. Необроблене значення показується ',
    introOnce: 'лише один раз',
    intro4: ' під час створення.',
    saveNow: 'Збережіть токен зараз — ми більше його не покажемо:',
    okSaved: 'Гаразд, зберіг',
    createNew: 'Створити новий токен',
    namePh: 'Назва токена (напр. deploy-script, integration-x)',
    creating: 'Створення…',
    createBtn: 'Створити токен',
    existing: 'Наявні токени',
    loading: 'Завантаження…',
    empty: 'Токени не створено.',
    colName: 'Назва',
    colCreatedBy: 'Створив',
    colCreated: 'Створено',
    colLastUsed: 'Востаннє використано',
    colStatus: 'Статус',
    save: 'Зберегти',
    cancel: 'Скасувати',
    statusRevoked: 'відкликано',
    statusActive: 'активний',
    rename: 'Перейменувати',
    revokeBtn: 'Відкликати',
    confirmRevoke:
      'Відкликати токен «{{name}}»? Відкликані токени неможливо видалити, лише позначити як неактивні.',
  },
  sr: {
    title: 'Приступни токени',
    intro1: 'Токени за Management API (',
    intro2: '). Користите заглавље ',
    intro3: '. Сирова вредност се приказује ',
    introOnce: 'само једном',
    intro4: ' при креирању.',
    saveNow: 'Сачувајте токен сада — нећемо га поново приказати:',
    okSaved: 'У реду, сачувано',
    createNew: 'Креирај нови токен',
    namePh: 'Назив токена (нпр. deploy-script, integration-x)',
    creating: 'Креирање…',
    createBtn: 'Креирај токен',
    existing: 'Постојећи токени',
    loading: 'Учитавање…',
    empty: 'Нема креираних токена.',
    colName: 'Назив',
    colCreatedBy: 'Креирао',
    colCreated: 'Креирано',
    colLastUsed: 'Последњи пут коришћен',
    colStatus: 'Статус',
    save: 'Сачувај',
    cancel: 'Откажи',
    statusRevoked: 'опозван',
    statusActive: 'активан',
    rename: 'Преименуј',
    revokeBtn: 'Опозови',
    confirmRevoke:
      'Опозвати токен „{{name}}“? Опозвани токени се не могу обрисати, само означити као неактивни.',
  },
  sq: {
    title: 'Tokenat e qasjes',
    intro1: 'Tokena për Management API (',
    intro2: '). Përdor header-in ',
    intro3: '. Vlera e papërpunuar shfaqet ',
    introOnce: 'vetëm një herë',
    intro4: ' gjatë krijimit.',
    saveNow: 'Ruaj tokenin tani — nuk do ta shfaqim sërish:',
    okSaved: 'OK, e ruajta',
    createNew: 'Krijo një token të ri',
    namePh: 'Emri i tokenit (p.sh. deploy-script, integration-x)',
    creating: 'Po krijohet…',
    createBtn: 'Krijo token',
    existing: 'Tokenat ekzistues',
    loading: 'Po ngarkohet…',
    empty: 'Asnjë token i krijuar.',
    colName: 'Emri',
    colCreatedBy: 'Krijuar nga',
    colCreated: 'Krijuar',
    colLastUsed: 'Përdorur së fundi',
    colStatus: 'Statusi',
    save: 'Ruaj',
    cancel: 'Anulo',
    statusRevoked: 'revokuar',
    statusActive: 'aktiv',
    rename: 'Riemërto',
    revokeBtn: 'Revoko',
    confirmRevoke:
      'Të revokohet tokeni "{{name}}"? Tokenat e revokuar nuk mund të fshihen, vetëm shënohen si joaktivë.',
  },
  mk: {
    title: 'Токени за пристап',
    intro1: 'Токени за Management API (',
    intro2: '). Користете го заглавието ',
    intro3: '. Суровата вредност се прикажува ',
    introOnce: 'само еднаш',
    intro4: ' при создавање.',
    saveNow: 'Зачувајте го токенот сега — нема да го прикажеме повторно:',
    okSaved: 'Во ред, зачував',
    createNew: 'Создади нов токен',
    namePh: 'Име на токенот (пр. deploy-script, integration-x)',
    creating: 'Се создава…',
    createBtn: 'Создади токен',
    existing: 'Постоечки токени',
    loading: 'Се вчитува…',
    empty: 'Нема создадени токени.',
    colName: 'Име',
    colCreatedBy: 'Создал',
    colCreated: 'Создадено',
    colLastUsed: 'Последно користен',
    colStatus: 'Статус',
    save: 'Зачувај',
    cancel: 'Откажи',
    statusRevoked: 'отповикан',
    statusActive: 'активен',
    rename: 'Преименувај',
    revokeBtn: 'Отповикај',
    confirmRevoke:
      'Да се отповика токенот „{{name}}“? Отповиканите токени не можат да се избришат, само се означуваат како неактивни.',
  },
  bs: {
    title: 'Pristupni tokeni',
    intro1: 'Tokeni za Management API (',
    intro2: '). Koristite zaglavlje ',
    intro3: '. Sirova vrijednost prikazuje se ',
    introOnce: 'samo jednom',
    intro4: ' pri kreiranju.',
    saveNow: 'Spremite token sada — nećemo ga ponovo prikazati:',
    okSaved: 'U redu, spremljeno',
    createNew: 'Kreiraj novi token',
    namePh: 'Naziv tokena (npr. deploy-script, integration-x)',
    creating: 'Kreiranje…',
    createBtn: 'Kreiraj token',
    existing: 'Postojeći tokeni',
    loading: 'Učitavanje…',
    empty: 'Nema kreiranih tokena.',
    colName: 'Naziv',
    colCreatedBy: 'Kreirao',
    colCreated: 'Kreirano',
    colLastUsed: 'Zadnji put korišten',
    colStatus: 'Status',
    save: 'Spremi',
    cancel: 'Otkaži',
    statusRevoked: 'opozvan',
    statusActive: 'aktivan',
    rename: 'Preimenuj',
    revokeBtn: 'Opozovi',
    confirmRevoke:
      'Opozvati token "{{name}}"? Opozvani tokeni se ne mogu obrisati, samo označiti kao neaktivni.',
  },
};

const authHeadersTr: Record<string, Dict> = {
  en: {
    title: 'Accepted authorization headers',
    intro1: 'Names of HTTP headers in which a client may pass the API key. ',
    intro2:
      ' is always accepted by default. You can add universal headers (empty "Plan" field) or ones assigned to a specific plan.',
    addNew: 'Add a new header',
    universalAllPlans: 'Universal (all plans)',
    adding: 'Adding…',
    add: 'Add',
    listTitle: 'Header list',
    loading: 'Loading…',
    empty: 'No headers configured.',
    colName: 'Name',
    colPlan: 'Plan',
    colCreated: 'Created',
    universal: 'universal',
    delete: 'Delete',
    confirmDelete: 'Delete header "{{name}}"?',
  },
  pl: {
    title: 'Akceptowane nagłówki autoryzacyjne',
    intro1:
      'Nazwy nagłówków HTTP, w których klient może podać klucz API. Domyślnie zawsze akceptowany jest ',
    intro2:
      '. Możesz dodać nagłówki uniwersalne (puste pole „Plan”) lub przypisane do konkretnego planu.',
    addNew: 'Dodaj nowy nagłówek',
    universalAllPlans: 'Uniwersalny (wszystkie plany)',
    adding: 'Dodaję…',
    add: 'Dodaj',
    listTitle: 'Lista nagłówków',
    loading: 'Ładowanie…',
    empty: 'Brak skonfigurowanych nagłówków.',
    colName: 'Nazwa',
    colPlan: 'Plan',
    colCreated: 'Utworzono',
    universal: 'uniwersalny',
    delete: 'Usuń',
    confirmDelete: 'Usunąć nagłówek „{{name}}”?',
  },
  de: {
    title: 'Akzeptierte Autorisierungs-Header',
    intro1: 'Namen der HTTP-Header, in denen ein Client den API-Schlüssel übergeben kann. ',
    intro2:
      ' wird standardmäßig immer akzeptiert. Sie können universelle Header (leeres Feld „Plan“) oder einem bestimmten Plan zugeordnete hinzufügen.',
    addNew: 'Neuen Header hinzufügen',
    universalAllPlans: 'Universell (alle Pläne)',
    adding: 'Wird hinzugefügt…',
    add: 'Hinzufügen',
    listTitle: 'Header-Liste',
    loading: 'Wird geladen…',
    empty: 'Keine Header konfiguriert.',
    colName: 'Name',
    colPlan: 'Plan',
    colCreated: 'Erstellt',
    universal: 'universell',
    delete: 'Löschen',
    confirmDelete: 'Header „{{name}}“ löschen?',
  },
  fr: {
    title: "En-têtes d'autorisation acceptés",
    intro1: 'Noms des en-têtes HTTP dans lesquels un client peut transmettre la clé API. ',
    intro2:
      ' est toujours accepté par défaut. Vous pouvez ajouter des en-têtes universels (champ « Plan » vide) ou affectés à un plan spécifique.',
    addNew: 'Ajouter un nouvel en-tête',
    universalAllPlans: 'Universel (tous les plans)',
    adding: 'Ajout…',
    add: 'Ajouter',
    listTitle: 'Liste des en-têtes',
    loading: 'Chargement…',
    empty: 'Aucun en-tête configuré.',
    colName: 'Nom',
    colPlan: 'Plan',
    colCreated: 'Créé',
    universal: 'universel',
    delete: 'Supprimer',
    confirmDelete: "Supprimer l'en-tête « {{name}} » ?",
  },
  es: {
    title: 'Encabezados de autorización aceptados',
    intro1: 'Nombres de los encabezados HTTP en los que un cliente puede pasar la clave API. ',
    intro2:
      ' siempre se acepta por defecto. Puedes añadir encabezados universales (campo «Plan» vacío) o asignados a un plan concreto.',
    addNew: 'Añadir un nuevo encabezado',
    universalAllPlans: 'Universal (todos los planes)',
    adding: 'Añadiendo…',
    add: 'Añadir',
    listTitle: 'Lista de encabezados',
    loading: 'Cargando…',
    empty: 'No hay encabezados configurados.',
    colName: 'Nombre',
    colPlan: 'Plan',
    colCreated: 'Creado',
    universal: 'universal',
    delete: 'Eliminar',
    confirmDelete: '¿Eliminar el encabezado «{{name}}»?',
  },
  it: {
    title: 'Header di autorizzazione accettati',
    intro1: 'Nomi degli header HTTP in cui un client può passare la chiave API. ',
    intro2:
      ' è sempre accettato per impostazione predefinita. Puoi aggiungere header universali (campo «Piano» vuoto) o assegnati a un piano specifico.',
    addNew: 'Aggiungi un nuovo header',
    universalAllPlans: 'Universale (tutti i piani)',
    adding: 'Aggiunta…',
    add: 'Aggiungi',
    listTitle: 'Elenco header',
    loading: 'Caricamento…',
    empty: 'Nessun header configurato.',
    colName: 'Nome',
    colPlan: 'Piano',
    colCreated: 'Creato',
    universal: 'universale',
    delete: 'Elimina',
    confirmDelete: "Eliminare l'header «{{name}}»?",
  },
  pt: {
    title: 'Cabeçalhos de autorização aceites',
    intro1: 'Nomes dos cabeçalhos HTTP em que um cliente pode passar a chave API. ',
    intro2:
      ' é sempre aceite por predefinição. Pode adicionar cabeçalhos universais (campo «Plano» vazio) ou atribuídos a um plano específico.',
    addNew: 'Adicionar um novo cabeçalho',
    universalAllPlans: 'Universal (todos os planos)',
    adding: 'A adicionar…',
    add: 'Adicionar',
    listTitle: 'Lista de cabeçalhos',
    loading: 'A carregar…',
    empty: 'Nenhum cabeçalho configurado.',
    colName: 'Nome',
    colPlan: 'Plano',
    colCreated: 'Criado',
    universal: 'universal',
    delete: 'Eliminar',
    confirmDelete: 'Eliminar o cabeçalho «{{name}}»?',
  },
  nl: {
    title: 'Geaccepteerde autorisatie-headers',
    intro1: 'Namen van HTTP-headers waarin een client de API-sleutel kan meegeven. ',
    intro2:
      ' wordt standaard altijd geaccepteerd. Je kunt universele headers toevoegen (leeg veld "Plan") of headers die aan een specifiek plan zijn toegewezen.',
    addNew: 'Een nieuwe header toevoegen',
    universalAllPlans: 'Universeel (alle plannen)',
    adding: 'Toevoegen…',
    add: 'Toevoegen',
    listTitle: 'Headerlijst',
    loading: 'Laden…',
    empty: 'Geen headers geconfigureerd.',
    colName: 'Naam',
    colPlan: 'Plan',
    colCreated: 'Aangemaakt',
    universal: 'universeel',
    delete: 'Verwijderen',
    confirmDelete: 'Header "{{name}}" verwijderen?',
  },
  da: {
    title: 'Accepterede autorisations-headere',
    intro1: 'Navne på HTTP-headere, hvor en klient kan angive API-nøglen. ',
    intro2:
      ' accepteres altid som standard. Du kan tilføje universelle headere (tomt "Plan"-felt) eller headere tildelt en bestemt plan.',
    addNew: 'Tilføj en ny header',
    universalAllPlans: 'Universel (alle planer)',
    adding: 'Tilføjer…',
    add: 'Tilføj',
    listTitle: 'Header-liste',
    loading: 'Indlæser…',
    empty: 'Ingen headere konfigureret.',
    colName: 'Navn',
    colPlan: 'Plan',
    colCreated: 'Oprettet',
    universal: 'universel',
    delete: 'Slet',
    confirmDelete: 'Slet header "{{name}}"?',
  },
  sv: {
    title: 'Accepterade auktoriseringsheaders',
    intro1: 'Namn på HTTP-headers där en klient kan ange API-nyckeln. ',
    intro2:
      ' accepteras alltid som standard. Du kan lägga till universella headers (tomt "Plan"-fält) eller headers tilldelade en specifik plan.',
    addNew: 'Lägg till en ny header',
    universalAllPlans: 'Universell (alla planer)',
    adding: 'Lägger till…',
    add: 'Lägg till',
    listTitle: 'Headerlista',
    loading: 'Laddar…',
    empty: 'Inga headers konfigurerade.',
    colName: 'Namn',
    colPlan: 'Plan',
    colCreated: 'Skapad',
    universal: 'universell',
    delete: 'Ta bort',
    confirmDelete: 'Ta bort header "{{name}}"?',
  },
  fi: {
    title: 'Hyväksytyt valtuutusotsikot',
    intro1: 'HTTP-otsikoiden nimet, joissa asiakas voi välittää API-avaimen. ',
    intro2:
      ' hyväksytään aina oletuksena. Voit lisätä universaaleja otsikoita (tyhjä "Suunnitelma"-kenttä) tai tiettyyn suunnitelmaan liitettyjä otsikoita.',
    addNew: 'Lisää uusi otsikko',
    universalAllPlans: 'Universaali (kaikki suunnitelmat)',
    adding: 'Lisätään…',
    add: 'Lisää',
    listTitle: 'Otsikkoluettelo',
    loading: 'Ladataan…',
    empty: 'Ei määritettyjä otsikoita.',
    colName: 'Nimi',
    colPlan: 'Suunnitelma',
    colCreated: 'Luotu',
    universal: 'universaali',
    delete: 'Poista',
    confirmDelete: 'Poistetaanko otsikko "{{name}}"?',
  },
  cs: {
    title: 'Akceptované autorizační hlavičky',
    intro1: 'Názvy HTTP hlaviček, ve kterých může klient předat API klíč. ',
    intro2:
      ' je vždy ve výchozím nastavení akceptováno. Můžete přidat univerzální hlavičky (prázdné pole „Plán“) nebo přiřazené ke konkrétnímu plánu.',
    addNew: 'Přidat novou hlavičku',
    universalAllPlans: 'Univerzální (všechny plány)',
    adding: 'Přidávání…',
    add: 'Přidat',
    listTitle: 'Seznam hlaviček',
    loading: 'Načítání…',
    empty: 'Žádné hlavičky nejsou nakonfigurovány.',
    colName: 'Název',
    colPlan: 'Plán',
    colCreated: 'Vytvořeno',
    universal: 'univerzální',
    delete: 'Smazat',
    confirmDelete: 'Smazat hlavičku „{{name}}“?',
  },
  sk: {
    title: 'Akceptované autorizačné hlavičky',
    intro1: 'Názvy HTTP hlavičiek, v ktorých môže klient odovzdať API kľúč. ',
    intro2:
      ' je vždy predvolene akceptované. Môžete pridať univerzálne hlavičky (prázdne pole „Plán“) alebo priradené ku konkrétnemu plánu.',
    addNew: 'Pridať novú hlavičku',
    universalAllPlans: 'Univerzálna (všetky plány)',
    adding: 'Pridáva sa…',
    add: 'Pridať',
    listTitle: 'Zoznam hlavičiek',
    loading: 'Načítava sa…',
    empty: 'Žiadne hlavičky nie sú nakonfigurované.',
    colName: 'Názov',
    colPlan: 'Plán',
    colCreated: 'Vytvorené',
    universal: 'univerzálna',
    delete: 'Odstrániť',
    confirmDelete: 'Odstrániť hlavičku „{{name}}“?',
  },
  sl: {
    title: 'Sprejete avtorizacijske glave',
    intro1: 'Imena glav HTTP, v katerih lahko odjemalec posreduje ključ API. ',
    intro2:
      ' je vedno privzeto sprejeto. Dodate lahko univerzalne glave (prazno polje »Načrt«) ali dodeljene določenemu načrtu.',
    addNew: 'Dodaj novo glavo',
    universalAllPlans: 'Univerzalna (vsi načrti)',
    adding: 'Dodajanje…',
    add: 'Dodaj',
    listTitle: 'Seznam glav',
    loading: 'Nalaganje…',
    empty: 'Ni nastavljenih glav.',
    colName: 'Ime',
    colPlan: 'Načrt',
    colCreated: 'Ustvarjeno',
    universal: 'univerzalna',
    delete: 'Izbriši',
    confirmDelete: 'Izbrišem glavo »{{name}}«?',
  },
  hr: {
    title: 'Prihvaćena autorizacijska zaglavlja',
    intro1: 'Nazivi HTTP zaglavlja u kojima klijent može proslijediti API ključ. ',
    intro2:
      ' uvijek se prihvaća prema zadanim postavkama. Možete dodati univerzalna zaglavlja (prazno polje „Plan“) ili dodijeljena određenom planu.',
    addNew: 'Dodaj novo zaglavlje',
    universalAllPlans: 'Univerzalno (svi planovi)',
    adding: 'Dodavanje…',
    add: 'Dodaj',
    listTitle: 'Popis zaglavlja',
    loading: 'Učitavanje…',
    empty: 'Nema konfiguriranih zaglavlja.',
    colName: 'Naziv',
    colPlan: 'Plan',
    colCreated: 'Stvoreno',
    universal: 'univerzalno',
    delete: 'Obriši',
    confirmDelete: 'Obrisati zaglavlje „{{name}}“?',
  },
  hu: {
    title: 'Elfogadott engedélyezési fejlécek',
    intro1: 'HTTP-fejlécek nevei, amelyekben az ügyfél átadhatja az API-kulcsot. ',
    intro2:
      ' alapértelmezetten mindig elfogadott. Hozzáadhat univerzális fejléceket (üres „Csomag” mező) vagy adott csomaghoz rendelteket.',
    addNew: 'Új fejléc hozzáadása',
    universalAllPlans: 'Univerzális (minden csomag)',
    adding: 'Hozzáadás…',
    add: 'Hozzáadás',
    listTitle: 'Fejlécek listája',
    loading: 'Betöltés…',
    empty: 'Nincsenek konfigurált fejlécek.',
    colName: 'Név',
    colPlan: 'Csomag',
    colCreated: 'Létrehozva',
    universal: 'univerzális',
    delete: 'Törlés',
    confirmDelete: 'Törli a(z) „{{name}}” fejlécet?',
  },
  ro: {
    title: 'Anteturi de autorizare acceptate',
    intro1: 'Numele anteturilor HTTP în care un client poate transmite cheia API. ',
    intro2:
      ' este întotdeauna acceptat în mod implicit. Puteți adăuga anteturi universale (câmpul „Plan” gol) sau atribuite unui plan specific.',
    addNew: 'Adăugați un antet nou',
    universalAllPlans: 'Universal (toate planurile)',
    adding: 'Se adaugă…',
    add: 'Adaugă',
    listTitle: 'Lista de anteturi',
    loading: 'Se încarcă…',
    empty: 'Niciun antet configurat.',
    colName: 'Nume',
    colPlan: 'Plan',
    colCreated: 'Creat',
    universal: 'universal',
    delete: 'Șterge',
    confirmDelete: 'Ștergeți antetul „{{name}}”?',
  },
  bg: {
    title: 'Приети заглавки за оторизация',
    intro1: 'Имена на HTTP заглавки, в които клиентът може да подаде API ключа. ',
    intro2:
      ' винаги се приема по подразбиране. Можете да добавите универсални заглавки (празно поле „План“) или присвоени към конкретен план.',
    addNew: 'Добавяне на нова заглавка',
    universalAllPlans: 'Универсална (всички планове)',
    adding: 'Добавяне…',
    add: 'Добави',
    listTitle: 'Списък със заглавки',
    loading: 'Зареждане…',
    empty: 'Няма конфигурирани заглавки.',
    colName: 'Име',
    colPlan: 'План',
    colCreated: 'Създадено',
    universal: 'универсална',
    delete: 'Изтрий',
    confirmDelete: 'Да се изтрие ли заглавката „{{name}}“?',
  },
  el: {
    title: 'Αποδεκτές κεφαλίδες εξουσιοδότησης',
    intro1: 'Ονόματα κεφαλίδων HTTP στις οποίες ο πελάτης μπορεί να περάσει το κλειδί API. ',
    intro2:
      ' γίνεται πάντα αποδεκτό από προεπιλογή. Μπορείτε να προσθέσετε καθολικές κεφαλίδες (κενό πεδίο «Πρόγραμμα») ή αντιστοιχισμένες σε συγκεκριμένο πρόγραμμα.',
    addNew: 'Προσθήκη νέας κεφαλίδας',
    universalAllPlans: 'Καθολική (όλα τα προγράμματα)',
    adding: 'Προσθήκη…',
    add: 'Προσθήκη',
    listTitle: 'Λίστα κεφαλίδων',
    loading: 'Φόρτωση…',
    empty: 'Δεν έχουν διαμορφωθεί κεφαλίδες.',
    colName: 'Όνομα',
    colPlan: 'Πρόγραμμα',
    colCreated: 'Δημιουργήθηκε',
    universal: 'καθολική',
    delete: 'Διαγραφή',
    confirmDelete: 'Διαγραφή της κεφαλίδας «{{name}}»;',
  },
  et: {
    title: 'Aktsepteeritud autoriseerimispäised',
    intro1: 'HTTP-päiste nimed, milles klient saab edastada API-võtme. ',
    intro2:
      ' aktsepteeritakse alati vaikimisi. Saate lisada universaalseid päiseid (tühi väli „Pakett“) või konkreetsele paketile määratud päiseid.',
    addNew: 'Lisa uus päis',
    universalAllPlans: 'Universaalne (kõik paketid)',
    adding: 'Lisamine…',
    add: 'Lisa',
    listTitle: 'Päiste loend',
    loading: 'Laadimine…',
    empty: 'Päiseid pole seadistatud.',
    colName: 'Nimi',
    colPlan: 'Pakett',
    colCreated: 'Loodud',
    universal: 'universaalne',
    delete: 'Kustuta',
    confirmDelete: 'Kustutada päis „{{name}}“?',
  },
  lv: {
    title: 'Pieņemtās autorizācijas galvenes',
    intro1: 'HTTP galveņu nosaukumi, kuros klients var nodot API atslēgu. ',
    intro2:
      ' vienmēr tiek pieņemts pēc noklusējuma. Varat pievienot universālas galvenes (tukšs lauks „Plāns“) vai piešķirtas konkrētam plānam.',
    addNew: 'Pievienot jaunu galveni',
    universalAllPlans: 'Universāla (visi plāni)',
    adding: 'Pievieno…',
    add: 'Pievienot',
    listTitle: 'Galveņu saraksts',
    loading: 'Notiek ielāde…',
    empty: 'Nav konfigurētu galveņu.',
    colName: 'Nosaukums',
    colPlan: 'Plāns',
    colCreated: 'Izveidots',
    universal: 'universāla',
    delete: 'Dzēst',
    confirmDelete: 'Dzēst galveni „{{name}}“?',
  },
  lt: {
    title: 'Priimtinos autorizacijos antraštės',
    intro1: 'HTTP antraščių pavadinimai, kuriose klientas gali perduoti API raktą. ',
    intro2:
      ' visada priimama pagal numatytuosius nustatymus. Galite pridėti universalių antraščių (tuščias laukas „Planas“) arba priskirtų konkrečiam planui.',
    addNew: 'Pridėti naują antraštę',
    universalAllPlans: 'Universali (visi planai)',
    adding: 'Pridedama…',
    add: 'Pridėti',
    listTitle: 'Antraščių sąrašas',
    loading: 'Įkeliama…',
    empty: 'Nėra sukonfigūruotų antraščių.',
    colName: 'Pavadinimas',
    colPlan: 'Planas',
    colCreated: 'Sukurta',
    universal: 'universali',
    delete: 'Ištrinti',
    confirmDelete: 'Ištrinti antraštę „{{name}}“?',
  },
  mt: {
    title: "Headers ta' awtorizzazzjoni aċċettati",
    intro1: "Ismijiet tal-headers HTTP li fihom klijent jista' jgħaddi ċ-ċavetta API. ",
    intro2:
      ' dejjem jiġi aċċettat b\'mod default. Tista\' żżid headers universali (kamp "Pjan" vojt) jew assenjati lil pjan speċifiku.',
    addNew: 'Żid header ġdid',
    universalAllPlans: 'Universali (il-pjanijiet kollha)',
    adding: 'Qed jiżdied…',
    add: 'Żid',
    listTitle: 'Lista tal-headers',
    loading: 'Qed jitgħabba…',
    empty: 'Ebda header konfigurat.',
    colName: 'Isem',
    colPlan: 'Pjan',
    colCreated: 'Maħluq',
    universal: 'universali',
    delete: 'Ħassar',
    confirmDelete: 'Tħassar il-header "{{name}}"?',
  },
  ga: {
    title: 'Ceanntásca údaraithe glactha',
    intro1: 'Ainmneacha na gceanntásc HTTP inar féidir le cliant an eochair API a sheoladh. ',
    intro2:
      ' glactar leis i gcónaí de réir réamhshocraithe. Is féidir leat ceanntásca uilíocha a chur leis (réimse "Plean" folamh) nó cinn a shanntar do phlean ar leith.',
    addNew: 'Cuir ceanntásc nua leis',
    universalAllPlans: 'Uilíoch (gach plean)',
    adding: 'Á chur leis…',
    add: 'Cuir leis',
    listTitle: 'Liosta ceanntásc',
    loading: 'Á luchtú…',
    empty: 'Níl aon cheanntásc cumraithe.',
    colName: 'Ainm',
    colPlan: 'Plean',
    colCreated: 'Cruthaithe',
    universal: 'uilíoch',
    delete: 'Scrios',
    confirmDelete: 'Scrios an ceanntásc "{{name}}"?',
  },
  no: {
    title: 'Aksepterte autorisasjonshoder',
    intro1: 'Navn på HTTP-hoder der en klient kan oppgi API-nøkkelen. ',
    intro2:
      ' aksepteres alltid som standard. Du kan legge til universelle hoder (tomt "Plan"-felt) eller hoder tildelt en bestemt plan.',
    addNew: 'Legg til et nytt hode',
    universalAllPlans: 'Universell (alle planer)',
    adding: 'Legger til…',
    add: 'Legg til',
    listTitle: 'Hodeliste',
    loading: 'Laster…',
    empty: 'Ingen hoder konfigurert.',
    colName: 'Navn',
    colPlan: 'Plan',
    colCreated: 'Opprettet',
    universal: 'universell',
    delete: 'Slett',
    confirmDelete: 'Slett hodet "{{name}}"?',
  },
  is: {
    title: 'Samþykktir heimildarhausar',
    intro1: 'Nöfn HTTP-hausa þar sem viðskiptavinur getur sent API-lykilinn. ',
    intro2:
      ' er alltaf samþykkt sjálfgefið. Þú getur bætt við algildum hausum (autt "Áætlun"-svæði) eða úthlutað tiltekinni áætlun.',
    addNew: 'Bæta við nýjum haus',
    universalAllPlans: 'Algilt (allar áætlanir)',
    adding: 'Bæti við…',
    add: 'Bæta við',
    listTitle: 'Hausalisti',
    loading: 'Hleður…',
    empty: 'Engir hausar stilltir.',
    colName: 'Nafn',
    colPlan: 'Áætlun',
    colCreated: 'Búið til',
    universal: 'algilt',
    delete: 'Eyða',
    confirmDelete: 'Eyða hausnum "{{name}}"?',
  },
  uk: {
    title: 'Прийнятні заголовки авторизації',
    intro1: 'Назви HTTP-заголовків, у яких клієнт може передати ключ API. ',
    intro2:
      ' завжди приймається за замовчуванням. Ви можете додати універсальні заголовки (порожнє поле «План») або призначені конкретному плану.',
    addNew: 'Додати новий заголовок',
    universalAllPlans: 'Універсальний (усі плани)',
    adding: 'Додавання…',
    add: 'Додати',
    listTitle: 'Список заголовків',
    loading: 'Завантаження…',
    empty: 'Заголовки не налаштовано.',
    colName: 'Назва',
    colPlan: 'План',
    colCreated: 'Створено',
    universal: 'універсальний',
    delete: 'Видалити',
    confirmDelete: 'Видалити заголовок «{{name}}»?',
  },
  sr: {
    title: 'Прихваћена ауторизациона заглавља',
    intro1: 'Називи HTTP заглавља у којима клијент може проследити API кључ. ',
    intro2:
      ' се увек прихвата подразумевано. Можете додати универзална заглавља (празно поље „План“) или додељена одређеном плану.',
    addNew: 'Додај ново заглавље',
    universalAllPlans: 'Универзално (сви планови)',
    adding: 'Додавање…',
    add: 'Додај',
    listTitle: 'Списак заглавља',
    loading: 'Учитавање…',
    empty: 'Нема конфигурисаних заглавља.',
    colName: 'Назив',
    colPlan: 'План',
    colCreated: 'Креирано',
    universal: 'универзално',
    delete: 'Обриши',
    confirmDelete: 'Обрисати заглавље „{{name}}“?',
  },
  sq: {
    title: 'Headerat e autorizimit të pranuar',
    intro1: 'Emrat e headerave HTTP në të cilët një klient mund të kalojë çelësin API. ',
    intro2:
      ' pranohet gjithmonë si parazgjedhje. Mund të shtoni headera universalë (fusha "Plani" bosh) ose të caktuar për një plan specifik.',
    addNew: 'Shto një header të ri',
    universalAllPlans: 'Universal (të gjitha planet)',
    adding: 'Po shtohet…',
    add: 'Shto',
    listTitle: 'Lista e headerave',
    loading: 'Po ngarkohet…',
    empty: 'Asnjë header i konfiguruar.',
    colName: 'Emri',
    colPlan: 'Plani',
    colCreated: 'Krijuar',
    universal: 'universal',
    delete: 'Fshi',
    confirmDelete: 'Të fshihet headeri "{{name}}"?',
  },
  mk: {
    title: 'Прифатени авторизациски заглавија',
    intro1: 'Имиња на HTTP заглавија во кои клиентот може да го испрати API клучот. ',
    intro2:
      ' секогаш се прифаќа стандардно. Можете да додадете универзални заглавија (празно поле „План“) или доделени на конкретен план.',
    addNew: 'Додај ново заглавие',
    universalAllPlans: 'Универзално (сите планови)',
    adding: 'Се додава…',
    add: 'Додај',
    listTitle: 'Список на заглавија',
    loading: 'Се вчитува…',
    empty: 'Нема конфигурирани заглавија.',
    colName: 'Име',
    colPlan: 'План',
    colCreated: 'Создадено',
    universal: 'универзално',
    delete: 'Избриши',
    confirmDelete: 'Да се избрише заглавието „{{name}}“?',
  },
  bs: {
    title: 'Prihvaćena autorizacijska zaglavlja',
    intro1: 'Nazivi HTTP zaglavlja u kojima klijent može proslijediti API ključ. ',
    intro2:
      ' uvijek se prihvaća prema zadanim postavkama. Možete dodati univerzalna zaglavlja (prazno polje „Plan“) ili dodijeljena određenom planu.',
    addNew: 'Dodaj novo zaglavlje',
    universalAllPlans: 'Univerzalno (svi planovi)',
    adding: 'Dodavanje…',
    add: 'Dodaj',
    listTitle: 'Popis zaglavlja',
    loading: 'Učitavanje…',
    empty: 'Nema konfiguriranih zaglavlja.',
    colName: 'Naziv',
    colPlan: 'Plan',
    colCreated: 'Kreirano',
    universal: 'univerzalno',
    delete: 'Obriši',
    confirmDelete: 'Obrisati zaglavlje „{{name}}“?',
  },
};

type FullBundle = Bundle & {
  nav: Dict;
  auth: Dict;
  settings: Dict;
  dashboard: Dict;
  portal: Dict;
  webhooks: Dict;
  correction: Dict;
  register: Dict;
  components: Dict;
  perf: Dict;
  forecast: Dict;
  viz: Dict;
  quickLinks: Dict;
  admin: Dict;
  help: Dict;
  modelAcc: Dict;
  authHeaders: Dict;
  adminTokens: Dict;
  adminDash: Dict;
  audit: Dict;
  actCodes: Dict;
  plans: Dict;
  admins: Dict;
  apiKeys: Dict;
  gravitee: Dict;
  cpw: Dict;
  clients: Dict;
  weather: Dict;
  prodAlerts: Dict;
  airQuality: Dict;
  confidence: Dict;
  archive: Dict;
  revenue: Dict;
  horizon: Dict;
  carbon: Dict;
  revenueChart: Dict;
};
const baseBundles: Record<string, Bundle> = {
  en,
  pl,
  de,
  fr,
  es,
  it,
  pt,
  nl,
  da,
  sv,
  fi,
  cs,
  sk,
  sl,
  hr,
  hu,
  ro,
  bg,
  el,
  et,
  lv,
  lt,
  mt,
  ga,
  no,
  is,
  uk,
  sr,
  sq,
  mk,
  bs,
};

/** i18next resources. Klucze niezdefiniowane dla danego języka → fallback en. */
export const resources: Record<string, { translation: FullBundle }> = Object.fromEntries(
  Object.keys(baseBundles).map((code) => {
    const base = baseBundles[code];
    const ex = extras[code] || extras.en;
    return [
      code,
      {
        translation: {
          ...base,
          common: { ...base.common, ...ex.common },
          nav: ex.nav,
          auth: authTr[code] || authTr.en,
          settings: settingsTr[code] || settingsTr.en,
          dashboard: dashboardTr[code] || dashboardTr.en,
          portal: portalTr[code] || portalTr.en,
          webhooks: webhooksTr[code] || webhooksTr.en,
          correction: correctionTr[code] || correctionTr.en,
          register: registerTr[code] || registerTr.en,
          components: {
            ...(componentsTr[code] || componentsTr.en),
            ...(componentsTr2[code] || componentsTr2.en),
          },
          perf: perfTr[code] || perfTr.en,
          forecast: forecastTr[code] || forecastTr.en,
          viz: vizTr[code] || vizTr.en,
          quickLinks: quickLinksTr[code] || quickLinksTr.en,
          admin: { ...(adminTr[code] || adminTr.en), ...(adminTr2[code] || adminTr2.en) },
          help: helpTr[code] || helpTr.en,
          modelAcc: modelAccTr[code] || modelAccTr.en,
          authHeaders: authHeadersTr[code] || authHeadersTr.en,
          adminTokens: adminTokensTr[code] || adminTokensTr.en,
          adminDash: adminDashTr[code] || adminDashTr.en,
          audit: auditTr[code] || auditTr.en,
          actCodes: actCodesTr[code] || actCodesTr.en,
          plans: plansTr[code] || plansTr.en,
          admins: adminsTr[code] || adminsTr.en,
          apiKeys: apiKeysTr[code] || apiKeysTr.en,
          gravitee: graviteeTr[code] || graviteeTr.en,
          cpw: cpwTr[code] || cpwTr.en,
          clients: clientsTr[code] || clientsTr.en,
          weather: weatherTr[code] || weatherTr.en,
          prodAlerts: prodAlertsTr[code] || prodAlertsTr.en,
          airQuality: airQualityTr[code] || airQualityTr.en,
          confidence: confidenceTr[code] || confidenceTr.en,
          archive: archiveTr[code] || archiveTr.en,
          revenue: revenueTr[code] || revenueTr.en,
          horizon: horizonTr[code] || horizonTr.en,
          carbon: carbonTr[code] || carbonTr.en,
          revenueChart: revenueChartTr[code] || revenueChartTr.en,
        },
      },
    ];
  })
);
